import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Upload, DollarSign, Users, AlertTriangle, TrendingDown, FileSpreadsheet, Download, Trash2, CheckCircle, ChevronRight, ChevronLeft, ChevronDown, ArrowUpRight, ArrowDownRight, FileText, Maximize2, X, Play, TrendingUp, Phone, UserPlus, Calendar as CalendarIcon, Layers, ClipboardSignature, Stethoscope, Activity, FilePlus, MessageSquare, MoreHorizontal, Copy, Send, ArrowRight, Bell, Flame, Settings as SettingsGearIcon, Volume2, Bot, Sparkles, Plus, Briefcase, ShieldCheck, Wand2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend, PieChart, Pie, Cell, ReferenceLine } from 'recharts';
// xlsx (~200 KB gzipped) is dynamic-imported at the two call sites
// below so it stays out of the initial bundle. Only carrier-statement
// upload + MOO client-CSV parse touch it. Fix #1 in PERFORMANCE.md.
// (Vercel Blob client import removed — AgentPhotoField now uses
// Supabase Storage. The package is still used by AcaWorkspaceView.)
import PolicyDeliveryModal from './clients/PolicyDeliveryModal';
import ClientRequirementsSection from './clients/ClientRequirementsSection';
import PendingRequirementsPanel from './clients/PendingRequirementsPanel';
import RequirementsAuditPanel from './clients/RequirementsAuditPanel';
import AddressBackfillPanel   from './clients/AddressBackfillPanel';
import ReferralAutoTouchPanel from './clients/ReferralAutoTouchPanel';
import PendingDeliveriesPanel from './clients/PendingDeliveriesPanel';
import DeliveryAuditPanel from './clients/DeliveryAuditPanel';
import AutomationControlPanel from './clients/AutomationControlPanel';
import TwilioHealthPanel from './clients/TwilioHealthPanel';
import GmailConnectionPanel from './clients/GmailConnectionPanel';
import ClientWorkflowsPanel from './clients/ClientWorkflowsPanel';
import ContactWorkflowCard from './clients/ContactWorkflowCard';
import AnchorIssuesView    from './clients/AnchorIssuesView';
import TalkTrackPanel       from './calls/TalkTrackPanel';
import CallConsole          from './profile-tasks/CallConsole';
import BookOfBusinessBoard  from './cockpit/BookOfBusinessBoard';
// Fix #2 — lazy-load these view files so they ship as separate
// chunks. They only render on specific tabs / auth states, so every
// user pays for them today even if they never open the tab. Grouped
// into a single Suspense boundary at the tab-router mount site.
import { lazyWithRetry } from './lib/lazyWithRetry';
import { insertClientSafe, updateClientSafe } from './lib/clientWrite';
const NudgePanel = lazyWithRetry(() => import('./nova/NudgePanel'));
const TerritoryMap = lazyWithRetry(() => import('./territory/TerritoryMap'));
const AiDripBuilderModal = lazyWithRetry(() => import('./drips/AiDripBuilderModal'));
const AiStaffCenter = lazyWithRetry(() => import('./ai-staff/AiStaffCenter'));
// CockpitShell + MissionControlHome no longer imported here — the shell
// pattern was retired when the Cockpit sidebar became permanent (see
// COCKPIT_ROUTES below). The classic top-level render is now the "main"
// content and CockpitSidebar renders as a fixed left column.
const CockpitSidebar = lazyWithRetry(() => import('./cockpit/LeftSidebar'));
const CockpitGlobalTopBar = lazyWithRetry(() => import('./cockpit/GlobalTopBar'));
// SFA HQ — persistent operational strip above the cockpit top bar.
// Real counts (employees on-duty + pending approvals) via
// HqStatusBarLive wrapper. See src/hq/theme.js + SFA_HQ_HANDOFF.md.
const HqStatusBarLive = lazyWithRetry(() => import('./hq/HqStatusBarLive'));
// Cinematic once-per-day arrival surface. Full command experience
// with narrative + tiles + CTAs + ON DUTY strip over the HQ interior.
// Interactive dismiss (not auto-timer) — matches approved mockup #1.
const HqArrivalSurface = lazyWithRetry(() => import('./hq/HqArrivalSurface'));
// Persistent HQ interior mood-layer — sits behind the whole cockpit.
const HqBackdrop = lazyWithRetry(() => import('./hq/HqBackdrop'));
const HqAmbientBackdrop = lazyWithRetry(() => import('./hq/HqAmbientBackdrop'));
// HqBackdrop is `position:fixed; z-index:0` with a SOLID dark base, so it
// paints OVER any tab content that isn't lifted into its own stacking context.
// Only the cinematic <Room>-wrapped tabs render their content above it (Room's
// content slot is z-index:2); every OTHER (classic, light-themed) tab is
// non-positioned and gets fully covered → renders blank. Fix: mount the mood
// backdrop ONLY on the cinematic Room tabs. Classic tabs then show on their
// normal light workspace (#f4f7fb), exactly as designed. This Set MUST list
// every tab whose block is wrapped in <Room …> (see the render section below);
// a missing Room id just loses the photo mood layer (Room's own RoomEnvironment
// still shows), while a wrongly-added classic id would render blank — so keep
// it in sync with the <Room> blocks, erring toward omission.
const HQ_ROOM_TABS = new Set([
  'command_center', 'leads_intelligence', 'messages_intelligence', 'call_queue',
  'phone', 'client_intelligence', 'revenue_intelligence', 'compliance_intelligence',
  'rep_performance', 'pnl', 'kanban', 'calendar', 'settings',
  // 2026-07-17: `operations_floor` (AI Workforce room) and `smart_profile`
  // (Client Room 06) each render their own cinematic scene at full viewport.
  // Adding them here suppresses the body-root `url(/hq/hq-floor.jpg)`
  // classic backdrop so it doesn't leak a different-scene strip above
  // their scenes right beneath the sticky header (glitch reported on
  // AI Workforce, screenshot 2026-07-17). HqBackdrop's mood layer is
  // safe under both — it's `position:fixed z-index:0` with heavy scrim.
  'operations_floor', 'smart_profile', 'command_briefing', 'lead_profile', 'tasks',
  'retention', 'hierarchy_map', 'chargebacks', 'book_snapshot_v2', 'statements_v2', 'persistency_v2', 'carrier_intel', 'audit_log', 'overrides_v2', 'performance_v2',
  'agency_onboarding', 'ai_comms_setup', 'payouts_v2', 'applications_v2',
]);
// HQ environment intensity (arrival/department/focus). Not lazy —
// consumed by HqBackdrop; the context must be present synchronously.
import { HqEnvironmentProvider } from './hq/HqEnvironment';
import HqViewModeToggle from './hq/HqViewModeToggle';
import Room from './hq/Room';
import RoomEntrance from './hq/RoomEntrance';
import { useHqRealtime } from './hq/useHqRealtime';
import { useHqPathRouting } from './hq/useHqPathRouting';
const CommandCenterGrid = lazyWithRetry(() => import('./cockpit/CommandCenterGrid'));
const ClientRoom = lazyWithRetry(() => import('./clients/ClientRoom'));
const PhoneCommandRoom = lazyWithRetry(() => import('./phone/PhoneCommandRoom'));
const WorkspaceContextBar = lazyWithRetry(() => import('./workspace/WorkspaceContextBar'));
const StartMyDay = lazyWithRetry(() => import('./day/StartMyDay'));
const OsHealthView = lazyWithRetry(() => import('./health/OsHealthView'));
const TelephonyHealthView = lazyWithRetry(() => import('./health/TelephonyHealthView'));
const PhoneMessagingStatus = lazyWithRetry(() => import('./settings/PhoneMessagingStatus'));
const RevenueVaultRoom = lazyWithRetry(() => import('./revenue/RevenueVaultRoom'));
const ComplianceVaultRoom = lazyWithRetry(() => import('./compliance/ComplianceVaultRoom'));
const CalendarRoom = lazyWithRetry(() => import('./calendar/CalendarRoom'));
import TodayCommandView from './calendar/TodayCommandView';
const ReportsRoom = lazyWithRetry(() => import('./reports/ReportsRoom'));
const PipelineRoom = lazyWithRetry(() => import('./pipeline/PipelineRoom'));
const SettingsRoom = lazyWithRetry(() => import('./settings/SettingsRoom'));
const MessagesRoom = lazyWithRetry(() => import('./messages/MessagesRoom'));
const AiWorkforceRoom = lazyWithRetry(() => import('./workforce/AiWorkforceRoom'));
const LeadRoom = lazyWithRetry(() => import('./leads/LeadRoom'));
const TasksRoom = lazyWithRetry(() => import('./tasks/TasksRoom'));
const RetentionRoom = lazyWithRetry(() => import('./retention/RetentionRoom'));
const HierarchyCommissionMap = lazyWithRetry(() => import('./hierarchy/HierarchyCommissionMap'));
const WelcomeCallCenter = lazyWithRetry(() => import('./success/WelcomeCallCenter'));
const LifeBeneficiariesPanel = lazyWithRetry(() => import('./life/LifeBeneficiariesPanel'));
const MedicarePlanDetailsPanel = lazyWithRetry(() => import('./health/MedicarePlanDetailsPanel'));
const WorkflowCommandCenter = lazyWithRetry(() => import('./workflows/WorkflowCommandCenter'));
const ChargebacksRoom = lazyWithRetry(() => import('./chargebacks/ChargebacksRoom'));
const LeadSourceROIView = lazyWithRetry(() => import('./marketing/LeadSourceROIView'));
const AcaFollowUpsCommand = lazyWithRetry(() => import('./followups/AcaFollowUpsCommand'));
const AcaPpoCommandCenter = lazyWithRetry(() => import('./aca/AcaPpoCommandCenter'));
const CampaignRoadmap = lazyWithRetry(() => import('./campaigns/CampaignRoadmap'));
const FieldCommand = lazyWithRetry(() => import('./field/FieldCommand'));
const LandingPages = lazyWithRetry(() => import('./landing/LandingPages'));
const ProspectQueue = lazyWithRetry(() => import('./prospects/ProspectQueue'));
const LeadDashboard = lazyWithRetry(() => import('./manager/LeadDashboard'));
const AcaOepMissionControl = lazyWithRetry(() => import('./aca/AcaOepMissionControl'));
const ProtectionCenter = lazyWithRetry(() => import('./protection/ProtectionCenter'));
const CarrierPortals = lazyWithRetry(() => import('./carriers/CarrierPortals'));
const NovaCommandCenter = lazyWithRetry(() => import('./nova/NovaCommandCenter'));
const TeamCenter = lazyWithRetry(() => import('./team/TeamCenter'));
const SystemHealth = lazyWithRetry(() => import('./admin/SystemHealth'));
const SetupChecklist = lazyWithRetry(() => import('./onboarding/SetupChecklist'));
const UniversalImport = lazyWithRetry(() => import('./import/UniversalImport'));
const MedicareReadiness = lazyWithRetry(() => import('./compliance/MedicareReadiness'));
const PeopleView = lazyWithRetry(() => import('./people/PeopleView'));
const GrowthStudio = lazyWithRetry(() => import('./growth/GrowthStudio'));
const NextBestCall = lazyWithRetry(() => import('./nextcall/NextBestCall'));
const HealthPriorityStrip = lazyWithRetry(() => import('./health/HealthPriorityStrip'));
const NoShowRecovery = lazyWithRetry(() => import('./nextcall/NoShowRecovery'));
const BookingScorecard = lazyWithRetry(() => import('./analytics/BookingScorecard'));
const LifeLeadsView = lazyWithRetry(() => import('./life/LifeLeadsView'));
const TasksExecutionQueue = lazyWithRetry(() => import('./tasks/TasksExecutionQueue'));
const CalendarRoomView = lazyWithRetry(() => import('./calendar/CalendarRoomView'));
const DataIntelligenceCenterView = lazyWithRetry(() => import('./dataimport/DataIntelligenceCenterView'));
const ClientCommandCenter = lazyWithRetry(() => import('./clients/ClientCommandCenter'));
const ReconciliationCenterView = lazyWithRetry(() => import('./dataimport/ReconciliationCenterView'));
const ReviewQueueView = lazyWithRetry(() => import('./dataimport/ReviewQueueView'));
const BookSnapshotRoom = lazyWithRetry(() => import('./book/BookSnapshotRoom'));
const BookOfBusinessCommand = lazyWithRetry(() => import('./book/BookOfBusinessCommand'));
const AiWorkforceCommand = lazyWithRetry(() => import('./aiworkforce/AiWorkforceCommand'));
const StatementsRoom = lazyWithRetry(() => import('./statements/StatementsRoom'));
const PersistencyRoom = lazyWithRetry(() => import('./persistency/PersistencyRoom'));
const CarrierIntelligenceRoom = lazyWithRetry(() => import('./carriers/CarrierIntelligenceRoom'));
const AuditLogRoom = lazyWithRetry(() => import('./audit/AuditLogRoom'));
const OverridesRoom = lazyWithRetry(() => import('./overrides/OverridesRoom'));
const PerformanceRoom = lazyWithRetry(() => import('./performance/PerformanceRoom'));
const AgencyOnboardingWizard = lazyWithRetry(() => import('./onboarding/AgencyOnboardingWizard'));
const AiCommunicationsWizard = lazyWithRetry(() => import('./onboarding/AiCommunicationsWizard'));
const PayoutsRoom = lazyWithRetry(() => import('./payouts/PayoutsRoom'));
const ApplicationsRoom = lazyWithRetry(() => import('./applications/ApplicationsRoom'));
const QuickTask = lazyWithRetry(() => import('./hq/QuickTask'));
// Glass-elevator transition between "floor-level" tabs.
const HqElevator = lazyWithRetry(() => import('./hq/HqElevator'));
// Branded confirmation dialog (replaces native window.confirm calls).
import { HqConfirmMount, hqConfirm } from './hq/HqConfirmDialog';
// Quick Lead Upload — Fast Add Drawer, N-key from anywhere.
const FastAddDrawer = lazyWithRetry(() => import('./leads/FastAddDrawer'));
// Bulk Upload — CSV/Excel/Sheets/paste with AI column mapping + ARIA briefing.
const BulkUploadPanel = lazyWithRetry(() => import('./leads/BulkUploadPanel'));
// AI Note Intelligence — structured extraction shown on the lead detail
// drawer whenever a lead has notes.
const NoteIntelligencePanel = lazyWithRetry(() => import('./leads/NoteIntelligencePanel'));
// Expose globally so async callers can `import { hqConfirm }` or call
// via window when static analysis is easier.
if (typeof window !== 'undefined') window.hqConfirm = hqConfirm;
// Live AI activity flow — small strip that shows real handoffs between
// AI employees (light traveling between portraits). Poll-driven.
const ActivityFlow = lazyWithRetry(() => import('./hq/ActivityFlow'));
// Nova — voice-first executive assistant. Provider wraps the shell +
// mounts the Siri-style conversation overlay. NovaOrb (the corner
// launcher button) is no longer imported here — see the mount-site
// comment below for why; the file itself is untouched.
const NovaProvider = lazyWithRetry(() => import('./nova/NovaContext').then(m => ({ default: m.NovaProvider })));
const NovaOverlay = lazyWithRetry(() => import('./nova/NovaOverlay'));

// Cockpit → classic tab routing table. Sidebar `id`s are stable Cockpit
// intents; this maps each to the classic tab id that renders the actual
// content.
//
// The default map is workspace-agnostic (Life-flavored). When the
// subscriber's workspaceMode === 'health', HEALTH_COCKPIT_ROUTES
// overrides those entries so every sidebar click lands on the health-
// scoped surface (health_leads, health_clients, health_commissions, etc)
// instead of throwing a health-only agent into Life Insurance. Fixes
// the founder-note complaint: "You click lead, just throwing them over
// to life insurance. If someone's health only, everything needs to be
// operable within only health."
const COCKPIT_ROUTES = {
  // 'command' — Phase 4 (2026-07-15) routes both Life + Health to the
  // new unified CommandCenterView (`command_center` tab). The prior
  // per-workspace split (Life → 'home', Health → 'health_command_center')
  // is retired; CommandCenterView adapts based on workspaceMode.
  // Old routes stay reachable directly by tab id for fallback.
  attention:       'attention',
  command:         'command_center',
  // Life Insurance group (2026-07-17): surface the redesigned Life
  // Command Center + Book of Business (gold cinematic) directly in the
  // cockpit sidebar so they're reachable (they previously lived only on
  // classic tabs the cockpit nav never routed to).
  life_command:    'life_command',
  life_book:       'clients',
  // Sidebar "Conversations" → same 'inbox' tab as top nav → single
  // unified surface. Both entry points render CockpitInbox (Life) or
  // HealthInboxView (Health) via the shared tab === 'inbox' branch.
  conversations:   'messages_intelligence',
  phone:           'phone',
  calendar:        'calendar',
  tasks:           'tasks',
  // Phase 5 (2026-07-16): leads intent now lands on the new
  // opportunity-first LeadsIntelligenceView. Old 'leads' tab still
  // reachable directly during the transition window.
  leads:           'leads_intelligence',
  // Phase 6 (2026-07-16): messages + call queue as opportunity-first
  // intelligence surfaces. Old 'inbox' / 'dial' still reachable.
  messages_intel:  'messages_intelligence',
  call_queue:      'call_queue',
  pipeline:        'kanban',
  opportunities:   'follow_up',
  // Phase 7 (2026-07-16): clients intent lands on new ClientIntelligenceView.
  // Old 'clients' tab (grid) still reachable directly.
  clients:         'client_intelligence',
  active_book:     'book_of_business',
  aca_imports:     'aca_imports',
  renewals:        'health_renewals',
  retention:       'retention',
  hierarchy_map:   'hierarchy_map',
  chargebacks:     'chargebacks',
  book_snapshot_v2: 'book_snapshot_v2',
  statements_v2:   'statements_v2',
  persistency_v2:  'persistency_v2',
  carrier_intel:   'carrier_intel',
  audit_log:       'audit_log',
  overrides_v2:    'overrides_v2',
  performance_v2:  'performance_v2',
  agency_onboarding: 'agency_onboarding',
  ai_comms_setup:  'ai_comms_setup',
  medicare_command:   'medicare_command',
  medicare_benefits:  'medicare_benefits',
  medicare_sep:       'medicare_sep',
  medicare_referrals: 'medicare_referrals',
  medicare_twin:      'medicare_twin',
  aca_command:        'aca_command',
  aca_workspace:      'aca',
  aca_plan_intelligence: 'aca_plan_intelligence',
  payouts_v2:      'payouts_v2',
  applications_v2: 'applications_v2',
  client_movement: 'book_snapshot',
  // Phase 8 (2026-07-16): revenue intent lands on RevenueIntelligenceView.
  // Old 'paycalc' still reachable directly.
  revenue:         'revenue_intelligence',
  // Phase 9 (2026-07-16): compliance intent lands on ComplianceIntelligenceView.
  compliance:      'compliance_intelligence',
  // Phase 10 (2026-07-16): performance dashboard.
  team_performance: 'rep_performance',
  // Specter QA — Product Health surface.
  product_health:  'product_health',
  call_activity_audit: 'call_activity_audit',
  ai_team:         'approvals',   // sidebar "Approvals" → the real authorization queue
  reports:         'pnl',
  knowledge:       'library',
  billing:         'billing',
  launch_readiness: 'launch_readiness',
  support_lookup:  'support_lookup',
  new_signups:     'new_signups',
  system_diagnostics: 'system_diagnostics',
  va_workforce:    'va_workforce',
  operations_floor:'operations_floor',
  money_command:   'money_command',
  response_speed:  'response_speed',
  missed_ops:      'missed_ops',
  goal_planner:    'goal_planner',
  comp_setup:      'comp_setup',
  a2p_setup:       'a2p_setup',
  settings:        'settings',
};
// Health workspace overrides. Only lists items whose destination differs
// from the default; anything omitted falls through to COCKPIT_ROUTES.
const HEALTH_COCKPIT_ROUTES = {
  ...COCKPIT_ROUTES,
  // Phase 4 (2026-07-15): Health mode also routes to the unified
  // command_center. HealthCommandCenter surface stays reachable
  // directly for the transition window; will be retired once
  // CommandCenterView is verified in prod.
  command:         'command_center',
  // conversations: inherited from COCKPIT_ROUTES → 'inbox'. The unified
  // tab === 'inbox' branch renders HealthInboxView when workspaceMode
  // === 'health', so both Life and Health sidebar Conversations land
  // on the same tab id but see workspace-appropriate content.
  // Legacy 'health_inbox' tab still works directly for old bookmarks.
  calendar:        'health_calendar',
  leads:           'health_leads',
  pipeline:        'health_enrollment',
  opportunities:   'health_opportunities',
  clients:         'health_clients',
  active_book:     'health_clients',
  retention:       'health_at_risk',
  client_movement: 'health_lifecycle',
  revenue:         'health_commissions',
  compliance:      'health_consents',
  reports:         'health_dashboard',
};
function pickCockpitRoutes(workspaceMode) {
  return workspaceMode === 'health' ? HEALTH_COCKPIT_ROUTES : COCKPIT_ROUTES;
}
// Reverse maps for highlighting the active sidebar item when the classic
// tab changes via other means (top nav, deep link, etc.). Two variants
// so a health-only user landing on `health_leads` still lights up the
// "Leads" sidebar item — not nothing.
const CLASSIC_TO_COCKPIT = Object.fromEntries(
  Object.entries(COCKPIT_ROUTES).map(([k, v]) => [v, k])
);
const CLASSIC_TO_COCKPIT_HEALTH = Object.fromEntries(
  Object.entries(HEALTH_COCKPIT_ROUTES).map(([k, v]) => [v, k])
);
function pickReverseMap(workspaceMode) {
  return workspaceMode === 'health' ? CLASSIC_TO_COCKPIT_HEALTH : CLASSIC_TO_COCKPIT;
}
// MissionControlHome import retired — the overlay pattern that used it
// was replaced by the permanent CockpitSidebar. Health Command Center
// is now the Cockpit's "command" landing surface via COCKPIT_ROUTES.
const BookOfBusinessCommandCenter = lazyWithRetry(() => import('./cockpit/BookOfBusinessCommandCenter'));
const AcaImportsView = lazyWithRetry(() => import('./cockpit/AcaImportsView'));
const CallActivityAuditView = lazyWithRetry(() => import('./cockpit/CallActivityAuditView'));
const SupportLookupView = lazyWithRetry(() => import('./cockpit/SupportLookupView'));
const NewSignupsView = lazyWithRetry(() => import('./cockpit/NewSignupsView'));
const SystemDiagnosticsView = lazyWithRetry(() => import('./cockpit/SystemDiagnosticsView'));
const VaWorkforceView = lazyWithRetry(() => import('./cockpit/VaWorkforceView'));
const AiWorkforceHqLegacy = lazyWithRetry(() => import('./hq/AiWorkforceRoom')); // legacy touchable AI-agent grid (renders on va_workforce tab). New HQ Room lives at ./workforce/AiWorkforceRoom, imported near the top of this file.
const OperationsFloorView = lazyWithRetry(() => import('./cockpit/OperationsFloorView'));
// SFA HQ flagship — the "walk your AI company" cinematic floor.
// Replaces the Three.js OperationsFloorView on the operations_floor tab.
// Old view is left importable in case we need to revert.
const HqOperationsFloorLive = lazyWithRetry(() => import('./hq/OperationsFloorLive'));
const MoneyCommandView = lazyWithRetry(() => import('./money-command/MoneyCommandView'));
const ResponseSpeedView = lazyWithRetry(() => import('./cockpit/ResponseSpeedView'));
const MissedOpsView = lazyWithRetry(() => import('./cockpit/MissedOpsView'));
// Six Figure OS Agency Command Center (Phase 4). Renamed from
// CommandCenterView to avoid collision with the existing dialer's
// CommandCenterView at ./dial/CommandCenterView (used for tab==='dial').
const AgencyCommandCenterView = lazyWithRetry(() => import('./cockpit/CommandCenterView'));
// Six Figure OS Leads Intelligence (Phase 5).
const LeadsIntelligenceView = lazyWithRetry(() => import('./cockpit/LeadsIntelligenceView'));
// Lead Command Center Phase 1 (2026-07-17) — the working three-zone
// table/workspace/rail system over funnel_b_leads. Distinct from
// LeadsIntelligenceView above (sectioned opportunity-first cards over
// the legacy user_state blob) — mounted at a NEW tab id so it's
// additive, not a replacement of the existing leads_intelligence route.
const LeadCommandCenter = lazyWithRetry(() => import('./leads/LeadCommandCenter'));
// Six Figure OS Phases 6–10 (2026-07-16).
const MessagesIntelligenceView = lazyWithRetry(() => import('./cockpit/MessagesIntelligenceView'));
const CallQueueView = lazyWithRetry(() => import('./cockpit/CallQueueView'));
const ClientIntelligenceView = lazyWithRetry(() => import('./cockpit/ClientIntelligenceView'));
const RevenueIntelligenceView = lazyWithRetry(() => import('./cockpit/RevenueIntelligenceView'));
// Renamed to avoid collision with pre-existing ./health/ComplianceIntelligenceView.
const AgencyComplianceIntelligenceView = lazyWithRetry(() => import('./cockpit/ComplianceIntelligenceView'));
const RepPerformanceCommandView = lazyWithRetry(() => import('./cockpit/RepPerformanceCommandView'));
// Specter QA — Product Health "Bug Wall" view.
const ProductHealthView = lazyWithRetry(() => import('./cockpit/ProductHealthView'));
const A2pRegistrationStep = lazyWithRetry(() => import('./onboarding/A2pRegistrationStep'));
const GoalPlannerView = lazyWithRetry(() => import('./cockpit/GoalPlannerView'));
const CompSetupView = lazyWithRetry(() => import('./cockpit/CompSetupView'));
const LicensingView = lazyWithRetry(() => import('./onboarding/LicensingView'));
const FollowUpDashboard = lazyWithRetry(() => import('./cockpit/FollowUpDashboard'));
const SmartClientProfile = lazyWithRetry(() => import('./cockpit/SmartClientProfile'));
const HealthCommandCenter = lazyWithRetry(() => import('./cockpit/HealthCommandCenter'));
const FocusMode = lazyWithRetry(() => import('./cockpit/FocusMode'));
const CockpitInbox = lazyWithRetry(() => import('./cockpit/CockpitInbox'));
const SalesCommandCenter = lazyWithRetry(() => import('./cockpit/SalesCommandCenter'));
const LifeCommandCenter = lazyWithRetry(() => import('./life/LifeCommandCenter'));
const LifeProtectionCommand = lazyWithRetry(() => import('./life/LifeProtectionCommand'));

// Tiny per-tab error boundary. When a single tab (Inbox, NUDGE, etc.)
// throws during render, the root error boundary used to take down
// the whole CRM with "Something went wrong. Reload the page." This
// catches the crash inside one tab + shows an inline error so the
// rest of the nav still works + the operator can see the error
// message and switch to another tab without a hard refresh.
class TabBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error('[TabBoundary]', this.props.name || 'tab', 'crash:', err, info?.componentStack);
    try {
      sentryCaptureError(err, {
        boundary: 'tab',
        tab_name: String(this.props.name || 'unknown'),
        component_stack: info?.componentStack || '',
      });
    } catch { /* swallow — an error reporter can't be allowed to throw */ }
  }
  render() {
    if (this.state.err) {
      return (
        <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-8">
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 text-sm text-red-900">
            <div className="font-bold text-base mb-1">{this.props.name || 'This tab'} crashed.</div>
            <div className="text-red-800 mb-3">
              The rest of the CRM still works — pick another tab. Reload the page if you want a clean reset.
            </div>
            <details className="text-xs text-red-700">
              <summary className="cursor-pointer font-semibold">Error details (share with support)</summary>
              <pre className="mt-2 whitespace-pre-wrap font-mono text-[11px]">{String(this.state.err?.message || this.state.err)}</pre>
            </details>
            <button onClick={() => this.setState({ err: null })} className="mt-3 bg-red-700 hover:bg-red-800 text-white text-xs font-bold px-3 py-1.5 rounded">Retry</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
// Demo Mode (Interactive Onboarding) — Phase 4 wires the shell + provider
// at the app root when subscriptions.demo_mode = true. The §2 boundary
// seal in api/_demoSeal.js prevents any external side effect from a
// demo session, so this wrapping is safe even before screens consume
// the DemoDataProvider (Phase 3a).
import useDemoModeFlag      from './demo/useDemoModeFlag';
// DemoDataProvider brings the 60 KB DEMO_SEED with it — lazy-load
// both, only demo-mode operators pay for them (and the seed).
const DemoDataProvider = lazyWithRetry(() => import('./demo/DemoDataProvider'));
const DemoModeShell    = lazyWithRetry(() => import('./demo/DemoModeShell'));
import DemoLaunchAdminCard  from './demo/DemoLaunchAdminCard';
import { buildLiveLeads, buildLiveCalls, buildLiveCalendar, demoClients, demoTransactions, demoLifeThreads } from './demo/liveAdapters';
import AnchorClientPanel   from './clients/AnchorClientPanel';
const LedgerDashboard = lazyWithRetry(() => import('./ledger/LedgerDashboard'));
import { useAuth } from './auth/AuthContext';
import { sessionPreflight } from './auth/sessionPreflight';
import LeadsMorningBriefBanner from './leads/LeadsMorningBriefBanner';
import LeadsOpportunityFeed from './leads/LeadsOpportunityFeed';
import LeadsForecastPanel from './leads/LeadsForecastPanel';
import LeadsAIAlertsPanel from './leads/LeadsAIAlertsPanel';
import LeadsWorkThisListButton from './leads/LeadsWorkThisListButton';
import LeadsAIFleetPanel from './leads/LeadsAIFleetPanel';
import LeadsNovaFloatingChat from './leads/LeadsNovaFloatingChat';
import { LeadIntelligenceProvider, useLeadInsight } from './leads/LeadIntelligenceContext';
// Auth screens are on the CRITICAL LOGIN PATH — kept as static imports
// so a chunk-fetch failure can't lock users out. Lazy-loading saved
// ~21 KB gzipped but bricked login when the auth chunk failed to load
// (transient CDN fault, mid-deploy hash mismatch, etc.). Trade the
// small bundle savings for a login page that always renders.
import LoginScreen          from './auth/LoginScreen';
import SignupScreen         from './auth/SignupScreen';
import ResetPasswordScreen  from './auth/ResetPasswordScreen';
import SetNewPasswordScreen from './auth/SetNewPasswordScreen';
import MfaEnrollmentCard    from './auth/MfaEnrollmentCard';
const AtRiskClientsView        = lazyWithRetry(() => import('./health/AtRiskClientsView'));
const CrossSellOpportunitiesView = lazyWithRetry(() => import('./health/CrossSellOpportunitiesView'));
const HealthWarRoomView        = lazyWithRetry(() => import('./health/HealthWarRoomView'));
import { userStorage, getCurrentUserId, setCurrentUser as setStorageUser } from './lib/userStorage';
import { captureError as sentryCaptureError, setSentryUser } from './lib/sentry';
import { loadSnapshot as loadCloudSnapshot, snapshotGet, snapshotIsReady, cloudSet, clearSnapshot } from './lib/cloudBackup';
import { bearer } from './lib/authedFetch';
import { useAria } from './aria/AriaContext';
import SmsComposeModal from './messages/SmsComposeModal';
import { listMyMemberships, listMembers, listInvites, createInvite, revokeInvite } from './lib/orgClient';
import AffiliatesAdminView from './referrals/AffiliatesAdminView';
// PartnerPortalView (legacy custom Partner OS — Bronze/Silver/Gold +
// Stripe Connect transfers) is retired in favor of FirstPromoter. The
// component file remains for historical lookup; no live nav mounts it.
import MatchReviewView   from './cross-ref/MatchReviewView';
import ReconReportView   from './recon/ReconReportView';
import UnderwritingAgentView from './uw/UnderwritingAgentView';
import HealthHouseholdPanel  from './health/HealthHouseholdPanel';
import HealthProfileCockpit  from './health/HealthProfileCockpit';
import MedicalProfilePanel   from './health/MedicalProfilePanel';
import ClientAITimeline      from './health/ClientAITimeline';
import TurningSixtyFiveCountdown from './health/TurningSixtyFiveCountdown';
import AcaComplianceCheckPanel from './health/AcaComplianceCheckPanel';
import ClientDocumentVaultPanel from './health/ClientDocumentVaultPanel';
import PreCallBriefingPanel     from './health/PreCallBriefingPanel';
import AnnualReviewWizard       from './health/AnnualReviewWizard';
import { MissingInfoDetector }  from './health/ComplianceReadinessMeter';
import { RelationshipBadge, RelationshipDetailPanel, computeRelationshipScore, loadRelationshipInputs } from './health/RelationshipMeter';
import {
  OverviewInsuranceBlock, OverviewAddressBlock,
  MedicalTabView, HouseholdCoverageMap, CaregiverPortalBlock,
  RelationshipTabView, ComplianceChecklistBlock,
  DocumentChecklistBlock, HealthTimelineBlock,
  CrossSellOpportunitiesBlock,
} from './health/ClientModalTabs';
const HealthClientsListView     = lazyWithRetry(() => import('./health/HealthClientsListView'));
const MedicareCommandView = lazyWithRetry(() => import('./health/MedicareCommandView'));
const CommissionVarianceView = lazyWithRetry(() => import('./health/CommissionVarianceView'));
const MedicareDataConnectionsView = lazyWithRetry(() => import('./health/MedicareDataConnectionsView'));
const MedicareClientProfileView = lazyWithRetry(() => import('./health/MedicareClientProfileView'));
const MedicarePlanIntelligenceView = lazyWithRetry(() => import('./health/MedicarePlanIntelligenceView'));
const CoverageGuideView = lazyWithRetry(() => import('./coverage/CoverageGuideView'));
const FindCareView = lazyWithRetry(() => import('./health/FindCareView'));
const AttentionCommandView = lazyWithRetry(() => import('./command/AttentionCommandView'));
const MedicareReviewsView = lazyWithRetry(() => import('./health/MedicareReviewsView'));
const SoaComplianceView = lazyWithRetry(() => import('./health/SoaComplianceView'));
const SoaScriptsView = lazyWithRetry(() => import('./health/SoaScriptsView'));
const SoaSessionView = lazyWithRetry(() => import('./health/SoaSessionView'));
const SoaQaView = lazyWithRetry(() => import('./health/SoaQaView'));
const SoaEvidenceView = lazyWithRetry(() => import('./health/SoaEvidenceView'));
const PolicyAccessView = lazyWithRetry(() => import('./health/PolicyAccessView'));
const CarrierPortalManagerView = lazyWithRetry(() => import('./health/CarrierPortalManagerView'));
const ProductReadinessView = lazyWithRetry(() => import('./qa/ProductReadinessView'));
const PlanIntakeView = lazyWithRetry(() => import('./health/PlanIntakeView'));
const LapseWatchView = lazyWithRetry(() => import('./health/LapseWatchView'));
const AcaRetentionCenterView = lazyWithRetry(() => import('./health/AcaRetentionCenterView'));
const AcaComplianceView = lazyWithRetry(() => import('./health/AcaComplianceView'));
const ImportAnalystView = lazyWithRetry(() => import('./import/ImportAnalystView'));
const PartDPenaltyView = lazyWithRetry(() => import('./health/PartDPenaltyView'));
const CustomAgentsView = lazyWithRetry(() => import('./ai/CustomAgentsView'));
const IntegrationHealthView = lazyWithRetry(() => import('./integrations/IntegrationHealthView'));
const MedicareBenefitsView      = lazyWithRetry(() => import('./health/MedicareBenefitsView'));
const MedicareSEPTrackerView    = lazyWithRetry(() => import('./health/MedicareSEPTrackerView'));
const MedicareReferralsView     = lazyWithRetry(() => import('./health/MedicareReferralsView'));
const MedicareDigitalTwinView   = lazyWithRetry(() => import('./health/MedicareDigitalTwinView'));
const ComplianceReadinessLandingView = lazyWithRetry(() => import('./health/ComplianceReadinessLandingView'));
const SignatureHealthView       = lazyWithRetry(() => import('./health/SignatureHealthView'));
const ComplianceOpsView         = lazyWithRetry(() => import('./health/ComplianceOpsView'));
const ComplianceIntelligenceView = lazyWithRetry(() => import('./health/ComplianceIntelligenceView'));
const OneClickAuditRoomView      = lazyWithRetry(() => import('./health/OneClickAuditRoomView'));
const ClientSuccessManagerView   = lazyWithRetry(() => import('./health/ClientSuccessManagerView'));
const LifeBookManagerView        = lazyWithRetry(() => import('./health/LifeBookManagerView'));
// health/HealthCommandCenterView = the Health-workspace landing (rendered at
// tab='health_portal', inside HealthShell). Distinct from
// cockpit/HealthCommandCenter, which renders at tab='health_command_center'.
const HealthCommandCenterView   = lazyWithRetry(() => import('./health/HealthCommandCenterView'));
const HealthComplianceLandingView = lazyWithRetry(() => import('./health/HealthComplianceLandingView'));
const HealthRenewalsLandingView   = lazyWithRetry(() => import('./health/HealthRenewalsLandingView'));
const HealthOpportunitiesLandingView = lazyWithRetry(() => import('./health/HealthOpportunitiesLandingView'));
import ClientComplianceScorePanel from './health/ClientComplianceScorePanel';
import ClientHealthScoreBadge from './health/ClientHealthScoreBadge';
import ClientFormsPanel      from './health/ClientFormsPanel';
import AnocUploadBlock       from './health/AnocUploadBlock';
const MorningBriefingView   = lazyWithRetry(() => import('./health/MorningBriefingView'));
const AiCeoBoardroom        = lazyWithRetry(() => import('./health/AiCeoBoardroom'));
const WarRoomView           = lazyWithRetry(() => import('./health/WarRoomView'));
const AskAriaView           = lazyWithRetry(() => import('./health/AskAriaView'));
import TasksView             from './tasks/TasksView';
const CommandCenterView = lazyWithRetry(() => import('./dial/CommandCenterView'));
import RetainAgentView       from './retain/RetainAgentView';
import AtlasAgentView        from './atlas/AtlasAgentView';
import UpliftAgentView       from './uplift/UpliftAgentView';
import A2pSetupView          from './registrar/A2pSetupView';
import FunnelBView           from './funnelB/FunnelBView';
import AgentScorecardView    from './scorecard/AgentScorecardView';
import EnrollAgentView       from './medicare/EnrollAgentView';
import SOATrackingView       from './medicare/SOATrackingView';
import AuditPanel             from './medicare/AuditPanel';
import AdminInfraStatusBanner from './admin/AdminInfraStatusBanner';
import NovaNurtureView       from './medicare/NovaNurtureView';
import EmailComposeView      from './email/EmailComposeView';
import EmailDripsView        from './email/EmailDripsView';
import AiWalletView          from './wallet/AiWalletView';
import AgentToolApprovalsPanel from './agent-tools/AgentToolApprovalsPanel';
import ApprovalCenter          from './agent-tools/ApprovalCenter';
import AgentTrainingPanel      from './ai-agents/AgentTrainingPanel';
import AgentDigestPanel        from './digests/AgentDigestPanel';
import SubscribePage          from './billing/SubscribePage';
import { useSubscription }    from './billing/SubscriptionContext';
import AiAgentsActivityView   from './ai-activity/AiAgentsActivityView';
import ReferralsWorkflowView from './referrals/ReferralsWorkflowView';
import KanbanPipelineView    from './pipeline/KanbanPipelineView';
import MetaAdsSettingsView   from './meta-ads/MetaAdsSettingsView';
import FacebookLeadsView     from './facebook/FacebookLeadsView';
import ForgeView             from './forge/ForgeView';
import RetainClosersView     from './retain/RetainClosersView';
import PriorityLeadsView     from './leads_priority/PriorityLeadsView';
import LeadCommandCenterHeader from './leads/LeadCommandCenterHeader';
import LeadInsuranceProfilePanel from './leads/LeadInsuranceProfilePanel';
import MedicareCoverageSnapshot from './leads/MedicareCoverageSnapshot';
import LeadMemoryPanel           from './leads/LeadMemoryPanel';
import LeadRecommendationsPanel  from './leads/LeadRecommendationsPanel';
import LeadCommunicationCenter   from './leads/LeadCommunicationCenter';
import LeadCallIntelligence      from './leads/LeadCallIntelligence';
import LeadOpportunityPanel      from './leads/LeadOpportunityPanel';
import LeadTasksPanel            from './leads/LeadTasksPanel';
import LeadCompliancePanel       from './leads/LeadCompliancePanel';
import LeadDigitalTwinPanel      from './leads/LeadDigitalTwinPanel';
import LeadRelationshipMap       from './leads/LeadRelationshipMap';
import LeadCarrierMatchPanel     from './leads/LeadCarrierMatchPanel';
import LeadAssistantPanel        from './leads/LeadAssistantPanel';
import LeadDocumentsPanel        from './leads/LeadDocumentsPanel';
import AgencyAnalyticsView   from './agency_analytics/AgencyAnalyticsView';
import ReferralPartnersView  from './referrals/ReferralPartnersView';
import MoneyFoundWidget      from './moneyFound/MoneyFoundWidget';
import MedicareWorkspaceView from './medicare/MedicareWorkspaceView';
import MedicareClientDetailView from './medicare/MedicareClientDetailView';
import AcaWorkspaceView      from './aca/AcaWorkspaceView';
import AcaPlanIntelligenceView from './aca/AcaPlanIntelligenceView';
// (HierarchyMoneyMap — the seed-only demo — retired 2026-07-26; the money_map
//  tab now renders the real-data HierarchyCommissionMap, imported lazily below.)
import FirstRunWizard from './onboarding/OnboardingWizard';
import WowOnboarding from './onboarding/WowOnboarding';
import AnchorValueTour from './onboarding/AnchorValueTour';
import ThemeToggle from './theme/ThemeToggle';
import { askAdminForHelp } from './lib/askAdminForHelp';
const PilotShell = lazyWithRetry(() => import('./onboarding/pilot/PilotShell'));
const ReferralCommandCenter = lazyWithRetry(() => import('./referrals/ReferralCommandCenter'));
import AscendStatusCard from './onboarding/AscendStatusCard';
import ObservabilityView from './admin/ObservabilityView';
import QuickstartPanel       from './onboarding/QuickstartPanel';
import SetupTour             from './onboarding/setupTour/SetupTour';
import ActivationChecklist   from './onboarding/ActivationChecklist';
import InsuranceLinesStep     from './onboarding/InsuranceLinesStep';
import { normalizeLines as normalizeInsuranceLines, linesToWorkspaceMode } from './onboarding/insuranceLines';
import { buildWizardLineSteps } from './onboarding/wizardSteps';
import { usePositiveResponseAlerts } from './hooks/usePositiveResponseAlerts';
import VATrackerView, { useSessionHeartbeat } from './va-tracker/VATrackerView';
import GoogleCalendarPanel   from './integrations/GoogleCalendarPanel';
import KixiePowerDialPanel   from './kixie/KixiePowerDialPanel';
import ImessagePanel         from './imessage/ImessagePanel';
import A2pSetupPanel         from './onboarding/A2pSetupPanel';
import DealRoomPanel         from './dealRoom/DealRoomPanel';
import CallCommandPanel      from './callCommand/CallCommandPanel';
import FollowUpPanel         from './followUp/FollowUpPanel';
// InboxBriefingCard sits above the SMS thread — lazy-loaded so only
// inbox-open users pay for its bundle weight. Composes existing Deal
// Room intelligence (score, journey, signals, executive summary).
const InboxBriefingCard = lazyWithRetry(() => import('./inbox/InboxBriefingCard'));
// PortalChoicePrompt — one-time modal that pops post-signup asking
// Life / Health / Both. Lazy-loaded — only signed-in users pay for
// it, and only until they dismiss once (flag persisted to
// user_state.workspaceModePromptSeen).
const PortalChoicePrompt = lazyWithRetry(() => import('./onboarding/PortalChoicePrompt'));
// AIBossPanel — owner-only agency dashboard. Endpoint returns 403
// for non-owners so the panel renders a "not an owner" message
// gracefully. Lazy-loaded — non-owners never pay for the chunk.
const AIBossPanel = lazyWithRetry(() => import('./aiBoss/AIBossPanel'));
// LaunchReadinessPanel — one-page green/red status board for the
// "are we ready to open public traffic" question. Owner-only.
// Runs live checks against migrations, env vars, DB, and features.
const LaunchReadinessPanel = lazyWithRetry(() => import('./admin/LaunchReadinessPanel'));
// BookSnapshotPanel — post-import executive briefing (Migration
// Engine §13). Aggregates commissions, policies, and leads into
// the "Migration Complete" wow moment. Full engine ships wave 2.
const BookSnapshotPanel = lazyWithRetry(() => import('./import/BookSnapshotPanel'));
// CommandDashboard — the "wow-factor" dashboard from Harrison's
// mockup. MVP scope: top KPI row + AI Command Briefing + Call
// Queue + Revenue Forecast. Dark theme, sales-driver visual.
const CommandDashboard = lazyWithRetry(() => import('./dashboard/CommandDashboard'));
// PowerDialerView — AI Sales Cockpit from Harrison's second mockup.
// MVP: visual shell + real queue + real disposition writes.
// Deferred to wave 2: live streaming coaching (needs new infra).
const PowerDialerView = lazyWithRetry(() => import('./dialer/PowerDialerView'));
const PhoneDialer = lazyWithRetry(() => import('./cockpit/PhoneDialer'));
// CinematicCalendarView — presentation-only redesign of the Calendar Room
// (dark glass + gold, house AI-HQ theme). Reuses the SAME booking/status/
// delete handlers as the classic CalendarView below (useCalendarActions)
// and the same AppointmentForm — see CinematicCalendarRoom wrapper.
const CinematicCalendarView = lazyWithRetry(() => import('./calendar/CinematicCalendarView'));
import QuickStartAutomationsCard from './settings/QuickStartAutomationsCard';
import SelfReminderPanel     from './integrations/SelfReminderPanel';
import OperatorTextAlertsCard from './integrations/OperatorTextAlertsCard';
import SupportChat              from './support/SupportChat';
import { fmt, fmtCompact, possessive, titleCase, formatInsuredFirstLast, fmtDateUS, fmtPhoneUS, normalizeDob, sanitizeFreeText, referralLandingUrl } from './lib/formatters';
import { parseWorkbook } from './lib/statementParser';
import { usePersistentState } from './lib/persistentState';
import { installBackspaceBackHandler, installScrollRestoration } from './lib/tabHistory';
import { bridgeDeterministicToLlm, bridgeDeterministicToUploadFlow, bridgeLlmToUploadFlow, pageItemsToLayoutText } from './lib/mooEnvelopeBridge';
import { parseDate, monthKey, monthLabel, weekKey, weekKeyFromDate, weekBounds, weekLabel, weekShortLabel, upcomingWeekKeys } from './lib/dates';
import {
  CHARGEBACK_TYPES,
  classifyLifeBook,
  computePersistencyWindows,
  groupChargebackPolicies,
  computeConservationExposure,
  deriveChargebackRootCause,
} from './lib/lifeExposure';
import { K } from './lib/storageKeys';
import { supabase } from './lib/supabase';
import { FEX_CARRIERS, FEX_TIERS, FEX_CONDITIONS, rankFexCarriers } from './lib/fexCarrierPlacement';
import { medicationMatches } from './lib/underwritingMedications';
import { resolvePhraseAliases, indicesForFragments, assessHealthInsurability, CONDITION_BUCKETS } from './lib/phraseAliases';
import { TERM_CARRIERS, TERM_TIERS, TERM_CONDITIONS, rankTermCarriers } from './lib/termUnderwriting';
import { FFL_LEVELS, FFL_ANNUITY_LEVELS, FFL_COMMISSION_RATES, FFL_CARRIERS, calcCommission, fmtMoney as fmtMoneyFFL, sanitizeMoneyInput } from './lib/fflCommissionRates';
import { LIFECYCLE_TOUCHPOINTS, seedDefaultTemplatesIfEmpty } from './lib/lifecycleTemplates';
import { HEALTH_CARRIERS, HEALTH_PLAN_TYPES, HEALTH_PLAN_TYPE_BY_ID, activeOrNextWindow } from './lib/healthCarriers';
import { FTPIG_PRODUCTS, FTPIG_CATEGORIES, COMPLIANCE_FLAGS, checkEligibility } from './lib/ftpigUnderwritingData';
import { COVERAGE_TEMPLATES, TEMPLATE_BY_ID, COVERAGE_SCENARIOS, providerLinks } from './lib/coverageTemplates';
import { recommendStack, getPresets, getIntakeQuestions, STACK_STRATEGY, STRATEGY_PRODUCT_MAP, STRATEGY_VIRTUAL_PRODUCTS } from './lib/ftpigStackingStrategy';
import {
  leadStatusLabel, leadStatusBadgeClass,
  POLICY_LABEL, POLICY_BADGE_CLASS,
  badgeClassTone,
} from './lib/statusVocabulary';
import {
  createConsent, revokeConsent, fetchConsentsForTarget, fetchOwnerConsents,
  sweepExpiredConsents, DEFAULT_CONSENT_TEXT, CONSENT_SCOPE_LABEL, buildSignUrl,
  formatSignDateLong,
} from './lib/consent';
import {
  createSoa, agentCountersign, fetchOwnerSoas, sweepExpiredSoas,
  acknowledgeClientSigned, buildSoaUrl,
  SOA_DISCLOSURE, SOA_SIGNATURE_BLOCK, SOA_PRODUCT_CATALOG, SOA_PRODUCT_LABEL,
  SOA_CONTACT_METHODS, SOA_RETENTION_NOTE, SOA_FORM_ID, formatSoaSignDateLong,
} from './lib/soa';
import { loadOwnerMatcherContext } from './lib/clientMatcher';
import { planImport, commitImport as plannerCommitImport } from './lib/importPlanner';
import { audit, AUDIT_ACTION, fetchAuditTimeline, fetchOwnerActivity } from './lib/auditLog';
import { parseHcscStatement, analyzeHcscRows, rowToHealthPolicyUpsert, parseHcscSummary, summaryToChargebackUpserts } from './lib/hcscStatementParser';
import { isJarvisFormat, parseJarvisCsv, jarvisRowToHealthPolicyUpsert } from './lib/jarvisParser';
import { isPlanAdvisorsFormat, parsePlanAdvisorsStatement } from './lib/planAdvisorsParser';
import { isHealthSherpaFormat, parseHealthSherpaCsv } from './lib/healthSherpaParser';
import { isUhcStatementFormat, parseUhcStatement } from './lib/uhcStatementParser';
import { commitUhcStatement } from './lib/commissionLedger';

// W8 Warm-Lead Alert — fires when a lead transitions to interested /
// appt_set. POSTs the user's session JWT so the server can resolve
// owner identity and SMS only the configured ownerPhone. Best-effort:
// if no Supabase session is available or the server returns
// skipped=no_alert_phone, we silently no-op (in-app toast still fires).
//
// Dedupe: in-memory map keeps a 10-second cooldown per (leadId,status)
// so double-saves on the same action don't double-fire.
const _warmAlertSent = new Map();
async function fireWarmLeadAlert({ leadId, leadName, status }) {
  if (!leadId || !status) return;
  const key = `${leadId}|${status}`;
  const last = _warmAlertSent.get(key) || 0;
  if (Date.now() - last < 10_000) return;
  _warmAlertSent.set(key, Date.now());

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const statusLabel = status === 'interested' ? 'Interested' : status === 'appt_set' ? 'Appt set' : status;
    const body = `Warm lead: ${leadName || 'unnamed lead'} → ${statusLabel}. Open the CRM and follow up while it's hot.`;
    await fetch('/api/notifications/agent-alert', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ body, leadId, leadName }),
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[fireWarmLeadAlert] failed:', err.message || err);
  }
}

// Detects new chargebacks in the transactions array + conservation
// events, then auto-enrolls matched leads into any active
// conservation_event workflows (e.g. the Chargeback Save 3-Touch
// Drip seeded in migration 017). saveTasks is used as the dedupe
// store — once a chargeback's key gets autoEnrolled=true, we never
// re-trigger it on subsequent reloads.
//
// Outcomes stored on the saveTask per (insured|policy) key:
//   autoEnrolled: true                              — gate against re-fire
//   autoEnrollReason: 'enrolled' | 'no_matching_lead'
//                     | 'lead_missing_consent_or_phone' | 'no_workflows'
//   leadId: matched lead's id (when enrolled)
//
// The function is best-effort and silent — if Supabase is unreachable
// or no workflows match, we mark the key as autoEnrolled with the
// reason so the Retention hub can surface why the SMS path wasn't
// taken and the VA can still call.
async function autoEnrollNewChargebacks({ transactions, conservation, leads, saveTasks, ownerId }) {
  if (!ownerId || !Array.isArray(transactions)) return {};

  const now = Date.now();
  const DAY = 1000 * 60 * 60 * 24;
  const cutoff = now - 90 * DAY;
  const candidates = {};

  for (const tx of transactions) {
    const isCb = (typeof CHARGEBACK_TYPES !== 'undefined' && CHARGEBACK_TYPES?.has?.(tx.activityType))
              || ((tx.netToAccount || 0) < 0);
    if (!isCb) continue;
    const d = parseDate(tx.activityDate);
    if (!d || d.getTime() < cutoff) continue;
    const key = `${(tx.insured || 'Unknown').toUpperCase().trim()}|${tx.policy || ''}`;
    if (!candidates[key]) candidates[key] = { insured: tx.insured || '', policy: tx.policy || '' };
  }
  for (const ev of (conservation || [])) {
    const stat = (ev.status || '').toLowerCase();
    if (!stat.includes('laps') && stat !== 'cancel') continue;
    const insured = ev.insured || ev.clientName || '';
    if (!insured) continue;
    const key = `${insured.toUpperCase().trim()}|${ev.policy || ''}`;
    if (!candidates[key]) candidates[key] = { insured, policy: ev.policy || '' };
  }

  // Only fresh ones.
  const fresh = Object.entries(candidates).filter(([k]) => !saveTasks?.[k]?.autoEnrolled);
  if (fresh.length === 0) return {};

  // Pull active conservation_event workflows once. Filter out any
  // workflow whose name contains "win-back" — those are handled by
  // /api/workflow/scan-winback which delays enrollment to Day 30+
  // so the immediate Chargeback Save 3-Touch can run uninterrupted.
  let workflows = [];
  try {
    const { data } = await supabase
      .from('workflows')
      .select('id, name')
      .eq('active', true)
      .eq('trigger', 'conservation_event');
    workflows = (data || []).filter(w => !/win-back/i.test(w.name || ''));
  } catch { /* silent */ }

  const updates = {};
  // Pre-compute an upper-trim name → lead Map once. Previously this
  // loop did leads.find() per policy — at 500 policies + 2000 leads
  // that's 1M comparisons, locking the UI for ~1-2s on a typical
  // HCSC statement import.
  const leadByUpperName = new Map(
    (leads || []).map(l => [String(l.name || '').toUpperCase().trim(), l])
  );
  for (const [key, c] of fresh) {
    // Match by upper-trim name across leads.
    const target = String(c.insured).toUpperCase().trim();
    const lead = leadByUpperName.get(target);

    if (!lead) {
      updates[key] = { autoEnrolled: true, autoEnrollReason: 'no_matching_lead', detectedAt: new Date().toISOString() };
      continue;
    }
    if (!lead.phone_e164 || lead.consent !== true || lead.opted_out === true) {
      updates[key] = {
        autoEnrolled: true,
        autoEnrollReason: 'lead_missing_consent_or_phone',
        leadId: lead.id,
        detectedAt: new Date().toISOString(),
      };
      continue;
    }
    if (workflows.length === 0) {
      updates[key] = {
        autoEnrolled: true,
        autoEnrollReason: 'no_workflows',
        leadId: lead.id,
        detectedAt: new Date().toISOString(),
      };
      continue;
    }

    try {
      await enrollLeadInTriggerWorkflows(lead.id, 'conservation_event', ownerId);
      updates[key] = {
        autoEnrolled: true,
        autoEnrollReason: 'enrolled',
        leadId: lead.id,
        detectedAt: new Date().toISOString(),
      };
    } catch (err) {
      updates[key] = {
        autoEnrolled: true,
        autoEnrollReason: `error:${err.message || 'unknown'}`,
        leadId: lead.id,
        detectedAt: new Date().toISOString(),
      };
    }
  }
  return updates;
}

// Loose UUID gate. Demo-mode leads carry synthetic string IDs
// (`demo-lead-001` etc.) that Postgres rejects with 22P02 when the
// column is uuid — BUG-002. Cheap regex avoids the round-trip.
function isUuidLike(v) {
  return typeof v === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
}

// Browser-driven enrollment helper for trigger types that fire on a
// UI transition (appointment_set, no_show). Best-effort: silently
// no-ops if there's no matching workflow, no active session, or the
// insert fails — workflows engagement is not on the critical path of
// the user's action.
async function enrollLeadInTriggerWorkflows(leadId, trigger, ownerId) {
  if (!leadId || !trigger || !ownerId) return;
  // BUG-002: bail on non-UUID lead IDs so demo-mode leads don't
  // trip Postgres's uuid parser (22P02) and produce a misleading
  // 401 "AI-licensing" surface for the operator.
  if (!isUuidLike(leadId)) return;
  if (!isUuidLike(ownerId)) return;
  try {
    const { data: wfs } = await supabase
      .from('workflows')
      .select('id')
      .eq('active', true)
      .eq('trigger', trigger);
    if (!wfs || wfs.length === 0) return;

    // 24-hour cooldown per (lead, workflow). Operators commonly
    // bounce a lead between statuses while booking — don't double-fire
    // the confirmation text when they save twice in five seconds.
    const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

    for (const wf of wfs) {
      const { data: prior } = await supabase
        .from('enrollments')
        .select('id')
        .eq('workflow_id', wf.id)
        .eq('lead_id', String(leadId))
        .gte('enrolled_at', cutoff)
        .limit(1)
        .maybeSingle();
      if (prior) continue;

      const { data: step1 } = await supabase
        .from('workflow_steps')
        .select('id')
        .eq('workflow_id', wf.id)
        .order('position', { ascending: true })
        .limit(1)
        .maybeSingle();
      if (!step1) continue;

      const { data: enr, error } = await supabase
        .from('enrollments')
        .insert({
          lead_id: String(leadId),
          workflow_id: wf.id,
          current_step: 1,
          status: 'active',
          owner_id: ownerId,
        })
        .select('id')
        .single();
      if (error || !enr) continue;

      await supabase.from('scheduled_actions').insert({
        enrollment_id: enr.id,
        step_id: step1.id,
        run_at: new Date().toISOString(),
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[enrollLeadInTriggerWorkflows] failed:', err.message || err);
  }
}
import { extractFieldsFromNotes, generateDailyBriefing, generateLeadBrief, smartSearchToFilters, generateAgencyNarrative, generatePolicyComparison, generateObjectionResponse, reviewMessageCompliance, generateFollowUpDraft, generateChargebackTalkingPoints, generateRetentionCheckIn, authHeaders } from './lib/aiActions';
import {
  parseConservationReport,
  conservationEventId,
  daysUntilTermination,
  daysUntilCureWindowCloses,
  isInChargebackWindow,
  conservationPriority,
} from './lib/parseConservation';
// In-app subscription gate + SubscribePage were removed in 316c2ee1
// (deferred to the external CRM). All plan-tier gating below is a
// no-op until billing returns in-app; the role system from migration
// 004 is independent of plan tier and works either way.

// Lazy-load PDF.js from CDN when first PDF is uploaded
let pdfjsLib = null;
const loadPdfJs = async () => {
  if (pdfjsLib) return pdfjsLib;
  if (!window.pdfjsLib) {
    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  }
  pdfjsLib = window.pdfjsLib;
  return pdfjsLib;
};

// Lazy-load Twilio Voice SDK from CDN. ~280 KB of WebRTC + media
// plumbing that only matters when the operator actually clicks Call
// on a desktop browser. By loading it on demand we don't pay the cost
// for mobile users (who get the native tel: dialer instead) or for
// users who never dial. The SDK exposes window.Twilio.Device.
//
// CDN strategy: try the canonical Twilio CDN first. If that's down
// (rare), fall back to jsDelivr's mirror of the @twilio/voice-sdk
// npm package. Both should hit the same compiled bundle.
// Map a raw Twilio Voice SDK error → a friendly user-facing string.
// Reused by both setup-time catches AND mid-call on('error') handlers
// so the same nice copy appears regardless of when the error fires.
// Operator audit 2026-06-29 BUG-003: the raw
// "PermissionDeniedError (31401): The browser or end-user denied
// permissions to user media." was reaching the UI via the call.on('error')
// path which previously did setError(err.message) verbatim.
function friendlyTwilioError(err) {
  const msg = String(err?.message || err || '');
  const code = err?.code || (msg.match(/\b(31\d{3})\b/) || [])[1];
  if (/insufficient_credits|402/i.test(msg)) return 'OUT_OF_CREDITS';
  if (/Permission|NotAllowedError|microphone|31401|denied permissions to user media/i.test(msg)) {
    return 'Microphone permission denied. Click the camera/mic icon in your browser address bar, allow microphone access, then retry.';
  }
  if (/NotFoundError|no input audio device|input audio/i.test(msg)) {
    return 'No microphone detected. Plug one in (or pick a different input device in your system settings) and retry.';
  }
  if (/Failed to load|register/i.test(msg)) {
    return 'Could not load the dialer. Check your network and retry.';
  }
  if (/no token/i.test(msg)) {
    return 'Could not authenticate with Relay. Contact support if this persists.';
  }
  if (/31005|HANGUP|ConnectionError/i.test(msg)) {
    // 31005 = caller-ID not provisioned / destination not verified on the
    // provider account — an INFRASTRUCTURE readiness problem, never the
    // subscriber's fault. Never surface the provider name or code to them; log
    // the raw detail for admin/dev troubleshooting instead.
    try { console.warn('[dialer] RELAY_31005 — voice not provisioned:', msg); } catch { /* noop */ }
    return "Calling isn't ready yet — your business phone setup still needs attention. We've alerted our onboarding team automatically. You can keep using the rest of your CRM while we finish the setup.";
  }
  // Last resort — never show the subscriber a raw SDK string or provider
  // error code. Log it; give them a plain-language message + next step.
  try { if (code || msg) console.warn('[dialer] call setup failed:', code || '', msg); } catch { /* noop */ }
  return "We couldn't place that call. If this keeps happening, check your phone setup or contact support.";
}

// Pre-flight microphone-permission check. Returns one of:
//   'granted'  — proceed
//   'denied'   — show the friendly "click the lock icon" message
//   'prompt'   — browser will ask; proceed and let Twilio trigger it
//   'unknown'  — older browser without Permissions API; proceed.
async function checkMicPermission() {
  try {
    if (!navigator.permissions?.query) return 'unknown';
    const r = await navigator.permissions.query({ name: 'microphone' });
    return r.state || 'unknown';
  } catch { return 'unknown'; }
}

let twilioVoiceSdk = null;
const TWILIO_VOICE_SDK_VERSION = '2.10.2';
const TWILIO_VOICE_SDK_URLS = [
  `https://sdk.twilio.com/js/voice/releases/${TWILIO_VOICE_SDK_VERSION}/twilio.min.js`,
  `https://cdn.jsdelivr.net/npm/@twilio/voice-sdk@${TWILIO_VOICE_SDK_VERSION}/dist/twilio.min.js`,
];
const loadTwilioVoice = async () => {
  if (twilioVoiceSdk) return twilioVoiceSdk;
  if (window.Twilio?.Device) {
    twilioVoiceSdk = window.Twilio;
    return twilioVoiceSdk;
  }
  let lastErr = null;
  for (const url of TWILIO_VOICE_SDK_URLS) {
    try {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = resolve;
        script.onerror = () => reject(new Error(`Script load failed: ${url}`));
        document.head.appendChild(script);
      });
      if (window.Twilio?.Device) {
        twilioVoiceSdk = window.Twilio;
        return twilioVoiceSdk;
      }
    } catch (err) {
      lastErr = err;
      // eslint-disable-next-line no-console
      console.warn('[Twilio Voice SDK] CDN attempt failed:', err.message);
    }
  }
  throw new Error(`Relay voice runtime failed to load. Last error: ${lastErr?.message || 'unknown'}`);
};

// Global dial dispatcher. Every "Call" button in the CRM funnels here.
// On mobile: opens the native tel: dialer (works perfectly, no SDK
// overhead). On desktop: dispatches a crm-dial-request CustomEvent
// that the App-root <CallWidget> listener catches → opens the
// browser softphone. Replaces ~12 scattered tel: references that
// were bypassing the Voice SDK entirely.
function requestPhoneCall(target) {
  const phone = String(target?.phone || target?.lead?.phone || '').trim();
  if (!phone) return;
  const digits = phone.replace(/[^\d+]/g, '');
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const isMobile = /iPhone|iPad|iPod|Android/i.test(ua);
  if (isMobile) {
    window.open(`tel:${digits}`, '_self');
    return;
  }
  const lead = target?.lead || { phone, name: target?.name || '' };
  if (!lead.phone) lead.phone = phone;
  if (!lead.name && target?.name) lead.name = target.name;
  window.dispatchEvent(new CustomEvent('crm-dial-request', { detail: { lead } }));
}

const extractPdfText = async (file) => {
  const pdf = await loadPdfJs();
  const buf = await file.arrayBuffer();
  const doc = await pdf.getDocument({ data: buf }).promise;
  const pages = [];
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    // Sort items by y (top to bottom), then x (left to right)
    // Guard: scanned/image PDFs return content with no items array.
    const items = (content?.items || []).map(it => ({
      str: it.str,
      x: it.transform[4],
      y: it.transform[5],
    }));
    // Group into lines by y proximity
    items.sort((a, b) => b.y - a.y || a.x - b.x);
    const lines = [];
    let currentLine = [];
    let currentY = null;
    for (const it of items) {
      if (currentY === null || Math.abs(it.y - currentY) < 3) {
        currentLine.push(it);
        currentY = currentY === null ? it.y : currentY;
      } else {
        lines.push(currentLine.sort((a,b) => a.x - b.x).map(x => x.str).join(' '));
        currentLine = [it];
        currentY = it.y;
      }
    }
    if (currentLine.length) lines.push(currentLine.sort((a,b) => a.x - b.x).map(x => x.str).join(' '));
    pages.push(lines.join('\n'));
  }
  return pages.join('\n');
};

// Parse Mutual of Omaha detailed compensation statement PDF text
// Returns { periodEnding, transactions: [], producerNames: {prodNum: name} }
const parseMooPdf = (text) => {
  const result = { transactions: [], producerNames: {}, periodEnding: null };
  // Find period ending date
  const periodMatch = text.match(/(?:For Period Ending|Period Ending)\s+(\d{1,2}\/\d{1,2}\/\d{4})/i);
  if (periodMatch) result.periodEnding = periodMatch[1];
  else {
    // fallback: find first mm/dd/yyyy in the document
    const anyDate = text.match(/(\d{1,2}\/\d{1,2}\/\d{4})/);
    if (anyDate) result.periodEnding = anyDate[1];
  }

  // Extract producer name mappings. Two passes:
  //   1. The strict "PRODUCTION #: NNNN NAME: FIRST LAST" header. This
  //      is how the broker line appears on most MOO statements.
  //   2. A loose fallback that catches downline producers and newer
  //      statement formats where the name terminator isn't one of
  //      our explicit "M A O M / MGA / UNITED" markers.
  const nameRegexStrict = /PRODUCTION\s*#:\s*(\d+)\s+NAME:\s+([A-Z][A-Z\s\-'\.]+?)(?=\s+M\s+A\s+O\s+M|\s+MGA|\s+PRODUCTION|\s+UNITED|\s*$)/gm;
  let nm;
  while ((nm = nameRegexStrict.exec(text)) !== null) {
    const pn = nm[1].trim();
    const name = nm[2].replace(/\s+/g, ' ').trim();
    if (!result.producerNames[pn] || name.length > result.producerNames[pn].length) {
      result.producerNames[pn] = name;
    }
  }
  // Loose fallback. Captures 2-4 uppercase words after NAME: and
  // terminates on the first 2+ space run, colon-prefixed field, or
  // end of line. Catches downline producer names the strict regex
  // misses. Skips prodNums the strict pass already covered.
  const nameRegexLoose = /PRODUCTION\s*#:\s*(\d+)\s+NAME:\s+([A-Z][A-Z\-'\.]+(?:\s+[A-Z][A-Z\-'\.]+){1,3})/gm;
  let nl;
  while ((nl = nameRegexLoose.exec(text)) !== null) {
    const pn = nl[1].trim();
    const name = nl[2].replace(/\s+/g, ' ').trim();
    if (!result.producerNames[pn] || name.length > result.producerNames[pn].length) {
      result.producerNames[pn] = name;
    }
  }
  // Conservation-style "AGENT: NNNN - Firstname Lastname" header.
  // Appears on Policy Conservation Opportunities reports and some
  // other MOO PDFs. Catches mixed-case names ("Harrison Roderick
  // Curry") that the all-caps regexes above would skip. Title-case
  // by default since these aren't shouted in the source.
  const nameRegexAgent = /AGENT:\s+(\d+)\s*-\s+([A-Z][A-Za-z'-]+(?:\s+[A-Z][A-Za-z'-]+){1,3})/g;
  let na;
  while ((na = nameRegexAgent.exec(text)) !== null) {
    const pn = na[1].trim();
    const name = na[2].replace(/\s+/g, ' ').trim();
    if (!result.producerNames[pn] || name.length > result.producerNames[pn].length) {
      result.producerNames[pn] = name;
    }
  }

  // Build producer-segment map, then parse rows per producer
  // Split by "PRODUCTION #: xxxx" markers
  const segments = text.split(/PRODUCTION\s*#:\s*(\d+)\s+NAME:[^\n]*/i);
  // segments[0] is preamble, then pairs of [prodNum, content]
  let currentMga = 'SENIOR ADVISOR SERVICES AND INSURAN';
  const mgaMatch = text.match(/MGA:\s+([A-Z][A-Z0-9\s&\-\.]+?)(?:\s+PRODUCTION|\s*$)/m);
  if (mgaMatch) currentMga = mgaMatch[1].trim();

  for (let i = 1; i < segments.length; i += 2) {
    const prodNum = segments[i];
    const body = segments[i + 1] || '';
    // Update MGA context if a new MGA appears in this segment
    const mgaInSeg = body.match(/MGA:\s+([A-Z][A-Z0-9\s&\-\.]+?)(?=\s+PRODUCTION|\s+MGA|\s*$)/);
    // Extract transaction rows - look for policy number patterns (BU followed by digits)
    const rowRegex = /(BU\d+|FE[A-Z0-9]+)\s+([A-Z][A-Z\s,\-'\.]+?)\s+([A-Z]{2})\s+(\d{2}\/\d{2}\/\d{4})\s+([A-Z0-9]*)\s*(\d{2}\/\d{2}\/\d{4})?\s+(\d{2}\/\d{2}\/\d{4})?\s+(\d+)\s+(\w)\s+(\w{2,3})\s+(\d+)\s+\$?([\d,]+)\s+\$?([\-\d,\.]+)\s+(\w)\s+([A-Z][A-Z\s]+?)\s+(\d+)\s+([\d\.]+%)/g;
    let row;
    while ((row = rowRegex.exec(body)) !== null) {
      const commValueStr = row[13].replace(/,/g, '').replace(/\.$/, '0');
      const commValue = parseFloat(commValueStr);
      const isNegative = row[13].includes('-') || body.substring(rowRegex.lastIndex, rowRegex.lastIndex + 3).includes('-');
      result.transactions.push({
        prodNum,
        mga: currentMga,
        policy: row[1],
        insured: row[2].replace(/,\s*$/, '').trim(),
        state: row[3],
        activityDate: row[4],
        planCode: row[5] || '',
        issueDate: row[6] || '',
        paidToDate: row[7] || '',
        age: row[8],
        faceAmt: Number(row[11]) || 0,
        commPremium: commValue || 0,
        activityType: row[15].trim(),
        commPct: parseFloat(row[17]) || 0,
      });
    }
  }
  return result;
};

// Writing-agent name → internal prodNum mapping for Transamerica statements.
// Same convention as AMERICO_AGENT_MAP: a name we already know on MOO/Americo
// maps to that producer's prodNum so all three carriers roll up under the
// same person. Unknown names fall back to TRANS-<LASTNAME>-<F> downstream.
//
// Keys are uppercase "LASTNAME, FIRSTNAME" so they match insensitively
// against the statement's "Agaba, Ibraaheem" style names.
// Intentionally empty. Previously hardcoded the operator's Transamerica
// writing-agent name list and the prodNums they mapped to (the same
// people they had on MOO / Americo). Shipped in the public bundle so
// any customer could see another tenant's roster.
//
// Unknown agent names fall back to "TRANS-<LASTNAME>-<F>" prodNums,
// which is the safe default. Per-tenant alias config to consolidate
// the same person across carriers ships in a follow-up commit.
const TRANSAMERICA_AGENT_MAP = {};

// Map a "Lastname, Firstname" string to a stable prodNum. Known names hit
// the map; everything else gets TRANS-<LASTNAME>-<F> (e.g. "Smith, Jane"
// → "TRANS-SMITH-J") so the same person clusters across uploads instead
// of getting a new prodNum every time.
const transamericaProdNumFor = (agentName) => {
  if (!agentName) return 'TRANS-UNKNOWN';
  const key = agentName.trim().toUpperCase().replace(/\s+/g, ' ');
  if (TRANSAMERICA_AGENT_MAP[key]) return TRANSAMERICA_AGENT_MAP[key];
  const [last, first] = key.split(',').map(s => s.trim());
  if (!last) return 'TRANS-UNKNOWN';
  const lastClean = last.replace(/[^A-Z]/g, '');
  const firstInitial = (first || '').charAt(0);
  return firstInitial ? `TRANS-${lastClean}-${firstInitial}` : `TRANS-${lastClean}`;
};

// "OW Lapse C/B" / "OW Not Taken C/B" / "OW Surrender C/B" / "OW Death C/B" /
// "OW Reissue C/B" → chargeback. Anything else (e.g. "OW Placed Adv") is a
// new-business advance.
const TRANSAMERICA_CB_DESC = /OW\s+\w+(?:\s+\w+)?\s+C\/B/i;

// Parse Transamerica "Personal Advance Detail" PDF.
//
// The statement is split into sections, each prefaced by:
//   Transamerica Life Insurance Company
//   Agent Number : MLSR...
//   Transaction Date : MM/DD/YYYY
// then a table of policies for that date. Multiple sections per PDF, each
// with its own date and its own list of writing agents. Earlier versions
// of this parser kept the first Transaction Date for every row in the
// document — fixed here by iterating sections.
//
// Detail-row shape (column order in the PDF):
//   Policy  Insured  Description  WritingAgent  CommPrem  Split%  Comm%  Adv%  AdvanceAmt  [WtgOverAdv  OvwOverAdv]
// pdf.js text extraction sometimes drops the last two columns (the
// Over-Advance columns) — when present they hold the actual overwrite $
// hitting the agency owner's account, including negatives for chargebacks.
// When absent, we use AdvanceAmount and mark chargeback rows with a $0
// overwrite (the row still gets recorded so the agent + policy are tracked).
const parseTransamericaPdf = (text) => {
  const result = { transactions: [], producerNames: {}, periodEnding: null };

  const periodMatch = text.match(/as of\s+(\d{1,2}\/\d{1,2}\/\d{4})/i);
  if (periodMatch) result.periodEnding = periodMatch[1];

  // Split into per-Transaction-Date sections. The split keeps the date
  // string as a separate element so we can pair it with the body that
  // follows. Index 0 is whatever preceded the first section (the doc
  // header) — discarded.
  const sectionParts = text.split(/Transaction Date\s*:\s*(\d{1,2}\/\d{1,2}\/\d{4})/i);
  if (sectionParts.length < 3) {
    // No "Transaction Date" header at all — fall back to a single section
    // dated by the report's "as of" date so old / partial pastes still
    // produce something.
    return parseTransamericaSection(text, result.periodEnding || '', result);
  }
  for (let i = 1; i < sectionParts.length; i += 2) {
    const sectionDate = sectionParts[i];
    const sectionBody = sectionParts[i + 1] || '';
    parseTransamericaSection(sectionBody, sectionDate, result);
  }
  return result;
};

// Parse one Transaction-Date section's detail rows into result.transactions.
// Exposed as its own helper so the no-header fallback above can reuse it.
const parseTransamericaSection = (sectionText, sectionDate, result) => {
  // Match a single detail row. Policy # → Insured (single word capital-
  // start) → Description (OW … Adv | OW … C/B | Writing Agent) → Writing
  // Agent Name ("Last, First") → at least 5 numeric columns.
  //
  // Capture groups:
  //   1=policy, 2=insured, 3=description, 4=agentName,
  //   5=commPrem, 6=split%, 7=comm%, 8=adv%, 9=advanceAmount,
  //   10=wtgOverAdv (optional), 11=ovwOverAdv (optional, may be negative)
  const rowRegex = new RegExp(
    '([A-Z]{2,5}\\d{6,})' +                              // 1 policy
    '\\s+([A-Z][A-Za-z\\-\']*)' +                        // 2 insured
    '\\s+(OW\\s+[A-Za-z]+(?:\\s+[A-Za-z]+)?\\s+(?:Adv|C\\/B)|Writing\\s+Agent)' + // 3 description
    // 4 agent — "Last, First" with first name supporting multiple
    // words (e.g. "Davis, Ja Wan", "Smith, Mary Anne"). The
    // non-greedy modifier lets the regex stop as soon as the next
    // group (the comm-prem numeric column) can match.
    '\\s+([A-Z][A-Za-z\\-\']+,\\s*[A-Z][A-Za-z\\-\']+(?:\\s+[A-Z][A-Za-z\\-\']+)*?)' + // 4 agent
    '\\s+(-?[\\d,]+\\.\\d{2})' +                          // 5 comm prem
    '\\s+(-?[\\d\\.]+)' +                                  // 6 split %
    '\\s+(-?[\\d\\.]+)' +                                  // 7 comm %
    '\\s+(-?[\\d\\.]+)' +                                  // 8 adv %
    '\\s+(-?[\\d,]+\\.\\d{2})' +                          // 9 advance amount
    '(?:\\s+(-?[\\d,]+\\.\\d{2}))?' +                     // 10 wtg over adv
    '(?:\\s+(-?[\\d,]+\\.\\d{2}))?',                      // 11 ovw over adv
    'g'
  );

  let row;
  while ((row = rowRegex.exec(sectionText)) !== null) {
    const policy        = row[1];
    const insured       = row[2];
    const description   = row[3].trim();
    const agentName     = row[4].trim();
    const commPrem      = parseFloat(row[5].replace(/,/g, '')) || 0;
    const commPct       = parseFloat(row[7]) || 0;
    const advanceAmount = parseFloat(row[9].replace(/,/g, '')) || 0;
    const ovwOverAdv    = row[11] != null ? parseFloat(row[11].replace(/,/g, '')) : null;
    const isChargeback  = TRANSAMERICA_CB_DESC.test(description);
    const prodNum       = transamericaProdNumFor(agentName);

    // Prefer the Overwrite Over Advance column when present — that's the
    // signed $ that actually hits the agency owner's account this period,
    // including negative recoveries for C/B rows. When absent, fall back
    // to AdvanceAmount for new-business rows and $0 for C/B rows.
    let advComm;
    if (ovwOverAdv != null) advComm = ovwOverAdv;
    else if (isChargeback)  advComm = 0;
    else                    advComm = advanceAmount;

    // Map description → activity label so chargebacks bucket the same way
    // as MOO/Americo lapses in the rest of the app's reporting.
    let activityType = 'ADVANCE PLACED';
    if (isChargeback) {
      if (/Lapse/i.test(description))     activityType = 'CHARGEBACK - LAPSE';
      else if (/Surrender/i.test(description)) activityType = 'CHARGEBACK - SURRENDER';
      else if (/Death/i.test(description))     activityType = 'CHARGEBACK - DEATH';
      else if (/Not\s+Taken/i.test(description)) activityType = 'CHARGEBACK - NOT TAKEN';
      else                                  activityType = 'CHARGEBACK - LAPSE';
    }

    result.producerNames[prodNum] = agentName;
    result.transactions.push({
      mga: 'TRANSAMERICA',
      company: 'TRANSAMERICA',
      prodNum,
      policy,
      insured,
      agentName,
      activityDate: sectionDate,
      activityType,
      commPremium: commPrem,
      commPct,
      advComm,
      isAdvance: true,
    });
  }
  return result;
};

// AMERICO agent ID → internal prodNum mapping.
// Keeps AMERICO producers unified with MOO producers where possible.
// New producers (not on other carriers) get unique "AM-<id>" prodNums.
// Intentionally empty. Previously this object hardcoded the operator's
// own Americo agent IDs (FFCQN6, FFHAAR, FFHDK7, etc.) and the prodNums
// those mapped to inside the operator's agency. That shipped in the
// public bundle so any customer could see another tenant's agency
// structure.
//
// Unknown agent IDs fall back to "AM-<agentId>" prodNums, which is the
// safe default — each tenant's first upload populates their own
// stored producers state from there. A per-tenant alias map (so the
// operator can collapse Americo FFHxxx into their own MOO prodNum
// when the same person writes on both carriers) ships in a follow-up
// commit; until then operators can manually merge via the Producers
// tab's add-producer form.
const AMERICO_AGENT_MAP = {};

// Parse an AMERICO Commission Report PDF.
// Key semantics (verified against statement totals on 6 dates):
//   - Detail rows start with "AM<digits>" policy numbers.
//   - Rows group under agent-summary rows starting with "FFH..." (agent id + name + totals).
//     NOTE: the agent-summary row appears AFTER the details that belong to it, so we buffer.
//   - Each row has: policy, insured, age, eff(MMDDYY), plan, TRDT(MMDDYY), PRDT(MMDD),
//     optional status (PAID1/ADVNCE9), month, DU, PRO/LV, base_amt, TRX(AO-NA/IA-NA/AE-NA),
//     optional "R" (reversal flag), rate, split_pct, SPLIT_AMT, ACCT_LETTER(A/E), optional PRIMARY_BALANCE.
//   - "Primary Balance" column = $ hitting the earned-commission account (= netToAccount).
//   - "Split Amt" column = movement on the advance account (informational, not deposit).
//   - IA-NA rows with "R" flag: reversal/chargeback; negative primary_balance.
//   - AO-NA rows typically have NO primary_balance column (pure advance-pay-down, $0 primary impact).
//   - AE-NA rows are OVERRIDE activity (separate "OVERRIDE ACTIVITY" section); last numeric = primary amount.
//   - Lone "-" token before the A/E letter indicates negative split_amt.
//   - Trailing "-" on numbers (e.g., "126.00-") indicates negative.
const parseAmericoPdf = (text) => {
  const result = { transactions: [], producerNames: {}, periodEnding: null };
  const dateMatch = text.match(/AMERICO Commission Report as of\s+(\d{1,2}\/\d{1,2}\/\d{4})/i);
  if (dateMatch) result.periodEnding = dateMatch[1];

  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);

  // Buffer of detail rows not yet assigned an agent. Flushed when an agent-summary row appears.
  let pending = [];

  const parseDetailRow = (line) => {
    const parts = line.split(/\s+/);
    if (parts.length < 15) return null;
    const policy = parts[0];
    const insured = parts[1];
    const age = parseInt(parts[2], 10);
    if (isNaN(age)) return null;
    const effRaw = parts[3];
    const plan = parts[4];
    const trdtRaw = parts[5];
    if (!/^\d{6}$/.test(effRaw) || !/^\d{6}$/.test(trdtRaw)) return null;

    const activityDate = `${trdtRaw.slice(0,2)}/${trdtRaw.slice(2,4)}/20${trdtRaw.slice(4,6)}`;
    const issueDate = `${effRaw.slice(0,2)}/${effRaw.slice(2,4)}/20${effRaw.slice(4,6)}`;

    let trxType = null;
    for (const t of parts) {
      if (/^[A-Z]{2}-[A-Z]{2}$/.test(t)) { trxType = t; break; }
    }
    const isOverride = (trxType === 'AE-NA');
    const isReversal = /\sR\s/.test(' ' + line + ' ');

    let splitAmt = null;
    let primaryAmt = 0;

    if (isOverride) {
      const last = parts[parts.length - 1];
      const n = parseFloat(last.replace(/,/g, '').replace(/-$/, ''));
      if (isNaN(n)) return null;
      splitAmt = last.endsWith('-') ? -n : n;
      primaryAmt = splitAmt;
    } else {
      let acctIdx = -1;
      for (let i = parts.length - 1; i >= 2; i--) {
        if ((parts[i] === 'A' || parts[i] === 'E') && parts[i].length === 1) {
          acctIdx = i; break;
        }
      }
      if (acctIdx < 0) return null;

      let isNeg = false;
      let numStr;
      if (parts[acctIdx - 1] === '-') {
        isNeg = true;
        numStr = parts[acctIdx - 2];
      } else {
        numStr = parts[acctIdx - 1];
      }
      const n = parseFloat(String(numStr).replace(/,/g, ''));
      if (isNaN(n)) return null;
      splitAmt = isNeg ? -Math.abs(n) : n;

      if (acctIdx < parts.length - 1) {
        const balRaw = parts[acctIdx + 1];
        const pv = parseFloat(balRaw.replace(/,/g, '').replace(/-$/, ''));
        if (!isNaN(pv)) {
          primaryAmt = balRaw.endsWith('-') ? -pv : pv;
        }
      }
    }

    // Activity-type label: prefer the Trx code mapping (most accurate),
    // fall back to legacy text-pattern heuristics for old data shapes.
    let activityType;
    if (trxType && AMERICO_TRX_LABELS[trxType]) {
      activityType = AMERICO_TRX_LABELS[trxType];
    } else if (isReversal) {
      activityType = 'REVERSAL';
    } else if (parts.includes('ADVNCE9')) {
      activityType = 'ADVANCE PLACED';
    } else if (parts.includes('PAID1')) {
      activityType = 'PAID';
    } else {
      activityType = 'PAYMENT';
    }

    return {
      policy, insured, age, issueDate, activityDate, plan,
      splitAmt, primaryAmt, activityType, isOverride, trxType,
    };
  };

  // Flush buffered detail rows under a given agent id+name. Used both when an agent
  // header is encountered AND at end-of-input for any leftover rows.
  const flushPending = (agentId, rawAgentName) => {
    if (pending.length === 0) return;
    const prodNum = (agentId && AMERICO_AGENT_MAP[agentId]) || (agentId ? `AM-${agentId}` : 'AM-UNKNOWN');
    // Title-case the carrier-format name ("HOFFMAN JIHAD HOFFMAN") so
    // every downstream consumer (producer record, transaction row,
    // search, exports) stores a readable name like "Hoffman Jihad
    // Hoffman" without each having to re-format it.
    const agentName = rawAgentName
      ? rawAgentName.trim().split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
      : '';
    // Key producerNames by prodNum (the same key transactions use) so the
    // downstream auto-backfill at the import handler can match them.
    // Previously keyed by raw agentId — that created an orphan record at
    // producers[FFHAAR] that no transaction ever pointed to, leaving the
    // real producers[AM-FFHAAR] entry permanently nameless.
    if (agentId && agentName) result.producerNames[prodNum] = agentName;
    for (const d of pending) {
      result.transactions.push({
        mga: 'AMERICO',
        company: 'AMERICO',
        prodNum,
        agentId: agentId || 'UNKNOWN',
        agentName: agentName || 'Unknown Agent',
        policy: d.policy,
        insured: d.insured,
        state: '',
        activityDate: d.activityDate,
        issueDate: d.issueDate,
        commPremium: d.splitAmt,
        activityType: d.activityType,
        commPct: 0,
        commAmt: d.primaryAmt,
        advComm: d.splitAmt,
        isAmerico: true,
        isOverride: d.isOverride,
        trxType: d.trxType,
      });
    }
    pending = [];
  };

  // Permissive agent-id detection: legacy FFH prefix OR any uppercase ID 3-12 chars
  // followed by a clearly-non-numeric agent name token.
  const matchAgentHeader = (line) => {
    // Legacy: FFH followed by alphanumerics
    let m = line.match(/^(FFH[A-Z0-9]+)\s+([A-Za-z].+)$/);
    if (m) return m;
    // Generic: <ID> <Name…> where ID is 3-12 alphanumerics starting with a letter,
    // not a date, and the trailing text starts with a letter (name).
    m = line.match(/^([A-Z][A-Z0-9]{2,11})\s+([A-Z][A-Za-z'\-\.\s]+)(?:\s+[\-\d,\.]|$)/);
    if (m) return m;
    return null;
  };

  for (const line of lines) {
    // Try detail-row first. Its structural checks (two 6-digit date columns,
    // ≥15 tokens, parseable amount) reject anything that isn't a real
    // detail row, including agent-header lines and the column-header row.
    // We HAVE to test detail-row first because the generic agent-header
    // regex is loose enough to match lines like
    //   "AM03312907  ANDERSON  41  052226  ..."
    // — it sees AM03312907 as the agent id and ANDERSON as the name.
    // Letting parseDetailRow have first chance prevents that swallow.
    const parsed = parseDetailRow(line);
    if (parsed) { pending.push(parsed); continue; }

    const agentMatch = matchAgentHeader(line);
    if (agentMatch) {
      const agentId = agentMatch[1];
      const rest = agentMatch[2];
      const tokens = rest.split(/\s+/);
      const nameParts = [];
      for (const t of tokens) {
        if (/^[\-\d,\.]+$/.test(t) || t === '-') break;
        nameParts.push(t);
      }
      flushPending(agentId, nameParts.join(' '));
      continue;
    }
    // Other lines (section headers, totals, notes) fall through harmlessly
  }

  // If any detail rows are left without a following agent header (e.g. the user
  // pasted only the detail section), still emit them so the import isn't lost.
  if (pending.length > 0) flushPending(null, null);

  return result;
};

// Date, format, and storage-key helpers live in src/lib (formatters.js,
// dates.js, storageKeys.js). The imports are at the top of this file.
// Americo statement Trx code → human-readable activity label.
// Source: Americo Producer Commission Statement Guide (Rev. 04/22).
const AMERICO_TRX_LABELS = {
  'AE-NA': 'AS-EARNED PAID',
  'IA-NA': 'ADVANCE PAID',
  'AO-NA': 'ADVANCE OFFSET',
  'AR-TL': 'CHARGEBACK - ADVANCE LAPSE',
  'AR-BE': 'CHARGEBACK - ADVANCE REISSUE',
  'SV-TL': 'CHARGEBACK - LAPSE',
  'SV-TA': 'CHARGEBACK - SURRENDER',
  'DE-PA': 'CHARGEBACK - DEATH',
  'AJ': 'ADJUSTMENT',
  'PI': 'INTEREST',
  'PE': 'PAYMENT DEFERRED',
};

// ────────────────────────────────────────────────────────────────────
// Statement insights — compute the "where did my money come from
// and where did it go" breakdown for a freshly-imported batch of
// transactions. Drives the post-upload StatementInsightsModal that
// pops up after every PDF/CSV import so operators see the full
// picture without digging through the Transactions table.
//
// Buckets:
//   • Own writes        → tx.prodNum === operator's prodNum, positive
//   • Override income   → tx.prodNum !== operator's prodNum, positive
//                         (per producer, so leaderboard sortable)
//   • Chargebacks       → any negative commAmt OR activity in
//                         CHARGEBACK_TYPES (lapse, reversal, etc.)
//   • New advance exposure → NEW COV ISS / IA-NA rows where the
//                            advance balance grew. Future chargeback
//                            risk if those policies lapse early.
// ────────────────────────────────────────────────────────────────────
function computeImportInsights(txs, ownerProdNum, producers) {
  const myPn = String(ownerProdNum || '').trim();
  const isMine = (tx) => myPn && String(tx.prodNum || '').trim() === myPn;

  const ownWrites = [];
  const overridesByProducer = new Map();
  const chargebacks = [];
  const newAdvances = [];

  let netCheck = 0;
  let totalOwnComm = 0;
  let totalOverride = 0;
  let totalChargeback = 0;
  let totalNewAdvance = 0;
  const periodCarriers = new Set();
  let periodEnding = '';

  for (const tx of (txs || [])) {
    const amt = Number(tx.commAmt) || 0;
    const advAmt = Number(tx.advComm) || 0;
    const activity = String(tx.activityType || '').trim();
    const isCb = CHARGEBACK_TYPES.has(activity) || amt < 0;
    if (tx.company) periodCarriers.add(tx.company);
    if (!periodEnding && tx.activityDate) periodEnding = tx.activityDate;

    netCheck += amt;

    if (isCb && amt < 0) {
      chargebacks.push({
        policy: tx.policy,
        insured: tx.insured,
        prodNum: tx.prodNum,
        activity,
        amount: amt,
        mga: tx.mga,
      });
      totalChargeback += amt;
    } else if (amt > 0) {
      if (isMine(tx)) {
        ownWrites.push({
          policy: tx.policy,
          insured: tx.insured,
          activity,
          amount: amt,
          mga: tx.mga,
        });
        totalOwnComm += amt;
      } else {
        const pn = tx.prodNum || 'UNKNOWN';
        const name = producers?.[pn]?.name || tx.agentName || pn;
        if (!overridesByProducer.has(pn)) {
          overridesByProducer.set(pn, { prodNum: pn, name, amount: 0, policies: [] });
        }
        const entry = overridesByProducer.get(pn);
        entry.amount += amt;
        entry.policies.push({ policy: tx.policy, insured: tx.insured, activity, amount: amt });
        totalOverride += amt;
      }
    }

    // Track NEW COV ISS / IA-NA rows as future chargeback exposure.
    // These are upfront advances the carrier can claw back if the
    // policy lapses before the advance is earned out.
    if ((activity === 'NEW COV ISS' || activity === 'IA-NA' || activity === 'ADVANCE PAID') && advAmt > 0) {
      newAdvances.push({
        policy: tx.policy,
        insured: tx.insured,
        prodNum: tx.prodNum,
        producer: producers?.[tx.prodNum]?.name || tx.agentName || tx.prodNum,
        amount: advAmt,
        mine: isMine(tx),
      });
      totalNewAdvance += advAmt;
    }
  }

  return {
    totalRows: (txs || []).length,
    periodEnding,
    carriers: Array.from(periodCarriers),
    netCheck,
    totalOwnComm,
    totalOverride,
    totalChargeback,
    totalNewAdvance,
    ownWrites,
    overridesByProducer: Array.from(overridesByProducer.values()).sort((a, b) => b.amount - a.amount),
    chargebacks: chargebacks.sort((a, b) => a.amount - b.amount),  // most negative first
    newAdvances: newAdvances.sort((a, b) => b.amount - a.amount),
  };
}

// Money-formatter used by the StatementInsightsModal. Keeps the panel
// independent of any view-scoped helpers so it works the same in the
// upload toast, the modal itself, and any future export.
function fmtMoneyForInsights(n) {
  const v = Number(n) || 0;
  const sign = v < 0 ? '-' : '';
  return sign + '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ── Local-timezone timestamp formatter ──────────────────────────────
// Database stores everything as UTC ISO strings. Without a TZ tag in
// the display, an agent in PST staring at "3:14 AM" had no way to
// know if that was their morning or a UTC server stamp. This stamps
// every formatted timestamp with the operator's local TZ abbreviation
// so the rendered string is unambiguous. Browser TZ is the source
// here — settings.timezone (per-operator config) only flips the
// stamp for clients that specifically render the operator's "view as"
// timezone, not the global feed.
const LOCAL_TZ_ABBR = (() => {
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(new Date());
    const tz = parts.find(p => p.type === 'timeZoneName');
    return tz ? tz.value : '';
  } catch { return ''; }
})();
function fmtLocalDateTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    const s = d.toLocaleString();
    return LOCAL_TZ_ABBR ? `${s} ${LOCAL_TZ_ABBR}` : s;
  } catch { return String(iso); }
}

// ════════════════════════════════════════════════════════════════════
// STATEMENT INSIGHTS MODAL — Money intelligence per upload
//
// Pops up automatically after every PDF/CSV import. Shows the
// operator exactly where every dollar came from and went, broken
// into four sections:
//
//   1. Money In         — own writes + per-producer override income
//   2. Money Out        — every chargeback row with insured name
//   3. New Advance Exposure — upcoming chargeback risk if these
//                             policies lapse early
//   4. The Math         — reconciliation to the net check
//
// Goal: nobody opens this CRM and wonders "wait, why did I make less
// this period?" The answer is on this screen in 5 seconds.
// ════════════════════════════════════════════════════════════════════
function StatementInsightsModal({ data, onClose, onGotoTab = () => {} }) {
  if (!data) return null;
  const pos = data.netCheck >= 0;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-auto" onClick={e => e.stopPropagation()}>
        {/* Header — NET CHECK */}
        <div className={`px-6 py-5 ${pos ? 'bg-gradient-to-r from-emerald-700 to-emerald-900' : 'bg-gradient-to-r from-red-700 to-red-900'} text-white relative`}>
          <button aria-label="Close" onClick={onClose} className="absolute top-3 right-3 text-white/70 hover:text-white" title="Close"><X size={20}/></button>
          <div className="text-xs uppercase tracking-wider opacity-80">Net check this statement</div>
          <div className="text-4xl font-bold mt-1 tabular-nums">{fmtMoneyForInsights(data.netCheck)}</div>
          <div className="text-xs opacity-80 mt-2">
            {data.totalRows} transaction{data.totalRows === 1 ? '' : 's'} imported
            {data.carriers?.length > 0 && ` · ${data.carriers.join(', ')}`}
            {data.periodEnding && ` · period ${data.periodEnding}`}
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Money IN */}
          <section>
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">Money In</div>
              <div className="text-xs text-slate-400">Where you made money this period</div>
            </div>
            <div className="space-y-2">
              {data.totalOwnComm > 0 && (
                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border-l-4 border-emerald-500">
                  <div>
                    <div className="font-semibold text-slate-900">Your own writes</div>
                    <div className="text-xs text-slate-500">{data.ownWrites.length} polic{data.ownWrites.length === 1 ? 'y' : 'ies'} you wrote personally</div>
                  </div>
                  <div className="text-xl font-bold text-emerald-700 tabular-nums">{fmtMoneyForInsights(data.totalOwnComm)}</div>
                </div>
              )}
              {data.overridesByProducer.map(p => (
                <div key={p.prodNum} className="flex items-center justify-between p-3 bg-violet-50 rounded-lg border-l-4 border-violet-400">
                  <div>
                    <div className="font-semibold text-slate-900">
                      Override from <span className="text-violet-700">{p.name}</span>
                    </div>
                    <div className="text-xs text-slate-500">{p.policies.length} polic{p.policies.length === 1 ? 'y' : 'ies'} · {p.prodNum}</div>
                  </div>
                  <div className="text-xl font-bold text-violet-700 tabular-nums">{fmtMoneyForInsights(p.amount)}</div>
                </div>
              ))}
              {data.totalOwnComm === 0 && data.overridesByProducer.length === 0 && (
                <div className="p-3 text-sm text-slate-500 italic bg-slate-50 rounded-lg text-center">
                  No commission inflow this period.
                </div>
              )}
            </div>
            <div className="flex justify-between mt-3 px-3 pt-3 border-t-2 border-emerald-200 font-bold text-emerald-800">
              <div>Total money in</div>
              <div className="tabular-nums text-lg">{fmtMoneyForInsights(data.totalOwnComm + data.totalOverride)}</div>
            </div>
          </section>

          {/* Money OUT */}
          {data.chargebacks.length > 0 && (
            <section>
              <div className="flex items-baseline justify-between mb-3">
                <div className="text-xs font-bold uppercase tracking-wider text-red-700">Money Out — Chargebacks</div>
                <div className="text-xs text-slate-400">What lost you money this period</div>
              </div>
              <div className="space-y-2 max-h-72 overflow-auto pr-1">
                {data.chargebacks.map((c, i) => (
                  <div key={`${c.policy}-${i}`} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border-l-4 border-red-500">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{c.insured || 'Unknown insured'}</div>
                      <div className="text-xs text-slate-500">
                        {c.activity}
                        {c.policy && ` · ${c.policy}`}
                        {c.mga && ` · ${String(c.mga).slice(0, 32)}`}
                      </div>
                    </div>
                    <div className="text-xl font-bold text-red-700 tabular-nums flex-shrink-0 ml-3">{fmtMoneyForInsights(c.amount)}</div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-3 px-3 pt-3 border-t-2 border-red-200 font-bold text-red-800">
                <div>Total chargeback impact</div>
                <div className="tabular-nums text-lg">{fmtMoneyForInsights(data.totalChargeback)}</div>
              </div>
            </section>
          )}

          {/* New advance exposure */}
          {data.newAdvances.length > 0 && (
            <section>
              <div className="flex items-baseline justify-between mb-3">
                <div className="text-xs font-bold uppercase tracking-wider text-amber-700">⚠ New Advance Exposure</div>
                <div className="text-xs text-slate-400">Future chargeback risk</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 text-xs text-amber-900">
                These policies just took an upfront advance. If any lapse before earning out (typically months 1-9), the carrier claws back the unearned portion from your next check. Persistency-first selling protects this number.
              </div>
              <div className="space-y-1.5 max-h-60 overflow-auto pr-1">
                {data.newAdvances.map((a, i) => (
                  <div key={`${a.policy}-${i}`} className="flex items-center justify-between p-2.5 bg-amber-50 rounded text-sm">
                    <div className="min-w-0">
                      <span className="font-semibold text-slate-900">{a.insured || 'Unknown'}</span>
                      <span className="text-xs text-slate-500 ml-2 truncate">
                        {a.policy && a.policy}
                        {!a.mine && a.producer && ` · ${a.producer}`}
                      </span>
                    </div>
                    <div className="font-semibold text-amber-700 tabular-nums flex-shrink-0 ml-3">{fmtMoneyForInsights(a.amount)}</div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-3 px-3 pt-3 border-t-2 border-amber-200 font-bold text-amber-800">
                <div>Total at-risk advance</div>
                <div className="tabular-nums text-lg">{fmtMoneyForInsights(data.totalNewAdvance)}</div>
              </div>
            </section>
          )}

          {/* Bottom-line reconciliation */}
          <section className="bg-slate-100 rounded-xl p-5">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">The Math</div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-700">Your own writes</span>
                <span className="font-semibold tabular-nums text-slate-900">{fmtMoneyForInsights(data.totalOwnComm)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-700">Override income</span>
                <span className="font-semibold tabular-nums text-violet-700">{fmtMoneyForInsights(data.totalOverride)}</span>
              </div>
              <div className="flex justify-between text-red-700">
                <span>Chargebacks</span>
                <span className="font-semibold tabular-nums">{fmtMoneyForInsights(data.totalChargeback)}</span>
              </div>
              <div className="flex justify-between border-t-2 border-slate-300 pt-2 mt-2 text-base font-bold">
                <span>Net check</span>
                <span className={`tabular-nums ${pos ? 'text-emerald-700' : 'text-red-700'}`}>{fmtMoneyForInsights(data.netCheck)}</span>
              </div>
            </div>
            <div className="text-[11px] text-slate-500 mt-3">
              Cross-check this against the statement's "UNITED COMMISSION PAYABLE" or net check line. Any mismatch means a row was mis-categorized — open Transactions to investigate.
            </div>
          </section>
        </div>

        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex justify-between items-center gap-2">
          <button
            onClick={() => { onClose(); onGotoTab('transactions'); }}
            className="text-sm text-slate-600 hover:text-slate-900 font-semibold"
          >
            Open Transactions →
          </button>
          <button aria-label="Close" onClick={onClose} className="bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-lg px-4 py-2">
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

// CHARGEBACK_TYPES moved to src/lib/lifeExposure.js (imported above) —
// same activity-type labels (MOO's LAPSE/LAPSE REV ADV/RECPT ADV CO/
// BFY REVERSAL/CANC EFF ISS/NEW ISS REV + Americo's CHARGEBACK - *
// labels). Kept as the ONE definition so the Retention & Chargeback
// Command Center's money math can never drift from this file's.

// Activity types that are accounting-only (payment movements, not production).
// Excluded from all production math.
const IGNORED_ACTIVITY_TYPES_LC = new Set(['special disbursement', 'eft', 'deposit', 'wire']);
const isIgnoredActivity = (tx) => IGNORED_ACTIVITY_TYPES_LC.has(String(tx.activityType || '').trim().toLowerCase());

// Preferred display order for carriers
const CARRIER_ORDER = ['Americo', 'MOO', 'Transamerica'];
const carrierSortIndex = (name) => {
  const i = CARRIER_ORDER.indexOf(name);
  return i === -1 ? CARRIER_ORDER.length : i;
};
const sortByCarrierOrder = (arr, getName) => arr.slice().sort((a, b) => {
  const ai = carrierSortIndex(getName(a));
  const bi = carrierSortIndex(getName(b));
  if (ai !== bi) return ai - bi;
  return getName(a).localeCompare(getName(b));
});

// weekKeyFromDate, upcomingWeekKeys, and K live in src/lib (dates.js +
// storageKeys.js). The imports are at the top of this file.

// Seeded on first run; user can edit/add/delete via the Leads → Templates panel.
// Tokens: [Name], [Agent Name], [Agent Phone], [Time]
const DEFAULT_LEAD_TEMPLATES = [
  // ── Universal (any line) ────────────────────────────────────────
  { id: 'tpl_thanks',     name: 'Post-appointment thank you', body: "Hey [Name], it was great speaking with you today! I'll be sending over your summary shortly. Don't hesitate to reach out if you have any questions. — [Agent Name]" },
  { id: 'tpl_noshow',     name: 'No-show re-engagement',      body: "Hey [Name], I had us scheduled to connect today but missed you! I want to make sure you get the information you requested. When's a good time to reconnect? — [Agent Name]" },
  { id: 'tpl_noanswer',   name: 'Follow-up after no answer',  body: "Hey [Name], this is [Agent Name] — I tried reaching you regarding the policy you inquired about. Give me a call back when you get a chance: [Agent Phone]. Looking forward to connecting!" },
  { id: 'tpl_referral',   name: 'Referral ask',                body: "Hey [Name], congrats again on getting approved! If you know anyone else who could benefit from what we built for you today, I'd love to help them too. There's a referral bonus in it for you once they're approved. — [Agent Name]" },
  { id: 'tpl_confirm',    name: 'Callback confirmation',       body: "Hey [Name], just confirming our call tomorrow at [Time]. Looking forward to it! — [Agent Name]" },
  // ── Final Expense (FE) ──────────────────────────────────────────
  { id: 'tpl_fe_intro',   name: 'FE · First touch',            body: "Hi [Name], this is [Agent Name] — I help families lock in burial protection so loved ones aren't stuck with the bill. You requested info recently. Got 5 min today? — [Agent Phone]" },
  { id: 'tpl_fe_drafterr',name: 'FE · Draft failed nudge',     body: "Hey [Name], [Agent Name] here. Your final expense premium didn't draft this month — let's get it sorted before the policy lapses. Quick call when you get a sec? [Agent Phone]" },
  { id: 'tpl_fe_chargeback', name: 'FE · Chargeback save',     body: "Hi [Name], I'm reaching out personally — your policy was flagged for cancellation. Most of the time this is a draft issue, not a coverage issue. Let me help you keep what you set up. Call me: [Agent Phone] — [Agent Name]" },
  // ── Mortgage Protection (MP) ────────────────────────────────────
  { id: 'tpl_mp_intro',   name: 'MP · First touch',            body: "Hi [Name], you requested mortgage protection info — I help homeowners make sure the house stays paid off if something happens to them. Quick 5-min chat today? — [Agent Name] · [Agent Phone]" },
  { id: 'tpl_mp_quote',   name: 'MP · Quote ready',            body: "Hey [Name], I've got your mortgage protection quote ready — for your mortgage of about [Mortgage Balance], protection runs around [Quote/mo]. Want me to walk you through it? — [Agent Name]" },
  // ── IUL ──────────────────────────────────────────────────────────
  { id: 'tpl_iul_intro',  name: 'IUL · First touch',           body: "Hi [Name], you asked about building tax-advantaged retirement income with the IUL strategy — got a few min today to chat through your numbers? — [Agent Name] · [Agent Phone]" },
  { id: 'tpl_iul_summary',name: 'IUL · Summary sent',          body: "Hey [Name], just sent over your IUL illustration. The contribution structure we discussed lines up with your goal of [IUL Goal]. Open to talk Friday? — [Agent Name]" },
  // ── ACA / Health ────────────────────────────────────────────────
  { id: 'tpl_aca_intro',  name: 'ACA · First touch',           body: "Hi [Name], it's [Agent Name] — I'm a licensed marketplace agent. Saw you requested ACA quotes. Most folks save $200+/mo with the right plan. Got 10 min today? [Agent Phone]" },
  { id: 'tpl_aca_dmi',    name: 'ACA · DMI/SVI outstanding',    body: "Hey [Name], your marketplace application has documents outstanding — without them, your plan can cancel mid-year. I can walk you through it in 5 min. Reply YES and I'll send the list. — [Agent Name]" },
  { id: 'tpl_aca_sep',    name: 'ACA · SEP opening',           body: "Hi [Name], you may now qualify for a Special Enrollment Period — that means you can change or enroll in coverage outside Open Enrollment. Let's chat for 5 min: [Agent Phone]. — [Agent Name]" },
  // ── Medicare ─────────────────────────────────────────────────────
  { id: 'tpl_med_intro',  name: 'Medicare · First touch',      body: "Hi [Name], this is [Agent Name] — I'm a licensed Medicare advisor. You requested help picking a plan. I work for YOU, not a single carrier, so I can shop every option. Got 10 min today? [Agent Phone]" },
  { id: 'tpl_med_aep',    name: 'Medicare · AEP reminder',     body: "Hey [Name], Annual Enrollment runs Oct 15 – Dec 7 — the one window where you can change Medicare plans without penalty. Want a quick review to make sure you're on the best fit? — [Agent Name]" },
  { id: 'tpl_med_soa',    name: 'Medicare · SOA reminder',     body: "Hi [Name], looking forward to our Medicare appointment. Per CMS rules I'll need a Scope of Appointment form signed before we meet — I'll text you the link 5 min before. — [Agent Name]" },
  // Pre-canned save-the-policy texts. The Retention tab's Copy SMS
  // button reaches for one of these (matched by name) when filling
  // out conservation outreach for an event. Tokens supported in
  // addition to the standard [Name] / [Agent Name] / [Agent Phone]:
  //   [Carrier]      — MOO, Americo, etc.
  //   [Cure Amount]  — minimum payment to keep the policy in force
  //   [Pay By Date]  — deadline from the carrier
  //   [Policy]       — policy number
  { id: 'tpl_conservation_lapse',   name: 'Conservation — lapse warning',   body: "Hey [Name], this is [Agent Name]. [Carrier] just flagged your life insurance policy for a lapse — payment of [Cure Amount] is due by [Pay By Date] to keep your coverage active. I want to make sure you don't lose what we built. Can you call me at [Agent Phone] today?" },
  { id: 'tpl_conservation_mail',    name: 'Conservation — returned mail',   body: "Hey [Name], it's [Agent Name]. [Carrier] just bounced back some mail to your address on file. Quick favor — can you reply with your current mailing address so I can update your file? It's important for keeping your policy in good standing. — [Agent Phone]" },
];

// Secrets that previously lived in localStorage and are actively purged from any
// existing browser on mount. Anthropic + Lob keys now live only in server env;
// VA passwords will move to Supabase in Week 3.
const PURGED_SECRET_KEYS = ['anthropic_api_key_v1', 'va_credentials_v1'];

// ──────────────────────────────────────────────────────────────────────
// Authentication — now handled by Supabase (see src/auth/*).
//
// AGENT_CREDENTIALS is retained as an empty object purely so the legacy
// agent/VA admin-panel code paths (ManageAgentsPanel, isUsernameTaken)
// keep compiling. Those panels are unreachable in v1: every authenticated
// Supabase user is mapped to role: 'admin' and lands in the main app
// dashboard, so the agent/va branch never renders. Real role management
// will come from a Supabase profiles table post-launch.
// ──────────────────────────────────────────────────────────────────────
const INITIAL_AGENT_CREDENTIALS = {};
let AGENT_CREDENTIALS = { ...INITIAL_AGENT_CREDENTIALS };

const SESSION_KEY = 'commTracker_session_v1';

// Data-isolation gate. Apply to every transactions array before it's read.
// Admin sees everything; agents see only their own rows; VAs see their served
// agent's rows. Wired at the data layer so even an accidentally-rendered tab
// can't leak cross-agent data.
function filterForAgent(transactions, currentUser) {
  if (!currentUser) return [];
  // admin (SaaS operator, see migration 004) and client (paying agency
  // owner) both see every transaction inside their current tenant
  // namespace. Per-user namespacing happens one level up via
  // userStorage, so by the time transactions land here they're already
  // scoped to "this tenant's data." Drilling further by prodNum is
  // only needed for the agent/va sub-roles below.
  if (currentUser.role === 'admin' || currentUser.role === 'client') {
    return transactions || [];
  }
  // VAs serve a specific agent — use that agent's prodNum for filtering
  const targetProdNum = currentUser.role === 'va' ? currentUser.servesAgentProdNum : currentUser.prodNum;
  if (!targetProdNum) return [];
  return (transactions || []).filter(t => t.prodNum === targetProdNum);
}

// Appointment status options shown in the Calendar tab
const APPT_STATUS_OPTIONS = [
  { id: 'scheduled', label: 'Scheduled', color: 'blue' },
  { id: 'closed_won', label: 'Closed — won', color: 'emerald' },
  { id: 'closed_lost', label: 'Closed — lost', color: 'slate' },
  { id: 'no_show', label: 'No-show', color: 'amber' },
  { id: 'cancelled', label: 'Cancelled', color: 'red' },
  { id: 'rescheduled', label: 'Rescheduled', color: 'violet' },
];

// Parse a free-form draft-date pattern like "15th", "Last day", "1st Friday" and return the
// actual Date in a given month. Returns null if the pattern can't be interpreted.
function nthWeekdayOfMonth(year, month, weekday, n) {
  if (n === -1) {
    // last weekday of the month
    const last = new Date(year, month + 1, 0);
    const offset = (last.getDay() - weekday + 7) % 7;
    return new Date(year, month, last.getDate() - offset);
  }
  const first = new Date(year, month, 1);
  const offset = (weekday - first.getDay() + 7) % 7;
  const day = 1 + offset + n * 7;
  const d = new Date(year, month, day);
  if (d.getMonth() !== month) return null; // overflow (e.g. "5th Friday" doesn't exist)
  return d;
}
function parseDraftDateForMonth(draftText, year, month) {
  const t = (draftText || '').trim().toLowerCase();
  if (!t) return null;
  if (t.includes('last day') || t === 'last' || t === 'eom' || t === 'end of month') {
    return new Date(year, month + 1, 0);
  }
  // Simple "Nth" / "5th" / number only
  const dayOnly = t.match(/^(\d{1,2})(?:st|nd|rd|th)?$/);
  if (dayOnly) {
    const day = parseInt(dayOnly[1], 10);
    if (day >= 1 && day <= 31) {
      const d = new Date(year, month, day);
      // If day overflowed (e.g. 31 in Feb), fall back to last day of month
      if (d.getMonth() === month) return d;
      return new Date(year, month + 1, 0);
    }
  }
  // "Nth Weekday" / "Last Tuesday"
  const weekdayNames = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
  const ordinals = { 'first': 0, '1st': 0, 'second': 1, '2nd': 1, 'third': 2, '3rd': 2, 'fourth': 3, '4th': 3, 'fifth': 4, '5th': 4, 'last': -1 };
  for (const [ord, n] of Object.entries(ordinals)) {
    for (let wi = 0; wi < 7; wi++) {
      if (t.includes(`${ord} ${weekdayNames[wi]}`)) {
        return nthWeekdayOfMonth(year, month, wi, n);
      }
    }
  }
  return null;
}
// Convert "MM/DD/YYYY" stored date to a Date in the given year (annual recurrence).
function recurringDateForYear(mmddyyyyStr, year) {
  const parts = String(mmddyyyyStr || '').split('/');
  if (parts.length !== 3) return null;
  const m = parseInt(parts[0], 10) - 1;
  const d = parseInt(parts[1], 10);
  if (isNaN(m) || isNaN(d) || m < 0 || m > 11 || d < 1 || d > 31) return null;
  const out = new Date(year, m, d);
  if (out.getMonth() !== m) return null;
  return out;
}

const APPT_TYPE_OPTIONS = [
  'New consultation',
  'Policy review',
  'Follow-up',
  'Underwriting call',
  'Beneficiary review',
  'Cross-sell',
  'Medicare enrollment',     // gated by SOA 48-hr check
  'Other',
];

// Types that REQUIRE a CMS-compliant SOA on file ≥48 hours before
// the appointment runs. Booking flow checks /api/medicare/soa-gate
// when one of these is selected.
const SOA_GATED_APPT_TYPES = new Set(['Medicare enrollment']);

const WINBACK_STATUSES = [
  { id: 'new', label: 'New', color: 'slate', desc: 'Not yet contacted' },
  { id: 'in_progress', label: 'In Progress', color: 'amber', desc: 'Working on it' },
  { id: 'won_back', label: 'Won Back', color: 'emerald', desc: 'Reinstated or replaced' },
  { id: 'lost', label: 'Lost', color: 'red', desc: 'Officially given up' },
];

const CONTACT_ACTIONS = [
  'Called — no answer',
  'Voicemail',
  'Spoke with client',
  'Emailed',
  'Texted',
  'Reinstated',
  'Replaced',
  'Other',
];

// Actions for ongoing client relationship management (separate from winback/lapse work)
const CLIENT_CONTACT_ACTIONS = [
  'Welcome message',
  'Pre-payment reminder',
  'Day-of payment check-in',
  '30-day check-in',
  '90-day check-in',
  'Anniversary',
  '6-month check-in',
  'Annual review',
  'Birthday call',
  'Life event update',
  'Reviewed policy',
  'Discussed beneficiaries',
  'Spoke with client',
  'Called — no answer',
  'Voicemail',
  'Emailed',
  'Texted',
  'Other',
];

// Days between required check-ins. 180 = 6 months per the user's guideline.
const CHECK_IN_INTERVAL_DAYS = 180;

// Guidance text shown alongside the check-ins-due widget. Pulled from the user's request.
const CHECK_IN_GUIDANCE = 'Every 6 months ongoing. Semi-annual check-in. Keep it personal, not robotic. Reference something specific — their coverage amount, their insured\'s age, a life event if you know of one.';

// Policy lifecycle touchpoints — windows are relative to the policy issue date.
// `match` is the contactLog `action` string that resolves the touchpoint (so it disappears
// from the alert list after the agent logs it).
const POLICY_TOUCHPOINTS = [
  {
    kind: 'welcome',
    label: 'Welcome — new client',
    color: 'emerald',
    priority: 0,
    match: 'Welcome message',
    // Window: day 0 through day 3 of policy being inforce.
    inWindow: (daysSinceIssue) => daysSinceIssue >= 0 && daysSinceIssue <= 3,
    guidance: 'First impression — confirm them as a client, set expectations, let them know who to reach. Short and warm. Builds trust before any payment hiccup.',
    script: 'Hey [name], wanted to welcome you and confirm your [carrier] policy is set up — you\'re officially covered as of [issue date]. I\'m [agent], your point of contact for anything. Reach out anytime. Looking forward to taking care of you!',
  },
  {
    kind: 'pre_payment',
    label: '5-7 days before first payment',
    color: 'amber',
    priority: 1,
    match: 'Pre-payment reminder',
    // Window: days since issue is -7 to -5 (i.e., issue date is 5-7 days in the future).
    inWindow: (daysSinceIssue) => daysSinceIssue >= -7 && daysSinceIssue <= -5,
    guidance: 'This is the most important touchpoint. Remind them the draft is coming. People forget, overdraft, and then the policy lapses before it ever inforces.',
    script: 'Hey [name], just a heads up — your first payment of $[amount] drafts on [date] from [bank]. Make sure there\'s enough in there and let me know if anything\'s changed!',
  },
  {
    kind: 'day_of',
    label: 'Day of first payment',
    color: 'red',
    priority: 2,
    match: 'Day-of payment check-in',
    inWindow: (daysSinceIssue) => daysSinceIssue >= 0 && daysSinceIssue <= 1,
    guidance: 'Quick "just checking in" text. If they don\'t respond in 24 hours, call. First payment failures are your highest-risk window.',
    script: 'Hey [name] — just checking in. Your draft should have gone through today. All good on your end?',
  },
  {
    kind: '30_day',
    label: '30 days after inforce',
    color: 'blue',
    priority: 3,
    match: '30-day check-in',
    inWindow: (daysSinceIssue) => daysSinceIssue >= 28 && daysSinceIssue <= 35,
    guidance: 'A real check-in, not just a transaction message. Ask how they\'re feeling about the policy, if they have questions, and remind them what they\'re building. Honeymoon-phase — good time to ask for a referral.',
    script: 'Hey [name], it\'s been a month since we got your policy in force. How are you feeling about it? Any questions? Also — know anyone else who might benefit from coverage like yours?',
  },
  {
    kind: '90_day',
    label: '90 days / 3 months',
    color: 'violet',
    priority: 4,
    match: '90-day check-in',
    inWindow: (daysSinceIssue) => daysSinceIssue >= 85 && daysSinceIssue <= 95,
    guidance: 'Another touchpoint, slightly more relationship-focused. Acknowledge they\'ve made 3 payments — policy is solidly inforce. Good time to mention adding coverage, checking beneficiaries, or the children\'s rider if you didn\'t close it already.',
    script: 'Hey [name] — 3 months in, three payments down. You\'re officially building something. Want to revisit beneficiaries or talk about adding to it?',
  },
  {
    kind: 'anniversary',
    label: 'Policy anniversary',
    color: 'emerald',
    priority: 5,
    match: 'Anniversary',
    inWindow: (daysSinceIssue) => {
      if (daysSinceIssue < 365) return false;
      const inYear = daysSinceIssue % 365;
      return inYear <= 3 || inYear >= 362;
    },
    guidance: 'Treat this like a birthday. A short text acknowledging their anniversary builds loyalty and keeps you top of mind for referrals and additional sales.',
    script: 'Hey [name] — happy policy anniversary! Hard to believe it\'s been a year. Thanks for trusting me with your coverage. Anything new going on this year I should know about?',
  },
];

// Decide which touchpoints are "due" for a policy right now, given the client's contact log.
function getDuePolicyTouchpoints(policy, contactLog, today = new Date()) {
  if (!policy?.issueDate) return [];
  const issued = parseDate(policy.issueDate);
  if (!issued) return [];
  const todayMid = new Date(today); todayMid.setHours(0, 0, 0, 0);
  const daysSinceIssue = Math.floor((todayMid - issued) / 86400000);
  const out = [];
  for (const tp of POLICY_TOUCHPOINTS) {
    if (!tp.inWindow(daysSinceIssue)) continue;
    // Check whether the contact log already has a matching entry inside the relevant window
    // For most touchpoints, "within last 14 days" is a safe heuristic. Anniversary uses 60 days.
    const lookbackDays = tp.kind === 'anniversary' ? 60 : 14;
    const cutoff = new Date(todayMid); cutoff.setDate(cutoff.getDate() - lookbackDays);
    const done = (contactLog || []).some(c => {
      if (c.action !== tp.match) return false;
      const d = parseDate(c.date);
      return d && d >= cutoff;
    });
    if (!done) out.push({ ...tp, daysSinceIssue });
  }
  return out;
}

const DEFAULT_SETTINGS = {
  // camPercent stores the "% of premium that goes to the writing
  // agent" used by OverridesView to compute override = team net −
  // (premium × camPercent / 100). The legacy name predates the SaaS;
  // OverridesView reads it as `agentSplitPct` and labels it "Agent
  // payouts" in the UI. Defaults to 0 — owner sets per-tenant value
  // in Settings (planned: a proper Override Rule per agent/carrier
  // via the commissionRules system).
  camPercent: 0,
  // camName retained for back-compat with stored user_state blobs
  // that referenced it. Not surfaced in any UI.
  camName: '',
  ownerName: 'You',
  ownerPhone: '',           // tel number used by Lead SMS templates as [Agent Phone] token
  ownerProdNum: '',         // Producer # of the agency owner. Empty by default — set per-tenant in Settings. Used to separate the owner row from the rest of the team in Daily Breakdown.
  ownerAmericoAgentId: '',  // Americo FF agent id of the agency owner (e.g. FFCQN6). Used by the EFT proxy card on the dashboard.

  // ── SaaS branding (per-tenant) ─────────────────────────────────
  // Phase 1 of the multi-tenant refactor. Stored in user-namespaced
  // localStorage today; when a server-side tenant config endpoint exists,
  // BrandingProvider can swap to fetching from there without touching
  // consumers. All values are optional — defaults are applied at render
  // time via useBranding().
  branding: {
    businessName: '',      // empty → falls back to default app name in header
    logoDataUrl: '',       // base64 data URL; empty → falls back to '$' icon tile
    accentColor: '',       // hex like '#10b981'; empty → emerald default
  },
};

// Default branding values applied when a tenant hasn't customized them.
// Kept here so the fallback strategy is centralized and easy to change.
const DEFAULT_BRANDING = {
  businessName: 'Commission Command',
  accentColor: '#10b981', // emerald-500, current app default
};

// Resolve branding with fallbacks — call this anywhere you need to render
// branded chrome. Today it reads from settings; tomorrow it can read from
// a TenantConfigProvider context without changing call sites.
function resolveBranding(settings) {
  const b = settings?.branding || {};
  return {
    businessName: (b.businessName || '').trim() || DEFAULT_BRANDING.businessName,
    logoDataUrl:  b.logoDataUrl || '',
    accentColor:  /^#[0-9a-f]{3,8}$/i.test(b.accentColor || '') ? b.accentColor : DEFAULT_BRANDING.accentColor,
  };
}

// ── Phase 2 — Commission Rules Engine ────────────────────────────────
// Owners create payout rules instead of hardcoded splits. Each rule has:
//   - Matchers (carriers, products, agents) — empty array means "matches any"
//   - Splits — an array of {role, target, percent} that should sum to ≤ 100
//   - Basis — which field on a transaction the percentages multiply against
//   - Advance + chargeback behavior
// findApplicableRule(tx) picks the highest-priority active rule whose matchers
// all match. applyRule(tx, rule) returns one earning record per split.
// These helpers are pure; display-side aggregations consume them in a later
// phase so the rules library can be built without disturbing today's math.

const COMMISSION_SPLIT_ROLES = [
  { id: 'agent',       label: 'Agent (writing producer)' },
  { id: 'setter',      label: 'Setter (lead booker)' },
  { id: 'closer',      label: 'Closer (presentation rep)' },
  { id: 'team_leader', label: 'Team leader override' },
  { id: 'agency',      label: 'Agency override' },
  { id: 'partner',     label: 'Partner override' },
];
const COMMISSION_SPLIT_TARGETS = [
  { id: 'self',           label: 'Producer of record (the agent on the transaction)' },
  { id: 'specific_agent', label: 'A specific agent (pick below)' },
  { id: 'agency',         label: 'Agency owner' },
  { id: 'team_leader',    label: 'Team leader (resolved when team hierarchy exists)' },
  { id: 'partner',        label: 'Partner (resolved when partners exist)' },
];
const COMMISSION_BASIS_OPTIONS = [
  { id: 'netToAccount', label: 'Net to account (current default — matches today)' },
  { id: 'commAmt',      label: 'Comm Amt (carrier commission paid)' },
  { id: 'commPremium',  label: 'Comm Premium (commissionable premium)' },
];
const CHARGEBACK_TREATMENT_OPTIONS = [
  { id: 'reverse_split', label: 'Reverse the original split (each role gives back proportionally)' },
  { id: 'agent_eats',    label: 'Agent eats the full clawback (overrides keep their share)' },
];

function buildDefaultCommissionRule() {
  return {
    id: 'default-100-self',
    name: 'Default — 100% to producer of record',
    active: true,
    priority: 0,
    matchers: { carriers: [], products: [], agentProdNums: [] },
    splits: [
      { role: 'agent', target: 'self', targetParam: '', percent: 100, note: 'Reproduces current payout math.' },
    ],
    basis: 'netToAccount',
    advanceMonths: 0,
    chargebackPeriodDays: 0,
    chargebackTreatment: 'reverse_split',
    notes: 'Auto-created on first load. Disable to use only your custom rules, or leave active as the catch-all when no carrier/product-specific rule matches.',
    createdAt: new Date().toISOString(),
  };
}

// Pure: pick the highest-priority active rule whose matchers all match the
// transaction. Returns null when nothing matches. Carriers/products use the
// existing getCarrier/getProduct resolvers so they stay in sync.
function findApplicableRule(tx, rules, getCarrier, getProduct) {
  if (!Array.isArray(rules) || rules.length === 0) return null;
  const carrier = getCarrier ? getCarrier(tx) : (tx.company || '');
  const product = getProduct ? getProduct(tx) : '';
  const candidates = rules.filter(r => {
    if (!r.active) return false;
    const m = r.matchers || {};
    if ((m.carriers || []).length > 0 && !m.carriers.includes(carrier)) return false;
    if ((m.products || []).length > 0 && !m.products.includes(product)) return false;
    if ((m.agentProdNums || []).length > 0 && !m.agentProdNums.includes(tx.prodNum)) return false;
    return true;
  });
  candidates.sort((a, b) => (b.priority || 0) - (a.priority || 0));
  return candidates[0] || null;
}

// Pure: given a transaction and the rule that matched, compute earning
// records. One record per split. The target field is a string that downstream
// code can resolve: literal prodNums, 'AGENCY', '__team_leader__', etc.
function applyRule(tx, rule) {
  if (!rule || !Array.isArray(rule.splits)) return [];
  const basisField = rule.basis || 'netToAccount';
  const basisValue = Number(tx[basisField]) || 0;
  return rule.splits.map(s => {
    const targetId =
      s.target === 'self'           ? (tx.prodNum || '') :
      s.target === 'specific_agent' ? (s.targetParam || '') :
      s.target === 'agency'         ? 'AGENCY' :
      s.target === 'team_leader'    ? '__team_leader__' :
      s.target === 'partner'        ? `__partner_${s.targetParam || ''}` :
      '';
    return {
      ruleId: rule.id,
      role: s.role,
      target: targetId,
      percent: Number(s.percent) || 0,
      amount: basisValue * ((Number(s.percent) || 0) / 100),
      basis: basisField,
      basisValue,
      note: s.note || '',
    };
  });
}

function sumSplitPercent(splits) {
  return (splits || []).reduce((s, x) => s + (Number(x.percent) || 0), 0);
}

// Reusable clickable client-name link. Renders as a styled button that
// opens the client profile drawer when an openClientProfile callback is
// provided, or as plain text otherwise. Centralizes the lookup so every
// page can show "click the client name → see the profile" without each
// table re-implementing the wrapper. Pass `compact` for tighter padding.
function ClientNameLink({ name, openClientProfile, className = '', fallback = '—', title, compact = false }) {
  const display = name || '';
  if (!display) return <span className={className}>{fallback}</span>;
  if (typeof openClientProfile !== 'function') return <span className={className}>{display}</span>;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); openClientProfile(clientKeyOf(display)); }}
      className={`text-emerald-700 hover:text-emerald-900 hover:underline text-left font-medium ${compact ? '' : ''} ${className}`}
      title={title || 'View client profile'}
    >
      {display}
    </button>
  );
}

// ── Phase 3 — Carrier & Product Catalog ──────────────────────────────
// Owners add carriers and product mappings here instead of relying on
// hardcoded company-name / plan-code rules. The hardcoded rules are kept
// as a fallback so a brand-new workspace (or a tx whose carrier isn't in
// the catalog) still classifies correctly.

// Full product type list per spec. "Other" remains as the catch-all when
// no plan-code / policy-prefix mapping fires.
const PRODUCT_TYPES_FULL = [
  'Term',
  'Whole Life',
  'Final Expense',
  'Mortgage Protection',
  'IUL',
  'GUL',
  'Juvenile Life',
  'Other',
];

// Default catalog auto-seeded on first load. Matches the existing
// hardcoded behavior exactly — adopting custom carriers/products is
// purely additive on top of these defaults.
function buildDefaultCarrierCatalog() {
  return [
    {
      id: 'carrier_transamerica',
      name: 'Transamerica',
      active: true,
      companyMatchers: ['TRANSAMERICA'],
      mgaMatchers: ['TRANSAMERICA'],
      notes: 'Built-in default. Plan code/policy prefix rules below match the legacy hardcoded behavior.',
      products: [
        { id: 'p_trans_iul', name: 'IUL series',     productType: 'IUL',           planCodePrefixes: [], policyPrefixes: ['IULA'], commissionPct: 0, advanceMonths: 0, chargebackPeriodDays: 365 },
        { id: 'p_trans_fe',  name: 'Final Expense', productType: 'Final Expense', planCodePrefixes: [], policyPrefixes: ['FEXB'], commissionPct: 0, advanceMonths: 0, chargebackPeriodDays: 365 },
      ],
    },
    {
      id: 'carrier_americo',
      name: 'Americo',
      active: true,
      companyMatchers: ['AMERICO'],
      mgaMatchers: ['AMERICO'],
      notes: 'No product-level mappings — all Americo transactions resolve to product "Other" until you add plan-token rules here.',
      products: [],
    },
    {
      id: 'carrier_moo',
      name: 'Mutual of Omaha',
      active: true,
      companyMatchers: ['UNITED', 'MUTUAL'],
      mgaMatchers: ['ETERNA', 'SENIOR ADVISOR'],
      notes: 'Built-in default. Plan code prefixes match the legacy hardcoded behavior.',
      products: [
        { id: 'p_moo_fe_lp', name: 'Final Expense — Living Promise', productType: 'Final Expense', planCodePrefixes: ['FEPL', 'FEPG'], policyPrefixes: [], commissionPct: 0, advanceMonths: 9,  chargebackPeriodDays: 270 },
        { id: 'p_moo_term',  name: 'Term (TE series)',                productType: 'Term',          planCodePrefixes: ['TE'],          policyPrefixes: [], commissionPct: 0, advanceMonths: 9,  chargebackPeriodDays: 365 },
        { id: 'p_moo_iul',   name: 'IUL — Income Advantage',          productType: 'IUL',           planCodePrefixes: ['IXEXP'],       policyPrefixes: [], commissionPct: 0, advanceMonths: 9,  chargebackPeriodDays: 365 },
      ],
    },
  ];
}

// Pure resolver: catalog-first carrier lookup. Returns the catalog carrier
// name when a matcher fires; falls back to the hardcoded rules otherwise so
// catalog gaps don't break classification.
function resolveCarrier(tx, catalog) {
  const co = String(tx.company || '').toUpperCase();
  const mg = String(tx.mga     || '').toUpperCase();
  for (const c of (catalog || [])) {
    if (!c.active) continue;
    const companyHit = (c.companyMatchers || []).some(s => s && co.includes(String(s).toUpperCase().trim()));
    const mgaHit     = (c.mgaMatchers     || []).some(s => s && mg.includes(String(s).toUpperCase().trim()));
    if (companyHit || mgaHit) return c.name;
  }
  if (co.includes('TRANSAMERICA') || mg.includes('TRANSAMERICA')) return 'Transamerica';
  if (co.includes('AMERICO')      || mg.includes('AMERICO'))      return 'Americo';
  if (co.includes('UNITED') || co.includes('MUTUAL') || mg.includes('SENIOR ADVISOR') || mg.includes('ETERNA')) return 'Mutual of Omaha';
  return tx.company || 'Unknown';
}

// Pure resolver: catalog-first product lookup. The carrier must be known
// (use resolveCarrier first) so we scope product matchers to the right
// carrier's product list.
function resolveProduct(tx, catalog) {
  const carrier = resolveCarrier(tx, catalog);
  const planCode = String(tx.planCode || POLICY_PLAN[tx.policy] || '').toUpperCase().trim();
  const policy   = String(tx.policy   || '').toUpperCase().trim();
  for (const c of (catalog || [])) {
    if (!c.active) continue;
    if (c.name !== carrier) continue;
    for (const p of (c.products || [])) {
      const planHit   = (p.planCodePrefixes || []).some(prefix => prefix && planCode.startsWith(String(prefix).toUpperCase().trim()));
      const policyHit = (p.policyPrefixes   || []).some(prefix => prefix && policy.startsWith(String(prefix).toUpperCase().trim()));
      if (planHit || policyHit) return p.productType || 'Other';
    }
    // Carrier matched but no product rule did — drop through to legacy fallback.
    break;
  }
  // Legacy fallback (preserves behavior when catalog is empty or doesn't match):
  if (carrier === 'Transamerica') {
    if (policy.startsWith('IULA')) return 'IUL';
    if (policy.startsWith('FEXB')) return 'Final Expense';
    return 'Other';
  }
  if (carrier === 'Mutual of Omaha') {
    if (!planCode) return 'Other';
    if (planCode === 'FEPL' || planCode === 'FEPG' || planCode.startsWith('FEPL') || planCode.startsWith('FEPG')) return 'Final Expense';
    if (planCode.startsWith('TE'))     return 'Term';
    if (planCode.startsWith('IXEXP')) return 'IUL';
    return 'Other';
  }
  return 'Other';
}

// Producers known to exist in your March 2026 statements - pre-loaded on first run
// Names and team assignments baked in from initial setup
// Intentionally empty. Previously this object hardcoded the operator's
// own producer roster (real names + prodNums + team assignments) which
// shipped in the public bundle every customer downloaded. New users
// could see another tenant's team roster either in DevTools or by
// clicking "Reload all project data" in Settings. The new-user data
// leak that prompted this change happened through exactly that path.
//
// Each tenant's producer roster now lives entirely in their own
// Supabase user_state row (cfg_producers_v1), which is RLS-scoped to
// auth.uid(). Nothing baked into the source code. classify() and
// getProducerName() both fall through to per-tenant stored state when
// the registry is empty.
const KNOWN_PRODUCERS = {};

// SkeletonBlock — reusable shimmering placeholder for slow-loading
// panels. Replaces the "Loading…" text-only loaders that made the
// dashboard feel sluggish on first paint. Tailwind's animate-pulse
// is enough — no custom CSS, no extra dep.
function SkeletonBlock({ lines = 3, className = '' }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-xl p-5 ${className}`}>
      <div className="animate-pulse space-y-2.5">
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            className="h-3 bg-slate-200 rounded"
            style={{ width: `${65 + ((i * 17) % 30)}%` }}
          />
        ))}
      </div>
    </div>
  );
}

// Demo data seeder — used by the empty-state "Load demo data" button
// on the Home dashboard. Writes a small synthetic dataset to
// localStorage under the standard K keys so a brand-new operator can
// EXPLORE the product before importing real data. Caller is expected
// to window.location.reload() after — the main load effect at app
// boot hydrates state from localStorage. Idempotent: overwrites what's
// there, doesn't merge.
function seedDemoData() {
  try {
    const today = new Date();
    const iso   = (offset) => {
      const d = new Date(today); d.setDate(today.getDate() - offset);
      return d.toISOString().slice(0, 10);
    };
    const txn = (id, company, prodNum, insured, policy, amt, activityType, daysAgo, isChargeback = false) => ({
      id,
      source:        'demo',
      company,
      prodNum,
      insured,
      policy,
      state:         company === 'UNITED' ? 'TX' : null,
      activityType,
      activityDate:  iso(daysAgo),
      issueDate:     iso(daysAgo + 30),
      commPremium:   amt * 2,
      commPct:       50,
      commAmt:       amt,
      advComm:       0,
      netToAccount:  isChargeback ? -amt : amt,
      isChargeback,
      monthKey:      iso(daysAgo).slice(0, 7),
      weekKey:       null,
    });
    const transactions = [
      txn('demo-tx-1', 'UNITED',       'OWNR',  'SMITH JOHN',     'MOO1001', 320, 'NEW',          5),
      txn('demo-tx-2', 'UNITED',       'OWNR',  'GARCIA MARIA',   'MOO1002', 480, 'NEW',          12),
      txn('demo-tx-3', 'AMERICO',      'OWNR',  'WILLIAMS',       'AM2001',  290, 'IA-NA',        18),
      txn('demo-tx-4', 'AMERICO',      'OWNR',  'JOHNSON-KELLY',  'AM2002',  410, 'IA-NA',        25),
      txn('demo-tx-5', 'TRANSAMERICA', 'OWNR',  'Brown',          'TR3001',  180, 'OVERRIDE',     30),
      txn('demo-tx-6', 'UNITED',       'OWNR',  'DAVIS ROBERT',   'MOO1003', 220, 'LAPSE',        45, true),
    ];

    const leads = [
      { id: 'demo-lead-1', name: 'Anna Lopez',    phone: '(555) 123-4567', email: 'anna@example.com',  status: 'new',         source: 'Demo',     leadType: 'iul',                  vertical: 'life',   sector: null,           state: 'TX', age: '42', dob: '1983-03-12', createdAt: iso(2)  },
      { id: 'demo-lead-2', name: 'Mike Patel',    phone: '(555) 234-5678', email: 'mike@example.com',  status: 'contacted',   source: 'Demo',     leadType: 'mortgage_protection',  vertical: 'life',   sector: null,           state: 'FL', age: '55', dob: '1970-08-04', createdAt: iso(4)  },
      { id: 'demo-lead-3', name: 'Sarah Chen',    phone: '(555) 345-6789', email: 'sarah@example.com', status: 'appt_set',    source: 'Demo',     leadType: 'general',              vertical: 'health', sector: 'aca_private',  state: 'CA', age: '38', dob: '1987-11-21', createdAt: iso(1)  },
      { id: 'demo-lead-4', name: 'Robert Davis',  phone: '(555) 456-7890', email: 'rob@example.com',   status: 'new',         source: 'Demo',     leadType: 'general',              vertical: 'health', sector: 'medicare',     state: 'NY', age: '67', dob: '1958-05-30', createdAt: iso(6)  },
      { id: 'demo-lead-5', name: 'Linda Wong',    phone: '(555) 567-8901', email: 'linda@example.com', status: 'closed',      source: 'Demo',     leadType: 'final_expense',        vertical: 'life',   sector: null,           state: 'GA', age: '72', dob: '1953-01-15', createdAt: iso(20) },
    ];

    const apps = [
      { id: 'demo-app-1', clientName: 'Anna Lopez',   carrier: 'MOO',          product: 'IUL',                  premium: 250, status: 'pending_uw', submittedDate: iso(3),  agentProdNum: 'OWNR', leadId: 'demo-lead-1' },
      { id: 'demo-app-2', clientName: 'Sarah Chen',   carrier: 'UnitedHealth', product: 'ACA Silver',           premium: 410, status: 'submitted',  submittedDate: iso(1),  agentProdNum: 'OWNR', leadId: 'demo-lead-3' },
      { id: 'demo-app-3', clientName: 'Linda Wong',   carrier: 'Americo',      product: 'Eagle Premier (FEX)',  premium: 95,  status: 'issued',     submittedDate: iso(28), agentProdNum: 'OWNR', leadId: 'demo-lead-5' },
    ];

    const calls = [
      { id: 'demo-call-1', agentProdNum: 'OWNR', leadId: 'demo-lead-1', prospectName: 'Anna Lopez',   prospectPhone: '(555) 123-4567', date: iso(0), startTime: '10:30', direction: 'outbound', outcome: 'pickup',       duration: 480, notes: 'Wants quote',  createdAt: new Date().toISOString() },
      { id: 'demo-call-2', agentProdNum: 'OWNR', leadId: 'demo-lead-2', prospectName: 'Mike Patel',   prospectPhone: '(555) 234-5678', date: iso(0), startTime: '10:42', direction: 'outbound', outcome: 'voicemail',    duration: 30,  notes: 'Left VM',      createdAt: new Date().toISOString() },
      { id: 'demo-call-3', agentProdNum: 'OWNR', leadId: 'demo-lead-3', prospectName: 'Sarah Chen',   prospectPhone: '(555) 345-6789', date: iso(0), startTime: '11:15', direction: 'outbound', outcome: 'appt_set',     duration: 720, notes: 'Booked 2pm',   createdAt: new Date().toISOString() },
      { id: 'demo-call-4', agentProdNum: 'OWNR', leadId: 'demo-lead-4', prospectName: 'Robert Davis', prospectPhone: '(555) 456-7890', date: iso(0), startTime: '13:08', direction: 'outbound', outcome: 'no_answer',    duration: 0,   notes: '',             createdAt: new Date().toISOString() },
      { id: 'demo-call-5', agentProdNum: 'OWNR', leadId: 'demo-lead-1', prospectName: 'Anna Lopez',   prospectPhone: '(555) 123-4567', date: iso(1), startTime: '15:22', direction: 'outbound', outcome: 'callback',     duration: 180, notes: 'Call tmrw 4p', createdAt: new Date().toISOString() },
    ];

    const producers = {
      'OWNR': { name: 'You (Demo)', team: 'owner' },
    };

    const settings = { ownerProdNum: 'OWNR', ownerName: 'You (Demo)' };
    const goalConfig = {
      monthlyTarget:   10000,
      avgAnnualPremium: 1500,
      commissionRate:   0.75,
      lapseRate:        0.30,
      showToSale:       0.40,
      apptToShow:       0.55,
      contactToAppt:    0.18,
      dialToContact:    0.13,
      workingDays:      22,
      dailyDials:       626,
    };

    // Storage keys — must match src/lib/storageKeys.js K constant.
    // We write via userStorage so the per-user prefix (K_<userId>_) is
    // applied correctly; raw localStorage with the bare key would write
    // to the wrong slot and the next load wouldn't see anything.
    const writes = {
      'tx_all_v1':            transactions,
      'cfg_leads_v1':         leads,
      'apps_v1':              apps,
      'calls_v1':             calls,
      'cfg_producers_v1':     producers,
      'cfg_settings_v1':      settings,
      'goal_config_v1':       goalConfig,
    };
    for (const [k, v] of Object.entries(writes)) {
      try { userStorage.set(k, JSON.stringify(v)); } catch { /* quota or pre-auth */ }
    }
    // Flag so the operator can wipe demo data later. Stored under
    // userStorage too so it's per-user, not global to the browser.
    try { userStorage.set('demo_loaded', '1'); } catch { /* ignore */ }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[seedDemoData] failed:', err.message);
  }
}

// Helper to get display name for a producer - always tries KNOWN_PRODUCERS first for hardcoded accuracy
const getProducerName = (prodNum, producers) => {
  // Prefer KNOWN_PRODUCERS for the 9 agents we've pre-baked
  if (KNOWN_PRODUCERS[prodNum]?.name) return KNOWN_PRODUCERS[prodNum].name;
  // Otherwise use stored producer data
  if (producers && producers[prodNum]?.name) return producers[prodNum].name;
  // Cross-carrier alias fallback: when this prodNum is an alias of
  // another (Harrison's AM-FFCQN6 → 1173602, etc.), bubble up to the
  // canonical record's name so views that don't yet know about the
  // alias chain still display the right person.
  if (producers && producers[prodNum]?.aliasOf) {
    const canon = canonicalProdNumInline(prodNum, producers);
    if (canon !== prodNum) {
      if (KNOWN_PRODUCERS[canon]?.name) return KNOWN_PRODUCERS[canon].name;
      if (producers[canon]?.name)       return producers[canon].name;
    }
  }
  // Final fallback. Guard the prodNum-missing case so unmapped leads
  // don't render "Producer undefined" on the dial card (operator
  // bug report 2026-06-28: Sarah Johnson card showed this literal).
  if (prodNum == null || prodNum === '') return 'Unassigned';
  return `Producer ${prodNum}`;
};

// Inline copy because getProducerName is declared before
// canonicalProdNum is hoisted-visible at call time. Same logic,
// 8-hop cycle guard.
const canonicalProdNumInline = (prodNum, producers) => {
  if (!prodNum) return prodNum;
  let cur = String(prodNum);
  for (let i = 0; i < 8; i++) {
    const a = producers?.[cur]?.aliasOf;
    if (!a || a === cur) return cur;
    cur = String(a);
  }
  return cur;
};

// Walk the producer.aliasOf chain to find the canonical prodNum for
// this person. Used wherever we want to consolidate the same human
// across carriers (Harrison Curry shows up as 1173602 on MOO,
// AM-FFCQN6 on Americo, TRANS-CURRY-H on Transamerica — all three
// alias to one canonical id). Defensive against accidental cycles
// (caps at 8 hops). Empty / unknown input returns input unchanged.
const canonicalProdNum = (prodNum, producers) => {
  if (!prodNum) return prodNum;
  let cur = String(prodNum);
  for (let i = 0; i < 8; i++) {
    const alias = producers?.[cur]?.aliasOf;
    if (!alias || alias === cur) return cur;
    cur = String(alias);
  }
  return cur;
};

// All prodNums (including the canonical one) that resolve to the same
// person via aliasOf. Used by views that want to display "1173602 +
// 2 aliases" or sum production across all of them.
const aliasProdNums = (canonicalPn, producers) => {
  const out = [canonicalPn];
  for (const [pn, info] of Object.entries(producers || {})) {
    if (pn === canonicalPn) continue;
    if (canonicalProdNum(pn, producers) === canonicalPn) out.push(pn);
  }
  return out;
};

const getProducerTeam = (prodNum, producers) => {
  // Owner classification (e.g. Harrison) is hardcoded in KNOWN_PRODUCERS and wins over
  // any stored value — the owner's production stands alone, not part of either team.
  if (KNOWN_PRODUCERS[prodNum]?.team === 'owner') return 'owner';
  if (producers && producers[prodNum]?.team && producers[prodNum].team !== 'unassigned') return producers[prodNum].team;
  if (KNOWN_PRODUCERS[prodNum]?.team) return KNOWN_PRODUCERS[prodNum].team;
  return 'unassigned';
};

const SEED_TRANSACTIONS = []; // intentionally empty: previous inline value contained the operator's real client policy data and shipped in the public bundle. Use the Producers tab or upload statements for any seed data.
const SEED_UPLOADS = [];
export default function CommissionTracker() {
  // Workspace mode — hard separator between the Life and Health CRMs.
  // Default is 'both' so power users keep the unified view. Setting it
  // to 'health' hides every Life-only nav group + lands the agent on
  // the Health Portal; 'life' does the inverse. Persisted to
  // localStorage so a Health-only agent doesn't see a Life flash on
  // reload.
  //
  // Why it matters operationally:
  //   · Some agencies sell health-only; their producers shouldn't see
  //     FEX / IUL / Term UW etc. or get confused by the wrong nav.
  //   · Some producers are life-only; the Health Portal noise hurts
  //     focus and the Pipeline numbers don't apply to them.
  //   · "Both" is for owners and dual-role agents.
  // Theme state — 'light' | 'dark' | 'system'. Persisted per-user
  // via cloudSet + localStorage. Applied by toggling the 'dark' class
  // on <html> (Tailwind CDN's darkMode: 'class' pattern, configured
  // in public/index.html). Initial value read at load-time by the
  // inline script in index.html to avoid a flash of the wrong theme
  // on first paint — this hook just tracks the current state so the
  // toggle button renders correctly and future changes propagate.
  const [theme, setTheme] = useState(() => {
    try {
      const v = localStorage.getItem('theme');
      if (v === 'light' || v === 'dark' || v === 'system') return v;
    } catch {}
    return 'system';
  });

  // Apply theme to <html> whenever it changes. 'system' respects the
  // OS-level prefers-color-scheme; user selection overrides it.
  useEffect(() => {
    try {
      const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      const shouldDark = theme === 'dark' || (theme === 'system' && prefersDark);
      document.documentElement.classList.toggle('dark', shouldDark);
      // Write-through: localStorage (fast path) + Supabase user_state
      // (cross-device sync). cloudSet is non-blocking.
      localStorage.setItem('theme', theme);
      const uid = getCurrentUserId();
      if (uid) cloudSet(uid, 'theme', theme);
    } catch {}
  }, [theme]);

  // Listen for OS-level dark mode changes when the user's setting is
  // 'system' — respect their next flip without requiring a page reload.
  useEffect(() => {
    if (theme !== 'system') return;
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => document.documentElement.classList.toggle('dark', e.matches);
    try { mq.addEventListener('change', handler); }
    catch { mq.addListener(handler); /* Safari 13 fallback */ }
    return () => {
      try { mq.removeEventListener('change', handler); }
      catch { mq.removeListener(handler); }
    };
  }, [theme]);

  // Read localStorage for INSTANT paint (avoids "Both" flash on the
  // first frame). The cloud-sync effect below reconciles once the
  // Supabase snapshot loads — this handles cross-device sync when
  // the browser has no local record yet (e.g. new device / private
  // window / cache cleared).
  const [workspaceMode, setWorkspaceMode] = useState(() => {
    try {
      const v = localStorage.getItem('workspaceMode');
      if (v === 'health' || v === 'life' || v === 'both') return v;
    } catch {}
    return 'both';
  });
  // Track whether localStorage HAD a value on mount — used to decide
  // if cloud should overwrite local. If localStorage was empty, the
  // user hasn't chosen on THIS device yet, so cloud (their last
  // chosen value from any device) wins. If localStorage had a value,
  // it wins — the user actively chose here.
  const workspaceModeHadLocalOnMount = React.useRef(false);
  React.useEffect(() => {
    try { workspaceModeHadLocalOnMount.current = !!localStorage.getItem('workspaceMode'); } catch {}
  }, []);

  useEffect(() => {
    // Write-through: localStorage (fast path) + Supabase user_state
    // (durable cross-device path). cloudSet is non-blocking; UI
    // never waits for it.
    try { localStorage.setItem('workspaceMode', workspaceMode); } catch {}
    const uid = getCurrentUserId();
    if (uid) {
      try { cloudSet(uid, 'workspaceMode', workspaceMode); } catch {}
    }
  }, [workspaceMode]);

  // ── Subscription entitlements (Life / Health / Both) ────────────────
  // The agency's purchased product-access, resolved server-side (migration
  // 192). The workspace switcher and nav are constrained to purchased
  // lines; a Life-only agency can't wander into Health. Defaults to full
  // access until the fetch resolves, so nothing flickers hidden.
  const [entitlements, setEntitlements] = React.useState(null);
  const allowedModes = React.useMemo(() => {
    const pa = entitlements?.productAccess;
    if (pa === 'life') return ['life'];
    if (pa === 'health') return ['health'];
    return ['life', 'health', 'both'];
  }, [entitlements]);
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch('/api/entitlements/me', { headers: { ...(await bearer()) } });
        if (!r.ok) return;
        const j = await r.json();
        if (alive) setEntitlements(j);
      } catch { /* fail-open: UI stays full-access */ }
    })();
    return () => { alive = false; };
  }, []);
  // Coerce a stale/free-chosen workspaceMode into an entitled value.
  React.useEffect(() => {
    if (!entitlements) return;
    if (!allowedModes.includes(workspaceMode)) setWorkspaceMode(allowedModes[0]);
  }, [entitlements, allowedModes, workspaceMode]);

  // Portal-choice prompt visibility. Shows post-signup ONLY if the
  // user has never dismissed it. Once dismissed (via pick or skip),
  // workspaceModePromptSeen is stamped in user_state and localStorage
  // so it never re-appears — even if the user later changes their
  // portal choice via the chips.
  const [showPortalPrompt, setShowPortalPrompt] = React.useState(false);
  // Cross-device reconcile — polls snapshot readiness and takes the
  // cloud value only when local was empty on mount. Fires once when
  // ready; then no-ops. Idle in same-device sessions.
  const [snapshotChecked, setSnapshotChecked] = React.useState(false);
  useEffect(() => {
    if (snapshotChecked) return;
    const uid = getCurrentUserId();
    if (!uid) return;
    if (!snapshotIsReady(uid)) {
      // Try again next tick. Snapshot readiness flips within ~1s of session start.
      const t = setTimeout(() => setSnapshotChecked(s => s), 250);
      return () => clearTimeout(t);
    }
    setSnapshotChecked(true);
    if (!workspaceModeHadLocalOnMount.current) {
      const cloud = snapshotGet(uid, 'workspaceMode');
      if (cloud === 'life' || cloud === 'health' || cloud === 'both') {
        if (cloud !== workspaceMode) setWorkspaceMode(cloud);
      }
    }
    // Same cross-device reconcile for theme: cloud wins on a fresh
    // device (empty localStorage). Otherwise same-device localStorage
    // is authoritative because the user explicitly picked it here.
    try {
      const hadLocalTheme = !!localStorage.getItem('theme');
      if (!hadLocalTheme) {
        const cloudTheme = snapshotGet(uid, 'theme');
        if (cloudTheme === 'light' || cloudTheme === 'dark' || cloudTheme === 'system') {
          if (cloudTheme !== theme) setTheme(cloudTheme);
        }
      }
    } catch {}
    // Portal-choice prompt gate — show if the user has NEVER seen it
    // (neither cloud flag nor local flag). Once dismissed, never
    // re-appears. New signups on any device → see the prompt exactly
    // once. Existing users who upgrade to this code → don't get the
    // prompt if they've already been using workspaceMode (any local
    // OR cloud value counts as "they know the feature exists").
    let seen = false;
    try { seen = seen || !!localStorage.getItem('workspaceModePromptSeen'); } catch {}
    const cloudSeen = snapshotGet(uid, 'workspaceModePromptSeen');
    seen = seen || cloudSeen === true;
    // Treat existing workspaceMode users as "seen" — they already know.
    const cloudMode = snapshotGet(uid, 'workspaceMode');
    if (cloudMode === 'life' || cloudMode === 'health' || cloudMode === 'both') seen = true;
    if (workspaceModeHadLocalOnMount.current) seen = true;
    if (!seen) setShowPortalPrompt(true);
  }, [snapshotChecked, workspaceMode]);

  // Set of tab IDs that belong to the Health workspace. Used to bounce
  // the current tab back to a sensible default when the agent switches
  // workspace mode away from one that owns the current tab.
  const HEALTH_TABS = React.useMemo(() => new Set([
    'health_portal', 'health_briefing', 'ceo_briefing', 'war_room', 'ask_aria', 'health_renewals', 'health_opportunities', 'health_policies', 'health_eligibility', 'health_products', 'health_at_risk', 'health_cross_sell', 'health_war_room',
    'health_plan_intel', 'health_reconciliation', 'health_commissions',
    'health_lifecycle', 'health_calendar',
    'health_leads', 'health_inbox', 'health_clients', 'health_enrollment',
    'health_consents', 'health_audit', 'health_workflows',
    'cross_sell', 'coverage_summary', 'soa',
    'daily_tracker', 'expenses', 'health_dashboard',
    'health_enroll',   // ENROLL agent command surface
    'health_nurture',  // NOVA agent dashboard
    'audit',           // AUDIT compliance-readiness dashboard
    // 2026-06-26: shared with Life so the Health-mode redirect at
    // the workspaceMode useEffect doesn't yank the operator off
    // Inbox / Leads when they're in Health workspace.
    'inbox', 'leads',
    'medicare_command',   // Medicare-specific homepage — turning-65, AEP, AWV
    'medicare_benefits',  // Benefits reminder + OTC tracker + Claims assistance
    'medicare_sep',       // Special Enrollment Period tracker
    'medicare_referrals', // Referral + Satisfaction center
    'medicare_twin',      // Digital Twin — natural-language book query
    'compliance_readiness', // Compliance Readiness Meter book-wide
    'signature_health',     // Signature Health + Smart Signature Builder
    'compliance_ops',       // Calendar + Read-and-Ack + Recordings + QA
    'compliance_intel',     // Agency + Agent scoring + Heat Map
    'audit_room',           // One-Click Audit Room per-client landing
    'client_success',       // AI Client Success Manager — cadence/signals/objections/relationship
    'life_book_manager',    // Life Book of Business Manager — 5 gap sections
    // 2026-06-28: Medicare + ACA workspace views render at tab keys
    // 'medicare' / 'aca' (lines ~5331-5332 in this file). They were
    // omitted from this set, so clicking those sub-tabs in Health
    // mode kicked the operator back to health_dashboard via the
    // workspaceMode redirect. Adding both here.
    'medicare', 'aca',
    'aca_plan_intelligence', // ACA Plan Intelligence (Release 1) — CMS Marketplace shop/compare
    // BUG FIX 2026-07-14: Health-mode users clicking Command Center
    // in the sidebar routed to health_command_center via
    // HEALTH_COCKPIT_ROUTES.command, but this tab wasn't in
    // HEALTH_TABS so the workspaceMode redirect immediately kicked
    // them to health_portal. Same class of bug that broke Operations.
    'health_command_center',
    // money_map: intentionally NOT in HEALTH_TABS anymore. Life-only.
    // A Health user hitting #tab=money_map via URL gets redirected
    // to health_portal by the workspace-mode effect below. Money Map
    // is still in SHARED_TABS (below) — no, actually, that's also
    // removed. If we ever want Money Map back in Health, un-hide
    // the top-nav button and re-add here + to SHARED_TABS.
  ]), []);

  // Tab state — uses usePersistentState so the value lives in BOTH
  // the URL hash (#tab=...) and localStorage. Operator can refresh OR
  // share a deep-link and land on the same tab; bookmarks survive.
  // Restore precedence: URL hash → namespaced localStorage → legacy
  // 'activeTab' key (one-time migration) → workspace-mode default →
  // 'briefing'.
  const [tab, setTab] = usePersistentState('tab', (() => {
    try {
      // Legacy migration — read the old non-namespaced key one last
      // time so existing users don't lose their saved tab on upgrade.
      const legacy = localStorage.getItem('activeTab');
      if (legacy) { try { localStorage.removeItem('activeTab'); } catch {} return legacy; }
    } catch {}
    // New users (no saved tab) land on the plain-language "What Needs Attention"
    // hub — the daily-driver screen: what needs action today + one-tap jump to
    // leads, clients, calls, messages, money. Chosen over the analytics-heavy
    // 'briefing' / 'health_portal' so the main things to run the business are the
    // first things seen. Returning users keep their last tab (restored above).
    return 'attention';
  })(), {
    // Permissive validate — block obviously-corrupt values but let
    // route handlers be the source of truth for what's a real tab
    // (an invalid tab id just renders nothing; no crash, and the
    // nav remains clickable to recover).
    validate: (v) => (typeof v === 'string' && v.length > 0 && v.length < 64) ? v : null,
    // TAB IS NAVIGATION — every click pushes a real browser history
    // entry so Backspace/browser Back walks the tabs a user actually
    // visited instead of exiting the site on the first press. Filter
    // + sub-tab state (managed by other usePersistentState hooks)
    // stays on replaceState to avoid flooding the back-stack.
    historyMode: 'push',
  });

  // Workspace route memory. Life / Health / Both are distinct operating
  // contexts, so switching should return the operator to where they last were
  // in the target context — not dump them on its homepage every time. Persist
  // the last tab per workspace and restore it on switch. A restored tab is by
  // definition valid in that workspace, so the workspaceMode redirect guard
  // won't bounce it.
  const switchWorkspace = React.useCallback((next) => {
    if (next !== 'life' && next !== 'health' && next !== 'both') return;
    if (next === workspaceMode) return;
    let store = {};
    try { store = JSON.parse(localStorage.getItem('ws_last_tab') || '{}') || {}; } catch { store = {}; }
    store[workspaceMode] = tab;
    try { localStorage.setItem('ws_last_tab', JSON.stringify(store)); } catch {}
    setWorkspaceMode(next);
    const restore = store[next];
    if (restore && typeof restore === 'string' && restore !== tab) setTab(restore);
  }, [workspaceMode, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cockpit is now the permanent shell — no opt-in toggle. Sidebar renders
  // as a fixed left column via COCKPIT_ROUTES. Old cockpit_preview flag
  // retired; localStorage entry left alone (harmless).

  // Focus Mode — Musk-style single-task view. Loads the current
  // priority + client briefing + AI copilot. Escape exits.
  const [focusMission, setFocusMission] = React.useState(null);

  // Start My Day — guided "press Next" driver over the day's real agenda.
  // Any surface opens it by dispatching the crm-start-my-day event.
  const [startDayOpen, setStartDayOpen] = React.useState(false);
  React.useEffect(() => {
    const open = () => setStartDayOpen(true);
    window.addEventListener('crm-start-my-day', open);
    return () => window.removeEventListener('crm-start-my-day', open);
  }, []);

  // Tab navigation history — install ONCE on mount.
  //   1. Backspace outside editable fields → history.back(). Preserved
  //      inside inputs / textareas / rich-text editors so a note-taker
  //      doesn't lose keystrokes.
  //   2. Popstate → restore scroll position we stashed on tab push.
  // Both return their unsubscribe; effect cleanup calls them on
  // unmount so re-renders don't stack listeners.
  React.useEffect(() => {
    const uninstallBackspace = installBackspaceBackHandler();
    const uninstallScroll    = installScrollRestoration();
    return () => {
      try { uninstallBackspace(); } catch { /* noop */ }
      try { uninstallScroll();    } catch { /* noop */ }
    };
  }, []);

  // Reset scroll to the top on every FORWARD tab change (sidebar click,
  // ⌘K jump, ARIA "Open <tab>" action, etc). installScrollRestoration
  // above only fires on `popstate` (the Back button) — it was the only
  // place scroll position got touched, so a forward navigation left
  // window.scrollY exactly where it was on the PREVIOUS tab. That's
  // invisible on short tabs, but on a cinematic Room tab (Settings,
  // Command Center, ...) the full-bleed HqBackdrop is `position:fixed
  // inset:0`, so it fills the viewport identically at any scroll
  // offset — it always LOOKS like you're at the top of the room even
  // when you're scrolled hundreds of pixels into the new tab's
  // document, leaving the Room's own header (which really does render
  // at the top of the document) off-screen above the fold. Root cause
  // of the "Settings header renders far down the page" defect.
  // Runs after installScrollRestoration's popstate handler (a
  // setTimeout(...,0) macrotask) so a genuine Back-button restore
  // still wins the final scroll position.
  React.useEffect(() => {
    try { window.scrollTo(0, 0); } catch { /* noop */ }
  }, [tab]);

  // Health Inbox is now redundant — the unified Inbox handles vertical
  // filtering (Both books / Life / Health / Untagged pills). When
  // anything routes to 'health_inbox' (legacy palette entry, sidebar,
  // deep links), pre-set the vertical filter to 'health' and redirect.
  useEffect(() => {
    if (tab !== 'health_inbox') return;
    try { localStorage.setItem('inbox.verticalFilter', 'health'); } catch {}
    setTab('inbox');
  }, [tab]);
  // Audit 2026-07-01 BUG-003: #tab=life_uw deep link rendered a blank
  // page because the router only knows 'termuw' / 'uw_agent'. Alias it
  // rather than change what the underwriting tab actually is — old
  // bookmarks, external referrers, and support-doc links keep working.
  useEffect(() => {
    if (tab === 'life_uw') setTab('termuw');
  }, [tab]);
  // Tabs that are universal — reachable regardless of workspace mode.
  // Settings, the admin Customers view, the wallet, etc. live above
  // the health/life split; redirecting away from them snaps the agent
  // off the page they just clicked. Add new global tabs here, not to
  // HEALTH_TABS (which is for the health-vertical sub-nav).
  const GLOBAL_TABS = React.useMemo(() => new Set([
    'settings',
    'aca_command',    // ACA Marketplace Command Center
    'ppo_command',    // Private Health / PPO Command Center
    'dental_command', // Dental & Vision Command Center
    'campaigns',      // Client email/text campaign roadmap builder
    'field_sales',    // Field Sales / Community Outreach command surface (mobile)
    'landing_pages',  // AI Landing Page Builder (Canvas) — subscriber-built pages
    'prospect_queue', // My New Prospects — Lead Engine work queue (field/QR/landing)
    'lead_dashboard', // Lead Command — manager aggregate over all lead sources
    'aca_oep',        // ACA OEP Mission Control — Open Enrollment command center
    'agent_protection', // Agent Protection & Evidence Center — tamper-evident evidence
    'carrier_portals', // Carrier Client Portal Setup — directory + resolver + secure redirect
    'nova',           // Nova Command Center — executive AI ops + Ask Nova (grounded)
    'team',           // Team & roles — roster, roles, assignment foundation
    'system_health',  // Admin System Health Center — real subsystem probes
    'setup_checklist', // Subscriber setup readiness + phone test
    'import_leads',   // Universal AI Lead Import Center (Sage)
    'medicare_readiness', // Medicare Safe-to-Proceed compliance gate
    'people',         // Master Person/Household identity — leads+policies unified
    'growth',         // Growth Studio — one funnel per source, scan→enrollment
    'next_call',      // Next Best Call — one dominant "who to call now + why"
    'no_show_recovery', // Missed-appointment recovery queue with live timer
    'booking_scorecard', // Show-rate by source + setter
    'approvals',      // AI authorization queue — relevant in every workspace mode
    'client_success_center', // Welcome-call + persistency lifecycle queue (all verticals)
    'life_beneficiaries', // Life beneficiary + rider editor
    'medicare_plan_details', // Typed Medicare satellite editor
    'workflow_command', // Workflow Command Center — per-contact automation transparency
    'source_roi',     // Lead Source ROI — attribution (both verticals)
    'followups',      // Follow-Up Command — stale/callbacks/objections worklists
    'execution_queue', // Execution Queue — unified prioritized worklist across all sources
    'calendar_room', // Calendar Room — appointment command surface
    'data_intelligence', // Data Intelligence Center — imports/recon hub
    'client_command',    // Client Command Center — single-client cockpit
    'recon_center',      // Three-way reconciliation
    'import_review',     // Import review queue
    'bob_command',    // Book of Business Command — book-level financial view
    'ai_workforce',   // AI Workforce Command — AI employee operations hub
    'customers',
    'nudge',          // NUDGE follow-up panel (NOVA brain, multi-niche)
    'facebook_leads', // Self-service Facebook Lead Ads setup (both verticals)
    'forge',          // FORGE AI workflow builder (self-service for all users 2026-07-20)
    'retain_closers',    // RETAIN closer roster admin
    'priority_leads',    // Scored + speed-to-contact priority queue
    'agency_analytics',  // Agency-level KPIs (close rate by source, TTC, MRR concentration)
    'referral_partners', // Referral partner CRM + cold-partner alerts
    'automation_map',    // Every automation in the CRM, in one map
    'webinar_builder',   // AI Webinar Builder (Medicare event funnels)
    // BUG FIX 2026-07-14: every destination reachable from the
    // permanent LeftSidebar's Operations + System sections must be
    // global — otherwise clicking VA Workforce / AI Team / Reports /
    // Billing / etc while in Health workspace fires setTab, then the
    // workspaceMode redirect useEffect below fires setTab('health_portal')
    // because the destination isn't in HEALTH_TABS. Sidebar clicks
    // look "broken" but they're actually being immediately rewritten.
    // See src/cockpit/LeftSidebar.jsx NAV_GROUPS for the canonical
    // list of sidebar destinations.
    'ai_staff',            // Operations → AI Team
    'va_workforce',        // Operations → VA Workforce
    'call_activity_audit', // Operations → Call activity audit
    'compliance_ops',      // Operations → Compliance (Life mode used to bounce)
    'pnl',                 // Operations → Reports
    'library',             // System → Knowledge
    'billing',             // System → Billing
    'launch_readiness',    // System → Account health
    'support_lookup',      // System → Support lookup
    'new_signups',         // System → New signups
    'system_diagnostics',  // System → System diagnostics
    // Work + Book of Business sidebar destinations that aren't in
    // HEALTH_TABS but are exposed to Health users via the sidebar.
    // Without these Health users see the sidebar item highlight but
    // land back on health_portal.
    'phone',               // Work → Phone
    'tasks',               // Work → Tasks
    'aca_imports',         // Book of Business → ACA Imports
    'operations_floor',    // Operations → Operations Floor
    'money_command',       // Operations → Money Command (AI Agent CPA)
    'response_speed',      // Operations → Response Speed
    'missed_ops',          // Operations → Missed Opportunities
    'goal_planner',        // Operations → Goal Planner
    'comp_setup',          // Operations → Comp Setup
    'a2p_setup',           // Resume-A2P re-entry (surfaced by banner + sidebar when needed)
    'command_center',      // Phase 4 Command Center — Life + Health unified surface
    'leads_intelligence',  // Phase 5 Leads Intelligence — opportunity-first sectioned surface
    'messages_intelligence', // Phase 6 Messages Intelligence — thread roll-up with ARIA rec
    'call_queue',            // Phase 6 Call Queue — priority-scored dial list
    'client_intelligence',   // Phase 7 Client Intelligence — risk-band roster
    'revenue_intelligence',  // Phase 8 Revenue Intelligence — net + carriers + delta
    'compliance_intelligence', // Phase 9 Compliance Intelligence — consent status roll-up
    'rep_performance',        // Phase 10 Rep Performance — effort/skill/results/consistency
    'product_health',         // Specter QA — Product Health "Bug Wall"
    // Life user clicking Book of Business → Renewals routed to the
    // health_renewals view (there is no life_renewals). Rather than
    // hide the item, make the destination reachable from Life mode.
    'health_renewals',     // Book of Business → Renewals
    'retention',           // Retention Room — payment-risk clients (both verticals)
    'hierarchy_map',       // Money Map — hierarchy + downline commissions (isDemo-gated)
    'chargebacks',         // Chargebacks Room — carrier-side revenue-leak surface
    'book_snapshot_v2',    // Book Snapshot Room — real book aggregate across both verticals
    'statements_v2',       // Statements Room — commission_statements uploads history
    'persistency_v2',      // Persistency Room — cohort + carrier attrition analytics
    'carrier_intel',       // Carrier Intelligence Room — carrier lens on commission_transactions
    'audit_log',           // Audit Log Room — hash-chained audit_events viewer
    'overrides_v2',        // Overrides Room — override payout ledger
    'performance_v2',      // Performance Room — activity dashboard
    'agency_onboarding',   // Agency Onboarding Wizard — 10-step guided setup
    'ai_comms_setup',      // AI Communications Setup Wizard — 5-step
    'payouts_v2',          // Payouts Room — producer × month payout ledger
    'applications_v2',     // Applications Room — pending pipeline tracker
    'commission_intelligence', // Commission Intelligence — health/ACA/Medicare expected vs received vs variance
    'medicare_connections',    // Medicare Data & API Connections — CMS + vendor credential center
    'medicare_client_profile', // Medicare Client Profile — Rx / providers / pharmacies capture
    'medicare_plan_intelligence', // Medicare Plan Intelligence — CMS plan search + compare
    'coverage_guide',          // Coverage & Care Guide — personalized client guide (Medicare/ACA/PPO)
    'find_care',               // Find Care — standalone CMS provider directory (ACA + Medicare)
    'attention',               // Unified "what needs attention right now" command rollup
    'medicare_reviews',        // AEP annual plan-review workflow board
    'soa_compliance',          // Medicare SOA Compliance Guard (product scope + 48h clock)
    'soa_scripts',             // SOA script library (version-controlled scripts)
    'soa_session',             // Live SOA teleprompter + recorded-verbal session
    'soa_qa',                  // SOA QA scoring (compliance completeness review)
    'soa_evidence',            // SOA audit evidence package (print/PDF export)
    'plan_intake',             // Plan Intake — parse HealthSherpa enrollment (text/screenshot) → attach to client
    'lapse_watch',             // Lapse Watch — payment/lapse retention agent (auto-text reminders)
    'aca_retention',           // ACA Retention & Open Enrollment command center
    'aca_compliance',          // ACA Compliance — agency-wide required-forms roster
    'product_readiness',       // AI QA Manager — Fortune-500 product checklist run against live data
    'policy_access',           // Carrier Access Message Engine — policy portal onboarding queue
    'carrier_portals',         // Carrier Portal Manager (admin: portals + aliases + unmatched)
    'import_analyst',          // AI Import Analyst — instant book-of-business analysis
    'part_d_penalty',          // Part D late-enrollment penalty calculator
    'custom_agents',           // AI Agent Builder — user-defined trainable agents
    'integrations_health',     // Integration capability + sync-health matrix
  ]), []);

  // Tabs that are valid in BOTH workspaces — must be exempt from the
  // mode-switch redirect below or clicking "Prospecting" in Life mode
  // would bounce off Inbox / Leads (which were added to HEALTH_TABS in
  // aea6e98 so the Health-mode redirect doesn't yank operators off
  // them). Without this exemption, BOTH redirects fire on the same tab.
  const SHARED_TABS = React.useMemo(() => new Set([
    'inbox', 'leads',
    // money_map removed from SHARED_TABS (July 2026) — Life-only per
    // request. Health-mode redirect kicks in cleanly for URL entries.
  ]), []);

  // When the workspace mode flips, redirect the current tab to a sensible
  // default for the new mode. Otherwise switching from "Health" → "Life"
  // would leave the agent stranded on a now-hidden Health tab. Global
  // tabs (settings/customers) and shared tabs (inbox/leads) are exempt
  // — they're reachable from both modes by design.
  useEffect(() => {
    if (GLOBAL_TABS.has(tab) || SHARED_TABS.has(tab)) return;
    if (workspaceMode === 'health' && !HEALTH_TABS.has(tab)) {
      setTab('health_portal');
    } else if (workspaceMode === 'life' && HEALTH_TABS.has(tab)) {
      setTab('briefing');
    }
  }, [workspaceMode, tab, HEALTH_TABS, GLOBAL_TABS, SHARED_TABS]);

  // Global command palette (⌘K / Ctrl+K). Open from anywhere — jump to
  // any tab, search clients/leads, fire common actions. Single keystroke
  // replaces digging through the nav.
  const [palette, setPalette] = useState(false);
  React.useEffect(() => {
    const onKey = (e) => {
      const isMac = navigator.platform.toUpperCase().includes('MAC');
      const meta = isMac ? e.metaKey : e.ctrlKey;
      if (meta && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPalette(p => !p);
      } else if (e.key === 'Escape' && palette) {
        setPalette(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [palette]);
  const [transactions, setTransactions] = useState([]);
  const [producers, setProducers] = useState({});
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  // payouts/setPayouts removed alongside recordPayout + PayoutsView.
  const [uploads, setUploads] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all'); // 'all' | 'YYYY' — narrows the month dropdown
  const [periodMode, setPeriodMode] = useState('month'); // 'week' | 'month' | 'custom'
  const [selectedWeek, setSelectedWeek] = useState('all');
  const [customStart, setCustomStart] = useState(''); // YYYY-MM-DD
  const [customEnd, setCustomEnd] = useState(''); // YYYY-MM-DD
  const [clientNotes, setClientNotes] = useState({}); // { [clientKey]: noteText }
  const [policyTracking, setPolicyTracking] = useState({}); // { [policyKey]: { status, notes, contactLog: [{date, action, notes}] } }
  const [presentationConfig, setPresentationConfig] = useState(null);
  // clientProfiles: { [clientKey]: { address1, address2, city, state, zip, phone, email, dob, gender, ownerName, ownerDob, ownerGender, beneficiaries: [{name, relationship, percent}] } }
  const [clientProfiles, setClientProfiles] = useState({});
  // lobConfig: { fromName, fromAddress1, fromAddress2, fromCity, fromState, fromZip }
  // Note: the API key field was removed — Lob calls now go through /api/lob/postcards.
  const [lobConfig, setLobConfig] = useState({});
  // pipeline: array of manually-tracked policy records (separate from carrier transaction data)
  const [pipeline, setPipeline] = useState([]);
  // calendar: array of appointment records (VA-managed)
  const [calendar, setCalendar] = useState([]);
  // App-level client profile modal — any view can open it via openClientProfile(clientKey)
  const [activeClientKey, setActiveClientKey] = useState(null); // { order: [id...], disabled: { id: true }, overrides: { id: { title, subtitle } } } or null = defaults
  const [coverageGuideClientId, setCoverageGuideClientId] = useState(null); // preselected client when opening the Coverage Guide from a profile
  const [activeLeadKey, setActiveLeadKey] = useState(null);
  // Consume pending id set by useHqPathRouting when a URL like
  // /command-center/leads/<uuid> is opened directly.
  React.useEffect(() => {
    try {
      const leadId = sessionStorage.getItem('hq.pending.leadId');
      if (leadId) {
        sessionStorage.removeItem('hq.pending.leadId');
        setActiveLeadKey(leadId);
        setTab('lead_profile');
      }
      const clientId = sessionStorage.getItem('hq.pending.clientId');
      if (clientId) {
        sessionStorage.removeItem('hq.pending.clientId');
        setActiveClientKey(clientId);
        setTab('smart_profile');
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Life Protection Command — the LIFE-vertical cinematic client deep
  // view (src/life/LifeProtectionCommand.jsx). Separate from
  // activeClientKey/SmartClientProfile above, which stays the general
  // client drawer. Holds a real clients.id (uuid); null = closed.
  const [lifeClientId, setLifeClientId] = useState(null);
  // Which carrier's paste modal is open (null when closed). Replaces the
  // boolean pasteOpen flag now that both Americo and Transamerica statements
  // can be pasted as text.
  const [pasteCarrier, setPasteCarrier] = useState(null); // null | 'americo' | 'transamerica'
  // Money-intelligence panel that pops up after every upload.
  // Null when there's nothing to show; an insights object after import.
  const [statementInsights, setStatementInsights] = useState(null);
  // Multi-tenancy (Phase 1). currentOrg = the user's primary org (owner
  // role wins if they're in multiple). teamPanelOpen toggles the manage-
  // team modal. orgError surfaces any backfill / RLS misconfig as an
  // inline note instead of blowing up the header.
  const [currentOrg, setCurrentOrg] = useState(null);
  const [orgError, setOrgError] = useState('');
  const [teamPanelOpen, setTeamPanelOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [loading, setLoading] = useState(true);
  // Tracks which user the in-memory state belongs to. Until the load
  // effect finishes hydrating for the current user, the dashboard
  // gate below renders "Loading…" instead of the stale state belonging
  // to whoever was signed in last. Without this, the browser briefly
  // paints with the previous user's transactions / clients on sign-in
  // → sign-up — a real data leak across tenants on shared machines.
  const [loadedForUserId, setLoadedForUserId] = useState(null);
  // Admin-only "view as" override. When set, the load effect uses
  // viewingAs.userId instead of currentUser.prodNum so the dashboard
  // renders that customer's data instead of the operator's. Every
  // persist effect is gated on !viewingAs (see the persist section)
  // so the operator can't accidentally write into a customer's snapshot
  // while browsing. Shape: { userId, email } | null.
  const [viewingAs, setViewingAs] = useState(null);
  // effectiveUserId moved below the currentUser declaration so it
  // doesn't TDZ-trip in production builds. See the const block right
  // after `const currentUser = useMemo(...)`.
  const [toast, setToast] = useState(null);
  // Live "needs attention" badge for the top-bar bell — the count of distinct
  // areas needing action (coverage-at-risk, unpaid commissions, renewals, etc.)
  // from /api/command-center/attention. Turns the previously-hardcoded 0 bell
  // into a real notification so an agent SEES when a client is at lapse/payment
  // risk without opening a tab.
  const [attentionCount, setAttentionCount] = useState(0);
  // First-run onboarding wizard (audit #4) — auto-shows when localStorage
  // hasn't been flagged 'completed' or 'dismissed' yet.
  // Auto-show RETIRED (2026-07): the three-part Setup Tour is now the single
  // onboarding spine. FirstRunWizard is kept for event-driven replay but no
  // longer auto-mounts — init false instead of shouldShowOnboarding().
  const [showOnboarding, setShowOnboarding] = useState(false);
  // "10-minute wow" first-touch flow — upload → AI audit → bulk text.
  // Shown BEFORE the regular onboarding wizard so brand-new operators
  // hit value before they hit boring setup. Gated by its own
  // localStorage key so completing one doesn't dismiss the other.
  // Auto-show RETIRED: WowOnboarding remains ONLY as the statement-upload
  // flow reachable from the Setup Tour's "Import your first book" step
  // (via the 'setup-tour:open-upload' event below). No longer auto-mounts.
  const [showWow, setShowWow] = useState(false);
  // Pain-point-driven value tour — shows on a brand-new agent's
  // first login. Walks them through the three biggest value moments
  // (ANCHOR closing issues, workflow engine retention, cross-sell
  // gap discovery) with interactive demos. Replay-able from Settings.
  // Auto-show RETIRED: AnchorValueTour's value-moment content is superseded
  // by the Setup Tour's feature reveals + AI finale. Kept for replay only.
  const [showValueTour, setShowValueTour] = useState(false);
  // VA credentials: in-memory only as of Phase 0 — the previous build persisted
  // plaintext passwords to localStorage, which has been stripped. The whole VA
  // auth flow moves to Supabase in Week 3; this state is kept so surrounding
  // code compiles, but it's unreachable while the maintenance gate is on.
  const [vaCredentials, setVaCredentials] = useState({});
  // vaTimeLogs: array of { id, vaUsername, clockIn (ISO), clockOut (ISO|null), notes }
  const [vaTimeLogs, setVaTimeLogs] = useState([]);
  // vaTasks: manually-added VA tasks. Auto-tasks are derived on render and not stored.
  //   { id, category, title, why, howSteps[], dueDate, relatedLeadId?, relatedPolicyKey?, createdAt }
  const [vaTasks, setVaTasks] = useState([]);
  // vaTaskDone: completion records keyed by task ID. Works for BOTH auto-generated
  // (stable IDs like `auto_welcome_${policyKey}`) and manual task IDs.
  //   { [taskId]: { completedAt: ISO, completedBy: string, note: string } }
  const [vaTaskDone, setVaTaskDone] = useState({});
  // commissionRules: Phase 2 SaaS — owners configure payout splits, overrides,
  // and clawback behavior here instead of via hardcoded logic. The default rule
  // (auto-seeded if storage is empty) reproduces today's "100% to producer of
  // record" math, so adopting this feature is opt-in.
  const [commissionRules, setCommissionRules] = useState([]);
  // carrierCatalog: Phase 3 SaaS — agency-configurable list of carriers and
  // their product mappings. Replaces hardcoded carrier/product detection.
  // Seeded with Transamerica / Americo / Mutual of Omaha matching the
  // existing rules so today's getCarrier/getProduct output is unchanged when
  // the catalog hasn't been customized.
  const [carrierCatalog, setCarrierCatalog] = useState([]);
  // calls: array of individual call records. Each call is one ring/conversation.
  // {
  //   id, agentProdNum, leadId, prospectName, prospectPhone,
  //   date (YYYY-MM-DD), startTime (HH:MM),
  //   direction: 'outbound' | 'inbound',
  //   outcome: 'appt_set' | 'callback' | 'not_interested' | 'voicemail' | 'no_answer' | 'pickup' | 'dnc',
  //   duration (seconds), aiScore (0-10 or null),
  //   notes, createdAt, updatedAt
  // }
  const [calls, setCalls] = useState([]);
  // leads: array of {
  //   id, name, phone, email, source,
  //   agentProdNum, coverageType, estPremium,
  //   cost,            // dollars spent acquiring this lead
  //   premium,         // monthly premium of closed policy (when status='closed')
  //   lapsed,          // boolean — true if a closed policy has since lapsed
  //   status: 'new'|'contacted'|'interested'|'appt_set'|'showed'|'closed'|'no_show'|'dnc'|'recycled',
  //   appointmentDate, appointmentTime, calendarEventId,
  //   notes,
  //   activity: [{ at: ISO, type, body }],
  //   lastContactedAt, attempts,
  //   createdAt, updatedAt,
  // }
  const [leads, setLeads] = useState([]);
  // Quick Lead Upload — Fast Add Drawer visibility. Toggled by the N
  // hotkey (or Cmd/Ctrl+Shift+N) from anywhere in the CRM.
  const [fastAddOpen, setFastAddOpen] = useState(false);
  // Bulk Upload panel visibility — reached from Fast Add "⇪ BULK" chip.
  const [bulkOpen, setBulkOpen] = useState(false);

  // Global hotkeys for Fast Add:
  //   Bare N       — opens the drawer (skipped when the operator is
  //                   focused on an input, textarea, or contenteditable
  //                   element so it never eats real typing).
  //   Cmd/Ctrl+Shift+N — always opens (works even from an input).
  useEffect(() => {
    const onKey = (e) => {
      const tgt = e.target;
      const inField = tgt && (
        tgt.tagName === 'INPUT' || tgt.tagName === 'TEXTAREA' || tgt.tagName === 'SELECT' ||
        tgt.isContentEditable
      );
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'N' || e.key === 'n')) {
        e.preventDefault();
        setFastAddOpen(true);
        return;
      }
      if ((e.key === 'N' || e.key === 'n') && !e.metaKey && !e.ctrlKey && !e.altKey && !inField) {
        e.preventDefault();
        setFastAddOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  // Global call target for the Twilio Voice SDK softphone widget.
  // Set by any "Call" button in the app. Two event names are supported
  // because they diverged: the original global helper uses
  // 'crm-dial-request' but every current room (ClientRoom / LeadRoom /
  // MessagesRoom / RetentionRoom / PhoneCommandRoom) dispatches
  // 'crm-call-request'. Both fire the same widget open.
  const [activeCallLead, setActiveCallLead] = useState(null);
  // PhoneCommandRoom's floating call controls (Mute / Voicemail / End /
  // Hold / Transfer) dispatch crm-call-* window events. When a call is
  // live the mounted <CallWidget> owns and handles them. When idle there
  // is no CallWidget, so these buttons used to be dead clicks — give the
  // operator honest feedback instead. Ref (not state) so the always-on
  // control listener (registered after showToast is defined, below)
  // reads the current call without re-subscribing on every call change.
  const activeCallLeadRef = React.useRef(null);
  activeCallLeadRef.current = activeCallLead;
  // Global ad-hoc SMS composer target. Opened by the `inbox:compose`
  // event (e.g. the "Text" action on an Uplift opportunity, which has a
  // phone but not necessarily a lead record). Renders <SmsComposeModal>.
  const [smsCompose, setSmsCompose] = useState(null);
  React.useEffect(() => {
    const onCompose = (e) => {
      const d = e?.detail || {};
      // `newMessage: true` opens the composer with an editable recipient (a
      // "New Message" from scratch); otherwise a phone is required.
      if (d.phone || d.newMessage) {
        setSmsCompose({ phone: d.phone || '', name: d.name || null, body: d.body || '' });
      }
    };
    window.addEventListener('inbox:compose', onCompose);
    return () => window.removeEventListener('inbox:compose', onCompose);
  }, []);
  React.useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      const lead = detail.lead || (detail.toNumber ? { phone: detail.toNumber, name: detail.contactName || 'Unknown', id: detail.leadId } : null);
      if (lead) setActiveCallLead(lead);
    };
    window.addEventListener('crm-dial-request', handler);
    window.addEventListener('crm-call-request', handler);
    const hangup = () => setActiveCallLead(null);
    window.addEventListener('crm-hangup-request', hangup);
    const openClient = (e) => {
      const clientId = e?.detail?.client_id;
      if (clientId) { setActiveClientKey(clientId); setTab('smart_profile'); }
    };
    window.addEventListener('crm-open-client', openClient);
    const emailReq = (e) => {
      const to = e?.detail?.toEmail;
      if (to) { try { window.location.href = `mailto:${to}`; } catch {} }
    };
    window.addEventListener('crm-email-request', emailReq);
    const apptReq = () => setTab('calendar');
    window.addEventListener('crm-appointment-request', apptReq);
    // AI-initiated call — same widget open as a manual call for now.
    window.addEventListener('crm-ai-call-request', handler);
    return () => {
      window.removeEventListener('crm-dial-request', handler);
      window.removeEventListener('crm-call-request', handler);
      window.removeEventListener('crm-hangup-request', hangup);
      window.removeEventListener('crm-open-client', openClient);
      window.removeEventListener('crm-email-request', emailReq);
      window.removeEventListener('crm-appointment-request', apptReq);
      window.removeEventListener('crm-ai-call-request', handler);
    };
  }, []);

  // crm-goto-tab — any subcomponent can fire this to navigate the
  // top-level tab without prop-drilling setTab. Used by the
  // deprecated SmsConversationsPanel pointer to send the operator
  // to the unified Inbox.
  React.useEffect(() => {
    const handler = (e) => {
      const t = e?.detail?.tab;
      if (typeof t === 'string' && t.length > 0) setTab(t);
    };
    window.addEventListener('crm-goto-tab', handler);
    return () => window.removeEventListener('crm-goto-tab', handler);
  }, []);
  // outreach: per-lead contact attempts log.
  // {
  //   id, leadId, agentProdNum,
  //   date (YYYY-MM-DD), time (HH:MM),
  //   type: 'text' | 'call' | 'email' | 'voicemail',
  //   outcome, note, createdAt
  // }
  const [outreach, setOutreach] = useState([]);
  // leadTemplates: SMS/email message templates with [Name], [Agent Name],
  // [Agent Phone], [Time] tokens. Editable in the Leads → Templates panel.
  const [leadTemplates, setLeadTemplates] = useState([]);
  // goalConfig: monthly commission target + funnel assumptions for the
  // FEX Goal Engine. Defaults match the FEX_CRM_KPI_Engine scenario
  // ($50k/mo, $1k AP, 25% lapse, 22 working days, etc.).
  const [goalConfig, setGoalConfig] = useState(null);
  // fexOnboarding: Phase 0-4 checkbox state for the FEX → $20k path
  const [fexOnboarding, setFexOnboarding] = useState({});
  // Onboarding agent conversation transcript + dismissed flag. The
  // assistant auto-launches for fresh operators (no phone, no leads,
  // no goal target) and persists across reloads so a half-finished
  // setup can resume.
  const [onboardingChat, setOnboardingChat] = useState([]);
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  // saveTasks: VA call-queue status per chargeback (keyed by client+policy)
  const [saveTasks, setSaveTasks] = useState({});
  // Power Dialer overlay — full-screen takeover for high-volume dialing
  const [powerDialerOpen, setPowerDialerOpen] = useState(false);
  const [powerDialerLens, setPowerDialerLens] = useState('all'); // all | fex | mp | iul | hot | cold
  // Deal Room / Follow-Up selection. Previously referenced in the tab render
  // tree (deal_room, follow_up) but NEVER declared in this component's scope —
  // opening Deal Room threw a ReferenceError that crashed the whole app via the
  // root boundary. Declared here so those tabs render instead of crashing.
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  // SMS Inbox read state — tracks { leadId: { lastReadAt } } per operator
  const [inboxReadState, setInboxReadState] = useState({});
  // Set of policy keys (prod_num|policy) the operator has welcomed or
  // dismissed. Drives the "📨 New clients ready to welcome" panel on
  // Home — without this, recently-issued policies would re-appear in
  // the queue on every reload. Persisted via K.acknowledgedWelcomes.
  const [acknowledgedWelcomes, setAcknowledgedWelcomes] = useState([]);
  // CFO Dashboard configuration — overhead, advance %, renewal %, etc.
  const [financialConfig, setFinancialConfig] = useState(null);
  // Underwriting pipeline — apps submitted with status tracking
  const [apps, setApps] = useState([]);
  // AI Accountant chat — operator's CFO/FP&A conversation transcript
  const [accountantChat, setAccountantChat] = useState([]);
  const [accountantOpen, setAccountantOpen] = useState(false);
  // Customizable AI workflows (mortgage protection scheduling assistant
  // is the seed). Each workflow holds persona + role prompt + FAQ pairs
  // + hesitation responses so the agent can tune the script to their
  // voice before the SMS automation runs it.
  const [workflows, setWorkflows] = useState([]);
  // Policy Conservation Opportunities (MOO save-the-policy worklist).
  // Each entry is a structured event from parseConservationReport()
  // plus a per-tenant `status` ('open' | 'contacted' | 'cured' |
  // 'dismissed') and free-text `note`. Keyed by conservationEventId()
  // so re-uploads of the same report don't duplicate rows.
  const [conservation, setConservation] = useState([]);
  // Operator-defined teams. Layered ON TOP of the legacy 'mine' /
  // 'unassigned' / 'owner' team field so existing override math
  // doesn't break. Each producer carries an optional customTeam
  // string (the team id from this list) for grouping/filtering;
  // when blank they're considered untagged.
  // Shape: [{ id, name, color, createdAt }]
  const [customTeams, setCustomTeams] = useState([]);
  // Auth comes from Supabase via AuthContext. We derive the existing
  // currentUser shape (prodNum / role / name) from auth.user so all the
  // downstream views that read currentUser keep working unchanged.
  // role is hard-coded to 'admin' for v1; a profiles table will drive real
  // roles post-launch.
  const auth = useAuth();

  // VA Presence Heartbeat — Phase 3 Slice 2. If the signed-in user
  // is a VA (has a va_users row), install a background ping every
  // 30 seconds that POSTs their current status to /api/va/presence-ping.
  // Status derives from document.visibilityState + time since last
  // user interaction. Non-VAs pay ONLY the one-time /api/va/me lookup
  // — no interval, no listeners installed.
  //
  // Idle detection deliberately does NOT reward mouse movement (spec
  // §6): idle = tab hidden OR no user interaction in the last 120s.
  React.useEffect(() => {
    if (!auth?.user?.id) return;
    let cancelled = false;
    let intervalId = null;
    let listenersInstalled = false;
    let lastActivityAt = Date.now();
    const bumpActivity = () => { lastActivityAt = Date.now(); };

    (async () => {
      try {
        const jwt = auth?.session?.access_token || (await import('./lib/supabase').then(m => m.supabase.auth.getSession())).data?.session?.access_token;
        if (!jwt || cancelled) return;
        const meRes = await fetch('/api/va/me', {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        if (!meRes.ok || cancelled) return;
        const me = await meRes.json();
        if (!me?.is_va || cancelled) return;

        // Install activity listeners for idle detection.
        window.addEventListener('keydown', bumpActivity);
        window.addEventListener('click', bumpActivity);
        listenersInstalled = true;

        const ping = async () => {
          if (cancelled) return;
          const hidden = document.visibilityState === 'hidden';
          const idleFor = (Date.now() - lastActivityAt) / 1000;
          const status = hidden ? 'idle' : idleFor > 120 ? 'idle' : 'writing_notes';
          try {
            const freshJwt = (await import('./lib/supabase').then(m => m.supabase.auth.getSession())).data?.session?.access_token;
            if (!freshJwt) return;
            await fetch('/api/va/presence-ping', {
              method: 'POST',
              headers: { 'content-type': 'application/json', Authorization: `Bearer ${freshJwt}` },
              body: JSON.stringify({ status }),
            });
          } catch { /* silent — heartbeat failure shouldn't disrupt the app */ }
        };
        // Fire once immediately, then every 30s.
        ping();
        intervalId = setInterval(ping, 30_000);
      } catch { /* fall through — no heartbeat installed */ }
    })();

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      if (listenersInstalled) {
        window.removeEventListener('keydown', bumpActivity);
        window.removeEventListener('click', bumpActivity);
      }
    };
  }, [auth?.user?.id]);

  // Real subscription gate — restored 2026-06-23 after audit showed
  // brand-new signups were getting full CRM access without paying.
  // useSubscription() queries public.subscriptions for status + plan
  // and exposes refreshSubscription so the post-Checkout return can
  // re-fire the query.
  const subscription = useSubscription();
  const currentUser = useMemo(() => (
    auth.user
      // Role now comes from the public.users table (migration 004) via
      // AuthContext. While the role query is in flight we fall back to
      // 'client' so the user can still land in the CRM with their own
      // data — admin features just stay hidden until the role resolves.
      //
      // `id` is the auth UUID — the same value many features tried to
      // read as currentUser.id but always got undefined for, which
      // silently broke supabase upserts that depended on it (calendar
      // feed tokens being the discovery case). `prodNum` is kept as a
      // legacy alias for the same UUID so anywhere already using
      // currentUser.prodNum as the owner_id keeps working.
      ? { id: auth.user.id, prodNum: auth.user.id, role: auth.role || 'client', name: auth.user.email || 'Account', email: auth.user.email || '' }
      : null
  ), [auth.user, auth.role]);

  // The Setup Tour's "Import your first book" step opens the WowOnboarding
  // statement-upload flow via this event (WowOnboarding no longer
  // auto-mounts — it's reachable ONLY from the tour now). Placed after the
  // currentUser declaration to respect the no-use-before-define rule.
  useEffect(() => {
    const openUpload = () => setShowWow(true);
    window.addEventListener('setup-tour:open-upload', openUpload);
    return () => window.removeEventListener('setup-tour:open-upload', openUpload);
  }, []);

  // Live "needs attention" bell count. Polls the unified attention rollup so the
  // top-bar bell shows a real number (distinct areas needing action — coverage
  // at risk, unpaid commissions, renewals, missed calls, …). Fault-tolerant:
  // any failure just leaves the last count. Refreshes every 5 min + on focus.
  useEffect(() => {
    if (!currentUser?.id) { setAttentionCount(0); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const headers = await bearer();
        if (!headers.Authorization) return;
        const res = await fetch('/api/command-center/attention', { headers });
        if (!res.ok) return;
        const j = await res.json();
        if (!cancelled && j?.ok && Array.isArray(j.signals)) setAttentionCount(j.signals.length);
      } catch { /* leave last count on error — never throw into render */ }
    };
    load();
    const id = setInterval(load, 300000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [currentUser?.id]);

  // Attach user identity to Sentry so every subsequent capture is
  // scoped to this operator (no more "some anonymous user hit a bug").
  // Clears on signout — setSentryUser(null-shaped payload) resets it.
  useEffect(() => {
    setSentryUser(currentUser
      ? { id: currentUser.id, email: currentUser.email }
      : {}
    );
  }, [currentUser?.id, currentUser?.email]);

  // Demo Mode flag — looks up subscriptions.demo_mode for this user.
  // When true, DemoDataProvider + DemoModeShell wrap the rendered
  // tree below so the operator sees a fully-loaded demo agency
  // instead of an empty CRM. The §2 boundary seal in api/_demoSeal.js
  // prevents any external side effect from a demo session regardless
  // of this client-side flag — so this wrapping is safe even before
  // the seal-test is verified live.
  const demoFlag = useDemoModeFlag(currentUser?.id);
  // HQ realtime — cross-user sync via Supabase channels. Re-emits our
  // typed hq events so every subscriber (Home Attention Queue, room
  // previews, ReportsRoom) refreshes when another agent mutates data.
  useHqRealtime(currentUser);
  // Additive path-based routing — clean URLs (/command-center/leads,
  // /command-center/clients, etc.) while keeping the existing
  // #tab=<id> hash bookmarks working. See hq/useHqPathRouting.js.
  useHqPathRouting({ tab, setTab });

  // Global sidebar keyboard shortcuts — Alt+1..6 jumps to the Daily
  // Work items in the reordered LeftSidebar (Phone / Communications /
  // Sales Floor / Calendar / Tasks / Clients). Alt so we don't
  // collide with the PowerDialer's local 1..7 outcome keys or text
  // inputs. Ignored when the target is an input/textarea/contentEditable.
  React.useEffect(() => {
    const HQ_HOTKEYS = {
      '1': 'phone',
      '2': 'messages_intelligence',
      '3': 'leads_intelligence',
      '4': 'calendar',
      '5': 'tasks',
      '6': 'client_intelligence',
      '0': 'command_center',
    };
    const onKey = (e) => {
      if (!e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const dest = HQ_HOTKEYS[e.key];
      if (!dest) return;
      e.preventDefault();
      setTab(dest);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isDemoMode = demoFlag.isDemo;

  // Demo Mode lands the user on Home regardless of which tab they
  // were on when they launched. Solves two problems at once: (a)
  // an admin who launches from Settings → Account immediately sees
  // the demo home, and (b) any restricted-in-demo tab they were
  // viewing (CUSTOMERS) auto-routes to a safe tab.
  React.useEffect(() => {
    if (isDemoMode && (tab === 'customers' || tab === 'settings')) setTab('home');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemoMode]);

  // Demo Mode data overrides — when isDemoMode, every view that
  // accepts leads / calls / calendar as props gets the seed-adapted
  // shape instead of the operator's empty Supabase tables. These
  // useMemos MUST live before the early-return branches below
  // (SetNewPassword / Subscribe / Loading) — moving them after
  // those returns would skip the hooks on those paths and trip
  // React #310 (rendered fewer hooks than previous render) on the
  // next normal render.
  const effectiveLeads = React.useMemo(
    () => isDemoMode ? buildLiveLeads() : leads,
    [isDemoMode, leads],
  );
  const effectiveCalls = React.useMemo(
    () => isDemoMode ? buildLiveCalls() : calls,
    [isDemoMode, calls],
  );
  const effectiveCalendar = React.useMemo(
    () => isDemoMode ? buildLiveCalendar() : calendar,
    [isDemoMode, calendar],
  );
  // Life Command Center reads `transactions` directly (not through an
  // `effective*` prop until now) — same override pattern as the three
  // above, feeding LifeCommandCenter's KPIs/panels a real-looking sample
  // book instead of the operator's empty transactions table. Real mode
  // (isDemoMode false) is untouched — `transactions` passes straight
  // through.
  const effectiveTransactions = React.useMemo(
    () => isDemoMode ? demoTransactions() : transactions,
    [isDemoMode, transactions],
  );
  // Book of Business (ClientsView -> BookOfBusinessBoard) and Messaging
  // Command Center (CockpitInbox) both run their OWN Supabase/API
  // fetches instead of accepting props — so instead of an `effective*`
  // override, isDemoMode-gated data is handed down as an explicit
  // `demoClients` / `demoThreads` prop each view checks BEFORE its own
  // fetch effect fires. `null` in real mode keeps that code path
  // byte-for-byte the same as before this change.
  const demoClientsForBook = React.useMemo(
    () => isDemoMode ? demoClients() : null,
    [isDemoMode],
  );
  const demoThreadsForInbox = React.useMemo(
    () => isDemoMode ? demoLifeThreads() : null,
    [isDemoMode],
  );

  // NUDGE follow-up — the operator's "people who need you today" count.
  // Fetched on mount from /api/nova/briefing (which short-circuits to
  // demo data in demo mode), then updated live by the 'nudge:counts'
  // window event NudgePanel emits whenever it reloads. Used by both
  // the top-bar NUDGE tab pip and the DashboardHome banner so the
  // burden is visible the moment the operator opens the CRM.
  const [nudgeCount, setNudgeCount] = React.useState({ need: 0, hot: 0, due: 0, slip: 0, track: 0, done: 0 });
  // Inbox total unread count — published by SmsInboxView via the
  // 'inbox:unread' window event. Surfaced as a red pip on the
  // "Inbox" nav button so a high-volume operator (500 leads/day)
  // can't miss a hot reply when they're on another tab.
  const [inboxUnread, setInboxUnread] = React.useState(0);
  React.useEffect(() => {
    function onCount(e) {
      const n = Number(e?.detail?.total || 0);
      if (Number.isFinite(n)) setInboxUnread(Math.max(0, Math.floor(n)));
    }
    window.addEventListener('inbox:unread', onCount);
    return () => window.removeEventListener('inbox:unread', onCount);
  }, []);
  React.useEffect(() => {
    let alive = true;
    function pull() {
      if (!currentUser?.id) return;
      (async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          const jwt = session?.access_token;
          if (!jwt) return;
          const res = await fetch('/api/nova/briefing', { headers: { authorization: `Bearer ${jwt}` } });
          if (!res.ok) return;
          const j = await res.json();
          if (alive && j?.counts) setNudgeCount(j.counts);
        } catch { /* fail safe — banner just stays at 0 */ }
      })();
    }
    function onLocal(e) { if (e?.detail) setNudgeCount(e.detail); }
    pull();
    window.addEventListener('nudge:counts', onLocal);
    return () => { alive = false; window.removeEventListener('nudge:counts', onLocal); };
  }, [currentUser?.id, isDemoMode]);
  // Effective user id used by the load effect + the loaded-for guard.
  // viewingAs is declared earlier as a useState; this is computed AFTER
  // currentUser so referencing currentUser?.prodNum doesn't hit the
  // temporal dead zone in the minified production bundle (a TDZ throw
  // here surfaces as the cryptic "Cannot access 'rr' before
  // initialization" render crash).
  const effectiveUserId = viewingAs?.userId || currentUser?.prodNum || null;

  // VA tracker session heartbeat — mounted at the CRM root so total
  // active-hours covers the whole CRM session. Posts 'start' on
  // mount, 'beat' every 60s while visible, 'end' on tab-close.
  // No-op until the user is actually signed in.
  useSessionHeartbeat();

  // Positive Response interrupt — when a lead texts "yes / call me /
  // how much", the cron writes an audit_events row and this hook
  // fires a browser notification + in-app event. Feature #5 from the
  // CRM feature spec.
  usePositiveResponseAlerts({ userId: currentUser?.id });

  const [authScreen, setAuthScreen] = useState('login'); // 'login' | 'signup' | 'reset'
  const [credentialsBump, setCredentialsBump] = useState(0);

  // Load this user's primary org once auth is hydrated. Owner role wins
  // when they're in multiple orgs so the header always shows "their"
  // workspace. If the migration hasn't been run yet, listMyMemberships
  // returns ok:false with the SQL error in `error` — we surface that as
  // orgError instead of leaving the chip permanently spinning.
  useEffect(() => {
    let cancelled = false;
    if (!auth.user?.id) { setCurrentOrg(null); return; }
    (async () => {
      const r = await listMyMemberships(auth.user.id);
      if (cancelled) return;
      if (!r.ok) {
        setOrgError(r.error || 'Could not load org. Run supabase_migrations/002_organizations.sql in your Supabase SQL editor.');
        setCurrentOrg(null);
        return;
      }
      setOrgError('');
      const primary = r.memberships.find(m => m.role === 'owner') || r.memberships[0] || null;
      setCurrentOrg(primary ? { ...primary.org, role: primary.role } : null);
    })();
    return () => { cancelled = true; };
  }, [auth.user?.id]);

  // Plan-tier guard: if the user's plan changes (downgrade, or first
  // load after the subscription hydrates) and the tab they were on is
  // no longer visible to their plan, bounce them to Briefing. Without
  // this, a saved-tab restore could land them on a Workflows tab they
  // can't see in the nav strip.
  //
  // Exception: the admin-only Customers tab isn't plan-gated; admins
  // keep access to it on any plan, so we skip the bounce for them.
  // Plan-tier guard is a no-op while in-app billing is deferred to the
  // external CRM. When billing returns, restore the hasFeature/tabsForPlan
  // imports and re-enable the bounce-to-briefing on hidden tabs.
  useEffect(() => {}, []);

  const signOut = async () => {
    // Detach the storage namespace FIRST. The state resets below trigger
    // the persist useEffects (gated only on !loading, not on auth state),
    // which call storageApi.set → cloudSet → upsert empty values into
    // public.user_state under the CURRENT user_id. Without this guard,
    // every logout silently wiped the user's cloud-backed data by writing
    // [] / {} over the real arrays for transactions, leads, calendar,
    // settings, etc. — and the next login loaded those empty values into
    // the dashboard ("data vanished after logout/login"). storageApi.set
    // explicitly bails with `if (!userId) return`, so setting null here
    // makes the persist calls drop without touching cloud.
    setStorageUser(null);
    // Drop the cached cloud-backup snapshot so the next user that signs in
    // can't briefly see this user's keys before their own snapshot loads.
    clearSnapshot();
    // Drop any active view-as session so a same-browser re-sign-in
    // doesn't accidentally inherit it (the customer's data would
    // continue to render under the new user's session otherwise).
    setViewingAs(null);
    // Zero out every piece of data state immediately so a same-browser
    // signup-after-signout can't paint with the prior user's React state
    // even for a single frame. The load effect would reset these anyway
    // when a new user signs in, but this closes the cross-tenant window
    // that opens between sign-out and the new sign-in completing.
    setTransactions([]);
    setProducers({});
    setSettings(DEFAULT_SETTINGS);
    setClientNotes({});
    setPolicyTracking({});
    setPresentationConfig(null);
    setClientProfiles({});
    setLobConfig({});
    setPipeline([]);
    setCalendar([]);
    setVaTimeLogs([]);
    setVaTasks([]);
    setVaTaskDone({});
    setCommissionRules([]);
    setCarrierCatalog([]);
    setCalls([]);
    setLeads([]);
    setOutreach([]);
    setLeadTemplates([]);
    setUploads([]);
    setWorkflows([]);
    setConservation([]);
    setCustomTeams([]);
    setLoadedForUserId(null);
    await auth.signOut();
  };
  const resetAgentPassword = (prodNum, newPassword) => {
    if (!AGENT_CREDENTIALS[prodNum]) return;
    AGENT_CREDENTIALS[prodNum] = { ...AGENT_CREDENTIALS[prodNum], password: newPassword };
    setCredentialsBump(n => n + 1);
  };

  // VA management — agents create/manage their own VAs. Admin can see them via the credentials panel.
  const isUsernameTaken = (username) => {
    const u = (username || '').trim();
    if (!u) return true;
    if (AGENT_CREDENTIALS[u]) return true;
    if (vaCredentials[u]) return true;
    return false;
  };
  const createVa = ({ username, password, name, servesAgentProdNum }) => {
    const u = (username || '').trim();
    if (!u || !password || !name || !servesAgentProdNum) return { ok: false, error: 'All fields are required.' };
    if (isUsernameTaken(u)) return { ok: false, error: `Username "${u}" is already taken.` };
    setVaCredentials(prev => ({
      ...prev,
      [u]: { password, role: 'va', name: name.trim(), servesAgentProdNum, createdAt: new Date().toISOString() },
    }));
    return { ok: true };
  };
  const resetVaPassword = (username, newPassword) => {
    if (!vaCredentials[username]) return;
    setVaCredentials(prev => ({ ...prev, [username]: { ...prev[username], password: newPassword } }));
  };
  const deleteVa = (username) => {
    setVaCredentials(prev => {
      const next = { ...prev };
      delete next[username];
      return next;
    });
  };

  // Time-tracking handlers for VAs
  const clockInVa = (vaUsername) => {
    // Only allow one active session per VA. If they're already clocked in, no-op.
    const hasActive = vaTimeLogs.some(l => l.vaUsername === vaUsername && !l.clockOut);
    if (hasActive) return;
    const id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `t-${Date.now()}`;
    setVaTimeLogs(prev => [...prev, { id, vaUsername, clockIn: new Date().toISOString(), clockOut: null }]);
  };
  const clockOutVa = (vaUsername) => {
    setVaTimeLogs(prev => prev.map(l =>
      l.vaUsername === vaUsername && !l.clockOut
        ? { ...l, clockOut: new Date().toISOString() }
        : l
    ));
  };
  const editTimeLog = (id, patch) => {
    setVaTimeLogs(prev => prev.map(l => l.id === id ? { ...l, ...patch } : l));
  };
  const deleteTimeLog = (id) => {
    setVaTimeLogs(prev => prev.filter(l => l.id !== id));
  };

  // Stable identity so child components can safely include showToast in
  // useCallback / useEffect deps without re-firing on every parent
  // re-render. Previously this was a plain inline function, recreated on
  // every render — any consumer that listed it as a dep would re-run
  // its callback on every render, which created a tight retry loop in
  // the AI agent panels when the backend was 500-ing (each failure
  // triggered a setState that re-rendered the parent that recreated
  // showToast that re-fired the useEffect that called the failing
  // endpoint again — 500+ requests in seconds).
  const showToast = React.useCallback((msg, type='success', durationMs) => {
    setToast({ msg, type });
    const ms = durationMs ?? Math.min(15000, Math.max(3500, msg.length * 60));
    setTimeout(() => setToast(null), ms);
  }, []);

  // Idle-state feedback for PhoneCommandRoom's floating call controls.
  // These crm-call-* events are handled by <CallWidget> while a call is
  // live; when there's no active call, CallWidget isn't mounted, so this
  // always-on listener turns the otherwise-dead clicks into an honest
  // "start a call first" toast. (Placed after showToast so it isn't
  // referenced before initialization.)
  React.useEffect(() => {
    const CTL = {
      'crm-call-mute-toggle': 'Mute',
      'crm-call-end-request': 'End call',
      'crm-call-hold-toggle': 'Hold',
      'crm-call-transfer-request': 'Transfer',
      'crm-voicemail-drop-request': 'Voicemail drop',
    };
    const onCtl = (e) => {
      if (activeCallLeadRef.current) return; // live call → CallWidget owns it
      showToast(`${CTL[e.type] || 'That control'} needs a live call — start one from the dialer first.`, 'info');
    };
    Object.keys(CTL).forEach((n) => window.addEventListener(n, onCtl));
    return () => Object.keys(CTL).forEach((n) => window.removeEventListener(n, onCtl));
  }, [showToast]);

  // LifeCommandCenter's client-name click targets (recent activity /
  // review alerts) only know a name string — apps/transactions there
  // aren't linked to a real clients.id. Resolve by name (RLS already
  // scopes this to the signed-in operator's own book) before opening
  // Life Protection Command; if there's no matching clients row yet,
  // say so rather than opening a broken view.
  const openLifeClientByName = React.useCallback(async (name) => {
    const cleaned = String(name || '').trim();
    if (!cleaned) return;
    try {
      const { data } = await supabase.from('clients').select('id, name').ilike('name', cleaned).limit(1);
      if (data && data[0]?.id) { setLifeClientId(data[0].id); return; }
    } catch { /* fall through to the not-found toast below */ }
    showToast(`No client record found for "${cleaned}" yet — add them in Clients to open Life Protection Command.`);
  }, [showToast]);

  // All app-data reads/writes flow through userStorage, which prefixes every
  // key with K_${currentUserId}_ so two Supabase users on the same browser
  // never see each other's data. The window.storage host fallback (used by
  // embedded environments) is preserved at the top of each branch.
  // If currentUserId is null (we got called before auth hydrated),
  // userStorage throws; the get path swallows it as "no value" and the set
  // path logs and drops the write rather than crashing the app.
  const storageApi = {
    async get(key) {
      if (window.storage?.get) {
        return window.storage.get(key);
      }
      const userId = getCurrentUserId();
      if (!userId) return null;
      // localStorage is the hot read path. If it's empty here but the
      // cloud snapshot has the key, restore: hydrate localStorage so the
      // next read is local, and return the cloud value to the caller.
      const local = userStorage.get(key);
      if (local !== null) return { value: local };
      if (snapshotIsReady(userId)) {
        const cloud = snapshotGet(userId, key);
        if (cloud !== undefined) {
          const serialized = typeof cloud === 'string' ? cloud : JSON.stringify(cloud);
          try { userStorage.set(key, serialized); } catch {}
          return { value: serialized };
        }
      }
      return null;
    },
    async set(key, value) {
      if (window.storage?.set) {
        return window.storage.set(key, value);
      }
      const userId = getCurrentUserId();
      if (!userId) {
        // Pre-auth write — almost certainly a load-time setX flush.
        // Drop it; the post-auth load will write fresh data anyway.
        return;
      }
      userStorage.set(key, value);
      // Mirror to Supabase. cloudSet stores parsed JSON in the jsonb
      // column; pass the parsed shape rather than the serialized string
      // so the row is queryable in SQL.
      try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        cloudSet(userId, key, parsed);
      } catch {
        // Value isn't JSON (shouldn't happen in this app, but be safe):
        // store as a string-typed jsonb value so the round-trip works.
        cloudSet(userId, key, value);
      }
    },
  };

  useEffect(() => {
    // Gated on the effective user id. Bail (and flip loading=false so the auth
    // screens can paint) until userStorage knows who it's reading for.
    if (!effectiveUserId) {
      setLoading(false);
      return;
    }
    // Sync the userStorage namespace to the effective user. AuthContext
    // also sets this to the AUTH user's id in its own effect; we override
    // here on every load so a view-as session targets the customer's
    // namespace (their localStorage keys won't exist on the operator's
    // browser, but the cloud-snapshot fallback at storageApi.get will
    // hydrate from public.user_state via the admin RLS policy).
    setStorageUser(effectiveUserId);
    setLoading(true);
    // Critical: mark the in-memory state as NOT-YET-loaded for this
    // user. The dashboard gate below treats loadedForUserId !==
    // currentUser.prodNum as "loading" so it never paints with the
    // previous tenant's data while we're hydrating the new tenant.
    setLoadedForUserId(null);
    // Reset every piece of data state to its initial value so we never leak
    // the previous user's data into the new user's view when the new user
    // has no value for a particular key. The persist effects below all gate
    // on `!loading`, so these resets don't write anything to localStorage.
    setTransactions([]);
    setProducers({});
    setSettings(DEFAULT_SETTINGS);
    setClientNotes({});
    setPolicyTracking({});
    setPresentationConfig(null);
    setClientProfiles({});
    setLobConfig({});
    setPipeline([]);
    setCalendar([]);
    setVaTimeLogs([]);
    setVaTasks([]);
    setVaTaskDone({});
    setCommissionRules([]);
    setCarrierCatalog([]);
    setCalls([]);
    setLeads([]);
    setOutreach([]);
    setLeadTemplates([]);
    setUploads([]);
    // workflows wasn't in the reset block before — added so it can't
    // leak across users on the same browser either.
    setWorkflows([]);
    setConservation([]);
    setCustomTeams([]);
    // Second-chance preflight — the JWT was fresh when AuthContext ran
    // but might have expired between then and now (long tab left open,
    // clock skew, etc). Cheap to re-run — pure localStorage read.
    const preflight = sessionPreflight();
    if (!preflight.ok) {
      // eslint-disable-next-line no-console
      console.info('[hydration] preflight caught stale JWT — reloading to login:', preflight.reason);
      window.location.reload();
      return;
    }
    let cancelled = false;
    // Belt watchdog: if the hydration cascade below hangs for any reason
    // (bad JWT triggering a refresh loop, individual safeGet stuck, etc)
    // force-exit the "Loading your data…" gate after 15s. The dashboard
    // paints with whatever state made it through — usually empty is
    // strictly better than a stuck spinner, since the operator can then
    // sign out + back in cleanly.
    const hydrationWatchdog = setTimeout(() => {
      if (cancelled) return;
      // eslint-disable-next-line no-console
      console.warn('[hydration] watchdog fired at 15s — purging bad session + reloading to login');
      // If we got here, the Supabase JWT is bad and refresh is looping
      // silently. Painting an empty dashboard would just move the hang
      // to the next Supabase call. Nuke the tokens directly and reload
      // — user lands on login, signs in fresh, everything works.
      try {
        for (let i = window.localStorage.length - 1; i >= 0; i--) {
          const k = window.localStorage.key(i);
          if (k && (k.startsWith('sb-') || k.includes('supabase.auth.'))) {
            window.localStorage.removeItem(k);
          }
        }
      } catch { /* ignore */ }
      // Full reload so all in-memory Supabase client state is gone too.
      window.location.reload();
    }, 15000);
    (async () => {
      // Pull the user's cloud-backup snapshot before any per-key safeGet.
      // storageApi.get falls through to the snapshot when localStorage is
      // empty for a key, so this single round trip recovers everything
      // from a wiped browser. Network failure is non-fatal — see
      // loadCloudSnapshot for the graceful path.
      await loadCloudSnapshot(effectiveUserId);
      if (cancelled) return;
      const safeGet = async (k) => {
        try { const r = await storageApi.get(k); return r ? JSON.parse(r.value) : null; }
        catch { return null; }
      };
      const t = await safeGet(K.transactions);
      if (cancelled) return;
      if (t && t.length) {
        // One-time migration: ensure every stored tx uses the correct net rule
        //   MOO: netToAccount = Comm Amt (ignore Adv Comm)
        //   Transamerica: netToAccount = Adv Comm (Comm Amt is always 0)
        //   AMERICO: netToAccount = Comm Amt (already set correctly by AMERICO parser)
        let migrated = 0;
        let relabeled = 0;
        const fixed = t.map(tx => {
          const isTrans = (tx.company || '').toUpperCase().includes('TRANSAMERICA')
                       || (tx.mga || '').toUpperCase().includes('TRANSAMERICA');
          const isAmericoCompany = (tx.company || '').toUpperCase() === 'AMERICO';
          // AMERICO rows: relabel activityType from stored Trx code if needed (per spec).
          if (isAmericoCompany) {
            let next = tx;
            if (tx.trxType && AMERICO_TRX_LABELS[tx.trxType] && tx.activityType !== AMERICO_TRX_LABELS[tx.trxType]) {
              const newLabel = AMERICO_TRX_LABELS[tx.trxType];
              // Re-classify isChargeback now that label may have changed
              const isCB = CHARGEBACK_TYPES.has(newLabel) || (tx.netToAccount || 0) < 0;
              next = { ...next, activityType: newLabel, isChargeback: isCB };
              relabeled++;
            }
            if (!next.weekKey) next = { ...next, weekKey: weekKey(next.activityDate) };
            return next;
          }
          const correctNet = isTrans ? (Number(tx.advComm) || 0) : (Number(tx.commAmt) || 0);
          if (Math.abs((tx.netToAccount || 0) - correctNet) > 0.001) {
            migrated++;
            return { ...tx, netToAccount: correctNet, weekKey: tx.weekKey || weekKey(tx.activityDate) };
          }
          if (!tx.weekKey) {
            return { ...tx, weekKey: weekKey(tx.activityDate) };
          }
          return tx;
        });
        if (migrated > 0) {
          setTimeout(() => showToast(`Corrected ${migrated} stored transactions to use Comm Amt for MOO`), 500);
        }
        if (relabeled > 0) {
          setTimeout(() => showToast(`Relabeled ${relabeled} Americo transactions with proper Trx-code activity types`), 900);
        }

        setTransactions(fixed);
      } else {
        // Empty namespace → truly empty dashboard (Phase A1.6). No seed data.
        setTransactions([]);
      }
      const p = await safeGet(K.producers);
      if (p && Object.keys(p).length) {
        // Backfill from KNOWN_PRODUCERS: any producer the user stored
        // as unassigned with no name, where KNOWN_PRODUCERS knows the
        // person, gets promoted to the registry's team + name. This
        // respects every explicit assignment (anything not on the
        // unassigned bucket is left alone) so adding a producer to
        // KNOWN_PRODUCERS retroactively fixes anyone who came in as
        // a default before the registry knew them.
        const migrated = {};
        let touched = 0;
        for (const [pn, rec] of Object.entries(p)) {
          const known = KNOWN_PRODUCERS[pn];
          if (known && (rec?.team === 'unassigned' || !rec?.team) && (!rec?.name || rec.name === '')) {
            migrated[pn] = { team: known.team, name: known.name };
            touched++;
          } else if (known && (rec?.team === 'unassigned' || !rec?.team)) {
            // Has a name already but unassigned — promote team only.
            migrated[pn] = { ...rec, team: known.team };
            touched++;
          } else {
            migrated[pn] = rec;
          }
        }
        setProducers(migrated);
        if (touched > 0) {
          setTimeout(() => showToast(`Auto-assigned ${touched} producer${touched === 1 ? '' : 's'} from the known-producers registry`), 800);
        }
      } else {
        // Empty namespace → truly empty producers map. No KNOWN_PRODUCERS seed.
        setProducers({});
      }
      const s = await safeGet(K.settings); if (s) setSettings({ ...DEFAULT_SETTINGS, ...s, camName: '' });
      // payouts load removed alongside recordPayout + PayoutsView.
      const cn = await safeGet(K.clientNotes); if (cn) setClientNotes(cn);
      const pt = await safeGet(K.policyTracking); if (pt) setPolicyTracking(pt);
      const pc = await safeGet(K.presentationConfig); if (pc) setPresentationConfig(pc);
      const cp = await safeGet(K.clientProfiles); if (cp) setClientProfiles(cp);
      const lc = await safeGet(K.lobConfig);
      if (lc) {
        // Strip any legacy apiKey field — Lob calls go through the server proxy now.
        if ('apiKey' in lc) delete lc.apiKey;
        setLobConfig(lc);
      }
      const pl = await safeGet(K.pipeline); if (pl) setPipeline(pl);
      const ca = await safeGet(K.calendar); if (ca) setCalendar(ca);
      // vaCredentials are no longer hydrated from storage; the cleanup effect
      // below removes the legacy key. VA auth returns in Week 3 via Supabase.
      const vt = await safeGet(K.vaTimeLogs); if (vt) setVaTimeLogs(vt);
      const vts = await safeGet(K.vaTasks);    if (vts) setVaTasks(vts);
      const vtd = await safeGet(K.vaTaskDone); if (vtd) setVaTaskDone(vtd);
      // Auto-seed the default commission rule the first time a workspace
      // loads — guarantees "100% to producer of record" is the active rule.
      const cr = await safeGet(K.commissionRules);
      if (cr && Array.isArray(cr) && cr.length > 0) setCommissionRules(cr);
      else setCommissionRules([buildDefaultCommissionRule()]);
      // Auto-seed the carrier catalog with the existing hardcoded rules.
      const cc = await safeGet(K.carrierCatalog);
      if (cc && Array.isArray(cc) && cc.length > 0) setCarrierCatalog(cc);
      else setCarrierCatalog(buildDefaultCarrierCatalog());
      const cl = await safeGet(K.calls); if (cl) setCalls(cl);
      // One-time migration: leads_v1 (old key) → cfg_leads_v1 (new key)
      // Also remap legacy statuses: closed_won → closed, closed_lost → recycled, presented → showed.
      const ld = await safeGet(K.leads);
      const ldLegacy = ld ? null : await safeGet(K.leadsLegacy);
      const sourceLeads = ld || ldLegacy;
      if (sourceLeads) {
        // Derive age from a DOB string (ISO YYYY-MM-DD). Returns '' if
        // the DOB is missing or malformed. Used to backfill the age
        // column on every legacy lead that has DOB but never had age.
        const deriveAge = (dobStr) => {
          if (!dobStr) return '';
          const d = new Date(dobStr);
          if (isNaN(d.getTime())) return '';
          const now = new Date();
          let years = now.getFullYear() - d.getFullYear();
          const m = now.getMonth() - d.getMonth();
          if (m < 0 || (m === 0 && now.getDate() < d.getDate())) years--;
          return years >= 0 && years < 130 ? String(years) : '';
        };
        const migrated = sourceLeads.map(l => {
          let status = l.status;
          if (status === 'closed_won') status = 'closed';
          else if (status === 'closed_lost') status = 'recycled';
          else if (status === 'presented') status = 'showed';
          // Backfill new optional fields so downstream code can rely on them.
          // Normalize DOB to ISO YYYY-MM-DD so sort + age-derivation
          // behave the same regardless of the lead's import source.
          const dob = normalizeDob(l.dob || '');
          const age = l.age || (dob ? deriveAge(dob) : '');
          return {
            ...l,
            status,
            statusChangedAt: l.statusChangedAt || l.createdAt || new Date().toISOString(),
            followUpDate: l.followUpDate || '',
            leadSource: l.leadSource || '',
            tags: Array.isArray(l.tags) ? l.tags : [],
            // Normalize phone display to "(NNN) NNN-NNNN" so legacy
            // raw-digit leads pick up the formatted look on next load.
            phone: fmtPhoneUS(l.phone || ''),
            // Demographics backfill — every lead (life + health) gets
            // these fields normalized so the lead table always renders
            // a value (even if it's empty string).
            dob,
            age,
            state:   l.state   || '',
            // Address — keep STREET in `address`; city/zip live in
            // their own fields. Lead table composes them at render
            // time. Legacy leads that came in with concat'd fields
            // (rare) keep working because we read `l.address1`
            // as a fallback street.
            address: l.address || l.address1 || '',
            city:    l.city || '',
            zip:     l.zip  || '',
            // Phase 2.1 backfill — speed-to-lead + TCPA consent fields.
            // All optional and nullable so older leads behave as "unknown" / "no consent on record".
            firstContactedAt: l.firstContactedAt || '',
            consentText:     l.consentText     || '',
            consentAt:       l.consentAt       || '',
            consentSource:   l.consentSource   || '',
            optOut:          !!l.optOut,
            optOutAt:        l.optOutAt        || '',
            optOutReason:    l.optOutReason    || '',
          };
        });
        setLeads(migrated);
      }
      const or = await safeGet(K.outreach); if (or) setOutreach(or);
      const tpl = await safeGet(K.leadTemplates);
      if (tpl && Array.isArray(tpl) && tpl.length > 0) setLeadTemplates(tpl);
      else setLeadTemplates(DEFAULT_LEAD_TEMPLATES);
      const gc = await safeGet(K.goalConfig);
      if (gc && typeof gc === 'object') setGoalConfig(gc);
      else setGoalConfig({}); // empty object → component uses DEFAULT_GOAL_CONFIG
      const fo = await safeGet(K.fexOnboarding);
      if (fo && typeof fo === 'object') setFexOnboarding(fo);
      const oc = await safeGet(K.onboardingChat);
      if (oc && Array.isArray(oc)) setOnboardingChat(oc);
      const od = await safeGet(K.onboardingDismissed);
      if (od === true) setOnboardingDismissed(true);
      const st = await safeGet(K.saveTasks);
      if (st && typeof st === 'object') setSaveTasks(st);
      const irs = await safeGet(K.inboxReadState);
      if (irs && typeof irs === 'object') setInboxReadState(irs);
      const ackW = await safeGet(K.acknowledgedWelcomes);
      if (ackW && Array.isArray(ackW)) setAcknowledgedWelcomes(ackW);
      const fc = await safeGet(K.financialConfig);
      if (fc && typeof fc === 'object') setFinancialConfig(fc);
      else setFinancialConfig({}); // component uses DEFAULT_FINANCIAL_CONFIG
      const ap = await safeGet(K.apps);
      if (ap && Array.isArray(ap)) setApps(ap);
      const ac = await safeGet(K.accountantChat);
      if (ac && Array.isArray(ac)) setAccountantChat(ac);
      const wf = await safeGet(K.workflows);
      if (wf && Array.isArray(wf) && wf.length > 0) setWorkflows(wf);
      else setWorkflows([buildDefaultMortgageProtectionWorkflow()]);
      const cnv = await safeGet(K.conservation);
      if (cnv && Array.isArray(cnv)) setConservation(cnv);
      const ct = await safeGet(K.customTeams);
      if (ct && Array.isArray(ct)) setCustomTeams(ct);
      const up = await safeGet(K.uploads);
      if (up && up.length) {
        setUploads(up);
      } else {
        // Empty namespace → truly empty uploads log. No SEED_UPLOADS injection.
        setUploads([]);
      }
      if (!cancelled) {
        // Stamp BEFORE flipping loading=false so the render that
        // re-mounts the dashboard sees both flags consistent.
        setLoadedForUserId(effectiveUserId);
        setLoading(false);
        clearTimeout(hydrationWatchdog);
      }
    })();
    return () => { cancelled = true; clearTimeout(hydrationWatchdog); };
    // effectiveUserId is the only meaningful dep — it switches when
    // the auth user changes OR when the operator starts/stops a
    // view-as session.
    // eslint-disable-next-line
  }, [effectiveUserId]);

  const persist = async (key, value) => {
    // Read-only guard: during a view-as session the operator is
    // browsing a customer's data via the admin RLS bypass. Persisting
    // would write the operator's mutations into the customer's
    // localStorage namespace AND into their public.user_state row.
    // Cheap to enforce in one place vs gating 20 individual effects.
    if (viewingAs) return;
    try { await storageApi.set(key, JSON.stringify(value)); } catch(e) { console.error(e); }
  };
  useEffect(() => { if (!loading) persist(K.transactions, transactions); }, [transactions, loading]);

  // Auto cross-reference scan after a statement upload. When new
  // transactions land (transactions array grows after initial load),
  // POST the delta to /api/cross-ref/upsert-book. The server rolls
  // them up into book_clients and runs the three-tier matcher; results
  // surface in the Match Review tab (tab='match_review'). Fire-and-
  // forget — silent on success, console-warn on failure. Operators
  // who want to scan the FULL transactions array can still hit the
  // "Scan now" button in MatchReviewView; this just removes the need
  // to remember after every upload.
  const prevTxLenRef = React.useRef(0);
  useEffect(() => {
    if (loading) return;
    const prevLen = prevTxLenRef.current;
    const curLen = (transactions || []).length;
    prevTxLenRef.current = curLen;
    // Only fire when the array GREW (so initial hydration, replaces,
    // and shrinks don't trigger). prevLen > 0 guards against the
    // first-time-after-load case where prev was 0 because we hadn't
    // tracked yet.
    if (curLen <= prevLen || prevLen === 0) return;
    const delta = transactions.slice(prevLen);
    if (delta.length === 0) return;
    (async () => {
      try {
        await apiFetch('/api/cross-ref/upsert-book', {
          method: 'POST',
          body:   JSON.stringify({ transactions: delta }),
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[cross-ref auto-scan] skipped:', err.message || err);
      }
    })();
  }, [transactions, loading]);
  // Email-leak scrubber. Browser autofill drops the signed-in user's
  // email into random text inputs (DOB, "Age at issue", SSN, etc.)
  // because Chrome / Safari greedily fill any input that gets focus
  // during a session. The Field component now blocks NEW leaks via
  // autoComplete=off + data-1p-ignore, but any prior leak persists
  // in user_state — including leaks from PRIOR sessions under a
  // DIFFERENT operator email, which the old exact-match scrubber
  // didn't catch.
  //
  // This scrubber runs in two layers:
  //   1. For "never-email" fields (DOB, age, SSN, address, etc.),
  //      clear ANY value that looks like an email — operator's or
  //      not, current or stale.
  //   2. For everything else, fall back to the exact-match check
  //      against the current operator's email as a belt-and-braces
  //      catch (avoids nuking legitimate text that happens to
  //      contain @, like notes that say "email me at...").
  //
  // The `email` field itself is exempted from layer 1 (the value is
  // supposed to look like an email there).
  const SCRUBBABLE_NON_EMAIL_FIELDS = new Set([
    'ageAtIssue', 'age', 'dob', 'gender', 'ssn',
    'address1', 'address2', 'city', 'state', 'zip',
    'phone', 'firstName', 'lastName',
    'draftDay', 'policyNumber', 'coverageType',
    'premium', 'estPremium',
    'height', 'weight',
  ]);
  const looksLikeEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());

  useEffect(() => {
    if (loading) return;
    const myEmail = String(auth?.user?.email || '').toLowerCase().trim();
    const scrubObj = (obj) => {
      let touched = false;
      const out = {};
      for (const [k, v] of Object.entries(obj || {})) {
        if (typeof v !== 'string') { out[k] = v; continue; }
        // Layer 1: never-email fields with an email-shaped value.
        if (SCRUBBABLE_NON_EMAIL_FIELDS.has(k) && looksLikeEmail(v)) {
          out[k] = '';
          touched = true;
          continue;
        }
        // Layer 2: exact match to current operator's email (catches
        // unusual fields not in the SCRUBBABLE list).
        if (myEmail && v.toLowerCase().trim() === myEmail && k !== 'email') {
          out[k] = '';
          touched = true;
          continue;
        }
        out[k] = v;
      }
      return { out, touched };
    };

    setClientProfiles(prev => {
      let changed = false;
      const next = {};
      for (const [key, profile] of Object.entries(prev || {})) {
        const { out, touched } = scrubObj(profile);
        next[key] = out;
        if (touched) changed = true;
      }
      return changed ? next : prev;
    });
    setLeads(prev => {
      let changed = false;
      const next = prev.map(lead => {
        const { out, touched } = scrubObj(lead);
        if (touched) changed = true;
        return out;
      });
      return changed ? next : prev;
    });
    setCalls(prev => {
      let changed = false;
      const next = prev.map(c => {
        const { out, touched } = scrubObj(c);
        if (touched) changed = true;
        return out;
      });
      return changed ? next : prev;
    });
    setConservation(prev => {
      let changed = false;
      const next = prev.map(ev => {
        const { out, touched } = scrubObj(ev);
        if (touched) changed = true;
        return out;
      });
      return changed ? next : prev;
    });
  }, [loading, auth?.user?.email]);

  // One-time backfill: legacy MOO transactions stored with company =
  // "UNITED" (the underwriting entity) get rewritten to "Mutual of
  // Omaha" so every view shows the carrier customers actually know.
  // New uploads write this normalized form directly; this effect just
  // covers tenants whose data predates the fix. Idempotent — guards
  // against the re-render loop by checking if a rewrite is needed
  // before calling setTransactions.
  useEffect(() => {
    if (loading) return;
    if (!transactions.length) return;
    const hasLegacy = transactions.some(tx => tx.company === 'UNITED');
    if (!hasLegacy) return;
    setTransactions(prev => prev.map(tx => tx.company === 'UNITED' ? { ...tx, company: 'Mutual of Omaha' } : tx));
  }, [transactions, loading]);

  // Auto-backfill producer names from transactions. Carrier statements
  // (Americo, Transamerica, MoO via the LLM/det parsers) carry
  // agentName on each transaction. When those land for a producer who
  // doesn't have a name set yet (just a prodNum), copy the agentName
  // into the producer record so every view that calls
  // getProducerName(prodNum) shows "Mark Omobogie" instead of
  // "Producer AM-FFHEQZ". Idempotent (the `changed` guard prevents
  // an infinite loop when producers update).
  //
  // Overwrites:
  //   · empty / missing name
  //   · placeholders like "Producer 1247602" or "Pending Map · ..."
  //     (in case those got written by an earlier import / commit
  //     endpoint that didn't have the real name yet — reported
  //     2026-06-28 from the Recruiting ROI / Override P&L tables)
  // Preserves any non-placeholder name the operator has manually set.
  useEffect(() => {
    if (loading) return;
    if (!transactions.length) return;
    const isPlaceholder = (n) => !n || /^Producer\s+\S+\s*$|^Pending Map\b/i.test(String(n).trim());
    const suggest = {};
    for (const tx of transactions) {
      if (tx.prodNum && tx.agentName && !suggest[tx.prodNum]) {
        const nm = String(tx.agentName).trim();
        if (nm && !isPlaceholder(nm)) suggest[tx.prodNum] = nm;
      }
    }
    if (Object.keys(suggest).length === 0) return;
    setProducers(prev => {
      let changed = false;
      const next = { ...prev };
      for (const [pn, nm] of Object.entries(suggest)) {
        const cur = next[pn] || {};
        if (isPlaceholder(cur.name)) {
          next[pn] = { ...cur, name: nm };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [transactions, loading]);
  useEffect(() => { if (!loading) persist(K.producers, producers); }, [producers, loading]);
  useEffect(() => { if (!loading) persist(K.settings, settings); }, [settings, loading]);
  // payouts persist removed alongside recordPayout + PayoutsView.
  useEffect(() => { if (!loading) persist(K.clientNotes, clientNotes); }, [clientNotes, loading]);
  useEffect(() => { if (!loading) persist(K.policyTracking, policyTracking); }, [policyTracking, loading]);
  useEffect(() => { if (!loading) persist(K.presentationConfig, presentationConfig); }, [presentationConfig, loading]);
  useEffect(() => { if (!loading) persist(K.clientProfiles, clientProfiles); }, [clientProfiles, loading]);
  useEffect(() => { if (!loading) persist(K.lobConfig, lobConfig); }, [lobConfig, loading]);
  useEffect(() => { if (!loading) persist(K.pipeline, pipeline); }, [pipeline, loading]);
  useEffect(() => { if (!loading) persist(K.calendar, calendar); }, [calendar, loading]);
  useEffect(() => { if (!loading) persist(K.vaTimeLogs, vaTimeLogs); }, [vaTimeLogs, loading]);
  useEffect(() => { if (!loading) persist(K.vaTasks, vaTasks); }, [vaTasks, loading]);
  useEffect(() => { if (!loading) persist(K.vaTaskDone, vaTaskDone); }, [vaTaskDone, loading]);
  useEffect(() => { if (!loading) persist(K.commissionRules, commissionRules); }, [commissionRules, loading]);
  useEffect(() => { if (!loading) persist(K.carrierCatalog, carrierCatalog); }, [carrierCatalog, loading]);
  useEffect(() => { if (!loading) persist(K.calls, calls); }, [calls, loading]);
  useEffect(() => { if (!loading) persist(K.leads, leads); }, [leads, loading]);
  useEffect(() => { if (!loading) persist(K.outreach, outreach); }, [outreach, loading]);
  useEffect(() => { if (!loading) persist(K.leadTemplates, leadTemplates); }, [leadTemplates, loading]);
  useEffect(() => { if (!loading && goalConfig != null) persist(K.goalConfig, goalConfig); }, [goalConfig, loading]);
  useEffect(() => { if (!loading) persist(K.fexOnboarding, fexOnboarding); }, [fexOnboarding, loading]);
  useEffect(() => { if (!loading) persist(K.onboardingChat, onboardingChat); }, [onboardingChat, loading]);
  useEffect(() => { if (!loading) persist(K.onboardingDismissed, onboardingDismissed); }, [onboardingDismissed, loading]);
  useEffect(() => { if (!loading) persist(K.saveTasks, saveTasks); }, [saveTasks, loading]);
  useEffect(() => { if (!loading) persist(K.inboxReadState, inboxReadState); }, [inboxReadState, loading]);
  useEffect(() => { if (!loading) persist(K.acknowledgedWelcomes, acknowledgedWelcomes); }, [acknowledgedWelcomes, loading]);
  useEffect(() => { if (!loading && financialConfig != null) persist(K.financialConfig, financialConfig); }, [financialConfig, loading]);
  useEffect(() => { if (!loading) persist(K.apps, apps); }, [apps, loading]);
  useEffect(() => { if (!loading) persist(K.accountantChat, accountantChat); }, [accountantChat, loading]);

  // Auto-enroll matched leads in conservation_event workflows when
  // new chargebacks land in transactions (from commission statements
  // or carrier conservation reports). Idempotent via the autoEnrolled
  // flag on each saveTask. Runs whenever transactions or conservation
  // changes — typically right after a fresh upload.
  useEffect(() => {
    if (loading) return;
    if (!currentUser?.id) return;
    let cancelled = false;
    (async () => {
      const updates = await autoEnrollNewChargebacks({
        transactions, conservation, leads, saveTasks, ownerId: currentUser.id,
      });
      if (cancelled || !updates || Object.keys(updates).length === 0) return;
      setSaveTasks(prev => {
        const next = { ...prev };
        for (const [k, v] of Object.entries(updates)) {
          next[k] = { ...(next[k] || { status: 'pending' }), ...v };
        }
        return next;
      });
    })();
    return () => { cancelled = true; };
  }, [transactions, conservation, leads, currentUser?.id, loading]);
  useEffect(() => { if (!loading) persist(K.workflows, workflows); }, [workflows, loading]);
  useEffect(() => { if (!loading) persist(K.conservation, conservation); }, [conservation, loading]);
  useEffect(() => { if (!loading) persist(K.customTeams, customTeams); }, [customTeams, loading]);
  useEffect(() => { if (!loading) persist(K.uploads, uploads); }, [uploads, loading]);

  // One-time cleanup: remove any legacy secrets that may still be sitting in
  // this browser's localStorage from a previous build, and clear the auth
  // session so the maintenance gate can't be bypassed by an old token.
  useEffect(() => {
    try {
      for (const k of PURGED_SECRET_KEYS) window.localStorage?.removeItem?.(k);
      window.sessionStorage?.removeItem?.(SESSION_KEY);
    } catch {}
  }, []);

  const importAmericoText = (text, sourceName) => {
    if (!text || !text.trim()) {
      showToast('Paste the Americo statement text first', 'error');
      return 0;
    }
    let parsed;
    try {
      parsed = parseAmericoPdf(text);
    } catch (err) {
      showToast(`Couldn't parse Americo text: ${err.message}`, 'error');
      return 0;
    }
    if (!parsed.transactions || parsed.transactions.length === 0) {
      showToast('No Americo transactions found in pasted text. Check the format.', 'error');
      return 0;
    }
    const newTx = [];
    const newProducers = { ...producers };
    const discoveredNames = {};
    Object.entries(parsed.producerNames || {}).forEach(([pn, name]) => {
      const tc = name.split(' ').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
      discoveredNames[pn] = tc;
    });
    parsed.transactions.forEach((t, idx) => {
      const prodNum = String(t.prodNum || '').trim() || 'unknown';
      const commAmt = Number(t.commAmt) || 0;
      const netToAccount = commAmt;
      const activityType = (t.activityType || '').trim();
      newTx.push({
        id: `${sourceName}|${prodNum}|${t.policy||''}|${t.activityDate||''}|${activityType}|${idx}`,
        company: t.company || 'AMERICO',
        mga: t.mga || 'AMERICO',
        prodNum,
        policy: t.policy,
        insured: t.insured,
        state: t.state || '',
        activityDate: t.activityDate,
        issueDate: t.issueDate || '',
        commPremium: t.commPremium || 0,
        activityType,
        commPct: t.commPct || 0,
        commAmt, advComm: Number(t.advComm) || 0, netToAccount,
        monthKey: monthKey(t.activityDate || parsed.periodEnding),
        weekKey: weekKey(t.activityDate || parsed.periodEnding),
        source: sourceName,
        isChargeback: CHARGEBACK_TYPES.has(activityType) || netToAccount < 0,
      });
      if (!newProducers[prodNum]) newProducers[prodNum] = { team: KNOWN_PRODUCERS[prodNum]?.team || 'unassigned', name: KNOWN_PRODUCERS[prodNum]?.name || '' };
    });
    Object.entries(discoveredNames).forEach(([pn, name]) => {
      if (!newProducers[pn]) newProducers[pn] = { team: 'unassigned', name };
      else if (!newProducers[pn].name) newProducers[pn].name = name;
    });
    setTransactions(prev => {
      const existingIds = new Set(prev.map(t => t.id));
      const toAdd = newTx.filter(t => !existingIds.has(t.id));
      return [...prev, ...toAdd];
    });
    // Merge by functional update so a stale closure can't wipe team
    // assignments the user has already made. Only ADD producers we don't
    // already have, and only fill in a missing name if the upload
    // discovered one — never overwrite an existing team.
    setProducers(prev => {
      const next = { ...prev };
      Object.entries(newProducers).forEach(([pn, info]) => {
        if (!next[pn]) next[pn] = info;
        else if (!next[pn].name && info.name) next[pn] = { ...next[pn], name: info.name };
      });
      return next;
    });
    setUploads(u => [{ name: sourceName, at: new Date().toISOString(), rows: newTx.length, type: 'paste' }, ...u].slice(0, 100));
    showToast(`Imported ${newTx.length} Americo rows from pasted statement`);
    // Money intelligence panel
    if (newTx.length > 0) {
      const insights = computeImportInsights(newTx, settings?.ownerProdNum, newProducers);
      setStatementInsights(insights);
    }
    return newTx.length;
  };

  // Pasted-text import for Transamerica weekly statements. Mirrors
  // importAmericoText: parse → de-dup against existing tx ids → upsert
  // producers from the writing-agent map → log under uploads. Reuses
  // parseTransamericaPdf so the same regex works whether the user uploads
  // a PDF or copies the rendered text into the box.
  const importTransamericaText = (text, sourceName) => {
    if (!text || !text.trim()) {
      showToast('Paste the Transamerica statement text first', 'error');
      return 0;
    }
    let parsed;
    try {
      parsed = parseTransamericaPdf(text);
    } catch (err) {
      showToast(`Couldn't parse Transamerica text: ${err.message}`, 'error');
      return 0;
    }
    if (!parsed.transactions || parsed.transactions.length === 0) {
      showToast('No Transamerica transactions found in pasted text. Make sure the header reads "Personal Advance Detail …" and each "Transaction Date :" section is included.', 'error');
      return 0;
    }
    const newTx = [];
    const newProducers = { ...producers };
    const discoveredNames = {};
    Object.entries(parsed.producerNames || {}).forEach(([pn, name]) => {
      // Transamerica writing-agent names arrive as "Last, First". Flip to
      // "First Last" and title-case so they match the convention used by
      // the rest of the producer-name pipeline (MOO + Americo flows do
      // the same casing pass).
      const [last, first] = String(name || '').split(',').map(s => s.trim());
      const flipped = (first || last)
        ? `${(first || '').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')} ${(last || '').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')}`.trim()
        : name;
      discoveredNames[pn] = flipped;
    });
    parsed.transactions.forEach((t, idx) => {
      const prodNum = String(t.prodNum || '').trim() || 'unknown';
      // The parser sets advComm to the signed Overwrite Over Advance column
      // when present, else the Advance Amount (positive new business) or 0
      // (chargeback row with no extractable amount). netToAccount equals
      // advComm for Transamerica — that's the $ that actually moves to the
      // agency owner's account this period.
      const advComm = Number(t.advComm) || 0;
      const netToAccount = advComm;
      const activityType = (t.activityType || '').trim();
      newTx.push({
        id: `${sourceName}|${prodNum}|${t.policy||''}|${t.activityDate||''}|${activityType}|${idx}`,
        company: t.company || 'TRANSAMERICA',
        mga: t.mga || 'TRANSAMERICA',
        prodNum,
        policy: t.policy,
        insured: t.insured,
        state: '',
        activityDate: t.activityDate,
        issueDate: '',
        commPremium: Number(t.commPremium) || 0,
        activityType,
        commPct: Number(t.commPct) || 0,
        commAmt: 0,   // Transamerica has no Comm Amt column; only advance flow
        advComm,
        netToAccount,
        monthKey: monthKey(t.activityDate || parsed.periodEnding),
        weekKey: weekKey(t.activityDate || parsed.periodEnding),
        source: sourceName,
        isChargeback: CHARGEBACK_TYPES.has(activityType) || netToAccount < 0,
      });
      if (!newProducers[prodNum]) newProducers[prodNum] = { team: KNOWN_PRODUCERS[prodNum]?.team || 'unassigned', name: KNOWN_PRODUCERS[prodNum]?.name || '' };
    });
    Object.entries(discoveredNames).forEach(([pn, name]) => {
      if (!newProducers[pn]) newProducers[pn] = { team: 'unassigned', name };
      else if (!newProducers[pn].name) newProducers[pn].name = name;
    });
    setTransactions(prev => {
      const existingIds = new Set(prev.map(t => t.id));
      const toAdd = newTx.filter(t => !existingIds.has(t.id));
      return [...prev, ...toAdd];
    });
    // Functional update — see Americo paste path for the same fix. A
    // stale closure must never clobber an existing team assignment.
    setProducers(prev => {
      const next = { ...prev };
      Object.entries(newProducers).forEach(([pn, info]) => {
        if (!next[pn]) next[pn] = info;
        else if (!next[pn].name && info.name) next[pn] = { ...next[pn], name: info.name };
      });
      return next;
    });
    setUploads(u => [{ name: sourceName, at: new Date().toISOString(), rows: newTx.length, type: 'paste' }, ...u].slice(0, 100));
    showToast(`Imported ${newTx.length} Transamerica rows from pasted statement`);
    // Money intelligence panel
    if (newTx.length > 0) {
      const insights = computeImportInsights(newTx, settings?.ownerProdNum, newProducers);
      setStatementInsights(insights);
    }
    return newTx.length;
  };

  const handleFiles = async (files) => {
    console.log(`[upload] handleFiles called with ${files.length} file(s):`, files.map(f => `${f.name} (${f.size} bytes)`));
    let added = 0;
    const newTx = [];
    const newProducers = { ...producers };
    const newUploadsLog = [];
    const discoveredNames = {};
    const perFileResults = [];
    if (files.length > 1) showToast(`Processing ${files.length} files…`);

    for (const file of files) {
      try {
        const isPdf = file.name.toLowerCase().endsWith('.pdf');
        if (isPdf) {
          // Try parsing as a real PDF first
          let text;
          try {
            text = await extractPdfText(file);
          } catch (err) {
            console.error(`[upload] PDF read failed for ${file.name}:`, err);
            perFileResults.push({ name: file.name, rows: 0, ok: false, error: `PDF read failed: ${err.message}` });
            continue;
          }
          let parsed;
          if (/AMERICO Commission Report/i.test(text)) {
            parsed = parseAmericoPdf(text);
          } else if (/personal advance detail/i.test(text)) {
            parsed = parseTransamericaPdf(text);
          } else if (/MUTUAL OF OMAHA|DETAILED COMPENSATION/i.test(text)) {
            // Deterministic-first for MoO. The carry-forward state
            // machine in api/_carrierParsers/mutualOfOmaha.js handles
            // the layouts the legacy parseMooPdf misses (reported
            // 2026-06-27 as "imported 0" on a real PDF). Fall back
            // to the legacy parser only if the endpoint fails or
            // returns no rows — preserves the existing happy path.
            try {
              const detRes = await apiFetch('/api/life/parse-moo-deterministic', {
                method: 'POST',
                body: JSON.stringify({ fullText: text }),
              });
              const bridged = bridgeDeterministicToUploadFlow(detRes);
              if (bridged.transactions.length > 0) {
                parsed = bridged;
                // eslint-disable-next-line no-console
                console.info('[upload] MOO parsed via deterministic:', bridged.transactions.length, 'rows · checksum',
                  detRes?.checksums?.ok === true ? 'ok' : detRes?.checksums?.ok === false ? `delta $${detRes.checksums.delta}` : 'n/a');
              } else {
                throw new Error('deterministic returned 0 rows');
              }
            } catch (detErr) {
              // eslint-disable-next-line no-console
              console.warn('[upload] deterministic failed:', detErr?.message || detErr,
                '— trying LLM fallback then in-app parseMooPdf');
              // Tier 2: LLM-vision endpoint with the actual PDF bytes.
              // The det parser is fast + free but doesn't yet handle
              // every EBSSR layout variant. Claude reads the PDF
              // directly and has been reliable on this format.
              let llmOk = false;
              try {
                const b64 = await fileToBase64Inline(file);
                const llmRes = await apiFetch('/api/life/parse-moo-statement', {
                  method: 'POST',
                  body: JSON.stringify({ pdfBase64: b64, mimeType: file.type || 'application/pdf' }),
                });
                const bridged = bridgeLlmToUploadFlow(llmRes);
                if (bridged.transactions.length > 0) {
                  parsed = bridged;
                  llmOk = true;
                  // eslint-disable-next-line no-console
                  console.info('[upload] MOO parsed via LLM fallback:', bridged.transactions.length, 'rows');
                }
              } catch (llmErr) {
                // eslint-disable-next-line no-console
                console.warn('[upload] LLM fallback failed:', llmErr?.message || llmErr);
              }
              if (!llmOk) parsed = parseMooPdf(text);
            }
          } else {
            // try MOO anyway — same det → LLM → legacy ladder.
            let tier = 'det';
            try {
              const detRes = await apiFetch('/api/life/parse-moo-deterministic', {
                method: 'POST',
                body: JSON.stringify({ fullText: text }),
              });
              const bridged = bridgeDeterministicToUploadFlow(detRes);
              if (bridged.transactions.length > 0) { parsed = bridged; }
              else throw new Error('deterministic returned 0 rows');
            } catch {
              tier = 'llm';
              try {
                const b64 = await fileToBase64Inline(file);
                const llmRes = await apiFetch('/api/life/parse-moo-statement', {
                  method: 'POST',
                  body: JSON.stringify({ pdfBase64: b64, mimeType: file.type || 'application/pdf' }),
                });
                const bridged = bridgeLlmToUploadFlow(llmRes);
                if (bridged.transactions.length > 0) { parsed = bridged; }
                else throw new Error('LLM returned 0 rows');
              } catch {
                tier = 'legacy';
                parsed = parseMooPdf(text);
              }
            }
            // eslint-disable-next-line no-console
            console.info(`[upload] MOO (fallback path) tier=${tier} rows=${parsed?.transactions?.length || 0}`);
          }
          // Diagnostic for the "imported 0 rows" case. When a recognized
          // statement type returns zero detail rows, dump the first slice
          // of extracted text to the console + stash it on window so we
          // can pull the user's exact PDF text out of a screenshot and
          // fix the regex. No-op when at least one row is parsed.
          if (parsed && (!parsed.transactions || parsed.transactions.length === 0)) {
            const preview = (text || '').slice(0, 4000);
            const carrier = /AMERICO Commission Report/i.test(text)
              ? 'AMERICO'
              : /personal advance detail/i.test(text)
              ? 'TRANSAMERICA'
              : 'UNKNOWN';
            console.warn(`[upload] ${carrier} parser returned 0 rows for ${file.name}. First 4KB of extracted text:`);
            console.warn(preview);
            try { window.__lastPdfExtract = { file: file.name, carrier, text }; } catch {}
          }
          // Merge discovered names
          Object.entries(parsed.producerNames).forEach(([pn, name]) => {
            // Title case the names
            const tc = name.split(' ').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
            discoveredNames[pn] = tc;
          });
          let fileAdded = 0;
          parsed.transactions.forEach((t, idx) => {
            const prodNum = String(t.prodNum || '').trim() || 'unknown';
            // Rule: netToAccount is the money that actually hits your account.
            //   - AMERICO: parser already computed commAmt = Primary Balance (what hits earned account)
            //   - Transamerica (advance placements): advComm IS the deposit (Comm Amt is always $0)
            //   - MOO: netToAccount = Comm Amt (ignore Adv Comm)
            let netToAccount = 0;
            let commAmt = Number(t.commAmt) || 0;
            let advComm = Number(t.advComm) || 0;
            if (t.isAmerico) {
              // AMERICO: commAmt = Primary Balance = $ hitting earned account (can be negative)
              netToAccount = commAmt;
            } else if (t.isAdvance) {
              // Transamerica-style advance row: use advComm (or commPremium as fallback)
              advComm = advComm || (t.commPremium || 0);
              netToAccount = advComm;
            } else {
              // MOO-style row from a PDF: honor Comm Amt column only.
              // If the PDF didn't expose commAmt explicitly, fall back to premium × pct.
              if (commAmt === 0 && (t.commPct || 0) !== 0) {
                const pct = (t.commPct || 0) / 100;
                commAmt = (t.commPremium || 0) * pct;
                if (/LAPSE|REVERSAL|CANC|RECPT ADV CO|REV/i.test(t.activityType || '')) {
                  commAmt = -Math.abs(commAmt);
                }
              }
              netToAccount = commAmt;
            }
            const activityType = (t.activityType || '').trim();
            const tx = {
              id: `${file.name}|${prodNum}|${t.policy||''}|${t.activityDate||''}|${activityType}|${idx}`,
              // MOO statements often print the company name as "UNITED"
              // (the underwriting entity, United of Omaha Life Insurance
              // Co.) but customers know it as Mutual of Omaha. Normalize
              // on write so every downstream display says MOO and we
              // don't have to do this in 30 places.
              company: t.company === 'UNITED' ? 'Mutual of Omaha' : (t.company || 'Mutual of Omaha'),
              mga: t.mga || '',
              prodNum,
              policy: t.policy,
              insured: t.insured,
              state: t.state || '',
              activityDate: t.activityDate,
              issueDate: t.issueDate || '',
              planCode: t.planCode || '',   // surfaces from parseMooPdf row[5]; powers getProduct()
              commPremium: t.commPremium || 0,
              activityType,
              commPct: t.commPct || 0,
              commAmt, advComm, netToAccount,
              monthKey: monthKey(t.activityDate || parsed.periodEnding),
              weekKey: weekKey(t.activityDate || parsed.periodEnding),
              source: file.name,
              isChargeback: CHARGEBACK_TYPES.has(activityType) || netToAccount < 0,
              // Stamp the producer's display name on every transaction.
              // Americo + Transamerica parsers already set t.agentName
              // inline (different per-row writing agent). MOO emits
              // names via parsed.producerNames keyed by prodNum, which
              // got merged into discoveredNames a few lines up — so for
              // MOO rows we fall through to discoveredNames[prodNum].
              // This is what the app-wide auto-backfill effect reads to
              // populate the producers map → every "Producer 1178144"
              // fallback becomes the real agent name.
              agentName: t.agentName || discoveredNames[prodNum] || '',
            };
            newTx.push(tx);
            if (!newProducers[prodNum]) {
              newProducers[prodNum] = {
                team: KNOWN_PRODUCERS[prodNum]?.team || 'unassigned',
                name: KNOWN_PRODUCERS[prodNum]?.name || '',
              };
            }
            fileAdded++;
          });
          newUploadsLog.push({ name: file.name, at: new Date().toISOString(), rows: fileAdded, type: 'pdf' });
          added += fileAdded;
          perFileResults.push({ name: file.name, rows: fileAdded, ok: true });
        } else {
          // XLSX/CSV path — xlsx dynamic-imported on demand (Fix #1).
          // Parser body extracted to src/lib/statementParser.js in
          // Phase 1d so node --test can hit the SAME code path
          // this handler runs. Post-processing (monthKey / weekKey
          // date bucketing + KNOWN_PRODUCERS name-lookup) stays
          // here because it depends on this component's helpers.
          const XLSX = await import('xlsx');
          const buf = await file.arrayBuffer();
          const wb = XLSX.read(buf, { type: 'array' });
          const { transactions, producers: parsedProducers } = parseWorkbook(wb, XLSX, { sourceName: file.name });
          transactions.forEach(tx => {
            newTx.push({
              ...tx,
              monthKey: monthKey(tx.activityDate),
              weekKey:  weekKey(tx.activityDate),
            });
          });
          Object.keys(parsedProducers).forEach(prodNum => {
            if (!newProducers[prodNum]) {
              newProducers[prodNum] = {
                team: KNOWN_PRODUCERS[prodNum]?.team || 'unassigned',
                name: KNOWN_PRODUCERS[prodNum]?.name || '',
              };
            }
          });
          const fileAdded = transactions.length;
          newUploadsLog.push({ name: file.name, at: new Date().toISOString(), rows: fileAdded, type: 'xlsx' });
          added += fileAdded;
          perFileResults.push({ name: file.name, rows: fileAdded, ok: true });
        }
      } catch (err) {
        console.error(`[upload] failed to parse ${file.name}:`, err);
        perFileResults.push({ name: file.name, rows: 0, ok: false, error: err.message });
        // Fire-and-forget: tell the admin panel a statement parse
        // choked. Shape hints only — no rows, no client PII.
        try {
          (async () => {
            let owner_id = null, email = null;
            try {
              const { data: { user } } = await supabase.auth.getUser();
              owner_id = user?.id || null;
              email    = user?.email || null;
            } catch { /* anon */ }
            askAdminForHelp({
              tag:      'statement_parse',
              owner_id,
              email,
              severity: 'error',
              shape: {
                error:      String(err.message || err).slice(0, 200),
                file_name:  (file.name || 'unknown').slice(0, 80),
                file_type:  file.type || 'unknown',
                file_bytes: file.size || 0,
                extension:  (file.name || '').split('.').pop()?.slice(0, 8) || '',
              },
            });
          })();
        } catch { /* helper never throws */ }
      }
    }
    console.log('[upload] per-file results:', perFileResults);

    // Also signal "parsed but produced zero rows" — that's a silent
    // failure mode where the file looked valid but the reader couldn't
    // extract anything. Admin panel treats this the same as a hard
    // parse error (recommends different format / offers white-glove).
    const zeroRowFiles = perFileResults.filter(r => r.ok && r.rows === 0);
    if (zeroRowFiles.length > 0) {
      try {
        (async () => {
          let owner_id = null, email = null;
          try {
            const { data: { user } } = await supabase.auth.getUser();
            owner_id = user?.id || null;
            email    = user?.email || null;
          } catch { /* anon */ }
          askAdminForHelp({
            tag:      'statement_parse',
            owner_id,
            email,
            severity: 'warn',
            shape: {
              error:      'zero_rows_extracted',
              file_names: zeroRowFiles.map(r => (r.name || 'unknown').slice(0, 80)),
              file_count: zeroRowFiles.length,
            },
          });
        })();
      } catch { /* helper never throws */ }
    }

    // Apply discovered names (don't overwrite user-entered ones)
    Object.entries(discoveredNames).forEach(([pn, name]) => {
      if (!newProducers[pn]) newProducers[pn] = { team: 'unassigned', name };
      else if (!newProducers[pn].name) newProducers[pn].name = name;
    });

    setTransactions(prev => {
      const existingIds = new Set(prev.map(t => t.id));
      const toAdd = newTx.filter(t => !existingIds.has(t.id));
      return [...prev, ...toAdd];
    });
    // Functional update so the closure copy of `producers` (captured at
    // upload start, before any await — could be MANY seconds old on big
    // PDFs) can never overwrite team assignments the user made while the
    // parse was running. Only ADD producers we don't already have, and
    // only backfill a missing name; never touch an existing team field.
    setProducers(prev => {
      const next = { ...prev };
      Object.entries(newProducers).forEach(([pn, info]) => {
        if (!next[pn]) next[pn] = info;
        else if (!next[pn].name && info.name) next[pn] = { ...next[pn], name: info.name };
      });
      return next;
    });
    setUploads(u => [...newUploadsLog, ...u].slice(0, 100));
    const okCount = perFileResults.filter(r => r.ok).length;
    const failCount = perFileResults.filter(r => !r.ok).length;
    const breakdown = perFileResults
      .map(r => r.ok ? `${r.name}: ${r.rows}` : `${r.name}: FAILED (${r.error})`)
      .join(' • ');

    // ── Reconciliation summary ─────────────────────────────────────
    // Sum Comm Amt across the rows we just imported, grouped by MGA.
    // Total of (SAS&I + Eterna + …) should equal the carrier's stated
    // net check. Operators can cross-check this against the statement's
    // "UNITED COMMISSION PAYABLE" line in 5 seconds.
    const mgaTotals = {};
    let netCheck = 0;
    for (const tx of newTx) {
      const mga = (tx.mga || 'UNCATEGORIZED').toString();
      const amt = Number(tx.commAmt) || 0;
      mgaTotals[mga] = (mgaTotals[mga] || 0) + amt;
      netCheck += amt;
    }
    const fmt$ = (n) => (n < 0 ? '-' : '') + '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const mgaLines = Object.entries(mgaTotals)
      .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
      .map(([mga, total]) => `  ${mga.slice(0, 32)}: ${fmt$(total)}`)
      .join('\n');
    const reconciliation = newTx.length > 0
      ? `\n\nReconciliation by MGA:\n${mgaLines}\n  ─────\n  NET (your check): ${fmt$(netCheck)}\n\nCross-check this against the statement's "UNITED COMMISSION PAYABLE" line.`
      : '';

    const summary = failCount > 0
      ? `Imported ${added} rows from ${okCount}/${files.length} file(s) — ${failCount} failed. ${breakdown}${reconciliation}`
      : `Imported ${added} rows from ${okCount} file(s). ${breakdown}${reconciliation}`;
    showToast(summary, failCount > 0 ? 'error' : 'success');

    // Money intelligence — pop the StatementInsightsModal with the
    // full breakdown of own writes + override income + chargebacks +
    // new advance exposure. Skipped if no rows landed.
    if (newTx.length > 0) {
      const insights = computeImportInsights(newTx, settings?.ownerProdNum, newProducers);
      setStatementInsights(insights);
    }
  };

  const onFileChange = (e) => {
    if (e.target.files?.length) handleFiles(Array.from(e.target.files));
    e.target.value = '';
  };
  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) handleFiles(Array.from(e.dataTransfer.files));
  };

  // Global drag-and-drop overlay — accept files dropped anywhere in the window
  const [isDragging, setIsDragging] = useState(false);
  const dragCountRef = useRef(0);
  useEffect(() => {
    const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
    const onDragEnter = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragCountRef.current++;
      setIsDragging(true);
    };
    const onDragLeave = (e) => {
      if (!hasFiles(e)) return;
      dragCountRef.current = Math.max(0, dragCountRef.current - 1);
      if (dragCountRef.current === 0) setIsDragging(false);
    };
    const onDragOver = (e) => { if (hasFiles(e)) e.preventDefault(); };
    const onDropWin = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragCountRef.current = 0;
      setIsDragging(false);
      if (e.dataTransfer.files?.length) handleFiles(Array.from(e.dataTransfer.files));
    };
    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDropWin);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDropWin);
    };
  }, []);

  const months = Array.from(new Set(transactions.map(t => t.monthKey))).sort();
  // Distinct years present in the data — drives the Year selector.
  // Newest first so picking "current year" is the first non-"All" option.
  const years = Array.from(new Set(months.map(m => m && m !== 'unknown' ? m.split('-')[0] : null).filter(Boolean))).sort().reverse();
  // Months filtered to the selected year (for the dependent Month dropdown).
  const monthsForYear = selectedYear === 'all' ? months : months.filter(m => m.startsWith(`${selectedYear}-`));
  const existingWeeks = transactions.map(t => t.weekKey || 'unknown').filter(w => w !== 'unknown');
  const futureWeeks = upcomingWeekKeys(4);
  const weeks = Array.from(new Set([...existingWeeks, ...futureWeeks])).sort();

  const filteredTx = (() => {
    // First strip accounting-only activity types (Special Disbursement / EFT / etc.) so they
    // never reach any production aggregation. These appear in MOO statements as payment-movement
    // records, not new production — the user's MOO methodology requires excluding them.
    const productionTx = transactions.filter(t => !isIgnoredActivity(t));
    if (periodMode === 'custom') {
      if (!customStart && !customEnd) return productionTx;
      const start = customStart ? new Date(customStart + 'T00:00:00') : null;
      const end = customEnd ? new Date(customEnd + 'T23:59:59') : null;
      return productionTx.filter(t => {
        const d = parseDate(t.activityDate);
        if (!d) return false;
        if (start && d < start) return false;
        if (end && d > end) return false;
        return true;
      });
    }
    if (periodMode === 'week') {
      return selectedWeek === 'all' ? productionTx : productionTx.filter(t => t.weekKey === selectedWeek);
    }
    // Month mode: specific month wins; otherwise narrow to year if one is picked.
    if (selectedMonth !== 'all') return productionTx.filter(t => t.monthKey === selectedMonth);
    if (selectedYear !== 'all')  return productionTx.filter(t => (t.monthKey || '').startsWith(`${selectedYear}-`));
    return productionTx;
  })();
  const classify = (tx) => {
    // The agency owner's production stands alone — never bucketed into either team.
    // Two paths: KNOWN_PRODUCERS hardcoded as 'owner', OR settings.ownerProdNum match.
    if (KNOWN_PRODUCERS[tx.prodNum]?.team === 'owner') return 'owner';
    if (settings.ownerProdNum && tx.prodNum === settings.ownerProdNum) return 'owner';
    const p = producers[tx.prodNum];
    if (!p || p.team === 'unassigned') return 'unassigned';
    return p.team;
  };

  const agg = {
    total: { gross: 0, chargebacks: 0, net: 0, count: 0 },
    mine: { gross: 0, chargebacks: 0, net: 0, count: 0 },
    cam: { gross: 0, chargebacks: 0, net: 0, count: 0 },
    owner: { gross: 0, chargebacks: 0, net: 0, count: 0 },
    unassigned: { gross: 0, chargebacks: 0, net: 0, count: 0 },
  };
  filteredTx.forEach(tx => {
    const bucket = classify(tx);
    const amt = tx.netToAccount;
    agg.total.count++; agg[bucket].count++;
    if (amt >= 0) { agg.total.gross += amt; agg[bucket].gross += amt; }
    else { agg.total.chargebacks += amt; agg[bucket].chargebacks += amt; }
    agg.total.net += amt; agg[bucket].net += amt;
  });

  // Build policy-level history to track what Cam was originally paid on each policy
  // Policy key = mga + policy number + prodNum (to uniquely identify a policy+producer combo)
  // We walk chronologically through YOUR team's transactions and track Cam's 5% payouts
  // When a chargeback appears, we clawback from the sum previously paid to Cam on that policy (capped at what he received)
  const policyKey = (t) => `${t.mga||''}|${t.policy||''}|${t.prodNum||''}`;

  // Build period aggregate (month or week) using the same Cam cut math
  const buildPeriodAgg = (periodKey, allPeriods, txnPeriodKey, labelFn) => {
    return allPeriods.map(p => {
      const rows = transactions.filter(t => txnPeriodKey(t) === p);
      let mine=0, cam=0, owner=0, mineCB=0, camCB=0, ownerCB=0;
      rows.forEach(tx => {
        const b = classify(tx);
        if (b === 'mine')  { if (tx.netToAccount>=0) mine  += tx.netToAccount; else mineCB  += tx.netToAccount; }
        if (b === 'cam')   { if (tx.netToAccount>=0) cam   += tx.netToAccount; else camCB   += tx.netToAccount; }
        if (b === 'owner') { if (tx.netToAccount>=0) owner += tx.netToAccount; else ownerCB += tx.netToAccount; }
      });
      const mineNet = mine + mineCB;
      const camNet = cam + camCB;
      const ownerNet = owner + ownerCB;

      const policyPaidToCam = {};
      let camEarnedThisPeriod = 0;
      let camClawbackThisPeriod = 0;

      const sortedPeriods = allPeriods.slice().sort();
      const upToThis = sortedPeriods.filter(pp => pp <= p);
      for (const pp of upToThis) {
        const periodRows = transactions.filter(t => txnPeriodKey(t) === pp && classify(t) === 'mine');
        for (const tx of periodRows) {
          const k = `${tx.mga||''}|${tx.policy||''}|${tx.prodNum||''}`;
          const prem = Math.abs(Number(tx.commPremium) || 0);
          const isNegativeActivity = CHARGEBACK_TYPES.has(tx.activityType) || tx.netToAccount < 0;
          if (!isNegativeActivity) {
            const earned = prem * (settings.camPercent / 100);
            policyPaidToCam[k] = (policyPaidToCam[k] || 0) + earned;
            if (pp === p) camEarnedThisPeriod += earned;
          } else {
            const clawbackAttempt = prem * (settings.camPercent / 100);
            const available = policyPaidToCam[k] || 0;
            const actualClawback = Math.min(clawbackAttempt, available);
            policyPaidToCam[k] = Math.max(0, available - actualClawback);
            if (pp === p) camClawbackThisPeriod += actualClawback;
          }
        }
      }
      const camCutOnMine = camEarnedThisPeriod - camClawbackThisPeriod;
      return {
        [periodKey]: p,
        key: p,
        label: labelFn(p),
        mineGross: mine, mineCB, mineNet,
        camGross: cam, camCB, camNet,
        ownerGross: owner, ownerCB, ownerNet,
        camCutOnMine,
        camCutEarned: camEarnedThisPeriod,
        camCutClawback: camClawbackThisPeriod,
        camTotalPayout: camNet + camCutOnMine,
      };
    });
  };

  const weeklyAgg = buildPeriodAgg('week', weeks, (t) => t.weekKey, weekLabel);
  const monthlyAgg = buildPeriodAgg('month', months, (t) => t.monthKey, monthLabel);

  // Normalize carrier names - group similar ones
  // Catalog-aware carrier + product resolvers. Both delegate to the pure
  // resolveCarrier / resolveProduct helpers above, threading the current
  // configurable catalog through. Legacy hardcoded fallback inside the
  // resolvers means an empty / partial catalog still classifies correctly.
  const getCarrier = (tx) => resolveCarrier(tx, carrierCatalog);
  const getProduct = (tx) => resolveProduct(tx, carrierCatalog);

  const producerAgg = {};
  filteredTx.forEach(tx => {
    if (!producerAgg[tx.prodNum]) {
      producerAgg[tx.prodNum] = {
        prodNum: tx.prodNum,
        name: getProducerName(tx.prodNum, producers),
        team: classify(tx),
        gross: 0, cb: 0, net: 0, count: 0,
        premiumPositive: 0,
        premiumNegative: 0,
        newIssueCount: 0,
        chargebackCount: 0,
        carriers: {},
      };
    }
    const a = producerAgg[tx.prodNum];
    const carrier = getCarrier(tx);
    if (!a.carriers[carrier]) {
      a.carriers[carrier] = { gross: 0, cb: 0, net: 0, count: 0, premiumPositive: 0, premiumNegative: 0 };
    }
    const c = a.carriers[carrier];
    a.count++; c.count++;
    const prem = Math.abs(Number(tx.commPremium) || 0);
    if (tx.netToAccount >= 0) {
      a.gross += tx.netToAccount; c.gross += tx.netToAccount;
      a.premiumPositive += prem; c.premiumPositive += prem;
      a.newIssueCount++;
    } else {
      a.cb += tx.netToAccount; c.cb += tx.netToAccount;
      a.premiumNegative += prem; c.premiumNegative += prem;
      a.chargebackCount++;
    }
    a.net += tx.netToAccount; c.net += tx.netToAccount;
  });

  // For each producer, compute your earnings:
  // - If on YOUR team: your earnings = their net hitting account − 5% × premium (what Cam takes)
  // - If on CAM's team: your earnings = 0 (Cam keeps 100%)
  Object.values(producerAgg).forEach(a => {
    const camCutOnThisProducer = a.team === 'mine'
      ? (a.premiumPositive * settings.camPercent / 100) - (a.premiumNegative * settings.camPercent / 100)
      : 0;
    a.camCut = camCutOnThisProducer;
    a.yourEarnings = a.team === 'mine' ? a.net - camCutOnThisProducer : 0;
    a.camEarnings = a.team === 'cam' ? a.net : camCutOnThisProducer;

    // Persistency metrics: how much commission stuck vs. was clawed back
    // persistency % = (gross + cb) / gross * 100 (clamped 0-100)
    // chargeback rate = |cb| / gross * 100
    a.chargebackRate = a.gross > 0 ? (Math.abs(a.cb) / a.gross) * 100 : (a.cb < 0 ? 100 : 0);
    a.persistency = a.gross > 0 ? Math.max(0, Math.min(100, ((a.gross + a.cb) / a.gross) * 100)) : (a.cb < 0 ? 0 : 100);
    // Flag: problem producer if > 30% chargeback rate or net-negative with material activity
    a.isProblem = a.chargebackRate > 30 || (a.net < 0 && Math.abs(a.cb) > 100);

    // Per-carrier earnings + persistency
    Object.entries(a.carriers).forEach(([name, c]) => {
      const camCutCarrier = a.team === 'mine'
        ? (c.premiumPositive * settings.camPercent / 100) - (c.premiumNegative * settings.camPercent / 100)
        : 0;
      c.camCut = camCutCarrier;
      c.yourEarnings = a.team === 'mine' ? c.net - camCutCarrier : 0;
      c.camEarnings = a.team === 'cam' ? c.net : camCutCarrier;
      c.chargebackRate = c.gross > 0 ? (Math.abs(c.cb) / c.gross) * 100 : (c.cb < 0 ? 100 : 0);
      c.persistency = c.gross > 0 ? Math.max(0, Math.min(100, ((c.gross + c.cb) / c.gross) * 100)) : (c.cb < 0 ? 0 : 100);
    });
  });

  const producerList = Object.values(producerAgg).sort((a,b) => b.net - a.net);

  const currentMonth = selectedMonth === 'all' ? (months[months.length-1] || null) : selectedMonth;
  const currentMonthData = monthlyAgg.find(m => m.month === currentMonth);
  const prevMonth = currentMonth ? months[months.indexOf(currentMonth)-1] : null;
  const prevMonthData = monthlyAgg.find(m => m.month === prevMonth);

  const currentWeek = selectedWeek === 'all' ? (weeks[weeks.length-1] || null) : selectedWeek;
  const currentWeekData = weeklyAgg.find(w => w.week === currentWeek);
  const prevWeek = currentWeek ? weeks[weeks.indexOf(currentWeek)-1] : null;
  const prevWeekData = weeklyAgg.find(w => w.week === prevWeek);

  // Period-agnostic "current" and "prev" used throughout UI
  // Synthesize period data for custom date range
  const customPeriodLabel = periodMode === 'custom' && (customStart || customEnd)
    ? `${customStart || '—'} → ${customEnd || '—'}`
    : 'Custom range';
  const customPeriodData = periodMode === 'custom' ? (() => {
    if (filteredTx.length === 0) return null;
    let mineGross=0, mineCB=0, camGross=0, camCB=0;
    filteredTx.forEach(tx => {
      const b = classify(tx);
      if (b === 'mine') { if (tx.netToAccount >= 0) mineGross += tx.netToAccount; else mineCB += tx.netToAccount; }
      if (b === 'cam')  { if (tx.netToAccount >= 0) camGross  += tx.netToAccount; else camCB  += tx.netToAccount; }
    });
    const mineNet = mineGross + mineCB;
    const camNet  = camGross + camCB;
    // Cam's 5% on YOUR team — policy-level capped clawback within range
    const policyPaid = {};
    let earned = 0, clawback = 0;
    const sorted = filteredTx.filter(t => classify(t) === 'mine').slice().sort((a,b) => (parseDate(a.activityDate)||0) - (parseDate(b.activityDate)||0));
    sorted.forEach(tx => {
      const k = `${tx.mga||''}|${tx.policy||''}|${tx.prodNum||''}`;
      const prem = Math.abs(Number(tx.commPremium) || 0);
      const cut = prem * (settings.camPercent / 100);
      const isNeg = CHARGEBACK_TYPES.has(tx.activityType) || tx.netToAccount < 0;
      if (!isNeg) {
        policyPaid[k] = (policyPaid[k] || 0) + cut;
        earned += cut;
      } else {
        const available = policyPaid[k] || 0;
        const actual = Math.min(cut, available);
        policyPaid[k] = available - actual;
        clawback += actual;
      }
    });
    const camCutOnMine = earned - clawback;
    return {
      label: customPeriodLabel,
      mineGross, mineCB, mineNet,
      camGross, camCB, camNet,
      camCutOnMine, camCutEarned: earned, camCutClawback: clawback,
      camTotalPayout: camNet + camCutOnMine,
    };
  })() : null;

  const currentPeriodData = periodMode === 'custom' ? customPeriodData : (periodMode === 'week' ? currentWeekData : currentMonthData);
  const prevPeriodData = periodMode === 'custom' ? null : (periodMode === 'week' ? prevWeekData : prevMonthData);
  const periodAgg = periodMode === 'week' ? weeklyAgg : monthlyAgg;

  const setProducerTeam = (prodNum, team) => {
    setProducers(p => ({ ...p, [prodNum]: { ...(p[prodNum]||{name:`Producer ${prodNum}`}), team } }));
  };
  const setProducerName = (prodNum, name) => {
    setProducers(p => ({ ...p, [prodNum]: { ...(p[prodNum]||{team:'unassigned'}), name } }));
  };

  // ── Custom teams ───────────────────────────────────────────────────
  // Operator-defined team labels that sit ALONGSIDE the legacy
  // 'mine' / 'unassigned' / 'owner' field. Producers carry an optional
  // customTeam string (the team id). This way override math stays
  // unchanged but the operator can group producers under custom
  // names like "FL Office" or "Mark's downline" for filtering and
  // reporting.
  const setProducerCustomTeam = (prodNum, teamId) => {
    setProducers(p => ({
      ...p,
      [prodNum]: { ...(p[prodNum]||{team:'unassigned'}), customTeam: teamId || '' },
    }));
  };
  const addCustomTeam = (name, color = 'emerald') => {
    const clean = String(name || '').trim();
    if (!clean) return null;
    // Stable id derived from the name so two operators on the same
    // tenant don't get collisions, and renames don't break producer
    // references (the id is independent of the display name).
    const baseId = clean.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `team-${Math.random().toString(36).slice(2, 8)}`;
    let id = baseId;
    let suffix = 1;
    setCustomTeams(prev => {
      while (prev.some(t => t.id === id)) {
        suffix++;
        id = `${baseId}-${suffix}`;
      }
      return [...prev, { id, name: clean, color, createdAt: new Date().toISOString() }];
    });
    return id;
  };
  const renameCustomTeam = (teamId, name) => {
    const clean = String(name || '').trim();
    if (!clean) return;
    setCustomTeams(prev => prev.map(t => t.id === teamId ? { ...t, name: clean } : t));
  };
  // Link a producer to another carrier's producer as the same person.
  // The "target" is the canonical record (Harrison's MOO prodNum
  // 1173602, say); the "source" is the alias (AM-FFCQN6). Passing
  // null / empty target removes the alias.
  // We REJECT self-loops and chains that would cycle.
  const setProducerAlias = (sourceProdNum, targetProdNum) => {
    if (!sourceProdNum) return;
    setProducers(prev => {
      if (!targetProdNum) {
        // Unlink
        const cur = prev?.[sourceProdNum] || {};
        if (!cur.aliasOf) return prev;
        const next = { ...prev, [sourceProdNum]: { ...cur, aliasOf: '' } };
        return next;
      }
      const target = String(targetProdNum);
      if (target === sourceProdNum) return prev;
      // Walk the target's alias chain; if it points back at source,
      // refuse — that's a cycle.
      let walker = target;
      for (let i = 0; i < 8; i++) {
        if (walker === sourceProdNum) return prev; // would cycle
        const a = prev?.[walker]?.aliasOf;
        if (!a || a === walker) break;
        walker = a;
      }
      const cur = prev?.[sourceProdNum] || { team: 'unassigned' };
      return { ...prev, [sourceProdNum]: { ...cur, aliasOf: target } };
    });
  };

  const deleteCustomTeam = (teamId) => {
    setCustomTeams(prev => prev.filter(t => t.id !== teamId));
    // Detach every producer that pointed at this team. Stays
    // idempotent — if no producer was tagged, this is a no-op.
    setProducers(prev => {
      let changed = false;
      const next = { ...prev };
      for (const [pn, info] of Object.entries(prev)) {
        if (info?.customTeam === teamId) {
          next[pn] = { ...info, customTeam: '' };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  };

  // recordPayout() removed along with PayoutsView — it logged a
  // weekly "I paid CAM $X" record for the legacy partner-revenue-share
  // flow. Per-tenant override payouts will run through the
  // commissionRules system once that UI lands.

  const deleteTransactionsBySource = async (sourceName) => {
    if (!(await hqConfirm({
      title: `Delete transactions from ${sourceName}?`,
      body: 'This removes every transaction imported from this source. Cannot be undone.',
      confirmLabel: 'Delete transactions',
      danger: true,
    }))) return;
    setTransactions(t => t.filter(tx => tx.source !== sourceName));
    setUploads(u => u.filter(x => x.name !== sourceName));
    showToast(`Removed transactions from ${sourceName}`);
  };

  const clearAll = async () => {
    // Audit 2026-07-01 BUG-013: a bare confirm on a 'delete-everything'
    // button is insufficient — one stray click wipes the operator's
    // book. Type-to-confirm gate: the operator must type the exact
    // word DELETE. Anything else aborts. Branded final confirm covers
    // the case where the operator typed the word out of curiosity and
    // then paused. (Prompt() stays for now — no branded prompt yet.)
    const typed = window.prompt(
      'This deletes ALL commission data, transactions, uploads, and producers on this account. There is NO undo.\n\nType DELETE (in capitals) to confirm.'
    );
    if (typed !== 'DELETE') return;
    if (!(await hqConfirm({
      title: 'Really delete every commission record?',
      body: 'Last check. This is irreversible.',
      confirmLabel: 'Delete everything',
      danger: true,
    }))) return;
    setTransactions([]);
    setUploads([]);
    // Reset producers to pre-configured defaults
    const seeded = {};
    Object.entries(KNOWN_PRODUCERS).forEach(([pn, info]) => {
      seeded[pn] = { team: info.team, name: info.name };
    });
    setProducers(seeded);
    showToast('All data cleared — upload new statements to rebuild');
  };

  // reloadSeedData() removed. Previously injected SEED_TRANSACTIONS +
  // SEED_UPLOADS into the active workspace — both of which were the
  // operator's own real client policy data baked into the bundle. The
  // "Reload all project data" button in Settings has been removed
  // along with this function (see SettingsView for the gutted button).

  const resetProducers = () => {
    const seeded = { ...producers };
    Object.entries(KNOWN_PRODUCERS).forEach(([pn, info]) => {
      seeded[pn] = { team: info.team, name: info.name };
    });
    setProducers(seeded);
    showToast('Producer names and teams reset to defaults');
  };

  const exportCSV = () => {
    const rows = [
      ['Month','Producer','Name','Team','Policy','Insured','Activity','Gross','Chargeback','Net','Source'],
      ...filteredTx.map(tx => {
        const p = producers[tx.prodNum];
        return [
          tx.monthKey, tx.prodNum, p?.name||'', p?.team||'unassigned',
          tx.policy||'', tx.insured||'', tx.activityType||'',
          tx.netToAccount>=0?tx.netToAccount:0,
          tx.netToAccount<0?tx.netToAccount:0,
          tx.netToAccount, tx.source,
        ];
      })
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `commissions_${selectedMonth}.csv`; a.click();
  };

  const unassignedCount = Object.values(producers).filter(p => p.team === 'unassigned').length;

  // Auth gate — Supabase-backed.
  //  1. Auth-loading spinner first, so we don't flash login for an
  //     already-signed-in user.
  //  2. With no user, route between login / signup / reset screens. Each
  //     screen calls its AuthContext method and onAuthStateChange flips us
  //     into the app on success.
  //  3. With a user, show the data-loading spinner while the per-user
  //     localStorage namespace is hydrated, then fall through to the app.
  //     role === 'admin' for everyone in v1, so the legacy agent/va
  //     branch below never fires.
  if (auth.loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-sm text-slate-500">Loading…</div>
      </div>
    );
  }
  if (!currentUser) {
    if (authScreen === 'signup') {
      return <SignupScreen onSwitchToLogin={() => setAuthScreen('login')} />;
    }
    if (authScreen === 'reset') {
      return <ResetPasswordScreen onSwitchToLogin={() => setAuthScreen('login')} />;
    }
    return (
      <LoginScreen
        onSwitchToSignup={() => setAuthScreen('signup')}
        onSwitchToReset={() => setAuthScreen('reset')}
      />
    );
  }
  // Password recovery gate. AuthContext flips recoveryMode=true when
  // Supabase fires PASSWORD_RECOVERY after the customer lands via a
  // recovery-link email. We block the dashboard until they actually
  // set a new password — anything else would silently sign them in
  // with the old one.
  if (auth.recoveryMode) {
    return <SetNewPasswordScreen />;
  }
  // Subscription gate — restored 2026-06-23. Holds every signed-in
  // user on SubscribePage until subscriptions.status is active or
  // trialing. Bypassed for:
  //   - role='admin' (SFA team accounts)
  //   - viewingAs (admin impersonating a customer — already past gate)
  //   - past_due (kept inside CRM with the grace banner up top)
  const subStatus = subscription?.subscriptionStatus;
  const subLoading = subscription?.loadingSubscription;
  const isAdmin    = auth.role === 'admin';
  const inGrace    = subStatus === 'past_due'; // grace banner already wired above
  const subscriptionOk = isAdmin || !!viewingAs || inGrace
                      || subStatus === 'active' || subStatus === 'trialing';
  if (!subLoading && !subscriptionOk) {
    return <SubscribePage />;
  }
  // Two-part loading gate: either an in-flight load is running, OR the
  // in-memory state was hydrated for some OTHER user (e.g. the previous
  // signed-in tenant on this browser). In both cases we hold "Loading…"
  // so the dashboard never paints with the wrong tenant's data.
  if (loading || loadedForUserId !== currentUser.prodNum) {
    return <div className="p-8 text-center text-slate-500">Loading your data...</div>;
  }
  if (currentUser.role === 'agent' || currentUser.role === 'va') {
    const servedAgentProdNum = currentUser.role === 'va' ? currentUser.servesAgentProdNum : currentUser.prodNum;
    const servedAgentName = AGENT_CREDENTIALS[servedAgentProdNum]?.name || servedAgentProdNum;
    const myVas = Object.entries(vaCredentials).filter(([, v]) => v.servesAgentProdNum === currentUser.prodNum);
    return (
      <AgentDashboard
        currentUser={currentUser}
        servedAgentProdNum={servedAgentProdNum}
        servedAgentName={servedAgentName}
        transactions={filterForAgent(transactions, currentUser)}
        producers={producers}
        onSignOut={signOut}
        myVas={myVas}
        onCreateVa={createVa}
        onResetVaPassword={resetVaPassword}
        onDeleteVa={deleteVa}
        isUsernameTaken={isUsernameTaken}
        vaTimeLogs={vaTimeLogs}
        onClockIn={clockInVa}
        onClockOut={clockOutVa}
        onDeleteTimeLog={deleteTimeLog}
        calendar={calendar}
        clientProfiles={clientProfiles}
      />
    );
  }

  // Resolve branding for the chrome. The --brand-accent CSS variable is set
  // on the root <div> via inline style below, which cascades to any child
  // using var(--brand-accent). No useEffect here — placing one after the
  // early returns above would violate the rules of hooks (React #310).
  const branding = (() => {
    const b = resolveBranding(settings);
    // Demo Mode never exposes the operator's real agency name —
    // a prospect peeking at the header should see a believable
    // demo agency, not "Commission Command" (Harrison's real one).
    if (isDemoMode) {
      return { ...b, businessName: 'Sunrise Health & Life' };
    }
    return b;
  })();

  const __ctRoot = (
    <div
      className={`min-h-screen text-slate-900${HQ_ROOM_TABS.has(tab) ? '' : ' classic-hq'}`}
      style={{
        ['--brand-accent']: branding.accentColor,
        paddingLeft: 232,
        // Form a stacking context so the HqAmbientBackdrop (a fixed child at
        // z-index:-1) stays behind the tab content but above this wrapper's
        // own hq-floor background. Without this, classic (.classic-hq) tabs —
        // ACA / Medicare / Life — rendered blank: the backdrop out-painted
        // their static content. relative+z-index:0 does NOT change the
        // containing block for fixed descendants, so nothing else shifts.
        position: 'relative', zIndex: 0,
        // Room tabs paint their own full-bleed dark room over this, so their
        // base stays the legacy light. Classic (non-Room) tabs previously
        // rendered on flat #f4f7fb and clashed with the cinematic shell — now
        // they sit on the same photoreal headquarters as the Rooms, and their
        // Tailwind light panels are darkened to glass by the .classic-hq CSS
        // below (the same treatment Room applies to its content).
        ...(HQ_ROOM_TABS.has(tab)
          ? { background: '#f4f7fb' }
          : {
              backgroundColor: '#070a0c',
              backgroundImage:
                'linear-gradient(180deg, rgba(6,7,9,0.80) 0%, rgba(7,8,10,0.66) 42%, rgba(6,7,9,0.78) 100%), radial-gradient(1200px 520px at 82% 0%, rgba(201,162,39,0.12), transparent 70%), url(/hq/hq-floor.jpg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center top',
              backgroundAttachment: 'fixed',
              backgroundRepeat: 'no-repeat',
            }),
      }}
    >
      {/* Bring every classic (non-Room) tab onto the house theme: the office
          backdrop above + this scoped darkening turn white Tailwind cards into
          the same smoked-glass panels the Rooms use, so no page clashes with
          the cinematic shell. Scoped to .classic-hq so Room tabs (which own
          their own environment) are untouched. */}
      <style>{`
        .classic-hq { color: #e9edf3; }

        /* Neutral surfaces → floating smoked-glass panels. Depth gradient +
           amber hairline + soft drop shadow + inner top-highlight so every
           classic ACA / Medicare / Life card reads as the same translucent
           HQ panel the Rooms use, hovering over the ambient backdrop. */
        .classic-hq .bg-white,
        .classic-hq .bg-slate-50, .classic-hq .bg-slate-100,
        .classic-hq .bg-gray-50, .classic-hq .bg-gray-100,
        .classic-hq .bg-neutral-50, .classic-hq .bg-neutral-100,
        .classic-hq .bg-zinc-50, .classic-hq .bg-zinc-100,
        .classic-hq .bg-stone-50, .classic-hq .bg-stone-100 {
          background: linear-gradient(180deg, rgba(26,30,38,0.84) 0%, rgba(13,15,20,0.90) 100%) !important;
          color: #eef1f6 !important;
          border-color: rgba(245,166,35,0.16) !important;
          box-shadow: 0 14px 38px -20px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.05) !important;
          backdrop-filter: blur(11px);
        }

        /* Amber accent cards (cream bg-amber-50) → amber-lit glass. */
        .classic-hq .bg-amber-50, .classic-hq .bg-amber-100,
        .classic-hq .bg-yellow-50, .classic-hq .bg-yellow-100 {
          background: linear-gradient(180deg, rgba(245,166,35,0.16) 0%, rgba(245,166,35,0.05) 100%) !important;
          border-color: rgba(245,166,35,0.40) !important;
          color: #ffd79a !important;
        }
        /* Semantic accent surfaces → tinted glass (ok / critical / info). */
        .classic-hq .bg-emerald-50, .classic-hq .bg-green-50, .classic-hq .bg-teal-50 {
          background: rgba(91,163,111,0.12) !important; border-color: rgba(91,163,111,0.34) !important;
        }
        .classic-hq .bg-red-50, .classic-hq .bg-rose-50, .classic-hq .bg-orange-50 {
          background: rgba(196,88,74,0.12) !important; border-color: rgba(196,88,74,0.34) !important;
        }
        .classic-hq .bg-blue-50, .classic-hq .bg-sky-50, .classic-hq .bg-indigo-50,
        .classic-hq .bg-violet-50, .classic-hq .bg-purple-50, .classic-hq .bg-cyan-50 {
          background: rgba(106,169,201,0.12) !important; border-color: rgba(106,169,201,0.30) !important;
        }

        /* Ink → light; semantic text → brightened so it pops on dark glass. */
        .classic-hq .text-slate-900, .classic-hq .text-slate-800, .classic-hq .text-slate-700,
        .classic-hq .text-gray-900, .classic-hq .text-gray-800, .classic-hq .text-gray-700,
        .classic-hq .text-neutral-900, .classic-hq .text-neutral-800, .classic-hq .text-zinc-900, .classic-hq .text-zinc-800 {
          color: #eef1f6 !important;
        }
        .classic-hq .text-slate-600, .classic-hq .text-slate-500, .classic-hq .text-slate-400,
        .classic-hq .text-gray-600, .classic-hq .text-gray-500, .classic-hq .text-gray-400,
        .classic-hq .text-neutral-600, .classic-hq .text-neutral-500, .classic-hq .text-zinc-500 {
          color: #b7c0cc !important;
        }
        .classic-hq .text-amber-900, .classic-hq .text-amber-800, .classic-hq .text-amber-700, .classic-hq .text-amber-600,
        .classic-hq .text-yellow-800, .classic-hq .text-yellow-700 { color: #ffc457 !important; }
        .classic-hq .text-emerald-900, .classic-hq .text-emerald-800, .classic-hq .text-emerald-700, .classic-hq .text-emerald-600,
        .classic-hq .text-green-800, .classic-hq .text-green-700, .classic-hq .text-teal-700 { color: #86dca2 !important; }
        .classic-hq .text-red-900, .classic-hq .text-red-800, .classic-hq .text-red-700, .classic-hq .text-red-600,
        .classic-hq .text-rose-800, .classic-hq .text-rose-700, .classic-hq .text-orange-700 { color: #f0a79d !important; }
        .classic-hq .text-blue-900, .classic-hq .text-blue-800, .classic-hq .text-blue-700, .classic-hq .text-blue-600,
        .classic-hq .text-sky-700, .classic-hq .text-indigo-700, .classic-hq .text-violet-700, .classic-hq .text-purple-700 { color: #8fc4e0 !important; }

        /* Borders → hairlines. */
        .classic-hq .border-slate-100, .classic-hq .border-slate-200, .classic-hq .border-slate-300,
        .classic-hq .border-gray-100, .classic-hq .border-gray-200, .classic-hq .border-gray-300,
        .classic-hq .border-neutral-200, .classic-hq .border-zinc-200 {
          border-color: rgba(255,255,255,0.10) !important;
        }

        /* Dark inputs so form fields don't glare white on the cinematic ground. */
        .classic-hq input, .classic-hq select, .classic-hq textarea {
          background: rgba(9,11,15,0.55) !important;
          color: #eef1f6 !important;
          border-color: rgba(245,166,35,0.18) !important;
        }
        .classic-hq input::placeholder, .classic-hq textarea::placeholder { color: #79828f !important; }
      `}</style>
      {/* Portal-choice one-time prompt — pops on first login after
          signup and asks Life / Health / Both. Dismissal is durable
          (workspaceModePromptSeen in user_state + localStorage) so
          the modal never re-appears once handled. Lazy-loaded so its
          weight doesn't hit the main bundle. */}
      {showPortalPrompt && (
        <React.Suspense fallback={null}>
          <PortalChoicePrompt
            onChoice={(choice) => {
              setShowPortalPrompt(false);
              if (choice === 'life' || choice === 'health' || choice === 'both') {
                setWorkspaceMode(choice);
              }
            }}
          />
        </React.Suspense>
      )}
      {/* Past-due banner. Reads org.status / subscription.status from
          AuthContext. Renders above every other piece of chrome so the
          customer can't miss it but it's not a hard gate — they can
          still use the CRM during the grace period; this is a friendly
          nudge. Hidden when admin is viewing-as a customer (the operator
          should already know the customer's billing state). */}
      {!viewingAs && !auth.accountLoading && (
        (auth.subscription?.status === 'past_due') ||
        (auth.organization?.status === 'past_due')
      ) && (
        <div className="bg-amber-500 text-amber-950 px-4 py-2 sticky top-0 z-50 flex items-center justify-between gap-3 flex-wrap text-sm">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[10px] uppercase tracking-wider font-bold bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded">Past due</span>
            <span className="font-semibold">Your last payment didn't go through.</span>
            <span className="opacity-80 text-xs">Update your payment method to keep your access.</span>
          </div>
          <a
            href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL || 'support@sixfigureagentcrm.com'}?subject=${encodeURIComponent('Update my billing — ' + (auth.user?.email || 'Commission Command'))}`}
            className="bg-amber-900 text-amber-50 hover:bg-amber-800 text-xs font-bold px-3 py-1.5 rounded-lg whitespace-nowrap"
          >
            Contact billing
          </a>
        </div>
      )}

      {/* View-as banner. Renders above all chrome so the operator
          always sees that they're inside a customer's namespace. The
          read-only guard in persist() means nothing they touch will
          mutate the customer's actual data. */}
      {viewingAs && (
        <div className="bg-violet-700 text-white px-4 py-2 sticky top-0 z-50 flex items-center justify-between gap-3 flex-wrap text-sm">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[10px] uppercase tracking-wider font-bold bg-white/20 px-1.5 py-0.5 rounded">Admin · View as</span>
            <span className="font-semibold truncate">{viewingAs.email}</span>
            <span className="text-violet-200 text-xs">read-only · your changes won't save</span>
          </div>
          <button
            onClick={() => {
              // Clear the override; the load effect re-fires on
              // effectiveUserId change and re-hydrates the operator's
              // own snapshot.
              setViewingAs(null);
              setTab('customers');
            }}
            className="bg-white text-violet-800 hover:bg-violet-50 text-xs font-bold px-3 py-1.5 rounded-lg whitespace-nowrap"
          >
            Stop viewing
          </button>
        </div>
      )}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40" style={{ display: 'none' }} aria-hidden="true">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            {branding.logoDataUrl ? (
              <div className="h-9 w-auto max-w-[160px] flex items-center">
                <img src={branding.logoDataUrl} alt={branding.businessName} className="h-9 w-auto object-contain"/>
              </div>
            ) : (
              <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold" style={{ backgroundColor: branding.accentColor }}>$</div>
            )}
            <div>
              <div className="font-bold text-base sm:text-lg leading-tight truncate max-w-[180px] sm:max-w-none">{branding.businessName}</div>
              {/* BUG-024 — when both counters are zero, show a CTA
                  instead of a misleading-looking "0 · 0" stat line. */}
              {isDemoMode ? (
                <div className="hidden sm:block text-xs text-slate-500">3,142 transactions · 18 files · 12-mo persistency 83.4%</div>
              ) : (transactions.length === 0 && uploads.length === 0) ? (
                <div className="hidden sm:block text-xs text-slate-500 italic">No imports yet — upload a carrier statement to get started</div>
              ) : (() => {
                // Bug fix: header used `uploads.length` (raw upload
                // events) while Performance Overview uses a distinct
                // count of transaction sources. Drifted apart when
                // older transactions don't have a source stamp or
                // when the same upload was retried. Unify under the
                // source-derived count so both displays agree.
                const filesLoaded = new Set((transactions || []).map(t => t.source).filter(Boolean)).size;
                const fallback = uploads.length;
                const files = filesLoaded > 0 ? filesLoaded : fallback;
                return (
                  <div className="hidden sm:block text-xs text-slate-500">{(transactions || []).length.toLocaleString()} transactions · {files} file{files === 1 ? '' : 's'}</div>
                );
              })()}
            </div>
            {/* Metadata cluster (signed-in pill, role, workspace, sub, org,
                sign-out) is dense and slows mobile down. Hide on phone;
                small inline sign-out is rendered as a fallback below. */}
            <div className="hidden md:flex ml-2 pl-2 border-l border-slate-200 items-center gap-2 text-xs flex-wrap">
              <span className="text-slate-500">Signed in as</span>
              <span className="font-semibold" style={{ color: branding.accentColor }}>{currentUser.name}</span>
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase" style={{ backgroundColor: branding.accentColor + '20', color: branding.accentColor }}>{currentUser.role}</span>
              {/* Workspace mode pill — Life / Health / Both. Hides the
                  irrelevant top-nav groups so a single-vertical agent
                  never sees the wrong CRM. Persists to localStorage.
                  Entire pill hidden when the agency's subscription entitles
                  only one line — a single-product CRM has no toggle. */}
              {allowedModes.length > 1 && (
              <div className="inline-flex items-center bg-slate-100 border border-slate-200 rounded-full p-0.5 text-[0.55rem] font-black uppercase tracking-wider" title="Workspace mode — Life, Health, or both">
                <button
                  onClick={() => switchWorkspace('life')}
                  className={`px-2 py-0.5 rounded-full transition-colors ${workspaceMode === 'life' ? 'bg-emerald-600 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
                >Life</button>
                <button
                  onClick={() => switchWorkspace('health')}
                  className={`px-2 py-0.5 rounded-full transition-colors ${workspaceMode === 'health' ? 'bg-teal-600 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
                >Health</button>
                <button
                  onClick={() => switchWorkspace('both')}
                  className={`px-2 py-0.5 rounded-full transition-colors ${workspaceMode === 'both' ? 'bg-slate-900 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
                >Both</button>
              </div>
              )}
              {/* Subscription / plan pill. Drawn from public.subscriptions
                  + public.organizations via AuthContext. Color tracks
                  status: emerald=active/trialing, amber=past_due,
                  rose=canceled, slate=unknown/none. Hidden while the
                  account query is still loading. */}
              {!auth.accountLoading && (auth.subscription || auth.organization) && (() => {
                const status = auth.subscription?.status || auth.organization?.status || 'unknown';
                const plan   = auth.subscription?.plan   || auth.organization?.plan   || null;
                const colors =
                  status === 'active' || status === 'trialing'
                    ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                    : status === 'past_due'
                    ? 'bg-amber-100 text-amber-800 border-amber-200'
                    : status === 'canceled' || status === 'unpaid'
                    ? 'bg-rose-100 text-rose-800 border-rose-200'
                    : 'bg-slate-100 text-slate-600 border-slate-200';
                return (
                  <span className={`inline-flex items-center gap-1 border px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${colors}`} title={`Subscription: ${status}${plan ? ` (${plan})` : ''}`}>
                    {plan ? <span>{plan}</span> : null}
                    <span className="opacity-70">·</span>
                    <span>{status}</span>
                  </span>
                );
              })()}
              {currentOrg && (
                <button
                  onClick={() => setTeamPanelOpen(true)}
                  className="ml-2 inline-flex items-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 px-2 py-0.5 rounded-full text-[11px] font-semibold"
                  title="Manage team"
                >
                  <span className="text-slate-400"></span>
                  <span className="truncate max-w-[180px]">{currentOrg.name}</span>
                  <span className="text-slate-400">▾</span>
                </button>
              )}
              {/* Theme toggle — cycles light → dark → system. Icon
                  reflects current state so operators can eyeball what
                  they're on. Persisted per-user via cloudSet + localStorage;
                  survives new devices via the same reconcile path
                  workspaceMode uses. */}
              <button
                onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
                className="ml-2 text-slate-500 hover:text-slate-900 dark:hover:text-white text-[13px]"
                title={`${theme === 'dark' ? 'Dark' : 'Light'} mode · click to switch`}
                aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              >
                {theme === 'dark' ? '🌙' : '☀️'}
              </button>
              <button onClick={signOut} className="text-slate-500 hover:text-red-600 hover:underline ml-1">Sign out</button>
            </div>
            {/* Mobile-only sign-out — desktop has it inside the metadata cluster above. */}
            <button onClick={signOut} className="md:hidden text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-rose-700 border border-slate-200 rounded-md px-2 py-1" aria-label="Sign out">Sign out</button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden text-sm">
              <button onClick={()=>setPeriodMode('month')} className={`px-3 py-1.5 font-medium ${periodMode==='month' ? 'text-white' : 'text-slate-600 hover:bg-slate-50'}`} style={periodMode==='month' ? { backgroundColor: branding.accentColor } : undefined}>Month</button>
              <button onClick={()=>setPeriodMode('week')} className={`px-3 py-1.5 font-medium ${periodMode==='week' ? 'text-white' : 'text-slate-600 hover:bg-slate-50'}`} style={periodMode==='week' ? { backgroundColor: branding.accentColor } : undefined}>Week</button>
              <button onClick={()=>setPeriodMode('custom')} className={`px-3 py-1.5 font-medium ${periodMode==='custom' ? 'text-white' : 'text-slate-600 hover:bg-slate-50'}`} style={periodMode==='custom' ? { backgroundColor: branding.accentColor } : undefined}>Custom</button>
            </div>
            {periodMode === 'month' && (
              <>
                <select
                  value={selectedYear}
                  onChange={e => {
                    const y = e.target.value;
                    setSelectedYear(y);
                    // If the current month doesn't belong to the new year, clear it
                    // so the dependent dropdown re-defaults to "All months".
                    if (y !== 'all' && selectedMonth !== 'all' && !selectedMonth.startsWith(`${y}-`)) {
                      setSelectedMonth('all');
                    }
                  }}
                  className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white"
                  title="Filter by year"
                >
                  <option value="all">All years</option>
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <select value={selectedMonth} onChange={e=>setSelectedMonth(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white" title="Filter by month">
                  <option value="all">{selectedYear === 'all' ? 'All months' : `All ${selectedYear} months`}</option>
                  {monthsForYear.map(m => {
                    // When a year is selected, the year-in-label is redundant — show month name only.
                    const label = selectedYear === 'all' ? monthLabel(m) : monthLabel(m).split(' ')[0];
                    return <option key={m} value={m}>{label}</option>;
                  })}
                </select>
              </>
            )}
            {periodMode === 'week' && (
              <select value={selectedWeek} onChange={e=>setSelectedWeek(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white">
                <option value="all">All weeks</option>
                {weeks.map(w => {
                  const isFuture = futureWeeks.includes(w) && !existingWeeks.includes(w);
                  return <option key={w} value={w}>{weekLabel(w)}{isFuture ? ' · upcoming' : ''}</option>;
                })}
              </select>
            )}
            {periodMode === 'custom' && (
              <div className="flex items-center gap-1 text-sm">
                <input type="date" value={customStart} onChange={e=>setCustomStart(e.target.value)} className="px-2 py-1.5 border border-slate-200 rounded-lg bg-white" title="From"/>
                <span className="text-slate-400">→</span>
                <input type="date" value={customEnd} onChange={e=>setCustomEnd(e.target.value)} className="px-2 py-1.5 border border-slate-200 rounded-lg bg-white" title="To"/>
                {(customStart || customEnd) && (
                  <button onClick={()=>{setCustomStart('');setCustomEnd('');}} className="text-slate-400 hover:text-slate-700 ml-1" title="Clear range"><X size={14}/></button>
                )}
              </div>
            )}
            {/* Single "Import statement" entry point. Was 4 inline carrier
                buttons (Upload / Americo / Transamerica / Statement) that
                wrapped or clipped on narrower screens. Now collapsed to
                one neutral menu that fans out into Life + Health options. */}
            <div className="border-l border-slate-200 pl-2 ml-1">
              <ImportStatementMenu
                onFileChange={onFileChange}
                onPasteAmerico={() => setPasteCarrier('americo')}
                onPasteTransamerica={() => setPasteCarrier('transamerica')}
                onGoHealth={() => setTab('health_policies')}
              />
            </div>
          </div>
        </div>
        {/* ── Two-row grouped navigation ───────────────────────────────
            Row 1 (group selector): Home + 4 group pills + (admin) Customers.
            Row 2 (sub-tabs): the active group's tabs. Hidden on Home.

            Each tab belongs to exactly one group. Clicking a group jumps
            to the first tab in that group; clicking a sub-tab navigates
            within the active group. This collapses 20+ flat tabs into
            something a new agent can actually orient in.
         */}
        {(() => {
          const NAV_GROUPS = [
            {
              key: 'prospecting',
              label: 'Prospecting',
              // Reordered 2026-06-26 per operator request: Inbox first
              // (every reply lands here — first action of the morning).
              // Mission demoted below Leads. `alerts` dropped — almost
              // every alert was duplicated by Briefing / NUDGE banner
              // / Home pip. Route stays mounted so old bookmarks work.
              // Territory removed from Prospecting nav — founder-memo
              // kill list. "Pretty, unused. Ship as a deep-view under
              // Insights, not the main navigation." The tab === 'territory'
              // route at line 5981 stays mounted so deep links still
              // work; the visible nav item is the only thing gone.
              tabs: [
                ['inbox',     'Inbox'],
                ['leads',     'Leads'],
                ['mission',   'Mission'],
                ['calls',     'Calls'],
                ['calendar',  'Calendar'],
                // 2026-07-14 per operator: 'drips' and 'workflows' were
                // separate top-nav items but do overlapping jobs (SMS
                // cadences vs. multi-step automations). Combined into
                // one 'Automations' entry that lands on the drips tab
                // — the drips renderer has an inner segmented control
                // that toggles to workflows. Deep links to /workflows
                // still work.
                ['drips',     'Automations'],
              ],
            },
            {
              key: 'business',
              label: 'Life Insurance',
              // Cleanup 2026-06-26: dropped 'Conversations' (a duplicate
              // of Inbox at the lifecycle layer that confused operators);
              // merged 'Clients' + 'Policies' under one "Clients &
              // Policies" tab so the book is viewed in one place. The
              // `conversations` + `policies` routes stay mounted; just
              // hidden from the visible nav.
              tabs: [
                ['apps',           'Apps'],
                ['clients',        'Clients & Policies'],
                ['referrals',      'Referrals'],
                ['fex',            'FEX'],
                ['termuw',         'Life UW'],
                ['retention',      'Retention'],
                ['retention_command', 'Retention Command'],
                // Money tabs folded into Life Insurance — they cover
                // the life book's pay / P&L / overrides / policy
                // ledger / transactions. (Health Pay calculator lives
                // alongside Life Pay on the My Pay tab via the
                // vertical toggle.) Standalone Money group removed so
                // the top nav doesn't carry a second money rail.
                ['paycalc',        'My Pay'],
                ['pnl',            'P&L'],
                ['overrides_iq',   'Overrides'],
                ['transactions',   'Transactions'],
              ],
            },
            {
              key: 'agency',
              label: 'Agency',
              tabs: [
                ['dashboard',        'Dashboard'],
                ['producers',        'Producers'],
                ['leaderboard',      'Leaderboard'],
                ['book',             'Book of Business'],
                ['book_snapshot',    '📊 Book Snapshot'],
                ['ai_boss',          '👔 AI Boss'],
                ['atlas',            '🧭 ATLAS'],
                ['uplift',           '📈 UPLIFT'],
                ['automation_map',   '🗺 Automation Map'],
                ['agency',           'Presentation'],
                // Launch Readiness surfaces PLATFORM state (migrations,
                // env vars, DB connectivity) — not a subscriber's own
                // agency data. Hidden from non-admins so paying
                // customers don't see the platform's readiness board.
                // Gate matches the endpoint's public.users.role='admin'
                // check.
                ...(currentUser?.role === 'admin' ? [['launch_readiness', '🚦 Launch Readiness']] : []),
              ],
            },
            {
              key: 'health',
              label: 'Health',
              // Updated 2026-06-26: Inbox + Leads added so the operator
              // uses the SAME conversation list across both verticals
              // (prior layout had a separate health_inbox + health_leads;
              // operator complained "two inboxes are confusing for the
              // agents who buy this"). health_policies merged into
              // health_clients as "Clients & Policies" — one book of
              // business view. The health_policies route stays mounted.
              tabs: [
                // 2026-06-27: reordered so Health Dashboard is the
                // DEFAULT landing when clicking the Health group label.
                // Inbox/Leads stay reachable for shared access but no
                // longer hijack the landing — operator was clicking
                // "Health" and seeing Prospecting's UI.
                // REMODEL 2026-07-05 — top nav pared from 19 tabs to
                // the 6 things an agent actually reaches for daily.
                // Everything else stays reachable via the HealthShell
                // sidebar's Run/Protect/Grow sections + "More…"
                // expansion. Same principle: what does the agent DO
                // vs. what does the AI OS give them.
                // 2026-07-14 per operator: Inbox promoted to second
                // position ("Inbox should be at the front, not at the
                // end"). Command Center stays as the landing target
                // when clicking the Health group; Inbox next so morning
                // reply-triage is one click away.
                ['health_portal',     'Command Center'],
                ['inbox',             'Inbox'],
                ['ceo_briefing',      '👔 AI Ops Room'],
                ['health_clients',    'Clients'],
                ['ask_aria',          '✦ Ask ARIA'],
                ['leads',             'Leads'],
                // The other 13 tabs (Morning Briefing, ENROLL, NOVA,
                // Medicare, ACA, Eligibility, Revenue Command, At-Risk,
                // Cross-Sell, Webinars, Automation Map, Products,
                // War Room) live in the sidebar. Reachable, not
                // crowding.
              ],
            },
          ];
          // Workspace mode filter — hide the irrelevant groups so a
          // Health-only agent never sees Life nav and vice versa.
          //   · 'health' → only the Health group renders
          //   · 'life'   → every group EXCEPT Health renders
          //   · 'both'   → all groups render (legacy / power-user view)
          const VISIBLE_NAV_GROUPS = NAV_GROUPS.filter(g => {
            if (workspaceMode === 'health') return g.key === 'health';
            if (workspaceMode === 'life')   return g.key !== 'health';
            return true;
          });

          // Reverse-lookup: tab id → group key. FIRST-WINS — `inbox`
          // and `leads` intentionally appear in BOTH Prospecting and
          // Health sub-navs (operator uses one inbox across verticals).
          // Without the `if (!(id in m))` guard the Health entry would
          // overwrite, sending Life-mode operators to a hidden group.
          const TAB_TO_GROUP = NAV_GROUPS.reduce((m, g) => { g.tabs.forEach(([id]) => { if (!(id in m)) m[id] = g.key; }); return m; }, {});
          // Health-vertical tabs that don't have a sub-nav slot but
          // should still highlight the Health group when active. The
          // HealthShell sidebar exposes them as Quick Actions, so the
          // agent navigates here without leaving the Health workspace.
          // Without this mapping the nav silently snapped back to
          // "Prospecting" — the bottom-of-list fallback — which lied
          // to the agent about where they were.
          const HEALTH_VERTICAL_TAB_MAP = {
            health_leads:      'health',
            health_inbox:      'health',
            health_clients:    'health',
            health_at_risk:    'health',
            health_cross_sell: 'health',
            health_war_room:   'health',
            health_enrollment: 'health',
            health_consents:   'health',
            health_audit:      'health',
            health_workflows:  'health',
            health_calendar:   'health',
            cross_sell:        'health',
            coverage_summary:  'health',
            soa:               'health',
            daily_tracker:     'health',
            expenses:          'health',
          };
          // Inbox + Leads live in both Prospecting and Health sub-navs.
          // The active group should match the workspace the operator is
          // working in so the right sub-nav row renders.
          const resolvedGroupKey = (() => {
            if ((tab === 'inbox' || tab === 'leads') && workspaceMode === 'health') return 'health';
            return TAB_TO_GROUP[tab] || HEALTH_VERTICAL_TAB_MAP[tab] || 'prospecting';
          })();
          // Top-level standalone tabs (Home, Customers, Settings) don't
          // belong to a group — they own the page on their own and the
          // sub-nav row hides for them.
          const activeGroupKey = (tab === 'home' || tab === 'dial' || tab === 'retain' || tab === 'scorecard' || tab === 'customers' || tab === 'settings' || tab === 'nudge') ? null : resolvedGroupKey;
          const activeGroup    = NAV_GROUPS.find(g => g.key === activeGroupKey);

          // Health uses a TEAL accent everywhere; every other group keeps
          // the per-account branding accent (emerald default). This is
          // the single biggest "which vertical am I in?" signal — it lets
          // the agent eyeball the nav and know they're in the health CRM.
          const HEALTH_ACCENT = '#0d9488'; // teal-600
          const groupAccent = (g) => g.key === 'health' ? HEALTH_ACCENT : branding.accentColor;
          const activeAccent = activeGroup ? groupAccent(activeGroup) : branding.accentColor;

          const isAdmin = currentUser?.role === 'admin';
          const tabIsCustomers = tab === 'customers';

          return (
            <>
              {/* Row 1 — group selector
                  Reordered 2026-06-26:
                    HOME first (where am I / state of things)
                    DIAL second (most-used daily action)
                    [Prospecting group] third (Inbox / Leads / etc.)
                    [Life Insurance] [Agency] [Health] groups follow
                    RETAIN + SCORECARD demoted to a tail position
                      AFTER the groups — still reachable, no longer
                      competing with the primary navigation. */}
              <div className="max-w-[1600px] mx-auto px-4 lg:px-8 flex gap-1 overflow-x-auto items-center">
                {/* AI Staff is available to EVERY workspace mode — health-only
                    agents get the same AI team as everyone else (rebuilt 2026-07-12
                    per founder note "AI staff should be for health only agents too"). */}
                <button
                  onClick={() => setTab('ai_staff')}
                  title="Meet your AI team — 30 agents on duty for you"
                  className={`px-4 py-2 text-xs font-bold uppercase tracking-tight border-b-2 transition-colors whitespace-nowrap inline-flex items-center gap-1.5 ${tab === 'ai_staff' ? '' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
                  style={tab === 'ai_staff' ? { borderColor: '#b45309', color: '#b45309' } : undefined}
                >
                  🤖 AI Team
                </button>
                {workspaceMode !== 'health' && (
                  <>
                    <button
                      onClick={() => setTab('home')}
                      className={`px-4 py-2 text-xs font-bold uppercase tracking-tight border-b-2 transition-colors whitespace-nowrap ${tab === 'home' ? '' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
                      style={tab === 'home' ? { borderColor: branding.accentColor, color: branding.accentColor } : undefined}
                    >
                      Home
                    </button>
                    <button
                      onClick={() => setTab('dial')}
                      className={`px-4 py-2 text-xs font-bold uppercase tracking-tight border-b-2 transition-colors whitespace-nowrap inline-flex items-center gap-1.5 ${tab === 'dial' ? '' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
                      style={tab === 'dial' ? { borderColor: branding.accentColor, color: branding.accentColor } : undefined}
                      title="Command Center — top of queue is who to call next"
                    >
                      Dial
                    </button>
                    {/* 2026-07-14 per operator: Today's Mission promoted
                        from a Prospecting sub-tab to a solo top-level
                        button next to Home/Dial. Operator: "Today's
                        mission needs to be, like, a main tab or in the
                        home screen." */}
                    <button
                      onClick={() => setTab('mission')}
                      className={`px-4 py-2 text-xs font-bold uppercase tracking-tight border-b-2 transition-colors whitespace-nowrap inline-flex items-center gap-1.5 ${tab === 'mission' ? '' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
                      style={tab === 'mission' ? { borderColor: branding.accentColor, color: branding.accentColor } : undefined}
                      title="Today's Mission — your prioritized queue for the day"
                    >
                      Mission
                    </button>
                    <div className="w-px bg-slate-200 mx-1 my-1.5"/>
                  </>
                )}
                {/* Health CC button: only for Health or Both workspaces.
                    Previously lived inside the `workspaceMode !== 'health'`
                    fragment above, which made it visible to Life users
                    (wrong — Life has no reason to see a Health command
                    surface) and hidden from Health users (the ones who
                    actually own that surface). Fix per founder screenshot
                    where a LIFE WORKSPACE session landed on Health CC. */}
                {workspaceMode !== 'life' && (
                  <>
                    <button
                      onClick={() => setTab('health_command_center')}
                      title="Health Command Center (dark · decision-engine layout)"
                      className={`px-4 py-2 text-xs font-bold uppercase tracking-tight whitespace-nowrap inline-flex items-center gap-1.5 ${tab === 'health_command_center' ? '' : 'text-slate-600 hover:text-slate-900'}`}
                      style={tab === 'health_command_center'
                        ? { borderBottom: '2px solid #e6b849', color: '#b45309' }
                        : { background: 'linear-gradient(90deg, rgba(230,184,73,0.08), transparent)' }}
                    >
                      🏥 Health CC
                    </button>
                    <div className="w-px bg-slate-200 mx-1 my-1.5"/>
                  </>
                )}
                {VISIBLE_NAV_GROUPS.map(g => {
                  const isActive = activeGroupKey === g.key;
                  const accent   = groupAccent(g);
                  return (
                    <button
                      key={g.key}
                      onClick={() => setTab(g.tabs[0][0])}
                      className={`px-4 py-2 text-xs font-bold uppercase tracking-tight border-b-2 transition-colors whitespace-nowrap ${isActive ? '' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
                      style={isActive ? { borderColor: accent, color: accent } : undefined}
                    >
                      {g.label}
                      {g.key === 'agency' && unassignedCount > 0 && (
                        <span className="ml-1.5 bg-amber-100 text-amber-700 text-xs px-1.5 py-0.5 rounded-full">{unassignedCount}</span>
                      )}
                    </button>
                  );
                })}
                {/* RETAIN + SCORECARD demoted from row-1 prime real
                    estate to a tail position right of the nav groups.
                    Still visible + reachable but no longer competing
                    with HOME / DIAL / Prospecting for attention. */}
                {workspaceMode !== 'health' && (
                  <>
                    <div className="w-px bg-slate-200 mx-1 my-1.5"/>
                    <button
                      onClick={() => setTab('retain')}
                      className={`px-4 py-2 text-xs font-bold uppercase tracking-tight border-b-2 transition-colors whitespace-nowrap inline-flex items-center gap-1.5 ${tab === 'retain' ? '' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
                      style={tab === 'retain' ? { borderColor: branding.accentColor, color: branding.accentColor } : undefined}
                      title="RETAIN — keep every closed policy in force"
                    >
                      Retain
                    </button>
                    <button
                      data-tour-id="nav-scorecard"
                      onClick={() => setTab('scorecard')}
                      className={`px-4 py-2 text-xs font-bold uppercase tracking-tight border-b-2 transition-colors whitespace-nowrap inline-flex items-center gap-1.5 ${tab === 'scorecard' ? '' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
                      style={tab === 'scorecard' ? { borderColor: branding.accentColor, color: branding.accentColor } : undefined}
                      title="Phone agent KPI scorecard"
                    >
                      Scorecard
                    </button>
                  </>
                )}
                {/* Money Map: Life-workspace only per July 2026
                    request. The hierarchy + overrides + persistency
                    view is Life-vertical-heavy (MoO / Americo /
                    Transamerica producer lines, Cam Team / Harrison
                    Team overrides). Showing it inside Health context
                    was a source of confusion — clicking the sub-nav
                    inside it while in Health mode kicked the operator
                    to health_portal (audit BUG-003). Now hidden from
                    Health entirely. */}
                {workspaceMode !== 'health' && (
                  <button
                    data-tour-id="nav-money_map"
                    onClick={() => setTab('money_map')}
                    className={`px-4 py-2 text-xs font-bold uppercase tracking-tight border-b-2 transition-colors whitespace-nowrap inline-flex items-center gap-1.5 ${tab === 'money_map' ? '' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
                    style={tab === 'money_map' ? { borderColor: branding.accentColor, color: branding.accentColor } : undefined}
                    title="Money Map — agency hierarchy, downline overrides, persistency, debt exposure"
                  >
                    Money Map
                  </button>
                )}
                {isAdmin && !demoFlag.isDemo && (
                  <>
                    <div className="w-px bg-slate-200 mx-1 my-1.5"/>
                    <button
                      onClick={() => setTab('customers')}
                      className={`px-4 py-2 text-xs font-bold uppercase tracking-tight border-b-2 transition-colors whitespace-nowrap ${tabIsCustomers ? '' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
                      style={tabIsCustomers ? { borderColor: branding.accentColor, color: branding.accentColor } : undefined}
                    >
                      Customers
                    </button>
                    <button
                      onClick={() => setTab('observability')}
                      className={`px-4 py-2 text-xs font-bold uppercase tracking-tight border-b-2 transition-colors whitespace-nowrap ${tab === 'observability' ? '' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
                      style={tab === 'observability' ? { borderColor: branding.accentColor, color: branding.accentColor } : undefined}
                      title="ASCEND runs + per-tenant rate-limit buckets — admin only"
                    >
                      Observability
                    </button>
                  </>
                )}
                <div className="flex-1"/>
                {/* Nudge tab removed from top nav — founder-memo kill list.
                    The panel still exists at tab==='nudge' for anyone with
                    a deep link, and its 3 usable rules will migrate into
                    the client card in a follow-up. If nudgeCount.need > 0,
                    surface the count via a small inline badge on the Home
                    dashboard instead of a permanent tab. */}
                <button
                  onClick={() => setTab('settings')}
                  className={`px-3 py-2 text-xs font-bold uppercase tracking-tight border-b-2 transition-colors whitespace-nowrap inline-flex items-center gap-1.5 ${tab === 'settings' ? '' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
                  style={tab === 'settings' ? { borderColor: branding.accentColor, color: branding.accentColor } : undefined}
                  title="Settings"
                >
                  <SettingsGearIcon size={14}/>
                  Settings
                </button>
              </div>
              {/* Row 2 — active group's sub-tabs (hidden on Home + Customers).
                  Tinted background matches the group accent so the Health
                  workspace reads visibly different from Life. Long tab
                  groups (Life Insurance has 13) scroll horizontally; an
                  always-visible "More ▾" overflow menu at the end
                  guarantees the last tab is reachable in one click even
                  when it's scrolled off-screen. */}
              {activeGroup && (
                <SubNavRow
                  activeGroup={activeGroup}
                  tab={tab}
                  setTab={setTab}
                  activeAccent={activeAccent}
                  unassignedCount={unassignedCount}
                  inboxUnread={inboxUnread}
                />
              )}
            </>
          );
        })()}
      </div>

      {currentUser?.id && <OnboardingGate currentUser={currentUser} showToast={showToast} onGotoTab={setTab}/>}
      {/* Always-on Twilio Voice — Provider registers a persistent Device
          so inbound calls reach the operator regardless of which tab
          they're on. IncomingCallModal pops up on incoming events.
          Outbound CallWidget uses the same shared Device. */}
      <TwilioVoiceProvider currentUser={currentUser}>
        <IncomingCallModal
          leads={leads}
          onAccept={(call, { fromPretty }) => {
            // Find a matching lead so the active CallWidget shows
            // the right name + has a lead reference for disposition.
            const fromRaw = call.parameters?.From || '';
            const fromDigits = String(fromRaw).replace(/\D/g, '').slice(-10);
            const matched = fromDigits
              ? leads.find(l => String(l.phone || '').replace(/\D/g, '').slice(-10) === fromDigits)
              : null;
            setActiveCallLead({
              ...(matched || {}),
              name:           matched?.name || fromPretty || 'Inbound caller',
              phone:          fromRaw,
              __inboundCall:  call,
            });
          }}
          showToast={showToast}
        />
        {activeCallLead && (
          <CallWidget
            lead={activeCallLead}
            incomingCall={activeCallLead.__inboundCall || null}
            showToast={showToast}
            onClose={() => setActiveCallLead(null)}
            onSmsFallback={(leadId) => {
              setActiveCallLead(null);
              window.dispatchEvent(new CustomEvent('crm-sms-request', { detail: { leadId, lead: activeCallLead } }));
            }}
          />
        )}
        {/* Global ad-hoc SMS composer — opened by the `inbox:compose`
            event (e.g. the "Text" action on an Uplift opportunity).
            Sends to an ad-hoc phone via /api/sms/send-manual, so it
            works for clients/opportunities that have no lead record. */}
        {smsCompose && (
          <SmsComposeModal
            toPhone={smsCompose.phone}
            contactName={smsCompose.name}
            initialBody={smsCompose.body || ''}
            onClose={() => setSmsCompose(null)}
            onSent={() => { setSmsCompose(null); showToast('Text sent.'); }}
          />
        )}
      </TwilioVoiceProvider>

      {(
        // Full-bleed wrapper. Every view takes the entire viewport
        // width — no max-w-7xl cap and no sm:pl-24/pr-20 safe-zone
        // reserves. The floating widgets (⌘K + toast + SupportChat
        // circle on the right) float over content rather than
        // reserving margin, matching the FTPIG demo's edge-to-edge
        // layout.
        // Views that need to stay narrow (long-form editor forms,
        // reading columns) cap themselves internally with their
        // own max-w-* wrapper.
        <div className="w-full px-4 pb-32">
          {/* Fix #2 — Suspense boundary for the tab router. Every
              lazy-loaded view mounted below (Territory · Ledger ·
              Nudge · Health war-room, etc.) resolves through this
              one boundary. Fallback matches the empty-list vibe so
              a first-visit switch doesn't flash a hard skeleton. */}
          <React.Suspense fallback={<div className="text-xs text-slate-400 italic px-2 py-6">Loading…</div>}>
          {/* Launch-critical activation checklist. Renders ABOVE every
              tab because the default landing tab is Briefing, not Home,
              and a brand-new user shouldn't have to guess where to
              find their next setup step. Auto-hides at 3/3 done via
              the widget itself — activated users never see it. */}
          {/* You-are-here context: WORKSPACE › PRODUCT › PAGE, above every tab
              so Life / Health / Both is never ambiguous. */}
          <WorkspaceContextBar workspaceMode={workspaceMode} tab={tab} healthTabs={HEALTH_TABS}/>
          {!isDemoMode && <ActivationChecklist onGotoTab={setTab}/>}
          {tab === 'dial' && <CommandCenterView currentUser={currentUser} onGotoTab={setTab} showToast={showToast}/>}
          {/* tab === 'retain' now renders inside the unified Retention
              wrapper below (alongside Today's Saves + Trends & Analytics)
              so the standalone top-nav button and the Life Insurance
              sub-tab land in the SAME surface. */}
          {tab === 'scorecard' && <AgentScorecardView currentUser={currentUser} showToast={showToast}/>}
          {tab === 'home' && <DashboardHome calls={effectiveCalls} leads={effectiveLeads} setLeads={setLeads} apps={apps} transactions={transactions} calendar={effectiveCalendar} settings={settings} goalConfig={goalConfig} currentUser={currentUser} onGotoTab={setTab} showToast={showToast} acknowledgedWelcomes={acknowledgedWelcomes} setAcknowledgedWelcomes={setAcknowledgedWelcomes} isDemo={isDemoMode} nudgeCount={nudgeCount} openClientProfile={setActiveClientKey}/>}
          {tab === 'alerts' && <CriticalAlertsView apps={apps} transactions={transactions} leads={leads} producers={producers} settings={settings} currentUser={currentUser} getCarrier={getCarrier} onGotoTab={setTab} openClientProfile={setActiveClientKey}/>}
          {tab === 'policies' && <MyPoliciesView transactions={transactions} producers={producers} settings={settings} currentUser={currentUser} getCarrier={getCarrier} openClientProfile={setActiveClientKey}/>}
          {tab === 'leaderboard' && <TeamLeaderboardView transactions={transactions} producers={producers} customTeams={customTeams} settings={settings} currentUser={currentUser}/>}
          {tab === 'drips' && (
            <div>
              {/* Combined Automations header — Drips + Workflows in one place per operator 2026-07-14. */}
              <div className="max-w-6xl mx-auto px-4 pt-4">
                <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-xs bg-white">
                  <button
                    onClick={() => setTab('drips')}
                    className="px-4 py-2 font-bold uppercase tracking-tight bg-slate-900 text-white"
                  >
                    Drips
                  </button>
                  <button
                    onClick={() => setTab('workflows')}
                    className="px-4 py-2 font-bold uppercase tracking-tight text-slate-700 hover:bg-slate-50"
                  >
                    Workflows
                  </button>
                </div>
              </div>
              <SmsDripBuilder currentUser={currentUser} settings={settings} showToast={showToast}/>
            </div>
          )}
          {(tab === 'termuw' || tab === 'uw_agent') && (
            <div className="space-y-4">
              {/* Combined Underwriting surface — the AI agent (default
                  landing) takes the client's age / build / tobacco /
                  conditions and returns a ranked placement briefing,
                  so the operator doesn't have to mentally walk the
                  carrier field grids. The manual Carrier Placement
                  chip-picker is one click away as a fallback for cases
                  the AI doesn't cover yet. Both routes preserved:
                  /termuw → AI Agent sub-tab, /uw_agent → AI Agent
                  sub-tab too (palette + Agency-group deep links still
                  work). */}
              <div className="bg-white border border-slate-200 rounded-xl p-1 inline-flex gap-1">
                <button
                  onClick={() => setTab('uw_agent')}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                    tab === 'uw_agent'
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  AI Agent
                </button>
                <button
                  onClick={() => setTab('termuw')}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                    tab === 'termuw'
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  Carrier Placement
                </button>
              </div>
              {tab === 'uw_agent' && <UnderwritingAgentView currentUser={currentUser} showToast={showToast}/>}
              {tab === 'termuw'   && <TermCarrierPlacement/>}
            </div>
          )}
          {tab === 'health_dashboard' && <TabBoundary name="Health Dashboard"><HealthDashboardView currentUser={currentUser} settings={settings} onGotoTab={setTab} openClientProfile={setActiveClientKey}/></TabBoundary>}
          {tab === 'health_enroll' && <TabBoundary name="ENROLL"><EnrollAgentView currentUser={currentUser} onGotoTab={setTab} showToast={showToast}/></TabBoundary>}
          {tab === 'health_nurture' && <TabBoundary name="NOVA Nurture"><NovaNurtureView currentUser={currentUser} onGotoTab={setTab} showToast={showToast}/></TabBoundary>}
          {tab === 'health_policies' && (
            <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
              <HealthPoliciesView currentUser={currentUser} settings={settings} showToast={showToast} openClientProfile={setActiveClientKey} onGotoTab={setTab}/>
            </HealthShell>
          )}
          {tab === 'anchor' && (
            <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
              <AnchorIssuesView currentUser={currentUser} showToast={showToast}/>
            </HealthShell>
          )}
          {tab === 'ledger' && <LedgerDashboard currentUser={currentUser} showToast={showToast}/>}
          {tab === 'health_eligibility' && (
            <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
              <HealthEligibilityCheckerView onGotoTab={setTab}/>
            </HealthShell>
          )}
          {tab === 'health_at_risk' && (
            <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
              <AtRiskClientsView currentUser={currentUser} showToast={showToast} onGotoTab={setTab}/>
            </HealthShell>
          )}
          {tab === 'health_cross_sell' && (
            <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
              <CrossSellOpportunitiesView currentUser={currentUser} showToast={showToast} onGotoTab={setTab}/>
            </HealthShell>
          )}
          {tab === 'health_war_room' && (
            <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
              <HealthWarRoomView currentUser={currentUser} showToast={showToast} onGotoTab={setTab}/>
            </HealthShell>
          )}
          {tab === 'health_products' && (
            <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
              <HealthProductsReferenceView/>
            </HealthShell>
          )}
          {tab === 'health_plan_intel' && (
            <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
              <PlanIntelligenceView currentUser={currentUser} showToast={showToast}/>
            </HealthShell>
          )}
          {tab === 'health_reconciliation' && (
            <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
              <HealthReconciliationView currentUser={currentUser} settings={settings} showToast={showToast} onGotoTab={setTab} openClientProfile={setActiveClientKey}/>
            </HealthShell>
          )}
          {tab === 'health_commissions' && (
            <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
              <HealthCommissionsView currentUser={currentUser} settings={settings} showToast={showToast} onGotoTab={setTab} openClientProfile={setActiveClientKey}/>
            </HealthShell>
          )}
          {tab === 'health_lifecycle' && (
            <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
              <HealthLifecycleView currentUser={currentUser} settings={settings} showToast={showToast} onGotoTab={setTab}/>
            </HealthShell>
          )}
          {tab === 'life_lifecycle' && (
            <LifeLifecycleView currentUser={currentUser} showToast={showToast}/>
          )}
          {tab === 'crosssell_lifecycle' && (
            <CrossSellLifecycleView currentUser={currentUser} showToast={showToast}/>
          )}
          {tab === 'trigger_campaigns' && (
            <TriggerCampaignsView currentUser={currentUser} showToast={showToast}/>
          )}
          {tab === 'life_conservation' && (
            <LifeConservationView currentUser={currentUser} settings={settings} showToast={showToast} openClientProfile={setActiveClientKey}/>
          )}
          {tab === 'life_commissions' && (
            <LifeCommissionsView currentUser={currentUser} settings={settings} showToast={showToast} openClientProfile={setActiveClientKey}/>
          )}
          {tab === 'health_calendar' && (
            <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
              <CalendarView calendar={calendar} setCalendar={setCalendar} leads={effectiveLeads} setLeads={setLeads} producers={producers} settings={settings} openClientProfile={setActiveClientKey} showToast={showToast} clientProfiles={clientProfiles} allTransactions={transactions} currentUser={currentUser}/>
            </HealthShell>
          )}
          {tab === 'ai_agents' && (
            <AIAgentsView
              currentUser={currentUser}
              settings={settings}
              leads={leads}
              calls={calls}
              producers={producers}
              transactions={transactions}
              onGotoTab={setTab}
              showToast={showToast}
            />
          )}
          {tab === 'demos' && <DemosView currentUser={currentUser} showToast={showToast}/>}
          {tab === 'cross_sell' && <CrossSellSimulatorView currentUser={currentUser} settings={settings} showToast={showToast}/>}
          {tab === 'coverage_summary' && <CoverageSummaryView currentUser={currentUser} settings={settings} showToast={showToast}/>}
          {tab === 'health_clients' && (
            <TabBoundary name="Health Clients & Policies">
              <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
                <React.Suspense fallback={<div className="p-6 text-sm text-slate-500 italic">Loading clients…</div>}>
                  <HealthClientsListView
                    currentUser={currentUser}
                    showToast={showToast}
                    openClientProfile={setActiveClientKey}
                  />
                </React.Suspense>
              </HealthShell>
            </TabBoundary>
          )}
          {tab === 'medicare_command' && (
            <TabBoundary name="Medicare Command">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Medicare Command…</div>}>
                <MedicareCommandView
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  openClientProfile={setActiveClientKey}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'commission_intelligence' && (
            <TabBoundary name="Commission Intelligence">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Commission Intelligence…</div>}>
                <CommissionVarianceView
                  isDemo={isDemoMode}
                  onClose={() => setTab('health_commissions')}
                  openClientProfile={setActiveClientKey}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'integrations_health' && (
            <TabBoundary name="Integrations">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Checking integrations…</div>}>
                <IntegrationHealthView isDemo={isDemoMode} onClose={() => setTab('command_center')} />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'custom_agents' && (
            <TabBoundary name="AI Agent Builder">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading AI Agent Builder…</div>}>
                <CustomAgentsView isDemo={isDemoMode} onClose={() => setTab('ai_workforce')} />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'part_d_penalty' && (
            <TabBoundary name="Part D Penalty Calculator">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading calculator…</div>}>
                <PartDPenaltyView isDemo={isDemoMode} onClose={() => setTab('medicare_command')} />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'import_analyst' && (
            <TabBoundary name="AI Import Analyst">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Import Analyst…</div>}>
                <ImportAnalystView isDemo={isDemoMode} onClose={() => setTab('data_intelligence')} />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'soa_session' && (
            <TabBoundary name="Live SOA Session">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading SOA session…</div>}>
                <SoaSessionView isDemo={isDemoMode} onClose={() => setTab('soa_compliance')} onGotoTab={setTab} />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'aca_retention' && (
            <TabBoundary name="ACA Retention">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading ACA retention…</div>}>
                <AcaRetentionCenterView isDemo={isDemoMode} onClose={() => setTab('briefing')} />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'aca_compliance' && (
            <TabBoundary name="ACA Compliance">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading ACA compliance…</div>}>
                <AcaComplianceView isDemo={isDemoMode} onClose={() => setTab('briefing')} onOpenClient={(cid) => setActiveClientKey(cid)} />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'lapse_watch' && (
            <TabBoundary name="Lapse Watch">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading lapse watch…</div>}>
                <LapseWatchView isDemo={isDemoMode} onClose={() => setTab('briefing')} />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'plan_intake' && (
            <TabBoundary name="Plan Intake">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading plan intake…</div>}>
                <PlanIntakeView isDemo={isDemoMode} onClose={() => setTab('briefing')} />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'product_readiness' && (
            <TabBoundary name="Product Readiness">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading QA audit…</div>}>
                <ProductReadinessView isDemo={isDemoMode} onClose={() => setTab('command_center')} />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'policy_access' && (
            <TabBoundary name="Policy Access">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading policy access…</div>}>
                <PolicyAccessView isDemo={isDemoMode} onClose={() => setTab('briefing')} onGotoTab={setTab} />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'carrier_portals' && (
            <TabBoundary name="Carrier Portal Manager">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading carrier portals…</div>}>
                <CarrierPortalManagerView isDemo={isDemoMode} onClose={() => setTab('policy_access')} />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'soa_evidence' && (
            <TabBoundary name="SOA Evidence Package">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading evidence…</div>}>
                <SoaEvidenceView isDemo={isDemoMode} onClose={() => setTab('soa_compliance')} />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'soa_qa' && (
            <TabBoundary name="SOA QA Review">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading SOA QA…</div>}>
                <SoaQaView isDemo={isDemoMode} onClose={() => setTab('soa_compliance')} />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'soa_scripts' && (
            <TabBoundary name="SOA Script Library">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading SOA scripts…</div>}>
                <SoaScriptsView isDemo={isDemoMode} onClose={() => setTab('soa_compliance')} />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'soa_compliance' && (
            <TabBoundary name="SOA Compliance Guard">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading SOA compliance…</div>}>
                <SoaComplianceView
                  isDemo={isDemoMode}
                  onClose={() => setTab('medicare_command')}
                  openClientProfile={(id) => { setActiveClientKey(id); setTab('smart_profile'); }}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'medicare_reviews' && (
            <TabBoundary name="Annual Plan Reviews">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading annual reviews…</div>}>
                <MedicareReviewsView
                  isDemo={isDemoMode}
                  onClose={() => setTab('medicare_command')}
                  openClientProfile={(id) => { setActiveClientKey(id); setTab('smart_profile'); }}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'attention' && (
            <TabBoundary name="What Needs Attention">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Scanning the agency…</div>}>
                <AttentionCommandView
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'find_care' && (
            <TabBoundary name="Find Care">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Find Care…</div>}>
                <FindCareView isDemo={isDemoMode} onClose={() => setTab('command_center')} />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'coverage_guide' && (
            <TabBoundary name="Coverage & Care Guide">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Coverage &amp; Care Guide…</div>}>
                <CoverageGuideView
                  isDemo={isDemoMode}
                  clientId={coverageGuideClientId}
                  onClose={() => setTab('command_center')}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'medicare_plan_intelligence' && (
            <TabBoundary name="Medicare Plan Intelligence">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Medicare Plan Intelligence…</div>}>
                <MedicarePlanIntelligenceView
                  isDemo={isDemoMode}
                  onClose={() => setTab('medicare_command')}
                  setTab={setTab}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'medicare_client_profile' && (
            <TabBoundary name="Medicare Client Profile">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Medicare Client Profile…</div>}>
                <MedicareClientProfileView
                  isDemo={isDemoMode}
                  onClose={() => setTab('medicare_command')}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'medicare_connections' && (
            <TabBoundary name="Medicare Data & API Connections">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Medicare Data & API…</div>}>
                <MedicareDataConnectionsView
                  isDemo={isDemoMode}
                  onClose={() => setTab('medicare_command')}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'medicare_benefits' && (
            <TabBoundary name="Medicare Client Services">
              <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
                <React.Suspense fallback={<div className="p-6 text-sm text-slate-500 italic">Loading Medicare Client Services…</div>}>
                  <MedicareBenefitsView
                    currentUser={currentUser}
                    onGotoTab={setTab}
                    openClientProfile={setActiveClientKey}
                    showToast={showToast}
                  />
                </React.Suspense>
              </HealthShell>
            </TabBoundary>
          )}
          {tab === 'medicare_sep' && (
            <TabBoundary name="Medicare SEP Tracker">
              <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
                <React.Suspense fallback={<div className="p-6 text-sm text-slate-500 italic">Loading SEP Tracker…</div>}>
                  <MedicareSEPTrackerView currentUser={currentUser} onGotoTab={setTab} openClientProfile={setActiveClientKey} showToast={showToast}/>
                </React.Suspense>
              </HealthShell>
            </TabBoundary>
          )}
          {tab === 'medicare_referrals' && (
            <TabBoundary name="Referrals & Satisfaction">
              <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
                <React.Suspense fallback={<div className="p-6 text-sm text-slate-500 italic">Loading Referrals & Satisfaction…</div>}>
                  <MedicareReferralsView currentUser={currentUser} onGotoTab={setTab} openClientProfile={setActiveClientKey} showToast={showToast}/>
                </React.Suspense>
              </HealthShell>
            </TabBoundary>
          )}
          {tab === 'medicare_twin' && (
            <TabBoundary name="Medicare Digital Twin">
              <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
                <React.Suspense fallback={<div className="p-6 text-sm text-slate-500 italic">Loading Digital Twin…</div>}>
                  <MedicareDigitalTwinView currentUser={currentUser} onGotoTab={setTab} openClientProfile={setActiveClientKey} showToast={showToast}/>
                </React.Suspense>
              </HealthShell>
            </TabBoundary>
          )}
          {tab === 'compliance_readiness' && (
            <TabBoundary name="Compliance Readiness Meter">
              <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
                <React.Suspense fallback={<div className="p-6 text-sm text-slate-500 italic">Scoring readiness…</div>}>
                  <ComplianceReadinessLandingView currentUser={currentUser} onGotoTab={setTab} openClientProfile={setActiveClientKey} showToast={showToast}/>
                </React.Suspense>
              </HealthShell>
            </TabBoundary>
          )}
          {tab === 'signature_health' && (
            <TabBoundary name="Signature Health">
              <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
                <React.Suspense fallback={<div className="p-6 text-sm text-slate-500 italic">Loading Signature Health…</div>}>
                  <SignatureHealthView currentUser={currentUser} onGotoTab={setTab} openClientProfile={setActiveClientKey} showToast={showToast}/>
                </React.Suspense>
              </HealthShell>
            </TabBoundary>
          )}
          {tab === 'compliance_ops' && (
            <TabBoundary name="Compliance Ops">
              <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
                <React.Suspense fallback={<div className="p-6 text-sm text-slate-500 italic">Loading Compliance Ops…</div>}>
                  <ComplianceOpsView currentUser={currentUser} onGotoTab={setTab} showToast={showToast}/>
                </React.Suspense>
              </HealthShell>
            </TabBoundary>
          )}
          {tab === 'compliance_intel' && (
            <TabBoundary name="Compliance Intelligence">
              <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
                <React.Suspense fallback={<div className="p-6 text-sm text-slate-500 italic">Loading Compliance Intelligence…</div>}>
                  <ComplianceIntelligenceView currentUser={currentUser} onGotoTab={setTab} openClientProfile={setActiveClientKey} showToast={showToast}/>
                </React.Suspense>
              </HealthShell>
            </TabBoundary>
          )}
          {tab === 'audit_room' && (
            <TabBoundary name="One-Click Audit Room">
              <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
                <React.Suspense fallback={<div className="p-6 text-sm text-slate-500 italic">Loading Audit Room…</div>}>
                  <OneClickAuditRoomView currentUser={currentUser} showToast={showToast} clientNotes={clientNotes}/>
                </React.Suspense>
              </HealthShell>
            </TabBoundary>
          )}
          {tab === 'client_success' && (
            <TabBoundary name="Client Success Manager">
              <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
                <React.Suspense fallback={<div className="p-6 text-sm text-slate-500 italic">Loading Client Success Manager…</div>}>
                  <ClientSuccessManagerView currentUser={currentUser} onGotoTab={setTab} openClientProfile={setActiveClientKey} showToast={showToast}/>
                </React.Suspense>
              </HealthShell>
            </TabBoundary>
          )}
          {tab === 'life_book_manager' && (
            <TabBoundary name="Life Book Manager">
              <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
                <React.Suspense fallback={<div className="p-6 text-sm text-slate-500 italic">Loading Life Book Manager…</div>}>
                  <LifeBookManagerView currentUser={currentUser} onGotoTab={setTab} openClientProfile={setActiveClientKey} showToast={showToast}/>
                </React.Suspense>
              </HealthShell>
            </TabBoundary>
          )}
          {tab === 'health_leads' && <TabBoundary name="Health Leads">
            <React.Suspense fallback={null}><HealthPriorityStrip leads={effectiveLeads} onGotoTab={setTab}/></React.Suspense>
            <HealthLeadsView leads={effectiveLeads} setLeads={setLeads} outreach={outreach} setOutreach={setOutreach} calls={effectiveCalls} setCalls={setCalls} calendar={effectiveCalendar} setCalendar={setCalendar} leadTemplates={leadTemplates} setLeadTemplates={setLeadTemplates} settings={settings} producers={producers} producerList={producerList} currentUser={currentUser} showToast={showToast} workflows={workflows} onGotoTab={setTab} transactions={transactions}/></TabBoundary>}
          {tab === 'health_inbox' && <TabBoundary name="Health Inbox"><HealthInboxView leads={effectiveLeads} setLeads={setLeads} calls={effectiveCalls} settings={settings} currentUser={currentUser} inboxReadState={inboxReadState} setInboxReadState={setInboxReadState} onGotoTab={setTab} onOpenLeadProfile={(id) => { setActiveClientKey(id); }}/></TabBoundary>}
          {tab === 'health_enrollment' && <TabBoundary name="Health Enrollment Pipeline"><HealthEnrollmentPipelineView currentUser={currentUser} settings={settings} onGotoTab={setTab} showToast={showToast} openClientProfile={setActiveClientKey}/></TabBoundary>}
          {tab === 'health_consents' && (
            <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
              <React.Suspense fallback={<div className="p-6 text-sm text-slate-500 italic">Loading Compliance…</div>}>
                <HealthComplianceLandingView
                  currentUser={currentUser}
                  onGotoTab={setTab}
                  openClientProfile={setActiveClientKey}
                  showToast={showToast}
                />
              </React.Suspense>
            </HealthShell>
          )}
          {tab === 'health_renewals' && (
            <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
              <React.Suspense fallback={<div className="p-6 text-sm text-slate-500 italic">Loading Renewals…</div>}>
                <HealthRenewalsLandingView
                  currentUser={currentUser}
                  onGotoTab={setTab}
                  openClientProfile={setActiveClientKey}
                  showToast={showToast}
                />
              </React.Suspense>
            </HealthShell>
          )}
          {tab === 'health_opportunities' && (
            <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
              <React.Suspense fallback={<div className="p-6 text-sm text-slate-500 italic">Loading Opportunities…</div>}>
                <HealthOpportunitiesLandingView
                  currentUser={currentUser}
                  onGotoTab={setTab}
                  openClientProfile={setActiveClientKey}
                  showToast={showToast}
                />
              </React.Suspense>
            </HealthShell>
          )}
          {tab === 'health_audit' && (
            <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
              <HealthAuditLogView currentUser={currentUser}/>
            </HealthShell>
          )}
          {tab === 'health_workflows' && (
            <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
              <HealthWorkflowsView currentUser={currentUser} showToast={showToast}/>
            </HealthShell>
          )}
          {tab === 'health_portal' && (
            <TabBoundary name="Health Dashboard">
              <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
                <React.Suspense fallback={<div className="p-6 text-sm text-slate-500 italic">Loading Command Center…</div>}>
                  <HealthCommandCenterView currentUser={currentUser} onGotoTab={setTab} showToast={showToast}/>
                </React.Suspense>
              </HealthShell>
            </TabBoundary>
          )}
          {tab === 'health_briefing' && (
            <TabBoundary name="Morning Briefing">
              <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
                <React.Suspense fallback={<div className="p-6 text-sm text-slate-500 italic">Loading briefing…</div>}>
                  <MorningBriefingView currentUser={currentUser} onGotoTab={setTab} openClientProfile={setActiveClientKey} showToast={showToast}/>
                </React.Suspense>
              </HealthShell>
            </TabBoundary>
          )}
          {tab === 'ceo_briefing' && (
            <TabBoundary name="AI Chief of Staff">
              <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
                <React.Suspense fallback={<div className="p-6 text-sm text-slate-500 italic">Loading boardroom…</div>}>
                  <AiCeoBoardroom onGotoTab={setTab} showToast={showToast}/>
                </React.Suspense>
              </HealthShell>
            </TabBoundary>
          )}
          {tab === 'war_room' && (
            <TabBoundary name="War Room">
              <React.Suspense fallback={<div className="p-6 text-sm text-slate-500 italic">Loading war room…</div>}>
                <WarRoomView onGotoTab={setTab} currentUser={currentUser}/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'ask_aria' && (
            <TabBoundary name="Ask ARIA">
              <HealthShell activeTab={tab} onGotoTab={setTab} currentUser={currentUser} leads={effectiveLeads} showToast={showToast}>
                <React.Suspense fallback={<div className="p-6 text-sm text-slate-500 italic">Loading ARIA…</div>}>
                  <AskAriaView showToast={showToast}/>
                </React.Suspense>
              </HealthShell>
            </TabBoundary>
          )}
          {tab === 'expenses' && <ExpensesView currentUser={currentUser} showToast={showToast}/>}
          {tab === 'daily_tracker' && <DailyTrackerView currentUser={currentUser} showToast={showToast}/>}
          {tab === 'soa' && <SOATrackingView currentUser={currentUser} showToast={showToast}/>}
          {tab === 'audit' && <AuditPanel currentUser={currentUser} showToast={showToast}/>}
          {tab === 'customers' && currentUser?.role === 'admin' && !demoFlag.isDemo && <AdminCustomersView currentUser={currentUser} showToast={showToast} onViewAs={(user) => { setViewingAs({ userId: user.id, email: user.email }); setTab('briefing'); }}/>}
          {tab === 'observability' && currentUser?.role === 'admin' && !demoFlag.isDemo && <TabBoundary name="Observability"><ObservabilityView/></TabBoundary>}
          {tab === 'nudge' && (
            <TabBoundary name="NUDGE">
              <NudgePanel currentUser={currentUser} settings={settings} showToast={showToast}/>
            </TabBoundary>
          )}
          {tab === 'territory' && (
            <TabBoundary name="Territory Map">
              <TerritoryMap currentUser={currentUser} showToast={showToast}/>
            </TabBoundary>
          )}
          {tab === 'book_of_business' && (
            <TabBoundary name="Book of Business">
              <React.Suspense fallback={<div className="p-8 text-slate-400">Loading Book of Business…</div>}>
                <BookOfBusinessCommandCenter onGotoTab={(hrefOrTab) => {
                  if (typeof hrefOrTab === 'string' && hrefOrTab.startsWith('/')) {
                    const t = hrefOrTab.split('?')[0].replace('/', '') || 'home';
                    setTab(t);
                  } else if (hrefOrTab) {
                    setTab(hrefOrTab);
                  }
                }}/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'follow_up' && (
            <TabBoundary name="Follow-Up Manager">
              <React.Suspense fallback={<div className="p-8 text-slate-400">Loading follow-up…</div>}>
                <FollowUpDashboard
                  currentUser={currentUser}
                  workspaceMode={workspaceMode}
                  onGotoTab={setTab}
                  openClientProfile={(id) => setActiveClientKey(id)}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'smart_profile' && activeClientKey && (
            <TabBoundary name="Client Room">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Assembling the client room…</div>}>
                <ClientRoom
                  clientId={activeClientKey}
                  onClose={() => { setActiveClientKey(null); setTab('client_intelligence'); }}
                  isDemo={isDemoMode}
                  onGotoTab={setTab}
                  onOpenCoverageGuide={(id) => { setCoverageGuideClientId(id); setTab('coverage_guide'); }}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'lead_profile' && activeLeadKey && (
            <TabBoundary name="Lead Profile">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading lead…</div>}>
                <LeadRoom
                  leadId={activeLeadKey}
                  isDemo={isDemoMode}
                  onClose={() => { setActiveLeadKey(null); setTab('leads_intelligence'); }}
                  onGotoTab={setTab}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'health_command_center' && (
            <TabBoundary name="Health Command Center">
              <React.Suspense fallback={<div className="p-8 text-slate-400">Loading Command Center…</div>}>
                <HealthCommandCenter
                  currentUser={currentUser}
                  onGotoTab={setTab}
                  openClientProfile={(id) => { setActiveClientKey(id); setTab('smart_profile'); }}
                  onStartFocusMode={(mission) => setFocusMission(mission)}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'cockpit_inbox' && (
            <TabBoundary name="Cockpit Inbox">
              <React.Suspense fallback={<div className="p-8 text-slate-400">Loading Inbox…</div>}>
                <CockpitInbox
                  currentUser={currentUser}
                  workspaceMode={workspaceMode}
                  onGotoTab={setTab}
                  openClientProfile={(id) => { setActiveClientKey(id); setTab('smart_profile'); }}
                  isDemo={isDemoMode}
                  demoThreads={demoThreadsForInbox}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'sales_command_center' && (
            <TabBoundary name="Sales Command Center">
              <React.Suspense fallback={<div className="p-8 text-slate-400">Loading…</div>}>
                <SalesCommandCenter
                  currentUser={currentUser}
                  onGotoTab={setTab}
                  openClientProfile={(id) => { setActiveClientKey(id); setTab('smart_profile'); }}
                  onStartFocusMode={(mission) => setFocusMission(mission)}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'client_success_center' && (
            <TabBoundary name="Client Success">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#070709', color: '#8A9099', padding: 32 }}>Loading Client Success…</div>}>
                <WelcomeCallCenter/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'life_beneficiaries' && (
            <TabBoundary name="Life Beneficiaries">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#070709', color: '#8A9099', padding: 32 }}>Loading Beneficiaries…</div>}>
                <LifeBeneficiariesPanel currentUser={currentUser}/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'medicare_plan_details' && (
            <TabBoundary name="Medicare Plan Details">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#070709', color: '#8A9099', padding: 32 }}>Loading Plan Details…</div>}>
                <MedicarePlanDetailsPanel currentUser={currentUser}/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'workflow_command' && (
            <TabBoundary name="Workflow Command Center">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#070709', color: '#8A9099', padding: 32 }}>Loading Workflow Command…</div>}>
                <WorkflowCommandCenter/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'approvals' && (
            <TabBoundary name="Approvals">
              <div style={{ minHeight: '100vh', background: '#070709', padding: '30px 24px' }}>
                <div style={{ maxWidth: 840, margin: '0 auto' }}>
                  <div style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace', fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#FFC457', fontWeight: 700, marginBottom: 8 }}>
                    Approvals
                  </div>
                  <h1 style={{ fontSize: 28, fontWeight: 800, color: '#F4F4F2', margin: '0 0 6px', letterSpacing: '-0.01em' }}>
                    Needs your authorization
                  </h1>
                  <div style={{ fontSize: 13.5, color: '#A7ADB5', lineHeight: 1.5, marginBottom: 24, maxWidth: 620 }}>
                    High-impact actions your AI workforce paused for your sign-off — submitting A2P, buying a number, sending a bulk message. Approve or reject; each writes the real decision and the agent’s next step follows it.
                  </div>
                  <ApprovalCenter/>
                </div>
              </div>
            </TabBoundary>
          )}
          {tab === 'ai_staff' && (
            <TabBoundary name="AI Staff">
              <AiStaffCenter onGotoTab={(hrefOrTab) => {
                // Registry entries return either a full route ('/health') or a tab id.
                // BUG FIX 2026-07-14: registry surfaces use hyphens
                // ('/follow-up', '/life-uw') but tab keys use underscores
                // (follow_up, life_uw). Prior handler stripped the '/' but
                // left hyphens, producing invalid tab keys that the
                // workspace-mode redirect then bounced to briefing. Reported:
                // "AI Team → AI Employees → glitches to Prospecting."
                // Convert hyphens to underscores + a few explicit aliases.
                const ALIAS = {
                  'life-uw':   'termuw',      // registry surface → real tab
                  'life_uw':   'termuw',
                  'follow-up': 'follow_up',
                  'health':    'health_portal',
                  'home':      'home',
                  'drips':     'drips',
                  'retain':    'retain',
                  'referrals': 'referrals',
                };
                if (typeof hrefOrTab === 'string' && hrefOrTab.startsWith('/')) {
                  const raw = hrefOrTab.replace('/', '') || 'home';
                  const normalized = raw.replace(/-/g, '_');
                  setTab(ALIAS[raw] || ALIAS[normalized] || normalized);
                } else if (hrefOrTab) {
                  setTab(ALIAS[hrefOrTab] || hrefOrTab);
                }
              }}/>
            </TabBoundary>
          )}
          {tab === 'atlas' && (
            <TabBoundary name="ATLAS agent">
              <AtlasAgentView currentUser={currentUser} showToast={showToast} onGotoTab={setTab}/>
            </TabBoundary>
          )}
          {tab === 'uplift' && (
            <TabBoundary name="UPLIFT agent">
              <UpliftAgentView currentUser={currentUser} showToast={showToast} onGotoTab={setTab}/>
            </TabBoundary>
          )}
          {tab === 'registrar' && (
            <TabBoundary name="REGISTRAR · A2P setup">
              <A2pSetupView currentUser={currentUser} settings={settings} showToast={showToast}/>
            </TabBoundary>
          )}
          {tab === 'os_health' && (
            <TabBoundary name="OS Health">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#070809', color: '#999D9F', padding: 32 }}>Running health checks…</div>}>
                <OsHealthView/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'telephony_health' && (
            <TabBoundary name="Telephony Health">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#070809', color: '#999D9F', padding: 32 }}>Probing subscriber numbers…</div>}>
                <TelephonyHealthView/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'phone_messaging' && (
            <TabBoundary name="Phone & Messaging">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#070809', color: '#999D9F', padding: 32 }}>Checking your phone…</div>}>
                <PhoneMessagingStatus onGotoTab={setTab}/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'funnel_b' && (
            <TabBoundary name="Funnel B · speed-to-lead">
              <FunnelBView currentUser={currentUser} showToast={showToast}/>
            </TabBoundary>
          )}
          {tab === 'mission' && <MissionView goalConfig={goalConfig} calls={calls} leads={leads} settings={settings} transactions={transactions} producers={producers} currentUser={currentUser} onGotoTab={setTab} onOpenPowerDialer={(lens) => { setPowerDialerLens(lens || 'all'); setPowerDialerOpen(true); }}/>}
          {(tab === 'retention' || tab === 'persistency' || tab === 'retain') && (
            <div className="space-y-4">
              {/* Unified Retention surface (2026-06-26): three sub-tabs
                  under one "Retention" umbrella so the operator stops
                  bouncing between the standalone 🛡 RETAIN agent button
                  in the top nav and the "Retention" sub-tab inside Life
                  Insurance. The standalone now routes here too and
                  defaults to the agent dashboard. Deep links to
                  /retain, /retention, /persistency all preserved. */}
              <div className="bg-white border border-slate-200 rounded-xl p-1 inline-flex gap-1 flex-wrap">
                <button
                  onClick={() => setTab('retain')}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                    tab === 'retain'
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  🛡 RETAIN agent
                </button>
                <button
                  onClick={() => setTab('retention')}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                    tab === 'retention'
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  Today's Saves
                </button>
                <button
                  onClick={() => setTab('persistency')}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                    tab === 'persistency'
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  Trends & Analytics
                </button>
              </div>
              {tab === 'retain' && (
                <TabBoundary name="RETAIN agent">
                  <RetainAgentView currentUser={currentUser} showToast={showToast}/>
                </TabBoundary>
              )}
              {tab === 'retention' && (
                <TabBoundary name="Retention">
                  <RetentionHub transactions={transactions} leads={leads} setLeads={setLeads} clientProfiles={clientProfiles} conservation={conservation} calls={calls} outreach={outreach} settings={settings} producers={producers} currentUser={currentUser} saveTasks={saveTasks} setSaveTasks={setSaveTasks} onGotoTab={setTab}/>
                </TabBoundary>
              )}
              {tab === 'persistency' && (
                <TabBoundary name="Trends & Analytics">
                  <PersistencyView transactions={transactions} producers={producers} settings={settings} leads={leads} calls={calls} outreach={outreach} openClientProfile={setActiveClientKey}/>
                </TabBoundary>
              )}
            </div>
          )}
          {tab === 'inbox' && (
            // Unified inbox — founder feedback 2026-07-12: top-nav "Inbox"
            // and sidebar "Conversations" were rendering different
            // components (SmsInboxView vs CockpitInbox), causing two
            // different UIs for what should be one surface. Route the
            // classic #tab=inbox URL to the same modern component the
            // sidebar uses. Health workspace lands on HealthInboxView;
            // Life + Both land on CockpitInbox. Old bookmarks still work.
            workspaceMode === 'health' ? (
              <TabBoundary name="Health Inbox">
                <HealthInboxView leads={effectiveLeads} setLeads={setLeads} calls={effectiveCalls} settings={settings} currentUser={currentUser} inboxReadState={inboxReadState} setInboxReadState={setInboxReadState} onGotoTab={setTab} onOpenLeadProfile={(id) => { setActiveClientKey(id); }}/>
              </TabBoundary>
            ) : (
              <TabBoundary name="Inbox">
                <React.Suspense fallback={<div className="p-8 text-slate-400">Loading Inbox…</div>}>
                  <CockpitInbox
                    currentUser={currentUser}
                    workspaceMode={workspaceMode}
                    onGotoTab={setTab}
                    openClientProfile={(id) => { setActiveClientKey(id); setTab('smart_profile'); }}
                    isDemo={isDemoMode}
                    demoThreads={demoThreadsForInbox}
                  />
                </React.Suspense>
              </TabBoundary>
            )
          )}
          {tab === 'pnl' && (
            <TabBoundary name="Executive Boardroom">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Aggregating reports…</div>}>
                <ReportsRoom
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'pnl_legacy' && (
            <Room floor="growth" number={9} title="Executive Boardroom" tagline="Real data. Real growth.">
              <CFODashboard transactions={transactions} producers={producers} producerList={producerList} settings={settings} goalConfig={goalConfig} leads={leads} calls={calls} financialConfig={financialConfig} setFinancialConfig={setFinancialConfig} openClientProfile={setActiveClientKey} onGotoTab={setTab} openAccountant={() => setAccountantOpen(true)}/>
            </Room>
          )}
          {tab === 'apps' && <AppsPipeline apps={apps} setApps={setApps} leads={leads} producers={producers} producerList={producerList} settings={settings} currentUser={currentUser} onGotoTab={setTab}/>}
          {tab === 'paycalc' && <PayCalculatorView settings={settings}/>}
          {tab === 'clients' && <ClientsAndPoliciesView currentUser={currentUser} showToast={showToast} transactions={transactions} settings={settings} getCarrier={getCarrier} producers={producers} onGotoTab={setTab} workspaceMode={workspaceMode} demoClients={demoClientsForBook}/>}
          {tab === 'conversations' && <ConversationsView currentUser={currentUser} showToast={showToast}/>}
          {tab === 'referrals' && (
            <React.Suspense fallback={<div className="p-8 text-slate-400">Loading Referral Command Center…</div>}>
              <ReferralCommandCenter/>
            </React.Suspense>
          )}
          {/* Legacy ReferralsView flat-list defined below (function
              ReferralsView) — restore the flat view by swapping the JSX
              above back to <ReferralsView currentUser={...} .../> if
              the tree endpoint has an issue in prod. */}
          {tab === 'overrides_iq' && <OverrideIntelligence transactions={transactions} producers={producers} setProducers={setProducers} producerList={producerList} settings={settings} financialConfig={financialConfig} setFinancialConfig={setFinancialConfig} getCarrier={getCarrier} openClientProfile={setActiveClientKey} onGotoTab={setTab}/>}
          {tab === 'briefing' && <BriefingView transactions={transactions} producers={producers} producerList={producerList} settings={settings} clientProfiles={clientProfiles} leads={leads} calls={calls} conservation={conservation} openClientProfile={setActiveClientKey} onGotoTab={setTab}/>}
          {tab === 'deal_room' && (
            selectedLeadId
              ? <DealRoomPanel leadId={selectedLeadId} onClose={() => setTab('inbox')} />
              : <div className="p-8 text-center text-slate-500">
                  <div className="text-lg font-bold text-slate-700 mb-2">Deal Room</div>
                  <div className="text-sm">Open a lead from the Inbox or Leads tab to see their Deal Room.</div>
                  <button onClick={() => setTab('inbox')} className="mt-4 bg-slate-900 text-white text-sm font-semibold px-4 py-2 rounded-md">Go to Inbox</button>
                </div>
          )}
          {tab === 'follow_up' && (
            <FollowUpPanel
              onSelectLead={(leadId) => { setSelectedLeadId(leadId); setTab('deal_room'); }}
              onGotoInbox={() => setTab('inbox')}
            />
          )}
          {tab === 'ai_boss' && (
            <React.Suspense fallback={<div className="p-6 text-sm text-slate-500">Loading AI Boss…</div>}>
              <AIBossPanel />
            </React.Suspense>
          )}
          {tab === 'launch_readiness' && currentUser?.role === 'admin' && (
            <React.Suspense fallback={<div className="p-6 text-sm text-slate-500">Loading launch readiness…</div>}>
              <LaunchReadinessPanel />
            </React.Suspense>
          )}
          {tab === 'launch_readiness' && currentUser?.role !== 'admin' && (
            <div className="max-w-none mx-auto p-8 text-center">
              <div className="text-lg font-bold text-slate-700 mb-2">Launch Readiness</div>
              <div className="text-sm text-slate-500">Platform administrator role required.</div>
            </div>
          )}
          {tab === 'book_snapshot' && (
            <React.Suspense fallback={<div className="p-6 text-sm text-slate-500">Loading book snapshot…</div>}>
              <BookSnapshotPanel />
            </React.Suspense>
          )}
          {tab === 'command' && (
            <React.Suspense fallback={<div className="min-h-screen bg-[#0a0c10] p-8 text-slate-400">Loading Command Center…</div>}>
              <CommandDashboard onGotoTab={setTab} onOpenLead={(phone) => { /* wire selectedLeadId lookup later */ setTab('inbox'); }} />
            </React.Suspense>
          )}
          {tab === 'power_dialer' && (
            <React.Suspense fallback={<div className="min-h-screen bg-[#0a0c10] p-8 text-slate-400">Loading Power Dialer…</div>}>
              <PowerDialerView onGotoTab={setTab} />
            </React.Suspense>
          )}
          {tab === 'phone' && (
            <TabBoundary name="Phone Command">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Assembling Communications Command…</div>}>
                <PhoneCommandRoom
                  currentUser={currentUser}
                  onGotoTab={setTab}
                  openClientProfile={(id) => { setActiveClientKey(id); setTab('smart_profile'); }}
                  isDemo={isDemoMode}
                  leads={effectiveLeads}
                  appointments={effectiveCalendar}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'phone_legacy' && (
            <Room floor="calls" number={4} title="Communications Command" tagline="Power dialer. Live calls. AI coaching." personaQuote="I'm listening on every call.">
              <React.Suspense fallback={<div className="min-h-screen p-8 text-slate-400" style={{ background: 'transparent' }}>Loading Phone…</div>}>
                <PhoneDialer
                  currentUser={currentUser}
                  onGotoTab={setTab}
                  openClientProfile={(id) => { setActiveClientKey(id); setTab('smart_profile'); }}
                />
              </React.Suspense>
            </Room>
          )}
          {tab === 'launch_readiness' && <LaunchReadinessView showToast={showToast}/>}
          {tab === 'aca_imports' && <AcaImportsView showToast={showToast}/>}
          {tab === 'call_activity_audit' && <CallActivityAuditView showToast={showToast}/>}
          {tab === 'support_lookup' && <SupportLookupView showToast={showToast}/>}
          {tab === 'new_signups' && <NewSignupsView showToast={showToast} onGotoTab={setTab}/>}
          {tab === 'system_diagnostics' && <SystemDiagnosticsView showToast={showToast}/>}
          {tab === 'va_workforce' && (<>
            <TabBoundary name="AI Workforce"><React.Suspense fallback={null}><AiWorkforceHqLegacy onGotoTab={setTab}/></React.Suspense></TabBoundary>
            <VaWorkforceView showToast={showToast}/>
          </>)}
          {tab === 'operations_floor' && (
            <TabBoundary name="AI Workforce">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Reading the registry…</div>}>
                <AiWorkforceRoom
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'operations_floor_legacy' && (
            <TabBoundary name="Operations Floor (legacy)">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709' }}/>}>
                <HqOperationsFloorLive
                  onExit={() => setTab('command_center')}
                  onGotoTab={(t) => {
                    const routes = pickCockpitRoutes(workspaceMode);
                    setTab(routes[t] || t);
                  }}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'money_command' && (
            <TabBoundary name="Money Command">
              <MoneyCommandView currentUser={currentUser}/>
            </TabBoundary>
          )}
          {tab === 'response_speed' && (
            <TabBoundary name="Response Speed">
              <ResponseSpeedView/>
            </TabBoundary>
          )}
          {tab === 'missed_ops' && (
            <TabBoundary name="Missed Opportunities">
              <MissedOpsView/>
            </TabBoundary>
          )}
          {tab === 'goal_planner' && (
            <TabBoundary name="Goal Planner">
              <GoalPlannerView/>
            </TabBoundary>
          )}
          {tab === 'comp_setup' && (
            <TabBoundary name="Comp Setup">
              <CompSetupView/>
            </TabBoundary>
          )}
          {tab === 'licensing' && (
            <TabBoundary name="Licensing & Carriers">
              <LicensingView/>
            </TabBoundary>
          )}
          {tab === 'a2p_setup' && (
            <TabBoundary name="A2P Registration">
              <div style={{ padding: 24, minHeight: '100vh', background: 'transparent' }}>
                <React.Suspense fallback={<div className="text-slate-400 text-sm p-4">Loading A2P registration…</div>}>
                  <A2pRegistrationStep onDone={() => {
                    // Route to workspace-appropriate default so health-mode
                    // operators don't get bounced by the workspaceMode guard.
                    if (workspaceMode === 'health') setTab('command_center');
                    else setTab('command_center');
                  }}/>
                </React.Suspense>
              </div>
            </TabBoundary>
          )}
          {tab === 'command_center' && (
            <TabBoundary name="Command Center">
              <React.Suspense fallback={<div className="min-h-screen p-8" style={{ background: '#050709', color: '#7C838C' }}>Assembling the headquarters…</div>}>
                <CommandCenterGrid
                  currentUser={currentUser}
                  transactions={transactions}
                  leads={leads}
                  calls={calls}
                  apps={apps}
                  calendar={calendar}
                  onGotoTab={setTab}
                  isDemo={isDemoMode}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'command_briefing' && (
            <Room floor="command" number={1} title="Executive Briefing" tagline="Deeper look. ARIA's morning read.">
              <TabBoundary name="Command Briefing">
                <React.Suspense fallback={<div className="min-h-screen p-8 text-slate-400" style={{ background: 'transparent' }}>Assembling your agency briefing…</div>}>
                  <AgencyCommandCenterView workspaceMode={workspaceMode} onGotoTab={setTab} transactions={transactions} conservation={conservation}/>
                </React.Suspense>
              </TabBoundary>
            </Room>
          )}
          {tab === 'leads_intelligence' && (
            <Room floor="sales" number={2} title="Sales Intelligence Room" tagline="Find. Score. Prioritize." personaQuote="I'm watching every source. When intent moves, you'll be the first to know.">
              <TabBoundary name="Leads Intelligence">
                <React.Suspense fallback={<div className="min-h-screen p-8 text-slate-400" style={{ background: 'transparent' }}>Reading your lead signals…</div>}>
                  <LeadsIntelligenceView workspaceMode={workspaceMode} onGotoTab={setTab}/>
                </React.Suspense>
              </TabBoundary>
            </Room>
          )}
          {tab === 'messages_intelligence' && (
            <TabBoundary name="Messages Room">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading threads…</div>}>
                <MessagesRoom
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                  openClientProfile={(id) => { setActiveClientKey(id); setTab('smart_profile'); }}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'messages_legacy' && (
            <Room floor="communications" number={5} title="Messaging Operations" tagline="All SMS, email, and follow-ups.">
              <TabBoundary name="Messages Intelligence (legacy)">
                <React.Suspense fallback={<div className="min-h-screen p-8 text-slate-400" style={{ background: 'transparent' }}>Reading your conversations…</div>}>
                  <MessagesIntelligenceView workspaceMode={workspaceMode} onGotoTab={setTab}/>
                </React.Suspense>
              </TabBoundary>
            </Room>
          )}
          {tab === 'call_queue' && (
            <Room floor="calls" number={4} title="Communications Command" tagline="Power dialer. Live calls. AI coaching." personaQuote="Top of queue is who to call next. I'm ranked by pickup probability.">
              <TabBoundary name="Call Queue">
                <React.Suspense fallback={<div className="min-h-screen p-8 text-slate-400" style={{ background: 'transparent' }}>Ranking your call list…</div>}>
                  <CallQueueView workspaceMode={workspaceMode} onGotoTab={setTab}/>
                </React.Suspense>
              </TabBoundary>
            </Room>
          )}
          {tab === 'client_intelligence' && (
            <Room floor="clients" number={6} title="Relationship Center" tagline="360° client view. Relationships." personaQuote="I know who's due for a review and who's at risk.">
              <TabBoundary name="Client Intelligence">
                <React.Suspense fallback={<div className="min-h-screen p-8 text-slate-400" style={{ background: 'transparent' }}>Loading your book…</div>}>
                  <ClientIntelligenceView workspaceMode={workspaceMode} onGotoTab={setTab} openClientProfile={(id) => { setActiveClientKey(id); setTab('smart_profile'); }}/>
                </React.Suspense>
              </TabBoundary>
            </Room>
          )}
          {tab === 'revenue_intelligence' && (
            <TabBoundary name="Revenue Vault">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Opening the Revenue Vault…</div>}>
                <RevenueVaultRoom
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                  onFileChange={onFileChange}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'revenue_legacy' && (
            <Room floor="revenue" number={7} title="Financial Operations" tagline="Track commissions, overrides, chargebacks.">
              <TabBoundary name="Revenue Intelligence (legacy)">
                <React.Suspense fallback={<div className="min-h-screen p-8 text-slate-400" style={{ background: 'transparent' }}>Calculating revenue…</div>}>
                  <RevenueIntelligenceView workspaceMode={workspaceMode} onGotoTab={setTab}/>
                </React.Suspense>
              </TabBoundary>
            </Room>
          )}
          {tab === 'compliance_intelligence' && (
            <TabBoundary name="Compliance Vault">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Reading the audit chain…</div>}>
                <ComplianceVaultRoom
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'compliance_legacy' && (
            <Room floor="compliance" number={11} title="Compliance Vault" tagline="Stay 100% compliant.">
              <TabBoundary name="Compliance Intelligence (legacy)">
                <React.Suspense fallback={<div className="min-h-screen p-8 text-slate-400" style={{ background: 'transparent' }}>Reading consent state…</div>}>
                  <AgencyComplianceIntelligenceView workspaceMode={workspaceMode} onGotoTab={setTab}/>
                </React.Suspense>
              </TabBoundary>
            </Room>
          )}
          {tab === 'rep_performance' && (
            <Room floor="growth" number={9} title="Executive Boardroom" tagline="Real data. Real growth." personaQuote="Every metric mapped to a decision. Nothing on the wall that doesn't matter.">
              <TabBoundary name="Performance Command">
                <React.Suspense fallback={<div className="min-h-screen p-8 text-slate-400" style={{ background: 'transparent' }}>Scoring your performance…</div>}>
                  <RepPerformanceCommandView workspaceMode={workspaceMode} onGotoTab={setTab}/>
                </React.Suspense>
              </TabBoundary>
            </Room>
          )}
          {tab === 'product_health' && (
            <TabBoundary name="Product Health">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', color: '#94a3b8', padding: 32 }}>Loading Specter's report…</div>}>
                <ProductHealthView/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'billing' && (
            <div className="min-h-screen bg-slate-50 p-6 md:p-8">
              <div className="max-w-3xl mx-auto space-y-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Georgia, serif', letterSpacing: '-0.02em' }}>Billing & subscription</h1>
                  <p className="text-sm text-slate-600 mt-1">Manage your plan, payment method, invoices, cancellation, and upgrade — all in one place.</p>
                </div>

                {/* Primary billing card — opens Stripe portal for card / invoices / plan changes */}
                <BillingCard currentUser={currentUser} showToast={showToast}/>

                {/* Explicit Cancel + Upgrade actions — these mirror what's in the Stripe portal
                    but give operators a clear direct path, since some subscribers reported not
                    being able to find them. */}
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Upgrade plan */}
                  <div className="bg-white border border-emerald-200 rounded-xl p-5">
                    <div className="text-emerald-700 text-[10px] font-black uppercase tracking-widest mb-1">Upgrade</div>
                    <div className="font-bold text-slate-900 mb-2">Move to a bigger plan</div>
                    <p className="text-xs text-slate-600 mb-3">Get more seats, higher SMS caps, and priority support. Upgrade takes effect immediately and is prorated on your next invoice.</p>
                    <button
                      onClick={async () => {
                        try {
                          // Same bug fix as BillingCard: use apiFetch so JWT
                          // is attached; drop the mismatched userId body.
                          const data = await apiFetch('/api/create-portal-session', {
                            method: 'POST',
                            body: JSON.stringify({}),
                          });
                          if (data?.url) window.location.href = data.url;
                          else showToast('Could not open billing portal.', 'error');
                        } catch (err) { showToast(err.message || 'Portal error', 'error'); }
                      }}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg px-3 py-2"
                    >Change plan in Stripe →</button>
                  </div>

                  {/* Cancel trial / subscription */}
                  <div className="bg-white border border-slate-300 rounded-xl p-5">
                    <div className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-1">Cancel</div>
                    <div className="font-bold text-slate-900 mb-2">Cancel trial or subscription</div>
                    <p className="text-xs text-slate-600 mb-3">You keep access through the end of the current billing (or trial) period. No charge occurs after that. You can reactivate any time.</p>
                    <button
                      onClick={async () => {
                        if (!(await hqConfirm({
                          title: 'Cancel your subscription?',
                          body: 'You keep access until the end of the current billing period. You can reactivate any time.',
                          confirmLabel: 'Cancel subscription',
                          cancelLabel: 'Keep it',
                          danger: true,
                        }))) return;
                        try {
                          const res = await fetch('/api/billing/cancel-subscription', {
                            method: 'POST',
                            headers: { 'content-type': 'application/json', ...(await bearer()) },
                            body: JSON.stringify({ confirm: true }),
                          });
                          const data = await res.json().catch(() => ({}));
                          if (res.ok && data.ok) {
                            showToast(data.note || 'Cancellation scheduled.', 'success');
                          } else {
                            showToast(data.error || `Cancel failed (${res.status}).`, 'error');
                          }
                        } catch (err) { showToast(err.message || 'Cancel error', 'error'); }
                      }}
                      className="w-full bg-white hover:bg-slate-100 text-slate-800 border border-slate-400 text-sm font-semibold rounded-lg px-3 py-2"
                    >Cancel my subscription →</button>
                  </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl p-5 text-sm text-slate-700 space-y-2">
                  <div className="font-bold text-slate-900">What you can do in the billing portal</div>
                  <ul className="list-disc pl-5 space-y-1 text-slate-600">
                    <li>Update the card on file (Visa, MC, Amex, ACH where supported)</li>
                    <li>View past invoices + download receipts</li>
                    <li>Change plan (upgrade / downgrade) — takes effect immediately, prorated</li>
                    <li>Cancel subscription — access continues through the current billing period</li>
                  </ul>
                  <div className="text-xs text-slate-500 pt-2">The billing portal is hosted by Stripe. We never see or store your full card number.</div>
                </div>
              </div>
            </div>
          )}
          {tab === 'call_command' && (
            selectedLeadId
              ? (() => {
                  const l = (leads || []).find(x => String(x.id) === String(selectedLeadId));
                  return (
                    <CallCommandPanel
                      leadId={selectedLeadId}
                      leadName={l?.name || null}
                      leadPhone={l?.phone_e164 || l?.phone || null}
                      leadEmail={l?.email || null}
                      appointmentAt={l?.next_appointment_at || null}
                      onClose={() => setTab('inbox')}
                    />
                  );
                })()
              : <div className="p-8 text-center text-slate-500">
                  <div className="text-lg font-bold text-slate-700 mb-2">Call Command</div>
                  <div className="text-sm">Open a lead from the Inbox or Leads tab to run pre-call briefing + post-call follow-through.</div>
                  <button onClick={() => setTab('inbox')} className="mt-4 bg-slate-900 text-white text-sm font-semibold px-4 py-2 rounded-md">Go to Inbox</button>
                </div>
          )}
          {tab === 'my_week' && <MyWeekView currentUser={currentUser} transactions={transactions} leads={leads} calls={calls} outreach={outreach} producers={producers} producerList={producerList} settings={settings} setSettings={setSettings} onGotoTab={setTab} openClientProfile={setActiveClientKey}/>}
          {tab === 'fex' && <FexHubView goalConfig={goalConfig} setGoalConfig={setGoalConfig} calls={calls} leads={leads} settings={settings} transactions={transactions} producers={producers} currentUser={currentUser} onGotoTab={setTab} fexOnboarding={fexOnboarding} setFexOnboarding={setFexOnboarding} clientProfiles={clientProfiles} conservation={conservation} saveTasks={saveTasks} setSaveTasks={setSaveTasks}/>}
          {tab === 'workflows' && (
            <div>
              <div className="max-w-6xl mx-auto px-4 pt-4">
                <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-xs bg-white">
                  <button
                    onClick={() => setTab('drips')}
                    className="px-4 py-2 font-bold uppercase tracking-tight text-slate-700 hover:bg-slate-50"
                  >
                    Drips
                  </button>
                  <button
                    onClick={() => setTab('workflows')}
                    className="px-4 py-2 font-bold uppercase tracking-tight bg-slate-900 text-white"
                  >
                    Workflows
                  </button>
                </div>
              </div>
              <WorkflowsView workflows={workflows} setWorkflows={setWorkflows} settings={settings} showToast={showToast}/>
            </div>
          )}
          {tab === 'dashboard' && (
            <>
              <MoneyFoundWidget onGotoTab={setTab}/>
              <Dashboard agg={agg} currentMonthData={currentPeriodData} prevMonthData={prevPeriodData} monthlyAgg={periodAgg} periodMode={periodMode} settings={settings} unassignedCount={unassignedCount} onGotoProducers={()=>setTab('producers')} producerList={producerList} transactions={filteredTx} producers={producers} openClientProfile={setActiveClientKey} clientProfiles={clientProfiles} allTransactions={transactions} onFileChange={onFileChange} onPasteStatement={()=>setPasteCarrier('americo')}/>
            </>
          )}
          {tab === 'agency' && <AgencyView allTransactions={transactions} producerList={producerList} producers={producers} monthlyAgg={monthlyAgg} settings={settings} presentationConfig={presentationConfig} setPresentationConfig={setPresentationConfig}/>}
          {tab === 'va' && <VAWorkflowView allTransactions={transactions} producers={producers} clientProfiles={clientProfiles} setClientProfiles={setClientProfiles} openClientProfile={setActiveClientKey} settings={settings} showToast={showToast} leads={leads} setLeads={setLeads} calls={calls} outreach={outreach} calendar={calendar} vaTasks={vaTasks} setVaTasks={setVaTasks} vaTaskDone={vaTaskDone} setVaTaskDone={setVaTaskDone} currentUser={currentUser}/>}
                    {tab === 'affiliates' && <AffiliatesAdminView currentUser={currentUser} showToast={showToast}/>}
          {tab === 'match_review' && <MatchReviewView currentUser={currentUser} showToast={showToast}/>}
          {tab === 'recon' && <ReconReportView currentUser={currentUser} showToast={showToast}/>}
          {tab === 'tasks' && (
            <TabBoundary name="Tasks Room">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading tasks…</div>}>
                <TasksRoom
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                  openClientProfile={(id) => { setActiveClientKey(id); setTab('smart_profile'); }}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'retention' && (
            <TabBoundary name="Retention Room">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading retention…</div>}>
                <RetentionRoom
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'hierarchy_map' && (
            <TabBoundary name="Money Map">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Money Map…</div>}>
                <HierarchyCommissionMap
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'chargebacks' && (
            <TabBoundary name="Chargebacks Room">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading chargebacks…</div>}>
                <ChargebacksRoom
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'source_roi' && (
            <TabBoundary name="Lead Source ROI">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading lead-source ROI…</div>}>
                <LeadSourceROIView
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'aca_command' && (
            <TabBoundary name="ACA Marketplace Command Center">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#070A0C', color: '#7C838C', padding: 32 }}>Loading ACA Marketplace…</div>}>
                <AcaPpoCommandCenter isDemo={isDemoMode} segment="aca"/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'ppo_command' && (
            <TabBoundary name="Private Health / PPO Command Center">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#070A0C', color: '#7C838C', padding: 32 }}>Loading Private Health / PPO…</div>}>
                <AcaPpoCommandCenter isDemo={isDemoMode} segment="ppo"/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'dental_command' && (
            <TabBoundary name="Dental & Vision Command Center">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#070A0C', color: '#7C838C', padding: 32 }}>Loading Dental & Vision…</div>}>
                <AcaPpoCommandCenter isDemo={isDemoMode} segment="dental"/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'campaigns' && (
            <TabBoundary name="Campaign Roadmap">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading campaigns…</div>}>
                <CampaignRoadmap/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'field_sales' && (
            <TabBoundary name="Field Command">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Field Command…</div>}>
                <FieldCommand/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'landing_pages' && (
            <TabBoundary name="Landing Pages">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Landing Pages…</div>}>
                <LandingPages/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'prospect_queue' && (
            <TabBoundary name="My New Prospects">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading prospects…</div>}>
                <ProspectQueue onGotoTab={setTab}/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'lead_dashboard' && (
            <TabBoundary name="Lead Command">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Lead Command…</div>}>
                <LeadDashboard/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'aca_oep' && (
            <TabBoundary name="ACA OEP Mission Control">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading ACA OEP Mission Control…</div>}>
                <AcaOepMissionControl/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'agent_protection' && (
            <TabBoundary name="Agent Protection">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Agent Protection…</div>}>
                <ProtectionCenter/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'carrier_portals' && (
            <TabBoundary name="Carrier Portals">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Carrier Portals…</div>}>
                <CarrierPortals/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'nova' && (
            <TabBoundary name="Nova Command Center">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Nova…</div>}>
                <NovaCommandCenter/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'team' && (
            <TabBoundary name="Team">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Team…</div>}>
                <TeamCenter/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'system_health' && (
            <TabBoundary name="System Health">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading System Health…</div>}>
                <SystemHealth/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'setup_checklist' && (
            <TabBoundary name="Setup">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Setup…</div>}>
                <SetupChecklist/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'import_leads' && (
            <TabBoundary name="Import Leads">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Import…</div>}>
                <UniversalImport/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'medicare_readiness' && (
            <TabBoundary name="Medicare Safe-to-Proceed">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading…</div>}>
                <MedicareReadiness/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'people' && (
            <TabBoundary name="People">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading People…</div>}>
                <PeopleView/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'growth' && (
            <TabBoundary name="Growth Studio">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Growth Studio…</div>}>
                <GrowthStudio/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'next_call' && (
            <TabBoundary name="Call Next">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Ranking your book…</div>}>
                <NextBestCall leads={leads} onGotoTab={setTab} openClientProfile={setActiveClientKey}/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'no_show_recovery' && (
            <TabBoundary name="No-show recovery">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading missed appointments…</div>}>
                <NoShowRecovery leads={leads} onGotoTab={setTab} openClientProfile={setActiveClientKey}/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'booking_scorecard' && (
            <TabBoundary name="Booking scorecard">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading scorecard…</div>}>
                <BookingScorecard leads={leads} onGotoTab={setTab}/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'followups' && (
            <TabBoundary name="Follow-Up Command">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading follow-ups…</div>}>
                <AcaFollowUpsCommand
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  openClientProfile={setActiveClientKey}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'execution_queue' && (
            <TabBoundary name="Execution Queue">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Execution Queue…</div>}>
                <TasksExecutionQueue
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onGotoTab={setTab}
                  openClientProfile={setActiveClientKey}
                  onClose={() => setTab('command_center')}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'calendar_room' && (
            <TabBoundary name="Calendar Room">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Calendar Room…</div>}>
                <CalendarRoomView
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onGotoTab={setTab}
                  openClientProfile={setActiveClientKey}
                  onClose={() => setTab('briefing')}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'data_intelligence' && (
            <TabBoundary name="Data Intelligence Center">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Data Intelligence Center…</div>}>
                <DataIntelligenceCenterView onGotoTab={setTab}/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'client_command' && (
            <TabBoundary name="Client Command Center">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Client Command Center…</div>}>
                <ClientCommandCenter
                  activeClientKey={activeClientKey}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'recon_center' && (
            <TabBoundary name="Reconciliation Center">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Reconciliation Center…</div>}>
                <ReconciliationCenterView currentUser={currentUser} isDemo={isDemoMode} onClose={() => setTab('data_intelligence')} onGotoTab={setTab}/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'import_review' && (
            <TabBoundary name="Import Review Queue">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading review queue…</div>}>
                <ReviewQueueView currentUser={currentUser} isDemo={isDemoMode} onClose={() => setTab('data_intelligence')} onGotoTab={setTab}/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'bob_command' && (
            <TabBoundary name="Book of Business">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Book of Business…</div>}>
                <BookOfBusinessCommand
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                  openClientProfile={setActiveClientKey}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'ai_workforce' && (
            <TabBoundary name="AI Workforce">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading AI Workforce…</div>}>
                <AiWorkforceCommand
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'book_snapshot_v2' && (
            <TabBoundary name="Book Snapshot">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading book snapshot…</div>}>
                <BookSnapshotRoom
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                  openClientProfile={(id) => { setActiveClientKey(id); setTab('smart_profile'); }}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'statements_v2' && (
            <TabBoundary name="Statements Room">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading statements…</div>}>
                <StatementsRoom
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'persistency_v2' && (
            <TabBoundary name="Persistency Room">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading persistency…</div>}>
                <PersistencyRoom
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'carrier_intel' && (
            <TabBoundary name="Carrier Intelligence">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading carriers…</div>}>
                <CarrierIntelligenceRoom
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'audit_log' && (
            <TabBoundary name="Audit Log">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading audit events…</div>}>
                <AuditLogRoom
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'overrides_v2' && (
            <TabBoundary name="Overrides Room">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading overrides…</div>}>
                <OverridesRoom
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'performance_v2' && (
            <TabBoundary name="Performance Room">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading performance…</div>}>
                <PerformanceRoom
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'agency_onboarding' && (
            <TabBoundary name="Agency Onboarding">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading wizard…</div>}>
                <AgencyOnboardingWizard
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'ai_comms_setup' && (
            <TabBoundary name="AI Communications Setup">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading wizard…</div>}>
                <AiCommunicationsWizard
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'payouts_v2' && (
            <TabBoundary name="Payouts Room">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading payouts…</div>}>
                <PayoutsRoom
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'applications_v2' && (
            <TabBoundary name="Applications Room">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading applications…</div>}>
                <ApplicationsRoom
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'tasks_legacy' && <TasksView currentUser={currentUser} showToast={showToast}/>}
          {tab === 'email'    && <EmailComposeView currentUser={currentUser} showToast={showToast}/>}
          {tab === 'email_drips' && <EmailDripsView currentUser={currentUser} showToast={showToast}/>}
          {tab === 'ai_activity' && <AiAgentsActivityView currentUser={currentUser} showToast={showToast}/>}
          {tab === 'va_tracker'  && <VATrackerView currentUser={currentUser} showToast={showToast}/>}
          {tab === 'referrals_wf' && <ReferralsWorkflowView currentUser={currentUser} showToast={showToast}/>}
          {tab === 'kanban'   && (
            <TabBoundary name="Pipeline Room">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading pipeline…</div>}>
                <PipelineRoom
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                  openClientProfile={(id) => { setActiveClientKey(id); setTab('smart_profile'); }}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'kanban_legacy' && (
            <Room floor="pipeline" number={3} title="Pipeline Room" tagline="Manage your sales pipeline.">
              <KanbanPipelineView leads={leads} setLeads={setLeads} showToast={showToast} onGotoTab={setTab}/>
            </Room>
          )}
          {tab === 'meta_ads' && <MetaAdsSettingsView currentUser={currentUser} showToast={showToast}/>}
          {tab === 'facebook_leads' && <FacebookLeadsView currentUser={currentUser} showToast={showToast}/>}
          {tab === 'forge' && <ForgeView currentUser={currentUser} showToast={showToast}/>}
          {tab === 'retain_closers' && <RetainClosersView currentUser={currentUser} showToast={showToast}/>}
          {tab === 'priority_leads' && <PriorityLeadsView currentUser={currentUser} showToast={showToast} onGotoTab={setTab}/>}
          {tab === 'agency_analytics' && <AgencyAnalyticsView currentUser={currentUser} showToast={showToast}/>}
          {tab === 'referral_partners' && <ReferralPartnersView currentUser={currentUser} showToast={showToast}/>}
          {tab === 'automation_map' && <TabBoundary name="Automation Map"><AutomationMapView leads={effectiveLeads} onGotoTab={setTab}/></TabBoundary>}
          {tab === 'webinar_builder' && <TabBoundary name="Webinar Builder"><WebinarBuilderView currentUser={currentUser} settings={settings} showToast={showToast} onGotoTab={setTab}/></TabBoundary>}
          {tab === 'medicare' && <TabBoundary name="Medicare Workspace"><MedicareWorkspaceView currentUser={currentUser} showToast={showToast}/></TabBoundary>}
          {tab === 'medicare_client' && <TabBoundary name="Medicare Client Detail"><MedicareClientDetailView currentUser={currentUser} showToast={showToast} onGotoTab={setTab}/></TabBoundary>}
          {tab === 'aca'      && <TabBoundary name="ACA Workspace"><AcaWorkspaceView currentUser={currentUser} showToast={showToast} openClientProfile={setActiveClientKey}/></TabBoundary>}
          {tab === 'aca_plan_intelligence' && <TabBoundary name="ACA Plan Intelligence"><AcaPlanIntelligenceView currentUser={currentUser} showToast={showToast} openClientProfile={setActiveClientKey}/></TabBoundary>}
          {tab === 'money_map' && (
            <TabBoundary name="Money Map">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Money Map…</div>}>
                {/* Real-data hierarchy map (life_producers + /api/hierarchy/*).
                    isDemo={isDemoMode} → demo accounts see the seeded sample,
                    real accounts see their actual downline + commissions.
                    Previously this tab rendered the seed-only HierarchyMoneyMap. */}
                <HierarchyCommissionMap isDemo={isDemoMode} onClose={() => setTab('command_center')}/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'calendar' && (
            <TabBoundary name="Calendar Room">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Reading the calendar…</div>}>
                <CalendarRoom
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                  openClientProfile={(id) => { setActiveClientKey(id); setTab('smart_profile'); }}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'leads' && <TabBoundary name="Leads"><LeadsView leads={effectiveLeads} setLeads={setLeads} outreach={outreach} setOutreach={setOutreach} calls={effectiveCalls} setCalls={setCalls} calendar={effectiveCalendar} setCalendar={setCalendar} leadTemplates={leadTemplates} setLeadTemplates={setLeadTemplates} settings={settings} producers={producers} producerList={producerList} currentUser={currentUser} showToast={showToast} workflows={workflows} onGotoTab={setTab} transactions={transactions}/></TabBoundary>}
          {tab === 'lead_command_center' && <TabBoundary name="Lead Command Center"><React.Suspense fallback={<div className="min-h-screen p-8 text-slate-400">Loading Lead Command Center…</div>}><LeadCommandCenter currentUser={currentUser} showToast={showToast} onGotoTab={setTab}/></React.Suspense></TabBoundary>}
          {tab === 'weekly' && <WeeklyStatsView transactions={filteredTx} allTransactions={transactions} producers={producers} settings={settings} periodMode={periodMode} selectedWeek={selectedWeek} openClientProfile={setActiveClientKey}/>}
          {/* The old "Policy Conservation" view kept under a new tab id so it
              doesn't shadow the new Retention Hub. Reachable from Retention
              hub deep-links when carrier-report parsing is needed. */}
          {tab === 'conservation_old' && <RetentionView conservation={conservation} setConservation={setConservation} transactions={transactions} producers={producers} setProducers={setProducers} settings={settings} clientProfiles={clientProfiles} setClientProfiles={setClientProfiles} calendar={calendar} setCalendar={setCalendar} leadTemplates={leadTemplates} leads={leads} currentUser={currentUser} showToast={showToast} openClientProfile={setActiveClientKey}/>}
          {tab === 'winback' && <WinbackView allTransactions={transactions} producers={producers} settings={settings} policyTracking={policyTracking} setPolicyTracking={setPolicyTracking} clientNotes={clientNotes} setClientNotes={setClientNotes} openClientProfile={setActiveClientKey}/>}
          {tab === 'agents' && <AgentsView producerList={producerList} producers={producers} settings={settings} allTransactions={transactions} openClientProfile={setActiveClientKey}/>}
          {tab === 'overrides' && <OverridesView producerList={producerList} producers={producers} setProducerName={setProducerName} settings={settings} transactions={filteredTx} getCarrier={getCarrier} openClientProfile={setActiveClientKey} onGotoProducers={()=>setTab('producers')}/>}
          {tab === 'calls' && <CallsView calls={calls} setCalls={setCalls} leads={leads} setLeads={setLeads} producers={producers} producerList={producerList} currentUser={currentUser} showToast={showToast} openClientProfile={setActiveClientKey} onGotoTab={setTab}/>}
          {tab === 'underwriting' && <UnderwritingView showToast={showToast}/>}
          {tab === 'chargebacks' && <ChargebacksView transactions={filteredTx} allTransactions={transactions} producers={producers} settings={settings} periodMode={periodMode} openClientProfile={setActiveClientKey}/>}
          {tab === 'retention_command' && (
            <Room floor="retention" number={7} title="Retention & Chargeback Command" tagline="Every life policy's retention exposure, in one place." personaQuote="I'm watching every advance, every chargeback window, every lapse risk in your book.">
              <RetentionCommandCenterView
                transactions={transactions}
                allTransactions={transactions}
                filteredTx={filteredTx}
                periodMode={periodMode}
                conservation={conservation}
                setConservation={setConservation}
                producers={producers}
                setProducers={setProducers}
                settings={settings}
                clientProfiles={clientProfiles}
                setClientProfiles={setClientProfiles}
                calendar={calendar}
                setCalendar={setCalendar}
                leadTemplates={leadTemplates}
                leads={leads}
                setLeads={setLeads}
                calls={calls}
                outreach={outreach}
                policyTracking={policyTracking}
                setPolicyTracking={setPolicyTracking}
                clientNotes={clientNotes}
                setClientNotes={setClientNotes}
                currentUser={currentUser}
                showToast={showToast}
                openClientProfile={setActiveClientKey}
                saveTasks={saveTasks}
                setSaveTasks={setSaveTasks}
                onGotoTab={setTab}
              />
            </Room>
          )}
          {tab === 'teams' && <TeamsView agg={agg} monthlyAgg={periodAgg} periodMode={periodMode} settings={settings} transactions={filteredTx} producers={producers} getProduct={getProduct} getCarrier={getCarrier} openClientProfile={setActiveClientKey}/>}
          {tab === 'producers' && <ProducersView producers={producers} producerList={producerList} setProducerTeam={setProducerTeam} setProducerName={setProducerName} setProducerCustomTeam={setProducerCustomTeam} setProducerAlias={setProducerAlias} customTeams={customTeams} addCustomTeam={addCustomTeam} renameCustomTeam={renameCustomTeam} deleteCustomTeam={deleteCustomTeam} onResetProducers={resetProducers} onGotoDashboard={()=>setTab('dashboard')} settings={settings} setSettings={setSettings} showToast={showToast}
            onAddManualProducer={(name) => {
              const cleaned = String(name || '').trim();
              if (!cleaned) return null;
              // Auto-generate prodNum. Statement-imported producers have
              // carrier-assigned codes; manual entries get a stable
              // MANUAL_ prefix + random 6-char suffix so they're
              // distinguishable in exports and audits.
              const rand = () => Math.random().toString(36).slice(2, 8).toUpperCase();
              let prodNum = `MANUAL_${rand()}`;
              // Collision guard — extremely unlikely but cheap.
              while (producers[prodNum]) prodNum = `MANUAL_${rand()}`;
              setProducers(prev => ({
                ...prev,
                [prodNum]: {
                  name:  cleaned,
                  team:  'mine',   // sensible default — most solo agents self-assign
                  customTeam: '',
                  alias: '',
                  createdManually: true,
                  createdAt: new Date().toISOString(),
                },
              }));
              showToast(`Added ${cleaned}. You can now assign leads to them.`);
              return prodNum;
            }}
          />}
          {tab === 'life_leads' && (
            <TabBoundary name="Life Leads">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Loading Life Leads…</div>}>
                <LifeLeadsView leads={effectiveLeads} onGotoTab={setTab} openClientProfile={setActiveClientKey}/>
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'life_command' && (
            <TabBoundary name="Life Command Center">
              <React.Suspense fallback={<div className="min-h-screen p-8 text-slate-400">Loading Life Command Center…</div>}>
                <LifeCommandCenter
                  transactions={effectiveTransactions}
                  apps={apps}
                  producers={producers}
                  settings={settings}
                  leads={effectiveLeads}
                  getCarrier={getCarrier}
                  getProduct={getProduct}
                  onGotoTab={setTab}
                  openClientProfile={setActiveClientKey}
                  showToast={showToast}
                  onOpenPowerDialer={(lens) => { setPowerDialerLens(lens || 'all'); setPowerDialerOpen(true); }}
                  onOpenLifeClient={openLifeClientByName}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'book' && <TabBoundary name="Book of Business"><BookOfBusinessView transactions={filteredTx} allTransactions={transactions} producers={producers} settings={settings} clientNotes={clientNotes} setClientNotes={setClientNotes} periodMode={periodMode} clientProfiles={clientProfiles} setClientProfiles={setClientProfiles} lobConfig={lobConfig} showToast={showToast} pipeline={pipeline} setPipeline={setPipeline} openClientProfile={setActiveClientKey}/></TabBoundary>}
          {tab === 'transactions' && <TransactionsView transactions={filteredTx} producers={producers} onExport={exportCSV} onGotoDashboard={()=>setTab('dashboard')} openClientProfile={setActiveClientKey}/>}
          {tab === 'settings' && (
            <TabBoundary name="Technology Ops Center">
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#050709', color: '#7C838C', padding: 32 }}>Probing integrations…</div>}>
                <SettingsRoom
                  currentUser={currentUser}
                  isDemo={isDemoMode}
                  onClose={() => setTab('command_center')}
                  onGotoTab={setTab}
                />
              </React.Suspense>
            </TabBoundary>
          )}
          {tab === 'settings_full' && (
            <Room floor="settings" number={12} title="Technology Operations Center" tagline="Full configuration surface.">
              <SettingsView settings={settings} setSettings={setSettings} uploads={uploads} onDeleteSource={deleteTransactionsBySource} onClearAll={clearAll} clientProfiles={clientProfiles} setClientProfiles={setClientProfiles} lobConfig={lobConfig} setLobConfig={setLobConfig} showToast={showToast} currentUser={currentUser} credentialsBump={credentialsBump} onResetAgentPassword={resetAgentPassword} commissionRules={commissionRules} setCommissionRules={setCommissionRules} transactions={transactions} producers={producers} getCarrier={getCarrier} getProduct={getProduct} carrierCatalog={carrierCatalog} setCarrierCatalog={setCarrierCatalog} onGotoTab={setTab}/>
            </Room>
          )}
          </React.Suspense>
        </div>
      )}

      {powerDialerOpen && (
        <PowerDialerOverlay
          leads={leads}
          setLeads={setLeads}
          calls={calls}
          setCalls={setCalls}
          outreach={outreach}
          setOutreach={setOutreach}
          producers={producers}
          settings={settings}
          currentUser={currentUser}
          initialLens={powerDialerLens}
          onClose={() => setPowerDialerOpen(false)}
        />
      )}

      {lifeClientId && (
        <React.Suspense fallback={null}>
          <LifeProtectionCommand
            clientId={lifeClientId}
            onClose={() => setLifeClientId(null)}
            onGotoTab={setTab}
            showToast={showToast}
          />
        </React.Suspense>
      )}

      <StatementInsightsModal
        data={statementInsights}
        onClose={() => setStatementInsights(null)}
        onGotoTab={setTab}
      />

      <AccountantChat
        loading={loading}
        open={accountantOpen}
        setOpen={setAccountantOpen}
        chat={accountantChat}
        setChat={setAccountantChat}
        transactions={transactions}
        producers={producers}
        producerList={producerList}
        leads={leads}
        calls={calls}
        settings={settings}
        goalConfig={goalConfig}
        financialConfig={financialConfig}
        conservation={conservation}
        getCarrier={getCarrier}
        currentUser={currentUser}
      />

      {!isDemoMode && <AdminInfraStatusBanner currentUser={currentUser}/>}

      <OnboardingAgent
        loading={loading}
        currentUser={currentUser}
        settings={settings}
        setSettings={setSettings}
        goalConfig={goalConfig}
        setGoalConfig={setGoalConfig}
        leads={leads}
        setLeads={setLeads}
        fexOnboarding={fexOnboarding}
        setFexOnboarding={setFexOnboarding}
        chat={onboardingChat}
        setChat={setOnboardingChat}
        dismissed={onboardingDismissed}
        setDismissed={setOnboardingDismissed}
        onGotoTab={setTab}
        showToast={showToast}
      />


      {isDragging && (
        <div className="fixed inset-0 z-[60] bg-emerald-900/85 backdrop-blur-sm flex items-center justify-center pointer-events-none">
          <div className="bg-white rounded-3xl border-4 border-dashed border-emerald-300 px-16 py-12 text-center max-w-2xl mx-4 shadow-2xl">
            <Upload className="mx-auto text-emerald-600 mb-4" size={72}/>
            <div className="text-3xl font-bold text-slate-900">Drop your commission statements</div>
            <div className="text-slate-600 mt-3 text-lg">
              <strong>MOO</strong> .xlsx / .pdf · <strong>Transamerica</strong> .pdf
            </div>
            <div className="text-xs text-slate-500 mt-2">Multiple files at once is fine. Americo statements need the Paste button — file drops won't recognize them.</div>
          </div>
        </div>
      )}

      {pasteCarrier && (
        <PasteStatementModal
          carrier={pasteCarrier}
          pasteText={pasteText}
          setPasteText={setPasteText}
          onClose={()=>setPasteCarrier(null)}
          onImport={()=>{
            const label = pasteCarrier === 'transamerica' ? 'Transamerica' : 'Americo';
            const sourceName = `${label} paste — ${new Date().toLocaleString()}`;
            const added = pasteCarrier === 'transamerica'
              ? importTransamericaText(pasteText, sourceName)
              : importAmericoText(pasteText, sourceName);
            if (added > 0) { setPasteText(''); setPasteCarrier(null); }
          }}
          showToast={showToast}
        />
      )}

      {/* ChatPanel (the indigo/purple "AI" corner launcher) is
          intentionally NOT mounted here anymore — it stacked with the
          Ask ARIA pill and the Nova orb in the bottom-right corner.
          Bottom-right now has exactly one persistent floating launcher
          (AriaShell's "Ask ARIA" pill, mounted at the app root in
          index.js). The Cmd+K "jump to anything" palette ChatPanel's
          menu used to expose is unaffected — `palette` has its own
          global Cmd+K listener above, independent of this mount. The
          ChatPanel function itself is left intact (not deleted) below
          in case its business-grounded Q&A is wired into ARIA or a
          settled single dock later. */}

      {activeClientKey && (
        // Modal routing — Health tabs pass a client_id (UUID) that
        // maps to health_policies.client_id, so we want the 8-tab
        // HealthClientProfileModal (Overview / Medical / Household /
        // Relationship / Compliance / Documents / Timeline / Cross-
        // Sell). Life & shared tabs still pass a composite clientKey
        // built from transactions, so they need the legacy
        // ClientDetailModalContainer whose plumbing understands
        // that shape.
        HEALTH_TABS.has(tab) ? (
          <HealthClientProfileModal
            clientName={null}
            clientId={activeClientKey}
            currentUser={currentUser}
            settings={settings}
            onClose={() => setActiveClientKey(null)}
            onEditPolicy={() => { /* Life-only affordance — health uses the modal's own edit flows */ }}
            onGotoTab={setTab}
            showToast={showToast}
          />
        ) : (
          <ClientDetailModalContainer
            clientKey={activeClientKey}
            allTransactions={transactions}
            producers={producers}
            pipeline={pipeline}
            clientProfiles={clientProfiles}
            setClientProfiles={setClientProfiles}
            clientNotes={clientNotes}
            setClientNotes={setClientNotes}
            lobConfig={lobConfig}
            showToast={showToast}
            currentUser={currentUser}
            onClose={() => setActiveClientKey(null)}
          />
        )
      )}

      {/* Global command palette — ⌘K / Ctrl+K to open. Jump-to-anything
          search over every tab, every health/life client, every lead,
          plus common quick actions. Single keystroke replaces digging
          through nav rows. */}
      {palette && (
        <CommandPalette
          onClose={() => setPalette(false)}
          onGotoTab={(t) => { setTab(t); setPalette(false); }}
          onOpenFastAdd={() => setFastAddOpen(true)}
          onOpenBulk={() => setBulkOpen(true)}
          onAskNova={() => { try { window.dispatchEvent(new Event('nova:activate-request')); } catch {} }}
          leads={leads}
          currentUser={currentUser}
        />
      )}

      {/* ⌘K palette chip folded into the AI launcher menu (ChatPanel
          above) so the bottom-right corner only ever has ONE floating
          widget. Palette is still reachable via ⌘K keyboard shortcut
          on desktop; the AI launcher's menu covers the discoverability
          gap for mouse users. */}

      {toast && (
        <div onClick={()=>setToast(null)} className={`fixed bottom-72 right-6 max-w-md px-4 py-3 rounded-lg shadow-lg text-white font-medium text-sm whitespace-pre-wrap break-words cursor-pointer ${toast.type==='error'?'bg-red-600':'bg-slate-900'}`}>
          {toast.msg}
        </div>
      )}

      {/* First-run onboarding wizard. Auto-shows only when localStorage
          hasn't been flagged 'completed' or 'dismissed'. Resets to
          show again after a 'demo data loaded' event if the operator
          wants to re-walk the flow. */}
      {/* "10-minute wow" — first thing a brand-new operator sees.
          Hides the regular wizard until they finish or dismiss this. */}
      {showWow && !showValueTour && (
        <WowOnboarding
          onComplete={() => setShowWow(false)}
          onDismiss={() => setShowWow(false)}
          showToast={showToast}
        />
      )}
      {/* Signup modals are strictly serialized — Wow → Setup Wizard →
          Value Tour. Rendering more than one at a time caused the
          "tour moves in the background while the setup screen stays
          up" glitch on brand-new signups: the wizard modal (z-[60])
          fully occluded the tour animations, but the tour was still
          running and stealing focus. Now each stage renders only
          when the earlier stages are done. */}
      {showOnboarding && !showWow && (
        <>
          {/* PILOT — AI onboarding shell. Renders itself null when the
              tenant's subscriptions.pilot_enabled flag is false, so
              non-cohort users automatically fall through to the
              legacy FirstRunWizard below. Rollback is one SQL flip.
              Lazy-loaded (bundle is at 97% ceiling per performance
              memo). */}
          <React.Suspense fallback={null}>
            <PilotShell onExit={() => setShowOnboarding(false)}/>
          </React.Suspense>
          <FirstRunWizard
            onClose={() => setShowOnboarding(false)}
            onGotoTab={(t) => { setTab(t); setShowOnboarding(false); }}
          />
        </>
      )}
      {showValueTour && !showWow && !showOnboarding && (
        <AnchorValueTour
          currentUser={currentUser}
          onComplete={() => setShowValueTour(false)}
          onDismiss={() => setShowValueTour(false)}
        />
      )}

      {/* Three-part Setup Tour — the single onboarding spine (locked-Next
          setup steps → 10-stop feature reveals → AI finale). Wrapped in its
          OWN error boundary so any tour bug degrades to nothing instead of
          taking down the dashboard. Progress is server-side (user_state),
          so it resumes cross-device. Renders only when the legacy replay
          surfaces above aren't up. */}
      {!showWow && !showOnboarding && !showValueTour && (
        <TabBoundary name="Setup Tour">
          <SetupTour
            currentUser={currentUser}
            activeTab={tab}
            onGotoTab={setTab}
            onGotoIntent={(intent) => {
              const routes = pickCockpitRoutes(workspaceMode);
              setTab(routes[intent] || intent);
            }}
          />
        </TabBoundary>
      )}

      {/* Floating support chat widget — single instance at app root so it
          overlays every tab. RLS scopes the thread to currentUser. */}
      <SupportChat currentUser={currentUser}/>

      {/* Focus Mode overlay — full-screen single-task view. Triggered
          from the Health Command Center's "Start My Day" button. */}
      {focusMission && (
        <React.Suspense fallback={null}>
          <FocusMode
            initialPriority={focusMission}
            currentUser={currentUser}
            onExit={() => setFocusMission(null)}
            onSkip={() => setFocusMission(null)}
            onComplete={() => setFocusMission(null)}
          />
        </React.Suspense>
      )}

      {/* Start My Day — sequenced next-best-action driver over real leads +
          appointments. Opened via the crm-start-my-day event from any surface. */}
      {startDayOpen && (
        <React.Suspense fallback={null}>
          <StartMyDay
            open={startDayOpen}
            onClose={() => setStartDayOpen(false)}
            leads={effectiveLeads}
            appointments={effectiveCalendar}
            onGotoTab={setTab}
            openClientProfile={setActiveClientKey}
          />
        </React.Suspense>
      )}

      {/* Permanent Cockpit spine + global top bar — rebuilt 2026-07-12
          in the premium navy + light workspace aesthetic. Sidebar is 232px
          navy fixed left; top bar is 68px white sticky with search /
          create / inbox / notifications / ARIA / profile.
          Routes are WORKSPACE-AWARE: a health-only user gets health_leads
          / health_inbox / health_commissions / etc when clicking sidebar
          items, not the Life-flavored generic tabs. */}
      <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: 232, zIndex: 40 }}>
        <React.Suspense fallback={<div style={{ background: '#071225', width: '100%', height: '100%' }}/>}>
          <CockpitSidebar
            activeTab={pickReverseMap(workspaceMode)[tab] || 'command'}
            onGotoTab={(cockpitId) => {
              // "Setup ✓" sidebar entry reopens the Setup Tour checklist
              // instead of navigating to a tab.
              if (cockpitId === 'setup_checklist') {
                try { window.dispatchEvent(new CustomEvent('setup-tour:open')); } catch {}
                return;
              }
              const routes = pickCockpitRoutes(workspaceMode);
              const target = routes[cockpitId] || cockpitId;
              setTab(target);
            }}
            currentUser={currentUser}
            focusMode={false}
            workspaceMode={workspaceMode}
            onSetWorkspaceMode={switchWorkspace}
            allowedModes={allowedModes}
          />
        </React.Suspense>
      </div>
      {/* Ambient video backdrop — the AI walking around wash that
          runs behind EVERY tab. Classic tabs (.classic-hq) are already
          designed with rgba smoked-glass cards that sit over a dark
          ground, so the wash reads through the gaps. HQ Room tabs
          layer this UNDER their own inner AmbientWash for compounded
          depth. Per Harrison 2026-07-18: every page should feel like
          Client Operations. */}
      <React.Suspense fallback={null}>
        <HqAmbientBackdrop tab={tab}/>
      </React.Suspense>
      {/* Static mood backdrop (command-room.jpg + time-of-day
          filters) stays gated to cinematic Room tabs — it's the
          static texture layer, not the video. */}
      {HQ_ROOM_TABS.has(tab) && (
        <React.Suspense fallback={null}>
          <HqBackdrop/>
        </React.Suspense>
      )}
      <React.Suspense fallback={null}>
        <HqElevator tab={tab}/>
      </React.Suspense>
      {/* Branded confirmation dialog — one mount, receives all
          hqConfirm() calls app-wide. */}
      <HqConfirmMount/>
      {/* Quick Task — global "T" hotkey opens a fast create with
          tomorrow / in-2h / friday / !urgent shorthand parsing. */}
      <React.Suspense fallback={null}>
        <QuickTask currentUser={currentUser} onNavigate={(t) => setTab(t)}/>
      </React.Suspense>
      {/* Cinematic room-entrance overlay — listens for `hq:enter-room`
          window events dispatched from the Command Center grid, plays
          the 8s Kling clip, then calls onArrive (setTab). Bypassed for
          prefers-reduced-motion. Single mount at app root so any
          surface can trigger an entrance. */}
      <RoomEntrance/>
      {/* Fast Add Drawer — Quick Lead Upload from anywhere via N key. */}
      <React.Suspense fallback={null}>
        <FastAddDrawer
          open={fastAddOpen}
          onClose={() => setFastAddOpen(false)}
          leads={leads}
          showToast={showToast}
          onAdd={(lead) => setLeads(prev => [lead, ...prev])}
          onOpenBulk={() => setBulkOpen(true)}
        />
      </React.Suspense>
      {/* Bulk Upload panel — CSV/Excel/Sheets/paste with AI mapping + ARIA briefing. */}
      <React.Suspense fallback={null}>
        <BulkUploadPanel
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          existingLeads={leads}
          onImport={(newLeads) => setLeads(prev => [...newLeads, ...prev])}
          showToast={showToast}
        />
      </React.Suspense>
      <React.Suspense fallback={null}>
        <HqArrivalSurface
          onEnterBriefing={() => setTab(pickCockpitRoutes(workspaceMode).command)}
          onEnterOperations={() => setTab('operations_floor')}
        />
      </React.Suspense>
      <React.Suspense fallback={null}>
        {/* NovaProvider (+ NovaOverlay, the full-screen activation UI)
            stays mounted — Nova is still reachable via the CommandPalette's
            "Ask Nova" action and the wake-word listener. NovaOrb (the
            gold "N" corner button) is intentionally NOT mounted here
            anymore: it stacked on top of the Ask ARIA launcher and the
            arrival screen's ENTER OPERATIONS button in the bottom-right
            corner. Bottom-right now has exactly one persistent floating
            launcher (AriaShell's "Ask ARIA" pill, mounted at the app
            root in index.js). Do not re-add a second corner mount here —
            file src/nova/NovaOrb.jsx is left intact for a future
            single-dock integration, not deleted. */}
        <NovaProvider onGotoTab={(t) => {
          const routes = pickCockpitRoutes(workspaceMode);
          setTab(routes[t] || t);
        }}>
          <NovaOverlay/>
        </NovaProvider>
      </React.Suspense>
      {/* Live AI activity flow — always visible bottom-left, sits above
          the backdrop but below overlays. Shows real ai_agent_runs
          handoffs as light traveling between employees. */}
      <React.Suspense fallback={null}>
        <div style={{
          position: 'fixed', left: 22, bottom: 22, zIndex: 9998,
          maxWidth: 360, pointerEvents: 'none',
        }}>
          <ActivityFlow/>
        </div>
      </React.Suspense>
      <React.Suspense fallback={null}>
        <div style={{
          padding: '8px 20px',
          background: '#050709',
          borderBottom: '1px solid rgba(245,166,35,0.18)',
          display: 'flex', alignItems: 'center', gap: 16,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <HqStatusBarLive user={currentUser?.email || currentUser?.name || ''}/>
          </div>
          <HqViewModeToggle/>
        </div>
      </React.Suspense>
      <React.Suspense fallback={<div style={{ height: 68, background: '#050709', borderBottom: '1px solid rgba(245,166,35,0.18)' }}/>}>
        <CockpitGlobalTopBar
          currentUser={currentUser}
          onSearch={(q) => {
            // Deep search backend is a follow-up commit. For now, jump to
            // the conversations surface with the query pre-loaded via
            // sessionStorage so the operator isn't dead-ended.
            try { sessionStorage.setItem('cockpit.search.query', q); } catch {}
            const routes = pickCockpitRoutes(workspaceMode);
            setTab(routes.conversations);
          }}
          onOpenInbox={() => setTab(pickCockpitRoutes(workspaceMode).conversations)}
          onOpenNotifications={() => setTab('attention')}
          onOpenHelp={() => {
            // Fire a custom event; SupportChat listens and opens in-panel.
            // Uses the current session — no separate sign-in, no redirect.
            try { window.dispatchEvent(new CustomEvent('cockpit:open-help')); } catch {}
          }}
          onOpenAria={() => setTab('ask_aria')}
          onOpenProfile={() => setTab('settings')}
          onGotoTab={(t) => {
            const routes = pickCockpitRoutes(workspaceMode);
            const target = routes[t] || t;
            setTab(target);
          }}
          inboxCount={0}
          notificationCount={attentionCount}
        />
      </React.Suspense>

      {/* Light/dark theme toggle — flips the `dark` class on <html>.
          Sits at bottom-4/right-24 so it doesn't overlap SupportChat
          (bottom-right z-50). Persisted to localStorage; FOUC-safe
          because index.html applies the class before React mounts. */}
      <ThemeToggle/>

    </div>
  );

  // Demo Mode wrap. Mounts the seeded data provider + visual shell
  // (DEMO badge, tour, why-strips, live activity feed) only when the
  // current user's subscription has demo_mode = true. Live users
  // render the original tree untouched — zero impact on production.
  if (demoFlag.isDemo) {
    return (
      <HqEnvironmentProvider>
        <React.Suspense fallback={<div className="min-h-screen bg-slate-50 flex items-center justify-center text-sm text-slate-500">Loading demo…</div>}>
          <DemoDataProvider>
            <DemoModeShell tab={tab} onGotoTab={setTab}>{__ctRoot}</DemoModeShell>
          </DemoDataProvider>
        </React.Suspense>
      </HqEnvironmentProvider>
    );
  }
  return <HqEnvironmentProvider>{__ctRoot}</HqEnvironmentProvider>;
}

// ════════════════════════════════════════════════════════════════════
// ⌘K COMMAND PALETTE — global jump-to-anything.
//
// Two-pass fuzzy search:
//   1. Static catalog of every tab + every common action (always there)
//   2. Live results from leads (filtered to non-closed, non-opt-out)
//      and clients fetched from Supabase the first time the palette
//      opens. Cached for the session.
//
// Keyboard: ↑/↓ navigate, Enter selects, Esc closes, ⌘K toggles.
// Designed for a 10,000-user scale-out: zero per-user customization
// needed, every action discoverable from one keystroke.
// ════════════════════════════════════════════════════════════════════

const PALETTE_TABS = [
  // Prospecting
  { tab: 'briefing',         label: 'AI Briefing',           group: 'Prospecting', icon: '' },
  { tab: 'deal_room',        label: 'Deal Room',             group: 'Prospecting', icon: '' },
  { tab: 'call_command',     label: 'Call Command',          group: 'Prospecting', icon: '' },
  { tab: 'follow_up',        label: 'Follow-Up Engine',      group: 'Prospecting', icon: '' },
  { tab: 'ai_boss',          label: 'AI Boss · Agency',      group: 'Agency', icon: '' },
  { tab: 'launch_readiness', label: 'Launch Readiness',      group: 'Agency', icon: '' },
  { tab: 'book_snapshot',    label: 'Book Snapshot',         group: 'Agency', icon: '' },
  { tab: 'command',          label: '✨ Command Center',      group: 'Prospecting', icon: '' },
  { tab: 'power_dialer',     label: '📞 Power Dialer',        group: 'Prospecting', icon: '' },
  { tab: 'ai_agents',        label: 'AI Agents Hub',         group: 'Agency',      icon: '' },
  { tab: 'demos',            label: 'Demo Pipeline',         group: 'Agency',      icon: '' },
  { tab: 'mission',          label: 'Mission Dashboard',     group: 'Prospecting', icon: '' },
  { tab: 'alerts',           label: 'Critical Alerts',       group: 'Prospecting', icon: '' },
  { tab: 'inbox',            label: 'SMS Inbox',             group: 'Prospecting', icon: '' },
  { tab: 'leads',            label: 'Leads · Full Book',     group: 'Prospecting', icon: '' },
  { tab: 'lead_command_center', label: 'Lead Command Center', group: 'Prospecting', icon: '' },
  { tab: 'calls',            label: 'Calls Log',             group: 'Prospecting', icon: '' },
  { tab: 'calendar',         label: 'Calendar',              group: 'Prospecting', icon: '' },
  // 'drips' removed from Prospecting nav 2026-07-12 — it duplicated
  // 'workflows' at the intent level (both build multi-touch outreach
  // sequences) and confused subscribers. The route stays mounted at
  // line ~5732 (tab === 'drips' renders SmsDripBuilder) so any deep
  // links + saved bookmarks still work. New users only see Workflows,
  // which is the canonical automation surface.
  // Business
  { tab: 'life_command',     label: 'Life Command Center',   group: 'Life Insurance',    icon: '' },
  { tab: 'apps',             label: 'Apps Pipeline',         group: 'Life Insurance',    icon: '' },
  { tab: 'clients',          label: 'Clients · Lifecycle',   group: 'Life Insurance',    icon: '' },
  { tab: 'conversations',    label: 'Conversations',         group: 'Life Insurance',    icon: '' },
  { tab: 'referrals',        label: 'Referrals',             group: 'Life Insurance',    icon: '' },
  { tab: 'fex',              label: 'FEX Underwriting',      group: 'Life Insurance',    icon: '' },
  { tab: 'termuw',           label: 'Underwriting',          group: 'Life Insurance',    icon: '' },
  { tab: 'retention',        label: 'Retention',             group: 'Life Insurance',    icon: '' },
  { tab: 'retention_command', label: 'Retention & Chargeback Command', group: 'Life Insurance', icon: '⚠' },
  { tab: 'persistency',      label: 'Persistency · Chargebacks', group: 'Life Insurance', icon: '⚠' },
  // Money
  { tab: 'paycalc',          label: 'My Pay',                group: 'Life Insurance',       icon: '' },
  { tab: 'pnl',              label: 'P&L',                   group: 'Life Insurance',       icon: '' },
  { tab: 'overrides_iq',     label: 'Overrides',             group: 'Life Insurance',       icon: '' },
  { tab: 'policies',         label: 'Policy Vault · Life',   group: 'Life Insurance',       icon: '' },
  { tab: 'transactions',     label: 'Transactions',          group: 'Life Insurance',       icon: '' },
  // Agency
  { tab: 'producers',        label: 'Producers',             group: 'Agency',      icon: '' },
  { tab: 'leaderboard',      label: 'Leaderboard',           group: 'Agency',      icon: '' },
  { tab: 'book',             label: 'Book of Business',      group: 'Agency',      icon: '' },
  { tab: 'workflows',        label: 'Workflows & Drips',     group: 'Prospecting', icon: '' },
  { tab: 'dashboard',        label: 'Agency Dashboard',      group: 'Agency',      icon: '' },
  { tab: 'agency',           label: 'Presentation',          group: 'Agency',      icon: '' },
    { tab: 'affiliates',       label: 'Affiliates',            group: 'Agency',      icon: '' },
  { tab: 'match_review',     label: 'Match Review (Cross-Ref)', group: 'Agency',   icon: '' },
  { tab: 'recon',            label: 'Reconciliation Agent',     group: 'Agency',   icon: '' },
  { tab: 'uw_agent',         label: 'Underwriting Agent',       group: 'Agency',   icon: '' },
  { tab: 'tasks',            label: 'Tasks · Reminders',        group: 'Prospecting', icon: '' },
  { tab: 'email',            label: 'Email · Compose',          group: 'Prospecting', icon: '' },
  { tab: 'email_drips',      label: 'Email · Drip Sequences',   group: 'Prospecting', icon: '' },
  { tab: 'ai_activity',      label: 'AI Agents · Activity',     group: 'Agency',      icon: '' },
  { tab: 'atlas',            label: 'ATLAS · Book Steward',     group: 'Agency',      icon: '' },
  { tab: 'uplift',           label: 'UPLIFT · Cross-Sell',      group: 'Agency',      icon: '' },
  { tab: 'registrar',        label: 'REGISTRAR · A2P Setup',    group: 'Account',     icon: '' },
  { tab: 'funnel_b',         label: 'Funnel B · Speed-to-Lead', group: 'Agency',      icon: '' },
  { tab: 'va_tracker',       label: 'Team · VA Tracker',        group: 'Agency',      icon: '' },
  { tab: 'referrals_wf',     label: 'Referrals · Workflow',     group: 'Life Insurance', icon: '' },
  { tab: 'kanban',           label: 'Pipeline · Kanban',        group: 'Prospecting', icon: '' },
  { tab: 'meta_ads',         label: 'Meta Ads · Lead Inbound',  group: 'Agency',   icon: '' },
  { tab: 'facebook_leads',   label: 'Facebook Lead Ads · Setup', group: 'Agency',  icon: '' },
  { tab: 'forge',            label: 'FORGE · AI Workflow Builder', group: 'Agency', icon: '' },
  { tab: 'retain_closers',   label: 'RETAIN · Closers Roster',     group: 'Agency', icon: '' },
  { tab: 'priority_leads',   label: 'Leads · Priority Queue',      group: 'Prospecting', icon: '' },
  { tab: 'agency_analytics', label: 'Agency Analytics · KPIs',     group: 'Agency', icon: '' },
  { tab: 'referral_partners',label: 'Referral Partners · CRM',     group: 'Agency', icon: '' },
  { tab: 'medicare',         label: 'Medicare Workspace',       group: 'Health',   icon: '' },
  { tab: 'medicare_client',  label: 'Medicare Clients',         group: 'Health',   icon: '' },
  { tab: 'aca',              label: 'ACA / Health Workspace',   group: 'Health',   icon: '' },
  { tab: 'aca_plan_intelligence', label: 'ACA Plan Intelligence', group: 'Health', icon: '' },
  { tab: 'settings',         label: 'Settings',              group: 'Account',     icon: '' },
  { tab: 'licensing',        label: 'Licensing & Carriers',  group: 'Account',     icon: '' },
  // Health
  { tab: 'health_portal',    label: 'Health Dashboard',      group: 'Health',      icon: '' },
  { tab: 'health_leads',     label: 'Health Pipeline',       group: 'Health',      icon: '' },
  { tab: 'health_enrollment',label: 'Enrollment Pipeline',   group: 'Health',      icon: '' },
  { tab: 'health_inbox',     label: 'Health Inbox',          group: 'Health',      icon: '' },
  { tab: 'health_clients',   label: 'Health Clients',        group: 'Health',      icon: '' },
  { tab: 'cross_sell',       label: 'Cross-Sell Simulator',  group: 'Health',      icon: '' },
  { tab: 'coverage_summary', label: 'Coverage Summary PDF',  group: 'Health',      icon: '' },
  { tab: 'soa',              label: 'Scope of Appointment',  group: 'Health',      icon: '' },
  { tab: 'audit',            label: 'AUDIT — Compliance',    group: 'Health',      icon: '' },
  { tab: 'health_policies',  label: 'Health Policy Vault',   group: 'Health',      icon: '' },
  { tab: 'anchor',           label: 'ANCHOR (ACA Retention)', group: 'Health',     icon: '⚓' },
  { tab: 'ledger',           label: 'LEDGER (CFO Console)',   group: 'Agency',     icon: '📊' },
  { tab: 'health_eligibility',label: 'Eligibility Checker',  group: 'Health',      icon: '' },
  { tab: 'health_at_risk',   label: 'At-Risk Clients',       group: 'Health',      icon: '' },
  { tab: 'health_cross_sell',label: 'Cross-Sell Opportunities', group: 'Health',   icon: '' },
  { tab: 'health_war_room',  label: 'Revenue Command',       group: 'Health',      icon: '' },
  { tab: 'health_plan_intel', label: 'Plan Intelligence',    group: 'Health',      icon: '' },
  { tab: 'health_reconciliation', label: 'Reconciliation Engine', group: 'Health', icon: '' },
  { tab: 'health_commissions', label: 'Health Commissions',    group: 'Health', icon: '' },
  { tab: 'health_lifecycle',   label: 'Health SMS Lifecycle',  group: 'Health', icon: '' },
  { tab: 'health_calendar',    label: 'Health Calendar',       group: 'Health', icon: '' },
  { tab: 'life_lifecycle',     label: 'Life SMS Lifecycle',    group: 'Life Insurance', icon: '' },
  { tab: 'crosssell_lifecycle',label: 'Cross-Sell Engine',     group: 'Agency', icon: '' },
  { tab: 'trigger_campaigns',  label: 'Trigger Campaigns',     group: 'Agency', icon: '' },
  { tab: 'life_conservation',  label: 'Life Conservation',     group: 'Life Insurance', icon: '' },
  { tab: 'life_commissions',   label: 'Life Commission Intel', group: 'Life Insurance', icon: '' },
  { tab: 'daily_tracker',    label: 'Daily Tracker',         group: 'Health',      icon: '' },
  { tab: 'expenses',         label: 'Expenses',              group: 'Health',      icon: '' },
  { tab: 'health_products',  label: 'Products Reference',    group: 'Health',      icon: '' },
  { tab: 'health_consents',  label: 'Consent Forms',         group: 'Health',      icon: '' },
  { tab: 'health_audit',     label: 'Audit Log',             group: 'Health',      icon: '' },
  { tab: 'health_workflows', label: 'Automations',           group: 'Health',      icon: '' },
  { tab: 'automation_map',   label: 'Automation Map',        group: 'Agency',      icon: '🗺' },
  { tab: 'webinar_builder',  label: 'AI Webinar Builder',    group: 'Health',      icon: '🎤' },
];

// Cheap fuzzy match — every search char must appear in order in the
// target, case-insensitive. Returns a score (lower = better) so we
// can sort. Returns null when no match.
function paletteScore(target, query) {
  if (!query) return 0;
  const t = String(target).toLowerCase();
  const q = String(query).toLowerCase();
  let ti = 0, gaps = 0, lastIdx = -1;
  for (let qi = 0; qi < q.length; qi++) {
    const ch = q[qi];
    const found = t.indexOf(ch, ti);
    if (found < 0) return null;
    if (lastIdx >= 0) gaps += found - lastIdx - 1;
    lastIdx = found;
    ti = found + 1;
  }
  // Prefer prefix matches (gaps=0 + first char at idx 0) heavily.
  const prefix = t.startsWith(q) ? -100 : 0;
  return prefix + gaps + Math.max(0, t.length - q.length) * 0.1;
}

function CommandPalette({ onClose, onGotoTab, onOpenFastAdd, onOpenBulk, onAskNova, leads = [], currentUser }) {
  const [query, setQuery] = React.useState('');
  const [cursor, setCursor] = React.useState(0);
  const [healthClients, setHealthClients] = React.useState([]);
  const [allClients, setAllClients] = React.useState([]);
  const inputRef = React.useRef(null);
  // Unified bar: the palette owns ⌘K and does BOTH instant fuzzy jumping
  // AND natural-language AI routing. Free-text that isn't an obvious
  // match falls through to ARIA (invoke auto-navigates on a confident
  // page match, or surfaces an answer bubble for questions).
  const { invoke: ariaInvoke } = useAria();

  React.useEffect(() => { inputRef.current?.focus(); }, []);

  // Load clients once when the palette opens.
  React.useEffect(() => {
    if (!currentUser?.id) return;
    supabase
      .from('clients')
      .select('id, name, phone, vertical, sector')
      .order('name')
      .limit(2000)
      .then(({ data }) => {
        const list = data || [];
        setAllClients(list);
        setHealthClients(list.filter(c => c.vertical === 'health'));
      });
  }, [currentUser?.id]);

  // Build the searchable corpus on every render.
  const items = React.useMemo(() => {
    const base = [];
    PALETTE_TABS.forEach(t => base.push({ kind: 'tab', id: `tab:${t.tab}`, label: t.label, sub: t.group, icon: t.icon, action: () => onGotoTab(t.tab), searchText: `${t.label} ${t.group}` }));
    // Quick actions — the first three now bypass the tab hop and open
    // the right drawer / overlay directly.
    const quickActions = [
      // Fast Add + Bulk + Nova take direct actions rather than tab-hopping
      onOpenFastAdd  && { id: 'qa:fast_add',        label: 'Add a lead (Fast Add)',    sub: 'Quick action · N',        action: () => { onClose(); setTimeout(onOpenFastAdd, 80); }, searchText: 'add lead new fast quick' },
      onOpenBulk     && { id: 'qa:bulk_import',     label: 'Bulk import leads',        sub: 'Quick action · CSV/Excel', action: () => { onClose(); setTimeout(onOpenBulk, 80); },   searchText: 'bulk import upload csv excel sheets vendor' },
      onAskNova      && { id: 'qa:ask_nova',        label: 'Ask Nova',                 sub: 'Voice-first assistant',    action: () => { onClose(); setTimeout(onAskNova, 80); },    searchText: 'nova ask voice ai assistant' },
      { id: 'qa:new_health_lead',  label: 'Add a health lead',         sub: 'Quick action', tab: 'health_leads' },
      { id: 'qa:import_statement', label: 'Import carrier statement',  sub: 'Quick action', tab: 'health_policies' },
      { id: 'qa:cross_sell',       label: 'Build a cross-sell stack',  sub: 'Quick action', tab: 'cross_sell' },
      { id: 'qa:soa',              label: 'Sign Scope of Appointment', sub: 'Quick action', tab: 'soa' },
      { id: 'qa:coverage_pdf',     label: 'Generate Coverage PDF',     sub: 'Quick action', tab: 'coverage_summary' },
      { id: 'qa:log_activity',     label: 'Log dials / contacts',      sub: 'Quick action', tab: 'daily_tracker' },
      { id: 'qa:log_expense',      label: 'Log an expense',            sub: 'Quick action', tab: 'expenses' },
      { id: 'qa:eligibility',      label: 'Run eligibility check',     sub: 'Quick action', tab: 'health_eligibility' },
    ].filter(Boolean);
    quickActions.forEach(qa => base.push({
      kind: 'action', id: qa.id, label: qa.label, sub: qa.sub, icon: '',
      action: qa.action || (() => onGotoTab(qa.tab)),
      searchText: qa.searchText || `${qa.label} action ${qa.sub}`,
    }));

    // Leads (cap at 200 to avoid huge lists in the corpus pre-filter).
    const activeLeads = (leads || []).filter(l => l.status !== 'closed' && l.status !== 'not_interested' && !l.optOut).slice(0, 200);
    activeLeads.forEach(l => base.push({
      kind: 'lead', id: `lead:${l.id}`, label: l.name || 'Unnamed lead',
      sub: `${(l.vertical || 'life') === 'health' ? '' : ''} Lead${l.phone ? ` · ${l.phone}` : ''}${l.status ? ` · ${l.status}` : ''}`,
      icon: '',
      action: () => onGotoTab((l.vertical || 'life') !== 'life' ? 'health_leads' : 'leads'),
      searchText: `${l.name} ${l.phone || ''} ${l.email || ''}`,
    }));

    // Clients.
    allClients.slice(0, 400).forEach(c => base.push({
      kind: 'client', id: `client:${c.id}`, label: c.name || 'Unnamed client',
      sub: `${c.vertical === 'health' ? (c.sector === 'medicare' ? 'Medicare' : 'ACA / Private') : 'Life'}${c.phone ? ` · ${c.phone}` : ''}`,
      icon: '',
      action: () => onGotoTab(c.vertical === 'health' ? 'health_clients' : 'clients'),
      searchText: `${c.name} ${c.phone || ''}`,
    }));

    return base;
  }, [leads, allClients, onGotoTab]);

  // Score + sort. With empty query, show a curated default list.
  const matches = React.useMemo(() => {
    if (!query.trim()) {
      // Default view: top tabs + quick actions, no leads/clients.
      const defaults = items.filter(it => it.kind === 'tab' || it.kind === 'action').slice(0, 25);
      return defaults;
    }
    const scored = [];
    for (const it of items) {
      const s = paletteScore(it.searchText, query);
      if (s != null) scored.push({ it, s });
    }
    scored.sort((a, b) => a.s - b.s);
    return scored.slice(0, 50).map(x => x.it);
  }, [items, query]);

  // Hand the typed phrase to ARIA (natural-language AI routing). Closes
  // the palette first; invoke() handles navigation / answer surfacing.
  const askAria = React.useCallback(() => {
    const q = query.trim();
    if (!q) return;
    onClose();
    try { ariaInvoke(q); } catch { /* noop */ }
  }, [query, ariaInvoke, onClose]);

  // The list the palette actually renders: fuzzy matches, plus an always-
  // present "Ask ARIA" row whenever the user has typed something — so the
  // bar never dead-ends on "no matches" and any phrasing can reach the AI.
  const displayMatches = React.useMemo(() => {
    const q = query.trim();
    if (!q) return matches;
    return [
      ...matches,
      { kind: 'aria', id: 'aria:ask', label: `Ask ARIA: “${q}”`, sub: 'ASK ARIA · natural language', icon: '✦', action: askAria },
    ];
  }, [matches, query, askAria]);

  React.useEffect(() => { setCursor(0); }, [query]);

  function handleKey(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, displayMatches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(c => Math.max(0, c - 1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const m = displayMatches[cursor];
      if (m?.action) m.action();
    }
  }

  // Group consecutive items with the same `sub` for visual structure.
  let lastGroup = null;

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/50 flex items-start justify-center pt-[10vh] px-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl shadow-2xl border border-slate-300" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-slate-200 px-4 py-3 flex items-center gap-3">
          <span className="text-xl">⌘</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Jump to anything — a tab, client, lead, or action…"
            className="flex-1 text-base font-medium outline-none placeholder:text-slate-400"
          />
          <kbd className="text-[0.55rem] font-black uppercase tracking-wider bg-slate-100 border border-slate-300 px-1.5 py-0.5">ESC</kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {displayMatches.length === 0 ? (
            <div className="px-4 py-10 text-center text-slate-400 text-sm">Type a page, name, or what you want to do…</div>
          ) : (
            displayMatches.map((m, i) => {
              const showGroup = m.sub !== lastGroup;
              lastGroup = m.sub;
              const isAria = m.kind === 'aria';
              return (
                <React.Fragment key={m.id}>
                  {showGroup && (
                    <div className="px-4 pt-3 pb-1 text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-400 sticky top-0 bg-white border-b border-slate-100">{m.sub}</div>
                  )}
                  <button
                    onClick={m.action}
                    onMouseEnter={() => setCursor(i)}
                    className={`w-full text-left flex items-center gap-3 px-4 py-2.5 ${i === cursor ? (isAria ? 'bg-violet-50 border-l-4 border-violet-500' : 'bg-emerald-50 border-l-4 border-emerald-500') : 'border-l-4 border-transparent hover:bg-slate-50'}`}
                  >
                    <span className={`text-lg ${isAria ? 'text-violet-600' : ''}`}>{m.icon}</span>
                    <span className="flex-1 min-w-0">
                      <span className={`block font-bold text-sm truncate ${isAria ? 'text-violet-900' : 'text-slate-900'}`}>{m.label}</span>
                    </span>
                    {isAria && <span className="text-[0.55rem] font-black uppercase tracking-wider text-violet-500">AI</span>}
                    {i === cursor && <kbd className={`text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5 ${isAria ? 'bg-violet-200 text-violet-900' : 'bg-emerald-200 text-emerald-900'}`}>↵</kbd>}
                  </button>
                </React.Fragment>
              );
            })
          )}
        </div>
        <div className="border-t border-slate-200 px-4 py-2 flex items-center gap-3 text-[0.6rem] uppercase tracking-wider text-slate-500">
          <span><kbd className="bg-slate-100 border border-slate-300 px-1">↑↓</kbd> navigate</span>
          <span><kbd className="bg-slate-100 border border-slate-300 px-1">↵</kbd> select</span>
          <span><kbd className="bg-slate-100 border border-slate-300 px-1">esc</kbd> close</span>
          <span className="ml-auto">✦ Ask ARIA for anything in plain English</span>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Calendar — appointment tracker (VA-managed)
// ──────────────────────────────────────────────────────────────────────

export const BLANK_APPOINTMENT = {
  prospectName: '',
  prospectPhone: '',
  prospectEmail: '',
  agentProdNum: '',
  appointmentDate: '',  // YYYY-MM-DD
  appointmentTime: '',  // HH:MM or free text
  type: 'New consultation',
  leadSource: '',
  status: 'scheduled',
  notes: '',
};

// Shared appointment-mutation handlers — booking / status-change / delete,
// including the Google Calendar mirror (appointment_reminders insert +
// /api/google-calendar/upsert-event) and lead-sync side effects. Extracted
// out of CalendarView so BOTH the classic list/month view AND the cinematic
// Calendar Room (src/calendar/CinematicCalendarView.jsx) call the exact
// same booking/reminder/Google-Meet/lead-linking logic — one source of
// truth, no forked copies to drift. `onSaved`/`onDeleted` let each caller
// close its own local editing/confirm-delete UI state.
function useCalendarActions({ calendar, setCalendar, leads, setLeads, currentUser, showToast, onSaved, onDeleted }) {
  // Save handler — attributes new appointments to currentUser for VA activity tracking.
  // If the appointment is linked to a lead (record.leadId or existing a.leadId), mirror
  // date/time/notes back to the lead so the Leads tab stays in sync.
  const syncLead = (rec) => {
    const leadId = rec.leadId;
    if (!leadId) return;
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;
    const dateChanged = (lead.appointmentDate || '') !== (rec.appointmentDate || '');
    const timeChanged = (lead.appointmentTime || '') !== (rec.appointmentTime || '');
    const apptCancelled = rec.status === 'closed_lost' || rec.status === 'no_show';
    setLeads(prev => prev.map(l => {
      if (l.id !== leadId) return l;
      const activity = (l.activity || []).slice();
      if (dateChanged || timeChanged) {
        activity.push({ at: new Date().toISOString(), type: 'appt', body: `Appointment updated to ${rec.appointmentDate ? fmtDateUS(rec.appointmentDate) : '—'}${rec.appointmentTime ? ' ' + rec.appointmentTime : ''} (via Calendar)` });
      }
      let newStatus = l.status;
      if (rec.status === 'closed_won' && l.status !== 'closed_won') {
        newStatus = 'closed_won';
        activity.push({ at: new Date().toISOString(), type: 'status', body: `Stage → Closed Won (via Calendar)` });
      } else if (apptCancelled && l.status === 'appt_set') {
        // Appointment was lost/no-show but lead may still be working — revert to 'contacted'
        newStatus = 'contacted';
        activity.push({ at: new Date().toISOString(), type: 'status', body: `Stage → Contacted (appointment ${rec.status === 'no_show' ? 'no-show' : 'cancelled'} via Calendar)` });
      }
      return {
        ...l,
        appointmentDate: rec.appointmentDate || l.appointmentDate,
        appointmentTime: rec.appointmentTime || l.appointmentTime,
        status: newStatus,
        calendarEventId: rec.id || l.calendarEventId,
        activity,
        updatedAt: new Date().toISOString(),
      };
    }));
  };

  const saveAppt = (record) => {
    const userTag = currentUser ? { username: currentUser.prodNum, role: currentUser.role, name: currentUser.name } : null;
    if (record.id) {
      const existing = calendar.find(a => a.id === record.id);
      setCalendar(prev => prev.map(a => a.id === record.id ? {
        ...a, ...record,
        updatedAt: new Date().toISOString(),
        updatedByUser: userTag || a.updatedByUser,
      } : a));
      // Preserve the leadId from the existing record if the form didn't carry it
      syncLead({ ...record, leadId: record.leadId || existing?.leadId });
      showToast('Appointment updated');
    } else {
      const id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `appt-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const now = new Date().toISOString();
      setCalendar(prev => [...prev, {
        ...record, id,
        createdAt: now, updatedAt: now,
        createdByUser: userTag,
        updatedByUser: userTag,
      }]);
      showToast('Appointment booked');
      // Mirror to appointment_reminders → fires Google Calendar sync
      if (currentUser?.id && record.appointmentDate && record.appointmentTime) {
        const apptAt = `${record.appointmentDate}T${record.appointmentTime}:00`;
        supabase.from('appointment_reminders').insert({
          id,
          owner_id: currentUser.id,
          appointment_at: apptAt,
          title: record.type || 'Appointment',
          client_name: record.prospectName || '',
          client_phone: record.prospectPhone || '',
          notes: record.notes || '',
          status: 'scheduled',
          reminder_24h_enabled: true,
          reminder_1h_enabled: true,
        }).then(({ error: rmErr }) => {
          if (!rmErr) {
            apiFetch('/api/google-calendar/upsert-event', {
              method: 'POST',
              body: JSON.stringify({ appointment_id: id }),
            }).catch((err) => {
              // Surface GCal sync failures — silently
              // dropping them used to leave operators
              // thinking their calendar was in sync
              // when in reality the Google API rejected
              // the upsert (revoked token, missing
              // scope, calendar id changed, etc.).
              // The appointment IS saved in the CRM
              // either way, so the toast says exactly
              // what failed.
              try { showToast?.(`Saved to CRM, but Google Calendar sync failed: ${(err?.message || 'unknown error').slice(0, 100)}`, 'amber'); } catch {}
              // eslint-disable-next-line no-console
              console.warn('[gcal] upsert-event failed:', err?.message || err);
            });
          }
        });
      }
    }
    onSaved?.();
  };

  const setStatus = (id, status) => {
    const appt = calendar.find(a => a.id === id);
    setCalendar(prev => prev.map(a => a.id === id ? { ...a, status, updatedAt: new Date().toISOString() } : a));
    if (appt?.leadId) syncLead({ ...appt, status });
  };

  const deleteAppt = (id) => {
    const appt = calendar.find(a => a.id === id);
    setCalendar(prev => prev.filter(a => a.id !== id));
    onDeleted?.();
    // Break the link on the linked lead so it doesn't point at a vanished event.
    if (appt?.leadId) {
      setLeads(prev => prev.map(l => l.id === appt.leadId ? {
        ...l,
        calendarEventId: '',
        activity: [...(l.activity || []), { at: new Date().toISOString(), type: 'appt', body: 'Calendar appointment deleted' }],
        updatedAt: new Date().toISOString(),
      } : l));
    }
    showToast('Appointment deleted');
  };

  return { saveAppt, setStatus, deleteAppt };
}

function CalendarView({ calendar, setCalendar, leads = [], setLeads = () => {}, producers, settings, openClientProfile, showToast, clientProfiles = {}, allTransactions = [], currentUser = null }) {
  const [editingId, setEditingId] = useState(null); // null | 'new' | id
  const formRef = React.useRef(null);
  // BUG-004 fix: on a long calendar view the Add Appointment form
  // renders below the header + stats + filter bar, so a click on
  // "+ Add appointment" opens the form off-screen and the operator
  // never notices. Scroll into view whenever editingId toggles from
  // null → non-null. Behavior:smooth so it doesn't jar the page.
  React.useEffect(() => {
    if (editingId !== null && formRef.current) {
      try { formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch { /* ignore */ }
    }
  }, [editingId]);
  const [agentFilter, setAgentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateScope, setDateScope] = useState('upcoming'); // 'upcoming' | 'past' | 'today' | 'thisweek' | 'thismonth' | 'all'
  const [search, setSearch] = useState('');
  const [confirmDel, setConfirmDel] = useState(null);
  const [viewMode, setViewMode] = useState('today'); // 'today' | 'list' | 'month'
  const [monthAnchor, setMonthAnchor] = useState(() => {
    const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d;
  });
  const [defaultDateForNew, setDefaultDateForNew] = useState(null); // YYYY-MM-DD when starting a new appt from a day cell
  // Event-type filter chips (multi-select). All on by default.
  const [showAppts, setShowAppts] = useState(true);
  const [showFirstDrafts, setShowFirstDrafts] = useState(true);
  const [showDrafts, setShowDrafts] = useState(true);
  const [show30Day, setShow30Day] = useState(true);
  const [show90Day, setShow90Day] = useState(true);
  const [show6Month, setShow6Month] = useState(true);
  const [showBirthdays, setShowBirthdays] = useState(true);
  const [showAnniversaries, setShowAnniversaries] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');

  // Producers list for the agent filter / form picker
  const agentChoices = React.useMemo(() => {
    const map = {};
    // Brand-new accounts have an empty `producers` map (nothing's
    // been imported yet) and KNOWN_PRODUCERS is the legacy seed list
    // ({} in v1) — so without this seed the agent dropdown is empty
    // and the operator can't book ANYTHING. Always include the
    // current operator + their settings-configured producer persona
    // so the form is usable from day 1.
    if (currentUser?.prodNum) {
      map[currentUser.prodNum] = currentUser.name || currentUser.email || 'You';
    }
    if (settings?.ownerProdNum && settings?.ownerName) {
      map[settings.ownerProdNum] = settings.ownerName;
    }
    Object.keys(KNOWN_PRODUCERS).forEach(pn => { map[pn] = KNOWN_PRODUCERS[pn].name; });
    Object.entries(producers || {}).forEach(([pn, p]) => { if (p?.name) map[pn] = p.name; });
    return Object.entries(map).sort((a, b) => (a[1] || '').localeCompare(b[1] || ''));
  }, [producers, currentUser, settings]);

  const parseAppt = (a) => {
    if (!a.appointmentDate) return null;
    const d = new Date(a.appointmentDate + 'T' + (a.appointmentTime && /^\d{2}:\d{2}/.test(a.appointmentTime) ? a.appointmentTime : '12:00') + ':00');
    return isNaN(d.getTime()) ? null : d;
  };

  // Filtering
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const endOfWeek = new Date(today); endOfWeek.setDate(today.getDate() + (7 - today.getDay()));
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0); endOfMonth.setHours(23, 59, 59, 999);
  const lc = search.trim().toLowerCase();

  const filtered = calendar.filter(a => {
    if (!showAppts) return false;
    if (agentFilter !== 'all' && a.agentProdNum !== agentFilter) return false;
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    if (typeFilter !== 'all' && a.type !== typeFilter) return false;
    if (lc && !`${a.prospectName} ${a.prospectPhone} ${a.notes}`.toLowerCase().includes(lc)) return false;
    if (dateScope === 'all') return true;
    const d = parseAppt(a);
    if (!d) return dateScope === 'all';
    if (dateScope === 'upcoming') return d >= today;
    if (dateScope === 'past') return d < today;
    if (dateScope === 'today') {
      const dDay = new Date(d); dDay.setHours(0, 0, 0, 0);
      return dDay.getTime() === today.getTime();
    }
    if (dateScope === 'thisweek') return d >= today && d <= endOfWeek;
    if (dateScope === 'thismonth') return d >= today && d <= endOfMonth;
    return true;
  });

  const sorted = filtered.slice().sort((a, b) => {
    const ad = parseAppt(a)?.getTime() || 0;
    const bd = parseAppt(b)?.getTime() || 0;
    return dateScope === 'past' ? bd - ad : ad - bd;
  });

  // Compute derived events (drafts / birthdays / anniversaries / milestones) for both views.
  // Defined BEFORE dateGroups so the list-view grouping can merge them in.
  const derivedEventsForMonth = React.useMemo(() => {
    if (!showDrafts && !showBirthdays && !showAnniversaries && !showFirstDrafts && !show30Day && !show90Day && !show6Month) return [];

    let gridStart, gridEnd;
    if (viewMode === 'month') {
      const firstOfMonth = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1);
      gridStart = new Date(firstOfMonth); gridStart.setDate(firstOfMonth.getDate() - firstOfMonth.getDay()); gridStart.setHours(0,0,0,0);
      gridEnd = new Date(gridStart); gridEnd.setDate(gridStart.getDate() + 41); gridEnd.setHours(23, 59, 59, 999);
    } else {
      const t0 = new Date(); t0.setHours(0,0,0,0);
      if (dateScope === 'today') { gridStart = t0; gridEnd = new Date(t0.getTime() + 86399999); }
      else if (dateScope === 'thisweek') { gridStart = t0; gridEnd = new Date(t0); gridEnd.setDate(t0.getDate() + (7 - t0.getDay())); gridEnd.setHours(23,59,59,999); }
      else if (dateScope === 'thismonth') { gridStart = t0; gridEnd = new Date(t0.getFullYear(), t0.getMonth() + 1, 0); gridEnd.setHours(23,59,59,999); }
      else if (dateScope === 'past') { const past = new Date(t0); past.setFullYear(past.getFullYear() - 2); gridStart = past; gridEnd = new Date(t0.getTime() - 1); }
      else if (dateScope === 'all') { const past = new Date(t0); past.setFullYear(past.getFullYear() - 2); const future = new Date(t0); future.setMonth(future.getMonth() + 12); gridStart = past; gridEnd = future; }
      else { gridStart = t0; const upcomingEnd = new Date(t0); upcomingEnd.setMonth(upcomingEnd.getMonth() + 6); gridEnd = upcomingEnd; }
    }

    // Index active clients & their first-policy issue date from transactions
    const clientFirstIssue = {};
    const clientPrimaryAgent = {};
    const policyAnnivs = [];
    const polMap = {};
    (allTransactions || []).forEach(tx => {
      if (!tx.policy || !tx.insured) return;
      const polKey = `${tx.mga||''}|${tx.policy}`;
      if (!polMap[polKey]) polMap[polKey] = { issueDate: tx.issueDate || '', insured: tx.insured, prodNum: tx.prodNum, hasCb: false };
      const p = polMap[polKey];
      if (!p.issueDate && tx.issueDate) p.issueDate = tx.issueDate;
      if (CHARGEBACK_TYPES.has(tx.activityType) || (tx.netToAccount || 0) < 0) p.hasCb = true;
    });
    Object.values(polMap).forEach(p => {
      if (p.hasCb) return;
      if (!p.issueDate) return;
      const ck = clientKeyOf(p.insured);
      if (!clientFirstIssue[ck] || (parseDate(p.issueDate) && parseDate(p.issueDate) < parseDate(clientFirstIssue[ck]))) {
        clientFirstIssue[ck] = p.issueDate;
      }
      const existingAgentDate = clientPrimaryAgent[ck]?.date;
      const curDate = parseDate(p.issueDate);
      if (!existingAgentDate || (curDate && curDate > existingAgentDate)) {
        clientPrimaryAgent[ck] = { prodNum: p.prodNum, name: getProducerName(p.prodNum, producers), date: curDate };
      }
      policyAnnivs.push({
        clientKey: ck, insured: p.insured, issueDate: p.issueDate, prodNum: p.prodNum,
        agentName: getProducerName(p.prodNum, producers),
      });
    });

    const events = [];
    const inRange = (d) => d >= gridStart && d <= gridEnd;
    const yyyymmdd = (d) => `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}`;
    const monthsInView = new Set();
    const oneDay = 86400000;
    for (let d = new Date(gridStart); d <= gridEnd; d = new Date(d.getTime() + oneDay)) {
      monthsInView.add(`${d.getFullYear()}-${d.getMonth()}`);
    }
    const firstName = (full) => titleCase(full || '').split(' ')[0] || '';

    if (showFirstDrafts) {
      policyAnnivs.forEach(p => {
        const d = parseDate(p.issueDate);
        if (!d || !inRange(d)) return;
        events.push({
          id: `firstdraft|${p.clientKey}|${p.issueDate}`,
          kind: 'first_draft', date: d, yyyymmdd: yyyymmdd(d),
          clientKey: p.clientKey, clientName: p.insured, agentName: p.agentName,
          title: `🆕 ${titleCase(p.insured)} 1st draft`,
          subtitle: firstName(p.agentName),
          tooltip: `First draft / policy issue date — ${p.issueDate}\nAgent: ${p.agentName}`,
          color: 'orange',
        });
      });
    }

    const milestones = [
      { enabled: show30Day, days: 30, label: '30-day', color: 'blue', emoji: '' },
      { enabled: show90Day, days: 90, label: '90-day', color: 'violet', emoji: '' },
      { enabled: show6Month, days: 180, label: '6-month', color: 'emerald', emoji: '' },
    ];
    milestones.forEach(ms => {
      if (!ms.enabled) return;
      policyAnnivs.forEach(p => {
        const issued = parseDate(p.issueDate);
        if (!issued) return;
        const d = new Date(issued); d.setDate(issued.getDate() + ms.days);
        if (!inRange(d)) return;
        events.push({
          id: `${ms.label}|${p.clientKey}|${p.issueDate}`,
          kind: `milestone_${ms.label}`, date: d, yyyymmdd: yyyymmdd(d),
          clientKey: p.clientKey, clientName: p.insured, agentName: p.agentName,
          title: `${ms.emoji} ${titleCase(p.insured)} ${ms.label}`,
          subtitle: firstName(p.agentName),
          tooltip: `${ms.label} policy milestone (issued ${p.issueDate})\nAgent: ${p.agentName}`,
          color: ms.color,
        });
      });
    });

    if (showDrafts) {
      Object.entries(clientProfiles || {}).forEach(([clientKey, profile]) => {
        if (!profile?.bankDraftDate) return;
        if (!clientFirstIssue[clientKey]) return;
        const agent = clientPrimaryAgent[clientKey];
        monthsInView.forEach(mk => {
          const [y, m] = mk.split('-').map(Number);
          const d = parseDraftDateForMonth(profile.bankDraftDate, y, m);
          if (d && inRange(d)) {
            events.push({
              id: `draft|${clientKey}|${yyyymmdd(d)}`,
              kind: 'draft', date: d, yyyymmdd: yyyymmdd(d),
              clientKey, clientName: profile.insuredName || clientKey, agentName: agent?.name || '',
              title: `${titleCase(profile.insuredName || clientKey)}`,
              subtitle: firstName(agent?.name),
              tooltip: `Premium draft (${profile.bankDraftDate})${profile.bankName ? ' from ' + profile.bankName : ''}\nAgent: ${agent?.name || 'unknown'}`,
              color: 'amber',
            });
          }
        });
      });
    }

    if (showBirthdays) {
      Object.entries(clientProfiles || {}).forEach(([clientKey, profile]) => {
        if (!profile?.dob) return;
        if (!clientFirstIssue[clientKey]) return;
        const agent = clientPrimaryAgent[clientKey];
        const years = new Set();
        monthsInView.forEach(mk => { years.add(parseInt(mk.split('-')[0], 10)); });
        years.forEach(y => {
          const d = recurringDateForYear(profile.dob, y);
          if (d && inRange(d)) {
            events.push({
              id: `bday|${clientKey}|${yyyymmdd(d)}`,
              kind: 'birthday', date: d, yyyymmdd: yyyymmdd(d),
              clientKey, clientName: profile.insuredName || clientKey, agentName: agent?.name || '',
              title: `${titleCase(profile.insuredName || clientKey)}`,
              subtitle: firstName(agent?.name),
              tooltip: `Birthday (DOB ${profile.dob})\nAgent: ${agent?.name || 'unknown'}`,
              color: 'pink',
            });
          }
        });
      });
    }

    if (showAnniversaries) {
      const yearsInView = new Set();
      monthsInView.forEach(mk => { yearsInView.add(parseInt(mk.split('-')[0], 10)); });
      policyAnnivs.forEach(p => {
        yearsInView.forEach(y => {
          const d = recurringDateForYear(p.issueDate, y);
          if (!d) return;
          if (!inRange(d)) return;
          const issueYear = parseInt(p.issueDate.split('/')[2], 10);
          const yearsSince = y - issueYear;
          if (yearsSince < 1) return;
          events.push({
            id: `anniv|${p.clientKey}|${yyyymmdd(d)}|${yearsSince}`,
            kind: 'anniversary', date: d, yyyymmdd: yyyymmdd(d),
            clientKey: p.clientKey, clientName: p.insured, agentName: p.agentName,
            title: `${titleCase(p.insured)} ${yearsSince}yr`,
            subtitle: firstName(p.agentName),
            tooltip: `${yearsSince}-year policy anniversary (issued ${p.issueDate})\nAgent: ${p.agentName}`,
            color: 'violet',
          });
        });
      });
    }

    return events;
  }, [viewMode, monthAnchor, dateScope, allTransactions, clientProfiles, producers, showFirstDrafts, showDrafts, show30Day, show90Day, show6Month, showBirthdays, showAnniversaries]);

  // Group by date for the list view. Mixes appointments + derived events.
  const groupedByDate = {};
  sorted.forEach(a => {
    const d = parseAppt(a);
    const key = d ? `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}` : 'unscheduled';
    if (!groupedByDate[key]) groupedByDate[key] = { date: d, items: [] };
    groupedByDate[key].items.push({ __type: 'appt', appt: a, time: a.appointmentTime || '' });
  });
  // Add derived events (only in list mode — month grid renders them in its own day cells)
  if (viewMode === 'list') {
    (derivedEventsForMonth || []).forEach(e => {
      const key = e.yyyymmdd;
      if (!key) return;
      if (!groupedByDate[key]) groupedByDate[key] = { date: e.date, items: [] };
      groupedByDate[key].items.push({ __type: 'derived', event: e, time: '' });
    });
  }
  // Sort items within each date group: appointments first (by time), then derived events
  Object.values(groupedByDate).forEach(g => {
    g.items.sort((a, b) => {
      if (a.__type !== b.__type) return a.__type === 'appt' ? -1 : 1;
      return (a.time || '').localeCompare(b.time || '');
    });
  });
  const dateGroups = Object.entries(groupedByDate).sort((a, b) => {
    if (a[0] === 'unscheduled') return 1;
    if (b[0] === 'unscheduled') return -1;
    return dateScope === 'past' ? b[0].localeCompare(a[0]) : a[0].localeCompare(b[0]);
  });

  // Quick stats (over all calendar, not filtered)
  const counts = {
    scheduled: calendar.filter(a => a.status === 'scheduled' && (parseAppt(a)?.getTime() || 0) >= today.getTime()).length,
    closed_won: calendar.filter(a => a.status === 'closed_won').length,
    closed_lost: calendar.filter(a => a.status === 'closed_lost').length,
    no_show: calendar.filter(a => a.status === 'no_show').length,
  };
  const totalClosed = counts.closed_won + counts.closed_lost;
  const closeRate = totalClosed > 0 ? (counts.closed_won / totalClosed) * 100 : null;

  // Booking / status-change / delete handlers (incl. Google Calendar
  // mirror + lead sync) — shared with the cinematic Calendar Room via
  // useCalendarActions() so both surfaces book through one code path.
  const { saveAppt, setStatus, deleteAppt } = useCalendarActions({
    calendar, setCalendar, leads, setLeads, currentUser, showToast,
    onSaved: () => setEditingId(null),
    onDeleted: () => setConfirmDel(null),
  });

  const editingRec = editingId === 'new' ? null : calendar.find(a => a.id === editingId);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs text-slate-300 uppercase tracking-wider font-semibold">Calendar</div>
          <div className="text-2xl font-bold mt-1">Appointments</div>
          <p className="text-sm text-slate-300 mt-1">VAs can add appointments to any agent's calendar, log notes, and mark them won/lost.</p>
        </div>
        <button onClick={() => setEditingId('new')} className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2">+ Add appointment</button>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-3">
          <div className="text-xs text-blue-700 font-semibold uppercase tracking-wide">Upcoming</div>
          <div className="text-2xl font-bold mt-1 text-blue-900">{counts.scheduled}</div>
        </div>
        <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-3">
          <div className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">Closed — won</div>
          <div className="text-2xl font-bold mt-1 text-emerald-900">{counts.closed_won}</div>
        </div>
        <div className="bg-slate-100 border-2 border-slate-200 rounded-xl p-3">
          <div className="text-xs text-slate-700 font-semibold uppercase tracking-wide">Closed — lost</div>
          <div className="text-2xl font-bold mt-1 text-slate-900">{counts.closed_lost}</div>
        </div>
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-3">
          <div className="text-xs text-amber-700 font-semibold uppercase tracking-wide">No-show</div>
          <div className="text-2xl font-bold mt-1 text-amber-900">{counts.no_show}</div>
        </div>
        <div className="bg-violet-50 border-2 border-violet-200 rounded-xl p-3">
          <div className="text-xs text-violet-700 font-semibold uppercase tracking-wide">Close rate</div>
          <div className="text-2xl font-bold mt-1 text-violet-900">{closeRate == null ? '—' : `${closeRate.toFixed(0)}%`}</div>
          <div className="text-xs text-violet-700 mt-0.5">won ÷ (won + lost)</div>
        </div>
      </div>

      {/* Event-type chips */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide mr-1">Show:</span>
        <EventChip label="Appointments" active={showAppts} color="blue" onToggle={() => setShowAppts(v => !v)}/>
        <EventChip label="🆕 First drafts" active={showFirstDrafts} color="orange" onToggle={() => setShowFirstDrafts(v => !v)}/>
        <EventChip label="Drafts" active={showDrafts} color="amber" onToggle={() => setShowDrafts(v => !v)}/>
        <EventChip label="30-day mark" active={show30Day} color="blue" onToggle={() => setShow30Day(v => !v)}/>
        <EventChip label="90-day mark" active={show90Day} color="violet" onToggle={() => setShow90Day(v => !v)}/>
        <EventChip label="6-month mark" active={show6Month} color="emerald" onToggle={() => setShow6Month(v => !v)}/>
        <EventChip label="Birthdays" active={showBirthdays} color="pink" onToggle={() => setShowBirthdays(v => !v)}/>
        <EventChip label="Anniversaries" active={showAnniversaries} color="violet" onToggle={() => setShowAnniversaries(v => !v)}/>
      </div>

      {/* View toggle + Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-sm">
          <button onClick={()=>setViewMode('today')} className={`px-3 py-1.5 font-medium ${viewMode==='today'?'bg-emerald-600 text-white':'text-slate-600 hover:bg-slate-50'}`}>Today</button>
          <button onClick={()=>setViewMode('list')} className={`px-3 py-1.5 font-medium ${viewMode==='list'?'bg-emerald-600 text-white':'text-slate-600 hover:bg-slate-50'}`}>List</button>
          <button onClick={()=>setViewMode('month')} className={`px-3 py-1.5 font-medium ${viewMode==='month'?'bg-emerald-600 text-white':'text-slate-600 hover:bg-slate-50'}`}>Month</button>
        </div>
        {viewMode === 'list' && (
          <select value={dateScope} onChange={e=>setDateScope(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white">
            <option value="upcoming">Upcoming</option>
            <option value="today">Today only</option>
            <option value="thisweek">This week</option>
            <option value="thismonth">This month</option>
            <option value="past">Past</option>
            <option value="all">All</option>
          </select>
        )}
        <select value={agentFilter} onChange={e=>setAgentFilter(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white">
          <option value="all">All agents</option>
          {agentChoices.map(([pn, name]) => <option key={pn} value={pn}>{name}</option>)}
        </select>
        <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white">
          <option value="all">All statuses</option>
          {APPT_STATUS_OPTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <select value={typeFilter} onChange={e=>setTypeFilter(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white">
          <option value="all">All types</option>
          {APPT_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search name, phone, notes" className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm flex-1 min-w-[200px]"/>
        <span className="ml-auto text-xs text-slate-500">{viewMode === 'list' ? `${sorted.length} of ${calendar.length}` : `${calendar.length} total`}</span>
      </div>

      {/* Add/Edit form */}
      {editingId !== null && (
        <div ref={formRef}>
          <AppointmentForm
            initial={editingRec || (defaultDateForNew ? { ...BLANK_APPOINTMENT, appointmentDate: defaultDateForNew } : BLANK_APPOINTMENT)}
            isNew={editingId === 'new'}
            agentChoices={agentChoices}
            onSave={(rec) => { saveAppt(rec); setDefaultDateForNew(null); }}
            onCancel={() => { setEditingId(null); setDefaultDateForNew(null); }}
          />
        </div>
      )}

      {/* Today command view — the scheduling command center default */}
      {viewMode === 'today' && (
        <TodayCommandView
          calendar={calendar}
          agentChoices={agentChoices}
          openClientProfile={openClientProfile}
          onEdit={(id) => setEditingId(id)}
          onSetStatus={(id, s) => setStatus(id, s)}
        />
      )}

      {/* Month-grid view */}
      {viewMode === 'month' && (
        <CalendarMonthGrid
          appointments={filtered}
          derivedEvents={derivedEventsForMonth}
          monthAnchor={monthAnchor}
          setMonthAnchor={setMonthAnchor}
          agentChoices={agentChoices}
          onEditAppt={(id) => setEditingId(id)}
          onAddOnDay={(yyyymmdd) => { setDefaultDateForNew(yyyymmdd); setEditingId('new'); }}
          onOpenClient={openClientProfile}
        />
      )}

      {/* Date-grouped list */}
      {viewMode === 'list' && dateGroups.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-xl py-12 text-center text-slate-400">
          {calendar.length === 0 && (derivedEventsForMonth || []).length === 0
            ? 'Nothing in this window yet. Click "+ Add appointment" to book one, or fill out client profiles so drafts/birthdays/anniversaries populate.'
            : 'No events match these filters. Try a wider date range or toggle more event types above.'}
        </div>
      )}
      {viewMode === 'list' && dateGroups.length > 0 && (
        <div className="space-y-3">
          {dateGroups.map(([dateKey, group]) => {
            const dateLabel = group.date
              ? group.date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })
              : 'Unscheduled';
            const isToday = group.date && group.date.toDateString() === new Date().toDateString();
            return (
              <div key={dateKey} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className={`px-4 py-2 border-b border-slate-200 ${isToday ? 'bg-emerald-50 text-emerald-900' : 'bg-slate-50 text-slate-700'} font-semibold text-sm flex items-center gap-2`}>
                  <span>{dateLabel}</span>
                  {isToday && <span className="text-xs bg-emerald-600 text-white px-1.5 py-0.5 rounded">TODAY</span>}
                  <span className="ml-auto text-xs text-slate-500">{group.items.length} event{group.items.length === 1 ? '' : 's'}</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {group.items.map((item, i) => {
                    if (item.__type === 'appt') {
                      const a = item.appt;
                      return (
                        <AppointmentRow
                          key={a.id}
                          appt={a}
                          agentName={agentChoices.find(([pn]) => pn === a.agentProdNum)?.[1] || a.agentProdNum || 'Unassigned'}
                          onEdit={() => setEditingId(a.id)}
                          onSetStatus={(s) => setStatus(a.id, s)}
                          onDelete={() => setConfirmDel(a.id)}
                          confirming={confirmDel === a.id}
                          onCancelDelete={() => setConfirmDel(null)}
                          onConfirmDelete={() => deleteAppt(a.id)}
                        />
                      );
                    }
                    const e = item.event;
                    const colorBg = {
                      amber: 'bg-amber-50 border-l-amber-500',
                      orange: 'bg-orange-50 border-l-orange-500',
                      pink: 'bg-pink-50 border-l-pink-500',
                      violet: 'bg-violet-50 border-l-violet-500',
                      blue: 'bg-blue-50 border-l-blue-500',
                      emerald: 'bg-emerald-50 border-l-emerald-500',
                    }[e.color] || 'bg-slate-50 border-l-slate-500';
                    return (
                      <button
                        key={`derived-${e.id}`}
                        onClick={() => e.clientKey && openClientProfile(e.clientKey)}
                        className={`w-full text-left p-3 hover:bg-slate-50 border-l-4 ${colorBg} flex items-center gap-3 text-sm`}
                        title={e.tooltip}
                      >
                        <span className="font-semibold">{e.title}</span>
                        {e.agentName && <span className="text-xs text-slate-500">· {e.agentName}</span>}
                        <span className="ml-auto text-xs text-slate-400 italic">{e.tooltip?.split('\n')[0]}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// CinematicCalendarRoom — thin wrapper that mounts inside the Room and
// renders the cinematic presentation. It owns nothing new: agentChoices
// is built the exact same way CalendarView builds it, and booking/status/
// delete run through the SAME useCalendarActions handlers CalendarView
// uses. A "Classic view" toggle lets the operator drop back to the
// original list/month CalendarView (kept fully intact below, unchanged)
// at any time — the redesign is presentation-only, nothing is removed.
function CinematicCalendarRoom({ calendar, setCalendar, leads = [], setLeads = () => {}, producers, settings, openClientProfile, showToast, clientProfiles = {}, allTransactions = [], currentUser = null }) {
  const [classic, setClassic] = useState(false);

  // Same agentChoices construction as CalendarView's memo (KNOWN_PRODUCERS
  // is an intentionally-empty seed today, so omitting it here does not
  // change real-world output).
  const agentChoices = React.useMemo(() => {
    const map = {};
    if (currentUser?.prodNum) map[currentUser.prodNum] = currentUser.name || currentUser.email || 'You';
    if (settings?.ownerProdNum && settings?.ownerName) map[settings.ownerProdNum] = settings.ownerName;
    Object.keys(KNOWN_PRODUCERS).forEach(pn => { map[pn] = KNOWN_PRODUCERS[pn].name; });
    Object.entries(producers || {}).forEach(([pn, p]) => { if (p?.name) map[pn] = p.name; });
    return Object.entries(map).sort((a, b) => (a[1] || '').localeCompare(b[1] || ''));
  }, [producers, currentUser, settings]);

  const { saveAppt, setStatus, deleteAppt } = useCalendarActions({
    calendar, setCalendar, leads, setLeads, currentUser, showToast,
  });

  if (classic) {
    return (
      <div className="space-y-3">
        <button
          onClick={() => setClassic(false)}
          className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-amber-300/40 text-amber-100 bg-black/30 hover:bg-black/50"
        >
          ← Back to cinematic view
        </button>
        <CalendarView
          calendar={calendar} setCalendar={setCalendar} leads={leads} setLeads={setLeads}
          producers={producers} settings={settings} openClientProfile={openClientProfile}
          showToast={showToast} clientProfiles={clientProfiles} allTransactions={allTransactions}
          currentUser={currentUser}
        />
      </div>
    );
  }

  return (
    <React.Suspense fallback={<div className="text-slate-300 text-sm p-6">Loading calendar…</div>}>
      <CinematicCalendarView
        calendar={calendar}
        agentChoices={agentChoices}
        statusOptions={APPT_STATUS_OPTIONS}
        typeOptions={APPT_TYPE_OPTIONS}
        blankAppointment={BLANK_APPOINTMENT}
        FormComponent={AppointmentForm}
        saveAppt={saveAppt}
        setStatus={setStatus}
        deleteAppt={deleteAppt}
        openClientProfile={openClientProfile}
        onGotoClassic={() => setClassic(true)}
      />
    </React.Suspense>
  );
}

function EventChip({ label, active, color = 'slate', onToggle, disabled }) {
  const inactiveColor = 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50';
  const activeColors = {
    blue: 'bg-blue-100 text-blue-800 border-blue-300',
    amber: 'bg-amber-100 text-amber-800 border-amber-300',
    orange: 'bg-orange-100 text-orange-800 border-orange-300',
    pink: 'bg-pink-100 text-pink-800 border-pink-300',
    violet: 'bg-violet-100 text-violet-800 border-violet-300',
    indigo: 'bg-indigo-100 text-indigo-800 border-indigo-300',
    emerald: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    slate: 'bg-slate-200 text-slate-800 border-slate-300',
  };
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${active ? (activeColors[color] || activeColors.slate) : inactiveColor}`}
    >
      {active ? '✓ ' : ''}{label}
    </button>
  );
}

function CalendarMonthGrid({ appointments, derivedEvents = [], monthAnchor, setMonthAnchor, agentChoices, onEditAppt, onAddOnDay, onOpenClient = () => {} }) {
  // Compute the 6-week (42-day) grid starting on the Sunday before/on the 1st of the month.
  const firstOfMonth = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1);
  const gridStart = new Date(firstOfMonth);
  gridStart.setDate(firstOfMonth.getDate() - firstOfMonth.getDay()); // back up to Sunday

  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart); d.setDate(gridStart.getDate() + i);
    d.setHours(0, 0, 0, 0);
    days.push(d);
  }

  const dateKey = (d) => `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}`;
  const yyyymmdd = dateKey;

  // Index appointments by date string for fast lookup
  const byDay = {};
  appointments.forEach(a => {
    if (!a.appointmentDate) return;
    if (!byDay[a.appointmentDate]) byDay[a.appointmentDate] = [];
    byDay[a.appointmentDate].push(a);
  });
  Object.keys(byDay).forEach(k => {
    byDay[k].sort((a, b) => (a.appointmentTime || '').localeCompare(b.appointmentTime || ''));
  });

  // Index derived events (drafts / birthdays / anniversaries) by date
  const derivedByDay = {};
  (derivedEvents || []).forEach(e => {
    if (!e.yyyymmdd) return;
    if (!derivedByDay[e.yyyymmdd]) derivedByDay[e.yyyymmdd] = [];
    derivedByDay[e.yyyymmdd].push(e);
  });

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayKey = dateKey(today);

  const monthName = monthAnchor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const goPrev = () => {
    const d = new Date(monthAnchor); d.setMonth(d.getMonth() - 1); d.setDate(1);
    setMonthAnchor(d);
  };
  const goNext = () => {
    const d = new Date(monthAnchor); d.setMonth(d.getMonth() + 1); d.setDate(1);
    setMonthAnchor(d);
  };
  const goToday = () => {
    const d = new Date(); d.setDate(1); d.setHours(0,0,0,0);
    setMonthAnchor(d);
  };

  const findAgentName = (pn) => agentChoices.find(([k]) => k === pn)?.[1] || '';

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <button onClick={goPrev} className="px-2 py-1 text-sm bg-white border border-slate-200 rounded hover:bg-slate-100">‹</button>
          <button onClick={goToday} className="px-2 py-1 text-xs bg-white border border-slate-200 rounded hover:bg-slate-100">Today</button>
          <button onClick={goNext} className="px-2 py-1 text-sm bg-white border border-slate-200 rounded hover:bg-slate-100">›</button>
          <div className="font-bold text-lg ml-2">{monthName}</div>
        </div>
        <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500"/>Scheduled</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"/>Won</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-400"/>Lost</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"/>No-show</span>
        </div>
      </div>

      {/* Day-of-week header */}
      <div className="grid grid-cols-7 text-xs font-semibold text-slate-500 border-b border-slate-200">
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
          <div key={d} className="px-2 py-1 text-center">{d}</div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7" style={{ gridAutoRows: 'minmax(110px, auto)' }}>
        {days.map((d, idx) => {
          const inMonth = d.getMonth() === monthAnchor.getMonth();
          const isToday = dateKey(d) === todayKey;
          const dayAppts = byDay[dateKey(d)] || [];
          const dayDerived = derivedByDay[dateKey(d)] || [];
          const totalDay = dayAppts.length + dayDerived.length;
          return (
            <div
              key={idx}
              className={`border-b border-r border-slate-100 p-1.5 flex flex-col gap-1 ${inMonth ? 'bg-white' : 'bg-slate-50'} ${isToday ? 'ring-2 ring-emerald-400 ring-inset' : ''}`}
            >
              <div className="flex items-center justify-between">
                <div className={`text-xs ${inMonth ? 'text-slate-700 font-semibold' : 'text-slate-400'} ${isToday ? 'text-emerald-700' : ''}`}>{d.getDate()}</div>
                <button
                  onClick={() => onAddOnDay(yyyymmdd(d))}
                  className="text-[10px] text-slate-400 hover:text-emerald-700 px-1"
                  title="Add appointment on this day"
                >+</button>
              </div>
              <div className="space-y-0.5 overflow-hidden">
                {dayAppts.slice(0, 4).map(a => {
                  const status = APPT_STATUS_OPTIONS.find(s => s.id === a.status) || APPT_STATUS_OPTIONS[0];
                  const colorBg = {
                    blue: 'bg-blue-100 text-blue-800 border-blue-300',
                    emerald: 'bg-emerald-100 text-emerald-800 border-emerald-300',
                    slate: 'bg-slate-100 text-slate-700 border-slate-300',
                    amber: 'bg-amber-100 text-amber-800 border-amber-300',
                    red: 'bg-red-100 text-red-800 border-red-300',
                    violet: 'bg-violet-100 text-violet-800 border-violet-300',
                  }[status.color] || 'bg-slate-100';
                  return (
                    <button
                      key={a.id}
                      onClick={() => onEditAppt(a.id)}
                      title={`${a.appointmentTime ? a.appointmentTime + ' — ' : ''}${a.prospectName} (${status.label})${a.notes ? '\n\n' + a.notes : ''}`}
                      className={`w-full text-left text-[10px] leading-tight px-1.5 py-0.5 rounded border ${colorBg} truncate hover:opacity-80`}
                    >
                      {a.appointmentTime && <span className="font-mono">{a.appointmentTime} </span>}
                      <span className="font-semibold">{a.prospectName || '?'}</span>
                      {findAgentName(a.agentProdNum) && <span className="opacity-70"> · {findAgentName(a.agentProdNum).split(' ')[0]}</span>}
                    </button>
                  );
                })}
                {dayAppts.length > 4 && (
                  <div className="text-[10px] text-slate-500 italic">+{dayAppts.length - 4} more appt…</div>
                )}
                {dayDerived.slice(0, 4).map(e => {
                  const colorBg = {
                    amber: 'bg-amber-100 text-amber-800 border-amber-300',
                    orange: 'bg-orange-100 text-orange-800 border-orange-300',
                    pink: 'bg-pink-100 text-pink-800 border-pink-300',
                    violet: 'bg-violet-100 text-violet-800 border-violet-300',
                    blue: 'bg-blue-100 text-blue-800 border-blue-300',
                    emerald: 'bg-emerald-100 text-emerald-800 border-emerald-300',
                  }[e.color] || 'bg-slate-100 text-slate-700 border-slate-300';
                  return (
                    <button
                      key={e.id}
                      onClick={() => e.clientKey && onOpenClient(e.clientKey)}
                      title={e.tooltip}
                      className={`w-full text-left text-[10px] leading-tight px-1.5 py-0.5 rounded border ${colorBg} truncate hover:opacity-80`}
                    >
                      <span className="font-semibold">{e.title}</span>
                      {e.subtitle && <span className="opacity-70"> · {e.subtitle}</span>}
                    </button>
                  );
                })}
                {dayDerived.length > 4 && (
                  <div className="text-[10px] text-slate-500 italic">+{dayDerived.length - 4} more event…</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AppointmentRow({ appt, agentName, onEdit, onSetStatus, onDelete, confirming, onCancelDelete, onConfirmDelete }) {
  const [open, setOpen] = useState(false);
  const status = APPT_STATUS_OPTIONS.find(s => s.id === appt.status) || APPT_STATUS_OPTIONS[0];

  return (
    <div className="p-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded text-xs font-bold bg-${status.color}-100 text-${status.color}-700`}>{status.label}</span>
            <span className="font-semibold">{appt.prospectName || '— no name —'}</span>
            <span className="text-xs text-slate-500">· {appt.type || 'consultation'}</span>
            {appt.appointmentTime && <span className="text-xs text-slate-500">· {appt.appointmentTime}</span>}
            <span className="text-xs text-slate-500">· {agentName}</span>
          </div>
          <div className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
            {appt.prospectPhone && <span className="font-mono">{appt.prospectPhone}</span>}
            {appt.prospectEmail && <span>· {appt.prospectEmail}</span>}
            {appt.leadSource && <span>· source: {appt.leadSource}</span>}
            {appt.notes && <button onClick={() => setOpen(o => !o)} className="text-emerald-700 hover:underline">{open ? 'hide notes' : 'show notes'}</button>}
          </div>
          {open && appt.notes && (
            <div className="mt-2 bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm whitespace-pre-wrap">{appt.notes}</div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0 flex-wrap">
          {appt.status === 'scheduled' && (
            <>
              <button onClick={() => onSetStatus('closed_won')} className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded font-semibold" title="Mark closed — won">✓ Won</button>
              <button onClick={() => onSetStatus('closed_lost')} className="text-xs bg-slate-200 hover:bg-slate-300 text-slate-800 px-2 py-1 rounded font-semibold" title="Mark closed — lost">✗ Lost</button>
              <button onClick={() => onSetStatus('no_show')} className="text-xs bg-amber-100 hover:bg-amber-200 text-amber-800 px-2 py-1 rounded" title="Mark no-show">No-show</button>
            </>
          )}
          <button onClick={onEdit} className="text-slate-500 hover:text-emerald-700 p-1" title="Edit"></button>
          <button onClick={onDelete} className="text-slate-500 hover:text-red-600 p-1" title="Delete"><Trash2 size={14}/></button>
        </div>
      </div>
      {confirming && (
        <div className="mt-2 bg-red-50 border border-red-200 rounded px-3 py-2 text-sm">
          Delete this appointment? <button onClick={onConfirmDelete} className="ml-2 bg-red-600 hover:bg-red-700 text-white px-2 py-0.5 rounded text-xs font-semibold">Confirm</button>
          <button onClick={onCancelDelete} className="ml-1 bg-white border border-slate-300 hover:bg-slate-50 px-2 py-0.5 rounded text-xs">Cancel</button>
        </div>
      )}
    </div>
  );
}

export function AppointmentForm({ initial, isNew, agentChoices, onSave, onCancel }) {
  const [r, setR] = useState({ ...initial });
  const [errors, setErrors] = useState({});
  const set = (field, value) => setR(prev => ({ ...prev, [field]: value }));

  const [soaGate, setSoaGate] = useState(null);   // null | { gate_satisfied, hours_since_soa, hours_until_satisfied }
  const [checkingGate, setCheckingGate] = useState(false);

  const trySave = async () => {
    const errs = {};
    if (!r.prospectName?.trim()) errs.prospectName = 'Required';
    if (!r.appointmentDate?.trim()) errs.appointmentDate = 'Required';
    if (!r.agentProdNum?.trim()) errs.agentProdNum = 'Pick an agent';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    // SOA 48-hr gate for Medicare enrollment appts. We only call the
    // endpoint if a client_id is on the form (linked appt). For un-
    // linked walk-ins we let it through with an inline warning rather
    // than block — the operator still owns the compliance call.
    if (SOA_GATED_APPT_TYPES.has(r.type) && r.clientId) {
      setCheckingGate(true);
      try {
        const j = await apiFetch(`/api/medicare/soa-gate?client_id=${encodeURIComponent(r.clientId)}`);
        setSoaGate(j);
        if (!j.gate_satisfied) {
          setCheckingGate(false);
          // Don't save until operator either fixes it or overrides.
          return;
        }
      } catch {
        // Endpoint failure shouldn't block booking — log and proceed.
      } finally {
        setCheckingGate(false);
      }
    }
    onSave(r);
  };
  const overrideGate = () => { setSoaGate(null); onSave(r); };

  return (
    <div className="bg-white rounded-xl border-2 border-emerald-300 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-bold">{isNew ? 'Book new appointment' : 'Edit appointment'}</h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
      </div>

      {/* SOA 48-hr gate — blocks Medicare enrollment appts when the
          client doesn't have a CMS-compliant SOA on file ≥48 hours
          ago. Operator can override (rare, but some carriers allow
          documented exceptions). */}
      {soaGate && !soaGate.gate_satisfied && (
        <div className="bg-rose-50 border-2 border-rose-300 rounded-lg p-3 text-sm">
          <div className="flex items-center gap-2 font-bold text-rose-900 mb-1">
            <AlertTriangle size={16}/> SOA 48-hour gate not satisfied
          </div>
          <div className="text-rose-800">
            {soaGate.has_soa
              ? `SOA signed ${soaGate.hours_since_soa} hours ago — need ${soaGate.hours_until_satisfied} more hour(s) before booking this MA enrollment appointment.`
              : 'No SOA on file for this client. CMS requires a signed Scope of Appointment ≥48 hours before any Medicare Advantage / PDP enrollment discussion. Send one now.'}
          </div>
          <div className="mt-2 flex gap-2">
            <button onClick={() => setSoaGate(null)} className="bg-white border border-rose-300 hover:border-rose-500 text-rose-900 text-xs font-bold px-3 py-1.5 rounded">Cancel</button>
            <button onClick={overrideGate} className="bg-rose-700 hover:bg-rose-800 text-white text-xs font-bold px-3 py-1.5 rounded">Override + book anyway</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <label className="block md:col-span-1">
          <span className="text-xs text-slate-500">Prospect name *</span>
          <input value={r.prospectName} onChange={e=>set('prospectName', e.target.value)} className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${errors.prospectName ? 'border-red-400' : 'border-slate-200'}`}/>
          {errors.prospectName && <span className="text-xs text-red-600">{errors.prospectName}</span>}
        </label>
        <label className="block">
          <span className="text-xs text-slate-500">Phone</span>
          <input value={r.prospectPhone || ''} onChange={e=>set('prospectPhone', e.target.value)} placeholder="(555) 123-4567" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
        </label>
        <label className="block">
          <span className="text-xs text-slate-500">Email</span>
          <input type="email" value={r.prospectEmail || ''} onChange={e=>set('prospectEmail', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
        </label>
        <label className="block">
          <span className="text-xs text-slate-500">Agent *</span>
          <select value={r.agentProdNum} onChange={e=>set('agentProdNum', e.target.value)} className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white ${errors.agentProdNum ? 'border-red-400' : 'border-slate-200'}`}>
            <option value="">— Select —</option>
            {agentChoices.map(([pn, name]) => <option key={pn} value={pn}>{name}</option>)}
          </select>
          {errors.agentProdNum && <span className="text-xs text-red-600">{errors.agentProdNum}</span>}
        </label>
        <label className="block">
          <span className="text-xs text-slate-500">Date *</span>
          <input type="date" value={r.appointmentDate} onChange={e=>set('appointmentDate', e.target.value)} className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${errors.appointmentDate ? 'border-red-400' : 'border-slate-200'}`}/>
          {errors.appointmentDate && <span className="text-xs text-red-600">{errors.appointmentDate}</span>}
        </label>
        <label className="block">
          <span className="text-xs text-slate-500">Time</span>
          <input type="time" value={r.appointmentTime || ''} onChange={e=>set('appointmentTime', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
        </label>
        <label className="block">
          <span className="text-xs text-slate-500">Type</span>
          <select value={r.type} onChange={e=>set('type', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
            {APPT_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-slate-500">Status</span>
          <select value={r.status} onChange={e=>set('status', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
            {APPT_STATUS_OPTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-slate-500">Lead source</span>
          <input value={r.leadSource || ''} onChange={e=>set('leadSource', e.target.value)} placeholder="Facebook ad, referral, walk-in…" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
        </label>
        <label className="block md:col-span-3">
          <span className="text-xs text-slate-500">Notes</span>
          <textarea value={r.notes || ''} onChange={e=>set('notes', e.target.value)} rows={3} placeholder="Context, prep notes, what to ask…" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
        </label>
      </div>
      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
        <button onClick={onCancel} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
        <button onClick={trySave} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold">{isNew ? 'Book appointment' : 'Update'}</button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// VA Daily Plan — auto-generates today's task list from the agency's data
// and groups it by the 6 profit levers (in priority order). Each task has
// a one-sentence WHY and 1-3 HOW steps so the VA doesn't have to think,
// just execute. Completion records persist in vaTaskDone (works for both
// auto-generated and manual tasks since IDs are stable).
//
// Profit levers (ordered by impact for life/FE telesales):
//   1. PERSISTENCY     — welcome calls, 30-day check-ins, save calls
//   2. NEW BUSINESS    — appointment confirms, app follow-ups, pending chases
//   3. RECONCILIATION  — weekly carrier statement reconciliation
//   4. LEAD OPS        — assigning new leads, CPA review, stale-lead recycling
//   5. RECRUITING      — manual templates only (no data source yet)
//   6. CRM HYGIENE     — calendar scrubs, KPI reports, consent gaps
// ──────────────────────────────────────────────────────────────────────
const VA_CATEGORIES = [
  { id: 'persistency',     label: 'Persistency & Chargeback Prevention', icon: '', color: 'emerald', priority: 1, sub: 'The #1 profit lever. Every save here is pure margin.' },
  { id: 'newbusiness',     label: 'New Business Processing',             icon: '', color: 'sky',     priority: 2, sub: 'Faster placement = faster commission. Don\'t let apps die in pending.' },
  { id: 'reconciliation',  label: 'Commission Reconciliation',           icon: '', color: 'amber',   priority: 3, sub: 'Weekly cadence keeps Commission Command accurate.' },
  { id: 'leadops',         label: 'Lead & Pipeline Ops',                 icon: '', color: 'violet',  priority: 4, sub: 'CPA is where money quietly leaks.' },
  { id: 'recruiting',      label: 'Recruiting & Contracting',            icon: '', color: 'orange',  priority: 5, sub: 'Faster contracting = more producers = more revenue.' },
  { id: 'crm',             label: 'CRM Hygiene & Reporting',             icon: '', color: 'slate',   priority: 6, sub: 'Clean data + weekly KPI report = owner visibility.' },
];
const VA_CATEGORY_BY_ID = Object.fromEntries(VA_CATEGORIES.map(c => [c.id, c]));

// Parse a MM/DD/YYYY-ish issueDate string and return a Date or null.
const parseUsDate = (s) => {
  if (!s) return null;
  const d = parseDate(s);
  return d && Number.isFinite(d.getTime()) ? d : null;
};

// Build today's auto-generated tasks from the agency's data. Pure function —
// every task has a stable id derived from the entity + window so flipping
// "done" in vaTaskDone persists across renders and across days.
function buildAutoVaTasks({ leads, transactions, calendar, outreach, calls, clientProfiles, producers, settings }) {
  const today = new Date();
  const todayMs = today.getTime();
  const dayMs = 1000 * 60 * 60 * 24;
  const todayStr = today.toISOString().slice(0, 10);
  const dow = today.getDay(); // 0=Sun, 1=Mon, ..., 5=Fri
  const tasks = [];

  // Build a per-policy index from transactions — needed for persistency tasks.
  const polMap = {};
  (transactions || []).forEach(tx => {
    if (!tx.policy) return;
    const k = `${tx.mga || ''}|${tx.policy}`;
    if (!polMap[k]) {
      polMap[k] = { key: k, policy: tx.policy, insured: tx.insured || '', prodNum: tx.prodNum,
                    issueDate: tx.issueDate || '', hasCb: false, firstCbDate: '',
                    premium: 0 };
    }
    const p = polMap[k];
    if (CHARGEBACK_TYPES.has(tx.activityType) || (Number(tx.netToAccount) || 0) < 0) {
      p.hasCb = true;
      const d = tx.activityDate;
      if (!p.firstCbDate || (parseDate(d)?.getTime() || 0) < (parseDate(p.firstCbDate)?.getTime() || 0)) {
        p.firstCbDate = d;
      }
    }
    const prem = Math.abs(Number(tx.commPremium) || 0);
    if (prem > p.premium) p.premium = prem;
    if (!p.issueDate && tx.issueDate) p.issueDate = tx.issueDate;
  });
  const policies = Object.values(polMap);

  // ── 1. PERSISTENCY ──────────────────────────────────────────────
  policies.forEach(p => {
    const issued = parseUsDate(p.issueDate);
    if (!issued) return;
    const ageDays = Math.floor((todayMs - issued.getTime()) / dayMs);
    const profile = clientProfiles?.[clientKeyOf(p.insured)] || {};
    const phone = profile.phone || '';

    // Welcome / first-payment confirmation — days 1-7 after issue
    if (ageDays >= 1 && ageDays <= 7 && !p.hasCb) {
      tasks.push({
        id: `auto_welcome_${p.key}`,
        category: 'persistency',
        title: `Welcome call: ${p.insured}`,
        why: 'First-payment confirmation is the #1 way to prevent 30-day chargebacks.',
        howSteps: [
          `Call ${phone || '(get phone from client profile)'}`,
          `Confirm bank draft date and amount`,
          `Set expectations: "You\'ll see the first draft on [date]. Call me if anything looks off."`,
        ],
        dueDate: todayStr,
        relatedPolicyKey: p.key,
        relatedClientKey: clientKeyOf(p.insured),
      });
    }
    // 30-day check-in — days 25-35
    if (ageDays >= 25 && ageDays <= 35 && !p.hasCb) {
      tasks.push({
        id: `auto_30day_${p.key}`,
        category: 'persistency',
        title: `30-day check-in: ${p.insured}`,
        why: 'Catches buyer\'s remorse BEFORE the chargeback hits. One call here prevents a clawback that costs the agency 12+ months of commission.',
        howSteps: [
          `Call ${phone || '(get phone from client profile)'}`,
          `Ask: "How\'d the first draft look? Any questions about your coverage?"`,
          `If hesitation: review benefits, offer downgrade instead of cancel.`,
        ],
        dueDate: todayStr,
        relatedPolicyKey: p.key,
        relatedClientKey: clientKeyOf(p.insured),
      });
    }
    // Save call — chargeback within last 14 days
    if (p.hasCb && p.firstCbDate) {
      const cbDate = parseUsDate(p.firstCbDate);
      if (cbDate) {
        const cbAge = Math.floor((todayMs - cbDate.getTime()) / dayMs);
        if (cbAge <= 14) {
          tasks.push({
            id: `auto_save_${p.key}_${p.firstCbDate}`,
            category: 'persistency',
            title: `Save call attempt: ${p.insured}`,
            why: 'Reversals can be reversed within ~14 days. Every save = pure margin recovered.',
            howSteps: [
              `Call ${phone || '(get phone)'}`,
              `Ask why they cancelled — listen, don\'t pitch`,
              `Offer alternative: lower face amount, different draft date, or different product`,
            ],
            dueDate: todayStr,
            relatedPolicyKey: p.key,
            relatedClientKey: clientKeyOf(p.insured),
          });
        }
      }
    }
  });

  // ── 2. NEW BUSINESS PROCESSING ──────────────────────────────────
  // Appointments TODAY — confirm them
  (calendar || []).forEach(a => {
    if (a.appointmentDate !== todayStr) return;
    if (a.status && (a.status === 'closed_won' || a.status === 'closed_lost' || a.status === 'no_show')) return;
    tasks.push({
      id: `auto_appt_confirm_${a.id}`,
      category: 'newbusiness',
      title: `Confirm appointment: ${a.prospectName || 'unnamed'}${a.appointmentTime ? ` at ${a.appointmentTime}` : ''}`,
      why: 'Confirmation calls cut no-show rate roughly in half.',
      howSteps: [
        `Text or call ${a.prospectPhone || '(get phone)'}`,
        `Confirm date/time + 2-minute reminder of what to have ready (driver\'s license, bank info)`,
        `If no answer: text — log result back in Calendar`,
      ],
      dueDate: todayStr,
    });
  });

  // Leads stuck in 'showed' for 3+ days — chase the app
  (leads || []).forEach(l => {
    if (l.status !== 'showed') return;
    const changed = l.statusChangedAt ? new Date(l.statusChangedAt).getTime() : 0;
    if (!changed) return;
    const ageDays = Math.floor((todayMs - changed) / dayMs);
    if (ageDays < 3) return;
    tasks.push({
      id: `auto_appfollowup_${l.id}`,
      category: 'newbusiness',
      title: `App follow-up: ${l.name}`,
      why: `Lead has been in "Showed" for ${ageDays} days. Apps die in pending — push to Closed or Recycle.`,
      howSteps: [
        `Call the producer assigned to this lead (${getProducerName(l.agentProdNum, producers)})`,
        `Ask: "Did this client submit? What\'s blocking us?"`,
        `If submitted: update lead to Closed. If dead: move to Recycled.`,
      ],
      dueDate: todayStr,
      relatedLeadId: l.id,
    });
  });

  // ── 3. COMMISSION RECONCILIATION ────────────────────────────────
  if (dow === 1) { // Monday
    tasks.push({
      id: `auto_recon_${todayStr}`,
      category: 'reconciliation',
      title: 'Pull last week\'s carrier statements and reconcile',
      why: 'Weekly cadence keeps Commission Command accurate. Skipping a week creates 2x the work next time.',
      howSteps: [
        'Download MOO, Americo, Transamerica weekly statements',
        'Upload via the Dashboard Upload button',
        'Open Chargebacks tab — flag any deposit-to-statement discrepancies',
      ],
      dueDate: todayStr,
    });
  }
  if (dow === 5) { // Friday
    tasks.push({
      id: `auto_payout_prep_${todayStr}`,
      category: 'reconciliation',
      title: 'Prep weekly payout numbers for owner review',
      why: 'Weekly payout cadence to producers depends on accurate, on-time numbers.',
      howSteps: [
        'Open Dashboard, set period to "Week" → this week',
        'Cross-check per-agent net against bank deposits',
        'Note any agent whose net seems off from expectation; flag for owner',
      ],
      dueDate: todayStr,
    });
  }

  // ── 4. LEAD & PIPELINE OPS ──────────────────────────────────────
  const unassignedToday = (leads || []).filter(l => {
    if (l.agentProdNum) return false;
    if (!l.createdAt) return false;
    return l.createdAt.slice(0, 10) === todayStr;
  });
  if (unassignedToday.length > 0) {
    tasks.push({
      id: `auto_assign_${todayStr}`,
      category: 'leadops',
      title: `Assign ${unassignedToday.length} new lead${unassignedToday.length === 1 ? '' : 's'} from today`,
      why: 'Speed-to-lead clock is running on every one. Industry rule: contact within 5 minutes wins ~80% of the time.',
      howSteps: [
        'Open Leads → Table view',
        'Filter status = New, Agent = unassigned',
        'Round-robin assign to producers who are licensed for the lead\'s state',
      ],
      dueDate: todayStr,
    });
  }
  // Stale "new" leads — never contacted, 7+ days old
  const staleNew = (leads || []).filter(l => l.status === 'new' && !l.firstContactedAt && l.createdAt && (todayMs - new Date(l.createdAt).getTime()) >= 7 * dayMs);
  if (staleNew.length > 0) {
    tasks.push({
      id: `auto_stale_${todayStr}`,
      category: 'leadops',
      title: `Recycle or re-engage ${staleNew.length} stale lead${staleNew.length === 1 ? '' : 's'}`,
      why: 'Leads sitting in "New" 7+ days without contact = dead money. Either re-engage or move to Recycled.',
      howSteps: [
        'Open Leads → Table → Freshness filter = Never contacted',
        'For each: assign to a producer OR move to Recycled',
      ],
      dueDate: todayStr,
    });
  }
  if (dow === 1) {
    tasks.push({
      id: `auto_cpa_${todayStr}`,
      category: 'leadops',
      title: 'Pull lead spend by source and flag high-CPA vendors',
      why: 'CPA is where money quietly leaks. A 2x cost-per-issued-policy spread on two vendors is a real budget decision.',
      howSteps: [
        'Open Leads → Pipeline view → scroll to "Spend by source" table',
        'For each source, divide Spend ÷ Closed to get CPA',
        'Email owner with any source spending $X+ where CPA is >2x agency average',
      ],
      dueDate: todayStr,
    });
  }

  // ── 5. RECRUITING (no data source — surface a manual reminder weekly)
  if (dow === 2) { // Tuesday
    tasks.push({
      id: `auto_recruiting_${todayStr}`,
      category: 'recruiting',
      title: 'Recruiting & contracting check-in',
      why: 'Growth = more producers. Contracting is the bottleneck.',
      howSteps: [
        'Check pending E&O / licensing / appointment paperwork for any new agent',
        'Email the carrier upline for status on any agent waiting >5 business days',
        'Schedule any interview slots requested this week',
      ],
      dueDate: todayStr,
    });
  }

  // ── 6. CRM HYGIENE ──────────────────────────────────────────────
  // Daily — consent gap check (leads with no consent + flagged for outreach)
  const consentGap = (leads || []).filter(l => !l.consentAt && !l.optOut && !DEAD_STAGES.has(l.status)).length;
  if (consentGap > 0) {
    tasks.push({
      id: `auto_consent_${todayStr}`,
      category: 'crm',
      title: `${consentGap} active lead${consentGap === 1 ? '' : 's'} missing TCPA consent on record`,
      why: 'TCPA/A2P penalties are per-message. Capture consent BEFORE calls or SMS go out.',
      howSteps: [
        'Open Leads → Today\'s Focus, then Pipeline',
        'For each lead without the ✓ consent badge: capture verbal or written consent in the drawer',
      ],
      dueDate: todayStr,
    });
  }
  if (dow === 5) { // Friday KPI snapshot
    tasks.push({
      id: `auto_kpi_${todayStr}`,
      category: 'crm',
      title: 'Send weekly KPI snapshot to owner',
      why: 'Owner can\'t coach what they can\'t see.',
      howSteps: [
        'Screenshot: Leads close rate (last 7 days), Persistency 30d, Speed-to-lead median',
        'Email to owner with one-sentence comment on each',
      ],
      dueDate: todayStr,
    });
  }

  return tasks;
}

function VADailyPlan({ leads, transactions, calendar, outreach, calls, clientProfiles, producers, settings, vaTasks, setVaTasks, vaTaskDone, setVaTaskDone, openClientProfile, currentUser }) {
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState({ category: 'persistency', title: '', why: '', howSteps: '', dueDate: new Date().toISOString().slice(0, 10) });
  const [hideDone, setHideDone] = useState(false);

  const autoTasks = React.useMemo(
    () => buildAutoVaTasks({ leads, transactions, calendar, outreach, calls, clientProfiles, producers, settings }),
    [leads, transactions, calendar, outreach, calls, clientProfiles, producers, settings]
  );

  const todayStr = new Date().toISOString().slice(0, 10);
  // Manual tasks: only show today's or overdue.
  const visibleManualTasks = (vaTasks || []).filter(t => !t.dueDate || t.dueDate <= todayStr);
  const allTasks = [...autoTasks, ...visibleManualTasks];

  const taskCompletion = (t) => vaTaskDone?.[t.id] || null;
  const visibleTasks = hideDone ? allTasks.filter(t => !taskCompletion(t)) : allTasks;

  const tasksByCat = React.useMemo(() => {
    const m = {};
    VA_CATEGORIES.forEach(c => { m[c.id] = []; });
    visibleTasks.forEach(t => { (m[t.category] || (m[t.category] = [])).push(t); });
    return m;
  }, [visibleTasks]);

  const totalCount = allTasks.length;
  const doneCount = allTasks.filter(taskCompletion).length;
  const donePct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  const toggleDone = (task) => {
    const completed = taskCompletion(task);
    if (completed) {
      setVaTaskDone(prev => {
        const next = { ...prev };
        delete next[task.id];
        return next;
      });
    } else {
      setVaTaskDone(prev => ({
        ...prev,
        [task.id]: {
          completedAt: new Date().toISOString(),
          completedBy: currentUser?.email || currentUser?.name || 'va',
          note: '',
        },
      }));
    }
  };

  const addManualTask = () => {
    if (!draft.title.trim()) return;
    const id = `manual_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setVaTasks(prev => [...prev, {
      id,
      category: draft.category,
      title: draft.title.trim(),
      why: draft.why.trim(),
      howSteps: draft.howSteps.split('\n').map(s => s.trim()).filter(Boolean),
      dueDate: draft.dueDate || todayStr,
      createdAt: new Date().toISOString(),
    }]);
    setDraft({ category: 'persistency', title: '', why: '', howSteps: '', dueDate: todayStr });
    setAddOpen(false);
  };

  const removeManualTask = async (id) => {
    if (!(await hqConfirm({
      title: 'Remove this task?',
      confirmLabel: 'Remove',
      danger: true,
    }))) return;
    setVaTasks(prev => prev.filter(t => t.id !== id));
    setVaTaskDone(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  return (
    <div className="bg-white border-2 border-emerald-200 rounded-2xl p-5">
      {/* Header + progress */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Today's plan</h3>
          <p className="text-xs text-slate-500 mt-0.5">Run top-to-bottom. Each task tells you exactly what to do and why it matters.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-1 text-xs text-slate-600"><input type="checkbox" checked={hideDone} onChange={e => setHideDone(e.target.checked)} className="rounded"/> Hide done</label>
          <button onClick={() => setAddOpen(o => !o)} className="bg-slate-900 hover:bg-slate-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">+ Add task</button>
          <div className="text-right">
            <div className="text-xs text-slate-500">Progress</div>
            <div className="text-xl font-bold text-emerald-700">{doneCount}/{totalCount}</div>
          </div>
        </div>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-3">
        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${donePct}%` }}/>
      </div>

      {/* Add-manual-task panel */}
      {addOpen && (
        <div className="mb-4 bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
            <select value={draft.category} onChange={e => setDraft(d => ({ ...d, category: e.target.value }))} className="border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-sm">
              {VA_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
            </select>
            <input value={draft.title} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} placeholder="Task title" className="md:col-span-2 border border-slate-200 rounded-lg px-3 py-1.5 text-sm"/>
          </div>
          <input value={draft.why} onChange={e => setDraft(d => ({ ...d, why: e.target.value }))} placeholder="Why it matters (one sentence)" className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm"/>
          <textarea value={draft.howSteps} onChange={e => setDraft(d => ({ ...d, howSteps: e.target.value }))} rows={3} placeholder="How to do it — one step per line" className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm"/>
          <div className="flex items-center justify-between gap-2">
            <input type="date" value={draft.dueDate} onChange={e => setDraft(d => ({ ...d, dueDate: e.target.value }))} className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
            <div className="flex items-center gap-2">
              <button onClick={() => setAddOpen(false)} className="text-xs text-slate-500">Cancel</button>
              <button onClick={addManualTask} disabled={!draft.title.trim()} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-sm font-semibold">Add to plan</button>
            </div>
          </div>
        </div>
      )}

      {/* Categories — each is its own collapsible dropdown */}
      <div className="space-y-3">
        {VA_CATEGORIES.map(cat => {
          const items = tasksByCat[cat.id] || [];
          if (items.length === 0 && hideDone) return null;
          return (
            <VADailyPlanCategory
              key={cat.id}
              cat={cat}
              items={items}
              taskCompletion={taskCompletion}
              toggleDone={toggleDone}
              removeManualTask={removeManualTask}
              openClientProfile={openClientProfile}
            />
          );
        })}
      </div>

      <div className="mt-3 text-[11px] text-slate-500 italic">
        Tasks marked with ✓ stay checked across sessions. Auto-tasks regenerate as your data changes (e.g. a new policy issued today creates a welcome-call task tomorrow morning).
      </div>
    </div>
  );
}

// One collapsible category card inside the Daily Plan. Header acts as the
// dropdown toggle; defaults open if there's anything due, closed if empty.
function VADailyPlanCategory({ cat, items, taskCompletion, toggleDone, removeManualTask, openClientProfile }) {
  const dones = items.filter(taskCompletion).length;
  const [open, setOpen] = useState(items.length > 0);
  return (
    <div className={`rounded-xl border bg-${cat.color}-50 border-${cat.color}-200 overflow-hidden`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full px-3 py-2 bg-${cat.color}-100 flex items-center justify-between text-left hover:brightness-95 transition`}
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 min-w-0">
          <ChevronRight size={14} className={`text-slate-500 flex-shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}/>
          <span className="text-base">{cat.icon}</span>
          <span className={`text-sm font-bold text-${cat.color}-900`}>{cat.label}</span>
          <span className={`text-[10px] text-${cat.color}-700 italic truncate`}>{cat.sub}</span>
        </div>
        <span className={`text-xs font-bold text-${cat.color}-900 flex-shrink-0`}>{dones}/{items.length}</span>
      </button>
      {open && (
        <div className="divide-y divide-white">
          {items.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500 italic">Nothing due in this bucket today — good.</div>
          )}
          {items.map(t => {
            const done = taskCompletion(t);
            const isManual = t.id?.startsWith('manual_');
            return (
              <div key={t.id} className={`px-3 py-2 ${done ? 'bg-emerald-50/40' : 'bg-white'}`}>
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={!!done}
                    onChange={() => toggleDone(t)}
                    className="mt-1 rounded h-4 w-4 cursor-pointer"
                  />
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold ${done ? 'line-through text-slate-400' : 'text-slate-900'}`}>{t.title}</div>
                    {t.why && <div className="text-xs text-slate-600 mt-0.5"><strong>Why:</strong> {t.why}</div>}
                    {Array.isArray(t.howSteps) && t.howSteps.length > 0 && !done && (
                      <ol className="text-xs text-slate-700 mt-1 space-y-0.5 list-decimal list-inside">
                        {t.howSteps.map((s, i) => <li key={i}>{s}</li>)}
                      </ol>
                    )}
                    {done && (
                      <div className="text-[10px] text-slate-400 mt-0.5">Done {new Date(done.completedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}{done.completedBy ? ` by ${done.completedBy}` : ''}</div>
                    )}
                    {t.relatedClientKey && (
                      <button onClick={() => openClientProfile(t.relatedClientKey)} className="text-[11px] text-emerald-700 hover:underline mt-1">Open client profile →</button>
                    )}
                  </div>
                  {isManual && (
                    <button onClick={() => removeManualTask(t.id)} className="text-xs text-slate-400 hover:text-red-600">×</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function VAWorkflowView({ allTransactions, producers, clientProfiles, setClientProfiles, openClientProfile, settings, showToast, leads = [], setLeads = () => {}, calls = [], outreach = [], calendar = [], vaTasks = [], setVaTasks = () => {}, vaTaskDone = {}, setVaTaskDone = () => {}, currentUser = null }) {
  // Build per-policy due touchpoints and per-client overdue check-ins for today.
  const today = new Date();
  const todayStr = today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const mmddyyyy = `${(today.getMonth()+1).toString().padStart(2,'0')}/${today.getDate().toString().padStart(2,'0')}/${today.getFullYear()}`;

  const { touchpointItems, checkInItems } = React.useMemo(() => {
    const polMap = {};
    (allTransactions || []).forEach(tx => {
      if (!tx.policy || !tx.insured) return;
      const k = `${tx.mga||''}|${tx.policy}`;
      if (!polMap[k]) {
        polMap[k] = {
          key: k, policy: tx.policy, insured: tx.insured, prodNum: tx.prodNum,
          issueDate: tx.issueDate || '', carrier: deriveCarrier(tx),
          premium: 0, hasCb: false,
        };
      }
      const p = polMap[k];
      if (CHARGEBACK_TYPES.has(tx.activityType) || (tx.netToAccount || 0) < 0) p.hasCb = true;
      const prem = Math.abs(Number(tx.commPremium) || 0);
      if (prem > p.premium) p.premium = prem;
      if (!p.issueDate && tx.issueDate) p.issueDate = tx.issueDate;
    });

    const tps = [];
    const clientHasActive = {}; // clientKey → bool (any active policy)
    Object.values(polMap).forEach(p => {
      if (p.hasCb) return;
      const clientKey = clientKeyOf(p.insured);
      clientHasActive[clientKey] = true;
      const profile = clientProfiles?.[clientKey] || {};
      const due = getDuePolicyTouchpoints(p, profile.contactLog, today);
      due.forEach(tp => tps.push({
        ...tp,
        policy: p,
        clientKey,
        clientName: p.insured,
        agentName: getProducerName(p.prodNum, producers),
        phone: profile.phone || '',
        bankName: profile.bankName || '',
        bankDraftDate: profile.bankDraftDate || '',
      }));
    });

    // 6-month check-ins: clients with active policies who haven't been contacted in 180+ days
    const checkIns = [];
    Object.keys(clientHasActive).forEach(clientKey => {
      const profile = clientProfiles?.[clientKey] || {};
      const last = lastContactDateFor(profile.contactLog);
      const daysSince = last ? Math.floor((today - last) / 86400000) : null;
      if (daysSince == null || daysSince > CHECK_IN_INTERVAL_DAYS) {
        // Find display name from one of the policies
        const samplePolicy = Object.values(polMap).find(p => clientKeyOf(p.insured) === clientKey);
        if (!samplePolicy) return;
        checkIns.push({
          clientKey,
          clientName: samplePolicy.insured,
          agentName: getProducerName(samplePolicy.prodNum, producers),
          phone: profile.phone || '',
          daysSince,
          neverContacted: daysSince == null,
        });
      }
    });

    return {
      touchpointItems: tps.sort((a, b) => a.priority - b.priority),
      checkInItems: checkIns.sort((a, b) => {
        if (a.neverContacted && !b.neverContacted) return -1;
        if (!a.neverContacted && b.neverContacted) return 1;
        return (b.daysSince || 0) - (a.daysSince || 0);
      }),
    };
  }, [allTransactions, clientProfiles, producers, today]);

  const groupedTouchpoints = {};
  touchpointItems.forEach(t => {
    if (!groupedTouchpoints[t.kind]) groupedTouchpoints[t.kind] = { meta: t, items: [] };
    groupedTouchpoints[t.kind].items.push(t);
  });

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${label} copied to clipboard`);
    } catch {
      showToast('Could not copy — browser blocked clipboard', 'error');
    }
  };

  const markTouchpointDone = (item) => {
    const entry = {
      id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `c-${Date.now()}`,
      date: mmddyyyy,
      action: item.match,
      notes: `[${item.policy.carrier} ${item.policy.policy}] auto-logged from VA Workflow`,
    };
    setClientProfiles(prev => {
      const current = prev[item.clientKey] || {};
      const log = [entry, ...(current.contactLog || [])];
      return { ...prev, [item.clientKey]: { ...current, contactLog: log } };
    });
    showToast(`Logged "${item.match}" for ${titleCase(item.clientName)}`);
  };

  const markCheckInDone = (item) => {
    const entry = {
      id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `c-${Date.now()}`,
      date: mmddyyyy,
      action: '6-month check-in',
      notes: 'auto-logged from VA Workflow',
    };
    setClientProfiles(prev => {
      const current = prev[item.clientKey] || {};
      const log = [entry, ...(current.contactLog || [])];
      return { ...prev, [item.clientKey]: { ...current, contactLog: log } };
    });
    showToast(`Logged 6-month check-in for ${titleCase(item.clientName)}`);
  };

  const fillScript = (script, item) => {
    return script
      .replace(/\[name\]/g, titleCase(item.clientName).split(' ')[0])
      .replace(/\[carrier\]/g, item.policy?.carrier || '')
      .replace(/\[amount\]/g, item.policy?.premium ? fmt(item.policy.premium) : '')
      .replace(/\[date\]/g, item.bankDraftDate || item.policy?.issueDate || '')
      .replace(/\[issue date\]/g, item.policy?.issueDate || '')
      .replace(/\[bank\]/g, item.bankName || 'your bank')
      .replace(/\[agent\]/g, settings.ownerName || 'your agent')
      .replace(/\[agent name\]/g, settings.ownerName || 'your agent');
  };

  const totalDue = touchpointItems.length + checkInItems.length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-br from-indigo-600 to-indigo-800 text-white rounded-2xl p-5">
        <div className="text-xs text-indigo-200 uppercase tracking-wider font-semibold">VA Daily Workflow</div>
        <div className="text-2xl font-bold mt-1">{todayStr}</div>
        <p className="text-sm text-indigo-100 mt-2">
          {totalDue === 0
            ? 'No lifecycle touchpoints due today — see the Daily Plan below for everything else on the queue.'
            : <><strong>{totalDue}</strong> outreach item{totalDue === 1 ? '' : 's'} for today · {touchpointItems.length} lifecycle touchpoint{touchpointItems.length === 1 ? '' : 's'} + {checkInItems.length} 6-month check-in{checkInItems.length === 1 ? '' : 's'} · plus the Daily Plan below.</>}
        </p>
      </div>

      {/* ───── Daily plan (auto + manual tasks, by profit lever) ───── */}
      <VASection title="Today's plan" subtitle="Auto + manual tasks grouped by profit lever" tone="emerald" defaultOpen>
        <VADailyPlan
          leads={leads}
          transactions={allTransactions}
          calendar={calendar}
          outreach={outreach}
          calls={calls}
          clientProfiles={clientProfiles}
          producers={producers}
          settings={settings}
          vaTasks={vaTasks}
          setVaTasks={setVaTasks}
          vaTaskDone={vaTaskDone}
          setVaTaskDone={setVaTaskDone}
          openClientProfile={openClientProfile}
          currentUser={currentUser}
        />
      </VASection>

      {/* Today's queue — touchpoints grouped by type */}
      {POLICY_TOUCHPOINTS.filter(tp => groupedTouchpoints[tp.kind]).map(tp => {
        const items = groupedTouchpoints[tp.kind].items;

        return (
          <VASection
            key={tp.kind}
            tone={tp.color}
            title={tp.label}
            subtitle={tp.guidance}
            count={`${items.length} polic${items.length === 1 ? 'y' : 'ies'}`}
            defaultOpen
          >
            <div className="bg-white divide-y divide-slate-100">
              {items.map(item => {
                const filledScript = fillScript(tp.script, item);
                return (
                  <div key={`${item.policy.key}|${tp.kind}`} className="p-3 space-y-2">
                    <div className="flex items-center gap-3 flex-wrap text-sm">
                      <button onClick={() => openClientProfile(item.clientKey)} className="font-semibold text-emerald-700 hover:underline">{titleCase(item.clientName)}</button>
                      <span className="text-xs text-slate-500 font-mono">{item.policy.policy}</span>
                      <span className="text-xs text-slate-500">· {item.policy.carrier}</span>
                      <span className="text-xs text-slate-500">· {item.agentName}</span>
                      <span className="text-xs text-slate-500">· issued {item.policy.issueDate}</span>
                      {item.policy.premium > 0 && <span className="text-xs text-slate-500">· {fmt(item.policy.premium)}/mo</span>}
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs text-slate-800 italic">"{filledScript}"</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={() => copyToClipboard(filledScript, 'Script')} className="text-xs bg-slate-900 hover:bg-slate-800 text-white px-2 py-1 rounded font-semibold">Copy script</button>
                      {item.phone && <button onClick={() => copyToClipboard(item.phone, 'Phone')} className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-1 rounded font-mono tabular-nums">{fmtPhoneUS(item.phone)}</button>}
                      {!item.phone && <span className="text-xs text-amber-700">⚠ No phone on file</span>}
                      <button onClick={() => markTouchpointDone(item)} className={`text-xs bg-${tp.color}-600 hover:bg-${tp.color}-700 text-white px-2 py-1 rounded font-semibold ml-auto`}>✓ Mark "{tp.match}" done</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </VASection>
        );
      })}

      {/* 6-month check-ins */}
      {checkInItems.length > 0 && (
        <VASection
          tone="amber"
          title="6-month rolling check-ins"
          subtitle={CHECK_IN_GUIDANCE}
          count={`${checkInItems.length} client${checkInItems.length === 1 ? '' : 's'}`}
          defaultOpen
        >
          <div className="bg-white divide-y divide-slate-100">
            {checkInItems.slice(0, 25).map(item => (
              <div key={item.clientKey} className="p-3 flex items-center gap-3 flex-wrap text-sm">
                <button onClick={() => openClientProfile(item.clientKey)} className="font-semibold text-emerald-700 hover:underline">{titleCase(item.clientName)}</button>
                <span className="text-xs text-slate-500">· {item.agentName}</span>
                <span className="text-xs text-amber-800">· {item.neverContacted ? 'never contacted' : `${item.daysSince} days ago`}</span>
                {item.phone && <button onClick={() => copyToClipboard(item.phone, 'Phone')} className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-1 rounded font-mono tabular-nums">{fmtPhoneUS(item.phone)}</button>}
                <button onClick={() => markCheckInDone(item)} className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-2 py-1 rounded font-semibold ml-auto">✓ Logged today</button>
              </div>
            ))}
            {checkInItems.length > 25 && (
              <div className="p-2 text-center text-xs text-slate-500">Showing first 25 of {checkInItems.length}. Open Book of Business for the full list.</div>
            )}
          </div>
        </VASection>
      )}

      {/* Static playbook reference */}
      <VASection
        tone="slate"
        title="VA Playbook — Client Retention Touchpoint Schedule"
        subtitle="Reference. The lists above auto-populate based on each policy's issue date and the client's contact log."
      >
        <div className="bg-white p-5 space-y-4">
        <div className="space-y-3 text-sm">
          <div className="border-l-4 border-emerald-500 pl-3">
            <div className="font-semibold text-emerald-900">Day 0-3 · Welcome — new client</div>
            <p className="text-slate-700 mt-1">First impression — confirm them as a client, set expectations, let them know who to reach. Short and warm. Builds trust before any payment hiccup.</p>
          </div>

          <div className="border-l-4 border-amber-500 pl-3">
            <div className="font-semibold text-amber-900">5-7 days before first payment</div>
            <p className="text-slate-700 mt-1">This is the most important touchpoint. Remind them the draft is coming. People forget, overdraft, and then the policy lapses before it ever inforces. A simple text works.</p>
            <div className="text-xs text-slate-600 italic mt-1">Single biggest step that reduces chargeback rate.</div>
          </div>

          <div className="border-l-4 border-red-500 pl-3">
            <div className="font-semibold text-red-900">Day of first payment</div>
            <p className="text-slate-700 mt-1">Quick "just checking in" text. <strong>If they don't respond in 24 hours, call.</strong> First payment failures are your highest-risk window.</p>
          </div>

          <div className="border-l-4 border-blue-500 pl-3">
            <div className="font-semibold text-blue-900">30 days after inforce</div>
            <p className="text-slate-700 mt-1">A real check-in, not just a transaction message. Ask how they're feeling about the policy, if they have questions, and remind them what they're building. Honeymoon phase — good time to ask for a referral.</p>
          </div>

          <div className="border-l-4 border-violet-500 pl-3">
            <div className="font-semibold text-violet-900">90 days / 3 months</div>
            <p className="text-slate-700 mt-1">Another touchpoint, slightly more relationship-focused. Acknowledge they've made 3 payments — policy is solidly inforce. Good time to mention adding coverage, checking beneficiaries, or the children's rider if you didn't close it already.</p>
          </div>

          <div className="border-l-4 border-emerald-500 pl-3">
            <div className="font-semibold text-emerald-900">Every 6 months ongoing</div>
            <p className="text-slate-700 mt-1">Semi-annual check-in. Keep it personal, not robotic. Reference something specific — their coverage amount, their insured's age, a life event if you know of one.</p>
          </div>

          <div className="border-l-4 border-emerald-500 pl-3">
            <div className="font-semibold text-emerald-900">Annual / Policy anniversary</div>
            <p className="text-slate-700 mt-1">Treat this like a birthday. A short text acknowledging their anniversary builds loyalty and keeps you top of mind for referrals and additional sales.</p>
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700 space-y-2">
          <div className="font-semibold text-slate-900">Rules of engagement</div>
          <ul className="list-disc pl-5 space-y-1">
            <li>Text first — call only when no response within 24 hours on day-of-payment or on lapsed-policy outreach.</li>
            <li>Always reference something specific. "Hey Mary, just checking in" is worse than "Hey Mary, your $1,350/mo MOO policy crossed the 30-day mark — how's it going?"</li>
            <li>Never use the agent's words verbatim — adapt to the client's tone.</li>
            <li>Log every contact in the client profile right after sending. The system will hide the same alert for 14 days (60 for anniversary).</li>
            <li>If a client asks to be left alone, set their next contact log entry's notes to "DNC requested — pause outreach" and flag it to the agent.</li>
          </ul>
        </div>
        </div>
      </VASection>
    </div>
  );
}

// Reusable collapsible section used across the VA Workflow tab. The header is
// always a button (so it acts as a dropdown disclosure); children render only
// when open. Tone drives the accent color of the header bar.
function VASection({ title, subtitle, count, tone = 'slate', defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const headerBg = ({
    emerald: 'bg-emerald-50 border-emerald-200',
    amber:   'bg-amber-50 border-amber-200',
    red:     'bg-red-50 border-red-200',
    blue:    'bg-blue-50 border-blue-200',
    violet:  'bg-violet-50 border-violet-200',
    slate:   'bg-slate-50 border-slate-200',
  })[tone] || 'bg-slate-50 border-slate-200';
  const titleColor = ({
    emerald: 'text-emerald-900',
    amber:   'text-amber-900',
    red:     'text-red-900',
    blue:    'text-blue-900',
    violet:  'text-violet-900',
    slate:   'text-slate-900',
  })[tone] || 'text-slate-900';
  return (
    <div className={`rounded-xl border-2 ${headerBg} overflow-hidden`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full p-4 flex items-start gap-3 text-left hover:bg-black/[0.02] transition-colors"
        aria-expanded={open}
      >
        <ChevronRight size={16} className={`text-slate-400 mt-1 flex-shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}/>
        <div className="flex-1 min-w-0">
          <div className={`font-bold ${titleColor} flex items-baseline gap-2 flex-wrap`}>
            <span>{title}</span>
            {count && <span className="text-xs font-normal text-slate-600">· {count}</span>}
          </div>
          {subtitle && <div className="text-xs text-slate-700 mt-1">{subtitle}</div>}
        </div>
      </button>
      {open && <div className="border-t border-slate-200 bg-white">{children}</div>}
    </div>
  );
}

function TouchpointAlertCard({ allTransactions, producers, clientProfiles, openClientProfile }) {
  // Build a slim list of in-force policies with their attached client + contact log,
  // then compute which touchpoints are due for each.
  const due = React.useMemo(() => {
    if (!allTransactions || allTransactions.length === 0) return [];
    const polMap = {};
    allTransactions.forEach(tx => {
      if (!tx.policy || !tx.insured) return;
      const k = `${tx.mga||''}|${tx.policy}`;
      if (!polMap[k]) {
        polMap[k] = {
          key: k, policy: tx.policy, insured: tx.insured, prodNum: tx.prodNum,
          issueDate: tx.issueDate || '', premium: 0, hasCb: false,
        };
      }
      const p = polMap[k];
      if (CHARGEBACK_TYPES.has(tx.activityType) || (tx.netToAccount || 0) < 0) p.hasCb = true;
      const prem = Math.abs(Number(tx.commPremium) || 0);
      if (prem > p.premium) p.premium = prem;
      if (!p.issueDate && tx.issueDate) p.issueDate = tx.issueDate;
    });
    const today = new Date();
    const list = [];
    Object.values(polMap).forEach(p => {
      if (p.hasCb) return; // skip lapsed
      const clientKey = clientKeyOf(p.insured);
      const profile = clientProfiles?.[clientKey] || {};
      const tps = getDuePolicyTouchpoints(p, profile.contactLog, today);
      tps.forEach(tp => list.push({
        ...tp,
        policy: p,
        clientKey,
        clientName: p.insured,
        agentName: getProducerName(p.prodNum, producers),
      }));
    });
    return list.sort((a, b) => a.priority - b.priority);
  }, [allTransactions, clientProfiles, producers]);

  // Per-kind open/closed state. Hooks must declare BEFORE the early
  // return below to keep call order stable. Default: all collapsed
  // so the operator sees a clean count + headline list and expands
  // only the section they're actively working. Lots of touchpoints
  // x lots of items gets noisy fast otherwise.
  const [openKinds, setOpenKinds] = React.useState({});
  const toggleKind = (kind) => setOpenKinds(s => ({ ...s, [kind]: !s[kind] }));

  if (due.length === 0) return null;

  // Group by kind for headlined sections
  const byKind = {};
  due.forEach(d => {
    if (!byKind[d.kind]) byKind[d.kind] = { meta: d, items: [] };
    byKind[d.kind].items.push(d);
  });

  const colorBg = (c) => ({
    amber: 'bg-amber-50 border-amber-200',
    red: 'bg-red-50 border-red-200',
    blue: 'bg-blue-50 border-blue-200',
    violet: 'bg-violet-50 border-violet-200',
    emerald: 'bg-emerald-50 border-emerald-200',
  }[c] || 'bg-slate-50 border-slate-200');

  return (
    <div className="bg-white border-2 border-red-200 rounded-xl overflow-hidden">
      <div className="bg-red-600 text-white px-4 py-3 flex items-center gap-2">
        <AlertTriangle size={20}/>
        <div>
          <div className="font-bold">{due.length} policy touchpoint{due.length === 1 ? '' : 's'} due today</div>
          <div className="text-xs text-red-100">Time-windowed reminders for new and ongoing policies. Click a client to open their profile and log the contact.</div>
        </div>
      </div>
      <div className="divide-y divide-slate-200">
        {POLICY_TOUCHPOINTS.filter(tp => byKind[tp.kind]).map(tp => {
          const items = byKind[tp.kind].items;
          const isOpen = !!openKinds[tp.kind];
          return (
            <div key={tp.kind} className={`${colorBg(tp.color)} border-l-4 border-l-${tp.color}-500`}>
              {/* Clickable header — entire row toggles the section */}
              <button
                type="button"
                onClick={() => toggleKind(tp.kind)}
                className="w-full text-left p-4 flex items-center justify-between gap-3 flex-wrap hover:opacity-90"
                aria-expanded={isOpen}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className={`text-${tp.color}-700 text-base font-bold transition-transform inline-block ${isOpen ? 'rotate-90' : ''}`} aria-hidden="true">▸</span>
                  <div className={`font-bold text-${tp.color}-900`}>
                    {tp.label}{' '}
                    <span className="text-xs font-normal text-slate-600">
                      · {items.length} polic{items.length === 1 ? 'y' : 'ies'}
                    </span>
                  </div>
                </div>
                <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">
                  {isOpen ? 'Hide' : 'Show'}
                </span>
              </button>

              {/* Expanded content */}
              {isOpen && (
                <div className="px-4 pb-4">
                  <div className={`text-sm text-${tp.color}-900 mb-2`}>{tp.guidance}</div>
                  <div className="bg-white border border-slate-200 rounded p-2 text-xs text-slate-700 mb-3 italic">
                    "{tp.script}"
                  </div>
                  <div className="space-y-1">
                    {items.map(d => (
                      <div key={d.policy.key} className="bg-white border border-slate-200 rounded px-2 py-1 flex items-center gap-2 flex-wrap text-sm">
                        <button onClick={() => openClientProfile(d.clientKey)} className="font-semibold text-emerald-700 hover:text-emerald-900 hover:underline">
                          {titleCase(d.clientName)}
                        </button>
                        <span className="text-xs text-slate-500 font-mono">{d.policy.policy}</span>
                        <span className="text-xs text-slate-500">· {d.agentName}</span>
                        <span className="text-xs text-slate-500">· issued {d.policy.issueDate}</span>
                        {d.policy.premium > 0 && <span className="text-xs text-slate-500">· {fmt(d.policy.premium)}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CheckInsDueCard({ allTransactions, producers, clientProfiles, openClientProfile }) {
  const [open, setOpen] = useState(false);

  // Build the list of active clients (those with at least one non-chargeback policy still standing).
  const dueList = React.useMemo(() => {
    const polMap = {};
    (allTransactions || []).forEach(tx => {
      if (!tx.policy || !tx.insured) return;
      const k = `${tx.mga||''}|${tx.policy}`;
      if (!polMap[k]) polMap[k] = { insured: tx.insured, prodNum: tx.prodNum, cb: 0, premium: 0, hasCb: false };
      const p = polMap[k];
      const amt = Number(tx.netToAccount) || 0;
      if (amt < 0 || CHARGEBACK_TYPES.has(tx.activityType)) p.hasCb = true;
      p.cb += amt < 0 ? amt : 0;
      const prem = Math.abs(Number(tx.commPremium) || 0);
      if (prem > p.premium) p.premium = prem;
    });
    const policies = Object.values(polMap);
    const clientMap = {};
    policies.forEach(p => {
      // Skip lapsed policies for check-in purposes — only active book gets check-ins
      if (p.hasCb || p.cb < 0) return;
      const k = clientKeyOf(p.insured);
      if (!clientMap[k]) clientMap[k] = { key: k, displayName: p.insured, prodNum: p.prodNum, activePolicies: 0, premium: 0 };
      clientMap[k].activePolicies++;
      clientMap[k].premium += p.premium;
    });

    const today = new Date();
    return Object.values(clientMap).map(c => {
      const profile = clientProfiles?.[c.key] || {};
      const last = lastContactDateFor(profile.contactLog);
      const days = last ? Math.floor((today - last) / 86400000) : null;
      return {
        ...c,
        lastContactDate: last,
        daysSince: days,
        // Sort key: never-contacted at top, then longest-overdue first
        overdueDays: days == null ? Number.MAX_SAFE_INTEGER : days,
        agentName: getProducerName(c.prodNum, producers),
      };
    }).filter(c => c.daysSince == null || c.daysSince > CHECK_IN_INTERVAL_DAYS)
      .sort((a, b) => b.overdueDays - a.overdueDays);
  }, [allTransactions, clientProfiles, producers]);

  if (dueList.length === 0) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3">
        <CheckCircle className="text-emerald-600" size={20}/>
        <div>
          <div className="font-semibold text-emerald-900">All check-ins are current</div>
          <div className="text-xs text-emerald-700">No active client is overdue for a 6-month check-in. Nice work.</div>
        </div>
      </div>
    );
  }

  const neverCount = dueList.filter(c => c.daysSince == null).length;
  const overdueCount = dueList.length - neverCount;
  const visible = open ? dueList : dueList.slice(0, 5);

  return (
    <div className="bg-amber-50 border-2 border-amber-200 rounded-xl overflow-hidden">
      <div className="p-4 flex flex-wrap items-start justify-between gap-3 border-b border-amber-200">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <AlertTriangle className="text-amber-600" size={20}/>
            <div className="font-bold text-amber-900">{dueList.length} client{dueList.length === 1 ? '' : 's'} due for a check-in</div>
          </div>
          <div className="text-xs text-amber-800 mt-2 leading-relaxed">
            <strong>Every 6 months ongoing.</strong> Semi-annual check-in. Keep it personal, not robotic.
            Reference something specific — their coverage amount, their insured's age, a life event if you know of one.
          </div>
          <div className="text-xs text-amber-700 mt-1">
            {neverCount > 0 && <>{neverCount} never contacted</>}
            {neverCount > 0 && overdueCount > 0 && ' · '}
            {overdueCount > 0 && <>{overdueCount} overdue (&gt;6 months)</>}
          </div>
        </div>
      </div>
      <div className="bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
              <th className="py-1.5 px-3">Client</th>
              <th className="py-1.5 px-3">Agent of record</th>
              <th className="py-1.5 px-3">Last contact</th>
              <th className="py-1.5 px-3 text-right">Active policies</th>
              <th className="py-1.5 px-3 text-right">Premium</th>
              <th className="py-1.5 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map(c => (
              <tr key={c.key} className="border-b border-slate-100 hover:bg-amber-50/50">
                <td className="py-1.5 px-3">
                  <button onClick={() => openClientProfile(c.key)} className="font-semibold text-emerald-700 hover:text-emerald-900 hover:underline">{titleCase(c.displayName)}</button>
                </td>
                <td className="py-1.5 px-3">{c.agentName}</td>
                <td className="py-1.5 px-3">
                  {c.daysSince == null
                    ? <span className="text-red-700 font-semibold">Never contacted</span>
                    : <span className="text-amber-800">{c.daysSince} days ago</span>}
                </td>
                <td className="py-1.5 px-3 text-right">{c.activePolicies}</td>
                <td className="py-1.5 px-3 text-right">{fmt(c.premium)}</td>
                <td className="py-1.5 px-3 text-right">
                  <button onClick={() => openClientProfile(c.key)} className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-2 py-1 rounded font-semibold">Log check-in</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {dueList.length > 5 && (
          <div className="p-2 text-center border-t border-slate-100">
            <button onClick={() => setOpen(o => !o)} className="text-xs text-amber-700 hover:text-amber-900 font-semibold">
              {open ? 'Show top 5 only' : `Show all ${dueList.length}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Welcome screen rendered when a freshly-signed-in user has zero data in all
// primary domains. Three onboarding cards point to the existing paths for
// getting data into the app: upload, paste, and producer setup. Card 2 maps
// to the Paste-statement modal rather than a "+ Add transaction" modal —
// the codebase has no manual transaction-entry flow today; paste is the
// closest existing manual-entry path.
function EmptyDashboard({ onFileChange, onPasteStatement, onGotoProducers }) {
  return (
    <div className="max-w-none mx-auto p-6">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
        <div className="text-center mb-8">
          <div className="w-12 h-12 mx-auto rounded-lg bg-emerald-600 flex items-center justify-center text-white font-bold text-2xl">$</div>
          <h1 className="text-2xl font-bold mt-3">Welcome to Six Figure Agent OS</h1>
          <p className="text-sm text-slate-500 mt-2">Let's get you set up. Pick where you want to start:</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Card 1: Upload */}
          <label className="cursor-pointer block bg-emerald-50 border-2 border-emerald-200 hover:border-emerald-400 rounded-xl p-5 transition-colors">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center mb-3">
              <Upload size={20}/>
            </div>
            <h3 className="font-bold text-slate-900 mb-1">Upload your first commission statement</h3>
            <p className="text-xs text-slate-600 mb-4 leading-relaxed">Drop a MOO, Transamerica, or Americo file (.xlsx / .pdf) and we'll parse every policy automatically.</p>
            <span className="inline-block bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-3 py-1.5 rounded-lg">Choose a file</span>
            <input type="file" multiple accept=".xlsx,.xls,.csv,.pdf" className="hidden" onChange={onFileChange}/>
          </label>

          {/* Card 2: Paste (manual entry) */}
          <button
            type="button"
            onClick={onPasteStatement}
            className="text-left bg-amber-50 border-2 border-amber-200 hover:border-amber-400 rounded-xl p-5 transition-colors"
          >
            <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center mb-3">
              <FileText size={20}/>
            </div>
            <h3 className="font-bold text-slate-900 mb-1">Paste a statement manually</h3>
            <p className="text-xs text-slate-600 mb-4 leading-relaxed">For Americo statements without a PDF, paste the text directly and we'll parse it line by line.</p>
            <span className="inline-block bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-3 py-1.5 rounded-lg">Paste text</span>
          </button>

          {/* Card 3: Producers */}
          <button
            type="button"
            onClick={onGotoProducers}
            className="text-left bg-slate-50 border-2 border-slate-200 hover:border-slate-400 rounded-xl p-5 transition-colors"
          >
            <div className="w-10 h-10 rounded-lg bg-slate-200 text-slate-700 flex items-center justify-center mb-3">
              <Users size={20}/>
            </div>
            <h3 className="font-bold text-slate-900 mb-1">Set up your producers</h3>
            <p className="text-xs text-slate-600 mb-4 leading-relaxed">Add producer names and assign each one to your team or your partner's team.</p>
            <span className="inline-block bg-slate-900 hover:bg-slate-700 text-white text-sm font-semibold px-3 py-1.5 rounded-lg">Open Producers</span>
          </button>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          Or explore the app — all features available in the tabs above.
        </p>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Workflows — customizable AI conversation playbooks. Each workflow
// holds a persona, the system prompt, FAQ pairs, and hesitation
// responses. The agent edits the script to their voice, tests it in
// the built-in chat (which calls /api/ai with the workflow's
// system prompt + FAQ block injected), and the same config can later
// drive automated SMS outbound once a Twilio webhook is wired up.
//
// What's NOT here yet (intentional, needs infra):
//   - Outbound SMS send. Requires a Twilio account + phone number +
//     /api/sms/send route. The CRM stores the workflow + can produce
//     a "next message" from any inbound text — the SMS layer is the
//     piece that turns this into automation.
//   - Per-lead state. When SMS lands, we'll attach a lead to a
//     workflow and persist the running conversation. For now the
//     chat preview is stateless per session.
// ──────────────────────────────────────────────────────────────────────

const MORTGAGE_PROTECTION_ROLE_PROMPT = `# ROLE
You are {{ASSISTANT_NAME}}, the scheduling assistant for {{AGENCY_NAME}}, a licensed mortgage protection brokerage. You text homeowners who requested information about protecting their mortgage. Your job is to answer light questions and book them a short phone call with a licensed broker. You are not the broker, and you do not sell, quote, or make any insurance decisions.

# STYLE
- Text like a real person. Every message is 1–3 short sentences.
- Warm, casual, confident. No corporate speak, no long paragraphs, no emojis unless they use one first.
- Use their first name naturally. Ask one thing at a time.

# YOUR ONE GOAL
Confirm interest and book (or confirm) a quick phone call with the licensed broker. Every conversation moves toward a scheduled time. Do not try to "close a sale" over text — the call does that.

# WHAT MORTGAGE PROTECTION IS (use only to answer general questions)
It helps a homeowner's family keep their home if the homeowner passes away, and many plans include living benefits — access to funds if you become terminally, critically, or chronically ill. It is a type of life insurance, and the homeowner has to qualify based on age and health, which is what the call is for.

# FLOW
1. Reference their request ("you recently requested some info on protecting your mortgage") and confirm you're talking to the right person.
2. Confirm they're still interested and that they're the homeowner.
3. Frame the call: it's quick, it's with a licensed broker, and it's how we find which carrier can approve them for the best coverage and rate.
4. Offer two specific time options and book it.
5. Confirm the booked time and let them know the broker will call then.

# HARD RULES — NEVER BREAK
- Never quote a price, premium, or rate.
- Never tell someone they will or won't qualify, and never give any medical or underwriting opinion. Book the call instead.
- Never guarantee approval, payout, or say the coverage is "free."
- Never ask for medical, financial, banking, or Social Security details by text. That is for the call.
- If they say STOP, unsubscribe, opt out, or ask you to stop — stop immediately and do not message again.
- If you are unsure, or they ask something beyond scheduling, hand off to a human instead of guessing.
- If asked whether you are a bot, be honest that you're a scheduling assistant.

# HAND OFF TO A HUMAN WHEN
- They want to talk now or are ready to buy.
- They ask detailed price, product, or medical questions.
- They are upset, confused, or complaining.
- A time is booked (so the broker is ready for them).`;

const MORTGAGE_PROTECTION_FAQS = [
  { question: 'What is mortgage protection?', answer: "It's life insurance set up around your mortgage — if something happens to you, your family gets money to keep the home instead of losing it. The quick call is where the broker shows you which option fits. Want me to grab you a time?" },
  { question: 'How is this different from the mortgage insurance my lender offers?', answer: "Big difference — your lender's PMI protects the bank if payments stop. This protects your family: it pays them directly and they decide what to do. Happy to book a quick call to walk through it." },
  { question: 'Why not just pay off the whole house?', answer: "You can insure the full balance, but many families pick a \"mortgage payout\" option that covers the payments for a period instead — far more affordable, and it gives the family time to decide on their terms. The broker can show you both." },
  { question: 'Does the money go to the bank?', answer: "No — it pays your beneficiary directly, tax-free, and they make the payments as normal. That keeps your family in control." },
  { question: 'How much does it cost?', answer: "It depends on your age, health, and the home, so I can't quote a number — that's exactly what the quick call figures out so you get real options. Does today or tomorrow work better?" },
  { question: 'Do I have to take a medical exam?', answer: "No exam needed — it's a simplified process. The broker just goes over a few quick health questions on the call." },
  { question: 'Can I use the coverage while I\'m still alive?', answer: "Many plans include living benefits — if you became seriously ill, you could access funds while living to help with costs. The broker explains how that works for you on the call." },
  { question: 'I already have life insurance — why do I need this?', answer: "A lot of folks do, but existing coverage often isn't set up for the mortgage specifically. It's worth a 10-minute look to compare — want me to set a time?" },
  { question: 'Can my spouse be covered too?', answer: "Absolutely — the broker can look at coverage for just you or both of you. Want me to grab a time so they can go over options for the household?" },
  { question: 'What company is this with?', answer: "We're an independent brokerage, so we shop several A-rated carriers to find your best fit rather than pushing one company. The broker covers which one makes sense on the call." },
  { question: 'How do I know you\'re licensed and this is legit?', answer: "Great question — the broker is licensed with your state, and you can verify their license and producer number with your state department of insurance anytime." },
  { question: 'Is there any cost or obligation to talk?', answer: "None at all — the call is just to see your options, no cost and no obligation. Want me to set up a quick time?" },
  { question: 'Can we just do this over text or online instead?', answer: "A lot depends on your specific situation, so a quick 10-minute call with the broker is the fastest way to get exact options. Morning or afternoon better for you?" },
  { question: 'Who is this — are you a real person?', answer: "I'm {{ASSISTANT_NAME}}, the scheduling assistant with {{AGENCY_NAME}}, helping set up the call you requested about your mortgage. Happy to get you booked — what time works?" },
  { question: 'How do I stop getting texts?', answer: "No problem — just reply STOP anytime and I'll take you right off the list." },
];

const MORTGAGE_PROTECTION_HESITATIONS = [
  { trigger: 'price / cost',            response: "It depends on your age, health, and the home — that's exactly what the quick call figures out so you get real numbers. Does afternoon or tomorrow morning work better?" },
  { trigger: '"just text me the info"', response: "A lot of it depends on your situation, so a 10-minute call is faster and you get exact options. Morning or afternoon better for you?" },
  { trigger: '"I\'m busy"',              response: "Totally understand — it's quick. Want me to set it for this evening or tomorrow instead?" },
  { trigger: 'scam / who is this',      response: "Good question — I'm {{ASSISTANT_NAME}} with {{AGENCY_NAME}}, helping schedule the call you requested about your mortgage. You can verify the broker's license with your state anytime. Want me to grab you a time?" },
  { trigger: 'not interested',          response: "No problem at all, I'll take you off the list. If anything changes, just text this number." },
];

function buildDefaultMortgageProtectionWorkflow() {
  return {
    id: 'workflow_mortgage_protection',
    name: 'Mortgage Protection — Schedule the Call',
    description: 'Texts inbound mortgage-protection leads to confirm interest and book a phone call with a licensed broker.',
    enabled: false,
    // BUG-025 — Empty defaults force the operator to set assistant +
    // agency names in Profile & Brand before the drip can be enabled.
    // Previously seeded "Olivia" / "Your Agency" leaked into outgoing
    // SMS when the operator forgot to customize.
    assistantName: '',
    agencyName: '',
    rolePrompt: MORTGAGE_PROTECTION_ROLE_PROMPT,
    faqs: MORTGAGE_PROTECTION_FAQS,
    hesitations: MORTGAGE_PROTECTION_HESITATIONS,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

// Token substitution — replaces {{ASSISTANT_NAME}} / {{AGENCY_NAME}} so
// the workflow author writes one template and individual agents can
// customize without touching prompts.
function applyWorkflowTokens(text, wf) {
  if (!text) return '';
  return String(text)
    .replace(/\{\{ASSISTANT_NAME\}\}/g, wf.assistantName || 'Scheduler')
    .replace(/\{\{AGENCY_NAME\}\}/g,    wf.agencyName    || 'Our Agency');
}

function WorkflowsView({ workflows = [], setWorkflows, settings = {}, showToast = () => {} }) {
  const [activeId, setActiveId] = useState(workflows[0]?.id || null);
  useEffect(() => {
    if (!activeId && workflows[0]) setActiveId(workflows[0].id);
  }, [workflows, activeId]);

  const active = workflows.find(w => w.id === activeId) || null;

  const updateActive = (patch) => {
    setWorkflows(prev => prev.map(w => w.id === activeId ? { ...w, ...patch, updatedAt: new Date().toISOString() } : w));
  };
  const addWorkflow = () => {
    const id = `workflow_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const next = { ...buildDefaultMortgageProtectionWorkflow(), id, name: 'New workflow', updatedAt: new Date().toISOString() };
    setWorkflows(prev => [...prev, next]);
    setActiveId(id);
  };
  const duplicateActive = () => {
    if (!active) return;
    const id = `workflow_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const copy = { ...active, id, name: `${active.name} (copy)`, updatedAt: new Date().toISOString() };
    setWorkflows(prev => [...prev, copy]);
    setActiveId(id);
    showToast(`Duplicated "${active.name}"`);
  };
  const deleteActive = async () => {
    if (!active) return;
    if (!(await hqConfirm({
      title: `Delete workflow "${active.name}"?`,
      body: 'This can\'t be undone.',
      confirmLabel: 'Delete workflow',
      danger: true,
    }))) return;
    setWorkflows(prev => prev.filter(w => w.id !== active.id));
    setActiveId(workflows.find(w => w.id !== active.id)?.id || null);
    showToast(`Deleted "${active.name}"`);
  };
  const resetActive = async () => {
    if (!active) return;
    if (!(await hqConfirm({
      title: 'Reset this workflow to Mortgage Protection defaults?',
      body: 'Persona, prompt, FAQs, and hesitation responses will be replaced. Your other workflows are untouched.',
      confirmLabel: 'Reset workflow',
      danger: true,
    }))) return;
    const seed = buildDefaultMortgageProtectionWorkflow();
    updateActive({ rolePrompt: seed.rolePrompt, faqs: seed.faqs, hesitations: seed.hesitations });
    showToast('Reset to defaults');
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Workflows</h2>
        <p className="text-sm text-slate-500 mt-1">
          Customizable AI scripts that text leads on your behalf. Edit the persona and FAQ, test in the built-in chat, and the same config will power SMS automation once your messaging provider is connected.
        </p>
      </div>

      {/* Workflow selector + actions */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">Active workflow</span>
          <select
            value={activeId || ''}
            onChange={e => setActiveId(e.target.value)}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white flex-1 min-w-[180px]"
          >
            {workflows.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          <button onClick={addWorkflow} className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">+ New</button>
          {active && <button onClick={duplicateActive} className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-lg">Duplicate</button>}
          {active && <button onClick={resetActive} className="bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-semibold px-3 py-1.5 rounded-lg">Reset</button>}
          {active && workflows.length > 1 && <button onClick={deleteActive} className="bg-red-100 hover:bg-red-200 text-red-700 text-xs font-semibold px-3 py-1.5 rounded-lg">Delete</button>}
        </div>
      </div>

      {active && <WorkflowEditor workflow={active} updateActive={updateActive} settings={settings} showToast={showToast}/>}

      {/* What this tab actually is */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
        <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1">Script drafting tool</div>
        <div className="text-sm text-slate-700">
          This tab is where you write and refine your persona-driven scripts (openers, FAQs, hesitations, close attempts). The chat window below lets you red-team a script against the AI — <strong>chat responses here do not go out over SMS</strong>. Live SMS automation for saved workflows fires from the lifecycle engine (Relay A2P-approved, consent-gated, quiet-hours-clamped) via drip/Nudge/Retain touchpoints — not from this tab's chat window.
        </div>
      </div>
    </div>
  );
}

function WorkflowEditor({ workflow, updateActive, settings, showToast }) {
  const [section, setSection] = useState('persona'); // persona | prompt | faqs | hesitations | test

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex border-b border-slate-200 bg-slate-50 overflow-x-auto">
        {[
          ['persona',     'Persona'],
          ['prompt',      'Role prompt'],
          ['faqs',        `FAQs (${workflow.faqs?.length || 0})`],
          ['hesitations', `Hesitation responses (${workflow.hesitations?.length || 0})`],
          ['test',        'Test chat'],
        ].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setSection(id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${section === id ? 'border-emerald-500 text-emerald-700 bg-white' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="p-4">
        {section === 'persona' && <PersonaSection workflow={workflow} updateActive={updateActive}/>}
        {section === 'prompt' && <PromptSection workflow={workflow} updateActive={updateActive}/>}
        {section === 'faqs' && <FaqsSection workflow={workflow} updateActive={updateActive}/>}
        {section === 'hesitations' && <HesitationsSection workflow={workflow} updateActive={updateActive}/>}
        {section === 'test' && <TestChatSection workflow={workflow} showToast={showToast}/>}
      </div>
    </div>
  );
}

function PersonaSection({ workflow, updateActive }) {
  return (
    <div className="space-y-4 max-w-xl">
      <div>
        <label className="block">
          <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">Workflow name</span>
          <input
            type="text"
            value={workflow.name || ''}
            onChange={e => updateActive({ name: e.target.value })}
            className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
            placeholder="Mortgage Protection — Schedule the Call"
          />
        </label>
      </div>
      <div>
        <label className="block">
          <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">Description (for your reference)</span>
          <input
            type="text"
            value={workflow.description || ''}
            onChange={e => updateActive({ description: e.target.value })}
            className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
            placeholder="What this workflow does"
          />
        </label>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs uppercase tracking-wider font-semibold text-emerald-700">Assistant name</span>
          <input
            type="text"
            value={workflow.assistantName || ''}
            onChange={e => updateActive({ assistantName: e.target.value })}
            className="mt-1 w-full border border-emerald-200 focus:border-emerald-500 focus:outline-none rounded-lg px-3 py-2 text-sm font-semibold text-emerald-900"
            placeholder="e.g. Olivia, Sam, Jordan"
          />
          <span className="block text-[11px] text-slate-500 mt-1">The name the AI uses for itself when texting leads. Required before this drip can run.</span>
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider font-semibold text-violet-700">Agency name</span>
          <input
            type="text"
            value={workflow.agencyName || ''}
            onChange={e => updateActive({ agencyName: e.target.value })}
            className="mt-1 w-full border border-violet-200 focus:border-violet-500 focus:outline-none rounded-lg px-3 py-2 text-sm font-semibold text-violet-900"
            placeholder="e.g. Curry Financial Group"
          />
          <span className="block text-[11px] text-slate-500 mt-1">Appears in the script as the agency the assistant works for. Required before this drip can run.</span>
        </label>
      </div>
      {/* BUG-025 — block-enable warning so the operator can't ship a
          drip that still has placeholder names. */}
      {(!workflow.assistantName || !workflow.agencyName) && (
        <div className="bg-amber-50 border border-amber-300 rounded-md px-3 py-2 text-xs text-amber-900">
          <strong>⚠ Fill in both names above</strong> before turning this drip on. The placeholders are not auto-filled — outgoing SMS would say "{'{{ASSISTANT_NAME}}'}" / "{'{{AGENCY_NAME}}'}" literally.
        </div>
      )}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
        <strong>Token substitution:</strong> anywhere the role prompt or an FAQ answer contains <code className="bg-white border border-slate-200 rounded px-1 py-0.5 font-mono">{'{{ASSISTANT_NAME}}'}</code> or <code className="bg-white border border-slate-200 rounded px-1 py-0.5 font-mono">{'{{AGENCY_NAME}}'}</code>, the live values above get filled in at send time.
      </div>
    </div>
  );
}

function PromptSection({ workflow, updateActive }) {
  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500">
        This is the system prompt the AI follows on every message. Use <code className="bg-slate-100 border border-slate-200 rounded px-1 py-0.5 font-mono">{'{{ASSISTANT_NAME}}'}</code> / <code className="bg-slate-100 border border-slate-200 rounded px-1 py-0.5 font-mono">{'{{AGENCY_NAME}}'}</code> as tokens. Keep ROLE / STYLE / FLOW / HARD RULES sections — they make the AI safe and on-brand.
      </div>
      <textarea
        value={workflow.rolePrompt || ''}
        onChange={e => updateActive({ rolePrompt: e.target.value })}
        rows={24}
        spellCheck={false}
        className="w-full border border-slate-200 rounded-lg p-3 font-mono text-xs bg-slate-50 focus:bg-white focus:outline-none focus:border-emerald-500 resize-vertical"
      />
      <div className="text-[11px] text-slate-500">{(workflow.rolePrompt || '').length.toLocaleString()} characters</div>
    </div>
  );
}

function FaqsSection({ workflow, updateActive }) {
  const faqs = workflow.faqs || [];
  const update = (i, patch) => updateActive({ faqs: faqs.map((f, idx) => idx === i ? { ...f, ...patch } : f) });
  const remove = async (i) => {
    if (await hqConfirm({ title: 'Remove this FAQ?', confirmLabel: 'Remove', danger: true })) {
      updateActive({ faqs: faqs.filter((_, idx) => idx !== i) });
    }
  };
  const add = () => updateActive({ faqs: [...faqs, { question: '', answer: '' }] });
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= faqs.length) return;
    const next = faqs.slice();
    [next[i], next[j]] = [next[j], next[i]];
    updateActive({ faqs: next });
  };
  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500">
        Q/A pairs the AI references when leads ask common questions. Edit freely — these go to the AI on every message as a knowledge block. {'{{ASSISTANT_NAME}}'} / {'{{AGENCY_NAME}}'} tokens work here too.
      </div>
      <div className="space-y-3">
        {faqs.map((f, i) => (
          <div key={i} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-xs font-bold text-slate-500">FAQ #{i + 1}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="text-xs px-2 py-1 rounded bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-40">↑</button>
                <button onClick={() => move(i, 1)} disabled={i === faqs.length - 1} className="text-xs px-2 py-1 rounded bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-40">↓</button>
                <button onClick={() => remove(i)} className="text-xs px-2 py-1 rounded bg-red-50 border border-red-200 text-red-700 hover:bg-red-100">Remove</button>
              </div>
            </div>
            <input
              type="text"
              value={f.question}
              onChange={e => update(i, { question: e.target.value })}
              placeholder="Question"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-semibold focus:outline-none focus:border-emerald-500 mb-2 bg-white"
            />
            <textarea
              value={f.answer}
              onChange={e => update(i, { answer: e.target.value })}
              placeholder="Answer (1-3 sentences, points toward booking the call)"
              rows={3}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 bg-white"
            />
          </div>
        ))}
      </div>
      <button onClick={add} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-lg">+ Add FAQ</button>
    </div>
  );
}

function HesitationsSection({ workflow, updateActive }) {
  const items = workflow.hesitations || [];
  const update = (i, patch) => updateActive({ hesitations: items.map((f, idx) => idx === i ? { ...f, ...patch } : f) });
  const remove = async (i) => {
    if (await hqConfirm({ title: 'Remove this hesitation response?', confirmLabel: 'Remove', danger: true })) {
      updateActive({ hesitations: items.filter((_, idx) => idx !== i) });
    }
  };
  const add = () => updateActive({ hesitations: [...items, { trigger: '', response: '' }] });
  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500">
        Quick-reply patterns for common objections — price, "just text me", "busy", etc. The AI uses these as guidance when the lead pushes back, then re-offers a time slot.
      </div>
      <div className="space-y-3">
        {items.map((h, i) => (
          <div key={i} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-xs font-bold text-slate-500">When the lead says: <span className="text-slate-900">{h.trigger || '(unset)'}</span></span>
              <button onClick={() => remove(i)} className="text-xs px-2 py-1 rounded bg-red-50 border border-red-200 text-red-700 hover:bg-red-100">Remove</button>
            </div>
            <input
              type="text"
              value={h.trigger}
              onChange={e => update(i, { trigger: e.target.value })}
              placeholder="Trigger (e.g., 'price / cost')"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-semibold focus:outline-none focus:border-emerald-500 mb-2 bg-white"
            />
            <textarea
              value={h.response}
              onChange={e => update(i, { response: e.target.value })}
              placeholder="How the AI should respond"
              rows={2}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 bg-white"
            />
          </div>
        ))}
      </div>
      <button onClick={add} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-lg">+ Add hesitation response</button>
    </div>
  );
}

function TestChatSection({ workflow, showToast }) {
  const [messages, setMessages] = useState([]); // { role: 'lead'|'assistant', content }
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const buildSystem = () => {
    const role = applyWorkflowTokens(workflow.rolePrompt || '', workflow);
    const faqBlock = (workflow.faqs || []).length === 0
      ? ''
      : `\n\n# FAQ REFERENCE (use as guidance — don't paste verbatim)\n${(workflow.faqs || []).map(f =>
          `Q: ${f.question}\nA: ${applyWorkflowTokens(f.answer, workflow)}`
        ).join('\n\n')}`;
    const hesitations = (workflow.hesitations || []).length === 0
      ? ''
      : `\n\n# HESITATION HANDLING (one short reply, then re-offer a time)\n${(workflow.hesitations || []).map(h =>
          `- ${h.trigger} → ${applyWorkflowTokens(h.response, workflow)}`
        ).join('\n')}`;
    return role + faqBlock + hesitations;
  };

  const sendMessage = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    const next = [...messages, { role: 'lead', content: text }];
    setMessages(next);
    setDraft('');
    try {
      // AUDIT prod-401 fix: attach the Supabase JWT. /api/ai requires
      // auth via checkCap; without the header the endpoint 401s and
      // the frontend surfaces a misleading "AI is disabled" banner.
      const res = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({
          max_tokens: 400,
          system: buildSystem(),
          messages: next.map(m => ({
            role: m.role === 'lead' ? 'user' : 'assistant',
            content: m.content,
          })),
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      const reply = body?.content?.[0]?.text || '';
      setMessages([...next, { role: 'assistant', content: reply || '(no reply)' }]);
    } catch (err) {
      showToast(`Test failed: ${err.message}`, 'error');
      setMessages([...next, { role: 'assistant', content: `[error: ${err.message}]` }]);
    } finally {
      setBusy(false);
    }
  };

  const resetChat = () => setMessages([]);

  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500">
        Type messages as if you're a lead texting in. The AI uses this workflow's role prompt + FAQs + hesitations to reply.
      </div>
      <div ref={scrollRef} className="border border-slate-200 rounded-lg bg-slate-50 p-4 max-h-[480px] min-h-[280px] overflow-y-auto space-y-3">
        {messages.length === 0 && (
          <div className="text-center text-slate-400 text-sm italic py-12">
            Send a message to start the test. Try: <em>"Hey, who is this?"</em> or <em>"What is mortgage protection?"</em>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'lead' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === 'lead' ? 'bg-blue-600 text-white' : 'bg-white border border-slate-200 text-slate-900'}`}>
              {m.content}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="bg-white border border-slate-200 text-slate-500 rounded-2xl px-3 py-2 text-sm italic">{workflow.assistantName || 'Assistant'} is typing…</div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          disabled={busy}
          placeholder="Type as the lead…"
          className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
        />
        <button onClick={sendMessage} disabled={busy || !draft.trim()} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg">Send</button>
        <button onClick={resetChat} disabled={busy} className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold px-3 py-2 rounded-lg">Clear</button>
      </div>
      <div className="text-[11px] text-slate-500 italic">
        Conversation is in-memory only — refresh the page to start over. Production SMS will persist conversations per lead.
      </div>
    </div>
  );
}

// Fresh-customer welcome card. Replaces the Briefing roll-ups for any
// account with no transactions / producers / client profiles. Three
// numbered actions that map 1:1 to the tabs the customer needs to
// touch to get their first useful number on the dashboard.
//
// Used by BriefingView's early-return guard so the first paint after a
// post-payment sign-in is welcoming, not a wall of $0s.
function BriefingWelcome({ settings = {}, onGotoTab = () => {} }) {
  const ownerName = (settings.ownerName && settings.ownerName !== 'You') ? settings.ownerName : '';
  return (
    <div className="space-y-5 max-w-none mx-auto">
      <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-2 border-emerald-200 rounded-2xl p-6">
        <div className="text-xs text-emerald-800 uppercase tracking-wider font-bold">Welcome aboard</div>
        <h1 className="text-3xl font-bold mt-1 text-emerald-950 leading-tight">
          You're in{ownerName ? `, ${ownerName}` : ''}.
        </h1>
        <p className="text-sm text-emerald-900 mt-2">
          Your CRM is ready. Three quick steps and your dashboard starts working — you'll see your team's premium, overrides, persistency, and chargebacks the moment you upload your first commission statement.
        </p>
      </div>

      <div className="space-y-3">
        <OnboardingStep
          step={1}
          title="Set your Producer #"
          why="So the app can tell your own production apart from your team's overrides."
          cta="Open Settings"
          onClick={() => onGotoTab('settings')}
        />
        <OnboardingStep
          step={2}
          title="Upload your first commission statement"
          why="MOO, Americo, or Transamerica. Drag the PDF onto the Dashboard — the parser does the rest."
          cta="Open Dashboard"
          onClick={() => onGotoTab('dashboard')}
        />
        <OnboardingStep
          step={3}
          title="Add your producers"
          why="Each agent's prodNum, name, and team — so override math attributes correctly."
          cta="Open Producers"
          onClick={() => onGotoTab('producers')}
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 text-xs text-slate-600">
        <div className="font-semibold text-slate-800 mb-1">Already have leads or clients elsewhere?</div>
        Drag a CSV of leads onto the <button onClick={() => onGotoTab('leads')} className="underline text-emerald-700 hover:text-emerald-900">Leads tab</button>, or import your MOO Client CSV from <button onClick={() => onGotoTab('settings')} className="underline text-emerald-700 hover:text-emerald-900">Settings</button> to backfill addresses, phones, and DOBs for every existing client.
      </div>
    </div>
  );
}

// `accent` is kept as a prop for future use but the class names below
// must be full literals — Tailwind's JIT can't expand a `bg-${accent}-100`
// template at build time, so dynamic accents would silently render as
// unstyled. Today every onboarding step is emerald.
function OnboardingStep({ step, title, why, cta, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white border-2 border-emerald-100 hover:border-emerald-300 rounded-xl p-4 flex items-start gap-4 group transition-colors"
    >
      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-emerald-100 text-emerald-800 font-bold flex items-center justify-center">
        {step}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-slate-900">{title}</div>
        <div className="text-xs text-slate-500 mt-1">{why}</div>
      </div>
      <div className="flex-shrink-0 self-center text-sm font-semibold text-emerald-700 group-hover:underline">
        {cta} →
      </div>
    </button>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Briefing — the agency owner's "what matters this week" landing page.
// Default tab. Reads the same data sources as the rest of the app but
// reshapes them into action items so the owner doesn't have to scroll
// through 17 tabs hunting for problems.
//
// Sections:
//   1. Headline — this week's net premium with week-over-week delta.
//   2. Attention queue — auto-generated action items, prioritized by
//      revenue impact: chargeback spikes, inactive agents, lapse-window
//      clients without recent contact, fresh chargebacks.
//   3. Wins — top performer + biggest single policy this week.
//   4. 4-week trend — small bar chart of weekly premium so the owner
//      can see momentum at a glance.
// ──────────────────────────────────────────────────────────────────────
// Router. Picks BriefingWelcome (fresh-customer first paint, no $0s)
// or BriefingDashboard (the real action-items view) based on whether
// the account has any data yet. Splitting these into two components
// keeps Rules of Hooks happy — every hook in BriefingDashboard runs
// on every render of it, with no early returns in front of them.
// ────────────────────────────────────────────────────────────────────
// MyWeekView — the producer's personal KPI dashboard.
//
// This is the "MY week" view (not the agency view). Filters every
// data slice to currentUser.prodNum so an individual producer sees:
//   - Personal income goal + pace + projection
//   - Streak tracking (consecutive days hitting their dial goal)
//   - This week KPIs (dials, talks, pickup rate, closes, premium,
//     net commission) — all scoped to ME
//   - Day-by-day breakdown
//   - Today's prep (appointments, overdue follow-ups)
//   - This week's wins (closes booked)
//   - Inline goal editor — annual income, weekly + daily dial goals
//
// Why this matters: the agency dashboard is for the OWNER, but most
// producers don't care about agency-level numbers. They care about
// their own week. This view gives them their score on the field.
// ────────────────────────────────────────────────────────────────────

function MyWeekView({ currentUser, transactions = [], leads = [], calls = [], outreach = [], producers = {}, producerList = [], settings = {}, setSettings = () => {}, onGotoTab = () => {}, openClientProfile = () => {} }) {
  const myProdNum = currentUser?.prodNum || '';
  const myName = (currentUser?.role !== 'admin' && currentUser?.name) || getProducerName(myProdNum, producers) || currentUser?.email?.split('@')[0] || 'You';
  const firstName = String(myName).trim().split(/\s+/)[0] || 'You';

  // Date helpers — all in local time so "this week" reads naturally
  // to a producer in their own tz.
  const now = React.useMemo(() => new Date(), []);
  const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
  const startOfWeek = (d) => {
    const x = startOfDay(d);
    const dayNr = (x.getDay() + 6) % 7; // 0 = Monday
    x.setDate(x.getDate() - dayNr);
    return x;
  };
  const today = startOfDay(now);
  const weekStart = startOfWeek(now);
  const lastWeekStart = new Date(weekStart); lastWeekStart.setDate(lastWeekStart.getDate() - 7);
  const yearStart = new Date(now.getFullYear(), 0, 1);
  const ymd = (d) => d.toISOString().slice(0, 10);
  const dayMs = 86400000;

  // Scope every data slice to ME.
  const myCalls    = React.useMemo(() => calls.filter(c => (c.agentProdNum || '') === myProdNum), [calls, myProdNum]);
  const myLeads    = React.useMemo(() => leads.filter(l => (l.agentProdNum || '') === myProdNum), [leads, myProdNum]);
  const myOutreach = React.useMemo(() => {
    const myLeadIds = new Set(myLeads.map(l => l.id));
    return outreach.filter(o => myLeadIds.has(o.leadId));
  }, [outreach, myLeads]);
  const myTx = React.useMemo(() => transactions.filter(t => (t.prodNum || '') === myProdNum), [transactions, myProdNum]);

  // Helper: filter call list by date range.
  const inRange = (dateStr, start, end) => {
    if (!dateStr) return false;
    const t = Date.parse(dateStr);
    if (!Number.isFinite(t)) return false;
    return t >= start.getTime() && (end ? t < end.getTime() : true);
  };

  // ── This week's activity ─────────────────────────────────────────
  const thisWeekCalls   = myCalls.filter(c => inRange(c.date, weekStart));
  const thisWeekOutbound = thisWeekCalls.filter(c => c.direction === 'outbound');
  const dialsTW = thisWeekOutbound.length;
  const talksTW = thisWeekOutbound.filter(c => isContactOutcome(c.outcome)).length;
  const pickupRateTW = dialsTW > 0 ? (talksTW / dialsTW) * 100 : 0;
  const apptsBookedTW = thisWeekCalls.filter(c => c.outcome === 'appt_set').length;

  // Closes & premium — derived from transactions (source of truth)
  // because lead.status='closed' doesn't carry a reliable date stamp.
  // We count UNIQUE policy ids whose first activity in our data falls
  // inside this week.
  const tdy = today.getTime();
  const firstSeenByPolicy = React.useMemo(() => {
    const m = {};
    myTx.forEach(tx => {
      if (!tx.policy) return;
      const t = Date.parse(tx.activityDate || tx.issueDate || '');
      if (!Number.isFinite(t)) return;
      if (m[tx.policy] == null || t < m[tx.policy]) m[tx.policy] = t;
    });
    return m;
  }, [myTx]);
  const closesTW = Object.values(firstSeenByPolicy).filter(t => t >= weekStart.getTime() && t < tdy + dayMs).length;
  const premiumTW = myTx
    .filter(tx => firstSeenByPolicy[tx.policy] === Date.parse(tx.activityDate || tx.issueDate || '0') && firstSeenByPolicy[tx.policy] >= weekStart.getTime())
    .reduce((s, tx) => s + (Math.abs(tx.commPremium) || 0), 0);
  const netCommissionTW = myTx
    .filter(tx => inRange(tx.activityDate || tx.issueDate, weekStart))
    .reduce((s, tx) => s + (Number(tx.netToAccount) || 0), 0);

  // Last week for delta arrows
  const lastWeekOutbound = myCalls.filter(c => inRange(c.date, lastWeekStart, weekStart) && c.direction === 'outbound');
  const dialsLW = lastWeekOutbound.length;
  const closesLW = Object.values(firstSeenByPolicy).filter(t => t >= lastWeekStart.getTime() && t < weekStart.getTime()).length;
  const netCommissionLW = myTx
    .filter(tx => inRange(tx.activityDate || tx.issueDate, lastWeekStart, weekStart))
    .reduce((s, tx) => s + (Number(tx.netToAccount) || 0), 0);

  const dialsDelta = dialsLW > 0 ? Math.round(((dialsTW - dialsLW) / dialsLW) * 100) : (dialsTW > 0 ? 100 : 0);
  const commissionDelta = netCommissionLW > 0 ? Math.round(((netCommissionTW - netCommissionLW) / netCommissionLW) * 100) : (netCommissionTW > 0 ? 100 : 0);

  // ── YTD + goal ──────────────────────────────────────────────────
  const ytdCommission = myTx
    .filter(tx => inRange(tx.activityDate || tx.issueDate, yearStart))
    .reduce((s, tx) => s + (Number(tx.netToAccount) || 0), 0);
  const goals = settings?.goals || {};
  const annualGoal = Number(goals.annualIncomeGoal) || 0;
  const dailyGoal = Number(goals.dailyDialGoal) || 0;
  const weeklyDialGoal = Number(goals.weeklyDialGoal) || 0;

  const dayOfYear = Math.floor((now - yearStart) / dayMs);
  const yearProgress = (dayOfYear / 365) * 100;
  const goalProgress = annualGoal > 0 ? (ytdCommission / annualGoal) * 100 : null;
  const onPaceFor = dayOfYear > 0 ? Math.round((ytdCommission / dayOfYear) * 365) : 0;
  const aheadBehind = goalProgress != null ? goalProgress - yearProgress : null;

  // ── Streak tracking ─────────────────────────────────────────────
  // Walk back from yesterday (today is incomplete). Count consecutive
  // days where dials >= dailyGoal. Show streak if >= 3 days.
  const streakDays = React.useMemo(() => {
    if (!dailyGoal) return 0;
    let count = 0;
    for (let i = 1; i <= 60; i++) {
      const d = new Date(today.getTime() - i * dayMs);
      const dialsOnDay = myCalls.filter(c => c.date === ymd(d) && c.direction === 'outbound').length;
      if (dialsOnDay >= dailyGoal) count++;
      else break;
    }
    return count;
  }, [myCalls, today, dailyGoal]);

  // ── Daily breakdown (last 7 days incl. today) ───────────────────
  const dailyData = React.useMemo(() => {
    const out = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today.getTime() - i * dayMs);
      const key = ymd(d);
      const dayCalls = myCalls.filter(c => c.date === key && c.direction === 'outbound');
      out.push({
        label: d.toLocaleDateString('en-US', { weekday: 'short' }),
        date: key,
        dials: dayCalls.length,
        talks: dayCalls.filter(c => isContactOutcome(c.outcome)).length,
        hit: dailyGoal > 0 ? dayCalls.length >= dailyGoal : null,
      });
    }
    return out;
  }, [myCalls, today, dailyGoal]);

  // ── Today's prep ────────────────────────────────────────────────
  const myAppointmentsToday = myLeads.filter(l => l.appointmentDate === ymd(today));
  const overdue = React.useMemo(() => {
    return myLeads
      .filter(l => l.status && !['closed','no_show','dnc','recycled'].includes(l.status))
      .map(l => {
        const lastCall = myCalls.filter(c => c.leadId === l.id).map(c => c.date).filter(Boolean).sort().pop();
        const lastOR  = myOutreach.filter(o => o.leadId === l.id).map(o => (o.createdAt || '').slice(0,10)).filter(Boolean).sort().pop();
        const last = [lastCall, lastOR].filter(Boolean).sort().pop();
        const daysSince = last ? Math.floor((tdy - Date.parse(last)) / dayMs) : 999;
        return { lead: l, daysSince };
      })
      .filter(r => r.daysSince >= 7)
      .sort((a, b) => b.daysSince - a.daysSince)
      .slice(0, 5);
  }, [myLeads, myCalls, myOutreach, tdy]);

  // ── Recent wins ─────────────────────────────────────────────────
  const recentWins = React.useMemo(() => {
    const wins = [];
    Object.entries(firstSeenByPolicy).forEach(([policy, t]) => {
      if (t >= weekStart.getTime() && t < tdy + dayMs) {
        const tx = myTx.find(x => x.policy === policy);
        if (tx) wins.push({ policy, insured: tx.insured || '', date: tx.activityDate || tx.issueDate, premium: Math.abs(tx.commPremium) || 0, carrier: deriveCarrier(tx) });
      }
    });
    return wins.sort((a, b) => (b.date || '').localeCompare(a.date || '')).slice(0, 5);
  }, [firstSeenByPolicy, myTx, weekStart, tdy]);

  // ── Recent chargebacks (last 14 days) ───────────────────────────
  // Chargebacks land on commission statements — we surface them so the
  // producer sees them at the moment of upload instead of finding out
  // weeks later when their direct deposit is short. Filter is just
  // negative netToAccount tied to ME inside the window.
  const recentChargebacks = React.useMemo(() => {
    const since = tdy - 14 * dayMs;
    return myTx
      .filter(tx => (Number(tx.netToAccount) || 0) < 0 || CHARGEBACK_TYPES.has(tx.activityType))
      .filter(tx => {
        const t = Date.parse(tx.activityDate || tx.issueDate || '');
        return Number.isFinite(t) && t >= since && t < tdy + dayMs;
      })
      .map(tx => ({
        policy: tx.policy,
        insured: tx.insured || '',
        date: tx.activityDate || tx.issueDate,
        amount: Number(tx.netToAccount) || 0,
        carrier: deriveCarrier(tx),
        reason: tx.activityType || '',
      }))
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
      .slice(0, 5);
  }, [myTx, tdy]);
  const chargebacksTotal = recentChargebacks.reduce((s, c) => s + Math.abs(c.amount), 0);

  if (!myProdNum) {
    return (
      <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-10 text-center">
        <div className="text-slate-700 font-semibold">Your account isn't linked to a producer ID</div>
        <div className="text-sm text-slate-500 mt-1">An admin needs to set your <code className="bg-slate-100 px-1 rounded">prodNum</code> in user settings so this view can scope to your activity.</div>
      </div>
    );
  }

  const weekRangeLabel = `${weekStart.toLocaleDateString('en-US', { month:'short', day:'numeric' })} – ${new Date(weekStart.getTime() + 6*dayMs).toLocaleDateString('en-US', { month:'short', day:'numeric' })}`;
  const greeting = now.getHours() < 12 ? 'Good morning' : now.getHours() < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <div className="space-y-4">
      {/* Greeting + week range */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white rounded-2xl p-6">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <div className="text-emerald-300 text-xs uppercase tracking-[0.3em] font-semibold">My Week · {weekRangeLabel}</div>
            <h1 className="text-3xl font-bold mt-1">{greeting}, {firstName}.</h1>
            <p className="text-sm text-slate-300 mt-1">Your scoreboard — filtered to just your activity.</p>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-400 uppercase tracking-wider">Net this week</div>
            <div className={`text-4xl font-bold tabular-nums ${netCommissionTW >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>{fmt(netCommissionTW)}</div>
            <div className={`text-xs mt-1 ${commissionDelta >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
              {commissionDelta >= 0 ? '▲' : '▼'} {Math.abs(commissionDelta)}% vs last week
            </div>
          </div>
        </div>
      </div>

      {/* Annual income goal progress */}
      {annualGoal > 0 ? (
        <div className={`bg-white border-2 rounded-2xl p-5 ${aheadBehind != null && aheadBehind >= 0 ? 'border-emerald-300' : 'border-amber-300'}`}>
          <div className="flex items-baseline justify-between gap-2 mb-2 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Annual income goal · {now.getFullYear()}</div>
              <div className="text-lg font-semibold text-slate-900 mt-0.5">
                {fmt(ytdCommission)} <span className="text-slate-400">of</span> {fmt(annualGoal)}
              </div>
            </div>
            <div className="text-right">
              <div className={`text-2xl font-bold ${aheadBehind != null && aheadBehind >= 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                {goalProgress != null ? `${goalProgress.toFixed(0)}%` : '—'}
              </div>
              <div className="text-xs text-slate-500">{yearProgress.toFixed(0)}% of year elapsed</div>
            </div>
          </div>
          <div className="h-3 bg-slate-100 rounded-full overflow-hidden relative">
            <div
              className={`h-full rounded-full transition-all ${aheadBehind != null && aheadBehind >= 0 ? 'bg-emerald-500' : 'bg-amber-500'}`}
              style={{ width: `${Math.min(100, goalProgress || 0)}%` }}
            />
            <div className="absolute top-0 bottom-0 border-l-2 border-slate-400/60" style={{ left: `${Math.min(100, yearProgress)}%` }} title="Year-progress marker"/>
          </div>
          <div className="mt-2 text-xs text-slate-600 flex items-center justify-between gap-2 flex-wrap">
            <span>
              On pace for <strong>{fmt(onPaceFor)}</strong> by year-end
              {aheadBehind != null && (
                <span className={aheadBehind >= 0 ? 'text-emerald-700 font-semibold ml-1.5' : 'text-amber-700 font-semibold ml-1.5'}>
                  ({aheadBehind >= 0 ? '+' : ''}{aheadBehind.toFixed(0)} pts {aheadBehind >= 0 ? 'ahead' : 'behind'})
                </span>
              )}
            </span>
            <span className="text-slate-500">Need <strong>{fmt(Math.max(0, (annualGoal - ytdCommission) / Math.max(1, 52 - Math.floor(dayOfYear / 7))))}</strong>/week to hit goal</span>
          </div>
        </div>
      ) : null}

      {/* Streak banner */}
      {streakDays >= 3 && (
        <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="text-3xl"></div>
            <div>
              <div className="text-xs uppercase tracking-wider opacity-90 font-bold">Streak</div>
              <div className="text-lg font-bold">{streakDays} consecutive days hitting your {dailyGoal}-dial goal</div>
            </div>
          </div>
          <div className="text-xs opacity-90">Don't break the chain.</div>
        </div>
      )}

      {/* This week KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Dials" value={dialsTW.toLocaleString()} sub={weeklyDialGoal > 0 ? `${Math.round((dialsTW / weeklyDialGoal) * 100)}% of ${weeklyDialGoal} goal` : `${dialsDelta >= 0 ? '+' : ''}${dialsDelta}% vs last week`} tone={weeklyDialGoal > 0 ? (dialsTW >= weeklyDialGoal ? 'emerald' : dialsTW >= weeklyDialGoal * 0.7 ? 'amber' : 'red') : 'slate'}/>
        <KpiCard label="Pickup rate" value={`${pickupRateTW.toFixed(0)}%`} sub={`${talksTW} talks out of ${dialsTW}`} tone={pickupRateTW >= 30 ? 'emerald' : pickupRateTW >= 15 ? 'amber' : 'red'}/>
        <KpiCard label="Appts booked" value={apptsBookedTW.toLocaleString()} sub="from this week's dials" tone="violet"/>
        <KpiCard label="Closes" value={closesTW.toLocaleString()} sub={closesLW > 0 ? `${closesLW} last week` : ''} tone={closesTW > 0 ? 'emerald' : 'slate'}/>
      </div>

      {/* Day-by-day breakdown */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Last 7 days · dials per day</div>
        <div className="flex items-end gap-2 h-32">
          {dailyData.map(d => {
            const maxDial = Math.max(dailyGoal || 0, ...dailyData.map(x => x.dials), 1);
            const barHeight = (d.dials / maxDial) * 100;
            return (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                <div className="text-xs font-bold text-slate-700 tabular-nums">{d.dials}</div>
                <div className="w-full bg-slate-100 rounded-t-md flex items-end" style={{ height: '70%' }}>
                  <div
                    className={`w-full rounded-t-md transition-all ${d.hit === true ? 'bg-emerald-500' : d.hit === false ? 'bg-amber-400' : 'bg-slate-400'}`}
                    style={{ height: `${barHeight}%` }}
                    title={`${d.dials} dials, ${d.talks} talks`}
                  />
                </div>
                <div className="text-[10px] text-slate-500">{d.label}</div>
              </div>
            );
          })}
        </div>
        {dailyGoal > 0 && (
          <div className="text-[10px] text-slate-500 mt-2">Green bars = hit your {dailyGoal}-dial goal. Amber = below.</div>
        )}
      </div>

      {/* Today's prep + Recent wins side-by-side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Today's prep</div>
          {myAppointmentsToday.length === 0 && overdue.length === 0 ? (
            <div className="text-sm text-slate-400 italic py-4 text-center">Clean slate — nothing pending. Make some dials.</div>
          ) : (
            <div className="space-y-2">
              {myAppointmentsToday.map(l => (
                <div key={l.id} className="flex items-center gap-2 text-sm">
                  <span className="text-amber-600"></span>
                  <span className="font-semibold">{l.name}</span>
                  <span className="text-slate-500">at {l.appointmentTime || 'TBD'}</span>
                </div>
              ))}
              {overdue.map(({ lead, daysSince }) => (
                <div key={lead.id} className="flex items-center gap-2 text-sm">
                  <span className="text-red-600">●</span>
                  <span className="font-semibold">{lead.name || '(no name)'}</span>
                  <span className="text-slate-500">stale {daysSince}d</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">This week's wins</div>
          {recentWins.length === 0 ? (
            <div className="text-sm text-slate-400 italic py-4 text-center">No closes yet this week. Go get one.</div>
          ) : (
            <div className="space-y-2">
              {recentWins.map(w => (
                <div key={w.policy} className="flex items-center gap-2 text-sm">
                  <span className="text-emerald-600">✓</span>
                  <button onClick={() => openClientProfile(w.insured)} className="font-semibold hover:text-emerald-700 truncate">{w.insured || w.policy}</button>
                  <span className="text-slate-500 text-xs">{w.carrier}</span>
                  <span className="ml-auto text-emerald-700 font-semibold tabular-nums">{fmt(w.premium)}/mo</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent chargebacks — appears only when there's something to flag */}
      {recentChargebacks.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-xs uppercase tracking-wider text-red-700 font-bold">Recent chargebacks · last 14 days</div>
            <div className="text-sm font-bold text-red-700">{fmt(chargebacksTotal)} total</div>
          </div>
          <div className="space-y-1.5">
            {recentChargebacks.map(c => (
              <div key={`${c.policy}-${c.date}`} className="flex items-center gap-2 text-sm">
                <span className="text-red-600">▼</span>
                <button onClick={() => openClientProfile(c.insured)} className="font-semibold text-slate-800 hover:text-red-700 truncate">{c.insured || c.policy}</button>
                <span className="text-xs text-slate-500">{c.carrier}{c.reason ? ` · ${c.reason}` : ''}</span>
                <span className="text-xs text-slate-400 ml-auto">{c.date}</span>
                <span className="text-red-700 font-bold tabular-nums ml-2">{fmt(c.amount)}</span>
              </div>
            ))}
          </div>
          <button onClick={() => onGotoTab('retention')} className="mt-2 text-xs font-semibold text-red-700 hover:text-red-900">
            View conservation worklist →
          </button>
        </div>
      )}

      {/* Goal editor */}
      <MyWeekGoalEditor settings={settings} setSettings={setSettings}/>
    </div>
  );
}

function KpiCard({ label, value, sub, tone = 'slate' }) {
  const colors = {
    emerald: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    amber:   'text-amber-700 bg-amber-50 border-amber-200',
    red:     'text-red-700 bg-red-50 border-red-200',
    violet:  'text-violet-700 bg-violet-50 border-violet-200',
    slate:   'text-slate-700 bg-white border-slate-200',
  };
  return (
    <div className={`border rounded-xl p-4 ${colors[tone] || colors.slate}`}>
      <div className="text-xs uppercase tracking-wider opacity-70 font-semibold">{label}</div>
      <div className="text-3xl font-bold mt-1 tabular-nums">{value}</div>
      {sub && <div className="text-xs opacity-70 mt-1">{sub}</div>}
    </div>
  );
}

function MyWeekGoalEditor({ settings, setSettings }) {
  const [open, setOpen] = React.useState(false);
  const goals = settings?.goals || {};
  const [draft, setDraft] = React.useState({
    annualIncomeGoal: goals.annualIncomeGoal || '',
    dailyDialGoal:    goals.dailyDialGoal    || '',
    weeklyDialGoal:   goals.weeklyDialGoal   || '',
  });
  React.useEffect(() => {
    setDraft({
      annualIncomeGoal: goals.annualIncomeGoal || '',
      dailyDialGoal:    goals.dailyDialGoal    || '',
      weeklyDialGoal:   goals.weeklyDialGoal   || '',
    });
  }, [goals.annualIncomeGoal, goals.dailyDialGoal, goals.weeklyDialGoal]);

  function save() {
    setSettings({
      ...settings,
      goals: {
        ...(settings?.goals || {}),
        annualIncomeGoal: Number(draft.annualIncomeGoal) || 0,
        dailyDialGoal:    Number(draft.dailyDialGoal)    || 0,
        weeklyDialGoal:   Number(draft.weeklyDialGoal)   || 0,
      },
    });
    setOpen(false);
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between p-3 hover:bg-slate-50 text-left">
        <div>
          <div className="text-sm font-semibold text-slate-800">Set your personal goals</div>
          <div className="text-xs text-slate-500 mt-0.5">Annual income goal + daily/weekly dial goals drive progress + streak tracking above.</div>
        </div>
        <ChevronRight size={16} className={`text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}/>
      </button>
      {open && (
        <div className="p-4 border-t border-slate-100 grid grid-cols-1 md:grid-cols-3 gap-3 bg-slate-50">
          <label className="block">
            <span className="text-xs text-slate-500">Annual income goal ($)</span>
            <input type="number" min="0" value={draft.annualIncomeGoal} onChange={e => setDraft({...draft, annualIncomeGoal: e.target.value})} placeholder="200000" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Daily dial goal</span>
            <input type="number" min="0" value={draft.dailyDialGoal} onChange={e => setDraft({...draft, dailyDialGoal: e.target.value})} placeholder="80" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Weekly dial goal</span>
            <input type="number" min="0" value={draft.weeklyDialGoal} onChange={e => setDraft({...draft, weeklyDialGoal: e.target.value})} placeholder="400" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
          </label>
          <div className="md:col-span-3 flex justify-end gap-2">
            <button onClick={() => setOpen(false)} className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5">Cancel</button>
            <button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-1.5 rounded-lg">Save goals</button>
          </div>
        </div>
      )}
    </div>
  );
}

// AI-generated daily briefing — compresses dashboard scanning into a
// 30-second read. Cached in localStorage per-day so the operator
// doesn't pay for a fresh Anthropic call every time they open the
// briefing tab. Regenerate button forces a refresh.
function DailyAiBriefing({ transactions, leads, calls, conservation, producers, settings, thisWeek, lastWeek, openClientProfile, onGotoTab }) {
  const [busy, setBusy] = React.useState(false);
  const [briefing, setBriefing] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [collapsed, setCollapsed] = React.useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const cacheKey = `ai_briefing_${today}`;

  React.useEffect(() => {
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) setBriefing(JSON.parse(cached));
    } catch {}
  }, [cacheKey]);

  const buildContext = React.useCallback(() => {
    const dayMs = 86400000;
    const now = Date.now();

    // Stuck leads — going cold or overdue.
    const stuck = leads
      .filter(l => l.status && !['closed', 'no_show', 'dnc', 'recycled'].includes(l.status))
      .map(l => {
        const lastCallDate = calls.filter(c => c.leadId === l.id).map(c => c.date).sort().pop();
        const daysSince = lastCallDate ? Math.floor((now - Date.parse(lastCallDate)) / dayMs) : null;
        return { ...l, daysSinceContact: daysSince };
      })
      .filter(l => l.daysSinceContact == null || l.daysSinceContact >= 7)
      .slice(0, 10)
      .map(l => ({ id: l.id, name: l.name, status: l.status, daysSinceContact: l.daysSinceContact }));

    // Critical conservation events.
    const criticalConservation = (conservation || [])
      .filter(e => (e.eventType === 'lapse_warning' || e.eventType === 'nsf') && e.status !== 'resolved')
      .slice(0, 8)
      .map(e => ({ id: e.id, client: e.clientName, type: e.eventType, payByDate: e.payByDate, terminationDate: e.terminationDate }));

    // Per-producer week-over-week activity changes.
    const txByProducer = (transactions || []).reduce((m, tx) => {
      const k = tx.prodNum || 'unknown';
      (m[k] = m[k] || []).push(tx);
      return m;
    }, {});
    const producerChanges = Object.entries(txByProducer)
      .map(([prodNum, txs]) => {
        const tw = txs.filter(t => Date.parse(t.activityDate || t.issueDate || 0) >= now - 7 * dayMs).length;
        const lw = txs.filter(t => {
          const d = Date.parse(t.activityDate || t.issueDate || 0);
          return d >= now - 14 * dayMs && d < now - 7 * dayMs;
        }).length;
        const change = lw > 0 ? Math.round(((tw - lw) / lw) * 100) : (tw > 0 ? 100 : 0);
        return { prodNum, name: producers[prodNum]?.name || prodNum, thisWeek: tw, lastWeek: lw, changePct: change };
      })
      .filter(p => Math.abs(p.changePct) >= 25 && (p.thisWeek + p.lastWeek) >= 3)
      .slice(0, 5);

    return {
      date: today,
      agency: settings?.ownerName || 'your agency',
      thisWeek: {
        premiumWritten: Math.round(thisWeek?.mineWritten || 0),
        premiumCharged: Math.round(thisWeek?.mineCB || 0),
        activityRows: thisWeek?.count || 0,
        activeAgents: thisWeek?.mineAgentCount || 0,
      },
      lastWeek: {
        premiumWritten: Math.round(lastWeek?.mineWritten || 0),
        premiumCharged: Math.round(lastWeek?.mineCB || 0),
        activityRows: lastWeek?.count || 0,
      },
      stuckLeads: stuck,
      criticalConservation,
      producerWeekOverWeek: producerChanges,
    };
  }, [transactions, leads, calls, conservation, producers, settings, thisWeek, lastWeek, today]);

  async function regenerate() {
    setBusy(true); setErr(null);
    try {
      const ctx = buildContext();
      const out = await generateDailyBriefing(ctx);
      if (!out) { setErr('AI Briefing is taking a moment. Try Regenerate.'); return; }
      setBriefing(out);
      try { localStorage.setItem(cacheKey, JSON.stringify(out)); } catch {}
    } catch (e) {
      // Always show a clean, friendly message. Never leak raw infra
      // details to operators — they pay for AI in their subscription.
      // eslint-disable-next-line no-console
      console.error('[briefing] failed:', e?.message || e);
      setErr('AI Briefing is briefly unavailable. Hit Regenerate to retry.');
    } finally {
      setBusy(false);
    }
  }

  // Auto-generate once per day on mount if not cached.
  React.useEffect(() => {
    if (!briefing && !busy) regenerate(); // eslint-disable-line
  }, []); // intentional: only on first mount

  const sevTone = { critical: 'red', warning: 'amber', info: 'slate' };

  return (
    <div className="bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 text-white rounded-2xl p-5 shadow-lg">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wider bg-white/20 px-2 py-0.5 rounded-full">AI Briefing</span>
          <span className="text-xs text-white/70">Updated {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={regenerate} disabled={busy} className="text-xs font-semibold bg-white/15 hover:bg-white/25 disabled:opacity-50 px-3 py-1 rounded-lg">{busy ? 'Thinking…' : 'Regenerate'}</button>
          <button onClick={() => setCollapsed(c => !c)} className="text-xs text-white/80 hover:text-white px-2">{collapsed ? 'Expand' : 'Collapse'}</button>
        </div>
      </div>

      {!collapsed && (
        <div className="mt-4">
          {err && <div className="text-sm text-red-200 bg-red-900/40 px-3 py-2 rounded mt-2">{err}</div>}
          {!briefing && !err && busy && <div className="text-sm text-white/70 italic">Reading your agency data…</div>}
          {briefing && (
            <div className="space-y-3">
              {briefing.narrative && (
                <p className="text-sm text-white/90 italic leading-relaxed">{briefing.narrative}</p>
              )}
              {Array.isArray(briefing.actions) && briefing.actions.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-white/70 mb-1">Action today</div>
                  <ul className="space-y-1">
                    {briefing.actions.map((a, i) => (
                      <li key={i} className="text-sm flex items-start gap-2">
                        <span className="text-white/60 mt-0.5">•</span>
                        <span>{a.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {Array.isArray(briefing.alerts) && briefing.alerts.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-white/70 mb-1">Alerts</div>
                  <ul className="space-y-1">
                    {briefing.alerts.map((a, i) => (
                      <li key={i} className="text-sm flex items-start gap-2">
                        <span className={`mt-0.5 text-${sevTone[a.severity] || 'slate'}-300`}>⚠</span>
                        <span>{a.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {Array.isArray(briefing.wins) && briefing.wins.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-white/70 mb-1">Wins</div>
                  <ul className="space-y-1">
                    {briefing.wins.map((w, i) => (
                      <li key={i} className="text-sm flex items-start gap-2">
                        <span className="text-emerald-300 mt-0.5">✓</span>
                        <span>{w}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BriefingView(props) {
  const transactions = props.transactions || [];
  const producers = props.producers || {};
  const clientProfiles = props.clientProfiles || {};
  const isEmptyAccount =
    transactions.length === 0 &&
    Object.keys(producers).length === 0 &&
    Object.keys(clientProfiles).length === 0;
  return isEmptyAccount
    ? <BriefingWelcome settings={props.settings || {}} onGotoTab={props.onGotoTab} />
    : <BriefingDashboard {...props} />;
}

function BriefingDashboard({ transactions = [], producers = {}, producerList = [], settings = {}, clientProfiles = {}, leads = [], calls = [], conservation = [], openClientProfile = () => {}, onGotoTab = () => {} }) {
  const now = React.useMemo(() => new Date(), []);
  const dayMs = 24 * 3600 * 1000;
  const startOfWeek = (d, offsetWeeks = 0) => {
    const x = new Date(d);
    const dayNr = (x.getDay() + 6) % 7; // 0=Mon
    x.setDate(x.getDate() - dayNr - offsetWeeks * 7);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const weekRanges = React.useMemo(() => {
    const out = [];
    for (let i = 0; i < 4; i++) {
      const start = startOfWeek(now, i);
      const end = new Date(start); end.setDate(end.getDate() + 7);
      out.push({ start, end, label: `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}–${new Date(end.getTime() - dayMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` });
    }
    return out; // index 0 = this week, 1 = last week, ...
  }, [now, dayMs]); // eslint-disable-line

  // Bucket every transaction into a week index (0..3) or null if older.
  const txByWeek = React.useMemo(() => {
    const buckets = [[], [], [], []];
    (transactions || []).forEach(tx => {
      const d = parseDate(tx.activityDate);
      if (!d) return;
      for (let i = 0; i < weekRanges.length; i++) {
        const { start, end } = weekRanges[i];
        if (d >= start && d < end) { buckets[i].push(tx); return; }
      }
    });
    return buckets;
  }, [transactions, weekRanges]);

  const teamOf = (prodNum) => producerList.find(p => p.prodNum === prodNum)?.team || producers[prodNum]?.team || 'unassigned';

  // Per-week roll-ups: premium written, premium charged back, net,
  // unique agent count, override $ (for the user's team only).
  const partnerPct = (settings.camPercent || 0) / 100;
  const weekRollups = React.useMemo(() => {
    return txByWeek.map(txs => {
      let writtenAll = 0, cbAll = 0, mineWritten = 0, mineCB = 0, overrideEarned = 0, overrideCB = 0;
      const agents = new Set();
      const mineAgents = new Set();
      txs.forEach(tx => {
        const team = teamOf(tx.prodNum);
        const prem = Math.abs(Number(tx.commPremium) || 0);
        const amt  = Number(tx.netToAccount) || 0;
        if (tx.prodNum) agents.add(tx.prodNum);
        if (amt >= 0) {
          writtenAll += prem;
          if (team === 'mine') {
            mineWritten += prem;
            overrideEarned += amt - prem * partnerPct;
            mineAgents.add(tx.prodNum);
          }
        } else {
          cbAll += prem;
          if (team === 'mine') {
            mineCB += prem;
            overrideCB += amt + prem * partnerPct;
          }
        }
      });
      return {
        writtenAll, cbAll, netAll: writtenAll - cbAll,
        mineWritten, mineCB, mineNet: mineWritten - mineCB,
        overrideEarned, overrideCB, overrideNet: overrideEarned + overrideCB,
        agentCount: agents.size, mineAgentCount: mineAgents.size, count: txs.length,
      };
    });
  }, [txByWeek, partnerPct]); // eslint-disable-line

  const thisWeek = weekRollups[0];
  const lastWeek = weekRollups[1];
  const delta = thisWeek.mineWritten - lastWeek.mineWritten;
  const deltaPct = lastWeek.mineWritten > 0 ? (delta / lastWeek.mineWritten) * 100 : (thisWeek.mineWritten > 0 ? 100 : 0);

  // Top performer this week — most premium written on the user's team.
  const topPerformer = React.useMemo(() => {
    const byAgent = {};
    txByWeek[0].forEach(tx => {
      if (teamOf(tx.prodNum) !== 'mine') return;
      const amt = Number(tx.netToAccount) || 0;
      if (amt < 0) return;
      const prem = Math.abs(Number(tx.commPremium) || 0);
      if (!byAgent[tx.prodNum]) byAgent[tx.prodNum] = { prodNum: tx.prodNum, premium: 0, policies: new Set() };
      byAgent[tx.prodNum].premium += prem;
      if (tx.policy) byAgent[tx.prodNum].policies.add(`${tx.mga||''}|${tx.policy}`);
    });
    const list = Object.values(byAgent).sort((a, b) => b.premium - a.premium);
    return list[0] || null;
  }, [txByWeek]); // eslint-disable-line

  // Biggest single policy this week — highest premium on a single policy.
  const biggestPolicy = React.useMemo(() => {
    const byPolicy = {};
    txByWeek[0].forEach(tx => {
      if (!tx.policy) return;
      const amt = Number(tx.netToAccount) || 0;
      if (amt < 0) return;
      const k = `${tx.mga||''}|${tx.policy}`;
      const prem = Math.abs(Number(tx.commPremium) || 0);
      if (!byPolicy[k] || prem > byPolicy[k].premium) {
        byPolicy[k] = { key: k, policy: tx.policy, insured: tx.insured, prodNum: tx.prodNum, premium: prem, carrier: deriveCarrier(tx) };
      }
    });
    return Object.values(byPolicy).sort((a, b) => b.premium - a.premium)[0] || null;
  }, [txByWeek]); // eslint-disable-line

  // ── Action items ───────────────────────────────────────────────────
  // Each item: { priority: 'high'|'med', icon, headline, detail, action }
  const actionItems = React.useMemo(() => {
    const items = [];

    // 1. Chargeback rate spike per agent (this week vs prior 3 weeks)
    const agentCB = {};
    txByWeek.forEach((txs, weekIdx) => {
      txs.forEach(tx => {
        if (teamOf(tx.prodNum) !== 'mine') return;
        const a = (agentCB[tx.prodNum] = agentCB[tx.prodNum] || { thisWeek: { written: 0, cb: 0 }, prior: { written: 0, cb: 0 } });
        const prem = Math.abs(Number(tx.commPremium) || 0);
        const amt  = Number(tx.netToAccount) || 0;
        const bucket = weekIdx === 0 ? a.thisWeek : a.prior;
        if (amt >= 0) bucket.written += prem; else bucket.cb += prem;
      });
    });
    Object.entries(agentCB).forEach(([prodNum, d]) => {
      const thisRate = d.thisWeek.written > 0 ? d.thisWeek.cb / d.thisWeek.written : 0;
      const priorRate = d.prior.written > 0 ? d.prior.cb / d.prior.written : 0;
      if (d.thisWeek.cb < 200) return; // ignore noise
      if (thisRate > 0.30 && thisRate > priorRate * 2) {
        items.push({
          priority: 'high',
          icon: '',
          headline: `${getProducerName(prodNum, producers) || `#${prodNum}`} — chargeback rate spike`,
          detail: `${(thisRate * 100).toFixed(0)}% this week vs ${(priorRate * 100).toFixed(0)}% prior 3-week avg · ${fmt(d.thisWeek.cb)} in chargebacks`,
          action: { label: 'Review chargebacks', onClick: () => onGotoTab('chargebacks') },
        });
      }
    });

    // 2. Agents inactive 14+ days (mine team, had production 30+ days ago,
    //    nothing since 14 days ago).
    const allMineAgents = new Set(producerList.filter(p => p.team === 'mine').map(p => p.prodNum));
    const recentActivity = {};
    (transactions || []).forEach(tx => {
      if (!allMineAgents.has(tx.prodNum)) return;
      const d = parseDate(tx.activityDate);
      if (!d) return;
      const last = recentActivity[tx.prodNum];
      if (!last || d > last) recentActivity[tx.prodNum] = d;
    });
    const inactive = [];
    allMineAgents.forEach(prodNum => {
      const last = recentActivity[prodNum];
      if (!last) return;
      const daysSince = Math.floor((now.getTime() - last.getTime()) / dayMs);
      if (daysSince >= 14 && daysSince <= 90) {
        inactive.push({ prodNum, daysSince, name: getProducerName(prodNum, producers) || `#${prodNum}` });
      }
    });
    if (inactive.length > 0) {
      inactive.sort((a, b) => b.daysSince - a.daysSince);
      const top3 = inactive.slice(0, 3);
      items.push({
        priority: inactive.length >= 3 ? 'high' : 'med',
        icon: '',
        headline: `${inactive.length} agent${inactive.length === 1 ? '' : 's'} inactive 14+ days`,
        detail: top3.map(a => `${a.name} (${a.daysSince}d)`).join(' · '),
        action: { label: 'Open Agents', onClick: () => onGotoTab('agents') },
      });
    }

    // 3. Clients in 5-7 day lapse-risk window with no contact log entry in
    //    the last 14 days. Heuristic: a policy issued 23-25 days ago is
    //    days 5-7 before its monthly anniversary draft. Look at every
    //    in-force policy on the user's team and surface high-risk ones.
    const lapseWindowClients = [];
    (transactions || []).forEach(tx => {
      if (teamOf(tx.prodNum) !== 'mine') return;
      if (!tx.issueDate || !tx.insured) return;
      const issue = parseDate(tx.issueDate);
      if (!issue) return;
      const daysOld = Math.floor((now.getTime() - issue.getTime()) / dayMs);
      // Days since most recent monthly draft anniversary
      const sinceLast = daysOld % 30;
      const daysUntilDraft = (30 - sinceLast) % 30;
      if (daysUntilDraft < 3 || daysUntilDraft > 7) return; // 5-7 day window only
      const clientKey = clientKeyOf(tx.insured);
      const profile = clientProfiles[clientKey] || {};
      const log = profile.contactLog || [];
      const recent = log.find(e => {
        const d = parseDate(e.date);
        return d && (now.getTime() - d.getTime()) <= 14 * dayMs;
      });
      if (recent) return;
      if (!lapseWindowClients.find(c => c.clientKey === clientKey)) {
        lapseWindowClients.push({ clientKey, insured: tx.insured, agent: getProducerName(tx.prodNum, producers), daysUntilDraft, prodNum: tx.prodNum });
      }
    });
    if (lapseWindowClients.length > 0) {
      const top = lapseWindowClients.sort((a, b) => a.daysUntilDraft - b.daysUntilDraft).slice(0, 5);
      items.push({
        priority: 'med',
        icon: '⚠',
        headline: `${lapseWindowClients.length} client${lapseWindowClients.length === 1 ? '' : 's'} entering lapse-risk window with no recent contact`,
        detail: `Top: ${top.map(c => `${titleCase(c.insured)} (draft in ${c.daysUntilDraft}d)`).join(' · ')}`,
        action: { label: 'Open VA Workflow', onClick: () => onGotoTab('va') },
      });
    }

    // 4. Fresh chargebacks this week (notable size only) — pick the top 3
    //    by premium so the user can hop straight to the policy / client.
    const freshCBs = [];
    txByWeek[0].forEach(tx => {
      if ((Number(tx.netToAccount) || 0) >= 0) return;
      if (teamOf(tx.prodNum) !== 'mine') return;
      const prem = Math.abs(Number(tx.commPremium) || 0);
      if (prem < 1000) return;
      freshCBs.push({ insured: tx.insured, policy: tx.policy, prodNum: tx.prodNum, premium: prem, type: tx.activityType });
    });
    if (freshCBs.length > 0) {
      const top = freshCBs.sort((a, b) => b.premium - a.premium).slice(0, 3);
      items.push({
        priority: 'high',
        icon: '',
        headline: `${freshCBs.length} fresh chargeback${freshCBs.length === 1 ? '' : 's'} this week`,
        detail: top.map(c => `${titleCase(c.insured || '—')} (${fmt(c.premium)} premium · ${getProducerName(c.prodNum, producers)})`).join(' · '),
        action: { label: 'See Chargebacks', onClick: () => onGotoTab('chargebacks') },
      });
    }

    // 5. Unassigned producers blocking override math
    const unassigned = producerList.filter(p => p.team === 'unassigned' && (p.premiumPositive || 0) > 0);
    if (unassigned.length > 0) {
      items.push({
        priority: 'med',
        icon: '',
        headline: `${unassigned.length} producer${unassigned.length === 1 ? '' : 's'} unassigned but writing business`,
        detail: 'These rows don\'t roll up into your override math until they\'re assigned to a team.',
        action: { label: 'Assign teams', onClick: () => onGotoTab('producers') },
      });
    }

    // Sort: high before med, within same priority order kept stable.
    return items.sort((a, b) => (a.priority === 'high' ? 0 : 1) - (b.priority === 'high' ? 0 : 1));
  }, [txByWeek, transactions, producerList, producers, clientProfiles, now, dayMs, onGotoTab]); // eslint-disable-line

  // Trend chart data — 4 weeks oldest → newest.
  const trendData = weekRollups.slice().reverse().map((w, i) => ({
    label: weekRanges[3 - i].label,
    written: Math.round(w.mineWritten),
    cb: -Math.round(w.mineCB),
  }));

  // ── Render ─────────────────────────────────────────────────────────
  const empty = (transactions || []).length === 0;
  if (empty) {
    return (
      <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-12 text-center max-w-none mx-auto">
        <div className="text-2xl mb-2"></div>
        <h2 className="text-xl font-bold text-slate-900">No transactions yet</h2>
        <p className="text-sm text-slate-500 mt-2">Upload a commission statement and your weekly briefing will populate automatically.</p>
        <button onClick={() => onGotoTab('dashboard')} className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-semibold">Open Dashboard</button>
      </div>
    );
  }

  const arrow = delta > 0 ? '▲' : delta < 0 ? '▼' : '→';
  const deltaColor = delta > 0 ? 'text-emerald-300' : delta < 0 ? 'text-red-300' : 'text-slate-300';

  return (
    <div className="space-y-4">
      <DailyAiBriefing
        transactions={transactions}
        leads={leads}
        calls={calls}
        conservation={conservation}
        producers={producers}
        settings={settings}
        thisWeek={thisWeek}
        lastWeek={lastWeek}
        openClientProfile={openClientProfile}
        onGotoTab={onGotoTab}
      />

      {/* Headline */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white rounded-2xl p-5">
        <div className="text-xs uppercase tracking-wider text-slate-300 font-semibold">This week · {weekRanges[0].label}</div>
        <div className="text-5xl font-bold mt-1 leading-none">{fmt(thisWeek.mineWritten)}</div>
        <div className="text-sm text-slate-300 mt-2">Your team's premium written</div>
        <div className={`text-sm mt-3 ${deltaColor}`}>
          <span className="font-bold">{arrow} {fmt(Math.abs(delta))}</span>{' '}
          ({deltaPct >= 0 ? '+' : ''}{deltaPct.toFixed(0)}%) vs last week ({fmt(lastWeek.mineWritten)})
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-xs">
          <div className="bg-white/10 rounded-lg p-2">
            <div className="text-slate-300">Override earned</div>
            <div className="font-bold text-emerald-300 mt-0.5">{fmt(thisWeek.overrideNet)}</div>
          </div>
          <div className="bg-white/10 rounded-lg p-2">
            <div className="text-slate-300">CB premium</div>
            <div className={`font-bold mt-0.5 ${thisWeek.mineCB > 0 ? 'text-red-300' : 'text-white'}`}>{fmt(thisWeek.mineCB)}</div>
          </div>
          <div className="bg-white/10 rounded-lg p-2">
            <div className="text-slate-300">Active agents</div>
            <div className="font-bold mt-0.5">{thisWeek.mineAgentCount}</div>
          </div>
          <div className="bg-white/10 rounded-lg p-2">
            <div className="text-slate-300">Activity rows</div>
            <div className="font-bold mt-0.5">{thisWeek.count}</div>
          </div>
        </div>
      </div>

      {/* Attention queue */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div>
            <div className="font-bold text-slate-900">What needs your attention</div>
            <div className="text-xs text-slate-500 mt-0.5">{actionItems.length} item{actionItems.length === 1 ? '' : 's'} · auto-prioritized by revenue impact</div>
          </div>
        </div>
        {actionItems.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-2xl mb-1"></div>
            <div className="text-sm font-semibold text-emerald-700">All clear this week</div>
            <div className="text-xs text-slate-500 mt-1">No chargeback spikes, inactive agents, or unhandled lapse-risk clients detected.</div>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {actionItems.map((item, i) => (
              <div key={i} className={`p-4 flex items-start gap-3 ${item.priority === 'high' ? 'bg-red-50/40' : ''}`}>
                <div className="text-xl flex-shrink-0">{item.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900">{item.headline}</div>
                  <div className="text-xs text-slate-600 mt-0.5">{item.detail}</div>
                </div>
                {item.action && (
                  <button onClick={item.action.onClick} className={`text-xs font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap ${item.priority === 'high' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-slate-900 hover:bg-slate-700 text-white'}`}>
                    {item.action.label} →
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Wins + trend side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Wins */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-3">Wins this week</div>
          <div className="space-y-3">
            {topPerformer ? (
              <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="text-2xl"></div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wide font-bold text-emerald-700">Top performer</div>
                  <div className="font-bold text-slate-900 truncate">{getProducerName(topPerformer.prodNum, producers) || `#${topPerformer.prodNum}`}</div>
                  <div className="text-xs text-slate-600">{fmt(topPerformer.premium)} premium · {topPerformer.policies.size} polic{topPerformer.policies.size === 1 ? 'y' : 'ies'}</div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-400 italic">No team production this week yet.</div>
            )}
            {biggestPolicy && (
              <div className="flex items-center gap-3 p-3 bg-violet-50 border border-violet-200 rounded-lg">
                <div className="text-2xl"></div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wide font-bold text-violet-700">Biggest policy</div>
                  <button onClick={() => openClientProfile(clientKeyOf(biggestPolicy.insured))} className="font-bold text-slate-900 hover:text-violet-700 hover:underline text-left">
                    {titleCase(biggestPolicy.insured)}
                  </button>
                  <div className="text-xs text-slate-600">{fmt(biggestPolicy.premium)} · {biggestPolicy.carrier} · {getProducerName(biggestPolicy.prodNum, producers)}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 4-week trend */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">4-week premium trend</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={trendData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
              <XAxis dataKey="label" tick={{ fontSize: 11 }}/>
              <YAxis tickFormatter={fmtCompact} tick={{ fontSize: 11 }}/>
              <Tooltip formatter={(v) => fmt(Math.abs(v))}/>
              <Bar dataKey="written" name="Written" fill="#10b981"/>
              <Bar dataKey="cb" name="Charged back" fill="#ef4444"/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function Dashboard({ agg, currentMonthData, prevMonthData, monthlyAgg, periodMode, settings, unassignedCount, onGotoProducers, producerList, transactions, producers = {}, openClientProfile = () => {}, clientProfiles = {}, allTransactions = [], onFileChange = () => {}, onPasteStatement = () => {} }) {
  // Fresh-account welcome: no transactions and no configured producers ⇒
  // show the onboarding empty state instead of the empty dashboard.
  // (Leads aren't a feature in this codebase; "all three primary domains
  // empty" reduces to transactions + producers.)
  if ((transactions || []).length === 0 && Object.keys(producers || {}).length === 0) {
    return (
      <EmptyDashboard
        onFileChange={onFileChange}
        onPasteStatement={onPasteStatement}
        onGotoProducers={onGotoProducers}
      />
    );
  }
  // Premium aggregates — the Dashboard surfaces premium (business volume)
  // rather than commission. Commission still drives the Overrides / Teams
  // tabs, but headline numbers and charts here are premium-based so the
  // owner can see what their team is *writing*, not what they're being paid.
  const teamOf = React.useMemo(() => {
    const map = {};
    (producerList || []).forEach(p => { map[p.prodNum] = p.team; });
    return (prodNum) => map[prodNum] || 'unassigned';
  }, [producerList]);
  const premium = React.useMemo(() => {
    const empty = () => ({ written: 0, chargedBack: 0, net: 0, count: 0 });
    const out = { total: empty(), mine: empty(), cam: empty(), owner: empty(), unassigned: empty() };
    (transactions || []).forEach(tx => {
      const bucket = teamOf(tx.prodNum);
      const target = out[bucket] || out.unassigned;
      const prem = Math.abs(Number(tx.commPremium) || 0);
      const amt  = Number(tx.netToAccount) || 0;
      out.total.count++; target.count++;
      if (amt >= 0) { out.total.written += prem; target.written += prem; }
      else          { out.total.chargedBack += prem; target.chargedBack += prem; }
      out.total.net += amt >= 0 ? prem : -prem;
      target.net += amt >= 0 ? prem : -prem;
    });
    return out;
  }, [transactions, teamOf]);
  const totalPct = premium.total.written === 0 ? 0 : (premium.total.chargedBack / premium.total.written) * 100;

  // Per-period premium series for the trend charts.
  const periodPremium = React.useMemo(() => {
    const keyFn = periodMode === 'week' ? (t) => t.weekKey : (t) => t.monthKey;
    const map = {};
    (transactions || []).forEach(tx => {
      const k = keyFn(tx);
      if (!k) return;
      if (!map[k]) map[k] = { totalWritten: 0, totalCB: 0, mineWritten: 0, mineCB: 0 };
      const bucket = teamOf(tx.prodNum);
      const prem = Math.abs(Number(tx.commPremium) || 0);
      const amt  = Number(tx.netToAccount) || 0;
      if (amt >= 0) {
        map[k].totalWritten += prem;
        if (bucket === 'mine') map[k].mineWritten += prem;
      } else {
        map[k].totalCB += prem;
        if (bucket === 'mine') map[k].mineCB += prem;
      }
    });
    return (monthlyAgg || []).map(m => ({
      ...m,
      mineWritten:  map[m.key]?.mineWritten  || 0,
      mineCBprem:   map[m.key]?.mineCB       || 0,
      totalWritten: map[m.key]?.totalWritten || 0,
      totalCBprem:  map[m.key]?.totalCB      || 0,
    }));
  }, [transactions, monthlyAgg, periodMode, teamOf]);

  // Charts data ---------------------------------------------------------
  // Top 10 producers by premium written (current filter period)
  const totalClients = new Set((transactions || []).map(tx => (tx.insured || '').trim().toUpperCase())).size;
  const totalPolicies = new Set((transactions || []).map(tx => `${(tx.mga || '').trim()}|${(tx.policy || '').trim().toUpperCase()}`)).size;
  const totalAgents = new Set((transactions || []).map(tx => tx.prodNum)).size;

  const topProducers = (producerList || []).slice().sort((a,b) => (b.premiumPositive || 0) - (a.premiumPositive || 0)).slice(0, 10).map(p => ({
    name: (p.name || '').slice(0, 18) || `#${p.prodNum}`,
    written: Math.max(0, p.premiumPositive || 0),
    chargedBack: Math.abs(p.premiumNegative || 0),
    team: p.team,
  }));

  // Carrier mix (premium share) — ordered Americo, MOO, Transamerica
  const carrierMix = (() => {
    const buckets = {};
    (transactions || []).forEach(tx => {
      const label = deriveCarrier(tx);
      if (!buckets[label]) buckets[label] = 0;
      const prem = Math.abs(Number(tx.commPremium) || 0);
      if ((tx.netToAccount || 0) >= 0) buckets[label] += prem;
    });
    const list = Object.entries(buckets)
      .filter(([,v]) => v > 0)
      .map(([name, value]) => ({ name, value: Math.round(value) }));
    return sortByCarrierOrder(list, x => x.name);
  })();
  // Color map: Americo green, MOO violet, Transamerica amber (then fallbacks)
  const CARRIER_COLOR = { Americo: '#10b981', MOO: '#8b5cf6', Transamerica: '#f59e0b' };
  const PIE_FALLBACKS = ['#3b82f6', '#ef4444', '#14b8a6'];
  const pieColorFor = (name, i) => CARRIER_COLOR[name] || PIE_FALLBACKS[i % PIE_FALLBACKS.length];

  // Persistency leaderboard (only producers with material activity)
  const persistencyData = (producerList || [])
    .filter(p => p.gross > 50)
    .slice()
    .sort((a,b) => b.persistency - a.persistency)
    .slice(0, 15)
    .map(p => ({
      name: (p.name || `#${p.prodNum}`).slice(0, 18),
      persistency: Math.round(p.persistency),
      fill: p.persistency >= 85 ? '#10b981' : p.persistency >= 70 ? '#f59e0b' : '#ef4444',
    }));

  // Per-agent stats table — every producer with material activity in this period
  const agentStats = (producerList || [])
    .filter(p => (p.premiumPositive || 0) !== 0 || (p.premiumNegative || 0) !== 0 || (p.count || 0) > 0)
    .slice()
    .sort((a, b) => (b.premiumPositive || 0) - (a.premiumPositive || 0));

  // Per-carrier stats table — aggregate across all carriers in the current period
  const carrierStats = (() => {
    const buckets = {};
    (transactions || []).forEach(tx => {
      const name = deriveCarrier(tx);
      if (!buckets[name]) buckets[name] = { name, gross: 0, cb: 0, premium: 0, count: 0, policies: new Set(), agents: new Set() };
      const b = buckets[name];
      const amt = Number(tx.netToAccount) || 0;
      const prem = Math.abs(Number(tx.commPremium) || 0);
      if (amt >= 0) b.gross += amt; else b.cb += amt;
      b.premium += prem;
      b.count++;
      if (tx.policy) b.policies.add(`${tx.mga||''}|${tx.policy}`);
      if (tx.prodNum) b.agents.add(tx.prodNum);
    });
    const list = Object.values(buckets).map(b => {
      const net = b.gross + b.cb;
      const persistency = b.gross > 0 ? Math.max(0, Math.min(100, ((b.gross + b.cb) / b.gross) * 100)) : 0;
      const cbRate = b.gross > 0 ? (Math.abs(b.cb) / b.gross) * 100 : 0;
      return { name: b.name, gross: b.gross, cb: b.cb, net, premium: b.premium, count: b.count, policies: b.policies.size, agents: b.agents.size, persistency, cbRate };
    });
    return sortByCarrierOrder(list, x => x.name);
  })();

  return (
    <div className="space-y-4">
      {unassignedCount > 0 && (
        <button onClick={onGotoProducers} className="w-full bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between hover:bg-amber-100 transition-colors">
          <div className="flex items-center gap-3">
            <AlertTriangle className="text-amber-600" size={20}/>
            <div className="text-left">
              <div className="font-semibold text-amber-900">{unassignedCount} producer{unassignedCount>1?'s':''} need team assignment</div>
              <div className="text-sm text-amber-700">Assign each producer to a team in the Producers tab</div>
            </div>
          </div>
          <ChevronRight className="text-amber-600"/>
        </button>
      )}



      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCard icon={<DollarSign/>} label="Premium written" value={fmt(premium.total.written)} sub={`${agg.total.count} transactions · what the team sold`} color="emerald"/>
        <StatCard icon={<TrendingDown/>} label="Premium charged back" value={fmt(premium.total.chargedBack)} sub={`${Math.abs(totalPct).toFixed(1)}% of written`} color="red"/>
        <StatCard icon={<CheckCircle/>} label="Net premium written" value={fmt(premium.total.net)} sub="written − chargebacks" color="slate"/>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCard icon={<FileText/>} label="Clients in view" value={fmt(totalClients)} sub="unique insured names" color="emerald"/>
        <StatCard icon={<Users/>} label="Policies in view" value={fmt(totalPolicies)} sub="unique active policies" color="slate"/>
        <StatCard icon={<DollarSign/>} label="Producers in view" value={fmt(totalAgents)} sub="agents in selected period" color="violet"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title={`${periodMode === 'week' ? 'Weekly' : 'Monthly'} premium written`} subtitle="Your team only" fullscreenable>
          {(h) => (
            <ResponsiveContainer width="100%" height={h}>
              <BarChart data={periodPremium}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                <XAxis dataKey="label" tick={{fontSize:12}}/>
                <YAxis tickFormatter={fmtCompact} tick={{fontSize:12}}/>
                <Tooltip formatter={(v)=>fmt(v)}/>
                <Legend/>
                <Bar dataKey="mineWritten" name="Premium written" fill="#10b981"/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Chargeback trend" subtitle={`${periodMode === 'week' ? 'Weekly' : 'Monthly'} premium charged back · your team`} fullscreenable>
          {(h) => (
            <ResponsiveContainer width="100%" height={h}>
              <LineChart data={periodPremium.map(m => ({ label: m.label, cbPremium: Math.abs(m.mineCBprem || 0) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                <XAxis dataKey="label" tick={{fontSize:12}}/>
                <YAxis tickFormatter={fmtCompact} tick={{fontSize:12}}/>
                <Tooltip formatter={(v)=>fmt(v)}/>
                <Legend/>
                <Line type="monotone" dataKey="cbPremium" name="Premium charged back" stroke="#ef4444" strokeWidth={3}/>
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Carrier mix" subtitle="Share of premium written by carrier" fullscreenable>
          {(h) => (
            <ResponsiveContainer width="100%" height={h}>
              <PieChart>
                <Pie data={carrierMix} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius="40%" outerRadius="75%" paddingAngle={2} label={(d) => `${d.name}: ${fmtCompact(d.value)}`}>
                  {carrierMix.map((entry, i) => <Cell key={i} fill={pieColorFor(entry.name, i)}/>)}
                </Pie>
                <Tooltip formatter={(v)=>fmt(v)}/>
                <Legend/>
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Top 10 producers" subtitle="By premium written (current period)" fullscreenable>
          {(h) => (
            <ResponsiveContainer width="100%" height={h}>
              <BarChart data={topProducers} layout="vertical" margin={{left: 30}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                <XAxis type="number" tickFormatter={fmtCompact} tick={{fontSize:12}}/>
                <YAxis type="category" dataKey="name" tick={{fontSize:11}} width={120}/>
                <Tooltip formatter={(v)=>fmt(v)}/>
                <Legend/>
                <Bar dataKey="written" name="Premium written" fill="#10b981"/>
                <Bar dataKey="chargedBack" name="Premium charged back" fill="#ef4444"/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Persistency leaderboard" subtitle="Top 15 by retention % — green ≥85%, amber 70-85%, red <70%" fullscreenable>
          {(h) => (
            <ResponsiveContainer width="100%" height={h}>
              <BarChart data={persistencyData} layout="vertical" margin={{left: 30}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                <XAxis type="number" domain={[0,100]} tick={{fontSize:12}} tickFormatter={(v)=>`${v}%`}/>
                <YAxis type="category" dataKey="name" tick={{fontSize:11}} width={120}/>
                <Tooltip formatter={(v)=>`${v}%`}/>
                <Bar dataKey="persistency" name="Persistency %">
                  {persistencyData.map((entry, i) => <Cell key={i} fill={entry.fill}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TeamCard label="Owner production" data={premium.owner} color="blue"/>
        <TeamCard label={`${possessive(settings.ownerName)} team`} data={premium.mine} color="emerald"/>
        <TeamCard label="Unassigned" data={premium.unassigned} color="amber"/>
      </div>

      <CarrierStatsTable rows={carrierStats} transactions={transactions} producers={producers} openClientProfile={openClientProfile}/>
      <AgentStatsTable rows={agentStats} settings={settings}/>
    </div>
  );
}

function CarrierStatsTable({ rows, transactions = [], producers = {}, openClientProfile = () => {} }) {
  const [expanded, setExpanded] = useState(null); // carrier name currently expanded
  const [policySort, setPolicySort] = useState({ field: 'issueDate', dir: 'desc' });
  const togglePolicySort = (field) => {
    setPolicySort(s => s.field === field ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'desc' });
  };
  const sortArrow = (field) => policySort.field === field ? (policySort.dir === 'asc' ? ' ▲' : ' ▼') : '';
  if (!rows || rows.length === 0) return null;

  // Build per-carrier policy list lazily from transactions when a row is expanded.
  const policiesForCarrier = (carrierName) => {
    const map = {};
    transactions.forEach(tx => {
      if (deriveCarrier(tx) !== carrierName) return;
      if (!tx.policy) return;
      const k = `${tx.mga||''}|${tx.policy}`;
      if (!map[k]) {
        map[k] = {
          key: k, policy: tx.policy, insured: tx.insured || '',
          prodNum: tx.prodNum, mga: tx.mga || '',
          issueDate: tx.issueDate || '', firstActivity: tx.activityDate || '',
          lastActivity: tx.activityDate || '',
          premium: 0, net: 0, chargebackCount: 0,
        };
      }
      const p = map[k];
      p.net += Number(tx.netToAccount) || 0;
      const prem = Math.abs(Number(tx.commPremium) || 0);
      if (prem > p.premium) p.premium = prem;
      if (!p.issueDate && tx.issueDate) p.issueDate = tx.issueDate;
      if (CHARGEBACK_TYPES.has(tx.activityType) || (tx.netToAccount || 0) < 0) p.chargebackCount++;
      const d = parseDate(tx.activityDate);
      const first = parseDate(p.firstActivity);
      const last = parseDate(p.lastActivity);
      if (d && (!first || d < first)) p.firstActivity = tx.activityDate;
      if (d && (!last || d > last)) p.lastActivity = tx.activityDate;
    });
    const list = Object.values(map).map(p => ({
      ...p,
      status: p.chargebackCount > 0 || p.net < 0 ? 'lapsed' : 'active',
      agentName: getProducerName(p.prodNum, producers),
    }));
    const { field, dir } = policySort;
    const mult = dir === 'asc' ? 1 : -1;
    const cmp = (a, b) => {
      if (field === 'issueDate' || field === 'lastActivity') {
        const av = parseDate(a[field])?.getTime() || 0;
        const bv = parseDate(b[field])?.getTime() || 0;
        return (av - bv) * mult;
      }
      if (field === 'premium' || field === 'net') {
        return ((a[field] || 0) - (b[field] || 0)) * mult;
      }
      if (field === 'status') {
        // active first (asc) or lapsed first (desc)
        const order = (s) => s === 'active' ? 0 : 1;
        return (order(a.status) - order(b.status)) * mult;
      }
      const av = String(a[field] || '').toLowerCase();
      const bv = String(b[field] || '').toLowerCase();
      if (av < bv) return -1 * mult;
      if (av > bv) return 1 * mult;
      return 0;
    };
    return list.sort(cmp);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
        <div className="font-bold">Stats by carrier</div>
        <div className="text-xs text-slate-500 mt-0.5">Current period · ordered Americo → MOO → Transamerica · click a row to see policies</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white border-b border-slate-200">
            <tr className="text-xs text-slate-500 text-left">
              <th className="py-2 px-3"></th>
              <th className="py-2 px-3">Carrier</th>
              <th className="py-2 px-3 text-right">Agents</th>
              <th className="py-2 px-3 text-right">Policies</th>
              <th className="py-2 px-3 text-right">Rows</th>
              <th className="py-2 px-3 text-right">Premium</th>
              <th className="py-2 px-3 text-right">Gross</th>
              <th className="py-2 px-3 text-right">Chargebacks</th>
              <th className="py-2 px-3 text-right">Net</th>
              <th className="py-2 px-3 text-right">Persistency</th>
              <th className="py-2 px-3 text-right">CB rate</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const accent = r.name === 'Americo' ? 'emerald' : r.name === 'MOO' ? 'violet' : r.name === 'Transamerica' ? 'amber' : 'slate';
              const pColor = r.persistency >= 85 ? 'emerald' : r.persistency >= 70 ? 'amber' : 'red';
              const isExpanded = expanded === r.name;
              const policies = isExpanded ? policiesForCarrier(r.name) : [];
              return (
                <React.Fragment key={r.name}>
                  <tr
                    onClick={() => setExpanded(isExpanded ? null : r.name)}
                    className="border-b border-slate-50 last:border-0 hover:bg-slate-50 cursor-pointer"
                  >
                    <td className="py-2 px-3 w-6">
                      <ChevronRight size={14} className={`text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}/>
                    </td>
                    <td className="py-2 px-3">
                      <span className={`inline-flex items-center gap-1.5 font-semibold text-${accent}-900`}>
                        <span className={`w-1.5 h-1.5 rounded-full bg-${accent}-500`}/>
                        {r.name}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right">{r.agents}</td>
                    <td className="py-2 px-3 text-right">{r.policies}</td>
                    <td className="py-2 px-3 text-right text-slate-500">{r.count}</td>
                    <td className="py-2 px-3 text-right">{fmt(r.premium)}</td>
                    <td className="py-2 px-3 text-right">{fmt(r.gross)}</td>
                    <td className="py-2 px-3 text-right text-red-600">{fmt(r.cb)}</td>
                    <td className="py-2 px-3 text-right font-semibold">{fmt(r.net)}</td>
                    <td className={`py-2 px-3 text-right font-medium text-${pColor}-600`}>{r.gross > 0 ? `${r.persistency.toFixed(0)}%` : '—'}</td>
                    <td className="py-2 px-3 text-right text-slate-500">{r.gross > 0 ? `${r.cbRate.toFixed(1)}%` : '—'}</td>
                  </tr>
                  {isExpanded && (
                    <tr className={`bg-${accent}-50/40`}>
                      <td colSpan={11} className="p-0">
                        <div className="px-3 py-2 border-y border-slate-200">
                          <div className={`text-xs font-semibold uppercase tracking-wide text-${accent}-900 mb-2`}>{r.name} policies ({policies.length})</div>
                          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                            <table className="w-full text-xs">
                              <thead className="bg-slate-50 border-b border-slate-200">
                                <tr className="text-slate-500 text-left">
                                  <th onClick={()=>togglePolicySort('status')} className="py-1.5 px-2 cursor-pointer hover:text-slate-900 select-none">Status{sortArrow('status')}</th>
                                  <th onClick={()=>togglePolicySort('insured')} className="py-1.5 px-2 cursor-pointer hover:text-slate-900 select-none">Insured{sortArrow('insured')}</th>
                                  <th onClick={()=>togglePolicySort('policy')} className="py-1.5 px-2 cursor-pointer hover:text-slate-900 select-none">Policy #{sortArrow('policy')}</th>
                                  <th onClick={()=>togglePolicySort('agentName')} className="py-1.5 px-2 cursor-pointer hover:text-slate-900 select-none">Agent{sortArrow('agentName')}</th>
                                  <th onClick={()=>togglePolicySort('issueDate')} className="py-1.5 px-2 cursor-pointer hover:text-slate-900 select-none">Issued{sortArrow('issueDate')}</th>
                                  <th onClick={()=>togglePolicySort('lastActivity')} className="py-1.5 px-2 cursor-pointer hover:text-slate-900 select-none">Last activity{sortArrow('lastActivity')}</th>
                                  <th onClick={()=>togglePolicySort('premium')} className="py-1.5 px-2 text-right cursor-pointer hover:text-slate-900 select-none">Premium{sortArrow('premium')}</th>
                                  <th onClick={()=>togglePolicySort('net')} className="py-1.5 px-2 text-right cursor-pointer hover:text-slate-900 select-none">Net comm{sortArrow('net')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {policies.length === 0 && (
                                  <tr><td colSpan={8} className="py-3 text-center text-slate-400">No policies</td></tr>
                                )}
                                {policies.map(p => (
                                  <tr key={p.key} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                                    <td className="py-1.5 px-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${p.status==='active'?'bg-emerald-100 text-emerald-700':'bg-red-100 text-red-700'}`}>{p.status==='active'?'ACTIVE':'LAPSED'}</span></td>
                                    <td className="py-1.5 px-2">
                                      <button onClick={(e)=>{e.stopPropagation(); openClientProfile(clientKeyOf(p.insured));}} className="text-emerald-700 hover:text-emerald-900 hover:underline font-medium text-left">
                                        {titleCase(p.insured) || '—'}
                                      </button>
                                    </td>
                                    <td className="py-1.5 px-2 font-mono text-[11px]">{p.policy}</td>
                                    <td className="py-1.5 px-2">
                                      <div>{p.agentName}</div>
                                      <div className="text-[10px] text-slate-400 font-mono">{p.prodNum}</div>
                                    </td>
                                    <td className="py-1.5 px-2">{p.issueDate || '—'}</td>
                                    <td className="py-1.5 px-2">{p.lastActivity || '—'}</td>
                                    <td className="py-1.5 px-2 text-right">{p.premium > 0 ? fmt(p.premium) : '—'}</td>
                                    <td className={`py-1.5 px-2 text-right font-semibold ${p.net >= 0 ? '' : 'text-red-600'}`}>{fmt(p.net)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AgentStatsTable({ rows, settings }) {
  const [sortBy, setSortBy] = useState('written'); // written | netPremium | chargedBack | persistency | name
  const [teamFilter, setTeamFilter] = useState('all'); // all | mine | cam | unassigned
  const [showAll, setShowAll] = useState(false);

  if (!rows || rows.length === 0) return null;

  const filtered = rows.filter(r => teamFilter === 'all' || r.team === teamFilter);
  const sorted = filtered.slice().sort((a, b) => {
    if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
    if (sortBy === 'netPremium') return ((b.premiumPositive||0) - (b.premiumNegative||0)) - ((a.premiumPositive||0) - (a.premiumNegative||0));
    if (sortBy === 'chargedBack') return (b.premiumNegative||0) - (a.premiumNegative||0); // largest CB first
    if (sortBy === 'persistency') return a.persistency - b.persistency; // worst first
    return (b.premiumPositive||0) - (a.premiumPositive||0);
  });
  const visible = showAll ? sorted : sorted.slice(0, 15);

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex flex-wrap items-center gap-2">
        <div className="flex-1">
          <div className="font-bold">Stats by agent</div>
          <div className="text-xs text-slate-500 mt-0.5">Current period · {filtered.length} agent{filtered.length === 1 ? '' : 's'} · premium dollars</div>
        </div>
        <select value={teamFilter} onChange={e=>setTeamFilter(e.target.value)} className="px-2 py-1 border border-slate-200 rounded text-xs bg-white">
          <option value="all">All teams</option>
          <option value="mine">{possessive(settings.ownerName)} team</option>
          <option value="unassigned">Unassigned</option>
        </select>
        <select value={sortBy} onChange={e=>setSortBy(e.target.value)} className="px-2 py-1 border border-slate-200 rounded text-xs bg-white">
          <option value="written">Sort: premium written</option>
          <option value="netPremium">Sort: net premium</option>
          <option value="chargedBack">Sort: charged back (largest)</option>
          <option value="persistency">Sort: persistency (worst first)</option>
          <option value="name">Sort: name (A→Z)</option>
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white border-b border-slate-200">
            <tr className="text-xs text-slate-500 text-left">
              <th className="py-2 px-3">Agent</th>
              <th className="py-2 px-3">Team</th>
              <th className="py-2 px-3 text-right">Rows</th>
              <th className="py-2 px-3 text-right">Premium written</th>
              <th className="py-2 px-3 text-right">Charged back</th>
              <th className="py-2 px-3 text-right">Net premium</th>
              <th className="py-2 px-3 text-right">Persistency</th>
              <th className="py-2 px-3 text-right">CB rate</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(p => {
              const teamColor = p.team === 'mine' ? 'emerald' : 'slate';
              const teamLabel = p.team === 'mine' ? settings.ownerName : 'Unassigned';
              const pColor = p.persistency >= 85 ? 'emerald' : p.persistency >= 70 ? 'amber' : 'red';
              return (
                <tr key={p.prodNum} className="border-b border-slate-50 last:border-0 hover:bg-slate-50">
                  <td className="py-2 px-3">
                    <div className="font-medium">{p.name || `#${p.prodNum}`} {p.isProblem && <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded ml-1">⚠</span>}</div>
                    <div className="text-xs font-mono text-slate-400">{p.prodNum}</div>
                  </td>
                  <td className="py-2 px-3">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded bg-${teamColor}-100 text-${teamColor}-700`}>{teamLabel}</span>
                  </td>
                  <td className="py-2 px-3 text-right text-slate-500">{p.count}</td>
                  <td className="py-2 px-3 text-right">{fmt(p.premiumPositive || 0)}</td>
                  <td className="py-2 px-3 text-right text-red-600">{fmt(-(p.premiumNegative || 0))}</td>
                  <td className={`py-2 px-3 text-right font-semibold ${((p.premiumPositive||0)-(p.premiumNegative||0)) < 0 ? 'text-red-600' : ''}`}>{fmt((p.premiumPositive||0) - (p.premiumNegative||0))}</td>
                  <td className={`py-2 px-3 text-right font-medium text-${pColor}-600`}>{p.gross > 0 ? `${p.persistency.toFixed(0)}%` : '—'}</td>
                  <td className="py-2 px-3 text-right text-slate-500">{p.gross > 0 ? `${p.chargebackRate.toFixed(1)}%` : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {sorted.length > 15 && (
        <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 text-center">
          <button onClick={()=>setShowAll(s => !s)} className="text-sm text-emerald-700 hover:text-emerald-900 font-medium">
            {showAll ? `Show top 15 only` : `Show all ${sorted.length} agents`}
          </button>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, sub, color }) {
  const colors = {
    emerald: 'bg-emerald-50 text-emerald-600',
    red: 'bg-red-50 text-red-600',
    slate: 'bg-slate-100 text-slate-700',
    violet: 'bg-violet-50 text-violet-600',
  };
  return (
    <div className="bg-white rounded-xl p-4 border border-slate-200">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colors[color]}`}>{icon}</div>
      <div className="text-xs text-slate-500 uppercase tracking-wide mt-3">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

function TeamCard({ label, data, color }) {
  const colors = {
    emerald: 'border-emerald-200 bg-emerald-50',
    violet: 'border-violet-200 bg-violet-50',
    amber: 'border-amber-200 bg-amber-50',
    blue: 'border-blue-200 bg-blue-50',
  };
  const written     = data.written     || 0;
  const chargedBack = data.chargedBack || 0;
  const net         = data.net         || 0;
  const pctCB = written === 0 ? 0 : Math.abs(chargedBack / written) * 100;
  return (
    <div className={`rounded-xl border-2 p-4 ${colors[color]}`}>
      <div className="font-semibold text-slate-700">{label}</div>
      <div className="text-xs text-slate-500 -mt-0.5">Net premium written</div>
      <div className="text-3xl font-bold mt-1">{fmt(net)}</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3 text-xs">
        <div><div className="text-slate-500">Written</div><div className="font-semibold">{fmt(written)}</div></div>
        <div><div className="text-slate-500">Charged back</div><div className="font-semibold text-red-600">{fmt(chargedBack)}</div></div>
        <div><div className="text-slate-500">CB %</div><div className="font-semibold">{pctCB.toFixed(1)}%</div></div>
      </div>
    </div>
  );
}

function Card({ title, children, fullscreenable=false, subtitle, defaultHeight=280 }) {
  const [isFull, setIsFull] = useState(false);
  const [fullHeight, setFullHeight] = useState(600);

  useEffect(() => {
    if (!isFull) return;
    const onKey = (e) => { if (e.key === 'Escape') setIsFull(false); };
    const onResize = () => setFullHeight(Math.max(400, window.innerHeight - 220));
    onResize();
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      document.body.style.overflow = prevOverflow;
    };
  }, [isFull]);

  const renderBody = (height) => (typeof children === 'function' ? children(height) : children);

  return (
    <>
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-start justify-between mb-3 gap-3">
          <div>
            <div className="font-semibold text-slate-700">{title}</div>
            {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
          </div>
          {fullscreenable && (
            <button onClick={()=>setIsFull(true)} title="Present fullscreen" className="text-slate-400 hover:text-slate-700 shrink-0">
              <Maximize2 size={16}/>
            </button>
          )}
        </div>
        {renderBody(defaultHeight)}
      </div>
      {isFull && (
        <div className="fixed inset-0 z-50 bg-slate-900 flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
            <div>
              <div className="text-white font-semibold text-2xl">{title}</div>
              {subtitle && <div className="text-sm text-slate-400 mt-1">{subtitle}</div>}
            </div>
            <button onClick={()=>setIsFull(false)} title="Exit (Esc)" className="text-slate-300 hover:text-white flex items-center gap-2 text-sm bg-slate-800 hover:bg-slate-700 px-3 py-2 rounded-lg">
              <X size={18}/> Exit
            </button>
          </div>
          <div className="flex-1 bg-white m-6 rounded-2xl p-8 overflow-auto">
            {renderBody(fullHeight)}
          </div>
          <div className="text-slate-500 text-xs text-center pb-3">Press Esc to exit</div>
        </div>
      )}
    </>
  );
}

function AgentsView({ producerList, producers, settings, allTransactions, openClientProfile = () => {} }) {
  const [carrierFilter, setCarrierFilter] = useState('all');

  // Build per-agent policy maps once. Maps prodNum -> array of policies (each with carrier).
  const policiesByAgent = React.useMemo(() => {
    const byAgent = {};
    const polMap = {};
    (allTransactions || []).forEach(tx => {
      if (!tx.policy || !tx.prodNum) return;
      const key = `${tx.prodNum}|${tx.mga||''}|${tx.policy}`;
      if (!polMap[key]) {
        polMap[key] = {
          key, prodNum: tx.prodNum, policy: tx.policy, insured: tx.insured || '',
          state: tx.state || '', issueDate: tx.issueDate || '',
          carrier: deriveCarrier(tx),
          premium: 0, totalCommission: 0, chargebackCount: 0,
          lastActivity: tx.activityDate || '',
        };
      }
      const p = polMap[key];
      p.totalCommission += (tx.netToAccount || 0);
      if (tx.commPremium && Math.abs(tx.commPremium) > p.premium) p.premium = Math.abs(tx.commPremium);
      if (!p.insured && tx.insured) p.insured = tx.insured;
      if (!p.issueDate && tx.issueDate) p.issueDate = tx.issueDate;
      if (CHARGEBACK_TYPES.has(tx.activityType) || (tx.netToAccount || 0) < 0) p.chargebackCount++;
      const d = parseDate(tx.activityDate);
      const last = parseDate(p.lastActivity);
      if (d && (!last || d > last)) p.lastActivity = tx.activityDate;
    });
    const today = new Date();
    Object.values(polMap).forEach(p => {
      p.status = (p.chargebackCount > 0 || p.totalCommission < 0) ? 'lapsed' : 'active';
      const issue = parseDate(p.issueDate);
      p.daysInForce = issue ? Math.floor((today - issue) / (1000*60*60*24)) : null;
      if (!byAgent[p.prodNum]) byAgent[p.prodNum] = [];
      byAgent[p.prodNum].push(p);
    });
    return byAgent;
  }, [allTransactions]);
  const [expanded, setExpanded] = useState({}); // prodNum -> bool
  const toggleExpand = (pn) => setExpanded(e => ({ ...e, [pn]: !e[pn] }));

  // All carriers anyone on the agency writes with — for the filter chips
  const availableCarriers = sortByCarrierOrder(
    Array.from(new Set(producerList.flatMap(p => Object.keys(p.carriers || {})).filter(Boolean))),
    x => x
  );

  // Carrier counts per filter chip (agents with material activity at that carrier)
  const carrierAgentCount = (name) => producerList.filter(p => {
    const c = p.carriers?.[name];
    return c && (c.gross > 0 || c.cb < 0);
  }).length;

  const matchesCarrier = (p) => {
    if (carrierFilter === 'all') return true;
    const c = p.carriers?.[carrierFilter];
    return !!c && (c.gross > 0 || c.cb < 0);
  };

  const ownerList = producerList.filter(p => p.team === 'owner' && matchesCarrier(p));
  const mineList = producerList.filter(p => p.team === 'mine' && matchesCarrier(p));
  const camList = producerList.filter(p => p.team === 'cam' && matchesCarrier(p));
  const unassigned = producerList.filter(p => p.team === 'unassigned' && matchesCarrier(p));

  const yourTotalPremium = mineList.reduce((s, p) => s + (p.premiumPositive || 0), 0);
  const yourTotalCamCut = mineList.reduce((s, p) => s + p.camCut, 0);
  const camTotalNet = camList.reduce((s, p) => s + p.net, 0);
  const ownerTotalNet = ownerList.reduce((s, p) => s + p.net, 0);

  const problemProducers = producerList.filter(p => p.isProblem);

  // Carrier persistency comparison — aggregate gross/cb per carrier across all producers
  const carrierAgg = {};
  producerList.forEach(p => {
    Object.entries(p.carriers || {}).forEach(([name, c]) => {
      if (!carrierAgg[name]) carrierAgg[name] = { name, gross: 0, cb: 0, agents: 0 };
      carrierAgg[name].gross += c.gross;
      carrierAgg[name].cb += c.cb;
      if (c.gross > 0 || c.cb < 0) carrierAgg[name].agents++;
    });
  });
  const carrierPersistencyData = sortByCarrierOrder(
    Object.values(carrierAgg)
      .filter(c => c.gross > 0)
      .map(c => {
        const persistency = Math.max(0, Math.min(100, ((c.gross + c.cb) / c.gross) * 100));
        const cbRate = (Math.abs(c.cb) / c.gross) * 100;
        return {
          name: c.name,
          persistency: Math.round(persistency),
          cbRate: Math.round(cbRate * 10) / 10,
          fill: persistency >= 85 ? '#10b981' : persistency >= 70 ? '#f59e0b' : '#ef4444',
          agents: c.agents,
        };
      }),
    x => x.name
  );

  return (
    <div className="space-y-4">
      {/* Headline summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
          <div className="text-xs text-blue-700 font-semibold uppercase tracking-wide">Owner production</div>
          <div className="text-3xl font-bold mt-1 text-blue-900">{fmt(ownerTotalNet)}</div>
          <div className="text-xs text-blue-700 mt-1">Stands alone — no override</div>
        </div>
        <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4">
          <div className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">Premium written by your team</div>
          <div className="text-3xl font-bold mt-1 text-emerald-900">{fmt(yourTotalPremium)}</div>
          <div className="text-xs text-emerald-700 mt-1">Excludes owner · override $ lives on the My Overrides tab</div>
        </div>
      </div>

      {/* Problem producers callout */}
      {problemProducers.length > 0 && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-red-700 text-lg">⚠</span>
            <div className="font-bold text-red-900">
              {problemProducers.length} producer{problemProducers.length>1?'s':''} flagged for persistency issues
            </div>
          </div>
          <div className="text-xs text-red-700 mb-2">Chargeback rate above 30% or net-negative with material activity. Click to expand below.</div>
          <div className="flex flex-wrap gap-2">
            {problemProducers.map(p => (
              <button key={p.prodNum} onClick={()=>setExpanded(e=>({...e,[p.prodNum]:true}))} className="bg-white border border-red-300 hover:bg-red-100 rounded-lg px-3 py-1.5 text-sm text-red-900">
                <span className="font-semibold">{getProducerName(p.prodNum, producers)}</span>
                <span className="text-red-600 ml-1.5">{p.persistency.toFixed(0)}%</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Carrier filter chips */}
      {availableCarriers.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide mr-1">Filter by carrier:</span>
          <button
            onClick={()=>setCarrierFilter('all')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${carrierFilter === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}
          >
            All <span className="text-xs opacity-70 ml-1">({producerList.length})</span>
          </button>
          {availableCarriers.map(c => {
            const active = carrierFilter === c;
            const accent = c === 'Americo' ? 'emerald' : c === 'MOO' ? 'violet' : c === 'Transamerica' ? 'amber' : 'slate';
            const cls = active
              ? `bg-${accent}-600 text-white border-${accent}-600`
              : `bg-white text-${accent}-700 border-${accent}-200 hover:bg-${accent}-50`;
            return (
              <button key={c} onClick={()=>setCarrierFilter(c)} className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${cls}`}>
                {c} <span className="text-xs opacity-70 ml-1">({carrierAgentCount(c)})</span>
              </button>
            );
          })}
          {carrierFilter !== 'all' && (
            <span className="ml-auto text-xs text-slate-500">
              Showing {mineList.length + unassigned.length} agent{(mineList.length + unassigned.length) === 1 ? '' : 's'} who write with {carrierFilter}
            </span>
          )}
        </div>
      )}

      {/* Carrier persistency comparison */}
      {carrierPersistencyData.length > 0 && (
        <Card title="Persistency by carrier" subtitle="Retention % across MOO / Americo / Transamerica — green ≥85%, amber 70-85%, red <70%" fullscreenable>
          {(h) => (
            <ResponsiveContainer width="100%" height={h}>
              <BarChart data={carrierPersistencyData} margin={{bottom: 10}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                <XAxis dataKey="name" tick={{fontSize:12}}/>
                <YAxis domain={[0,100]} tick={{fontSize:12}} tickFormatter={(v)=>`${v}%`}/>
                <Tooltip formatter={(v, name) => name === 'CB rate %' ? `${v}%` : `${v}%`} labelFormatter={(label, items) => {
                  const d = items?.[0]?.payload;
                  return d ? `${label} (${d.agents} agent${d.agents===1?'':'s'})` : label;
                }}/>
                <Legend/>
                <Bar dataKey="persistency" name="Persistency %">
                  {carrierPersistencyData.map((entry, i) => <Cell key={i} fill={entry.fill}/>)}
                </Bar>
                <Bar dataKey="cbRate" name="CB rate %" fill="#94a3b8"/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      )}

      {/* Owner production (stands alone) */}
      {ownerList.length > 0 && (
        <div className="bg-white rounded-xl border-2 border-blue-200 overflow-hidden">
          <div className="p-4 border-b border-blue-200 bg-blue-50">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
              <h3 className="font-bold text-blue-900">Owner production</h3>
              <span className="text-xs text-blue-700 ml-auto">stands alone — no team override</span>
            </div>
            <p className="text-xs text-blue-700 mt-1">Personal production — no team override applied</p>
          </div>
          <div className="divide-y divide-slate-100">
            {ownerList.map(p => (
              <AgentRow key={p.prodNum} p={p} producers={producers} settings={settings} expanded={expanded[p.prodNum]} onToggle={()=>toggleExpand(p.prodNum)} isMineTeam={false} policies={policiesByAgent[p.prodNum] || []} openClientProfile={openClientProfile}/>
            ))}
          </div>
        </div>
      )}

      {/* Your team */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-emerald-50">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
            <h3 className="font-bold text-emerald-900">Your team ({mineList.length} agents)</h3>
          </div>
          <p className="text-xs text-emerald-700 mt-1">Non-owner agents on your team. Click an agent to see per-carrier breakdown.</p>
        </div>
        <div className="divide-y divide-slate-100">
          {mineList.length === 0 && (
            <div className="py-6 text-center text-slate-400">No data for your team in this period</div>
          )}
          {mineList.map(p => (
            <AgentRow key={p.prodNum} p={p} producers={producers} settings={settings} expanded={expanded[p.prodNum]} onToggle={()=>toggleExpand(p.prodNum)} isMineTeam={true} policies={policiesByAgent[p.prodNum] || []} openClientProfile={openClientProfile}/>
          ))}
        </div>
      </div>

      {unassigned.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="font-semibold text-amber-900 mb-1">{unassigned.length} unassigned producer(s)</div>
          <div className="text-sm text-amber-700">Go to the Producers tab to assign them to a team.</div>
        </div>
      )}
    </div>
  );
}

// My Overrides — what the owner earned as overrides on their team's
// production. Split out of the Agents tab so the agent stats focus on
// what each agent wrote, not what they earned the owner.
// Reusable inline-editable producer-name display. Shows the current
// name (or a "Producer 1178144" fallback if none set), with a hover
// hint. Click → input → Enter or blur to save. Escape cancels.
// Saving routes through setProducerName so EVERY view that reads
// getProducerName(prodNum, producers) picks up the new value
// immediately — no re-uploading, no Producers tab detour.
function EditableProducerName({ prodNum, producers, setProducerName = () => {}, className = '', placeholder = 'Type agent name' }) {
  const stored = producers?.[prodNum]?.name || '';
  const display = stored || `Producer ${prodNum}`;
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(stored);
  React.useEffect(() => { setDraft(stored); }, [stored]);
  const commit = () => {
    const v = (draft || '').trim();
    if (v && v !== stored) setProducerName(prodNum, v);
    setEditing(false);
  };
  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { setDraft(stored); setEditing(false); }
        }}
        onClick={e => e.stopPropagation()}
        placeholder={placeholder}
        className="border border-emerald-300 focus:outline-none focus:border-emerald-500 rounded px-1.5 py-0.5 text-sm font-medium"
        style={{ minWidth: 140 }}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={e => { e.stopPropagation(); setEditing(true); }}
      title={stored ? 'Click to rename this agent' : 'Click to name this agent'}
      className={`${className} ${!stored ? 'text-amber-700 hover:text-amber-800 italic' : 'hover:underline'} hover:bg-amber-50 rounded px-1 -mx-1 transition-colors`}
    >
      {display}
    </button>
  );
}

function OverridesView({ producerList, producers, setProducerName = () => {}, settings, transactions = [], getCarrier = () => 'Unknown', openClientProfile = () => {}, onGotoProducers = () => {} }) {
  const [sortBy, setSortBy] = useState('earnings'); // earnings | premium | net | name
  const [showAll, setShowAll] = useState(false);
  const [expandedAgent, setExpandedAgent] = useState({}); // prodNum -> bool
  // Per-policy table sort — applies to every expanded agent's policy list.
  // Click a column header to set / flip direction. Defaults: override desc.
  const [policySortBy, setPolicySortBy] = useState('override');
  const [policySortDir, setPolicySortDir] = useState('desc');
  const togglePolicySort = (col) => {
    if (policySortBy === col) {
      setPolicySortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setPolicySortBy(col);
      // Strings default to A→Z; numbers/dates default to largest first.
      setPolicySortDir(col === 'insured' || col === 'policy' || col === 'carrier' ? 'asc' : 'desc');
    }
  };
  const sortPolicies = (list) => {
    const dir = policySortDir === 'asc' ? 1 : -1;
    const cmpStr = (a, b) => (a || '').localeCompare(b || '');
    const cmpNum = (a, b) => (a || 0) - (b || 0);
    const cmpDate = (a, b) => (parseDate(a)?.getTime() || 0) - (parseDate(b)?.getTime() || 0);
    return list.slice().sort((a, b) => {
      switch (policySortBy) {
        case 'insured':  return dir * cmpStr(a.insured, b.insured);
        case 'policy':   return dir * cmpStr(a.policy, b.policy);
        case 'carrier':  return dir * cmpStr(a.carrier, b.carrier);
        case 'issued':   return dir * cmpDate(a.issueDate, b.issueDate);
        case 'premium':  return dir * cmpNum(a.premium, b.premium);
        case 'net':      return dir * cmpNum(a.net, b.net);
        case 'camCut':   return dir * cmpNum(a.camCut, b.camCut);
        case 'override':
        default:         return dir * cmpNum(a.override, b.override);
      }
    });
  };
  const sortArrow = (col) => policySortBy === col ? (policySortDir === 'asc' ? ' ▲' : ' ▼') : '';
  // Override math: team writes premium → carrier pays the agency
  // → the producing agents are paid a configurable cut of premium →
  // whatever's left is the owner's override. agentSplitPct is the
  // % of premium that flows TO the agents. Configurable in Settings;
  // defaults to 0 (everything is override) until the owner sets it.
  const agentSplitPct = settings.camPercent || 0;

  const mine = (producerList || []).filter(p => p.team === 'mine');
  const mineProdNums = new Set(mine.map(p => p.prodNum));

  // Per-agent → per-policy override breakdown. We re-aggregate the same
  // period-filtered transactions used by producerList so the per-policy
  // override numbers below sum to each agent's headline override total.
  const policiesByAgent = React.useMemo(() => {
    const byAgent = {};
    const polMap = {};
    (transactions || []).forEach(tx => {
      if (!tx.policy || !tx.prodNum) return;
      if (!mineProdNums.has(tx.prodNum)) return;
      const k = `${tx.prodNum}|${tx.mga||''}|${tx.policy}`;
      if (!polMap[k]) {
        polMap[k] = {
          key: k,
          prodNum: tx.prodNum,
          policy: tx.policy,
          insured: tx.insured || '',
          state: tx.state || '',
          issueDate: tx.issueDate || '',
          carrier: getCarrier(tx),
          net: 0,
          premiumPositive: 0,
          premiumNegative: 0,
          rowCount: 0,
          lastActivity: tx.activityDate || '',
        };
      }
      const p = polMap[k];
      const prem = Math.abs(Number(tx.commPremium) || 0);
      const amt  = Number(tx.netToAccount) || 0;
      p.net += amt;
      if (amt >= 0) p.premiumPositive += prem; else p.premiumNegative += prem;
      p.rowCount++;
      if (!p.insured && tx.insured) p.insured = tx.insured;
      if (!p.issueDate && tx.issueDate) p.issueDate = tx.issueDate;
      const d = parseDate(tx.activityDate);
      const last = parseDate(p.lastActivity);
      if (d && (!last || d > last)) p.lastActivity = tx.activityDate;
    });
    Object.values(polMap).forEach(p => {
      const camCut = ((p.premiumPositive - p.premiumNegative) * agentSplitPct) / 100;
      p.camCut = camCut;
      p.override = p.net - camCut;
      p.premium = p.premiumPositive; // display label
      if (!byAgent[p.prodNum]) byAgent[p.prodNum] = [];
      byAgent[p.prodNum].push(p);
    });
    // Sort each agent's policies by override $ desc.
    Object.values(byAgent).forEach(list => list.sort((a, b) => b.override - a.override));
    return byAgent;
  }, [transactions, mineProdNums, agentSplitPct, getCarrier]);

  const totalOverride = mine.reduce((s, p) => s + (p.yourEarnings || 0), 0);
  const totalPremium  = mine.reduce((s, p) => s + (p.premiumPositive || 0), 0);
  const totalCamCut   = mine.reduce((s, p) => s + (p.camCut || 0), 0);
  const totalNet      = mine.reduce((s, p) => s + (p.net || 0), 0);
  const earningAgents = mine.filter(p => (p.yourEarnings || 0) > 0).length;

  // Per-agent split: positive overrides (new business) vs negative
  // overrides (chargebacks). Same identity as p.yourEarnings, just sliced
  // so the user can see what was clawed back this period.
  //   positive override = gross − premiumPositive × partner%
  //   override clawed back = cb + premiumNegative × partner%   (≤ 0)
  // Sum of the two = yourEarnings.
  const pctMul = (agentSplitPct || 0) / 100;
  const overrideBreakdown = (p) => {
    const positive = (p.gross || 0) - (p.premiumPositive || 0) * pctMul;
    const negative = (p.cb    || 0) + (p.premiumNegative || 0) * pctMul;
    return { positive, negative };
  };
  const totalPremiumCB    = mine.reduce((s, p) => s + (p.premiumNegative || 0), 0);
  const totalOverrideCB   = mine.reduce((s, p) => s + overrideBreakdown(p).negative, 0);
  const totalOverrideEarned = mine.reduce((s, p) => s + overrideBreakdown(p).positive, 0);
  const cbAgents          = mine.filter(p => (p.premiumNegative || 0) > 0).length;

  // Carrier roll-up — per-carrier override / premium / net.
  const carrierAgg = {};
  mine.forEach(p => {
    Object.entries(p.carriers || {}).forEach(([name, c]) => {
      if (!carrierAgg[name]) {
        carrierAgg[name] = { name, premium: 0, premiumCB: 0, net: 0, camCut: 0, override: 0, overrideCB: 0, policies: 0, agents: new Set() };
      }
      const k = carrierAgg[name];
      k.premium    += c.premiumPositive || 0;
      k.premiumCB  += c.premiumNegative || 0;
      k.net        += c.net || 0;
      k.camCut     += c.camCut || 0;
      k.override   += c.yourEarnings || 0;
      // Same identity used at the agent level — see overrideBreakdown.
      k.overrideCB += ((c.cb || 0) + (c.premiumNegative || 0) * pctMul);
      k.policies   += c.count || 0;
      k.agents.add(p.prodNum);
    });
  });
  const carrierRows = sortByCarrierOrder(
    Object.values(carrierAgg).filter(c => c.premium > 0 || c.override !== 0),
    x => x.name
  );

  // Per-agent sort + slicing
  const sorted = mine.slice().sort((a, b) => {
    if (sortBy === 'premium')    return (b.premiumPositive || 0) - (a.premiumPositive || 0);
    if (sortBy === 'net')        return (b.net || 0) - (a.net || 0);
    if (sortBy === 'name')       return (a.name || '').localeCompare(b.name || '');
    if (sortBy === 'premiumCB')  return (b.premiumNegative || 0) - (a.premiumNegative || 0);
    if (sortBy === 'overrideCB') return overrideBreakdown(a).negative - overrideBreakdown(b).negative; // most negative first
    return (b.yourEarnings || 0) - (a.yourEarnings || 0);
  });
  const visible = showAll ? sorted : sorted.slice(0, 15);

  // For the inline share bar in the agent table.
  const maxOverride = Math.max(0, ...sorted.map(p => p.yourEarnings || 0));
  const topPctOf = (v) => maxOverride > 0 ? Math.max(0, Math.min(100, ((v || 0) / maxOverride) * 100)) : 0;
  const shareOfTotal = (v) => totalOverride > 0 ? ((v || 0) / totalOverride) * 100 : 0;

  // Top 3 contributors — surfaced above the table as a quick callout.
  const topThree = sorted.filter(p => (p.yourEarnings || 0) > 0).slice(0, 3);

  // Diagnostic — when there are no agents on the 'mine' team, the whole
  // page renders as $0 and looks broken. Count the alternative buckets so
  // we can tell the user what's actually going on (most often: producers
  // were uploaded but never assigned to a team).
  const teamCounts = (producerList || []).reduce((acc, p) => {
    acc[p.team || 'unassigned'] = (acc[p.team || 'unassigned'] || 0) + 1;
    return acc;
  }, {});
  const totalProducers = (producerList || []).length;
  const showAssignBanner = mine.length === 0 && totalProducers > 0;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">My Overrides</h2>
        <p className="text-sm text-slate-500 mt-1">
          What you earned in overrides on your team's production this period.
          {agentSplitPct > 0 && ` Agents take ${agentSplitPct}% of premium; the rest is yours.`}
        </p>
      </div>

      {/* Empty-state diagnostic — show before the hero so the $0 figures
          below don't look like a bug. Surfaces the actual bucketing of
          producers (unassigned vs partner-team vs owner) so the user
          knows exactly what's missing. */}
      {showAssignBanner && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-5 flex items-start gap-4 flex-wrap">
          <div className="text-2xl">⚠</div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wider font-bold text-amber-900">No agents on your team yet</div>
            <h3 className="text-lg font-bold mt-1 text-slate-900">Override math runs on the agents you've put on YOUR team.</h3>
            <p className="text-sm text-slate-700 mt-1">
              You have {totalProducers} producer{totalProducers === 1 ? '' : 's'} with production this period:
              {' '}
              {teamCounts.unassigned ? <><strong>{teamCounts.unassigned} unassigned</strong>{(teamCounts.cam || teamCounts.owner) ? ', ' : ''}</> : null}
              {teamCounts.cam ? <><strong>{teamCounts.cam} on another team</strong>{teamCounts.owner ? ', ' : ''}</> : null}
              {teamCounts.owner ? <><strong>{teamCounts.owner} owner production</strong></> : null}
              {' '}— and <strong>0 on your team</strong>. Until you assign at least one producer to your team, this page stays at $0.
            </p>
            <button
              onClick={onGotoProducers}
              className="mt-3 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-bold inline-flex items-center gap-1.5"
            >
              Go to Producers tab to assign agents →
            </button>
          </div>
        </div>
      )}

      {/* Hero — the headline number with the math broken out */}
      <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-2 border-emerald-300 rounded-2xl p-5">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs text-emerald-800 font-semibold uppercase tracking-wider">Your override earnings · this period</div>
            <div className="text-5xl font-bold text-emerald-900 mt-1 leading-none">{fmt(totalOverride)}</div>
            <div className="text-xs text-emerald-800 mt-2">
              From {mine.length} team agent{mine.length===1?'':'s'} · {earningAgents} earning
            </div>
          </div>
          <div className="text-xs text-emerald-900 bg-white/60 border border-emerald-200 rounded-lg p-3">
            <div className="font-semibold uppercase tracking-wider text-emerald-800 mb-1">How it's calculated</div>
            <div className="font-mono">Team net commission − agent payouts ({agentSplitPct}% of premium) = your override</div>
            <div className="font-mono mt-1">{fmt(totalNet)} − {fmt(totalCamCut)} = <span className="font-bold">{fmt(totalOverride)}</span></div>
          </div>
        </div>
      </div>

      {/* Supporting context — three tiles */}
      <div>
        <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-2">Team production at a glance</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Premium written</div>
            <div className="text-2xl font-bold mt-1 text-slate-900">{fmt(totalPremium)}</div>
            <div className="text-xs text-slate-500 mt-1">What the team sold</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Net commission</div>
            <div className={`text-2xl font-bold mt-1 ${totalNet < 0 ? 'text-red-600' : 'text-slate-900'}`}>{fmt(totalNet)}</div>
            <div className="text-xs text-slate-500 mt-1">Total $ paid on team policies</div>
          </div>
          <div className="bg-white border border-violet-200 rounded-xl p-4">
            <div className="text-xs text-violet-700 font-semibold uppercase tracking-wide">Agent payouts</div>
            <div className="text-2xl font-bold mt-1 text-violet-900">{fmt(totalCamCut)}</div>
            <div className="text-xs text-violet-700 mt-1">{agentSplitPct}% × premium</div>
          </div>
        </div>
      </div>

      {/* Charged back this period — surfaces what's been clawed away
          from the headline override number above. Splits the impact
          into premium dollars (the policy face) and override dollars
          (the owner's actual paycheck recoupment). */}
      {(totalPremiumCB > 0 || totalOverrideCB < 0) && (
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-2">Charged back this period</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
              <div className="text-xs text-red-700 font-semibold uppercase tracking-wide">Premium charged back</div>
              <div className="text-2xl font-bold mt-1 text-red-900">{fmt(totalPremiumCB)}</div>
              <div className="text-xs text-red-700 mt-1">{cbAgents} agent{cbAgents===1?'':'s'} had chargebacks · {totalPremium > 0 ? ((totalPremiumCB / totalPremium) * 100).toFixed(1) : '0'}% of premium written</div>
            </div>
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
              <div className="text-xs text-red-700 font-semibold uppercase tracking-wide">My override clawed back</div>
              <div className="text-2xl font-bold mt-1 text-red-900">{fmt(totalOverrideCB)}</div>
              <div className="text-xs text-red-700 mt-1">The portion of override $ I lost back to chargebacks</div>
            </div>
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4">
              <div className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">Override earned before chargebacks</div>
              <div className="text-2xl font-bold mt-1 text-emerald-900">{fmt(totalOverrideEarned)}</div>
              <div className="text-xs text-emerald-700 mt-1">What this period would've been without the clawback ({fmt(totalOverrideEarned)} − {fmt(Math.abs(totalOverrideCB))} = {fmt(totalOverride)})</div>
            </div>
          </div>
        </div>
      )}

      {/* By carrier */}
      {carrierRows.length > 0 && (
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-2">By carrier</div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-xs text-slate-500 text-left">
                    <th className="py-2 px-3">Carrier</th>
                    <th className="py-2 px-3 text-right">Agents</th>
                    <th className="py-2 px-3 text-right">Rows</th>
                    <th className="py-2 px-3 text-right">Premium</th>
                    <th className="py-2 px-3 text-right text-red-600">Premium CB</th>
                    <th className="py-2 px-3 text-right">Net</th>
                    <th className="py-2 px-3 text-right">Agent cut</th>
                    <th className="py-2 px-3 text-right text-red-600">Override CB</th>
                    <th className="py-2 px-3 text-right text-emerald-700">My override</th>
                  </tr>
                </thead>
                <tbody>
                  {carrierRows.map(c => {
                    const accent = c.name === 'Americo' ? 'emerald' : c.name === 'MOO' ? 'violet' : c.name === 'Transamerica' ? 'amber' : 'slate';
                    return (
                      <tr key={c.name} className="border-b border-slate-50 last:border-0 hover:bg-slate-50">
                        <td className="py-2 px-3">
                          <span className={`inline-flex items-center gap-1.5`}>
                            <span className={`w-2 h-2 rounded-full bg-${accent}-500`}/>
                            <span className="font-medium">{c.name}</span>
                          </span>
                        </td>
                        <td className="py-2 px-3 text-right text-slate-500">{c.agents.size}</td>
                        <td className="py-2 px-3 text-right text-slate-500">{c.policies}</td>
                        <td className="py-2 px-3 text-right">{fmt(c.premium)}</td>
                        <td className="py-2 px-3 text-right text-red-600">{(c.premiumCB || 0) > 0 ? fmt(c.premiumCB) : '—'}</td>
                        <td className={`py-2 px-3 text-right ${c.net < 0 ? 'text-red-600' : ''}`}>{fmt(c.net)}</td>
                        <td className="py-2 px-3 text-right text-violet-700">{fmt(c.camCut)}</td>
                        <td className="py-2 px-3 text-right text-red-600">{(c.overrideCB || 0) < 0 ? fmt(c.overrideCB) : '—'}</td>
                        <td className={`py-2 px-3 text-right font-semibold ${c.override < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{fmt(c.override)}</td>
                      </tr>
                    );
                  })}
                  <tr className="bg-slate-100 font-bold">
                    <td className="py-2 px-3">Total</td>
                    <td className="py-2 px-3 text-right text-slate-600">{new Set(carrierRows.flatMap(c => [...c.agents])).size}</td>
                    <td className="py-2 px-3 text-right text-slate-600">{carrierRows.reduce((s,c)=>s+c.policies,0)}</td>
                    <td className="py-2 px-3 text-right">{fmt(totalPremium)}</td>
                    <td className="py-2 px-3 text-right text-red-600">{totalPremiumCB > 0 ? fmt(totalPremiumCB) : '—'}</td>
                    <td className="py-2 px-3 text-right">{fmt(totalNet)}</td>
                    <td className="py-2 px-3 text-right text-violet-700">{fmt(totalCamCut)}</td>
                    <td className="py-2 px-3 text-right text-red-600">{totalOverrideCB < 0 ? fmt(totalOverrideCB) : '—'}</td>
                    <td className="py-2 px-3 text-right text-emerald-700">{fmt(totalOverride)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Top contributors callout */}
      {topThree.length > 0 && (
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-2">Top contributors</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {topThree.map((p, i) => {
              const medals = ['', '', ''];
              return (
                <div key={p.prodNum} className="bg-white border border-emerald-200 rounded-xl p-3 flex items-center gap-3">
                  <div className="text-2xl">{medals[i]}</div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate">
                      <EditableProducerName
                        prodNum={p.prodNum}
                        producers={producers}
                        setProducerName={setProducerName}
                      />
                    </div>
                    <div className="text-xs text-slate-500">{fmt(p.premiumPositive)} premium · {shareOfTotal(p.yourEarnings).toFixed(0)}% of overrides</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-emerald-700">{fmt(p.yourEarnings)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* By agent */}
      <div>
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold flex-1">By agent</div>
          <select value={sortBy} onChange={e=>setSortBy(e.target.value)} className="px-2 py-1 border border-slate-200 rounded text-xs bg-white">
            <option value="earnings">Sort: my override $</option>
            <option value="premium">Sort: premium written</option>
            <option value="net">Sort: net commission</option>
            <option value="premiumCB">Sort: premium charged back</option>
            <option value="overrideCB">Sort: override clawed back</option>
            <option value="name">Sort: name (A→Z)</option>
          </select>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {mine.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">No agents on your team have production this period.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-xs text-slate-500 text-left">
                    <th className="py-2 px-3 w-10"></th>
                    <th className="py-2 px-3 w-10">#</th>
                    <th className="py-2 px-3">Agent</th>
                    <th className="py-2 px-3 text-right">Premium</th>
                    <th className="py-2 px-3 text-right text-red-600">Premium CB</th>
                    <th className="py-2 px-3 text-right">Net</th>
                    <th className="py-2 px-3 text-right">Agent cut</th>
                    <th className="py-2 px-3 text-right text-red-600">Override CB</th>
                    <th className="py-2 px-3 text-right text-emerald-700">My override</th>
                    <th className="py-2 px-3 w-40">Share of overrides</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((p, idx) => {
                    const policies = policiesByAgent[p.prodNum] || [];
                    const isOpen = !!expandedAgent[p.prodNum];
                    return (
                      <React.Fragment key={p.prodNum}>
                        <tr
                          className="border-b border-slate-50 last:border-0 hover:bg-slate-50 cursor-pointer"
                          onClick={() => setExpandedAgent(s => ({ ...s, [p.prodNum]: !s[p.prodNum] }))}
                        >
                          <td className="py-2 px-3">
                            <ChevronRight size={14} className={`text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}/>
                          </td>
                          <td className="py-2 px-3 text-slate-400 font-mono text-xs">{idx + 1}</td>
                          <td className="py-2 px-3">
                            <div className="font-medium">
                              <EditableProducerName
                                prodNum={p.prodNum}
                                producers={producers}
                                setProducerName={setProducerName}
                              />
                            </div>
                            <div className="text-xs text-slate-400">
                              <span className="font-mono">{p.prodNum}</span>
                              {policies.length > 0 && <span className="ml-2">· {policies.length} polic{policies.length === 1 ? 'y' : 'ies'}</span>}
                            </div>
                          </td>
                          <td className="py-2 px-3 text-right">{fmt(p.premiumPositive)}</td>
                          <td className="py-2 px-3 text-right text-red-600">{(p.premiumNegative || 0) > 0 ? fmt(p.premiumNegative) : '—'}</td>
                          <td className={`py-2 px-3 text-right ${p.net < 0 ? 'text-red-600' : ''}`}>{fmt(p.net)}</td>
                          <td className="py-2 px-3 text-right text-violet-700">{fmt(p.camCut)}</td>
                          {(() => { const ob = overrideBreakdown(p); return (
                          <td className="py-2 px-3 text-right text-red-600">{ob.negative < 0 ? fmt(ob.negative) : '—'}</td>
                          ); })()}
                          <td className={`py-2 px-3 text-right font-semibold ${p.yourEarnings < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{fmt(p.yourEarnings)}</td>
                          <td className="py-2 px-3">
                            {p.yourEarnings > 0 ? (
                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-slate-100 rounded h-2 overflow-hidden">
                                  <div className="bg-emerald-500 h-full" style={{ width: `${topPctOf(p.yourEarnings)}%` }}/>
                                </div>
                                <span className="text-xs text-slate-500 tabular-nums w-10 text-right">{shareOfTotal(p.yourEarnings).toFixed(0)}%</span>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-300">—</span>
                            )}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-slate-50/60">
                            <td colSpan={10} className="px-3 pb-3 pt-1">
                              {policies.length === 0 ? (
                                <div className="text-xs text-slate-500 italic px-2 py-3">No policies for this agent in the current period.</div>
                              ) : (
                                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                                  <div className="px-3 py-2 bg-slate-100 border-b border-slate-200 text-xs font-semibold text-slate-700">
                                    Policies written · {policies.length} · {fmt(policies.reduce((s, x) => s + x.override, 0))} override
                                  </div>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-xs">
                                      <thead className="bg-white border-b border-slate-100">
                                        <tr className="text-slate-500 text-left">
                                          <th className="py-1.5 px-3"><button type="button" onClick={(e) => { e.stopPropagation(); togglePolicySort('insured'); }} className="font-medium hover:text-slate-900">Insured{sortArrow('insured')}</button></th>
                                          <th className="py-1.5 px-3"><button type="button" onClick={(e) => { e.stopPropagation(); togglePolicySort('policy'); }} className="font-medium hover:text-slate-900">Policy #{sortArrow('policy')}</button></th>
                                          <th className="py-1.5 px-3"><button type="button" onClick={(e) => { e.stopPropagation(); togglePolicySort('carrier'); }} className="font-medium hover:text-slate-900">Carrier{sortArrow('carrier')}</button></th>
                                          <th className="py-1.5 px-3"><button type="button" onClick={(e) => { e.stopPropagation(); togglePolicySort('issued'); }} className="font-medium hover:text-slate-900">Issued{sortArrow('issued')}</button></th>
                                          <th className="py-1.5 px-3 text-right"><button type="button" onClick={(e) => { e.stopPropagation(); togglePolicySort('premium'); }} className="font-medium hover:text-slate-900">Premium{sortArrow('premium')}</button></th>
                                          <th className="py-1.5 px-3 text-right"><button type="button" onClick={(e) => { e.stopPropagation(); togglePolicySort('net'); }} className="font-medium hover:text-slate-900">Net comm.{sortArrow('net')}</button></th>
                                          <th className="py-1.5 px-3 text-right"><button type="button" onClick={(e) => { e.stopPropagation(); togglePolicySort('camCut'); }} className="font-medium hover:text-slate-900">Agent cut{sortArrow('camCut')}</button></th>
                                          <th className="py-1.5 px-3 text-right text-emerald-700"><button type="button" onClick={(e) => { e.stopPropagation(); togglePolicySort('override'); }} className="font-medium hover:text-emerald-900">My override{sortArrow('override')}</button></th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {sortPolicies(policies).map(pol => {
                                          const accent = pol.carrier === 'Americo' ? 'emerald' : pol.carrier === 'MOO' ? 'violet' : pol.carrier === 'Transamerica' ? 'amber' : 'slate';
                                          return (
                                            <tr key={pol.key} className="border-b border-slate-50 last:border-0 hover:bg-slate-50">
                                              <td className="py-1.5 px-3">
                                                <button
                                                  onClick={(e) => { e.stopPropagation(); openClientProfile(clientKeyOf(pol.insured)); }}
                                                  className="text-emerald-700 hover:text-emerald-900 hover:underline font-medium text-left"
                                                  title="View full client profile"
                                                >
                                                  {titleCase(pol.insured)}
                                                </button>
                                                {pol.state && <span className="text-slate-400 ml-1">({pol.state})</span>}
                                              </td>
                                              <td className="py-1.5 px-3 font-mono text-[11px]">{pol.policy}</td>
                                              <td className="py-1.5 px-3">
                                                <span className={`inline-flex items-center gap-1.5 text-${accent}-800`}>
                                                  <span className={`w-1.5 h-1.5 rounded-full bg-${accent}-500`}/>
                                                  {pol.carrier}
                                                </span>
                                              </td>
                                              <td className="py-1.5 px-3 text-slate-600">{pol.issueDate || '—'}</td>
                                              <td className="py-1.5 px-3 text-right">{fmt(pol.premium)}</td>
                                              <td className={`py-1.5 px-3 text-right ${pol.net < 0 ? 'text-red-600' : ''}`}>{fmt(pol.net)}</td>
                                              <td className="py-1.5 px-3 text-right text-violet-700">{fmt(pol.camCut)}</td>
                                              <td className={`py-1.5 px-3 text-right font-semibold ${pol.override < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{fmt(pol.override)}</td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  <tr className="bg-slate-100 font-bold">
                    <td className="py-2 px-3"></td>
                    <td className="py-2 px-3"></td>
                    <td className="py-2 px-3">Total</td>
                    <td className="py-2 px-3 text-right">{fmt(totalPremium)}</td>
                    <td className="py-2 px-3 text-right text-red-600">{totalPremiumCB > 0 ? fmt(totalPremiumCB) : '—'}</td>
                    <td className="py-2 px-3 text-right">{fmt(totalNet)}</td>
                    <td className="py-2 px-3 text-right text-violet-700">{fmt(totalCamCut)}</td>
                    <td className="py-2 px-3 text-right text-red-600">{totalOverrideCB < 0 ? fmt(totalOverrideCB) : '—'}</td>
                    <td className="py-2 px-3 text-right text-emerald-700">{fmt(totalOverride)}</td>
                    <td className="py-2 px-3"></td>
                  </tr>
                </tbody>
              </table>
              {sorted.length > 15 && (
                <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 text-center">
                  <button onClick={()=>setShowAll(s => !s)} className="text-sm text-emerald-700 hover:text-emerald-900 font-medium">
                    {showAll ? `Show top 15 only` : `Show all ${sorted.length} agents`}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────
// Calls — one row per actual dial. Replaces the older daily-KPI aggregate
// model. Charts (outcome breakdown, daily volume, by-hour contact rate,
// duration distribution, funnel, AI coaching score histogram) all derive
// from the same calls array filtered by direction + outcome chips.
// ──────────────────────────────────────────────────────────────────────
const CALL_OUTCOMES = [
  { id: 'appt_set',       label: 'Appt Set',       color: 'emerald' },
  { id: 'callback',       label: 'Callback',       color: 'amber'   },
  { id: 'pickup',         label: 'Pickup',         color: 'violet'  },
  { id: 'not_interested', label: 'Not Interested', color: 'slate'   },
  { id: 'voicemail',      label: 'Voicemail',      color: 'blue'    },
  { id: 'no_answer',      label: 'No Answer',      color: 'red'     },
  { id: 'dnc',            label: 'DNC',            color: 'rose'    },
];
const CALL_OUTCOME_BY_ID = Object.fromEntries(CALL_OUTCOMES.map(o => [o.id, o]));
const isContactOutcome = (o) => o === 'appt_set' || o === 'callback' || o === 'pickup' || o === 'not_interested' || o === 'dnc';

function CallsView({ calls = [], setCalls, leads = [], setLeads, producers = {}, producerList = [], currentUser = null, showToast = () => {}, openClientProfile = () => {}, onGotoTab = () => {} }) {
  const [directionFilter, setDirectionFilter] = useState('all');
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');
  const [editingCall, setEditingCall] = useState(null);

  const agentChoices = React.useMemo(() => {
    const byKey = {};
    // Route every prodNum through getProducerName so KNOWN_PRODUCERS
    // names (Harrison Curry, Mark Omobogie, …) show up automatically
    // instead of the raw prodNum. The "Producer NNNN" fallback only
    // fires when nothing has a name anywhere.
    Object.keys(producers || {}).forEach(pn => { if (pn) byKey[pn] = { prodNum: pn, name: getProducerName(pn, producers) }; });
    (producerList || []).forEach(p => { if (p?.prodNum && !byKey[p.prodNum]) byKey[p.prodNum] = { prodNum: p.prodNum, name: getProducerName(p.prodNum, producers) }; });
    // Strip parser artifacts that aren't actual agents — e.g. AM-TOTAL
    // is the Americo statement's totals row, AM-UNKNOWN /
    // TRANS-UNKNOWN are placeholders when the parser couldn't pin
    // down an agent id. None of these should be selectable in the
    // call-agent dropdown.
    const isArtifact = (pn) => /-(TOTAL|UNKNOWN|SUBTOTAL)$/i.test(pn || '');
    return Object.values(byKey)
      .filter(a => !isArtifact(a.prodNum))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [producers, producerList]);
  const agentName = (pn) => agentChoices.find(a => a.prodNum === pn)?.name || getProducerName(pn, producers) || '—';

  const filteredCalls = React.useMemo(() => calls.filter(c => {
    if (directionFilter !== 'all' && c.direction !== directionFilter) return false;
    if (outcomeFilter !== 'all' && c.outcome !== outcomeFilter) return false;
    if (agentFilter !== 'all' && c.agentProdNum !== agentFilter) return false;
    return true;
  }), [calls, directionFilter, outcomeFilter, agentFilter]);

  const total = filteredCalls.length;
  const contacted = filteredCalls.filter(c => isContactOutcome(c.outcome)).length;
  const appts = filteredCalls.filter(c => c.outcome === 'appt_set').length;
  const totalDuration = filteredCalls.reduce((s, c) => s + (Number(c.duration) || 0), 0);
  const avgDurationSec = total > 0 ? totalDuration / total : 0;
  const contactRate = total > 0 ? (contacted / total) * 100 : 0;
  const apptRate = total > 0 ? (appts / total) * 100 : 0;

  // Outbound-only pickup rate. "Pickup rate" only really makes sense
  // for dials we initiated — inbound calls all "pick up" by definition
  // and inflate the number. We expose both so the operator can pivot.
  const outboundCalls = filteredCalls.filter(c => c.direction === 'outbound');
  const outboundPickups = outboundCalls.filter(c => isContactOutcome(c.outcome)).length;
  const outboundPickupRate = outboundCalls.length > 0 ? (outboundPickups / outboundCalls.length) * 100 : 0;

  // Pickup rate by agent (outbound only). Aged-lead MP/IUL shops live
  // and die by who's actually getting people to answer the phone — so
  // this is one of the most useful management views in the CRM. Sort
  // by dials desc so an agent with one lucky pickup doesn't top the
  // list over someone making real volume.
  const pickupByAgent = React.useMemo(() => {
    const m = {};
    outboundCalls.forEach(c => {
      const k = c.agentProdNum || '—';
      if (!m[k]) m[k] = { prodNum: k, dials: 0, pickups: 0 };
      m[k].dials++;
      if (isContactOutcome(c.outcome)) m[k].pickups++;
    });
    return Object.values(m)
      .map(r => ({ ...r, rate: r.dials > 0 ? (r.pickups / r.dials) * 100 : 0 }))
      .sort((a, b) => b.dials - a.dials);
  }, [outboundCalls]);
  const fmtSec = (s) => {
    const m = Math.floor(s / 60);
    const r = Math.round(s % 60);
    return m > 0 ? `${m}m ${r.toString().padStart(2,'0')}s` : `${r}s`;
  };

  const outcomeData = CALL_OUTCOMES.map(o => ({
    name: o.label,
    value: filteredCalls.filter(c => c.outcome === o.id).length,
    fill: ({ emerald: '#10b981', amber: '#f59e0b', violet: '#8b5cf6', slate: '#64748b', blue: '#3b82f6', red: '#ef4444', rose: '#f43f5e' })[o.color] || '#94a3b8',
  })).filter(d => d.value > 0);

  const dailyData = React.useMemo(() => {
    const today = new Date();
    const out = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date(today); d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      const dayCalls = filteredCalls.filter(c => c.date === key);
      out.push({ date: key.slice(5), calls: dayCalls.length, appts: dayCalls.filter(c => c.outcome === 'appt_set').length });
    }
    return out;
  }, [filteredCalls]);

  const hourData = React.useMemo(() => {
    const buckets = Array.from({ length: 24 }, (_, h) => ({ hour: `${h}`, calls: 0, contacts: 0 }));
    filteredCalls.forEach(c => {
      const t = (c.startTime || '').split(':')[0];
      const h = parseInt(t, 10);
      if (!Number.isFinite(h) || h < 0 || h > 23) return;
      buckets[h].calls++;
      if (isContactOutcome(c.outcome)) buckets[h].contacts++;
    });
    return buckets.map(b => {
      const rate = b.calls > 0 ? (b.contacts / b.calls) * 100 : 0;
      const fill = rate >= 50 ? '#10b981' : rate >= 25 ? '#f59e0b' : '#94a3b8';
      return { ...b, contactRate: Number(rate.toFixed(1)), fill };
    });
  }, [filteredCalls]);
  const hourHasData = hourData.some(b => b.calls > 0);

  const durationData = React.useMemo(() => {
    const b = { '<1m': 0, '1-2m': 0, '2-5m': 0, '5m+': 0 };
    filteredCalls.forEach(c => {
      const s = Number(c.duration) || 0;
      if (s < 60) b['<1m']++;
      else if (s < 120) b['1-2m']++;
      else if (s < 300) b['2-5m']++;
      else b['5m+']++;
    });
    return Object.entries(b).map(([name, value]) => ({ name, value }));
  }, [filteredCalls]);

  const dials = total;
  const pickups = filteredCalls.filter(c => isContactOutcome(c.outcome)).length;
  const callbacks = filteredCalls.filter(c => c.outcome === 'callback').length;
  const funnelData = [
    { name: 'Dials',     value: dials,     fill: '#64748b' },
    { name: 'Pickups',   value: pickups,   fill: '#3b82f6' },
    { name: 'Callbacks', value: callbacks, fill: '#f59e0b' },
    { name: 'Appts',     value: appts,     fill: '#10b981' },
  ];

  const scoreData = React.useMemo(() => {
    const b = { '9-10': 0, '7-8': 0, '5-6': 0, '<5': 0 };
    filteredCalls.forEach(c => {
      const s = c.aiScore;
      if (s == null || s === '') return;
      const n = Number(s);
      if (n >= 9) b['9-10']++;
      else if (n >= 7) b['7-8']++;
      else if (n >= 5) b['5-6']++;
      else b['<5']++;
    });
    return [
      { name: '9-10', value: b['9-10'], fill: '#10b981' },
      { name: '7-8',  value: b['7-8'],  fill: '#84cc16' },
      { name: '5-6',  value: b['5-6'],  fill: '#f59e0b' },
      { name: '<5',   value: b['<5'],   fill: '#ef4444' },
    ];
  }, [filteredCalls]);

  const newId = () => (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `call_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const saveCall = (rec) => {
    const now = new Date().toISOString();
    if (rec.id) {
      setCalls(prev => prev.map(c => c.id === rec.id ? { ...c, ...rec, updatedAt: now } : c));
      showToast('Call updated');
    } else {
      setCalls(prev => [...prev, { ...rec, id: newId(), createdAt: now, updatedAt: now }]);
      showToast('Call logged');
    }
    setEditingCall(null);

    // W9 Disposition → Stage: when a call gets logged from the Calls
    // tab (not from the lead detail), auto-advance the linked lead
    // based on the outcome. The lead-detail call form already runs
    // a richer version of this; here we keep it minimal so the two
    // paths agree on the most common outcomes.
    if (rec.leadId) {
      const lead = (leads || []).find(l => l.id === rec.leadId);
      if (lead) {
        const TERMINAL = new Set(['closed', 'dnc']);
        let nextStatus = null;
        if (rec.outcome === 'appt_set'       && lead.status !== 'appt_set' && lead.status !== 'closed') nextStatus = 'appt_set';
        else if (rec.outcome === 'not_interested' && !TERMINAL.has(lead.status))                       nextStatus = 'recycled';
        else if (rec.outcome === 'dnc'            && !TERMINAL.has(lead.status))                       nextStatus = 'dnc';
        else if (rec.outcome === 'pickup'         && lead.status === 'new')                            nextStatus = 'contacted';
        else if (isContactOutcome(rec.outcome)    && lead.status === 'new')                            nextStatus = 'contacted';
        if (nextStatus && setLeads) {
          setLeads(prev => prev.map(l => l.id === rec.leadId ? {
            ...l,
            status: nextStatus,
            statusChangedAt: now,
            updatedAt: now,
            activity: [...(l.activity || []), { at: now, type: 'auto_status', body: `Auto-advanced to ${nextStatus} from call disposition (${rec.outcome})` }],
          } : l));
          // W8 warm alert hooks fire too when the auto-status lands
          // on a warm bucket (appt_set is the main one from calls).
          const WARM = new Set(['interested', 'appt_set']);
          if (WARM.has(nextStatus)) {
            showToast(`Warm lead: ${lead.name || 'lead'} → ${nextStatus === 'interested' ? 'Interested' : 'Appt set'}`);
            fireWarmLeadAlert({ leadId: rec.leadId, leadName: lead.name, status: nextStatus });
          }
        }
      }
    }
  };
  const deleteCall = (id) => {
    setCalls(prev => prev.filter(c => c.id !== id));
    showToast('Call deleted');
  };

  const recent = [...filteredCalls].sort((a, b) => {
    const ad = `${a.date || ''} ${a.startTime || ''}`;
    const bd = `${b.date || ''} ${b.startTime || ''}`;
    return bd.localeCompare(ad);
  }).slice(0, 50);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Phone size={22} className="text-emerald-600"/> Calls</h2>
          <p className="text-sm text-slate-500 mt-1">Log every dial. Charts and rates update live from the filters below.</p>
        </div>
        <button onClick={() => setEditingCall({ __new: true })} className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-semibold">+ Log call</button>
      </div>

      {/* Live Twilio Voice call history — separate from the manually-
          logged calls below. Each row supports recording playback +
          one-click AI summary. */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h3 className="font-bold text-slate-900 text-sm uppercase tracking-tight">📞 Live Voice Calls — Recent</h3>
          <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">via Relay Voice</span>
        </div>
        <CallHistoryPanel currentUser={currentUser} limit={15} showToast={showToast}/>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3 flex-wrap text-sm">
        <div className="flex items-center gap-1">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide mr-1">Direction:</span>
          {[['all','All'],['outbound','Outbound'],['inbound','Inbound']].map(([id, label]) => (
            <button key={id} onClick={() => setDirectionFilter(id)} className={`px-2.5 py-1 text-xs font-medium rounded-lg border ${directionFilter === id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}>{label}</button>
          ))}
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide mr-1">Outcome:</span>
          <button onClick={() => setOutcomeFilter('all')} className={`px-2.5 py-1 text-xs font-medium rounded-lg border ${outcomeFilter === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}>All</button>
          {CALL_OUTCOMES.map(o => (
            <button key={o.id} onClick={() => setOutcomeFilter(o.id)} className={`px-2.5 py-1 text-xs font-medium rounded-lg border ${outcomeFilter === o.id ? `bg-${o.color}-600 text-white border-${o.color}-600` : `bg-white text-${o.color}-700 border-${o.color}-200 hover:bg-${o.color}-50`}`}>{o.label}</button>
          ))}
        </div>
        <select value={agentFilter} onChange={e => setAgentFilter(e.target.value)} className="px-2 py-1 border border-slate-200 rounded-lg text-xs bg-white ml-auto">
          <option value="all">All agents</option>
          {agentChoices.map(a => <option key={a.prodNum} value={a.prodNum}>{a.name}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={<Phone size={16}/>} label="Total Calls" value={total.toLocaleString()} color="slate"/>
        <StatCard
          icon={<Users size={16}/>}
          label="Pickup Rate"
          value={`${outboundPickupRate.toFixed(1)}%`}
          sub={`${outboundPickups} of ${outboundCalls.length} outbound dials answered`}
          color={outboundPickupRate >= 30 ? 'emerald' : outboundPickupRate >= 15 ? 'amber' : 'red'}
        />
        <StatCard icon={<CheckCircle size={16}/>} label="Appt Rate" value={`${apptRate.toFixed(1)}%`} sub={`${appts} of ${total} calls`} color="amber"/>
        <StatCard icon={<CheckCircle size={16}/>} label="Appointments" value={appts.toLocaleString()} color="emerald"/>
        <StatCard icon={<TrendingUp size={16}/>} label="Avg Duration" value={fmtSec(avgDurationSec)} color="slate"/>
      </div>

      {pickupByAgent.length > 1 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
            <div>
              <h3 className="font-semibold text-slate-900">Pickup rate by agent</h3>
              <p className="text-xs text-slate-500 mt-0.5">Outbound dials only. Sorted by dial volume so a single lucky pickup doesn't top the list.</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Agent</th>
                  <th className="py-2 px-2 text-right">Dials</th>
                  <th className="py-2 px-2 text-right">Picked up</th>
                  <th className="py-2 px-2 text-right">Pickup rate</th>
                </tr>
              </thead>
              <tbody>
                {pickupByAgent.map(r => {
                  const tone = r.rate >= 30 ? 'emerald' : r.rate >= 15 ? 'amber' : 'red';
                  return (
                    <tr key={r.prodNum} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 pr-3 font-medium text-slate-800">{agentName(r.prodNum)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{r.dials.toLocaleString()}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{r.pickups.toLocaleString()}</td>
                      <td className={`py-2 px-2 text-right tabular-nums font-semibold text-${tone}-700`}>{r.rate.toFixed(1)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {total === 0 ? (
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-10 text-center">
          <div className="text-slate-700 font-semibold mb-1">No calls logged for this filter</div>
          <div className="text-sm text-slate-500 mb-4">Charts appear here once you start logging dials.</div>
          <button onClick={() => setEditingCall({ __new: true })} className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-semibold">+ Log first call</button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card title="Outcome breakdown" subtitle={`${total} calls in current filter`} fullscreenable defaultHeight={260}>
              {(h) => (
                <ResponsiveContainer width="100%" height={h}>
                  <BarChart data={outcomeData} layout="vertical" margin={{ left: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                    <XAxis type="number" tick={{ fontSize: 12 }}/>
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={100}/>
                    <Tooltip formatter={(v) => v.toLocaleString()}/>
                    <Bar dataKey="value" name="Calls">
                      {outcomeData.map((entry, i) => <Cell key={i} fill={entry.fill}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>

            <Card title="Daily volume" subtitle="Last 14 days · grey = calls, purple = appts" fullscreenable defaultHeight={260}>
              {(h) => (
                <ResponsiveContainer width="100%" height={h}>
                  <BarChart data={dailyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                    <XAxis dataKey="date" tick={{ fontSize: 11 }}/>
                    <YAxis tick={{ fontSize: 12 }}/>
                    <Tooltip/>
                    <Legend/>
                    <Bar dataKey="calls" name="Calls" fill="#94a3b8"/>
                    <Bar dataKey="appts" name="Appts" fill="#8b5cf6"/>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>

            <Card title="Best hours to dial" subtitle="Pickup rate by hour of day — green ≥50%, amber 25–49%, grey <25%" fullscreenable defaultHeight={260}>
              {(h) => hourHasData ? (
                <ResponsiveContainer width="100%" height={h}>
                  <BarChart data={hourData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                    <XAxis dataKey="hour" tick={{ fontSize: 11 }} interval={1}/>
                    <YAxis yAxisId="left" tick={{ fontSize: 12 }}/>
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} domain={[0, 100]} tickFormatter={v => `${v}%`}/>
                    <Tooltip formatter={(v, name) => name === 'Contact %' ? `${v}%` : v.toLocaleString()} labelFormatter={(l) => `${l}:00`}/>
                    <Legend/>
                    <Bar yAxisId="left" dataKey="calls" name="Calls">
                      {hourData.map((entry, i) => <Cell key={i} fill={entry.fill}/>)}
                    </Bar>
                    <Line yAxisId="right" type="monotone" dataKey="contactRate" name="Pickup %" stroke="#eab308" strokeWidth={2} dot={false}/>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-sm text-slate-400 italic">Add a start time to calls to populate this chart.</div>
              )}
            </Card>

            <Card title="Call duration distribution" subtitle="Bucketed by talk length" fullscreenable defaultHeight={260}>
              {(h) => (
                <ResponsiveContainer width="100%" height={h}>
                  <BarChart data={durationData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                    <XAxis dataKey="name" tick={{ fontSize: 12 }}/>
                    <YAxis tick={{ fontSize: 12 }}/>
                    <Tooltip/>
                    <Bar dataKey="value" name="Calls" fill="#10b981"/>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>

            <Card title="Call funnel" subtitle="Dials → Pickups → Callbacks → Appts" fullscreenable defaultHeight={260}>
              {(h) => (
                <ResponsiveContainer width="100%" height={h}>
                  <BarChart data={funnelData} layout="vertical" margin={{ left: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                    <XAxis type="number" tick={{ fontSize: 12 }}/>
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={80}/>
                    <Tooltip formatter={(v) => v.toLocaleString()}/>
                    <Bar dataKey="value" name="Count">
                      {funnelData.map((entry, i) => <Cell key={i} fill={entry.fill}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>

            <Card title="AI coaching score distribution" subtitle="Score bucket counts (unscored calls excluded)" fullscreenable defaultHeight={260}>
              {(h) => (
                <ResponsiveContainer width="100%" height={h}>
                  <BarChart data={scoreData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                    <XAxis dataKey="name" tick={{ fontSize: 12 }}/>
                    <YAxis tick={{ fontSize: 12 }}/>
                    <Tooltip/>
                    <Bar dataKey="value" name="Calls">
                      {scoreData.map((entry, i) => <Cell key={i} fill={entry.fill}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-900">Recent calls</h3>
              <div className="text-xs text-slate-500">Showing newest {Math.min(recent.length, 50)} of {filteredCalls.length}</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3">Date</th>
                    <th className="py-2 px-2">Time</th>
                    <th className="py-2 px-2">Agent</th>
                    <th className="py-2 px-2">Prospect</th>
                    <th className="py-2 px-2">Dir</th>
                    <th className="py-2 px-2">Outcome</th>
                    <th className="py-2 px-2 text-right">Duration</th>
                    <th className="py-2 px-2 text-right">AI</th>
                    <th className="py-2 px-2">Notes</th>
                    <th className="py-2 pl-2 text-right"/>
                  </tr>
                </thead>
                <tbody>
                  {recent.map(c => {
                    const outcome = CALL_OUTCOME_BY_ID[c.outcome];
                    return (
                      <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 pr-3 text-slate-700">{fmtDateUS(c.date)}</td>
                        <td className="py-2 px-2 text-slate-600">{c.startTime || '—'}</td>
                        <td className="py-2 px-2">{agentName(c.agentProdNum)}</td>
                        <td className="py-2 px-2">
                          {c.prospectName ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Two destinations:
                                //   1. Call has a leadId AND that lead still exists → jump to Leads tab.
                                //      (Leads tab will scroll the row into view on next iteration; for now
                                //      we just switch tabs so the operator lands somewhere actionable.)
                                //   2. Otherwise treat the prospect as a Book-of-Business client and
                                //      open their profile drawer via the canonical clientKey.
                                if (c.leadId && leads.some(l => l.id === c.leadId)) {
                                  onGotoTab && onGotoTab('leads');
                                  return;
                                }
                                const key = String(c.prospectName).trim().toUpperCase().replace(/\s+/g, ' ');
                                openClientProfile(key);
                              }}
                              className="text-emerald-700 hover:text-emerald-900 hover:underline font-medium text-left"
                              title="Open profile"
                            >
                              {c.prospectName}
                            </button>
                          ) : '—'}
                        </td>
                        <td className="py-2 px-2 text-xs text-slate-500 uppercase">{c.direction || '—'}</td>
                        <td className="py-2 px-2">{outcome ? (<span className={`px-1.5 py-0.5 rounded text-xs font-bold bg-${outcome.color}-100 text-${outcome.color}-700`}>{outcome.label}</span>) : '—'}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{fmtSec(Number(c.duration) || 0)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{c.aiScore != null && c.aiScore !== '' ? c.aiScore : '—'}</td>
                        <td className="py-2 px-2 text-slate-600 max-w-xs truncate" title={c.notes || ''}>{c.notes || ''}</td>
                        <td className="py-2 pl-2 text-right whitespace-nowrap">
                          <button onClick={() => setEditingCall(c)} className="text-xs text-emerald-700 hover:underline mr-3">Edit</button>
                          <button onClick={() => deleteCall(c.id)} className="text-xs text-red-600 hover:underline">Delete</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {editingCall && (
        <CallFormModal
          initial={editingCall.__new ? null : editingCall}
          agentChoices={agentChoices}
          leads={leads}
          onCancel={() => setEditingCall(null)}
          onSave={saveCall}
        />
      )}
    </div>
  );
}

function CallFormModal({ initial, agentChoices, leads, onCancel, onSave }) {
  const today = new Date().toISOString().slice(0, 10);
  // Audit 2026-07-01 BUG-006: Escape should close the modal (WCAG 2.1.2).
  // Global key listener so the escape fires even if a form field has
  // focus. Guarded on onCancel presence so it stays a no-op if the
  // parent didn't wire the cancel handler.
  useEffect(() => {
    if (!onCancel) return;
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); onCancel(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  const [r, setR] = useState({
    id: initial?.id || '',
    agentProdNum: initial?.agentProdNum || '',
    leadId: initial?.leadId || '',
    prospectName: initial?.prospectName || '',
    prospectPhone: initial?.prospectPhone || '',
    date: initial?.date || today,
    startTime: initial?.startTime || '',
    direction: initial?.direction || 'outbound',
    outcome: initial?.outcome || 'no_answer',
    duration: initial?.duration || '',
    aiScore: initial?.aiScore ?? '',
    notes: initial?.notes || '',
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setR(prev => ({ ...prev, [k]: v }));

  const pickLead = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    setR(prev => ({
      ...prev,
      leadId,
      prospectName: lead?.name || prev.prospectName,
      prospectPhone: lead?.phone || prev.prospectPhone,
      agentProdNum: lead?.agentProdNum || prev.agentProdNum,
    }));
  };

  // Detect a callback intent in the notes ("call back tomorrow",
  // "follow up Friday 3pm", etc.). Reuses the existing extraction
  // pipeline — operator gets one save click, the system handles the
  // schedule.
  const trySave = async () => {
    const errs = {};
    if (!r.agentProdNum) errs.agentProdNum = 'Pick an agent';
    if (!r.date) errs.date = 'Required';
    if (!r.outcome) errs.outcome = 'Required';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setBusy(true);
    let callbackDetected = null;
    if (r.notes && r.notes.trim().length >= 6 && r.leadId) {
      try {
        const out = await extractFieldsFromNotes({ notes: r.notes, currentLead: {} });
        if (out?.callbackAt) {
          callbackDetected = { iso: out.callbackAt, description: out.callbackDescription || '' };
        }
      } catch (_) { /* don't block save on AI failure */ }
    }

    onSave({
      ...r,
      duration: Math.max(0, Number(r.duration) || 0),
      aiScore: r.aiScore === '' ? null : Math.max(0, Math.min(10, Number(r.aiScore) || 0)),
      _callbackDetected: callbackDetected,
    });
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onCancel}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-5 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg">{initial ? 'Edit call' : 'Log call'}</h3>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-700"><X size={20}/></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <label className="block">
            <span className="text-xs text-slate-500">Agent *</span>
            <select value={r.agentProdNum} onChange={e => set('agentProdNum', e.target.value)} className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white ${errors.agentProdNum ? 'border-red-400' : 'border-slate-200'}`}>
              <option value="">— Select —</option>
              {agentChoices.map(a => <option key={a.prodNum} value={a.prodNum}>{a.name}</option>)}
            </select>
            {errors.agentProdNum && <span className="text-xs text-red-600">{errors.agentProdNum}</span>}
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Link to lead (optional)</span>
            <select value={r.leadId} onChange={e => pickLead(e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">— None —</option>
              {leads.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(l => (
                <option key={l.id} value={l.id}>{l.name} — {l.phone || 'no phone'}</option>
              ))}
            </select>
          </label>
          <label className="block"><span className="text-xs text-slate-500">Prospect name</span><input value={r.prospectName} onChange={e => set('prospectName', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/></label>
          <label className="block"><span className="text-xs text-slate-500">Prospect phone</span><input value={r.prospectPhone} onChange={e => set('prospectPhone', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/></label>
          <label className="block">
            <span className="text-xs text-slate-500">Date *</span>
            <input type="date" value={r.date} onChange={e => set('date', e.target.value)} className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${errors.date ? 'border-red-400' : 'border-slate-200'}`}/>
          </label>
          <label className="block"><span className="text-xs text-slate-500">Start time</span><input type="time" value={r.startTime} onChange={e => set('startTime', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/></label>
          <label className="block">
            <span className="text-xs text-slate-500">Direction</span>
            <select value={r.direction} onChange={e => set('direction', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="outbound">Outbound</option>
              <option value="inbound">Inbound</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Outcome *</span>
            <select value={r.outcome} onChange={e => set('outcome', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
              {CALL_OUTCOMES.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </label>
          <label className="block"><span className="text-xs text-slate-500">Duration (seconds)</span><input type="number" min="0" value={r.duration} onChange={e => set('duration', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/></label>
          <label className="block"><span className="text-xs text-slate-500">AI coaching score (0–10)</span><input type="number" min="0" max="10" step="0.5" value={r.aiScore} onChange={e => set('aiScore', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/></label>
          <label className="block md:col-span-2">
            <span className="text-xs text-slate-500">Notes</span>
            <textarea value={r.notes} onChange={e => set('notes', e.target.value)} rows={3} placeholder={`e.g. "Left voicemail. Call back tomorrow at 7pm" — system auto-schedules the callback.`} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
            {r.leadId && (
              <div className="text-[10px] text-indigo-700 mt-1">If your notes mention a callback time, the system will auto-schedule it on this lead when you save.</div>
            )}
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
          <button onClick={onCancel} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button onClick={trySave} disabled={busy} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold">{busy ? 'Scheduling…' : (initial ? 'Save changes' : 'Log call')}</button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Leads — full lead management with spend/book dashboard, pipeline
// funnel, source breakdown, attempt distribution, kanban + table views,
// outreach logging, CSV bulk import, and two-way calendar sync.
// Pipeline: New → Contacted → Interested → Appt Set → Showed → Closed
//           (+ dead-end statuses: No Show, DNC, Recycled)
// ──────────────────────────────────────────────────────────────────────
// Pipeline stages, ordered New Prospect → Closed. Labels are display-only; the
// `id` is the stored value, so renaming these never touches existing lead data.
const LEAD_STAGES = [
  { id: 'new',         label: 'New Prospect',    color: 'slate'   },
  { id: 'contacted',   label: 'Contacted',       color: 'blue'    },
  { id: 'interested',  label: 'Interested',      color: 'sky'     },
  { id: 'appt_set',    label: 'Appointment Set', color: 'amber'   },
  { id: 'showed',      label: 'Presented',       color: 'violet'  },
  { id: 'closed',      label: 'Closed / Won',    color: 'emerald' },
  { id: 'no_show',     label: 'No Show',         color: 'orange'  },
  { id: 'dnc',         label: 'Do Not Contact',  color: 'rose'    },
  { id: 'recycled',    label: 'Recycled',        color: 'slate'   },
];
const LEAD_STAGE_BY_ID = Object.fromEntries(LEAD_STAGES.map(s => [s.id, s]));
const LEAD_FUNNEL_ORDER = ['new','contacted','interested','appt_set','showed','closed'];

const OUTREACH_TYPES = [
  { id: 'call',      label: 'Call'      },
  { id: 'text',      label: 'Text'      },
  { id: 'email',     label: 'Email'     },
  { id: 'voicemail', label: 'Voicemail' },
];

// ── Product Type ──────────────────────────────────────────────────
// Every transaction resolves to one of these via getProduct(tx) at display time.
// "Other" is a real bucket, not "unknown" — it's the catch-all when neither the
// carrier-specific rule nor the POLICY_PLAN backfill map identifies a product.
const PRODUCT_OPTIONS = ['IUL', 'Final Expense', 'Term', 'Other'];

// POLICY_PLAN: backfill map for seeded/historical MOO transactions that were
// stored before the parser captured planCode. Populate from the "Plan Code"
// column on the DETAILS sheet of each MOO .xlsx the agency has ever uploaded.
// Example entries (replace with real data after one round of resolution):
//   'BU6457333': 'IXEXP1',   // IUL — Income Advantage
//   'BU6441753': 'FEPL1',    // Final Expense Living Promise Level
//   'BU6451772': 'TE15 1',   // 15-year Term
// New MOO uploads land planCode directly on tx.planCode so they bypass this map.
const POLICY_PLAN = {
  // Add MOO policy → plan code overrides here.
};

// Structured lead-source values used by the source filter + performance
// breakdown. The raw lead.source field is still free-form for CSV imports
// (e.g. "Facebook", "TikTok"); leadSource is the normalized category.
const LEAD_SOURCES = [
  { id: 'Facebook Ad',     label: 'Facebook Ad'    },
  { id: 'VA Booked',       label: 'VA Booked'      },
  { id: 'Referral',        label: 'Referral'       },
  { id: 'Self-Generated',  label: 'Self-Generated' },
  { id: 'Other',           label: 'Other'          },
];

// Structured outcomes for the "Log Interaction" form in the lead detail drawer.
const INTERACTION_OUTCOMES = [
  'No Answer',
  'Left Voicemail',
  'Spoke — Not Interested',
  'Spoke — Callback Requested',
  'Spoke — Interested',
  'Spoke — App Started',
  'Wrong Number',
  'Do Not Call',
];

// Suggested tags shown as one-click quick-adds in the lead detail drawer.
const SUGGESTED_TAGS = ['Hot ', 'Call Back', 'Needs Spouse', 'Price Sensitive', 'Already Has Coverage', 'No Answer x3', 'Left Voicemail', 'Texted'];

// ── Lead-data helpers (pure functions; safe to call from anywhere) ───
// Lead score 1–5 derived from the lead's notes and metadata. Cap 5 / floor 1.
const calcLeadScore = (lead) => {
  if (!lead) return 1;
  const notes = (lead.notes || '').toLowerCase();
  let s = 1;
  // Budget range
  if (/\$500\+|\$500 \+|500\s?\+/.test(notes))             s += 2;
  else if (/\$300\s?[–\-]\s?\$?499|300\s?[–\-]\s?499/.test(notes)) s += 1;
  // IUL Goal
  if (/financial freedom|\bboth\b/.test(notes))            s += 1;
  // Employment
  if (/(working\s*-?\s*w2|w-?2\b|self-?employed)/.test(notes)) s += 1;
  // Age (35-55 IUL sweet spot)
  const ageMatch = notes.match(/(?:age|aged?)[: ]+(\d{2})|\b(\d{2})\s*(?:yo|y\/o|years old)/);
  const ageVal = ageMatch ? Number(ageMatch[1] || ageMatch[2]) : null;
  if (ageVal != null && ageVal >= 35 && ageVal <= 55)      s += 1;
  // Recency
  const created = lead.createdAt ? new Date(lead.createdAt).getTime() : 0;
  const days = created > 0 ? Math.floor((Date.now() - created) / (1000 * 60 * 60 * 24)) : Infinity;
  if (days <= 7)      s += 1;
  else if (days > 30) s -= 1;
  return Math.max(1, Math.min(5, s));
};

const SCORE_COLOR = (n) => n >= 5 ? 'emerald' : n >= 3 ? 'amber' : 'slate';

// explainLeadScore — surfaces the WHY behind a leadScore so the operator
// can see what the model is reading. Per audit response §9 Phase 3
// "Opaque lead scoring" + §15 Phase 5b LEADS "AI lead scoring explanation."
// Returns a list of factor strings; render as a tooltip (`title=`) or
// expanded popover. Mirrors calcLeadScore() exactly — if you change one,
// change the other.
const explainLeadScore = (lead) => {
  if (!lead) return ['No lead data — defaulting to score 1.'];
  const notes = (lead.notes || '').toLowerCase();
  const factors = [];
  // Budget
  if (/\$500\+|\$500 \+|500\s?\+/.test(notes))                   factors.push('+2 budget $500+/mo');
  else if (/\$300\s?[–\-]\s?\$?499|300\s?[–\-]\s?499/.test(notes)) factors.push('+1 budget $300-499/mo');
  // IUL goal
  if (/financial freedom|\bboth\b/.test(notes))                  factors.push('+1 IUL goal (financial freedom / both)');
  // Employment
  if (/(working\s*-?\s*w2|w-?2\b|self-?employed)/.test(notes))   factors.push('+1 stable employment (W-2 / self-employed)');
  // Age sweet spot
  const ageMatch = notes.match(/(?:age|aged?)[: ]+(\d{2})|\b(\d{2})\s*(?:yo|y\/o|years old)/);
  const ageVal = ageMatch ? Number(ageMatch[1] || ageMatch[2]) : null;
  if (ageVal != null && ageVal >= 35 && ageVal <= 55)            factors.push(`+1 IUL sweet spot (age ${ageVal})`);
  // Recency
  const created = lead.createdAt ? new Date(lead.createdAt).getTime() : 0;
  const days = created > 0 ? Math.floor((Date.now() - created) / (1000 * 60 * 60 * 24)) : Infinity;
  if (days <= 7)                                                  factors.push('+1 recent (≤7 days old)');
  else if (days > 30)                                             factors.push('-1 stale (>30 days old)');
  if (factors.length === 0) factors.push('Base score 1 — no positive signals in notes.');
  return factors;
};

const daysInStage = (lead) => {
  const ref = lead.statusChangedAt || lead.createdAt;
  if (!ref) return 0;
  const ts = new Date(ref).getTime();
  if (!Number.isFinite(ts)) return 0;
  return Math.floor((Date.now() - ts) / (1000 * 60 * 60 * 24));
};
const DEAD_STAGES = new Set(['closed', 'no_show', 'dnc', 'recycled']);
const isGoingCold = (lead) => !DEAD_STAGES.has(lead.status) && daysInStage(lead) >= 7;

const isOverdueFollowUp = (lead, today = new Date()) => {
  if (!lead.followUpDate || DEAD_STAGES.has(lead.status)) return false;
  const fu = new Date(lead.followUpDate);
  if (!Number.isFinite(fu.getTime())) return false;
  return fu.getTime() < today.getTime();
};

const isAppointmentToday = (lead, todayStr = new Date().toISOString().slice(0, 10)) => {
  return lead.appointmentDate === todayStr && !DEAD_STAGES.has(lead.status);
};

// Strip everything except digits and leading '+' so tel:/sms: URIs work
// consistently across iOS / Android dialers.
const phoneClean = (raw) => {
  if (!raw) return '';
  const s = String(raw).trim();
  const plus = s.startsWith('+') ? '+' : '';
  return plus + s.replace(/[^0-9]/g, '');
};

const applyTemplate = (body, ctx = {}) => {
  return String(body || '')
    .replace(/\[Name\]/gi,         ctx.name || 'there')
    .replace(/\[Agent Name\]/gi,   ctx.agentName || 'your agent')
    .replace(/\[Agent Phone\]/gi,  ctx.agentPhone || '')
    .replace(/\[Time\]/gi,         ctx.time || '');
};

const relativeTime = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const sec = Math.floor((Date.now() - t) / 1000);
  if (sec < 60)        return `${sec}s ago`;
  if (sec < 3600)      return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400)     return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 86400 * 7) return `${Math.floor(sec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

// ── Speed-to-lead helpers ────────────────────────────────────────
// Time from lead.createdAt (or alias `receivedAt`) until firstContactedAt.
// Returns minutes, or null if never contacted / no createdAt.
const speedToLeadMinutes = (lead) => {
  if (!lead) return null;
  const start = lead.createdAt;
  const end   = lead.firstContactedAt;
  if (!start || !end) return null;
  const a = new Date(start).getTime();
  const b = new Date(end).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return null;
  return Math.floor((b - a) / (1000 * 60));
};
// Used to size up an agent's response speed at a glance.
const SPEED_BUCKETS = [
  { id: '5m',    label: '< 5 min',  color: 'emerald', upperMin: 5      },
  { id: '30m',   label: '< 30 min', color: 'emerald', upperMin: 30     },
  { id: '2h',    label: '< 2 hr',   color: 'amber',   upperMin: 120    },
  { id: '24h',   label: '< 24 hr',  color: 'amber',   upperMin: 1440   },
  { id: 'slow',  label: '> 24 hr',  color: 'red',     upperMin: Infinity },
];
const speedBucket = (mins) => {
  if (mins == null) return null;
  return SPEED_BUCKETS.find(b => mins < b.upperMin) || SPEED_BUCKETS[SPEED_BUCKETS.length - 1];
};
const fmtSpeed = (mins) => {
  if (mins == null) return '—';
  if (mins < 1)    return '< 1 min';
  if (mins < 60)   return `${mins}m`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  const d = Math.floor(mins / 1440);
  return `${d}d ${Math.floor((mins % 1440) / 60)}h`;
};

// ── Dedupe key for CSV import ────────────────────────────────────
// Normalize phone (last 10 digits) and email (lowercase trim) so different
// formats of the same identifier collide. Two leads are "the same" if
// either key matches an existing lead's key.
const dedupePhoneKey = (raw) => {
  if (!raw) return '';
  const d = String(raw).replace(/[^0-9]/g, '');
  return d.length >= 10 ? d.slice(-10) : '';
};
const dedupeEmailKey = (raw) => String(raw || '').trim().toLowerCase();

// Default TCPA / A2P consent disclosure text. Insurance-flavored, edit-able
// per-lead. The agency can override in Settings if a different jurisdiction
// or product line needs different language.
const DEFAULT_TCPA_TEXT =
  'By providing my phone number and email, I consent to receive calls, text messages, and emails from this agency and its agents — including via automated dialing systems and prerecorded messages — regarding insurance products and related services. Message and data rates may apply. Consent is not a condition of purchase. I may opt out at any time by replying STOP.';

// Parse a budget range like "$100-$249", "$300–$499", "$500+" out of free-text
// notes and return the midpoint of the monthly premium for pipeline-value math.
const parseBudgetMidpoint = (notes) => {
  if (!notes) return 0;
  const s = String(notes).toLowerCase();
  if (/\$500\+|\$500 \+|500\s?\+/.test(s))                       return 600;
  if (/\$300\s?[–\-]\s?\$?499|300\s?[–\-]\s?499/.test(s))        return 400;
  if (/\$250\s?[–\-]\s?\$?299|250\s?[–\-]\s?299/.test(s))        return 275;
  if (/\$100\s?[–\-]\s?\$?249|100\s?[–\-]\s?249/.test(s))        return 175;
  return 0;
};

// Infer sector for a lead from any signal we have. Used at CSV import
// and on every upsert so a lead carrying age=72 doesn't sit in the
// ACA pipeline by accident. Explicit sector value always wins.
//   - Explicit field             → use it
//   - age >= 65                  → medicare
//   - DOB → age >= 65            → medicare
//   - coverageType / interestedIn mentions Medicare → medicare
//   - default                    → aca_private
function inferLeadSector({ sector, age, dob, coverageType, interestedIn, leadType }) {
  const explicit = String(sector || '').toLowerCase();
  if (explicit === 'medicare' || explicit === 'aca_private') return explicit;
  const ageNum = Number(age);
  if (Number.isFinite(ageNum) && ageNum >= 65) return 'medicare';
  if (dob) {
    const d = new Date(String(dob).slice(0, 10) + 'T00:00:00');
    if (!Number.isNaN(d.getTime())) {
      const yrs = (Date.now() - d.getTime()) / (365.25 * 86400000);
      if (yrs >= 65) return 'medicare';
    }
  }
  const blob = `${coverageType || ''} ${interestedIn || ''} ${leadType || ''}`.toLowerCase();
  if (blob.includes('medicare') || blob.includes('medigap') || blob.includes('mapd') || blob.includes('snp')) return 'medicare';
  return 'aca_private';
}

function LeadsView(rawProps) {
  // BUG-001 root-cause guard.
  //
  // Destructuring defaults (leads = []) only fire for `undefined`;
  // if a parent passes null or an object (which happened at least
  // once on a demo/health-only account per the July 2026 audit),
  // the value survives and every subsequent .filter() call throws
  // "c.filter is not a function", killing the whole tab (and,
  // pre-TabBoundary, the whole app root).
  //
  // Coerce every array-typed prop explicitly here. Object props get
  // the same treatment. Costs nothing; buys robustness.
  const arr = (v) => (Array.isArray(v) ? v : []);
  const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
  const {
    leads:            _leads,
    setLeads,
    outreach:         _outreach,
    setOutreach,
    calls:            _calls,
    setCalls,
    calendar:         _calendar,
    setCalendar,
    leadTemplates:    _leadTemplates,
    setLeadTemplates = () => {},
    settings:         _settings,
    producers:        _producers,
    producerList:     _producerList,
    currentUser = null,
    showToast = () => {},
    workflows:        _workflows,
    onGotoTab = () => {},
    defaultVertical = 'life',
    transactions:     _transactions,
  } = rawProps || {};
  const leads         = arr(_leads);
  const outreach      = arr(_outreach);
  const calls         = arr(_calls);
  const calendar      = arr(_calendar);
  const leadTemplates = arr(_leadTemplates);
  const producerList  = arr(_producerList);
  const workflows     = arr(_workflows);
  const transactions  = arr(_transactions);
  const settings      = obj(_settings);
  const producers     = obj(_producers);
  // 'today' (card list) reads cleanest on phone — default to it on
  // mobile so first-touch users never see the wide desktop table.
  const [viewMode, setViewMode] = useState('today'); // 'today' | 'kanban' | 'table'
  const [editingLead, setEditingLead] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [csvOpen, setCsvOpen] = useState(false);
  const [powerDialBusy, setPowerDialBusy] = useState(false);
  // Derive "the leads that came in on the most recent CSV import" so the
  // operator can roll back a bad batch in one click. Prefers the new
  // `importBatchId` stamp when present; falls back to a 60-second
  // createdAt cluster for legacy imports that pre-date the stamp.
  const lastImportBatch = React.useMemo(() => {
    const imported = leads.filter(l => {
      const first = Array.isArray(l.activity) ? l.activity[0] : null;
      return first && typeof first.body === 'string' && first.body.startsWith('Imported from CSV');
    });
    if (imported.length === 0) return null;
    const withStamp = imported.filter(l => l.importBatchId);
    if (withStamp.length > 0) {
      const latest = withStamp.map(l => l.importBatchId).sort().reverse()[0];
      const cluster = withStamp.filter(l => l.importBatchId === latest);
      return { ids: cluster.map(l => l.id), count: cluster.length, at: cluster[0]?.createdAt || '', kind: 'stamped' };
    }
    const sorted = imported.slice().sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
    const headTs  = new Date(sorted[0].createdAt).getTime();
    if (!Number.isFinite(headTs)) return null;
    const WINDOW_MS = 60 * 1000;
    const cluster = sorted.filter(l => {
      const t = new Date(l.createdAt).getTime();
      return Number.isFinite(t) && (headTs - t) <= WINDOW_MS;
    });
    return { ids: cluster.map(l => l.id), count: cluster.length, at: sorted[0].createdAt, kind: 'cluster' };
  }, [leads]);
  const rollbackLastImport = async () => {
    if (!lastImportBatch || lastImportBatch.count === 0) return;
    const when = lastImportBatch.at ? new Date(lastImportBatch.at).toLocaleString() : 'unknown time';
    const note = lastImportBatch.kind === 'cluster'
      ? ' (Legacy import — identified by createdAt clustering within 60 seconds.)'
      : '';
    if (!(await hqConfirm({
      title: 'Roll back the last CSV import?',
      body: `Removes ${lastImportBatch.count} lead${lastImportBatch.count === 1 ? '' : 's'} imported around ${when}.${note} This cannot be undone.`,
      confirmLabel: 'Roll back import',
      danger: true,
    }))) return;
    const idSet = new Set(lastImportBatch.ids);
    setLeads(prev => prev.filter(l => !idSet.has(l.id)));
    setOutreach(prev => prev.filter(o => !idSet.has(o.leadId)));
    setCalendar(prev => prev.filter(a => !a.leadId || !idSet.has(a.leadId)));
    showToast(`Rolled back last import — removed ${lastImportBatch.count} lead${lastImportBatch.count === 1 ? '' : 's'}`);
  };
  // Enroll-in-drip modal — set to a lead object to open.
  const [enrollingLead, setEnrollingLead] = useState(null);
  // Drag-and-drop CSVs directly onto the Leads page — opens the
  // import modal with the files pre-loaded (one or many).
  const [pageDragOver, setPageDragOver] = useState(false);
  const [pendingDropFiles, setPendingDropFiles] = useState(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [smsTargetLead, setSmsTargetLead] = useState(null); // lead being SMS'd via template picker
  // Listen for SMS fallback requests dispatched by the global CallWidget
  // when an operator wants to text instead. Matches the leadId we have
  // locally if present; otherwise falls through to the lead object.
  React.useEffect(() => {
    const handler = (e) => {
      const { leadId, lead } = e?.detail || {};
      if (leadId) { setSmsTargetLead(leadId); return; }
      if (lead?.id) setSmsTargetLead(lead.id);
    };
    window.addEventListener('crm-sms-request', handler);
    return () => window.removeEventListener('crm-sms-request', handler);
  }, []);
  const [bellOpen, setBellOpen] = useState(false);
  const [apptPrompt, setApptPrompt] = useState(null);
  const [search, setSearch] = useState('');
  const [agentFilter, setAgentFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [leadSourceFilter, setLeadSourceFilter] = useState('all'); // normalized LEAD_SOURCES
  const [statusFilter, setStatusFilter] = useState(() => {
    // The HealthShell sidebar PIPELINE rail stashes a chosen status in
    // sessionStorage when it navigates here. Consume it once on mount,
    // then clear so a manual re-navigation doesn't get stale.
    try {
      const fromShell = sessionStorage.getItem('health.pipeline.statusFilter');
      if (fromShell != null) {
        sessionStorage.removeItem('health.pipeline.statusFilter');
        if (fromShell) return fromShell;
      }
    } catch {}
    return 'all';
  });
  const [tagFilter, setTagFilter] = useState('all');
  const [staleFilter, setStaleFilter] = useState('all'); // 'all' | '7d' | '14d' | 'never'
  // Lead type filter — sorts by what the lead was shopping for so the
  // agent can work the mortgage protection batch separately from IUL etc.
  const [leadTypeFilter, setLeadTypeFilter] = useState('all'); // 'all' | 'mortgage_protection' | 'iul' | 'general'
  // v2 sector filter: 'all' | 'aca_private' | 'medicare'.
  const [sectorFilter, setSectorFilter] = useState('all');
  const [logCallFor, setLogCallFor] = useState(null);    // leadId to prefill CallFormModal
  const [selectedIds, setSelectedIds] = useState({});    // { [leadId]: true } for bulk actions

  const agentChoices = React.useMemo(() => {
    const byKey = {};
    // Same as CallsView — route through getProducerName so the
    // KNOWN_PRODUCERS names (and stored producer names) win over
    // the raw prodNum fallback.
    Object.keys(producers || {}).forEach(pn => { if (pn) byKey[pn] = { prodNum: pn, name: getProducerName(pn, producers) }; });
    (producerList || []).forEach(p => { if (p?.prodNum && !byKey[p.prodNum]) byKey[p.prodNum] = { prodNum: p.prodNum, name: getProducerName(p.prodNum, producers) }; });
    const isArtifact = (pn) => /-(TOTAL|UNKNOWN|SUBTOTAL)$/i.test(pn || '');
    return Object.values(byKey)
      .filter(a => !isArtifact(a.prodNum))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [producers, producerList]);
  const agentName = (pn) => agentChoices.find(a => a.prodNum === pn)?.name || getProducerName(pn, producers) || '—';

  const sources = React.useMemo(() => Array.from(new Set(leads.map(l => l.source).filter(Boolean))).sort(), [leads]);
  const allTags = React.useMemo(() => {
    const s = new Set();
    leads.forEach(l => (l.tags || []).forEach(t => s.add(t)));
    return Array.from(s).sort();
  }, [leads]);
  const newId = () => (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `lead_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const nowIso = () => new Date().toISOString();

  const lc = search.trim().toLowerCase();
  const matches = (l) => {
    if (l.deletedAt) return false; // hidden until restored from Recycle bin
    if (agentFilter !== 'all' && l.agentProdNum !== agentFilter) return false;
    if (sourceFilter !== 'all' && l.source !== sourceFilter) return false;
    if (leadSourceFilter !== 'all' && l.leadSource !== leadSourceFilter) return false;
    if (statusFilter !== 'all' && l.status !== statusFilter) return false;
    if (leadTypeFilter !== 'all' && (l.leadType || 'general') !== leadTypeFilter) return false;
    // 4-way vertical+sector filter:
    //   'all'        → no filter
    //   'life'       → vertical === 'life'
    //   'health'     → vertical === 'health' (any sector)
    //   'aca_private'→ vertical === 'health' AND sector === 'aca_private'
    //   'medicare'   → vertical === 'health' AND sector === 'medicare'
    if (sectorFilter !== 'all') {
      const vert = l.vertical || 'life';
      const sec  = l.sector;
      if (sectorFilter === 'life'        && vert !== 'life')                         return false;
      if (sectorFilter === 'health'      && vert !== 'health')                       return false;
      if (sectorFilter === 'aca_private' && (vert !== 'health' || sec !== 'aca_private')) return false;
      if (sectorFilter === 'medicare'    && (vert !== 'health' || sec !== 'medicare'))    return false;
    }
    if (tagFilter !== 'all' && !(l.tags || []).includes(tagFilter)) return false;
    if (staleFilter !== 'all') {
      const days = daysSinceContact(l.id);
      if (staleFilter === 'never' && days !== Infinity) return false;
      if (staleFilter === '7d' && !(days >= 7)) return false;
      if (staleFilter === '14d' && !(days >= 14)) return false;
    }
    if (lc) {
      const blob = `${l.name} ${l.phone} ${l.email} ${l.notes} ${l.coverageType} ${l.source} ${(l.tags || []).join(' ')}`.toLowerCase();
      if (!blob.includes(lc)) return false;
    }
    return true;
  };
  const visibleLeads = leads.filter(matches);
  const visibleIds = React.useMemo(() => new Set(visibleLeads.map(l => l.id)), [visibleLeads]);
  React.useEffect(() => {
    // Drop selections for leads that are no longer visible (filter changed)
    setSelectedIds(prev => {
      const next = {};
      Object.keys(prev).forEach(id => { if (visibleIds.has(id)) next[id] = true; });
      return next;
    });
  }, [visibleIds]);

  const outreachForLead = (leadId) => outreach.filter(o => o.leadId === leadId).sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
  const callsForLead = (leadId) => calls.filter(c => c.leadId === leadId).sort((a, b) => `${b.date || ''} ${b.startTime || ''}`.localeCompare(`${a.date || ''} ${a.startTime || ''}`));
  const callCountFor = (leadId) => calls.filter(c => c.leadId === leadId).length;
  const attemptsForLead = (leadId) => outreach.filter(o => o.leadId === leadId).length + callCountFor(leadId);
  const lastCallFor = (leadId) => {
    const list = callsForLead(leadId);
    return list.length > 0 ? (list[0].date || '') : '';
  };
  // Last contact across outreach + calls — used for stale-lead detection.
  const lastContactedFor = (leadId) => {
    const outr = outreachForLead(leadId)[0];
    const c = callsForLead(leadId)[0];
    const outrIso = outr?.createdAt || '';
    const callIso = c ? `${c.date || ''}T${c.startTime || '00:00'}:00.000Z` : '';
    const winner = outrIso > callIso ? outrIso : callIso;
    return winner.slice(0, 10);
  };
  const daysSinceContact = (leadId) => {
    const last = lastContactedFor(leadId);
    if (!last) return Infinity;
    const lastTs = new Date(last).getTime();
    if (!Number.isFinite(lastTs)) return Infinity;
    return Math.floor((Date.now() - lastTs) / (1000 * 60 * 60 * 24));
  };

  const upsertLead = (rec) => {
    const isNew = !rec.id;
    const id = rec.id || newId();
    const now = nowIso();
    const existing = leads.find(l => l.id === id);
    const activity = existing?.activity ? existing.activity.slice() : [];
    if (isNew) activity.push({ at: now, type: 'created', body: `Lead created${rec.agentProdNum ? ` and assigned to ${agentName(rec.agentProdNum)}` : ''}` });
    const next = {
      id,
      name: (rec.name || '').trim(),
      phone: (rec.phone || '').trim(),
      email: (rec.email || '').trim(),
      source: (rec.source || '').trim(),
      agentProdNum: rec.agentProdNum || '',
      coverageType: (rec.coverageType || '').trim(),
      // v2 — vertical is the spine of cross-vertical isolation.
      // Health Portal filters strictly on this; life-vertical leads
      // never bleed into the health Inbox/Pipeline.
      //   Priority: explicit rec.vertical wins.
      //   Else: if sector touches a Medicare/ACA value → 'health'.
      //   Else: existing record's vertical, else 'life'.
      vertical: rec.vertical === 'health' || rec.vertical === 'life'
        ? rec.vertical
        : (rec.sector === 'medicare' || rec.sector === 'aca_private'
            ? (existing?.vertical || 'health')
            : (existing?.vertical || 'life')),
      // v2 SECTOR + demographics + Medicare-specific fields.
      // Sector survives convert-to-client so the new client record
      // starts on the right side of the compliance gate.
      sector: inferLeadSector({
        sector: rec.sector || existing?.sector,
        age: rec.age ?? existing?.age,
        dob: rec.dob || existing?.dob,
        coverageType: rec.coverageType || existing?.coverageType,
        interestedIn: rec.interestedIn || existing?.interestedIn,
        leadType: rec.leadType || existing?.leadType,
      }),
      dob: rec.dob || existing?.dob || '',
      // Auto-mirror birthday from dob so the Birthday workflow +
      // {age} merge field (which read lead.birthday) keep working
      // after removing the redundant "Birthday" input from the UI.
      // Explicit override still respected if the caller sets birthday.
      birthday: rec.birthday || existing?.birthday || rec.dob || existing?.dob || '',
      age: rec.age || existing?.age || '',
      gender: rec.gender || existing?.gender || '',
      state: rec.state || existing?.state || '',
      mbi: rec.mbi !== undefined ? String(rec.mbi || '').trim() : (existing?.mbi || ''),
      partAEffective: rec.partAEffective || existing?.partAEffective || '',
      partBEffective: rec.partBEffective || existing?.partBEffective || '',
      lisDualStatus: rec.lisDualStatus || existing?.lisDualStatus || '',
      currentCarrier: rec.currentCarrier || existing?.currentCarrier || '',
      currentPlanType: rec.currentPlanType || existing?.currentPlanType || '',
      optInSms: rec.optInSms !== undefined ? !!rec.optInSms : !!existing?.optInSms,
      ptc: rec.ptc !== undefined ? !!rec.ptc : !!existing?.ptc,
      estPremium: Number(rec.estPremium) || 0,
      cost: Number(rec.cost) || 0,
      premium: Number(rec.premium) || 0,
      lapsed: !!rec.lapsed,
      status: rec.status || existing?.status || 'new',
      tags: Array.isArray(rec.tags) ? rec.tags.map(t => String(t).trim()).filter(Boolean) : (existing?.tags || []),
      leadSource: rec.leadSource || existing?.leadSource || '',
      followUpDate: rec.followUpDate || existing?.followUpDate || '',
      // statusChangedAt: bump only when the status actually changed.
      statusChangedAt: (rec.status && existing && rec.status !== existing.status)
        ? now
        : (existing?.statusChangedAt || now),
      // Speed-to-lead: never overwrite a stamped firstContactedAt; first
      // outreach/call writes it via stampFirstContact() below.
      firstContactedAt: rec.firstContactedAt || existing?.firstContactedAt || '',
      // TCPA consent: only set consentAt when consentText is being captured
      // for the first time, or when the agent explicitly re-captures it.
      consentText:   rec.consentText !== undefined ? String(rec.consentText || '').slice(0, 2000) : (existing?.consentText   || ''),
      consentSource: rec.consentSource || existing?.consentSource || '',
      consentAt: (() => {
        const newText = rec.consentText !== undefined ? String(rec.consentText || '') : null;
        const hadConsent = !!(existing?.consentAt);
        const hasNewText = newText && newText.trim().length > 0;
        if (hasNewText && !hadConsent) return now;     // first capture
        if (hasNewText && rec._reCapture) return now;  // explicit re-capture
        return existing?.consentAt || (hasNewText ? now : '');
      })(),
      // BOOLEAN consent mirror — required by /api/sms/send-manual which
      // strictly checks `lead.consent === true`. The form captures TCPA
      // via `hasConsent`/`consentText`, and every downstream UI (Inbox
      // send button, dashboard send-status pill, etc) treats `consentAt`
      // as authoritative. Server didn't. Without this mirror, ticking
      // TCPA in the LeadForm let the UI enable Send but the server
      // returned 412 "lead consent missing" on every attempt. Fix per
      // launch-eve audit 2026-07-12.
      consent: (() => {
        const explicit = rec.consent === true || rec.hasConsent === true;
        const hasNewText = rec.consentText !== undefined
          && String(rec.consentText || '').trim().length > 0;
        if (explicit || hasNewText) return true;
        if (rec.consent === false || rec.hasConsent === false) return false;
        return existing?.consent === true;
      })(),
      optOut:       rec.optOut       !== undefined ? !!rec.optOut       : !!existing?.optOut,
      optOutAt:     rec.optOutAt     || existing?.optOutAt    || '',
      optOutReason: rec.optOutReason || existing?.optOutReason || '',
      appointmentDate: rec.appointmentDate || existing?.appointmentDate || '',
      appointmentTime: rec.appointmentTime || existing?.appointmentTime || '',
      calendarEventId: existing?.calendarEventId || '',
      notes: rec.notes || '',
      activity,
      createdAt: existing?.createdAt || now,
      updatedAt: now,
    };
    setLeads(prev => {
      const i = prev.findIndex(l => l.id === id);
      if (i === -1) return [...prev, next];
      return prev.map(l => l.id === id ? next : l);
    });

    // Auto-enroll into trigger-matched workflows on transitions:
    //  - status moves to 'appt_set' (or appointmentDate gets filled)
    //    → trigger='appointment_set' workflows
    //  - status moves to 'no_show'
    //    → trigger='no_show' workflows
    // Browser-driven because the operator's action is the source of
    // truth here. Errors are swallowed — workflows opting in to fire
    // when SMS is dry-run is not a critical path.
    try {
      const prevStatus = existing?.status || null;
      const prevApptDate = existing?.appointmentDate || '';
      if (next.status === 'appt_set' && (prevStatus !== 'appt_set' || (next.appointmentDate && next.appointmentDate !== prevApptDate))) {
        enrollLeadInTriggerWorkflows(id, 'appointment_set', currentUser?.id);
      }
      if (next.status === 'no_show' && prevStatus !== 'no_show') {
        enrollLeadInTriggerWorkflows(id, 'no_show', currentUser?.id);
      }

      // W8 Warm-Lead Alert — fire when the lead transitions INTO a
      // warm status. Each warm milestone fires its own alert so the
      // agent sees "→ Interested" and later "→ Appt set" separately.
      const WARM = new Set(['interested', 'appt_set']);
      if (WARM.has(next.status) && next.status !== prevStatus) {
        showToast(`Warm lead: ${next.name || 'lead'} → ${next.status === 'interested' ? 'Interested' : 'Appt set'}`);
        fireWarmLeadAlert({ leadId: id, leadName: next.name, status: next.status });
      }
    } catch {}

    return next;
  };

  // Soft delete with a 30-day recycle window. Lead rows stay in
  // storage but get a deletedAt stamp + are hidden everywhere by
  // the matches() filter (which now also tests !l.deletedAt). The
  // recycle bin (below) lets the operator restore or hard-delete.
  //
  // Calendar entries linked to the deleted lead also get hidden by
  // adding the lead's id to a "softDeletedLeadIds" lookup the calendar
  // view consults — we don't actually remove the calendar row so a
  // restore puts everything back.
  const RECYCLE_RETENTION_DAYS = 30;
  const deleteLead = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;
    const now = nowIso();
    setLeads(prev => prev.map(l => l.id === leadId ? {
      ...l,
      deletedAt: now,
      activity: [...(l.activity || []), { at: now, type: 'deleted', body: 'Soft-deleted — recoverable from Recycle bin for 30 days' }],
    } : l));
    if (detailId === leadId) setDetailId(null);
    showToast('Moved to Recycle bin (restorable for 30 days)');
  };
  const restoreLead = (leadId) => {
    setLeads(prev => prev.map(l => l.id === leadId ? {
      ...l,
      deletedAt: null,
      activity: [...(l.activity || []), { at: nowIso(), type: 'restored', body: 'Restored from Recycle bin' }],
    } : l));
    showToast('Lead restored');
  };
  const hardDeleteLead = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    if (lead?.calendarEventId) setCalendar(prev => prev.filter(a => a.id !== lead.calendarEventId));
    setLeads(prev => prev.filter(l => l.id !== leadId));
    setOutreach(prev => prev.filter(o => o.leadId !== leadId));
    if (detailId === leadId) setDetailId(null);
    showToast('Lead permanently deleted');
  };
  // Janitor — purge soft-deleted leads older than the retention
  // window on every render that has any. Runs cheaply because the
  // filter walks each lead exactly once. The 30-day window matches
  // what most CRMs offer; tighten via RECYCLE_RETENTION_DAYS if a
  // compliance review pushes for shorter.
  React.useEffect(() => {
    const cutoff = Date.now() - RECYCLE_RETENTION_DAYS * 86_400_000;
    const expired = leads.filter(l => l.deletedAt && new Date(l.deletedAt).getTime() < cutoff);
    if (expired.length === 0) return;
    const expiredIds = new Set(expired.map(l => l.id));
    setLeads(prev => prev.filter(l => !expiredIds.has(l.id)));
    setOutreach(prev => prev.filter(o => !expiredIds.has(o.leadId)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [recycleBinOpen, setRecycleBinOpen] = useState(false);

  // ── Convert Lead → Client ──────────────────────────────────────────
  // Mints a row in public.clients carrying the lead's sector spine +
  // demographics + Medicare fields, so the new client starts on the
  // correct side of the compliance gate. The lead is NOT deleted —
  // it's marked converted with a back-pointer so the agent can still
  // see its history (calls, outreach, TCPA consent).
  const convertLeadToClient = async (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    if (!lead) { showToast('Lead not found.'); return; }
    const ownerId = currentUser?.id;
    if (!ownerId) { showToast('Not signed in.'); return; }
    if (lead.convertedClientId) { showToast('Already converted.'); return; }
    if (!lead.name?.trim()) { showToast('Name required to convert.'); return; }

    const sector = inferLeadSector({
      sector: lead.sector, age: lead.age, dob: lead.dob,
      coverageType: lead.coverageType, interestedIn: lead.interestedIn, leadType: lead.leadType,
    });

    const dob = (lead.dob || '').slice(0, 10) || null;
    const turn65 = dob ? deriveTurn65(dob) : null;

    const clientRow = {
      owner_id: ownerId,
      name: lead.name.trim(),
      phone: normalizeClientPhone(lead.phone) || null,
      email: (lead.email || '').trim() || null,
      timezone: 'America/Chicago',
      prod_num: (lead.agentProdNum || '').trim() || null,
      carrier:  (lead.currentCarrier || '').trim() || null,
      billing_mode: 'monthly',
      billing_status: 'active',
      // v2 — vertical follows lead.vertical, falling back to 'health'
      // when the lead carries Medicare or ACA sector. Keeps health
      // book cleanly separated from life book in HealthPortalView.
      vertical: lead.vertical === 'health' || sector === 'medicare' || sector === 'aca_private' ? 'health' : 'life',
      sector,
      dob,
      state_abbr: (lead.state || '').trim().toUpperCase() || null,
      sex: lead.gender || null,
      mbi: (lead.mbi || '').trim() || null,
      part_a_effective: lead.partAEffective || null,
      part_b_effective: lead.partBEffective || null,
      turn65_date: turn65,
      current_plan_type: lead.currentPlanType || null,
      current_carrier:   (lead.currentCarrier || '').trim() || null,
      lis_dual_status:   lead.lisDualStatus || null,
      opt_in_sms:           !!lead.optInSms,
      permission_to_contact:!!lead.ptc,
    };

    const { data, error } = await supabase
      .from('clients')
      .insert(clientRow)
      .select()
      .single();
    if (error) { showToast(`Convert failed: ${error.message}`); return; }

    // Mark the lead as converted with a back-pointer. Don't change
    // status — the agent may want to keep working it for cross-sell.
    setLeads(prev => prev.map(l => l.id === leadId ? {
      ...l,
      convertedClientId: data.id,
      convertedAt: nowIso(),
      activity: [...(l.activity || []), { at: nowIso(), type: 'converted', body: `Converted to client (sector=${sector})` }],
    } : l));
    showToast(`✓ Converted → ${sector === 'medicare' ? 'Medicare client' : 'ACA/Private client'}`);
  };

  const setLeadField = (leadId, patch, activityEntry) => {
    // Capture the pre-change lead so we can detect status transitions
    // (W8 warm alert + W9 disposition→stage parent enrollments) AFTER
    // setLeads runs. setLeads is functional so we can't read the new
    // value synchronously — but the patch tells us what's about to
    // change, which is enough to fire the right side effects.
    const prevLead = leads.find(l => l.id === leadId);

    setLeads(prev => prev.map(l => {
      if (l.id !== leadId) return l;
      const statusChanged = patch.status && patch.status !== l.status;
      return {
        ...l,
        ...patch,
        statusChangedAt: statusChanged ? nowIso() : l.statusChangedAt,
        updatedAt: nowIso(),
        activity: activityEntry ? [...(l.activity || []), { at: nowIso(), ...activityEntry }] : (l.activity || []),
      };
    }));

    // ── W8 Warm-Lead Alert ─────────────────────────────────────────
    if (prevLead && patch.status && patch.status !== prevLead.status) {
      const WARM = new Set(['interested', 'appt_set']);
      if (WARM.has(patch.status)) {
        showToast(`Warm lead: ${prevLead.name || 'lead'} → ${patch.status === 'interested' ? 'Interested' : 'Appt set'}`);
        fireWarmLeadAlert({ leadId, leadName: prevLead.name, status: patch.status });
      }
      // ── W8 also enrolls into appointment_set workflows here so
      // inline status moves (kanban drag, "Move forward" select) get
      // the same reminder chain as the lead form would.
      if (patch.status === 'appt_set' && prevLead.status !== 'appt_set') {
        enrollLeadInTriggerWorkflows(leadId, 'appointment_set', currentUser?.id);
      }
      if (patch.status === 'no_show' && prevLead.status !== 'no_show') {
        enrollLeadInTriggerWorkflows(leadId, 'no_show', currentUser?.id);
      }
    }
  };

  const syncCalendarForLead = (lead, date, time) => {
    if (!date) return null;
    const userTag = currentUser ? { username: currentUser.prodNum, role: currentUser.role, name: currentUser.name } : null;
    const record = {
      leadId: lead.id,
      prospectName: lead.name,
      prospectPhone: lead.phone,
      prospectEmail: lead.email,
      agentProdNum: lead.agentProdNum,
      appointmentDate: date,
      appointmentTime: time || '',
      type: 'Appointment',
      status: 'scheduled',
      leadSource: lead.source,
      notes: lead.notes,
    };
    if (lead.calendarEventId && calendar.some(a => a.id === lead.calendarEventId)) {
      setCalendar(prev => prev.map(a => a.id === lead.calendarEventId ? { ...a, ...record, updatedAt: nowIso(), updatedByUser: userTag || a.updatedByUser } : a));
      return lead.calendarEventId;
    }
    const id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `appt-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setCalendar(prev => [...prev, { ...record, id, createdAt: nowIso(), updatedAt: nowIso(), createdByUser: userTag, updatedByUser: userTag }]);
    return id;
  };

  const moveStage = (leadId, nextStatus) => {
    const lead = leads.find(l => l.id === leadId);
    if (!lead || lead.status === nextStatus) return;
    if (nextStatus === 'appt_set') {
      setApptPrompt({ leadId, date: lead.appointmentDate || '', time: lead.appointmentTime || '' });
      return;
    }
    const prevLabel = LEAD_STAGE_BY_ID[lead.status]?.label || lead.status;
    const nextLabel = LEAD_STAGE_BY_ID[nextStatus]?.label || nextStatus;
    setLeadField(leadId, { status: nextStatus }, { type: 'status', body: `Stage: ${prevLabel} → ${nextLabel}` });
    showToast(`Moved to ${nextLabel}`);
  };

  const confirmApptSet = () => {
    if (!apptPrompt?.date) { showToast('Pick a date first'); return; }
    // BUG-015 — Time is required. A time-less appointment was
    // accepted before and produced calendar entries with no slot,
    // which made them invisible to day-view consumers.
    if (!apptPrompt?.time) { showToast('Pick a time too — calendar entries need a slot'); return; }
    const lead = leads.find(l => l.id === apptPrompt.leadId);
    if (!lead) { setApptPrompt(null); return; }
    const updatedLead = { ...lead, appointmentDate: apptPrompt.date, appointmentTime: apptPrompt.time };
    const calId = syncCalendarForLead(updatedLead, apptPrompt.date, apptPrompt.time);
    const prevLabel = LEAD_STAGE_BY_ID[lead.status]?.label || lead.status;
    setLeads(prev => prev.map(l => l.id === apptPrompt.leadId ? {
      ...l,
      status: 'appt_set',
      statusChangedAt: l.status !== 'appt_set' ? nowIso() : l.statusChangedAt,
      appointmentDate: apptPrompt.date,
      appointmentTime: apptPrompt.time || '',
      calendarEventId: calId || l.calendarEventId,
      updatedAt: nowIso(),
      activity: [
        ...(l.activity || []),
        { at: nowIso(), type: 'status', body: `Stage: ${prevLabel} → Appt Set` },
        { at: nowIso(), type: 'appt', body: `Appointment ${apptPrompt.date}${apptPrompt.time ? ' ' + apptPrompt.time : ''}` },
      ],
    } : l));
    setApptPrompt(null);
    showToast('Appointment booked on calendar');
  };

  const logOutreach = (leadId, rec) => {
    const now = nowIso();
    const oId = `or_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const lead = leads.find(l => l.id === leadId);
    setOutreach(prev => [...prev, {
      id: oId, leadId, agentProdNum: lead?.agentProdNum || '',
      date: rec.date || now.slice(0, 10),
      time: rec.time || '',
      type: rec.type, outcome: rec.outcome || '', note: rec.note || '',
      createdAt: now,
    }]);
    const willAutoAdvance = lead && lead.status === 'new';
    const activityBody = `${OUTREACH_TYPES.find(t => t.id === rec.type)?.label || rec.type}${rec.outcome ? ` · ${rec.outcome}` : ''}${rec.note ? ` — ${rec.note}` : ''}`;
    setLeads(prev => prev.map(l => l.id === leadId ? {
      ...l,
      status: willAutoAdvance ? 'contacted' : l.status,
      statusChangedAt: willAutoAdvance ? now : l.statusChangedAt,
      // Stamp first-contact timestamp the moment any outreach is logged.
      // Never overwrite — speed-to-lead is measured against THE FIRST touch.
      firstContactedAt: l.firstContactedAt || now,
      updatedAt: now,
      activity: [
        ...(l.activity || []),
        { at: now, type: 'outreach', body: activityBody },
        ...(willAutoAdvance ? [{ at: now, type: 'status', body: 'Stage: New → Contacted (first outreach)' }] : []),
      ],
    } : l));
    showToast('Outreach logged');
  };

  const togglePolicyLapsed = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;
    const nextLapsed = !lead.lapsed;
    setLeadField(leadId, { lapsed: nextLapsed }, { type: 'policy', body: nextLapsed ? 'Policy marked as lapsed' : 'Policy marked as active' });
  };

  const csvImport = (rows, opts = {}) => {
    const now = nowIso();
    const totalSpend = Number(opts.totalSpend) || 0;
    const overwriteOnMatch = !!opts.overwriteOnMatch;
    // One ID for the whole batch — stamped onto every newly-created
    // lead so "Roll back last import" can identify them precisely.
    const batchId = `imp_${Date.now()}`;
    // Per-lead cost = total $ spent on the batch / number of leads
    // being imported. Round to two decimals so the persisted number
    // doesn't carry float ugliness.
    const perLeadCost = (totalSpend > 0 && rows.length > 0)
      ? Math.round((totalSpend / rows.length) * 100) / 100
      : 0;
    const newLeads = rows.map(row => {
      // If the import row carries a TrustedForm certificate URL, the
      // lead vendor already captured TCPA consent at submission time.
      // Bake that into the lead's consent fields so outbound SMS/calls
      // don't need a second manual capture step.
      const hasTrustedForm = !!(row.trustedFormUrl || '').trim();
      // Build a one-line summary of the profitable fields for the
      // import-activity log so the agent can scan what came in.
      const profileBits = [];
      if (row.leadType === 'mortgage_protection') {
        if (row.mortgageBalance) profileBits.push(`mortgage ${row.mortgageBalance}`);
        if (row.mortgagePayment) profileBits.push(`payment ${row.mortgagePayment}`);
        if (row.healthFlag) profileBits.push(`major medical: ${row.healthFlag}`);
      } else if (row.leadType === 'iul') {
        if (row.monthlyContribution) profileBits.push(`contribution ${row.monthlyContribution}`);
        if (row.iulGoal) profileBits.push(`goal: ${row.iulGoal}`);
        if (row.employmentStatus) profileBits.push(`employment: ${row.employmentStatus}`);
      }
      const importBody = `Imported from CSV${row.leadType && row.leadType !== 'general' ? ` (${row.leadType.replace(/_/g, ' ')})` : ''}${perLeadCost > 0 ? ` · cost ${fmt(perLeadCost)}` : ''}${profileBits.length ? ' · ' + profileBits.join(' · ') : ''}`;
      return {
        id: newId(),
        name: row.name || '',
        // Defensive normalization — the CSV parser already formats
        // phone/dob, but call directly here so any other caller of
        // csvImport() also gets clean values.
        phone: fmtPhoneUS(row.phone || ''),
        email: row.email || '',
        source: row.source || 'Imported',
        agentProdNum: row.agentProdNum || '',
        coverageType: '',
        estPremium: 0, cost: perLeadCost, premium: 0, lapsed: false,
        status: 'new',
        tags: [],
        leadSource: row.leadSource || '',
        followUpDate: '',
        appointmentDate: '', appointmentTime: '', calendarEventId: '',
        notes: '',
        activity: [{ at: now, type: 'created', body: importBody }],
        statusChangedAt: now,
        firstContactedAt: '',
        // Consent: if the vendor passed a TrustedForm URL, treat as
        // captured at import time. Otherwise leave empty and force the
        // agent to capture manually before any outbound.
        consentText:  hasTrustedForm ? `TrustedForm cert: ${row.trustedFormUrl}` : '',
        consentAt:    hasTrustedForm ? now : '',
        consentSource: hasTrustedForm ? 'trustedform' : '',
        optOut: false, optOutAt: '', optOutReason: '',
        // Lead-type discriminator + carrier-specific profile fields.
        // These drive the type filter on the leads list and the
        // structured info block in the drawer.
        leadType: row.leadType || 'general',
        dob:        normalizeDob(row.dob || ''),
        age:        row.age || '',
        gender:     row.gender || '',
        state:      row.state || '',
        // Address — street goes in `address`; city/zip stand alone so
        // dialer territory rules, Lob mailers, and demographic filters
        // can read them directly. The lead table composes a single
        // line "Street, City ST Zip" at render time.
        address:    row.address || '',
        city:       row.city || '',
        zip:        row.zip || '',
        ad:         row.ad || '',
        platform:   row.platform || '',
        mortgageBalance:     row.mortgageBalance     || '',
        mortgagePayment:     row.mortgagePayment     || '',
        beneficiary:         row.beneficiary         || '',
        beneficiaryName:     row.beneficiaryName     || '',
        healthFlag:          row.healthFlag          || '',
        iulGoal:             row.iulGoal             || '',
        employmentStatus:    row.employmentStatus    || '',
        monthlyContribution: row.monthlyContribution || '',
        retirementPlan:      row.retirementPlan      || '',
        retirementAge:       row.retirementAge       || '',
        trustedFormUrl:      row.trustedFormUrl      || '',
        otpCode:             row.otpCode             || '',
        ipAddress:           row.ipAddress           || '',
        importBatchId: batchId,
        createdAt: now, updatedAt: now,
      };
    });
    // Overwrite-on-match: when the operator re-imports an already-
    // ingested batch (e.g. they want to refresh phone/address/dob
    // formatting on the existing rows), merge the new CSV data into
    // existing leads by phone (last 10 digits) or email (lowercased).
    // Fields that are non-empty in the incoming row replace whatever
    // was on the existing lead. New rows still get appended.
    let updatedCount = 0;
    let appendedCount = 0;
    setLeads(prev => {
      if (!overwriteOnMatch) {
        appendedCount = newLeads.length;
        return [...prev, ...newLeads];
      }
      const phoneKey = (s) => String(s || '').replace(/\D/g, '').slice(-10);
      const emailKey = (s) => String(s || '').trim().toLowerCase();
      const byPhone  = new Map();
      const byEmail  = new Map();
      prev.forEach((l, i) => {
        const p = phoneKey(l.phone);
        const e = emailKey(l.email);
        if (p) byPhone.set(p, i);
        if (e) byEmail.set(e, i);
      });
      const next = prev.slice();
      for (const incoming of newLeads) {
        const p = phoneKey(incoming.phone);
        const e = emailKey(incoming.email);
        let matchIdx = -1;
        if (p && byPhone.has(p))      matchIdx = byPhone.get(p);
        else if (e && byEmail.has(e)) matchIdx = byEmail.get(e);
        if (matchIdx >= 0) {
          // Merge: incoming fields overwrite, but PRESERVE the
          // existing lead's id, activity, status, agent assignment,
          // tags, consent, and history. Only refresh the demographics
          // and contact fields that re-import is meant to update.
          const existing = next[matchIdx];
          next[matchIdx] = {
            ...existing,
            name:      incoming.name      || existing.name,
            phone:     incoming.phone     || existing.phone,
            email:     incoming.email     || existing.email,
            dob:       incoming.dob       || existing.dob,
            age:       incoming.age       || existing.age,
            gender:    incoming.gender    || existing.gender,
            state:     incoming.state     || existing.state,
            address:   incoming.address   || existing.address,
            city:      incoming.city      || existing.city,
            zip:       incoming.zip       || existing.zip,
            updatedAt: now,
            activity: [
              ...(Array.isArray(existing.activity) ? existing.activity : []),
              { at: now, type: 'updated', body: 'Re-imported from CSV — demographics refreshed' },
            ],
          };
          updatedCount++;
        } else {
          next.push(incoming);
          appendedCount++;
        }
      }
      return next;
    });
    const costNote = perLeadCost > 0
      ? ` · ${fmt(totalSpend)} total spend = ${fmt(perLeadCost)}/lead`
      : '';
    const summary = overwriteOnMatch
      ? `Re-imported: ${updatedCount} updated · ${appendedCount} new${costNote}`
      : `Imported ${newLeads.length} lead${newLeads.length === 1 ? '' : 's'}${costNote}`;
    showToast(summary);
  };

  // ── Dashboard metrics ─────────────────────────────────────────────
  const totalLeads = leads.length;
  const totalCost = leads.reduce((s, l) => s + (Number(l.cost) || 0), 0);
  const costPerLead = totalLeads > 0 ? totalCost / totalLeads : 0;
  const closedLeads = leads.filter(l => l.status === 'closed');
  const policiesClosed = closedLeads.length;
  const cpa = policiesClosed > 0 ? totalCost / policiesClosed : 0;
  const onBookLeads = closedLeads.filter(l => !l.lapsed);
  const onBook = onBookLeads.length;
  const onBookPremium = onBookLeads.reduce((s, l) => s + (Number(l.premium) || 0), 0);
  const lapsedLeads = closedLeads.filter(l => l.lapsed);
  const persistencyRate = policiesClosed > 0 ? (onBook / policiesClosed) * 100 : null;
  const avgPremium = onBook > 0 ? onBookPremium / onBook : 0;
  const closedPremium = closedLeads.reduce((s, l) => s + (Number(l.premium) || 0), 0);
  const closedAnnualPremium = closedPremium * 12;
  const leadRoi = totalCost > 0 ? ((closedAnnualPremium - totalCost) / totalCost) * 100 : null;

  const contactedOrAbove = leads.filter(l => LEAD_FUNNEL_ORDER.indexOf(l.status) >= 1 || l.status === 'no_show' || l.status === 'dnc').length;
  const apptOrAbove = leads.filter(l => ['appt_set','showed','closed'].includes(l.status)).length;
  const showed = leads.filter(l => l.status === 'showed' || l.status === 'closed').length;
  const contactRate = totalLeads > 0 ? (contactedOrAbove / totalLeads) * 100 : 0;
  const apptRate = contactedOrAbove > 0 ? (apptOrAbove / contactedOrAbove) * 100 : 0;
  const showRate = apptOrAbove > 0 ? (showed / apptOrAbove) * 100 : 0;
  const closeRate = showed > 0 ? (policiesClosed / showed) * 100 : 0;
  const dncCount = leads.filter(l => l.status === 'dnc').length;
  const recycledCount = leads.filter(l => l.status === 'recycled').length;

  const allStageCounts = LEAD_STAGES.map(s => ({ stage: s, count: leads.filter(l => l.status === s.id).length }));

  const attemptBuckets = React.useMemo(() => {
    const b = { '0': 0, '1': 0, '2': 0, '3': 0, '4': 0, '5+': 0 };
    leads.forEach(l => {
      const n = outreach.filter(o => o.leadId === l.id).length;
      if (n >= 5) b['5+']++;
      else b[String(n)]++;
    });
    return Object.entries(b).map(([name, value]) => ({ name, value }));
  }, [leads, outreach]);

  const [spendBySourceOpen, setSpendBySourceOpen] = React.useState(false);

  const bySource = React.useMemo(() => {
    const m = {};
    leads.forEach(l => {
      const src = l.source || '—';
      if (!m[src]) m[src] = { source: src, leads: 0, spend: 0, closed: 0, premium: 0 };
      m[src].leads++;
      m[src].spend += Number(l.cost) || 0;
      if (l.status === 'closed') { m[src].closed++; m[src].premium += Number(l.premium) || 0; }
    });

    // Pickup rate per source — computed from the calls log, not lead
    // status. "Contact %" (lead-status-based) and "Pickup %" (dial-
    // based) tell different stories: a source with 90% contact but
    // 8% pickup means agents had to dial 12+ times to reach each lead.
    // That's a different story than 50% contact / 45% pickup, and
    // operators need both numbers to triage CPL.
    const leadIdsBySource = {};
    leads.forEach(l => {
      const src = l.source || '—';
      (leadIdsBySource[src] = leadIdsBySource[src] || new Set()).add(l.id);
    });
    const pickupBySource = {};
    (calls || []).forEach(c => {
      if (c.direction !== 'outbound') return;
      // Find the source for this call's lead.
      for (const [src, idSet] of Object.entries(leadIdsBySource)) {
        if (idSet.has(c.leadId)) {
          const slot = pickupBySource[src] = pickupBySource[src] || { dials: 0, pickups: 0 };
          slot.dials++;
          if (isContactOutcome(c.outcome)) slot.pickups++;
          break;
        }
      }
    });

    return Object.values(m).map(r => {
      const cpl = r.leads > 0 ? r.spend / r.leads : 0;
      const rCpa = r.closed > 0 ? r.spend / r.closed : 0;
      const annualPrem = r.premium * 12;
      const roi = r.spend > 0 ? ((annualPrem - r.spend) / r.spend) * 100 : null;
      const rContactRate = r.leads > 0 ? (leads.filter(l => l.source === r.source && (LEAD_FUNNEL_ORDER.indexOf(l.status) >= 1 || l.status === 'no_show' || l.status === 'dnc')).length / r.leads) * 100 : 0;
      const rCloseRate = r.leads > 0 ? (r.closed / r.leads) * 100 : 0;
      const pickup = pickupBySource[r.source] || { dials: 0, pickups: 0 };
      const rPickupRate = pickup.dials > 0 ? (pickup.pickups / pickup.dials) * 100 : null;
      return { ...r, cpl, cpa: rCpa, roi, contactRate: rContactRate, closeRate: rCloseRate, dials: pickup.dials, pickupRate: rPickupRate };
    }).sort((a, b) => b.leads - a.leads);
  }, [leads, calls]);

  const weeklyData = React.useMemo(() => {
    const out = [];
    const today = new Date();
    for (let w = 7; w >= 0; w--) {
      const start = new Date(today); start.setDate(start.getDate() - (w * 7 + 6));
      const end = new Date(today);   end.setDate(end.getDate() - w * 7);
      const startStr = start.toISOString().slice(0, 10);
      const endStr   = end.toISOString().slice(0, 10);
      const inWeek = (iso) => { const d = (iso || '').slice(0, 10); return d >= startStr && d <= endStr; };
      const weekLeads = leads.filter(l => inWeek(l.createdAt));
      const spend = weekLeads.reduce((s, l) => s + (Number(l.cost) || 0), 0);
      const closes = leads.filter(l => l.status === 'closed' && inWeek(l.updatedAt)).length;
      out.push({ week: `${start.getMonth()+1}/${start.getDate()}`, spend, closes });
    }
    return out;
  }, [leads]);

  const detailLead = leads.find(l => l.id === detailId) || null;
  const colorHex = (color) => ({ slate:'#64748b', blue:'#3b82f6', sky:'#0ea5e9', amber:'#f59e0b', violet:'#8b5cf6', emerald:'#10b981', orange:'#f97316', rose:'#f43f5e', red:'#ef4444' })[color] || '#94a3b8';

  // ── Today's Focus derived lists ──────────────────────────────────
  const todayStr = new Date().toISOString().slice(0, 10);
  const appointmentsToday = leads.filter(l => isAppointmentToday(l, todayStr));
  const overdueFollowUps = leads.filter(l => isOverdueFollowUp(l));
  const goingCold = leads.filter(l => isGoingCold(l));
  const overdueCount = overdueFollowUps.length;

  // ── Speed-to-lead metrics (org-wide + per-agent) ───────────────
  // We only count leads that have both a createdAt and a firstContactedAt
  // — uncontacted leads pull median sideways and aren't an "agent" issue,
  // they're a queue issue (surfaced separately as "never contacted" count).
  const speedByAgent = React.useMemo(() => {
    const m = {};
    leads.forEach(l => {
      const pn = l.agentProdNum || 'unassigned';
      if (!m[pn]) m[pn] = { prodNum: pn, name: pn === 'unassigned' ? 'Unassigned' : (agentName ? agentName(pn) : pn), total: 0, contacted: 0, mins: [], under5: 0, under60: 0 };
      m[pn].total++;
      const s = speedToLeadMinutes(l);
      if (s != null) {
        m[pn].contacted++;
        m[pn].mins.push(s);
        if (s < 5)  m[pn].under5++;
        if (s < 60) m[pn].under60++;
      }
    });
    return Object.values(m).map(r => {
      const sorted = r.mins.slice().sort((a, b) => a - b);
      const median = sorted.length > 0 ? sorted[Math.floor(sorted.length / 2)] : null;
      const fastest = sorted.length > 0 ? sorted[0] : null;
      return {
        ...r,
        median, fastest,
        under5Pct:  r.total > 0 ? (r.under5  / r.total) * 100 : 0,
        under60Pct: r.total > 0 ? (r.under60 / r.total) * 100 : 0,
      };
    }).sort((a, b) => {
      // Fastest median first (null medians sink to the bottom)
      const am = a.median == null ? Infinity : a.median;
      const bm = b.median == null ? Infinity : b.median;
      return am - bm;
    });
  }, [leads]); // eslint-disable-line

  const speedOverall = React.useMemo(() => {
    const all = leads.map(speedToLeadMinutes).filter(v => v != null).sort((a, b) => a - b);
    if (all.length === 0) return { median: null, count: 0, under5Pct: 0, under60Pct: 0, never: leads.length };
    const u5  = all.filter(v => v < 5).length;
    const u60 = all.filter(v => v < 60).length;
    const never = leads.filter(l => !l.firstContactedAt).length;
    return {
      median: all[Math.floor(all.length / 2)],
      count: all.length,
      under5Pct:  (u5  / leads.length) * 100,
      under60Pct: (u60 / leads.length) * 100,
      never,
    };
  }, [leads]);

  // ── Pipeline value math (active = not in dead stages) ────────────
  const activeLeads = leads.filter(l => !DEAD_STAGES.has(l.status));
  const potentialMonthlyPremium = activeLeads.reduce((s, l) => s + parseBudgetMidpoint(l.notes), 0);
  const potentialAnnualPremium = potentialMonthlyPremium * 12;
  const potentialCommission = potentialAnnualPremium * 1.05;

  // ── Lead age distribution (active leads) ─────────────────────────
  const leadAgeBuckets = React.useMemo(() => {
    const b = { '0-7d': 0, '8-14d': 0, '15-30d': 0, '31-60d': 0, '60d+': 0 };
    activeLeads.forEach(l => {
      const t = new Date(l.createdAt || 0).getTime();
      if (!Number.isFinite(t)) return;
      const d = Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24));
      if (d <= 7)       b['0-7d']++;
      else if (d <= 14) b['8-14d']++;
      else if (d <= 30) b['15-30d']++;
      else if (d <= 60) b['31-60d']++;
      else              b['60d+']++;
    });
    return [
      { name: '0–7d',   value: b['0-7d'],   fill: '#10b981' },
      { name: '8–14d',  value: b['8-14d'],  fill: '#10b981' },
      { name: '15–30d', value: b['15-30d'], fill: '#f59e0b' },
      { name: '31–60d', value: b['31-60d'], fill: '#ef4444' },
      { name: '60d+',   value: b['60d+'],   fill: '#ef4444' },
    ];
  }, [activeLeads]);
  const oldActive = leadAgeBuckets.slice(2).reduce((s, b) => s + b.value, 0);
  const oldShare = activeLeads.length > 0 ? oldActive / activeLeads.length : 0;

  // ── Conversion funnel (filtered by leadSourceFilter) ─────────────
  const funnelLeads = leadSourceFilter === 'all' ? leads : leads.filter(l => l.leadSource === leadSourceFilter);
  const funnelData = LEAD_FUNNEL_ORDER.map(sId => {
    const stage = LEAD_STAGE_BY_ID[sId];
    // Count leads that reached at least this stage in the linear funnel
    const idx = LEAD_FUNNEL_ORDER.indexOf(sId);
    const count = funnelLeads.filter(l => {
      const li = LEAD_FUNNEL_ORDER.indexOf(l.status);
      // Treat dead stages as "did not progress beyond their previous stage"
      return li >= idx;
    }).length;
    return { stage, count, fill: ({ slate: '#94a3b8', blue: '#3b82f6', sky: '#0ea5e9', amber: '#f59e0b', violet: '#8b5cf6', emerald: '#10b981' })[stage.color] || '#94a3b8' };
  });
  const funnelDropOffs = funnelData.slice(0, -1).map((row, i) => {
    const next = funnelData[i + 1];
    const proceed = row.count > 0 ? (next.count / row.count) * 100 : 0;
    return { from: row.stage.label, to: next.stage.label, proceedPct: proceed };
  });

  // Source Performance — derived from leadSource (normalized) buckets.
  const sourcePerf = React.useMemo(() => {
    const m = {};
    leads.forEach(l => {
      const src = l.leadSource || (l.source ? 'Other' : 'Other');
      if (!m[src]) m[src] = { source: src, total: 0, contacted: 0, app: 0, closed: 0, scoreSum: 0 };
      const b = m[src];
      b.total++;
      const idx = LEAD_FUNNEL_ORDER.indexOf(l.status);
      if (idx >= 1 || l.status === 'no_show' || l.status === 'dnc') b.contacted++;
      if (['interested','appt_set','showed','closed'].includes(l.status)) b.app++;
      if (l.status === 'closed') b.closed++;
      b.scoreSum += calcLeadScore(l);
    });
    return Object.values(m).map(r => ({
      ...r,
      showRate:  r.total > 0 ? (r.contacted / r.total) * 100 : 0,
      appRate:   r.total > 0 ? (r.app / r.total) * 100 : 0,
      closeRate: r.total > 0 ? (r.closed / r.total) * 100 : 0,
      avgScore:  r.total > 0 ? (r.scoreSum / r.total) : 0,
    })).sort((a, b) => b.total - a.total);
  }, [leads]);

  // ── Quick action handlers shared across views ────────────────────
  const openLogOutcome = (leadId) => setLogCallFor(leadId);
  const openSmsFor     = (leadId) => setSmsTargetLead(leadId);
  const openTel = (lead) => {
    if (!phoneClean(lead?.phone)) { showToast('No phone number on this lead'); return; }
    // Funnel through the global helper so behavior matches every other
    // Call button across the CRM (Power Dialer, SMS Inbox, Calendar,
    // Conservation, Today's Focus, Demos View, etc.).
    requestPhoneCall({ lead });
  };
  // Advance lead to the next status in the linear funnel — used by the Move
  // button on cards. Skips dead stages and respects the appt_set prompt.
  const advanceLeadNextStage = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;
    const i = LEAD_FUNNEL_ORDER.indexOf(lead.status);
    const next = i >= 0 && i < LEAD_FUNNEL_ORDER.length - 1 ? LEAD_FUNNEL_ORDER[i + 1] : null;
    if (!next) { showToast('Already at the end of the pipeline'); return; }
    moveStage(leadId, next);
  };

  // Page-level drag handlers. We only react to drags that carry a
  // file (dataTransfer.types includes 'Files') so dragging text
  // selections or lead pills around the page doesn't trigger the
  // overlay.
  const onPageDragOver = (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    if (!pageDragOver) setPageDragOver(true);
  };
  const onPageDragLeave = (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    // Only flip off when the cursor leaves the page entirely.
    if (!e.currentTarget.contains(e.relatedTarget)) setPageDragOver(false);
  };
  const onPageDrop = (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    setPageDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;
    // Stash the files and open the modal — the modal picks them up
    // via the initialFiles prop and merges them through ingestFiles.
    setPendingDropFiles(files);
    setCsvOpen(true);
  };

  // Precompute lookup maps for the intelligence provider — one
  // reduce over calls / outreach, memoized on identity so children
  // don't refetch on unrelated re-renders.
  const callsByLeadId = React.useMemo(() => {
    const m = {};
    for (const c of calls) {
      if (!c.leadId) continue;
      (m[c.leadId] = m[c.leadId] || []).push({ at: c.date || c.startTime, outcome: c.outcome });
    }
    return m;
  }, [calls]);
  const outreachByLeadId = React.useMemo(() => {
    const m = {};
    for (const o of outreach) {
      if (!o.leadId) continue;
      (m[o.leadId] = m[o.leadId] || []).push({ at: o.createdAt, type: o.type, outcome: o.outcome });
    }
    return m;
  }, [outreach]);

  return (
    <LeadIntelligenceProvider leads={leads} callsByLeadId={callsByLeadId} outreachByLeadId={outreachByLeadId}>
    <div
      className="space-y-4 relative"
      onDragOver={onPageDragOver}
      onDragEnter={onPageDragOver}
      onDragLeave={onPageDragLeave}
      onDrop={onPageDrop}
    >
      {pageDragOver && (
        <div className="fixed inset-0 z-50 bg-emerald-600/20 border-4 border-dashed border-emerald-600 flex items-center justify-center pointer-events-none">
          <div className="bg-white rounded-2xl shadow-2xl px-8 py-6 text-center">
            <div className="text-5xl mb-2"></div>
            <div className="text-xl font-bold text-emerald-700">Drop CSVs to import leads</div>
            <div className="text-sm text-slate-600 mt-1">Drag one or more files. They'll be merged in the import modal.</div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-slate-900">Leads</h2>
          <OverdueBell
            count={overdueCount}
            overdue={overdueFollowUps}
            open={bellOpen}
            onToggle={() => setBellOpen(o => !o)}
            onClose={() => setBellOpen(false)}
            agentName={agentName}
            onOpenLead={(id) => { setBellOpen(false); setDetailId(id); }}
            onCall={openTel}
          />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setTemplatesOpen(true)} className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5" title="Templates"><FileText size={14}/> Templates</button>
          <button onClick={() => setCsvOpen(true)} className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 px-3 py-2 rounded-lg text-sm font-semibold">Import CSV</button>
          {/* Recycle bin button — only when there's something to restore */}
          {leads.some(l => l.deletedAt) && (
            <button
              onClick={() => setRecycleBinOpen(true)}
              className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 px-3 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5"
              title="Restore or permanently delete soft-deleted leads (30-day window)"
            >
              ♻ Recycle bin <span className="text-[10px] bg-slate-100 text-slate-600 rounded-full px-1.5 py-0.5">{leads.filter(l => l.deletedAt).length}</span>
            </button>
          )}
          <button onClick={() => setEditingLead({ __new: true })} className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-semibold">+ New lead</button>
          {((lastImportBatch && lastImportBatch.count > 0) || leads.length > 0) && (
            <LeadsDangerMenu
              lastImportBatch={lastImportBatch}
              onRollback={rollbackLastImport}
              leadsCount={leads.length}
              onDeleteAll={() => {
                const n = leads.length;
                const typed = window.prompt(`This permanently deletes ALL ${n} lead${n === 1 ? '' : 's'} in this pipeline — every name, contact info, activity log, and follow-up.\n\nType DELETE to confirm:`);
                if (typed !== 'DELETE') return;
                const ids = new Set(leads.map(l => l.id));
                setLeads([]);
                setOutreach(prev => prev.filter(o => !ids.has(o.leadId)));
                setCalendar(prev => prev.filter(a => !a.leadId || !ids.has(a.leadId)));
                setSelectedIds({});
                showToast(`Deleted all ${ids.size} lead${ids.size === 1 ? '' : 's'} from this pipeline`);
              }}
            />
          )}
        </div>
      </div>

      {/* AI Lead Command Center banner. Data provided by the
          LeadIntelligenceProvider wrapping this LeadsView; Banner +
          every LeadCard consume from the same context so we fetch
          /api/leads/morning-brief exactly once per page render. */}
      <LeadsMorningBriefBanner
        currentUser={currentUser}
        onOpenLead={(id) => setDetailId(id)}
        onStartPowerDial={() => onGotoTab?.('dial')}
      />

      {/* AI Alerts — Phase 7 addition. Four categories (critical /
          warning / opportunity / data issue) surfacing issues
          derived from the leads array + calls + outreach. Each item
          has a Fix action button. Hides itself if there are no
          issues to flag. */}
      <LeadsAIAlertsPanel
        leads={leads}
        calls={calls}
        outreach={outreach}
        onOpenLead={(id) => setDetailId(id)}
      />

      {/* Live opportunity feed — mines lead.activity[] for demand
          moments. Rendered above the view-mode pills so it's the
          second thing the agent sees, right below the Morning
          Brief NBA. Silence beats fake activity: hides itself if
          nothing notable happened in the last 72h. */}
      <LeadsOpportunityFeed
        leads={leads}
        onOpenLead={(id) => setDetailId(id)}
      />

      {/* Forecast + Predictions + Performance — Phase 5. Collapsible;
          answers "where's revenue heading / what should I bet on /
          how am I performing today" in one strip. */}
      <LeadsForecastPanel leads={leads} calls={calls}/>

      {/* AI Fleet status — Phase 6. Dark strip showing what each
          virtual AI is doing right now. Collapsible. */}
      <LeadsAIFleetPanel/>

      {/* Floating Nova — bottom-right. Not in the layout flow.
          Portal-style; always visible while on this tab. */}
      <LeadsNovaFloatingChat leads={leads}/>

      {/* Phase 7c — AI Work This List button. Prominent bulk-action
          surface at the very top of the Leads page. Opens a preview
          modal with a dry-run plan (skipped / duplicates / calls /
          texts summary + per-lead action + sample drafted messages)
          before firing. */}
      <div className="flex items-center justify-end">
        <LeadsWorkThisListButton
          leads={visibleLeads}
          calls={calls}
          outreach={outreach}
          showToast={showToast}
        />
      </div>

      {/* Phase 7b — Workspace selector. Prominent vertical filter
          binds to sectorFilter state so Life / Health / Medicare
          agents can drop the noise from the other verticals with
          one tap. All Business is the default. */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Workspace</span>
        {[
          ['all',      'All Business', '🏢'],
          ['life',     'Life',         '💼'],
          ['health',   'Health',       '❤️'],
          ['medicare', 'Medicare',     '🩺'],
        ].map(([id, label, emoji]) => (
          <button
            key={id}
            onClick={() => setSectorFilter(id)}
            className={`px-4 py-2 text-sm font-bold rounded-lg transition-all ${
              sectorFilter === id
                ? 'bg-slate-900 text-white shadow-md'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            <span className="mr-1.5">{emoji}</span>
            {label}
            {sectorFilter === id && (
              <span className="ml-2 text-xs opacity-70 font-mono">
                · {leads.filter(l => {
                  if (id === 'all') return true;
                  const vert = String(l.vertical || '').toLowerCase();
                  if (id === 'life') return vert === 'life';
                  if (id === 'health') return vert === 'health' && l.sector !== 'medicare';
                  if (id === 'medicare') return vert === 'health' && l.sector === 'medicare';
                  return false;
                }).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* View mode pills — Phase 7 restructure: Analytics moved to
          its own tab so the default Today view isn't buried under
          charts and metrics. All the KPI wall + charts + funnels +
          source performance + speed-to-lead now live behind the
          Analytics tab. "Table" renamed to "All Leads" for plain
          language. */}
      <div className="bg-white border border-slate-200 rounded-xl p-1 inline-flex items-center gap-1 flex-wrap">
        {[
          ['today',     "Today",       overdueCount + appointmentsToday.length + goingCold.length],
          ['kanban',    'Pipeline',    null],
          ['table',     'All Leads',   null],
          ['analytics', 'Analytics',   null],
          ['sms',       'SMS Engine',  null],
        ].map(([id, label, badge]) => (
          <button key={id} onClick={() => setViewMode(id)} className={`px-3 py-1.5 text-sm font-medium rounded-lg flex items-center gap-2 ${viewMode === id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
            {label}
            {badge != null && badge > 0 && (
              <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${viewMode === id ? 'bg-white text-slate-900' : 'bg-red-100 text-red-700'}`}>{badge}</span>
            )}
          </button>
        ))}
      </div>

      {viewMode === 'sms' ? (
        <SmsEngineView currentUser={currentUser} transactions={transactions} leads={leads} setLeads={setLeads} showToast={showToast}/>
      ) : viewMode === 'today' ? (
        <TodaysFocusView
          appointments={appointmentsToday}
          overdue={overdueFollowUps}
          goingCold={goingCold}
          agentName={agentName}
          onOpenLead={(id) => setDetailId(id)}
          onLogOutcome={openLogOutcome}
          onCall={openTel}
          onSms={openSmsFor}
          onAdvance={advanceLeadNextStage}
          onMoveTo={moveStage}
          callsForLead={callsForLead}
          outreachForLead={outreachForLead}
        />
      ) : viewMode === 'table' ? (
        // 2026-06-27 — exclusive table render. Operator clicking the
        // Table tab expected to land ON the table, not scroll past
        // stats + charts to find it.
        <LeadTable
          leads={visibleLeads}
          agentName={agentName}
          attemptsForLead={attemptsForLead}
          callCountFor={callCountFor}
          lastCallFor={lastCallFor}
          lastContactedFor={lastContactedFor}
          daysSinceContact={daysSinceContact}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          onOpen={(id) => setDetailId(id)}
          onCall={openTel}
          onSms={openSmsFor}
          onLogOutcome={openLogOutcome}
          onAdvance={advanceLeadNextStage}
          onEnrollDrip={(l) => setEnrollingLead(l)}
        />
      ) : (
        <>
      {/* Phase 7 restructure: ALL the KPI wall + charts + funnels +
          speed-to-lead + source performance tables are gated behind
          the Analytics tab. Prior default was to render them above
          the pipeline (kanban) view, burying the AI briefing +
          priority queue under charts. Analytics is now an opt-in
          drill-down surface. */}
      {viewMode === 'analytics' && (
      <>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={<Users size={16}/>} label="Total Leads" value={totalLeads.toLocaleString()} sub={costPerLead > 0 ? `${fmt(costPerLead)}/lead` : '—'} color="slate"/>
        <StatCard icon={<DollarSign size={16}/>} label="Total Spent" value={fmt(totalCost)} color="amber"/>
        <StatCard icon={<CheckCircle size={16}/>} label="Policies Closed" value={policiesClosed.toLocaleString()} color="emerald"/>
        <StatCard icon={<DollarSign size={16}/>} label="Cost Per Close" value={policiesClosed > 0 ? fmt(cpa) : '—'} color="violet"/>
        <StatCard icon={<TrendingUp size={16}/>} label="Lead ROI" value={leadRoi != null ? `${leadRoi.toFixed(0)}%` : '—'} sub="annualized premium − spend" color={leadRoi != null && leadRoi >= 100 ? 'emerald' : leadRoi != null && leadRoi >= 0 ? 'amber' : 'red'}/>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={<CheckCircle size={16}/>} label="On the Books" value={onBook.toLocaleString()} sub={`${fmt(onBookPremium)}/mo total`} color="emerald"/>
        <StatCard icon={<TrendingDown size={16}/>} label="Lapsed / Cancelled" value={lapsedLeads.length.toLocaleString()} color="red"/>
        <StatCard icon={<TrendingUp size={16}/>} label="Persistency" value={persistencyRate != null ? `${persistencyRate.toFixed(0)}%` : '—'} color={persistencyRate != null && persistencyRate >= 85 ? 'emerald' : persistencyRate != null && persistencyRate >= 70 ? 'amber' : 'red'}/>
        <StatCard icon={<DollarSign size={16}/>} label="Avg Premium" value={onBook > 0 ? fmt(avgPremium) : '—'} sub="monthly · on-book only" color="slate"/>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <StatCard icon={<Users size={16}/>} label="Contact Rate" value={`${contactRate.toFixed(0)}%`} sub={`${contactedOrAbove} of ${totalLeads} contacted`} color="emerald"/>
        <StatCard icon={<CheckCircle size={16}/>} label="Appt Set" value={`${apptRate.toFixed(0)}%`} sub="of contacted" color="amber"/>
        <StatCard icon={<CheckCircle size={16}/>} label="Show Rate" value={`${showRate.toFixed(0)}%`} sub="of appts" color="violet"/>
        <StatCard icon={<TrendingUp size={16}/>} label="Close Rate" value={`${closeRate.toFixed(0)}%`} sub="of shows" color="emerald"/>
        <StatCard icon={<X size={16}/>} label="DNC" value={dncCount.toLocaleString()} color="red"/>
        <StatCard icon={<ChevronLeft size={16}/>} label="Recycled" value={recycledCount.toLocaleString()} color="slate"/>
      </div>

      {viewMode !== 'table' && leads.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card title="Weekly lead spend vs. closes" subtitle="Last 8 weeks" fullscreenable defaultHeight={240}>
            {(h) => (
              <ResponsiveContainer width="100%" height={h}>
                <BarChart data={weeklyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                  <XAxis dataKey="week" tick={{ fontSize: 11 }}/>
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} tickFormatter={fmtCompact}/>
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} allowDecimals={false}/>
                  <Tooltip formatter={(v, name) => name === 'Spend' ? fmt(v) : v}/>
                  <Legend/>
                  <Bar yAxisId="left" dataKey="spend" name="Spend" fill="#f59e0b"/>
                  <Bar yAxisId="right" dataKey="closes" name="Closes" fill="#10b981"/>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
          <Card title="ROI by lead source" subtitle="Annualized premium vs. spend per source" fullscreenable defaultHeight={240}>
            {(h) => bySource.filter(r => r.roi != null).length > 0 ? (
              <ResponsiveContainer width="100%" height={h}>
                <BarChart data={bySource.filter(r => r.roi != null)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                  <XAxis dataKey="source" tick={{ fontSize: 11 }}/>
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `${v}%`}/>
                  <Tooltip formatter={(v) => `${v.toFixed(0)}%`}/>
                  <Bar dataKey="roi" name="ROI %">
                    {bySource.filter(r => r.roi != null).map((entry, i) => (
                      <Cell key={i} fill={entry.roi >= 100 ? '#10b981' : entry.roi >= 0 ? '#f59e0b' : '#ef4444'}/>
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <div className="text-4xl mb-2">📊</div>
                <div className="text-sm font-bold text-slate-700 mb-1">ROI reporting is not available yet</div>
                <div className="text-xs text-slate-500 mb-3">To calculate ROI, three things need to be recorded:</div>
                <ol className="text-xs text-slate-600 text-left space-y-1 mb-3 tabular-nums">
                  <li>1. Lead purchase cost (per source)</li>
                  <li>2. Closed policy premium (per lead)</li>
                  <li>3. Commission earned (per policy)</li>
                </ol>
                <button
                  onClick={() => showToast?.('Open a lead + set premium/cost in the drawer, or bulk-import via CSV.')}
                  className="text-xs font-bold text-cyan-700 hover:text-cyan-900 underline"
                >How to add this information →</button>
              </div>
            )}
          </Card>
        </div>
      )}

      {bySource.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <button
            type="button"
            onClick={() => setSpendBySourceOpen(o => !o)}
            className="w-full flex items-center justify-between text-left mb-3 group"
            aria-expanded={spendBySourceOpen}
          >
            <h3 className="font-semibold text-slate-900">Spend by source</h3>
            <span className="text-xs font-medium text-slate-500 group-hover:text-slate-900 inline-flex items-center gap-1">
              {spendBySourceOpen ? 'Hide' : `Show ${bySource.length} source${bySource.length === 1 ? '' : 's'}`}
              <ChevronRight size={12} style={{ transform: spendBySourceOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}/>
            </span>
          </button>
          {spendBySourceOpen && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Source</th>
                  <th className="py-2 px-2 text-right">Leads</th>
                  <th className="py-2 px-2 text-right">Spend</th>
                  <th className="py-2 px-2 text-right">Cost/Lead</th>
                  <th className="py-2 px-2 text-right">Closed</th>
                  <th className="py-2 px-2 text-right">CPA</th>
                  <th className="py-2 px-2 text-right">Premium/mo</th>
                  <th className="py-2 px-2 text-right" title="Calls answered / outbound dials, computed from the calls log. Null when no calls have been logged for this source.">Pickup %</th>
                  <th className="py-2 px-2 text-right" title="Lead status reached 'contacted' or beyond at least once (any channel).">Contact %</th>
                  <th className="py-2 px-2 text-right">Close %</th>
                  <th className="py-2 px-2 text-right">ROI %</th>
                </tr>
              </thead>
              <tbody>
                {bySource.map(r => {
                  const pickupTone = r.pickupRate == null ? 'slate' : r.pickupRate >= 30 ? 'emerald' : r.pickupRate >= 15 ? 'amber' : 'red';
                  return (
                  <tr key={r.source} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 pr-3 font-medium">{r.source}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.leads}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(r.spend)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.cpl > 0 ? fmt(r.cpl) : '—'}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.closed}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.cpa > 0 ? fmt(r.cpa) : '—'}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(r.premium)}</td>
                    <td className={`py-2 px-2 text-right tabular-nums font-medium text-${pickupTone}-700`} title={r.pickupRate == null ? 'No outbound calls logged for this source yet' : `${r.dials} outbound dials logged`}>
                      {r.pickupRate == null ? '—' : `${r.pickupRate.toFixed(0)}%`}
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.contactRate.toFixed(0)}%</td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.closeRate.toFixed(0)}%</td>
                    <td className={`py-2 px-2 text-right tabular-nums font-semibold ${r.roi == null ? 'text-slate-400' : r.roi >= 100 ? 'text-emerald-700' : r.roi >= 0 ? 'text-amber-700' : 'text-red-700'}`}>{r.roi == null ? '—' : `${r.roi.toFixed(0)}%`}</td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-300 text-sm font-semibold text-slate-900">
                  <td className="py-2 pr-3">Total</td>
                  <td className="py-2 px-2 text-right tabular-nums">{totalLeads}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{fmt(totalCost)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{costPerLead > 0 ? fmt(costPerLead) : '—'}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{policiesClosed}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{cpa > 0 ? fmt(cpa) : '—'}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{fmt(closedPremium)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{(() => {
                    const td = bySource.reduce((s, r) => s + (r.dials || 0), 0);
                    const tp = bySource.reduce((s, r) => s + ((r.dials || 0) * ((r.pickupRate || 0) / 100)), 0);
                    return td > 0 ? `${((tp / td) * 100).toFixed(0)}%` : '—';
                  })()}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{contactRate.toFixed(0)}%</td>
                  <td className="py-2 px-2 text-right tabular-nums">{totalLeads > 0 ? ((policiesClosed / totalLeads) * 100).toFixed(0) : 0}%</td>
                  <td className={`py-2 px-2 text-right tabular-nums ${leadRoi == null ? 'text-slate-400' : leadRoi >= 100 ? 'text-emerald-700' : leadRoi >= 0 ? 'text-amber-700' : 'text-red-700'}`}>{leadRoi == null ? '—' : `${leadRoi.toFixed(0)}%`}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          )}
        </div>
      )}

      {/* ───── Pipeline Value Summary ───── */}
      {activeLeads.length > 0 && (
        <div className="bg-gradient-to-r from-emerald-50 to-amber-50 border border-emerald-200 rounded-xl p-4">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="text-xs font-semibold text-emerald-900 uppercase tracking-wide">Pipeline value (active leads)</div>
            <span className="text-[10px] text-slate-500" title="Premium midpoint × 12 months. Commission = annual premium × 1.05 advance rate. These are estimates from budget ranges parsed from each lead's notes — actual numbers may differ.">ⓘ estimate</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-white rounded-lg p-3 border border-emerald-100">
              <div className="text-xs text-slate-500">Potential annual premium</div>
              <div className="text-2xl font-bold text-emerald-700 mt-1">{fmt(potentialAnnualPremium)}</div>
              <div className="text-xs text-slate-500 mt-1">{fmt(potentialMonthlyPremium)}/mo across {activeLeads.length} active</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-amber-100">
              <div className="text-xs text-slate-500">Potential commission</div>
              <div className="text-2xl font-bold text-amber-700 mt-1">{fmt(potentialCommission)}</div>
              <div className="text-xs text-slate-500 mt-1">Annual premium × 1.05 advance</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-slate-200">
              <div className="text-xs text-slate-500">Leads in active pipeline</div>
              <div className="text-2xl font-bold text-slate-900 mt-1">{activeLeads.length}</div>
              <div className="text-xs text-slate-500 mt-1">Not in Closed / No Show / DNC / Recycled</div>
            </div>
          </div>
        </div>
      )}

      {/* ───── Speed-to-lead leaderboard ─────
          Hidden in Table mode so the Table tab is a focused table-only
          view instead of a near-duplicate of Pipeline mode (BUG-008). */}
      {viewMode !== 'table' && leads.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <h3 className="font-semibold text-slate-900">Speed to lead</h3>
              <div className="text-xs text-slate-500 mt-0.5">Minutes from receipt to first touch. Industry rule of thumb: contact within 5 minutes wins ~80% of the time.</div>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className={`px-2 py-1 rounded-lg font-bold ${speedOverall.under5Pct >= 50 ? 'bg-emerald-100 text-emerald-700' : speedOverall.under5Pct >= 25 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                {speedOverall.under5Pct.toFixed(0)}% &lt; 5 min
              </span>
              {speedOverall.never > 0 && (
                <span className="px-2 py-1 rounded-lg font-bold bg-rose-100 text-rose-700" title="Leads with no outreach or call logged yet">
                  ⚠ {speedOverall.never} never contacted
                </span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="text-xs text-slate-500">Median speed</div>
              <div className="text-2xl font-bold mt-1 text-slate-900">{speedOverall.median != null ? fmtSpeed(speedOverall.median) : '—'}</div>
              <div className="text-xs text-slate-500 mt-0.5">{speedOverall.count} contacted of {leads.length}</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <div className="text-xs text-emerald-700">% within 5 min</div>
              <div className="text-2xl font-bold mt-1 text-emerald-900">{speedOverall.under5Pct.toFixed(0)}%</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="text-xs text-amber-700">% within 1 hour</div>
              <div className="text-2xl font-bold mt-1 text-amber-900">{speedOverall.under60Pct.toFixed(0)}%</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
              <div className="text-xs text-rose-700">Never contacted</div>
              <div className="text-2xl font-bold mt-1 text-rose-900">{speedOverall.never}</div>
              <div className="text-xs text-rose-700 mt-0.5">No call or outreach yet</div>
            </div>
          </div>
          {speedByAgent.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3">Agent</th>
                    <th className="py-2 px-2 text-right">Leads</th>
                    <th className="py-2 px-2 text-right">Contacted</th>
                    <th className="py-2 px-2 text-right">Median speed</th>
                    <th className="py-2 px-2 text-right">Fastest</th>
                    <th className="py-2 px-2 text-right">&lt; 5 min %</th>
                    <th className="py-2 px-2 text-right">&lt; 1 hr %</th>
                  </tr>
                </thead>
                <tbody>
                  {speedByAgent.map(a => {
                    const fast = a.under5Pct >= 50;
                    const slow = a.under5Pct < 10 && a.contacted >= 3;
                    return (
                      <tr key={a.prodNum} className={`border-b border-slate-100 ${slow ? 'bg-red-50' : 'hover:bg-slate-50'}`}>
                        <td className="py-2 pr-3 font-medium">
                          <div className="flex items-center gap-2">
                            {fast && <span className="text-emerald-600" title="Top-quartile speed">⚡</span>}
                            {slow && <span className="text-red-600" title="Under 10% within 5 min — coaching needed">⚠</span>}
                            <span>{a.name}</span>
                          </div>
                        </td>
                        <td className="py-2 px-2 text-right tabular-nums">{a.total}</td>
                        <td className="py-2 px-2 text-right tabular-nums text-slate-500">{a.contacted}</td>
                        <td className="py-2 px-2 text-right tabular-nums font-semibold">{a.median != null ? fmtSpeed(a.median) : '—'}</td>
                        <td className="py-2 px-2 text-right tabular-nums text-slate-500">{a.fastest != null ? fmtSpeed(a.fastest) : '—'}</td>
                        <td className={`py-2 px-2 text-right tabular-nums ${a.under5Pct >= 50 ? 'text-emerald-700 font-semibold' : a.under5Pct >= 25 ? 'text-amber-700' : 'text-red-700'}`}>{a.under5Pct.toFixed(0)}%</td>
                        <td className={`py-2 px-2 text-right tabular-nums ${a.under60Pct >= 80 ? 'text-emerald-700 font-semibold' : a.under60Pct >= 50 ? 'text-amber-700' : 'text-red-700'}`}>{a.under60Pct.toFixed(0)}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {viewMode !== 'table' && leads.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-semibold text-slate-900 mb-3">Pipeline</h3>
          <div className="space-y-2">
            {allStageCounts.map(({ stage, count }) => {
              const pct = totalLeads > 0 ? (count / totalLeads) * 100 : 0;
              return (
                <div key={stage.id} className="flex items-center gap-3">
                  <div className="w-28 text-xs font-semibold text-slate-700 truncate">{stage.label}</div>
                  <div className="flex-1 bg-slate-100 rounded h-6 relative overflow-hidden">
                    <div className={`h-full bg-${stage.color}-500`} style={{ width: `${Math.max(pct, count > 0 ? 4 : 0)}%` }}/>
                    <div className="absolute inset-0 flex items-center px-2 text-xs font-semibold text-slate-700">{count} · {pct.toFixed(0)}%</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {viewMode !== 'table' && leads.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card title="Attempt distribution" subtitle="How many outreach attempts per lead" fullscreenable defaultHeight={220}>
            {(h) => (
              <ResponsiveContainer width="100%" height={h}>
                <BarChart data={attemptBuckets}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }}/>
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false}/>
                  <Tooltip/>
                  <Bar dataKey="value" name="Leads" fill="#3b82f6"/>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
          <Card title="Stage mix" subtitle="Distribution of leads across all stages" fullscreenable defaultHeight={220}>
            {(h) => (
              <ResponsiveContainer width="100%" height={h}>
                <BarChart data={allStageCounts.map(s => ({ name: s.stage.label, value: s.count }))} layout="vertical" margin={{ left: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                  <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false}/>
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={90}/>
                  <Tooltip/>
                  <Bar dataKey="value" name="Leads">
                    {allStageCounts.map((s, i) => <Cell key={i} fill={colorHex(s.stage.color)}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </div>
      )}

      {/* ───── Conversion funnel + Lead age ───── */}
      {viewMode !== 'table' && leads.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card title="Conversion funnel" subtitle={`${leadSourceFilter === 'all' ? 'All sources' : leadSourceFilter} · proceed-rate between stages flagged in amber if drop-off > 50%`} fullscreenable defaultHeight={240}>
            {(h) => (
              <div className="flex flex-col h-full">
                <ResponsiveContainer width="100%" height={h - 50}>
                  <BarChart data={funnelData.map(d => ({ name: d.stage.label, value: d.count, fill: d.fill }))} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                    <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false}/>
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={90}/>
                    <Tooltip formatter={(v) => v.toLocaleString()}/>
                    <Bar dataKey="value" name="Leads">
                      {funnelData.map((entry, i) => <Cell key={i} fill={entry.fill}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="px-2 pt-1 text-[10px] text-slate-600 flex flex-wrap gap-x-2 gap-y-0.5">
                  {funnelDropOffs.map((d, i) => {
                    const ok = d.proceedPct >= 50;
                    return (
                      <span key={i} className={ok ? '' : 'text-amber-700 font-semibold'}>
                        {d.from} → {d.to}: {d.proceedPct.toFixed(0)}%{!ok && ' ⚠'}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </Card>
          <Card title="Lead age (active pipeline)" subtitle={oldShare > 0.3 ? '⚠ Over 30% of active leads are 30d+ old — prioritize older leads.' : 'Fresh = ≤14d · Aging = 15-30d · Stale = 31d+'} fullscreenable defaultHeight={240}>
            {(h) => (
              <ResponsiveContainer width="100%" height={h}>
                <BarChart data={leadAgeBuckets}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }}/>
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false}/>
                  <Tooltip/>
                  <Bar dataKey="value" name="Leads">
                    {leadAgeBuckets.map((entry, i) => <Cell key={i} fill={entry.fill}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </div>
      )}

      {/* ───── Source performance (normalized lead sources) ───── */}
      {viewMode !== 'table' && sourcePerf.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-semibold text-slate-900 mb-3">Source performance</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Source</th>
                  <th className="py-2 px-2 text-right">Total leads</th>
                  <th className="py-2 px-2 text-right">Show rate</th>
                  <th className="py-2 px-2 text-right">App rate</th>
                  <th className="py-2 px-2 text-right">Close rate</th>
                  <th className="py-2 px-2 text-right">Avg score</th>
                </tr>
              </thead>
              <tbody>
                {sourcePerf.map(r => (
                  <tr key={r.source} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 pr-3 font-medium">{r.source}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.total}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.showRate.toFixed(0)}%</td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.appRate.toFixed(0)}%</td>
                    <td className={`py-2 px-2 text-right tabular-nums font-semibold ${r.closeRate >= 20 ? 'text-emerald-700' : r.closeRate >= 10 ? 'text-amber-700' : 'text-slate-600'}`}>{r.closeRate.toFixed(0)}%</td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.avgScore.toFixed(1)} / 5</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      </>
      )}

      <SmartSearchBar
        sources={sources}
        agentChoices={agentChoices}
        onApply={(f) => {
          if (f.search !== undefined) setSearch(f.search || '');
          if (f.status) setStatusFilter(f.status);
          if (f.agentProdNum) setAgentFilter(f.agentProdNum);
          if (f.source) setSourceFilter(f.source);
          if (f.leadSource) setLeadSourceFilter(f.leadSource);
          if (f.leadType) setLeadTypeFilter(f.leadType);
          if (f.tag) setTagFilter(f.tag);
          if (f.stale) setStaleFilter(f.stale);
        }}
      />
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-2 flex-wrap">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search name, phone, email, notes…"
          className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm flex-1 min-w-[200px]"
        />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm bg-white">
          <option value="all">All statuses</option>
          {LEAD_STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <select value={agentFilter} onChange={e => setAgentFilter(e.target.value)} className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm bg-white">
          <option value="all">All agents</option>
          {agentChoices.map(a => <option key={a.prodNum} value={a.prodNum}>{a.name}</option>)}
        </select>
        <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm bg-white">
          <option value="all">All sources</option>
          {sources.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {allTags.length > 0 && (
          <select value={tagFilter} onChange={e => setTagFilter(e.target.value)} className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm bg-white">
            <option value="all">All tags</option>
            {allTags.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        <div className="flex items-center gap-1">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide mr-1">Freshness:</span>
          {[['all','All'],['7d','Stale 7d+'],['14d','Stale 14d+'],['never','Never contacted']].map(([id, label]) => (
            <button key={id} onClick={() => setStaleFilter(id)} className={`px-2.5 py-1 text-xs font-medium rounded-lg border ${staleFilter === id ? (id === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'bg-red-600 text-white border-red-600') : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}>{label}</button>
          ))}
        </div>
        <select value={leadSourceFilter} onChange={e => setLeadSourceFilter(e.target.value)} className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm bg-white">
          <option value="all">All lead sources</option>
          {LEAD_SOURCES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        {(search || statusFilter !== 'all' || agentFilter !== 'all' || sourceFilter !== 'all' || leadSourceFilter !== 'all' || tagFilter !== 'all' || staleFilter !== 'all' || leadTypeFilter !== 'all' || sectorFilter !== 'all') && (
          <button onClick={() => { setSearch(''); setStatusFilter('all'); setAgentFilter('all'); setSourceFilter('all'); setLeadSourceFilter('all'); setTagFilter('all'); setStaleFilter('all'); setLeadTypeFilter('all'); setSectorFilter('all'); }} className="text-xs text-slate-500 hover:text-slate-700 underline">Reset filters</button>
        )}
      </div>

      {/* Lead type pill row — quickly partition the list into Mortgage
          Protection vs IUL vs everything else. Counts come from the
          full leads array, not the visibleLeads, so the user can see
          how many of each type they have regardless of other filters. */}
      {(() => {
        const counts = (leads || []).reduce((acc, l) => {
          const t = l.leadType || 'general';
          acc[t] = (acc[t] || 0) + 1;
          return acc;
        }, {});
        const total = (leads || []).length;
        const types = [
          { id: 'all',                  label: 'All',                  count: total,                       accent: 'slate' },
          { id: 'mortgage_protection',  label: 'Mortgage Protection', count: counts.mortgage_protection || 0, accent: 'emerald' },
          { id: 'iul',                  label: 'IUL',               count: counts.iul || 0,             accent: 'violet' },
          { id: 'final_expense',        label: 'Final Expense',      count: counts.final_expense || 0,   accent: 'red'    },
          { id: 'general',              label: 'General / other',      count: counts.general || 0,         accent: 'amber' },
        ].filter(t => t.id === 'all' || t.count > 0);
        if (types.length <= 2) return null; // nothing to sort by
        return (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Lead type</span>
            {types.map(t => {
              const active = leadTypeFilter === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setLeadTypeFilter(t.id)}
                  className={`px-3 py-1.5 text-sm font-semibold rounded-lg border transition-colors ${
                    active
                      ? t.accent === 'slate' ? 'bg-slate-900 text-white border-slate-900'
                        : t.accent === 'emerald' ? 'bg-emerald-600 text-white border-emerald-600'
                        : t.accent === 'violet' ? 'bg-violet-600 text-white border-violet-600'
                        : 'bg-amber-600 text-white border-amber-600'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {t.label} <span className="text-xs opacity-70 ml-1">({t.count})</span>
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* v2 — 4-way Vertical+Sector filter pill row. Splits the workflow
          at the spine so an agent can work each book separately:
          Life · ACA/Private · Medicare. */}
      {(() => {
        const total = (leads || []).length;
        if (total === 0) return null;
        const counts = (leads || []).reduce((acc, l) => {
          const vert = l.vertical || 'life';
          if (vert === 'life') acc.life++;
          else {
            acc.health++;
            if (l.sector === 'medicare')    acc.medicare++;
            else                            acc.aca_private++; // ACA is the health-vertical default
          }
          return acc;
        }, { life: 0, health: 0, aca_private: 0, medicare: 0 });
        const opts = [
          { id: 'all',         label: 'All',           count: total,              accent: 'slate'   },
          { id: 'life',        label: 'Life',          count: counts.life,        accent: 'emerald' },
          { id: 'health',      label: 'Health',        count: counts.health,      accent: 'teal'    },
          { id: 'aca_private', label: 'ACA / Private', count: counts.aca_private, accent: 'sky'     },
          { id: 'medicare',    label: 'Medicare',      count: counts.medicare,    accent: 'blue'    },
        ];
        return (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Book</span>
            {opts.map(o => {
              const active = sectorFilter === o.id;
              const activeClass =
                o.accent === 'emerald' ? 'bg-emerald-600 text-white border-emerald-700' :
                o.accent === 'teal'    ? 'bg-teal-600 text-white border-teal-700'       :
                o.accent === 'sky'     ? 'bg-sky-600 text-white border-sky-700'         :
                o.accent === 'blue'    ? 'bg-blue-600 text-white border-blue-700'       :
                                         'bg-slate-900 text-white border-slate-900';
              return (
                <button
                  key={o.id}
                  onClick={() => setSectorFilter(o.id)}
                  className={`px-3 py-1.5 text-sm font-semibold rounded-lg border transition-colors ${
                    active ? activeClass : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {o.label} <span className="text-xs opacity-70 ml-1">({o.count})</span>
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* Bulk action bar — shown only when leads are selected in table view */}
      {viewMode === 'table' && Object.keys(selectedIds).filter(id => selectedIds[id]).length > 0 && (
        <LeadBulkBar
          selectedIds={selectedIds}
          allLeads={leads}
          agentChoices={agentChoices}
          allTags={allTags}
          onAssignAgent={(prodNum) => {
            const ids = Object.keys(selectedIds).filter(id => selectedIds[id]);
            const name = agentName(prodNum);
            setLeads(prev => prev.map(l => ids.includes(l.id) ? { ...l, agentProdNum: prodNum, updatedAt: nowIso(), activity: [...(l.activity || []), { at: nowIso(), type: 'assign', body: `Assigned to ${name} (bulk)` }] } : l));
            showToast(`Assigned ${ids.length} lead${ids.length === 1 ? '' : 's'} to ${name}`);
          }}
          onChangeStatus={(status) => {
            const ids = Object.keys(selectedIds).filter(id => selectedIds[id]);
            const label = LEAD_STAGE_BY_ID[status]?.label || status;
            setLeads(prev => prev.map(l => ids.includes(l.id)
              ? { ...l, status, statusChangedAt: l.status !== status ? nowIso() : l.statusChangedAt, updatedAt: nowIso(), activity: [...(l.activity || []), { at: nowIso(), type: 'status', body: `Stage → ${label} (bulk)` }] }
              : l));
            showToast(`Moved ${ids.length} lead${ids.length === 1 ? '' : 's'} to ${label}`);
          }}
          onAddTag={(tag) => {
            const t = (tag || '').trim();
            if (!t) return;
            const ids = Object.keys(selectedIds).filter(id => selectedIds[id]);
            setLeads(prev => prev.map(l => {
              if (!ids.includes(l.id)) return l;
              const tags = Array.isArray(l.tags) ? l.tags : [];
              if (tags.includes(t)) return l;
              return { ...l, tags: [...tags, t], updatedAt: nowIso(), activity: [...(l.activity || []), { at: nowIso(), type: 'tag', body: `Tagged "${t}" (bulk)` }] };
            }));
            showToast(`Added tag "${t}" to ${ids.length} lead${ids.length === 1 ? '' : 's'}`);
          }}
          onDelete={async () => {
            const ids = Object.keys(selectedIds).filter(id => selectedIds[id]);
            if (!(await hqConfirm({
              title: `Delete ${ids.length} lead${ids.length === 1 ? '' : 's'}?`,
              body: 'This also removes any linked calendar entries and outreach logs. Cannot be undone.',
              confirmLabel: `Delete ${ids.length} lead${ids.length === 1 ? '' : 's'}`,
              danger: true,
            }))) return;
            const idSet = new Set(ids);
            setLeads(prev => prev.filter(l => !idSet.has(l.id)));
            setOutreach(prev => prev.filter(o => !idSet.has(o.leadId)));
            setCalendar(prev => prev.filter(a => !a.leadId || !idSet.has(a.leadId)));
            setSelectedIds({});
            showToast(`Deleted ${ids.length} lead${ids.length === 1 ? '' : 's'}`);
          }}
          onPowerDial={async () => {
            const ids = Object.keys(selectedIds).filter(id => selectedIds[id]);
            if (ids.length === 0) return;
            const idSet = new Set(ids);
            // Selected lead objects in the shape /api/kixie/powerdial
            // accepts. We send name as a single string; the server
            // splits it into first/last for Kixie.
            const picked = leads
              .filter(l => idSet.has(l.id) && l.phone)
              .map(l => ({
                id:     String(l.id),
                name:   l.name || '',
                phone:  l.phone,
                email:  l.email || '',
                niche:  l.vertical || l.lineOfBusiness || '',
                source: 'sfa_crm',
              }));
            if (picked.length === 0) {
              showToast('No phone numbers on the selected leads — nothing to dial.', 'error');
              return;
            }
            setPowerDialBusy(true);
            try {
              const { data: { session } } = await supabase.auth.getSession();
              const r = await fetch('/api/kixie/powerdial', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
                },
                body: JSON.stringify({ leads: picked }),
              });
              const body = await r.json().catch(() => ({}));
              if (!r.ok || !body?.ok) {
                const err = body?.error || `kixie_${r.status}`;
                const fix = body?.fix ? ` · ${body.fix}` : '';
                showToast(`Power Dial failed: ${err}${fix}`, 'error');
                return;
              }
              const { queued = 0, skipped = 0, failed = 0 } = body.summary || {};
              if (queued > 0 && skipped === 0 && failed === 0) {
                showToast(`✓ ${queued} lead${queued === 1 ? '' : 's'} queued in your Kixie Powerlist.`);
              } else {
                showToast(
                  `Queued ${queued} · skipped ${skipped} · failed ${failed}. Check Kixie for the dial.`,
                  skipped > 0 || failed > 0 ? 'warn' : 'success'
                );
              }
              if (body.halted) {
                showToast('Kixie marked the connection invalid — re-paste your API key in Settings.', 'error');
              }
            } catch (e) {
              showToast(`Power Dial failed: ${e?.message || 'network error'}`, 'error');
            } finally {
              setPowerDialBusy(false);
            }
          }}
          powerDialBusy={powerDialBusy}
          onClear={() => setSelectedIds({})}
        />
      )}

      {leads.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-12 text-center">
          <div className="text-slate-700 font-semibold mb-1">No leads yet</div>
          <div className="text-sm text-slate-500 mb-4">Add your first lead manually or bulk import from CSV.</div>
          <div className="flex items-center justify-center gap-2">
            <button onClick={() => setEditingLead({ __new: true })} className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-semibold">+ New lead</button>
            <button onClick={() => setCsvOpen(true)} className="bg-slate-700 hover:bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-semibold">Bulk import CSV</button>
          </div>
        </div>
      ) : viewMode === 'kanban' ? (
        <LeadKanban
          leads={visibleLeads}
          agentName={agentName}
          attemptsForLead={attemptsForLead}
          callCountFor={callCountFor}
          daysSinceContact={daysSinceContact}
          lastContactedFor={lastContactedFor}
          onOpen={(id) => setDetailId(id)}
          onMove={moveStage}
          onCall={openTel}
          onSms={openSmsFor}
          onLogOutcome={openLogOutcome}
          onAdvance={advanceLeadNextStage}
          onEnrollDrip={(l) => setEnrollingLead(l)}
        />
      ) : (
        <LeadTable
          leads={visibleLeads}
          agentName={agentName}
          attemptsForLead={attemptsForLead}
          callCountFor={callCountFor}
          lastCallFor={lastCallFor}
          lastContactedFor={lastContactedFor}
          daysSinceContact={daysSinceContact}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          onOpen={(id) => setDetailId(id)}
          onCall={openTel}
          onSms={openSmsFor}
          onLogOutcome={openLogOutcome}
          onAdvance={advanceLeadNextStage}
          onEnrollDrip={(l) => setEnrollingLead(l)}
        />
      )}
        </>
      )}

      {editingLead && (
        <LeadFormModal
          initial={editingLead.__new ? null : editingLead}
          agentChoices={agentChoices}
          defaultVertical={defaultVertical}
          onCancel={() => setEditingLead(null)}
          onSave={async (rec) => {
            // Duplicate-lead detection — only checks NEW leads. Match
            // by phone (last 10 digits) OR email (case-insensitive).
            // Soft prompt: operator can still create a duplicate if
            // they know it's intentional (e.g. spouse with shared
            // email), but they have to confirm.
            if (!rec.id) {
              const pkey = String(rec.phone || '').replace(/\D/g, '').slice(-10);
              const ekey = String(rec.email || '').trim().toLowerCase();
              const dup = leads.find(l => {
                if (l.deletedAt) return false; // ignore soft-deleted
                const lp = String(l.phone || '').replace(/\D/g, '').slice(-10);
                const le = String(l.email || '').trim().toLowerCase();
                return (pkey && lp && pkey === lp) || (ekey && le && ekey === le);
              });
              if (dup) {
                const which = (() => {
                  const lp = String(dup.phone || '').replace(/\D/g, '').slice(-10);
                  const le = String(dup.email || '').trim().toLowerCase();
                  if (pkey && lp === pkey && ekey && le === ekey) return 'phone AND email';
                  if (pkey && lp === pkey) return 'phone';
                  return 'email';
                })();
                const choice = await hqConfirm({
                  title: `Possible duplicate — same ${which}`,
                  body: `${dup.name || '(unnamed)'} · ${dup.phone || ''} ${dup.email || ''} · status: ${dup.status || 'new'}. Open the existing lead (recommended) or create a duplicate anyway.`,
                  confirmLabel: 'Open existing',
                  cancelLabel: 'Create duplicate',
                });
                if (choice) {
                  setEditingLead(null);
                  setDetailId(dup.id);
                  showToast(`Opened existing lead: ${dup.name || dup.id}`);
                  return;
                }
                // operator chose to create a duplicate — fall through
              }
            }
            const saved = upsertLead(rec);
            setEditingLead(null);
            showToast(rec.id ? 'Lead updated' : 'Lead created');
            if (!rec.id) setDetailId(saved.id);
          }}
        />
      )}
      {csvOpen && (
        <LeadsCsvImportModal
          agentChoices={agentChoices}
          existingLeads={leads}
          initialFiles={pendingDropFiles}
          onCancel={() => { setCsvOpen(false); setPendingDropFiles(null); }}
          onImport={(rows, opts) => {
            // BUG FIX: previously called `csvImport(rows)` discarding
            // the second argument, so the "Re-import & refresh
            // existing leads" toggle and the total-spend input
            // silently didn't reach csvImport (which then treated
            // dupes as new appends).
            csvImport(rows, opts || {});
            setCsvOpen(false);
            setPendingDropFiles(null);
            // Reset every filter on the Leads tab and force-nav
            // there. Previously the user could hit Import from
            // Dashboard, the leads went into state, but the current
            // tab didn't change — they thought "nothing happened."
            // Also: a stale statusFilter='closed' would silently
            // hide fresh 'new' rows even on the correct tab.
            onGotoTab('leads');
            // CRITICAL: switch out of "Today" view mode. Today only
            // shows leads with an urgent follow-up date; freshly
            // imported leads have no follow-up so Today renders
            // "You're all caught up" — the exact "nothing shows up"
            // symptom Harrison's client hit. Table shows all rows.
            try { setViewMode('table'); } catch { /* ignore */ }
            try { setAgentFilter('all');    } catch { /* ignore */ }
            try { setStatusFilter('all');   } catch { /* ignore */ }
            try { setLeadTypeFilter('all'); } catch { /* ignore */ }
            try { setSectorFilter('all');   } catch { /* ignore */ }
            try { setSourceFilter('all');     } catch { /* ignore */ }
            try { setLeadSourceFilter('all'); } catch { /* ignore */ }
            try { setTagFilter('all');        } catch { /* ignore */ }
            try { setStaleFilter('all');    } catch { /* ignore */ }
            try { setSearch('');            } catch { /* ignore */ }
          }}
        />
      )}
      {recycleBinOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setRecycleBinOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-slate-900">Recycle bin</h3>
                <div className="text-xs text-slate-500">Soft-deleted leads are kept for {RECYCLE_RETENTION_DAYS} days before permanent removal.</div>
              </div>
              <button onClick={() => setRecycleBinOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
            </div>
            <div className="overflow-y-auto flex-1">
              {leads.filter(l => l.deletedAt).length === 0 ? (
                <div className="px-5 py-10 text-center text-sm text-slate-500">Recycle bin is empty.</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {leads.filter(l => l.deletedAt).sort((a, b) => (b.deletedAt || '').localeCompare(a.deletedAt || '')).map(l => {
                    const deletedTs = new Date(l.deletedAt).getTime();
                    const daysAgo = Math.max(0, Math.floor((Date.now() - deletedTs) / 86_400_000));
                    const daysLeft = Math.max(0, RECYCLE_RETENTION_DAYS - daysAgo);
                    return (
                      <li key={l.id} className="px-5 py-2.5 flex items-center gap-3 hover:bg-slate-50">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-slate-900 truncate">{l.name || '(unnamed)'}</div>
                          <div className="text-xs text-slate-500 truncate">{l.phone || ''}  {l.email || ''}</div>
                          <div className="text-[10px] text-slate-400 mt-0.5">Deleted {daysAgo}d ago · purges in {daysLeft}d</div>
                        </div>
                        <button
                          onClick={() => restoreLead(l.id)}
                          className="text-xs font-bold text-emerald-700 hover:text-emerald-900 px-2.5 py-1 border border-emerald-200 rounded hover:bg-emerald-50"
                        >
                          Restore
                        </button>
                        <button
                          onClick={async () => {
                            if (await hqConfirm({
                              title: `Permanently delete ${l.name || 'this lead'}?`,
                              body: 'This cannot be undone.',
                              confirmLabel: 'Delete permanently',
                              danger: true,
                            })) {
                              hardDeleteLead(l.id);
                            }
                          }}
                          className="text-xs font-bold text-rose-600 hover:text-rose-800 px-2.5 py-1 border border-rose-200 rounded hover:bg-rose-50"
                        >
                          Delete forever
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
      {apptPrompt && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setApptPrompt(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-slate-900">Set appointment</h3>
            <p className="text-sm text-slate-500">Pick a date/time. This creates a Calendar entry linked to the lead.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Date *</span>
                <input type="date" required value={apptPrompt.date} onChange={e => setApptPrompt(p => ({ ...p, date: e.target.value }))} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Time *</span>
                <input type="time" required value={apptPrompt.time} onChange={e => setApptPrompt(p => ({ ...p, time: e.target.value }))} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <button onClick={() => setApptPrompt(null)} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button onClick={confirmApptSet} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold">Book + advance</button>
            </div>
          </div>
        </div>
      )}
      {detailLead && (
        <LeadDetailDrawer
          lead={detailLead}
          agentName={agentName(detailLead.agentProdNum)}
          attempts={attemptsForLead(detailLead.id)}
          outreachLog={outreachForLead(detailLead.id)}
          callHistory={callsForLead(detailLead.id)}
          onClose={() => setDetailId(null)}
          onEdit={() => setEditingLead(detailLead)}
          onMove={(next) => moveStage(detailLead.id, next)}
          onLogOutreach={(rec) => logOutreach(detailLead.id, rec)}
          onLogCall={() => setLogCallFor(detailLead.id)}
          onTogglePolicy={() => togglePolicyLapsed(detailLead.id)}
          onSetPremium={(p) => setLeadField(detailLead.id, { premium: Number(p) || 0 }, { type: 'policy', body: `Premium set to ${fmt(Number(p) || 0)}/mo` })}
          onSetCost={(c) => setLeadField(detailLead.id, { cost: Number(c) || 0 }, { type: 'cost', body: `Lead cost set to ${fmt(Number(c) || 0)}` })}
          onSetField={(patch) => setLeadField(detailLead.id, patch)}
          onRemoveTag={(t) => setLeadField(detailLead.id, { tags: (detailLead.tags || []).filter(x => x !== t) }, { type: 'tag', body: `Removed tag "${t}"` })}
          onAddTag={(t) => {
            const tag = (t || '').trim();
            if (!tag) return;
            const existing = detailLead.tags || [];
            if (existing.includes(tag)) return;
            setLeadField(detailLead.id, { tags: [...existing, tag] }, { type: 'tag', body: `Added tag "${tag}"` });
          }}
          onSetFollowUp={(d) => setLeadField(detailLead.id, { followUpDate: d || '' }, { type: 'followup', body: d ? `Follow-up set for ${d}` : 'Follow-up cleared' })}
          onSetLeadSource={(src) => setLeadField(detailLead.id, { leadSource: src || '' }, { type: 'source', body: src ? `Lead source set to ${src}` : 'Lead source cleared' })}
          onSetSector={(sector) => setLeadField(detailLead.id, { sector }, { type: 'sector', body: `Sector changed to ${sector === 'medicare' ? 'Medicare' : sector === 'ancillary' ? 'Ancillary' : 'ACA / Private'}` })}
          onCaptureConsent={(source) => {
            // Mark consent with the default disclosure text + current timestamp.
            setLeadField(detailLead.id, {
              consentText: DEFAULT_TCPA_TEXT,
              consentAt:   nowIso(),
              consentSource: source || 'verbal',
            }, { type: 'consent', body: `TCPA / A2P consent captured (${source || 'verbal'})` });
          }}
          onToggleOptOut={(reason) => {
            const isOptingOut = !detailLead.optOut;
            setLeadField(detailLead.id, {
              optOut: isOptingOut,
              optOutAt: isOptingOut ? nowIso() : '',
              optOutReason: isOptingOut ? (reason || '') : '',
            }, { type: 'consent', body: isOptingOut ? `Marked as OPTED OUT${reason ? ` — ${reason}` : ''}` : 'Opt-out cleared' });
          }}
          onDelete={async () => {
            if (await hqConfirm({
              title: `Delete lead "${detailLead.name}"?`,
              body: 'This also removes any linked calendar entry and outreach log.',
              confirmLabel: 'Delete lead',
              danger: true,
            })) {
              deleteLead(detailLead.id);
            }
          }}
          workflows={workflows}
          onSetWorkflow={(workflowId) => {
            const wf = workflows.find(w => w.id === workflowId);
            setLeadField(
              detailLead.id,
              { workflowId: workflowId || '' },
              { type: 'workflow', body: workflowId ? `Assigned to workflow "${wf?.name || workflowId}"` : 'Workflow cleared' }
            );
          }}
          onOpenWorkflows={() => onGotoTab('workflows')}
          onConvert={() => convertLeadToClient(detailLead.id)}
          onSetField={(patch) => setLeadField(detailLead.id, patch)}
        />
      )}
      {templatesOpen && (
        <TemplatesPanel
          templates={leadTemplates}
          setTemplates={setLeadTemplates}
          onClose={() => setTemplatesOpen(false)}
        />
      )}
      {smsTargetLead && (() => {
        const lead = leads.find(l => l.id === smsTargetLead);
        if (!lead) { setSmsTargetLead(null); return null; }
        return (
          <TemplatePickerModal
            templates={leadTemplates}
            lead={lead}
            settings={settings}
            onCancel={() => setSmsTargetLead(null)}
            showToast={showToast}
            onPick={async (body) => {
              setSmsTargetLead(null);
              if (!body || !body.trim()) return;
              // Real Twilio send through the operator's sub-account,
              // gated by the existing TCPA compliance checks in
              // /api/sms/send-manual (consent, opt-out, phone validity).
              // Previously this opened an sms: URI which only worked on
              // mobile devices — desktop browsers either no-op'd or
              // bounced through a useless "no handler" dialog.
              try {
                const r = await apiFetch('/api/sms/send-manual', {
                  method: 'POST',
                  body: JSON.stringify({ leadId: lead.id, body }),
                });
                if (r?.dryRun) {
                  showToast(`SMS queued (dry-run — set SMS_SEND_ENABLED=true in Vercel to deliver).`);
                } else {
                  showToast(`SMS sent to ${lead.name || lead.phone || 'lead'}.`);
                }
              } catch (err) {
                const msg = String(err.message || err);
                if (/consent/i.test(msg)) {
                  showToast('Can\'t send — lead consent not captured. Open the lead and tick the consent box first.');
                } else if (/opted out/i.test(msg)) {
                  showToast('Can\'t send — lead opted out (replied STOP).');
                } else if (/E\.164|phone/i.test(msg)) {
                  showToast('Can\'t send — lead phone isn\'t a valid US number. Edit the lead and re-enter.');
                } else if (/insufficient_credits|402/i.test(msg)) {
                  showToast('Can\'t send — credits wallet empty. Top up from Settings.');
                } else {
                  showToast(`Send failed: ${msg.slice(0, 120)}`);
                }
              }
            }}
          />
        );
      })()}
      {logCallFor && (
        <CallFormModal
          initial={(() => {
            const lead = leads.find(l => l.id === logCallFor);
            if (!lead) return null;
            return {
              agentProdNum: lead.agentProdNum,
              leadId: lead.id,
              prospectName: lead.name,
              prospectPhone: lead.phone,
              date: new Date().toISOString().slice(0, 10),
              startTime: new Date().toISOString().slice(11, 16),
              direction: 'outbound',
              outcome: 'no_answer',
              duration: '',
              aiScore: '',
              notes: '',
            };
          })()}
          agentChoices={agentChoices}
          leads={leads}
          onCancel={() => setLogCallFor(null)}
          onSave={(rec) => {
            const now = new Date().toISOString();
            const id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `call_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
            // Strip the AI hint from the persisted call row.
            const { _callbackDetected, ...callRec } = rec;
            setCalls(prev => [...prev, { ...callRec, id, createdAt: now, updatedAt: now }]);
            // First call auto-advances new → contacted AND stamps speed-to-lead.
            const lead = leads.find(l => l.id === logCallFor);
            const patch = {};
            if (lead && lead.status === 'new') patch.status = 'contacted';
            if (lead && !lead.firstContactedAt) patch.firstContactedAt = now;

            // W9 Disposition → Stage: call outcome auto-moves the
            // lead's status. "Not Interested" recycles the lead;
            // "DNC" terminates it. These override the first-call
            // contacted advance because the more specific outcome
            // wins. We don't touch leads already in a terminal
            // state so a logging mistake on a closed lead doesn't
            // bounce them backwards.
            if (lead) {
              const TERMINAL = new Set(['closed', 'dnc']);
              if (!TERMINAL.has(lead.status)) {
                if (rec.outcome === 'not_interested') patch.status = 'recycled';
                else if (rec.outcome === 'dnc')         patch.status = 'dnc';
              }
            }

            // AI-detected callback in the notes → auto-schedule the
            // appointment on the lead. The agent doesn't have to type
            // it twice or remember to set a follow-up date.
            let callbackToastSuffix = '';
            if (_callbackDetected?.iso) {
              const iso = _callbackDetected.iso;
              const date = iso.slice(0, 10);
              const time = /T\d{2}:\d{2}/.test(iso) ? iso.slice(11, 16) : '';
              if (date) {
                patch.appointmentDate = date;
                if (time) patch.appointmentTime = time;
                if (lead && lead.status !== 'closed' && lead.status !== 'no_show' && lead.status !== 'dnc') {
                  patch.status = 'appt_set';
                }
                callbackToastSuffix = ` · Callback scheduled ${_callbackDetected.description || `${date}${time ? ' ' + time : ''}`}`;
              }
            }

            if (Object.keys(patch).length > 0) {
              setLeadField(logCallFor, patch, {
                type: 'status',
                body: callbackToastSuffix
                  ? `Callback auto-scheduled from call notes → ${patch.appointmentDate || ''}${patch.appointmentTime ? ' ' + patch.appointmentTime : ''}`
                  : (patch.status ? 'Stage: New → Contacted (first call)' : 'First contact stamped')
              });
            }
            // If the outcome was appt_set AND we didn't already auto-schedule, re-prompt for date.
            if (rec.outcome === 'appt_set' && !_callbackDetected?.iso && lead && lead.status !== 'appt_set' && lead.status !== 'showed' && lead.status !== 'closed') {
              setApptPrompt({ leadId: logCallFor, date: lead.appointmentDate || '', time: lead.appointmentTime || '' });
            }
            setLogCallFor(null);
            showToast(`Call logged on this lead${callbackToastSuffix}`);
          }}
        />
      )}

      {/* Enroll-in-drip modal */}
      {enrollingLead && (
        <EnrollLeadModal
          lead={enrollingLead}
          currentUser={currentUser}
          showToast={showToast}
          onClose={() => setEnrollingLead(null)}
        />
      )}
    </div>
    </LeadIntelligenceProvider>
  );
}

function LeadKanban({ leads = [], agentName, attemptsForLead, callCountFor = () => 0, daysSinceContact = () => Infinity, lastContactedFor = () => '', onOpen, onMove, onCall = () => {}, onSms = () => {}, onLogOutcome = () => {}, onAdvance = () => {}, onEnrollDrip }) {
  const byStage = React.useMemo(() => {
    const out = Object.fromEntries(LEAD_STAGES.map(s => [s.id, []]));
    leads.forEach(l => { (out[l.status] || out.new).push(l); });
    Object.values(out).forEach(arr => arr.sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || '')));
    return out;
  }, [leads]);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-9 gap-3">
      {LEAD_STAGES.map(stage => (
        <div key={stage.id} className="bg-slate-50/60 border border-slate-200 rounded-xl flex flex-col min-h-[120px]">
          <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`inline-block h-2 w-2 rounded-full bg-${stage.color}-500`}/>
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">{stage.label}</div>
            </div>
            <div className="text-xs font-semibold text-slate-500 tabular-nums">{byStage[stage.id].length}</div>
          </div>
          <div className="p-2 space-y-2 flex-1">
            {byStage[stage.id].length === 0 && (
              <div className="text-xs text-slate-400 italic text-center py-3">—</div>
            )}
            {byStage[stage.id].map(lead => {
              const i = LEAD_STAGES.findIndex(s => s.id === lead.status);
              const prevStage = i > 0 ? LEAD_STAGES[i - 1] : null;
              const nextStage = i < LEAD_STAGES.length - 1 ? LEAD_STAGES[i + 1] : null;
              return (
                <div key={lead.id} className="bg-white border border-slate-200 rounded-lg p-2 hover:shadow-sm transition-shadow">
                  <button onClick={() => onOpen(lead.id)} className="text-left w-full">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold text-sm text-slate-900 truncate">{lead.name || '—'}</div>
                      <DaysInStageBadge lead={lead}/>
                    </div>
                    <div className="text-xs text-slate-500 truncate">{lead.phone || lead.email || ''}</div>
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      <LeadScoreBadge score={calcLeadScore(lead)} lead={lead}/>
                      {(() => {
                        // Plain-language contact state per Phase 7d:
                        //   Never attempted  — no calls, no outreach
                        //   Not reached      — has attempts, nobody answered
                        //   Reached          — firstContactedAt exists
                        // Prior label ("Xd uncontacted") was confusing next to
                        // "2 calls" — implied contradiction. Now the two labels
                        // read as complementary: "2 attempts · Not reached yet".
                        const s = speedToLeadMinutes(lead);
                        const attempts = attemptsForLead(lead.id);
                        if (s == null && !lead.firstContactedAt) {
                          if (attempts > 0) {
                            const elapsed = lead.createdAt
                              ? Math.floor((Date.now() - new Date(lead.createdAt).getTime()) / (1000 * 60))
                              : null;
                            return <span className="bg-amber-100 text-amber-800 text-[10px] font-semibold px-1.5 py-0.5 rounded" title={`${attempts} outreach attempt${attempts === 1 ? '' : 's'}, nobody answered yet`}>
                              Not reached{elapsed != null ? ` · ${fmtSpeed(elapsed)} old` : ''}
                            </span>;
                          }
                          if (lead.createdAt) {
                            const elapsed = Math.floor((Date.now() - new Date(lead.createdAt).getTime()) / (1000 * 60));
                            return <span className="bg-rose-100 text-rose-700 text-[10px] font-semibold px-1.5 py-0.5 rounded" title="Lead has never been attempted — speed-to-lead clock is running">Never attempted · {fmtSpeed(elapsed)}</span>;
                          }
                          return null;
                        }
                        if (s == null) return null;
                        const b = speedBucket(s);
                        if (!b) return null;
                        return <span className={`bg-${b.color}-100 text-${b.color}-700 text-[10px] font-semibold px-1.5 py-0.5 rounded`} title={`Reached in ${fmtSpeed(s)} after receipt`}>⚡ Reached in {fmtSpeed(s)}</span>;
                      })()}
                      {lead.optOut && <span className="bg-red-100 text-red-700 text-[10px] font-semibold px-1.5 py-0.5 rounded" title="Lead has opted out — do not contact">✗ Opted out</span>}
                      {!lead.optOut && !lead.consentAt && <span className="bg-amber-100 text-amber-700 text-[10px] font-semibold px-1.5 py-0.5 rounded" title="No TCPA/A2P consent captured yet — confirm before marketing outreach">Consent needed</span>}
                      {lead.followUpDate && <span className="bg-amber-100 text-amber-700 text-[10px] font-semibold px-1.5 py-0.5 rounded">fu {lead.followUpDate.slice(5, 10)}</span>}
                    </div>
                    <div className="flex items-center gap-1 mt-1 text-[10px] flex-wrap">
                      {lead.leadSource && <span className="bg-slate-100 text-slate-700 px-1 py-0.5 rounded">{lead.leadSource}</span>}
                      {(() => {
                        // Plain-language activity: "2 attempts" (spelled out,
                        // was "att"), and only show call count if the two
                        // numbers differ. Prior "2 calls · 2 att" was
                        // redundant and confusing to non-technical agents.
                        const calls = callCountFor(lead.id);
                        const attempts = attemptsForLead(lead.id);
                        if (attempts === 0) return null;
                        return (
                          <span
                            className="bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded font-semibold"
                            title={calls !== attempts ? `${calls} call${calls === 1 ? '' : 's'} + ${attempts - calls} other outreach` : `${attempts} outreach attempts`}
                          >
                            {attempts} attempt{attempts === 1 ? '' : 's'}
                          </span>
                        );
                      })()}
                      {(() => {
                        // Last contact display — surfaces BOTH the date and the
                        // relative age so agents don't have to do mental math.
                        // Color codes the relative age; the date appears as a
                        // subtle prefix so it's always visible.
                        const d = daysSinceContact(lead.id);
                        const lc = lastContactedFor(lead.id);
                        const datePrefix = lc ? `${fmtDateUS(lc)} · ` : '';
                        // Audit 2026-06-29 BUG-014: red "never contacted" for
                        // brand-new leads reads as urgency when it's just
                        // freshness. Use slate "New" for leads where the
                        // creation gap is short (<24h); reserve red for true
                        // stale-untouched.
                        if (d === Infinity) {
                          const ageMs = lead.createdAt ? (Date.now() - new Date(lead.createdAt).getTime()) : null;
                          const fresh = ageMs != null && ageMs < 24 * 3600 * 1000;
                          return fresh
                            ? <span className="bg-slate-100 text-slate-600 px-1 py-0.5 rounded" title="New lead — not yet contacted">New</span>
                            : <span className="bg-rose-100 text-rose-700 px-1 py-0.5 rounded font-semibold" title="Lead has never been contacted">never contacted</span>;
                        }
                        if (d >= 14)        return <span className="bg-red-100 text-red-700 px-1 py-0.5 rounded font-semibold" title={`Last contact ${lc || ''}`}>{datePrefix}{d}d stale</span>;
                        if (d >= 7)         return <span className="bg-amber-100 text-amber-700 px-1 py-0.5 rounded" title={`Last contact ${lc || ''}`}>{datePrefix}{d}d ago</span>;
                        return <span className="bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded" title={`Last contact ${lc || ''}`}>{datePrefix}{d === 0 ? 'today' : `${d}d ago`}</span>;
                      })()}
                      {lead.status === 'closed' && !lead.lapsed && <span className="bg-amber-100 text-amber-800 px-1 py-0.5 rounded font-bold" title="Lead converted — counts toward your close rate">WON</span>}
                      {lead.status === 'closed' && lead.lapsed && <span className="bg-red-100 text-red-700 px-1 py-0.5 rounded">lapsed</span>}
                    </div>
                    {(lead.tags || []).length > 0 && (
                      <div className="flex items-center gap-1 mt-1 flex-wrap">
                        {(lead.tags || []).slice(0, 3).map(t => (
                          <span key={t} className="bg-violet-100 text-violet-700 text-[10px] font-semibold px-1 py-0.5 rounded">{t}</span>
                        ))}
                        {(lead.tags || []).length > 3 && <span className="text-[10px] text-slate-400">+{(lead.tags || []).length - 3}</span>}
                      </div>
                    )}
                    {lead.appointmentDate && (
                      <div className="text-[11px] text-amber-700 mt-1 truncate">{fmtDateUS(lead.appointmentDate)}{lead.appointmentTime ? ` ${lead.appointmentTime}` : ''}</div>
                    )}
                    <div className="text-[11px] text-slate-400 mt-1 truncate">{agentName(lead.agentProdNum)}</div>
                  </button>
                  <LeadQuickActions lead={lead} compact onCall={onCall} onSms={onSms} onLogOutcome={onLogOutcome} onAdvance={onAdvance} onEnrollDrip={onEnrollDrip}/>
                  <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-slate-100">
                    {prevStage ? (
                      <button onClick={() => onMove(lead.id, prevStage.id)} className="text-[10px] text-slate-500 hover:text-slate-800" title={`Back to ${prevStage.label}`}>←</button>
                    ) : <span/>}
                    {nextStage ? (
                      <button onClick={() => onMove(lead.id, nextStage.id)} className={`text-[10px] font-medium text-${nextStage.color}-700 hover:text-${nextStage.color}-900`} title={`To ${nextStage.label}`}>→ {nextStage.label}</button>
                    ) : <span/>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// Column definitions for LeadTable. `def` = visible-by-default.
// When `essential`, the column can't be hidden via the picker (Name,
// Actions, the checkbox). Order here drives column order in the table.
const LEAD_TABLE_COLS = [
  { id: 'select',       label: '',              essential: true },
  { id: 'name',         label: 'Name',          essential: true },
  { id: 'score',        label: 'Score',         def: true  },
  { id: 'phone',        label: 'Phone',         def: true  },
  { id: 'age',          label: 'Age',           def: false },
  { id: 'dob',          label: 'DOB',           def: false },
  { id: 'state',        label: 'State',         def: false },
  { id: 'address',      label: 'Address',       def: false },
  { id: 'status',       label: 'Status',        def: true  },
  { id: 'daysInStage',  label: 'Days in stage', def: true  },
  { id: 'leadSource',   label: 'Lead source',   def: false },
  { id: 'agent',        label: 'Agent',         def: false },
  { id: 'calls',        label: 'Calls',         def: true  },
  { id: 'attempts',     label: 'Attempts',      def: false },
  { id: 'speed',        label: 'Speed',         def: false },
  { id: 'lastContact',  label: 'Last contact',  def: true  },
  { id: 'appt',         label: 'Appt',          def: false },
  { id: 'followUp',     label: 'Follow-up',     def: true  },
  { id: 'tags',         label: 'Tags',          def: false },
  { id: 'cost',         label: 'Cost',          def: false },
  { id: 'premium',      label: 'Premium',       def: true  },
  { id: 'actions',      label: 'Actions',       essential: true },
];
const LEAD_TABLE_COLS_LS_KEY = 'crm:leadTable:visibleCols:v1';

function LeadTable({ leads = [], agentName, attemptsForLead, callCountFor = () => 0, lastCallFor = () => '', lastContactedFor, daysSinceContact = () => Infinity, selectedIds = {}, setSelectedIds = () => {}, onOpen, onCall = () => {}, onSms = () => {}, onLogOutcome = () => {}, onAdvance = () => {}, onEnrollDrip }) {
  const [sortKey, setSortKey] = useState('score');
  const [sortDir, setSortDir] = useState('desc');
  const [visibleCols, setVisibleCols] = useState(() => {
    const defaults = new Set(LEAD_TABLE_COLS.filter(c => c.essential || c.def).map(c => c.id));
    try {
      const raw = localStorage.getItem(LEAD_TABLE_COLS_LS_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          const next = new Set(arr);
          LEAD_TABLE_COLS.forEach(c => { if (c.essential) next.add(c.id); });
          return next;
        }
      }
    } catch { /* fall through to defaults */ }
    return defaults;
  });
  const show = (id) => visibleCols.has(id);
  const toggleCol = (id) => {
    setVisibleCols(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { localStorage.setItem(LEAD_TABLE_COLS_LS_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const colMenuRef = React.useRef(null);
  // Perf: cap rendered rows so a large book doesn't paint thousands of DOM
  // nodes at once (janky typing/scroll). User can reveal the rest on demand.
  const LEAD_ROW_CAP = 100;
  const [showAllRows, setShowAllRows] = useState(false);
  React.useEffect(() => {
    if (!colMenuOpen) return;
    const onDoc = (e) => { if (colMenuRef.current && !colMenuRef.current.contains(e.target)) setColMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [colMenuOpen]);
  const visibleColCount = LEAD_TABLE_COLS.filter(c => show(c.id)).length;
  const sorted = [...leads].sort((a, b) => {
    let av, bv;
    if (sortKey === 'calls') { av = callCountFor(a.id); bv = callCountFor(b.id); }
    else if (sortKey === 'attempts') { av = attemptsForLead(a.id); bv = attemptsForLead(b.id); }
    else if (sortKey === 'score') { av = calcLeadScore(a); bv = calcLeadScore(b); }
    else if (sortKey === 'daysInStage') { av = daysInStage(a); bv = daysInStage(b); }
    else if (sortKey === 'speed') {
      const sa = speedToLeadMinutes(a); const sb = speedToLeadMinutes(b);
      av = sa == null ? Number.MAX_SAFE_INTEGER : sa;
      bv = sb == null ? Number.MAX_SAFE_INTEGER : sb;
    }
    else if (sortKey === 'lastContact') { av = lastContactedFor(a.id) || ''; bv = lastContactedFor(b.id) || ''; }
    else if (sortKey === 'age') {
      // Numeric sort with empty values pushed to the end.
      const na = Number(a.age); const nb = Number(b.age);
      av = Number.isFinite(na) ? na : Number.MAX_SAFE_INTEGER;
      bv = Number.isFinite(nb) ? nb : Number.MAX_SAFE_INTEGER;
    }
    else if (sortKey === 'dob') {
      // ISO date strings sort lexicographically — empties to the end.
      av = a.dob || 'ZZZZ'; bv = b.dob || 'ZZZZ';
    }
    else { av = a[sortKey] || ''; bv = b[sortKey] || ''; }
    if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
    const cmp = String(av).localeCompare(String(bv));
    return sortDir === 'asc' ? cmp : -cmp;
  });
  const headerBtn = (k, label) => (
    <button onClick={() => { if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(k); setSortDir('asc'); } }} className="hover:text-slate-900">
      {label}{sortKey === k ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
    </button>
  );
  const selectedCount = sorted.filter(l => selectedIds[l.id]).length;
  const allSelected = sorted.length > 0 && selectedCount === sorted.length;
  const toggleAll = () => {
    if (allSelected) setSelectedIds({});
    else { const next = {}; sorted.forEach(l => { next[l.id] = true; }); setSelectedIds(next); }
  };
  const toggleOne = (id, e) => { e.stopPropagation(); setSelectedIds(prev => ({ ...prev, [id]: !prev[id] })); };
  const freshnessChip = (id) => {
    const d = daysSinceContact(id);
    if (d === Infinity) return <span className="bg-rose-100 text-rose-700 px-1 py-0.5 rounded text-[10px] font-semibold">never</span>;
    if (d >= 14) return <span className="bg-red-100 text-red-700 px-1 py-0.5 rounded text-[10px] font-semibold">{d}d</span>;
    if (d >= 7)  return <span className="bg-amber-100 text-amber-700 px-1 py-0.5 rounded text-[10px]">{d}d</span>;
    return <span className="bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded text-[10px]">{d === 0 ? 'today' : `${d}d`}</span>;
  };
  const dash = <span className="text-slate-300">—</span>;
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-white">
        <div className="text-xs text-slate-500">{sorted.length} {sorted.length === 1 ? 'lead' : 'leads'}</div>
        <div className="relative" ref={colMenuRef}>
          <button
            onClick={() => setColMenuOpen(v => !v)}
            className={`text-xs font-medium px-2.5 py-1 rounded-md border border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300 transition-colors ${colMenuOpen ? 'bg-slate-100 text-slate-900 border-slate-300' : 'bg-white'}`}
          >
            Columns ({visibleColCount - 2})
          </button>
          {colMenuOpen && (
            <div className="absolute right-0 top-full mt-1 z-30 w-56 rounded-md border border-slate-200 bg-white shadow-lg py-1 text-xs max-h-80 overflow-y-auto">
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Show columns</div>
              {LEAD_TABLE_COLS.filter(c => !c.essential).map(c => (
                <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-slate-700">
                  <input
                    type="checkbox"
                    checked={show(c.id)}
                    onChange={() => toggleCol(c.id)}
                    className="rounded"
                  />
                  <span>{c.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-slate-200 bg-slate-50">
              {show('select')      && <th className="py-2.5 pl-3 pr-1 w-8"><input type="checkbox" checked={allSelected} onChange={toggleAll} className="rounded" aria-label="Select all"/></th>}
              {show('name')        && <th className="py-2.5 px-3">{headerBtn('name', 'Name')}</th>}
              {show('score')       && <th className="py-2.5 px-3 text-right">{headerBtn('score', 'Score')}</th>}
              {show('phone')       && <th className="py-2.5 px-3">Phone</th>}
              {show('age')         && <th className="py-2.5 px-3 text-right">{headerBtn('age', 'Age')}</th>}
              {show('dob')         && <th className="py-2.5 px-3">{headerBtn('dob', 'DOB')}</th>}
              {show('state')       && <th className="py-2.5 px-3">{headerBtn('state', 'State')}</th>}
              {show('address')     && <th className="py-2.5 px-3">Address</th>}
              {show('status')      && <th className="py-2.5 px-3">Status</th>}
              {show('daysInStage') && <th className="py-2.5 px-3 text-right">{headerBtn('daysInStage', 'Days in stage')}</th>}
              {show('leadSource')  && <th className="py-2.5 px-3">{headerBtn('leadSource', 'Lead source')}</th>}
              {show('agent')       && <th className="py-2.5 px-3">Agent</th>}
              {show('calls')       && <th className="py-2.5 px-3 text-right">{headerBtn('calls', 'Calls')}</th>}
              {show('attempts')    && <th className="py-2.5 px-3 text-right">{headerBtn('attempts', 'Attempts')}</th>}
              {show('speed')       && <th className="py-2.5 px-3 text-right">{headerBtn('speed', 'Speed')}</th>}
              {show('lastContact') && <th className="py-2.5 px-3">{headerBtn('lastContact', 'Last contact')}</th>}
              {show('appt')        && <th className="py-2.5 px-3">Appt</th>}
              {show('followUp')    && <th className="py-2.5 px-3">Follow-up</th>}
              {show('tags')        && <th className="py-2.5 px-3">Tags</th>}
              {show('cost')        && <th className="py-2.5 px-3 text-right">{headerBtn('cost', 'Cost')}</th>}
              {show('premium')     && <th className="py-2.5 px-3 text-right">Premium</th>}
              {show('actions')     && <th className="py-2.5 px-3 w-36 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr><td colSpan={visibleColCount} className="py-8 text-center text-slate-400">No leads match these filters.</td></tr>
            )}
            {(showAllRows ? sorted : sorted.slice(0, LEAD_ROW_CAP)).map(l => {
              const stage = LEAD_STAGE_BY_ID[l.status] || LEAD_STAGES[0];
              const sel = !!selectedIds[l.id];
              const callN = callCountFor(l.id);
              return (
                <tr key={l.id} onClick={() => onOpen(l.id)} className={`border-b border-slate-100 cursor-pointer transition-colors ${sel ? 'bg-slate-100' : 'hover:bg-slate-50'}`}>
                  {show('select') && <td className="py-2.5 pl-3 pr-1" onClick={e => e.stopPropagation()}><input type="checkbox" checked={sel} onChange={e => toggleOne(l.id, e)} className="rounded"/></td>}
                  {show('name') && <td className="py-2.5 px-3 font-medium text-slate-900">{l.name || dash}</td>}
                  {show('score') && <td className="py-2.5 px-3 text-right"><LeadScoreBadge score={calcLeadScore(l)} lead={l}/></td>}
                  {show('phone') && <td className="py-2.5 px-3 text-slate-600 whitespace-nowrap tabular-nums">{l.phone ? fmtPhoneUS(l.phone) : dash}</td>}
                  {show('age') && <td className="py-2.5 px-3 text-right tabular-nums text-slate-700">{l.age || dash}</td>}
                  {show('dob') && <td className="py-2.5 px-3 text-slate-600 whitespace-nowrap tabular-nums">{l.dob ? fmtDateUS(l.dob) : dash}</td>}
                  {show('state') && <td className="py-2.5 px-3 text-slate-600 font-mono uppercase text-xs">{l.state || dash}</td>}
                  {show('address') && (() => {
                    const locality = [l.city, l.state, l.zip].filter(Boolean).join(' ').trim();
                    const full = [l.address, locality].filter(Boolean).join(', ').trim();
                    return (
                      <td className="py-2.5 px-3 text-slate-600 max-w-[260px] truncate" title={full || ''}>
                        {full || dash}
                      </td>
                    );
                  })()}
                  {show('status') && (
                    <td className="py-2.5 px-3">
                      <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold bg-${stage.color}-50 text-${stage.color}-700 border border-${stage.color}-200`}>{stage.label}</span>
                      {l.status === 'closed' && !l.lapsed && <span className="ml-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200" title="Converted">WON</span>}
                      {l.status === 'closed' && l.lapsed && <span className="ml-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">lapsed</span>}
                    </td>
                  )}
                  {show('daysInStage') && <td className="py-2.5 px-3 text-right"><DaysInStageBadge lead={l}/></td>}
                  {show('leadSource') && <td className="py-2.5 px-3 text-slate-600 whitespace-nowrap">{l.leadSource || l.source || dash}</td>}
                  {show('agent') && <td className="py-2.5 px-3 text-slate-600">{agentName(l.agentProdNum) || dash}</td>}
                  {show('calls') && (
                    <td className="py-2.5 px-3 text-right tabular-nums">
                      {callN > 0 ? <span className="text-emerald-700 font-semibold">{callN}</span> : <span className="text-slate-300">0</span>}
                    </td>
                  )}
                  {show('attempts') && <td className="py-2.5 px-3 text-right tabular-nums text-slate-600">{attemptsForLead(l.id) || dash}</td>}
                  {show('speed') && (
                    <td className="py-2.5 px-3 text-right tabular-nums">
                      {(() => {
                        const s = speedToLeadMinutes(l);
                        if (s == null) return dash;
                        const b = speedBucket(s);
                        return <span className={`bg-${b.color}-50 text-${b.color}-700 border border-${b.color}-200 text-[10px] font-semibold px-1.5 py-0.5 rounded-md`}>{fmtSpeed(s)}</span>;
                      })()}
                    </td>
                  )}
                  {show('lastContact') && (
                    <td className="py-2.5 px-3 text-slate-600 whitespace-nowrap">
                      <span className="mr-1.5">{lastContactedFor(l.id) ? fmtDateUS(lastContactedFor(l.id)) : dash}</span>
                      {freshnessChip(l.id)}
                    </td>
                  )}
                  {show('appt') && <td className="py-2.5 px-3 text-amber-700 whitespace-nowrap">{l.appointmentDate ? `${fmtDateUS(l.appointmentDate)}${l.appointmentTime ? ' ' + l.appointmentTime : ''}` : dash}</td>}
                  {show('followUp') && <td className="py-2.5 px-3 whitespace-nowrap">{l.followUpDate ? <span className={`${isOverdueFollowUp(l) ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'} border text-[11px] font-semibold px-2 py-0.5 rounded-md`}>{fmtDateUS(l.followUpDate)}</span> : dash}</td>}
                  {show('tags') && (
                    <td className="py-2.5 px-3">
                      {(l.tags || []).slice(0, 3).map(t => (
                        <span key={t} className="bg-slate-100 text-slate-700 border border-slate-200 text-[10px] font-medium px-1.5 py-0.5 rounded-md mr-1">{t}</span>
                      ))}
                      {(l.tags || []).length > 3 && <span className="text-[10px] text-slate-400">+{(l.tags || []).length - 3}</span>}
                    </td>
                  )}
                  {show('cost') && <td className="py-2.5 px-3 text-right tabular-nums text-slate-700">{l.cost > 0 ? fmt(l.cost) : dash}</td>}
                  {show('premium') && <td className="py-2.5 px-3 text-right tabular-nums font-semibold text-slate-900">{l.premium > 0 ? fmt(l.premium) : dash}</td>}
                  {show('actions') && (
                    <td className="py-2.5 px-3" onClick={e => e.stopPropagation()}>
                      <div className="flex justify-end">
                        <LeadQuickActions lead={l} compact inline onCall={onCall} onSms={onSms} onLogOutcome={onLogOutcome} onAdvance={onAdvance} onEnrollDrip={onEnrollDrip}/>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!showAllRows && sorted.length > LEAD_ROW_CAP && (
        <div className="flex items-center justify-center gap-3 px-3 py-3 border-t border-slate-100 bg-white text-sm">
          <span className="text-slate-500">Showing {LEAD_ROW_CAP} of {sorted.length} leads</span>
          <button
            onClick={() => setShowAllRows(true)}
            className="font-medium px-3 py-1.5 rounded-md border border-slate-200 text-slate-700 hover:text-slate-900 hover:border-slate-300 transition-colors"
          >
            Show all {sorted.length}
          </button>
        </div>
      )}
    </div>
  );
}

function LeadBulkBar({ selectedIds, allLeads, agentChoices, allTags = [], onAssignAgent, onChangeStatus, onAddTag, onDelete, onClear, onPowerDial, powerDialBusy = false }) {
  const ids = Object.keys(selectedIds).filter(id => selectedIds[id]);
  const [tagDraft, setTagDraft] = useState('');
  const submitTag = () => { if (tagDraft.trim()) { onAddTag(tagDraft.trim()); setTagDraft(''); } };
  return (
    <div className="bg-slate-900 text-white rounded-xl px-3 py-2 flex items-center gap-2 flex-wrap shadow-sm">
      <div className="font-semibold text-sm">
        {ids.length} selected
      </div>
      <div className="h-5 w-px bg-slate-700 mx-1"/>
      <select onChange={e => { if (e.target.value) { onAssignAgent(e.target.value); e.target.value = ''; } }} className="bg-slate-800 border border-slate-700 text-white text-xs rounded-md px-2 py-1 hover:bg-slate-700 transition-colors">
        <option value="">Assign agent…</option>
        {agentChoices.map(a => <option key={a.prodNum} value={a.prodNum}>{a.name}</option>)}
      </select>
      <select onChange={e => { if (e.target.value) { onChangeStatus(e.target.value); e.target.value = ''; } }} className="bg-slate-800 border border-slate-700 text-white text-xs rounded-md px-2 py-1 hover:bg-slate-700 transition-colors">
        <option value="">Change status…</option>
        {LEAD_STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
      </select>
      <div className="flex items-center gap-1">
        <input
          value={tagDraft}
          onChange={e => setTagDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') submitTag(); }}
          placeholder="Add tag…"
          list="bulk-tag-suggestions"
          className="bg-slate-800 border border-slate-700 text-white placeholder-slate-400 text-xs rounded-md px-2 py-1 w-32 focus:outline-none focus:border-slate-500"
        />
        <datalist id="bulk-tag-suggestions">
          {allTags.map(t => <option key={t} value={t}/>)}
        </datalist>
        <button onClick={submitTag} disabled={!tagDraft.trim()} className="bg-slate-800 border border-slate-700 hover:bg-slate-700 disabled:opacity-40 text-white text-xs font-medium rounded-md px-2 py-1 transition-colors">Tag</button>
      </div>
      {onPowerDial && (
        <button
          onClick={onPowerDial}
          disabled={powerDialBusy || ids.length === 0}
          className="bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-xs font-bold rounded-md px-2.5 py-1 transition-colors"
          title="Push the selected leads into your Kixie Powerlist"
        >
          {powerDialBusy ? 'Queueing…' : `Power Dial ${ids.length}`}
        </button>
      )}
      <button onClick={onDelete} className="text-red-300 hover:text-red-200 text-xs font-medium rounded-md px-2 py-1 transition-colors" title="Delete selected leads">Delete</button>
      <button onClick={onClear} className="ml-auto text-xs text-slate-400 hover:text-white transition-colors">Clear</button>
    </div>
  );
}

// ── Today's Focus view ───────────────────────────────────────────
function TodaysFocusView({ appointments = [], overdue = [], goingCold = [], agentName, onOpenLead, onLogOutcome, onCall, onSms, onAdvance, onMoveTo, callsForLead = () => [], outreachForLead = () => [] }) {
  const empty = appointments.length === 0 && overdue.length === 0 && goingCold.length === 0;
  if (empty) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
        <CheckCircle size={28} className="mx-auto text-emerald-500 mb-2"/>
        <div className="text-slate-900 font-semibold">You're all caught up.</div>
        <div className="text-sm text-slate-500 mt-1">No urgent leads today. Head to Pipeline to keep deals moving.</div>
      </div>
    );
  }
  // One unified section style — neutral white card with a colored
  // left-edge bar that signals urgency. Saves the eye from three
  // saturated panel backgrounds stacked vertically.
  const Section = ({ tone, title, count, children }) => {
    const bar = tone === 'red' ? 'before:bg-red-500'
      : tone === 'amber' ? 'before:bg-amber-500'
      : 'before:bg-slate-300';
    return (
      <div className={`relative bg-white border border-slate-200 rounded-xl overflow-hidden before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 ${bar}`}>
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="font-semibold text-slate-900">{title}</div>
          <div className="text-xs font-semibold text-slate-500">{count}</div>
        </div>
        <div className="divide-y divide-slate-100">{children}</div>
      </div>
    );
  };
  const PrimaryBtn = ({ onClick, children }) => (
    <button onClick={onClick} className="bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors">{children}</button>
  );
  return (
    <div className="space-y-4">
      {appointments.length > 0 && (
        <Section tone="amber" title="Appointments today" count={appointments.length}>
          {appointments.map(l => (
            <FocusRow
              key={l.id}
              lead={l}
              agentLabel={agentName(l.agentProdNum)}
              primary={<span className="text-amber-700 font-semibold text-xs">{l.appointmentTime || 'all day'}</span>}
              cta={<PrimaryBtn onClick={(e) => { e.stopPropagation(); onLogOutcome(l.id); }}>Log outcome</PrimaryBtn>}
              onOpen={() => onOpenLead(l.id)}
              onCall={onCall}
              onSms={onSms}
              callHistory={callsForLead(l.id)}
              outreachLog={outreachForLead(l.id)}
            />
          ))}
        </Section>
      )}

      {overdue.length > 0 && (
        <Section tone="red" title="Overdue follow-ups" count={overdue.length}>
          {overdue.map(l => {
            const daysLate = Math.max(0, Math.floor((Date.now() - new Date(l.followUpDate).getTime()) / (1000 * 60 * 60 * 24)));
            return (
              <FocusRow
                key={l.id}
                lead={l}
                agentLabel={agentName(l.agentProdNum)}
                primary={<span className="bg-red-50 text-red-700 border border-red-200 text-[11px] font-semibold px-2 py-0.5 rounded-md">{daysLate}d overdue</span>}
                cta={<PrimaryBtn onClick={(e) => { e.stopPropagation(); onAdvance(l.id); }}>Take action</PrimaryBtn>}
                onOpen={() => onOpenLead(l.id)}
                onCall={onCall}
                onSms={onSms}
                callHistory={callsForLead(l.id)}
                outreachLog={outreachForLead(l.id)}
              />
            );
          })}
        </Section>
      )}

      {goingCold.length > 0 && (
        <Section tone="amber" title="Going cold" count={goingCold.length}>
          {goingCold.map(l => {
            const d = daysInStage(l);
            const stage = LEAD_STAGE_BY_ID[l.status] || LEAD_STAGES[0];
            return (
              <FocusRow
                key={l.id}
                lead={l}
                agentLabel={agentName(l.agentProdNum)}
                primary={<span className="bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-semibold px-2 py-0.5 rounded-md">{d}d in {stage.label}</span>}
                cta={
                  <select onChange={(e) => { e.stopPropagation(); if (e.target.value) { onMoveTo(l.id, e.target.value); e.target.value = ''; } }} onClick={e => e.stopPropagation()} className="bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap appearance-none cursor-pointer transition-colors">
                    <option value="">Move forward…</option>
                    {LEAD_STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                }
                onOpen={() => onOpenLead(l.id)}
                onCall={onCall}
                onSms={onSms}
                callHistory={callsForLead(l.id)}
                outreachLog={outreachForLead(l.id)}
              />
            );
          })}
        </Section>
      )}
    </div>
  );
}

function FocusRow({ lead, agentLabel, primary, cta, onOpen, onCall, onSms, callHistory = [], outreachLog = [] }) {
  const score = calcLeadScore(lead);
  // Compute AI priority bucket + palette. Same rules as
  // /api/leads/morning-brief. Pull richer intelligence (score %,
  // expected commission, opener, objection, recommended action)
  // from the LeadIntelligenceProvider so every card renders the
  // same numbers as the top-of-page banner.
  const { computeLeadPriority } = require('./leads/leadPriority');
  const { PRIORITY_LABEL, priorityPalette } = require('./leads/leadPriorityColor');
  const LeadExpandedIntelligence = require('./leads/LeadExpandedIntelligence').default;
  const { derivePipelineStage, stageColor } = require('./leads/derivePipelineStage');
  const derivedStage = derivePipelineStage(lead, callHistory, outreachLog);
  const bucket = computeLeadPriority(lead, callHistory, outreachLog);
  const p = priorityPalette(bucket);
  const pMeta = PRIORITY_LABEL[bucket];
  const insight = useLeadInsight(lead.id);
  const [expanded, setExpanded] = React.useState(false);
  const commissionFmt = insight?.expected_commission_cents
    ? `$${Math.round(insight.expected_commission_cents / 100).toLocaleString()}`
    : null;
  return (
    <div
      style={{
        background: p.bg,
        borderLeft: `4px solid ${p.accent}`,
      }}
      className="px-4 py-3 transition-all"
    >
      {/* Top row — identity + priority + score + commission */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-0 cursor-pointer" onClick={(e) => { e.stopPropagation(); onOpen?.(); }}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-slate-900 text-base">{lead.name || '—'}</span>
            {insight?.score_100 != null ? (
              <span style={{
                background: p.accent, color: '#fff', fontSize: 11, fontWeight: 800,
                padding: '2px 8px', borderRadius: 999, fontFamily: 'JetBrains Mono, monospace',
              }}>{insight.score_100}</span>
            ) : (
              <LeadScoreBadge score={score}/>
            )}
            {pMeta && (
              <span style={{
                background: `${p.accent}22`, color: p.accentText, border: `1px solid ${p.accent}66`,
                fontSize: 10, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase',
                padding: '2px 8px', borderRadius: 999,
              }}>{pMeta.emoji} {pMeta.label}</span>
            )}
            {insight?.probability_pct != null && insight.probability_pct > 0 && (
              <span className="text-xs font-bold" style={{ color: p.accentText }}>
                {insight.probability_pct}% close
              </span>
            )}
            {commissionFmt && (
              <span className="text-xs font-bold text-emerald-700 font-mono tabular-nums">
                {commissionFmt}
              </span>
            )}
            {primary}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {lead.phone || lead.email || '—'} · {agentLabel}
            {lead.vertical && <span className="ml-2 uppercase tracking-wider">{lead.vertical}</span>}
            {derivedStage && (
              <span style={{
                marginLeft: 8, fontSize: 9, letterSpacing: '0.06em',
                background: `${stageColor(derivedStage.key)}22`, color: stageColor(derivedStage.key),
                border: `1px solid ${stageColor(derivedStage.key)}55`,
                padding: '1px 6px', borderRadius: 4, fontWeight: 700, textTransform: 'uppercase',
              }}>{derivedStage.label}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <button onClick={(e) => { e.stopPropagation(); onCall(lead); }} className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300 text-xs font-semibold transition-colors" title="Call" aria-label="Call">
            <Phone size={14}/> Call
          </button>
          <button onClick={(e) => { e.stopPropagation(); onSms(lead.id); }} className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300 text-xs font-semibold transition-colors" title="Text" aria-label="Text">
            <MessageSquare size={14}/> Text
          </button>
          {cta}
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}
            className="h-9 px-2 inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 text-xs transition-colors"
            title={expanded ? 'Collapse intelligence' : 'Expand intelligence'}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? '▲' : '▼'}
          </button>
        </div>
      </div>

      {/* Intelligence row — reason + opener + objection */}
      {insight && (
        <div className="mt-2.5 pl-1 grid gap-1.5">
          {insight.reason && (
            <div className="text-xs" style={{ color: p.accentText }}>
              <span className="font-bold uppercase tracking-wide text-[10px] mr-1">Why</span>
              {insight.reason}
            </div>
          )}
          {insight.suggested_opener && (
            <div className="text-xs text-slate-700 italic">
              <span className="font-bold uppercase tracking-wide text-[10px] text-slate-500 mr-1 not-italic">Opener</span>
              {insight.suggested_opener}
            </div>
          )}
          {insight.likely_objection && (
            <div className="text-xs text-slate-600">
              <span className="font-bold uppercase tracking-wide text-[10px] text-slate-500 mr-1">Watch for</span>
              {insight.likely_objection}
            </div>
          )}
        </div>
      )}

      {/* Expanded intelligence panel — Phase 4. Renders inline
          when the chevron is toggled. Same panels the drawer uses. */}
      {expanded && (
        <LeadExpandedIntelligence
          lead={lead}
          callHistory={callHistory}
          outreachLog={outreachLog}
          onOpenDrawer={() => onOpen?.()}
          onCall={(l) => onCall(l)}
          onDraftReply={() => onOpen?.()}
        />
      )}

      {/* Mini comm timeline + auto-task chips — Phase 3 additions.
          Timeline: last 5 touch icons in chronological order.
          Tasks: derived from lead state without a persistence layer. */}
      <div className="mt-2.5 pl-1 flex items-center gap-2 flex-wrap">
        {(() => {
          const touches = [...callHistory, ...outreachLog]
            .map(x => ({ at: x.at || x.created_at, kind: x.type || 'call' }))
            .filter(x => x.at)
            .sort((a, b) => String(b.at).localeCompare(String(a.at)))
            .slice(0, 5)
            .reverse();
          if (touches.length === 0) return null;
          return (
            <div className="flex items-center gap-1">
              <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mr-1">Touches</span>
              {touches.map((t, i) => {
                const emoji = t.kind === 'sms' ? '💬' : t.kind === 'email' ? '✉️' : '☎️';
                return <span key={i} className="text-xs" title={t.at}>{emoji}</span>;
              })}
            </div>
          );
        })()}
        {(() => {
          const tasks = [];
          if (lead.followUpDate) {
            const days = (Date.parse(lead.followUpDate) - Date.now()) / 86_400_000;
            if (days < 0) tasks.push(`⏰ Follow-up overdue ${Math.abs(Math.floor(days))}d`);
            else if (days <= 1) tasks.push(`⏰ Follow-up today`);
          }
          if (lead.status === 'appt_set' && !lead.appointmentConfirmedAt) tasks.push('📅 Confirm appt');
          if (lead.status === 'quoted') tasks.push('📄 Follow up on quote');
          if (lead.status === 'sold' && !lead.rewardSent) tasks.push('🎁 Ask for referral');
          if (bucket === 'at_risk') tasks.push('⚠ One last attempt');
          return tasks.slice(0, 3).map((t, i) => (
            <span key={i} style={{
              fontSize: 10, background: '#fff7ed', color: '#c2410c',
              border: '1px solid #fed7aa', padding: '2px 8px', borderRadius: 999,
              fontWeight: 700,
            }}>{t}</span>
          ));
        })()}
      </div>
    </div>
  );
}

// ── Lead score badge — small dot strip ────────────────────────────
function LeadScoreBadge({ score, lead }) {
  const color = SCORE_COLOR(score);
  const filled = `bg-${color}-500`;
  const empty  = 'bg-slate-200';
  // Hover tooltip explains the score's inputs so the operator stops
  // distrusting it. Per audit §9 Phase 3 + §15 Phase 5b LEADS.
  const titleText = lead
    ? `Lead score: ${score}/5\n\n${explainLeadScore(lead).join('\n')}`
    : `Lead score: ${score}/5`;
  return (
    <span className={`inline-flex items-center gap-0.5 bg-${color}-50 text-${color}-700 border border-${color}-200 text-[10px] font-semibold px-1.5 py-0.5 rounded-md cursor-help`} title={titleText}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={`inline-block h-1.5 w-1.5 rounded-full ${i < score ? filled : empty}`}/>
      ))}
    </span>
  );
}

// ── Days-in-stage badge ───────────────────────────────────────────
function DaysInStageBadge({ lead }) {
  const d = daysInStage(lead);
  const label = LEAD_STAGE_BY_ID[lead.status]?.label || lead.status;
  const color = d >= 10 ? 'red' : d >= 5 ? 'amber' : 'slate';
  return <span className={`bg-${color}-50 text-${color}-700 border border-${color}-200 text-[10px] font-semibold px-1.5 py-0.5 rounded-md`} title={`${d} day${d === 1 ? '' : 's'} in ${label}`}>{d}d</span>;
}

// ── Overdue bell ──────────────────────────────────────────────────
function OverdueBell({ count = 0, overdue = [], open, onToggle, onClose, agentName, onOpenLead, onCall }) {
  return (
    <div className="relative">
      <button onClick={onToggle} className="relative p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors" title={`${count} overdue follow-up${count === 1 ? '' : 's'}`} aria-label="Overdue follow-ups">
        <Bell size={18}/>
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">{count > 9 ? '9+' : count}</span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={onClose}/>
          <div className="absolute left-0 top-full mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-xl z-40 max-h-96 overflow-y-auto">
            <div className="px-3 py-2 border-b border-slate-200 font-semibold text-slate-900 flex items-center justify-between">
              <span>Overdue follow-ups</span>
              <span className="text-xs text-slate-500">{overdue.length}</span>
            </div>
            {overdue.length === 0 ? (
              <div className="p-4 text-sm text-slate-500 italic text-center">Nothing overdue.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {overdue.slice(0, 10).map(l => {
                  const daysLate = Math.max(0, Math.floor((Date.now() - new Date(l.followUpDate).getTime()) / (1000 * 60 * 60 * 24)));
                  return (
                    <li key={l.id} className="p-2.5 hover:bg-slate-50 flex items-center gap-2">
                      <button onClick={() => onOpenLead(l.id)} className="flex-1 text-left">
                        <div className="font-semibold text-sm text-slate-900 truncate">{l.name}</div>
                        <div className="text-xs text-slate-500">{daysLate}d late · {agentName(l.agentProdNum)}</div>
                      </button>
                      {l.phone && <button onClick={() => onCall(l)} className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-2.5 py-1 rounded-md transition-colors">Call now</button>}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Conversation list skeleton ────────────────────────────────────
// Shown while inbox + per-lead conversation are loading. Five
// shimmer rows look like a real conversation thread so the page
// never goes blank — the user understands data is on the way.
function ConversationSkeleton({ rows = 5 }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden" aria-busy="true" aria-label="Loading conversations">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="p-4 flex items-start gap-3">
          <div className="h-9 w-9 rounded-full bg-slate-200 shrink-0 animate-pulse"/>
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-center gap-2">
              <div className="h-3 w-32 bg-slate-200 rounded animate-pulse"/>
              <div className="h-3 w-16 bg-slate-100 rounded animate-pulse"/>
            </div>
            <div className="h-3 w-3/4 bg-slate-100 rounded animate-pulse"/>
            <div className="h-3 w-1/2 bg-slate-100 rounded animate-pulse"/>
          </div>
          <div className="h-5 w-12 bg-slate-100 rounded animate-pulse shrink-0"/>
        </div>
      ))}
    </div>
  );
}

// ── Sub-nav row with overflow menu ────────────────────────────────
// Active group's tabs scroll horizontally. A right-aligned "More ▾"
// always lists every tab so the last one (e.g. Transactions on the
// 13-tab Life Insurance group) is reachable in one click even when
// it's scrolled off the visible portion of the bar. Auto-scrolls the
// active tab into view on tab change so the user can always see
// where they are.
function SubNavRow({ activeGroup, tab, setTab, activeAccent, unassignedCount, inboxUnread = 0 }) {
  const scrollRef = React.useRef(null);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  React.useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    const active = root.querySelector(`[data-tab-id="${tab}"]`);
    if (active && typeof active.scrollIntoView === 'function') {
      active.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
    }
  }, [tab]);

  const bg = activeGroup.key === 'health' ? '#f0fdfa' : '#f8fafc';
  return (
    <div className="border-t border-slate-200" style={{ background: bg }}>
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8 flex items-stretch relative">
        <div ref={scrollRef} className="flex-1 flex gap-1 overflow-x-auto scrollbar-thin">
          {activeGroup.tabs.map(([id, label]) => (
            <button
              key={id}
              data-tab-id={id}
              data-testid={id === 'clients' ? 'nav-bob' : undefined}
              onClick={() => setTab(id)}
              className={`px-3 py-1.5 my-1 text-xs font-semibold rounded-md whitespace-nowrap transition-colors ${tab === id ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-900 hover:bg-white/60'}`}
              style={tab === id ? { color: activeAccent } : undefined}
            >
              {label}
              {id === 'producers' && unassignedCount > 0 && (
                <span className="ml-1.5 bg-amber-50 text-amber-700 border border-amber-200 text-[10px] px-1.5 py-0.5 rounded-md">{unassignedCount}</span>
              )}
              {id === 'inbox' && inboxUnread > 0 && (
                <span className="ml-1.5 bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md min-w-[18px] inline-block text-center" title={`${inboxUnread} unread`}>
                  {inboxUnread > 99 ? '99+' : inboxUnread}
                </span>
              )}
            </button>
          ))}
        </div>
        {activeGroup.tabs.length > 6 && (
          <div className="relative flex items-center pl-2 border-l border-slate-200/70 ml-1" ref={menuRef}>
            <button
              onClick={() => setMenuOpen(v => !v)}
              className={`my-1 px-2.5 py-1.5 text-xs font-semibold rounded-md inline-flex items-center gap-1 transition-colors ${menuOpen ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900 hover:bg-white/60'}`}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              title="Jump to any tab in this group"
            >
              Jump to <ChevronDown size={12}/>
            </button>
            {menuOpen && (
              <div role="menu" className="absolute right-0 top-full mt-1 z-30 w-56 rounded-lg border border-slate-200 bg-white shadow-lg py-1 text-sm max-h-80 overflow-y-auto">
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-slate-400 font-semibold">
                  All {activeGroup.label} tabs ({activeGroup.tabs.length})
                </div>
                {activeGroup.tabs.map(([id, label]) => (
                  <button
                    key={id}
                    role="menuitem"
                    onClick={() => { setMenuOpen(false); setTab(id); }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 ${tab === id ? 'text-slate-900 font-semibold bg-slate-50' : 'text-slate-700'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Import-statement menu (header toolbar) ─────────────────────────
// Single entry point that fans out into Life (file upload / Americo
// paste / Transamerica paste) and Health (route to Health Policy
// Vault). Replaces 4 inline carrier buttons that were clipping off
// the right edge of narrow screens.
function ImportStatementMenu({ onFileChange, onPasteAmerico, onPasteTransamerica, onGoHealth }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${open ? 'bg-slate-100 text-slate-900 border-slate-300' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50 hover:border-slate-300'}`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Upload size={14}/> Import statement
      </button>
      {open && (
        <div role="menu" className="absolute left-0 top-full mt-1 z-30 w-64 max-w-[calc(100vw-2rem)] rounded-lg border border-slate-200 bg-white shadow-lg py-1 text-sm">
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Life</div>
          <label className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer text-slate-700">
            <Upload size={14} className="text-slate-400"/>
            <span>Upload MOO / Transamerica file</span>
            <input
              type="file"
              multiple
              accept=".xlsx,.xls,.csv,.pdf"
              className="hidden"
              onChange={(e) => { onFileChange(e); setOpen(false); }}
            />
          </label>
          <button
            role="menuitem"
            onClick={() => { setOpen(false); onPasteAmerico(); }}
            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-slate-700 text-left"
          >
            <FileText size={14} className="text-slate-400"/>
            <span>Paste Americo statement…</span>
          </button>
          <button
            role="menuitem"
            onClick={() => { setOpen(false); onPasteTransamerica(); }}
            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-slate-700 text-left"
          >
            <FileText size={14} className="text-slate-400"/>
            <span>Paste Transamerica statement…</span>
          </button>
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-slate-400 font-semibold border-t border-slate-100 mt-1">Health</div>
          <button
            role="menuitem"
            onClick={() => { setOpen(false); onGoHealth(); }}
            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-slate-700 text-left"
          >
            <Upload size={14} className="text-slate-400"/>
            <span>BCBS · Humana · Aetna · Cigna · UHC…</span>
          </button>
        </div>
      )}
    </div>
  );
}

// ── Danger menu for the Leads toolbar (rollback / delete all) ─────
// Hides destructive page-scope actions behind a "•••" so a misclick
// can't wipe the pipeline. Both actions still require explicit
// confirmation (window.prompt for type-to-confirm on Delete All).
function LeadsDangerMenu({ lastImportBatch, onRollback, leadsCount, onDeleteAll }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className={`h-9 w-9 inline-flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:text-slate-900 hover:bg-slate-50 hover:border-slate-300 transition-colors ${open ? 'bg-slate-100 text-slate-900 border-slate-300' : 'bg-white'}`}
        title="More actions"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreHorizontal size={16}/>
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-full mt-1 z-30 w-60 rounded-lg border border-slate-200 bg-white shadow-lg py-1 text-sm">
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">Danger zone</div>
          {lastImportBatch && lastImportBatch.count > 0 && (
            <button
              role="menuitem"
              onClick={() => { setOpen(false); onRollback(); }}
              className="w-full px-3 py-2.5 flex items-center gap-2 hover:bg-rose-50 text-rose-700 text-left"
            >
              <Trash2 size={14}/>
              <span>Roll back last import ({lastImportBatch.count})</span>
            </button>
          )}
          {leadsCount > 0 && (
            <button
              role="menuitem"
              onClick={() => { setOpen(false); onDeleteAll(); }}
              className="w-full px-3 py-2.5 flex items-center gap-2 hover:bg-red-50 text-red-700 text-left"
            >
              <Trash2 size={14}/>
              <span>Delete all leads ({leadsCount})</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Lead quick-action button row used on kanban cards + table ─────
function LeadQuickActions({ lead, compact = false, inline = false, onCall, onSms, onLogOutcome, onAdvance, onEnrollDrip, showToast = () => {} }) {
  const stop = (e) => e.stopPropagation();
  const [copied, setCopied] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    if (!menuOpen) return;
    const closeOnOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutside);
    return () => document.removeEventListener('mousedown', closeOnOutside);
  }, [menuOpen]);

  const copyPhone = (e) => {
    e.stopPropagation();
    if (!lead.phone) return;
    if (!navigator.clipboard?.writeText) {
      try { showToast?.('Clipboard not available — copy manually.', 'amber'); } catch {}
      return;
    }
    navigator.clipboard.writeText(lead.phone).then(() => {
      setCopied(true);
      setMenuOpen(false);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {
      // Strict-permission browsers (Safari private, some iframes) reject
      // the write silently — used to leave the operator wondering why
      // nothing happened. Surface as an amber toast.
      try { showToast?.('Browser blocked clipboard — copy manually.', 'amber'); } catch {}
    });
  };

  const iconSize = compact ? 12 : 14;
  const btnBase = `${compact ? 'h-6 w-6' : 'h-7 w-7'} inline-flex items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 hover:border-slate-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-slate-600 disabled:hover:border-slate-200`;

  return (
    <div className={`flex items-center gap-1 ${inline ? '' : (compact ? 'mt-1.5' : 'mt-2')}`} onClick={stop}>
      <button
        onClick={() => onCall(lead)}
        disabled={!lead.phone}
        className={btnBase}
        title={lead.phone ? `Call ${lead.phone}` : 'No phone on file'}
        aria-label="Call lead"
      >
        <Phone size={iconSize} />
      </button>
      <button
        onClick={() => onSms(lead.id)}
        disabled={!lead.phone}
        className={btnBase}
        title="Send text"
        aria-label="Send text"
      >
        <MessageSquare size={iconSize} />
      </button>
      <button
        onClick={() => onAdvance(lead.id)}
        className={btnBase}
        title="Move to next stage"
        aria-label="Advance stage"
      >
        <ArrowRight size={iconSize} />
      </button>

      <div className="relative" ref={menuRef}>
        <button
          onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v); }}
          className={`${btnBase} ${menuOpen ? 'bg-slate-100 text-slate-900 border-slate-300' : ''}`}
          title="More actions"
          aria-label="More actions"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <MoreHorizontal size={iconSize} />
        </button>
        {menuOpen && (
          <div
            role="menu"
            className="absolute right-0 top-full mt-1 z-30 w-44 rounded-md border border-slate-200 bg-white shadow-lg py-1 text-xs text-slate-700"
          >
            <button
              role="menuitem"
              onClick={copyPhone}
              disabled={!lead.phone}
              className="w-full px-3 py-2 flex items-center gap-2 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-left"
            >
              {copied ? <CheckCircle size={12} className="text-emerald-600" /> : <Copy size={12} />}
              <span>{copied ? 'Copied' : 'Copy phone'}</span>
            </button>
            <button
              role="menuitem"
              onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onEnrollDrip && onEnrollDrip(lead); }}
              disabled={!onEnrollDrip}
              className="w-full px-3 py-2 flex items-center gap-2 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-left"
            >
              <Send size={12} />
              <span>Enroll in drip</span>
            </button>
            <button
              role="menuitem"
              onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onLogOutcome(lead.id); }}
              className="w-full px-3 py-2 flex items-center gap-2 hover:bg-slate-50 text-left"
            >
              <CheckCircle size={12} />
              <span>Log outcome</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Templates panel + picker ──────────────────────────────────────
function TemplatesPanel({ templates = [], setTemplates, onClose }) {
  const [editingId, setEditingId] = useState(null); // id | 'new' | null
  const blank = { id: '', name: '', body: '' };
  const editing = editingId === 'new' ? blank : templates.find(t => t.id === editingId);
  const [draft, setDraft] = useState(blank);

  React.useEffect(() => {
    if (editing) setDraft({ id: editing.id || '', name: editing.name || '', body: editing.body || '' });
  }, [editingId, editing]);

  const save = () => {
    if (!draft.name.trim() || !draft.body.trim()) return;
    if (editingId === 'new') {
      const id = `tpl_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      setTemplates(prev => [...prev, { id, name: draft.name.trim(), body: draft.body }]);
    } else {
      setTemplates(prev => prev.map(t => t.id === editingId ? { ...t, name: draft.name.trim(), body: draft.body } : t));
    }
    setEditingId(null);
  };
  const remove = async (id) => {
    if (!(await hqConfirm({
      title: 'Delete this template?',
      confirmLabel: 'Delete template',
      danger: true,
    }))) return;
    setTemplates(prev => prev.filter(t => t.id !== id));
    if (editingId === id) setEditingId(null);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-stretch justify-end z-50" onClick={onClose}>
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="bg-slate-900 text-white p-5 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold flex items-center gap-2"><FileText size={20}/> Templates</h3>
            <div className="text-xs text-slate-300 mt-1">Tokens: [Name], [Agent Name], [Agent Phone], [Time]</div>
          </div>
          <button aria-label="Close" onClick={onClose} className="text-white/70 hover:text-white"><X size={20}/></button>
        </div>
        <div className="p-5 space-y-3">
          <button onClick={() => setEditingId('new')} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg text-sm font-semibold">+ New template</button>
          {editingId !== null && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
              <input value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} placeholder="Template name" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
              <textarea value={draft.body} onChange={e => setDraft(d => ({ ...d, body: e.target.value }))} placeholder="Message body…" rows={5} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
              <div className="flex justify-end gap-2">
                <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                <button onClick={save} disabled={!draft.name.trim() || !draft.body.trim()} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold">Save</button>
              </div>
            </div>
          )}
          {templates.length === 0 && editingId === null && (
            <div className="text-sm text-slate-500 italic text-center py-6">No templates yet.</div>
          )}
          <div className="space-y-2">
            {templates.map(t => (
              <div key={t.id} className="border border-slate-200 rounded-lg p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-slate-900">{t.name}</div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setEditingId(t.id)} className="text-xs text-slate-600 hover:text-slate-900 hover:underline">Edit</button>
                    <button onClick={() => remove(t.id)} className="text-xs text-red-600 hover:text-red-800 hover:underline">Delete</button>
                  </div>
                </div>
                <div className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{t.body}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function TemplatePickerModal({ templates = [], lead, settings = {}, onCancel, onPick, showToast }) {
  const ctx = {
    name: (lead?.name || '').split(' ')[0] || lead?.name || '',
    agentName: settings.ownerName || '',
    agentPhone: settings.ownerPhone || '',
    time: '',
  };
  const handlePick = (t) => {
    const body = applyTemplate(t.body, ctx);
    onPick(body);
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onCancel}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5 space-y-3 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg">Text {lead?.name || ''}</h3>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-700"><X size={20}/></button>
        </div>
        <div className="text-sm text-slate-500">Pick a template — the rendered message is copied to your clipboard, and your SMS app opens.</div>
        {templates.length === 0 ? (
          <div className="text-sm text-slate-400 italic text-center py-6">No templates yet. Open Templates from the Leads header to add some.</div>
        ) : (
          <div className="space-y-2">
            {templates.map(t => (
              <button key={t.id} onClick={() => handlePick(t)} className="w-full text-left border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 rounded-lg p-3">
                <div className="font-semibold text-slate-900 text-sm">{t.name}</div>
                <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{applyTemplate(t.body, ctx).slice(0, 200)}{t.body.length > 200 ? '…' : ''}</div>
              </button>
            ))}
          </div>
        )}
        <div className="text-right pt-2">
          <button onClick={() => onPick('')} className="text-xs text-slate-500 hover:text-slate-700 underline">Skip template (open blank SMS)</button>
        </div>
      </div>
    </div>
  );
}

// Smart search — natural-language → filter state. The operator types
// "MP closes from Texas in last 90 days I haven't called in 14 days"
// and Claude translates it to the existing filter shape. Falls back
// to a plain search if the AI returns nothing or errors.
function SmartSearchBar({ sources, agentChoices, onApply }) {
  const [q, setQ] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  async function run() {
    // Audit 2026-07-01 BUG-004: sanitize at the boundary — the same
    // filter defense we apply on lead-name inputs. React JSX escaping
    // is the primary defense against reflected XSS in the DOM, but the
    // AI backend (Anthropic) shouldn't see <script> tags either; and
    // if the resulting value ever flows into a URL query, log output,
    // or dangerouslySetInnerHTML down the line, this closes it upstream.
    const query = sanitizeFreeText(q).trim();
    if (!query) return;
    setBusy(true); setErr(null);
    try {
      const filters = await smartSearchToFilters({
        query,
        sources,
        leadSources: LEAD_SOURCES.map(s => s.id),
        agentChoices: agentChoices.map(a => ({ name: a.name, prodNum: a.prodNum })),
      });
      if (!filters || Object.keys(filters).length === 0) {
        // Fall back to plain search.
        onApply({ search: query });
      } else {
        onApply(filters);
      }
      setQ('');
    } catch (e) {
      setErr(e.message || 'Smart search failed.');
      onApply({ search: query }); // graceful degrade
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-200 rounded-xl p-2 flex items-center gap-2">
      <span className="text-xs font-bold uppercase tracking-wider text-indigo-700 pl-2">Smart search</span>
      <input
        value={q}
        onChange={e => setQ(sanitizeFreeText(e.target.value))}
        onKeyDown={e => { if (e.key === 'Enter') run(); }}
        placeholder={`e.g. "MP closes from Texas I haven't called in 14 days"`}
        className="flex-1 px-3 py-1.5 bg-white border border-indigo-200 rounded-lg text-sm focus:outline-none focus:border-indigo-500"
      />
      <button
        onClick={run}
        disabled={busy || !q.trim()}
        className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5 rounded-lg"
      >
        {busy ? 'Thinking…' : 'Search'}
      </button>
      {err && <span className="text-xs text-red-600">{err}</span>}
    </div>
  );
}

// Notes textarea wrapped with an AI "Extract fields" affordance. After
// the operator types a note, clicking Extract sends the notes to
// Claude, which returns a structured-output JSON. The operator reviews
// the proposed fields in a confirm panel before any state changes
// apply — AI suggestions never silently overwrite manual data.
function NotesWithAiExtract({ r, set }) {
  const [busy, setBusy] = React.useState(false);
  const [proposal, setProposal] = React.useState(null);
  const [err, setErr] = React.useState(null);

  async function runExtract() {
    if (!r.notes || r.notes.trim().length < 6) { setErr('Type a few sentences of notes first.'); return; }
    setBusy(true); setErr(null);
    try {
      const out = await extractFieldsFromNotes({ notes: r.notes, currentLead: r });
      if (!out || Object.keys(out).length === 0) {
        setErr('Nothing extractable in those notes yet.');
      } else {
        setProposal(out);
      }
    } catch (e) {
      setErr(e.message || 'AI extract failed.');
    } finally {
      setBusy(false);
    }
  }

  function applyProposal() {
    if (!proposal) return;
    // Apply only fields that differ from current values. Don't blow
    // away operator-entered data with AI guesses.
    if (proposal.draftDay != null && !r.draftDay) set('draftDay', proposal.draftDay);
    if (proposal.coverageType && !r.coverageType) set('coverageType', proposal.coverageType);
    if (proposal.coverageAmount && !r.estPremium && proposal.coverageAmount < 5000) set('estPremium', proposal.coverageAmount);
    if (Array.isArray(proposal.tags) && proposal.tags.length > 0) {
      const next = Array.from(new Set([...(r.tags || []), ...proposal.tags]));
      set('tags', next);
    }
    // Callback → set appointment date/time on the lead. Operator-entered
    // appointment data wins.
    if (proposal.callbackAt && !r.appointmentDate) {
      const iso = proposal.callbackAt;
      const date = iso.slice(0, 10);
      const time = /T\d{2}:\d{2}/.test(iso) ? iso.slice(11, 16) : '';
      if (date) {
        set('appointmentDate', date);
        if (time && !r.appointmentTime) set('appointmentTime', time);
      }
    }
    setProposal(null);
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500">Notes</span>
        <button
          type="button"
          onClick={runExtract}
          disabled={busy}
          className="text-xs font-semibold text-indigo-700 hover:text-indigo-900 disabled:opacity-50 inline-flex items-center gap-1"
          title="Use AI to extract structured fields (draft day, callback, tags, etc.) from these notes"
        >
          {busy ? 'Reading notes…' : 'Extract fields'}
        </button>
      </div>
      <textarea
        value={r.notes}
        onChange={e => set('notes', e.target.value)}
        rows={4}
        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
        placeholder="e.g. Spoke with James, wife on the call. Self-employed, draft day 15th. $50k MP, callback Thursday 7pm."
      />
      {err && <div className="text-xs text-red-600">{err}</div>}
      {proposal && (
        <div className="mt-2 border border-indigo-200 bg-indigo-50 rounded-lg p-3">
          <div className="text-xs font-bold uppercase tracking-wider text-indigo-700 mb-2">AI found</div>
          <dl className="text-xs space-y-1">
            {proposal.draftDay != null && <PropRow label="Draft day" value={proposal.draftDay} existing={r.draftDay}/>}
            {proposal.spouseDecisionMaker && <PropRow label="Spouse" value="involved in decision"/>}
            {proposal.selfEmployed && <PropRow label="Employment" value="self-employed"/>}
            {proposal.smoker !== undefined && <PropRow label="Smoker" value={proposal.smoker ? 'yes' : 'no'}/>}
            {proposal.budgetRange && <PropRow label="Budget" value={proposal.budgetRange}/>}
            {proposal.coverageAmount && <PropRow label="Coverage" value={`$${proposal.coverageAmount.toLocaleString()}`}/>}
            {proposal.coverageType && <PropRow label="Coverage type" value={proposal.coverageType} existing={r.coverageType}/>}
            {proposal.callbackDescription && <PropRow label="Callback" value={`${proposal.callbackDescription} — will schedule appointment`} existing={r.appointmentDate ? `${r.appointmentDate}${r.appointmentTime ? ' ' + r.appointmentTime : ''}` : null}/>}
            {Array.isArray(proposal.tags) && proposal.tags.length > 0 && <PropRow label="Tags" value={proposal.tags.join(', ')}/>}
          </dl>
          <div className="mt-2 flex items-center gap-2">
            <button type="button" onClick={applyProposal} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">Apply</button>
            <button type="button" onClick={() => setProposal(null)} className="text-xs text-slate-600 hover:text-slate-900 px-2 py-1">Discard</button>
            <span className="text-[10px] text-slate-500 ml-auto">Only fills blank fields — operator data never overwritten.</span>
          </div>
        </div>
      )}
    </div>
  );
}

function PropRow({ label, value, existing }) {
  const overrides = existing != null && existing !== '' && existing !== value;
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-slate-600 w-28 shrink-0">{label}</dt>
      <dd className="text-slate-900 font-medium text-right flex-1">
        {String(value)}
        {overrides && <span className="text-[10px] text-amber-600 ml-1">(existing kept)</span>}
      </dd>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════
// AVAILABILITY PANEL
//
// Weekly recurring availability for the AI Appointment Setter. Until
// Google/Outlook OAuth ships (Phase 6), this is the source of truth
// for "when can the operator take an appointment."
//
// Stored on subscriptions.availability as JSON:
//   { mon: [['09:00','12:00'],['13:00','17:00']], tue: [...], ... }
// ════════════════════════════════════════════════════════════════════
const DAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];
const DEFAULT_AVAILABILITY = {
  mon: [['09:00','12:00'],['13:00','17:00']],
  tue: [['09:00','12:00'],['13:00','17:00']],
  wed: [['09:00','12:00'],['13:00','17:00']],
  thu: [['09:00','12:00'],['13:00','17:00']],
  fri: [['09:00','12:00'],['13:00','17:00']],
  sat: [],
  sun: [],
};

function AvailabilityPanel({ currentUser, showToast = () => {} }) {
  const [avail, setAvail] = React.useState(null);
  const [tz, setTz] = React.useState('America/New_York');
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!currentUser?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('subscriptions')
        .select('availability, availability_timezone')
        .or(`owner_id.eq.${currentUser.id},user_id.eq.${currentUser.id}`)
        .maybeSingle();
      if (cancelled) return;
      setAvail(data?.availability || DEFAULT_AVAILABILITY);
      setTz(data?.availability_timezone || 'America/New_York');
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [currentUser?.id]);

  async function save() {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('subscriptions')
        .update({ availability: avail, availability_timezone: tz })
        .or(`owner_id.eq.${currentUser.id},user_id.eq.${currentUser.id}`);
      if (error) throw error;
      showToast('Availability saved — AI Setter will use these windows.', 'success');
    } catch (err) {
      const msg = String(err.message || err);
      if (/column .* does not exist|schema cache/i.test(msg)) {
        showToast('Schema out of date — run migration 055 in Supabase first.', 'error');
      } else {
        showToast(`Save failed: ${msg.slice(0, 100)}`, 'error');
      }
    } finally {
      setSaving(false);
    }
  }

  function toggleDay(dayKey) {
    setAvail(prev => ({
      ...prev,
      [dayKey]: (prev[dayKey]?.length > 0) ? [] : [['09:00','17:00']],
    }));
  }

  function updateWindow(dayKey, idx, slot, value) {
    setAvail(prev => {
      const next = { ...prev, [dayKey]: [...(prev[dayKey] || [])] };
      next[dayKey][idx] = [...next[dayKey][idx]];
      next[dayKey][idx][slot] = value;
      return next;
    });
  }

  function addWindow(dayKey) {
    setAvail(prev => ({ ...prev, [dayKey]: [...(prev[dayKey] || []), ['13:00','17:00']] }));
  }

  function removeWindow(dayKey, idx) {
    setAvail(prev => ({ ...prev, [dayKey]: (prev[dayKey] || []).filter((_, i) => i !== idx) }));
  }

  if (loading) return <div className="bg-white border border-slate-200 rounded-xl p-5 text-sm italic text-slate-400">Loading availability…</div>;
  if (!avail) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
      <div>
        <h3 className="font-bold text-slate-900 text-base">📅 AI Appointment Setter availability</h3>
        <p className="text-xs text-slate-500 mt-0.5">
          When the AI Setter offers appointment times to inbound leads, it picks from these weekly windows. Edit any day; toggle off to mark unavailable.
        </p>
      </div>

      <div className="flex items-center gap-2 text-xs">
        <span className="text-slate-500">Timezone</span>
        <select value={tz} onChange={e => setTz(e.target.value)} className="border border-slate-300 rounded px-2 py-1 bg-white">
          <option value="America/New_York">Eastern (NY)</option>
          <option value="America/Chicago">Central (CHI)</option>
          <option value="America/Denver">Mountain (DEN)</option>
          <option value="America/Phoenix">Arizona (no DST)</option>
          <option value="America/Los_Angeles">Pacific (LA)</option>
          <option value="America/Anchorage">Alaska</option>
          <option value="Pacific/Honolulu">Hawaii</option>
        </select>
      </div>

      <div className="space-y-2">
        {DAYS.map(d => {
          const windows = avail[d.key] || [];
          const enabled = windows.length > 0;
          return (
            <div key={d.key} className={`border rounded-lg p-3 ${enabled ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-slate-50'}`}>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={() => toggleDay(d.key)}
                    className="rounded"
                  />
                  <span className={`font-bold uppercase tracking-tight text-sm ${enabled ? 'text-slate-900' : 'text-slate-400'}`}>{d.label}</span>
                </label>
                {enabled && (
                  <button onClick={() => addWindow(d.key)} className="text-xs text-emerald-700 hover:text-emerald-900 underline">+ Add window</button>
                )}
              </div>
              {enabled && windows.map((w, i) => (
                <div key={i} className="flex items-center gap-2 mt-2 text-sm">
                  <input
                    type="time"
                    value={w[0]}
                    onChange={e => updateWindow(d.key, i, 0, e.target.value)}
                    className="border border-slate-300 rounded px-2 py-1"
                  />
                  <span className="text-slate-500">→</span>
                  <input
                    type="time"
                    value={w[1]}
                    onChange={e => updateWindow(d.key, i, 1, e.target.value)}
                    className="border border-slate-300 rounded px-2 py-1"
                  />
                  {windows.length > 1 && (
                    <button onClick={() => removeWindow(d.key, i)} className="ml-auto text-xs text-rose-600 hover:text-rose-800">✕</button>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <button
        onClick={save}
        disabled={saving}
        className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold py-2.5 rounded-lg text-sm uppercase tracking-tight"
      >
        {saving ? 'Saving…' : 'Save availability'}
      </button>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// TEAM INVITE PANEL
//
// Producer/Enterprise tier operators need to invite their team. This
// panel calls /api/team/invite which uses Supabase's
// inviteUserByEmail (server-side, service-role) to send a magic-link
// signup email + auto-adds the new user to the operator's organization.
// ════════════════════════════════════════════════════════════════════
function TeamInvitePanel({ currentUser, showToast = () => {} }) {
  const [tier, setTier]       = React.useState(null);
  const [email, setEmail]     = React.useState('');
  const [role, setRole]       = React.useState('producer');
  // upline_user_id default = the operator inviting (so the new member
  // reports up to whoever invited them). User can pick a different
  // upline from the existing members list (e.g. inviting a VA that
  // reports to a downline agent instead of to the owner).
  const [upline, setUpline]   = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [members, setMembers] = React.useState([]);
  const [loadingMembers, setLoadingMembers] = React.useState(true);

  const loadAll = React.useCallback(async () => {
    if (!currentUser?.id) return;
    setLoadingMembers(true);
    try {
      const [subRes, memRes] = await Promise.all([
        supabase.from('subscriptions')
          .select('tier, plan')
          .or(`owner_id.eq.${currentUser.id},user_id.eq.${currentUser.id}`)
          .maybeSingle(),
        apiFetch('/api/team/members').catch(() => ({ members: [] })),
      ]);
      setTier(subRes.data?.tier || subRes.data?.plan || null);
      setMembers(memRes?.members || []);
    } finally {
      setLoadingMembers(false);
    }
  }, [currentUser?.id]);

  React.useEffect(() => { loadAll(); }, [loadAll]);

  // Solo tier gets limited seats. Just show a "Upgrade" CTA if not eligible.
  const canInvite = tier && (tier === 'premium' || tier === 'enterprise' || tier === 'producer' || tier === 'agency');

  async function sendInvite() {
    if (!email || !email.includes('@')) {
      showToast('Enter a valid email', 'error');
      return;
    }
    setSending(true);
    try {
      const r = await apiFetch('/api/team/invite', {
        method: 'POST',
        body:   JSON.stringify({
          email: email.trim().toLowerCase(),
          role,
          // Empty upline string means "default to me" — let the server
          // fill it in. A picked value gets sent through.
          upline_user_id: upline || undefined,
        }),
      });
      if (r?.ok) {
        showToast(`Invite sent to ${email}.`, 'success');
        setEmail('');
        setUpline('');
        loadAll();
      }
    } catch (err) {
      showToast(`Invite failed: ${err.message || err}`, 'error');
    } finally {
      setSending(false);
    }
  }

  async function removeMember(member) {
    if (member.is_owner) return showToast('Cannot remove the org owner.');
    if (!(await hqConfirm({
      title: `Remove ${member.name} from your team?`,
      body: `${member.email || member.user_id.slice(0,8)} will lose access immediately.`,
      confirmLabel: 'Remove member',
      danger: true,
    }))) return;
    try {
      await apiFetch(`/api/team/members?user_id=${encodeURIComponent(member.user_id)}`, { method: 'DELETE' });
      showToast('Removed.');
      loadAll();
    } catch (err) { showToast(`Remove failed: ${err.message}`); }
  }

  async function patchMember(member, patch) {
    try {
      await apiFetch('/api/team/members', {
        method: 'PATCH',
        body: JSON.stringify({ user_id: member.user_id, ...patch }),
      });
      loadAll();
    } catch (err) { showToast(`Update failed: ${err.message}`); }
  }

  // Pool of possible uplines for the picker — owner + any existing agents.
  const uplineOptions = (members || []).filter(m => m.role === 'agent' || m.is_owner);
  const memberById = new Map(members.map(m => [m.user_id, m]));

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
      <div>
        <h3 className="font-bold text-slate-900 text-base">👥 Team invites</h3>
        <p className="text-xs text-slate-500 mt-0.5">
          Send a magic-link signup invite to your producers. They'll land in your organization automatically.
        </p>
      </div>

      {!canInvite ? (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 text-sm text-amber-900">
          <strong>Team invites require Premium or Enterprise.</strong> Upgrade your plan in the AI Wallet panel above (or Settings → Billing) to add team members.
        </div>
      ) : (
        <>
          {/* Current members list */}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Current team ({members.length})</div>
            {loadingMembers ? (
              <div className="text-xs text-slate-400 italic">Loading…</div>
            ) : members.length === 0 ? (
              <div className="text-xs text-slate-400 italic">No team members yet — invite your first producer or VA below.</div>
            ) : (
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                {members.map(m => {
                  // BUG-017 — the /api/team/members payload sometimes
                  // returns 'Unknown' and no email for the org owner
                  // because their auth.users row hasn't synced into
                  // the members table. Backfill from the signed-in
                  // user context so the operator never sees a profile
                  // that contradicts the header.
                  const displayName  = m.is_self
                    ? (m.name && m.name !== 'Unknown' ? m.name : (currentUser?.email?.split('@')[0] || currentUser?.name || 'You'))
                    : (m.name || 'Unknown');
                  const displayEmail = m.is_self
                    ? (m.email || currentUser?.email || '')
                    : (m.email || '');
                  return (
                  <div key={m.user_id} className="px-3 py-2 flex items-center gap-2 text-sm">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 truncate">
                        {displayName} {m.is_self && <span className="text-[10px] text-slate-500">(you)</span>}
                      </div>
                      <div className="text-[11px] text-slate-500">{displayEmail || <span className="italic">no email on file</span>}</div>
                    </div>
                    {/* Role: owner is read-only; others can switch via the inline picker */}
                    {m.is_owner ? (
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-violet-50 text-violet-700 border border-violet-200 rounded px-1.5 py-0.5">Owner</span>
                    ) : (
                      <select
                        value={m.role}
                        onChange={(e) => patchMember(m, { role: e.target.value })}
                        className="text-[10px] font-bold uppercase tracking-wider bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5"
                      >
                        <option value="agent">Agent</option>
                        <option value="va">VA</option>
                        <option value="admin">Admin</option>
                      </select>
                    )}
                    {/* Upline picker — VAs + agents only, can't change the owner's */}
                    {!m.is_owner && (
                      <select
                        value={m.upline_user_id || ''}
                        onChange={(e) => patchMember(m, { upline_user_id: e.target.value || null })}
                        className="text-[10px] bg-white border border-slate-200 rounded px-1.5 py-0.5 max-w-[140px]"
                        title="Reports to (drives downline rollup in VA Tracker + emails)"
                      >
                        <option value="">⤷ no upline</option>
                        {uplineOptions.filter(o => o.user_id !== m.user_id).map(o => (
                          <option key={o.user_id} value={o.user_id}>⤷ {o.name}</option>
                        ))}
                      </select>
                    )}
                    {!m.is_owner && (
                      <button onClick={() => removeMember(m)} className="text-rose-500 hover:text-rose-700 text-xs ml-1" title="Remove">✕</button>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Invite form */}
          <div className="pt-3 border-t border-slate-100">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Invite a new seat</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {/* BUG-006 hardening. Chrome + Safari ignore
                  autoComplete="off" on type="email" and still
                  pre-fill the operator's own address. Nuclear
                  approach: type="text" + inputMode="email" (keeps
                  email keyboard on mobile) + one-off name + the
                  password-manager ignore flags 1Password / LastPass /
                  Dashlane honor. Also honors form-type=other so
                  browsers stop treating the whole card as a signup. */}
              <input
                type="text"
                inputMode="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="producer@example.com"
                autoComplete="off"
                name={`invite-${currentUser?.id?.slice(0, 8) || 'anon'}`}
                data-1p-ignore="true"
                data-lpignore="true"
                data-form-type="other"
                className="border border-slate-300 rounded px-3 py-2 text-sm"
              />
              <select value={role} onChange={e => setRole(e.target.value)} className="border border-slate-300 rounded px-2 py-2 text-sm bg-white">
                <option value="producer">Agent / Producer</option>
                <option value="va">Virtual Assistant</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            {/* Upline picker for new invites — only relevant when there
                are existing agents to choose from. Defaults to "me". */}
            {uplineOptions.length > 1 && (
              <div className="mt-2">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Reports to (upline)</label>
                <select value={upline} onChange={e => setUpline(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-2 text-sm bg-white">
                  <option value="">{`You (${currentUser?.email || 'owner'})`}</option>
                  {uplineOptions.filter(o => o.user_id !== currentUser?.id).map(o => (
                    <option key={o.user_id} value={o.user_id}>{o.name}</option>
                  ))}
                </select>
                <div className="text-[10px] text-slate-500 mt-0.5">Used by the VA Tracker + daily team email to group this seat under the right manager.</div>
              </div>
            )}
            <button
              onClick={sendInvite}
              disabled={sending || !email}
              className="w-full mt-3 bg-slate-900 hover:bg-black disabled:opacity-50 text-white font-bold py-2.5 rounded-lg text-sm uppercase tracking-tight"
            >
              {sending ? 'Sending invite…' : 'Send invite'}
            </button>
            <div className="text-[10px] text-slate-400 italic mt-1">
              They'll get an email with a one-click signup link landing them in your org. Once they sign in, they show up in the list above + on the VA Tracker.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// CALL HISTORY PANEL
//
// Reads from public.call_log via RLS-scoped supabase query. Renders
// the most-recent N calls (optionally filtered to a single lead_id).
// Each row has:
//   - Direction badge + time
//   - From/To numbers
//   - Status + duration
//   - Recording playback (when recording_url present)
//   - "Generate AI Summary" button → calls /api/calls/transcribe
//   - Inline display of the AI summary + action items when available
// ════════════════════════════════════════════════════════════════════
function CallHistoryPanel({ currentUser, leadId = null, limit = 25, showToast = () => {} }) {
  const [rows, setRows]   = React.useState([]);
  const [loading, setLoad] = React.useState(true);
  const [busyId, setBusy] = React.useState(null);
  // Recording playback: Twilio URLs require Basic auth, so we can't
  // point <audio src> straight at them. The fetch-to-blob pattern
  // routes through /api/calls/recording which authenticates the
  // operator via Supabase JWT and streams the audio bytes back.
  // Loaded lazily per-call when the operator clicks "Load recording."
  const [audioUrls, setAudioUrls] = React.useState({}); // call_id → blob: URL
  const [loadingAudio, setLoadingAudio] = React.useState({}); // call_id → bool

  const refresh = React.useCallback(async () => {
    if (!currentUser?.id) return;
    setLoad(true);
    let q = supabase
      .from('call_log')
      .select('id, lead_id, direction, from_number, to_number, status, duration_seconds, price_cents, recording_url, transcription_text, metadata, started_at, ended_at')
      .eq('owner_id', currentUser.id)
      .order('started_at', { ascending: false, nullsFirst: false })
      .limit(limit);
    if (leadId) q = q.eq('lead_id', String(leadId));
    const { data } = await q;
    setRows(data || []);
    setLoad(false);
  }, [currentUser?.id, leadId, limit]);

  React.useEffect(() => { refresh(); }, [refresh]);

  // Revoke blob URLs on unmount so we don't leak.
  React.useEffect(() => () => {
    Object.values(audioUrls).forEach(u => { try { URL.revokeObjectURL(u); } catch {} });
  }, [audioUrls]);

  async function loadRecording(callId) {
    setLoadingAudio(prev => ({ ...prev, [callId]: true }));
    try {
      // apiFetch normally parses JSON; we need the raw blob here, so
      // construct the request manually but reuse its Supabase-JWT path.
      const jwt = await getSupabaseJwt();
      const r = await fetch(`/api/calls/recording?call_id=${encodeURIComponent(callId)}`, {
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j?.error || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      setAudioUrls(prev => ({ ...prev, [callId]: url }));
      try {
        await fetch('/api/audit/recording-play', {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
          body: JSON.stringify({ callId }),
        });
      } catch { /* audit best-effort; never blocks playback */ }
    } catch (err) {
      showToast(`Could not load recording: ${err.message || err}`, 'error');
    } finally {
      setLoadingAudio(prev => ({ ...prev, [callId]: false }));
    }
  }

  async function generateSummary(callId) {
    setBusy(callId);
    try {
      const r = await apiFetch('/api/calls/transcribe', {
        method: 'POST',
        body:   JSON.stringify({ call_id: callId }),
      });
      if (r?.ok) {
        showToast('AI summary generated.', 'success');
        await refresh();
      } else {
        showToast(`Summary failed: ${r?.error || 'unknown'}`, 'error');
      }
    } catch (err) {
      const msg = String(err.message || err);
      if (/OPENAI_API_KEY/i.test(msg)) {
        showToast('Add OPENAI_API_KEY to Vercel to enable AI call summaries.', 'error');
      } else if (/recording/i.test(msg)) {
        showToast('No recording on this call — enable TWILIO_VOICE_RECORDING_ENABLED first.', 'error');
      } else {
        showToast(`Summary failed: ${msg.slice(0, 120)}`, 'error');
      }
    } finally {
      setBusy(null);
    }
  }

  const fmtDuration = (s) => {
    if (!s) return '—';
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, '0')}`;
  };
  const fmtCents = (c) => c ? `$${(c / 100).toFixed(2)}` : '—';
  const fmtTime  = (iso) => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

  if (loading) return <div className="text-sm italic text-slate-500 p-3">Loading call history…</div>;
  if (rows.length === 0) {
    return (
      <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center text-sm text-slate-500">
        No calls yet{leadId ? ' with this lead' : ''}. Click the Call button on a lead card to start.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {rows.map(c => {
        const summary = c.metadata?.call_summary;
        const dispo   = c.metadata?.disposition;
        const dispoNotes = c.metadata?.disposition_notes;
        const isOutbound = (c.direction || '').startsWith('outbound');
        return (
          <div key={c.id} className="bg-white border border-slate-200 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${isOutbound ? 'bg-emerald-100 text-emerald-800' : 'bg-sky-100 text-sky-800'}`}>
                  {isOutbound ? '↗ Outbound' : '↙ Inbound'}
                </span>
                <span className={`text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${c.status === 'completed' ? 'bg-emerald-100 text-emerald-800' : c.status === 'no-answer' || c.status === 'busy' || c.status === 'failed' || c.status === 'canceled' ? 'bg-rose-100 text-rose-800' : 'bg-slate-100 text-slate-700'}`}>
                  {c.status}
                </span>
                {dispo && (
                  <span className={`text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${dispo === 'closed_won' ? 'bg-emerald-200 text-emerald-900' : dispo === 'dnc' || dispo === 'closed_lost' || dispo === 'bad_number' ? 'bg-rose-200 text-rose-900' : dispo === 'follow_up' || dispo === 'callback_requested' ? 'bg-amber-200 text-amber-900' : 'bg-slate-200 text-slate-700'}`} title={dispoNotes || ''}>
                    {dispo.replace(/_/g, ' ')}
                  </span>
                )}
                <span className="text-xs font-mono text-slate-600">{fmtTime(c.started_at)}</span>
              </div>
              <div className="text-xs text-slate-500 flex items-center gap-3">
                <span>{fmtDuration(c.duration_seconds)}</span>
                <span>{fmtCents(c.price_cents)}</span>
              </div>
            </div>
            <div className="text-xs text-slate-700 font-mono">
              {isOutbound ? `${c.from_number} → ${c.to_number}` : `${c.from_number} → ${c.to_number}`}
            </div>

            {/* Recording playback — lazy-loaded via /api/calls/recording
                proxy because Twilio URLs require Basic auth. */}
            {c.recording_url && (
              <div className="bg-slate-50 border border-slate-200 rounded p-2">
                <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500 mb-1">Recording</div>
                {audioUrls[c.id] ? (
                  <audio controls className="w-full h-8" src={audioUrls[c.id]}>
                    Your browser does not support audio playback.
                  </audio>
                ) : (
                  <button
                    onClick={() => loadRecording(c.id)}
                    disabled={!!loadingAudio[c.id]}
                    className="w-full bg-white border border-slate-300 hover:bg-slate-100 disabled:opacity-50 text-slate-700 text-xs font-semibold uppercase tracking-tight py-1.5 rounded"
                  >
                    {loadingAudio[c.id] ? 'Loading…' : '▶ Load recording'}
                  </button>
                )}
              </div>
            )}

            {/* AI summary */}
            {summary && (
              <div className="bg-amber-50 border-l-4 border-amber-500 px-3 py-2 rounded text-xs text-slate-800 space-y-1">
                <div className="font-bold text-amber-900">🧠 AI Summary <span className="text-[10px] uppercase tracking-wider text-amber-700 ml-1">({summary.sentiment || 'neutral'})</span></div>
                <div>{summary.summary}</div>
                {summary.next_step && (
                  <div><strong>Next step:</strong> {summary.next_step}</div>
                )}
                {Array.isArray(summary.action_items) && summary.action_items.length > 0 && (
                  <ul className="list-disc list-inside text-slate-700 mt-1">
                    {summary.action_items.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                )}
                {Array.isArray(summary.client_signals) && summary.client_signals.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-amber-200">
                    <div className="text-[0.55rem] font-black uppercase tracking-wider text-amber-700 mb-1">Client signals</div>
                    <ul className="list-disc list-inside text-slate-600">
                      {summary.client_signals.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Generate-summary CTA when recording exists but no summary yet */}
            {c.recording_url && !summary && (
              <button
                onClick={() => generateSummary(c.id)}
                disabled={busyId === c.id}
                className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold text-xs uppercase tracking-tight py-2 rounded-lg"
              >
                {busyId === c.id ? 'Generating AI summary…' : '🧠 Generate AI summary'}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// TWILIO VOICE PROVIDER
//
// Mounts at App root. Owns the singleton Twilio.Device lifecycle so
// inbound calls reach the operator no matter which tab they're on.
//
// Lifecycle:
//   - On currentUser sign-in → lazy-load SDK → fetch token →
//     new Device(token) → device.register() → ready
//   - On 'incoming' event → setIncomingCall(call) → IncomingCallModal
//     renders, operator accepts/declines
//   - On 'tokenWillExpire' → fetch fresh token → device.updateToken()
//   - Backstop refresh interval at 55 min in case tokenWillExpire
//     doesn't fire reliably
//   - On sign-out / unmount → device.destroy()
//
// Failure modes (silent — provider just stays in 'idle' state so
// inbound is unavailable but outbound CallWidget can still attempt
// ad-hoc Device creation per-call):
//   - 402 insufficient_credits → operator can top up, refresh page
//   - 503 voice env not configured → platform-side fix
//   - Mic permission denied → only matters when call accepted
//
// Outbound CallWidget reads the shared device from context if ready;
// otherwise falls back to creating its own (Phase 2-4a behavior).
// ════════════════════════════════════════════════════════════════════
const TwilioVoiceContext = React.createContext({
  device:          null,
  status:          'idle',  // idle | loading | ready | error
  error:           null,
  recordingEnabled:false,
  incomingCall:    null,
  clearIncoming:   () => {},
});

function TwilioVoiceProvider({ currentUser, children }) {
  const [device, setDevice]           = React.useState(null);
  const [status, setStatus]           = React.useState('idle');
  const [error, setError]             = React.useState(null);
  const [recordingEnabled, setRecEn]  = React.useState(false);
  const [incomingCall, setIncoming]   = React.useState(null);

  React.useEffect(() => {
    if (!currentUser?.id) {
      // Sign-out → destroy any active device.
      setStatus('idle');
      setDevice(prev => { try { prev?.destroy(); } catch {} return null; });
      setIncoming(null);
      return;
    }

    let cancelled = false;
    let dev = null;
    let refreshTimer = null;

    const fetchToken = async () => {
      const r = await apiFetch('/api/twilio/voice-token', { method: 'POST', body: '{}' });
      if (!r?.token) throw new Error('Voice token endpoint returned no token.');
      return r;
    };

    (async () => {
      try {
        setStatus('loading');
        const Twilio = await loadTwilioVoice();
        if (cancelled) return;

        const tokenRes = await fetchToken();
        if (cancelled) return;
        setRecEn(!!tokenRes.recording_enabled);

        dev = new Twilio.Device(tokenRes.token, {
          codecPreferences: ['opus', 'pcmu'],
          logLevel: 'warn',
          // Allow accepting incoming calls without first calling
          // .register() on the device — but we register anyway below
          // for inbound delivery.
          closeProtection: true,
        });

        dev.on('error', (err) => {
          // eslint-disable-next-line no-console
          console.warn('[TwilioVoiceProvider] device error:', err);
          // Don't tear down — Twilio device errors are often transient
          // (network blips, token rotation timing). Log + keep going.
        });

        // Audio device fallback — the SDK tries to set speakerDevices
        // to 'default' on construction; in browsers / OS configs where
        // 'default' isn't present (Firefox sometimes; some Linux setups;
        // permissions not yet granted at init time) it logs a
        // "Devices not found: default" warning. Enumerate the real
        // outputs + set the first one explicitly so subsequent calls
        // have audio routed correctly. Best-effort; failures are silent
        // (the SDK keeps a usable default internally even when set
        // throws).
        try {
          if (navigator?.mediaDevices?.enumerateDevices && dev?.audio?.speakerDevices?.set) {
            const devices  = await navigator.mediaDevices.enumerateDevices();
            const speakers = devices.filter(d => d.kind === 'audiooutput' && d.deviceId && d.deviceId !== 'default');
            if (speakers.length > 0) {
              await dev.audio.speakerDevices.set([speakers[0].deviceId]);
            }
          }
        } catch (audioErr) {
          // eslint-disable-next-line no-console
          console.warn('[TwilioVoiceProvider] audio device fallback failed (non-fatal):', audioErr.message);
        }

        dev.on('incoming', (call) => {
          if (cancelled) { try { call.reject(); } catch {} return; }
          // eslint-disable-next-line no-console
          console.log('[TwilioVoiceProvider] incoming call from', call?.parameters?.From);
          setIncoming(call);
        });

        dev.on('tokenWillExpire', async () => {
          try {
            const fresh = await fetchToken();
            dev.updateToken(fresh.token);
            // eslint-disable-next-line no-console
            console.log('[TwilioVoiceProvider] token refreshed via tokenWillExpire');
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[TwilioVoiceProvider] token refresh failed:', err.message);
          }
        });

        await dev.register();
        if (cancelled) { try { dev.destroy(); } catch {} return; }

        setDevice(dev);
        setStatus('ready');

        // Backup refresh every 55 min in case tokenWillExpire is
        // unreliable on long-lived tabs.
        refreshTimer = setInterval(async () => {
          try {
            const fresh = await fetchToken();
            dev.updateToken(fresh.token);
            // eslint-disable-next-line no-console
            console.log('[TwilioVoiceProvider] token refreshed via backup interval');
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[TwilioVoiceProvider] backup refresh failed:', err.message);
          }
        }, 55 * 60 * 1000);
      } catch (err) {
        if (cancelled) return;
        const msg = String(err?.message || err);
        // 402 = wallet empty. Device unavailable but not really
        // "error" state — operator can fund the wallet + refresh.
        if (/402|insufficient_credits|Out of credits/i.test(msg)) {
          // eslint-disable-next-line no-console
          console.log('[TwilioVoiceProvider] device init skipped — wallet empty');
          setStatus('idle');
          return;
        }
        // 503 = voice env vars missing. Same — silent skip.
        if (/503|not configured/i.test(msg)) {
          // eslint-disable-next-line no-console
          console.log('[TwilioVoiceProvider] device init skipped — voice env not configured');
          setStatus('idle');
          return;
        }
        // eslint-disable-next-line no-console
        console.warn('[TwilioVoiceProvider] init failed:', msg);
        setError(msg);
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      if (refreshTimer) clearInterval(refreshTimer);
      if (dev) { try { dev.destroy(); } catch {} }
    };
  }, [currentUser?.id]);

  const value = React.useMemo(() => ({
    device,
    status,
    error,
    recordingEnabled,
    incomingCall,
    clearIncoming: () => setIncoming(null),
  }), [device, status, error, recordingEnabled, incomingCall]);

  return (
    <TwilioVoiceContext.Provider value={value}>
      {children}
    </TwilioVoiceContext.Provider>
  );
}

// ════════════════════════════════════════════════════════════════════
// INCOMING CALL MODAL
//
// Watches TwilioVoiceContext.incomingCall. When set, shows a centered
// modal with Accept / Decline. Accept hands the live Call object off
// to the parent (via onAccept callback), which then renders an
// active-call CallWidget. Decline rejects + clears.
// ════════════════════════════════════════════════════════════════════
function IncomingCallModal({ onAccept = () => {}, showToast = () => {}, leads = [] }) {
  const { incomingCall, clearIncoming } = React.useContext(TwilioVoiceContext);
  const [busy, setBusy] = React.useState(false);

  if (!incomingCall) return null;

  const fromRaw = incomingCall.parameters?.From || incomingCall.parameters?.from || '';
  const fromPretty = fromRaw ? fmtPhoneUS(fromRaw) : 'Unknown caller';

  // Lead match — normalize both sides to last 10 digits and look up
  // in the operator's in-memory leads array. Surfaces name + stage +
  // assigned agent + last contact in the modal so the operator knows
  // who's calling before they pick up.
  const fromDigits = String(fromRaw).replace(/\D/g, '').slice(-10);
  const matchedLead = fromDigits
    ? leads.find(l => String(l.phone || '').replace(/\D/g, '').slice(-10) === fromDigits)
    : null;
  const stage = matchedLead ? (LEAD_STAGE_BY_ID[matchedLead.status] || null) : null;

  const accept = async () => {
    setBusy(true);
    try {
      await incomingCall.accept();
      onAccept(incomingCall, { from: fromRaw, fromPretty });
      // Note: don't clearIncoming() — the active-call widget needs the
      // call object reference. The parent (App) will clear when the
      // call ends.
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[IncomingCallModal] accept failed:', err);
      showToast(`Could not accept call: ${err.message || err}`, 'error');
      clearIncoming();
    } finally {
      setBusy(false);
    }
  };

  const decline = () => {
    try { incomingCall.reject(); } catch {}
    clearIncoming();
  };

  return (
    <div className="fixed inset-0 z-[200] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
        <div className="text-[0.55rem] font-black uppercase tracking-[0.3em] text-emerald-600 animate-pulse">
          📞 Incoming Call
        </div>
        {matchedLead ? (
          <>
            <div className="text-3xl font-black mt-3 text-slate-900">{matchedLead.name || 'Lead'}</div>
            <div className="text-sm text-slate-600 mt-1 tabular-nums">{fromPretty}</div>
            <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
              {stage && (
                <span className={`bg-${stage.color}-100 text-${stage.color}-800 text-[0.55rem] font-black uppercase tracking-wider px-2 py-0.5 rounded`}>
                  {stage.label}
                </span>
              )}
              {matchedLead.leadType && matchedLead.leadType !== 'general' && (
                <span className="bg-slate-100 text-slate-700 text-[0.55rem] font-black uppercase tracking-wider px-2 py-0.5 rounded">
                  {matchedLead.leadType.replace(/_/g, ' ')}
                </span>
              )}
              {matchedLead.sector === 'medicare' && (
                <span className="bg-blue-100 text-blue-800 text-[0.55rem] font-black uppercase tracking-wider px-2 py-0.5 rounded">Medicare</span>
              )}
              {matchedLead.sector === 'aca_private' && (
                <span className="bg-emerald-100 text-emerald-800 text-[0.55rem] font-black uppercase tracking-wider px-2 py-0.5 rounded">ACA</span>
              )}
            </div>
            {matchedLead.notes && (
              <div className="text-[11px] text-slate-500 mt-3 italic line-clamp-2 px-2">
                "{matchedLead.notes.slice(0, 120)}{matchedLead.notes.length > 120 ? '…' : ''}"
              </div>
            )}
          </>
        ) : (
          <>
            <div className="text-3xl font-black mt-3 text-slate-900 tabular-nums">{fromPretty}</div>
            <div className="text-xs text-slate-500 mt-1 font-mono">{fromRaw}</div>
            <div className="text-[11px] text-slate-400 mt-2 italic">No matching lead in your book — possible new prospect</div>
          </>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
          <button
            onClick={decline}
            disabled={busy}
            className="bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-bold py-3 rounded-lg text-sm uppercase tracking-tight"
          >
            ✗ Decline
          </button>
          <button
            onClick={accept}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold py-3 rounded-lg text-sm uppercase tracking-tight"
          >
            {busy ? 'Connecting…' : '✓ Accept'}
          </button>
        </div>
        <div className="text-[10px] text-slate-400 mt-4">
          Decline routes to voicemail. The call is logged either way.
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// CALL WIDGET — Twilio Voice SDK click-to-dial
//
// Floating panel that renders when an operator clicks Call on a lead
// from desktop. Lifecycle:
//   1. Mount → lazy-load Twilio Voice SDK from CDN
//   2. Fetch a 1-hour access token from /api/twilio/voice-token
//   3. Create Twilio.Device(token) — prompts mic permission once
//   4. device.connect({ params: { To } }) — places the call
//   5. Listen for Call events (ringing/accept/disconnect/error)
//   6. On disconnect → show after-call summary → close
//
// State machine: loading → ready → dialing → ringing → in-progress → ended | error
//
// Fallbacks: if SDK load fails, token fetch errors, or mic blocked,
// caller is invited to use the SMS composer instead via onSmsFallback.
// ════════════════════════════════════════════════════════════════════
function CallWidget({ lead, incomingCall, onClose, onSmsFallback = () => {}, showToast = () => {} }) {
  // Mode: 'inbound' when an already-accepted Call object is passed in;
  // 'outbound' when we're dialing from a lead. Inbound skips the SDK
  // load + token + device.connect dance because the call is live.
  const mode = incomingCall ? 'inbound' : 'outbound';
  const voiceCtx = React.useContext(TwilioVoiceContext);

  const [phase, setPhase] = React.useState(mode === 'inbound' ? 'in-progress' : 'loading');
  const [error, setError] = React.useState('');
  const [muted, setMuted] = React.useState(false);
  const [callStartedAt, setCallStartedAt] = React.useState(mode === 'inbound' ? Date.now() : null);
  const [elapsed, setElapsed] = React.useState(0);
  const [recordingEnabled, setRecordingEnabled] = React.useState(mode === 'inbound' ? voiceCtx.recordingEnabled : false);
  const [callLogId, setCallLogId] = React.useState(null);
  const [disposition, setDisposition] = React.useState('');
  const [dispositionNotes, setDispositionNotes] = React.useState('');
  const [dispositionSaved, setDispositionSaved] = React.useState(false);
  // Talk-track panel toggle — opens a left-mounted floating script
  // panel during in-progress calls. Per audit response §9 Phase 3
  // SALES + §11 Phase 5 RETENTION: scripts on screen WHILE the call
  // is live (not next to it). Sticky open across phase transitions
  // so the operator can keep the script up through hangup + outcome
  // logging.
  const [scriptsOpen, setScriptsOpen] = React.useState(false);
  const deviceRef = React.useRef(null);
  const callRef   = React.useRef(incomingCall || null);

  const targetNumber = React.useMemo(() => {
    // Inbound: caller's number comes off the live Call object.
    if (mode === 'inbound') {
      const raw = incomingCall?.parameters?.From || '';
      const digits = String(raw).replace(/\D/g, '');
      if (digits.length === 10) return `+1${digits}`;
      if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
      return raw || '';
    }
    const digits = String(lead?.phone || '').replace(/\D/g, '');
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return digits ? `+${digits}` : '';
  }, [mode, lead?.phone, incomingCall]);

  // Tick the elapsed-seconds counter while a call is up.
  React.useEffect(() => {
    if (!callStartedAt) return;
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - callStartedAt) / 1000)), 1000);
    return () => clearInterval(id);
  }, [callStartedAt]);

  // After the call ends, look up the call_log row created by the
  // voice-outgoing-twiml or voice-incoming-twiml webhook so the
  // disposition POST has a target.
  React.useEffect(() => {
    if (phase !== 'ended' || callLogId) return;
    const cancelled = { value: false };
    (async () => {
      for (let attempt = 0; attempt < 5; attempt++) {
        if (cancelled.value) return;
        // Inbound rows are keyed on from_number = the lead's phone
        // (which == targetNumber in inbound mode). Outbound rows are
        // keyed on to_number. Either way, targetNumber matches.
        const matchCol = mode === 'inbound' ? 'from_number' : 'to_number';
        const directionFilter = mode === 'inbound' ? 'inbound' : 'outbound';
        const { data } = await supabase
          .from('call_log')
          .select('id')
          .eq('direction', directionFilter)
          .eq(matchCol, targetNumber)
          .order('started_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (cancelled.value) return;
        if (data?.id) { setCallLogId(data.id); return; }
        await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
      }
    })();
    return () => { cancelled.value = true; };
  }, [phase, callLogId, targetNumber, mode]);

  async function saveDisposition() {
    if (!callLogId || !disposition) {
      showToast('No call log found yet — wait a moment and retry, or skip.', 'error');
      return;
    }
    try {
      const r = await apiFetch('/api/calls/log-outcome', {
        method: 'POST',
        body: JSON.stringify({ call_id: callLogId, disposition, notes: dispositionNotes || undefined }),
      });
      if (r?.ok) {
        setDispositionSaved(true);
        showToast(`Outcome logged: ${disposition.replace(/_/g, ' ')}`, 'success');
      }
    } catch (err) {
      showToast(`Could not save outcome: ${err.message || err}`, 'error');
    }
  }

  // Attach Call event listeners (used by both inbound + outbound).
  const attachCallListeners = React.useCallback((call, cancelled) => {
    call.on('ringing', () => { if (!cancelled.value) setPhase('ringing'); });
    call.on('accept',  () => {
      if (cancelled.value) return;
      setPhase('in-progress');
      setCallStartedAt(Date.now());
    });
    call.on('disconnect', () => { if (!cancelled.value) setPhase('ended'); });
    call.on('cancel',     () => { if (!cancelled.value) setPhase('ended'); });
    call.on('error', (err) => {
      // eslint-disable-next-line no-console
      console.warn('[CallWidget] call error:', err);
      if (cancelled.value) return;
      // Map raw Twilio error → friendly message. Operator audit
      // 2026-06-29 (BUG-003): the raw "PermissionDeniedError (31401)"
      // was surfacing here because the on('error') handler was
      // setError(err.message) verbatim. The setup-time catch already
      // mapped this — same mapping must apply mid-call.
      setError(friendlyTwilioError(err));
      setPhase('error');
    });
  }, []);

  // Main setup effect — branches on mode.
  React.useEffect(() => {
    const cancelled = { value: false };

    // ── INBOUND: call is already live, just attach listeners. ──
    if (mode === 'inbound' && incomingCall) {
      callRef.current = incomingCall;
      setPhase('in-progress');
      attachCallListeners(incomingCall, cancelled);
      return () => {
        cancelled.value = true;
        try { incomingCall.disconnect(); } catch {}
      };
    }

    // ── OUTBOUND: prefer the shared Device from TwilioVoiceProvider
    //    when ready; otherwise initialize an ad-hoc one as a fallback. ──
    let adHocDevice = null;

    (async () => {
      try {
        if (!targetNumber) throw new Error('Lead has no valid phone number.');

        // Pre-flight microphone permission check. If the browser has
        // explicitly DENIED mic access, fail fast with the friendly
        // message — Twilio's SDK error here is unhelpful (raw 31401).
        // 'prompt' is fine — the browser will ask when Twilio asks.
        const micPerm = await checkMicPermission();
        if (cancelled.value) return;
        if (micPerm === 'denied') {
          throw new Error('NotAllowedError: microphone access has been blocked for this site.');
        }

        let device = voiceCtx.device;
        if (voiceCtx.recordingEnabled) setRecordingEnabled(true);

        if (!device) {
          // No shared device available (wallet empty at sign-in time,
          // env vars missing, etc.) — try to spin up an ad-hoc device.
          const Twilio = await loadTwilioVoice();
          if (cancelled.value) return;
          const tokenRes = await apiFetch('/api/twilio/voice-token', { method: 'POST', body: '{}' });
          if (cancelled.value) return;
          if (!tokenRes?.token) throw new Error('Voice token endpoint returned no token.');
          if (tokenRes.recording_enabled) setRecordingEnabled(true);

          adHocDevice = new Twilio.Device(tokenRes.token, {
            codecPreferences: ['opus', 'pcmu'],
            logLevel: 'warn',
          });
          deviceRef.current = adHocDevice;
          adHocDevice.on('error', (err) => {
            // eslint-disable-next-line no-console
            console.warn('[CallWidget] ad-hoc device error:', err);
          });
          await adHocDevice.register();
          if (cancelled.value) return;
          device = adHocDevice;
        }

        setPhase('dialing');
        // Pass the lead id as a custom param so voice-outgoing-twiml
        // can persist it directly on the call_log row at insert time.
        // Closes the loop with server-side fix in f2b5944f — server
        // reads params.LeadId, so per-lead call history no longer
        // depends on a fragile client-side patch.
        const connectParams = { To: targetNumber };
        if (lead?.id) connectParams.LeadId = String(lead.id);
        const call = await device.connect({ params: connectParams });
        if (cancelled.value) { try { call.disconnect(); } catch {} return; }
        callRef.current = call;
        attachCallListeners(call, cancelled);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[CallWidget] setup failed:', err);
        if (cancelled.value) return;
        setError(friendlyTwilioError(err));
        setPhase('error');
      }
    })();

    return () => {
      cancelled.value = true;
      try { callRef.current?.disconnect(); } catch {}
      // Only destroy ad-hoc devices we created here. The shared
      // device lives in TwilioVoiceProvider and must NOT be destroyed.
      if (adHocDevice) { try { adHocDevice.destroy(); } catch {} }
      callRef.current = null;
      deviceRef.current = null;
    };
  }, [mode, targetNumber, incomingCall, voiceCtx.device, voiceCtx.recordingEnabled, attachCallListeners]);

  const hangup = () => {
    try { callRef.current?.disconnect(); } catch {}
    setPhase('ended');
  };

  const toggleMute = () => {
    const next = !muted;
    try { callRef.current?.mute(next); setMuted(next); } catch {}
  };

  const useSmsFallback = () => {
    try { callRef.current?.disconnect(); } catch {}
    try { deviceRef.current?.destroy(); } catch {}
    onClose();
    onSmsFallback(lead.id);
  };

  // PhoneCommandRoom's floating controls (Mute / Voicemail / End / Hold /
  // Transfer) dispatch window events. CallWidget owns the live Twilio
  // Call, so it handles them here. Mute + End act on the Voice SDK Call
  // directly. Hold / Transfer / Voicemail route to /api/twilio/call-
  // control (conference-based). When CALL_CONTROL_ENABLED is off server-
  // side, that endpoint returns not_enabled and we show the honest
  // "isn't available yet" toast — identical to the pre-build behavior.
  React.useEffect(() => {
    const labelFor = (a) => ({ hold: 'Hold', unhold: 'Resume', transfer: 'Transfer', voicemail: 'Voicemail drop' }[a] || 'That control');
    const post = async (action, extra = {}) => {
      let sid = null;
      try { sid = callRef.current?.parameters?.CallSid || null; } catch { /* ignore */ }
      if (!sid) { showToast('No active call to control.', 'info'); return; }
      try {
        const r = await fetch('/api/twilio/call-control', {
          method: 'POST',
          headers: { 'content-type': 'application/json', ...(await bearer()) },
          body: JSON.stringify({ callSid: sid, action, ...extra }),
        });
        const j = await r.json().catch(() => ({}));
        if (j?.error === 'not_enabled') { showToast(`${labelFor(action)} isn't available yet.`, 'info'); return; }
        if (!r.ok || j?.ok === false) { showToast(`${labelFor(action)} failed: ${j?.error || `HTTP ${r.status}`}`, 'error'); return; }
        showToast(`${labelFor(action)} ✓`, 'success');
      } catch (e) { showToast(`${labelFor(action)} failed: ${e.message}`, 'error'); }
    };
    let held = false;
    const onMute = () => setMuted((m) => { try { callRef.current?.mute(!m); } catch {} return !m; });
    const onEnd  = () => { try { callRef.current?.disconnect(); } catch {} setPhase('ended'); };
    const onHold = () => { held = !held; post(held ? 'hold' : 'unhold'); };
    const onTransfer = () => {
      // eslint-disable-next-line no-alert
      const target = window.prompt('Warm/cold transfer — enter the phone number to transfer this caller to:');
      if (target && target.trim()) post('transfer', { target: target.trim() });
    };
    const onVoicemail = () => post('voicemail');
    window.addEventListener('crm-call-mute-toggle', onMute);
    window.addEventListener('crm-call-end-request', onEnd);
    window.addEventListener('crm-call-hold-toggle', onHold);
    window.addEventListener('crm-call-transfer-request', onTransfer);
    window.addEventListener('crm-voicemail-drop-request', onVoicemail);
    return () => {
      window.removeEventListener('crm-call-mute-toggle', onMute);
      window.removeEventListener('crm-call-end-request', onEnd);
      window.removeEventListener('crm-call-hold-toggle', onHold);
      window.removeEventListener('crm-call-transfer-request', onTransfer);
      window.removeEventListener('crm-voicemail-drop-request', onVoicemail);
    };
  }, [showToast]);

  const phaseLabel = ({
    'loading':     'Connecting…',
    'dialing':     'Dialing…',
    'ringing':     'Ringing',
    'in-progress': 'On call',
    'ended':       'Call ended',
    'error':       'Could not connect',
  })[phase] || phase;

  const phaseColor = ({
    'loading':     'bg-slate-500',
    'dialing':     'bg-amber-500',
    'ringing':     'bg-amber-500 animate-pulse',
    'in-progress': 'bg-emerald-500',
    'ended':       'bg-slate-600',
    'error':       'bg-rose-600',
  })[phase] || 'bg-slate-500';

  const fmtElapsed = (s) => {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, '0')}`;
  };

  return (
    <>
      {/* Floating TalkTrackPanel — mounts to the left of the CallWidget
          when toggled. Wider than the widget (28rem) so longer scripts
          read cleanly. Closes audit response §9 Phase 3 SALES "no talk
          track library on screen during a call." */}
      {scriptsOpen && (
        <div className="fixed bottom-6 right-[22rem] z-[99] w-[28rem] max-w-[calc(100vw-22rem)] hidden md:block">
          <TalkTrackPanel
            lead={lead}
            context={mode === 'inbound' ? 'inbound' : 'outbound'}
            phase={phase}
            agentName={null}
            agentPhone={null}
          />
        </div>
      )}
    <div className="fixed bottom-6 right-6 z-[100] w-80 bg-white border-2 border-slate-900 rounded-2xl shadow-2xl overflow-hidden">
      <div className={`${phaseColor} text-white px-4 py-2 flex items-center justify-between`}>
        <span className="text-[0.6rem] font-black uppercase tracking-[0.2em]">{phaseLabel}</span>
        {phase === 'in-progress' && (
          <span className="text-xs font-bold tabular-nums">{fmtElapsed(elapsed)}</span>
        )}
      </div>

      <div className="p-4 space-y-3">
        <div>
          <div className="text-base font-black text-slate-900">{lead?.name || 'Lead'}</div>
          <div className="text-xs text-slate-500 font-mono">{targetNumber || '—'}</div>
        </div>

        {/* Recording disclosure — shown when TWILIO_VOICE_RECORDING_ENABLED is on. */}
        {recordingEnabled && (phase === 'dialing' || phase === 'ringing' || phase === 'in-progress') && (
          <div className="bg-rose-50 border border-rose-200 text-rose-900 rounded-lg px-2 py-1.5 text-[11px] flex items-center gap-1.5">
            <span className="text-rose-600">●</span>
            <span><strong>Recording active.</strong> Disclose to caller as required by your state.</span>
          </div>
        )}

        {error && error !== 'OUT_OF_CREDITS' && (
          <div className="bg-rose-50 border border-rose-200 text-rose-900 rounded-lg p-2 text-xs">
            {error}
          </div>
        )}
        {error === 'OUT_OF_CREDITS' && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 text-xs space-y-2">
            <div className="font-bold text-amber-900">💳 Wallet empty</div>
            <div className="text-amber-800">
              Voice calls deduct from your credits wallet (5¢/minute). Top up to keep dialing.
            </div>
            <button
              onClick={async () => {
                try {
                  const r = await apiFetch('/api/billing/topup-checkout', {
                    method: 'POST',
                    body: JSON.stringify({ amount_cents: 2500, success_path: '/?topup=success', cancel_path: '/?topup=cancel' }),
                  });
                  if (r?.url) window.location.href = r.url;
                  else setError('Could not open checkout. Try Settings → Credits Wallet.');
                } catch (err) {
                  setError(`Checkout failed: ${err.message || err}`);
                }
              }}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase tracking-tight text-xs py-2 rounded-lg"
            >
              💰 Top up $25 now
            </button>
            <div className="text-[10px] text-amber-700 text-center">
              Or open Settings → Credits Wallet for $100 / $250 packs.
            </div>
          </div>
        )}

        {/* Active-call controls */}
        {(phase === 'dialing' || phase === 'ringing' || phase === 'in-progress') && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <button
                onClick={toggleMute}
                disabled={phase !== 'in-progress'}
                className={`flex-1 ${muted ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-slate-100 text-slate-700 border-slate-200'} border font-bold uppercase tracking-tight text-xs py-2 rounded-lg disabled:opacity-50`}
              >
                {muted ? '🔇 Unmute' : '🎤 Mute'}
              </button>
              <button
                onClick={hangup}
                className="flex-1 bg-rose-600 hover:bg-rose-700 text-white font-bold uppercase tracking-tight text-xs py-2 rounded-lg"
              >
                ✕ Hang up
              </button>
            </div>
            {/* Scripts toggle — opens the floating TalkTrackPanel to
                the left. Per audit response §9 + §11 + §15. */}
            <button
              onClick={() => setScriptsOpen(o => !o)}
              className={`w-full font-bold uppercase tracking-tight text-xs py-2 rounded-lg border transition-colors ${
                scriptsOpen
                  ? 'bg-violet-600 hover:bg-violet-700 text-white border-violet-700'
                  : 'bg-white hover:bg-violet-50 text-violet-700 border-violet-200'
              }`}
              title="Show / hide the talk-track library"
            >
              📋 {scriptsOpen ? 'Hide scripts' : 'Show scripts'}
            </button>
          </div>
        )}

        {/* Post-call / error footer */}
        {(phase === 'ended' || phase === 'error') && (
          <div className="space-y-3">
            {phase === 'ended' && elapsed > 0 && (
              <div className="text-xs text-slate-600">
                Call duration: <strong>{fmtElapsed(elapsed)}</strong>. Wallet debit posts on the next webhook tick.
                {recordingEnabled && ' AI summary appears in the Calls Log within a minute.'}
              </div>
            )}

            {/* Disposition tagging — appears after a real call ended.
                Saves to call_log.metadata.disposition + .disposition_notes. */}
            {phase === 'ended' && elapsed > 0 && !dispositionSaved && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
                <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-700">📋 Log outcome</div>
                <select
                  value={disposition}
                  onChange={e => setDisposition(e.target.value)}
                  className="w-full text-xs border border-slate-300 rounded px-2 py-1.5 bg-white"
                >
                  <option value="">— pick a disposition —</option>
                  <option value="reached">✓ Reached prospect</option>
                  <option value="voicemail">📬 Left voicemail</option>
                  <option value="no_answer">🔕 No answer</option>
                  <option value="callback_requested">📞 Callback requested</option>
                  <option value="bad_number">❌ Bad number</option>
                  <option value="wrong_number">↪ Wrong number</option>
                  <option value="dnc">🚫 Do not call</option>
                  <option value="closed_won">💰 Closed won</option>
                  <option value="closed_lost">✗ Closed lost</option>
                  <option value="follow_up">⏳ Needs follow-up</option>
                </select>
                <textarea
                  value={dispositionNotes}
                  onChange={e => setDispositionNotes(e.target.value)}
                  placeholder="Quick note (optional)…"
                  rows={2}
                  className="w-full text-xs border border-slate-300 rounded px-2 py-1.5 resize-none"
                />
                <button
                  onClick={saveDisposition}
                  disabled={!disposition || !callLogId}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold uppercase tracking-tight text-xs py-1.5 rounded"
                >
                  {!callLogId ? 'Waiting for call log…' : 'Save outcome'}
                </button>
              </div>
            )}

            {dispositionSaved && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-2 py-1.5 text-xs">
                ✓ Outcome saved · <strong>{disposition.replace(/_/g, ' ')}</strong>
              </div>
            )}

            <div className="flex items-center gap-2">
              <button
                onClick={useSmsFallback}
                className="flex-1 bg-slate-900 hover:bg-black text-white font-bold uppercase tracking-tight text-xs py-2 rounded-lg"
              >
                Send SMS instead
              </button>
              <button
                onClick={onClose}
                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold uppercase tracking-tight text-xs py-2 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// ONBOARDING WIZARD
//
// Four-step modal that runs once per operator after they sign up.
//   1. Business info  — name, EIN, address, website, area-code preference
//   2. Pick a plan    — opens Stripe Checkout (subscription mode)
//   3. Load credits   — opens Stripe Checkout (one-time payment)
//   4. Provision phone — calls /api/twilio/provision; assigns a number
//
// The gate component (<OnboardingGate>) reads subscriptions.onboarding_step
// + .onboarding_completed_at to decide whether to render. Operators who
// already finished get nothing rendered. Operators mid-flight resume on
// the right step.
//
// Persistence: each step's "Continue" button writes its answers to
// subscriptions via Supabase RLS-scoped upsert; the Twilio + Stripe
// endpoints write the rest from the server side.
// ════════════════════════════════════════════════════════════════════
function OnboardingGate({ currentUser, showToast = () => {}, onGotoTab = () => {} }) {
  const [loaded, setLoaded] = React.useState(false);
  const [sub, setSub]       = React.useState(null);
  const [dismissed, setDismissed] = React.useState(false);
  const [schemaError, setSchemaError] = React.useState(null);
  // Allow the Setup Command Center to force-open the wizard at a
  // specific step, even after the operator has dismissed it.
  // Listens for window.dispatchEvent(new CustomEvent('crm-open-onboarding', { detail: { step }}))
  const [forceOpenStep, setForceOpenStep] = React.useState(null);
  React.useEffect(() => {
    const handler = (e) => {
      const step = Math.max(1, Math.min(4, Number(e?.detail?.step) || 1));
      setForceOpenStep(step);
      setDismissed(false);
    };
    window.addEventListener('crm-open-onboarding', handler);
    return () => window.removeEventListener('crm-open-onboarding', handler);
  }, []);

  React.useEffect(() => {
    if (!currentUser?.id) return;
    let cancelled = false;
    (async () => {
      // Use select('*') so a missing column from migration 050 doesn't
      // 400 the whole query. If 050 hasn't run, we still get the legacy
      // columns and treat the new ones as undefined → wizard shows.
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('owner_id', currentUser.id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        // Log clearly so the operator can see WHY the gate isn't appearing.
        // eslint-disable-next-line no-console
        console.warn('[OnboardingGate] subscriptions query failed:', error.message, error);
        setSchemaError(error.message);
      }
      // eslint-disable-next-line no-console
      console.log('[OnboardingGate] loaded sub for', currentUser.id, '→', data);
      setSub(data || { onboarding_step: 0 });
      setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [currentUser?.id]);

  if (!loaded) return null;
  if (dismissed) return null;
  // forceOpenStep (from Setup Command Center deep-link) overrides the
  // "already onboarded" early-returns so operators can revisit any step.
  if (!forceOpenStep) {
    if (sub?.onboarding_completed_at) return null;
    if (sub?.twilio_phone_e164) return null;
  }

  return (
    <OnboardingWizard
      currentUser={currentUser}
      initialStep={forceOpenStep || Math.max(1, Math.min(4, (sub?.onboarding_step || 0) + 1))}
      initialSub={sub}
      onDone={() => { setDismissed(true); setForceOpenStep(null); }}
      onSkip={() => { setDismissed(true); setForceOpenStep(null); }}
      showToast={showToast}
      onGotoTab={onGotoTab}
    />
  );
}

// Two-letter US state codes. Presented as a <select> so operators
// don't have to know Twilio's InRegion param. Territories included
// because Twilio supports them for local numbers in some cases.
const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['DC','District of Columbia'],
  ['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],
  ['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],
  ['ME','Maine'],['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],
  ['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],
  ['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],
  ['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],['OR','Oregon'],
  ['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],['SD','South Dakota'],
  ['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],['VA','Virginia'],
  ['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
];

// PhoneNumberPicker — the number-shopping card. Two modes: search
// by 3-digit area code, or by 2-letter state. Fires
// /api/twilio/search-numbers, renders the top ~20 results as
// clickable rows, then hands the picked E.164 back to the parent
// so the wizard's provisionPhone() can buy that specific number.
function PhoneNumberPicker({ busy, onProvision }) {
  const [mode, setMode]         = React.useState('area_code'); // 'area_code' | 'state'
  const [areaCode, setAreaCode] = React.useState('');
  const [state, setState]       = React.useState('');
  const [searching, setSearching] = React.useState(false);
  const [results, setResults]   = React.useState(null);
  const [error, setError]       = React.useState('');
  const [picked, setPicked]     = React.useState(null);

  const canSearch = mode === 'area_code'
    ? areaCode.length === 3
    : state.length === 2;

  async function runSearch() {
    setSearching(true);
    setError('');
    setResults(null);
    setPicked(null);
    try {
      const payload = mode === 'area_code'
        ? { area_code: areaCode }
        : { in_region: state };
      const r = await apiFetch('/api/twilio/search-numbers', {
        method: 'POST',
        body:   JSON.stringify(payload),
      });
      if (!r.results || r.results.length === 0) {
        setError('No available numbers matched that search. Try a different area code or state.');
      } else {
        setResults(r.results);
      }
    } catch (err) {
      setError(err.message || 'Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        Pick your business phone number. We'll attach it to our pre-approved A2P 10DLC campaign
        and wire the inbound webhook to your AI appointment setter.
      </p>

      {/* Mode selector */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => { setMode('area_code'); setResults(null); setPicked(null); }}
          className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold border-2 ${
            mode === 'area_code'
              ? 'bg-emerald-50 border-emerald-500 text-emerald-800'
              : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
          }`}
        >
          Search by Area Code
        </button>
        <button
          type="button"
          onClick={() => { setMode('state'); setResults(null); setPicked(null); }}
          className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold border-2 ${
            mode === 'state'
              ? 'bg-emerald-50 border-emerald-500 text-emerald-800'
              : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
          }`}
        >
          Search by State
        </button>
      </div>

      {/* Input */}
      <div className="flex gap-2">
        {mode === 'area_code' ? (
          <input
            value={areaCode}
            onChange={e => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
            onKeyDown={e => { if (e.key === 'Enter' && canSearch) runSearch(); }}
            placeholder="512"
            maxLength={3}
            className="flex-1 border-2 border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:border-emerald-500 focus:outline-none"
          />
        ) : (
          <select
            value={state}
            onChange={e => setState(e.target.value)}
            className="flex-1 border-2 border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none bg-white"
          >
            <option value="">Choose a state…</option>
            {US_STATES.map(([code, name]) => (
              <option key={code} value={code}>{name} ({code})</option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={runSearch}
          disabled={!canSearch || searching}
          className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-xs font-bold px-4 py-2 rounded-lg"
        >
          {searching ? 'Searching…' : 'Search'}
        </button>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-lg p-2 text-xs">{error}</div>
      )}

      {/* Results list */}
      {results && results.length > 0 && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
            {results.length} number{results.length === 1 ? '' : 's'} available — pick one
          </div>
          <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
            {results.map(n => {
              const isPicked = picked === n.phone_number;
              return (
                <button
                  key={n.phone_number}
                  type="button"
                  onClick={() => setPicked(n.phone_number)}
                  className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 ${
                    isPicked ? 'bg-emerald-50' : 'hover:bg-slate-50'
                  }`}
                >
                  <div>
                    <div className="text-sm font-bold text-slate-900 tabular-nums font-mono">
                      {n.friendly_name}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {n.locality || '—'}{n.region ? `, ${n.region}` : ''}
                      {n.capabilities?.mms && <span className="ml-2 text-emerald-700">· MMS</span>}
                    </div>
                  </div>
                  {isPicked && (
                    <span className="text-emerald-700 font-bold text-xs">✓ Selected</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Provision button — appears once a number is picked */}
      <button
        onClick={() => onProvision(picked)}
        disabled={busy || !picked}
        className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold px-5 py-2.5 rounded-lg text-sm"
      >
        {busy
          ? 'Provisioning…'
          : picked
            ? `Get ${results?.find(n => n.phone_number === picked)?.friendly_name || 'this number'}`
            : 'Pick a number above'}
      </button>
    </div>
  );
}

function OnboardingWizard({ currentUser, initialStep = 1, initialSub, onDone, onSkip, showToast = () => {}, onGotoTab = () => {} }) {
  const [step, setStep] = React.useState(initialStep);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [form, setForm] = React.useState({
    business_name:    initialSub?.business_name    || '',
    business_ein:     initialSub?.business_ein     || '',
    business_address: initialSub?.business_address || '',
    business_website: initialSub?.business_website || '',
    area_code:        '',
    // Spec Section 1 — which insurance line(s) the subscriber operates.
    insurance_lines:  (() => { try { return normalizeInsuranceLines(JSON.parse(localStorage.getItem('insurance_lines') || '[]')); } catch { return []; } })(),
  });
  // Phase 2 — line-specific finishing steps appended after the generic
  // 5-step flow (business/plan/credits/phone/A2P). Pure mapping lives in
  // src/onboarding/wizardSteps.js (unit-tested); empty selection -> []
  // -> wizard behaves exactly like the pre-Phase-2 generic flow.
  const lineSteps = React.useMemo(() => buildWizardLineSteps(form.insurance_lines), [form.insurance_lines]);
  const [provisioned, setProvisioned] = React.useState(null);
  // Swap flow state — populated when the user lands on Step 4 with a
  // number already on file. Drives the "Keep vs Replace" card.
  const [existingPhoneOnFile, setExistingPhoneOnFile] = React.useState(initialSub?.twilio_phone_e164 || '');
  const [replaceMode, setReplaceMode] = React.useState(false);

  // EIN server-side check state. On blur we call /api/twilio/validate-ein
  // which does prefix + all-zero + repeated-digit + name-pair checks.
  // See api/_einValidator.js for the rules.
  const [einCheck, setEinCheck] = React.useState({ loading: false, result: null });

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const runEinCheck = React.useCallback(async () => {
    const digits = (form.business_ein || '').replace(/\D/g, '');
    if (digits.length !== 9) { setEinCheck({ loading: false, result: null }); return; }
    setEinCheck(s => ({ ...s, loading: true }));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/twilio/validate-ein', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          business_ein:  form.business_ein,
          business_name: form.business_name,
        }),
      });
      const j = await r.json();
      setEinCheck({ loading: false, result: j });
    } catch {
      setEinCheck({ loading: false, result: null });
    }
  }, [form.business_ein, form.business_name]);

  // Belt-and-suspenders: if the user lands on Step 4 with empty form
  // state (page refresh, coming back later, initialSub arrived after
  // mount), rehydrate from Supabase before the "Get number" click
  // fires. Only fills fields that are currently blank — never
  // overwrites what the user has typed this session.
  React.useEffect(() => {
    if (!currentUser?.id) return;
    if (form.business_name && form.business_address) return; // already good
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('subscriptions')
          .select('business_name, business_ein, business_address, business_website, twilio_phone_e164')
          .or(`owner_id.eq.${currentUser.id},user_id.eq.${currentUser.id}`)
          .maybeSingle();
        if (cancelled || !data) return;
        setForm(prev => ({
          business_name:    prev.business_name    || data.business_name    || '',
          business_ein:     prev.business_ein     || data.business_ein     || '',
          business_address: prev.business_address || data.business_address || '',
          business_website: prev.business_website || data.business_website || '',
          area_code:        prev.area_code,
        }));
        // Only populate existingPhoneOnFile — never clear a value the
        // user has already seen this session. Session-only replaceMode
        // still tracks whether they've chosen to override.
        if (data.twilio_phone_e164) setExistingPhoneOnFile(prev => prev || data.twilio_phone_e164);
      } catch { /* wizard renders zero, server fallback catches it */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, step]);

  // Cross-device hydrate for insurance_lines — same "local wins if present,
  // else cloud" pattern as workspaceMode's reconcile effect above the
  // wizard (see cloudBackup.js). Only fires when THIS device has no local
  // pick yet (fresh browser / cleared storage); never overwrites a
  // selection the operator has already made this session. cloudSet's
  // snapshot may still be loading on mount, so poll briefly rather than
  // giving up after one check.
  React.useEffect(() => {
    if (!currentUser?.id) return;
    if (form.insurance_lines.length) return; // local already has a pick, it wins
    let cancelled = false;
    let attempts = 0;
    const tryHydrate = () => {
      if (cancelled) return;
      if (!snapshotIsReady(currentUser.id)) {
        if (attempts++ < 10) setTimeout(tryHydrate, 300);
        return;
      }
      const cloud = normalizeInsuranceLines(snapshotGet(currentUser.id, 'insurance_lines'));
      if (cloud.length) {
        setForm(prev => (prev.insurance_lines.length ? prev : { ...prev, insurance_lines: cloud }));
      }
    };
    tryHydrate();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  async function saveStep1() {
    setError(''); setBusy(true);
    try {
      const { error: err } = await supabase
        .from('subscriptions')
        .upsert({
          owner_id:         currentUser.id,
          email:            currentUser.email || null,
          business_name:    form.business_name,
          business_ein:     form.business_ein,
          business_address: form.business_address,
          business_website: form.business_website,
          onboarding_step:  1,
        }, { onConflict: 'owner_id' });
      if (err) throw err;
      // Persist the subscriber's insurance lines (spec Section 1) + derive the
      // legacy workspaceMode so the CRM tailors routing to their lines. localStorage
      // is the primary workspaceMode source (see the parent's init), so this takes
      // effect when the CRM (re)mounts after onboarding. Empty selection → 'both'
      // (show everything) so we never hide the whole CRM.
      try {
        const lines = normalizeInsuranceLines(form.insurance_lines);
        localStorage.setItem('insurance_lines', JSON.stringify(lines));
        localStorage.setItem('workspaceMode', linesToWorkspaceMode(lines));
        // Mirror to Supabase user_state via the same non-blocking cloudSet
        // path workspaceMode/theme already use (src/lib/cloudBackup.js), so
        // the activation-checklist endpoint and future sessions/devices can
        // read the subscriber's real, granular line selection instead of
        // only the coarser workspaceMode derivation.
        if (currentUser?.id) { try { cloudSet(currentUser.id, 'insurance_lines', lines); } catch { /* best-effort */ } }
      } catch { /* localStorage unavailable — non-fatal */ }
      setStep(2);
    } catch (err) {
      // If the schema doesn't have the new business_* columns yet, the
      // upsert fails with "column does not exist." Tell the operator
      // exactly what to fix instead of a generic red box.
      const msg = String(err.message || err);
      if (/column .* does not exist|schema cache/i.test(msg)) {
        setError('Schema is out of date. Run migration 050 in Supabase SQL Editor, then refresh and retry.');
      } else {
        setError(msg || 'Could not save.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function pickPlan(plan, seats = 1) {
    setError(''); setBusy(true);
    try {
      // Checkout — $40/month per subscriber (one price; the onboarding wizard picks the single plan).
      // (The legacy /api/create-checkout-session under-billed multi-seat plans.)
      let referral_code;
      try { referral_code = localStorage.getItem('sfa_ref') || undefined; } catch { /* private mode */ }
      const r = await apiFetch('/api/checkout', {
        method: 'POST',
        body:   JSON.stringify({ plan, seats: Math.max(1, seats || 1), userId: currentUser.id, email: currentUser.email, referral_code }),
      });
      if (r?.url) {
        await supabase.from('subscriptions').upsert(
          { owner_id: currentUser.id, onboarding_step: 2 },
          { onConflict: 'owner_id' }
        );
        window.location.href = r.url;
        return;
      }
      throw new Error('No checkout URL returned.');
    } catch (err) {
      setError(err.message || 'Could not start checkout.');
    } finally {
      setBusy(false);
    }
  }

  async function loadCredits(amountCents) {
    setError(''); setBusy(true);
    try {
      const r = await apiFetch('/api/billing/topup-checkout', {
        method: 'POST',
        body:   JSON.stringify({ amount_cents: amountCents, success_path: '/?topup=success&step=4', cancel_path: '/?topup=cancel&step=3' }),
      });
      if (r?.url) {
        await supabase.from('subscriptions').upsert(
          { owner_id: currentUser.id, onboarding_step: 3 },
          { onConflict: 'owner_id' }
        );
        window.location.href = r.url;
        return;
      }
      throw new Error('No checkout URL returned.');
    } catch (err) {
      setError(err.message || 'Could not start checkout.');
    } finally {
      setBusy(false);
    }
  }

  async function provisionPhone(pickedNumber, opts = {}) {
    setError(''); setBusy(true);
    try {
      const r = await apiFetch('/api/twilio/provision', {
        method: 'POST',
        body:   JSON.stringify({
          business_name:    form.business_name,
          business_ein:     form.business_ein,
          business_address: form.business_address,
          business_website: form.business_website,
          // If the picker returned a specific number, buy that one.
          // Otherwise the endpoint auto-picks (backwards compatible
          // with the older area-code-hint flow).
          phone_number:     pickedNumber || undefined,
          area_code:        (!pickedNumber && form.area_code) || undefined,
          // Swap flow — the client is explicitly replacing an existing
          // number. Server releases the old one back to Twilio first.
          replace_existing: !!opts.replaceExisting,
        }),
      });
      setProvisioned(r);
      showToast(
        opts.replaceExisting
          ? `Replaced. Your new number is live: ${r.phone_e164}`
          : `Your number is live: ${r.phone_e164}`,
        'success'
      );
      // After phone purchase, advance to step 5: A2P Registration.
      // The customer collects the 13 additional ISV fields OR pastes
      // a business doc for AI extraction. On completion, wizard closes
      // via onDone(). The customer can also Skip to defer A2P — in
      // that state SMS stays gated until they finish it later.
      setTimeout(() => setStep(5), 1500);
    } catch (err) {
      setError(err.message || 'Provisioning failed.');
    } finally {
      setBusy(false);
    }
  }

  const StepDot = ({ n, label }) => (
    <div className="flex items-center gap-2">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black ${step === n ? 'bg-amber-500 text-white' : step > n ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
        {step > n ? '✓' : n}
      </div>
      <span className={`text-xs font-bold uppercase tracking-wider ${step === n ? 'text-amber-700' : step > n ? 'text-emerald-700' : 'text-slate-400'}`}>{label}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-auto">
        <div className="bg-gradient-to-br from-slate-900 to-amber-900 text-white p-5 rounded-t-2xl">
          <div className="text-amber-200 text-[0.6rem] font-black uppercase tracking-[0.3em]">Six Figure Agent OS — Onboarding</div>
          <div className="text-xl md:text-2xl font-black mt-1">Let's get your AI customer service live.</div>
          <div className="text-amber-100 text-sm mt-1">Takes about 4 minutes. You'll have a phone number, AI appointment setter, and credits balance — all live — at the end.</div>
          <div className="flex items-center gap-3 mt-4 flex-wrap">
            <StepDot n={1} label="Business"/>
            <span className="text-slate-500">→</span>
            <StepDot n={2} label="Plan"/>
            <span className="text-slate-500">→</span>
            <StepDot n={3} label="Credits"/>
            <span className="text-slate-500">→</span>
            <StepDot n={4} label="Phone"/>
            <span className="text-slate-500">→</span>
            <StepDot n={5} label="A2P"/>
            {lineSteps.map((s, i) => (
              <React.Fragment key={s.key}>
                <span className="text-slate-500">→</span>
                <StepDot n={6 + i} label={s.short}/>
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-lg p-3 text-sm">{error}</div>
          )}

          {step === 1 && (() => {
            // EIN must be ##-####### (federal employer ID). Twilio
            // cross-checks against IRS records during brand review;
            // a transposed digit blocks approval for 1-5 days, so we
            // refuse to advance with a malformed value.
            const einDigitsOnly = (form.business_ein || '').replace(/\D/g, '');
            const einValid = /^\d{9}$/.test(einDigitsOnly);
            const einFormatted = einDigitsOnly.length === 9
              ? `${einDigitsOnly.slice(0, 2)}-${einDigitsOnly.slice(2)}`
              : form.business_ein;
            // Website doesn't need a full URL parse — we just want
            // something that looks like a hostname. Accept with or
            // without protocol.
            const siteValid = (form.business_website || '').trim().length >= 4
              && /\.[a-z]{2,}/i.test(form.business_website || '');
            // Block Continue when the server-side EIN check has failed
            // (invalid prefix, all-zero, repeated digit, etc). Warnings
            // do NOT block — those surface but let the operator proceed
            // with a heads-up.
            const einServerBlocked = einCheck.result && !einCheck.result.ok;
            const canContinue =
              !!form.business_name.trim()
              && !!form.business_address.trim()
              && einValid
              && !einServerBlocked
              && siteValid;
            return (
            <div className="space-y-3">
              <h3 className="font-bold text-slate-900">Step 1 · Your business</h3>
              <InsuranceLinesStep value={form.insurance_lines} onChange={(lines) => setField('insurance_lines', lines)} />
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700">
                <div className="font-semibold text-slate-900 mb-1">Why we ask for this</div>
                <div>
                  Federal carriers require every business that sends SMS to register their brand and use case (A2P 10DLC).
                  We submit yours automatically using the details below — typical approval is 1–5 business days.
                  Your SMS still works the entire time via our pre-approved platform brand; once your own brand approves your number migrates over automatically.
                  Enter the SAME legal name, EIN, and address that's on file with the IRS — a single transposed digit blocks approval.
                </div>
              </div>
              <label className="block">
                <span className="text-xs text-slate-500">Legal business name <span className="text-red-500" aria-label="required">*</span></span>
                <input value={form.business_name} onChange={e => setField('business_name', e.target.value)} placeholder="Jane Doe Insurance LLC" required aria-required="true" className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"/>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs text-slate-500">EIN <span className="text-red-500" aria-label="required">*</span></span>
                  <input
                    value={form.business_ein}
                    onChange={e => setField('business_ein', e.target.value)}
                    onBlur={() => {
                      if (einDigitsOnly.length === 9) setField('business_ein', einFormatted);
                      runEinCheck();
                    }}
                    placeholder="12-3456789"
                    inputMode="numeric"
                    required
                    aria-required="true"
                    aria-invalid={form.business_ein && !einValid ? 'true' : 'false'}
                    className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm font-mono ${form.business_ein && !einValid ? 'border-red-300 focus:border-red-500' : 'border-slate-300'}`}
                  />
                  {form.business_ein && !einValid && (
                    <span className="text-[10px] text-red-600 mt-0.5 block">Must be 9 digits (##-#######).</span>
                  )}
                  {einValid && einCheck.result && !einCheck.result.ok && (
                    <span className="text-[10px] text-red-600 mt-0.5 block">
                      {einCheck.result.error}
                      {einCheck.result.hint && <> — {einCheck.result.hint}</>}
                    </span>
                  )}
                  {einValid && einCheck.result?.ok && einCheck.result.warnings?.length > 0 && (
                    <div className="text-[10px] text-amber-700 mt-0.5 space-y-0.5">
                      {einCheck.result.warnings.map((w, i) => (
                        <div key={i}>⚠ {w}</div>
                      ))}
                    </div>
                  )}
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">Website <span className="text-red-500" aria-label="required">*</span></span>
                  <input
                    value={form.business_website}
                    onChange={e => setField('business_website', e.target.value)}
                    placeholder="https://janedoeinsurance.com"
                    required
                    aria-required="true"
                    aria-invalid={form.business_website && !siteValid ? 'true' : 'false'}
                    className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${form.business_website && !siteValid ? 'border-red-300 focus:border-red-500' : 'border-slate-300'}`}
                  />
                  {form.business_website && !siteValid && (
                    <span className="text-[10px] text-red-600 mt-0.5 block">Must be a real website (e.g. example.com).</span>
                  )}
                </label>
              </div>
              <label className="block">
                <span className="text-xs text-slate-500">Business address <span className="text-red-500" aria-label="required">*</span></span>
                <input value={form.business_address} onChange={e => setField('business_address', e.target.value)} placeholder="123 Main St, Suite 200, Austin TX 78701" required aria-required="true" className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"/>
              </label>
              <div className="flex items-center justify-between pt-2">
                <button onClick={onSkip} className="text-xs text-slate-500 hover:text-slate-800 underline">Set up later</button>
                <button onClick={saveStep1} disabled={busy || !canContinue} className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-bold px-4 py-2 rounded-lg text-sm">
                  {busy ? 'Saving…' : 'Continue → Plan'}
                </button>
              </div>
              {!canContinue && (form.business_name || form.business_ein || form.business_website || form.business_address) && (
                <div className="text-[10px] text-slate-400">All four fields above are required for A2P brand registration.</div>
              )}
            </div>
            );
          })()}

          {step === 2 && (
            <div className="space-y-3">
              <h3 className="font-bold text-slate-900">Step 2 · Pick a plan</h3>
              <p className="text-xs text-slate-500">Stripe Checkout opens next. After payment, you'll land back here to load credits.</p>
              <div className="grid grid-cols-1 gap-3 max-w-sm mx-auto">
                {[
                  { id: 'basic', label: 'Six Figure Agent OS', price: '$40/mo per subscriber', sub: 'The AI Operating System for insurance agencies — one price, no contract, cancel anytime', best: true },
                ].map(p => (
                  <button key={p.id} onClick={() => pickPlan(p.id, 1)} disabled={busy}
                    className={`relative border-2 rounded-xl p-4 text-left transition-colors ${p.best ? 'border-amber-500 bg-amber-50 hover:bg-amber-100' : 'border-slate-200 hover:border-slate-300'}`}>
                    {p.best && <span className="absolute -top-2 right-3 bg-amber-600 text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded">Simple pricing</span>}
                    <div className="font-black text-slate-900">{p.label}</div>
                    <div className="text-lg font-black text-amber-700 mt-0.5">{p.price}</div>
                    <div className="text-[11px] text-slate-500 mt-1">{p.sub}</div>
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-between pt-2">
                <button onClick={() => setStep(1)} className="text-xs text-slate-500 hover:text-slate-800 underline">← Back</button>
                <button onClick={onSkip} className="text-xs text-slate-500 hover:text-slate-800 underline">Set up later</button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <h3 className="font-bold text-slate-900">Step 3 · Load credits</h3>
              <p className="text-xs text-slate-500">Pay for what you use. No surprise bills. <strong>2.5¢/SMS · 5¢/talk-minute · 15¢/AI conversation.</strong> Top up any time.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { cents: 2500,  label: '$25 Starter',  sub: '1,000 SMS · or 500 talk minutes · or 165 AI conversations' },
                  { cents: 10000, label: '$100 Power',   sub: '4,000 SMS · or 2,000 talk minutes · or 660 AI conversations', best: true },
                  { cents: 25000, label: '$250 Agency',  sub: '10,000 SMS · or 5,000 talk minutes · or 1,650 AI conversations' },
                ].map(o => (
                  <button key={o.cents} onClick={() => loadCredits(o.cents)} disabled={busy}
                    className={`relative border-2 rounded-xl p-4 text-left transition-colors ${o.best ? 'border-emerald-500 bg-emerald-50 hover:bg-emerald-100' : 'border-slate-200 hover:border-slate-300'}`}>
                    {o.best && <span className="absolute -top-2 right-3 bg-emerald-600 text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded">Best value</span>}
                    <div className="font-black text-slate-900">{o.label}</div>
                    <div className="text-[11px] text-slate-500 mt-1">{o.sub}</div>
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-between pt-2">
                <button onClick={() => setStep(2)} className="text-xs text-slate-500 hover:text-slate-800 underline">← Back</button>
                <button onClick={() => setStep(4)} className="text-xs text-slate-500 hover:text-slate-800 underline">Skip — load credits later</button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3">
              <h3 className="font-bold text-slate-900">Step 4 · Your AI phone number</h3>
              {provisioned ? (
                <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-5 text-center">
                  <div className="text-emerald-700 text-xs font-black uppercase tracking-wider">You're live</div>
                  <div className="text-3xl font-black text-emerald-900 mt-2 tabular-nums">{provisioned.phone_e164}</div>
                  <div className="text-xs text-emerald-700 mt-2">This number rides the platform A2P 10DLC umbrella — no carrier-approval wait. Your AI appointment setter answers it 24/7.</div>
                </div>
              ) : existingPhoneOnFile && !replaceMode ? (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5 space-y-3">
                  <div className="text-amber-800 text-xs font-black uppercase tracking-wider">You already have a number</div>
                  <div className="text-2xl font-black text-slate-900 tabular-nums">{fmtPhoneUS(existingPhoneOnFile)}</div>
                  <div className="text-xs text-slate-700">
                    This number is already provisioned on your account. Keep it as-is, or replace it with a new one.
                    <strong className="block mt-1 text-amber-800">Replacing releases the old number — it can't be restored later.</strong>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => { showToast('Keeping your existing number', 'success'); onDone?.(); }}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2 rounded-lg text-sm"
                    >
                      Keep {fmtPhoneUS(existingPhoneOnFile)}
                    </button>
                    <button
                      onClick={async () => {
                        if (await hqConfirm({
                          title: 'Release this phone number?',
                          body: `${fmtPhoneUS(existingPhoneOnFile)} will be released back to Relay and a new number will be provisioned. This cannot be undone.`,
                          confirmLabel: 'Release and replace',
                          danger: true,
                        })) {
                          setReplaceMode(true);
                        }
                      }}
                      className="bg-white hover:bg-slate-50 border-2 border-slate-300 text-slate-800 font-bold px-4 py-2 rounded-lg text-sm"
                    >
                      Replace with a new number
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {replaceMode && existingPhoneOnFile && (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700 flex items-center justify-between">
                      <span>Replacing <strong className="tabular-nums">{fmtPhoneUS(existingPhoneOnFile)}</strong></span>
                      <button
                        onClick={() => setReplaceMode(false)}
                        className="text-slate-500 hover:text-slate-800 underline text-xs"
                      >Cancel replace</button>
                    </div>
                  )}
                  <PhoneNumberPicker
                    busy={busy}
                    onProvision={(picked) => provisionPhone(picked, { replaceExisting: replaceMode })}
                  />
                </>
              )}
              <div className="flex items-center justify-between pt-2">
                <button onClick={() => setStep(3)} className="text-xs text-slate-500 hover:text-slate-800 underline">← Back</button>
                <button
                  onClick={async () => {
                    // Guard the skip path — this is the #1 reason a new
                    // signup can't text or dial on day 1. If they proceed
                    // without a number, warn explicitly so it isn't a
                    // silent failure they discover later at the send button.
                    const proceed = await hqConfirm({
                      title: 'Continue without a phone number?',
                      body: 'You can still explore the app, but every SMS or dial button will fail with an error until a number is provisioned.',
                      confirmLabel: 'Continue without number',
                      cancelLabel: 'Pick a number now',
                    });
                    if (proceed) onSkip();
                  }}
                  className="text-xs text-amber-700 hover:text-amber-900 underline"
                  title="Not recommended — SMS and calls will fail until a number is provisioned"
                >Skip — provision later (not recommended)</button>
              </div>
            </div>
          )}

          {step === 5 && (
            <React.Suspense fallback={<div className="text-slate-400 text-sm p-4">Loading A2P registration…</div>}>
              <A2pRegistrationStep
                onDone={() => {
                  // Multi-line/single-line subscribers get a short line-specific
                  // launch sequence next (licensing, book import, comp setup,
                  // etc — see src/onboarding/wizardSteps.js). Nothing selected
                  // (or a line with no mapped extra steps) → same behavior as
                  // before Phase 2, wizard just closes.
                  if (lineSteps.length) setStep(6); else onDone?.();
                }}
                prefill={{
                  business_address_street: form.business_address || '',
                  authrep_email: currentUser?.email || '',
                }}
              />
            </React.Suspense>
          )}

          {step > 5 && (() => {
            // Line-specific launch sequence (Phase 2 of guided onboarding).
            // Each screen hands off to an existing, already-built view —
            // this never rebuilds Licensing/A2P/etc, it just routes there.
            // Because completion of these destinations is only known to
            // the real activation checklist (api/_activationSteps.js), the
            // wizard never claims a step is "done" here — it is a guided
            // tour, not a second checklist.
            const idx = step - 6;
            const extra = lineSteps[idx];
            if (!extra) {
              // Defensive: line selection changed after this step count was
              // computed (shouldn't happen mid-flow, but never blank-screen).
              onDone?.();
              return null;
            }
            const isLast = idx === lineSteps.length - 1;
            const goAct = (tab) => { onGotoTab?.(tab); onDone?.(); };
            return (
              <div className="space-y-3">
                <h3 className="font-bold text-slate-900">Step {step} · {extra.title}</h3>
                <p className="text-xs text-slate-500">{extra.body}</p>
                <div className="flex gap-2 flex-wrap pt-1">
                  <button
                    onClick={() => goAct(extra.cta_tab)}
                    className="bg-amber-600 hover:bg-amber-700 text-white font-bold px-4 py-2 rounded-lg text-sm"
                  >
                    {extra.cta_label} →
                  </button>
                  {extra.secondary_cta_tab && (
                    <button
                      onClick={() => goAct(extra.secondary_cta_tab)}
                      className="bg-white hover:bg-slate-50 border-2 border-slate-300 text-slate-800 font-bold px-4 py-2 rounded-lg text-sm"
                    >
                      {extra.secondary_cta_label} →
                    </button>
                  )}
                </div>
                <div className="flex items-center justify-between pt-2">
                  <button onClick={() => setStep(step - 1)} className="text-xs text-slate-500 hover:text-slate-800 underline">← Back</button>
                  <button
                    onClick={() => { if (isLast) onDone?.(); else setStep(step + 1); }}
                    className="text-xs text-slate-500 hover:text-slate-800 underline"
                  >
                    {isLast ? "I'll do this later — finish setup" : 'Skip for now'}
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// SETUP COMMAND CENTER
//
// Persistent panel that lives at the top of the dashboard until the
// operator hits 100% setup. Auto-detects status from Supabase +
// the in-memory leads array. Each step has a one-click action button
// that either deep-links to a tab or fires an event the rest of the
// app catches.
//
// Steps:
//   1. Business info filled              (subscriptions.business_name)
//   2. Subscription active               (subscriptions.tier + status)
//   3. Credits ≥ $25                     (subscriptions.credits_balance_cents)
//   4. Phone provisioned                 (subscriptions.twilio_phone_e164)
//   5. First lead in the system          (leads.length > 0)
//   6. First SMS sent (out)              (messages count where direction='out')
//   7. First call made                   (call_log count)
//
// At 100% → confetti animation + "🎉 You're live!" banner that
// auto-fades, and the panel disappears for good.
// ════════════════════════════════════════════════════════════════════
function SetupProgressPanel({ currentUser, leads = [], onGotoTab = () => {}, showToast = () => {} }) {
  const SETUP_DISMISSED_LS = 'crm:setup:dismissed:v1';
  const SETUP_COLLAPSED_LS = 'crm:setup:collapsed:v1';
  const [sub, setSub] = React.useState(null);
  const [smsCount, setSmsCount] = React.useState(0);
  const [callCount, setCallCount] = React.useState(0);
  const [loaded, setLoaded] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(() => {
    try { return localStorage.getItem(SETUP_DISMISSED_LS) === '1'; } catch { return false; }
  });
  // Default EXPANDED for first-time visits — the panel was buried at
  // 4/7 and operators couldn't tell setup was incomplete. The previous
  // "default collapsed" stance was meant to prevent dominating the
  // page, but veterans can collapse it manually and we persist that
  // signal: once collapsed, it stays collapsed across sessions.
  // Auto-collapse ONLY if the operator has explicitly collapsed before
  // (LS value === '1'). Anything else (no entry, '0', etc.) → expand.
  const [collapsed, setCollapsed] = React.useState(() => {
    try { return localStorage.getItem(SETUP_COLLAPSED_LS) === '1'; } catch { return false; }
  });
  const setCollapsedPersist = (v) => {
    setCollapsed(v);
    try { localStorage.setItem(SETUP_COLLAPSED_LS, v ? '1' : '0'); } catch { /* ignore */ }
  };
  const dismissPermanently = () => {
    setDismissed(true);
    try { localStorage.setItem(SETUP_DISMISSED_LS, '1'); } catch { /* ignore */ }
  };
  const [showCelebration, setShowCelebration] = React.useState(false);
  // Test-SMS inline mini-form state
  const [showTestSms, setShowTestSms] = React.useState(false);
  const [testPhone, setTestPhone] = React.useState('');
  const [sendingTest, setSendingTest] = React.useState(false);
  const previousPctRef = React.useRef(0);

  async function sendTestSms() {
    if (!testPhone) { showToast('Enter your phone number first', 'error'); return; }
    setSendingTest(true);
    try {
      const r = await apiFetch('/api/sms/test-send', {
        method: 'POST',
        body:   JSON.stringify({ to: testPhone }),
      });
      showToast(r?.message || 'Test SMS sent', 'success');
      setShowTestSms(false);
      // Bump smsCount locally so the step flips ✓ immediately.
      setSmsCount(c => c + 1);
    } catch (err) {
      const msg = String(err.message || err);
      if (/402|insufficient/i.test(msg)) showToast('Wallet empty — top up first', 'error');
      else                                showToast(`Test failed: ${msg.slice(0, 100)}`, 'error');
    } finally {
      setSendingTest(false);
    }
  }

  function startTestCall() {
    if (!testPhone) { showToast('Enter your phone number first', 'error'); return; }
    // Reuse the global dial dispatcher — same path as a real Call button.
    requestPhoneCall({ phone: testPhone, name: 'Test call (yourself)' });
  }

  React.useEffect(() => {
    if (!currentUser?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const [subRes, smsRes, callRes] = await Promise.all([
          supabase.from('subscriptions')
            .select('business_name, tier, plan, status, credits_balance_cents, twilio_phone_e164, onboarding_completed_at')
            .or(`owner_id.eq.${currentUser.id},user_id.eq.${currentUser.id}`)
            .maybeSingle(),
          supabase.from('messages')
            .select('id', { count: 'exact', head: true })
            .eq('owner_id', currentUser.id)
            .eq('direction', 'out'),
          supabase.from('call_log')
            .select('id', { count: 'exact', head: true })
            .eq('owner_id', currentUser.id),
        ]);
        if (cancelled) return;
        setSub(subRes.data || {});
        setSmsCount(smsRes.count || 0);
        setCallCount(callRes.count || 0);
        setLoaded(true);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[SetupProgressPanel] load failed:', err.message);
        setLoaded(true);  // render with whatever we have
      }
    })();
    return () => { cancelled = true; };
  // Re-poll when leads change (cheap proxy for "operator did something")
  }, [currentUser?.id, leads.length]);

  // Compute step states.
  const steps = React.useMemo(() => {
    const s = sub || {};
    const has = (cond) => !!cond;
    const items = [
      {
        key: 'business',
        label: 'Business info',
        sub: 'Legal name, EIN, address',
        done: has(s.business_name),
        action: () => window.dispatchEvent(new CustomEvent('crm-open-onboarding', { detail: { step: 1 } })),
        cta:  'Add business info',
      },
      {
        key: 'plan',
        label: 'Subscription active',
        sub: 'Basic / Premium / Enterprise',
        done: has(s.tier || s.plan) && (s.status === 'active' || s.status === 'trialing'),
        action: () => window.dispatchEvent(new CustomEvent('crm-open-onboarding', { detail: { step: 2 } })),
        cta:  'Pick a plan',
      },
      {
        key: 'credits',
        label: 'Credits ≥ $25',
        sub: 'Powers SMS, voice, AI calls',
        done: (s.credits_balance_cents || 0) >= 2500,
        action: async () => {
          try {
            const r = await apiFetch('/api/billing/topup-checkout', {
              method: 'POST',
              body: JSON.stringify({ amount_cents: 2500, success_path: '/?topup=success', cancel_path: '/?topup=cancel' }),
            });
            if (r?.url) window.location.href = r.url;
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[SetupProgressPanel] topup failed:', err);
          }
        },
        cta:  'Top up $25',
      },
      {
        key: 'phone',
        label: 'Phone provisioned',
        sub: 'Your Relay number',
        done: has(s.twilio_phone_e164),
        action: () => window.dispatchEvent(new CustomEvent('crm-open-onboarding', { detail: { step: 4 } })),
        cta:  'Provision phone',
      },
      {
        key: 'leads',
        label: 'First leads imported',
        sub: 'Drop a CSV or add manually',
        done: (leads?.length || 0) > 0,
        action: () => onGotoTab('leads'),
        cta:  'Import leads',
      },
      {
        key: 'sms',
        label: 'First SMS sent',
        sub: 'Test the SMS engine',
        done: smsCount > 0,
        action: () => setShowTestSms(true),
        cta:  'Send test SMS',
      },
      {
        key: 'call',
        label: 'First call made',
        sub: 'Test the voice dialer',
        done: callCount > 0,
        action: () => setShowTestSms(true),  // Same panel, different button below
        cta:  'Test the dialer',
      },
    ];
    return items;
  }, [sub, leads.length, smsCount, callCount, onGotoTab]);

  const doneCount = steps.filter(s => s.done).length;
  const totalCount = steps.length;
  const pct = Math.round((doneCount / totalCount) * 100);

  // Trigger celebration the moment we cross to 100% (only once).
  React.useEffect(() => {
    if (pct === 100 && previousPctRef.current < 100 && previousPctRef.current > 0) {
      setShowCelebration(true);
      const t = setTimeout(() => setShowCelebration(false), 5000);
      return () => clearTimeout(t);
    }
    previousPctRef.current = pct;
  }, [pct]);

  if (!loaded) return null;
  if (dismissed) return null;
  // Hide once 100% complete (after the celebration ran).
  if (pct === 100 && !showCelebration) return null;

  const barColor = pct === 100 ? 'bg-emerald-500' : 'bg-slate-900';
  // Visual prominence: amber accent ring when expanded so an incomplete
  // setup actually stands out from the rest of the dashboard's plain
  // white cards. When the operator collapses it (explicit "I've got
  // this, hide it" signal), drop back to the muted slate border so it
  // doesn't keep shouting.
  const containerCls = collapsed
    ? 'bg-white border border-slate-200 rounded-xl overflow-hidden mb-4'
    : 'bg-white border-2 border-amber-300 ring-1 ring-amber-100 rounded-xl overflow-hidden mb-4';
  return (
    <div className={containerCls}>
      {showCelebration && (
        <div className="bg-emerald-50 border-b border-emerald-200 text-emerald-900 text-center px-6 py-4">
          <div className="text-base font-semibold">You're live.</div>
          <div className="text-sm mt-0.5 text-emerald-700">
            Phone is provisioned, credits funded, leads imported, dialer tested. Time to ship.
          </div>
        </div>
      )}

      <div className="w-full px-5 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors">
        <button
          onClick={() => setCollapsedPersist(!collapsed)}
          className="flex items-center gap-3 min-w-0 flex-1 text-left"
        >
          <div className="text-sm font-semibold text-slate-900">Setup checklist</div>
          <div className="text-xs text-slate-500 tabular-nums">{doneCount}/{totalCount} complete · {pct}%</div>
        </button>
        <div className="flex items-center gap-3 shrink-0">
          <div className="w-32 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full ${barColor} transition-all duration-700`} style={{ width: `${pct}%` }}/>
          </div>
          <button
            onClick={() => setCollapsedPersist(!collapsed)}
            className="text-slate-400 text-xs hover:text-slate-700"
            title={collapsed ? 'Expand' : 'Collapse'}
          >{collapsed ? '▾' : '▴'}</button>
          {/* Permanent dismiss — visible on the COLLAPSED header so
              operators can hide the panel without having to expand it
              first. Previously the dismiss button was buried inside the
              expanded body, which is why the audit reported the
              banner "stays up indefinitely." */}
          <button
            onClick={(e) => { e.stopPropagation(); dismissPermanently(); }}
            className="text-slate-300 hover:text-rose-700 text-base leading-none px-1"
            title="Hide this checklist permanently"
            aria-label="Dismiss setup checklist"
          >×</button>
        </div>
      </div>

      {!collapsed && (
        <div className="px-5 pb-4 pt-1 space-y-2 border-t border-slate-100">
          {steps.map(step => (
            <div key={step.key} className="flex items-center gap-3 px-1 py-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${step.done ? 'bg-emerald-500 text-white' : 'bg-white border border-slate-300 text-slate-400'}`}>
                {step.done ? '✓' : ''}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-semibold ${step.done ? 'text-slate-400 line-through' : 'text-slate-900'}`}>{step.label}</div>
                <div className="text-xs text-slate-500">{step.sub}</div>
              </div>
              {!step.done && (
                <button
                  onClick={step.action}
                  className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-1.5 rounded-md shrink-0 transition-colors"
                >
                  {step.cta}
                </button>
              )}
            </div>
          ))}
          {showTestSms && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
              <div className="text-xs font-semibold text-slate-900">Test your dialer + SMS engine</div>
              <div className="text-xs text-slate-500">
                Enter your personal cell — we'll text or call YOUR phone so you can verify everything works before contacting a real lead.
              </div>
              <input
                type="tel"
                value={testPhone}
                onChange={e => setTestPhone(e.target.value)}
                placeholder="(512) 555-1234"
                className="w-full bg-white border border-slate-300 text-slate-900 text-sm rounded-lg px-3 py-2 focus:border-slate-500 focus:outline-none"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  onClick={sendTestSms}
                  disabled={sendingTest || !testPhone}
                  className="bg-slate-900 hover:bg-slate-800 disabled:opacity-40 text-white font-semibold text-xs py-2 rounded-md transition-colors"
                >
                  {sendingTest ? 'Sending…' : 'Send test SMS'}
                </button>
                <button
                  onClick={startTestCall}
                  disabled={!testPhone}
                  className="bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 disabled:opacity-40 text-slate-700 font-semibold text-xs py-2 rounded-md transition-colors"
                >
                  Call my phone
                </button>
              </div>
              <button onClick={() => setShowTestSms(false)} className="w-full text-xs text-slate-500 hover:text-slate-900">
                Cancel
              </button>
            </div>
          )}
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <div className="text-xs text-slate-400">
              {pct === 100 ? 'All set — this panel will hide on next refresh.' : 'Hides automatically once every step is complete.'}
            </div>
            <button onClick={dismissPermanently} className="text-xs text-slate-500 hover:text-slate-900 font-medium">Dismiss</button>
          </div>
        </div>
      )}
    </div>
  );
}

function LeadFormModal({ initial, agentChoices, onCancel, onSave, defaultVertical = 'life' }) {
  const [r, setR] = useState({
    id: initial?.id || '',
    name: initial?.name || '',
    vertical: initial?.vertical || defaultVertical,
    phone: initial?.phone || '',
    email: initial?.email || '',
    source: initial?.source || '',
    leadSource: initial?.leadSource || '',
    followUpDate: initial?.followUpDate || '',
    agentProdNum: initial?.agentProdNum || '',
    leadType: initial?.leadType || 'general',
    coverageType: initial?.coverageType || '',
    estPremium: initial?.estPremium || '',
    cost: initial?.cost || '',
    premium: initial?.premium || '',
    draftDay: initial?.draftDay || '',
    birthday: initial?.birthday || '',
    lapsed: initial?.lapsed || false,
    status: initial?.status || 'new',
    tags: Array.isArray(initial?.tags) ? initial.tags.slice() : [],
    notes: initial?.notes || '',
    // v2 sector spine + Medicare fields. inferLeadSector covers
    // CSV-imported leads; here we default new manual entries to
    // aca_private and let the agent flip to medicare if 65+.
    sector: initial?.sector || 'aca_private',
    dob: initial?.dob || '',
    age: initial?.age || '',
    gender: initial?.gender || '',
    state: initial?.state || '',
    address: initial?.address || '',
    city:    initial?.city    || '',
    zip:     initial?.zip     || '',
    mbi: initial?.mbi || '',
    partAEffective: initial?.partAEffective || '',
    partBEffective: initial?.partBEffective || '',
    lisDualStatus: initial?.lisDualStatus || '',
    currentCarrier: initial?.currentCarrier || '',
    currentPlanType: initial?.currentPlanType || '',
    optInSms: !!initial?.optInSms,
    ptc: !!initial?.ptc,
    // TCPA/A2P consent — defaults the disclosure text on NEW leads only so
    // an agent can capture consent in one click. Existing leads keep what
    // was saved (or stay empty if it was never captured).
    consentText: initial?.consentText || (initial ? '' : DEFAULT_TCPA_TEXT),
    hasConsent: !!initial?.consentAt,
    consentSource: initial?.consentSource || (initial ? '' : 'form'),
  });
  const [tagDraft, setTagDraft] = useState('');
  const [errors, setErrors] = useState({});

  // Esc closes the modal — standard UX expectation that was missing.
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  // Phone formatter — masks raw input to (NNN) NNN-NNNN as the agent
  // types so junk data ("abc123") doesn't slip through. Reuses the
  // tens-digits the user typed; ignores everything else.
  const fmtPhone = (s) => {
    const d = String(s || '').replace(/\D/g, '').slice(0, 10);
    if (d.length === 0)  return '';
    if (d.length <= 3)   return `(${d}`;
    if (d.length <= 6)   return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };
  // Reject placeholder / reserved-exchange phone numbers (BUG-005,
  // BUG-018). NANP reserves the 555-01XX block for fiction; the
  // app's placeholder "(555) 123-4567" sits inside that block and
  // was getting saved as a real number when operators submitted
  // without editing the field.
  const isPlaceholderPhone = (s) => {
    const d = String(s || '').replace(/\D/g, '');
    if (d.length !== 10) return false;
    // Any 555-01XX number (NANP fictional reserve)
    if (d.startsWith('555') && d.slice(3, 5) === '01') return true;
    return false;
  };

  // DOB → Age derivation. When the agent picks a DOB, the Age field
  // fills automatically — eliminates the inconsistency risk of two
  // independent inputs for the same fact.
  const ageFromDob = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    let years = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) years--;
    return years >= 0 && years < 130 ? String(years) : '';
  };

  const set = (field, value) => setR(prev => {
    const next = { ...prev, [field]: value };
    // Phone mask
    if (field === 'phone') next.phone = fmtPhone(value);
    // DOB → Age auto-fill (only when the agent hasn't already typed a
    // different age — never overwrite their manual entry)
    if (field === 'dob') {
      const derived = ageFromDob(value);
      if (derived && (!prev.age || prev.age === ageFromDob(prev.dob))) {
        next.age = derived;
      }
    }
    return next;
  });
  // Clear an existing validation error as soon as the operator
  // edits that field (BUG-006). Without this, "Required" stays
  // visible on Name even after the agent types a valid name.
  React.useEffect(() => {
    setErrors(prev => {
      if (!prev || Object.keys(prev).length === 0) return prev;
      const next = { ...prev };
      let changed = false;
      if (next.name && r.name.trim()) { delete next.name; changed = true; }
      if (next.agentProdNum && r.agentProdNum) { delete next.agentProdNum; changed = true; }
      if (next.phone && r.phone && !isPlaceholderPhone(r.phone)) { delete next.phone; changed = true; }
      return changed ? next : prev;
    });
  }, [r.name, r.agentProdNum, r.phone]);
  const addTag = (raw) => {
    const t = (raw || '').trim().replace(/,$/, '');
    if (!t) return;
    setR(prev => prev.tags.includes(t) ? prev : { ...prev, tags: [...prev.tags, t] });
    setTagDraft('');
  };
  const removeTag = (t) => setR(prev => ({ ...prev, tags: prev.tags.filter(x => x !== t) }));
  const trySave = () => {
    const errs = {};
    if (!r.name.trim()) errs.name = 'Required';
    // Require agent ONLY when the account has producers configured.
    // Original BUG-007 fix prevented orphaned leads slipping through
    // a mis-selected form on multi-agent accounts. But a brand-new
    // solo signup has zero producers and no way to configure one
    // before adding their first lead — hard-blocking their first
    // task. Compromise: if agentChoices is empty (solo new signup),
    // save the lead ownerless AND surface a one-line hint on the
    // save toast so the operator knows to assign later. Multi-agent
    // accounts still block on missing selection.
    if (!r.agentProdNum && (agentChoices?.length || 0) > 0) {
      errs.agentProdNum = 'Pick an agent';
    }
    // Reject the literal placeholder phone (BUG-005). A blank field is
    // fine — only typed-in fake phones get blocked.
    if (r.phone && isPlaceholderPhone(r.phone)) {
      errs.phone = 'That looks like the example phone — please enter a real number or leave blank';
    }
    // Audit 2026-07-01 BUG-014: fmtPhone masks input as they type but
    // never blocks a partial number. A 3-digit "(555" would save as-is
    // and every downstream SMS attempt would fail silently. Require
    // either 10 digits (US) or blank.
    if (r.phone) {
      const digits = String(r.phone).replace(/\D/g, '');
      if (digits.length > 0 && digits.length !== 10 && !errs.phone) {
        errs.phone = 'Enter a 10-digit US phone number, or leave blank.';
      }
    }
    // Email format check (operator audit 2026-06-29 BUG-005). Empty is
    // fine — only typed-in invalid emails get flagged. Loose RFC-ish
    // pattern: anything with one @ and a dotted right side. Bounces
    // strings like "notanemail" or "user@" without false-positiving
    // edge-case real addresses.
    if (r.email && r.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email.trim())) {
      errs.email = 'Enter a valid email (or leave blank)';
    }
    setErrors(errs);
    if (Object.keys(errs).length === 0) {
      // commit any pending tag draft before save
      const pending = tagDraft.trim();
      const finalTags = pending && !r.tags.includes(pending) ? [...r.tags, pending] : r.tags;
      // Only persist consentText when the agent actually checked the box —
      // otherwise we'd inadvertently "fake" consent on every save.
      const consentText = r.hasConsent ? r.consentText : '';
      const consentSource = r.hasConsent ? (r.consentSource || 'form') : '';
      // Sanitize free-text name AT SAVE TIME (not per-keystroke). The
      // sanitizer's .trim() call was eating trailing spaces the moment
      // the operator hit space, which made the space bar feel broken.
      // Applying it here preserves the prompt-injection defense without
      // breaking normal typing behavior.
      onSave({ ...r, name: sanitizeFreeText(r.name), tags: finalTags, consentText, consentSource });
    }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onCancel}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-5 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg">{initial ? 'Edit lead' : 'New lead'}</h3>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-700"><X size={20}/></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <label className="block md:col-span-2">
            <span className="text-xs text-slate-500">Name *</span>
            <input
              value={r.name}
              onChange={e => set('name', e.target.value)}
              maxLength={120}
              className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${errors.name ? 'border-red-400' : 'border-slate-200'}`}
            />
            {errors.name && <span className="text-xs text-red-600">{errors.name}</span>}
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Phone</span>
            <input
              value={r.phone}
              onChange={e => set('phone', e.target.value)}
              placeholder="e.g. (415) 555-2671"
              className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${errors.phone ? 'border-red-400' : 'border-slate-200'}`}
            />
            {errors.phone && <span className="text-xs text-red-600">{errors.phone}</span>}
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Email</span>
            <input
              type="email"
              value={r.email}
              onChange={e => set('email', e.target.value)}
              className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${errors.email ? 'border-red-400' : 'border-slate-200'}`}
            />
            {errors.email && <span className="text-xs text-red-600">{errors.email}</span>}
          </label>
          <label className="block"><span className="text-xs text-slate-500">Source</span><input value={r.source} onChange={e => set('source', e.target.value)} placeholder="Facebook, Google, TikTok, Referral…" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/></label>
          <label className="block">
            <span className="text-xs text-slate-500">Assigned agent *</span>
            <select value={r.agentProdNum} onChange={e => set('agentProdNum', e.target.value)} className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white ${errors.agentProdNum ? 'border-red-400' : 'border-slate-200'}`}>
              <option value="">— Select —</option>
              {agentChoices.map(a => <option key={a.prodNum} value={a.prodNum}>{a.name}</option>)}
            </select>
            {errors.agentProdNum && <span className="text-xs text-red-600">{errors.agentProdNum}</span>}
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Lead source</span>
            <select value={r.leadSource || ''} onChange={e => set('leadSource', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">— Not set —</option>
              {LEAD_SOURCES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Follow-up date</span>
            <input type="datetime-local" value={r.followUpDate || ''} onChange={e => set('followUpDate', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Status</span>
            <select value={r.status} onChange={e => set('status', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
              {LEAD_STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs text-slate-500">Tags (Enter or comma to add)</span>
            <div className="mt-1 border border-slate-200 rounded-lg px-2 py-1.5 flex items-center gap-1 flex-wrap min-h-[2.5rem]">
              {r.tags.map(t => (
                <span key={t} className="inline-flex items-center gap-1 bg-violet-100 text-violet-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                  {t}
                  <button type="button" onClick={() => removeTag(t)} className="text-violet-500 hover:text-violet-800" aria-label={`Remove ${t}`}>×</button>
                </span>
              ))}
              <input
                value={tagDraft}
                onChange={e => {
                  const v = e.target.value;
                  if (v.endsWith(',')) addTag(v); else setTagDraft(v);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); addTag(tagDraft); }
                  else if (e.key === 'Backspace' && tagDraft === '' && r.tags.length > 0) { removeTag(r.tags[r.tags.length - 1]); }
                }}
                placeholder={r.tags.length === 0 ? "hot, callback, spanish-speaking…" : ""}
                className="flex-1 outline-none text-sm min-w-[120px]"
              />
            </div>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Lead type</span>
            <select value={r.leadType} onChange={e => set('leadType', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="general">General / other</option>
              <option value="final_expense">Final Expense (FEX)</option>
              <option value="mortgage_protection">Mortgage Protection</option>
              <option value="iul">IUL</option>
            </select>
            <span className="text-[10px] text-slate-400 mt-0.5 block">Drives which SMS workflow auto-enrolls + the Goal Engine funnel.</span>
          </label>
          {/* v2 — VERTICAL spine. Determines which Portal sees this
              lead. Health Portal pipeline + inbox are filtered to
              vertical='health' so the two books never mix. */}
          <div className="md:col-span-2">
            <span className="text-xs text-slate-500">Business Vertical</span>
            <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { id: 'life',   label: 'Life',   sub: 'FEX · Term · IUL · MP' },
                { id: 'health', label: 'Health', sub: 'ACA · Medicare · Supplemental' },
              ].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => set('vertical', opt.id)}
                  className={`border-2 rounded p-2 text-left ${r.vertical === opt.id
                    ? (opt.id === 'health' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-800 bg-slate-100')
                    : 'border-slate-200 hover:border-slate-300'}`}
                >
                  <div className="font-black text-sm">{opt.label}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{opt.sub}</div>
                </button>
              ))}
            </div>
          </div>
          {/* v2 — sector spine. Determines pipeline + compliance gate.
              Only meaningful inside the health vertical. */}
          {r.vertical === 'health' && (
          <div className="md:col-span-2">
            <span className="text-xs text-slate-500">Sector</span>
            <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { id: 'aca_private', label: 'ACA / Private', sub: 'Under-65 · year-round' },
                { id: 'medicare',    label: 'Medicare',      sub: '65+ · SOA required' },
              ].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => set('sector', opt.id)}
                  className={`border-2 rounded p-2 text-left ${r.sector === opt.id
                    ? (opt.id === 'medicare' ? 'border-blue-500 bg-blue-50' : 'border-emerald-500 bg-emerald-50')
                    : 'border-slate-200 hover:border-slate-300'}`}
                >
                  <div className="font-black text-sm">{opt.label}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{opt.sub}</div>
                </button>
              ))}
            </div>
          </div>
          )}
          <label className="block"><span className="text-xs text-slate-500">DOB</span><input type="date" value={r.dob || ''} onChange={e => set('dob', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/></label>
          <label className="block"><span className="text-xs text-slate-500">State</span><input value={r.state || ''} maxLength={2} onChange={e => set('state', e.target.value.toUpperCase())} placeholder="TX" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm uppercase font-mono"/></label>
          <label className="block">
            <span className="text-xs text-slate-500">Age</span>
            <input type="number" min="0" max="120" value={r.age || ''} onChange={e => set('age', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Gender</span>
            <select value={r.gender || ''} onChange={e => set('gender', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">—</option><option value="M">M</option><option value="F">F</option><option value="X">X</option>
            </select>
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs text-slate-500">Street address</span>
            <input value={r.address || ''} onChange={e => set('address', e.target.value)} placeholder="123 Main St" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">City</span>
            <input value={r.city || ''} onChange={e => set('city', e.target.value)} placeholder="Scottsdale" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Zip</span>
            <input value={r.zip || ''} onChange={e => set('zip', e.target.value)} placeholder="85255" maxLength={10} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono"/>
          </label>
          {r.sector === 'medicare' && (
            <div className="md:col-span-2 border-2 border-blue-200 bg-blue-50/40 rounded p-3 space-y-3">
              <div className="text-[10px] font-black uppercase tracking-wider text-blue-700">Medicare</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <label className="block col-span-2"><span className="text-xs text-slate-500">MBI</span><input value={r.mbi || ''} onChange={e => set('mbi', e.target.value.toUpperCase())} placeholder="1EG4-TE5-MK73" className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 font-mono"/></label>
                <label className="block"><span className="text-xs text-slate-500">Part A effective</span><input type="date" value={r.partAEffective || ''} onChange={e => set('partAEffective', e.target.value)} className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"/></label>
                <label className="block"><span className="text-xs text-slate-500">Part B effective</span><input type="date" value={r.partBEffective || ''} onChange={e => set('partBEffective', e.target.value)} className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"/></label>
                <label className="block"><span className="text-xs text-slate-500">Current carrier</span><input value={r.currentCarrier || ''} onChange={e => set('currentCarrier', e.target.value)} placeholder="UHC / Humana…" className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"/></label>
                <label className="block">
                  <span className="text-xs text-slate-500">Current plan type</span>
                  <select value={r.currentPlanType || ''} onChange={e => set('currentPlanType', e.target.value)} className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 bg-white">
                    <option value="">—</option><option value="MA">MA</option><option value="MAPD">MAPD</option><option value="PDP">PDP</option><option value="Med Supp">Med Supp</option><option value="Original Medicare">Original Medicare only</option>
                  </select>
                </label>
                <label className="block col-span-2">
                  <span className="text-xs text-slate-500">LIS / Dual status</span>
                  <select value={r.lisDualStatus || ''} onChange={e => set('lisDualStatus', e.target.value)} className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 bg-white">
                    <option value="">— none / unknown —</option>
                    <option value="LIS_full">LIS — Full subsidy</option>
                    <option value="LIS_partial">LIS — Partial subsidy</option>
                    <option value="FBDE">FBDE — Full Benefit Dual Eligible</option>
                    <option value="QMB">QMB</option>
                    <option value="SLMB">SLMB</option>
                    <option value="QI">QI</option>
                  </select>
                </label>
              </div>
              <div className="text-[10px] text-blue-900 bg-blue-100/60 border border-blue-200 rounded px-2 py-1.5 uppercase tracking-wider font-bold">
                ⚠ Medicare enrollment requires SOA ≥48h before appt + recorded call + current AHIP/carrier certs.
              </div>
            </div>
          )}
          <label className="md:col-span-2 flex items-start gap-2 text-xs">
            <input type="checkbox" className="mt-0.5" checked={!!r.optInSms} onChange={e => set('optInSms', e.target.checked)}/>
            <span>SMS opt-in (required before any text)</span>
          </label>
          <label className="md:col-span-2 flex items-start gap-2 text-xs">
            <input type="checkbox" className="mt-0.5" checked={!!r.ptc} onChange={e => set('ptc', e.target.checked)}/>
            <span>Permission to Contact (PTC) — REQUIRED for Medicare outbound. No unsolicited Medicare SMS.</span>
          </label>
          <label className="block"><span className="text-xs text-slate-500">Coverage type</span><input value={r.coverageType} onChange={e => set('coverageType', e.target.value)} placeholder="Final Expense, Term Life, IUL…" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/></label>
          <label className="block"><span className="text-xs text-slate-500">Est. monthly premium ($)</span><input type="number" min="0" value={r.estPremium} onChange={e => set('estPremium', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/></label>
          <label className="block"><span className="text-xs text-slate-500">Lead cost ($)</span><input type="number" min="0" step="0.01" value={r.cost} onChange={e => set('cost', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/></label>
          <label className="block"><span className="text-xs text-slate-500">Premium on close ($/mo)</span><input type="number" min="0" value={r.premium} onChange={e => set('premium', e.target.value)} placeholder="Set when status = Closed" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/></label>
          <label className="block">
            <span className="text-xs text-slate-500">Draft day of month (1–31)</span>
            <input type="number" min="1" max="31" value={r.draftDay || ''} onChange={e => set('draftDay', e.target.value)} placeholder="e.g. 15" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
            <span className="text-[10px] text-slate-400 mt-0.5 block">Used by the Payment Reminder workflow to text 3 days before the bank draft.</span>
          </label>
          {/* Removed redundant "Birthday" input — DOB above already drives
              the Birthday workflow and {age} merge field. At save time,
              lead.birthday is auto-mirrored from lead.dob so
              api/workflow/scan-birthdays.js + api/workflow/tick.js
              (which read lead.birthday) keep working without change.
              Fixes founder-reported "DOB tab twice" confusion. */}
          <div className="md:col-span-2">
            <NotesWithAiExtract r={r} set={set}/>
          </div>
        </div>

        {/* TCPA / A2P consent — opt-in checkbox + editable disclosure text */}
        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 space-y-2">
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">TCPA / A2P consent</div>
          <label className="inline-flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={r.hasConsent} onChange={e => set('hasConsent', e.target.checked)} className="rounded mt-0.5"/>
            <span>I confirm this lead consented (verbally, in writing, or by web-form opt-in) to the disclosure below. <strong>Required before calls or SMS go out.</strong></span>
          </label>
          {r.hasConsent && (
            <>
              <textarea
                value={r.consentText}
                onChange={e => set('consentText', e.target.value)}
                rows={3}
                placeholder={DEFAULT_TCPA_TEXT}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs font-mono bg-white"
              />
              <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
                <select value={r.consentSource} onChange={e => set('consentSource', e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1 text-xs bg-white">
                  <option value="form">Web form opt-in</option>
                  <option value="verbal">Verbal (recorded call)</option>
                  <option value="written">Written / signed</option>
                  <option value="sms">SMS opt-in</option>
                  <option value="csv import">CSV import (vendor-attested)</option>
                </select>
                <span>Timestamped on save.</span>
              </div>
            </>
          )}
          {!r.hasConsent && (
            <div className="text-xs text-amber-700">
              ⚠ Without consent on record, this lead will display a "no consent" badge and the SMS/Call buttons should not be used until you capture consent.
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
          <button onClick={onCancel} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button onClick={trySave} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold">{initial ? 'Save changes' : 'Create lead'}</button>
        </div>
      </div>
    </div>
  );
}

function LeadsCsvImportModal({ agentChoices, existingLeads = [], onCancel, onImport, initialFiles = null }) {
  const [text, setText] = useState('');
  const [defaultAgent, setDefaultAgent] = useState('');
  const [defaultSource, setDefaultSource] = useState('');
  const [skipDupes, setSkipDupes] = useState(true);
  // Re-import / refresh mode — when on, duplicates are MERGED into the
  // existing matched leads (phone or email) instead of either being
  // skipped or appended as new records. Useful for refreshing the
  // formatting / address fields on already-ingested batches.
  const [overwriteOnMatch, setOverwriteOnMatch] = useState(false);
  // Total $ spent on this batch (e.g., $500 for a list of 50 leads).
  // Divided across the imported leads as per-lead `cost` so ROI math
  // (revenue per lead, cost per appt, cost per close) works without
  // the agent doing manual division per row.
  const [totalSpend, setTotalSpend] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [droppedFileName, setDroppedFileName] = useState('');
  const [ingestError, setIngestError] = useState('');
  const fileInputRef = useRef(null);

  // Read one file's text as CSV. Supports:
  //   .csv / .tsv / .txt   → read as text; tabs auto-normalized to commas
  //   .xlsx / .xls / .xlsm → dynamic-import xlsx and export sheet 1 as CSV
  // Also has a BINARY SNIFF fallback: if a file we thought was text
  // turns out to start with the ZIP magic 'PK' (which every .xlsx is)
  // we re-read it as an Excel workbook. That catches any file whose
  // extension is missing or whose MIME is `application/octet-stream`,
  // so no matter how the file arrives we never dump raw ZIP bytes into
  // the textarea.
  const parseXlsxBuffer = async (buf) => {
    const XLSX = await import('xlsx');
    const wb  = XLSX.read(buf, { type: 'array' });
    const sheetName = wb.SheetNames[0];
    if (!sheetName) return '';
    const ws = wb.Sheets[sheetName];
    return XLSX.utils.sheet_to_csv(ws, { blankrows: false });
  };

  const readFileAsCsv = async (file) => {
    const name = (file.name || '').toLowerCase();
    const mime = (file.type || '').toLowerCase();
    const isExcelByName = /\.(xlsx|xls|xlsm|xlsb)$/i.test(name);
    const isExcelByMime = mime.includes('spreadsheetml')
      || mime.includes('ms-excel')
      || mime.includes('officedocument');

    if (isExcelByName || isExcelByMime) {
      const buf = await file.arrayBuffer();
      try { return await parseXlsxBuffer(buf); }
      catch (err) {
        console.error('[csv import] xlsx parse failed:', err);
        throw new Error(`Could not parse "${file.name}" as Excel: ${err.message}`);
      }
    }

    // Text path. Sniff the first 2 bytes — if they're the ZIP magic
    // 'PK' this is actually a mis-typed .xlsx (common when Safari
    // strips the MIME on drag from Finder). Re-read as xlsx.
    const buf = await file.arrayBuffer();
    const head = new Uint8Array(buf.slice(0, 2));
    if (head[0] === 0x50 && head[1] === 0x4b /* 'PK' */) {
      try { return await parseXlsxBuffer(buf); }
      catch (err) {
        throw new Error(`"${file.name}" looks like an Excel file but couldn't be parsed: ${err.message}`);
      }
    }

    const raw = new TextDecoder('utf-8', { fatal: false }).decode(buf);
    return raw.includes('\t') && !raw.includes(',')
      ? raw.split(/\r?\n/).map(l => l.split('\t').map(c => /[",\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c).join(',')).join('\n')
      : raw;
  };

  // Ingest one OR many CSV/TSV files. When multiple files arrive (the
  // common drag-multi-from-Finder case), we keep the FIRST file's
  // header row and concat each subsequent file's BODY rows under it.
  // Files whose first line doesn't look like a header (no commas)
  // get appended as-is.
  const ingestFiles = async (files) => {
    const arr = Array.from(files || []).filter(Boolean);
    if (arr.length === 0) return;
    arr.forEach(f => {
      const name = (f.name || '').toLowerCase();
      const mime = (f.type || '').toLowerCase();
      const looksLikeCsvOrExcel = /\.(csv|tsv|txt|xlsx|xls|xlsm)$/i.test(name)
        || mime.startsWith('text/')
        || mime === 'application/vnd.ms-excel'
        || mime.includes('spreadsheetml')
        || !mime;
      if (!looksLikeCsvOrExcel) {
        console.warn(`[csv import] file "${f.name}" has unusual type "${mime}" — attempting to read as text anyway.`);
      }
    });
    setIngestError('');
    try {
      const texts = await Promise.all(arr.map(readFileAsCsv));
      let combined;
      if (texts.length === 1) {
        combined = texts[0];
      } else {
        const firstLines = texts[0].split(/\r?\n/);
        const header = firstLines[0];
        const bodies = [firstLines.slice(1).join('\n')];
        for (let i = 1; i < texts.length; i++) {
          const lines = texts[i].split(/\r?\n/);
          // Skip the next file's first line only if it looks like a
          // header — exact match against the first file's header OR
          // identical column count + non-numeric leading token.
          const first = lines[0] || '';
          const looksLikeHeader = first === header || (/^[A-Za-z"]/.test(first) && first.includes(','));
          bodies.push((looksLikeHeader ? lines.slice(1) : lines).join('\n'));
        }
        combined = [header, ...bodies].filter(Boolean).join('\n');
      }
      setText(combined);
      setDroppedFileName(arr.length === 1 ? (arr[0].name || 'pasted file') : `${arr.length} files: ${arr.map(f => f.name).join(', ')}`);
    } catch (err) {
      console.error('[csv import] failed to read files:', err);
      // Surface the parse error inline so the user isn't left staring
      // at raw ZIP bytes (or an empty textarea) wondering what happened.
      setIngestError(err.message || 'Could not read the file. Try saving it as .csv or .xlsx and drop again.');
      setText('');
      setDroppedFileName('');
      // Fire-and-forget: tell the admin panel a subscriber tripped
      // over an import. Shape hints only — no rows, no PII.
      try {
        // Resolve identity lazily so we don't add a prop to the modal
        // just for a fire-and-forget signal. Silent on any failure.
        (async () => {
          let owner_id = null, email = null;
          try {
            const { data: { user } } = await supabase.auth.getUser();
            owner_id = user?.id || null;
            email    = user?.email || null;
          } catch { /* anonymous signal is still valuable to the admin */ }
          askAdminForHelp({
            tag:      'lead_csv_import',
            owner_id,
            email,
            severity: 'warn',
            shape: {
              error: String(err.message || err).slice(0, 200),
              file_count: arr.length,
              file_names: arr.map(f => (f.name || 'unknown').slice(0, 80)),
              file_types: arr.map(f => f.type || 'unknown'),
              file_bytes: arr.map(f => f.size || 0),
            },
          });
        })();
      } catch { /* helper never throws, but paranoia */ }
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragOver) setDragOver(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Ignore child-element transitions — only flip off when the cursor
    // genuinely leaves the dropzone bounds.
    if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length > 0) ingestFiles(files);
  };

  // If the parent passed pre-dropped files (drag-onto-page handoff),
  // ingest them once on mount so the textarea is already populated
  // when the modal opens.
  useEffect(() => {
    if (initialFiles && initialFiles.length > 0) ingestFiles(initialFiles);
    // eslint-disable-next-line
  }, [initialFiles]);

  // Pre-build dedupe keysets from existing leads — O(1) match per row.
  const existingPhoneKeys = React.useMemo(() => {
    const s = new Set();
    existingLeads.forEach(l => { const k = dedupePhoneKey(l.phone); if (k) s.add(k); });
    return s;
  }, [existingLeads]);
  const existingEmailKeys = React.useMemo(() => {
    const s = new Set();
    existingLeads.forEach(l => { const k = dedupeEmailKey(l.email); if (k) s.add(k); });
    return s;
  }, [existingLeads]);

  const parsed = React.useMemo(() => {
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return [];
    const splitCsv = (line) => {
      const out = []; let cur = ''; let inQ = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQ) { if (ch === '"' && line[i+1] === '"') { cur += '"'; i++; } else if (ch === '"') inQ = false; else cur += ch; }
        else { if (ch === ',') { out.push(cur); cur = ''; } else if (ch === '"') inQ = true; else cur += ch; }
      }
      out.push(cur);
      return out.map(s => s.trim());
    };
    // Header normalization: real vendor CSVs ship every naming
    // convention imaginable — 'first_name', 'first-name', 'FirstName',
    // 'First Name', 'F Name', 'FIRST NAME'. Collapse everything to
    // lowercase + strip separators so the matcher only has to know
    // ONE canonical form. This was the "Parsed 0 rows" root cause on
    // Harrison's client's CSV — the header row used underscores.
    const norm = (s) => String(s || '').toLowerCase().trim().replace(/[_\-.]+/g, ' ').replace(/\s+/g, ' ');
    const rawHeader = splitCsv(lines[0]);
    const header = rawHeader.map(norm);
    // Greedy column matching — normalize BOTH sides so 'first_name'
    // === 'First Name' === 'firstname' all resolve.
    const findCol = (names) => {
      for (const n of names) {
        const nk = norm(n);
        // Direct match first
        const direct = header.indexOf(nk);
        if (direct >= 0) return direct;
        // Space-collapsed match ('firstname' vs 'first name')
        const collapsed = nk.replace(/\s+/g, '');
        const flatHit = header.findIndex(h => h.replace(/\s+/g, '') === collapsed);
        if (flatHit >= 0) return flatHit;
      }
      return -1;
    };
    const idx = {
      first: findCol(['firstname', 'first', 'first name', 'f name', 'fname', 'given name']),
      last:  findCol(['lastname', 'last', 'last name', 'l name', 'lname', 'surname', 'family name']),
      name:  findCol(['name', 'full name', 'client name', 'contact', 'contact name', 'lead name', 'customer', 'customer name', 'prospect', 'prospect name', 'lead']),
      phone: findCol(['phone', 'mobile', 'cell', 'phone number', 'cell phone', 'mobile phone', 'home phone', 'contact phone', 'phone1', 'primary phone', 'tel', 'telephone']),
      email: findCol(['email', 'email address', 'e mail', 'primary email', 'contact email']),
      source: findCol(['source', 'leadsource', 'lead source']),
      ad:    findCol(['ad', 'ad name', 'ad set', 'creative']),
      platform: findCol(['platform']),
      dob:   findCol(['dob', 'birthdate', 'date of birth', 'birth date']),
      age:   findCol(['age']),
      gender: findCol(['gender']),
      state: findCol(['state']),
      // "Interested In" is the lead-type discriminator — vendor CSVs
      // ship this column to indicate what the lead was shopping for.
      interestedIn: findCol(['interested in', 'interestedin', 'product', 'lead type']),
      // Mortgage Protection fields
      mortgageBalance:  findCol(['mortgage balance', 'mortgagebalance']),
      mortgagePayment:  findCol(['mortgage payment', 'mortgagepayment']),
      beneficiary:      findCol(['beneficiary', 'beneficiary type']),
      beneficiaryName:  findCol(['beneficiary name', 'beneficiaryname']),
      healthFlag:       findCol(['history of heart attack stroke cancer', 'health history', 'major medical']),
      // IUL fields
      iulGoal:          findCol(['iul goal', 'goal']),
      employmentStatus: findCol(['employment status', 'employmentstatus', 'employment']),
      monthlyContribution: findCol(['desired mo contribution', 'monthly contribution', 'desired monthly contribution']),
      retirementPlan:   findCol(['current retirement plan', 'retirement plan']),
      retirementAge:    findCol(['desired retirement age', 'retirement age']),
      // TCPA / compliance
      trustedFormUrl:   findCol(['trusted form url', 'trustedform url', 'trustedform', 'trusted_form_url']),
      otpCode:          findCol(['otp code', 'otp', 'otp_code']),
      ipAddress:        findCol(['ip address', 'ip', 'ip_address']),
      // v2 SECTOR + Medicare-specific columns. Sector column is
      // optional — if missing we auto-infer from age/DOB/interestedIn.
      sector:           findCol(['sector', 'vertical', 'lob', 'line of business']),
      mbi:              findCol(['mbi', 'medicare beneficiary id', 'medicare id']),
      partAEffective:   findCol(['part a effective', 'part a', 'part_a_effective']),
      partBEffective:   findCol(['part b effective', 'part b', 'part_b_effective']),
      lisDualStatus:    findCol(['lis', 'lis status', 'dual eligible', 'dual status', 'lis_dual_status']),
      currentCarrier:   findCol(['current carrier', 'existing carrier', 'current_carrier']),
      currentPlanType:  findCol(['current plan type', 'plan type', 'current_plan_type']),
      ptc:              findCol(['ptc', 'permission to contact', 'permission_to_contact']),
      optInSms:         findCol(['opt-in sms', 'opt in sms', 'sms opt in', 'sms_opt_in']),
      // Address — single column wins; otherwise we glue together any
      // split fields (street + apt + city + zip) into one display
      // string. State is already its own column above so we don't
      // duplicate it.
      address:          findCol(['address', 'street address', 'street', 'home address', 'mailing address', 'street_address']),
      address1:         findCol(['address 1', 'address1', 'address line 1', 'addr 1', 'address_line_1', 'street 1']),
      address2:         findCol(['address 2', 'address2', 'address line 2', 'addr 2', 'address_line_2', 'apt', 'suite', 'unit']),
      city:             findCol(['city', 'town']),
      zip:              findCol(['zip', 'zipcode', 'zip code', 'postal', 'postal code', 'postal_code']),
    };
    // Detect lead-type from a few common signals. "Interested In" wins
    // if present; otherwise we infer from which carrier-specific
    // columns are populated in the row body.
    const detectLeadType = (cols) => {
      const interested = idx.interestedIn >= 0 ? (cols[idx.interestedIn] || '').toLowerCase() : '';
      if (interested.includes('mortgage')) return 'mortgage_protection';
      if (interested.includes('iul') || interested.includes('universal life') || interested.includes('retirement')) return 'iul';
      if (interested.includes('final expense') || interested.includes('burial') || interested.includes('fex')) return 'final_expense';
      if (idx.mortgageBalance >= 0 && (cols[idx.mortgageBalance] || '').trim()) return 'mortgage_protection';
      if (idx.monthlyContribution >= 0 && (cols[idx.monthlyContribution] || '').trim()) return 'iul';
      return 'general';
    };
    // Content-sniffing fallback for header-less CSVs.
    // If NONE of the core column aliases matched, the first line
    // isn't a real header (client CSVs frequently ship raw exports
    // with no header at all — first line is data). Parse every line
    // including lines[0], and infer each column by content:
    //   digits (10-11)      → phone
    //   contains '@'        → email
    //   letters + space     → name
    //   date-ish pattern    → dob
    //   2-letter uppercase  → state
    // This is the "Parsed 0 rows" recovery path Harrison's client hit.
    const noHeader = idx.name < 0 && idx.first < 0 && idx.phone < 0 && idx.email < 0;
    const looksLikePhone = (v) => {
      const d = String(v || '').replace(/\D/g, '');
      return d.length >= 10 && d.length <= 11;
    };
    const looksLikeEmail = (v) => /@/.test(String(v || ''));
    const looksLikeDate  = (v) => /^\d{4}-\d{1,2}-\d{1,2}|\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(String(v || '').trim());
    const looksLikeState = (v) => /^[A-Z]{2}$/.test(String(v || '').trim());
    const looksLikeTimestamp = (v) => /^\d{4}-\d{2}-\d{2}[ T]\d/.test(String(v || '').trim());
    const looksLikeName  = (v) => {
      const s = String(v || '').trim();
      // Two+ alpha tokens, or one long alpha token — but NOT a date/timestamp/phone.
      if (!s || looksLikePhone(s) || looksLikeEmail(s) || looksLikeDate(s) || looksLikeTimestamp(s)) return false;
      return /^[A-Za-z][A-Za-z .'()\-,]*[A-Za-z.)]$/.test(s) && s.length >= 3;
    };
    // Vendor lead exports are almost always in the shape
    //   [timestamp?, name, phone, dob, age/range, state, family_note?]
    // So we prefer the FIRST name-shaped column, not the longest.
    // Longest-wins was picking up trailing "Madhia Nadeemmy waife"-
    // style family notes over the real name.
    // Also: skip a name-shaped column if it looks like a US state
    // full-name (rare but not zero).
    const inferRow = (cols) => {
      const r = { name: '', phone: '', email: '', dob: '', state: '' };
      for (const raw of cols) {
        const v = String(raw || '').trim();
        if (!v) continue;
        if (!r.email && looksLikeEmail(v)) { r.email = v; continue; }
        if (!r.phone && looksLikePhone(v)) { r.phone = v; continue; }
        if (!r.dob   && looksLikeDate(v))  { r.dob = v; continue; }
        if (!r.state && looksLikeState(v)) { r.state = v; continue; }
        if (!r.name && looksLikeName(v))   { r.name = v; }
      }
      return r;
    };

    // Track inline dupes WITHIN the CSV itself too — same number listed twice.
    const csvPhoneSeen = new Set();
    const csvEmailSeen = new Set();
    const rowLines = noHeader ? lines : lines.slice(1);
    return rowLines.map(line => {
      const cols = splitCsv(line);
      // Header-less path: infer core fields from row content, and
      // seed non-core fields with the inference result so DOB / state
      // still populate downstream.
      const sniffed = noHeader ? inferRow(cols) : null;
      let name = idx.name >= 0 ? (cols[idx.name] || '').trim() : '';
      if (!name) {
        name = `${idx.first >= 0 ? cols[idx.first] || '' : ''} ${idx.last >= 0 ? cols[idx.last] || '' : ''}`.trim();
      }
      if (!name && sniffed) name = sniffed.name;
      const hasAd = idx.ad >= 0 && (cols[idx.ad] || '').trim();
      let phone = fmtPhoneUS(idx.phone >= 0 ? cols[idx.phone] || '' : '');
      if (!phone && sniffed?.phone) phone = fmtPhoneUS(sniffed.phone);
      let email = idx.email >= 0 ? cols[idx.email] || '' : '';
      if (!email && sniffed?.email) email = sniffed.email;
      // Fallback name — never drop a row that has a usable identifier.
      // Prefer email prefix ('sarah.j@x.com' → 'Sarah J'), else phone,
      // else a generic marker so counting works.
      if (!name) {
        if (email) {
          const prefix = email.split('@')[0].replace(/[._-]+/g, ' ').trim();
          name = prefix
            ? prefix.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
            : '';
        }
        if (!name && phone) name = phone;
        if (!name)          name = '(no name)';
      }
      const pKey = dedupePhoneKey(phone);
      const eKey = dedupeEmailKey(email);
      // Match priority: phone first (most reliable), then email.
      const matchesExisting = (pKey && existingPhoneKeys.has(pKey)) || (eKey && existingEmailKeys.has(eKey));
      const matchesInCsv = (pKey && csvPhoneSeen.has(pKey)) || (eKey && csvEmailSeen.has(eKey));
      if (pKey) csvPhoneSeen.add(pKey);
      if (eKey) csvEmailSeen.add(eKey);
      const dupReason = matchesExisting ? 'in library' : matchesInCsv ? 'in this CSV' : '';
      const get = (i) => i >= 0 ? (cols[i] || '').trim() : '';
      const leadType = detectLeadType(cols);
      return {
        name,
        phone,
        email,
        source: get(idx.source) || defaultSource,
        leadSource: hasAd ? 'Facebook Ad' : '',
        agentProdNum: defaultAgent,
        leadType,
        // Common demographics — surfaced in the drawer regardless of type.
        dob:     normalizeDob(get(idx.dob) || sniffed?.dob || ''),
        age:     get(idx.age),
        gender:  get(idx.gender),
        state:   get(idx.state) || sniffed?.state || '',
        // Address — capture EVERY address part the CSV ships. Street
        // goes into `address`; city/zip become their own lead fields
        // so downstream code (Lob mailers, geo filters, dialer
        // territory rules) can read them directly. The lead table
        // composes a single readable line from all four at render time.
        address: get(idx.address) || [get(idx.address1), get(idx.address2)].filter(Boolean).join(' ').trim(),
        city:    get(idx.city),
        zip:     get(idx.zip),
        ad:      get(idx.ad),
        platform: get(idx.platform),
        // Type-specific profit drivers. We store every field on every
        // lead regardless of type so changing a lead's type later
        // doesn't lose data the row originally carried.
        mortgageBalance:  get(idx.mortgageBalance),
        mortgagePayment:  get(idx.mortgagePayment),
        beneficiary:      get(idx.beneficiary),
        beneficiaryName:  get(idx.beneficiaryName),
        healthFlag:       get(idx.healthFlag),
        iulGoal:          get(idx.iulGoal),
        employmentStatus: get(idx.employmentStatus),
        monthlyContribution: get(idx.monthlyContribution),
        retirementPlan:   get(idx.retirementPlan),
        retirementAge:    get(idx.retirementAge),
        // TCPA evidence — captured at lead-purchase time, not by the
        // agent. Keep it on the record so consent is auditable.
        trustedFormUrl:   get(idx.trustedFormUrl),
        otpCode:          get(idx.otpCode),
        ipAddress:        get(idx.ipAddress),
        // v2 sector + Medicare fields. inferLeadSector falls back to
        // age/DOB/interestedIn when the Sector column isn't present.
        sector: inferLeadSector({
          sector:        get(idx.sector),
          age:           get(idx.age),
          dob:           get(idx.dob),
          interestedIn:  idx.interestedIn >= 0 ? get(idx.interestedIn) : '',
          leadType,
        }),
        mbi:              get(idx.mbi),
        partAEffective:   get(idx.partAEffective),
        partBEffective:   get(idx.partBEffective),
        lisDualStatus:    get(idx.lisDualStatus),
        currentCarrier:   get(idx.currentCarrier),
        currentPlanType:  get(idx.currentPlanType),
        ptc:              ['1','true','yes','y'].includes((get(idx.ptc) || '').toLowerCase()),
        optInSms:         ['1','true','yes','y'].includes((get(idx.optInSms) || '').toLowerCase()),
        _dup: dupReason,
      };
    // Keep any row that has SOMETHING to work with — a name, a phone,
    // or an email. Rows with none of the three are truly blank and
    // filtered out. The old filter `r => r.name` silently dropped
    // every row when the CSV's name column wasn't recognized. Never
    // again.
    }).filter(r => (r.name && r.name !== '(no name)') || r.phone || r.email);
  }, [text, defaultAgent, defaultSource, existingPhoneKeys, existingEmailKeys]);

  const dupCount = parsed.filter(p => p._dup).length;
  const newCount = parsed.length - dupCount;
  // When overwrite mode is on, include duplicates so they can be merged.
  // When off, honor the skip-duplicates flag.
  const toImport = overwriteOnMatch
    ? parsed
    : (skipDupes ? parsed.filter(p => !p._dup) : parsed);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onCancel}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl p-5 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg">Bulk import leads from CSV</h3>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-700"><X size={20}/></button>
        </div>
        <div className="text-sm text-slate-600">
          Paste CSV rows below. First row should be the header. Columns recognized (any subset is fine — column order doesn't matter):
          <div className="space-y-1 mt-1">
            <code className="block bg-slate-100 rounded px-2 py-1 text-xs font-mono"><strong>Core:</strong> First Name / Last Name (or Name), Phone, Email, Source, DOB, Age, Gender, State, Ad, Platform</code>
            <code className="block bg-emerald-50 border border-emerald-200 rounded px-2 py-1 text-xs font-mono"><strong>Mortgage Protection:</strong> Interested In, Mortgage Balance, Mortgage Payment, Beneficiary, Beneficiary Name, History of Heart Attack Stroke Cancer</code>
            <code className="block bg-violet-50 border border-violet-200 rounded px-2 py-1 text-xs font-mono"><strong>IUL:</strong> Interested In, IUL Goal, Employment Status, Desired Mo Contribution, Current Retirement Plan, Desired Retirement Age</code>
            <code className="block bg-amber-50 border border-amber-200 rounded px-2 py-1 text-xs font-mono"><strong>TCPA:</strong> Trusted Form URL, OTP Code, IP Address — if a TrustedForm URL is present, consent is captured automatically on import.</code>
            <code className="block bg-blue-50 border border-blue-200 rounded px-2 py-1 text-xs font-mono"><strong>Medicare:</strong> Sector (medicare/aca_private), MBI, Part A Effective, Part B Effective, LIS Status, Current Carrier, Current Plan Type, PTC, Opt-in SMS — sector auto-infers from age/DOB/Interested In when omitted.</code>
          </div>
          <div className="text-xs text-slate-500 mt-2">Lead type is detected from the "Interested In" column (or inferred from which type-specific columns are populated). Use the Lead Type pills above the list to sort imported leads by type.</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs text-slate-500">Default assigned agent</span>
            <select value={defaultAgent} onChange={e => setDefaultAgent(e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">— None (assign later) —</option>
              {agentChoices.map(a => <option key={a.prodNum} value={a.prodNum}>{a.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Default source (if CSV is missing)</span>
            <input value={defaultSource} onChange={e => setDefaultSource(e.target.value)} placeholder="e.g. Facebook" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
          </label>
        </div>
        {/* Total spend on this batch — splits evenly across the
            imported leads as per-lead `cost` so ROI math (CPL, cost
            per appt, cost per close) works out of the box. */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <label className="flex-1 min-w-[200px]">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-800">Total spent on this batch</span>
              <div className="flex items-center mt-1">
                <span className="px-3 py-2 bg-white border border-r-0 border-amber-300 rounded-l-lg text-amber-700 font-bold">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={totalSpend}
                  onChange={e => setTotalSpend(e.target.value.replace(/[^0-9.]/g, ''))}
                  placeholder="500"
                  className="flex-1 px-3 py-2 border border-amber-300 rounded-r-lg text-sm bg-white focus:outline-none focus:border-amber-500"
                />
              </div>
            </label>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Per-lead cost</div>
              <div className="text-xl font-bold text-amber-900">
                {(() => {
                  const t = Number(totalSpend) || 0;
                  const n = toImport.length;
                  if (t === 0 || n === 0) return '—';
                  return fmt(t / n);
                })()}
              </div>
              <div className="text-[10px] text-amber-700">across {toImport.length} lead{toImport.length === 1 ? '' : 's'}</div>
            </div>
          </div>
          <div className="text-[11px] text-amber-800 mt-2">
            Optional. If set, this dollar amount is divided evenly across the {toImport.length} new lead{toImport.length === 1 ? '' : 's'} you're importing and stored as each lead's <code className="bg-white border border-amber-200 rounded px-1 py-0.5 font-mono text-[10px]">cost</code>. Powers CPL, cost-per-appt, and cost-per-close in the funnel metrics.
          </div>
        </div>
        {/* Drag-and-drop CSV zone wrapping the textarea. Drop a file
            anywhere on this region to read it into the box; you can
            still type/paste directly. Click the highlighted bar at
            the top to open the OS file picker. */}
        <div
          onDragOver={onDragOver}
          onDragEnter={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          className={`relative rounded-lg border-2 border-dashed transition-colors ${
            dragOver ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 bg-white'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.tsv,.txt,.xlsx,.xls,.xlsm,text/csv,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            multiple
            onChange={e => ingestFiles(e.target.files)}
            className="hidden"
          />
          {ingestError && (
            <div className="bg-rose-50 border border-rose-200 text-rose-800 text-xs px-3 py-2 rounded-t-lg">
              ⚠ {ingestError}
            </div>
          )}
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50 rounded-t-lg text-xs">
            <div className="flex items-center gap-2 text-slate-600">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="bg-slate-900 hover:bg-slate-800 text-white px-3 py-1 rounded font-semibold text-xs"
              >
                Choose file…
              </button>
              <span className={dragOver ? 'text-emerald-700 font-semibold' : 'text-slate-500'}>
                {dragOver ? 'Drop to load' : 'or drag CSV / Excel (.xlsx, .xls) files anywhere in this box'}
              </span>
            </div>
            {droppedFileName && (
              <span className="text-slate-500 truncate max-w-[260px]" title={droppedFileName}>
                Loaded: <span className="font-semibold text-slate-700">{droppedFileName}</span>
                <button
                  type="button"
                  onClick={() => { setText(''); setDroppedFileName(''); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  className="ml-2 text-slate-400 hover:text-slate-700"
                  title="Clear"
                >×</button>
              </span>
            )}
          </div>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            rows={10}
            placeholder={`Drop a CSV file here, or paste rows directly.\nFirstName,LastName,Phone,Email,Source\nJane,Doe,555-1234,jane@example.com,Facebook\nJohn,Smith,555-5678,,Referral`}
            className="w-full px-3 py-2 text-sm font-mono bg-transparent focus:outline-none rounded-b-lg resize-y"
          />
        </div>
        <div className="text-sm text-slate-600 space-y-1">
          <div>
            Parsed <strong>{parsed.length}</strong> row{parsed.length === 1 ? '' : 's'}:
            <span className="ml-2 inline-flex items-center gap-2">
              <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded text-xs font-semibold">{newCount} new</span>
              {dupCount > 0 && (
                <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-xs font-semibold" title="Match on phone (last 10 digits) or email (lowercase)">⚠ {dupCount} duplicate{dupCount === 1 ? '' : 's'}</span>
              )}
            </span>
          </div>
          {parsed.length === 0 && text.trim().length > 0 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-2 text-xs space-y-1">
              <div className="font-semibold">Parsed 0 rows despite having data. Common causes:</div>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>Header row missing recognized column names like <code className="bg-white px-1">Phone</code>, <code className="bg-white px-1">Email</code>, or <code className="bg-white px-1">Name</code>. Add a header row and try again.</li>
                <li>No phone / email / name detectable in any row. Every lead needs at least one of the three.</li>
                <li>Rows separated by an unusual delimiter (semicolon, pipe). Re-save as comma-separated.</li>
              </ul>
            </div>
          )}
          {dupCount > 0 && (
            <div className="space-y-1.5">
              <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={skipDupes && !overwriteOnMatch} disabled={overwriteOnMatch} onChange={e => setSkipDupes(e.target.checked)} className="rounded"/>
                Skip duplicates on import (recommended). Uncheck to import every row — duplicates will create new records.
              </label>
              <label className="inline-flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                <input type="checkbox" checked={overwriteOnMatch} onChange={e => setOverwriteOnMatch(e.target.checked)} className="rounded"/>
                <span><strong>Re-import / refresh existing leads.</strong> Match on phone or email and overwrite name, phone, email, DOB, age, state, address, city, zip on the matching record. Activity, status, agent assignment, and consent are preserved.</span>
              </label>
            </div>
          )}
          {dupCount > 0 && (
            <div className="text-xs text-slate-500 max-h-32 overflow-y-auto border border-amber-200 rounded p-2 bg-amber-50">
              {parsed.filter(p => p._dup).slice(0, 8).map((p, i) => (
                <div key={i}>⚠ <strong>{p.name}</strong> — dupe ({p._dup}){p.phone ? ` · ${p.phone}` : ''}</div>
              ))}
              {dupCount > 8 && <div className="text-slate-400 italic mt-1">…and {dupCount - 8} more</div>}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
          <button onClick={onCancel} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button onClick={() => onImport(toImport, { totalSpend: Number(totalSpend) || 0, overwriteOnMatch })} disabled={toImport.length === 0} className={`px-4 py-2 ${overwriteOnMatch ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'} disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold`}>
            {overwriteOnMatch ? `Re-import & refresh ${toImport.length} row${toImport.length === 1 ? '' : 's'}` : `Import ${toImport.length} lead${toImport.length === 1 ? '' : 's'}`}
            {dupCount > 0 && !overwriteOnMatch && skipDupes && <span className="ml-1 text-xs opacity-80">(skipping {dupCount})</span>}
            {dupCount > 0 && overwriteOnMatch && <span className="ml-1 text-xs opacity-80">({dupCount} updating · {parsed.length - dupCount} new)</span>}
          </button>
        </div>
      </div>
    </div>
  );
}

// On-demand AI pre-brief for the lead detail drawer. Default collapsed
// — the operator clicks "Get pre-brief" before dialing. Result is
// cached per-lead-per-session so reopening the drawer doesn't re-bill.
// ═══════════════════════════════════════════════════════════════════
// LEAD HISTORY — Last contacted banner + unified timeline
//
// The setter question the drawer needs to answer first, always:
// "When did we last touch this person, and how?" — so the operator
// doesn't double-call someone who was dialed 3 hours ago.
//
// Timeline is a read composition, not a new table:
//   calls   → call rows (already writes on Log Call)
//   outreach → sms/email/manual outreach rows (already writes on
//              Log Outreach + auto-writes from the SMS webhook path)
//   activity → the lead's inline activity[] which carries lead-
//              created, status transitions, page-view synthetic
//              events, consent captures, opt-outs
// Each source normalizes to { at, direction, kind, label, body,
// outcome, actor, marked, gates } so the render pipeline is one path.
// ═══════════════════════════════════════════════════════════════════

// Normalize the three sources into one unified timeline event shape.
function unifyLeadTimeline({ callHistory = [], outreachLog = [], activity = [], agentName }) {
  const events = [];

  callHistory.forEach(c => {
    const iso = c.date ? `${c.date}T${c.startTime || '00:00'}:00Z` : (c.createdAt || '');
    const outcomeLabel = String(c.outcome || '').replace(/_/g, ' ');
    events.push({
      at:        iso,
      direction: c.direction === 'inbound' ? 'IN' : 'OUT',
      kind:      'call',
      label:     `Call · ${c.direction === 'inbound' ? 'IN' : 'OUT'}`,
      body:      [outcomeLabel, c.notes].filter(Boolean).join(' — '),
      outcome:   outcomeLabel,
      actor:     agentName || 'operator',
      marked:    'human',
      gates:     'canDial ✓ · DNC ✓ · business hours ✓',
    });
  });

  outreachLog.forEach(o => {
    const iso = o.createdAt || (o.date ? `${o.date}T${o.time || '00:00'}:00Z` : '');
    const dir = /reply|inbound|received|opt_?in/i.test(String(o.outcome || o.note || '')) ? 'IN' : 'OUT';
    const kind = o.type === 'sms' ? 'sms' : o.type === 'email' ? 'email' : o.type === 'call' ? 'call' : 'outreach';
    const kindLabel = kind === 'sms' ? 'Text' : kind === 'email' ? 'Email' : kind === 'call' ? 'Call' : 'Outreach';
    events.push({
      at:        iso,
      direction: dir,
      kind,
      label:     `${kindLabel} · ${dir}`,
      body:      [o.outcome, o.note].filter(Boolean).join(' — '),
      outcome:   o.outcome || '',
      actor:     agentName || 'operator',
      marked:    kind === 'call' ? 'human' : 'auto',
      gates:     kind === 'sms' ? 'consent ✓ · quiet hours ✓' : kind === 'email' ? 'CAN-SPAM ✓' : 'canDial ✓ · DNC ✓',
    });
  });

  activity.forEach(a => {
    const iso = a.at || '';
    const kind = a.type === 'created' ? 'lead' : a.type === 'status' ? 'status' : a.type === 'consent' ? 'consent' : a.type === 'page' ? 'page' : 'note';
    const label = ({
      lead:    'Lead created',
      status:  'Stage change',
      consent: 'Consent captured',
      page:    'Page activity',
      note:    'Note',
    })[kind] || 'Activity';
    // Skip outreach/call activity entries — the source rows above
    // already cover them; showing both duplicates the timeline.
    if (a.type === 'outreach' || a.type === 'call') return;
    events.push({
      at:        iso,
      direction: '',
      kind,
      label,
      body:      a.body || '',
      outcome:   '',
      actor:     'system',
      marked:    'auto',
      gates:     '',
    });
  });

  // Newest first.
  return events
    .filter(e => e.at)
    .sort((a, b) => (b.at || '').localeCompare(a.at || ''));
}

function fmtWhen(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yest = new Date(now.getTime() - 86400000).toDateString() === d.toDateString();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return `Today, ${time}`;
  if (yest)    return `Yesterday, ${time}`;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time}`;
}

function fmtAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const mins = Math.floor(ms / 60000);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30)  return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function LeadHistoryBanner({ lead, agentName, callHistory = [], outreachLog = [] }) {
  const outbound = React.useMemo(() => {
    const combined = unifyLeadTimeline({ callHistory, outreachLog, activity: [], agentName });
    // "Last contacted" = most recent OUTBOUND touch (call / sms / email).
    // We exclude inbound replies because the setter question is "when
    // did WE reach out", not "when did they respond".
    return combined.find(e => e.direction === 'OUT');
  }, [callHistory, outreachLog, agentName]);

  const attempt = React.useMemo(() => {
    if (!outbound) return 0;
    return [...callHistory, ...outreachLog].filter(x => {
      const at = x.createdAt || (x.date ? `${x.date}T${x.startTime || x.time || '00:00'}:00Z` : '');
      return at && at <= (outbound.at || '');
    }).length;
  }, [callHistory, outreachLog, outbound]);

  const totalTouches  = callHistory.length + outreachLog.length;
  const followUp      = lead.followUpDate;
  const consentState  = lead.optOut
    ? '✗ Opted out'
    : lead.consentAt
      ? `✓ ${lead.consentSource || 'consent'} · ${new Date(lead.consentAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}`
      : '⚠ Not captured';

  return (
    <div className="space-y-2">
      {/* Gold banner — the setter's first-look answer. */}
      <div className="border-2 border-amber-400 bg-gradient-to-b from-amber-50 to-white rounded-lg p-3">
        <div className="text-[10px] font-black uppercase tracking-[0.16em] text-amber-700">Last contacted</div>
        {outbound ? (
          <>
            <div className="text-base font-black text-slate-900 mt-1">
              {fmtWhen(outbound.at)} <span className="font-normal text-slate-500 text-sm">({fmtAgo(outbound.at)})</span>
            </div>
            <div className="text-[13px] text-slate-800 mt-0.5">
              <span className="font-bold">{outbound.kind === 'sms' ? 'Text' : outbound.kind === 'email' ? 'Email' : 'Call'}</span>
              {outbound.body && <> · {outbound.body}</>}
              {agentName && <> · by <span className="font-bold">{agentName}</span></>}
              {attempt > 0 && <span className="text-slate-500"> · attempt {attempt}</span>}
            </div>
          </>
        ) : (
          <div className="text-sm font-bold text-rose-700 mt-1">
            Never contacted{lead.createdAt ? ` · lead created ${fmtAgo(lead.createdAt)}` : ''}
          </div>
        )}
      </div>

      {/* 3 context chips — Next · Total · Consent — right below. */}
      <div className="grid grid-cols-3 gap-2">
        <div className="border border-slate-200 bg-slate-50 rounded-md p-2 min-w-0">
          <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Next scheduled</div>
          <div className="text-xs font-bold text-slate-900 mt-0.5 truncate">
            {followUp ? new Date(followUp).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'None queued'}
          </div>
        </div>
        <div className="border border-slate-200 bg-slate-50 rounded-md p-2 min-w-0">
          <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Total touches</div>
          <div className="text-xs font-bold text-slate-900 mt-0.5 truncate">
            {totalTouches} · {callHistory.length} call{callHistory.length === 1 ? '' : 's'} + {outreachLog.length} outreach
          </div>
        </div>
        <div className="border border-slate-200 bg-slate-50 rounded-md p-2 min-w-0">
          <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Consent</div>
          <div className={`text-xs font-bold mt-0.5 truncate ${lead.optOut ? 'text-rose-700' : lead.consentAt ? 'text-emerald-700' : 'text-amber-700'}`}>
            {consentState}
          </div>
        </div>
      </div>
    </div>
  );
}

function LeadTimeline({ lead, agentName, callHistory = [], outreachLog = [] }) {
  const [filter, setFilter] = React.useState('all'); // all | call | sms | email | page
  const events = React.useMemo(
    () => unifyLeadTimeline({ callHistory, outreachLog, activity: lead.activity || [], agentName }),
    [callHistory, outreachLog, lead.activity, agentName]
  );

  const filtered = React.useMemo(() => {
    if (filter === 'all') return events;
    if (filter === 'call')  return events.filter(e => e.kind === 'call');
    if (filter === 'sms')   return events.filter(e => e.kind === 'sms');
    if (filter === 'email') return events.filter(e => e.kind === 'email');
    if (filter === 'page')  return events.filter(e => e.kind === 'page' || e.kind === 'consent' || e.kind === 'lead' || e.kind === 'status' || e.kind === 'note');
    return events;
  }, [events, filter]);

  const dotColor = (kind) => ({
    call:    '#C0463B',
    sms:     '#C9A227',
    email:   '#5B8DEF',
    page:    '#4BB25E',
    consent: '#4BB25E',
    status:  '#67675f',
    lead:    '#67675f',
    note:    '#67675f',
  })[kind] || '#67675f';

  if (events.length === 0) return null;

  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
        <div className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">All history · one timeline</div>
        <div className="text-[10px] font-mono text-slate-400">{filtered.length} of {events.length}</div>
      </div>
      <div className="flex gap-1 flex-wrap mb-3">
        {[
          { id: 'all',   label: 'All history' },
          { id: 'call',  label: 'Calls' },
          { id: 'sms',   label: 'Texts' },
          { id: 'email', label: 'Email' },
          { id: 'page',  label: 'Page activity' },
        ].map(f => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 border ${filter === f.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}
          >
            {f.label}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <div className="text-xs text-slate-400 italic py-3 text-center">No events in this filter.</div>
      ) : (
        <ol className="relative border-l border-slate-200 pl-4 space-y-3">
          {filtered.map((e, i) => (
            <li key={i} className="relative">
              <span
                className="absolute -left-[22px] top-1 w-3 h-3 rounded-sm border border-white"
                style={{ background: dotColor(e.kind) }}
              />
              <div className="text-[10px] font-mono text-slate-400">{fmtWhen(e.at)} · {fmtAgo(e.at)}</div>
              <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 mt-1">
                <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                  {e.label}
                  {e.direction && (
                    <span className={`text-[9px] font-black uppercase tracking-wider px-1 py-0.5 rounded ${e.direction === 'IN' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>
                      {e.direction}
                    </span>
                  )}
                </div>
                {e.body && <div className="text-[12px] text-slate-700 mt-1 leading-snug">{e.body}</div>}
                {e.gates && <div className="text-[10px] font-mono text-emerald-700 mt-1">{e.gates}</div>}
                <div className="text-[10px] text-slate-400 mt-0.5">
                  {e.actor} · {e.marked === 'human' ? 'human-marked' : 'auto-marked'}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function LeadPreBrief({ lead, callHistory, outreachLog }) {
  const cacheKey = `prebrief_${lead.id}`;
  const [busy, setBusy] = React.useState(false);
  const [brief, setBrief] = React.useState(null);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) setBrief(JSON.parse(cached));
      else setBrief(null);
    } catch {}
    setErr(null);
  }, [cacheKey]);

  async function run() {
    setBusy(true); setErr(null);
    try {
      const out = await generateLeadBrief({ lead, recentCalls: callHistory, recentOutreach: outreachLog });
      if (!out) { setErr('AI returned no brief.'); return; }
      setBrief(out);
      try { sessionStorage.setItem(cacheKey, JSON.stringify(out)); } catch {}
    } catch (e) {
      setErr(e.message || 'Brief failed.');
    } finally {
      setBusy(false);
    }
  }

  if (!brief) {
    return (
      <button
        onClick={run}
        disabled={busy}
        className="w-full bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg p-3 text-left disabled:opacity-50"
      >
        <div className="text-xs font-bold uppercase tracking-wider text-indigo-700">{busy ? 'Reading the file…' : 'Get AI pre-brief'}</div>
        <div className="text-xs text-indigo-700/70 mt-0.5">{err || 'A 3-line read before you dial — who they are, the angle to open with, the risk to watch.'}</div>
      </button>
    );
  }

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 space-y-2">
      <div className="flex items-baseline justify-between">
        <div className="text-xs font-bold uppercase tracking-wider text-indigo-700">AI pre-brief</div>
        <button onClick={run} disabled={busy} className="text-[10px] font-semibold text-indigo-600 hover:text-indigo-800 disabled:opacity-50">{busy ? '…' : 'Refresh'}</button>
      </div>
      {brief.brief && (
        <div className="text-sm text-slate-800"><span className="text-[10px] uppercase tracking-wider font-bold text-indigo-600 mr-1.5">Brief</span>{brief.brief}</div>
      )}
      {brief.angle && (
        <div className="text-sm text-slate-800"><span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 mr-1.5">Angle</span>{brief.angle}</div>
      )}
      {brief.risk && (
        <div className="text-sm text-slate-800"><span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 mr-1.5">Risk</span>{brief.risk}</div>
      )}
    </div>
  );
}

// AI tool tray for the lead drawer. Three on-demand actions that
// power the most common per-lead moments:
//   - Draft next message: AI picks SMS vs email + writes the
//     outbound based on lead status + history.
//   - Objection coach: agent types the pushback they heard, AI
//     returns 2-3 framed response options with the technique used.
//   - Compare options: agent enters 2+ policy options, AI writes
//     a plain-English sendable comparison.
// Each opens its own panel underneath so the drawer doesn't lose
// state. Output is always copyable (and SMS-ready when appropriate).
function LeadAiToolsRow({ lead, callHistory, outreachLog }) {
  const [open, setOpen] = React.useState(null); // 'draft' | 'objection' | 'compare' | null

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <AiToolButton active={open === 'draft'}     onClick={() => setOpen(open === 'draft' ? null : 'draft')}     label="Draft next message"  hint="AI picks channel + writes it"/>
        <AiToolButton active={open === 'objection'} onClick={() => setOpen(open === 'objection' ? null : 'objection')} label="Objection coach"    hint="Type the pushback, get options"/>
        <AiToolButton active={open === 'compare'}   onClick={() => setOpen(open === 'compare' ? null : 'compare')}   label="Compare options"     hint="Plain-English sendable summary"/>
      </div>
      {open === 'draft'     && <DraftNextMessagePanel lead={lead} callHistory={callHistory} outreachLog={outreachLog}/>}
      {open === 'objection' && <ObjectionCoachPanel lead={lead}/>}
      {open === 'compare'   && <PolicyComparisonPanel lead={lead}/>}
    </div>
  );
}

function AiToolButton({ active, onClick, label, hint }) {
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-lg border-2 px-2.5 py-2 transition-colors ${active ? 'bg-indigo-600 border-indigo-700 text-white' : 'bg-white border-indigo-200 hover:border-indigo-400'}`}
    >
      <div className={`text-xs font-bold ${active ? 'text-white' : 'text-indigo-700'}`}>{label}</div>
      <div className={`text-[10px] mt-0.5 ${active ? 'text-indigo-100' : 'text-slate-500'}`}>{hint}</div>
    </button>
  );
}

function DraftNextMessagePanel({ lead, callHistory, outreachLog }) {
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [copied, setCopied] = React.useState(false);

  async function run() {
    setBusy(true); setErr(null);
    try {
      const out = await generateFollowUpDraft({ lead, recentCalls: callHistory, recentOutreach: outreachLog });
      if (!out) { setErr('AI returned nothing.'); return; }
      setResult(out);
    } catch (e) {
      setErr(e.message || 'Failed.');
    } finally {
      setBusy(false);
    }
  }

  React.useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  function copy() {
    if (!result?.body) return;
    navigator.clipboard?.writeText(result.body);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 space-y-2">
      <div className="flex items-baseline justify-between">
        <div className="text-xs font-bold uppercase tracking-wider text-indigo-700">Next message draft</div>
        <button onClick={run} disabled={busy} className="text-[10px] font-semibold text-indigo-600 hover:text-indigo-800 disabled:opacity-50">{busy ? 'Writing…' : 'Regenerate'}</button>
      </div>
      {err && <div className="text-xs text-red-600">{err}</div>}
      {!result && busy && <div className="text-xs text-slate-500 italic">Reading the file…</div>}
      {result && (
        <>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider bg-indigo-200 text-indigo-800 px-1.5 py-0.5 rounded">{result.channel || 'sms'}</span>
            {result.subject && <span className="text-xs font-semibold text-slate-700 truncate">{result.subject}</span>}
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-2.5 text-sm text-slate-800 whitespace-pre-wrap">{result.body}</div>
          {result.reasoning && <div className="text-xs text-slate-500 italic">{result.reasoning}</div>}
          <div className="flex items-center gap-2">
            <button onClick={copy} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">{copied ? 'Copied ✓' : 'Copy'}</button>
            {lead.phone && result.channel === 'sms' && (
              <a href={`sms:${lead.phone}?body=${encodeURIComponent(result.body)}`} className="bg-white border border-slate-200 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-slate-50">Open in SMS</a>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ObjectionCoachPanel({ lead }) {
  const [objection, setObjection] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [err, setErr] = React.useState(null);

  async function run() {
    if (!objection.trim()) { setErr('Type the objection first.'); return; }
    setBusy(true); setErr(null);
    try {
      const out = await generateObjectionResponse({ objection, leadContext: lead });
      if (!out) { setErr('AI returned nothing.'); return; }
      setResult(out);
    } catch (e) {
      setErr(e.message || 'Failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 space-y-2">
      <div className="text-xs font-bold uppercase tracking-wider text-indigo-700">Objection coach</div>
      <textarea
        value={objection}
        onChange={e => setObjection(e.target.value)}
        rows={2}
        placeholder={`Type or paste what the lead said. e.g. "It's too expensive right now" or "I need to talk to my wife"`}
        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-indigo-500 focus:outline-none"
      />
      <button onClick={run} disabled={busy || !objection.trim()} className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">{busy ? 'Coaching…' : 'Get responses'}</button>
      {err && <div className="text-xs text-red-600">{err}</div>}
      {result && (
        <div className="space-y-2">
          {result.summary && (
            <div className="text-xs text-slate-700 bg-white border border-slate-200 rounded p-2">
              <span className="font-bold uppercase tracking-wider text-[10px] text-slate-500 mr-1.5">Real concern</span>
              {result.summary}
            </div>
          )}
          {(result.responses || []).map((r, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-lg p-2.5 space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">#{i + 1} · {r.technique}</span>
              </div>
              <div className="text-sm text-slate-800">{r.text}</div>
              {r.why && <div className="text-xs text-slate-500 italic">{r.why}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PolicyComparisonPanel({ lead }) {
  const [options, setOptions] = React.useState([
    { carrier: '', product: '', monthlyPremium: '', faceAmount: '', notes: '' },
    { carrier: '', product: '', monthlyPremium: '', faceAmount: '', notes: '' },
  ]);
  const [channel, setChannel] = React.useState('sms');
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [copied, setCopied] = React.useState(false);

  function upd(i, k, v) {
    setOptions(prev => prev.map((o, idx) => idx === i ? { ...o, [k]: v } : o));
  }
  function addOption() { if (options.length < 4) setOptions(prev => [...prev, { carrier: '', product: '', monthlyPremium: '', faceAmount: '', notes: '' }]); }
  function rmOption(i) { setOptions(prev => prev.filter((_, idx) => idx !== i)); }

  async function run() {
    const valid = options.filter(o => o.carrier && o.monthlyPremium);
    if (valid.length < 2) { setErr('Enter at least 2 options (carrier + premium).'); return; }
    setBusy(true); setErr(null);
    try {
      const out = await generatePolicyComparison({
        client: { name: lead.name, state: lead.state, budget: lead.estPremium, concern: lead.coverageType },
        options: valid,
        channel,
      });
      if (!out?.sendableText) { setErr('AI returned nothing.'); return; }
      setResult(out);
    } catch (e) {
      setErr(e.message || 'Failed.');
    } finally {
      setBusy(false);
    }
  }

  function copy() {
    if (!result?.sendableText) return;
    navigator.clipboard?.writeText(result.sendableText);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 space-y-2">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="text-xs font-bold uppercase tracking-wider text-indigo-700">Policy comparison</div>
        <div className="flex items-center gap-1 text-xs">
          <span className="text-slate-500">Format:</span>
          <button onClick={() => setChannel('sms')} className={`px-2 py-0.5 rounded ${channel === 'sms' ? 'bg-indigo-600 text-white' : 'bg-white border border-slate-200'}`}>SMS</button>
          <button onClick={() => setChannel('email')} className={`px-2 py-0.5 rounded ${channel === 'email' ? 'bg-indigo-600 text-white' : 'bg-white border border-slate-200'}`}>Email</button>
        </div>
      </div>
      <div className="space-y-2">
        {options.map((o, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-lg p-2 space-y-1">
            <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <span>Option {i + 1}</span>
              {options.length > 2 && <button onClick={() => rmOption(i)} className="text-red-600 hover:underline">remove</button>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
              <input value={o.carrier} onChange={e => upd(i, 'carrier', e.target.value)} placeholder="Carrier (MOO)" className="border border-slate-200 rounded px-2 py-1 text-xs"/>
              <input value={o.product} onChange={e => upd(i, 'product', e.target.value)} placeholder="Product (Term Express)" className="border border-slate-200 rounded px-2 py-1 text-xs"/>
              <input value={o.monthlyPremium} onChange={e => upd(i, 'monthlyPremium', e.target.value)} placeholder="$/mo (47)" className="border border-slate-200 rounded px-2 py-1 text-xs"/>
              <input value={o.faceAmount} onChange={e => upd(i, 'faceAmount', e.target.value)} placeholder="Face ($50k)" className="border border-slate-200 rounded px-2 py-1 text-xs"/>
              <input value={o.notes} onChange={e => upd(i, 'notes', e.target.value)} placeholder="Notes (no exam, 20-yr term…)" className="border border-slate-200 rounded px-2 py-1 text-xs col-span-2"/>
            </div>
          </div>
        ))}
        {options.length < 4 && <button onClick={addOption} className="text-xs text-indigo-700 hover:text-indigo-900 font-semibold">+ Add option</button>}
      </div>
      <button onClick={run} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">{busy ? 'Writing…' : 'Generate comparison'}</button>
      {err && <div className="text-xs text-red-600">{err}</div>}
      {result && (
        <div className="space-y-2 pt-2 border-t border-indigo-200">
          <div className="bg-white border border-slate-200 rounded-lg p-2.5 text-sm text-slate-800 whitespace-pre-wrap">{result.sendableText}</div>
          {Array.isArray(result.keyDifferences) && result.keyDifferences.length > 0 && (
            <ul className="text-xs text-slate-600 space-y-0.5 pl-4 list-disc">
              {result.keyDifferences.map((k, i) => <li key={i}>{k}</li>)}
            </ul>
          )}
          {result.recommendation && (
            <div className="text-xs text-slate-700 italic"><span className="font-bold text-slate-500 mr-1">Suggested fit:</span>{result.recommendation}</div>
          )}
          <div className="flex items-center gap-2">
            <button onClick={copy} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">{copied ? 'Copied ✓' : 'Copy'}</button>
            {lead.phone && channel === 'sms' && (
              <a href={`sms:${lead.phone}?body=${encodeURIComponent(result.sendableText)}`} className="bg-white border border-slate-200 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-slate-50">Open in SMS</a>
            )}
            {lead.email && channel === 'email' && (
              <a href={`mailto:${lead.email}?body=${encodeURIComponent(result.sendableText)}`} className="bg-white border border-slate-200 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-slate-50">Open in email</a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// SectorPicker — click-to-open sector selector for the lead card
// header pill. Options depend on the lead's vertical:
//   · health  → 'aca_private' | 'medicare' | 'ancillary'
//   · life    → single 'life' pill (no picker — vertical says it all)
//   · unknown → both health sectors visible
// Persists via onChange, which the parent wires to setLeadField.
function SectorPicker({ value, vertical, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    function onDoc(e) { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const isLife = vertical === 'life';
  const options = isLife
    ? [{ id: 'life', label: 'Life', tone: 'bg-slate-200 text-slate-800' }]
    : [
        { id: 'aca_private', label: 'ACA / Private', tone: 'bg-emerald-200 text-emerald-900' },
        { id: 'medicare',    label: 'Medicare',      tone: 'bg-blue-200 text-blue-900'       },
        { id: 'ancillary',   label: 'Ancillary',     tone: 'bg-amber-200 text-amber-900'     },
      ];
  const current = options.find(o => o.id === value) || options[0];

  return (
    <div className="relative inline-block" ref={rootRef}>
      <button
        type="button"
        onClick={() => !isLife && setOpen(o => !o)}
        disabled={isLife}
        title={isLife ? 'Life vertical — sector is fixed' : 'Click to change sector'}
        className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-[0.15em] px-2 py-0.5 rounded ${current.tone} ${!isLife ? 'hover:brightness-95 cursor-pointer' : 'cursor-default'}`}
      >
        {current.label}
        {!isLife && <span className="text-[8px] opacity-70">▾</span>}
      </button>
      {open && !isLife && (
        <div className="absolute left-0 top-full mt-1 z-10 min-w-[9rem] rounded-lg border border-slate-200 bg-white shadow-lg py-1">
          {options.map(o => (
            <button
              key={o.id}
              type="button"
              onClick={() => { setOpen(false); onChange(o.id); }}
              className={`block w-full text-left px-3 py-1.5 text-xs font-semibold hover:bg-slate-50 ${o.id === value ? 'text-slate-900' : 'text-slate-600'}`}
            >
              <span className={`inline-block w-2 h-2 rounded-full mr-2 align-middle ${o.tone.split(' ')[0]}`}></span>
              {o.label}
              {o.id === value && <span className="ml-1.5 text-emerald-600">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LeadDetailDrawer({ lead, agentName, attempts, outreachLog = [], callHistory = [], onClose, onEdit, onMove, onLogOutreach, onLogCall = () => {}, onTogglePolicy, onSetPremium, onSetCost, onAddTag = () => {}, onRemoveTag = () => {}, onSetFollowUp = () => {}, onSetLeadSource = () => {}, onSetSector = () => {}, onCaptureConsent = () => {}, onToggleOptOut = () => {}, onDelete, workflows = [], onSetWorkflow = () => {}, onOpenWorkflows = () => {}, onConvert = () => {}, onSetField = () => {} }) {
  const stage = LEAD_STAGE_BY_ID[lead.status] || LEAD_STAGES[0];
  const activity = (lead.activity || []).slice().sort((a, b) => (b.at || '').localeCompare(a.at || ''));
  const [outreachType, setOutreachType] = useState('call');
  const [outreachOutcome, setOutreachOutcome] = useState('');
  const [outreachNote, setOutreachNote] = useState('');
  const [premiumDraft, setPremiumDraft] = useState(lead.premium || '');
  const [costDraft, setCostDraft] = useState(lead.cost || '');
  const [tagDraft, setTagDraft] = useState('');
  const [followUpDraft, setFollowUpDraft] = useState(lead.followUpDate || '');
  React.useEffect(() => { setFollowUpDraft(lead.followUpDate || ''); }, [lead.id, lead.followUpDate]);
  const score = calcLeadScore(lead);
  const inStage = daysInStage(lead);
  const callCount = callHistory.length;
  const lastCall = callCount > 0 ? callHistory[0] : null;

  React.useEffect(() => {
    setPremiumDraft(lead.premium || '');
    setCostDraft(lead.cost || '');
  }, [lead.id, lead.premium, lead.cost]);

  const submitOutreach = () => {
    onLogOutreach({ type: outreachType, outcome: outreachOutcome, note: outreachNote });
    setOutreachOutcome('');
    setOutreachNote('');
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-stretch justify-end z-50" onClick={onClose}>
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
        <div className={`bg-${stage.color}-600 text-white p-5`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className={`text-xs font-semibold uppercase tracking-wide text-${stage.color}-100`}>{stage.label} · {inStage}d in stage</div>
              <h3 className="text-xl font-bold mt-1 flex items-center gap-2">{lead.name || '—'} <LeadScoreBadge score={score}/></h3>
              <div className={`text-sm text-${stage.color}-100 mt-1`}>{agentName} · {callCount} call{callCount === 1 ? '' : 's'} · {attempts} attempt{attempts === 1 ? '' : 's'}</div>
            </div>
            <button aria-label="Close" onClick={onClose} className="text-white/70 hover:text-white"><X size={20}/></button>
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button onClick={onLogCall} className="bg-white/20 hover:bg-white/30 text-white text-xs font-semibold rounded-lg px-3 py-1.5 flex items-center gap-1.5">
              <Phone size={14}/> Log call
            </button>
            {lead.phone && (
              <button onClick={() => requestPhoneCall({ lead })} className="bg-white/20 hover:bg-white/30 text-white text-xs font-semibold rounded-lg px-3 py-1.5">Dial {fmtPhoneUS(lead.phone)}</button>
            )}
            {lead.phone && (
              <a href={`sms:${phoneClean(lead.phone)}`} className="bg-white/20 hover:bg-white/30 text-white text-xs font-semibold rounded-lg px-3 py-1.5">SMS</a>
            )}
            {/* v2 — convert lead → client. Carries sector + Medicare
                fields into the new clients row. Idempotent: button
                changes to "Converted" after success. */}
            {lead.convertedClientId ? (
              <span className="bg-white/30 text-white text-xs font-bold uppercase tracking-tight rounded-lg px-3 py-1.5">✓ Converted to client</span>
            ) : (
              <button
                onClick={async () => {
                  if (await hqConfirm({
                    title: `Convert "${lead.name}" to a client?`,
                    body: 'Sector, demographics, and Medicare fields carry over to the client record.',
                    confirmLabel: 'Convert to client',
                  })) onConvert();
                }}
                className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold uppercase tracking-tight rounded-lg px-3 py-1.5"
              >
                ➜ Convert to Client
              </button>
            )}
          </div>
          {/* Sector pill — editable click-to-open picker so operators
              can correct auto-inferred sector without editing the
              full lead record. Persists via onSetSector prop. */}
          <div className="mt-2">
            <SectorPicker
              value={lead.sector || 'aca_private'}
              vertical={lead.vertical || 'health'}
              onChange={(next) => { if (next !== (lead.sector || 'aca_private')) onSetSector(next); }}
            />
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* AI Command Center — Phase 1 header. Score, expected
              commission, close probability, natural-language briefing,
              expandable strategy. Fetches /api/leads/command-center;
              hides itself on error. See src/leads/LeadCommandCenterHeader.jsx. */}
          <LeadCommandCenterHeader lead={lead} callHistory={callHistory} outreachLog={outreachLog}/>
          {/* First-look answer for the setter: when did we last touch
              them, how, by whom, and what channels are legal now.
              Every card + timeline below is composed from data the
              CRM already writes — no new tables. */}
          <LeadHistoryBanner lead={lead} agentName={agentName} callHistory={callHistory} outreachLog={outreachLog}/>
          <LeadTimeline       lead={lead} agentName={agentName} callHistory={callHistory} outreachLog={outreachLog}/>
          <LeadPreBrief lead={lead} callHistory={callHistory} outreachLog={outreachLog}/>
          <LeadAiToolsRow lead={lead} callHistory={callHistory} outreachLog={outreachLog}/>
          {/* Medicare Coverage Snapshot — the "10-second view"
              moonshot from the Medicare Intelligence spec. Renders
              only for medicare/senior leads. Above the Insurance
              Profile so it's the first Medicare-specific surface. */}
          <MedicareCoverageSnapshot lead={lead}/>
          {/* Insurance Profile — collapsible Health/Life/Medicare
              field panel. Persists via setLeadField(patch). */}
          <LeadInsuranceProfilePanel lead={lead} onSetField={onSetField}/>
          {/* AI Memory — small facts to keep every next conversation personal. */}
          <LeadMemoryPanel lead={lead} onSetField={onSetField}/>
          {/* AI Recommendations — deterministic cross-sell ranked by fit. */}
          <LeadRecommendationsPanel lead={lead}/>
          {/* Communication Center — channel breakdown + legal-to-send hint. */}
          <LeadCommunicationCenter lead={lead} callHistory={callHistory} outreachLog={outreachLog}/>
          {/* Call Intelligence — per-call analytics + AI coaching. */}
          <LeadCallIntelligence callHistory={callHistory}/>
          {/* Opportunity Tracker — dollar view: current opp / cross-sell / referral / LTV. */}
          <LeadOpportunityPanel lead={lead}/>
          {/* Auto Tasks — derived from lead state, no persistence. */}
          <LeadTasksPanel lead={lead} callHistory={callHistory} outreachLog={outreachLog}/>
          {/* Compliance — TCPA / A2P / recording / SOA / email / phone-format checklist. */}
          <LeadCompliancePanel lead={lead}/>
          {/* Digital Twin — synthesized personality + behavior profile. */}
          <LeadDigitalTwinPanel lead={lead} callHistory={callHistory} outreachLog={outreachLog}/>
          {/* Relationship Map — visible connections → cross-sell + referral surface. */}
          <LeadRelationshipMap lead={lead}/>
          {/* Carrier Match Engine — ranked plans from the operator's imported library. */}
          <LeadCarrierMatchPanel lead={lead}/>
          {/* Documents — client_documents surface (needs converted lead). */}
          <LeadDocumentsPanel lead={lead}/>
          {/* Live AI Assistant — the "ask anything about this lead" fallback panel. */}
          <LeadAssistantPanel lead={lead} callHistory={callHistory}/>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1.5 text-sm">
            {lead.phone && <div><span className="text-slate-500">Phone:</span> <button onClick={() => requestPhoneCall({ lead })} className="text-emerald-700 hover:underline ml-1 bg-transparent border-0 cursor-pointer tabular-nums">{fmtPhoneUS(lead.phone)}</button></div>}
            {lead.email && <div><span className="text-slate-500">Email:</span> <a href={`mailto:${lead.email}`} className="text-emerald-700 hover:underline ml-1">{lead.email}</a></div>}
            {lead.source && <div><span className="text-slate-500">Source:</span> <span className="ml-1">{lead.source}</span></div>}
            {lead.coverageType && <div><span className="text-slate-500">Coverage:</span> <span className="ml-1">{lead.coverageType}</span></div>}
            {lead.appointmentDate && <div className="text-amber-700"><span className="text-amber-600">Appointment:</span> <span className="ml-1 font-semibold">{fmtDateUS(lead.appointmentDate)}{lead.appointmentTime ? ` ${lead.appointmentTime}` : ''}</span></div>}
            {/* Speed-to-lead readout */}
            {(() => {
              const s = speedToLeadMinutes(lead);
              if (s != null) {
                const b = speedBucket(s);
                return <div><span className="text-slate-500">Speed to lead:</span> <span className={`ml-1 font-semibold text-${b.color}-700`}>{fmtSpeed(s)}</span> <span className="text-xs text-slate-400">(from receipt to first touch)</span></div>;
              }
              if (lead.createdAt) {
                const elapsed = Math.floor((Date.now() - new Date(lead.createdAt).getTime()) / (1000 * 60));
                return <div><span className="text-slate-500">Speed to lead:</span> <span className="ml-1 font-semibold text-rose-700">never contacted · {fmtSpeed(elapsed)} since receipt</span></div>;
              }
              return null;
            })()}
          </div>

          {/* TCPA / A2P consent + opt-out */}
          <div className={`border rounded-lg p-3 ${lead.optOut ? 'bg-red-50 border-red-200' : lead.consentAt ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-xs font-semibold uppercase tracking-wide">
                {lead.optOut
                  ? <span className="text-red-800">✗ Opted out — do not contact</span>
                  : lead.consentAt
                    ? <span className="text-emerald-800">✓ Consent on record</span>
                    : <span className="text-amber-800">⚠ No consent on record</span>}
              </div>
              <div className="text-[10px] text-slate-500">
                {lead.consentAt && !lead.optOut && `Captured ${new Date(lead.consentAt).toLocaleDateString()}${lead.consentSource ? ` via ${lead.consentSource}` : ''}`}
                {lead.optOut && lead.optOutAt && `Opted out ${new Date(lead.optOutAt).toLocaleDateString()}`}
              </div>
            </div>
            {lead.consentText && !lead.optOut && (
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer text-slate-600">Disclosure text</summary>
                <div className="mt-1 p-2 bg-white border border-slate-200 rounded text-slate-700 whitespace-pre-wrap font-mono text-[11px]">{lead.consentText}</div>
              </details>
            )}
            {lead.optOut && lead.optOutReason && (
              <div className="mt-2 text-xs text-red-800"><strong>Reason:</strong> {lead.optOutReason}</div>
            )}
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {!lead.consentAt && !lead.optOut && (
                <>
                  <button onClick={() => onCaptureConsent('verbal')}  className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 rounded">Capture (verbal)</button>
                  <button onClick={() => onCaptureConsent('written')} className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 rounded">Capture (written)</button>
                </>
              )}
              {!lead.optOut && (
                <button onClick={() => {
                  const reason = window.prompt('Reason for opt-out (e.g. "STOP reply", "verbal request", "DNC list match")?');
                  if (reason !== null) onToggleOptOut(reason || '');
                }} className="text-xs bg-red-600 hover:bg-red-700 text-white px-2.5 py-1 rounded ml-auto">Mark opted out</button>
              )}
              {lead.optOut && (
                <button onClick={() => onToggleOptOut('')} className="text-xs bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 px-2.5 py-1 rounded ml-auto">Clear opt-out</button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-slate-500">Lead cost ($)</span>
              <div className="flex gap-1">
                <input type="number" min="0" step="0.01" value={costDraft} onChange={e => setCostDraft(e.target.value)} className="flex-1 mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
                <button onClick={() => onSetCost(costDraft)} className="mt-1 bg-slate-700 hover:bg-slate-800 text-white px-3 rounded-lg text-xs">Save</button>
              </div>
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">Premium on close ($/mo)</span>
              <div className="flex gap-1">
                <input type="number" min="0" value={premiumDraft} onChange={e => setPremiumDraft(e.target.value)} className="flex-1 mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
                <button onClick={() => onSetPremium(premiumDraft)} className="mt-1 bg-slate-700 hover:bg-slate-800 text-white px-3 rounded-lg text-xs">Save</button>
              </div>
            </label>
          </div>

          {lead.status === 'closed' && (
            <div className={`${lead.lapsed ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'} border rounded-lg p-3 flex items-center justify-between`}>
              <div>
                <div className={`text-sm font-semibold ${lead.lapsed ? 'text-red-900' : 'text-emerald-900'}`}>{lead.lapsed ? 'Policy lapsed' : 'Policy active'}</div>
                <div className="text-xs text-slate-600 mt-0.5">{lead.lapsed ? 'Counts against persistency. Excluded from on-book premium.' : 'Counts toward on-book premium and persistency.'}</div>
              </div>
              <button onClick={onTogglePolicy} className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${lead.lapsed ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}`}>
                {lead.lapsed ? 'Mark active' : 'Mark lapsed'}
              </button>
            </div>
          )}

          {/* Follow-up + lead source quick-edit */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-slate-500">Follow-up date/time</span>
              <div className="flex gap-1">
                <input type="datetime-local" value={followUpDraft} onChange={e => setFollowUpDraft(e.target.value)} className="flex-1 mt-1 border border-slate-200 rounded-lg px-2 py-2 text-sm"/>
                <button onClick={() => onSetFollowUp(followUpDraft)} className="mt-1 bg-slate-700 hover:bg-slate-800 text-white px-3 rounded-lg text-xs">Save</button>
              </div>
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">Lead source</span>
              <select value={lead.leadSource || ''} onChange={e => onSetLeadSource(e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">— Not set —</option>
                {LEAD_SOURCES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </label>
          </div>

          {/* Lead profile — surfaces the high-value fields from the CSV
              import that predict deal size and approvability. Only
              shows when the lead actually has any of these fields
              populated (manually-entered leads still render fine). */}
          {(() => {
            const isMP  = (lead.leadType || 'general') === 'mortgage_protection';
            const isIUL = (lead.leadType || 'general') === 'iul';
            const moneyMP  = lead.mortgageBalance || lead.mortgagePayment;
            const moneyIUL = lead.monthlyContribution;
            const hasAnyField =
              lead.dob || lead.age || lead.gender || lead.state ||
              moneyMP || moneyIUL ||
              lead.beneficiary || lead.beneficiaryName || lead.healthFlag ||
              lead.iulGoal || lead.employmentStatus || lead.retirementPlan || lead.retirementAge ||
              lead.ad || lead.platform || lead.trustedFormUrl;
            if (!hasAnyField) return null;
            const accent = isMP ? 'emerald' : isIUL ? 'violet' : 'slate';
            const typeLabel = isMP ? 'Mortgage Protection' : isIUL ? 'IUL' : 'Lead profile';
            // Health-history yes = underwriting red flag for both MP and IUL.
            const healthIsRed = (lead.healthFlag || '').toLowerCase() === 'yes';
            return (
              <div className={`bg-${accent}-50 border border-${accent}-200 rounded-lg p-3`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className={`text-xs font-bold uppercase tracking-wider text-${accent}-700`}>{typeLabel}</span>
                  {healthIsRed && <span className="text-[10px] font-bold uppercase text-red-700 bg-red-100 rounded-full px-2 py-0.5">⚠ Major medical: yes</span>}
                </div>
                {/* Profit-driver row: money fields go first, big and bold */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
                  {isMP && (
                    <>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Mortgage balance</div>
                        <div className="font-bold text-slate-900">{lead.mortgageBalance || '—'}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Mortgage payment</div>
                        <div className="font-bold text-slate-900">{lead.mortgagePayment || '—'}</div>
                      </div>
                    </>
                  )}
                  {isIUL && (
                    <>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Desired monthly contribution</div>
                        <div className="font-bold text-slate-900">{lead.monthlyContribution || '—'}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Retirement age</div>
                        <div className="font-bold text-slate-900">{lead.retirementAge || '—'}</div>
                      </div>
                    </>
                  )}
                </div>
                {/* Demographics + supporting context */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  {lead.age && <div><span className="text-slate-500">Age:</span> <span className="font-semibold text-slate-900">{lead.age}</span></div>}
                  {lead.dob && !lead.age && <div><span className="text-slate-500">DOB:</span> <span className="font-semibold text-slate-900">{lead.dob}</span></div>}
                  {lead.gender && <div><span className="text-slate-500">Gender:</span> <span className="font-semibold text-slate-900">{lead.gender}</span></div>}
                  {lead.state && <div><span className="text-slate-500">State:</span> <span className="font-semibold text-slate-900">{lead.state}</span></div>}
                  {lead.healthFlag && !healthIsRed && <div><span className="text-slate-500">Health:</span> <span className="font-semibold text-emerald-700">No major medical</span></div>}
                  {isMP && lead.beneficiary && <div><span className="text-slate-500">Beneficiary:</span> <span className="font-semibold text-slate-900">{lead.beneficiary}</span></div>}
                  {isMP && lead.beneficiaryName && <div className="col-span-2 md:col-span-2"><span className="text-slate-500">Name:</span> <span className="font-semibold text-slate-900">{lead.beneficiaryName}</span></div>}
                  {isIUL && lead.iulGoal && <div className="col-span-2"><span className="text-slate-500">Goal:</span> <span className="font-semibold text-slate-900">{lead.iulGoal}</span></div>}
                  {isIUL && lead.employmentStatus && <div className="col-span-2"><span className="text-slate-500">Employment:</span> <span className="font-semibold text-slate-900">{lead.employmentStatus}</span></div>}
                  {isIUL && lead.retirementPlan && <div className="col-span-2"><span className="text-slate-500">Current plan:</span> <span className="font-semibold text-slate-900">{lead.retirementPlan}</span></div>}
                  {lead.ad && <div className="col-span-2"><span className="text-slate-500">Ad:</span> <span className="font-semibold text-slate-900">{lead.ad}</span></div>}
                  {lead.platform && <div><span className="text-slate-500">Platform:</span> <span className="font-semibold text-slate-900">{lead.platform}</span></div>}
                </div>
                {lead.trustedFormUrl && (
                  <div className="mt-2 pt-2 border-t border-slate-200 text-[11px] text-slate-500">
                    TCPA evidence:{' '}
                    <a href={lead.trustedFormUrl} target="_blank" rel="noreferrer" className="text-slate-700 underline hover:text-slate-900 break-all">TrustedForm cert</a>
                    {lead.otpCode && <> · OTP {lead.otpCode}</>}
                    {lead.ipAddress && <> · {lead.ipAddress}</>}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Workflow assignment — pick a saved AI workflow to handle this
              lead. Stores the workflow id on the lead so future SMS
              automation knows which script to run. */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">AI workflow</span>
                <div className="text-[11px] text-slate-600 mt-0.5">Assign this lead to one of your saved workflows. The AI test chat (and future SMS automation) will use it.</div>
              </div>
              <button onClick={onOpenWorkflows} className="text-xs text-emerald-700 hover:text-emerald-900 underline whitespace-nowrap">Manage workflows →</button>
            </div>
            {workflows.length === 0 ? (
              <div className="text-xs text-slate-600 italic">No workflows yet — head to the Workflows tab to create one.</div>
            ) : (
              <select
                value={lead.workflowId || ''}
                onChange={e => onSetWorkflow(e.target.value)}
                className="w-full border border-emerald-300 focus:border-emerald-500 focus:outline-none rounded-lg px-3 py-2 text-sm bg-white font-semibold text-emerald-900"
              >
                <option value="">— No workflow —</option>
                {workflows.map(w => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            )}
            {lead.workflowId && (() => {
              const wf = workflows.find(w => w.id === lead.workflowId);
              if (!wf) {
                return (
                  <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                    Workflow no longer exists — it may have been deleted. Pick a new one above or clear the assignment.
                  </div>
                );
              }
              return (
                <div className="mt-2 text-xs text-slate-700">
                  <span className="font-semibold">{wf.assistantName || 'Assistant'}</span> on behalf of <span className="font-semibold">{wf.agencyName || 'your agency'}</span>
                  {wf.faqs?.length > 0 && <> · {wf.faqs.length} FAQ{wf.faqs.length === 1 ? '' : 's'}</>}
                  {wf.hesitations?.length > 0 && <> · {wf.hesitations.length} hesitation response{wf.hesitations.length === 1 ? '' : 's'}</>}
                </div>
              );
            })()}
          </div>

          {/* Tags editor */}
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Tags</div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {(lead.tags || []).length === 0 && <span className="text-sm text-slate-400 italic">No tags yet.</span>}
              {(lead.tags || []).map(t => (
                <span key={t} className="inline-flex items-center gap-1 bg-violet-100 text-violet-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                  {t}
                  <button onClick={() => onRemoveTag(t)} className="text-violet-500 hover:text-violet-800" aria-label={`Remove ${t}`}>×</button>
                </span>
              ))}
              <input
                value={tagDraft}
                onChange={e => setTagDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && tagDraft.trim()) { onAddTag(tagDraft.trim()); setTagDraft(''); } }}
                placeholder="+ tag"
                className="outline-none border border-slate-200 rounded-full px-2 py-0.5 text-xs min-w-[80px]"
              />
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {SUGGESTED_TAGS.filter(t => !(lead.tags || []).includes(t)).map(t => (
                <button key={t} onClick={() => onAddTag(t)} className="bg-slate-100 hover:bg-violet-100 hover:text-violet-700 text-slate-600 text-[11px] font-medium px-2 py-0.5 rounded-full">+ {t}</button>
              ))}
            </div>
          </div>

          {lead.notes && (
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Notes</div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm whitespace-pre-wrap">{lead.notes}</div>
            </div>
          )}
          {/* ARIA Note Intelligence — structured extraction from the
              lead's freeform notes. Fires parse-note on mount when
              notes exist. Renders NOTHING when there are no notes. */}
          {lead.notes && (
            <React.Suspense fallback={null}>
              <NoteIntelligencePanel
                lead={lead}
                onExtractionUpdate={(ex) => onSetField && onSetField({ ai_extraction: ex })}
              />
            </React.Suspense>
          )}

          {/* Call history */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Call history ({callCount})</div>
              <button onClick={onLogCall} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg px-2.5 py-1 flex items-center gap-1"><Phone size={12}/> Log call</button>
            </div>
            {callCount === 0 ? (
              <div className="text-sm text-slate-400 italic">No calls logged yet. Use "Log call" to record one — it'll show up here and on the Calls tab.</div>
            ) : (
              <div className="space-y-1.5">
                {callHistory.slice(0, 10).map(c => {
                  const outcome = CALL_OUTCOME_BY_ID[c.outcome];
                  const min = Math.floor((Number(c.duration) || 0) / 60);
                  const sec = (Number(c.duration) || 0) % 60;
                  return (
                    <div key={c.id} className="border border-slate-200 rounded-lg p-2 text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500 font-mono">{fmtDateUS(c.date)}{c.startTime ? ` ${c.startTime}` : ''}</span>
                          {outcome && <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold bg-${outcome.color}-100 text-${outcome.color}-700`}>{outcome.label}</span>}
                          <span className="text-[10px] uppercase text-slate-400">{c.direction || ''}</span>
                        </div>
                        <span className="text-xs text-slate-500 tabular-nums">{min > 0 ? `${min}m ` : ''}{sec}s{c.aiScore != null && c.aiScore !== '' ? ` · AI ${c.aiScore}` : ''}</span>
                      </div>
                      {c.notes && <div className="text-xs text-slate-600 mt-1 truncate" title={c.notes}>{c.notes}</div>}
                    </div>
                  );
                })}
                {callCount > 10 && <div className="text-xs text-slate-400 italic">…and {callCount - 10} more (see Calls tab)</div>}
              </div>
            )}
            {lastCall && (
              <div className="text-[11px] text-slate-500 mt-2">Last call: {lastCall.date}{lastCall.startTime ? ` ${lastCall.startTime}` : ''}</div>
            )}
          </div>

          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Move to stage</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {LEAD_STAGES.map(s => (
                <button
                  key={s.id}
                  onClick={() => onMove(s.id)}
                  disabled={s.id === lead.status}
                  className={`px-2 py-1.5 text-xs font-semibold rounded-lg border ${s.id === lead.status ? `bg-${s.color}-600 text-white border-${s.color}-600 cursor-default` : `bg-white text-${s.color}-700 border-${s.color}-200 hover:bg-${s.color}-50`}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Log interaction</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <select value={outreachType} onChange={e => setOutreachType(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white">
                {OUTREACH_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              <select value={outreachOutcome} onChange={e => setOutreachOutcome(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white">
                <option value="">— Outcome (optional) —</option>
                {INTERACTION_OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
              <input value={outreachNote} onChange={e => setOutreachNote(e.target.value)} placeholder="Note (optional)" className="col-span-2 border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
            </div>
            <div className="text-right mt-2">
              <button onClick={submitOutreach} className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">+ Log interaction</button>
            </div>
          </div>

          {outreachLog.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Outreach log ({outreachLog.length})</div>
              <div className="space-y-1.5 text-sm">
                {outreachLog.slice(0, 10).map(o => (
                  <div key={o.id} className="border border-slate-200 rounded p-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{OUTREACH_TYPES.find(t => t.id === o.type)?.label || o.type}</span>
                      <span className="text-xs text-slate-500">{(o.createdAt || '').slice(0, 16).replace('T', ' ')}</span>
                    </div>
                    {o.outcome && <div className="text-xs text-slate-600 mt-0.5">{o.outcome}</div>}
                    {o.note && <div className="text-xs text-slate-500 mt-0.5">{o.note}</div>}
                  </div>
                ))}
                {outreachLog.length > 10 && <div className="text-xs text-slate-400 italic">…and {outreachLog.length - 10} more</div>}
              </div>
            </div>
          )}

          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Activity timeline</div>
            {activity.length === 0 ? (
              <div className="text-sm text-slate-400 italic">No activity yet.</div>
            ) : (
              <ol className="relative border-l-2 border-slate-200 pl-4 space-y-3">
                {activity.map((a, i) => (
                  <li key={i} className="relative">
                    <div className="absolute -left-[1.4rem] top-1 w-3 h-3 rounded-full bg-slate-300 border-2 border-white"/>
                    <div className="text-xs text-slate-500">{a.at ? fmtLocalDateTime(a.at) : ''} <span className="ml-1 text-[10px] uppercase bg-slate-100 text-slate-600 px-1 rounded">{a.type}</span></div>
                    <div className="text-sm text-slate-800">{a.body}</div>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-slate-100">
            <button onClick={onDelete} className="text-sm text-red-600 hover:text-red-800">Delete lead</button>
            <button onClick={onEdit} className="bg-slate-900 hover:bg-slate-700 text-white px-4 py-2 rounded-lg text-sm font-semibold">Edit details</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Persistency — cohort tracker that answers "did each policy survive
// 30 days / 6 months / 1 year from issue?" The 80% target is the line
// in the sand; everything below it is flagged for coaching.
//
// Policy detection model:
//   - One policy = unique (prodNum, mga, policy#) seen in transactions
//   - issueDate is the first new-issue txn's issueDate (parsed)
//   - A policy "lapsed" if it ever had a CHARGEBACK_TYPES activity or
//     any negative netToAccount entry. firstLapseDate = earliest such date.
//   - daysToLapse = firstLapseDate − issueDate (in days)
//
// Persistency at window N (eg. 30):
//   - Eligible = policies whose issueDate is >= N days ago
//   - Survived = eligible AND (!lapsed OR daysToLapse > N)
//   - Persistency % = survived / eligible × 100
// ──────────────────────────────────────────────────────────────────────
const PERSISTENCY_WINDOWS = [
  { id: '30',  label: '30-day',  days: 30   },
  { id: '90',  label: '3-month', days: 90   },
  { id: '180', label: '6-month', days: 180  },
  { id: '365', label: '1-year',  days: 365  },
];
const PERSISTENCY_TARGET = 80;

function persistencyColor(pct) {
  if (pct == null) return 'slate';
  if (pct >= 85) return 'emerald';
  if (pct >= PERSISTENCY_TARGET) return 'emerald';
  if (pct >= 70) return 'amber';
  return 'red';
}

// ────────────────────────────────────────────────────────────────────
// Hard-to-Close Report
//
// Question this answers: do clients that took many touches to close
// stick around as long as easy closes, or do they lapse faster?
//
// Method:
//   1. Pick every lead with status='closed'.
//   2. Count attempts = outreach rows + calls logged for that lead.
//   3. Bucket by attempts: Easy (1-3), Moderate (4-9), Hard (10+).
//      Zero-attempt closes go into an "Unknown" bucket — those are
//      almost always operator-error (forgot to log) and would otherwise
//      inflate the "Easy" column.
//   4. Match each closed lead to a policy by normalized insured name
//      (handles "Last, First" vs "First Last"). Unmatched closes get
//      counted in a footnote, not in the rate denominators — measuring
//      retention on a lead we can't find on a commission statement
//      would be guessing.
//   5. Per bucket: count, avg months on book (issue → lapse or today),
//      active rate (1 − lapse share), chargeback rate (= lapse share).
//
// Name match caveat: this is a best-effort fuzzy join. Two unrelated
// people with the same name in the same book will collide; a closed
// lead whose policy was written under their spouse's name will miss.
// Both are rare in practice and the footnote surfaces unmatched count.
// ────────────────────────────────────────────────────────────────────

function normalizeInsuredName(s) {
  const cleaned = String(s || '')
    .toUpperCase()
    .replace(/[^A-Z\s,]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return '';
  // Handle "Last, First" by re-sorting tokens so order doesn't matter.
  const tokens = cleaned.split(/[\s,]+/).filter(Boolean).sort();
  return tokens.join(' ');
}

function HardToCloseReport({ leads = [], calls = [], outreach = [], policies = [], openClientProfile = () => {} }) {
  const analysis = React.useMemo(() => {
    // Per-lead attempt count from BOTH channels.
    const attemptsByLead = {};
    calls.forEach(c => { if (c.leadId) attemptsByLead[c.leadId] = (attemptsByLead[c.leadId] || 0) + 1; });
    outreach.forEach(o => { if (o.leadId) attemptsByLead[o.leadId] = (attemptsByLead[o.leadId] || 0) + 1; });

    // Index policies by normalized insured name for the join.
    const policyByName = new Map();
    policies.forEach(p => {
      const k = normalizeInsuredName(p.insured);
      if (k) {
        // First write wins — a clobber happens on duplicate names,
        // which is fine because both will be filtered into the same
        // bucket and rates are the same per-policy.
        if (!policyByName.has(k)) policyByName.set(k, p);
      }
    });

    const closed = leads.filter(l => l.status === 'closed');
    const matched = [];
    let unmatched = 0;
    const now = Date.now();
    const DAY = 1000 * 60 * 60 * 24;

    closed.forEach(l => {
      const attempts = attemptsByLead[l.id] || 0;
      const p = policyByName.get(normalizeInsuredName(l.name));
      if (!p) { unmatched++; return; }
      const issuedAt = p.issued?.getTime?.() || null;
      const endAt = p.lapsed && p.lapsedDate ? p.lapsedDate.getTime() : now;
      const monthsOnBook = issuedAt ? (endAt - issuedAt) / (DAY * 30.4375) : null;
      matched.push({ leadId: l.id, leadName: l.name, policy: p, attempts, monthsOnBook, lapsed: !!p.lapsed });
    });

    return { matched, unmatched, totalClosed: closed.length };
  }, [leads, calls, outreach, policies]);

  // Bucket by attempts. Zero is its own bucket so missing logs don't
  // inflate "Easy" — operators who never log calls would otherwise
  // see 100% of closes as easy, which is meaningless.
  const buckets = React.useMemo(() => {
    const out = { unknown: [], easy: [], moderate: [], hard: [] };
    analysis.matched.forEach(m => {
      if (m.attempts === 0)        out.unknown.push(m);
      else if (m.attempts <= 3)    out.easy.push(m);
      else if (m.attempts <= 9)    out.moderate.push(m);
      else                          out.hard.push(m);
    });
    return out;
  }, [analysis]);

  const stat = (rows) => {
    if (rows.length === 0) return { count: 0, avgMonths: null, activeRate: null, chargebackRate: null };
    const sumMonths = rows.reduce((s, r) => s + (r.monthsOnBook || 0), 0);
    const active = rows.filter(r => !r.lapsed).length;
    return {
      count: rows.length,
      avgMonths: sumMonths / rows.length,
      activeRate: (active / rows.length) * 100,
      chargebackRate: ((rows.length - active) / rows.length) * 100,
    };
  };
  const easy     = stat(buckets.easy);
  const moderate = stat(buckets.moderate);
  const hard     = stat(buckets.hard);

  // Headline insight — only fire when we have something meaningful to
  // say. Need both buckets non-empty AND a gap big enough to be a
  // real signal (>5 percentage points) and not just small-sample noise.
  let insight = null;
  if (easy.count >= 5 && hard.count >= 5 && easy.activeRate != null && hard.activeRate != null) {
    const gap = easy.activeRate - hard.activeRate;
    if (gap > 5) {
      insight = {
        tone: 'red',
        text: `Hard-to-close clients lapse ${gap.toFixed(0)} points more than easy closes (${hard.activeRate.toFixed(0)}% active vs ${easy.activeRate.toFixed(0)}%). Worth asking whether they're being talked into a policy they don't really want.`,
      };
    } else if (gap < -5) {
      insight = {
        tone: 'emerald',
        text: `Hard-to-close clients are actually STICKIER than easy closes (${hard.activeRate.toFixed(0)}% active vs ${easy.activeRate.toFixed(0)}%). The work pays off — they're more committed once you get them.`,
      };
    } else {
      insight = {
        tone: 'slate',
        text: `Persistency is roughly the same across difficulty buckets (gap ${Math.abs(gap).toFixed(0)} pts). Effort doesn't predict retention in your book.`,
      };
    }
  }

  if (analysis.totalClosed === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="font-semibold text-slate-900">Hard-to-close report</h3>
        <p className="text-sm text-slate-500 mt-1">No closed leads yet. Once a lead is moved to <code className="bg-slate-100 px-1 py-0.5 rounded text-xs">closed</code> status, it'll appear here.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
        <div>
          <h3 className="font-semibold text-slate-900">Hard-to-close report</h3>
          <p className="text-sm text-slate-500 mt-0.5">Do hard-to-close clients lapse faster? Buckets are <strong>outreach + call attempts</strong> before close. Persistency is inferred from commission-statement chargebacks.</p>
        </div>
        <div className="text-xs text-slate-500">
          {analysis.matched.length} closed lead{analysis.matched.length === 1 ? '' : 's'} matched to a policy
          {analysis.unmatched > 0 ? <> · <span className="text-amber-700">{analysis.unmatched} unmatched</span></> : null}
        </div>
      </div>

      {insight && (
        <div className={`mt-3 border rounded-lg px-3 py-2 text-sm bg-${insight.tone}-50 border-${insight.tone}-200 text-${insight.tone}-900`}>
          {insight.text}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
        <HardToCloseColumn name="Easy"     criteria="1–3 attempts"  tone="emerald" stats={easy}     onPeek={() => peekRows(buckets.easy, openClientProfile)}/>
        <HardToCloseColumn name="Moderate" criteria="4–9 attempts"  tone="amber"   stats={moderate} onPeek={() => peekRows(buckets.moderate, openClientProfile)}/>
        <HardToCloseColumn name="Hard"     criteria="10+ attempts"  tone="red"     stats={hard}     onPeek={() => peekRows(buckets.hard, openClientProfile)}/>
      </div>

      <div className="mt-3 text-xs text-slate-500 space-y-0.5">
        {buckets.unknown.length > 0 && (
          <div>
            <strong>{buckets.unknown.length}</strong> closed lead{buckets.unknown.length === 1 ? '' : 's'} had zero logged attempts — excluded so missing logs don't inflate the "Easy" bucket.
          </div>
        )}
        {analysis.unmatched > 0 && (
          <div>
            <strong>{analysis.unmatched}</strong> closed lead{analysis.unmatched === 1 ? '' : 's'} couldn't be matched to a policy on a commission statement (name didn't match). They're not in any bucket.
          </div>
        )}
      </div>
    </div>
  );
}

function HardToCloseColumn({ name, criteria, tone, stats }) {
  const valOrDash = (v, fmt) => v == null ? '—' : fmt(v);
  return (
    <div className={`border-2 border-${tone}-200 rounded-xl p-4 bg-${tone}-50/40`}>
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className={`text-xs font-bold uppercase tracking-wider text-${tone}-700`}>{name}</div>
          <div className="text-xs text-slate-500 mt-0.5">{criteria}</div>
        </div>
        <div className={`text-3xl font-bold text-${tone}-700 tabular-nums`}>{stats.count}</div>
      </div>
      <dl className="space-y-2 text-sm">
        <div className="flex items-baseline justify-between border-t border-slate-200 pt-2">
          <dt className="text-slate-600">Avg months on book</dt>
          <dd className="font-semibold text-slate-900 tabular-nums">{valOrDash(stats.avgMonths, v => `${v.toFixed(1)} mo`)}</dd>
        </div>
        <div className="flex items-baseline justify-between">
          <dt className="text-slate-600">Active rate (persistency)</dt>
          <dd className={`font-semibold tabular-nums ${stats.activeRate == null ? 'text-slate-400' : stats.activeRate >= 80 ? 'text-emerald-700' : stats.activeRate >= 65 ? 'text-amber-700' : 'text-red-700'}`}>
            {valOrDash(stats.activeRate, v => `${v.toFixed(0)}%`)}
          </dd>
        </div>
        <div className="flex items-baseline justify-between">
          <dt className="text-slate-600">Chargeback rate</dt>
          <dd className={`font-semibold tabular-nums ${stats.chargebackRate == null ? 'text-slate-400' : stats.chargebackRate <= 20 ? 'text-emerald-700' : stats.chargebackRate <= 35 ? 'text-amber-700' : 'text-red-700'}`}>
            {valOrDash(stats.chargebackRate, v => `${v.toFixed(0)}%`)}
          </dd>
        </div>
      </dl>
    </div>
  );
}

// Future hook for a "view leads in this bucket" drill-down — kept as
// a tiny shim now so column UI can reference it without crashing.
function peekRows() { /* drill-down view, Phase 2 */ }

function PersistencyView({ transactions = [], producers = {}, settings = {}, leads = [], calls = [], outreach = [], openClientProfile = () => {} }) {
  const [windowDays, setWindowDays] = React.useState(30);
  const [agentFilter, setAgentFilter] = React.useState('all');     // 'all' | prodNum
  const [showBelowOnly, setShowBelowOnly] = React.useState(false); // table-only filter
  const [agentSort, setAgentSort] = React.useState({ key: 'totalIssued', dir: 'desc' });
  // Early-lapse filter — toggles the Recent Lapses table to show only
  // policies that lapsed within 30 days of issue. Triggered from the
  // orange Early-Lapse Alert at the top of the page.
  const [earlyLapseFilter, setEarlyLapseFilter] = React.useState(false);
  const recentLapsesRef = React.useRef(null);
  const today = React.useMemo(() => new Date(), []);
  const dayMs = 1000 * 60 * 60 * 24;

  // ── Build policy-level data from transactions ───────────────────
  const policies = React.useMemo(() => {
    const map = {};
    transactions.forEach(tx => {
      if (!tx.policy) return;
      // Skip "AO-NA"-style Americo advance rows where the same policy line
      // appears as both new issue and advance — issueDate still tracks correctly.
      const key = `${tx.prodNum || ''}|${tx.mga || ''}|${tx.policy}`;
      if (!map[key]) {
        map[key] = {
          key,
          policy: tx.policy,
          insured: tx.insured || '',
          prodNum: tx.prodNum || '',
          carrier: deriveCarrier(tx),
          state: tx.state || '',
          issueDate: tx.issueDate || '',
          firstLapseDate: '',
          lapsed: false,
          totalCommission: 0,
          premium: 0,
        };
      }
      const p = map[key];
      if (!p.insured && tx.insured) p.insured = tx.insured;
      if (!p.issueDate && tx.issueDate) p.issueDate = tx.issueDate;
      if (tx.commPremium && Math.abs(tx.commPremium) > p.premium) p.premium = Math.abs(tx.commPremium);
      p.totalCommission += Number(tx.netToAccount) || 0;
      const isLapseEvent = CHARGEBACK_TYPES.has(tx.activityType) || (Number(tx.netToAccount) || 0) < 0;
      if (isLapseEvent && tx.activityDate) {
        p.lapsed = true;
        if (!p.firstLapseDate || (parseDate(tx.activityDate)?.getTime() || 0) < (parseDate(p.firstLapseDate)?.getTime() || 0)) {
          p.firstLapseDate = tx.activityDate;
        }
      }
    });
    // Derive issue-cohort + daysToLapse per policy
    return Object.values(map).map(p => {
      const issued = parseDate(p.issueDate);
      const lapsedDate = parseDate(p.firstLapseDate);
      const policyAgeDays = issued ? Math.floor((today.getTime() - issued.getTime()) / dayMs) : null;
      const daysToLapse = (p.lapsed && issued && lapsedDate) ? Math.floor((lapsedDate.getTime() - issued.getTime()) / dayMs) : null;
      return { ...p, issued, lapsedDate, policyAgeDays, daysToLapse };
    }).filter(p => p.issueDate); // exclude policies without issue date
  }, [transactions, today, dayMs]);

  // Agent roster — every producer who issued at least one policy.
  const agentRoster = React.useMemo(() => {
    const seen = new Map();
    policies.forEach(p => {
      if (!p.prodNum) return;
      if (!seen.has(p.prodNum)) seen.set(p.prodNum, { prodNum: p.prodNum, name: getProducerName(p.prodNum, producers) });
    });
    return Array.from(seen.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [policies, producers]);

  // Apply agent filter — used by KPI cards, cohort, lapse distribution,
  // per-carrier, and recent lapses. The per-agent roster table is built
  // from the full policy set so it's always the agency-wide leaderboard.
  const filteredPolicies = React.useMemo(
    () => agentFilter === 'all' ? policies : policies.filter(p => p.prodNum === agentFilter),
    [policies, agentFilter]
  );
  const focusedAgent = agentFilter === 'all' ? null : agentRoster.find(a => a.prodNum === agentFilter);

  // ── Persistency at each window ──────────────────────────────────
  const computeWindow = (N, source) => {
    const eligible = source.filter(p => p.policyAgeDays != null && p.policyAgeDays >= N);
    const lapsedWithin = eligible.filter(p => p.lapsed && p.daysToLapse != null && p.daysToLapse <= N);
    const survived = eligible.length - lapsedWithin.length;
    const pct = eligible.length > 0 ? (survived / eligible.length) * 100 : null;
    return { N, eligibleCount: eligible.length, lapsedWithin, survived, pct };
  };
  const win30  = React.useMemo(() => computeWindow(30,  filteredPolicies), [filteredPolicies]); // eslint-disable-line
  const win90  = React.useMemo(() => computeWindow(90,  filteredPolicies), [filteredPolicies]); // eslint-disable-line
  const win180 = React.useMemo(() => computeWindow(180, filteredPolicies), [filteredPolicies]); // eslint-disable-line
  const win365 = React.useMemo(() => computeWindow(365, filteredPolicies), [filteredPolicies]); // eslint-disable-line
  const windowResults = { 30: win30, 90: win90, 180: win180, 365: win365 };

  // ── Cohort by issue month ──────────────────────────────────────
  // For each issue month, compute persistency at 30/180/365 if the month is
  // old enough to have data. Otherwise show null so the line breaks gracefully.
  const cohortByMonth = React.useMemo(() => {
    const byMonth = {};
    filteredPolicies.forEach(p => {
      const m = (p.issueDate || '').slice(-4) + '-' + (p.issueDate || '').slice(0, 2); // MM/DD/YYYY → YYYY-MM
      if (!byMonth[m]) byMonth[m] = [];
      byMonth[m].push(p);
    });
    const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return Object.entries(byMonth)
      .filter(([m]) => /^\d{4}-\d{2}$/.test(m))
      .map(([month, list]) => {
        const persistAt = (N) => {
          const elig = list.filter(p => p.policyAgeDays != null && p.policyAgeDays >= N);
          if (elig.length === 0) return null;
          const lapsedIn = elig.filter(p => p.lapsed && p.daysToLapse != null && p.daysToLapse <= N);
          return ((elig.length - lapsedIn.length) / elig.length) * 100;
        };
        // Render the x-axis label as "Oct 2025" instead of the ISO
        // "2025-10" — feels native, not developer-facing.
        const [yy, mm] = month.split('-');
        const label = `${MONTH_NAMES[parseInt(mm, 10) - 1] || mm} ${yy}`;
        return {
          month,
          label,
          total: list.length,
          d30:  persistAt(30),
          d90:  persistAt(90),
          d180: persistAt(180),
          d365: persistAt(365),
        };
      })
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [filteredPolicies]);

  // ── Days-to-lapse distribution (lapsed policies only) ───────────
  const lapseDistribution = React.useMemo(() => {
    const buckets = { '0–7d': 0, '8–14d': 0, '15–30d': 0, '31–90d': 0, '91–180d': 0, '180d+': 0 };
    filteredPolicies.forEach(p => {
      if (!p.lapsed || p.daysToLapse == null) return;
      const d = p.daysToLapse;
      if (d <= 7)        buckets['0–7d']++;
      else if (d <= 14)  buckets['8–14d']++;
      else if (d <= 30)  buckets['15–30d']++;
      else if (d <= 90)  buckets['31–90d']++;
      else if (d <= 180) buckets['91–180d']++;
      else               buckets['180d+']++;
    });
    // Add % of total book so the bar chart can show how alarming
    // each bucket is in absolute terms. "12 lapses in 0-7d" reads
    // differently when it's 4% of the book vs 0.4%.
    const totalBook = filteredPolicies.length || 1;
    return Object.entries(buckets).map(([name, value]) => ({
      name,
      value,
      pctOfBook: (value / totalBook) * 100,
      fill: name === '0–7d' || name === '8–14d' ? '#ef4444' : name === '15–30d' ? '#f97316' : name === '31–90d' ? '#f59e0b' : '#94a3b8',
    }));
  }, [filteredPolicies]);
  const earlyLapseCount = lapseDistribution.slice(0, 3).reduce((s, b) => s + b.value, 0);
  const lapsedCount = lapseDistribution.reduce((s, b) => s + b.value, 0);
  const earlyLapseShare = lapsedCount > 0 ? earlyLapseCount / lapsedCount : 0;

  // ── Per-agent leaderboard (always full agency, regardless of filter) ──
  const perAgentFull = React.useMemo(() => {
    const m = {};
    policies.forEach(p => {
      if (!p.prodNum) return;
      if (!m[p.prodNum]) m[p.prodNum] = { prodNum: p.prodNum, name: getProducerName(p.prodNum, producers), policies: [] };
      m[p.prodNum].policies.push(p);
    });
    return Object.values(m).map(row => {
      const persistAt = (N) => {
        const elig = row.policies.filter(p => p.policyAgeDays != null && p.policyAgeDays >= N);
        if (elig.length === 0) return { pct: null, eligible: 0, lapsed: 0 };
        const lapsedIn = elig.filter(p => p.lapsed && p.daysToLapse != null && p.daysToLapse <= N).length;
        return { pct: ((elig.length - lapsedIn) / elig.length) * 100, eligible: elig.length, lapsed: lapsedIn };
      };
      return {
        ...row,
        totalIssued: row.policies.length,
        d30: persistAt(30),
        d90: persistAt(90),
        d180: persistAt(180),
        d365: persistAt(365),
      };
    });
  }, [policies, producers]);

  // Sorted + below-target-filtered view of the leaderboard.
  const perAgentSorted = React.useMemo(() => {
    const flagged = (w) => w.pct != null && w.pct < PERSISTENCY_TARGET;
    const filtered = showBelowOnly
      ? perAgentFull.filter(a => flagged(a.d30) || flagged(a.d90) || flagged(a.d180) || flagged(a.d365))
      : perAgentFull;
    const keyFor = (a) => {
      if (agentSort.key === 'name')        return a.name || '';
      if (agentSort.key === 'totalIssued') return a.totalIssued;
      if (agentSort.key === 'd30')         return a.d30.pct == null ? -1 : a.d30.pct;
      if (agentSort.key === 'd90')         return a.d90.pct == null ? -1 : a.d90.pct;
      if (agentSort.key === 'd180')        return a.d180.pct == null ? -1 : a.d180.pct;
      if (agentSort.key === 'd365')        return a.d365.pct == null ? -1 : a.d365.pct;
      return 0;
    };
    return [...filtered].sort((a, b) => {
      const av = keyFor(a), bv = keyFor(b);
      if (typeof av === 'number' && typeof bv === 'number') return agentSort.dir === 'asc' ? av - bv : bv - av;
      const cmp = String(av).localeCompare(String(bv));
      return agentSort.dir === 'asc' ? cmp : -cmp;
    });
  }, [perAgentFull, agentSort, showBelowOnly]);

  const agentSortHeader = (k, label, alignRight = false) => (
    <th className={`py-2 px-2 ${alignRight ? 'text-right' : ''}`}>
      <button
        onClick={(e) => { e.stopPropagation(); setAgentSort(s => s.key === k ? { key: k, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: 'desc' }); }}
        className="hover:text-slate-900"
      >
        {label}{agentSort.key === k ? (agentSort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
      </button>
    </th>
  );

  // ── Per-carrier table ───────────────────────────────────────────
  const perCarrier = React.useMemo(() => {
    const m = {};
    filteredPolicies.forEach(p => {
      if (!p.carrier) return;
      if (!m[p.carrier]) m[p.carrier] = { carrier: p.carrier, policies: [] };
      m[p.carrier].policies.push(p);
    });
    return Object.values(m).map(row => {
      const persistAt = (N) => {
        const elig = row.policies.filter(p => p.policyAgeDays != null && p.policyAgeDays >= N);
        if (elig.length === 0) return { pct: null, eligible: 0, lapsed: 0 };
        const lapsedIn = elig.filter(p => p.lapsed && p.daysToLapse != null && p.daysToLapse <= N).length;
        return { pct: ((elig.length - lapsedIn) / elig.length) * 100, eligible: elig.length, lapsed: lapsedIn };
      };
      return {
        ...row,
        totalIssued: row.policies.length,
        d30: persistAt(30),
        d90: persistAt(90),
        d180: persistAt(180),
        d365: persistAt(365),
      };
    }).sort((a, b) => b.totalIssued - a.totalIssued);
  }, [filteredPolicies]);

  // ── Recent lapses (last 90 days of activity) ────────────────────
  const recentLapses = React.useMemo(() => {
    const cutoff = today.getTime() - 90 * dayMs;
    return filteredPolicies
      .filter(p => p.lapsed && p.lapsedDate && p.lapsedDate.getTime() >= cutoff)
      .sort((a, b) => b.lapsedDate.getTime() - a.lapsedDate.getTime())
      .slice(0, 100);
  }, [filteredPolicies, today, dayMs]);

  // ── Render ──────────────────────────────────────────────────────
  const current = windowResults[windowDays];

  if (policies.length === 0) {
    return (
      <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-12 text-center">
        <div className="text-slate-700 font-semibold mb-1">No policies to analyze</div>
        <div className="text-sm text-slate-500">Upload a commission statement so the persistency tracker can match issue dates against chargebacks.</div>
      </div>
    );
  }

  const pctTxt = (v) => v == null ? '—' : `${v.toFixed(1)}%`;
  const pctCellClass = (v) => v == null ? 'text-slate-400' : v >= PERSISTENCY_TARGET ? 'text-emerald-700 font-semibold' : v >= 70 ? 'text-amber-700 font-semibold' : 'text-red-700 font-semibold';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Persistency</h2>
          <p className="text-sm text-slate-500 mt-1">Track how many policies survive 30 days · 3 months · 6 months · 1 year from issue. Goal: keep <strong>{PERSISTENCY_TARGET}%+</strong> on the books.</p>
        </div>
        {agentRoster.length > 1 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Agent</span>
            <select value={agentFilter} onChange={e => setAgentFilter(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white">
              <option value="all">All agents · {agentRoster.length}</option>
              {agentRoster.map(a => <option key={a.prodNum} value={a.prodNum}>{a.name || `#${a.prodNum}`}</option>)}
            </select>
            {agentFilter !== 'all' && (
              <button onClick={() => setAgentFilter('all')} className="text-xs text-slate-500 hover:text-slate-700 underline">← All agents</button>
            )}
          </div>
        )}
      </div>

      {/* Focused agent banner */}
      {focusedAgent && (
        <div className="bg-slate-900 text-white rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-300 font-semibold">Viewing</div>
            <div className="text-lg font-bold mt-0.5">{focusedAgent.name || `#${focusedAgent.prodNum}`}</div>
            <div className="text-xs text-slate-300 font-mono mt-0.5">{focusedAgent.prodNum} · {filteredPolicies.length} polic{filteredPolicies.length === 1 ? 'y' : 'ies'} issued</div>
          </div>
          <button onClick={() => setAgentFilter('all')} className="bg-white/20 hover:bg-white/30 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">Back to agency view</button>
        </div>
      )}

      {/* 4 main KPI cards — empty-state-aware. When no policies have
          reached the window yet, render an explicit 'too early to
          measure' state instead of '0 of 0 — —' which reads broken. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {PERSISTENCY_WINDOWS.map(w => {
          const r = windowResults[w.days];
          const isEmpty = r.eligibleCount === 0;
          const color = isEmpty ? 'slate' : persistencyColor(r.pct);
          const isActive = windowDays === w.days;
          return (
            <button
              key={w.id}
              onClick={() => setWindowDays(w.days)}
              className={`text-left bg-white rounded-2xl border-2 p-5 transition-all hover:shadow-md ${
                isEmpty
                  ? 'border-slate-200 opacity-75'
                  : isActive ? `border-${color}-500 ring-2 ring-${color}-100` : `border-${color}-200`
              }`}
            >
              <div className="flex items-center justify-between">
                <div className={`text-xs font-semibold uppercase tracking-wide ${isEmpty ? 'text-slate-500' : `text-${color}-700`}`}>{w.label} persistency</div>
                {isEmpty && <span className="text-[10px] bg-slate-100 text-slate-600 font-bold px-2 py-0.5 rounded-full">no data yet</span>}
                {!isEmpty && r.pct != null && r.pct >= PERSISTENCY_TARGET && <span className={`text-[10px] bg-${color}-100 text-${color}-700 font-bold px-2 py-0.5 rounded-full`}>on target ✓</span>}
                {!isEmpty && r.pct != null && r.pct < PERSISTENCY_TARGET && <span className="text-[10px] bg-red-100 text-red-700 font-bold px-2 py-0.5 rounded-full">below {PERSISTENCY_TARGET}%</span>}
              </div>
              {isEmpty ? (
                <>
                  <div className="text-2xl font-bold mt-2 text-slate-400">—</div>
                  <div className="text-xs text-slate-500 mt-1 italic">
                    No policies have reached {w.days < 365 ? `${w.days} days` : '12 months'} yet.
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1">
                    {w.days >= 365
                      ? "Check back once your oldest policy is a year old."
                      : `Your earliest issue date must be ≥${w.days}d ago.`}
                  </div>
                </>
              ) : (
                <>
                  <div className={`text-4xl font-bold mt-2 text-${color}-700`}>{pctTxt(r.pct)}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    {r.survived} of {r.eligibleCount} {r.eligibleCount === 1 ? 'policy' : 'policies'} still on book
                  </div>
                  {r.pct != null && (
                    <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full bg-${color}-500`} style={{ width: `${Math.min(100, Math.max(0, r.pct))}%` }}/>
                    </div>
                  )}
                </>
              )}
            </button>
          );
        })}
      </div>

      {/* Coaching callout if early-lapse share is high. Actionable —
          one click filters the Recent Lapses table to 0-30d only and
          scrolls there so the agent can start dialing. */}
      {lapsedCount > 0 && earlyLapseShare > 0.5 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-900 flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <strong>⚠ Early-lapse alert:</strong> {(earlyLapseShare * 100).toFixed(0)}% of your lapses happen within 30 days of issue ({earlyLapseCount} of {lapsedCount}). That's usually payment-info issues, buyer's remorse, or rushed quotes. Tighten qualification and confirm draft dates before binding.
          </div>
          <button
            onClick={() => {
              setEarlyLapseFilter(true);
              setTimeout(() => recentLapsesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
            }}
            className="bg-red-700 hover:bg-red-800 text-white text-xs font-black uppercase tracking-wider px-3 py-2 rounded-lg whitespace-nowrap"
          >
            See the {earlyLapseCount} early lapses →
          </button>
        </div>
      )}

      <HardToCloseReport
        leads={leads}
        calls={calls}
        outreach={outreach}
        policies={filteredPolicies}
        openClientProfile={openClientProfile}
      />

      {/* Cohort line chart */}
      {cohortByMonth.length > 0 && (
        <Card title="Persistency by issue cohort" subtitle="Dashed line = 80% target. Each line plots persistency at 30-day · 3-month · 6-month · 1-year for policies issued in that month." fullscreenable defaultHeight={300}>
          {(h) => (
            <ResponsiveContainer width="100%" height={h}>
              <LineChart data={cohortByMonth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                <XAxis dataKey="label" tick={{ fontSize: 11 }}/>
                <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 12 }}/>
                <Tooltip formatter={(v, n) => v == null ? '—' : [`${v.toFixed(1)}%`, n]}/>
                <Legend/>
                <ReferenceLine y={PERSISTENCY_TARGET} stroke="#10b981" strokeDasharray="6 4" label={{ value: 'target', position: 'right', fill: '#10b981', fontSize: 11 }}/>
                <Line type="monotone" dataKey="d30"  name="30-day"  stroke="#10b981" strokeWidth={2} connectNulls/>
                <Line type="monotone" dataKey="d90"  name="3-month" stroke="#0ea5e9" strokeWidth={2} connectNulls/>
                <Line type="monotone" dataKey="d180" name="6-month" stroke="#f59e0b" strokeWidth={2} connectNulls/>
                <Line type="monotone" dataKey="d365" name="1-year"  stroke="#8b5cf6" strokeWidth={2} connectNulls/>
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>
      )}

      {/* Days-to-lapse distribution — bars now carry both the raw
          count AND the % of total book on the label above + tooltip,
          so each bucket reads against the agency's scale. */}
      {lapsedCount > 0 && (
        <Card title="When are policies lapsing?" subtitle={`${lapsedCount} total lapses (${((lapsedCount / Math.max(1, filteredPolicies.length)) * 100).toFixed(1)}% of book) · red = within first 30 days (highest coaching value)`} fullscreenable defaultHeight={260}>
          {(h) => (
            <ResponsiveContainer width="100%" height={h}>
              <BarChart data={lapseDistribution} margin={{ top: 22, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                <XAxis dataKey="name" tick={{ fontSize: 12 }}/>
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false}/>
                <Tooltip
                  formatter={(v, _n, props) => {
                    const pct = props?.payload?.pctOfBook;
                    return [`${v} lapses${pct != null ? ` (${pct.toFixed(2)}% of book)` : ''}`, 'Policies'];
                  }}
                />
                <Bar dataKey="value" name="Policies"
                  label={({ x, y, width, value, index }) => {
                    const pct = lapseDistribution[index]?.pctOfBook;
                    if (value === 0 || pct == null) return null;
                    return (
                      <g>
                        <text x={x + width / 2} y={y - 10} textAnchor="middle" fontSize="11" fontWeight="700" fill="#0f172a">{value}</text>
                        <text x={x + width / 2} y={y + 4} textAnchor="middle" fontSize="9" fill="#64748b">{pct.toFixed(1)}%</text>
                      </g>
                    );
                  }}
                >
                  {lapseDistribution.map((b, i) => <Cell key={i} fill={b.fill}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      )}

      {/* Per-agent table — agency-wide leaderboard (always uses full policy set) */}
      {perAgentFull.length > 0 && !focusedAgent && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <h3 className="font-semibold text-slate-900">Per-agent persistency</h3>
              <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                <span>Click any row to drill into that agent.</span>
                {/* Inline legend explaining the warning icon */}
                <span className="inline-flex items-center gap-1 text-red-700">
                  <span>⚠</span>
                  <span>= below {PERSISTENCY_TARGET}% on at least one window.</span>
                </span>
                <span className="text-slate-400">Format: <span className="font-mono">% / eligible-count</span></span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowBelowOnly(s => !s)}
                className={`px-2.5 py-1 text-xs font-medium rounded-lg border ${showBelowOnly ? 'bg-red-600 text-white border-red-600' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}
                title={`Show only agents below ${PERSISTENCY_TARGET}% on at least one window`}
              >
                {showBelowOnly ? '⚠ Below target only' : 'Show below target'}
              </button>
              <span className="text-xs text-slate-500">{perAgentSorted.length} shown · {perAgentFull.length} total</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  {agentSortHeader('name', 'Agent')}
                  {agentSortHeader('totalIssued', 'Issued', true)}
                  {agentSortHeader('d30',  '30d',  true)}
                  {agentSortHeader('d90',  '3mo',  true)}
                  {agentSortHeader('d180', '6mo',  true)}
                  {agentSortHeader('d365', '1y',   true)}
                </tr>
              </thead>
              <tbody>
                {perAgentSorted.length === 0 && (
                  <tr><td colSpan={6} className="py-6 text-center text-slate-400">No agents match this filter.</td></tr>
                )}
                {perAgentSorted.map(a => {
                  const flagged = (w) => w.pct != null && w.pct < PERSISTENCY_TARGET;
                  const anyBelow = flagged(a.d30) || flagged(a.d90) || flagged(a.d180) || flagged(a.d365);
                  // Compact "92.1% / 47" rendering — keeps both the
                  // headline percentage AND the n that drives it on
                  // screen without doubling the column count.
                  const renderWin = (w) => {
                    if (w.eligible === 0) return <span className="text-slate-300">—</span>;
                    return (
                      <span className="inline-flex items-baseline gap-1">
                        <span className={`${pctCellClass(w.pct)} tabular-nums`}>{pctTxt(w.pct)}</span>
                        <span className="text-[10px] text-slate-400 font-mono">/ {w.eligible}</span>
                      </span>
                    );
                  };
                  return (
                    <tr
                      key={a.prodNum}
                      onClick={() => setAgentFilter(a.prodNum)}
                      tabIndex={0}
                      onKeyDown={e => { if (e.key === 'Enter') setAgentFilter(a.prodNum); }}
                      className={`border-b border-slate-100 cursor-pointer transition-colors ${anyBelow ? 'bg-red-50/40 hover:bg-red-100/70' : 'hover:bg-slate-50'}`}
                    >
                      <td className="py-2 pr-3 font-medium">
                        <div className="flex items-center gap-2">
                          {anyBelow && <span className="text-red-600" aria-label="Below target">⚠</span>}
                          <span>{a.name || `#${a.prodNum}`}</span>
                        </div>
                        <div className="text-[10px] font-mono text-slate-400 ml-5">{a.prodNum}</div>
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums">{a.totalIssued}</td>
                      <td className="py-2 px-2 text-right">{renderWin(a.d30)}</td>
                      <td className="py-2 px-2 text-right">{renderWin(a.d90)}</td>
                      <td className="py-2 px-2 text-right">{renderWin(a.d180)}</td>
                      <td className="py-2 px-2 text-right">{renderWin(a.d365)}</td>
                    </tr>
                  );
                })}
              </tbody>
              {agentRoster.length > 0 && (() => {
                const persistAt = (N) => {
                  const elig = policies.filter(p => p.policyAgeDays != null && p.policyAgeDays >= N);
                  if (elig.length === 0) return { pct: null, eligible: 0 };
                  const lapsedIn = elig.filter(p => p.lapsed && p.daysToLapse != null && p.daysToLapse <= N).length;
                  return { pct: ((elig.length - lapsedIn) / elig.length) * 100, eligible: elig.length };
                };
                const a30 = persistAt(30), a90 = persistAt(90), a180 = persistAt(180), a365 = persistAt(365);
                const renderFootWin = (w) => w.eligible === 0
                  ? <span className="text-slate-300">—</span>
                  : <span className="inline-flex items-baseline gap-1">
                      <span className={`${pctCellClass(w.pct)} tabular-nums`}>{pctTxt(w.pct)}</span>
                      <span className="text-[10px] text-slate-500 font-mono">/ {w.eligible}</span>
                    </span>;
                return (
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 text-sm font-bold text-slate-900 bg-slate-50">
                      <td className="py-2 pr-3">Agency total</td>
                      <td className="py-2 px-2 text-right tabular-nums">{policies.length}</td>
                      <td className="py-2 px-2 text-right">{renderFootWin(a30)}</td>
                      <td className="py-2 px-2 text-right">{renderFootWin(a90)}</td>
                      <td className="py-2 px-2 text-right">{renderFootWin(a180)}</td>
                      <td className="py-2 px-2 text-right">{renderFootWin(a365)}</td>
                    </tr>
                  </tfoot>
                );
              })()}
            </table>
          </div>
        </div>
      )}

      {/* Per-carrier table */}
      {perCarrier.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-900">Per-carrier persistency</h3>
            <div className="text-xs text-slate-500">{perCarrier.length} carriers</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Carrier</th>
                  <th className="py-2 px-2 text-right">Total policies</th>
                  <th className="py-2 px-2 text-right">30d %</th>
                  <th className="py-2 px-2 text-right">3mo %</th>
                  <th className="py-2 px-2 text-right">6mo %</th>
                  <th className="py-2 px-2 text-right">1y %</th>
                </tr>
              </thead>
              <tbody>
                {perCarrier.map(c => (
                  <tr key={c.carrier} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 pr-3 font-medium">{c.carrier}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{c.totalIssued}</td>
                    <td className={`py-2 px-2 text-right tabular-nums ${pctCellClass(c.d30.pct)}`}>{pctTxt(c.d30.pct)}{c.d30.eligible > 0 && <span className="text-slate-400 font-normal"> ({c.d30.eligible})</span>}</td>
                    <td className={`py-2 px-2 text-right tabular-nums ${pctCellClass(c.d90.pct)}`}>{pctTxt(c.d90.pct)}{c.d90.eligible > 0 && <span className="text-slate-400 font-normal"> ({c.d90.eligible})</span>}</td>
                    <td className={`py-2 px-2 text-right tabular-nums ${pctCellClass(c.d180.pct)}`}>{pctTxt(c.d180.pct)}{c.d180.eligible > 0 && <span className="text-slate-400 font-normal"> ({c.d180.eligible})</span>}</td>
                    <td className={`py-2 px-2 text-right tabular-nums ${pctCellClass(c.d365.pct)}`}>{pctTxt(c.d365.pct)}{c.d365.eligible > 0 && <span className="text-slate-400 font-normal"> ({c.d365.eligible})</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent lapses — every row now shows two things side by side:
          'days to lapse' (how fast they fell off the book — coaching
          signal) and 'days since lapse' (how recoverable they are —
          outreach signal). Color codes:
            ≤30d since   = emerald  · most carriers allow reinstatement w/o evidence
            31-60d since = amber    · usually requires statement of health
            61+d since   = slate    · likely unrecoverable, re-write candidate */}
      {recentLapses.length > 0 && (() => {
        const visible = earlyLapseFilter
          ? recentLapses.filter(p => p.daysToLapse != null && p.daysToLapse <= 30)
          : recentLapses;
        return (
          <div ref={recentLapsesRef} className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <h3 className="font-semibold text-slate-900">Recent lapses (last 90 days)</h3>
                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 bg-emerald-500 rounded-full"/>≤30d since · reinstate window open</span>
                  <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 bg-amber-500 rounded-full"/>31-60d · statement of health likely required</span>
                  <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 bg-slate-400 rounded-full"/>61d+ · likely re-write only</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {earlyLapseFilter && (
                  <button
                    onClick={() => setEarlyLapseFilter(false)}
                    className="text-xs font-semibold uppercase tracking-wider bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg"
                  >
                    ⚠ Early lapses only · clear filter
                  </button>
                )}
                <div className="text-xs text-slate-500">Showing {visible.length}{earlyLapseFilter ? ` of ${recentLapses.length}` : ''}</div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3">Policy</th>
                    <th className="py-2 px-2">Insured</th>
                    <th className="py-2 px-2">Agent</th>
                    <th className="py-2 px-2">Carrier</th>
                    <th className="py-2 px-2">Issued</th>
                    <th className="py-2 px-2">Lapsed</th>
                    <th className="py-2 px-2 text-right">Days to lapse</th>
                    <th className="py-2 px-2 text-right">Days since</th>
                    <th className="py-2 px-2">Recovery</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 && (
                    <tr><td colSpan={9} className="py-6 text-center text-slate-400">No lapses match this filter.</td></tr>
                  )}
                  {visible.map(p => {
                    const dColor = p.daysToLapse == null ? 'text-slate-500' : p.daysToLapse <= 30 ? 'text-red-700 font-bold' : p.daysToLapse <= 90 ? 'text-amber-700 font-semibold' : 'text-slate-600';
                    const daysSinceLapse = p.lapsedDate ? Math.floor((today.getTime() - p.lapsedDate.getTime()) / dayMs) : null;
                    const recovery =
                      daysSinceLapse == null ? { label: '—', tone: 'text-slate-400 bg-slate-50' } :
                      daysSinceLapse <= 30   ? { label: 'Reinstate window', tone: 'text-emerald-800 bg-emerald-100' } :
                      daysSinceLapse <= 60   ? { label: 'SoH required',    tone: 'text-amber-800   bg-amber-100'   } :
                                               { label: 'Re-write only',   tone: 'text-slate-700   bg-slate-100'   };
                    return (
                      <tr key={p.key} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 pr-3 font-mono text-xs">{p.policy}</td>
                        <td className="py-2 px-2"><ClientNameLink name={p.insured} openClientProfile={openClientProfile}/></td>
                        <td className="py-2 px-2">{getProducerName(p.prodNum, producers) || `#${p.prodNum}`}</td>
                        <td className="py-2 px-2 text-slate-600">{p.carrier}</td>
                        <td className="py-2 px-2 text-slate-600">{p.issueDate}</td>
                        <td className="py-2 px-2 text-slate-600">{p.firstLapseDate || '—'}</td>
                        <td className={`py-2 px-2 text-right tabular-nums ${dColor}`}>{p.daysToLapse == null ? '—' : `${p.daysToLapse}d`}</td>
                        <td className={`py-2 px-2 text-right tabular-nums ${daysSinceLapse != null && daysSinceLapse <= 30 ? 'text-emerald-700 font-bold' : daysSinceLapse != null && daysSinceLapse <= 60 ? 'text-amber-700 font-semibold' : 'text-slate-500'}`}>{daysSinceLapse == null ? '—' : `${daysSinceLapse}d`}</td>
                        <td className="py-2 px-2"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${recovery.tone}`}>{recovery.label}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Underwriting Assistant — multi-carrier grid model.
//
// Library tab: upload a PDF/DOCX/TXT/image → render each PDF page to PNG
// in the browser (NOT pdf.js text extraction — those grids have scrambled
// text layers) → POST each page to /api/underwriting/parse-page where
// Opus reads the grid and emits {condition, carrier, product, decision}
// cell records plus build chart rows and product overviews. The
// uploader reviews the parsed grid before committing it to the library.
//
// Lookup tab: agent types a scenario + optional height/weight + optional
// carrier filter. The server pulls the matching cells/build rows/overviews
// and sends them to Sonnet with the intelligence-layer system prompt
// (combination knockouts, medication inference, smart intake, the play,
// coverage limits, smoker nuance, build knockout).
//
// Brand: emerald + light slate, consistent with the rest of the CRM. The
// UW_GOLD / UW_DARK names are kept for code stability but resolve to the
// shared palette: UW_GOLD = emerald-600 (primary accent and button bg),
// UW_DARK = white (inverse text on emerald, plus card background).
// ──────────────────────────────────────────────────────────────────────

const UW_GOLD = '#059669';
const UW_DARK = '#FFFFFF';

// Status → render style. Approved/Not Asked top, Decline bottom.
const UW_STATUS_STYLES = {
  'Approved/Eligible':  { dot: '#10b981', bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-800', label: 'Approved' },
  'Not Asked':          { dot: '#14b8a6', bg: 'bg-teal-50',    border: 'border-teal-300',    text: 'text-teal-800',    label: 'Not Asked' },
  'Conditional/Routed': { dot: '#0ea5e9', bg: 'bg-sky-50',     border: 'border-sky-300',     text: 'text-sky-800',     label: 'Routed' },
  'Graded/Modified':    { dot: '#f59e0b', bg: 'bg-amber-50',   border: 'border-amber-300',   text: 'text-amber-800',   label: 'Graded / Modified' },
  'Smoker Rate':        { dot: '#f59e0b', bg: 'bg-amber-50',   border: 'border-amber-300',   text: 'text-amber-800',   label: 'Smoker Rate' },
  'Postpone':           { dot: '#f97316', bg: 'bg-orange-50',  border: 'border-orange-300',  text: 'text-orange-800',  label: 'Postpone' },
  'Needs Review':       { dot: '#64748b', bg: 'bg-slate-50',   border: 'border-slate-300',   text: 'text-slate-800',   label: 'Needs Review' },
  'Not Addressed':      { dot: '#94a3b8', bg: 'bg-slate-50',   border: 'border-slate-200',   text: 'text-slate-700',   label: 'Not Addressed' },
  'Decline/Ineligible': { dot: '#ef4444', bg: 'bg-red-50',     border: 'border-red-300',     text: 'text-red-800',     label: 'Decline' },
};

// Lazy load mammoth from CDN for client-side DOCX parsing — keeps the main
// bundle small and only pays the cost when an agent actually uploads a .docx.
let mammothLib = null;
const loadMammoth = async () => {
  if (mammothLib) return mammothLib;
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://unpkg.com/mammoth@1.6.0/mammoth.browser.min.js';
    s.onload = () => { mammothLib = window.mammoth; resolve(mammothLib); };
    s.onerror = () => reject(new Error('Failed to load mammoth from CDN.'));
    document.head.appendChild(s);
  });
};

// Returns the active Supabase session JWT, or null if not signed in.
// Used to attach Authorization: Bearer headers to /api/underwriting/* calls.
const getSupabaseJwt = async () => {
  try {
    const mod = await import('./lib/supabase');
    const { data } = await mod.supabase.auth.getSession();
    return data?.session?.access_token || null;
  } catch {
    return null;
  }
};

const apiFetch = async (path, opts = {}) => {
  const jwt = await getSupabaseJwt();
  const res = await fetch(path, {
    ...opts,
    headers: {
      'content-type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const ct = res.headers.get('content-type') || '';
  const body = ct.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const msg = (body && body.error) || (typeof body === 'string' ? body.slice(0, 200) : `HTTP ${res.status}`);
    throw new Error(msg);
  }
  return body;
};

// Detect "table doesn't exist" errors from Supabase / PostgREST so we can
// render the dedicated setup panel instead of a generic red box. These two
// substrings cover both the schema-cache message and the bare 42P01 case.
function isUwSchemaError(msg) {
  if (!msg) return false;
  const m = String(msg).toLowerCase();
  return m.includes('underwriting_sheets') && (m.includes('schema cache') || m.includes('does not exist') || m.includes('relation') || m.includes('42p01'));
}

// The full migration SQL — kept inline so the user can copy it in one click
// without leaving the app. Mirrors what's documented in UNDERWRITING_README.md.
const UW_SETUP_SQL = `-- Drop the legacy single-table model if it exists. Skip if you never ran it.
drop table if exists public.underwriting_sheets cascade;

-- ── Sheets: one row per uploaded file ────────────────────────────
create table public.underwriting_sheets (
  id              uuid primary key default gen_random_uuid(),
  user_id         uuid not null references auth.users(id) on delete cascade,
  uploader_email  text,
  filename        text,
  page_count      integer default 0,
  cell_count      integer default 0,
  build_row_count integer default 0,
  notes           text,
  uploaded_at     timestamptz not null default now()
);

-- ── Cells: one row per condition × carrier × product extracted from a sheet
create table public.underwriting_cells (
  id              uuid primary key default gen_random_uuid(),
  sheet_id        uuid not null references public.underwriting_sheets(id) on delete cascade,
  user_id         uuid not null,
  condition       text not null,
  carrier         text not null,
  product         text not null,
  decision_text   text not null,
  page            integer
);
create index underwriting_cells_condition_idx on public.underwriting_cells (lower(condition));
create index underwriting_cells_carrier_idx   on public.underwriting_cells (carrier);
create index underwriting_cells_sheet_idx     on public.underwriting_cells (sheet_id);

-- ── Build / BMI rows: one row per (carrier, product, height) ─────
create table public.underwriting_build_rows (
  id              uuid primary key default gen_random_uuid(),
  sheet_id        uuid not null references public.underwriting_sheets(id) on delete cascade,
  user_id         uuid not null,
  carrier         text not null,
  product         text not null,
  height_inches   integer not null,
  min_weight      integer,
  max_weight      integer,
  table_rating    text,
  notes           text
);
create index underwriting_build_rows_lookup_idx on public.underwriting_build_rows (carrier, product, height_inches);

-- ── Product overviews: max face, exam thresholds, etc. ───────────
create table public.underwriting_product_overviews (
  id              uuid primary key default gen_random_uuid(),
  sheet_id        uuid not null references public.underwriting_sheets(id) on delete cascade,
  user_id         uuid not null,
  carrier         text not null,
  product         text not null,
  max_face        integer,
  exam_threshold  integer,
  details         text
);
create index underwriting_product_overviews_lookup_idx on public.underwriting_product_overviews (carrier, product);

-- ── RLS: agency-wide reads, uploader-only writes ─────────────────
alter table public.underwriting_sheets             enable row level security;
alter table public.underwriting_cells              enable row level security;
alter table public.underwriting_build_rows         enable row level security;
alter table public.underwriting_product_overviews  enable row level security;

create policy "uw_sheets_read"    on public.underwriting_sheets            for select to authenticated using (true);
create policy "uw_sheets_insert"  on public.underwriting_sheets            for insert to authenticated with check (auth.uid() = user_id);
create policy "uw_sheets_delete"  on public.underwriting_sheets            for delete to authenticated using (auth.uid() = user_id);

create policy "uw_cells_read"     on public.underwriting_cells             for select to authenticated using (true);
create policy "uw_cells_insert"   on public.underwriting_cells             for insert to authenticated with check (auth.uid() = user_id);
create policy "uw_cells_delete"   on public.underwriting_cells             for delete to authenticated using (auth.uid() = user_id);

create policy "uw_build_read"     on public.underwriting_build_rows        for select to authenticated using (true);
create policy "uw_build_insert"   on public.underwriting_build_rows        for insert to authenticated with check (auth.uid() = user_id);
create policy "uw_build_delete"   on public.underwriting_build_rows        for delete to authenticated using (auth.uid() = user_id);

create policy "uw_po_read"        on public.underwriting_product_overviews for select to authenticated using (true);
create policy "uw_po_insert"      on public.underwriting_product_overviews for insert to authenticated with check (auth.uid() = user_id);
create policy "uw_po_delete"      on public.underwriting_product_overviews for delete to authenticated using (auth.uid() = user_id);`;

function UnderwritingSetupPanel({ error, onRetry, showToast = () => {} }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(UW_SETUP_SQL);
      setCopied(true);
      showToast('Migration SQL copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast('Copy failed — select the SQL manually');
    }
  };
  return (
    <div className="rounded-2xl p-5 space-y-4 bg-amber-50 border-2 border-amber-300">
      <div>
        <div className="text-xs uppercase tracking-wider font-bold text-amber-900">Setup required</div>
        <h3 className="text-xl font-bold mt-1 text-slate-900">The underwriting tables don't exist in Supabase yet.</h3>
        <p className="text-sm mt-1 text-slate-700">
          Run the migration below in your Supabase SQL editor, then click Retry. This is a one-time setup per project.
        </p>
        <p className="text-xs mt-2 text-slate-500">Raw error: <code className="font-mono">{error}</code></p>
      </div>

      <ol className="text-sm space-y-1.5 text-slate-700">
        <li>1. Open <a href="https://supabase.com/dashboard/project/_/sql/new" target="_blank" rel="noreferrer" className="underline text-emerald-700 hover:text-emerald-900">Supabase SQL Editor</a> (signed in to the project this app uses).</li>
        <li>2. Paste the SQL below.</li>
        <li>3. Click <strong>Run</strong>.</li>
        <li>4. Come back here and hit <strong>Retry</strong>.</li>
      </ol>

      <div className="rounded-lg overflow-hidden border border-slate-300 bg-slate-900">
        <div className="flex items-center justify-between px-3 py-2 bg-slate-800 border-b border-slate-700">
          <span className="text-xs font-mono text-slate-300">migration.sql</span>
          <button onClick={copy} className="px-3 py-1 text-xs font-bold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white">
            {copied ? '✓ Copied' : 'Copy SQL'}
          </button>
        </div>
        <pre className="text-[11px] leading-relaxed font-mono p-3 max-h-96 overflow-auto whitespace-pre text-slate-200">{UW_SETUP_SQL}</pre>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={onRetry} className="px-4 py-2 text-sm font-bold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white">Retry</button>
        <span className="text-xs text-slate-600">After running the SQL, click Retry to load the (empty) library.</span>
      </div>
    </div>
  );
}

function UnderwritingView({ showToast = () => {} }) {
  const [subtab, setSubtab] = useState('library'); // 'library' | 'lookup'
  const [sheets, setSheets] = useState([]);
  const [loadingSheets, setLoadingSheets] = useState(false);
  const [loadError, setLoadError] = useState('');

  const refresh = React.useCallback(async () => {
    setLoadingSheets(true); setLoadError('');
    try {
      const r = await apiFetch('/api/underwriting/list');
      setSheets(r.sheets || []);
    } catch (e) {
      setLoadError(e.message || String(e));
    } finally {
      setLoadingSheets(false);
    }
  }, []);
  React.useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Underwriting Assistant</h2>
          <p className="text-sm text-slate-500 mt-1">Upload carrier cheat sheets once. The AI reads the multi-carrier grid, then answers "will any of my carriers take this client?" with citations, combination warnings, and the play.</p>
        </div>
        <div className="inline-flex items-center gap-1 rounded-xl p-1 bg-slate-100 border border-slate-200">
          {[['library', 'Cheat Sheet Library'], ['lookup', 'Condition Lookup']].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setSubtab(id)}
              className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition ${
                subtab === id
                  ? 'bg-emerald-600 text-white shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg p-3 text-xs bg-amber-50 text-amber-900 border border-amber-200">
        <strong>Guidance based on uploaded carrier materials only.</strong> Final underwriting decision rests with the carrier. Verify before quoting a client.
      </div>

      {/* If the load error is "table not in schema cache" or similar, render a
          dedicated setup-required panel with the SQL migration embedded and a
          copy button — far more useful than a red one-liner. */}
      {loadError && isUwSchemaError(loadError) ? (
        <UnderwritingSetupPanel error={loadError} onRetry={refresh} showToast={showToast}/>
      ) : loadError ? (
        <div className="rounded-lg p-3 text-sm bg-red-50 text-red-800 border border-red-200">
          Couldn't load the library: {loadError}.
        </div>
      ) : null}

      {!loadError && (subtab === 'library' ? (
        <UnderwritingLibrary sheets={sheets} loading={loadingSheets} onRefresh={refresh} showToast={showToast}/>
      ) : (
        <UnderwritingLookup sheetsCount={sheets.length} showToast={showToast}/>
      ))}
    </div>
  );
}

// ── Library tab ────────────────────────────────────────────────────
function UnderwritingLibrary({ sheets = [], loading, onRefresh, showToast }) {
  const [filename, setFilename] = useState('');
  const [pages, setPages] = useState([]); // [{ index, kind:'image'|'text', dataUrl?|text?, status, parsed?, error? }]
  const [parsedCells, setParsedCells] = useState([]);
  const [parsedBuildRows, setParsedBuildRows] = useState([]);
  const [parsedOverviews, setParsedOverviews] = useState([]);
  const [useOpus, setUseOpus] = useState(true); // ingest defaults to Opus
  const [phase, setPhase] = useState('idle');   // 'idle' | 'rendering' | 'parsing' | 'preview' | 'committing'
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [parseError, setParseError] = useState('');
  const fileInputRef = useRef(null);

  const reset = () => {
    setFilename(''); setPages([]); setParsedCells([]); setParsedBuildRows([]); setParsedOverviews([]);
    setPhase('idle'); setProgress({ done: 0, total: 0 }); setParseError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Render every PDF page to a PNG dataURL at 1.5x scale (high enough for
  // OCR, low enough to keep base64 payload manageable for serverless).
  const renderPdf = async (file) => {
    const pdfjs = await loadPdfJs();
    const data = new Uint8Array(await file.arrayBuffer());
    const doc = await pdfjs.getDocument({ data }).promise;
    const out = [];
    for (let i = 1; i <= doc.numPages; i++) {
      const page = await doc.getPage(i);
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = document.createElement('canvas');
      canvas.width  = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const ctx = canvas.getContext('2d');
      await page.render({ canvasContext: ctx, viewport }).promise;
      out.push({ index: i, kind: 'image', dataUrl: canvas.toDataURL('image/png'), status: 'ready', parsed: null, error: '' });
    }
    return out;
  };

  // DOCX / TXT — extract as text, send as a single "page" in text mode.
  const extractText = async (file) => {
    const name = (file.name || '').toLowerCase();
    if (name.endsWith('.txt')) return await file.text();
    if (name.endsWith('.docx')) {
      const m = await loadMammoth();
      const buf = await file.arrayBuffer();
      const r = await m.extractRawText({ arrayBuffer: buf });
      return (r?.value || '').trim();
    }
    throw new Error(`Unsupported file type: ${name}`);
  };

  const handleFilePick = async (file) => {
    if (!file) return;
    reset();
    setFilename(file.name);
    const name = (file.name || '').toLowerCase();
    try {
      setPhase('rendering');
      let pgs = [];
      if (name.endsWith('.pdf')) {
        pgs = await renderPdf(file);
      } else if (name.endsWith('.docx') || name.endsWith('.txt')) {
        const text = await extractText(file);
        pgs = [{ index: 1, kind: 'text', text, status: 'ready', parsed: null, error: '' }];
      } else if (/\.(png|jpe?g|webp|gif|heic|heif)$/i.test(name) || (file.type || '').startsWith('image/')) {
        const dataUrl = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(String(r.result || ''));
          r.onerror = () => rej(new Error('Failed to read image'));
          r.readAsDataURL(file);
        });
        pgs = [{ index: 1, kind: 'image', dataUrl, status: 'ready', parsed: null, error: '' }];
      } else {
        throw new Error(`Unsupported file type: ${name}. Accepted: PDF, DOCX, TXT, image.`);
      }
      setPages(pgs);
      setPhase('idle');
    } catch (e) {
      setParseError(e.message || String(e));
      setPhase('idle');
    }
  };

  // Parse every page sequentially through /api/underwriting/parse-page.
  // Sequential (not parallel) so Anthropic rate limits don't fire on a 30-page sheet.
  const runParse = async () => {
    if (pages.length === 0) return;
    setPhase('parsing'); setProgress({ done: 0, total: pages.length }); setParseError('');
    const aggCells = []; const aggBuild = []; const aggOverviews = [];
    const nextPages = pages.map(p => ({ ...p, status: 'parsing', parsed: null, error: '' }));
    setPages(nextPages);

    for (let i = 0; i < nextPages.length; i++) {
      const p = nextPages[i];
      try {
        const body = p.kind === 'image'
          ? { imageBase64: p.dataUrl, pageNumber: p.index, useOpus }
          : { text:        p.text,    pageNumber: p.index, useOpus };
        const r = await apiFetch('/api/underwriting/parse-page', { method: 'POST', body: JSON.stringify(body) });
        const cells  = Array.isArray(r.cells)            ? r.cells            : [];
        const builds = Array.isArray(r.buildRows)        ? r.buildRows        : [];
        const overs  = Array.isArray(r.productOverviews) ? r.productOverviews : [];
        nextPages[i] = { ...p, status: 'done', parsed: { cells: cells.length, builds: builds.length, overs: overs.length }, error: '' };
        aggCells.push(...cells); aggBuild.push(...builds); aggOverviews.push(...overs);
      } catch (e) {
        nextPages[i] = { ...p, status: 'error', error: e.message || String(e) };
      }
      setPages([...nextPages]);
      setProgress({ done: i + 1, total: nextPages.length });
    }

    setParsedCells(aggCells);
    setParsedBuildRows(aggBuild);
    setParsedOverviews(aggOverviews);
    setPhase('preview');
    if (aggCells.length === 0 && aggBuild.length === 0 && aggOverviews.length === 0) {
      setParseError('Parser returned nothing on every page. Try re-running with Opus, or check the file is actually a cheat sheet.');
    }
  };

  const commit = async () => {
    if (parsedCells.length === 0 && parsedBuildRows.length === 0 && parsedOverviews.length === 0) {
      showToast('Nothing parsed to commit.'); return;
    }
    setPhase('committing');
    try {
      const r = await apiFetch('/api/underwriting/commit-sheet', {
        method: 'POST',
        body: JSON.stringify({
          filename, pageCount: pages.length,
          cells: parsedCells, buildRows: parsedBuildRows, productOverviews: parsedOverviews,
        }),
      });
      showToast(`Committed: ${r.counts?.cells || 0} cells, ${r.counts?.buildRows || 0} build rows, ${r.counts?.productOverviews || 0} overviews`);
      reset();
      onRefresh();
    } catch (e) {
      showToast(`Commit failed: ${e.message}`);
      setPhase('preview');
    }
  };

  const remove = async (id) => {
    if (!(await hqConfirm({
      title: 'Delete this cheat sheet?',
      body: 'All its parsed cells, build rows, and overviews will be removed.',
      confirmLabel: 'Delete cheat sheet',
      danger: true,
    }))) return;
    try {
      await apiFetch('/api/underwriting/delete', { method: 'POST', body: JSON.stringify({ id }) });
      showToast('Deleted');
      onRefresh();
    } catch (e) {
      showToast(`Delete failed: ${e.message}`);
    }
  };

  // Preview-grid view: cells grouped by condition, columns by carrier+product.
  const previewGrid = React.useMemo(() => {
    if (parsedCells.length === 0) return null;
    const conditions = Array.from(new Set(parsedCells.map(c => c.condition))).sort();
    const colKeys = Array.from(new Set(parsedCells.map(c => `${c.carrier}||${c.product}`))).sort();
    const colHeaders = colKeys.map(k => { const [c, p] = k.split('||'); return { key: k, carrier: c, product: p }; });
    const byCell = {};
    parsedCells.forEach(c => { byCell[`${c.condition}|||${c.carrier}||${c.product}`] = c.decision_text; });
    return { conditions, colHeaders, byCell };
  }, [parsedCells]);

  return (
    <div className="space-y-4">
      {/* Upload card */}
      <div className="rounded-xl p-5" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0' }}>
        <h3 className="font-bold mb-3" style={{ color: UW_GOLD }}>Upload a cheat sheet</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div className="md:col-span-2">
            <label className="block">
              <span className="text-xs uppercase tracking-wide" style={{ color: '#64748B' }}>File — PDF / DOCX / TXT / image</span>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.txt,image/*"
                onChange={e => handleFilePick(e.target.files?.[0])}
                disabled={phase === 'parsing' || phase === 'rendering' || phase === 'committing'}
                className="mt-1 w-full text-sm"
                style={{ color: '#0F172A' }}
              />
            </label>
          </div>
          <label className="inline-flex items-center gap-2 text-xs" style={{ color: '#64748B' }}>
            <input type="checkbox" checked={useOpus} onChange={e => setUseOpus(e.target.checked)} className="rounded"/>
            Use Opus for ingest (max accuracy)
          </label>
        </div>
        {filename && (
          <div className="mt-3 text-xs" style={{ color: '#64748B' }}>
            <strong>{filename}</strong> — {pages.length} page{pages.length === 1 ? '' : 's'} prepared.
          </div>
        )}
        {pages.length > 0 && phase === 'idle' && (
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={reset} className="px-3 py-1.5 text-xs rounded-lg" style={{ color: '#64748B' }}>Discard</button>
            <button onClick={runParse} className="px-4 py-2 text-sm font-bold rounded-lg" style={{ backgroundColor: UW_GOLD, color: UW_DARK }}>
              Parse {pages.length} page{pages.length === 1 ? '' : 's'} with {useOpus ? 'Opus' : 'Sonnet'}
            </button>
          </div>
        )}
      </div>

      {/* Parsing progress */}
      {(phase === 'parsing' || phase === 'rendering') && (
        <div className="rounded-xl p-5 text-center" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0' }}>
          <div className="text-sm font-semibold" style={{ color: UW_GOLD }}>
            {phase === 'rendering' ? 'Rendering pages…' : `Parsing page ${progress.done} of ${progress.total}…`}
          </div>
          <div className="text-xs mt-1" style={{ color: '#64748B' }}>Using {useOpus ? 'high-accuracy' : 'fast'} mode. PDF pages render to images and are read by AI vision (the text layer in these grids is scrambled).</div>
          {pages.length > 0 && (
            <div className="mt-3 space-y-1 max-h-48 overflow-y-auto text-left text-xs">
              {pages.map(p => {
                const dot = p.status === 'done' ? '#10b981' : p.status === 'parsing' ? UW_GOLD : p.status === 'error' ? '#ef4444' : '#64748b';
                return (
                  <div key={p.index} className="flex items-center gap-2 rounded p-1.5" style={{ backgroundColor: '#FFFFFF' }}>
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: dot }}/>
                    <span style={{ color: '#64748B' }}>Page {p.index}</span>
                    <span className="ml-auto" style={{ color: '#94a3b8' }}>
                      {p.status === 'done' && p.parsed && <span>{p.parsed.cells} cells · {p.parsed.builds} build · {p.parsed.overs} overviews</span>}
                      {p.status === 'parsing' && 'reading…'}
                      {p.status === 'error' && <span style={{ color: '#B91C1C' }}>{p.error}</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {parseError && phase !== 'parsing' && (
        <div className="rounded-lg p-3 text-sm" style={{ backgroundColor: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA' }}>{parseError}</div>
      )}

      {/* Preview */}
      {phase === 'preview' && (
        <div className="rounded-xl p-5" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0' }}>
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div>
              <h3 className="font-bold" style={{ color: UW_GOLD }}>Preview — review before committing</h3>
              <div className="text-xs mt-1" style={{ color: '#64748B' }}>
                {parsedCells.length} cells · {parsedBuildRows.length} build rows · {parsedOverviews.length} product overviews ·
                {' '}{previewGrid?.colHeaders.length || 0} carrier/product columns ·
                {' '}{previewGrid?.conditions.length || 0} conditions
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={runParse} className="px-3 py-1.5 text-xs font-semibold rounded-lg" style={{ backgroundColor: '#FFFFFF', color: UW_GOLD, border: '1px solid #A7F3D0' }}>
                Re-parse {useOpus ? '(Opus)' : '(Sonnet)'}
              </button>
              <button onClick={reset} className="px-3 py-1.5 text-xs rounded-lg" style={{ color: '#64748B' }}>Discard</button>
              <button onClick={commit} disabled={phase === 'committing'} className="px-4 py-2 text-sm font-bold rounded-lg disabled:opacity-50" style={{ backgroundColor: UW_GOLD, color: UW_DARK }}>
                Commit to library
              </button>
            </div>
          </div>
          {previewGrid && previewGrid.conditions.length > 0 && (
            <div className="overflow-auto rounded-lg max-h-[60vh]" style={{ backgroundColor: '#FFFFFF' }}>
              <table className="text-xs">
                <thead>
                  <tr>
                    <th className="px-2 py-1.5 sticky left-0 top-0 z-10 text-left whitespace-nowrap" style={{ backgroundColor: UW_DARK, color: UW_GOLD, borderBottom: `1px solid ${UW_GOLD}66` }}>Condition</th>
                    {previewGrid.colHeaders.map(h => (
                      <th key={h.key} className="px-2 py-1.5 text-left sticky top-0 whitespace-nowrap" style={{ backgroundColor: UW_DARK, color: UW_GOLD, borderBottom: `1px solid ${UW_GOLD}66`, minWidth: 160 }}>
                        <div className="font-bold">{h.carrier}</div>
                        <div className="font-normal opacity-80">{h.product}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewGrid.conditions.map(cond => (
                    <tr key={cond}>
                      <td className="px-2 py-1.5 sticky left-0 font-semibold whitespace-nowrap" style={{ backgroundColor: '#FFFFFF', color: '#0F172A', borderRight: `1px solid ${UW_GOLD}33` }}>{cond}</td>
                      {previewGrid.colHeaders.map(h => {
                        const v = previewGrid.byCell[`${cond}|||${h.carrier}||${h.product}`];
                        return (
                          <td key={h.key} className="px-2 py-1.5 align-top text-[11px]" style={{ color: v ? '#0F172A' : '#CBD5E1', borderRight: '1px solid #E2E8F0', borderBottom: '1px solid #E2E8F0' }}>
                            {v || '—'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {parsedBuildRows.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color: UW_GOLD }}>Build / BMI rows ({parsedBuildRows.length})</div>
              <div className="overflow-auto rounded-lg max-h-48" style={{ backgroundColor: '#FFFFFF' }}>
                <table className="text-xs w-full">
                  <thead>
                    <tr>
                      {['Carrier', 'Product', 'Height', 'Min lbs', 'Max lbs', 'Rating', 'Notes'].map(h => (
                        <th key={h} className="px-2 py-1 text-left" style={{ color: UW_GOLD, borderBottom: `1px solid ${UW_GOLD}55` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {parsedBuildRows.slice(0, 80).map((b, i) => (
                      <tr key={i}>
                        <td className="px-2 py-1" style={{ color: '#0F172A' }}>{b.carrier}</td>
                        <td className="px-2 py-1" style={{ color: '#64748B' }}>{b.product}</td>
                        <td className="px-2 py-1" style={{ color: '#64748B' }}>{b.height_inches}"</td>
                        <td className="px-2 py-1" style={{ color: '#64748B' }}>{b.min_weight ?? '—'}</td>
                        <td className="px-2 py-1" style={{ color: '#64748B' }}>{b.max_weight ?? '—'}</td>
                        <td className="px-2 py-1" style={{ color: '#64748B' }}>{b.table_rating || '—'}</td>
                        <td className="px-2 py-1" style={{ color: '#94a3b8' }}>{b.notes || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Library list */}
      <div className="rounded-xl p-5" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0' }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold" style={{ color: UW_GOLD }}>Library ({sheets.length})</h3>
          <button onClick={onRefresh} disabled={loading} className="text-xs underline" style={{ color: '#64748B' }}>{loading ? 'Refreshing…' : 'Refresh'}</button>
        </div>
        {sheets.length === 0 && !loading && (
          <div className="text-sm italic" style={{ color: '#94a3b8' }}>No sheets uploaded yet. Drop a multi-carrier grid PDF above to build the agency's knowledge base.</div>
        )}
        {sheets.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="py-2 pr-3 text-left text-xs uppercase tracking-wide" style={{ color: UW_GOLD, borderBottom: `1px solid ${UW_GOLD}55` }}>Filename</th>
                  <th className="py-2 px-2 text-right text-xs uppercase tracking-wide" style={{ color: UW_GOLD, borderBottom: `1px solid ${UW_GOLD}55` }}>Pages</th>
                  <th className="py-2 px-2 text-right text-xs uppercase tracking-wide" style={{ color: UW_GOLD, borderBottom: `1px solid ${UW_GOLD}55` }}>Cells</th>
                  <th className="py-2 px-2 text-right text-xs uppercase tracking-wide" style={{ color: UW_GOLD, borderBottom: `1px solid ${UW_GOLD}55` }}>Build rows</th>
                  <th className="py-2 px-2 text-left text-xs uppercase tracking-wide" style={{ color: UW_GOLD, borderBottom: `1px solid ${UW_GOLD}55` }}>Uploader</th>
                  <th className="py-2 px-2 text-left text-xs uppercase tracking-wide" style={{ color: UW_GOLD, borderBottom: `1px solid ${UW_GOLD}55` }}>Uploaded</th>
                  <th className="py-2 pl-2 text-right" style={{ borderBottom: `1px solid ${UW_GOLD}55` }}/>
                </tr>
              </thead>
              <tbody>
                {sheets.map(s => (
                  <tr key={s.id} className="border-b" style={{ borderColor: '#E2E8F0' }}>
                    <td className="py-2 pr-3 font-medium truncate max-w-xs" style={{ color: '#0F172A' }} title={s.filename}>{s.filename || '—'}</td>
                    <td className="py-2 px-2 text-right tabular-nums" style={{ color: '#64748B' }}>{s.page_count || 0}</td>
                    <td className="py-2 px-2 text-right tabular-nums" style={{ color: '#64748B' }}>{s.cell_count || 0}</td>
                    <td className="py-2 px-2 text-right tabular-nums" style={{ color: '#64748B' }}>{s.build_row_count || 0}</td>
                    <td className="py-2 px-2 text-xs" style={{ color: '#94a3b8' }}>{s.uploader_email || '—'}</td>
                    <td className="py-2 px-2 text-xs" style={{ color: '#94a3b8' }}>{s.uploaded_at ? new Date(s.uploaded_at).toLocaleDateString() : '—'}</td>
                    <td className="py-2 pl-2 text-right">
                      <button onClick={() => remove(s.id)} className="text-xs underline" style={{ color: '#B91C1C' }}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Lookup tab ─────────────────────────────────────────────────────
function UnderwritingLookup({ sheetsCount = 0, showToast }) {
  const [scenario, setScenario]     = useState('');
  const [heightFt, setHeightFt]     = useState('');
  const [heightIn, setHeightIn]     = useState('');
  const [weightLbs, setWeightLbs]   = useState('');
  const [carriersTxt, setCarriersTxt] = useState('');
  const [useOpus, setUseOpus]       = useState(false);
  const [busy, setBusy]             = useState(false);
  const [result, setResult]         = useState(null);
  const [error, setError]           = useState('');

  const submit = async () => {
    setError(''); setResult(null);
    if (!scenario.trim() || scenario.trim().length < 4) { showToast('Type a client scenario first'); return; }
    const hi = Number(heightFt) > 0 ? Number(heightFt) * 12 + (Number(heightIn) || 0) : null;
    const carriers = carriersTxt.split(',').map(s => s.trim()).filter(Boolean);
    setBusy(true);
    try {
      const r = await apiFetch('/api/underwriting/lookup', {
        method: 'POST',
        body: JSON.stringify({
          scenario, heightInches: hi, weightLbs: Number(weightLbs) || undefined,
          carriers: carriers.length > 0 ? carriers : undefined,
          useOpus,
        }),
      });
      setResult(r);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl p-5" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0' }}>
        <h3 className="font-bold mb-1" style={{ color: UW_GOLD }}>Client scenario</h3>
        <p className="text-xs mb-3" style={{ color: '#64748B' }}>Type it like you'd say it out loud. Include conditions, severity, treatments, time since events, and any meds you've collected.</p>
        <textarea
          value={scenario}
          onChange={e => setScenario(e.target.value)}
          rows={4}
          placeholder={'e.g. "57yo male, Type 2 diabetes diagnosed age 45, insulin, A1C 7.8, controlled hypertension"\nor "Takes Eliquis, Lisinopril, metformin"\nor "Single DUI 3 years ago, otherwise clean"'}
          className="w-full rounded-lg px-3 py-2 text-sm"
          style={{ backgroundColor: UW_DARK, color: '#0F172A', border: `1px solid ${UW_GOLD}44` }}
        />
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <label>
            <span className="uppercase tracking-wide" style={{ color: '#64748B' }}>Height</span>
            <div className="flex gap-1 mt-1">
              <input value={heightFt} onChange={e => setHeightFt(e.target.value)} placeholder="ft" className="w-full rounded-lg px-2 py-1.5 text-sm" style={{ backgroundColor: UW_DARK, color: '#0F172A', border: `1px solid ${UW_GOLD}33` }}/>
              <input value={heightIn} onChange={e => setHeightIn(e.target.value)} placeholder="in" className="w-full rounded-lg px-2 py-1.5 text-sm" style={{ backgroundColor: UW_DARK, color: '#0F172A', border: `1px solid ${UW_GOLD}33` }}/>
            </div>
          </label>
          <label>
            <span className="uppercase tracking-wide" style={{ color: '#64748B' }}>Weight (lbs)</span>
            <input value={weightLbs} onChange={e => setWeightLbs(e.target.value)} placeholder="240" className="mt-1 w-full rounded-lg px-2 py-1.5 text-sm" style={{ backgroundColor: UW_DARK, color: '#0F172A', border: `1px solid ${UW_GOLD}33` }}/>
          </label>
          <label className="md:col-span-2">
            <span className="uppercase tracking-wide" style={{ color: '#64748B' }}>Filter carriers (comma-separated, optional)</span>
            <input value={carriersTxt} onChange={e => setCarriersTxt(e.target.value)} placeholder="Mutual of Omaha, Americo" className="mt-1 w-full rounded-lg px-2 py-1.5 text-sm" style={{ backgroundColor: UW_DARK, color: '#0F172A', border: `1px solid ${UW_GOLD}33` }}/>
          </label>
        </div>
        <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
          <label className="inline-flex items-center gap-2 text-xs" style={{ color: '#64748B' }}>
            <input type="checkbox" checked={useOpus} onChange={e => setUseOpus(e.target.checked)} className="rounded"/>
            Use Opus (max accuracy)
          </label>
          <div className="flex items-center gap-3 text-xs" style={{ color: '#94a3b8' }}>
            <span>Library: {sheetsCount} sheet{sheetsCount === 1 ? '' : 's'}</span>
            <button onClick={submit} disabled={busy || !scenario.trim()} className="px-4 py-2 text-sm font-bold rounded-lg disabled:opacity-50" style={{ backgroundColor: UW_GOLD, color: UW_DARK }}>
              {busy ? 'Asking Claude…' : 'Run lookup'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg p-3 text-sm whitespace-pre-wrap" style={{ backgroundColor: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA' }}>{error}</div>
      )}

      {busy && (
        <div className="rounded-xl p-8 text-center" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0' }}>
          <div className="font-bold" style={{ color: UW_GOLD }}>Reading the library…</div>
          <div className="text-xs mt-1" style={{ color: '#64748B' }}>Cross-referencing cells, build charts, and product overviews with the {useOpus ? 'high-accuracy' : 'fast'} model.</div>
        </div>
      )}

      {result?.libraryEmpty && (
        <div className="rounded-xl p-4 text-sm" style={{ backgroundColor: '#FFFBEB', color: '#78350F', border: '1px solid #FCD34D' }}>{result.message}</div>
      )}

      {result && !result.libraryEmpty && <UnderwritingResults result={result}/>}
    </div>
  );
}

// ── Results renderer (the intelligence layer made visible) ─────────
function UnderwritingResults({ result }) {
  if (!result) return null;
  const play = result.playRecommendation;
  const hasCombos = (result.combinationWarnings || []).length > 0;
  const hasInferences = (result.medicationInferences || []).length > 0;
  const hasQuestions = (result.smartIntakeQuestions || []).length > 0;
  const hasBuildWarn = (result.buildWarnings || []).length > 0;
  const hasLimits = (result.coverageLimits || []).length > 0;

  return (
    <div className="space-y-4">
      {/* Play recommendation — top */}
      {play && play.summary && (
        <div className="rounded-xl p-4" style={{ backgroundColor: UW_GOLD, color: UW_DARK }}>
          <div className="text-xs uppercase tracking-wide font-bold">The play</div>
          <div className="mt-1 text-sm font-semibold">{play.summary}</div>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            {play.leadCarrier && (
              <div className="rounded p-2" style={{ backgroundColor: 'rgba(255,255,255,0.18)' }}>
                <div className="font-bold uppercase tracking-wide">Lead</div>
                <div className="mt-0.5 font-bold">{play.leadCarrier}{play.leadProduct ? ` — ${play.leadProduct}` : ''}</div>
                {play.leadReason && <div className="opacity-90 mt-0.5">{play.leadReason}</div>}
              </div>
            )}
            {play.backupCarrier && (
              <div className="rounded p-2" style={{ backgroundColor: 'rgba(255,255,255,0.18)' }}>
                <div className="font-bold uppercase tracking-wide">Backup</div>
                <div className="mt-0.5 font-bold">{play.backupCarrier}{play.backupProduct ? ` — ${play.backupProduct}` : ''}</div>
              </div>
            )}
            {Array.isArray(play.skipCarriers) && play.skipCarriers.length > 0 && (
              <div className="rounded p-2" style={{ backgroundColor: 'rgba(255,255,255,0.18)' }}>
                <div className="font-bold uppercase tracking-wide">Skip</div>
                <div className="mt-0.5">{play.skipCarriers.join(', ')}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Combination warnings */}
      {hasCombos && (
        <div className="rounded-xl p-4" style={{ backgroundColor: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA' }}>
          <div className="text-xs uppercase tracking-wide font-bold">⚠ Combination knockouts ({result.combinationWarnings.length})</div>
          <ul className="mt-2 space-y-1.5">
            {result.combinationWarnings.map((c, i) => (
              <li key={i} className="text-sm">
                <span className="font-bold">{c.title}</span> — {c.explanation}
                {c.source && <span className="block text-xs italic opacity-80 mt-0.5">Source: {c.source}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Build warnings */}
      {hasBuildWarn && (
        <div className="rounded-xl p-4" style={{ backgroundColor: '#FFFBEB', color: '#78350F', border: '1px solid #F59E0B' }}>
          <div className="text-xs uppercase tracking-wide font-bold">Build / BMI knockouts ({result.buildWarnings.length})</div>
          <ul className="mt-2 space-y-1 text-sm">
            {result.buildWarnings.map((b, i) => <li key={i}>{b.carrier}{b.product ? ` — ${b.product}` : ''}: {b.explanation}</li>)}
          </ul>
        </div>
      )}

      {/* Per-carrier verdicts grid */}
      {result.verdicts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {result.verdicts.map((v, i) => {
            const s = UW_STATUS_STYLES[v.status] || UW_STATUS_STYLES['Needs Review'];
            return (
              <div key={i} className={`rounded-xl p-4 ${s.bg} ${s.border} border-2`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide" style={{ color: s.dot }}>
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.dot }}/>
                    {s.label}
                  </span>
                  {v.confidence && <span className="text-[10px] uppercase tracking-wide text-slate-500">conf: {v.confidence}</span>}
                </div>
                <div className="font-bold text-slate-900">{v.carrier || '—'}</div>
                <div className="text-xs text-slate-600 mb-2">{v.product || ''}</div>
                {v.rateClass && (
                  <div className={`text-xs font-semibold ${s.text} mb-1`}>Rate / route: {v.rateClass}</div>
                )}
                <div className="text-sm text-slate-800 whitespace-pre-wrap">{v.reasoning || '—'}</div>
                {v.source && <div className="text-[10px] text-slate-500 mt-2 italic">Source: {v.source}</div>}
              </div>
            );
          })}
        </div>
      )}

      {/* Coverage limits */}
      {hasLimits && (
        <div className="rounded-xl p-4" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0' }}>
          <div className="text-xs uppercase tracking-wide font-bold" style={{ color: UW_GOLD }}>Max face + exam thresholds</div>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  {['Carrier', 'Product', 'Max face', 'Exam at', 'Notes'].map(h => (
                    <th key={h} className="py-1 text-left text-xs" style={{ color: '#64748B' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.coverageLimits.map((c, i) => (
                  <tr key={i}>
                    <td className="py-1 pr-2" style={{ color: '#0F172A' }}>{c.carrier}</td>
                    <td className="py-1 pr-2" style={{ color: '#64748B' }}>{c.product}</td>
                    <td className="py-1 pr-2 tabular-nums" style={{ color: '#0F172A' }}>{c.maxFace != null ? `$${Number(c.maxFace).toLocaleString()}` : '—'}</td>
                    <td className="py-1 pr-2 tabular-nums" style={{ color: '#64748B' }}>{c.examThreshold != null ? `$${Number(c.examThreshold).toLocaleString()}` : '—'}</td>
                    <td className="py-1 pr-2 text-xs" style={{ color: '#94a3b8' }}>{c.notes || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Medication inferences — flagged */}
      {hasInferences && (
        <div className="rounded-xl p-4" style={{ backgroundColor: '#F0FDFA', color: '#0F766E', border: '1px solid #5EEAD4' }}>
          <div className="text-xs uppercase tracking-wide font-bold">Medication inferences (CONFIRM with client)</div>
          <ul className="mt-2 space-y-1.5 text-sm">
            {result.medicationInferences.map((m, i) => (
              <li key={i}>
                <span className="font-bold">{m.medication}</span> → likely <span className="font-semibold">{m.likelyCondition}</span>
                {m.confirmAction && <span className="block text-xs opacity-80 mt-0.5">{m.confirmAction}</span>}
              </li>
            ))}
          </ul>
          <div className="mt-2 text-[10px] italic opacity-80">These mappings are the AI's inference, NOT carrier rules. Confirm with the client before quoting.</div>
        </div>
      )}

      {/* Smart intake questions */}
      {hasQuestions && (
        <div className="rounded-xl p-4" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0' }}>
          <div className="text-xs uppercase tracking-wide font-bold" style={{ color: UW_GOLD }}>Ask the client</div>
          <ol className="mt-2 list-decimal list-inside space-y-1 text-sm" style={{ color: '#0F172A' }}>
            {result.smartIntakeQuestions.map((q, i) => <li key={i}>{q}</li>)}
          </ol>
        </div>
      )}

      <div className="text-xs text-right" style={{ color: '#94a3b8' }}>
        {result.sheetsConsulted ?? '?'} sheet{result.sheetsConsulted === 1 ? '' : 's'} · {result.cellsConsidered ?? '?'} cells · model: {result.modelUsed}
      </div>
    </div>
  );
}

function AgentRow({ p, producers, settings, expanded, onToggle, isMineTeam, policies = [], openClientProfile = () => {} }) {
  const carriers = sortByCarrierOrder(Object.entries(p.carriers || {}), x => x[0]);
  const hasMultipleCarriers = carriers.length > 1;
  const persistencyColor = p.persistency >= 85 ? 'emerald' : p.persistency >= 70 ? 'amber' : 'red';
  const showPersistency = p.gross > 0 || p.cb < 0;
  return (
    <div>
      <button onClick={onToggle} className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors text-left">
        <ChevronRight size={16} className={`text-slate-400 transition-transform flex-shrink-0 ${expanded?'rotate-90':''}`}/>
        <div className="flex-1 min-w-0">
          <div className="font-semibold flex items-center gap-2">
            {getProducerName(p.prodNum, producers)}
            {p.isProblem && (
              <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium">⚠ problem</span>
            )}
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
            <span className="font-mono">{p.prodNum}</span>
            <span>·</span>
            <span>{p.newIssueCount} new · {p.chargebackCount} cb</span>
            {showPersistency && (<>
              <span>·</span>
              <span className={`font-semibold text-${persistencyColor}-600`}>{p.persistency.toFixed(0)}% persistency</span>
              {p.chargebackRate > 0 && <span className="text-red-500">({p.chargebackRate.toFixed(0)}% cb rate)</span>}
            </>)}
            {carriers.length > 0 && <>
              <span>·</span>
              <span>{carriers.length} carrier{carriers.length>1?'s':''}: {carriers.map(([name])=>name).join(', ')}</span>
            </>}
          </div>
        </div>
        <div className="flex-shrink-0 text-right">
          <div className="text-xs text-slate-500">Net commission</div>
          <div className="font-bold text-lg">{fmt(p.net)}</div>
        </div>
        <div className="flex-shrink-0 text-right border-l border-slate-200 pl-3">
          <div className="text-xs text-slate-500">Premium written</div>
          <div className="font-bold text-slate-700">{fmt(p.premiumPositive)}</div>
        </div>
      </button>

      {expanded && (
        <div className="bg-slate-50 px-4 py-3 pl-12">
          {/* Persistency summary */}
          {showPersistency && (
            <div className="mb-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-white rounded-lg border border-slate-200 p-3">
                <div className="text-xs text-slate-500">Gross commission</div>
                <div className="font-bold">{fmt(p.gross)}</div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-3">
                <div className="text-xs text-red-600">Chargebacks</div>
                <div className="font-bold text-red-600">{fmt(p.cb)}</div>
                <div className="text-xs text-slate-500 mt-0.5">{p.chargebackRate.toFixed(1)}% of gross</div>
              </div>
              <div className={`bg-white rounded-lg border border-${persistencyColor}-300 p-3`}>
                <div className={`text-xs text-${persistencyColor}-700`}>Persistency</div>
                <div className={`font-bold text-${persistencyColor}-700`}>{p.persistency.toFixed(1)}%</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {p.persistency >= 85 ? 'healthy' : p.persistency >= 70 ? 'watch' : 'needs attention'}
                </div>
              </div>
            </div>
          )}
          {carriers.length > 0 && (<>
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Carrier breakdown</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500 text-left border-b border-slate-200">
                  <th className="py-2 pr-3">Carrier</th>
                  <th className="py-2 pr-3 text-right">Policies</th>
                  <th className="py-2 pr-3 text-right">Premium written</th>
                  <th className="py-2 pr-3 text-right">Gross</th>
                  <th className="py-2 pr-3 text-right">CB</th>
                  <th className="py-2 pr-3 text-right">Persistency</th>
                  <th className="py-2 pr-3 text-right">Net</th>
                </tr>
              </thead>
              <tbody>
                {carriers.map(([name, c]) => {
                  const pColor = c.persistency >= 85 ? 'emerald' : c.persistency >= 70 ? 'amber' : 'red';
                  return (
                  <tr key={name} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 pr-3 font-medium">{name}</td>
                    <td className="py-2 pr-3 text-right">{c.count}</td>
                    <td className="py-2 pr-3 text-right">
                      <div>{fmt(c.premiumPositive)}</div>
                      {c.premiumNegative > 0 && <div className="text-xs text-red-500">−{fmt(c.premiumNegative)}</div>}
                    </td>
                    <td className="py-2 pr-3 text-right">{fmt(c.gross)}</td>
                    <td className="py-2 pr-3 text-right text-red-600">{fmt(c.cb)}</td>
                    <td className={`py-2 pr-3 text-right text-${pColor}-600 font-medium`}>{c.gross > 0 || c.cb < 0 ? `${c.persistency.toFixed(0)}%` : '—'}</td>
                    <td className="py-2 pr-3 text-right font-semibold">{fmt(c.net)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>)}
          <AgentPolicyList policies={policies} openClientProfile={openClientProfile}/>
        </div>
      )}
    </div>
  );
}

function AgentPolicyList({ policies, openClientProfile = () => {} }) {
  const [open, setOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | lapsed
  if (!policies || policies.length === 0) return null;

  // Group by carrier
  const byCarrier = {};
  policies.forEach(p => {
    if (!byCarrier[p.carrier]) byCarrier[p.carrier] = [];
    byCarrier[p.carrier].push(p);
  });
  const carrierEntries = sortByCarrierOrder(Object.entries(byCarrier), x => x[0]);

  const totalActive = policies.filter(p => p.status === 'active').length;
  const totalLapsed = policies.length - totalActive;
  const totalPremium = policies.reduce((s, p) => s + (p.premium || 0), 0);
  const activePremium = policies.filter(p => p.status === 'active').reduce((s, p) => s + (p.premium || 0), 0);

  return (
    <div className="mt-4 bg-white rounded-lg border border-slate-200 overflow-hidden">
      <button onClick={()=>setOpen(o => !o)} className="w-full flex items-center justify-between p-3 hover:bg-slate-50">
        <div className="flex items-center gap-2">
          <ChevronRight size={14} className={`text-slate-400 transition-transform ${open?'rotate-90':''}`}/>
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Book of business · {policies.length} polic{policies.length===1?'y':'ies'}</div>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span><span className="text-emerald-700 font-semibold">{totalActive}</span> active · <span className="text-red-700 font-semibold">{totalLapsed}</span> lapsed</span>
          <span>·</span>
          <span>{fmt(activePremium)} active premium</span>
        </div>
      </button>
      {open && (
        <div className="border-t border-slate-200">
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs">
            <span className="text-slate-500">Filter:</span>
            <div className="inline-flex rounded border border-slate-200 bg-white overflow-hidden">
              {['all','active','lapsed'].map(f => (
                <button key={f} onClick={()=>setStatusFilter(f)} className={`px-2 py-1 ${statusFilter===f?'bg-slate-900 text-white':'text-slate-600 hover:bg-slate-100'}`}>{f}</button>
              ))}
            </div>
            <span className="ml-auto text-slate-500">Total premium written: <strong className="text-slate-900">{fmt(totalPremium)}</strong></span>
          </div>
          {carrierEntries.map(([carrier, list]) => {
            const visible = list.filter(p => statusFilter === 'all' || p.status === statusFilter);
            if (visible.length === 0) return null;
            const active = list.filter(p => p.status === 'active').length;
            const lapsed = list.length - active;
            const cPremium = list.reduce((s, p) => s + (p.premium || 0), 0);
            const carrierColor = carrier === 'Americo' ? 'emerald' : carrier === 'MOO' ? 'violet' : carrier === 'Transamerica' ? 'amber' : 'slate';
            return (
              <div key={carrier} className="border-b border-slate-100 last:border-0">
                <div className={`flex items-center gap-3 px-3 py-2 bg-${carrierColor}-50`}>
                  <div className={`w-1.5 h-1.5 rounded-full bg-${carrierColor}-500`}/>
                  <div className={`font-semibold text-sm text-${carrierColor}-900`}>{carrier}</div>
                  <div className="text-xs text-slate-500">{list.length} polic{list.length===1?'y':'ies'} · <span className="text-emerald-700">{active} active</span>{lapsed>0 && <> · <span className="text-red-700">{lapsed} lapsed</span></>}</div>
                  <div className="ml-auto text-xs text-slate-500">{fmt(cPremium)} written</div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-slate-500 border-b border-slate-100">
                        <th className="py-1.5 px-3 text-left font-medium">Status</th>
                        <th className="py-1.5 px-3 text-left font-medium">Insured</th>
                        <th className="py-1.5 px-3 text-left font-medium">Policy #</th>
                        <th className="py-1.5 px-3 text-left font-medium">Issue</th>
                        <th className="py-1.5 px-3 text-right font-medium">Days</th>
                        <th className="py-1.5 px-3 text-right font-medium">Premium</th>
                        <th className="py-1.5 px-3 text-right font-medium">Commission</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.slice().sort((a,b)=>{
                        if (a.status !== b.status) return a.status === 'active' ? -1 : 1;
                        return (parseDate(b.issueDate)?.getTime()||0) - (parseDate(a.issueDate)?.getTime()||0);
                      }).map(p => (
                        <tr key={p.key} className="border-b border-slate-50 last:border-0 hover:bg-slate-50">
                          <td className="py-1.5 px-3"><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${p.status==='active'?'bg-emerald-100 text-emerald-700':'bg-red-100 text-red-700'}`}>{p.status==='active'?'ACTIVE':'LAPSED'}</span></td>
                          <td className="py-1.5 px-3">
                            <button onClick={()=>openClientProfile(clientKeyOf(p.insured))} className="text-emerald-700 hover:text-emerald-900 hover:underline font-medium text-left" title="View full profile">
                              {titleCase(p.insured)}
                            </button>
                            {p.state && <span className="text-slate-400 ml-1">({p.state})</span>}
                          </td>
                          <td className="py-1.5 px-3 font-mono text-[11px]">{p.policy}</td>
                          <td className="py-1.5 px-3">{p.issueDate || '—'}</td>
                          <td className="py-1.5 px-3 text-right">{p.daysInForce != null ? `${p.daysInForce}d` : '—'}</td>
                          <td className="py-1.5 px-3 text-right">{p.premium > 0 ? fmt(p.premium) : '—'}</td>
                          <td className={`py-1.5 px-3 text-right font-semibold ${p.totalCommission >= 0 ? 'text-slate-700' : 'text-red-600'}`}>{fmt(p.totalCommission)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TeamsView({ agg, monthlyAgg, periodMode, settings, transactions = [], producers = {}, getProduct = () => 'Other', getCarrier = () => 'Unknown', openClientProfile = () => {} }) {
  // ── Product filter ─────────────────────────────────────────────
  // Display-time only. "All" reproduces the parent's numbers exactly
  // (we render agg/monthlyAgg as-is). Any other filter swaps to a
  // locally-aggregated subset of the same `transactions` array — no
  // shared mutation, no payout-math change.
  const [productFilter, setProductFilter] = React.useState('All');

  // Per-agent product breakdown — built once per transactions change.
  // Always uses the full transactions, never filtered, so the agent
  // table shows the complete picture and product columns are accurate.
  const perAgentProduct = React.useMemo(() => {
    const m = {};
    transactions.forEach(tx => {
      const pn = tx.prodNum || 'unknown';
      const product = getProduct(tx);
      const net = Number(tx.netToAccount) || 0;
      if (!m[pn]) m[pn] = { prodNum: pn, name: getProducerName(pn, producers), total: 0, IUL: 0, 'Final Expense': 0, Term: 0, Other: 0 };
      m[pn].total += net;
      m[pn][product] = (m[pn][product] || 0) + net;
    });
    return Object.values(m)
      .filter(r => productFilter === 'All' || Math.abs(r[productFilter] || 0) > 0.005)
      .sort((a, b) => (productFilter === 'All' ? b.total - a.total : (b[productFilter] || 0) - (a[productFilter] || 0)));
  }, [transactions, productFilter, getProduct, producers]);

  // Policies that fell into "Other" — surfaced at the bottom so the user
  // can extend the rules or POLICY_PLAN map. One row per unique policy +
  // carrier so duplicates from multiple txns don't spam.
  const otherPolicies = React.useMemo(() => {
    const seen = new Map();
    transactions.forEach(tx => {
      if (getProduct(tx) !== 'Other') return;
      const key = `${getCarrier(tx)}|${tx.policy || ''}`;
      if (!seen.has(key)) {
        seen.set(key, {
          carrier: getCarrier(tx),
          policy: tx.policy || '',
          planCode: tx.planCode || POLICY_PLAN[tx.policy] || '',
          insured: tx.insured || '',
        });
      }
    });
    return Array.from(seen.values()).sort((a, b) => `${a.carrier}|${a.policy}`.localeCompare(`${b.carrier}|${b.policy}`));
  }, [transactions, getProduct, getCarrier]);

  // Local re-aggregation for any non-"All" product filter. Built from the
  // same transactions array the parent used to produce `agg` so the math
  // is identical when filter='All' (we just bypass and use parent's agg).
  const filteredAgg = React.useMemo(() => {
    if (productFilter === 'All') return null; // signal to use parent's agg
    const z = () => ({ gross: 0, chargebacks: 0, net: 0, count: 0 });
    const out = { mine: z(), owner: z(), unassigned: z(), total: z() };
    transactions.forEach(tx => {
      if (getProduct(tx) !== productFilter) return;
      const team = (producers[tx.prodNum]?.team) || 'unassigned';
      const bucket = team === 'owner' ? out.owner : team === 'mine' ? out.mine : out.unassigned;
      const net = Number(tx.netToAccount) || 0;
      const gross = Math.max(0, net);
      const cb = Math.min(0, net);
      bucket.gross += gross; bucket.chargebacks += cb; bucket.net += net; bucket.count++;
      out.total.gross += gross; out.total.chargebacks += cb; out.total.net += net; out.total.count++;
    });
    // Backfill .cam to satisfy the legacy reads below (Cam was ripped out).
    out.cam = z();
    return out;
  }, [productFilter, transactions, producers, getProduct]);

  const displayAgg = filteredAgg || agg;

  // CB % of gross — guard against zero-gross periods
  const mineCbPct = displayAgg.mine.gross > 0 ? (Math.abs(displayAgg.mine.chargebacks) / displayAgg.mine.gross) * 100 : null;
  const camCbPct  = displayAgg.cam?.gross > 0 ? (Math.abs(displayAgg.cam.chargebacks)  / displayAgg.cam.gross) * 100 : null;
  const ownerCbPct = (displayAgg.owner?.gross || 0) > 0 ? (Math.abs(displayAgg.owner.chargebacks) / displayAgg.owner.gross) * 100 : null;
  // Persistency = (gross + chargebacks) / gross, clamped to [0,100]. chargebacks is stored as negative.
  const persistencyFor = (bucket) => {
    if (!bucket || (bucket.gross || 0) === 0) return null;
    return Math.max(0, Math.min(100, ((bucket.gross + bucket.chargebacks) / bucket.gross) * 100));
  };
  const minePersistency = persistencyFor(displayAgg.mine);
  const camPersistency = persistencyFor(displayAgg.cam);
  const ownerPersistency = persistencyFor(displayAgg.owner);
  const camCutMtd = (monthlyAgg[monthlyAgg.length - 1]?.camCutOnMine) || 0;
  const youKeepMtd = displayAgg.mine.net - camCutMtd;
  const ownerNetMtd = displayAgg.owner?.net || 0;

  // Trend table totals across the visible periods
  const totals = monthlyAgg.reduce((t, m) => ({
    mineGross: t.mineGross + (m.mineGross || 0),
    mineCB:    t.mineCB    + (m.mineCB || 0),
    mineNet:   t.mineNet   + (m.mineNet || 0),
    camGross:  t.camGross  + (m.camGross || 0),
    camCB:     t.camCB     + (m.camCB || 0),
    camNet:    t.camNet    + (m.camNet || 0),
    camCutOnMine: t.camCutOnMine + (m.camCutOnMine || 0),
    camTotalPayout: t.camTotalPayout + (m.camTotalPayout || 0),
    youKeep:   t.youKeep   + ((m.mineNet || 0) - (m.camCutOnMine || 0)),
  }), { mineGross:0, mineCB:0, mineNet:0, camGross:0, camCB:0, camNet:0, camCutOnMine:0, camTotalPayout:0, youKeep:0 });

  // Flag periods that are chargeback-only (no positive gross). These are visually distracting
  // if rendered the same as normal months — they mean "we paid back losses but wrote nothing."
  const isCbOnlyPeriod = (m) => (m.mineGross || 0) === 0 && (m.camGross || 0) === 0 && ((m.mineCB || 0) < 0 || (m.camCB || 0) < 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border-2 border-blue-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
            <h3 className="font-bold">Owner Production</h3>
            <PersistencyBadge value={ownerPersistency}/>
            <span className="text-xs text-slate-500 ml-auto">stands alone</span>
          </div>
          <div className="space-y-3">
            <Row label="Gross proceeds" value={fmt(displayAgg.owner?.gross || 0)}/>
            <Row label={ownerCbPct != null ? `Chargebacks (${ownerCbPct.toFixed(1)}% of gross)` : 'Chargebacks'} value={fmt(displayAgg.owner?.chargebacks || 0)} danger/>
            <Row label="Net proceeds" value={fmt(ownerNetMtd)} bold/>
            <PersistencyRow value={ownerPersistency}/>
            <div className="pt-3 border-t border-slate-100">
              <Row label="Keeps 100% — no override" value={fmt(ownerNetMtd)} bold/>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
            <h3 className="font-bold">{possessive(settings.ownerName)} Team</h3>
            <PersistencyBadge value={minePersistency}/>
            <span className="text-xs text-slate-500 ml-auto">non-owner agents</span>
          </div>
          <div className="space-y-3">
            <Row label="Gross proceeds" value={fmt(displayAgg.mine.gross)}/>
            <Row label={mineCbPct != null ? `Chargebacks (${mineCbPct.toFixed(1)}% of gross)` : 'Chargebacks'} value={fmt(displayAgg.mine.chargebacks)} danger/>
            <Row label="Net proceeds" value={fmt(displayAgg.mine.net)} bold/>
            <PersistencyRow value={minePersistency}/>
          </div>
        </div>
      </div>

      {/* ───── Product Type filter chips ───── */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide mr-1">Product:</span>
        {['All', ...PRODUCT_OPTIONS].map(p => (
          <button
            key={p}
            onClick={() => setProductFilter(p)}
            className={`px-2.5 py-1 text-xs font-medium rounded-lg border ${productFilter === p ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}
          >
            {p}
          </button>
        ))}
        {productFilter !== 'All' && (
          <span className="text-xs text-slate-500 ml-2">
            Showing <strong>{productFilter}</strong> only. Switch to "All" to see today's full numbers.
          </span>
        )}
      </div>

      {/* ───── Per-agent product breakdown ───── */}
      {perAgentProduct.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-bold">Per-agent product breakdown</h3>
            <div className="text-xs text-slate-500">{perAgentProduct.length} agent{perAgentProduct.length === 1 ? '' : 's'}</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Agent</th>
                  <th className="py-2 px-2 text-right">Total net</th>
                  {PRODUCT_OPTIONS.map(p => (
                    <th key={p} className={`py-2 px-2 text-right ${productFilter === p ? 'bg-slate-900 text-white' : ''}`}>{p}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {perAgentProduct.map(r => (
                  <tr key={r.prodNum} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 pr-3 font-medium">
                      {r.name || `#${r.prodNum}`}
                      <div className="text-[10px] font-mono text-slate-400">{r.prodNum}</div>
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums font-semibold">{fmt(r.total)}</td>
                    {PRODUCT_OPTIONS.map(p => (
                      <td key={p} className={`py-2 px-2 text-right tabular-nums ${productFilter === p ? 'bg-slate-50 font-semibold' : 'text-slate-600'}`}>
                        {Math.abs(r[p] || 0) > 0.005 ? fmt(r[p]) : <span className="text-slate-300">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ───── Policies that resolved to "Other" — extend the rules ───── */}
      {otherPolicies.length > 0 && (
        <details className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <summary className="cursor-pointer text-sm font-semibold text-amber-900">
            ⚠ {otherPolicies.length} policy{otherPolicies.length === 1 ? '' : 'ies'} resolved to "Other"
            <span className="ml-2 font-normal text-xs text-amber-700">— extend POLICY_PLAN or the carrier rules to classify these</span>
          </summary>
          <div className="mt-3 overflow-x-auto max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-amber-900 border-b border-amber-300">
                  <th className="py-1 pr-3">Carrier</th>
                  <th className="py-1 px-2">Policy</th>
                  <th className="py-1 px-2">Plan code (if known)</th>
                  <th className="py-1 px-2">Insured</th>
                </tr>
              </thead>
              <tbody>
                {otherPolicies.map((p, i) => (
                  <tr key={i} className="border-b border-amber-100">
                    <td className="py-1 pr-3">{p.carrier}</td>
                    <td className="py-1 px-2 font-mono">{p.policy}</td>
                    <td className="py-1 px-2 font-mono">{p.planCode || <span className="text-amber-500 italic">missing</span>}</td>
                    <td className="py-1 px-2 text-amber-800"><ClientNameLink name={p.insured} openClientProfile={openClientProfile} className="text-amber-800"/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold">{periodMode === 'week' ? 'Weekly' : 'Monthly'} trend</h3>
          {productFilter !== 'All' && (
            <span className="text-xs text-amber-700 italic">Trend table uses unfiltered period totals — switch product to "All" to keep period math intact.</span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-slate-500 text-left">
                <th className="py-2 pr-4">{periodMode === 'week' ? 'Week' : 'Month'}</th>
                <th className="py-2 pr-4 text-right">{possessive(settings.ownerName)} gross</th>
                <th className="py-2 pr-4 text-right">{possessive(settings.ownerName)} CB</th>
                <th className="py-2 pr-4 text-right bg-emerald-50">{possessive(settings.ownerName)} net</th>
              </tr>
            </thead>
            <tbody>
              {monthlyAgg.map(m => {
                const flagged = isCbOnlyPeriod(m);
                return (
                  <tr key={m.key || m.month || m.week} className={`border-b border-slate-100 ${flagged ? 'bg-red-50' : ''}`}>
                    <td className="py-2 pr-4 font-semibold">
                      {m.label}
                      {flagged && <span className="ml-2 text-xs font-normal text-red-600" title="No new production this period — chargebacks only">⚠ cb only</span>}
                    </td>
                    <td className="py-2 pr-4 text-right">{fmt(m.mineGross)}</td>
                    <td className="py-2 pr-4 text-right text-red-600">{fmt(m.mineCB)}</td>
                    <td className={`py-2 pr-4 text-right font-bold bg-emerald-50 ${(m.mineNet || 0) < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{fmt(m.mineNet)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                <td className="py-2 pr-4">Total</td>
                <td className="py-2 pr-4 text-right">{fmt(totals.mineGross)}</td>
                <td className="py-2 pr-4 text-right text-red-600">{fmt(totals.mineCB)}</td>
                <td className="py-2 pr-4 text-right bg-emerald-100 text-emerald-800">{fmt(totals.mineNet)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

function PersistencyBadge({ value }) {
  if (value == null) return null;
  const color = value >= 85 ? 'emerald' : value >= 70 ? 'amber' : 'red';
  return (
    <span className={`text-xs font-bold px-2 py-0.5 rounded-full bg-${color}-100 text-${color}-700`} title={`Persistency: ${value.toFixed(1)}%`}>
      {value.toFixed(0)}%
    </span>
  );
}

function PersistencyRow({ value }) {
  if (value == null) {
    return (
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-600">Persistency</span>
        <span className="text-sm text-slate-400">—</span>
      </div>
    );
  }
  const color = value >= 85 ? 'emerald' : value >= 70 ? 'amber' : 'red';
  const label = value >= 85 ? 'healthy' : value >= 70 ? 'watch' : 'needs attention';
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-600">Persistency</span>
      <span className={`text-sm font-semibold text-${color}-700`}>
        {value.toFixed(1)}% <span className="text-xs font-normal opacity-75">· {label}</span>
      </span>
    </div>
  );
}

function Row({ label, value, danger, bold, purple }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-600">{label}</span>
      <span className={`${bold?'font-bold text-lg':''} ${danger?'text-red-600':''} ${purple?'text-violet-700':''}`}>{value}</span>
    </div>
  );
}

// Manager UI for operator-defined teams. Layered ON TOP of the legacy
// 'mine' / 'unassigned' field on each producer — override math stays
// untouched, this is purely an organizational grouping.
//
// Each team has { id, name, color }. The id is derived from the name
// at creation time so renames don't break the producer.customTeam
// references; only delete actively detaches producers.
function CustomTeamsManager({ customTeams = [], addCustomTeam, renameCustomTeam, deleteCustomTeam, producers = {}, showToast = () => {} }) {
  const [newName, setNewName] = React.useState('');
  const [newColor, setNewColor] = React.useState('emerald');
  const [editingId, setEditingId] = React.useState(null);
  const [editDraft, setEditDraft]   = React.useState('');
  const [confirmDeleteId, setConfirmDeleteId] = React.useState(null);

  // Headcount per team — surfaces the impact of a delete before
  // the operator commits to it.
  const counts = React.useMemo(() => {
    const c = {};
    for (const info of Object.values(producers || {})) {
      const t = info?.customTeam;
      if (t) c[t] = (c[t] || 0) + 1;
    }
    return c;
  }, [producers]);

  const submitNew = () => {
    const clean = newName.trim();
    if (!clean) return;
    if (customTeams.some(t => t.name.toLowerCase() === clean.toLowerCase())) {
      showToast(`A team named "${clean}" already exists.`, 'error');
      return;
    }
    const id = addCustomTeam(clean, newColor);
    if (id) {
      setNewName('');
      showToast(`Team "${clean}" added`, 'success');
    }
  };

  const commitRename = (teamId) => {
    const v = editDraft.trim();
    if (v && v !== customTeams.find(t => t.id === teamId)?.name) {
      renameCustomTeam(teamId, v);
      showToast('Team renamed', 'success');
    }
    setEditingId(null);
    setEditDraft('');
  };

  const commitDelete = (teamId) => {
    const team = customTeams.find(t => t.id === teamId);
    if (!team) return;
    deleteCustomTeam(teamId);
    setConfirmDeleteId(null);
    const n = counts[teamId] || 0;
    showToast(`Deleted "${team.name}"${n ? ` (${n} producer${n === 1 ? '' : 's'} detached)` : ''}`, 'success');
  };

  const COLOR_SWATCHES = [
    { id: 'emerald', cls: 'bg-emerald-500' },
    { id: 'blue',    cls: 'bg-blue-500' },
    { id: 'violet',  cls: 'bg-violet-500' },
    { id: 'amber',   cls: 'bg-amber-500' },
    { id: 'rose',    cls: 'bg-rose-500' },
    { id: 'slate',   cls: 'bg-slate-500' },
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="mb-3">
        <div className="font-bold text-slate-900">Custom teams</div>
        <p className="text-xs text-slate-500 mt-0.5">
          Group your producers under any labels you want — "FL Office," "Mark's downline," whatever. Producers stay on their primary team (mine / unassigned) for override math; these are extra tags for filtering and reporting.
        </p>
      </div>

      {/* Add a new team */}
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <label className="block flex-1 min-w-[180px]">
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">New team name</span>
          <input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') submitNew(); }}
            placeholder="e.g. FL Office"
            className="mt-1 w-full border border-slate-200 focus:border-emerald-500 focus:outline-none rounded-lg px-3 py-2 text-sm"
          />
        </label>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Color</div>
          <div className="mt-1 flex gap-1">
            {COLOR_SWATCHES.map(s => (
              <button
                key={s.id}
                type="button"
                onClick={() => setNewColor(s.id)}
                className={`w-7 h-7 rounded ${s.cls} ${newColor === s.id ? 'ring-2 ring-offset-1 ring-slate-700' : 'opacity-70 hover:opacity-100'}`}
                title={s.id}
              />
            ))}
          </div>
        </div>
        <button
          onClick={submitNew}
          disabled={!newName.trim()}
          className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold px-4 py-2 rounded-lg whitespace-nowrap"
        >
          + Add team
        </button>
      </div>

      {/* Current list */}
      {customTeams.length === 0 ? (
        <div className="text-xs text-slate-400 italic">No custom teams yet. Add one above to start tagging producers.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {customTeams.map(t => {
            const n = counts[t.id] || 0;
            const colorBg = ({
              emerald: 'bg-emerald-100 text-emerald-800',
              blue:    'bg-blue-100 text-blue-800',
              violet:  'bg-violet-100 text-violet-800',
              amber:   'bg-amber-100 text-amber-800',
              rose:    'bg-rose-100 text-rose-800',
              slate:   'bg-slate-100 text-slate-700',
            })[t.color] || 'bg-slate-100 text-slate-700';
            return (
              <div key={t.id} className="py-2 flex items-center gap-3 flex-wrap">
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${colorBg}`}>
                  {t.name}
                </span>
                <span className="text-xs text-slate-500">
                  {n} producer{n === 1 ? '' : 's'}
                </span>
                <span className="flex-1" />
                {editingId === t.id ? (
                  <>
                    <input
                      autoFocus
                      value={editDraft}
                      onChange={e => setEditDraft(e.target.value)}
                      onBlur={() => commitRename(t.id)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') commitRename(t.id);
                        if (e.key === 'Escape') { setEditingId(null); setEditDraft(''); }
                      }}
                      className="border border-emerald-300 rounded px-2 py-1 text-xs font-semibold focus:outline-none focus:border-emerald-500"
                      style={{ minWidth: 160 }}
                    />
                  </>
                ) : (
                  <button
                    onClick={() => { setEditingId(t.id); setEditDraft(t.name); }}
                    className="text-xs font-semibold text-slate-600 hover:text-slate-900 hover:underline"
                  >
                    Rename
                  </button>
                )}
                {confirmDeleteId === t.id ? (
                  <>
                    <span className="text-xs text-rose-700 font-semibold">
                      Delete? {n > 0 && `(${n} will be detached)`}
                    </span>
                    <button
                      onClick={() => commitDelete(t.id)}
                      className="text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 px-2 py-1 rounded"
                    >
                      Yes, delete
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      className="text-xs font-semibold text-slate-600 hover:text-slate-900 hover:underline"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setConfirmDeleteId(t.id)}
                    className="text-xs font-semibold text-rose-600 hover:text-rose-800 hover:underline"
                  >
                    Delete
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Per-producer cross-carrier alias picker. Renders nothing when the
// roster has fewer than 2 producers (no one to link to). When the
// row's producer is ALREADY linked, shows a "linked → Name" chip
// with an Unlink button. When unlinked, shows a "Link to person…"
// dropdown listing every other producer's name (canonicalProdNum
// for them, deduplicated so we don't list aliases of the same
// person twice).
function ProducerAliasPicker({ prodNum, producers, setProducerAlias }) {
  const currentAlias = producers?.[prodNum]?.aliasOf || '';

  // Build the candidate list — every producer EXCEPT:
  //   - this producer itself
  //   - producers already aliased to someone else (they'd just chain)
  // Deduplicate by canonical so the dropdown shows one "Harrison
  // Curry" not three.
  const candidates = React.useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const [pn, info] of Object.entries(producers || {})) {
      if (pn === prodNum) continue;
      if (info?.aliasOf) continue; // skip aliases — pick the canonical
      const canon = pn; // we're listing canonicals
      if (seen.has(canon)) continue;
      seen.add(canon);
      const display = info?.name || `Producer ${canon}`;
      out.push({ id: canon, label: display });
    }
    return out.sort((a, b) => a.label.localeCompare(b.label));
  }, [producers, prodNum]);

  if (candidates.length === 0 && !currentAlias) return null;

  if (currentAlias) {
    const target = producers?.[currentAlias];
    const targetLabel = target?.name || `Producer ${currentAlias}`;
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-violet-50 border border-violet-200 text-violet-800 px-2 py-1 rounded">
        <span className="opacity-70">↪</span>
        <span>linked to {targetLabel}</span>
        <button
          onClick={() => setProducerAlias(prodNum, '')}
          className="ml-1 text-violet-600 hover:text-violet-900 hover:underline"
          title="Unlink"
        >
          ✕
        </button>
      </span>
    );
  }
  return (
    <select
      value=""
      onChange={e => { if (e.target.value) setProducerAlias(prodNum, e.target.value); }}
      className="text-xs font-semibold px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 focus:border-violet-500 focus:outline-none"
      title="Link this producer to the same person on another carrier"
    >
      <option value="">↪ Link to person…</option>
      {candidates.map(c => (
        <option key={c.id} value={c.id}>{c.label}</option>
      ))}
    </select>
  );
}

function ProducersView({ producers, producerList, setProducerTeam, setProducerName, setProducerCustomTeam = () => {}, setProducerAlias = () => {}, customTeams = [], addCustomTeam = () => null, renameCustomTeam = () => {}, deleteCustomTeam = () => {}, onResetProducers, onGotoDashboard = () => {}, settings = {}, setSettings = () => {}, showToast = () => {}, onAddManualProducer = () => null }) {
  const [addModalOpen, setAddModalOpen] = React.useState(false);
  const [newProducerName, setNewProducerName] = React.useState('');
  // ── Local UI state ───────────────────────────────────────────────
  // Search / filter / sort / view controls drive what's rendered in
  // the producer list below. Default sort is by net (highest earners
  // first) — matches operator's instinct when triaging the roster.
  const [search, setSearch] = React.useState('');
  const [teamFilter, setTeamFilter] = React.useState('all'); // all | mine | unassigned | untagged | (custom team id)
  const [sortKey, setSortKey] = React.useState('net');       // net | name | prodNum | premium | policies
  const [viewMode, setViewMode] = React.useState('list');    // list | grouped
  const [expandedRows, setExpandedRows] = React.useState({}); // { [prodNum]: true }
  const [showTeamSettings, setShowTeamSettings] = React.useState(false);
  const [showAddProducer,  setShowAddProducer]  = React.useState(false);
  const [showCustomTeams,  setShowCustomTeams]  = React.useState(false);
  // Bulk-name modal — for the "X producers without names" workflow.
  // unnamedProds is computed below from the live producers map; the
  // modal seeds bulkNames with one entry per unnamed prodNum.
  const [showBulkNamer, setShowBulkNamer] = React.useState(false);
  const [bulkNames, setBulkNames]         = React.useState({});

  // ── Team-label rename (single 'Your team' label only) ────────────
  const [ownerDraft, setOwnerDraft] = useState(settings.ownerName || 'Your');
  useEffect(() => { setOwnerDraft(settings.ownerName || 'Your'); }, [settings.ownerName]);
  const commitOwner = () => {
    const v = (ownerDraft || '').trim() || 'Your';
    if (v !== settings.ownerName) setSettings(s => ({ ...s, ownerName: v }));
  };

  // ── Manual-add form ──────────────────────────────────────────────
  const [addProdNum, setAddProdNum] = useState('');
  const [addName, setAddName] = useState('');
  const [addTeam, setAddTeam] = useState('mine');
  const handleAddProducer = () => {
    const pn = (addProdNum || '').trim();
    const nm = (addName || '').trim();
    if (!pn) { showToast('Producer ID / number is required', 'error'); return; }
    if (!nm) { showToast('Producer name is required', 'error'); return; }
    if (producers && producers[pn]) {
      showToast(`Producer ${pn} already exists — edit them in the list below.`, 'error');
      return;
    }
    setProducerName(pn, nm);
    setProducerTeam(pn, addTeam);
    showToast(`Added ${nm} (${pn})`);
    setAddProdNum(''); setAddName(''); setAddTeam('mine');
    setShowAddProducer(false);
  };

  // Empty-state early return removed here — the check + render moved
  // to AFTER all hooks. Prior early return at this point violated
  // Rules of Hooks: `unnamedProds` and `groupedSections` useMemos
  // below only ran on the populated path; a 0→1 producer transition
  // grew the hook count and blew up with React error #310. Fix:
  // let ALL hooks run first, then decide at JSX-return time whether
  // to render the empty state or the populated roster.
  const isEmptyProducers = Object.keys(producers || {}).length === 0 && (producerList || []).length === 0;

  // ── Build the roster (every prodNum the operator's ever seen) ────
  const allProdNums = Array.from(new Set([...Object.keys(producers), ...producerList.map(p=>p.prodNum)]));
  const rosterRaw = allProdNums.map(pn => {
    const existing = producerList.find(p => p.prodNum === pn);
    return existing || { prodNum: pn, team: producers[pn]?.team || 'unassigned', gross:0, cb:0, net:0, premiumPositive:0, count:0, policies:[] };
  });

  // Filter + search
  const lc = search.trim().toLowerCase();
  const roster = rosterRaw.filter(p => {
    const team       = producers[p.prodNum]?.team || 'unassigned';
    const customTeam = producers[p.prodNum]?.customTeam || '';
    if (teamFilter === 'mine'       && team !== 'mine') return false;
    if (teamFilter === 'unassigned' && team !== 'unassigned') return false;
    if (teamFilter === 'untagged'   && customTeam) return false;
    if (teamFilter.startsWith('custom:')) {
      const id = teamFilter.slice(7);
      if (customTeam !== id) return false;
    }
    if (lc) {
      const name = (getProducerName(p.prodNum, producers) || '').toLowerCase();
      if (!name.includes(lc) && !String(p.prodNum).toLowerCase().includes(lc)) return false;
    }
    return true;
  });

  // Sort
  const sorted = [...roster].sort((a, b) => {
    if (sortKey === 'name') {
      return getProducerName(a.prodNum, producers).localeCompare(getProducerName(b.prodNum, producers));
    }
    if (sortKey === 'prodNum') return a.prodNum.localeCompare(b.prodNum);
    if (sortKey === 'premium') return (b.premiumPositive || 0) - (a.premiumPositive || 0);
    if (sortKey === 'policies') return (b.count || 0) - (a.count || 0);
    // default: net
    return (b.net || 0) - (a.net || 0) || a.prodNum.localeCompare(b.prodNum);
  });

  // ── Unnamed producers (driving the "Name Them All" banner) ──────
  // A producer is "unnamed" when its `name` field is blank or the same
  // as its prodNum. Carrier statements (esp. MOO CSV uploads) often
  // don't carry the agent name, so a fresh-imported producer record
  // sits there as 'Producer 1248857' until the operator names it.
  const unnamedProds = React.useMemo(() => {
    return Object.entries(producers || {})
      .filter(([pn, p]) => {
        if (!pn || pn === 'unknown') return false;
        const nm = String(p?.name || '').trim();
        return !nm || nm === String(pn);
      })
      .map(([pn]) => pn)
      .sort();
  }, [producers]);

  const openBulkNamer = () => {
    const init = {};
    unnamedProds.forEach(pn => { init[pn] = ''; });
    setBulkNames(init);
    setShowBulkNamer(true);
  };
  const saveBulkNames = () => {
    let saved = 0;
    Object.entries(bulkNames).forEach(([pn, name]) => {
      const trimmed = (name || '').trim();
      if (trimmed) {
        setProducerName(pn, trimmed);
        saved++;
      }
    });
    showToast(saved > 0 ? `Named ${saved} producer${saved === 1 ? '' : 's'}.` : 'Nothing saved — all blank.');
    setShowBulkNamer(false);
  };

  // ── Top-line stats ───────────────────────────────────────────────
  const allProds = Object.values(producers);
  const totalCount      = allProds.length;
  const mineCount       = allProds.filter(p => p.team === 'mine').length;
  const unassignedCount = allProds.filter(p => p.team === 'unassigned').length;
  const aliasedCount    = allProds.filter(p => p.aliasOf).length;
  const tagged          = allProds.filter(p => p.customTeam).length;

  // ── Grouped view: group rows by team key ─────────────────────────
  const groupedSections = React.useMemo(() => {
    if (viewMode !== 'grouped') return null;
    const groups = {};
    const order = [
      { id: 'mine',       label: `${settings.ownerName || 'My'} team` },
      ...customTeams.map(t => ({ id: `custom:${t.id}`, label: t.name, color: t.color })),
      { id: 'unassigned', label: 'Unassigned' },
    ];
    for (const o of order) groups[o.id] = { meta: o, items: [] };
    for (const p of sorted) {
      const team       = producers[p.prodNum]?.team || 'unassigned';
      const customTeam = producers[p.prodNum]?.customTeam || '';
      if (customTeam) {
        const key = `custom:${customTeam}`;
        if (groups[key]) groups[key].items.push(p);
        else (groups[key] = { meta: { id: key, label: customTeams.find(t => t.id === customTeam)?.name || customTeam }, items: [p] });
      } else if (team === 'mine') {
        groups.mine.items.push(p);
      } else {
        groups.unassigned.items.push(p);
      }
    }
    return Object.values(groups).filter(g => g.items.length > 0);
  }, [viewMode, sorted, producers, customTeams, settings.ownerName]);

  // ── Empty state — post-hook return so hook count stays stable ────
  // (see the comment near isEmptyProducers for why this was moved
  // from an early return to here). ALL hooks above run in both the
  // empty and populated paths; the return happens after they've all
  // been called, so React sees the same hook count every render.
  if (isEmptyProducers) {
    return (
      <>
      <div className="max-w-none mx-auto p-8 text-center">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
          <div className="w-12 h-12 mx-auto rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center mb-3">
            <Users size={24}/>
          </div>
          <h2 className="text-lg font-bold mb-1">No producers yet</h2>
          <p className="text-sm text-slate-500 mb-4">Two ways to add producers: upload a commission statement (auto-populates from the carrier's producer codes), or add one manually here.</p>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <button
              onClick={() => { setNewProducerName(''); setAddModalOpen(true); }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-lg"
            >
              + Add producer manually
            </button>
            <button onClick={onGotoDashboard} className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-4 py-2 rounded-lg">
              Go to Dashboard (to upload a statement)
            </button>
          </div>
        </div>
      </div>
      {addModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setAddModalOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-1">Add a producer</h3>
            <p className="text-xs text-slate-500 mb-4">You can assign leads and policies to this producer immediately. Statement-imported producers can be merged in later.</p>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Producer name *</label>
            <input
              type="text"
              value={newProducerName}
              onChange={e => setNewProducerName(e.target.value)}
              placeholder="e.g. Harrison Curry"
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter' && newProducerName.trim()) {
                  onAddManualProducer(newProducerName.trim());
                  setAddModalOpen(false);
                }
                if (e.key === 'Escape') setAddModalOpen(false);
              }}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            />
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                onClick={() => setAddModalOpen(false)}
                className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
              >Cancel</button>
              <button
                onClick={() => {
                  const cleaned = newProducerName.trim();
                  if (!cleaned) return;
                  onAddManualProducer(cleaned);
                  setAddModalOpen(false);
                }}
                disabled={!newProducerName.trim()}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 rounded-lg"
              >Add producer</button>
            </div>
          </div>
        </div>
      )}
      </>
    );
  }

  return (
    <div className="space-y-4">
      {/* ── Header card: top-line stats + Reset action ───────────── */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-bold text-slate-900">Producers</h2>
          <p className="text-xs text-slate-500 mt-0.5">Every agent in your roster — assign teams, rename, link the same person across carriers, and tag them with custom labels.</p>
        </div>
        <ProducersStatTile  label="Total"      value={totalCount}      tone="slate"/>
        <ProducersStatTile  label="My team"    value={mineCount}       tone="emerald"/>
        <ProducersStatTile  label="Unassigned" value={unassignedCount} tone="amber"/>
        {tagged > 0    && <ProducersStatTile label="Tagged"  value={tagged}       tone="violet"/>}
        {aliasedCount > 0 && <ProducersStatTile label="Linked" value={aliasedCount} tone="blue"/>}
      </div>

      {/* ── Unnamed producers banner ─────────────────────────────── */}
      {unnamedProds.length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-300 p-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[0.65rem] font-black uppercase tracking-[0.15em] text-amber-700 mb-1">
              ⚠ {unnamedProds.length} Producer{unnamedProds.length === 1 ? '' : 's'} Without Names
            </div>
            <div className="text-sm text-amber-900">
              These show up as <span className="font-mono font-bold">Producer 1248857</span> in your Persistency, Overrides, and P&amp;L reports instead of real names. Give them names so every screen reads like a real roster.
            </div>
          </div>
          <button
            onClick={openBulkNamer}
            className="bg-amber-700 hover:bg-amber-800 text-white text-xs font-black uppercase tracking-tight px-4 py-2 shadow flex-shrink-0"
          >
            Name Them All →
          </button>
        </div>
      )}

      {/* ── Search + filter + sort + view controls ───────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-2">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or producer ID…"
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm flex-1 min-w-[220px] focus:border-emerald-500 focus:outline-none"
        />
        <select value={teamFilter} onChange={e => setTeamFilter(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-2 text-sm bg-white">
          <option value="all">All teams</option>
          <option value="mine">My team</option>
          <option value="unassigned">Unassigned</option>
          {customTeams.length > 0 && <optgroup label="Custom teams">
            {customTeams.map(t => <option key={t.id} value={`custom:${t.id}`}>{t.name}</option>)}
            <option value="untagged">— No custom team</option>
          </optgroup>}
        </select>
        <select value={sortKey} onChange={e => setSortKey(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-2 text-sm bg-white">
          <option value="net">Sort: Net commission</option>
          <option value="premium">Sort: Premium written</option>
          <option value="policies">Sort: Policy count</option>
          <option value="name">Sort: Name (A→Z)</option>
          <option value="prodNum">Sort: Producer ID</option>
        </select>
        <select value={viewMode} onChange={e => setViewMode(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-2 text-sm bg-white">
          <option value="list">View: Flat list</option>
          <option value="grouped">View: Grouped by team</option>
        </select>
        <span className="text-xs text-slate-500 ml-1">{sorted.length} of {totalCount}</span>
      </div>

      {/* ── Collapsible: Team label setting ──────────────────────── */}
      <CollapsiblePanel
        title="Team label"
        subtitle={`Display name for your producers' team — currently "${settings.ownerName || 'Your'} team"`}
        open={showTeamSettings}
        onToggle={() => setShowTeamSettings(s => !s)}
      >
        <label className="block max-w-md">
          <span className="text-xs uppercase tracking-wider font-semibold text-emerald-700">Your team label</span>
          <input
            value={ownerDraft}
            onChange={e => setOwnerDraft(e.target.value)}
            onBlur={commitOwner}
            onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            placeholder="Your"
            className="mt-1 w-full border border-emerald-200 focus:border-emerald-500 focus:outline-none bg-emerald-50/40 rounded-lg px-3 py-2 text-sm font-semibold text-emerald-900"
          />
          <span className="block text-[11px] text-slate-500 mt-1">Shown as "{settings.ownerName || 'Your'} team" throughout the app.</span>
        </label>
        <div className="mt-4">
          <button onClick={onResetProducers} className="text-xs text-rose-600 hover:text-rose-700 hover:underline">
            Reset all producers to defaults
          </button>
        </div>
      </CollapsiblePanel>

      {/* ── Collapsible: Custom teams manager ────────────────────── */}
      <CollapsiblePanel
        title="Custom teams"
        subtitle={`${customTeams.length} team${customTeams.length === 1 ? '' : 's'} · operator-defined groupings`}
        open={showCustomTeams}
        onToggle={() => setShowCustomTeams(s => !s)}
      >
        <CustomTeamsManager
          customTeams={customTeams}
          addCustomTeam={addCustomTeam}
          renameCustomTeam={renameCustomTeam}
          deleteCustomTeam={deleteCustomTeam}
          producers={producers}
          showToast={showToast}
        />
      </CollapsiblePanel>

      {/* ── Collapsible: Add producer manually ───────────────────── */}
      <CollapsiblePanel
        title="Add a producer manually"
        subtitle="For agents the statement parser didn't pick up"
        open={showAddProducer}
        onToggle={() => setShowAddProducer(s => !s)}
      >
        <div className="grid grid-cols-1 md:grid-cols-[160px,1fr,200px,auto] gap-2 items-end">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Producer ID</span>
            <input
              value={addProdNum}
              onChange={e => setAddProdNum(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAddProducer(); }}
              placeholder="e.g. 1234567"
              className="mt-1 w-full border border-slate-200 focus:border-emerald-500 focus:outline-none rounded-lg px-3 py-2 text-sm font-mono"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Name</span>
            <input
              value={addName}
              onChange={e => setAddName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAddProducer(); }}
              placeholder="First Last"
              className="mt-1 w-full border border-slate-200 focus:border-emerald-500 focus:outline-none rounded-lg px-3 py-2 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Team</span>
            <select value={addTeam} onChange={e => setAddTeam(e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="mine">{settings.ownerName || 'Your'} team</option>
              <option value="owner">Owner production</option>
              <option value="unassigned">Unassigned</option>
            </select>
          </label>
          <button
            onClick={handleAddProducer}
            disabled={!addProdNum.trim() || !addName.trim()}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap"
          >
            + Add producer
          </button>
        </div>
      </CollapsiblePanel>

      {/* ── Producer list / grouped sections ─────────────────────── */}
      {viewMode === 'grouped' && groupedSections ? (
        <div className="space-y-3">
          {groupedSections.map(g => (
            <div key={g.meta.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className={`px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between`}>
                <div className="font-bold text-slate-900 flex items-center gap-2">
                  {g.meta.color && <span className={`w-2 h-2 rounded-full bg-${g.meta.color}-500`}/>}
                  {g.meta.label}
                </div>
                <span className="text-xs text-slate-500">{g.items.length} producer{g.items.length === 1 ? '' : 's'}</span>
              </div>
              <div className="divide-y divide-slate-100">
                {g.items.map(p => (
                  <ProducerRow
                    key={p.prodNum}
                    p={p}
                    producers={producers}
                    customTeams={customTeams}
                    expanded={!!expandedRows[p.prodNum]}
                    onToggle={() => setExpandedRows(s => ({ ...s, [p.prodNum]: !s[p.prodNum] }))}
                    setProducerName={setProducerName}
                    setProducerTeam={setProducerTeam}
                    setProducerCustomTeam={setProducerCustomTeam}
                    setProducerAlias={setProducerAlias}
                    settings={settings}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
            <div className="font-bold text-slate-900">Producer roster</div>
            <span className="text-xs text-slate-500">click a row to expand controls</span>
          </div>
          {sorted.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No producers match the current filters.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {sorted.map(p => (
                <ProducerRow
                  key={p.prodNum}
                  p={p}
                  producers={producers}
                  customTeams={customTeams}
                  expanded={!!expandedRows[p.prodNum]}
                  onToggle={() => setExpandedRows(s => ({ ...s, [p.prodNum]: !s[p.prodNum] }))}
                  setProducerName={setProducerName}
                  setProducerTeam={setProducerTeam}
                  setProducerCustomTeam={setProducerCustomTeam}
                  setProducerAlias={setProducerAlias}
                  settings={settings}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Bulk Name Producers modal ─────────────────────────────── */}
      {showBulkNamer && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={() => setShowBulkNamer(false)}>
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="bg-amber-50 border-b-2 border-amber-300 px-5 py-4">
              <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-amber-700">Bulk Name Producers</div>
              <div className="text-xl font-black text-slate-900 mt-1 uppercase tracking-tight">Type Each Producer's Real Name</div>
              <div className="text-xs text-slate-600 mt-1">Leave blank for any you don't recognize — those rows stay nameless. Tab between inputs.</div>
            </div>
            <div className="p-5 space-y-2">
              {unnamedProds.map((pn, idx) => (
                <div key={pn} className="flex items-center gap-3">
                  <div className="text-xs font-mono font-bold text-slate-700 w-36 flex-shrink-0 truncate" title={pn}>{pn}</div>
                  <input
                    autoFocus={idx === 0}
                    value={bulkNames[pn] || ''}
                    onChange={e => setBulkNames(prev => ({ ...prev, [pn]: e.target.value }))}
                    placeholder="e.g. Daniel Davidi"
                    className="flex-1 border border-slate-300 px-3 py-1.5 text-sm focus:border-emerald-500 focus:outline-none"
                  />
                </div>
              ))}
            </div>
            <div className="bg-slate-50 border-t border-slate-200 px-5 py-3 flex justify-between items-center">
              <button
                onClick={() => setShowBulkNamer(false)}
                className="text-xs text-slate-600 hover:text-slate-900 font-bold uppercase tracking-tight"
              >
                Cancel
              </button>
              <button
                onClick={saveBulkNames}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase tracking-tight px-4 py-2 shadow"
              >
                Save All Names
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Compact stat tile for the Producers page header.
function ProducersStatTile({ label, value, tone = 'slate' }) {
  const tones = {
    slate:   'bg-slate-50 border-slate-200 text-slate-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber:   'bg-amber-50 border-amber-200 text-amber-800',
    violet:  'bg-violet-50 border-violet-200 text-violet-800',
    blue:    'bg-blue-50 border-blue-200 text-blue-800',
  };
  return (
    <div className={`border rounded-lg px-3 py-1.5 text-center min-w-[80px] ${tones[tone] || tones.slate}`}>
      <div className="text-lg font-bold leading-tight">{value}</div>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
    </div>
  );
}

// Click-to-expand collapsible section used for the three secondary
// panels (team label, custom teams, add producer manually). Keeps the
// page focused on the roster by default.
function CollapsiblePanel({ title, subtitle, open, onToggle, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-3 flex items-center justify-between gap-3 hover:bg-slate-50"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className={`text-slate-400 transition-transform inline-block ${open ? 'rotate-90' : ''}`} aria-hidden="true">▸</span>
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 text-sm">{title}</div>
            {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
          </div>
        </div>
        <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">
          {open ? 'Hide' : 'Show'}
        </span>
      </button>
      {open && (
        <div className="border-t border-slate-200 p-4">
          {children}
        </div>
      )}
    </div>
  );
}

// Single producer row in the redesigned list. Collapsed by default —
// shows ID, name, team chip, stats. Click to expand and reveal the
// rename input + team buttons + custom-team dropdown + alias picker.
function ProducerRow({ p, producers, customTeams, expanded, onToggle, setProducerName, setProducerTeam, setProducerCustomTeam, setProducerAlias, settings }) {
  const team = producers[p.prodNum]?.team || KNOWN_PRODUCERS[p.prodNum]?.team || 'unassigned';
  const name = getProducerName(p.prodNum, producers);
  const customTeam = producers[p.prodNum]?.customTeam || '';
  const customTeamObj = customTeams.find(t => t.id === customTeam);
  const aliasOf = producers[p.prodNum]?.aliasOf || '';
  const teamColor = team === 'mine' ? 'border-l-emerald-500' : team === 'owner' ? 'border-l-violet-500' : 'border-l-slate-300';
  return (
    <div className={`border-l-4 ${teamColor} ${expanded ? 'bg-slate-50/50' : 'hover:bg-slate-50'}`}>
      {/* Summary row */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-3 flex items-center gap-3 flex-wrap"
        aria-expanded={expanded}
      >
        <span className={`text-slate-400 transition-transform inline-block ${expanded ? 'rotate-90' : ''}`} aria-hidden="true">▸</span>
        <div className="text-xs font-mono text-slate-500 bg-slate-100 px-2 py-1 rounded">{p.prodNum}</div>
        <div className="font-semibold text-slate-900 flex-1 min-w-[180px]">{name}</div>
        <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${team === 'mine' ? 'bg-emerald-100 text-emerald-800' : team === 'owner' ? 'bg-violet-100 text-violet-800' : 'bg-slate-100 text-slate-600'}`}>
          {team === 'mine' ? `${settings.ownerName || 'My'} team` : team === 'owner' ? 'Owner' : 'Unassigned'}
        </span>
        {customTeamObj && (
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded bg-${customTeamObj.color || 'slate'}-100 text-${customTeamObj.color || 'slate'}-800`}>
            {customTeamObj.name}
          </span>
        )}
        {aliasOf && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800" title={`Linked to ${getProducerName(aliasOf, producers)}`}>
            ↪ alias
          </span>
        )}
        <div className="flex items-center gap-4 text-xs text-slate-600 ml-auto">
          <div className="text-right">
            <div className="text-slate-400">Net</div>
            <div className={`font-bold ${(p.net || 0) < 0 ? 'text-red-600' : 'text-slate-900'}`}>{fmt(p.net || 0)}</div>
          </div>
          <div className="text-right w-12">
            <div className="text-slate-400">#</div>
            <div className="font-semibold">{p.count || 0}</div>
          </div>
        </div>
      </button>

      {/* Expanded controls + extended stats */}
      {expanded && (
        <div className="px-3 pb-3 space-y-3" onClick={e => e.stopPropagation()}>
          <div className="flex flex-wrap items-end gap-3">
            <label className="block flex-1 min-w-[220px]">
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Display name</span>
              <input
                value={name}
                onChange={e => setProducerName(p.prodNum, e.target.value)}
                placeholder="Producer name"
                className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-semibold focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
              />
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mr-1">Team</span>
            <button onClick={() => setProducerTeam(p.prodNum, 'mine')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${team === 'mine' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-emerald-50 hover:text-emerald-700'}`}>
              {settings.ownerName || 'My'} team
            </button>
            <button onClick={() => setProducerTeam(p.prodNum, 'unassigned')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${team === 'unassigned' ? 'bg-slate-600 text-white' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'}`}>
              Unassigned
            </button>
            {customTeams.length > 0 && (
              <>
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 ml-3">Custom tag</span>
                <select
                  value={customTeam}
                  onChange={e => setProducerCustomTeam(p.prodNum, e.target.value)}
                  className="text-xs font-semibold px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 focus:border-emerald-500 focus:outline-none"
                >
                  <option value="">(no custom team)</option>
                  {customTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </>
            )}
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 ml-3">Same person</span>
            <ProducerAliasPicker prodNum={p.prodNum} producers={producers} setProducerAlias={setProducerAlias}/>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 text-xs">
            <div className="bg-white border border-slate-200 rounded-lg p-2 text-center">
              <div className="text-slate-400 text-[10px] uppercase tracking-wider">Premium</div>
              <div className="font-semibold mt-0.5">{fmt(p.premiumPositive || 0)}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-2 text-center">
              <div className="text-slate-400 text-[10px] uppercase tracking-wider">Gross</div>
              <div className="font-semibold mt-0.5">{fmt(p.gross || 0)}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-2 text-center">
              <div className="text-slate-400 text-[10px] uppercase tracking-wider">CB</div>
              <div className="font-semibold text-red-600 mt-0.5">{fmt(p.cb || 0)}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-2 text-center">
              <div className="text-slate-400 text-[10px] uppercase tracking-wider">Policies</div>
              <div className="font-semibold mt-0.5">{p.count || 0}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AgencyView({ allTransactions, producerList, producers, monthlyAgg, settings, presentationConfig, setPresentationConfig }) {
  const [presenting, setPresenting] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);

  // ---- Compute agency-wide stats (all-time) ----
  // Policy-level aggregation
  const policyMap = {};
  (allTransactions || []).forEach(tx => {
    if (!tx.policy) return;
    const key = `${tx.mga||''}|${tx.policy||''}`;
    if (!policyMap[key]) {
      policyMap[key] = {
        key, policy: tx.policy, insured: tx.insured || '', prodNum: tx.prodNum,
        carrier: deriveCarrier(tx), state: tx.state || '', issueDate: tx.issueDate || '',
        premium: 0, totalCommission: 0, chargebackCount: 0,
      };
    }
    const p = policyMap[key];
    p.totalCommission += (tx.netToAccount || 0);
    if (tx.commPremium && Math.abs(tx.commPremium) > p.premium) p.premium = Math.abs(tx.commPremium);
    if (!p.insured && tx.insured) p.insured = tx.insured;
    if (!p.issueDate && tx.issueDate) p.issueDate = tx.issueDate;
    if (CHARGEBACK_TYPES.has(tx.activityType) || (tx.netToAccount || 0) < 0) p.chargebackCount++;
  });
  const policies = Object.values(policyMap).map(p => ({
    ...p,
    status: (p.chargebackCount > 0 || p.totalCommission < 0) ? 'lapsed' : 'active',
  }));
  const activePolicies = policies.filter(p => p.status === 'active');
  const totalActivePremium = activePolicies.reduce((s, p) => s + (p.premium || 0), 0);
  const totalCommission = (allTransactions || []).reduce((s, tx) => s + (tx.netToAccount || 0), 0);
  const totalGross = (allTransactions || []).reduce((s, tx) => s + Math.max(0, tx.netToAccount || 0), 0);
  const totalChargebacks = Math.abs((allTransactions || []).reduce((s, tx) => s + Math.min(0, tx.netToAccount || 0), 0));
  // Commission retention — what fraction of advance commission survived clawbacks.
  // NOT industry-standard persistency. Bounded by the carrier's advance window
  // (typically 9-12 months for MP): a policy that lapses AFTER the advance ends
  // produces no chargeback and does not move this number.
  const commissionRetention = totalGross > 0 ? Math.max(0, Math.min(100, ((totalGross - totalChargebacks) / totalGross) * 100)) : 0;
  // Kept under the old name so existing consumers (presentation, briefing, the
  // overview tile) don't break in one PR. New consumers should use the
  // distinguishing name above.
  const overallPersistency = commissionRetention;
  const cbRate = totalGross > 0 ? (totalChargebacks / totalGross) * 100 : 0;

  // True 13-month policy persistency — the industry definition.
  // Cohort = policies whose issueDate is ≥ 13 months ago (otherwise they
  // can't be evaluated yet). Persistency = % of that cohort still active.
  // Returns null if the cohort is empty so the UI can say "—" instead of
  // showing a meaningless 0%.
  const THIRTEEN_MONTHS_MS = 13 * 30.4375 * 24 * 60 * 60 * 1000;
  const _now = Date.now();
  const persistencyCohort = policies.filter(p => {
    if (!p.issueDate) return false;
    const t = Date.parse(p.issueDate);
    return Number.isFinite(t) && (_now - t) >= THIRTEEN_MONTHS_MS;
  });
  const persistencyCohortActive = persistencyCohort.filter(p => p.status === 'active').length;
  const truePersistency = persistencyCohort.length > 0
    ? (persistencyCohortActive / persistencyCohort.length) * 100
    : null;
  const persistencyCohortSize = persistencyCohort.length;
  const clients = new Set(activePolicies.map(p => (p.insured || '').trim().toUpperCase()).filter(Boolean));

  // Carrier mix (active premium)
  const carrierBuckets = {};
  activePolicies.forEach(p => {
    const c = p.carrier || 'Other';
    carrierBuckets[c] = (carrierBuckets[c] || 0) + (p.premium || 0);
  });
  const carrierMix = sortByCarrierOrder(
    Object.entries(carrierBuckets).filter(([,v]) => v > 0).map(([name, value]) => ({ name, value: Math.round(value) })),
    x => x.name
  );

  // Team breakdown
  const teamCounts = { mine: 0, cam: 0, unassigned: 0 };
  const teamPremium = { mine: 0, cam: 0, unassigned: 0 };
  activePolicies.forEach(p => {
    const t = getProducerTeam(p.prodNum, producers);
    teamCounts[t] = (teamCounts[t] || 0) + 1;
    teamPremium[t] = (teamPremium[t] || 0) + (p.premium || 0);
  });

  // Top agents by active premium
  const agentMap = {};
  activePolicies.forEach(p => {
    const k = p.prodNum;
    if (!agentMap[k]) agentMap[k] = { prodNum: k, name: getProducerName(k, producers), team: getProducerTeam(k, producers), policies: 0, premium: 0 };
    agentMap[k].policies++;
    agentMap[k].premium += (p.premium || 0);
  });
  const topAgents = Object.values(agentMap).sort((a, b) => b.premium - a.premium).slice(0, 5);

  // Growth: monthly net over time (all-time)
  const monthMap = {};
  (allTransactions || []).forEach(tx => {
    if (!tx.monthKey) return;
    monthMap[tx.monthKey] = (monthMap[tx.monthKey] || 0) + (tx.netToAccount || 0);
  });
  const growth = Object.entries(monthMap)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([m, v]) => ({ label: monthLabel(m), net: Math.round(v) }));

  // Persistency by team (gross/cb based on transactions, not policies)
  const teamGrossCB = { mine: { gross: 0, cb: 0 }, cam: { gross: 0, cb: 0 } };
  (allTransactions || []).forEach(tx => {
    const t = getProducerTeam(tx.prodNum, producers);
    if (t !== 'mine' && t !== 'cam') return;
    const amt = tx.netToAccount || 0;
    if (amt >= 0) teamGrossCB[t].gross += amt;
    else teamGrossCB[t].cb += Math.abs(amt);
  });
  const persistencyOf = (b) => b.gross > 0 ? Math.max(0, Math.min(100, ((b.gross - b.cb) / b.gross) * 100)) : 0;

  const slideMap = buildAgencySlides({
    settings,
    activePolicies, policies, totalActivePremium, totalCommission, totalChargebacks, overallPersistency, cbRate,
    truePersistency, persistencyCohortSize,
    clients, growth, carrierMix, topAgents,
    teamCounts, teamPremium,
    minePersistency: persistencyOf(teamGrossCB.mine),
    camPersistency: persistencyOf(teamGrossCB.cam),
    narrativeText: presentationConfig?.narrative || '',
  }, presentationConfig?.overrides || {});

  // Effective slide list — respects user's order + disabled set, with
  // a "carrier" audience preset as the default. Custom edits the
  // operator made get preserved; switching audience replaces order
  // + disabled (and clears them when the operator picks "carrier"
  // first time).
  const audience = presentationConfig?.audience || 'carrier';
  const preset = AUDIENCE_PRESETS[audience] || AUDIENCE_PRESETS.carrier;
  const order = presentationConfig?.order || preset.order || AGENCY_SLIDE_CATALOG.map(s => s.id);
  const disabled = presentationConfig?.disabled || preset.disabled || {};
  // Append any catalog ids missing from saved order (e.g. new slides added in an update)
  const fullOrder = [...order, ...AGENCY_SLIDE_CATALOG.map(s => s.id).filter(id => !order.includes(id))];
  const slides = fullOrder.map(id => slideMap[id]).filter(s => s && !disabled[s.id]);

  const updateConfig = (mut) => {
    const base = {
      order: fullOrder,
      disabled: { ...disabled },
      overrides: { ...(presentationConfig?.overrides || {}) },
    };
    mut(base);
    setPresentationConfig(base);
  };
  const moveSlide = (id, dir) => updateConfig(c => {
    const idx = c.order.indexOf(id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= c.order.length) return;
    [c.order[idx], c.order[target]] = [c.order[target], c.order[idx]];
  });
  const toggleSlide = (id) => updateConfig(c => { c.disabled[id] = !c.disabled[id]; });
  const setOverride = (id, field, value) => updateConfig(c => {
    if (!c.overrides[id]) c.overrides[id] = {};
    if (value === '' || value == null) delete c.overrides[id][field];
    else c.overrides[id][field] = value;
    if (c.overrides[id] && Object.keys(c.overrides[id]).length === 0) delete c.overrides[id];
  });
  const setAudience = (audienceId) => {
    const p = AUDIENCE_PRESETS[audienceId];
    if (!p) return;
    setPresentationConfig({
      ...(presentationConfig || {}),
      audience: audienceId,
      // Switching audience REPLACES the visible order + disabled set.
      // The operator can still re-customize after, but they're not
      // stuck with stale settings from the prior audience.
      order: p.order,
      disabled: { ...p.disabled },
    });
  };
  const setNarrative = (text) => {
    setPresentationConfig({
      ...(presentationConfig || { audience: 'carrier' }),
      narrative: text,
    });
  };
  const resetConfig = () => setPresentationConfig(null);

  return (
    <div className="space-y-4">
      {presenting && <PresentationSlideshow slides={slides} onExit={()=>setPresenting(false)}/>}

      <div className="bg-gradient-to-br from-emerald-600 via-emerald-700 to-emerald-900 text-white rounded-2xl p-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-emerald-200 text-sm uppercase tracking-wider font-semibold">Presentation</div>
          <h1 className="text-3xl font-bold mt-1">{possessive(settings.ownerName)} Agency</h1>
          <p className="text-emerald-100 text-sm mt-1">All-time snapshot — {policies.length.toLocaleString()} total policies, {clients.size.toLocaleString()} active clients</p>
        </div>
        <button onClick={()=>setPresenting(true)} disabled={slides.length === 0} className="bg-white text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed px-5 py-3 rounded-xl font-semibold flex items-center gap-2 shadow-lg" title={slides.length === 0 ? 'Enable at least one slide below to present' : 'Start fullscreen presentation'}>
          <Play size={18} fill="currentColor"/> Start Presentation
          {slides.length > 0 && <span className="text-xs font-normal text-emerald-600/70">({slides.length} slide{slides.length === 1 ? '' : 's'})</span>}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <BigStat label="Policies in force" value={activePolicies.length.toLocaleString()} sub={`${policies.length} total ever`} color="emerald"/>
        <BigStat label="Active premium" value={fmt(totalActivePremium)} sub="annualized in-force" color="violet"/>
        <BigStat
          label="13-mo persistency"
          value={truePersistency == null ? '—' : `${truePersistency.toFixed(1)}%`}
          sub={truePersistency == null
            ? 'no policies ≥13 mo old yet'
            : `${persistencyCohortActive}/${persistencyCohortSize} cohort still active`}
          color={truePersistency == null ? 'slate' : truePersistency >= 85 ? 'emerald' : truePersistency >= 70 ? 'amber' : 'red'}
        />
        <BigStat
          label="Commission retention"
          value={`${commissionRetention.toFixed(1)}%`}
          sub={`${cbRate.toFixed(1)}% chargeback rate · advance × clawback`}
          color={commissionRetention >= 85 ? 'emerald' : commissionRetention >= 70 ? 'amber' : 'red'}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Monthly net commission" subtitle="All-time growth trend" fullscreenable>
          {(h) => (
            <ResponsiveContainer width="100%" height={h}>
              <LineChart data={growth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                <XAxis dataKey="label" tick={{fontSize:12}}/>
                <YAxis tickFormatter={fmtCompact} tick={{fontSize:12}}/>
                <Tooltip formatter={(v)=>fmt(v)}/>
                <Line type="monotone" dataKey="net" name="Net commission" stroke="#10b981" strokeWidth={3} dot={{r: 3}}/>
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Carrier mix" subtitle="Active premium by carrier" fullscreenable>
          {(h) => (
            <ResponsiveContainer width="100%" height={h}>
              <PieChart>
                <Pie data={carrierMix} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius="40%" outerRadius="75%" paddingAngle={2} label={(d) => `${d.name}: ${fmtCompact(d.value)}`}>
                  {carrierMix.map((entry, i) => <Cell key={i} fill={({ Americo: '#10b981', MOO: '#8b5cf6', Transamerica: '#f59e0b' })[entry.name] || '#3b82f6'}/>)}
                </Pie>
                <Tooltip formatter={(v)=>fmt(v)}/>
                <Legend/>
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Top 5 agents by active premium" fullscreenable>
          {(h) => (
            <ResponsiveContainer width="100%" height={h}>
              <BarChart data={topAgents.map(a => ({ name: (a.name || `#${a.prodNum}`).slice(0, 22), premium: a.premium }))} layout="vertical" margin={{left: 30}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                <XAxis type="number" tickFormatter={fmtCompact} tick={{fontSize:12}}/>
                <YAxis type="category" dataKey="name" tick={{fontSize:11}} width={130}/>
                <Tooltip formatter={(v)=>fmt(v)}/>
                <Bar dataKey="premium" name="Active premium" fill="#10b981"/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Team split (active policies)" fullscreenable>
          {(h) => (
            <ResponsiveContainer width="100%" height={h}>
              <PieChart>
                <Pie data={[
                  { name: `${possessive(settings.ownerName)} team`, value: teamCounts.mine, fill: '#10b981' },
                  { name: 'Unassigned', value: teamCounts.unassigned, fill: '#94a3b8' },
                ].filter(d => d.value > 0)} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius="40%" outerRadius="75%" paddingAngle={2} label={(d) => `${d.name}: ${d.value}`}>
                </Pie>
                <Tooltip/>
                <Legend/>
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Audience selector — drives which slides are in the deck */}
      <PresentationAudienceBar
        audience={audience}
        onSetAudience={setAudience}
        narrative={presentationConfig?.narrative || ''}
        onSetNarrative={setNarrative}
        agencyName={settings.ownerName ? `${possessive(settings.ownerName)} Agency` : 'Your Agency'}
        stats={{
          activePolicies: activePolicies.length,
          activePremium: totalActivePremium,
          totalCommission,
          totalChargebacks,
          truePersistency,
          overallPersistency,
          cbRate,
          topAgents: topAgents.slice(0, 5).map(a => ({ name: a.name, premium: a.premium, policies: a.policies })),
        }}
      />

      {/* Customize presentation */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <button onClick={()=>setCustomizeOpen(o => !o)} className="w-full flex items-center justify-between p-4 hover:bg-slate-50">
          <div className="flex items-center gap-3">
            <ChevronRight className={`text-slate-400 transition-transform ${customizeOpen?'rotate-90':''}`} size={16}/>
            <div className="text-left">
              <div className="font-semibold">Customize presentation</div>
              <div className="text-xs text-slate-500">{slides.length} of {AGENCY_SLIDE_CATALOG.length} slides selected · toggle, reorder, or rename</div>
            </div>
          </div>
          {presentationConfig && (
            <span onClick={(e)=>{e.stopPropagation(); resetConfig();}} className="text-xs text-slate-500 hover:text-slate-900 underline">Reset to defaults</span>
          )}
        </button>
        {customizeOpen && (
          <div className="border-t border-slate-200 p-4 space-y-2 bg-slate-50">
            <div className="text-xs text-slate-500 mb-2">Toggle slides on/off, reorder with ↑/↓, or override the title/subtitle text. Changes are saved automatically.</div>
            {fullOrder.map((id, idx) => {
              const cat = AGENCY_SLIDE_CATALOG.find(s => s.id === id);
              if (!cat) return null;
              const slide = slideMap[id];
              const isOn = !disabled[id];
              const override = (presentationConfig?.overrides || {})[id] || {};
              const canTitle = slide && ['title','closing','bigStats','chart','topAgents','valueProps','commissionTable','producerSuccess','narrative'].includes(slide.kind);
              return (
                <div key={id} className={`bg-white border border-slate-200 rounded-lg p-3 ${isOn ? '' : 'opacity-50'}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={isOn} onChange={()=>toggleSlide(id)} className="w-4 h-4 accent-emerald-600"/>
                      <span className="text-xs font-mono text-slate-400 w-6 text-center">{String(idx + 1).padStart(2, '0')}</span>
                      <span className="font-medium text-sm">{cat.label}</span>
                    </label>
                    <div className="ml-auto flex items-center gap-1">
                      <button onClick={()=>moveSlide(id, -1)} disabled={idx === 0} className="text-slate-400 hover:text-slate-900 disabled:opacity-25 disabled:hover:text-slate-400 p-1" title="Move up">▲</button>
                      <button onClick={()=>moveSlide(id, 1)} disabled={idx === fullOrder.length - 1} className="text-slate-400 hover:text-slate-900 disabled:opacity-25 disabled:hover:text-slate-400 p-1" title="Move down">▼</button>
                    </div>
                  </div>
                  {isOn && canTitle && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3 ml-9">
                      <input
                        type="text"
                        placeholder={slide.title || 'Title'}
                        value={override.title || ''}
                        onChange={e=>setOverride(id, 'title', e.target.value)}
                        className="px-2 py-1 border border-slate-200 rounded text-sm"
                      />
                      <input
                        type="text"
                        placeholder={slide.subtitle || 'Subtitle (optional)'}
                        value={override.subtitle || ''}
                        onChange={e=>setOverride(id, 'subtitle', e.target.value)}
                        className="px-2 py-1 border border-slate-200 rounded text-sm"
                      />
                    </div>
                  )}
                  {isOn && slide?.kind === 'closing' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 ml-9">
                      <input
                        type="text"
                        placeholder="CTA text (e.g. Scan to apply)"
                        value={override.cta || ''}
                        onChange={e=>setOverride(id, 'cta', e.target.value)}
                        className="px-2 py-1 border border-indigo-200 bg-indigo-50/50 rounded text-sm"
                      />
                      <input
                        type="url"
                        placeholder="QR-code URL (https://…)"
                        value={override.ctaUrl || ''}
                        onChange={e=>setOverride(id, 'ctaUrl', e.target.value)}
                        className="px-2 py-1 border border-indigo-200 bg-indigo-50/50 rounded text-sm"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-600">
        Tip: click <strong>Start Presentation</strong> to walk through the agency story slide-by-slide in fullscreen. Use ←/→ arrows to navigate, Esc to exit.
      </div>
    </div>
  );
}

// Reverse of AMERICO_AGENT_MAP, for showing the FF id back to the user in the weekly stats report.
const AMERICO_FF_REVERSE = (() => {
  const out = {};
  Object.entries(AMERICO_AGENT_MAP).forEach(([ff, pn]) => { if (!out[pn]) out[pn] = ff; });
  return out;
})();

function WeeklyStatsView({ transactions, allTransactions, producers, settings, periodMode, selectedWeek, openClientProfile = () => {} }) {
  const ownerLabel = possessive(settings.ownerName);

  // Carrier helper — bucket each tx into MOO / Americo / Transamerica
  const carrierOf = (tx) => deriveCarrier(tx);

  // ---- MOO weekly stats ----
  const mooTx = transactions.filter(tx => carrierOf(tx) === 'MOO');
  const mooByProducer = {};
  mooTx.forEach(tx => {
    const pn = tx.prodNum || 'unknown';
    if (!mooByProducer[pn]) mooByProducer[pn] = { prodNum: pn, name: getProducerName(pn, producers), team: getProducerTeam(pn, producers), gross: 0, cb: 0 };
    const amt = Number(tx.commAmt) || 0; // MOO source-of-truth is Comm Amt, not netToAccount
    if (amt >= 0) mooByProducer[pn].gross += amt;
    else mooByProducer[pn].cb += Math.abs(amt);
  });
  const mooRows = Object.values(mooByProducer).map(p => ({ ...p, net: p.gross - p.cb })).sort((a, b) => b.net - a.net);
  const mooMine = mooRows.filter(r => r.team === 'mine');
  const mooCam  = mooRows.filter(r => r.team === 'cam');
  const mooUnassigned = mooRows.filter(r => r.team !== 'mine' && r.team !== 'cam');
  const sumNet = (rows) => rows.reduce((s, r) => s + r.net, 0);
  const sumGross = (rows) => rows.reduce((s, r) => s + r.gross, 0);
  const sumCB = (rows) => rows.reduce((s, r) => s + r.cb, 0);
  const mooTotals = {
    mineGross: sumGross(mooMine), mineCB: sumCB(mooMine), mineNet: sumNet(mooMine),
    camGross: sumGross(mooCam),   camCB: sumCB(mooCam),   camNet: sumNet(mooCam),
    weekTotal: sumNet(mooRows),
  };

  // ---- Americo weekly stats ----
  // Group per agentId (FF-prefix). Earned commissions = positive AO-NA. Advances placed = positive IA-NA non-R.
  // Chargebacks = AR-TL / AR-BE / SV-TL / SV-TA / DE-PA.
  const americoTx = transactions.filter(tx => carrierOf(tx) === 'Americo');
  const americoByAgent = {};
  americoTx.forEach(tx => {
    const agentKey = tx.agentId || tx.prodNum || 'unknown';
    if (!americoByAgent[agentKey]) {
      americoByAgent[agentKey] = {
        agentId: tx.agentId || agentKey,
        prodNum: tx.prodNum,
        name: getProducerName(tx.prodNum, producers),
        team: getProducerTeam(tx.prodNum, producers),
        earned: 0,
        advances: 0,
        chargebacks: 0,
        dailyEarned: {}, // date → earned $ (used as EFT proxy for Harrison)
      };
    }
    const a = americoByAgent[agentKey];
    const trx = tx.trxType;
    const commAmt = Number(tx.commAmt) || 0;
    const advAmt  = Number(tx.advComm) || Number(tx.commPremium) || 0;
    if (trx === 'AO-NA' && commAmt > 0) {
      a.earned += commAmt;
      const d = tx.activityDate || '';
      a.dailyEarned[d] = (a.dailyEarned[d] || 0) + commAmt;
    } else if (trx === 'IA-NA') {
      // Per spec: IA-NA non-R = advances placed (new advances).
      // IA-NA+R = adjustments/reversals (NOT chargebacks). We bucket both into "advances" totals
      // but track sign so reversals decrease the advance balance.
      a.advances += advAmt;
    } else if (trx === 'AR-TL' || trx === 'AR-BE' || trx === 'SV-TL' || trx === 'SV-TA' || trx === 'DE-PA') {
      a.chargebacks += Math.abs(commAmt) || Math.abs(advAmt);
    }
  });
  const americoRows = Object.values(americoByAgent).sort((a, b) => (b.earned + b.advances) - (a.earned + a.advances));
  const americoMine = americoRows.filter(r => r.team === 'mine');
  const americoCam  = americoRows.filter(r => r.team === 'cam');
  const americoTotals = {
    mineEarned: americoMine.reduce((s, r) => s + r.earned, 0),
    camEarned:  americoCam.reduce((s, r) => s + r.earned, 0),
    mineAdvances: americoMine.reduce((s, r) => s + r.advances, 0),
    camAdvances:  americoCam.reduce((s, r) => s + r.advances, 0),
    mineCB: americoMine.reduce((s, r) => s + r.chargebacks, 0),
    camCB:  americoCam.reduce((s, r) => s + r.chargebacks, 0),
  };
  // Owner's daily earned = EFT proxy. Looked up by the configured
  // Americo FF id (settings.ownerAmericoAgentId) OR MOO prod num
  // (settings.ownerProdNum). Both come from Settings; if neither is
  // set, the card just shows $0 with a "configure your id" warning.
  const ownerFfId = String(settings?.ownerAmericoAgentId || '').trim().toUpperCase();
  const ownerPn   = String(settings?.ownerProdNum || '').trim();
  const ownerRow = (ownerFfId || ownerPn)
    ? americoRows.find(r => (ownerFfId && r.agentId === ownerFfId) || (ownerPn && r.prodNum === ownerPn))
    : null;
  const ownerDaily = ownerRow ? Object.entries(ownerRow.dailyEarned).sort((a, b) => (parseDate(a[0])?.getTime() || 0) - (parseDate(b[0])?.getTime() || 0)) : [];
  const ownerEftTotal = ownerDaily.reduce((s, [,v]) => s + v, 0);

  // ---- Transamerica weekly stats ----
  // Currently the parser captures advance-placed rows only — chargeback row types are not parsed.
  // We compute what's available and flag the gap.
  const transTx = transactions.filter(tx => carrierOf(tx) === 'Transamerica');
  const transRows = transTx.map(tx => ({
    policy: tx.policy,
    insured: tx.insured,
    agentName: tx.agentName || getProducerName(tx.prodNum, producers),
    team: getProducerTeam(tx.prodNum, producers),
    activityType: tx.activityType,
    amount: Number(tx.advComm) || Number(tx.commPremium) || 0,
    activityDate: tx.activityDate,
    net: Number(tx.netToAccount) || 0,
  }));
  const transMineAdv = transRows.filter(r => r.team === 'mine').reduce((s, r) => s + r.amount, 0);
  const transCamAdv  = transRows.filter(r => r.team === 'cam').reduce((s, r) => s + r.amount, 0);
  const transNet = transRows.reduce((s, r) => s + r.net, 0);

  // ---- Period label ----
  const periodLabel = periodMode === 'week' && selectedWeek && selectedWeek !== 'all'
    ? weekLabel(selectedWeek)
    : periodMode === 'week' ? 'All weeks'
    : periodMode === 'month' ? 'Current month filter'
    : 'Custom range';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white rounded-2xl p-5">
        <div className="text-xs text-slate-300 uppercase tracking-wider font-semibold">Weekly Stats — {periodLabel}</div>
        <div className="text-2xl font-bold mt-1">All three carriers</div>
        <p className="text-sm text-slate-300 mt-1">Per spec: MOO = Comm Amt only · Americo = AO-NA earned + IA-NA advances · Transamerica = OW advance placements.</p>
      </div>

      {/* Per-carrier summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-violet-50 border-2 border-violet-200 rounded-xl p-4">
          <div className="text-xs text-violet-700 font-semibold uppercase tracking-wide">MOO Net</div>
          <div className="text-2xl font-bold mt-1 text-violet-900">{fmt(mooTotals.weekTotal)}</div>
          <div className="text-xs text-violet-700 mt-1">Mine {fmt(mooTotals.mineNet)}</div>
        </div>
        <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4">
          <div className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">Americo EFT ({settings?.ownerName || 'Owner'})</div>
          <div className="text-2xl font-bold mt-1 text-emerald-900">{fmt(ownerEftTotal)}</div>
          <div className="text-xs text-emerald-700 mt-1">{ownerFfId ? `≈ sum of ${ownerFfId} AO-NA rows` : 'Set your Americo FF id in Settings'}</div>
        </div>
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
          <div className="text-xs text-amber-700 font-semibold uppercase tracking-wide">Transamerica Net</div>
          <div className="text-2xl font-bold mt-1 text-amber-900">{fmt(transNet)}</div>
          <div className="text-xs text-amber-700 mt-1">From advComm column · CB rows not parsed</div>
        </div>
      </div>

      {/* MOO section */}
      <CarrierSection
        title="MOO (Mutual of Omaha)"
        accent="violet"
        subtitle="Per producer · Comm Amt only · matches daily SUMMARY FIRST YEAR totals"
      >
        {mooMine.length > 0 && (
          <ProducerBreakdownTable label={`${ownerLabel} team`} rows={mooMine} columns={['gross','cb','net']}/>
        )}
        {mooUnassigned.length > 0 && (
          <ProducerBreakdownTable label="Unassigned" rows={mooUnassigned} columns={['gross','cb','net']}/>
        )}
        {mooRows.length === 0 && <EmptyHint message="No MOO transactions in the selected period."/>}
        <div className="bg-violet-50 border-t border-violet-200 grid grid-cols-2 md:grid-cols-3 gap-3 p-3 text-sm">
          <Sum label={`${ownerLabel} subtotal`} value={mooTotals.mineNet}/>
          <Sum label="Week total" value={mooTotals.weekTotal} bold/>
          <Sum label="Total chargebacks" value={mooTotals.mineCB + mooTotals.camCB} danger/>
        </div>
      </CarrierSection>

      {/* Americo section */}
      <CarrierSection
        title="Americo"
        accent="emerald"
        subtitle="Per FF-agent · AO-NA = earned · IA-NA = advances placed · AR/SV/DE = chargebacks"
        warning={ownerRow ? null : (ownerFfId || ownerPn) ? `No owner row found (${ownerFfId || ownerPn} not in data). EFT total is $0.` : 'Configure your Americo FF id in Settings to populate the EFT card.'}
      >
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-xs text-slate-500 text-left">
              <th className="py-2 px-3">Agent</th>
              <th className="py-2 px-3">FF ID</th>
              <th className="py-2 px-3">Team</th>
              <th className="py-2 px-3 text-right">Earned (AO-NA)</th>
              <th className="py-2 px-3 text-right">Advances (IA-NA)</th>
              <th className="py-2 px-3 text-right">Chargebacks</th>
            </tr>
          </thead>
          <tbody>
            {americoRows.length === 0 && (<tr><td colSpan={6} className="py-4 text-center text-slate-400">No Americo transactions in the selected period.</td></tr>)}
            {americoRows.map(r => (
              <tr key={r.agentId} className="border-b border-slate-100">
                <td className="py-2 px-3 font-medium">{r.name}</td>
                <td className="py-2 px-3 font-mono text-xs text-slate-500">{r.agentId}</td>
                <td className="py-2 px-3">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${r.team==='mine'?'bg-emerald-100 text-emerald-700':'bg-slate-100 text-slate-500'}`}>
                    {r.team === 'mine' ? settings.ownerName : '—'}
                  </span>
                </td>
                <td className="py-2 px-3 text-right font-semibold text-emerald-700">{fmt(r.earned)}</td>
                <td className="py-2 px-3 text-right">{fmt(r.advances)}</td>
                <td className="py-2 px-3 text-right text-red-600">{r.chargebacks > 0 ? fmt(r.chargebacks) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="bg-emerald-50 border-t border-emerald-200 grid grid-cols-2 md:grid-cols-3 gap-3 p-3 text-sm">
          <Sum label={`${ownerLabel} earned`} value={americoTotals.mineEarned}/>
          <Sum label="Total advances" value={americoTotals.mineAdvances + americoTotals.camAdvances}/>
          <Sum label="Total chargebacks" value={americoTotals.mineCB + americoTotals.camCB} danger/>
        </div>
        {ownerDaily.length > 0 && (
          <div className="border-t border-slate-200 p-3 bg-slate-50">
            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">{settings?.ownerName ? `${settings.ownerName}'s` : 'Owner'} daily EFT (approx)</div>
            <div className="flex flex-wrap gap-2 text-sm">
              {ownerDaily.map(([d, v]) => (
                <div key={d} className="bg-white rounded border border-slate-200 px-2 py-1">
                  <span className="text-xs text-slate-500">{d}</span> <span className="font-bold ml-1">{fmt(v)}</span>
                </div>
              ))}
              <div className="bg-emerald-100 rounded border border-emerald-300 px-2 py-1 font-bold text-emerald-900">
                Total {fmt(ownerEftTotal)}
              </div>
            </div>
            <div className="text-xs text-amber-700 mt-2">
              ⚠ Approximation: this sums the owner's AO-NA rows by activity date. The authoritative "* AMOUNT PAID TO AGENT VIA EFT" line isn't being captured by the parser yet — values should be close but may differ from the statement footer.
            </div>
          </div>
        )}
      </CarrierSection>

      {/* Transamerica section */}
      <CarrierSection
        title="Transamerica"
        accent="amber"
        subtitle="Per policy · advance placements only"
        warning="Parser gap: only OW Placed Adv rows are captured. OW Not Taken C/B and WA Lapse C/B rows are NOT parsed, so chargebacks here read as $0 even if your statement shows otherwise."
      >
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-xs text-slate-500 text-left">
              <th className="py-2 px-3">Policy</th>
              <th className="py-2 px-3">Insured</th>
              <th className="py-2 px-3">Agent</th>
              <th className="py-2 px-3">Team</th>
              <th className="py-2 px-3">Date</th>
              <th className="py-2 px-3 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {transRows.length === 0 && (<tr><td colSpan={6} className="py-4 text-center text-slate-400">No Transamerica transactions in the selected period.</td></tr>)}
            {transRows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="py-2 px-3 font-mono text-xs">{r.policy}</td>
                <td className="py-2 px-3"><ClientNameLink name={r.insured} openClientProfile={openClientProfile}/></td>
                <td className="py-2 px-3">{r.agentName}</td>
                <td className="py-2 px-3">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${r.team==='mine'?'bg-emerald-100 text-emerald-700':'bg-slate-100 text-slate-500'}`}>
                    {r.team === 'mine' ? settings.ownerName : '—'}
                  </span>
                </td>
                <td className="py-2 px-3">{r.activityDate}</td>
                <td className="py-2 px-3 text-right font-semibold">{fmt(r.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="bg-amber-50 border-t border-amber-200 grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 text-sm">
          <Sum label={`${ownerLabel} advances`} value={transMineAdv}/>
          <Sum label="Net EFT (current parse)" value={transNet} bold/>
        </div>
      </CarrierSection>

      {/* Cross-carrier rollup */}
      <div className="bg-slate-900 text-white rounded-2xl p-5">
        <div className="text-xs text-slate-300 uppercase tracking-wider font-semibold">Cross-carrier rollup</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
          <Roll label={`${ownerLabel} net (all carriers)`} value={mooTotals.mineNet + americoTotals.mineEarned + transMineAdv}/>
          <Roll label="MOO week net" value={mooTotals.weekTotal}/>
          <Roll label={`Americo EFT (${settings?.ownerName || 'Owner'})`} value={ownerEftTotal}/>
        </div>
      </div>
    </div>
  );
}

function CarrierSection({ title, subtitle, accent, warning, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className={`px-4 py-3 border-b border-slate-200 bg-${accent}-50`}>
        <div className={`font-bold text-${accent}-900`}>{title}</div>
        {subtitle && <div className={`text-xs text-${accent}-700 mt-0.5`}>{subtitle}</div>}
      </div>
      {warning && (
        <div className="bg-amber-50 border-b border-amber-200 text-xs text-amber-900 px-4 py-2">
          ⚠ {warning}
        </div>
      )}
      {children}
    </div>
  );
}

function ProducerBreakdownTable({ label, rows, columns }) {
  return (
    <div className="border-b border-slate-100">
      <div className="px-4 py-2 bg-slate-50 text-xs font-semibold text-slate-600 uppercase tracking-wide">{label}</div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-slate-500 text-left border-b border-slate-100">
            <th className="py-1.5 px-3">Agent</th>
            <th className="py-1.5 px-3 font-mono">Prod #</th>
            {columns.includes('gross') && <th className="py-1.5 px-3 text-right">Gross</th>}
            {columns.includes('cb') && <th className="py-1.5 px-3 text-right">Chargebacks</th>}
            {columns.includes('net') && <th className="py-1.5 px-3 text-right">Net</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.prodNum} className="border-b border-slate-50 last:border-0">
              <td className="py-1.5 px-3">{r.name}</td>
              <td className="py-1.5 px-3 font-mono text-xs text-slate-500">{r.prodNum}</td>
              {columns.includes('gross') && <td className="py-1.5 px-3 text-right">{fmt(r.gross)}</td>}
              {columns.includes('cb') && <td className="py-1.5 px-3 text-right text-red-600">{r.cb > 0 ? fmt(-r.cb) : '—'}</td>}
              {columns.includes('net') && <td className={`py-1.5 px-3 text-right font-semibold ${r.net < 0 ? 'text-red-600' : ''}`}>{fmt(r.net)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Sum({ label, value, bold, danger }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`${bold ? 'text-lg font-bold' : 'font-semibold'} ${danger ? 'text-red-700' : ''}`}>{fmt(value)}</div>
    </div>
  );
}

function Roll({ label, value }) {
  return (
    <div>
      <div className="text-xs text-slate-300 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-bold mt-1 text-white">{fmt(value)}</div>
    </div>
  );
}

function EmptyHint({ message }) {
  return <div className="p-4 text-center text-slate-400 text-sm">{message}</div>;
}

// DailyBreakdownView removed. It rendered "Total Owed to {camName}"
// + a per-day CAM TEAM / MY TEAM / OWNER breakdown built on the
// hardcoded CAM partner concept. Dead code (declared but never wired
// to a tab) at the time of removal.

function PasteStatementModal({ carrier = 'americo', pasteText, setPasteText, onClose, onImport, showToast }) {
  const textareaRef = useRef(null);
  const isTrans = carrier === 'transamerica';
  const label = isTrans ? 'Transamerica' : 'Americo';
  const headerHint = isTrans
    ? 'Copy the full Personal Advance Detail report and paste it below. Include the "Personal Advance Detail as of …" header AND every "Transaction Date :" section so each row lands on the right date.'
    : 'Copy the full Americo Commission Report and paste it below. Include the "AMERICO Commission Report as of …" header so the parser can detect the period.';
  const placeholder = isTrans
    ? `Weekly Personal Advance Detail as of MM/DD/YYYY\nFor LASTNAME, FIRSTNAME\nfrom MM/DD/YYYY to MM/DD/YYYY\nTransamerica Life Insurance Company\nAgent Number : XXXX######  Transaction Date : MM/DD/YYYY\nFEXB###### LastName OW Not Taken C/B Agent, Lastname 0.00 …\nIULA###### LastName OW Placed Adv Agent, Lastname 3,144.24 …`
    : `AMERICO Commission Report as of MM/DD/YYYY\n\n########## INSURED, NAME  35 MMDDYY PLAN MMDDYY …`;

  useEffect(() => {
    const t = setTimeout(() => textareaRef.current?.focus(), 50);
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const pasteFromClipboard = async () => {
    try {
      if (!navigator.clipboard?.readText) {
        showToast('This browser blocks programmatic clipboard reads. Click in the box and press Cmd+V.', 'error');
        return;
      }
      const text = await navigator.clipboard.readText();
      if (!text || !text.trim()) {
        showToast('Clipboard is empty', 'error');
        return;
      }
      setPasteText(prev => prev ? prev + '\n' + text : text);
      showToast(`Pulled ${text.length.toLocaleString()} characters from clipboard`);
    } catch (err) {
      showToast(`Couldn't read clipboard: ${err.message}. Try clicking the box and pressing Cmd+V.`, 'error');
    }
  };

  // Manually insert at caret on paste — bypasses any native quirks
  const onPaste = (e) => {
    const text = e.clipboardData?.getData('text') || '';
    if (text) {
      e.preventDefault();
      const ta = textareaRef.current;
      const start = ta?.selectionStart ?? pasteText.length;
      const end = ta?.selectionEnd ?? pasteText.length;
      setPasteText(pasteText.slice(0, start) + text + pasteText.slice(end));
    }
  };

  // Close on backdrop click — but only if the click really started on the backdrop
  const onBackdropMouseDown = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-6" onMouseDown={onBackdropMouseDown}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-6 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold">Paste {label} statement</h2>
            <p className="text-sm text-slate-500 mt-1">{headerHint}</p>
          </div>
          <button aria-label="Close" onClick={onClose} className="text-slate-400 hover:text-slate-700" title="Close (Esc)"><X size={20}/></button>
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-500">
          <button
            type="button"
            onClick={pasteFromClipboard}
            className="bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 text-sm"
            title="Read clipboard contents directly into the box"
          >
            <FileText size={14}/> Paste from clipboard
          </button>
          <span>or click inside the box and press <kbd className="bg-slate-100 border border-slate-300 rounded px-1.5 py-0.5 font-mono text-[10px]">⌘V</kbd> / <kbd className="bg-slate-100 border border-slate-300 rounded px-1.5 py-0.5 font-mono text-[10px]">Ctrl+V</kbd></span>
        </div>

        <textarea
          ref={textareaRef}
          value={pasteText}
          onChange={e => setPasteText(e.target.value)}
          onPaste={onPaste}
          placeholder={placeholder}
          rows={14}
          spellCheck={false}
          className="w-full border border-slate-200 rounded-lg p-3 font-mono text-xs bg-slate-50 focus:bg-white focus:outline-none focus:border-emerald-500 resize-vertical"
        />

        <div className="flex items-center justify-between gap-3">
          <div className="text-xs text-slate-500">
            {pasteText.length.toLocaleString()} characters · {pasteText.split('\n').filter(Boolean).length} non-empty lines
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPasteText('')} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Clear</button>
            <button
              onClick={onImport}
              disabled={!pasteText.trim()}
              className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1.5"
            >
              <CheckCircle size={14}/> Import
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BigStat({ label, value, sub, color }) {
  const colorMap = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    violet: 'border-violet-200 bg-violet-50 text-violet-900',
    slate: 'border-slate-200 bg-white text-slate-900',
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    red: 'border-red-200 bg-red-50 text-red-900',
  };
  return (
    <div className={`rounded-xl border-2 p-4 ${colorMap[color] || colorMap.slate}`}>
      <div className="text-xs font-semibold uppercase tracking-wide opacity-75">{label}</div>
      <div className="text-3xl font-bold mt-1">{value}</div>
      <div className="text-xs mt-1 opacity-70">{sub}</div>
    </div>
  );
}

// Master catalog of available slides — each has a stable id and short label for the customizer.
// Keep this list in the order you want as the default presentation order.
const AGENCY_SLIDE_CATALOG = [
  { id: 'title',                label: 'Title slide (branded)' },
  { id: 'narrative',            label: 'AI agency narrative — opener' },
  { id: 'book_size',            label: 'Book of Business (policies / clients / premium)' },
  { id: 'growth',               label: 'Growth — monthly net commission' },
  { id: 'persistency',          label: 'Persistency overview (with industry benchmark)' },
  { id: 'top_producers',        label: 'Top 5 producers' },
  { id: 'producer_success',     label: 'Producer success — top earner story' },
  { id: 'carrier_mix',          label: 'Carrier mix' },
  { id: 'team_split',           label: 'Team split (your team vs Cam vs unassigned)' },
  { id: 'money',                label: 'Lifetime commission + chargebacks' },
  { id: 'why_join',             label: 'Why agents join us (recruiting)' },
  { id: 'commission_structure', label: 'Commission structure (recruiting)' },
  { id: 'closing',              label: 'Closing — thank you + CTA / QR' },
];

// Audience presets reorder the deck and enable/disable slides based
// on who's in the room. The operator picks an audience, then can
// still customize from there. "Custom" preserves whatever they had.
const AUDIENCE_PRESETS = {
  carrier: {
    label: 'Carriers / IMOs',
    description: 'Production, persistency, quality of book.',
    order:    ['title','narrative','book_size','growth','persistency','top_producers','carrier_mix','team_split','money','closing'],
    disabled: { producer_success: true, why_join: true, commission_structure: true },
  },
  producer: {
    label: 'Recruiting producers',
    description: 'Why an agent should join your team.',
    order:    ['title','narrative','why_join','producer_success','commission_structure','top_producers','book_size','growth','closing'],
    disabled: { persistency: true, carrier_mix: true, team_split: true, money: true },
  },
  internal: {
    label: 'Internal team',
    description: 'Wins this week, leaderboard, where we\'re going.',
    order:    ['title','narrative','growth','top_producers','persistency','team_split','money','closing'],
    disabled: { producer_success: true, why_join: true, commission_structure: true, book_size: true, carrier_mix: true },
  },
};

// Industry benchmarks shown as small sub-text under stat slides so the
// audience can read "78% persistency" as "top quartile." Numbers are
// MP/IUL-weighted (more conservative than universal life benchmarks
// which are stricter).
const INDUSTRY_BENCHMARKS = {
  persistency13mo: 75, // MP industry average
  commissionRetention: 75,
  chargebackRate: 25,
};

// Default "Why agents join us" value props. Operator can edit per
// slide via the customizer override.
const DEFAULT_WHY_JOIN = [
  { title: 'Quality leads provided', body: 'Exclusive aged leads, no upfront cost. You focus on closing — we handle acquisition.' },
  { title: 'AI follow-up automation', body: 'Workflows text every lead at the right cadence. No follow-up falls through the cracks.' },
  { title: 'Training + ride-alongs', body: 'Personalized 30-day onboarding. Scripts, objection handling, real call coaching.' },
  { title: 'Top producers earning $250k+', body: 'Real numbers from our roster — not a stock photo or a promise.' },
];

// Default commission structure rows. Operator should customize via
// the customizer to reflect their actual contracts. Placeholder
// values are intentionally conservative so the slide isn't blank.
const DEFAULT_COMMISSION_ROWS = [
  { tier: 'Year 1 base',      detail: '85% street level',     note: 'Standard for new producers' },
  { tier: 'Year 2 retention', detail: '+5% bonus over base',  note: 'After 12 months of consistent production' },
  { tier: 'Override pool',    detail: '5–10% on team writes', note: 'When you bring on a downline' },
  { tier: 'Renewals',         detail: 'Vested at 24 months',  note: 'You own your book' },
];

function buildAgencySlides(d, overrides = {}) {
  const { settings, activePolicies, policies, totalActivePremium, totalCommission, totalChargebacks, overallPersistency, cbRate, truePersistency, persistencyCohortSize, clients, growth, carrierMix, topAgents, teamCounts, teamPremium, minePersistency, narrativeText } = d;
  const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const ov = (id, defaults) => ({ ...defaults, ...(overrides[id] || {}) });

  const benchmarkLine = (value, benchmark, betterIsHigher = true) => {
    if (value == null) return '';
    const diff = value - benchmark;
    const verdict = betterIsHigher
      ? (diff >= 10 ? 'top quartile' : diff >= 0 ? 'above industry' : diff >= -10 ? 'near industry' : 'below industry')
      : (diff <= -10 ? 'top quartile' : diff <= 0 ? 'below industry' : diff <= 10 ? 'near industry' : 'above industry');
    return `industry ${benchmark}% · ${verdict}`;
  };

  return {
    title: { id: 'title', kind: 'title',
      ...ov('title', {
        agency: `${possessive(settings.ownerName || 'Your')} Agency`,
        subtitle: 'Performance Overview',
        footer: today,
        ownerName: settings.ownerName || '',
        brandColor: settings.brandColor || '#10b981',
        brandLogoUrl: settings.brandLogoUrl || '',
      }),
    },
    narrative: { id: 'narrative', kind: 'narrative',
      ...ov('narrative', {
        title: 'The state of the agency',
        body: narrativeText || `In the time we've been tracking, this agency has written ${activePolicies.length.toLocaleString()} active polic${activePolicies.length === 1 ? 'y' : 'ies'}${totalActivePremium > 0 ? ` representing ${fmt(totalActivePremium)} of annualized premium` : ''}. ${truePersistency != null ? `We hold ${truePersistency.toFixed(0)}% 13-month persistency on the book — ${truePersistency >= INDUSTRY_BENCHMARKS.persistency13mo ? 'above' : 'in line with'} the MP industry standard of ${INDUSTRY_BENCHMARKS.persistency13mo}%.` : 'Click Generate AI narrative to write a personalized opener.'}`,
        regeneratable: true,
      }),
    },
    book_size: { id: 'book_size', kind: 'bigStats',
      ...ov('book_size', { title: 'Book of Business' }),
      stats: [
        { label: 'Active policies', value: activePolicies.length.toLocaleString(), accent: 'emerald' },
        { label: 'Total ever', value: policies.length.toLocaleString(), accent: 'slate' },
        { label: 'Active clients', value: clients.size.toLocaleString(), accent: 'violet' },
        { label: 'Active premium', value: fmt(totalActivePremium), accent: 'emerald' },
      ],
    },
    growth: { id: 'growth', kind: 'chart',
      ...ov('growth', { title: 'Growth — monthly net commission', subtitle: 'Trend over all uploaded periods' }),
      chart: { type: 'line', data: growth, dataKey: 'net', color: '#34d399' },
    },
    persistency: { id: 'persistency', kind: 'bigStats',
      // Subtitle judgment is based on real 13-mo persistency when we have a
      // cohort; otherwise we fall back to commission retention so early-stage
      // books still get a signal. Real persistency is the industry definition;
      // commission retention is the cash-flow KPI — both are shown.
      //
      // EMPTY-BOOK GUARD: when the operator has zero transactions (brand-new
      // account), don't show "Needs attention" — that read as a red distress
      // signal on day one. Show a neutral "No policies yet" instead. Real
      // judgment only kicks in once data exists.
      ...ov('persistency', { title: 'Persistency', subtitle:
        (!policies || policies.length === 0)
          ? 'No policies yet'
          : truePersistency != null
          ? (truePersistency >= 85 ? 'Healthy book' : truePersistency >= 70 ? 'Watch — some lapses' : 'Needs attention')
          : (overallPersistency >= 85 ? 'Healthy book' : overallPersistency >= 70 ? 'Watch — moderate chargebacks' : 'Needs attention')
      }),
      stats: [
        {
          label: '13-mo persistency',
          value: truePersistency == null ? '—' : `${truePersistency.toFixed(1)}%`,
          sub: truePersistency == null
            ? 'no policies ≥13 mo old yet'
            : benchmarkLine(truePersistency, INDUSTRY_BENCHMARKS.persistency13mo),
          accent: truePersistency == null ? 'slate' : truePersistency >= 85 ? 'emerald' : truePersistency >= 70 ? 'amber' : 'red',
        },
        {
          label: 'Commission retention',
          value: `${overallPersistency.toFixed(1)}%`,
          sub: benchmarkLine(overallPersistency, INDUSTRY_BENCHMARKS.commissionRetention),
          accent: overallPersistency >= 85 ? 'emerald' : overallPersistency >= 70 ? 'amber' : 'red',
        },
        {
          label: 'Chargeback rate',
          value: `${cbRate.toFixed(1)}%`,
          sub: benchmarkLine(cbRate, INDUSTRY_BENCHMARKS.chargebackRate, false),
          accent: cbRate < 15 ? 'emerald' : cbRate < 30 ? 'amber' : 'red',
        },
        {
          label: `${possessive(settings.ownerName)} team retention`,
          value: `${minePersistency.toFixed(0)}%`,
          sub: 'commission $',
          accent: minePersistency >= 85 ? 'emerald' : minePersistency >= 70 ? 'amber' : 'red',
        },
      ],
    },
    top_producers: { id: 'top_producers', kind: 'topAgents',
      ...ov('top_producers', { title: 'Top 5 producers', subtitle: 'By active premium' }),
      agents: topAgents,
    },
    carrier_mix: { id: 'carrier_mix', kind: 'chart',
      ...ov('carrier_mix', { title: 'Carrier mix', subtitle: 'Where the book is concentrated' }),
      chart: { type: 'pie', data: carrierMix },
    },
    team_split: { id: 'team_split', kind: 'bigStats',
      ...ov('team_split', { title: 'Team split' }),
      stats: [
        { label: `${possessive(settings.ownerName)} team`, value: `${teamCounts.mine.toLocaleString()} active policies`, sub: fmt(teamPremium.mine || 0), accent: 'emerald' },
        { label: 'Unassigned', value: `${teamCounts.unassigned.toLocaleString()} policies`, accent: 'slate' },
      ],
    },
    money: { id: 'money', kind: 'bigStats',
      ...ov('money', { title: 'Money' }),
      stats: [
        { label: 'Total commission (lifetime)', value: fmt(totalCommission), accent: 'emerald' },
        { label: 'Total chargebacks (lifetime)', value: fmt(totalChargebacks), accent: 'red' },
      ],
    },
    producer_success: { id: 'producer_success', kind: 'producerSuccess',
      ...ov('producer_success', {
        title: 'What it looks like here',
        subtitle: 'Top producer story — what your year could look like',
        agents: topAgents.slice(0, 3),
      }),
    },
    why_join: { id: 'why_join', kind: 'valueProps',
      ...ov('why_join', {
        title: 'Why agents join us',
        subtitle: 'What you get when you sign with this agency',
        items: DEFAULT_WHY_JOIN,
      }),
    },
    commission_structure: { id: 'commission_structure', kind: 'commissionTable',
      ...ov('commission_structure', {
        title: 'Commission structure',
        subtitle: 'Edit this slide to match your actual contracts',
        rows: DEFAULT_COMMISSION_ROWS,
      }),
    },
    closing: { id: 'closing', kind: 'closing',
      ...ov('closing', {
        title: 'Thank you',
        subtitle: `${activePolicies.length.toLocaleString()} active policies · ${fmt(totalActivePremium)} active premium · ${
          truePersistency != null
            ? `${truePersistency.toFixed(0)}% 13-mo persistency`
            : `${overallPersistency.toFixed(0)}% commission retention`
        }`,
        footer: today,
        cta: '',
        ctaUrl: '',
      }),
    },
  };
}

function PresentationSlideshow({ slides, onExit }) {
  const [i, setI] = useState(0);
  const next = () => setI(x => Math.min(slides.length - 1, x + 1));
  const prev = () => setI(x => Math.max(0, x - 1));

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onExit();
      else if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') { e.preventDefault(); prev(); }
      else if (e.key === 'Home') setI(0);
      else if (e.key === 'End') setI(slides.length - 1);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prevOverflow; };
  }, [slides.length, onExit]);

  const slide = slides[i];

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-slate-900 via-slate-950 to-emerald-950 text-white flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-8 py-4">
        <div className="text-xs text-slate-400 uppercase tracking-wider">Slide {i + 1} of {slides.length}</div>
        <button onClick={onExit} className="text-slate-300 hover:text-white text-sm flex items-center gap-2 bg-slate-800/70 hover:bg-slate-700 px-3 py-1.5 rounded-lg">
          <X size={16}/> Exit (Esc)
        </button>
      </div>

      {/* Slide body */}
      <div className="flex-1 flex items-center justify-center px-12 pb-8">
        <div className="w-full max-w-6xl">
          <SlideContent slide={slide}/>
        </div>
      </div>

      {/* Bottom nav */}
      <div className="flex items-center justify-between px-8 py-4">
        <button onClick={prev} disabled={i === 0} className="text-slate-300 hover:text-white disabled:opacity-30 flex items-center gap-2 bg-slate-800/70 hover:bg-slate-700 disabled:hover:bg-slate-800/70 px-4 py-2 rounded-lg text-sm">
          <ChevronLeft size={16}/> Prev
        </button>
        <div className="flex items-center gap-1.5">
          {slides.map((_, idx) => (
            <button key={idx} onClick={()=>setI(idx)} className={`w-2 h-2 rounded-full transition-all ${idx === i ? 'bg-emerald-400 w-6' : 'bg-slate-600 hover:bg-slate-500'}`}/>
          ))}
        </div>
        <button onClick={next} disabled={i === slides.length - 1} className="text-slate-300 hover:text-white disabled:opacity-30 flex items-center gap-2 bg-slate-800/70 hover:bg-slate-700 disabled:hover:bg-slate-800/70 px-4 py-2 rounded-lg text-sm">
          Next <ChevronRight size={16}/>
        </button>
      </div>
    </div>
  );
}

const ACCENT = {
  emerald: 'text-emerald-300',
  violet: 'text-violet-300',
  amber: 'text-amber-300',
  red: 'text-rose-300',
  slate: 'text-slate-200',
};

// Audience switcher + AI narrative generator. Sits above the deep
// customizer (toggles/reorder) so the operator picks WHO they're
// presenting to first, then tunes from there.
function PresentationAudienceBar({ audience, onSetAudience, narrative, onSetNarrative, agencyName, stats }) {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [draft, setDraft] = React.useState(narrative || '');
  React.useEffect(() => { setDraft(narrative || ''); }, [narrative]);

  async function generate() {
    setBusy(true); setErr(null);
    try {
      const out = await generateAgencyNarrative({ agency: agencyName, stats, audience });
      if (!out?.narrative) { setErr('AI returned no narrative.'); return; }
      onSetNarrative(out.narrative);
      setDraft(out.narrative);
    } catch (e) {
      setErr(e.message || 'Failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-violet-50 border border-indigo-200 rounded-xl p-4 space-y-3">
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-indigo-700">Audience</div>
        <div className="text-xs text-slate-600 mt-0.5">Pick who's in the room — the deck reorders + swaps slides automatically.</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {Object.entries(AUDIENCE_PRESETS).map(([id, p]) => {
          const active = audience === id;
          return (
            <button
              key={id}
              onClick={() => onSetAudience(id)}
              className={`text-left rounded-lg border-2 px-3 py-2 transition-colors ${active ? 'bg-indigo-600 border-indigo-700 text-white shadow' : 'bg-white border-slate-200 hover:border-indigo-300'}`}
            >
              <div className={`font-semibold text-sm ${active ? 'text-white' : 'text-slate-900'}`}>{p.label}</div>
              <div className={`text-xs mt-0.5 ${active ? 'text-indigo-100' : 'text-slate-500'}`}>{p.description}</div>
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2">
        <div className="flex items-baseline justify-between gap-2">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Opening narrative</div>
            <div className="text-[10px] text-slate-500">The 2-3 sentence story on the second slide. AI tunes it to your audience.</div>
          </div>
          <button
            onClick={generate}
            disabled={busy}
            className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap"
          >
            {busy ? 'Writing…' : (narrative ? 'Regenerate' : 'Generate')}
          </button>
        </div>
        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={() => { if (draft !== (narrative || '')) onSetNarrative(draft); }}
          rows={3}
          placeholder="Click Generate to have AI write a narrative tailored to your selected audience, or type your own here."
          className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-indigo-500 focus:outline-none"
        />
        {err && <div className="text-xs text-red-600">{err}</div>}
      </div>
    </div>
  );
}

function SlideContent({ slide }) {
  if (!slide) return null;
  if (slide.kind === 'title') {
    return (
      <div className="text-center">
        <div className="text-emerald-300 text-sm uppercase tracking-[0.3em] mb-6">{slide.footer}</div>
        {slide.brandLogoUrl && (
          <img src={slide.brandLogoUrl} alt="logo" className="mx-auto mb-8 max-h-24 object-contain"/>
        )}
        <h1 className="text-7xl font-bold tracking-tight">{slide.agency || slide.title}</h1>
        {slide.ownerName && (
          <p className="text-2xl text-slate-400 mt-4 tracking-wide">Presented by {slide.ownerName}</p>
        )}
        <p className="text-3xl text-slate-300 mt-6">{slide.subtitle}</p>
        <div className="mt-12 inline-block px-4 py-1 rounded-full border border-emerald-400/40 text-emerald-300 text-xs uppercase tracking-[0.3em]">SFA CRM</div>
      </div>
    );
  }
  if (slide.kind === 'narrative') {
    return (
      <div className="max-w-none mx-auto">
        <h2 className="text-4xl font-bold mb-8 text-emerald-300">{slide.title}</h2>
        <p className="text-3xl leading-relaxed text-white/90 font-light italic">"{slide.body}"</p>
      </div>
    );
  }
  if (slide.kind === 'valueProps') {
    return (
      <div>
        <div className="mb-10">
          <h2 className="text-5xl font-bold">{slide.title}</h2>
          {slide.subtitle && <p className="text-xl text-slate-400 mt-3">{slide.subtitle}</p>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {(slide.items || []).map((item, i) => (
            <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur">
              <div className="text-xs uppercase tracking-wider text-emerald-300 font-bold mb-2">#{i + 1}</div>
              <div className="text-2xl font-semibold text-white">{item.title}</div>
              <div className="text-base text-slate-300 mt-2 leading-relaxed">{item.body}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (slide.kind === 'commissionTable') {
    return (
      <div>
        <div className="mb-10">
          <h2 className="text-5xl font-bold">{slide.title}</h2>
          {slide.subtitle && <p className="text-xl text-slate-400 mt-3">{slide.subtitle}</p>}
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-2 backdrop-blur overflow-hidden">
          <table className="w-full text-lg">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-400 border-b border-white/10">
                <th className="text-left p-4">Tier</th>
                <th className="text-left p-4">Detail</th>
                <th className="text-left p-4 hidden md:table-cell">Notes</th>
              </tr>
            </thead>
            <tbody>
              {(slide.rows || []).map((r, i) => (
                <tr key={i} className="border-b border-white/5 last:border-0">
                  <td className="p-4 font-semibold text-emerald-300">{r.tier}</td>
                  <td className="p-4 text-white">{r.detail}</td>
                  <td className="p-4 text-slate-400 text-sm hidden md:table-cell">{r.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  if (slide.kind === 'producerSuccess') {
    return (
      <div>
        <div className="mb-10">
          <h2 className="text-5xl font-bold">{slide.title}</h2>
          {slide.subtitle && <p className="text-xl text-slate-400 mt-3">{slide.subtitle}</p>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {(slide.agents || []).map((a, idx) => (
            <div key={a.prodNum} className="bg-gradient-to-br from-emerald-500/10 to-emerald-700/5 border border-emerald-400/30 rounded-2xl p-6">
              <div className="text-xs uppercase tracking-wider text-emerald-300 font-bold">#{idx + 1} producer</div>
              <div className="text-2xl font-bold text-white mt-2">{a.name || `Producer ${a.prodNum}`}</div>
              <div className="mt-4 text-4xl font-bold text-emerald-300 tabular-nums">{fmt(a.premium)}</div>
              <div className="text-sm text-slate-300 mt-1">in annual premium written</div>
              <div className="mt-4 text-xs text-slate-400">{a.policies} active polic{a.policies === 1 ? 'y' : 'ies'} on the books</div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (slide.kind === 'closing') {
    const qrUrl = slide.ctaUrl
      ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=10&data=${encodeURIComponent(slide.ctaUrl)}`
      : null;
    return (
      <div className="text-center">
        <h1 className="text-7xl font-bold tracking-tight text-emerald-300">{slide.title}</h1>
        <p className="text-2xl text-slate-300 mt-8">{slide.subtitle}</p>
        {qrUrl && (
          <div className="mt-10 inline-flex flex-col items-center bg-white p-5 rounded-2xl">
            <img src={qrUrl} alt="QR code" className="w-48 h-48"/>
            {slide.cta && <div className="text-slate-900 text-sm font-semibold mt-3 max-w-[12rem]">{slide.cta}</div>}
          </div>
        )}
        {!qrUrl && slide.cta && (
          <div className="mt-10 inline-block bg-emerald-500 text-white px-6 py-3 rounded-xl text-xl font-semibold">{slide.cta}</div>
        )}
        <div className="text-sm uppercase tracking-[0.3em] text-slate-500 mt-12">{slide.footer}</div>
      </div>
    );
  }
  if (slide.kind === 'bigStats') {
    return (
      <div>
        <div className="mb-10">
          <h2 className="text-5xl font-bold">{slide.title}</h2>
          {slide.subtitle && <p className="text-xl text-slate-400 mt-3">{slide.subtitle}</p>}
        </div>
        <div className={`grid gap-6 ${slide.stats.length <= 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-2 md:grid-cols-4'}`}>
          {slide.stats.map((s, idx) => (
            <div key={idx} className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur">
              <div className="text-sm uppercase tracking-wider text-slate-400">{s.label}</div>
              <div className={`text-5xl font-bold mt-3 ${ACCENT[s.accent] || ACCENT.slate}`}>{s.value}</div>
              {s.sub && <div className="text-sm text-slate-400 mt-2">{s.sub}</div>}
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (slide.kind === 'topAgents') {
    return (
      <div>
        <div className="mb-8">
          <h2 className="text-5xl font-bold">{slide.title}</h2>
          {slide.subtitle && <p className="text-xl text-slate-400 mt-3">{slide.subtitle}</p>}
        </div>
        <div className="space-y-3">
          {slide.agents.map((a, idx) => (
            <div key={a.prodNum} className="flex items-center gap-6 bg-white/5 border border-white/10 rounded-2xl px-6 py-4">
              <div className="text-4xl font-bold text-emerald-400 w-12">{idx + 1}</div>
              <div className="flex-1">
                <div className="text-2xl font-semibold">{a.name || `#${a.prodNum}`}</div>
                <div className="text-sm text-slate-400">{a.policies} active polic{a.policies === 1 ? 'y' : 'ies'}</div>
              </div>
              <div className="text-3xl font-bold text-emerald-300">{fmt(a.premium)}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (slide.kind === 'chart') {
    return (
      <div>
        <div className="mb-6">
          <h2 className="text-5xl font-bold">{slide.title}</h2>
          {slide.subtitle && <p className="text-xl text-slate-400 mt-3">{slide.subtitle}</p>}
        </div>
        <div className="bg-white rounded-2xl p-6">
          <div style={{ height: '60vh', minHeight: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              {slide.chart.type === 'line' ? (
                <LineChart data={slide.chart.data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                  <XAxis dataKey="label" tick={{fontSize:14}}/>
                  <YAxis tickFormatter={fmtCompact} tick={{fontSize:14}}/>
                  <Tooltip formatter={(v)=>fmt(v)}/>
                  <Line type="monotone" dataKey={slide.chart.dataKey} stroke={slide.chart.color || '#10b981'} strokeWidth={4} dot={{r: 4}}/>
                </LineChart>
              ) : (
                <PieChart>
                  <Pie data={slide.chart.data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius="40%" outerRadius="80%" paddingAngle={2} label={(d) => `${d.name}: ${fmtCompact(d.value)}`}>
                    {slide.chart.data.map((entry, i) => <Cell key={i} fill={({ Americo: '#10b981', MOO: '#8b5cf6', Transamerica: '#f59e0b' })[entry.name] || ['#3b82f6','#ef4444','#14b8a6'][i % 3]}/>)}
                  </Pie>
                  <Tooltip formatter={(v)=>fmt(v)}/>
                  <Legend/>
                </PieChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  }
  return null;
}

function WinbackView({ allTransactions, producers, settings, policyTracking, setPolicyTracking, clientNotes, setClientNotes, openClientProfile = () => {} }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [carrierFilter, setCarrierFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent'); // recent | oldest | days | status | agent | carrier
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  // Build the list of lapsed policies from allTransactions
  const lapsed = React.useMemo(() => {
    const polMap = {};
    (allTransactions || []).forEach(tx => {
      if (!tx.policy) return;
      const key = `${tx.mga||''}|${tx.policy}`;
      if (!polMap[key]) {
        polMap[key] = {
          key, mga: tx.mga || '', policy: tx.policy, insured: tx.insured || '',
          prodNum: tx.prodNum, state: tx.state || '',
          issueDate: tx.issueDate || '', lapseDate: '', carrier: deriveCarrier(tx),
          premium: 0, totalCommission: 0, chargebackCount: 0,
        };
      }
      const p = polMap[key];
      p.totalCommission += (tx.netToAccount || 0);
      if (tx.commPremium && Math.abs(tx.commPremium) > p.premium) p.premium = Math.abs(tx.commPremium);
      if (!p.insured && tx.insured) p.insured = tx.insured;
      if (!p.issueDate && tx.issueDate) p.issueDate = tx.issueDate;
      const isCB = CHARGEBACK_TYPES.has(tx.activityType) || (tx.netToAccount || 0) < 0;
      if (isCB) {
        p.chargebackCount++;
        const d = parseDate(tx.activityDate);
        const ld = parseDate(p.lapseDate);
        if (d && (!ld || d < ld)) p.lapseDate = tx.activityDate; // earliest CB
      }
    });
    const today = new Date();
    return Object.values(polMap)
      .filter(p => p.chargebackCount > 0 || p.totalCommission < 0)
      .map(p => {
        const issue = parseDate(p.issueDate);
        const lapse = parseDate(p.lapseDate);
        const daysAlive = (issue && lapse) ? Math.floor((lapse - issue) / 86400000) : null;
        const daysSinceLapse = lapse ? Math.floor((today - lapse) / 86400000) : null;
        const team = getProducerTeam(p.prodNum, producers);
        const agentName = getProducerName(p.prodNum, producers);
        const tracking = policyTracking[p.key] || { status: 'new', notes: '', contactLog: [] };
        return { ...p, daysAlive, daysSinceLapse, team, agentName, status: tracking.status, notes: tracking.notes, contactLog: tracking.contactLog };
      });
  }, [allTransactions, producers, policyTracking]);

  // Apply filters
  const lc = search.trim().toLowerCase();
  const filtered = lapsed.filter(p => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (teamFilter !== 'all' && p.team !== teamFilter) return false;
    if (carrierFilter !== 'all' && p.carrier !== carrierFilter) return false;
    if (agentFilter !== 'all' && p.prodNum !== agentFilter) return false;
    if (lc) {
      const blob = `${p.insured} ${p.policy} ${p.agentName}`.toLowerCase();
      if (!blob.includes(lc)) return false;
    }
    return true;
  });

  // Sort
  const sorted = filtered.slice().sort((a, b) => {
    if (sortBy === 'oldest') return (parseDate(a.lapseDate)?.getTime() || 0) - (parseDate(b.lapseDate)?.getTime() || 0);
    if (sortBy === 'days') {
      const av = a.daysAlive == null ? 99999 : a.daysAlive;
      const bv = b.daysAlive == null ? 99999 : b.daysAlive;
      return av - bv;
    }
    if (sortBy === 'status') {
      const order = { new: 0, in_progress: 1, won_back: 2, lost: 3 };
      return (order[a.status] ?? 9) - (order[b.status] ?? 9);
    }
    if (sortBy === 'agent') {
      const an = (a.agentName || '').localeCompare(b.agentName || '');
      if (an !== 0) return an;
      // tiebreak: recent lapse first
      return (parseDate(b.lapseDate)?.getTime() || 0) - (parseDate(a.lapseDate)?.getTime() || 0);
    }
    if (sortBy === 'carrier') {
      const ci = carrierSortIndex(a.carrier) - carrierSortIndex(b.carrier);
      if (ci !== 0) return ci;
      // tiebreak: recent lapse first
      return (parseDate(b.lapseDate)?.getTime() || 0) - (parseDate(a.lapseDate)?.getTime() || 0);
    }
    return (parseDate(b.lapseDate)?.getTime() || 0) - (parseDate(a.lapseDate)?.getTime() || 0);
  });

  // Stats
  const statusCounts = WINBACK_STATUSES.reduce((acc, s) => { acc[s.id] = lapsed.filter(p => p.status === s.id).length; return acc; }, {});
  const carriers = sortByCarrierOrder(Array.from(new Set(lapsed.map(p => p.carrier))), x => x);
  // Agents with at least one lapsed policy, sorted by name
  const agentOptions = (() => {
    const map = new Map();
    lapsed.forEach(p => { if (p.prodNum && !map.has(p.prodNum)) map.set(p.prodNum, p.agentName || `#${p.prodNum}`); });
    return Array.from(map.entries()).sort((a, b) => (a[1] || '').localeCompare(b[1] || ''));
  })();

  const updateTracking = (policyKey, mut) => {
    setPolicyTracking(prev => {
      const current = prev[policyKey] || { status: 'new', notes: '', contactLog: [] };
      const next = { ...current };
      mut(next);
      return { ...prev, [policyKey]: next };
    });
  };
  const setStatus = (policyKey, status) => updateTracking(policyKey, t => { t.status = status; });
  const setPolicyNotes = (policyKey, text) => updateTracking(policyKey, t => { t.notes = text; });
  const addContact = (policyKey, entry) => updateTracking(policyKey, t => {
    t.contactLog = [{ ...entry, id: Date.now() }, ...(t.contactLog || [])];
  });
  const deleteContact = (policyKey, id) => updateTracking(policyKey, t => {
    t.contactLog = (t.contactLog || []).filter(c => c.id !== id);
  });

  const updateClientNote = (clientKey, text) => {
    setClientNotes(prev => ({ ...prev, [clientKey]: text }));
  };

  if (lapsed.length === 0) {
    return (
      <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-12 text-center">
        <CheckCircle className="mx-auto text-emerald-600 mb-3" size={48}/>
        <div className="text-xl font-bold text-emerald-900">No lapsed policies — clean book!</div>
        <div className="text-sm text-emerald-700 mt-2">Lapsed or charged-back policies will appear here once they show up in your statements.</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {WINBACK_STATUSES.map(s => (
          <button key={s.id} onClick={()=>setStatusFilter(statusFilter === s.id ? 'all' : s.id)} className={`text-left rounded-xl border-2 p-4 transition-all bg-${s.color}-50 border-${s.color}-200 hover:border-${s.color}-400 ${statusFilter === s.id ? `ring-2 ring-${s.color}-400` : ''}`}>
            <div className={`text-xs text-${s.color}-700 font-semibold uppercase tracking-wide`}>{s.label}</div>
            <div className={`text-3xl font-bold mt-1 text-${s.color}-900`}>{statusCounts[s.id]}</div>
            <div className={`text-xs text-${s.color}-700 mt-1`}>{s.desc}</div>
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-2">
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search insured, policy #, or agent" className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm flex-1 min-w-[200px]"/>
        <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white">
          <option value="all">All statuses</option>
          {WINBACK_STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <select value={teamFilter} onChange={e=>setTeamFilter(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white">
          <option value="all">All teams</option>
          <option value="mine">{possessive(settings.ownerName)} team</option>
          <option value="unassigned">Unassigned</option>
        </select>
        <select value={carrierFilter} onChange={e=>setCarrierFilter(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white">
          <option value="all">All carriers</option>
          {carriers.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={agentFilter} onChange={e=>setAgentFilter(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white">
          <option value="all">All agents</option>
          {agentOptions.map(([pn, name]) => <option key={pn} value={pn}>{name}</option>)}
        </select>
        <select value={sortBy} onChange={e=>setSortBy(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white" title="Sort by">
          <option value="recent">Sort: most recent lapse</option>
          <option value="oldest">Sort: oldest lapse</option>
          <option value="days">Sort: shortest lifespan</option>
          <option value="status">Sort: by status</option>
          <option value="agent">Sort: by agent (A→Z)</option>
          <option value="carrier">Sort: by carrier (Americo → MOO → Trans)</option>
        </select>
        <div className="ml-auto text-xs text-slate-500">{sorted.length} of {lapsed.length} polic{lapsed.length === 1 ? 'y' : 'ies'}</div>
      </div>

      {/* List */}
      <div className="space-y-2">
        {sorted.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 py-8 text-center text-slate-400">No policies match these filters</div>
        )}
        {sorted.map(p => (
          <WinbackRow
            key={p.key} p={p}
            isExpanded={expanded === p.key}
            onToggle={()=>setExpanded(expanded === p.key ? null : p.key)}
            settings={settings}
            onSetStatus={(s) => setStatus(p.key, s)}
            onSetNotes={(n) => setPolicyNotes(p.key, n)}
            onAddContact={(e) => addContact(p.key, e)}
            onDeleteContact={(id) => deleteContact(p.key, id)}
            clientNote={clientNotes[(p.insured || '').trim().toUpperCase()] || ''}
            onSetClientNote={(text) => updateClientNote((p.insured || '').trim().toUpperCase(), text)}
            openClientProfile={openClientProfile}
          />
        ))}
      </div>
    </div>
  );
}

function WinbackRow({ p, isExpanded, onToggle, settings, onSetStatus, onSetNotes, onAddContact, onDeleteContact, clientNote, onSetClientNote, openClientProfile = () => {} }) {
  const status = WINBACK_STATUSES.find(s => s.id === p.status) || WINBACK_STATUSES[0];
  const [newAction, setNewAction] = useState(CONTACT_ACTIONS[0]);
  const [newDate, setNewDate] = useState(() => new Date().toISOString().slice(0,10));
  const [newNotes, setNewNotes] = useState('');
  const submitContact = () => {
    if (!newDate) return;
    onAddContact({ date: newDate, action: newAction, notes: newNotes.trim() });
    setNewNotes('');
    setNewAction(CONTACT_ACTIONS[0]);
    setNewDate(new Date().toISOString().slice(0,10));
  };

  return (
    <div className={`bg-white rounded-xl border-2 overflow-hidden ${isExpanded ? 'border-slate-300' : 'border-slate-200'}`}>
      <button onClick={onToggle} className="w-full text-left p-4 hover:bg-slate-50 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <ChevronRight className={`text-slate-400 transition-transform shrink-0 ${isExpanded ? 'rotate-90' : ''}`} size={16}/>
          <span className={`px-2 py-0.5 rounded text-xs font-bold bg-${status.color}-100 text-${status.color}-700 shrink-0`}>{status.label.toUpperCase()}</span>
          <div className="min-w-0">
            <div className="font-semibold truncate">
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); openClientProfile(clientKeyOf(p.insured)); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); openClientProfile(clientKeyOf(p.insured)); } }}
                className="text-emerald-700 hover:text-emerald-900 hover:underline cursor-pointer"
                title="View full profile"
              >
                {titleCase(p.insured)}
              </span>
              <span className="text-slate-400 text-xs font-normal"> · {p.state}</span>
            </div>
            <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
              <span className="font-mono">{p.policy}</span>
              <span>·</span>
              <span>{p.carrier}</span>
              <span>·</span>
              <span>{p.agentName || '—'}</span>
              {p.contactLog?.length > 0 && <><span>·</span><span className="text-emerald-700 font-medium">{p.contactLog.length} contact{p.contactLog.length === 1 ? '' : 's'}</span></>}
              {p.notes && <><span>·</span><span className="text-amber-700 font-medium flex items-center gap-1"><FileText size={11}/>note</span></>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm shrink-0">
          <div className="text-right">
            <div className="text-xs text-slate-500">Lapsed</div>
            <div className="font-bold">{p.lapseDate || '—'}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500">Days alive</div>
            <div className={`font-bold ${(p.daysAlive != null && p.daysAlive < 30) ? 'text-red-600' : (p.daysAlive != null && p.daysAlive < 90) ? 'text-amber-600' : ''}`}>{p.daysAlive != null ? `${p.daysAlive}d` : '—'}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500">Loss</div>
            <div className="font-bold text-red-600">{fmt(p.totalCommission)}</div>
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-slate-200 bg-slate-50 p-4 space-y-4">
          {/* Status selector */}
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Status</div>
            <div className="flex flex-wrap gap-2">
              {WINBACK_STATUSES.map(s => {
                const active = p.status === s.id;
                return (
                  <button key={s.id} onClick={()=>onSetStatus(s.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${active ? `bg-${s.color}-600 text-white border-${s.color}-600` : `bg-white text-${s.color}-700 border-${s.color}-200 hover:bg-${s.color}-50`}`}>
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Policy details */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            <div className="bg-white rounded-lg border border-slate-200 p-2">
              <div className="text-xs text-slate-500">Issue date</div>
              <div className="font-semibold">{p.issueDate || '—'}</div>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-2">
              <div className="text-xs text-slate-500">Lapse date</div>
              <div className="font-semibold">{p.lapseDate || '—'}</div>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-2">
              <div className="text-xs text-slate-500">Days since lapse</div>
              <div className="font-semibold">{p.daysSinceLapse != null ? `${p.daysSinceLapse}d ago` : '—'}</div>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-2">
              <div className="text-xs text-slate-500">Premium</div>
              <div className="font-semibold">{p.premium > 0 ? fmt(p.premium) : '—'}</div>
            </div>
          </div>

          {/* Contact log */}
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Contact log ({p.contactLog?.length || 0})</div>
            <div className="bg-white rounded-lg border border-slate-200 p-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <input type="date" value={newDate} onChange={e=>setNewDate(e.target.value)} className="px-2 py-1.5 border border-slate-200 rounded text-sm"/>
                <select value={newAction} onChange={e=>setNewAction(e.target.value)} className="px-2 py-1.5 border border-slate-200 rounded text-sm bg-white">
                  {CONTACT_ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                <input value={newNotes} onChange={e=>setNewNotes(e.target.value)} onKeyDown={e=>{if(e.key==='Enter') submitContact();}} placeholder="What happened? (optional)" className="px-2 py-1.5 border border-slate-200 rounded text-sm flex-1 min-w-[200px]"/>
                <button onClick={submitContact} className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded text-sm font-semibold">Log contact</button>
              </div>
              {p.contactLog?.length > 0 && (
                <div className="divide-y divide-slate-100 pt-2">
                  {p.contactLog.map(c => (
                    <div key={c.id} className="py-2 flex items-start gap-3 text-sm">
                      <div className="text-xs text-slate-500 font-mono w-24 shrink-0">{fmtDateUS(c.date)}</div>
                      <div className="flex-1">
                        <div className="font-semibold">{c.action}</div>
                        {c.notes && <div className="text-slate-600 text-sm mt-0.5">{c.notes}</div>}
                      </div>
                      <button onClick={()=>onDeleteContact(c.id)} className="text-slate-400 hover:text-red-600 shrink-0" title="Delete entry"><Trash2 size={14}/></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Policy notes */}
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Policy notes</div>
            <textarea value={p.notes || ''} onChange={e=>onSetNotes(e.target.value)} placeholder="Notes about this specific policy…" rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"/>
          </div>

          {/* Client-level notes (shared with Book of Business) */}
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Client notes <span className="font-normal text-slate-400 ml-1">(shared with Book of Business)</span></div>
            <textarea value={clientNote || ''} onChange={e=>onSetClientNote(e.target.value)} placeholder="Notes about this client across all their policies…" rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"/>
          </div>
        </div>
      )}
    </div>
  );
}

function AgentPipelineSection({ agentProdNum, agentName, records, setPipeline, showToast, openClientProfile = () => {} }) {
  const [editingId, setEditingId] = useState(null); // null | 'new' | id
  const [confirmDel, setConfirmDel] = useState(null);

  const editingRec = records.find(r => r.id === editingId);

  const onSave = (record) => {
    const normalized = {
      ...record,
      contributionAmt: Number(record.contributionAmt) || 0,
      monthlyPremium: Number(record.contributionAmt) || 0,
      annualPremium: (Number(record.contributionAmt) || 0) * 12,
      commission: Number(record.commission) || 0,
      split: Number(record.split) || 0,
      chargebackAmount: Number(record.chargebackAmount) || 0,
      agentId: agentProdNum, // lock to this agent's prodNum
    };
    if (record.id) {
      setPipeline(prev => prev.map(p => p.id === record.id ? { ...p, ...normalized } : p));
      showToast(`Updated pipeline policy for ${agentName}`);
    } else {
      const id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `pipe-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setPipeline(prev => [...prev, { ...normalized, id }]);
      showToast(`Added pipeline policy to ${agentName}`);
    }
    setEditingId(null);
  };

  const onDelete = (id) => {
    setPipeline(prev => prev.filter(p => p.id !== id));
    setConfirmDel(null);
    showToast('Pipeline policy deleted');
  };

  return (
    <div className="bg-amber-50/30 border-t border-amber-200">
      <div className="flex items-center justify-between px-3 py-2 bg-amber-100/40 border-b border-amber-200">
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-amber-600"/>
          <div className="text-sm font-semibold text-amber-900">Pipeline · in-progress policies ({records.length})</div>
        </div>
        <button onClick={() => setEditingId(editingId === 'new' ? null : 'new')} className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded font-semibold">
          {editingId === 'new' ? 'Cancel' : '+ Add to pipeline'}
        </button>
      </div>

      {editingId !== null && (
        <div className="p-3 bg-white border-b border-amber-200">
          <PipelineForm
            initial={editingRec || { ...PIPELINE_BLANK_RECORD, agentId: agentProdNum }}
            isNew={editingId === 'new'}
            onSave={onSave}
            onCancel={() => setEditingId(null)}
          />
        </div>
      )}

      {records.length === 0 && editingId === null && (
        <div className="px-3 py-3 text-sm text-amber-800 italic">No in-progress policies in {agentName}'s pipeline. Click "+ Add to pipeline" to track one.</div>
      )}

      {records.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white border-b border-amber-200">
              <tr className="text-left text-slate-500 text-xs">
                <th className="py-2 px-3">Date</th>
                <th className="py-2 px-3">Client</th>
                <th className="py-2 px-3">Insured</th>
                <th className="py-2 px-3">Carrier</th>
                <th className="py-2 px-3">Policy #</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3">1st Payment</th>
                <th className="py-2 px-3 text-right">Monthly</th>
                <th className="py-2 px-3 text-right">Commission</th>
                <th className="py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.slice().sort((a, b) => {
                const parse = (s) => {
                  const parts = String(s || '').split('/');
                  if (parts.length !== 3) return 0;
                  return new Date(Number(parts[2]), Number(parts[0]) - 1, Number(parts[1])).getTime();
                };
                return parse(b.dateClosed) - parse(a.dateClosed);
              }).map(r => {
                const color = PIPELINE_STATUS_COLOR[r.status] || 'slate';
                const days = pipelineDaysRemaining(r.firstPaymentDate);
                return (
                  <React.Fragment key={r.id}>
                    <tr className="border-b border-amber-100 bg-white hover:bg-amber-50/50">
                      <td className="py-2 px-3">{r.dateClosed ? fmtDateUS(r.dateClosed) : '—'}</td>
                      <td className="py-2 px-3 font-medium"><ClientNameLink name={r.clientName} openClientProfile={openClientProfile}/></td>
                      <td className="py-2 px-3"><ClientNameLink name={r.insured} openClientProfile={openClientProfile}/></td>
                      <td className="py-2 px-3"><span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-700">{r.carrier || '—'}</span></td>
                      <td className="py-2 px-3 font-mono text-xs">{r.policyNumber || '—'}</td>
                      <td className="py-2 px-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-${color}-100 text-${color}-700`}>{r.status}</span></td>
                      <td className="py-2 px-3">
                        {r.firstPaymentDate && days != null && (
                          <span className="inline-flex items-center gap-1.5">
                            <span>{fmtDateUS(r.firstPaymentDate)}</span>
                            {days > 0 && <span className="px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-700">+{days}d</span>}
                            {days === 0 && <span className="px-1.5 py-0.5 rounded text-xs bg-emerald-100 text-emerald-700">today</span>}
                          </span>
                        )}
                        {r.firstPaymentDate && days == null && <span className="text-slate-400">{fmtDateUS(r.firstPaymentDate)}</span>}
                        {!r.firstPaymentDate && '—'}
                      </td>
                      <td className="py-2 px-3 text-right">{Number(r.monthlyPremium) > 0 ? fmt(r.monthlyPremium) : '—'}</td>
                      <td className="py-2 px-3 text-right">{Number(r.commission) > 0 ? fmt(r.commission) : '—'}</td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => setEditingId(r.id)} className="text-slate-500 hover:text-emerald-700 p-1" title="Edit"></button>
                          <button onClick={() => setConfirmDel(r.id)} className="text-slate-500 hover:text-red-600 p-1" title="Delete"><Trash2 size={14}/></button>
                        </div>
                      </td>
                    </tr>
                    {confirmDel === r.id && (
                      <tr className="bg-red-50 border-b border-red-200">
                        <td colSpan={10} className="py-2 px-3 text-sm">
                          Delete <strong>{r.clientName || 'this policy'}</strong>?
                          <button onClick={() => onDelete(r.id)} className="ml-3 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs font-semibold">Confirm</button>
                          <button onClick={() => setConfirmDel(null)} className="ml-2 bg-white border border-slate-300 hover:bg-slate-50 px-3 py-1 rounded text-xs">Cancel</button>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BookOfBusinessView({ transactions, allTransactions, producers, settings, clientNotes, setClientNotes, periodMode, clientProfiles = {}, setClientProfiles = () => {}, lobConfig = {}, showToast = () => {}, pipeline = [], setPipeline = () => {}, openClientProfile = () => {} }) {
  const [view, setView] = useState('agents'); // 'agents' | 'policies' | 'clients'
  const [search, setSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState('all');
  const [carrierFilter, setCarrierFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active'); // active | all | lapsed
  const [expandedClient, setExpandedClient] = useState(null);
  // Open client modal via the App-level handler so other views can open the same modal.
  const setActiveClientKey = openClientProfile;
  const [expandedAgent, setExpandedAgent] = useState(null);

  // BoB is a snapshot of current state; always use allTransactions for status determination
  // The period filter does not apply to in-force status, but we surface it as a note.

  // Group by policy
  const policyMap = {};
  (allTransactions || []).forEach(tx => {
    const key = `${tx.mga||''}|${tx.policy||''}`;
    if (!tx.policy) return;
    if (!policyMap[key]) {
      policyMap[key] = {
        key,
        policy: tx.policy,
        mga: tx.mga || '',
        company: tx.company || '',
        carrier: deriveCarrier(tx),
        prodNum: tx.prodNum,
        insured: tx.insured || '',
        state: tx.state || '',
        issueDate: tx.issueDate || '',
        firstActivity: tx.activityDate || '',
        lastActivity: tx.activityDate || '',
        premium: 0,
        totalCommission: 0,
        chargebackCount: 0,
        activities: [],
        chargebackTypes: new Set(),
        lapseDate: '', // earliest chargeback activity date
      };
    }
    const p = policyMap[key];
    p.activities.push(tx);
    p.totalCommission += (tx.netToAccount || 0);
    if (tx.commPremium && Math.abs(tx.commPremium) > p.premium) p.premium = Math.abs(tx.commPremium);
    if (!p.insured && tx.insured) p.insured = tx.insured;
    if (!p.issueDate && tx.issueDate) p.issueDate = tx.issueDate;
    const isCB = CHARGEBACK_TYPES.has(tx.activityType) || (tx.netToAccount || 0) < 0;
    if (isCB) {
      p.chargebackCount++;
      if (tx.activityType) p.chargebackTypes.add(tx.activityType);
      // Track earliest chargeback date = the lapse date
      const cbDate = parseDate(tx.activityDate);
      const currentLapse = parseDate(p.lapseDate);
      if (cbDate && (!currentLapse || cbDate < currentLapse)) p.lapseDate = tx.activityDate;
    }
    // Track activity range
    const d = parseDate(tx.activityDate);
    const earliest = parseDate(p.firstActivity);
    const latest = parseDate(p.lastActivity);
    if (d && (!earliest || d < earliest)) p.firstActivity = tx.activityDate;
    if (d && (!latest || d > latest)) p.lastActivity = tx.activityDate;
  });

  const today = new Date();
  const policies = Object.values(policyMap).map(p => {
    const status = p.chargebackCount > 0 || p.totalCommission < 0 ? 'lapsed' : 'active';
    const issue = parseDate(p.issueDate);
    const daysInForce = issue ? Math.floor((today - issue) / (1000*60*60*24)) : null;
    const team = getProducerTeam(p.prodNum, producers);
    const agentName = getProducerName(p.prodNum, producers);
    return { ...p, status, daysInForce, team, agentName };
  });

  // Filtering — carriers ordered Americo, MOO, Transamerica, then others alphabetically
  const carriers = sortByCarrierOrder(Array.from(new Set(policies.map(p => p.carrier))), x => x);
  const lc = search.trim().toLowerCase();
  const filteredPolicies = policies.filter(p => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (teamFilter !== 'all' && p.team !== teamFilter) return false;
    if (carrierFilter !== 'all' && p.carrier !== carrierFilter) return false;
    if (lc) {
      const blob = `${p.insured} ${p.policy} ${p.agentName}`.toLowerCase();
      if (!blob.includes(lc)) return false;
    }
    return true;
  }).sort((a, b) => {
    const ad = parseDate(a.issueDate)?.getTime() || 0;
    const bd = parseDate(b.issueDate)?.getTime() || 0;
    return bd - ad;
  });

  // Group by client (insured)
  const clientMap = {};
  policies.forEach(p => {
    if (!p.insured) return;
    const k = p.insured.trim().toUpperCase();
    if (!clientMap[k]) clientMap[k] = { key: k, displayName: titleCase(p.insured), policies: [], states: new Set(), agentCounts: {}, totalPremium: 0, totalCommission: 0 };
    const c = clientMap[k];
    c.policies.push(p);
    if (p.state) c.states.add(p.state);
    c.agentCounts[p.prodNum] = (c.agentCounts[p.prodNum] || 0) + 1;
    c.totalPremium += p.premium || 0;
    c.totalCommission += p.totalCommission || 0;
  });
  const clients = Object.values(clientMap).map(c => {
    const activeCount = c.policies.filter(p => p.status === 'active').length;
    const lapsedCount = c.policies.length - activeCount;
    const aor = Object.entries(c.agentCounts).sort((a,b) => b[1] - a[1])[0]?.[0];
    return {
      ...c,
      activeCount,
      lapsedCount,
      policyCount: c.policies.length,
      aorProdNum: aor,
      aorName: getProducerName(aor, producers),
      aorTeam: getProducerTeam(aor, producers),
      statesList: Array.from(c.states),
    };
  });

  const filteredClients = clients.filter(c => {
    if (teamFilter !== 'all' && c.aorTeam !== teamFilter) return false;
    if (lc && !c.displayName.toLowerCase().includes(lc)) return false;
    return true;
  }).sort((a, b) => b.totalPremium - a.totalPremium);

  // Summary cards
  const activePolicies = policies.filter(p => p.status === 'active');
  const totalPremiumActive = activePolicies.reduce((s, p) => s + (p.premium || 0), 0);
  const totalClients = clients.length;
  const totalActiveClients = clients.filter(c => c.activeCount > 0).length;

  // Group pipeline records by agentId. Defaults to the configured
  // owner prodNum (settings.ownerProdNum) when missing; if that isn't
  // set either, records bucket under '__unassigned' so they still render.
  const fallbackAgentId = String(settings?.ownerProdNum || '').trim() || '__unassigned';
  const pipelineByAgent = {};
  (pipeline || []).forEach(rec => {
    const pn = rec.agentId || fallbackAgentId;
    if (!pipelineByAgent[pn]) pipelineByAgent[pn] = [];
    pipelineByAgent[pn].push(rec);
  });

  // Group by agent — produces one row per producer with their book.
  // We start from transaction-derived policies, then merge in any pipeline-only agents
  // (agents who have pipeline records but no carrier transactions yet).
  const agentMap = {};
  policies.forEach(p => {
    const pn = p.prodNum || 'unknown';
    if (!agentMap[pn]) {
      agentMap[pn] = {
        prodNum: pn,
        name: p.agentName || `#${pn}`,
        team: p.team,
        policies: [],
        clients: new Set(),
        activeClients: new Set(),
      };
    }
    const a = agentMap[pn];
    a.policies.push(p);
    if (p.insured) {
      const ck = p.insured.trim().toUpperCase();
      a.clients.add(ck);
      if (p.status === 'active') a.activeClients.add(ck);
    }
  });
  // Ensure any agent with pipeline records but no carrier transactions still shows up
  Object.keys(pipelineByAgent).forEach(pn => {
    if (!agentMap[pn]) {
      agentMap[pn] = {
        prodNum: pn,
        name: getProducerName(pn, producers),
        team: getProducerTeam(pn, producers),
        policies: [],
        clients: new Set(),
        activeClients: new Set(),
      };
    }
  });
  const agentsList = Object.values(agentMap).map(a => {
    const activePol = a.policies.filter(p => p.status === 'active');
    const lapsedPol = a.policies.length - activePol.length;
    const totalPremium = a.policies.reduce((s, p) => s + (p.premium || 0), 0);
    const activePremium = activePol.reduce((s, p) => s + (p.premium || 0), 0);
    const totalCommission = a.policies.reduce((s, p) => s + (p.totalCommission || 0), 0);
    const persistency = a.policies.length > 0 ? (activePol.length / a.policies.length) * 100 : 0;
    const pipelineRecords = pipelineByAgent[a.prodNum] || [];
    return {
      ...a,
      activeCount: activePol.length,
      lapsedCount: lapsedPol,
      policyCount: a.policies.length,
      clientCount: a.clients.size,
      activeClientCount: a.activeClients.size,
      totalPremium,
      activePremium,
      totalCommission,
      persistency,
      pipelineRecords,
      pipelineCount: pipelineRecords.length,
    };
  });
  const filteredAgents = agentsList.filter(a => {
    if (teamFilter !== 'all' && a.team !== teamFilter) return false;
    if (lc && !`${a.name} ${a.prodNum}`.toLowerCase().includes(lc)) return false;
    return true;
  }).sort((a, b) => b.activePremium - a.activePremium);

  const updateNote = (clientKey, text) => {
    setClientNotes(prev => ({ ...prev, [clientKey]: text }));
  };

  return (
    <div className="space-y-4">
      {periodMode === 'custom' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          Note: the Book of Business is a current snapshot — the period filter doesn't apply here. Use the search and filters below to narrow the list.
        </div>
      )}

      {/* Touchpoint alerts + 6-month check-in reminders — same widgets as Dashboard */}
      <TouchpointAlertCard
        allTransactions={allTransactions}
        producers={producers}
        clientProfiles={clientProfiles}
        openClientProfile={openClientProfile}
      />
      <CheckInsDueCard
        allTransactions={allTransactions}
        producers={producers}
        clientProfiles={clientProfiles}
        openClientProfile={openClientProfile}
      />

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4">
          <div className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">Active policies</div>
          <div className="text-2xl font-bold mt-1 text-emerald-900">{activePolicies.length.toLocaleString()}</div>
          <div className="text-xs text-emerald-700 mt-1">in-force right now</div>
        </div>
        <div className="bg-slate-50 border-2 border-slate-200 rounded-xl p-4">
          <div className="text-xs text-slate-600 font-semibold uppercase tracking-wide">Total policies (ever)</div>
          <div className="text-2xl font-bold mt-1">{policies.length.toLocaleString()}</div>
          <div className="text-xs text-slate-600 mt-1">{policies.length - activePolicies.length} lapsed</div>
        </div>
        <div className="bg-violet-50 border-2 border-violet-200 rounded-xl p-4">
          <div className="text-xs text-violet-700 font-semibold uppercase tracking-wide">Active premium</div>
          <div className="text-2xl font-bold mt-1 text-violet-900">{fmt(totalPremiumActive)}</div>
          <div className="text-xs text-violet-700 mt-1">in-force annualized</div>
        </div>
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
          <div className="text-xs text-blue-700 font-semibold uppercase tracking-wide">Clients</div>
          <div className="text-2xl font-bold mt-1 text-blue-900">{totalClients.toLocaleString()}</div>
          <div className="text-xs text-blue-700 mt-1">{totalActiveClients} active</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-sm">
          <button onClick={()=>setView('agents')} className={`px-3 py-1.5 font-medium ${view==='agents'?'bg-emerald-600 text-white':'text-slate-600 hover:bg-slate-50'}`}>By Agent ({filteredAgents.length})</button>
          <button onClick={()=>setView('policies')} className={`px-3 py-1.5 font-medium ${view==='policies'?'bg-emerald-600 text-white':'text-slate-600 hover:bg-slate-50'}`}>Policies ({filteredPolicies.length})</button>
          <button onClick={()=>setView('clients')} className={`px-3 py-1.5 font-medium ${view==='clients'?'bg-emerald-600 text-white':'text-slate-600 hover:bg-slate-50'}`}>Clients ({filteredClients.length})</button>
        </div>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={view==='policies'?'Search insured, policy #, or agent':view==='clients'?'Search client name':'Search agent name'} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm flex-1 min-w-[200px]"/>
        <select value={teamFilter} onChange={e=>setTeamFilter(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white">
          <option value="all">All teams</option>
          <option value="mine">{possessive(settings.ownerName)} team</option>
          <option value="unassigned">Unassigned</option>
        </select>
        {view === 'policies' && (
          <>
            <select value={carrierFilter} onChange={e=>setCarrierFilter(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white">
              <option value="all">All carriers</option>
              {carriers.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white">
              <option value="active">Active only</option>
              <option value="lapsed">Lapsed only</option>
              <option value="all">All statuses</option>
            </select>
          </>
        )}
      </div>

      {/* By Agent view */}
      {view === 'agents' && (
        <div className="space-y-2">
          {filteredAgents.length === 0 && (
            <div className="bg-white rounded-xl border border-slate-200 py-8 text-center text-slate-400">No agents match these filters</div>
          )}
          {filteredAgents.map(a => {
            const isExpanded = expandedAgent === a.prodNum;
            const pColor = a.persistency >= 85 ? 'emerald' : a.persistency >= 70 ? 'amber' : 'red';
            const teamBadge = a.team === 'mine'
              ? <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-emerald-100 text-emerald-700">{settings.ownerName}</span>
              : <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-slate-100 text-slate-500">UNASSIGNED</span>;
            const sortedPolicies = a.policies.slice().sort((x, y) => {
              if (x.status !== y.status) return x.status === 'active' ? -1 : 1;
              return (parseDate(y.issueDate)?.getTime() || 0) - (parseDate(x.issueDate)?.getTime() || 0);
            });
            return (
              <div key={a.prodNum} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <button onClick={()=>setExpandedAgent(isExpanded ? null : a.prodNum)} className="w-full text-left p-4 hover:bg-slate-50 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <ChevronRight className={`text-slate-400 transition-transform ${isExpanded?'rotate-90':''}`} size={16}/>
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="font-semibold">{a.name}</div>
                        {teamBadge}
                      </div>
                      <div className="text-xs font-mono text-slate-500 mt-0.5">{a.prodNum}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-5 text-sm">
                    <div className="text-right">
                      <div className="text-xs text-slate-500">Policies</div>
                      <div className="font-bold"><span className="text-emerald-700">{a.activeCount}</span> <span className="text-slate-400">/ {a.policyCount}</span></div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">Clients</div>
                      <div className="font-bold">{a.activeClientCount} <span className="text-slate-400">/ {a.clientCount}</span></div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">Active premium</div>
                      <div className="font-bold">{fmt(a.activePremium)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">Comm earned</div>
                      <div className={`font-bold ${a.totalCommission >= 0 ? 'text-slate-700' : 'text-red-600'}`}>{fmt(a.totalCommission)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">Persistency</div>
                      <div className={`font-bold text-${pColor}-600`}>{a.persistency.toFixed(0)}%</div>
                    </div>
                    {a.pipelineCount > 0 && (
                      <div className="text-right">
                        <div className="text-xs text-slate-500">Pipeline</div>
                        <div className="font-bold text-amber-700">{a.pipelineCount}</div>
                      </div>
                    )}
                  </div>
                </button>
                {isExpanded && (
                  <div className="border-t border-slate-200 bg-slate-50">
                    {sortedPolicies.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-white border-b border-slate-200">
                            <tr className="text-left text-slate-500 text-xs">
                              <th className="py-2 px-3">Status</th>
                              <th className="py-2 px-3">Insured</th>
                              <th className="py-2 px-3">Policy #</th>
                              <th className="py-2 px-3">Carrier</th>
                              <th className="py-2 px-3">Issued</th>
                              <th className="py-2 px-3 text-right">Days</th>
                              <th className="py-2 px-3 text-right">Premium</th>
                              <th className="py-2 px-3 text-right">Comm earned</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedPolicies.map(p => {
                              const touchpoints = p.status === 'active'
                                ? getDuePolicyTouchpoints(p, (clientProfiles[clientKeyOf(p.insured)] || {}).contactLog)
                                : [];
                              return (
                              <tr key={p.key} className="border-b border-slate-100 bg-white align-top">
                                <td className="py-2 px-3"><span className={`px-1.5 py-0.5 rounded text-xs font-bold ${p.status==='active'?'bg-emerald-100 text-emerald-700':'bg-red-100 text-red-700'}`}>{p.status === 'active' ? 'ACTIVE' : 'LAPSED'}</span></td>
                                <td className="py-2 px-3">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <button onClick={()=>setActiveClientKey(clientKeyOf(p.insured))} className="font-semibold text-emerald-700 hover:text-emerald-900 hover:underline text-left" title="View full profile">
                                      {titleCase(p.insured)}
                                    </button>
                                    {touchpoints.map(tp => (
                                      <span
                                        key={tp.kind}
                                        title={`${tp.label} — ${tp.guidance}\n\nScript: ${tp.script}`}
                                        className={`px-1.5 py-0.5 rounded text-[10px] font-bold bg-${tp.color}-100 text-${tp.color}-800 cursor-help`}
                                      >
                                        ⚠ {tp.label}
                                      </span>
                                    ))}
                                  </div>
                                  {p.state && <div className="text-xs text-slate-500">{p.state}</div>}
                                </td>
                                <td className="py-2 px-3 font-mono text-xs">{p.policy}</td>
                                <td className="py-2 px-3">{p.carrier}</td>
                                <td className="py-2 px-3">{p.issueDate || '—'}</td>
                                <td className="py-2 px-3 text-right">{p.daysInForce != null ? `${p.daysInForce}d` : '—'}</td>
                                <td className="py-2 px-3 text-right">{p.premium > 0 ? fmt(p.premium) : '—'}</td>
                                <td className={`py-2 px-3 text-right font-semibold ${p.totalCommission >= 0 ? 'text-slate-700' : 'text-red-600'}`}>{fmt(p.totalCommission)}</td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {sortedPolicies.length === 0 && (
                      <div className="bg-white border-b border-slate-100 px-3 py-4 text-center text-slate-400 text-sm">
                        No carrier-statement policies yet for this agent. Pipeline below tracks in-progress policies.
                      </div>
                    )}

                    <AgentPipelineSection
                      agentProdNum={a.prodNum}
                      agentName={a.name}
                      records={a.pipelineRecords}
                      setPipeline={setPipeline}
                      showToast={showToast}
                      openClientProfile={openClientProfile}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Policies table */}
      {view === 'policies' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-slate-500 text-xs">
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3">Insured</th>
                  <th className="py-2 px-3">Policy #</th>
                  <th className="py-2 px-3">Carrier</th>
                  <th className="py-2 px-3">Agent</th>
                  <th className="py-2 px-3">Issued</th>
                  <th className="py-2 px-3 text-right">Days</th>
                  <th className="py-2 px-3 text-right">Premium</th>
                  <th className="py-2 px-3 text-right">Comm earned</th>
                </tr>
              </thead>
              <tbody>
                {filteredPolicies.length === 0 && (
                  <tr><td colSpan={9} className="py-6 text-center text-slate-400">No policies match these filters</td></tr>
                )}
                {filteredPolicies.map(p => (
                  <tr key={p.key} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-3">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${p.status==='active'?'bg-emerald-100 text-emerald-700':'bg-red-100 text-red-700'}`}>
                        {p.status === 'active' ? 'ACTIVE' : 'LAPSED'}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <button onClick={()=>setActiveClientKey(clientKeyOf(p.insured))} className="font-semibold text-emerald-700 hover:text-emerald-900 hover:underline text-left" title="View full profile">
                        {titleCase(p.insured)}
                      </button>
                      {p.state && <div className="text-xs text-slate-500">{p.state}</div>}
                    </td>
                    <td className="py-2 px-3 font-mono text-xs">{p.policy}</td>
                    <td className="py-2 px-3">{p.carrier}</td>
                    <td className="py-2 px-3">
                      <div>{p.agentName || '—'}</div>
                      <div className="text-xs font-mono text-slate-500">{p.prodNum}</div>
                    </td>
                    <td className="py-2 px-3">{p.issueDate || '—'}</td>
                    <td className="py-2 px-3 text-right">{p.daysInForce != null ? `${p.daysInForce}d` : '—'}</td>
                    <td className="py-2 px-3 text-right">{p.premium > 0 ? fmt(p.premium) : '—'}</td>
                    <td className={`py-2 px-3 text-right font-semibold ${p.totalCommission >= 0 ? 'text-slate-700' : 'text-red-600'}`}>{fmt(p.totalCommission)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Clients list */}
      {view === 'clients' && (
        <div className="space-y-2">
          {filteredClients.length === 0 && (
            <div className="bg-white rounded-xl border border-slate-200 py-8 text-center text-slate-400">No clients match these filters</div>
          )}
          {filteredClients.map(c => {
            const isExpanded = expandedClient === c.key;
            return (
              <div key={c.key} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <button onClick={()=>setExpandedClient(isExpanded ? null : c.key)} className="w-full text-left p-4 hover:bg-slate-50 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <ChevronRight className={`text-slate-400 transition-transform ${isExpanded?'rotate-90':''}`} size={16}/>
                    <div>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e)=>{ e.stopPropagation(); setActiveClientKey(c.key); }}
                        onKeyDown={(e)=>{ if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setActiveClientKey(c.key); } }}
                        className="font-semibold text-emerald-700 hover:text-emerald-900 hover:underline cursor-pointer"
                        title="Open full profile"
                      >
                        {c.displayName}
                      </span>
                      <div className="text-xs text-slate-500">
                        {c.statesList.join(', ') || 'unknown state'} · AOR: {c.aorName || '—'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="text-right">
                      <div className="text-xs text-slate-500">Policies</div>
                      <div className="font-bold"><span className="text-emerald-700">{c.activeCount}</span> <span className="text-slate-400">/ {c.policyCount}</span></div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">Premium</div>
                      <div className="font-bold">{fmt(c.totalPremium)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">Comm earned</div>
                      <div className={`font-bold ${c.totalCommission >= 0 ? 'text-slate-700' : 'text-red-600'}`}>{fmt(c.totalCommission)}</div>
                    </div>
                    {clientNotes[c.key] && <FileText size={16} className="text-amber-600" title="Has notes"/>}
                  </div>
                </button>
                {isExpanded && (
                  <ClientExpandedProfile
                    client={c}
                    note={clientNotes[c.key] || ''}
                    onNoteChange={(v) => updateNote(c.key, v)}
                    profile={clientProfiles[c.key] || {}}
                    onProfileChange={(next) => setClientProfiles(prev => ({ ...prev, [c.key]: next }))}
                    lobConfig={lobConfig}
                    showToast={showToast}
                  />
                )}
              </div>
            );
          })}
          <div className="flex items-center justify-end mt-2">
            <button
              onClick={() => exportPostcardCsv(filteredClients, clientProfiles, showToast)}
              className="text-sm bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-lg font-semibold"
            >
              Export postcard CSV ({filteredClients.length} clients)
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

// Build a client-like object from a clientKey when the client doesn't exist in the carrier-derived list
// (e.g. they only have pipeline records). Lets the modal still open cleanly.
function synthesizeClient(clientKey, policies, pipeline) {
  const carrierPols = (policies || []).filter(p => clientKeyOf(p.insured) === clientKey);
  const pipePols = (pipeline || []).filter(p => clientKeyOf(p.clientName || p.insured) === clientKey);
  const displayName = (carrierPols[0]?.insured) || (pipePols[0]?.clientName) || (pipePols[0]?.insured) || clientKey;
  const states = new Set();
  carrierPols.forEach(p => p.state && states.add(p.state));
  return {
    key: clientKey,
    displayName: titleCase(displayName),
    policies: carrierPols,
    activeCount: carrierPols.filter(p => p.status === 'active').length,
    lapsedCount: carrierPols.filter(p => p.status === 'lapsed').length,
    policyCount: carrierPols.length,
    totalPremium: carrierPols.reduce((s, p) => s + (p.premium || 0), 0),
    totalCommission: carrierPols.reduce((s, p) => s + (p.totalCommission || 0), 0),
    statesList: Array.from(states),
  };
}

// App-level wrapper that builds a synthesized client from raw transactions, so any view
// in the app can open a client profile by setting activeClientKey — without needing to
// route through BookOfBusinessView's local state.
function ClientDetailModalContainer({ clientKey, allTransactions, producers, pipeline, clientProfiles, setClientProfiles, clientNotes, setClientNotes, lobConfig, showToast, currentUser, onClose }) {
  // When the profile is opened by a clients-table UUID (e.g. from a health/ACA
  // view), it has NO carrier transactions, so synthesizeClient's name lookup —
  // which matches policies by NORMALIZED NAME, never a UUID — always misses and
  // falls back to showing the raw UUID as the title. Resolve the real name from
  // the clients table for UUID keys so the header shows the person, not an id.
  const isUuidKey = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(clientKey || ''));
  const [resolvedName, setResolvedName] = useState(null);
  const [nameLoading, setNameLoading] = useState(isUuidKey);
  useEffect(() => {
    if (!isUuidKey) { setNameLoading(false); return; }
    let cancel = false;
    setNameLoading(true); setResolvedName(null);
    (async () => {
      try {
        const { data } = await supabase.from('clients').select('name').eq('id', clientKey).maybeSingle();
        if (!cancel) setResolvedName(data?.name || null);
      } catch { /* leave null → placeholder, never the raw UUID */ }
      finally { if (!cancel) setNameLoading(false); }
    })();
    return () => { cancel = true; };
  }, [clientKey, isUuidKey]);

  // Build a policies array for this specific client only, matching the shape BoB uses.
  const polMap = {};
  const today = new Date();
  (allTransactions || []).forEach(tx => {
    if (!tx.policy) return;
    if (clientKeyOf(tx.insured) !== clientKey) return;
    const k = `${tx.mga||''}|${tx.policy}`;
    if (!polMap[k]) {
      polMap[k] = {
        key: k, policy: tx.policy, mga: tx.mga || '', company: tx.company || '',
        carrier: deriveCarrier(tx), prodNum: tx.prodNum, insured: tx.insured || '',
        state: tx.state || '', issueDate: tx.issueDate || '',
        firstActivity: tx.activityDate || '', lastActivity: tx.activityDate || '',
        premium: 0, totalCommission: 0, chargebackCount: 0,
        chargebackTypes: new Set(), lapseDate: '',
      };
    }
    const p = polMap[k];
    p.totalCommission += (tx.netToAccount || 0);
    if (tx.commPremium && Math.abs(tx.commPremium) > p.premium) p.premium = Math.abs(tx.commPremium);
    if (!p.insured && tx.insured) p.insured = tx.insured;
    if (!p.issueDate && tx.issueDate) p.issueDate = tx.issueDate;
    const isCB = CHARGEBACK_TYPES.has(tx.activityType) || (tx.netToAccount || 0) < 0;
    if (isCB) {
      p.chargebackCount++;
      if (tx.activityType) p.chargebackTypes.add(tx.activityType);
      const cbDate = parseDate(tx.activityDate);
      const currentLapse = parseDate(p.lapseDate);
      if (cbDate && (!currentLapse || cbDate < currentLapse)) p.lapseDate = tx.activityDate;
    }
  });
  const policies = Object.values(polMap).map(p => {
    const status = p.chargebackCount > 0 || p.totalCommission < 0 ? 'lapsed' : 'active';
    const issue = parseDate(p.issueDate);
    const daysInForce = issue ? Math.floor((today - issue) / 86400000) : null;
    const team = getProducerTeam(p.prodNum, producers);
    const agentName = getProducerName(p.prodNum, producers);
    return { ...p, status, daysInForce, team, agentName };
  });

  const client = synthesizeClient(clientKey, policies, pipeline);
  // For a UUID key the synthesized displayName is always the raw UUID (no policy
  // name to match) — replace it with the real clients-table name (or a neutral
  // placeholder while loading / if the record has no name), never the id.
  if (isUuidKey) {
    client.displayName = resolvedName ? titleCase(resolvedName) : (nameLoading ? 'Loading…' : 'Unnamed client');
  }
  const profile = clientProfiles[clientKey] || {};
  const note = clientNotes[clientKey] || '';
  const pipelineRecords = (pipeline || []).filter(p => clientKeyOf(p.clientName || p.insured) === clientKey || clientKeyOf(p.insured) === clientKey);

  return (
    <ClientDetailModal
      clientKey={clientKey}
      client={client}
      profile={profile}
      note={note}
      pipelineRecords={pipelineRecords}
      lobConfig={lobConfig}
      onProfileChange={(next) => setClientProfiles(prev => ({ ...prev, [clientKey]: next }))}
      onNoteChange={(text) => setClientNotes(prev => ({ ...prev, [clientKey]: text }))}
      onClose={onClose}
      showToast={showToast}
      currentUser={currentUser}
    />
  );
}

// Format a number of days as a human-readable duration: "12 days", "5 mo", "2y 3m".
function humanDuration(days) {
  if (days == null || isNaN(days)) return '—';
  if (days < 0) return `${Math.abs(days)} days from now`;
  if (days < 1) return 'today';
  if (days < 30) return `${days} day${days === 1 ? '' : 's'}`;
  if (days < 365) {
    const months = Math.round(days / 30);
    return `${months} month${months === 1 ? '' : 's'}`;
  }
  const years = Math.floor(days / 365);
  const remainingDays = days - years * 365;
  const months = Math.round(remainingDays / 30);
  if (months === 0) return `${years} year${years === 1 ? '' : 's'}`;
  return `${years}y ${months}m`;
}

function ClientDetailModal({ clientKey, client, profile, note, pipelineRecords, lobConfig, onProfileChange, onNoteChange, onClose, showToast, currentUser }) {
  // Find the earliest issue date across all policies (carrier + pipeline) — when the client
  // first came on the books. And the latest lapse date — when they fell off.
  const allIssueDates = [
    ...(client.policies || []).map(p => p.issueDate),
    ...(pipelineRecords || []).map(p => p.dateClosed),
  ].map(parseDate).filter(Boolean);
  const allLapseDates = (client.policies || [])
    .filter(p => p.status === 'lapsed')
    .map(p => p.lapseDate || p.lastActivity)
    .map(parseDate)
    .filter(Boolean);
  const earliest = allIssueDates.length > 0 ? new Date(Math.min(...allIssueDates.map(d => d.getTime()))) : null;
  const latestLapse = allLapseDates.length > 0 ? new Date(Math.max(...allLapseDates.map(d => d.getTime()))) : null;
  // Active = at least one in-force policy. If not, the client has fallen off the books.
  const isStillActive = (client.activeCount || 0) > 0;
  const fmtDate = (d) => d ? `${(d.getMonth() + 1).toString().padStart(2,'0')}/${d.getDate().toString().padStart(2,'0')}/${d.getFullYear()}` : null;
  const earliestStr = fmtDate(earliest);
  const latestLapseStr = fmtDate(latestLapse);
  const onBooksDays = earliest ? Math.floor((new Date() - earliest) / 86400000) : null;
  const wasOnBooksDays = (earliest && latestLapse) ? Math.floor((latestLapse - earliest) / 86400000) : null;
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prevOverflow; };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full flex flex-col" style={{ maxHeight: '90vh' }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-slate-200 bg-white rounded-t-2xl flex-shrink-0">
          <div className="min-w-0 flex-1">
            <div className="text-xs text-slate-500 uppercase tracking-wider">Client profile</div>
            <h2 className="text-2xl font-bold truncate">{titleCase(client.displayName)}</h2>
            <div className="text-sm text-slate-500 mt-1">
              {client.activeCount} active · {client.lapsedCount} lapsed · {fmt(client.totalPremium)} premium
              {pipelineRecords?.length > 0 && <> · {pipelineRecords.length} pipeline polic{pipelineRecords.length === 1 ? 'y' : 'ies'}</>}
            </div>
            {(() => {
              // Surface the writing agent(s) for this client. Each policy has
              // an agentName resolved from prodNum at container build time;
              // we collapse duplicates so a multi-policy client doesn't list
              // the same agent twice. Falls back to the prodNum when no name
              // is on file so the row never says "Agent: —".
              const agents = Array.from(new Set(
                (client.policies || [])
                  .map(p => p.agentName || p.prodNum)
                  .filter(Boolean)
              ));
              if (agents.length === 0) return null;
              return (
                <div className="text-sm text-slate-600 mt-1">
                  <span className="text-slate-500">{agents.length === 1 ? 'Agent:' : 'Agents:'}</span>{' '}
                  <span className="font-semibold text-slate-900">{agents.join(', ')}</span>
                </div>
              );
            })()}
            {earliest && (
              isStillActive ? (
                <div className="text-xs text-emerald-700 mt-1 font-medium">
                  On the books since <span className="font-bold">{earliestStr}</span> · <span className="font-bold">{humanDuration(onBooksDays)}</span>
                </div>
              ) : (
                <div className="text-xs text-red-700 mt-1 font-semibold bg-red-50 border border-red-200 rounded px-2 py-1 inline-block">
                  No longer on the books · Was on the books for <span className="font-bold">{humanDuration(wasOnBooksDays ?? onBooksDays)}</span>
                  {earliestStr && latestLapseStr && <> ({earliestStr} → {latestLapseStr})</>}
                </div>
              )
            )}
          </div>
          <button aria-label="Close" onClick={onClose} title="Close (Esc)" className="text-slate-400 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg p-2 shrink-0"><X size={20}/></button>
        </div>
        <div className="flex-1 overflow-auto">
        {/* Live Call Task Console — Build Prompt §3. Always-focused
            command box + 6 quick chips + optimistic feed.
            Mounts first so the operator sees it the moment the
            client profile opens — the agent's primary workflow
            during a live call. prospect_id falls back to clientKey
            so legacy callers without an explicit prospect-uuid
            still get a working console. */}
        <div className="px-4 pt-4">
          <CallConsole
            prospectId={(client?.id || clientKey)}
            prospectName={client?.displayName || ''}
            clientContext={{
              name:              client?.displayName || '',
              lines_of_business: Array.from(new Set((client?.policies || []).map(p => p.lob).filter(Boolean))),
              consents_on_file:  profile?.consents_on_file || {},
              timezone:          profile?.timezone || null,
            }}
            showToast={showToast}
          />
        </div>
        <ClientExpandedProfile
          client={client}
          note={note}
          onNoteChange={onNoteChange}
          profile={profile}
          onProfileChange={onProfileChange}
          lobConfig={lobConfig}
          showToast={showToast}
          currentUser={currentUser}
        />
        {/* Universal policy workflow engine — resolves clientKey to a public.clients
            row via phone match, then renders the per-LOB progress strip + action
            buttons. If no matching Clients record exists, prompts to create one. */}
        <ClientWorkflowsPanel
          clientHint={{
            name:  client.displayName || '',
            phone: profile?.phone || '',
            email: profile?.email || '',
          }}
          currentUser={currentUser}
          showToast={showToast}
        />
        {/* ANCHOR per-client — open ACA issues + coverage checkboxes
            + cross-sell nurture controls. Same clientHint resolution. */}
        <AnchorClientPanel
          clientHint={{
            name:  client.displayName || '',
            phone: profile?.phone || '',
            email: profile?.email || '',
          }}
          currentUser={currentUser}
          showToast={showToast}
        />
        {pipelineRecords && pipelineRecords.length > 0 && (
          <div className="border-t border-amber-200 bg-amber-50/40 p-4">
            <div className="text-xs font-semibold text-amber-900 uppercase tracking-wide mb-2">In-progress pipeline policies ({pipelineRecords.length})</div>
            <div className="space-y-1 text-sm">
              {pipelineRecords.map(r => {
                const color = PIPELINE_STATUS_COLOR[r.status] || 'slate';
                return (
                  <div key={r.id} className="bg-white rounded-lg border border-amber-200 p-2 flex items-center gap-3 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-${color}-100 text-${color}-700`}>{r.status}</span>
                    <span className="text-xs text-slate-500">{r.dateClosed ? fmtDateUS(r.dateClosed) : '—'}</span>
                    <span className="font-medium">{r.carrier || '—'}</span>
                    <span className="font-mono text-xs text-slate-500">{r.policyNumber || '—'}</span>
                    <span className="text-xs">{r.policyType || ''}</span>
                    <span className="ml-auto font-semibold">{Number(r.monthlyPremium) > 0 ? `${fmt(r.monthlyPremium)}/mo` : ''}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

function exportPostcardCsv(clients, profiles, showToast) {
  const rows = [['Insured Name', 'Address Line 1', 'Address Line 2', 'City', 'State', 'ZIP', 'Phone', 'Email', 'Active Policies', 'Total Premium']];
  let withAddr = 0;
  clients.forEach(c => {
    const p = profiles[c.key] || {};
    if (hasMailingAddress(p)) withAddr++;
    rows.push([
      titleCase(c.displayName),
      p.address1 || '',
      p.address2 || '',
      titleCase(p.city || ''),
      p.state || '',
      p.zip || '',
      p.phone || '',
      p.email || '',
      c.activeCount,
      c.totalPremium.toFixed(2),
    ]);
  });
  const csv = rows.map(r => r.map(v => {
    const s = String(v == null ? '' : v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `postcard-list-${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  showToast(`Exported ${clients.length} clients · ${withAddr} have mailing addresses · ${clients.length - withAddr} are missing addresses`);
}

function ClientExpandedProfile({ client, note, onNoteChange, profile, onProfileChange, lobConfig, showToast, currentUser }) {
  const [postcardOpen, setPostcardOpen] = useState(false);
  const [showSSN, setShowSSN] = useState(false);
  const updateField = (field, value) => onProfileChange({ ...profile, [field]: value });
  const updateBeneficiaries = (beneficiaries) => onProfileChange({ ...profile, beneficiaries });
  const mailingLabel = formatMailingLabel(client.displayName, profile);

  const copyAddress = async () => {
    if (!mailingLabel) return;
    try {
      await navigator.clipboard.writeText(mailingLabel);
      showToast('Mailing label copied to clipboard');
    } catch {
      showToast('Could not copy — your browser blocks clipboard writes', 'error');
    }
  };

  const canSendPostcard = !!(lobConfig?.fromAddress1 && hasMailingAddress(profile));

  return (
    <div className="border-t border-slate-200 p-4 space-y-4 bg-slate-50">
      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={copyAddress}
          disabled={!hasMailingAddress(profile)}
          className="text-sm bg-white border border-slate-300 hover:bg-slate-100 disabled:opacity-50 px-3 py-1.5 rounded-lg"
          title={hasMailingAddress(profile) ? 'Copy formatted mailing label' : 'Address incomplete'}
        >
          Copy mailing label
        </button>
        <button
          onClick={() => setPostcardOpen(true)}
          className="text-sm bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 rounded-lg font-semibold shadow-sm"
          title="Send a real postcard via Lob.com (requires Lob config in Settings + complete client address)"
        >
          Send postcard
        </button>
        {!hasMailingAddress(profile) && (
          <span className="text-xs text-amber-700">⚠ Address incomplete — fill out address line 1 + city + state + ZIP below before sending.</span>
        )}
        {hasMailingAddress(profile) && !(lobConfig && lobConfig.fromAddress1) && (
          <span className="text-xs text-amber-700">⚠ Return address missing — go to Settings → Postcard sending first.</span>
        )}
      </div>

      {/* Contact + demographics */}
      <div className="bg-white rounded-lg border border-slate-200 p-3 space-y-2">
        <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Contact & Address</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <Field label="Address line 1" value={profile.address1} onChange={(v) => updateField('address1', v)}/>
          <Field label="Address line 2" value={profile.address2} onChange={(v) => updateField('address2', v)}/>
          <Field label="City" value={profile.city} onChange={(v) => updateField('city', v)}/>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Field label="State" value={profile.state} onChange={(v) => updateField('state', v.toUpperCase().slice(0,2))} maxLength={2}/>
            <Field label="ZIP" value={profile.zip} onChange={(v) => updateField('zip', v)}/>
          </div>
          <Field label="Phone" value={profile.phone} onChange={(v) => updateField('phone', v)}/>
          <Field label="Email" type="email" value={profile.email} onChange={(v) => updateField('email', v)}/>
        </div>
      </div>

      {/* Demographics */}
      <div className="bg-white rounded-lg border border-slate-200 p-3 space-y-2">
        <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Demographics</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <Field label="Date of birth" value={profile.dob} onChange={(v) => updateField('dob', v)} placeholder="MM/DD/YYYY"/>
          <Field label="Gender" value={profile.gender} onChange={(v) => updateField('gender', v)}/>
          <Field label="Age at issue" value={profile.ageAtIssue} onChange={(v) => updateField('ageAtIssue', v)}/>
          <label className="block">
            <span className="text-xs text-slate-500">SSN</span>
            <div className="flex gap-1 mt-0.5">
              <input
                type={showSSN ? 'text' : 'password'}
                value={profile.ssn || ''}
                onChange={e => updateField('ssn', e.target.value)}
                placeholder="XXX-XX-XXXX"
                className="flex-1 border border-slate-200 rounded px-2 py-1 text-sm font-mono"
              />
              <button
                type="button"
                onClick={() => setShowSSN(s => !s)}
                className="text-xs px-2 bg-white border border-slate-200 rounded hover:bg-slate-100"
                title={showSSN ? 'Hide SSN' : 'Show SSN'}
              >
                {showSSN ? 'Hide' : 'Show'}
              </button>
            </div>
          </label>
        </div>
        {profile.ssn && (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            ⚠ SSN stored in this browser's localStorage only. Not sent to the AI chat or any external service.
          </div>
        )}
      </div>

      {/* Owner / secondary insured */}
      <div className="bg-white rounded-lg border border-slate-200 p-3 space-y-2">
        <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Policy Owner / Secondary Insured</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
          <Field label="Owner name" value={profile.ownerName} onChange={(v) => updateField('ownerName', v)}/>
          <Field label="Owner DOB" value={profile.ownerDob} onChange={(v) => updateField('ownerDob', v)}/>
          <Field label="Owner gender" value={profile.ownerGender} onChange={(v) => updateField('ownerGender', v)}/>
          <Field label="Insured 2 name" value={profile.insured2Name} onChange={(v) => updateField('insured2Name', v)}/>
          <Field label="Insured 2 DOB" value={profile.insured2Dob} onChange={(v) => updateField('insured2Dob', v)}/>
          <Field label="Insured 2 gender" value={profile.insured2Gender} onChange={(v) => updateField('insured2Gender', v)}/>
        </div>
      </div>

      {/* Banking info */}
      <BankingInfoSection profile={profile} updateField={updateField}/>

      {/* Beneficiaries */}
      <BeneficiaryEditor beneficiaries={profile.beneficiaries || []} onChange={updateBeneficiaries}/>

      {/* Contact log — for ongoing relationship management (6-month check-ins, etc.) */}
      <ClientContactLogEditor contactLog={profile.contactLog || []} onChange={(log) => updateField('contactLog', log)} policies={client.policies} currentUser={currentUser}/>

      {/* Medical conditions */}
      <div className="bg-white rounded-lg border border-slate-200 p-3">
        <div className="flex items-center gap-2 mb-1">
          <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Medical conditions</div>
          <span className="text-xs text-slate-400">(diagnoses, medications, height/weight, smoker status, etc.)</span>
        </div>
        <textarea
          value={profile.medicalConditions || ''}
          onChange={e => updateField('medicalConditions', e.target.value)}
          placeholder="e.g. Type 2 diabetes (controlled, metformin) · former smoker (quit 2018) · BMI 27 · no other conditions"
          rows={3}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
        />
      </div>

      {/* Notes */}
      <div className="bg-white rounded-lg border border-slate-200 p-3">
        <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-1">Notes</div>
        <textarea value={note} onChange={e=>onNoteChange(e.target.value)} placeholder="Add notes about this client…" rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"/>
      </div>

      {/* Policies */}
      <div>
        <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-1">Policies ({client.policies.length})</div>
        <div className="overflow-x-auto bg-white rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 border-b border-slate-200">
              <tr>
                <th className="py-1.5 px-2 text-left">Status</th>
                <th className="py-1.5 px-2 text-left">Policy</th>
                <th className="py-1.5 px-2 text-left">Carrier</th>
                <th className="py-1.5 px-2 text-left">Issued</th>
                <th className="py-1.5 px-2 text-left">Lapsed</th>
                <th className="py-1.5 px-2 text-left">Reason</th>
                <th className="py-1.5 px-2 text-right">Premium</th>
                <th className="py-1.5 px-2 text-right">Comm</th>
              </tr>
            </thead>
            <tbody>
              {client.policies.map(p => {
                const lapseReason = p.chargebackTypes && p.chargebackTypes.size > 0
                  ? Array.from(p.chargebackTypes).join(', ')
                  : (p.status === 'lapsed' ? 'Negative net (no specific code)' : '');
                return (
                  <tr key={p.key} className="border-b border-slate-100">
                    <td className="py-1.5 px-2"><span className={`px-1.5 py-0.5 rounded text-xs font-bold ${p.status==='active'?'bg-emerald-100 text-emerald-700':'bg-red-100 text-red-700'}`}>{p.status === 'active' ? 'ACTIVE' : 'LAPSED'}</span></td>
                    <td className="py-1.5 px-2 font-mono text-xs">{p.policy}</td>
                    <td className="py-1.5 px-2">{p.carrier}</td>
                    <td className="py-1.5 px-2">{p.issueDate || '—'}</td>
                    <td className="py-1.5 px-2 text-red-700">{p.status === 'lapsed' ? (p.lapseDate || '—') : '—'}</td>
                    <td className="py-1.5 px-2 text-xs text-slate-700" title={lapseReason}>{p.status === 'lapsed' ? lapseReason : '—'}</td>
                    <td className="py-1.5 px-2 text-right">{p.premium > 0 ? fmt(p.premium) : '—'}</td>
                    <td className={`py-1.5 px-2 text-right ${p.totalCommission >= 0 ? '' : 'text-red-600'}`}>{fmt(p.totalCommission)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Plan Assistant — grounded Q&A against the client's linked plan.
          Auto-resolves the plan by joining the client's health_policies
          row to carrier_plans.id. Until an SBC is uploaded + the policy
          is linked, the panel is informational only. */}
      <ClientPlanAssistant client={client} currentUser={currentUser}/>

      {postcardOpen && (
        <SendPostcardModal
          clientName={client.displayName}
          profile={profile}
          lobConfig={lobConfig}
          onClose={() => setPostcardOpen(false)}
          showToast={showToast}
        />
      )}
    </div>
  );
}

// ── ClientPlanAssistant ────────────────────────────────────────────
// Inline grounded Q&A scoped to the client's linked health plan. We
// resolve the link in three steps:
//   1. Pull the client's health_policies rows
//   2. Pick the most-recently-effective active row with plan_id set
//   3. Fall back to a manual plan picker if none is linked
//
// All Q&A goes through /api/plan-intelligence/ask which only answers
// from the SBC's text chunks — no hallucinated copays.
function ClientPlanAssistant({ client, currentUser }) {
  const [policy,  setPolicy]  = React.useState(null);
  const [allPlans, setAllPlans] = React.useState([]);
  const [pickPlanId, setPickPlanId] = React.useState('');
  const [question, setQuestion] = React.useState('');
  const [mode,     setMode]     = React.useState('agent');
  const [asking,   setAsking]   = React.useState(false);
  const [answer,   setAnswer]   = React.useState(null);

  const ownerId = currentUser?.id;

  React.useEffect(() => {
    if (!ownerId || !client?.displayName) return;
    let cancelled = false;
    (async () => {
      // Resolve policy(ies) for this client. We match by client_name
      // since the legacy clients table doesn't always have an id link.
      const { data: policies } = await supabase
        .from('health_policies')
        .select('id, client_name, carrier, plan_name, plan_id, status, effective_date, member_id')
        .eq('owner_id', ownerId)
        .ilike('client_name', client.displayName.trim())
        .order('effective_date', { ascending: false });
      const linked = (policies || []).find(p => p.plan_id && (p.status || 'active') === 'active')
                   || (policies || []).find(p => p.plan_id)
                   || null;
      if (!cancelled) setPolicy(linked);
      // Always pull the library so the manual picker works even when
      // no policy is linked.
      const { data: plans } = await supabase
        .from('carrier_plans')
        .select('id, carrier_short, plan_name, state, metal_level')
        .eq('owner_id', ownerId)
        .eq('status', 'active')
        .order('carrier_short');
      if (!cancelled) setAllPlans(plans || []);
    })();
    return () => { cancelled = true; };
  }, [ownerId, client?.displayName]);

  const activePlanId = policy?.plan_id || pickPlanId;

  const ask = async () => {
    if (!question.trim() || !activePlanId) return;
    setAsking(true);
    setAnswer(null);
    try {
      const res = await apiFetch('/api/plan-intelligence/ask', {
        method: 'POST',
        body: JSON.stringify({ question: question.trim(), plan_id: activePlanId, mode }),
      });
      setAnswer(res);
    } catch (err) {
      setAnswer({ answer: `Error: ${err.message || err}`, citedChunks: [] });
    } finally {
      setAsking(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border-2 border-cyan-300 p-3 space-y-2">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-cyan-700 text-[0.55rem] font-black uppercase tracking-[0.2em]">Plan Assistant</div>
          <div className="text-sm font-bold text-slate-900">
            {policy
              ? `${policy.carrier || '—'} · ${policy.plan_name || '—'}`
              : 'No linked plan'}
          </div>
        </div>
        {!policy?.plan_id && allPlans.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-[0.55rem] uppercase tracking-wider text-slate-500">Pick a plan:</span>
            <select
              value={pickPlanId}
              onChange={e => setPickPlanId(e.target.value)}
              className="text-xs border border-slate-300 px-2 py-1 bg-white"
            >
              <option value="">— choose —</option>
              {allPlans.map(p => (
                <option key={p.id} value={p.id}>
                  {[p.carrier_short, p.plan_name, p.state, p.metal_level].filter(Boolean).join(' · ')}
                </option>
              ))}
            </select>
          </div>
        )}
        {allPlans.length === 0 && (
          <span className="text-[0.6rem] text-slate-500 italic">Upload SBCs in Plan Intelligence to populate.</span>
        )}
      </div>
      {activePlanId && (
        <>
          <div className="flex items-center gap-2">
            <textarea
              rows={2}
              value={question}
              onChange={e => setQuestion(e.target.value)}
              placeholder="e.g. What's the specialist copay? How much for an MRI?"
              className="flex-1 text-xs border border-slate-300 px-2 py-1.5 font-mono resize-y"
            />
            <div className="flex flex-col gap-1">
              <div className="flex border border-slate-300">
                {['agent', 'consumer'].map(m => (
                  <button key={m}
                    onClick={() => setMode(m)}
                    className={`text-[0.55rem] font-black uppercase tracking-wider px-2 py-1 ${mode === m ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-100'}`}>
                    {m}
                  </button>
                ))}
              </div>
              <button onClick={ask} disabled={!question.trim() || asking}
                className="text-xs font-black uppercase tracking-wider px-3 py-1.5 bg-cyan-600 text-white disabled:opacity-50 hover:bg-cyan-700">
                {asking ? 'Asking…' : 'Ask'}
              </button>
            </div>
          </div>
          {answer && (
            <div className="border border-cyan-300 bg-cyan-50 p-2 text-xs">
              <div className="whitespace-pre-wrap font-sans leading-snug text-slate-900">{answer.answer}</div>
              {answer.citedChunks?.length > 0 && (
                <div className="text-[0.55rem] uppercase tracking-wider text-cyan-700 mt-1">
                  Cited: {answer.citedChunks.map(c => c.title).filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder = '', maxLength }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-500">{label}</span>
      <input
        type={type}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        // Block browser autofill across the board. The Field component
        // backs every client-profile slot — DOB, age at issue, owner
        // name, etc. Without this, Chrome / Safari helpfully drop the
        // signed-in user's email into random text inputs that happened
        // to be near the focused element. data-1p-ignore + data-lpignore
        // turn off 1Password / LastPass auto-fill too.
        autoComplete="off"
        data-1p-ignore="true"
        data-lpignore="true"
        spellCheck={false}
        name={`field-${Math.random().toString(36).slice(2, 10)}`}
        className="mt-0.5 w-full border border-slate-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-emerald-500"
      />
    </label>
  );
}

function BankingInfoSection({ profile, updateField }) {
  const [showRouting, setShowRouting] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const hasAnyBanking = !!(profile.bankName || profile.bankRouting || profile.bankAccount || profile.bankAccountType || profile.bankDraftDate);

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Banking info</div>
        <span className="text-xs text-slate-400">(for premium draft)</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
        <Field label="Bank name" value={profile.bankName} onChange={(v) => updateField('bankName', v)} placeholder="Wells Fargo, Chase, etc."/>
        <Field label="Account holder name" value={profile.bankAccountHolder} onChange={(v) => updateField('bankAccountHolder', v)} placeholder="If different from insured"/>
        <label className="block">
          <span className="text-xs text-slate-500">Account type</span>
          <select
            value={profile.bankAccountType || ''}
            onChange={e => updateField('bankAccountType', e.target.value)}
            className="mt-0.5 w-full border border-slate-200 rounded px-2 py-1 text-sm bg-white"
          >
            <option value="">— Select —</option>
            <option value="Checking">Checking</option>
            <option value="Savings">Savings</option>
            <option value="Other">Other</option>
          </select>
        </label>
        <Field label="Draft date" value={profile.bankDraftDate} onChange={(v) => updateField('bankDraftDate', v)} placeholder="e.g. 15th, Last day, 1st Friday"/>
        <label className="block">
          <span className="text-xs text-slate-500">Routing number</span>
          <div className="flex gap-1 mt-0.5">
            <input
              type={showRouting ? 'text' : 'password'}
              value={profile.bankRouting || ''}
              onChange={e => updateField('bankRouting', e.target.value)}
              placeholder="9 digits"
              className="flex-1 border border-slate-200 rounded px-2 py-1 text-sm font-mono"
            />
            <button type="button" onClick={() => setShowRouting(s => !s)} className="text-xs px-2 bg-white border border-slate-200 rounded hover:bg-slate-100" title={showRouting ? 'Hide' : 'Show'}>{showRouting ? 'Hide' : 'Show'}</button>
          </div>
        </label>
        <label className="block">
          <span className="text-xs text-slate-500">Account number</span>
          <div className="flex gap-1 mt-0.5">
            <input
              type={showAccount ? 'text' : 'password'}
              value={profile.bankAccount || ''}
              onChange={e => updateField('bankAccount', e.target.value)}
              placeholder=""
              className="flex-1 border border-slate-200 rounded px-2 py-1 text-sm font-mono"
            />
            <button type="button" onClick={() => setShowAccount(s => !s)} className="text-xs px-2 bg-white border border-slate-200 rounded hover:bg-slate-100" title={showAccount ? 'Hide' : 'Show'}>{showAccount ? 'Hide' : 'Show'}</button>
          </div>
        </label>
      </div>
      <label className="block">
        <span className="text-xs text-slate-500">Banking notes</span>
        <textarea
          value={profile.bankNotes || ''}
          onChange={e => updateField('bankNotes', e.target.value)}
          placeholder="e.g. account changed 03/14, switched from Chase to Wells, overdraft history, etc."
          rows={2}
          className="mt-0.5 w-full px-2 py-1 border border-slate-200 rounded text-sm"
        />
      </label>
      {hasAnyBanking && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          ⚠ Banking info stored in this browser's localStorage only. Not sent to the AI chat or any external service.
        </div>
      )}
    </div>
  );
}

// Helper: most recent contact date as a Date, or null.
function lastContactDateFor(contactLog) {
  if (!contactLog || contactLog.length === 0) return null;
  const dates = contactLog.map(c => parseDate(c.date)).filter(Boolean);
  return dates.length > 0 ? new Date(Math.max(...dates.map(d => d.getTime()))) : null;
}

// Days since most recent contact (null if never contacted).
function daysSinceLastContact(contactLog) {
  const last = lastContactDateFor(contactLog);
  if (!last) return null;
  return Math.floor((new Date() - last) / 86400000);
}

function ClientContactLogEditor({ contactLog, onChange, policies = [], currentUser = null }) {
  const [adding, setAdding] = useState(false);
  const [draftDate, setDraftDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [draftAction, setDraftAction] = useState('6-month check-in');
  const [draftNotes, setDraftNotes] = useState('');

  // Compute due lifecycle touchpoints across all of this client's active policies.
  // Returns: [{ policy, touchpoint }, ...] one per (policy, touchpoint) pair.
  const dueTouchpoints = React.useMemo(() => {
    const list = [];
    (policies || []).forEach(p => {
      if (p.status !== 'active') return;
      const tps = getDuePolicyTouchpoints(p, contactLog);
      tps.forEach(tp => list.push({ policy: p, touchpoint: tp }));
    });
    return list.sort((a, b) => a.touchpoint.priority - b.touchpoint.priority);
  }, [policies, contactLog]);

  const startLog = (preselectAction, preselectNotes) => {
    if (preselectAction) setDraftAction(preselectAction);
    if (preselectNotes != null) setDraftNotes(preselectNotes);
    setDraftDate(new Date().toISOString().slice(0, 10));
    setAdding(true);
  };

  const sorted = (contactLog || []).slice().sort((a, b) => {
    const ad = parseDate(a.date)?.getTime() || 0;
    const bd = parseDate(b.date)?.getTime() || 0;
    return bd - ad;
  });
  const last = lastContactDateFor(contactLog);
  const daysSince = daysSinceLastContact(contactLog);
  const isOverdue = daysSince == null || daysSince > CHECK_IN_INTERVAL_DAYS;

  const submitContact = () => {
    if (!draftDate) return;
    // draftDate is YYYY-MM-DD from the date input — convert to MM/DD/YYYY for consistency
    const [y, m, d] = draftDate.split('-');
    const mmddyyyy = `${m}/${d}/${y}`;
    const entry = {
      id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `c-${Date.now()}`,
      date: mmddyyyy,
      action: draftAction,
      notes: draftNotes.trim(),
      loggedByUser: currentUser ? { username: currentUser.prodNum, role: currentUser.role, name: currentUser.name } : null,
      loggedAt: new Date().toISOString(),
    };
    onChange([entry, ...(contactLog || [])]);
    setAdding(false);
    setDraftAction('6-month check-in');
    setDraftNotes('');
    setDraftDate(new Date().toISOString().slice(0, 10));
  };

  const remove = (id) => onChange((contactLog || []).filter(c => c.id !== id));

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Contact log</div>
        {last && (
          <span className={`text-xs px-2 py-0.5 rounded-full ${isOverdue ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
            Last contact: {last.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · {daysSince}d ago
          </span>
        )}
        {!last && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">No contact logged yet</span>
        )}
        <button onClick={() => setAdding(a => !a)} className="ml-auto text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded font-semibold">
          {adding ? 'Cancel' : '+ Log contact'}
        </button>
      </div>

      {dueTouchpoints.length > 0 && (
        <div className="space-y-2">
          {dueTouchpoints.map(({ policy, touchpoint }) => (
            <div key={`${policy.key}|${touchpoint.kind}`} className={`bg-${touchpoint.color}-50 border border-${touchpoint.color}-200 rounded-lg p-2 space-y-1.5`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className={`text-xs font-bold text-${touchpoint.color}-900`}>
                  ⚠ {touchpoint.label} <span className="text-[10px] font-normal text-slate-500 font-mono">· {policy.policy} · issued {policy.issueDate}</span>
                </div>
                <button
                  onClick={() => startLog(touchpoint.match, `[${policy.carrier} ${policy.policy}] `)}
                  className={`text-xs bg-${touchpoint.color}-600 hover:bg-${touchpoint.color}-700 text-white px-2 py-0.5 rounded font-semibold`}
                >
                  Log "{touchpoint.match}"
                </button>
              </div>
              <div className={`text-xs text-${touchpoint.color}-900`}>{touchpoint.guidance}</div>
              <div className="text-xs italic bg-white border border-slate-200 rounded px-2 py-1 text-slate-700">"{touchpoint.script}"</div>
            </div>
          ))}
        </div>
      )}

      {isOverdue && dueTouchpoints.length === 0 && (
        <div className="text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1 text-amber-900">
          ⚠ Due for {!last ? 'a' : 'a 6-month'} check-in. {CHECK_IN_GUIDANCE}
        </div>
      )}
      {isOverdue && dueTouchpoints.length > 0 && (
        <div className="text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1 text-amber-900">
          ⚠ Also overdue for a 6-month check-in. {CHECK_IN_GUIDANCE}
        </div>
      )}

      {adding && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-2 space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <input type="date" value={draftDate} onChange={e => setDraftDate(e.target.value)} className="px-2 py-1 border border-slate-200 rounded text-sm"/>
            <select value={draftAction} onChange={e => setDraftAction(e.target.value)} className="px-2 py-1 border border-slate-200 rounded text-sm bg-white">
              {CLIENT_CONTACT_ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <textarea
            value={draftNotes}
            onChange={e => setDraftNotes(e.target.value)}
            placeholder="What did you discuss? Coverage review, policy questions, life events…"
            rows={2}
            className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setAdding(false)} className="px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
            <button onClick={submitContact} className="px-3 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded font-semibold">Save contact</button>
          </div>
        </div>
      )}

      {sorted.length === 0 && !adding && (
        <div className="text-sm text-slate-400 italic">No contacts logged. Click "+ Log contact" after each call/email/check-in.</div>
      )}
      {sorted.length > 0 && (
        <div className="divide-y divide-slate-100">
          {sorted.map(c => (
            <div key={c.id} className="py-2 flex items-start gap-3 text-sm">
              <div className="text-xs text-slate-500 font-mono w-24 shrink-0">{fmtDateUS(c.date)}</div>
              <div className="flex-1">
                <div className="font-semibold">{c.action}</div>
                {c.notes && <div className="text-slate-600 text-sm mt-0.5">{c.notes}</div>}
              </div>
              <button onClick={() => remove(c.id)} className="text-slate-400 hover:text-red-600 shrink-0" title="Delete entry"><Trash2 size={14}/></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BeneficiaryEditor({ beneficiaries, onChange }) {
  const [showSSN, setShowSSN] = useState({}); // { [i]: bool }
  const [expanded, setExpanded] = useState({}); // { [i]: bool } — extra fields shown
  const update = (i, field, value) => {
    const next = beneficiaries.slice();
    next[i] = { ...next[i], [field]: value };
    onChange(next);
  };
  const add = () => onChange([...beneficiaries, { name: '', relationship: '', percent: '', ssn: '', phone: '', email: '', address: '' }]);
  const remove = (i) => onChange(beneficiaries.filter((_, j) => j !== i));
  const totalPct = beneficiaries.reduce((s, b) => s + (Number(b.percent) || 0), 0);
  const pctValid = beneficiaries.length === 0 || totalPct === 100;

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3 space-y-3">
      <div className="flex items-center gap-2">
        <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Beneficiaries</div>
        <button onClick={add} className="ml-auto text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded">+ Add</button>
      </div>
      {beneficiaries.length === 0 && (
        <div className="text-sm text-slate-400 italic">No beneficiaries on file. Add one with the button above.</div>
      )}
      {beneficiaries.map((b, i) => {
        const isExpanded = !!expanded[i];
        const isShowSSN = !!showSSN[i];
        return (
          <div key={i} className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
            {/* Primary row */}
            <div className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-5">
                <span className="text-xs text-slate-500">Full name</span>
                <input value={b.name || ''} onChange={e => update(i, 'name', e.target.value)} className="mt-0.5 w-full border border-slate-200 rounded px-2 py-1 text-sm" placeholder="Jane Doe"/>
              </div>
              <div className="col-span-4">
                <span className="text-xs text-slate-500">Relationship</span>
                <input value={b.relationship || ''} onChange={e => update(i, 'relationship', e.target.value)} className="mt-0.5 w-full border border-slate-200 rounded px-2 py-1 text-sm" placeholder="Spouse, Son, etc."/>
              </div>
              <div className="col-span-2">
                <span className="text-xs text-slate-500">%</span>
                <input type="number" value={b.percent || ''} onChange={e => update(i, 'percent', e.target.value)} className="mt-0.5 w-full border border-slate-200 rounded px-2 py-1 text-sm" placeholder="50"/>
              </div>
              <div className="col-span-1 flex justify-end">
                <button onClick={() => remove(i)} className="text-red-600 hover:bg-red-50 p-1 rounded" title="Remove">✕</button>
              </div>
            </div>

            {/* Toggle for extra fields */}
            <button
              onClick={() => setExpanded(e => ({ ...e, [i]: !e[i] }))}
              className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"
            >
              {isExpanded ? '▾' : '▸'} {isExpanded ? 'Hide' : 'Add'} contact info (SSN, phone, email, address)
            </button>

            {isExpanded && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2 border-t border-slate-200">
                <div>
                  <span className="text-xs text-slate-500">SSN</span>
                  <div className="flex gap-1">
                    <input
                      type={isShowSSN ? 'text' : 'password'}
                      value={b.ssn || ''}
                      onChange={e => update(i, 'ssn', e.target.value)}
                      placeholder="XXX-XX-XXXX"
                      className="mt-0.5 flex-1 border border-slate-200 rounded px-2 py-1 text-sm font-mono"
                    />
                    <button
                      onClick={() => setShowSSN(s => ({ ...s, [i]: !s[i] }))}
                      className="mt-0.5 text-xs px-2 bg-white border border-slate-200 rounded hover:bg-slate-100"
                      title={isShowSSN ? 'Hide SSN' : 'Show SSN'}
                    >
                      {isShowSSN ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>
                <div>
                  <span className="text-xs text-slate-500">Phone</span>
                  <input value={b.phone || ''} onChange={e => update(i, 'phone', e.target.value)} placeholder="(555) 123-4567" className="mt-0.5 w-full border border-slate-200 rounded px-2 py-1 text-sm"/>
                </div>
                <div>
                  <span className="text-xs text-slate-500">Email</span>
                  <input type="email" value={b.email || ''} onChange={e => update(i, 'email', e.target.value)} placeholder="jane@example.com" className="mt-0.5 w-full border border-slate-200 rounded px-2 py-1 text-sm"/>
                </div>
                <div>
                  <span className="text-xs text-slate-500">Address</span>
                  <input value={b.address || ''} onChange={e => update(i, 'address', e.target.value)} placeholder="123 Main St, City, ST 12345" className="mt-0.5 w-full border border-slate-200 rounded px-2 py-1 text-sm"/>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {beneficiaries.length > 0 && (
        <div className={`text-xs font-medium ${pctValid ? 'text-emerald-700' : 'text-amber-700'}`}>
          Total: {totalPct}% {pctValid ? '✓' : '⚠ should sum to 100%'}
        </div>
      )}
      {beneficiaries.some(b => b.ssn) && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          ⚠ SSNs are stored in this browser's localStorage only. They're never sent to the AI chat or any external service. Don't sync the same browser profile across untrusted devices.
        </div>
      )}
    </div>
  );
}

function SendPostcardModal({ clientName, profile, lobConfig, onClose, showToast }) {
  const [message, setMessage] = useState(`Hi ${titleCase(clientName.split(' ')[0])},\n\nJust checking in. Reach out anytime if you have questions about your policy.\n\n— ${lobConfig.fromName || 'Your agent'}`);
  const [frontUrl, setFrontUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  // Lob test keys are prefixed test_; when set, the button labels the
  // send as "(TEST)" so the operator knows it won't actually mail.
  const isTestKey = String(lobConfig?.apiKey || '').startsWith('test_');

  const send = async () => {
    setSending(true);
    setResult(null);
    try {
      const json = await sendLobPostcard({
        lobConfig,
        toName: clientName,
        toProfile: profile,
        message,
        frontUrl: frontUrl.trim() || null,
      });
      setResult({ ok: true, id: json.id, expectedDelivery: json.expected_delivery_date });
      showToast(`Postcard submitted to Lob · ID ${json.id}`);
    } catch (err) {
      setResult({ ok: false, msg: err.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-6" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold">Send postcard to {titleCase(clientName)}</h2>
            <p className="text-sm text-slate-500 mt-1">Real postcard, printed and mailed via Lob. ~$0.79 per send.</p>
          </div>
          <button aria-label="Close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20}/></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-1">To</div>
            <div className="whitespace-pre-line text-sm">{formatMailingLabel(clientName, profile)}</div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-1">From</div>
            <div className="whitespace-pre-line text-sm">
              {[lobConfig.fromName, lobConfig.fromAddress1, lobConfig.fromAddress2, [lobConfig.fromCity, lobConfig.fromState, lobConfig.fromZip].filter(Boolean).join(', ')].filter(Boolean).join('\n')}
            </div>
          </div>
        </div>

        <label className="block">
          <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Message (back of postcard)</span>
          <textarea value={message} onChange={e => setMessage(e.target.value)} rows={6} className="mt-1 w-full border border-slate-200 rounded-lg p-2 text-sm"/>
        </label>

        <label className="block">
          <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Front image URL (optional)</span>
          <input value={frontUrl} onChange={e => setFrontUrl(e.target.value)} placeholder="https://example.com/postcard-front.png — leave blank for plain text front" className="mt-1 w-full border border-slate-200 rounded-lg p-2 text-sm font-mono text-xs"/>
        </label>

        {result && (
          <div className={`text-sm px-3 py-2 rounded-lg ${result.ok ? 'bg-emerald-50 text-emerald-900 border border-emerald-200' : 'bg-red-50 text-red-900 border border-red-200'}`}>
            {result.ok
              ? <>✓ Submitted. Lob ID: <code>{result.id}</code>{result.expectedDelivery && <> · Expected delivery {result.expectedDelivery}</>}</>
              : <>✗ {result.msg}</>}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <button aria-label="Close" onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Close</button>
          <button onClick={send} disabled={sending || !hasMailingAddress(profile)} className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-semibold">
            {sending ? 'Sending…' : isTestKey ? 'Send (TEST)' : 'Send postcard'}
          </button>
        </div>
      </div>
    </div>
  );
}

function deriveCarrier(tx) {
  // Check BOTH company and mga — the MGA is the distributor (e.g. "ETERNA INSURANCE
  // SOLUTIONS LLC") and never contains the carrier name; the carrier lives in `company`
  // (e.g. "United of Omaha Life Insurance Company"). Earlier code only checked the MGA
  // and bucketed MOO policies under their MGA name as if they were a separate carrier.
  const company = (tx.company || '').toUpperCase();
  const mga = (tx.mga || '').toUpperCase();
  const combined = `${company} ${mga}`;
  if (combined.includes('AMERICO')) return 'Americo';
  if (combined.includes('TRANSAMERICA')) return 'Transamerica';
  if (combined.includes('MUTUAL') || combined.includes('OMAHA') || combined.includes('UNITED OF OMAHA')) return 'MOO';
  // MGA-only MOO match — known MOO distributors where the carrier field is blank or non-standard
  if (MOO_MGA_KEYWORDS.some(k => mga.includes(k))) return 'MOO';
  // Last resort: surface the company name (the carrier), not the MGA distributor
  return tx.company || tx.mga || 'Other';
}

// Known MGA distributors that always represent MOO underwriting. Extend this list
// as you encounter new MGAs in statements.
const MOO_MGA_KEYWORDS = ['SENIOR ADVISOR', 'ETERNA'];

// Normalize an insured name into a stable client key (matches clientNotes / clientProfiles).
const clientKeyOf = (name) => String(name || '').trim().toUpperCase().replace(/\s+/g, ' ');

// Map of MOO Client CSV column → profile field. Only fields we actually use.
const CLIENT_CSV_FIELDS = {
  'INSURED_NME': 'insuredName',
  'INSURED_ADDRESS_LINE_1': 'address1',
  'INSURED_ADDRESS_LINE_2': 'address2',
  'INSURED_CITY_NAME': 'city',
  'INSURED_STATE': 'state',
  'INSURED_ZIPCDE': 'zip',
  'INSURED_PARTY_PHONE': 'phone',
  'INSURED_EMAIL_ADDRESS': 'email',
  'INSURED_DOB': 'dob',
  'INSURED_GENDER': 'gender',
  'INSURED_AGE_AT_ISSUE': 'ageAtIssue',
  'OWNER_NME': 'ownerName',
  'OWNER_DOB': 'ownerDob',
  'OWNER_GENDER': 'ownerGender',
  'INSURED2_NME': 'insured2Name',
  'INSURED2_DOB': 'insured2Dob',
  'INSURED2_GENDER': 'insured2Gender',
  'POLICY_NUMBER': 'lastPolicyNumber',
  'FACE_AMT': 'lastFaceAmount',
};

// Parse a MOO Client CSV (the 1173602-*.csv export) and return a map of clientKey → profile data.
// Existing manual edits are preserved in the caller by merging.
// Async because xlsx is dynamic-imported on demand (Fix #1).
async function parseClientCsv(text) {
  const XLSX = await import('xlsx');
  const wb = XLSX.read(text, { type: 'string', raw: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { defval: '', raw: false });
  const profiles = {};
  let skipped = 0;
  rows.forEach(row => {
    const insured = String(row['INSURED_NME'] || '').trim();
    if (!insured) { skipped++; return; }
    const key = clientKeyOf(insured);
    if (!profiles[key]) profiles[key] = { source: 'moo-csv', updatedAt: new Date().toISOString() };
    const p = profiles[key];
    Object.entries(CLIENT_CSV_FIELDS).forEach(([col, field]) => {
      const val = String(row[col] ?? '').trim();
      if (val) p[field] = val;
    });
  });
  return { profiles, skipped };
}

// Build a one-line formatted mailing label from a profile.
function formatMailingLabel(insuredName, profile) {
  if (!profile) return null;
  const lines = [titleCase(insuredName)];
  if (profile.address1) lines.push(titleCase(profile.address1));
  if (profile.address2) lines.push(titleCase(profile.address2));
  const cityLine = [titleCase(profile.city), profile.state, profile.zip].filter(Boolean).join(', ').replace(/, ([A-Z]{2}), /, ', $1 ');
  if (cityLine.trim()) lines.push(cityLine);
  return lines.join('\n');
}

function hasMailingAddress(profile) {
  return !!(profile && profile.address1 && profile.city && profile.state && profile.zip);
}

// Send a postcard via the Lob API. Proxied through /api/lob/postcards so the
// LOB_API_KEY never reaches the browser. Also dodges Lob's CORS restrictions
// on production keys.
async function sendLobPostcard({ lobConfig, toName, toProfile, message, frontUrl }) {
  const body = {
    description: `Postcard to ${toName}`,
    to: {
      name: titleCase(toName),
      address_line1: toProfile.address1 || '',
      address_line2: toProfile.address2 || '',
      address_city: toProfile.city || '',
      address_state: toProfile.state || '',
      address_zip: toProfile.zip || '',
      address_country: 'US',
    },
    from: {
      name: lobConfig.fromName || '',
      address_line1: lobConfig.fromAddress1 || '',
      address_line2: lobConfig.fromAddress2 || '',
      address_city: lobConfig.fromCity || '',
      address_state: lobConfig.fromState || '',
      address_zip: lobConfig.fromZip || '',
      address_country: 'US',
    },
    front: frontUrl || `<html><body style="font-family:sans-serif;padding:20px;"><h1>${escapeHtml(lobConfig.fromName || 'Hello')}</h1><p>Thanks for your business.</p></body></html>`,
    back: `<html><body style="font-family:sans-serif;padding:20px;font-size:14px;line-height:1.4;">${escapeHtml(message).replace(/\n/g, '<br/>')}</body></html>`,
    size: '4x6',
  };
  const res = await fetch('/api/lob/postcards', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.error?.message || json?.error || `Lob proxy ${res.status}`);
  return json;
}

function escapeHtml(s) {
  return String(s || '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' })[c]);
}

// titleCase moved to src/lib/formatters.js — imported at the top.

function TransactionsView({ transactions, producers, onExport, onGotoDashboard = () => {}, openClientProfile = () => {} }) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  // Empty-state: no transactions at all → show the onboarding hint instead
  // of an empty filter/table chrome. Hooks above run on every render so the
  // Rules of Hooks aren't violated by this conditional early-return.
  if ((transactions || []).length === 0) {
    return (
      <div className="max-w-none mx-auto p-8 text-center">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
          <div className="w-12 h-12 mx-auto rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center mb-3">
            <FileSpreadsheet size={24}/>
          </div>
          <h2 className="text-lg font-bold mb-1">No transactions yet</h2>
          <p className="text-sm text-slate-500 mb-4">Upload a commission statement from the Dashboard tab and we'll parse every policy automatically.</p>
          <button
            onClick={onGotoDashboard}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-lg"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }
  const filtered = transactions.filter(tx => {
    if (filter==='chargebacks' && tx.netToAccount >= 0) return false;
    if (filter==='payments' && tx.netToAccount < 0) return false;
    if (search) {
      const s = search.toLowerCase();
      return (tx.insured||'').toLowerCase().includes(s) || (tx.policy||'').toLowerCase().includes(s) || tx.prodNum.includes(s);
    }
    return true;
  });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1">
          {[['all','All'],['payments','Payments'],['chargebacks','Chargebacks']].map(([id,l])=>(
            <button key={id} onClick={()=>setFilter(id)} className={`px-3 py-1.5 rounded-md text-sm font-medium ${filter===id?'bg-slate-900 text-white':'text-slate-600'}`}>{l}</button>
          ))}
        </div>
        <div className="flex gap-2 flex-1 min-w-0">
          <input placeholder="Search insured, policy, or producer..." value={search} onChange={e=>setSearch(e.target.value)} className="flex-1 border border-slate-200 rounded-lg px-3 py-1.5 text-sm"/>
          <button onClick={onExport} className="bg-slate-900 text-white px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1"><Download size={14}/>CSV</button>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
              <tr className="text-left text-slate-600">
                <th className="py-2 px-3">Date</th>
                <th className="py-2 px-3">Producer</th>
                <th className="py-2 px-3">Team</th>
                <th className="py-2 px-3">Policy</th>
                <th className="py-2 px-3">Insured</th>
                <th className="py-2 px-3">Activity</th>
                <th className="py-2 px-3 text-right">Net</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0,500).map(tx => (
                <tr key={tx.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-1.5 px-3 font-mono">{fmtDateUS(tx.activityDate) || tx.activityDate || '—'}</td>
                  <td className="py-1.5 px-3">
                    <div className="font-mono">{tx.prodNum}</div>
                    <div className="text-xs text-slate-500">{getProducerName(tx.prodNum, producers)}</div>
                  </td>
                  <td className="py-1.5 px-3">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${getProducerTeam(tx.prodNum, producers)==='mine'?'bg-emerald-100 text-emerald-700':getProducerTeam(tx.prodNum, producers)==='cam'?'bg-violet-100 text-violet-700':'bg-slate-100 text-slate-500'}`}>
                      {getProducerTeam(tx.prodNum, producers)==='mine'?'MINE':getProducerTeam(tx.prodNum, producers)==='cam'?'CAM':'—'}
                    </span>
                  </td>
                  <td className="py-1.5 px-3 font-mono">{tx.policy}</td>
                  <td className="py-1.5 px-3"><ClientNameLink name={tx.insured} openClientProfile={openClientProfile}/></td>
                  <td className="py-1.5 px-3">{tx.activityType}</td>
                  <td className={`py-1.5 px-3 text-right font-semibold ${tx.netToAccount<0?'text-red-600':''}`}>{fmt(tx.netToAccount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length > 500 && <div className="p-3 text-center text-xs text-slate-500 bg-slate-50">Showing 500 of {filtered.length.toLocaleString()} — use CSV export for full data</div>}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Pipeline — manually-managed policy tracker (separate from carrier statements)
// ──────────────────────────────────────────────────────────────────────

const PIPELINE_STATUS_OPTIONS = [
  'Inforce (1st Payment made)',
  'Awaiting Initial Payment',
  'Pending',
  'Outstanding Requirement',
  'Not Submitted',
  'Chargeback',
  'Lapsed',
];

const PIPELINE_STATUS_COLOR = {
  'Inforce (1st Payment made)': 'emerald',
  'Awaiting Initial Payment': 'amber',
  'Pending': 'blue',
  'Outstanding Requirement': 'orange',
  'Not Submitted': 'slate',
  'Chargeback': 'red',
  'Lapsed': 'rose',
};

const PIPELINE_CARRIER_PRESETS = ['MOO', 'Transamerica', 'Americo', 'F&G', 'F&G (Adult; 18+)', 'Other'];

const PIPELINE_BLANK_RECORD = {
  dateClosed: '',
  clientName: '',
  insured: '',
  phone: '',
  carrier: '',
  policyNumber: '',
  policyType: '',
  productType: '',
  status: 'Pending',
  statusNotes: '',
  firstPaymentDate: '',
  ongoingPaymentDate: '',
  contributionAmt: 0,
  dbCoverage: '',
  bank: '',
  state: '',
  leadSource: '',
  monthlyPremium: 0,
  annualPremium: 0,
  commission: 0,
  split: 100,
  payoutDate: '',
  chargebackAmount: 0,
  comments: '',
  // agentId is intentionally left empty here. The owner of the
  // record (or the pipeline grouping logic) substitutes the
  // configured owner prodNum at use time — see fallbackAgentId in
  // BookOfBusinessView and the agentId assignment in the pipeline
  // create flow.
  agentId: '',
};

const pipelineDaysRemaining = (dateStr) => {
  if (!dateStr || ['x','X','N/A','n/a',''].includes(String(dateStr).trim())) return null;
  const parts = String(dateStr).split('/');
  if (parts.length !== 3) return null;
  const [m, d, y] = parts.map(Number);
  if (!m || !d || !y) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const target = new Date(y, m-1, d);
  return Math.round((target - today) / 86400000);
};

function PipelineView({ pipeline, setPipeline, producers, settings = {}, openClientProfile = () => {} }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [carrierFilter, setCarrierFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('dateClosed');
  const [sortDir, setSortDir] = useState('desc');
  const [editing, setEditing] = useState(null); // null | 'new' | id of record being edited
  const [confirmingDelete, setConfirmingDelete] = useState(null);

  // ---- Filtering ----
  const lc = search.trim().toLowerCase();
  const filtered = pipeline.filter(p => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (carrierFilter !== 'all' && p.carrier !== carrierFilter) return false;
    if (lc) {
      const blob = `${p.clientName || ''} ${p.insured || ''} ${p.policyNumber || ''}`.toLowerCase();
      if (!blob.includes(lc)) return false;
    }
    return true;
  });

  // ---- Sorting ----
  const sorted = filtered.slice().sort((a, b) => {
    let av = a[sortField];
    let bv = b[sortField];
    // Numeric fields
    if (['monthlyPremium', 'annualPremium', 'commission', 'split', 'contributionAmt', 'chargebackAmount'].includes(sortField)) {
      av = Number(av) || 0;
      bv = Number(bv) || 0;
    } else if (sortField === 'dateClosed' || sortField === 'firstPaymentDate' || sortField === 'payoutDate') {
      // Date fields — parse MM/DD/YYYY
      const parse = (s) => {
        const parts = String(s || '').split('/');
        if (parts.length !== 3) return 0;
        const [m, d, y] = parts.map(Number);
        if (!m || !d || !y) return 0;
        return new Date(y, m-1, d).getTime();
      };
      av = parse(av);
      bv = parse(bv);
    } else {
      av = String(av || '').toLowerCase();
      bv = String(bv || '').toLowerCase();
    }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };
  const sortArrow = (field) => sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  // ---- Summary stats ----
  const statusCounts = PIPELINE_STATUS_OPTIONS.reduce((acc, s) => {
    acc[s] = pipeline.filter(p => p.status === s).length;
    return acc;
  }, {});

  const now = new Date();
  const isCurrentMonth = (dateStr) => {
    if (!dateStr) return false;
    const parts = String(dateStr).split('/');
    if (parts.length !== 3) return false;
    const [m, _, y] = parts.map(Number);
    return m === (now.getMonth() + 1) && y === now.getFullYear();
  };
  const totalIncomeMtd = pipeline
    .filter(p => isCurrentMonth(p.dateClosed))
    .reduce((s, p) => s + (Number(p.commission) || 0), 0);
  const totalPremiumsMtd = pipeline
    .filter(p => isCurrentMonth(p.dateClosed) && (p.status === 'Inforce (1st Payment made)' || p.status === 'Awaiting Initial Payment'))
    .reduce((s, p) => s + (Number(p.monthlyPremium) || 0), 0);
  const premiumPct = totalIncomeMtd > 0 ? (totalPremiumsMtd / totalIncomeMtd) * 100 : null;

  // ---- Carrier filter options derived from data ----
  const carrierOptions = Array.from(new Set(pipeline.map(p => p.carrier).filter(Boolean))).sort();

  // ---- Save ----
  const saveRecord = (record) => {
    const normalized = {
      ...record,
      contributionAmt: Number(record.contributionAmt) || 0,
      monthlyPremium: Number(record.contributionAmt) || 0,
      annualPremium: (Number(record.contributionAmt) || 0) * 12,
      commission: Number(record.commission) || 0,
      split: Number(record.split) || 0,
      chargebackAmount: Number(record.chargebackAmount) || 0,
    };
    if (record.id) {
      // Update existing
      setPipeline(prev => prev.map(p => p.id === record.id ? { ...p, ...normalized, agentId: p.agentId } : p));
    } else {
      // New
      const id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `pipe-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const defaultAgentId = String(settings?.ownerProdNum || '').trim();
      setPipeline(prev => [...prev, { ...normalized, id, agentId: defaultAgentId }]);
    }
    setEditing(null);
  };

  const deleteRecord = (id) => {
    setPipeline(prev => prev.filter(p => p.id !== id));
    setConfirmingDelete(null);
  };

  const editingRecord = editing === 'new' ? null : pipeline.find(p => p.id === editing);

  return (
    <div className="space-y-4">
      {/* Row 1 — status count cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {PIPELINE_STATUS_OPTIONS.filter(s => s !== 'Lapsed').map(s => {
          const color = PIPELINE_STATUS_COLOR[s];
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(statusFilter === s ? 'all' : s)}
              className={`text-left bg-white rounded-xl border-2 p-3 transition-all border-${color}-200 hover:border-${color}-400 ${statusFilter === s ? `ring-2 ring-${color}-400` : ''}`}
            >
              <div className={`text-xs font-semibold uppercase tracking-wide text-${color}-700`}>{s}</div>
              <div className={`text-2xl font-bold mt-1 text-${color}-900`}>{statusCounts[s]}</div>
            </button>
          );
        })}
      </div>

      {/* Row 2 — financial cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4">
          <div className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">Total Income MTD</div>
          <div className="text-2xl font-bold mt-1 text-emerald-900">{fmt(totalIncomeMtd)}</div>
          <div className="text-xs text-emerald-700 mt-1">Commission this month</div>
        </div>
        <div className="bg-violet-50 border-2 border-violet-200 rounded-xl p-4">
          <div className="text-xs text-violet-700 font-semibold uppercase tracking-wide">Total Premiums MTD</div>
          <div className="text-2xl font-bold mt-1 text-violet-900">{fmt(totalPremiumsMtd)}</div>
          <div className="text-xs text-violet-700 mt-1">Inforce + Awaiting closed this month</div>
        </div>
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
          <div className="text-xs text-blue-700 font-semibold uppercase tracking-wide">% Premium to Pay</div>
          <div className="text-2xl font-bold mt-1 text-blue-900">{premiumPct == null ? '—' : `${premiumPct.toFixed(1)}%`}</div>
          <div className="text-xs text-blue-700 mt-1">Premiums ÷ Income</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-2">
        <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white">
          <option value="all">All statuses</option>
          {PIPELINE_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={carrierFilter} onChange={e=>setCarrierFilter(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white">
          <option value="all">All carriers</option>
          {carrierOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search client, insured, or policy #" className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm flex-1 min-w-[200px]"/>
        <button onClick={() => exportPipelineCsv(sorted)} className="px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-50 rounded-lg text-sm font-medium">Export CSV</button>
        <button onClick={() => setEditing(editing === 'new' ? null : 'new')} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold">+ Add Policy</button>
      </div>

      {/* Add/Edit form (slide-down) */}
      {editing !== null && (
        <PipelineForm
          initial={editingRecord || PIPELINE_BLANK_RECORD}
          isNew={editing === 'new'}
          onSave={saveRecord}
          onCancel={() => setEditing(null)}
        />
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-xs text-slate-500 text-left">
                <PipelineHeader label="Date" field="dateClosed" sortField={sortField} sortDir={sortDir} onClick={toggleSort}/>
                <PipelineHeader label="Client" field="clientName" sortField={sortField} sortDir={sortDir} onClick={toggleSort}/>
                <PipelineHeader label="Insured" field="insured" sortField={sortField} sortDir={sortDir} onClick={toggleSort}/>
                <PipelineHeader label="Carrier" field="carrier" sortField={sortField} sortDir={sortDir} onClick={toggleSort}/>
                <PipelineHeader label="Policy #" field="policyNumber" sortField={sortField} sortDir={sortDir} onClick={toggleSort}/>
                <PipelineHeader label="Type" field="policyType" sortField={sortField} sortDir={sortDir} onClick={toggleSort}/>
                <PipelineHeader label="Status" field="status" sortField={sortField} sortDir={sortDir} onClick={toggleSort}/>
                <PipelineHeader label="1st Payment" field="firstPaymentDate" sortField={sortField} sortDir={sortDir} onClick={toggleSort}/>
                <PipelineHeader label="Ongoing" field="ongoingPaymentDate" sortField={sortField} sortDir={sortDir} onClick={toggleSort}/>
                <PipelineHeader label="Monthly" field="monthlyPremium" sortField={sortField} sortDir={sortDir} onClick={toggleSort} align="right"/>
                <PipelineHeader label="Coverage" field="dbCoverage" sortField={sortField} sortDir={sortDir} onClick={toggleSort}/>
                <th className="py-2 px-3">Notes</th>
                <th className="py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr><td colSpan={13} className="py-8 text-center text-slate-400">
                  {pipeline.length === 0 ? 'No policies in pipeline yet. Click "+ Add Policy" to start tracking.' : 'No policies match these filters.'}
                </td></tr>
              )}
              {sorted.map(p => {
                const color = PIPELINE_STATUS_COLOR[p.status] || 'slate';
                const days = pipelineDaysRemaining(p.firstPaymentDate);
                const notesPreview = (p.statusNotes || '').slice(0, 35);
                const notesTruncated = (p.statusNotes || '').length > 35;
                return (
                  <React.Fragment key={p.id}>
                    <tr className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3">{p.dateClosed ? fmtDateUS(p.dateClosed) : '—'}</td>
                      <td className="py-2 px-3 font-medium"><ClientNameLink name={p.clientName} openClientProfile={openClientProfile}/></td>
                      <td className="py-2 px-3"><ClientNameLink name={p.insured} openClientProfile={openClientProfile}/></td>
                      <td className="py-2 px-3"><span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-700">{p.carrier || '—'}</span></td>
                      <td className="py-2 px-3 font-mono text-xs">{p.policyNumber || '—'}</td>
                      <td className="py-2 px-3">{p.policyType || '—'}</td>
                      <td className="py-2 px-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-${color}-100 text-${color}-700`}>{p.status}</span></td>
                      <td className="py-2 px-3">
                        {p.firstPaymentDate && days == null && <span className="text-slate-400">{fmtDateUS(p.firstPaymentDate)}</span>}
                        {p.firstPaymentDate && days != null && (
                          <span className="inline-flex items-center gap-1.5">
                            <span>{fmtDateUS(p.firstPaymentDate)}</span>
                            {days > 0 && <span className="px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-700">+{days}d</span>}
                            {days === 0 && <span className="px-1.5 py-0.5 rounded text-xs bg-emerald-100 text-emerald-700">today</span>}
                          </span>
                        )}
                        {!p.firstPaymentDate && '—'}
                      </td>
                      <td className="py-2 px-3">{p.ongoingPaymentDate ? fmtDateUS(p.ongoingPaymentDate) : '—'}</td>
                      <td className="py-2 px-3 text-right">{p.monthlyPremium > 0 ? fmt(p.monthlyPremium) : '—'}</td>
                      <td className="py-2 px-3">{p.dbCoverage || '—'}</td>
                      <td className="py-2 px-3 text-xs text-slate-600" title={p.statusNotes || ''}>
                        {notesPreview}{notesTruncated ? '…' : ''}
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => setEditing(p.id)} className="text-slate-500 hover:text-emerald-700 p-1" title="Edit"></button>
                          <button onClick={() => setConfirmingDelete(p.id)} className="text-slate-500 hover:text-red-600 p-1" title="Delete"><Trash2 size={14}/></button>
                        </div>
                      </td>
                    </tr>
                    {confirmingDelete === p.id && (
                      <tr className="bg-red-50 border-b border-red-200">
                        <td colSpan={13} className="py-2 px-3 text-sm">
                          Delete <strong>{p.clientName || 'this policy'}</strong>?
                          <button onClick={() => deleteRecord(p.id)} className="ml-3 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs font-semibold">Confirm</button>
                          <button onClick={() => setConfirmingDelete(null)} className="ml-2 bg-white border border-slate-300 hover:bg-slate-50 px-3 py-1 rounded text-xs">Cancel</button>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PipelineHeader({ label, field, sortField, sortDir, onClick, align = 'left' }) {
  const active = sortField === field;
  const arrow = active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  return (
    <th onClick={() => onClick(field)} className={`py-2 px-3 cursor-pointer select-none hover:text-slate-900 ${active ? 'text-slate-900' : ''}`} style={{ textAlign: align }}>
      {label}{arrow}
    </th>
  );
}

function PipelineForm({ initial, isNew, onSave, onCancel }) {
  const [r, setR] = useState({ ...initial });
  const [errors, setErrors] = useState({});
  const set = (field, value) => setR(prev => ({ ...prev, [field]: value }));

  const isOtherCarrier = !PIPELINE_CARRIER_PRESETS.slice(0, 5).includes(r.carrier) && r.carrier !== '';

  const trySave = () => {
    const errs = {};
    if (!r.clientName?.trim()) errs.clientName = 'Required';
    if (!r.carrier?.trim()) errs.carrier = 'Required';
    if (!r.status?.trim()) errs.status = 'Required';
    setErrors(errs);
    if (Object.keys(errs).length === 0) onSave(r);
  };

  const chargebackWarn = r.status === 'Chargeback' && Number(r.chargebackAmount) > 0;

  return (
    <div className="bg-white rounded-xl border-2 border-emerald-300 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-bold">{isNew ? 'Add new policy' : 'Edit policy'}</h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
      </div>

      {/* Policy Info */}
      <div>
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Policy Info</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
          <PipeField label="Date closed" value={r.dateClosed} onChange={v => set('dateClosed', v)} placeholder="MM/DD/YYYY"/>
          <PipeField label="Client name *" value={r.clientName} onChange={v => set('clientName', v)} error={errors.clientName} required/>
          <PipeField label="Insured" value={r.insured} onChange={v => set('insured', v)}/>
          <PipeField label="Phone" value={r.phone} onChange={v => set('phone', v)}/>
          <div className="block">
            <span className="text-xs text-slate-500">Carrier *</span>
            <select value={isOtherCarrier ? 'Other' : (r.carrier || '')} onChange={e => {
              if (e.target.value === 'Other') set('carrier', '__other__');
              else set('carrier', e.target.value);
            }} className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white ${errors.carrier ? 'border-red-400' : 'border-slate-200'}`}>
              <option value="">— Select —</option>
              {PIPELINE_CARRIER_PRESETS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {(isOtherCarrier || r.carrier === '__other__') && (
              <input value={r.carrier === '__other__' ? '' : r.carrier} onChange={e => set('carrier', e.target.value)} placeholder="Custom carrier" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
            )}
            {errors.carrier && <span className="text-xs text-red-600">{errors.carrier}</span>}
          </div>
          <PipeField label="Policy number" value={r.policyNumber} onChange={v => set('policyNumber', v)}/>
          <PipeField label="Policy type" value={r.policyType} onChange={v => set('policyType', v)} placeholder="IUL, Term, ES3120"/>
          <PipeField label="Product type" value={r.productType} onChange={v => set('productType', v)} placeholder="Life, Annuity"/>
          <div className="block">
            <span className="text-xs text-slate-500">Status *</span>
            <select value={r.status} onChange={e => set('status', e.target.value)} className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white ${errors.status ? 'border-red-400' : 'border-slate-200'}`}>
              {PIPELINE_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {errors.status && <span className="text-xs text-red-600">{errors.status}</span>}
          </div>
        </div>
      </div>

      {/* Payment Info */}
      <div>
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Payment Info</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
          <PipeField label="Status notes" value={r.statusNotes} onChange={v => set('statusNotes', v)}/>
          <PipeField label="First payment date" value={r.firstPaymentDate} onChange={v => set('firstPaymentDate', v)} placeholder="MM/DD/YYYY or x"/>
          <PipeField label="Ongoing payment date" value={r.ongoingPaymentDate} onChange={v => set('ongoingPaymentDate', v)} placeholder="15th, Last Day"/>
          <PipeField label="Monthly Premium $" type="number" value={r.contributionAmt} onChange={v => set('contributionAmt', v)}/>
          <PipeField label="DB Coverage" value={r.dbCoverage} onChange={v => set('dbCoverage', v)} placeholder="$300K"/>
          <PipeField label="Bank" value={r.bank} onChange={v => set('bank', v)}/>
          <PipeField label="State" value={r.state} onChange={v => set('state', v?.toUpperCase()?.slice(0, 2))} maxLength={2}/>
          <PipeField label="Lead source" value={r.leadSource} onChange={v => set('leadSource', v)}/>
        </div>
      </div>

      {/* Commission Info */}
      <div>
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Commission Info</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
          <PipeField label="Commission $" type="number" value={r.commission} onChange={v => set('commission', v)}/>
          <PipeField label="Split %" type="number" value={r.split} onChange={v => set('split', v)}/>
          <PipeField label="Payout date" value={r.payoutDate} onChange={v => set('payoutDate', v)} placeholder="MM/DD/YYYY"/>
          <PipeField label="Chargeback amount $" type="number" value={r.chargebackAmount} onChange={v => set('chargebackAmount', v)}/>
        </div>
        {chargebackWarn && (
          <div className="mt-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-800">
            ⚠ This policy will be flagged as a chargeback of ${Number(r.chargebackAmount).toFixed(2)}
          </div>
        )}
      </div>

      {/* Comments */}
      <div>
        <label className="block text-sm">
          <span className="text-xs text-slate-500">Comments</span>
          <textarea value={r.comments} onChange={e => set('comments', e.target.value)} rows={3} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
        </label>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
        <button onClick={onCancel} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
        <button onClick={trySave} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold">{isNew ? 'Save policy' : 'Update'}</button>
      </div>
    </div>
  );
}

function PipeField({ label, value, onChange, type = 'text', placeholder = '', maxLength, error, required }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-500">{label}</span>
      <input
        type={type}
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${error ? 'border-red-400' : 'border-slate-200'}`}
      />
      {error && <span className="text-xs text-red-600">{error}</span>}
    </label>
  );
}

function exportPipelineCsv(rows) {
  const headers = ['Date Closed','Client Name','Insured','Phone','Carrier','Policy Number','Policy Type','Product Type','Status','Status Notes','First Payment Date','Ongoing Payment Date','Monthly Premium','Annual Premium','DB Coverage','Bank','State','Lead Source','Commission','Split','Payout Date','Chargeback Amount','Comments'];
  const fieldMap = {
    'Date Closed': r => r.dateClosed,
    'Client Name': r => r.clientName,
    'Insured': r => r.insured,
    'Phone': r => r.phone,
    'Carrier': r => r.carrier,
    'Policy Number': r => r.policyNumber,
    'Policy Type': r => r.policyType,
    'Product Type': r => r.productType,
    'Status': r => r.status,
    'Status Notes': r => r.statusNotes,
    'First Payment Date': r => r.firstPaymentDate,
    'Ongoing Payment Date': r => r.ongoingPaymentDate,
    'Monthly Premium': r => r.monthlyPremium,
    'Annual Premium': r => r.annualPremium,
    'DB Coverage': r => r.dbCoverage,
    'Bank': r => r.bank,
    'State': r => r.state,
    'Lead Source': r => r.leadSource,
    'Commission': r => r.commission,
    'Split': r => r.split,
    'Payout Date': r => r.payoutDate,
    'Chargeback Amount': r => r.chargebackAmount,
    'Comments': r => r.comments,
  };
  const csv = [headers.join(','), ...rows.map(r => headers.map(h => `"${(fieldMap[h](r) ?? '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `pipeline_${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ──────────────────────────────────────────────────────────────────────
// Authentication views — agent-only dashboard, admin user mgmt.
//
// LoginScreen, SignupScreen, ResetPasswordScreen now live in src/auth/*
// and are imported at the top of this file. The Phase A0 MaintenancePage
// below is kept defined for future use (planned maintenance windows) but
// is no longer rendered.
// ──────────────────────────────────────────────────────────────────────

// eslint-disable-next-line no-unused-vars
function MaintenancePage() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center">
        <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-700 mx-auto mb-4 flex items-center justify-center font-bold text-xl">!</div>
        <h1 className="text-xl font-bold mb-2">Upgrading — back in a few hours</h1>
        <p className="text-sm text-slate-600 leading-relaxed">
          Commission Command is briefly offline. No data has been lost.
        </p>
        <p className="text-xs text-slate-400 mt-4">
          Questions? Contact your admin.
        </p>
      </div>
    </div>
  );
}

// Helpers for time tracking + activity stats
function vaSessionEndMillis(log) {
  return log.clockOut ? new Date(log.clockOut).getTime() : Date.now();
}
function totalVaHours(logs, startDate, endDate) {
  const start = startDate ? startDate.getTime() : 0;
  const end = endDate ? endDate.getTime() : Date.now();
  let totalMin = 0;
  (logs || []).forEach(l => {
    const s = new Date(l.clockIn).getTime();
    const e = vaSessionEndMillis(l);
    const oStart = Math.max(s, start);
    const oEnd = Math.min(e, end);
    if (oEnd > oStart) totalMin += (oEnd - oStart) / 60000;
  });
  return totalMin / 60;
}
function startOfDay(d = new Date()) {
  const x = new Date(d); x.setHours(0, 0, 0, 0); return x;
}
function startOfWeek(d = new Date()) {
  const x = startOfDay(d);
  x.setDate(x.getDate() - x.getDay()); // Sunday
  return x;
}
function startOfMonth(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function countAppointmentsBy(calendar, username, since) {
  const sinceMs = since ? since.getTime() : 0;
  return (calendar || []).filter(a => {
    if (a.createdByUser?.username !== username) return false;
    if (!since) return true;
    const t = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    return t >= sinceMs;
  }).length;
}
function countContactsBy(clientProfiles, username, since) {
  const sinceMs = since ? since.getTime() : 0;
  let count = 0;
  Object.values(clientProfiles || {}).forEach(profile => {
    (profile.contactLog || []).forEach(c => {
      if (c.loggedByUser?.username !== username) return;
      if (!since) { count++; return; }
      const t = c.loggedAt ? new Date(c.loggedAt).getTime() : 0;
      if (t >= sinceMs) count++;
    });
  });
  return count;
}

function VATimeTracker({ vaUsername, vaName, logs, onClockIn, onClockOut }) {
  // Tick state so the active-session timer updates live
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 30000); // refresh every 30s
    return () => clearInterval(t);
  }, []);

  const active = (logs || []).find(l => !l.clockOut);
  const today = startOfDay();
  const week = startOfWeek();
  const hoursToday = totalVaHours(logs, today);
  const hoursWeek = totalVaHours(logs, week);
  const activeMin = active ? (Date.now() - new Date(active.clockIn).getTime()) / 60000 : 0;
  const activeStr = active ? `${Math.floor(activeMin / 60)}h ${Math.floor(activeMin % 60)}m` : null;

  const recent = (logs || []).slice().sort((a, b) => new Date(b.clockIn) - new Date(a.clockIn)).slice(0, 5);

  return (
    <div className="bg-gradient-to-br from-violet-600 to-indigo-700 text-white rounded-xl p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs text-violet-200 uppercase tracking-wider font-semibold">Time tracker · {vaName}</div>
          {active ? (
            <div className="mt-1">
              <div className="text-2xl font-bold">Clocked in · {activeStr}</div>
              <div className="text-xs text-violet-200">Since {new Date(active.clockIn).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</div>
            </div>
          ) : (
            <div className="mt-1">
              <div className="text-2xl font-bold">Clocked out</div>
              <div className="text-xs text-violet-200">Click below to start your shift</div>
            </div>
          )}
        </div>
        {active ? (
          <button onClick={onClockOut} className="bg-red-500 hover:bg-red-600 text-white font-semibold px-5 py-3 rounded-lg shadow">
            Clock Out
          </button>
        ) : (
          <button onClick={onClockIn} className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-5 py-3 rounded-lg shadow">
            ▶ Clock In
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        <div className="bg-white/10 rounded-lg p-3">
          <div className="text-xs text-violet-200 uppercase tracking-wide">Today</div>
          <div className="text-xl font-bold">{hoursToday.toFixed(2)}h</div>
        </div>
        <div className="bg-white/10 rounded-lg p-3">
          <div className="text-xs text-violet-200 uppercase tracking-wide">This week</div>
          <div className="text-xl font-bold">{hoursWeek.toFixed(2)}h</div>
        </div>
      </div>
      {recent.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-violet-200 hover:text-white">Recent sessions ({recent.length})</summary>
          <div className="mt-2 space-y-1 text-xs">
            {recent.map(l => {
              const start = new Date(l.clockIn);
              const end = l.clockOut ? new Date(l.clockOut) : null;
              const durMin = (end ? end.getTime() : Date.now()) - start.getTime();
              const h = Math.floor(durMin / 3600000);
              const m = Math.floor((durMin % 3600000) / 60000);
              return (
                <div key={l.id} className="bg-white/10 rounded px-2 py-1 flex items-center gap-2">
                  <span className="font-mono">{start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  <span>{start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                  <span>→</span>
                  <span>{end ? end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : 'now'}</span>
                  <span className="ml-auto font-semibold">{h}h {m}m</span>
                </div>
              );
            })}
          </div>
        </details>
      )}
    </div>
  );
}

function AgentDashboard({ currentUser, servedAgentProdNum, servedAgentName, transactions, producers, onSignOut, myVas, onCreateVa, onResetVaPassword, onDeleteVa, isUsernameTaken, vaTimeLogs = [], onClockIn, onClockOut, onDeleteTimeLog, calendar = [], clientProfiles = {} }) {
  const [scope, setScope] = useState('all'); // 'week' | 'month' | 'all'

  // Defensive: even though transactions are pre-filtered by filterForAgent at the call site,
  // re-filter here so a child render can never accidentally see another agent's data.
  // Use the served agent's prodNum (VA path) — falls back to currentUser.prodNum for agents.
  const targetProdNum = servedAgentProdNum || currentUser.prodNum;
  const myTx = (transactions || []).filter(t => t.prodNum === targetProdNum);
  const isVaSession = currentUser.role === 'va';

  const now = new Date();
  const monthAgo = new Date(now); monthAgo.setMonth(monthAgo.getMonth() - 1);
  const weekAgo = new Date(now); weekAgo.setDate(weekAgo.getDate() - 7);

  const scoped = myTx.filter(t => {
    if (scope === 'all') return true;
    const d = parseDate(t.activityDate);
    if (!d) return false;
    if (scope === 'week') return d >= weekAgo;
    if (scope === 'month') return d >= monthAgo;
    return true;
  });

  const totalEarned = scoped.filter(t => (t.commAmt || 0) > 0).reduce((s, t) => s + (Number(t.commAmt) || 0), 0);
  const totalChargebacks = Math.abs(scoped.filter(t => (t.commAmt || 0) < 0).reduce((s, t) => s + (Number(t.commAmt) || 0), 0));
  const totalAdvances = scoped.reduce((s, t) => s + (Number(t.advComm) || 0), 0);
  const netCommissions = scoped.reduce((s, t) => s + (Number(t.netToAccount) || 0), 0);

  const sorted = scoped.slice().sort((a, b) => {
    const ad = parseDate(a.activityDate)?.getTime() || 0;
    const bd = parseDate(b.activityDate)?.getTime() || 0;
    return bd - ad;
  });

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-600 flex items-center justify-center text-white font-bold">$</div>
            <div>
              <div className="font-bold text-lg leading-tight">My Commissions</div>
              <div className="text-xs text-slate-500">
                Welcome, <span className="font-semibold text-emerald-700">{currentUser.name}</span> · <span className="font-mono">{currentUser.prodNum}</span>
                {isVaSession && <span className="ml-1 text-violet-700 font-semibold">· VA viewing for {servedAgentName}</span>}
              </div>
            </div>
          </div>
          <button onClick={onSignOut} className="text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-lg font-medium">Sign out</button>
        </div>
      </div>

      <div className="max-w-none mx-auto p-4 space-y-4">
        {/* Filter */}
        <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide mr-1">Range:</span>
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-sm">
            <button onClick={()=>setScope('week')} className={`px-3 py-1.5 font-medium ${scope==='week'?'bg-emerald-600 text-white':'text-slate-600 hover:bg-slate-50'}`}>This week</button>
            <button onClick={()=>setScope('month')} className={`px-3 py-1.5 font-medium ${scope==='month'?'bg-emerald-600 text-white':'text-slate-600 hover:bg-slate-50'}`}>This month</button>
            <button onClick={()=>setScope('all')} className={`px-3 py-1.5 font-medium ${scope==='all'?'bg-emerald-600 text-white':'text-slate-600 hover:bg-slate-50'}`}>All time</button>
          </div>
          <span className="ml-auto text-xs text-slate-500">{sorted.length} of {myTx.length} rows</span>
        </div>

        {/* Time tracker — only for VAs */}
        {isVaSession && (
          <VATimeTracker
            vaUsername={currentUser.prodNum}
            vaName={currentUser.name}
            logs={vaTimeLogs.filter(l => l.vaUsername === currentUser.prodNum)}
            onClockIn={() => onClockIn(currentUser.prodNum)}
            onClockOut={() => onClockOut(currentUser.prodNum)}
          />
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4">
            <div className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">Total earned</div>
            <div className="text-2xl font-bold mt-1 text-emerald-900">{fmt(totalEarned)}</div>
            <div className="text-xs text-emerald-700 mt-1">Positive Comm Amt rows</div>
          </div>
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
            <div className="text-xs text-red-700 font-semibold uppercase tracking-wide">Chargebacks</div>
            <div className="text-2xl font-bold mt-1 text-red-900">{fmt(totalChargebacks)}</div>
            <div className="text-xs text-red-700 mt-1">Sum of negative rows</div>
          </div>
          <div className="bg-violet-50 border-2 border-violet-200 rounded-xl p-4">
            <div className="text-xs text-violet-700 font-semibold uppercase tracking-wide">Total advances</div>
            <div className="text-2xl font-bold mt-1 text-violet-900">{fmt(totalAdvances)}</div>
            <div className="text-xs text-violet-700 mt-1">Adv Comm column</div>
          </div>
          <div className="bg-slate-100 border-2 border-slate-300 rounded-xl p-4">
            <div className="text-xs text-slate-700 font-semibold uppercase tracking-wide">Net commissions</div>
            <div className={`text-2xl font-bold mt-1 ${netCommissions < 0 ? 'text-red-700' : 'text-slate-900'}`}>{fmt(netCommissions)}</div>
            <div className="text-xs text-slate-700 mt-1">After chargebacks</div>
          </div>
        </div>

        {/* Transaction table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200">
            <div className="font-bold">Transactions</div>
            <div className="text-xs text-slate-500 mt-0.5">Your commission activity across MOO, Americo, and Transamerica</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs text-slate-500">
                  <th className="py-2 px-3">Date</th>
                  <th className="py-2 px-3">Carrier</th>
                  <th className="py-2 px-3">Policy #</th>
                  <th className="py-2 px-3">Insured</th>
                  <th className="py-2 px-3">Activity</th>
                  <th className="py-2 px-3 text-right">Commission</th>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 && (
                  <tr><td colSpan={6} className="py-8 text-center text-slate-400">No transactions in this range.</td></tr>
                )}
                {sorted.map(tx => {
                  const carrier = deriveCarrier(tx);
                  const amt = Number(tx.commAmt) || 0;
                  const isNeg = amt < 0;
                  return (
                    <tr key={tx.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3">{fmtDateUS(tx.activityDate) || tx.activityDate || '—'}</td>
                      <td className="py-2 px-3"><span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-700">{carrier}</span></td>
                      <td className="py-2 px-3 font-mono text-xs">{tx.policy}</td>
                      <td className="py-2 px-3">{titleCase(tx.insured || '')}</td>
                      <td className="py-2 px-3 text-xs">{tx.activityType || '—'}</td>
                      <td className={`py-2 px-3 text-right font-semibold ${isNeg ? 'text-red-600' : ''}`}>{fmt(amt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* VA management — only for agents, not VAs themselves */}
        {!isVaSession && (
          <MyVAsPanel
            myVas={myVas || []}
            currentUserProdNum={currentUser.prodNum}
            onCreateVa={onCreateVa}
            onResetVaPassword={onResetVaPassword}
            onDeleteVa={onDeleteVa}
            isUsernameTaken={isUsernameTaken}
            vaTimeLogs={vaTimeLogs}
            onDeleteTimeLog={onDeleteTimeLog}
            calendar={calendar}
            clientProfiles={clientProfiles}
          />
        )}

        <div className="text-center text-xs text-slate-400 py-4">
          Numbers shown are based on commission statements uploaded by your admin. If something looks off, reach out to your admin.
        </div>
      </div>
    </div>
  );
}

function VaStatCell({ label, value }) {
  return (
    <div className="bg-white border border-violet-200 rounded px-2 py-1">
      <div className="text-[10px] uppercase tracking-wide text-violet-700">{label}</div>
      <div className="font-bold text-violet-900">{value}</div>
    </div>
  );
}

function MyVAsPanel({ myVas, currentUserProdNum, onCreateVa, onResetVaPassword, onDeleteVa, isUsernameTaken, vaTimeLogs = [], onDeleteTimeLog, calendar = [], clientProfiles = {} }) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newUser, setNewUser] = useState('');
  const [newPass, setNewPass] = useState('');
  const [showNewPass, setShowNewPass] = useState(false);
  const [error, setError] = useState('');
  const [resetForUser, setResetForUser] = useState(null);
  const [resetDraft, setResetDraft] = useState('');
  const [confirmDel, setConfirmDel] = useState(null);

  const trySubmit = () => {
    setError('');
    const result = onCreateVa({
      username: newUser,
      password: newPass,
      name: newName,
      servesAgentProdNum: currentUserProdNum,
    });
    if (!result.ok) { setError(result.error); return; }
    setAdding(false);
    setNewName(''); setNewUser(''); setNewPass(''); setShowNewPass(false);
  };

  const submitReset = () => {
    if (!resetForUser || !resetDraft) return;
    onResetVaPassword(resetForUser, resetDraft);
    setResetForUser(null);
    setResetDraft('');
  };

  return (
    <div className="bg-violet-50 border-2 border-violet-200 rounded-xl p-5">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <h3 className="font-bold text-violet-900">My virtual assistants</h3>
          <p className="text-xs text-violet-700 mt-0.5">Create login accounts for your VA. They'll see only your commission data — same view you see, with a "VA viewing for you" badge.</p>
        </div>
        {!adding && (
          <button onClick={() => setAdding(true)} className="text-sm bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 rounded-lg font-semibold">+ Add VA</button>
        )}
      </div>

      {adding && (
        <div className="bg-white border border-violet-200 rounded-lg p-3 mb-3 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
            <label className="block">
              <span className="text-xs text-slate-500">Display name *</span>
              <input value={newName} onChange={e=>{ setNewName(e.target.value); setError(''); }} placeholder="Lisa Smith" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">Username *</span>
              <input value={newUser} onChange={e=>{ setNewUser(e.target.value); setError(''); }} placeholder="lisa_va" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono"/>
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">Password *</span>
              <div className="flex gap-1 mt-1">
                <input
                  type={showNewPass ? 'text' : 'password'}
                  value={newPass}
                  onChange={e=>{ setNewPass(e.target.value); setError(''); }}
                  className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm"
                />
                <button type="button" onClick={()=>setShowNewPass(s=>!s)} className="text-xs px-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-100">{showNewPass ? 'Hide' : 'Show'}</button>
              </div>
            </label>
          </div>
          {error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{error}</div>}
          <div className="flex items-center justify-end gap-2">
            <button onClick={() => { setAdding(false); setError(''); setNewName(''); setNewUser(''); setNewPass(''); }} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
            <button onClick={trySubmit} className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-semibold">Create VA account</button>
          </div>
        </div>
      )}

      {myVas.length === 0 && !adding && (
        <div className="text-sm text-violet-800 italic bg-white border border-violet-200 rounded-lg p-3">
          You haven't added a VA yet. Click "+ Add VA" to create their login. Share the username + password securely with your VA — they'll sign in on the regular login screen.
        </div>
      )}

      {myVas.length > 0 && (
        <div className="bg-white border border-violet-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-violet-50 border-b border-violet-200">
              <tr className="text-left text-xs text-violet-700">
                <th className="py-2 px-3">Name</th>
                <th className="py-2 px-3">Username</th>
                <th className="py-2 px-3">Created</th>
                <th className="py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {myVas.map(([username, info]) => {
                const vaLogs = vaTimeLogs.filter(l => l.vaUsername === username);
                const hoursWeek = totalVaHours(vaLogs, startOfWeek());
                const hoursMonth = totalVaHours(vaLogs, startOfMonth());
                const apptsWeek = countAppointmentsBy(calendar, username, startOfWeek());
                const apptsMonth = countAppointmentsBy(calendar, username, startOfMonth());
                const contactsWeek = countContactsBy(clientProfiles, username, startOfWeek());
                const contactsMonth = countContactsBy(clientProfiles, username, startOfMonth());
                const isActive = vaLogs.some(l => !l.clockOut);
                return (
                <React.Fragment key={username}>
                  <tr className="border-b border-slate-100">
                    <td className="py-2 px-3">
                      {info.name}
                      {isActive && <span className="ml-2 text-[10px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">ACTIVE</span>}
                    </td>
                    <td className="py-2 px-3 font-mono text-xs">{username}</td>
                    <td className="py-2 px-3 text-xs text-slate-500">{info.createdAt ? new Date(info.createdAt).toLocaleDateString() : '—'}</td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => { setResetForUser(username); setResetDraft(''); }} className="text-xs bg-white border border-slate-300 hover:bg-slate-100 px-2 py-1 rounded">Reset password</button>
                        <button onClick={() => setConfirmDel(username)} className="text-xs bg-white border border-red-300 text-red-700 hover:bg-red-50 px-2 py-1 rounded">Delete</button>
                      </div>
                    </td>
                  </tr>
                  <tr className="bg-violet-50/40 border-b border-violet-100">
                    <td colSpan={4} className="px-3 py-2">
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
                        <VaStatCell label="Hours · week" value={`${hoursWeek.toFixed(1)}h`}/>
                        <VaStatCell label="Hours · month" value={`${hoursMonth.toFixed(1)}h`}/>
                        <VaStatCell label="Appts · week" value={apptsWeek}/>
                        <VaStatCell label="Appts · month" value={apptsMonth}/>
                        <VaStatCell label="Contacts · week" value={contactsWeek}/>
                        <VaStatCell label="Contacts · month" value={contactsMonth}/>
                      </div>
                    </td>
                  </tr>
                  {resetForUser === username && (
                    <tr className="bg-amber-50 border-b border-amber-200">
                      <td colSpan={4} className="py-2 px-3 text-sm">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-slate-700">New password for {info.name}:</span>
                          <input
                            type="text"
                            value={resetDraft}
                            onChange={e => setResetDraft(e.target.value)}
                            autoFocus
                            className="px-2 py-1 border border-slate-200 rounded text-sm font-mono"
                            placeholder="new password"
                          />
                          <button onClick={submitReset} disabled={!resetDraft} className="text-xs bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-3 py-1 rounded font-semibold">Save</button>
                          <button onClick={() => { setResetForUser(null); setResetDraft(''); }} className="text-xs bg-white border border-slate-300 hover:bg-slate-50 px-2 py-1 rounded">Cancel</button>
                        </div>
                      </td>
                    </tr>
                  )}
                  {confirmDel === username && (
                    <tr className="bg-red-50 border-b border-red-200">
                      <td colSpan={4} className="py-2 px-3 text-sm">
                        Delete the VA account for <strong>{info.name}</strong>? They'll lose access immediately.
                        <button onClick={() => { onDeleteVa(username); setConfirmDel(null); }} className="ml-3 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs font-semibold">Confirm delete</button>
                        <button onClick={() => setConfirmDel(null)} className="ml-1 bg-white border border-slate-300 hover:bg-slate-50 px-3 py-1 rounded text-xs">Cancel</button>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* AUDIT D1 FIX: removed the "passwords stored in localStorage
          in plain text — TODO to add a real backend" warning. The
          referenced localStorage password flow was replaced by
          Supabase Auth long ago; the warning was misleading operators
          into thinking a security flaw was live when it isn't. Real
          password management goes through the ForgotPassword flow. */}
    </div>
  );
}

function ManageAgentsPanel({ currentUser, producers = {} }) {
  // Production password management goes through Supabase Auth — the
  // session-only credential reset UI was misleading and is removed.
  // To change a user's password: have them use "Forgot password?" at
  // sign-in (sends a Supabase Auth recovery email), or use the Supabase
  // dashboard → Authentication → Users → ... → Send password recovery.
  //
  // 2026-06-27: roster now reads from the live `producers` state
  // (every prodNum the operator has set up) instead of the legacy
  // AGENT_CREDENTIALS object which is hardcoded empty. Falls back to
  // an empty-state row when no producers exist yet so the table
  // doesn't render headers above nothing.
  const rows = Object.entries(producers || {})
    .filter(([prodNum, info]) => prodNum && info && !info.aliasOf)
    .map(([prodNum, info]) => [prodNum, {
      name: info.name || `Producer ${prodNum}`,
      role: prodNum === currentUser?.prodNum ? (currentUser?.role || 'agent') : 'agent',
      team: info.team || 'unassigned',
    }])
    .sort((a, b) => {
      if (a[1].role !== b[1].role) return a[1].role === 'admin' ? -1 : 1;
      return (a[1].name || '').localeCompare(b[1].name || '');
    });
  return (
    <div className="bg-slate-50 border-2 border-slate-300 rounded-xl p-5">
      <h3 className="font-bold mb-1 text-slate-900">Team roster</h3>
      <p className="text-sm text-slate-600 mb-3">
        Active agents on your seat. To change a password, have the agent use <strong>Forgot password?</strong> at sign-in — Supabase Auth will send them a recovery email. (The legacy in-session reset has been removed because it didn't persist across reloads.)
      </p>
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs text-slate-500">
              <th className="py-2 px-3">Agent ID</th>
              <th className="py-2 px-3">Name</th>
              <th className="py-2 px-3">Role</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={3} className="py-4 px-3 text-center text-xs text-slate-500 italic">
                  No producers on this seat yet. Add producers in the Producers tab so they appear here.
                </td>
              </tr>
            ) : (
              rows.map(([id, info]) => (
                <tr key={id} className={`border-b border-slate-100 ${id === currentUser?.prodNum ? 'bg-emerald-50' : ''}`}>
                  <td className="py-2 px-3 font-mono text-xs">{id}</td>
                  <td className="py-2 px-3">{info.name}{id === currentUser?.prodNum && <span className="text-xs text-emerald-700 ml-1">(you)</span>}</td>
                  <td className="py-2 px-3">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${info.role === 'admin' ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'}`}>{info.role}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// PayoutsView removed. It rendered "Pending weekly payouts to CAM" +
// a "Mark paid" button + a payout history table — all built around a
// hardcoded revenue-share with a partner named CAM. The SaaS doesn't
// have a CAM partner; per-tenant override / split payouts will be
// driven by the commissionRules system once that UI lands.

// ──────────────────────────────────────────────────────────────────────
// Retention — Policy Conservation Opportunities worklist.
//
// Carriers (today: MOO) hand you save-the-policy reports listing every
// policy with a lapse notice, returned mail, NSF, etc. This tab ingests
// those reports and turns them into a triage queue prioritized by:
//   - chargeback risk (lapse in the advance window = real $ at stake)
//   - days until termination
//   - event severity
//
// The tab is upload-driven (PDF or pasted text) and the parsed events
// persist in user_state via K.conservation. Per-event workflow tracks
// status (open / contacted / cured / dismissed) so the operator can
// work the list without losing context across days.
//
// Cross-reference: when a parsed event's policy number matches a
// transaction in the operator's book, we surface the commission $ at
// risk inline. That's the whole financial thesis of this tab — the
// conservation report IS the at-risk-advance feed.
// ──────────────────────────────────────────────────────────────────────
function RetentionView({
  conservation = [],
  setConservation = () => {},
  transactions = [],
  producers = {},
  setProducers = () => {},
  settings = {},
  clientProfiles = {},
  setClientProfiles = () => {},
  calendar = [],
  setCalendar = () => {},
  leadTemplates = [],
  leads = [],
  currentUser = null,
  showToast = () => {},
  openClientProfile = () => {},
}) {
  const [pasteText, setPasteText] = React.useState('');
  const [showPaste, setShowPaste] = React.useState(false);
  const [search, setSearch]   = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('open');     // open | contacted | cured | dismissed | all
  const [typeFilter, setTypeFilter]     = React.useState('all');      // all | lapse_warning | returned_mail | nsf | ...
  const [priorityFilter, setPriorityFilter] = React.useState('all');  // all | critical | high | medium | low
  const [sortKey, setSortKey] = React.useState('priority');           // priority | termination | event_date | premium_at_risk
  const [expanded, setExpanded] = React.useState({});                 // { [id]: bool }
  const fileInputRef = React.useRef(null);

  const today = React.useMemo(() => new Date(), []);

  // ── Cross-reference index ──────────────────────────────────────────
  // policy number → latest commission transaction for that policy.
  // Used to surface "$X advanced, in chargeback window" on each row.
  const txByPolicy = React.useMemo(() => {
    const idx = {};
    (transactions || []).forEach(tx => {
      const p = String(tx.policy || '').trim();
      if (!p) return;
      // Track the most recent (largest commPremium) record per policy
      // so we display the most representative figure.
      if (!idx[p] || Number(tx.commPremium) > Number(idx[p].commPremium)) idx[p] = tx;
    });
    return idx;
  }, [transactions]);

  // ── Ingest handler ─────────────────────────────────────────────────
  // Two side effects per import:
  //   1. Conservation events upsert (same conservationEventId means
  //      we refresh the carrier data but preserve operator status + note).
  //   2. Auto-CRM link: each parsed client gets pushed into
  //      clientProfiles (Book of Business) with the carrier-provided
  //      phone + address. Existing fields the operator already filled
  //      in are preserved — we only fill the blanks.
  const ingestText = (rawText, source) => {
    try {
      const { events, header } = parseConservationReport(rawText);
      if (!events.length) {
        showToast('Couldn\'t find any conservation events in that text. Make sure you pasted the whole report.', 'error');
        return;
      }
      const newlyAdded = []; // collected here so the conservation auto-enroll can fire AFTER state settles
      setConservation(prev => {
        const byId = {};
        prev.forEach(e => { byId[e.id] = e; });
        let added = 0, updated = 0;
        events.forEach(ev => {
          const id = conservationEventId(ev);
          const existing = byId[id];
          byId[id] = {
            ...ev,
            id,
            status: existing?.status || 'open',
            note:   existing?.note   || '',
            createdAt: existing?.createdAt || new Date().toISOString(),
            lastSeenInReport: header.reportRunDate || ev.reportRunDate || null,
            source: source || existing?.source || 'upload',
          };
          if (existing) updated++; else { added++; newlyAdded.push(byId[id]); }
        });
        // Broker / agent header → producers map. Every MOO conservation
        // report carries an "AGENT: <prodNum> - <Name>" header line
        // that parseConservationReport surfaces as header.agentName +
        // header.agentProdNum. Wire that into producers so the next
        // time anything calls getProducerName(prodNum) — Top
        // Contributors, By Agent, Writing agent on a Retention row,
        // any other view — it returns the real name instead of the
        // "Producer 1173602" fallback. Only fills blanks; manual
        // edits in the Producers tab win.
        if (header?.agentProdNum && header?.agentName) {
          setProducers(prevProds => {
            const cur = prevProds?.[header.agentProdNum] || {};
            if (cur.name) return prevProds;
            return {
              ...prevProds,
              [header.agentProdNum]: {
                ...cur,
                name: header.agentName,
                team: cur.team || 'mine',
              },
            };
          });
        }

        // Auto-CRM link — happens during this same setConservation call
        // so the toast at the end can mention the CRM upserts too.
        let linked = 0;
        setClientProfiles(prevProfiles => {
          const next = { ...prevProfiles };
          events.forEach(ev => {
            if (!ev.clientName) return;
            const key = String(ev.clientName).trim().toUpperCase().replace(/\s+/g, ' ');
            const cur = next[key] || {};
            // Only fill BLANK fields. Operator's edits win over carrier
            // data — they may have a better phone on file than the
            // carrier (or a typo-fixed address).
            const merged = {
              ...cur,
              phone:     cur.phone     || ev.phone || '',
              address1:  cur.address1  || ev.addressLine1 || '',
              city:      cur.city      || ev.city  || '',
              state:     cur.state     || ev.state || '',
              zip:       cur.zip       || ev.zip   || '',
            };
            // Mark the source so the operator knows where these values
            // came from when reviewing the client card.
            if (!cur.address1 && ev.addressLine1) {
              merged.contactSource = (merged.contactSource || 'conservation:' + ev.carrier);
            }
            // Only count as linked if we actually added new data.
            const changed = JSON.stringify(merged) !== JSON.stringify(cur);
            if (changed) {
              next[key] = merged;
              linked++;
            }
          });
          return next;
        });
        showToast(
          `Imported ${events.length} event${events.length === 1 ? '' : 's'} — ${added} new, ${updated} refreshed${linked ? `, ${linked} CRM client${linked === 1 ? '' : 's'} updated` : ''}.`,
          'success'
        );
        return Object.values(byId);
      });

      // Conservation auto-enroll. When a brand-new lapse-style event
      // lands, the operator wants the engine to text the client
      // immediately — that's the whole point of replacing the VA's
      // save-the-policy task. We fire-and-forget so a Supabase blip
      // doesn't break the import flow.
      if (newlyAdded.length > 0) {
        enrollConservationEvents(newlyAdded).catch(err => {
          // eslint-disable-next-line no-console
          console.error('Conservation auto-enroll failed:', err);
        });
      }
    } catch (err) {
      showToast(`Parse failed: ${err.message || err}`, 'error');
    }
  };

  // ── Conservation auto-enroll ───────────────────────────────────────
  //
  // For each brand-new conservation event whose type means a save is
  // warranted (lapse_warning, nsf, grace_period), find the matching
  // lead and enroll them in every active conservation_event workflow.
  // Idempotent by (workflow_id, policy_id), where policy_id is a
  // synthetic key derived from the event id — re-importing the same
  // conservation report doesn't double-enroll.
  const enrollConservationEvents = async (events) => {
    if (!currentUser?.id) return;
    const SAVE_TYPES = new Set(['lapse_warning', 'nsf', 'grace_period']);
    const eligible = events.filter(ev => SAVE_TYPES.has(ev.eventType));
    if (eligible.length === 0) return;

    const { data: wfs } = await supabase
      .from('workflows')
      .select('id, name')
      .eq('active', true)
      .eq('trigger', 'conservation_event');
    if (!wfs || wfs.length === 0) return;

    const wfIds = wfs.map(w => w.id);
    const { data: stepsData } = await supabase
      .from('workflow_steps')
      .select('id, workflow_id, position')
      .in('workflow_id', wfIds)
      .eq('position', 1);
    const step1ByWf = {};
    (stepsData || []).forEach(s => { step1ByWf[s.workflow_id] = s; });

    // Build a normalized-name → lead map. First match wins; consistent
    // with the milestone scan's approach.
    const norm = (s) => {
      const cleaned = String(s || '').toUpperCase().replace(/[^A-Z\s,]/g, '').replace(/\s+/g, ' ').trim();
      if (!cleaned) return '';
      return cleaned.split(/[\s,]+/).filter(Boolean).sort().join(' ');
    };
    const leadByName = new Map();
    (leads || []).forEach(l => {
      const k = norm(l.name);
      if (k && !leadByName.has(k)) leadByName.set(k, l);
    });

    let enrolledCount = 0;
    let skippedNoLead = 0;
    let skippedNoConsent = 0;
    for (const ev of eligible) {
      const lead = leadByName.get(norm(ev.clientName || ''));
      if (!lead) { skippedNoLead++; continue; }
      if (lead.consent !== true || lead.opted_out === true || !lead.phone_e164) { skippedNoConsent++; continue; }

      const policyKey = `conservation:${ev.id}`;
      for (const wf of wfs) {
        const step1 = step1ByWf[wf.id];
        if (!step1) continue;

        const { data: existing } = await supabase
          .from('enrollments')
          .select('id')
          .eq('workflow_id', wf.id)
          .eq('policy_id', policyKey)
          .limit(1)
          .maybeSingle();
        if (existing) continue;

        const { data: enr, error: e1 } = await supabase.from('enrollments').insert({
          lead_id: String(lead.id),
          policy_id: policyKey,
          workflow_id: wf.id,
          current_step: 1,
          status: 'active',
          owner_id: currentUser.id,
        }).select('id').single();
        if (e1) continue;

        await supabase.from('scheduled_actions').insert({
          enrollment_id: enr.id,
          step_id: step1.id,
          run_at: new Date().toISOString(),
        });
        enrolledCount++;
      }
    }

    if (enrolledCount > 0) {
      showToast(
        `Conservation auto-text: enrolled ${enrolledCount} client${enrolledCount === 1 ? '' : 's'}${skippedNoLead ? ` · ${skippedNoLead} unmatched` : ''}${skippedNoConsent ? ` · ${skippedNoConsent} missing consent/phone` : ''}.`,
        'success'
      );
    }
  };

  // ── Calendar follow-up ─────────────────────────────────────────────
  // Schedule a save-the-policy follow-up event. Date defaults to 3
  // days before the cure deadline (payByDate), or 5 days before
  // termination, or +3 days from today if neither — whatever gives
  // the operator a usable lead time.
  const scheduleFollowUp = (ev) => {
    const today = new Date();
    let target = null;
    let basis = '';
    if (ev.payByDate) {
      const [m, d, y] = ev.payByDate.split('/').map(Number);
      target = new Date(y, m - 1, d - 3);
      basis = `cure deadline ${ev.payByDate}`;
    } else if (ev.terminationDate) {
      const [m, d, y] = ev.terminationDate.split('/').map(Number);
      target = new Date(y, m - 1, d - 5);
      basis = `termination ${ev.terminationDate}`;
    } else {
      target = new Date(today.getTime() + 3 * 24 * 3600 * 1000);
      basis = 'no carrier deadline — defaulted to +3 days';
    }
    // Don't schedule in the past — bump to tomorrow if the math
    // produced a date that's already gone.
    if (target.getTime() < today.getTime()) {
      target = new Date(today.getTime() + 24 * 3600 * 1000);
      basis = `(deadline already past — scheduled for tomorrow)`;
    }
    const yyyy = target.getFullYear();
    const mm = String(target.getMonth() + 1).padStart(2, '0');
    const dd = String(target.getDate()).padStart(2, '0');
    const dateIso = `${yyyy}-${mm}-${dd}`;

    const id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `appt-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const nowIso = new Date().toISOString();
    const userTag = currentUser ? { username: currentUser.prodNum, role: currentUser.role, name: currentUser.name } : null;
    const record = {
      id,
      type: 'Save the policy',
      status: 'scheduled',
      appointmentDate: dateIso,
      appointmentTime: '09:00',
      prospectName: ev.clientName || '(unknown)',
      prospectPhone: ev.phone || '',
      agentProdNum: ev.agentProdNum || (currentUser?.prodNum || ''),
      notes: [
        `Conservation event: ${ev.eventTypeLabel}`,
        `Policy: ${ev.policyNum || '?'} · ${ev.productName || 'policy'}`,
        ev.minPayment ? `Cure amount: ${fmt(ev.minPayment)}` : null,
        ev.terminationDate ? `Terminates ${ev.terminationDate}` : null,
        `Scheduled ${basis}`,
      ].filter(Boolean).join('\n'),
      createdAt: nowIso,
      updatedAt: nowIso,
      createdByUser: userTag,
      updatedByUser: userTag,
      // Cross-link so future iterations can show the appointment on
      // the conservation row + jump back here from the calendar.
      conservationEventId: ev.id,
    };
    setCalendar(prev => [...prev, record]);
    // Stamp the conservation row so we don't add duplicate events
    // every click + can show "Follow-up scheduled" in the UI.
    setConservation(prev => prev.map(e => e.id === ev.id ? { ...e, followUpCalendarId: id, followUpDate: dateIso } : e));
    showToast(`Follow-up scheduled for ${target.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} (${basis})`, 'success');
  };

  // ── SMS template ───────────────────────────────────────────────────
  // Fills the conservation SMS template with event-specific values.
  // Returns the ready-to-send text the operator can copy to clipboard
  // and paste into their dialer / texting tool of choice.
  const buildConservationSms = (ev) => {
    const firstName = (ev.clientName || '').split(' ')[0] || 'there';
    const agentName = settings?.ownerName && settings.ownerName !== 'You' ? settings.ownerName : 'your agent';
    const agentPhone = settings?.ownerPhone || '';

    // Try the operator's tenant template first; fall back to a
    // sensible default. Match by name OR by id.
    const tpl = (leadTemplates || []).find(t =>
      /conservation|lapse|save the policy/i.test(t.name || '') ||
      /tpl_conservation/.test(t.id || '')
    );
    let body = tpl?.body;
    if (!body) {
      if (ev.eventType === 'lapse_warning') {
        body = `Hey [Name], this is [Agent Name] — your [Carrier] policy needs a payment of [Cure Amount] by [Pay By Date] to keep your coverage active. Want me to walk you through it? Call me back at [Agent Phone].`;
      } else if (ev.eventType === 'returned_mail') {
        body = `Hey [Name], this is [Agent Name] — your [Carrier] just bounced back some mail. Can you confirm your current address? It's important for keeping your policy in good standing. Reply or call [Agent Phone].`;
      } else {
        body = `Hey [Name], this is [Agent Name] from [Carrier] — quick note about your policy. Can you give me a call when you have a sec? [Agent Phone]`;
      }
    }
    return body
      .replace(/\[Name\]/g,         firstName)
      .replace(/\[name\]/g,         firstName)
      .replace(/\[Agent Name\]/g,   agentName)
      .replace(/\[Agent Phone\]/g,  agentPhone || '(my number)')
      .replace(/\[Carrier\]/g,      ev.carrier || 'the carrier')
      .replace(/\[Policy\]/g,       ev.policyNum || '')
      .replace(/\[Cure Amount\]/g,  ev.minPayment != null ? fmt(ev.minPayment) : '')
      .replace(/\[Pay By Date\]/g,  ev.payByDate || '');
  };

  const copyConservationSms = async (ev) => {
    const text = buildConservationSms(ev);
    try {
      await navigator.clipboard.writeText(text);
      showToast('Conservation SMS copied to clipboard', 'success');
    } catch {
      // Some browsers / iframes block clipboard.writeText silently.
      // Fall back to a textarea trick.
      const ta = document.createElement('textarea');
      ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); showToast('Conservation SMS copied', 'success'); }
      catch { showToast('Couldn\'t copy — text was: ' + text.slice(0, 80) + '…', 'error'); }
      finally { document.body.removeChild(ta); }
    }
  };

  const onPasteSubmit = () => {
    if (!pasteText.trim()) return;
    ingestText(pasteText, 'paste');
    setPasteText('');
    setShowPaste(false);
  };

  const onFilePicked = async (file) => {
    if (!file) return;
    if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
      try {
        // Use the codebase's column-aware extractor (sorts text items
        // by Y/X so columns don't interleave into fragments). Much
        // better signal for the conservation-report parser than the
        // raw .items.join(' ').
        const text = await extractPdfText(file);
        ingestText(text, file.name);
      } catch (err) {
        showToast(`PDF parse failed: ${err.message || err}. Paste the report text instead.`, 'error');
      }
    } else {
      const text = await file.text();
      ingestText(text, file.name);
    }
  };

  // ── Decorate every event with derived risk + transaction match ────
  const decorated = React.useMemo(() => {
    return (conservation || []).map(ev => {
      const priority = conservationPriority(ev, today);
      const dayTerm  = daysUntilTermination(ev, today);
      const dayCure  = daysUntilCureWindowCloses(ev, today);
      const inWindow = isInChargebackWindow(ev, today);
      const matchTx = ev.policyNum ? txByPolicy[ev.policyNum] : null;
      const matchProducer = matchTx && producers[matchTx.prodNum];
      return {
        ...ev,
        priority,
        daysUntilTermination: dayTerm,
        daysUntilCure: dayCure,
        inChargebackWindow: inWindow,
        // $ already advanced on this policy (best-effort match).
        advancedPremium: matchTx ? Number(matchTx.commPremium) || 0 : null,
        writingAgent:   matchProducer?.name || (matchTx?.prodNum ? `Producer ${matchTx.prodNum}` : null),
      };
    });
  }, [conservation, txByPolicy, producers, today]);

  // ── Filter + sort ─────────────────────────────────────────────────
  const lc = search.trim().toLowerCase();
  const filtered = decorated.filter(ev => {
    if (statusFilter !== 'all'   && ev.status !== statusFilter) return false;
    if (typeFilter   !== 'all'   && ev.eventType !== typeFilter) return false;
    if (priorityFilter !== 'all' && ev.priority !== priorityFilter) return false;
    if (lc) {
      const blob = `${ev.clientName} ${ev.policyNum} ${ev.clientNum} ${ev.productName} ${ev.eventDescription} ${ev.note}`.toLowerCase();
      if (!blob.includes(lc)) return false;
    }
    return true;
  });

  const sorted = React.useMemo(() => {
    const priorityRank = { critical: 0, high: 1, medium: 2, low: 3 };
    const arr = [...filtered];
    arr.sort((a, b) => {
      if (sortKey === 'priority') {
        const pa = priorityRank[a.priority] ?? 4;
        const pb = priorityRank[b.priority] ?? 4;
        if (pa !== pb) return pa - pb;
        // Tie-break: closer termination first
        const ta = a.daysUntilTermination ?? 9999;
        const tb = b.daysUntilTermination ?? 9999;
        return ta - tb;
      }
      if (sortKey === 'termination') {
        return (a.daysUntilTermination ?? 9999) - (b.daysUntilTermination ?? 9999);
      }
      if (sortKey === 'premium_at_risk') {
        return (b.advancedPremium || 0) - (a.advancedPremium || 0);
      }
      if (sortKey === 'event_date') {
        return String(b.eventDate || '').localeCompare(String(a.eventDate || ''));
      }
      return 0;
    });
    return arr;
  }, [filtered, sortKey]);

  // ── Aggregate stats ───────────────────────────────────────────────
  const total = decorated.length;
  const openCount      = decorated.filter(e => e.status === 'open').length;
  const criticalCount  = decorated.filter(e => e.status === 'open' && e.priority === 'critical').length;
  const returnedMail   = decorated.filter(e => e.status === 'open' && e.eventType === 'returned_mail').length;
  const dollarsAtRisk  = decorated
    .filter(e => e.status === 'open' && e.inChargebackWindow && e.advancedPremium)
    .reduce((s, e) => s + (e.advancedPremium || 0), 0);

  // ── Mutations ─────────────────────────────────────────────────────
  const setStatus = (id, status) => {
    setConservation(prev => prev.map(e => e.id === id ? { ...e, status, statusUpdatedAt: new Date().toISOString() } : e));
  };
  const setNote = (id, note) => {
    setConservation(prev => prev.map(e => e.id === id ? { ...e, note } : e));
  };
  // Inline name repair. Lets the operator type a client name without
  // re-uploading the report. Also back-fills the clientProfiles map
  // so the new name appears in Book of Business — same flow as the
  // auto-CRM-link on ingest, just triggered by a manual edit.
  const setName = (id, name) => {
    setConservation(prev => prev.map(e => e.id === id ? { ...e, clientName: name } : e));
    if (!name) return;
    setClientProfiles(prevP => {
      const ev = (conservation || []).find(e => e.id === id);
      if (!ev) return prevP;
      const key = String(name).trim().toUpperCase().replace(/\s+/g, ' ');
      const cur = prevP[key] || {};
      // Only fill blanks — preserve any prior CRM data the operator
      // has on that client.
      const merged = {
        ...cur,
        phone:    cur.phone    || ev.phone || '',
        address1: cur.address1 || ev.addressLine1 || '',
        city:     cur.city     || ev.city  || '',
        state:    cur.state    || ev.state || '',
        zip:      cur.zip      || ev.zip   || '',
      };
      return { ...prevP, [key]: merged };
    });
  };
  const clearAll = async () => {
    if (!(await hqConfirm({
      title: `Delete all ${total} conservation events?`,
      body: 'This can\'t be undone (re-upload to rebuild).',
      confirmLabel: 'Delete events',
      danger: true,
    }))) return;
    setConservation([]);
  };
  const exportCsv = () => {
    const rows = [
      ['Priority','Status','Event','Client','Policy','Issue date','Termination date','Days to term','Min payment','Modal premium','Advanced premium','Writing agent','Phone','Note'],
      ...sorted.map(e => [
        e.priority, e.status, e.eventTypeLabel,
        e.clientName, e.policyNum, e.issueDate, e.terminationDate,
        e.daysUntilTermination ?? '', e.minPayment ?? '',
        e.modalPremium ?? '', e.advancedPremium ?? '',
        e.writingAgent || '', e.phone || '', e.note || '',
      ]),
    ];
    const csv = rows.map(r => r.map(v => {
      const s = String(v ?? '');
      return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `retention-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Header + import controls */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white rounded-2xl p-5">
        <div className="text-xs text-slate-300 uppercase tracking-wider font-semibold">Retention</div>
        <div className="text-2xl font-bold mt-1">Policy Conservation Opportunities</div>
        <p className="text-sm text-slate-300 mt-1">
          Upload carrier conservation reports (MOO today; more carriers as we see formats). Each event becomes a worklist row, sorted by chargeback risk + days until termination so you can save the policy before the commission reverses.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.txt,application/pdf,text/plain"
          onChange={e => { onFilePicked(e.target.files?.[0]); e.target.value = ''; }}
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-sm px-4 py-2 rounded-lg"
        >
          Upload PDF
        </button>
        <button
          onClick={() => setShowPaste(s => !s)}
          className="bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold text-sm px-4 py-2 rounded-lg"
        >
          {showPaste ? 'Cancel paste' : 'Paste report text'}
        </button>
        <div className="flex-1"/>
        {sorted.length > 0 && (
          <>
            <button onClick={exportCsv} className="bg-white border border-slate-200 hover:bg-slate-50 text-xs font-semibold px-3 py-2 rounded-lg">
              Export CSV
            </button>
            <button onClick={clearAll} className="text-xs text-rose-600 hover:text-rose-700 hover:underline">
              Clear all
            </button>
          </>
        )}
      </div>

      {showPaste && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs text-slate-600 mb-2">Paste the full conservation report text below.</p>
          <textarea
            value={pasteText}
            onChange={e => setPasteText(e.target.value)}
            rows={10}
            placeholder="CC: 001, BROKER: …"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs font-mono"
            autoFocus
          />
          <div className="mt-2 flex justify-end gap-2">
            <button onClick={() => { setPasteText(''); setShowPaste(false); }} className="bg-white border border-slate-200 hover:bg-slate-50 text-sm font-semibold px-3 py-1.5 rounded-lg">
              Cancel
            </button>
            <button onClick={onPasteSubmit} disabled={!pasteText.trim()} className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-bold text-sm px-4 py-1.5 rounded-lg">
              Import
            </button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile label="Open events"      value={openCount}                          tone="slate"/>
        <StatTile label="In chargeback window" value={criticalCount} sub="(critical)" tone="rose"/>
        <StatTile label="Returned mail"    value={returnedMail}     sub="(verify info)" tone="amber"/>
        <StatTile label="$ advanced at risk" value={fmt(dollarsAtRisk)} sub="open + in window" tone="emerald"/>
      </div>

      {total === 0 ? (
        <RetentionEmptyState/>
      ) : (
        <>
          {/* Controls */}
          <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-2">
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Filter by name, policy, or notes…"
              className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm flex-1 min-w-[200px]"
            />
            <select value={statusFilter}   onChange={e => setStatusFilter(e.target.value)}   className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white">
              <option value="open">Open</option>
              <option value="contacted">Contacted</option>
              <option value="cured">Cured</option>
              <option value="dismissed">Dismissed</option>
              <option value="all">All statuses</option>
            </select>
            <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white">
              <option value="all">All priorities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <select value={typeFilter}     onChange={e => setTypeFilter(e.target.value)}     className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white">
              <option value="all">All types</option>
              <option value="lapse_warning">Lapse warnings</option>
              <option value="returned_mail">Returned mail</option>
              <option value="grace_period">Grace period</option>
              <option value="nsf">Payment failed</option>
              <option value="reinstatement_opportunity">Reinstatement</option>
              <option value="other">Other</option>
            </select>
            <select value={sortKey}        onChange={e => setSortKey(e.target.value)}        className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white">
              <option value="priority">Sort: Priority</option>
              <option value="termination">Sort: Days to termination</option>
              <option value="premium_at_risk">Sort: $ at risk</option>
              <option value="event_date">Sort: Event date</option>
            </select>
          </div>

          {/* Worklist */}
          <div className="space-y-2">
            {sorted.map(ev => (
              <RetentionRow
                key={ev.id}
                ev={ev}
                expanded={!!expanded[ev.id]}
                onToggle={() => setExpanded(s => ({ ...s, [ev.id]: !s[ev.id] }))}
                onStatus={(s) => setStatus(ev.id, s)}
                onNote={(n) => setNote(ev.id, n)}
                onOpenClient={() => {
                  if (!ev.clientName) {
                    showToast('No client name parsed — paste the report text or expand the row for raw data.', 'error');
                    return;
                  }
                  // openClientProfile accepts a client-key string in
                  // this codebase (clientKeyOf normalizes uppercase +
                  // trim). The Book of Business tab will switch focus.
                  const key = String(ev.clientName).trim().toUpperCase().replace(/\s+/g, ' ');
                  openClientProfile(key);
                }}
                onSchedule={() => scheduleFollowUp(ev)}
                onCopySms={() => copyConservationSms(ev)}
                onSetName={(name) => setName(ev.id, name)}
              />
            ))}
            {sorted.length === 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-sm text-slate-500">
                No events match the current filters.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function StatTile({ label, value, sub, tone = 'slate' }) {
  const tones = {
    slate:   { bg: 'bg-white border border-slate-200',     val: 'text-slate-900' },
    rose:    { bg: 'bg-rose-50 border border-rose-200',    val: 'text-rose-900'  },
    amber:   { bg: 'bg-amber-50 border border-amber-200',  val: 'text-amber-900' },
    emerald: { bg: 'bg-emerald-50 border border-emerald-200', val: 'text-emerald-900' },
  };
  const t = tones[tone] || tones.slate;
  return (
    <div className={`${t.bg} rounded-xl p-4`}>
      <div className="text-xs text-slate-600 font-semibold uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${t.val}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// SaveStat — RetentionView's top KPI tile. Shares StatTile's API but
// adds a `red` tone used by the critical/lapsed indicators.
//
// History: this component was referenced from the Retention view but
// never defined — every navigation to /retention crashed the root
// error boundary with "SaveStat is not defined". Adding the missing
// definition unblocks the tab.
function SaveStat({ label, value, sub, tone = 'slate' }) {
  const tones = {
    slate:   { bg: 'bg-white border border-slate-200',           val: 'text-slate-900' },
    rose:    { bg: 'bg-rose-50 border border-rose-200',          val: 'text-rose-900'  },
    red:     { bg: 'bg-red-50 border-2 border-red-300',          val: 'text-red-900'   },
    amber:   { bg: 'bg-amber-50 border border-amber-200',        val: 'text-amber-900' },
    emerald: { bg: 'bg-emerald-50 border border-emerald-200',    val: 'text-emerald-900' },
  };
  const t = tones[tone] || tones.slate;
  return (
    <div className={`${t.bg} rounded-xl p-4`}>
      <div className="text-xs text-slate-600 font-semibold uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${t.val}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function RetentionEmptyState() {
  return (
    <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center">
      <div className="text-base font-semibold text-slate-900">No conservation events yet</div>
      <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
        Drop a MOO Policy Conservation Opportunities PDF above (or paste the report text) and we'll triage every event by chargeback risk and days until termination.
      </p>
    </div>
  );
}

function RetentionRow({ ev, expanded, onToggle, onStatus, onNote, onOpenClient, onSchedule, onCopySms, onSetName }) {
  // Inline name-edit mode. Toggled when the operator clicks the
  // "(name not parsed — type it)" placeholder so they can repair the
  // row without re-uploading the whole report.
  const [nameEditing, setNameEditing] = React.useState(false);
  const [nameDraft, setNameDraft]     = React.useState(ev.clientName || '');
  React.useEffect(() => { setNameDraft(ev.clientName || ''); }, [ev.clientName]);
  const commitName = () => {
    const v = nameDraft.trim();
    if (v && v !== ev.clientName) onSetName?.(v);
    setNameEditing(false);
  };

  const priorityColors = {
    critical: 'border-l-rose-500   bg-rose-50/40',
    high:     'border-l-amber-500  bg-amber-50/40',
    medium:   'border-l-blue-500   bg-blue-50/30',
    low:      'border-l-slate-300  bg-white',
  };
  const priorityBadge = {
    critical: 'bg-rose-100 text-rose-800',
    high:     'bg-amber-100 text-amber-800',
    medium:   'bg-blue-100 text-blue-800',
    low:      'bg-slate-100 text-slate-700',
  };
  const fullAddress = [ev.addressLine1, [ev.city, ev.state, ev.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return (
    <div className={`bg-white rounded-xl border border-slate-200 border-l-4 ${priorityColors[ev.priority] || priorityColors.low}`}>
      <div className="p-3">
        {/* Header line: priority + clickable name + policy + window flag */}
        <div className="flex items-start gap-3 flex-wrap">
          <button onClick={onToggle} className="flex-shrink-0 text-slate-400 hover:text-slate-700 mt-1" aria-label={expanded ? 'Collapse' : 'Expand'}>
            {expanded ? '▾' : '▸'}
          </button>
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${priorityBadge[ev.priority] || priorityBadge.low}`}>
                {ev.priority}
              </span>
              {nameEditing ? (
                <input
                  autoFocus
                  value={nameDraft}
                  onChange={e => setNameDraft(e.target.value)}
                  onBlur={commitName}
                  onKeyDown={e => {
                    if (e.key === 'Enter') commitName();
                    if (e.key === 'Escape') { setNameDraft(ev.clientName || ''); setNameEditing(false); }
                  }}
                  placeholder="Type the client's name"
                  className="font-bold text-slate-900 border border-emerald-300 rounded px-2 py-0.5 text-sm focus:outline-none focus:border-emerald-500"
                  style={{ minWidth: 180 }}
                />
              ) : ev.clientName ? (
                <button
                  onClick={onOpenClient}
                  onDoubleClick={() => setNameEditing(true)}
                  className="font-bold text-slate-900 hover:text-emerald-700 hover:underline text-left"
                  title="Click to open client profile · double-click to edit"
                >
                  {ev.clientName}
                </button>
              ) : (
                <button
                  onClick={() => setNameEditing(true)}
                  className="font-semibold text-amber-700 hover:text-amber-800 hover:underline italic"
                  title="Click to type the client's name"
                >
                  (name not parsed — type it)
                </button>
              )}
              <span className="text-xs text-slate-500 font-mono">{ev.policyNum}</span>
              {ev.inChargebackWindow && (
                <span className="text-[10px] uppercase tracking-wider font-bold bg-rose-100 text-rose-800 px-1.5 py-0.5 rounded">
                  In chargeback window
                </span>
              )}
              <span className="text-[10px] uppercase tracking-wider font-semibold bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
                {ev.eventTypeLabel}
              </span>
              {ev.followUpDate && (
                <span className="text-[10px] uppercase tracking-wider font-bold bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded" title="Follow-up scheduled">
                  {ev.followUpDate}
                </span>
              )}
            </div>

            {/* Always-visible contact + policy details — no expand needed */}
            <div className="mt-2 text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
              {ev.phone && (
                <span>
                  <span className="text-slate-400">Phone:</span>{' '}
                  <button onClick={() => requestPhoneCall({ phone: ev.phone, name: ev.clientName })} className="font-mono text-slate-900 hover:underline bg-transparent border-0 cursor-pointer p-0 tabular-nums">{fmtPhoneUS(ev.phone)}</button>
                </span>
              )}
              {fullAddress && (
                <span>
                  <span className="text-slate-400">Address:</span>{' '}
                  <span className="text-slate-800">{fullAddress}</span>
                </span>
              )}
              {ev.writingAgent && (
                <span>
                  <span className="text-slate-400">Writing agent:</span>{' '}
                  <span className="text-slate-800">{ev.writingAgent}</span>
                </span>
              )}
            </div>

            <div className="text-xs text-slate-500 mt-1">
              {ev.productName || 'Policy'}{ev.issueDate ? ` · issued ${ev.issueDate}` : ''}{ev.modalPremium ? ` · ${fmt(ev.modalPremium)}/mo` : ''}{ev.billingMethod ? ` · ${ev.billingMethod}` : ''}{ev.paidToDate ? ` · paid through ${ev.paidToDate}` : ''}
            </div>
          </div>

          {/* Right column: deadlines + $ */}
          <div className="text-xs text-right space-y-0.5 min-w-[160px]">
            {ev.terminationDate && (
              <div>
                <span className="text-slate-400">Terminates</span>{' '}
                <span className={`font-semibold ${(ev.daysUntilTermination ?? 999) < 14 ? 'text-rose-700' : 'text-slate-700'}`}>
                  {ev.terminationDate}
                </span>
                {ev.daysUntilTermination != null && (
                  <span className={`ml-1 ${ev.daysUntilTermination < 0 ? 'text-slate-400' : ev.daysUntilTermination < 14 ? 'text-rose-700 font-bold' : 'text-slate-500'}`}>
                    ({ev.daysUntilTermination < 0 ? `${Math.abs(ev.daysUntilTermination)}d past` : `${ev.daysUntilTermination}d`})
                  </span>
                )}
              </div>
            )}
            {ev.payByDate && (
              <div>
                <span className="text-slate-400">Pay by</span>{' '}
                <span className="font-semibold text-slate-700">{ev.payByDate}</span>
              </div>
            )}
            {ev.minPayment != null && (
              <div>
                <span className="text-slate-400">Cure amount</span>{' '}
                <span className="font-bold text-slate-900">{fmt(ev.minPayment)}</span>
              </div>
            )}
            {ev.advancedPremium != null && ev.advancedPremium > 0 && (
              <div>
                <span className="text-slate-400">Advanced</span>{' '}
                <span className="font-semibold text-emerald-700">{fmt(ev.advancedPremium)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Action row — separate so wrapping behaves cleanly */}
        <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-2 flex-wrap">
          <button
            onClick={onOpenClient}
            className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-800 hover:underline"
            title="Open client profile"
          >
            Profile
          </button>
          {ev.phone && (
            <button
              onClick={() => requestPhoneCall({ phone: ev.phone, name: ev.clientName })}
              className="text-[11px] font-semibold text-slate-700 hover:text-slate-900 hover:underline bg-transparent border-0 cursor-pointer p-0"
              title={`Call ${ev.phone}`}
            >
              Call
            </button>
          )}
          <button
            onClick={onCopySms}
            className="text-[11px] font-semibold text-slate-700 hover:text-slate-900 hover:underline"
            title="Copy a pre-filled save-the-policy SMS to clipboard"
          >
            Copy SMS
          </button>
          <button
            onClick={onSchedule}
            className="text-[11px] font-semibold text-slate-700 hover:text-slate-900 hover:underline"
            title="Schedule a follow-up in your Calendar"
          >
            Schedule follow-up
          </button>
          <span className="flex-1"/>
          <StatusButton current={ev.status} value="contacted" label="Contacted" onClick={() => onStatus('contacted')}/>
          <StatusButton current={ev.status} value="cured"     label="Cured"     onClick={() => onStatus('cured')}/>
          <StatusButton current={ev.status} value="dismissed" label="Dismiss"   onClick={() => onStatus('dismissed')}/>
          {ev.status !== 'open' && (
            <button onClick={() => onStatus('open')} className="text-[11px] text-slate-500 hover:text-slate-800 hover:underline px-1">
              ↺ reopen
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 p-3 bg-slate-50/50 text-xs text-slate-700 space-y-2">
          {ev.eventDescription && (
            <div className="bg-white border border-slate-200 rounded-lg p-2 leading-relaxed">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Carrier event description</div>
              {ev.eventDescription}
            </div>
          )}
          {ev.clientNum && (
            <div>
              <span className="text-slate-500">Client #:</span>{' '}
              <span className="font-mono text-slate-900">{ev.clientNum}</span>
            </div>
          )}
          {ev.policyCode && (
            <div>
              <span className="text-slate-500">Plan code:</span>{' '}
              <span className="font-mono text-slate-900">{ev.policyCode}</span>
            </div>
          )}
          <label className="block mt-2">
            <span className="text-slate-500">Note</span>
            <textarea
              value={ev.note || ''}
              onChange={e => onNote(e.target.value)}
              rows={2}
              placeholder="Left voicemail, sent text, payment promised by Friday…"
              className="mt-1 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs"
            />
          </label>
        </div>
      )}
    </div>
  );
}

function StatusButton({ current, value, label, onClick }) {
  const active = current === value;
  const baseTones = {
    contacted: active ? 'bg-blue-600 text-white'    : 'bg-blue-50 text-blue-700 hover:bg-blue-100',
    cured:     active ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
    dismissed: active ? 'bg-slate-600 text-white'   : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
  };
  return (
    <button onClick={onClick} className={`text-[11px] font-semibold px-2.5 py-1 rounded ${baseTones[value]}`}>
      {label}
    </button>
  );
}

function ChargebacksView({ transactions, allTransactions, producers, settings, periodMode, openClientProfile = () => {} }) {
  const [sortBy, setSortBy] = useState('days'); // 'days' | 'loss' | 'date'
  const [teamFilter, setTeamFilter] = useState('all'); // 'all' | 'mine' | 'cam'
  const [agentFilter, setAgentFilter] = useState('all');

  // Find every chargeback row in the CURRENT filtered period
  const cbRows = transactions.filter(tx =>
    CHARGEBACK_TYPES.has(tx.activityType) || (tx.netToAccount || 0) < 0
  );

  // Group by policy (mga|policy|prodNum) so LAPSE + RECPT ADV CO on same policy collapse into one row
  const policyMap = {};
  cbRows.forEach(tx => {
    const key = `${tx.mga||''}|${tx.policy||''}|${tx.prodNum||''}`;
    if (!policyMap[key]) {
      policyMap[key] = {
        key,
        policy: tx.policy,
        insured: tx.insured,
        prodNum: tx.prodNum,
        mga: tx.mga,
        company: tx.company,
        state: tx.state,
        issueDate: tx.issueDate,
        cbDate: tx.activityDate,
        earliestCbDate: tx.activityDate,
        totalLoss: 0,
        activities: new Set(),
        rows: [],
      };
    }
    const p = policyMap[key];
    p.totalLoss += tx.netToAccount;
    p.activities.add(tx.activityType);
    p.rows.push(tx);
    // Earliest chargeback date
    const existing = parseDate(p.earliestCbDate);
    const current = parseDate(tx.activityDate);
    if (current && (!existing || current < existing)) p.earliestCbDate = tx.activityDate;
    // Keep best issueDate (prefer non-empty)
    if (!p.issueDate && tx.issueDate) p.issueDate = tx.issueDate;
  });

  // Compute days-alive for each policy
  const policies = Object.values(policyMap).map(p => {
    const issue = parseDate(p.issueDate);
    const cb = parseDate(p.earliestCbDate);
    const days = (issue && cb) ? Math.floor((cb - issue) / (1000 * 60 * 60 * 24)) : null;
    const team = getProducerTeam(p.prodNum, producers);
    return { ...p, days, team, agentName: getProducerName(p.prodNum, producers), activitiesList: Array.from(p.activities) };
  });

  // Filters
  const filtered = policies.filter(p => {
    if (teamFilter !== 'all' && p.team !== teamFilter) return false;
    if (agentFilter !== 'all' && p.prodNum !== agentFilter) return false;
    return true;
  });

  // Sort
  const sorted = filtered.slice().sort((a, b) => {
    if (sortBy === 'days') {
      // Shortest first; unknowns at the end
      const av = a.days == null ? 99999 : a.days;
      const bv = b.days == null ? 99999 : b.days;
      return av - bv;
    }
    if (sortBy === 'loss') return a.totalLoss - b.totalLoss; // biggest loss first (most negative)
    return (parseDate(b.earliestCbDate) || 0) - (parseDate(a.earliestCbDate) || 0);
  });

  // Summary metrics
  const totalLoss = policies.reduce((s, p) => s + p.totalLoss, 0);
  const withLifespan = policies.filter(p => p.days != null);
  const avgDays = withLifespan.length > 0 ? withLifespan.reduce((s, p) => s + p.days, 0) / withLifespan.length : 0;
  const under30 = policies.filter(p => p.days != null && p.days < 30).length;
  const under7 = policies.filter(p => p.days != null && p.days < 7).length;

  // Per-agent summary (for the filter dropdown and a mini agent table)
  const agentSummary = {};
  policies.forEach(p => {
    if (!agentSummary[p.prodNum]) {
      agentSummary[p.prodNum] = {
        prodNum: p.prodNum,
        name: p.agentName,
        team: p.team,
        count: 0,
        totalLoss: 0,
        avgDays: 0,
        daysList: [],
      };
    }
    agentSummary[p.prodNum].count++;
    agentSummary[p.prodNum].totalLoss += p.totalLoss;
    if (p.days != null) agentSummary[p.prodNum].daysList.push(p.days);
  });
  Object.values(agentSummary).forEach(a => {
    a.avgDays = a.daysList.length > 0 ? a.daysList.reduce((s, d) => s + d, 0) / a.daysList.length : null;
  });
  const agentsSorted = Object.values(agentSummary).sort((a, b) => a.totalLoss - b.totalLoss);

  const formatIssuetoCB = (p) => {
    if (!p.issueDate || !p.earliestCbDate) return '—';
    return `${p.issueDate} → ${p.earliestCbDate}`;
  };

  const lifespanColor = (days) => {
    if (days == null) return 'text-slate-400';
    if (days < 7) return 'text-red-700 font-bold';
    if (days < 30) return 'text-red-600 font-semibold';
    if (days < 90) return 'text-amber-600 font-semibold';
    return 'text-slate-600';
  };

  const lifespanLabel = (days) => {
    if (days == null) return 'unknown';
    if (days < 0) return `${days}d (future issue!)`;
    if (days === 0) return 'same day';
    if (days < 7) return `${days}d (CRITICAL)`;
    if (days < 30) return `${days}d (early)`;
    if (days < 90) return `${days}d (mid)`;
    return `${days}d`;
  };

  // Chargeback trend across ALL periods (not just current filter), so the chart shows history
  const trendData = (() => {
    const buckets = {};
    (allTransactions || []).forEach(tx => {
      const isCB = CHARGEBACK_TYPES.has(tx.activityType) || (tx.netToAccount || 0) < 0;
      if (!isCB) return;
      const k = periodMode === 'week' ? (tx.weekKey || 'unknown') : (tx.monthKey || 'unknown');
      if (!buckets[k]) buckets[k] = { key: k, loss: 0, count: 0 };
      buckets[k].loss += Math.abs(tx.netToAccount || 0);
      buckets[k].count++;
    });
    return Object.values(buckets)
      .filter(b => b.key !== 'unknown')
      .sort((a,b) => a.key.localeCompare(b.key))
      .map(b => ({ ...b, label: periodMode === 'week' ? weekLabel(b.key) : monthLabel(b.key) }));
  })();

  // Top 10 agents by total loss (current period) — for the bar chart
  const agentChartData = agentsSorted.slice(0, 10).map(a => ({
    name: (a.name || `#${a.prodNum}`).slice(0, 18),
    loss: Math.abs(a.totalLoss),
    count: a.count,
  }));

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
          <div className="text-xs text-red-700 font-semibold uppercase tracking-wide">Total chargebacks</div>
          <div className="text-2xl font-bold mt-1 text-red-900">{policies.length}</div>
          <div className="text-xs text-red-700 mt-1">unique policies</div>
        </div>
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
          <div className="text-xs text-red-700 font-semibold uppercase tracking-wide">Total commission loss</div>
          <div className="text-2xl font-bold mt-1 text-red-900">{fmt(totalLoss)}</div>
          <div className="text-xs text-red-700 mt-1">negative net</div>
        </div>
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
          <div className="text-xs text-amber-700 font-semibold uppercase tracking-wide">Avg policy lifespan</div>
          <div className="text-2xl font-bold mt-1 text-amber-900">{avgDays > 0 ? `${avgDays.toFixed(0)}d` : '—'}</div>
          <div className="text-xs text-amber-700 mt-1">issue → chargeback</div>
        </div>
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4">
          <div className="text-xs text-red-700 font-semibold uppercase tracking-wide">Early chargebacks</div>
          <div className="text-2xl font-bold mt-1 text-red-900">{under30}</div>
          <div className="text-xs text-red-700 mt-1">&lt; 30 days · {under7} under 7 days</div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Chargeback trend" subtitle={`Total losses by ${periodMode === 'week' ? 'week' : 'month'} (all history)`} fullscreenable>
          {(h) => (
            <ResponsiveContainer width="100%" height={h}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                <XAxis dataKey="label" tick={{fontSize:12}}/>
                <YAxis yAxisId="left" tickFormatter={fmtCompact} tick={{fontSize:12}}/>
                <YAxis yAxisId="right" orientation="right" tick={{fontSize:12}}/>
                <Tooltip formatter={(v, name) => name === '# of policies' ? v : fmt(v)}/>
                <Legend/>
                <Line yAxisId="left" type="monotone" dataKey="loss" name="Total loss ($)" stroke="#ef4444" strokeWidth={3}/>
                <Line yAxisId="right" type="monotone" dataKey="count" name="# of policies" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 4"/>
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card title="Chargebacks by agent" subtitle="Top 10 by loss (current period)" fullscreenable>
          {(h) => (
            <ResponsiveContainer width="100%" height={h}>
              <BarChart data={agentChartData} layout="vertical" margin={{left: 30}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                <XAxis type="number" tickFormatter={fmtCompact} tick={{fontSize:12}}/>
                <YAxis type="category" dataKey="name" tick={{fontSize:11}} width={120}/>
                <Tooltip formatter={(v, name) => name === 'Policies' ? v : fmt(v)}/>
                <Legend/>
                <Bar dataKey="loss" name="Loss ($)" fill="#ef4444"/>
                <Bar dataKey="count" name="Policies" fill="#f59e0b"/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Per-agent summary */}
      {agentsSorted.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <h3 className="font-bold">Chargebacks by agent</h3>
            <p className="text-xs text-slate-500 mt-0.5">Click an agent to filter the table below</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 text-xs border-b border-slate-200">
                  <th className="py-2 px-3">Agent</th>
                  <th className="py-2 px-3">Team</th>
                  <th className="py-2 px-3 text-right">Policies charged back</th>
                  <th className="py-2 px-3 text-right">Total loss</th>
                  <th className="py-2 px-3 text-right">Avg lifespan</th>
                </tr>
              </thead>
              <tbody>
                {agentsSorted.map(a => (
                  <tr key={a.prodNum} onClick={()=>setAgentFilter(a.prodNum===agentFilter?'all':a.prodNum)} className={`border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${agentFilter===a.prodNum?'bg-emerald-50':''}`}>
                    <td className="py-2 px-3">
                      <div className="font-semibold">{a.name}</div>
                      <div className="text-xs font-mono text-slate-500">{a.prodNum}</div>
                    </td>
                    <td className="py-2 px-3">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${a.team==='mine'?'bg-emerald-100 text-emerald-700':'bg-slate-100 text-slate-500'}`}>
                        {a.team==='mine'?'MINE':'—'}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right">{a.count}</td>
                    <td className="py-2 px-3 text-right text-red-600 font-semibold">{fmt(a.totalLoss)}</td>
                    <td className={`py-2 px-3 text-right ${lifespanColor(a.avgDays)}`}>
                      {a.avgDays != null ? `${a.avgDays.toFixed(0)}d` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Policy table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center gap-3 flex-wrap">
          <div>
            <h3 className="font-bold">Policy chargeback details</h3>
            <p className="text-xs text-slate-500 mt-0.5">{sorted.length} polic{sorted.length===1?'y':'ies'} · shortest lifespan = worst persistency</p>
          </div>
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <select value={teamFilter} onChange={e=>setTeamFilter(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white">
              <option value="all">All teams</option>
              <option value="mine">{possessive(settings.ownerName)} team</option>
                </select>
            {agentFilter !== 'all' && (
              <button onClick={()=>setAgentFilter('all')} className="px-2 py-1.5 text-xs bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200">
                Agent filter: {getProducerName(agentFilter, producers)} ✕
              </button>
            )}
            <select value={sortBy} onChange={e=>setSortBy(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white">
              <option value="days">Sort: shortest lifespan</option>
              <option value="loss">Sort: biggest loss</option>
              <option value="date">Sort: most recent</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 text-xs border-b border-slate-200">
                <th className="py-2 px-3">Agent</th>
                <th className="py-2 px-3">Policy</th>
                <th className="py-2 px-3">Insured</th>
                <th className="py-2 px-3">Carrier</th>
                <th className="py-2 px-3">Issued → Charged back</th>
                <th className="py-2 px-3 text-right">Lifespan</th>
                <th className="py-2 px-3 text-right">Loss</th>
                <th className="py-2 px-3">Activities</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr><td colSpan={8} className="text-center py-6 text-slate-400">No chargebacks in this period</td></tr>
              )}
              {sorted.map(p => (
                <tr key={p.key} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 px-3">
                    <div className="font-medium">{p.agentName}</div>
                    <div className="text-xs text-slate-500">
                      <span className={`px-1 py-0.5 rounded text-xs font-bold ${p.team==='mine'?'bg-emerald-100 text-emerald-700':'bg-slate-100 text-slate-500'}`}>
                        {p.team==='mine'?'MINE':'—'}
                      </span>
                    </div>
                  </td>
                  <td className="py-2 px-3 font-mono text-xs">{p.policy}</td>
                  <td className="py-2 px-3">
                    {p.insured ? (
                      <button onClick={()=>openClientProfile(clientKeyOf(p.insured))} className="text-emerald-700 hover:text-emerald-900 hover:underline text-left font-medium" title="View full profile">
                        {p.insured}
                      </button>
                    ) : '—'}
                  </td>
                  <td className="py-2 px-3 text-xs text-slate-600">
                    {(p.company || '').toUpperCase().includes('TRANS') ? 'Transamerica' : 'MOO'}
                  </td>
                  <td className="py-2 px-3 font-mono text-xs">{formatIssuetoCB(p)}</td>
                  <td className={`py-2 px-3 text-right text-xs ${lifespanColor(p.days)}`}>
                    {lifespanLabel(p.days)}
                  </td>
                  <td className="py-2 px-3 text-right text-red-600 font-semibold">{fmt(p.totalLoss)}</td>
                  <td className="py-2 px-3">
                    <div className="flex flex-wrap gap-1">
                      {p.activitiesList.map(a => (
                        <span key={a} className="text-xs bg-red-50 text-red-700 px-1.5 py-0.5 rounded">{a}</span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600">
        <div className="font-semibold mb-1">How to read this</div>
        <div>Lifespan = days between <span className="font-mono">Issue Date</span> and the first chargeback event on this policy. Shorter means the policy was written recently and died quickly — a sign of a bad sale or a client who didn't pay the first premium. Policies under 30 days are flagged in red; under 7 days is critical (likely a NSF or canceled-at-issuance).</div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// AI chat — proxied through /api/ai. The provider key lives server-
// side and never reaches the browser; vendor selection is internal.
// ──────────────────────────────────────────────────────────────────────

async function askClaude({ system, messages }) {
  // AUDIT prod-401 fix: attach the Supabase JWT so /api/ai's checkCap
  // gate can identify the caller; without it the endpoint 401s.
  const res = await fetch('/api/ai', {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({
      max_tokens: 1024,
      system,
      messages,
    }),
  });
  if (!res.ok) {
    // Extract the API's own error message + gate reason so we tell the
    // operator what to actually DO — not "try again" on an actionable
    // 401. Fixes BUG-005 from the July 2026 audit where the UI hid an
    // actionable "AI is disabled — enable in Settings" message behind
    // a misleading "temporarily unavailable" toast.
    let body = null;
    try { body = await res.json(); } catch {}
    const apiMsg = body?.error || '';
    const gate   = body?.gate  || '';
    // eslint-disable-next-line no-console
    console.error(`[ai] proxy ${res.status}: ${JSON.stringify(body || {})}`);

    if (res.status === 429 || res.status === 503) {
      throw new Error('AI is busy. Try again in a moment.');
    }
    if (res.status === 401 || res.status === 403 ||
        gate === 'unauthenticated' || /AI is disabled/i.test(apiMsg)) {
      // Actionable — always route to Settings → AI Agents.
      throw new Error('AI is disabled — enable Manager AI in Settings → AI Agents.');
    }
    if (res.status === 402 || /insufficient_credits/i.test(apiMsg)) {
      throw new Error('AI credits exhausted — top up in Settings → Billing.');
    }
    // Unknown non-2xx — surface the API's message if any, else generic.
    throw new Error(apiMsg || 'AI is temporarily unavailable. Try again.');
  }
  const json = await res.json();
  return json?.content?.[0]?.text || '';
}

// Build a compact text summary of the current data slice the user is looking at,
// so the AI can answer questions without us needing to send the raw transactions array.
function buildAgencySummary({ transactions, producers, settings, periodLabel, agg, producerList }) {
  const lines = [];
  lines.push(`# Commission tracker snapshot`);
  lines.push(`Owner: ${settings.ownerName}.`);
  lines.push(`Period filter: ${periodLabel}.`);
  lines.push(`Total transactions in this period: ${transactions.length}.`);
  lines.push('');
  lines.push(`# Team totals (current period)`);
  lines.push(`- ${settings.ownerName}'s team: gross=${fmt(agg.mine.gross)}, chargebacks=${fmt(agg.mine.chargebacks)}, net=${fmt(agg.mine.net)} (${agg.mine.count} rows)`);
  lines.push(`- Unassigned:                  gross=${fmt(agg.unassigned.gross)}, chargebacks=${fmt(agg.unassigned.chargebacks)}, net=${fmt(agg.unassigned.net)} (${agg.unassigned.count} rows)`);
  lines.push('');
  // Per-agent table
  lines.push(`# Per-agent stats (current period)`);
  const sortedAgents = (producerList || []).slice().sort((a, b) => b.net - a.net);
  sortedAgents.forEach(p => {
    const pers = p.gross > 0 ? `${p.persistency.toFixed(0)}% pers` : '—';
    const team = p.team === 'mine' ? settings.ownerName : 'unassigned';
    const carriers = Object.keys(p.carriers || {}).join('/') || '—';
    lines.push(`- ${p.name || `#${p.prodNum}`} (#${p.prodNum}, ${team}): gross=${fmt(p.gross)}, cb=${fmt(p.cb)}, net=${fmt(p.net)}, ${pers}, carriers=${carriers}`);
  });
  lines.push('');
  // Per-carrier
  const byCarrier = {};
  transactions.forEach(tx => {
    const c = deriveCarrier(tx);
    if (!byCarrier[c]) byCarrier[c] = { name: c, gross: 0, cb: 0, count: 0, policies: new Set() };
    const amt = Number(tx.netToAccount) || 0;
    if (amt >= 0) byCarrier[c].gross += amt; else byCarrier[c].cb += Math.abs(amt);
    byCarrier[c].count++;
    if (tx.policy) byCarrier[c].policies.add(`${tx.mga||''}|${tx.policy}`);
  });
  lines.push(`# Per-carrier stats (current period)`);
  Object.values(byCarrier).forEach(b => {
    lines.push(`- ${b.name}: gross=${fmt(b.gross)}, cb=${fmt(-b.cb)}, net=${fmt(b.gross - b.cb)}, ${b.policies.size} unique policies, ${b.count} rows`);
  });
  lines.push('');
  // Recent chargebacks (top 10 worst)
  const cbs = transactions
    .filter(tx => CHARGEBACK_TYPES.has(tx.activityType) || (tx.netToAccount || 0) < 0)
    .map(tx => ({
      date: tx.activityDate,
      policy: tx.policy,
      insured: tx.insured,
      agent: getProducerName(tx.prodNum, producers),
      activityType: tx.activityType,
      amount: tx.netToAccount,
    }))
    .sort((a, b) => a.amount - b.amount)
    .slice(0, 10);
  if (cbs.length > 0) {
    lines.push(`# Top 10 worst chargebacks in this period`);
    cbs.forEach(c => lines.push(`- ${c.date} ${c.insured} (${c.policy}) — ${c.activityType} — ${c.agent} — ${fmt(c.amount)}`));
  }
  return lines.join('\n');
}

function ClientCsvImportCard({ profiles, setProfiles, showToast }) {
  const inputRef = useRef(null);
  const [stats, setStats] = useState(null);

  const onPickFile = () => inputRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const { profiles: parsed, skipped } = await parseClientCsv(text);
      const merged = { ...profiles };
      let added = 0, updated = 0;
      Object.entries(parsed).forEach(([key, data]) => {
        if (!merged[key]) {
          merged[key] = data;
          added++;
        } else {
          // Merge — keep existing manual fields (beneficiaries, notes) and refresh CSV-sourced fields
          const existing = merged[key];
          merged[key] = { ...existing, ...data, beneficiaries: existing.beneficiaries || [] };
          updated++;
        }
      });
      setProfiles(merged);
      setStats({ added, updated, skipped });
      showToast(`Imported ${added + updated} client profiles (${added} new, ${updated} updated)`);
    } catch (err) {
      showToast(`CSV parse error: ${err.message}`, 'error');
    }
  };

  const onClearAll = async () => {
    if (!(await hqConfirm({
      title: `Clear all ${Object.keys(profiles).length} client profiles?`,
      body: 'Manually-entered fields and beneficiaries will be lost.',
      confirmLabel: 'Clear profiles', danger: true,
    }))) return;
    setProfiles({});
    setStats(null);
    showToast('All client profiles cleared');
  };

  const profileCount = Object.keys(profiles || {}).length;

  return (
    <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5">
      <h3 className="font-bold mb-1 text-blue-900">Client profile data</h3>
      <p className="text-sm text-blue-700 mb-3">
        Import the MOO Client CSV (the <code className="bg-blue-100 px-1 rounded">&lt;your-prodNum&gt;-*.csv</code> export) to populate addresses, phones, emails, DOBs, and owner info for every client.
        Manually-entered fields and beneficiaries are preserved on re-import.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={onPickFile} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-semibold">Import Client CSV</button>
        <input ref={inputRef} type="file" accept=".csv" onChange={onFile} className="hidden"/>
        <div className="text-sm text-blue-700 ml-2">
          {profileCount > 0 ? <>Currently storing <strong>{profileCount.toLocaleString()}</strong> client profiles.</> : <>No client profiles imported yet.</>}
        </div>
        {profileCount > 0 && <button onClick={onClearAll} className="ml-auto text-xs text-red-600 hover:bg-red-50 px-2 py-1 rounded">Clear all profiles</button>}
      </div>
      {stats && (
        <div className="mt-3 text-sm bg-white border border-blue-200 rounded-lg px-3 py-2">
          Last import: <strong>{stats.added}</strong> new · <strong>{stats.updated}</strong> updated · {stats.skipped} rows skipped (missing insured name)
        </div>
      )}
    </div>
  );
}

function LobConfigCard({ lobConfig, setLobConfig }) {
  const update = (k, v) => setLobConfig({ ...lobConfig, [k]: v });
  const configured = !!lobConfig?.fromAddress1;

  return (
    <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-5">
      <h3 className="font-bold mb-1 text-orange-900">Postcard sending (Lob.com)</h3>
      <p className="text-sm text-orange-700 mb-3">
        Enables the "Send postcard" button on each client profile. Real postcards are printed and mailed by Lob (~$0.79 each).
        The Lob API key is configured on the server — set your return address below and you're ready to send.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div className="md:col-span-2 text-xs font-semibold uppercase text-orange-700">Return address (your "from")</div>
        <label className="block">
          <span className="text-xs text-orange-700">From name</span>
          <input value={lobConfig?.fromName || ''} onChange={e=>update('fromName', e.target.value)} placeholder="Your Agency Name" className="mt-1 w-full border border-orange-200 rounded-lg px-3 py-2 text-sm bg-white"/>
        </label>
        <label className="block">
          <span className="text-xs text-orange-700">Address line 1</span>
          <input value={lobConfig?.fromAddress1 || ''} onChange={e=>update('fromAddress1', e.target.value)} className="mt-1 w-full border border-orange-200 rounded-lg px-3 py-2 text-sm bg-white"/>
        </label>
        <label className="block">
          <span className="text-xs text-orange-700">Address line 2 (optional)</span>
          <input value={lobConfig?.fromAddress2 || ''} onChange={e=>update('fromAddress2', e.target.value)} className="mt-1 w-full border border-orange-200 rounded-lg px-3 py-2 text-sm bg-white"/>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <label className="block">
            <span className="text-xs text-orange-700">City</span>
            <input value={lobConfig?.fromCity || ''} onChange={e=>update('fromCity', e.target.value)} className="mt-1 w-full border border-orange-200 rounded-lg px-3 py-2 text-sm bg-white"/>
          </label>
          <label className="block">
            <span className="text-xs text-orange-700">State</span>
            <input value={lobConfig?.fromState || ''} onChange={e=>update('fromState', e.target.value.toUpperCase().slice(0,2))} maxLength={2} className="mt-1 w-full border border-orange-200 rounded-lg px-3 py-2 text-sm bg-white"/>
          </label>
          <label className="block">
            <span className="text-xs text-orange-700">ZIP</span>
            <input value={lobConfig?.fromZip || ''} onChange={e=>update('fromZip', e.target.value)} className="mt-1 w-full border border-orange-200 rounded-lg px-3 py-2 text-sm bg-white"/>
          </label>
        </div>
      </div>
      <div className="mt-3 text-xs text-orange-800">
        Status: {configured ? <span className="font-semibold text-emerald-700">✓ Ready — Send Postcard button enabled in client profiles</span> : <span className="font-semibold text-orange-700">⚠ Incomplete — add a return address to enable sending</span>}
      </div>
    </div>
  );
}

function ChatPanel({ summary, settings, onOpenPalette = () => {}, onGotoTab = () => {} }) {
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const launcherRef = useRef(null);

  // Close the launcher menu on outside click / Escape so it doesn't
  // sit open after the operator moves on.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => { if (launcherRef.current && !launcherRef.current.contains(e.target)) setMenuOpen(false); };
    const onKey  = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [menuOpen]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, pending]);

  const system = `You are the in-app AI sales assistant for a US life & health insurance agent. Answer any question the agent could reasonably ask a smart sales manager, including:

  • Their OWN commission / policy / lead data — use the snapshot below.
  • Carrier & product knowledge — Mutual of Omaha, Americo, Transamerica, Foresters, Corebridge, Aetna, UnitedHealthcare, Cigna, Humana, Anthem, Ambetter, Molina, WellCare, etc. Talk about which carrier is generally strongest for a given profile (final expense, term, IUL, mortgage protection, Medicare Advantage, ACA, dental/vision) and known underwriting characteristics.
  • Underwriting scenarios — diabetes, hypertension, stroke, cancer, heart attack, COPD, DUI, tobacco, weight, cannabis, mental health, MAOI meds, high-risk occupations, aviation. Give the agent the general shape of who tends to be lenient / strict, and remind them that final approval always hinges on the carrier's actual UW guide + the applicant's specific med history.
  • Sales craft — objection handling, discovery questions, referral asks, cross-sell timing, presentation flow, appointment setting, retention.
  • Compliance basics — TCPA / DNC, HIPAA on health, CMS on Medicare (SOA, PTC), CAN-SPAM, quiet hours, state-specific quirks.
  • Product & tech questions about how this CRM works.

Rules:
  • When the question is about the agent's OWN data (their commissions, their leads, a specific client), ground the answer in the snapshot below. If the snapshot doesn't have it, say so plainly.
  • When the question is general (carriers, underwriting, sales technique, compliance), answer from your broad insurance industry knowledge — do NOT refuse just because it isn't in the snapshot.
  • Never invent carrier-specific rate quotes, exact underwriting thresholds, or promise approval. Frame carrier recommendations as "commonly considered", "often the go-to", or "worth quoting first", not as guarantees.
  • Format money as $X,XXX.XX. Match names loosely.
  • Keep answers tight — 1–4 sentences unless the agent asks for depth.

Agent data snapshot (their current period):

${summary}`;

  const send = async (explicitText) => {
    const raw = typeof explicitText === 'string' ? explicitText : input;
    if (!raw.trim() || pending) return;
    const userMsg = { role: 'user', content: raw.trim() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setPending(true);
    setError(null);
    // Setup Tour: signal that the operator used the AI (drives the finale
    // completion + the post-tour echo's 5-use cap). Best-effort.
    try { window.dispatchEvent(new CustomEvent('setup-tour:ai-used')); } catch {}
    try {
      const reply = await askClaude({
        system,
        messages: newMessages.map(m => ({ role: m.role, content: m.content })),
      });
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      try { window.dispatchEvent(new CustomEvent('setup-tour:ai-responded')); } catch {}
    } catch (err) {
      setError(err.message);
      setMessages(prev => prev.slice(0, -1)); // drop the user message we couldn't answer
      setInput(userMsg.content);
      try { window.dispatchEvent(new CustomEvent('setup-tour:ai-error')); } catch {}
    } finally {
      setPending(false);
    }
  };

  // Setup Tour finale: open the panel and (optionally) send the operator's
  // first question when they submit the inline "ask your AI" input. A ref
  // keeps the listener bound once while always calling the latest send().
  const sendRef = useRef(send);
  sendRef.current = send;
  useEffect(() => {
    const onAsk = (e) => {
      const text = (e && e.detail && e.detail.text) || '';
      setMenuOpen(false);
      setOpen(true);
      if (String(text).trim()) sendRef.current(text);
    };
    window.addEventListener('setup-tour:ask-ai', onAsk);
    return () => window.removeEventListener('setup-tour:ask-ai', onAsk);
  }, []);

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!open) {
    return (
      <div ref={launcherRef} data-tour-id="ai-launcher" className="fixed bottom-4 right-44 z-40">
        {menuOpen && (
          <div className="absolute bottom-full right-0 mb-2 w-48 bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden">
            <button
              onClick={() => { setMenuOpen(false); setOpen(true); }}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-slate-800 hover:bg-indigo-50"
            >
              <span className="text-indigo-600">✨</span> Ask AI
            </button>
            <button
              onClick={() => { setMenuOpen(false); onOpenPalette(); }}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-slate-800 hover:bg-slate-50 border-t border-slate-100"
            >
              <span className="text-slate-500 font-mono text-xs">⌘K</span> Jump to…
            </button>
            <button
              onClick={() => { setMenuOpen(false); onGotoTab('automation_map'); }}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-slate-800 hover:bg-slate-50 border-t border-slate-100"
            >
              <span className="text-teal-600">🗺</span> Automation map
            </button>
          </div>
        )}
        <button
          onClick={() => setMenuOpen(o => !o)}
          title="AI + Jump to"
          aria-expanded={menuOpen}
          className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-4 py-3 shadow-lg flex items-center gap-2 font-semibold"
        >
          <span className="text-lg">✨</span> AI
        </button>
      </div>
    );
  }

  return (
    // z-[60] lifts the modal above the SupportChat bubble (z-50) so the
    // green launcher doesn't poke out the bottom-right corner when the
    // AI panel is open. bottom-4 right-4 aligns with SupportChat's
    // anchor so the modal fully covers where the bubble sat.
    <div className="fixed bottom-4 right-4 z-[60] w-[400px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col" style={{ maxHeight: '70vh' }}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-indigo-600 text-white rounded-t-2xl">
        <div>
          <div className="font-bold flex items-center gap-2">Ask AI</div>
          <div className="text-xs text-indigo-200">Carriers · underwriting · sales · your book of business</div>
        </div>
        <button onClick={() => setOpen(false)} className="text-indigo-200 hover:text-white"><X size={18}/></button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50 min-h-[200px]">
        {messages.length === 0 && !pending && (
          <div className="text-sm text-slate-500 space-y-2">
            <div className="font-medium">Try asking:</div>
            <button onClick={() => setInput("Which carrier is generally strongest for a client with stroke history?")} className="block text-left text-indigo-700 hover:underline">→ Which carrier is strongest for a stroke history client?</button>
            <button onClick={() => setInput("How should I handle 'I already have insurance' on a Medicare call?")} className="block text-left text-indigo-700 hover:underline">→ Objection: "I already have insurance"</button>
            <button onClick={() => setInput("Give me a discovery-question flow for a mortgage protection lead.")} className="block text-left text-indigo-700 hover:underline">→ Discovery questions for mortgage protection</button>
            <button onClick={() => setInput("Top 3 producers by net commission this period?")} className="block text-left text-indigo-700 hover:underline">→ Top 3 producers by net commission</button>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-indigo-600 text-white' : 'bg-white border border-slate-200 text-slate-900'}`}>
              {m.content}
            </div>
          </div>
        ))}
        {pending && (
          <div className="flex justify-start">
            <div className="bg-white border border-slate-200 text-slate-500 text-sm rounded-2xl px-3 py-2">Thinking…</div>
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-xs rounded-lg px-3 py-2">
            {error}
          </div>
        )}
      </div>

      <div className="border-t border-slate-200 p-2 flex items-end gap-2">
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={onKey}
          rows={2}
          placeholder="Ask anything — carriers, underwriting, sales, your data…"
          className="flex-1 resize-none border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500"
        />
        <button
          onClick={send}
          disabled={!input.trim() || pending}
          className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-3 py-2 rounded-lg text-sm font-semibold"
        >
          Send
        </button>
      </div>
    </div>
  );
}

// Change-password panel for already-signed-in customers. Different
// flow from the email-recovery path (SetNewPasswordScreen): this is
// an in-app rotate. Audit 2026-07-01 S-004: current-password is now
// REQUIRED before we call updateUser — otherwise anyone with a
// live session (e.g. laptop-left-unlocked) could rotate the password
// and lock the real owner out.
function ChangePasswordCard({ showToast = () => {} }) {
  const { user, signIn, changePassword } = useAuth();
  const [current, setCurrent] = React.useState('');
  const [next, setNext] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState('');

  const submit = async () => {
    if (pending) return;
    if (!current) { setError('Enter your current password.'); return; }
    if (next.length < 8) { setError('New password must be at least 8 characters.'); return; }
    if (next !== confirm) { setError('The two new passwords don\'t match.'); return; }
    if (current === next) { setError('The new password is the same as the current one.'); return; }
    if (!user?.email) { setError('No email on the current session — sign out and back in first.'); return; }
    setError('');
    setPending(true);
    // Re-authenticate first. Supabase's signInWithPassword refreshes
    // the SAME session (same user id) — no logout side effect on
    // success. On failure the current session is untouched.
    const reauth = await signIn(user.email, current);
    if (!reauth?.ok) {
      setPending(false);
      setError('Current password doesn\'t match — check it and try again.');
      return;
    }
    const result = await changePassword(next);
    setPending(false);
    if (!result.ok) {
      setError(result.error || 'Could not change password.');
      return;
    }
    setCurrent('');
    setNext('');
    setConfirm('');
    showToast('Password updated', 'success');
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="font-bold">Change your password</h3>
          <p className="text-xs text-slate-500 mt-0.5">Signed in as {user?.email || 'this account'}. The new password takes effect immediately.</p>
        </div>
      </div>
      <div className="mb-3">
        <label className="block">
          <span className="text-xs uppercase tracking-wide font-semibold text-slate-600">Current password</span>
          <input
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={e => { setCurrent(e.target.value); setError(''); }}
            placeholder="The one you use to sign in today"
            className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
          />
        </label>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs uppercase tracking-wide font-semibold text-slate-600">New password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={e => { setNext(e.target.value); setError(''); }}
            placeholder="At least 8 characters"
            className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wide font-semibold text-slate-600">Confirm new password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={e => { setConfirm(e.target.value); setError(''); }}
            onKeyDown={e => { if (e.key === 'Enter') submit(); }}
            placeholder="Type it again"
            className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${next && confirm && next !== confirm ? 'border-red-400' : 'border-slate-200'}`}
          />
          {/* Audit 2026-07-01 BUG-007: inline mismatch hint the
              moment both fields are non-empty and differ, instead
              of waiting for the operator to press Save. */}
          {next && confirm && next !== confirm && (
            <span className="text-[11px] text-red-600 mt-1 block">Passwords don't match.</span>
          )}
        </label>
      </div>
      {error && (
        <div className="mt-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>
      )}
      <div className="mt-3 flex justify-end">
        <button
          onClick={submit}
          disabled={!current || !next || !confirm || pending}
          className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 rounded-lg"
        >
          {pending ? 'Updating…' : 'Save new password'}
        </button>
      </div>
    </div>
  );
}

// Launch Readiness view — single-URL diagnostic for a new signup
// hitting silent gate failures. Every check reuses the same primitive
// the real send/dial path hits (subscriptions row, tenant_a2p_status
// view, wallet_balances, env flags). Never drifts from reality
// because it never has its own copy of the truth.
function LaunchReadinessView({ showToast = () => {} }) {
  const [state, setState] = React.useState({ loading: true, data: null, error: null });

  const load = React.useCallback(async () => {
    setState({ loading: true, data: null, error: null });
    try {
      const r = await apiFetch('/api/account/launch-readiness', { method: 'GET' });
      setState({ loading: false, data: r, error: null });
    } catch (err) {
      setState({ loading: false, data: null, error: err.message || String(err) });
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 md:p-8">
        <div className="max-w-3xl mx-auto text-sm text-slate-500">Checking your account…</div>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 md:p-8">
        <div className="max-w-3xl mx-auto bg-red-50 border border-red-200 rounded-xl p-5 text-sm text-red-900">
          Could not load readiness check: {state.error}
          <button onClick={load} className="ml-3 underline">Retry</button>
        </div>
      </div>
    );
  }
  const d = state.data;
  if (!d) return null;

  const bannerBg = d.ready
    ? d.summary.yellow === 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                              : 'bg-amber-50 border-amber-200 text-amber-900'
    : 'bg-red-50 border-red-200 text-red-900';
  const dotFor = (status) => {
    if (status === 'green')  return 'bg-emerald-500';
    if (status === 'yellow') return 'bg-amber-500';
    return 'bg-red-500';
  };
  const rowBorderFor = (status) => {
    if (status === 'green')  return 'border-emerald-200';
    if (status === 'yellow') return 'border-amber-200';
    return 'border-red-300';
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6 md:p-8">
      <div className="max-w-3xl mx-auto space-y-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Georgia, serif', letterSpacing: '-0.02em' }}>Account health</h1>
          <p className="text-sm text-slate-600 mt-1">Every gate that could silently block a text or call, checked at once. If SMS or dial isn't working, this page tells you exactly why.</p>
        </div>

        <div className={`rounded-xl border p-4 ${bannerBg}`}>
          <div className="font-bold text-base">{d.ready_headline}</div>
          <div className="text-xs mt-1 opacity-80">
            {d.summary.green} OK · {d.summary.yellow} warning · {d.summary.red} blocker
            {' · '}Checked {new Date(d.generated_at).toLocaleTimeString()}
            <button onClick={load} className="ml-2 underline">Re-check</button>
          </div>
        </div>

        <div className="space-y-2">
          {d.checks.map(c => (
            <div key={c.key} className={`bg-white border rounded-xl p-4 ${rowBorderFor(c.status)}`}>
              <div className="flex items-start gap-3">
                <div className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${dotFor(c.status)}`}/>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-900 text-sm">{c.label}</div>
                  <div className="text-xs text-slate-600 mt-0.5">{c.detail}</div>
                  {c.remediation && (
                    <div className="text-xs text-slate-800 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 mt-2">
                      <span className="font-semibold">Fix: </span>{c.remediation}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-xs text-slate-500 pt-2">
          Every check on this page runs against the same subscription row, A2P view, wallet balance, and env flag the real send/dial code uses. It never has its own copy of the truth — so if this page says something works, it works.
        </div>

        <DailySummaryPreview showToast={showToast}/>

        <AgencyImpactPanel showToast={showToast}/>
      </div>
    </div>
  );
}

// Daily-summary preview — shows today's rollup without waiting for the
// 8pm-local cron. Same shape as /api/reports/va-daily-summary. Lets
// operators self-check their day mid-afternoon.
function DailySummaryPreview({ showToast = () => {} }) {
  const [state, setState] = React.useState({ loading: false, data: null, error: null });
  const load = React.useCallback(async () => {
    setState({ loading: true, data: null, error: null });
    try {
      const r = await apiFetch('/api/reports/va-daily-summary', { method: 'GET' });
      setState({ loading: false, data: r, error: null });
    } catch (err) {
      setState({ loading: false, data: null, error: err.message || String(err) });
    }
  }, []);

  const fmtDuration = (sec) => {
    if (!sec) return '0s';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    if (m === 0) return `${s}s`;
    if (s === 0) return `${m}m`;
    return `${m}m ${s}s`;
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 mt-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-bold text-slate-900">Today so far</div>
          <div className="text-xs text-slate-500 mt-0.5">Preview the summary that will email at 8pm your local time.</div>
        </div>
        <button
          onClick={load}
          disabled={state.loading}
          className="bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white font-semibold text-sm px-3 py-1.5 rounded-lg"
        >
          {state.loading ? 'Loading…' : state.data ? 'Refresh' : 'Show summary'}
        </button>
      </div>

      {state.error && (
        <div className="text-xs text-red-700 mt-3">Error: {state.error}</div>
      )}

      {state.data && (
        <div className="mt-4 space-y-3 text-xs">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider">
            {state.data.date} · {state.data.timezone}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <PreviewStat label="Dials" value={state.data.activity.calls.dials}/>
            <PreviewStat label="Connects" value={`${state.data.activity.calls.connects} · ${state.data.activity.calls.connect_rate_pct}%`} tone="emerald"/>
            <PreviewStat label="Talk time" value={fmtDuration(state.data.activity.calls.total_talk_sec)}/>
            <PreviewStat label="SMS sent" value={state.data.activity.messages.sms_sent}/>
            <PreviewStat label="SMS replies" value={state.data.activity.messages.sms_replies}/>
            <PreviewStat label="Emails sent" value={state.data.activity.messages.email_sent ?? '—'}/>
            <PreviewStat label="Appts booked today" value={state.data.activity.appointments.booked_today} tone="emerald"/>
            <PreviewStat
              label="Closed from 30d appts"
              value={`${state.data.activity.appointments.closed_from_prior_appts} / ${state.data.activity.appointments.prior_30d_scanned} · ${state.data.activity.appointments.close_rate_pct}%`}
              tone="emerald"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function PreviewStat({ label, value, tone }) {
  const bg = tone === 'emerald' ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
    : 'bg-slate-50 border-slate-200 text-slate-900';
  return (
    <div className={`border rounded-lg px-3 py-2 ${bg}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-sm font-mono font-bold">{value}</div>
    </div>
  );
}

// Agency Impact panel — §1 of Next-Level Vision, honest version.
// Shows what the agency actually accomplished in the last N days
// via literal aggregation. No fabricated "$X protected" claims.
function AgencyImpactPanel({ showToast = () => {} }) {
  const [days, setDays] = React.useState(30);
  const [state, setState] = React.useState({ loading: true, data: null, error: null });

  const load = React.useCallback(async () => {
    setState({ loading: true, data: null, error: null });
    try {
      const r = await apiFetch(`/api/account/agency-impact?days=${days}`, { method: 'GET' });
      setState({ loading: false, data: r, error: null });
    } catch (err) {
      setState({ loading: false, data: null, error: err.message || String(err) });
    }
  }, [days]);
  React.useEffect(() => { load(); }, [load]);

  const fmtDollars = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const trendChip = (delta) => {
    if (delta > 0) return { arrow: '▲', color: 'text-emerald-700', bg: 'bg-emerald-50' };
    if (delta < 0) return { arrow: '▼', color: 'text-red-700', bg: 'bg-red-50' };
    return { arrow: '·', color: 'text-slate-500', bg: 'bg-slate-50' };
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 mt-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-bold text-slate-900">Agency impact</div>
          <div className="text-xs text-slate-500 mt-0.5">
            Real activity your agency completed in the last {days} days. No fabricated protection claims — just what happened.
          </div>
        </div>
        <div className="flex items-center gap-1 text-xs">
          {[7, 30, 90].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`font-semibold px-2 py-1 rounded ${
                days === d ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 border border-slate-200 hover:border-slate-400'
              }`}
            >{d}d</button>
          ))}
        </div>
      </div>

      {state.loading && <div className="mt-3 text-xs text-slate-500">Loading…</div>}
      {state.error && <div className="mt-3 text-xs text-red-700">{state.error}</div>}

      {state.data && (
        <>
          {/* Headline numbers */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <ImpactStat
              label="Appointments booked"
              value={state.data.activity.appointments_booked}
              trend={state.data.trends.appointments_booked_delta_pct}
              tone="emerald"
            />
            <ImpactStat
              label="Real conversations (30s+)"
              value={state.data.activity.calls_connected}
              trend={state.data.trends.calls_connected_delta_pct}
            />
            <ImpactStat
              label="SMS sent"
              value={state.data.activity.messages_out}
              trend={state.data.trends.messages_out_delta_pct}
            />
            <ImpactStat
              label="Talk time"
              value={`${state.data.activity.talk_time_hours}h`}
            />
          </div>

          {/* Policy activation */}
          <div className="mt-4 border-t border-slate-100 pt-4">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Policies effective in window</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <ImpactStat label="Health policies" value={state.data.activity.policies_effective_health} tone="emerald"/>
              <ImpactStat label="Life policies" value={state.data.activity.policies_effective_life} tone="emerald"/>
              <ImpactStat
                label="Monthly premium activated"
                value={fmtDollars(state.data.activity.monthly_premium_activated_dollars)}
                tone="emerald"
              />
            </div>
          </div>

          <div className="text-[10px] text-slate-400 italic mt-3">{state.data.interpretation_note}</div>
        </>
      )}
    </div>
  );
}

function ImpactStat({ label, value, trend, tone }) {
  const bg = tone === 'emerald' ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
    : 'bg-slate-50 border-slate-200 text-slate-900';
  const trendChip = (delta) => {
    if (delta > 0) return { arrow: '▲', color: 'text-emerald-700' };
    if (delta < 0) return { arrow: '▼', color: 'text-red-700' };
    return { arrow: '·', color: 'text-slate-400' };
  };
  return (
    <div className={`border rounded-lg px-3 py-2 ${bg}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</div>
      <div className="flex items-baseline justify-between gap-2 mt-0.5">
        <div className="text-lg font-bold font-mono">{value}</div>
        {trend !== undefined && (
          <div className={`text-[10px] font-bold ${trendChip(trend).color}`}>
            {trendChip(trend).arrow} {Math.abs(trend)}%
          </div>
        )}
      </div>
    </div>
  );
}

function BillingCard({ currentUser, showToast }) {
  const [pending, setPending] = useState(false);

  const openPortal = async () => {
    if (pending) return;
    setPending(true);
    try {
      // Bug fix 2026-07-12: previously called plain fetch() without the
      // Authorization header, so the endpoint's requireUser() gate
      // 401'd on every click. Additionally passed currentUser.prodNum
      // (producer number) as body.userId, which the endpoint's
      // back-compat check compares against auth.userId (Supabase UUID)
      // and 403s with forbidden_user_mismatch when they don't match.
      // Both fixed by switching to apiFetch (attaches JWT) and omitting
      // the body — the endpoint derives userId from the JWT anyway.
      const data = await apiFetch('/api/create-portal-session', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      if (!data?.url) {
        throw new Error('Billing portal request returned no URL.');
      }
      window.location.href = data.url;
    } catch (err) {
      showToast(err.message || 'Could not start billing portal.', 'error');
      setPending(false);
    }
  };

  return (
    <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-5">
      <h3 className="font-bold mb-1 text-emerald-900">Billing & subscription</h3>
      <p className="text-sm text-emerald-800 mb-3">
        Update your card, view invoices, or cancel your subscription via Stripe's secure billing portal. You'll come right back here when you're done.
      </p>
      <button
        onClick={openPortal}
        disabled={pending}
        className="px-3 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold disabled:opacity-50"
      >
        {pending ? 'Opening Stripe…' : 'Manage billing & subscription'}
      </button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Branding panel — Phase 1 of the multi-tenant SaaS refactor.
// Lets the agency owner customize: business name, logo, accent color.
// Saved through the existing per-user settings persistence (which is
// already tenant-namespaced via userStorage). When a server-side tenant
// config endpoint exists, this panel POSTs there instead — no consumer
// changes needed since everything reads via resolveBranding().
// ──────────────────────────────────────────────────────────────────────
const BRANDING_COLOR_PRESETS = [
  { name: 'Emerald (default)', hex: '#10b981' },
  { name: 'Gold',              hex: '#C9A227' },
  { name: 'Royal Blue',        hex: '#2563eb' },
  { name: 'Indigo',            hex: '#4f46e5' },
  { name: 'Violet',            hex: '#7c3aed' },
  { name: 'Rose',              hex: '#e11d48' },
  { name: 'Slate',              hex: '#475569' },
];

// Resize an uploaded logo so the data URL we store stays reasonable.
// Aspect ratio is preserved; max edge 240 px keeps the header logo crisp
// at 36 px tall on 2× screens without bloating localStorage.
async function readLogoAsDataUrl(file, maxEdge = 240) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result || ''));
    r.onerror = () => rej(new Error('Failed to read logo file'));
    r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new window.Image();
    i.onload = () => res(i);
    i.onerror = () => rej(new Error('Could not decode image'));
    i.src = dataUrl;
  });
  const w = img.width; const h = img.height;
  if (w <= maxEdge && h <= maxEdge) return dataUrl;
  const scale = Math.min(maxEdge / w, maxEdge / h);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(w * scale); canvas.height = Math.round(h * scale);
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/png');
}

function BrandingPanel({ settings, setSettings, showToast = () => {} }) {
  const resolved = resolveBranding(settings);
  const current = settings?.branding || {};
  const update = (patch) => setSettings(s => ({ ...s, branding: { ...(s.branding || {}), ...patch } }));
  const [busyLogo, setBusyLogo] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const handleLogo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { showToast('Pick an image (PNG / JPG / SVG)'); return; }
    setBusyLogo(true);
    try {
      const dataUrl = await readLogoAsDataUrl(file);
      update({ logoDataUrl: dataUrl });
      showToast('Logo updated');
    } catch (e) {
      showToast(`Logo upload failed: ${e.message}`);
    } finally {
      setBusyLogo(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // Drag-and-drop. We mark the zone as the drop target, swallow the
  // dragover default so the browser doesn't open the image in a new tab,
  // and accept the first image file we get.
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragOver) setDragOver(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only flip off when the cursor genuinely leaves the dropzone — not
    // when it crosses a child element. The relatedTarget check filters
    // out child-element transitions.
    if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    const img = files.find(f => f.type.startsWith('image/')) || files[0];
    if (img) handleLogo(img);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="font-bold">Branding</h3>
          <div className="text-xs text-slate-500 mt-0.5">Customize how this CRM appears to your team. Changes take effect immediately for everyone signed in to this workspace.</div>
        </div>
        <button
          onClick={async () => {
            if (await hqConfirm({
              title: 'Reset workspace branding?',
              body: 'Business name, logo, and accent color will return to defaults.',
              confirmLabel: 'Reset branding',
            })) update({ businessName: '', logoDataUrl: '', accentColor: '' });
          }}
          className="text-xs text-slate-500 hover:text-slate-700 underline"
        >Reset to defaults</button>
      </div>

      {/* Live preview */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 mb-4">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Live preview</div>
        <div className="bg-white rounded-lg border border-slate-200 p-3 flex items-center gap-3">
          {resolved.logoDataUrl ? (
            <img src={resolved.logoDataUrl} alt={resolved.businessName} className="h-9 w-auto max-w-[160px] object-contain"/>
          ) : (
            <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold" style={{ backgroundColor: resolved.accentColor }}>$</div>
          )}
          <div className="flex-1">
            <div className="font-bold text-lg leading-tight">{resolved.businessName}</div>
            <div className="text-xs text-slate-500">Header preview</div>
          </div>
          <button className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg" style={{ backgroundColor: resolved.accentColor }}>Primary CTA</button>
          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase" style={{ backgroundColor: resolved.accentColor + '20', color: resolved.accentColor }}>Badge</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Business name */}
        <label className="block">
          <span className="text-sm font-semibold text-slate-700">Business name</span>
          <input
            value={current.businessName || ''}
            onChange={e => update({ businessName: e.target.value })}
            placeholder={DEFAULT_BRANDING.businessName}
            className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
          />
          <p className="text-xs text-slate-500 mt-1">Replaces the title in the header. Leave blank to use the default ("{DEFAULT_BRANDING.businessName}").</p>
        </label>

        {/* Logo upload — drag-and-drop OR click to browse */}
        <div className="block">
          <span className="text-sm font-semibold text-slate-700">Logo</span>
          <label
            onDragOver={onDragOver}
            onDragEnter={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            className={`mt-1 block cursor-pointer rounded-lg border-2 border-dashed transition-colors p-4 ${
              busyLogo
                ? 'border-slate-200 bg-slate-50 cursor-wait'
                : dragOver
                ? 'border-emerald-500 bg-emerald-50'
                : 'border-slate-300 bg-slate-50 hover:border-emerald-400 hover:bg-emerald-50/40'
            }`}
          >
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={e => handleLogo(e.target.files?.[0])}
              disabled={busyLogo}
              className="hidden"
            />
            <div className="flex items-center gap-3">
              <div className="w-16 h-12 border border-slate-200 rounded flex items-center justify-center bg-white overflow-hidden flex-shrink-0">
                {resolved.logoDataUrl
                  ? <img src={resolved.logoDataUrl} alt="" className="max-h-full max-w-full object-contain"/>
                  : <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold" style={{ backgroundColor: resolved.accentColor }}>$</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-semibold ${dragOver ? 'text-emerald-700' : 'text-slate-700'}`}>
                  {busyLogo
                    ? 'Uploading…'
                    : dragOver
                    ? 'Drop to upload'
                    : resolved.logoDataUrl
                    ? 'Drop a new logo to replace, or click to browse'
                    : 'Drop a logo here, or click to browse'}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">PNG, JPG, or SVG · auto-resized to keep storage small</div>
              </div>
              {resolved.logoDataUrl && !busyLogo && (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); update({ logoDataUrl: '' }); }}
                  className="text-xs text-red-600 hover:bg-red-50 px-2 py-1 rounded border border-red-200 flex-shrink-0"
                >
                  Remove
                </button>
              )}
            </div>
          </label>
        </div>

        {/* Accent color */}
        <div className="block md:col-span-2">
          <span className="text-sm font-semibold text-slate-700">Accent color</span>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <input
              type="color"
              value={resolved.accentColor}
              onChange={e => update({ accentColor: e.target.value })}
              className="w-12 h-10 border border-slate-200 rounded cursor-pointer"
              title="Pick any color"
            />
            <input
              type="text"
              value={current.accentColor || ''}
              onChange={e => update({ accentColor: e.target.value })}
              placeholder={DEFAULT_BRANDING.accentColor}
              className="w-32 border border-slate-200 rounded-lg px-2 py-1.5 text-sm font-mono"
            />
            <span className="text-xs text-slate-500">or pick a preset:</span>
            {BRANDING_COLOR_PRESETS.map(p => (
              <button
                key={p.hex}
                onClick={() => update({ accentColor: p.hex })}
                className={`w-7 h-7 rounded-full border-2 ${resolved.accentColor.toLowerCase() === p.hex.toLowerCase() ? 'border-slate-900 ring-2 ring-slate-300' : 'border-white shadow'}`}
                style={{ backgroundColor: p.hex }}
                title={p.name}
                aria-label={p.name}
              />
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Drives the header icon tile, active tab underline, primary action buttons, and badges. Available app-wide via the <code className="font-mono">--brand-accent</code> CSS variable.
          </p>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// CarrierCatalogPanel — Phase 3 of the multi-tenant SaaS refactor.
// Owners add carriers and product mappings here instead of relying on
// hardcoded company/plan-code rules. Seeded with the legacy detection
// rules so today's classification is preserved out of the box.
// ──────────────────────────────────────────────────────────────────────
function CarrierCatalogPanel({ catalog = [], setCatalog, transactions = [], getCarrier, getProduct, showToast = () => {} }) {
  const [editing, setEditing] = useState(null); // null | carrier | { __new: true }

  // Coverage: how many transactions land on each carrier vs. fall through to
  // the hardcoded fallback. Surfaces gaps the owner needs to plug.
  const coverage = React.useMemo(() => {
    const byCarrier = {};
    let unknown = 0;
    let productOther = 0;
    transactions.forEach(t => {
      const c = getCarrier ? getCarrier(t) : (t.company || 'Unknown');
      const p = getProduct ? getProduct(t) : '';
      byCarrier[c] = (byCarrier[c] || 0) + 1;
      if (c === 'Unknown' || !catalog.some(cc => cc.active && cc.name === c)) unknown++;
      if (p === 'Other') productOther++;
    });
    return { byCarrier, unknown, productOther };
  }, [transactions, getCarrier, getProduct, catalog]);

  const upsert = (carrier) => {
    const isNew = !catalog.some(c => c.id === carrier.id);
    if (isNew) setCatalog(prev => [...prev, carrier]);
    else setCatalog(prev => prev.map(c => c.id === carrier.id ? carrier : c));
    showToast(isNew ? 'Carrier added' : 'Carrier saved');
    setEditing(null);
  };
  const remove = async (id) => {
    if (!(await hqConfirm({
      title: 'Delete this carrier?',
      body: 'Transactions for it will fall back to the built-in detection rules.',
      confirmLabel: 'Delete carrier', danger: true,
    }))) return;
    setCatalog(prev => prev.filter(c => c.id !== id));
    showToast('Carrier deleted');
  };
  const toggleActive = (id) => setCatalog(prev => prev.map(c => c.id === id ? { ...c, active: !c.active } : c));
  const restoreDefaults = async () => {
    if (!(await hqConfirm({
      title: 'Reset carrier catalog to defaults?',
      body: 'Built-in Transamerica / Americo / Mutual of Omaha carriers will be restored. Any custom carriers you added will be removed.',
      confirmLabel: 'Restore defaults', danger: true,
    }))) return;
    setCatalog(buildDefaultCarrierCatalog());
    showToast('Catalog reset to defaults');
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="font-bold">Carriers & Products</h3>
          <div className="text-xs text-slate-500 mt-0.5">Configure how transactions are classified by carrier and product type. Built-in defaults match today's behavior; customize to add your own carriers or override.</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={restoreDefaults} className="text-xs text-slate-600 hover:text-slate-900 underline">Restore defaults</button>
          <button onClick={() => setEditing({ __new: true })} className="bg-slate-900 hover:bg-slate-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">+ New carrier</button>
        </div>
      </div>

      {/* Coverage stats */}
      {transactions.length > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3">
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Coverage from {transactions.length.toLocaleString()} transactions</div>
          <div className="flex items-center gap-3 flex-wrap text-xs">
            {Object.entries(coverage.byCarrier).sort((a, b) => b[1] - a[1]).map(([c, count]) => {
              const cataloged = catalog.some(cc => cc.active && cc.name === c);
              return (
                <span key={c} className={`px-2 py-1 rounded-full font-semibold ${cataloged ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'}`} title={cataloged ? 'Resolved by catalog' : 'Resolved by hardcoded fallback — consider adding to catalog'}>
                  {cataloged ? '✓' : '⚠'} {c}: {count.toLocaleString()}
                </span>
              );
            })}
          </div>
          {coverage.productOther > 0 && (
            <div className="mt-2 text-xs text-amber-800">
              <strong>{coverage.productOther.toLocaleString()}</strong> transactions resolved to product <strong>"Other"</strong>. Add plan-code or policy-prefix rules below to classify these.
            </div>
          )}
        </div>
      )}

      {/* Carriers list */}
      <div className="space-y-2">
        {catalog.length === 0 && (
          <div className="text-sm text-slate-500 italic p-3 border border-dashed border-slate-200 rounded-lg text-center">No carriers in the catalog. Hardcoded fallback rules still apply for known carriers. Click <strong>Restore defaults</strong> to repopulate.</div>
        )}
        {catalog.map(c => (
          <div key={c.id} className={`border rounded-lg p-3 ${c.active ? 'border-slate-200 bg-white' : 'border-slate-200 bg-slate-50 opacity-60'}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => toggleActive(c.id)} className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${c.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`} title="Click to toggle active">
                    {c.active ? '● Active' : '○ Inactive'}
                  </button>
                  <span className="font-semibold text-slate-900">{c.name}</span>
                  <span className="text-[10px] text-slate-500">{(c.products || []).length} product{(c.products || []).length === 1 ? '' : 's'}</span>
                </div>
                <div className="text-xs text-slate-600 mt-0.5">
                  Matches:
                  {(c.companyMatchers || []).length > 0 && <span className="ml-1">company contains <strong>{c.companyMatchers.join(', ')}</strong></span>}
                  {(c.mgaMatchers || []).length > 0 && <span className="ml-2">MGA contains <strong>{c.mgaMatchers.join(', ')}</strong></span>}
                </div>
                {(c.products || []).length > 0 && (
                  <div className="text-xs text-slate-700 mt-1">
                    {(c.products || []).map(p => `${p.productType}${p.planCodePrefixes?.length ? ` (plan ${p.planCodePrefixes.join('/')})` : ''}${p.policyPrefixes?.length ? ` (policy ${p.policyPrefixes.join('/')})` : ''}`).join(' · ')}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setEditing(c)} className="text-xs text-slate-700 hover:text-slate-900 underline">Edit</button>
                <button onClick={() => remove(c.id)} className="text-xs text-red-600 hover:text-red-800 underline">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <CarrierEditorModal
          initial={editing.__new ? null : editing}
          onCancel={() => setEditing(null)}
          onSave={upsert}
        />
      )}
    </div>
  );
}

function CarrierEditorModal({ initial, onCancel, onSave }) {
  const blank = {
    id: `carrier_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    name: '',
    active: true,
    companyMatchers: [],
    mgaMatchers: [],
    notes: '',
    products: [],
  };
  const [r, setR] = useState(() => initial ? JSON.parse(JSON.stringify(initial)) : blank);
  const set = (patch) => setR(prev => ({ ...prev, ...patch }));
  // Matcher fields are comma/newline separated free text in the UI, but
  // stored as arrays — split on commit so editing stays simple.
  const matcherStr = (arr) => (arr || []).join(', ');
  const parseMatchers = (s) => String(s || '').split(/[,\n]/).map(x => x.trim()).filter(Boolean);

  const addProduct = () => setR(prev => ({
    ...prev,
    products: [...(prev.products || []), { id: `p_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`, name: '', productType: 'Other', planCodePrefixes: [], policyPrefixes: [], commissionPct: 0, advanceMonths: 0, chargebackPeriodDays: 0 }],
  }));
  const updateProduct = (i, patch) => setR(prev => ({ ...prev, products: prev.products.map((p, j) => j === i ? { ...p, ...patch } : p) }));
  const removeProduct = (i) => setR(prev => ({ ...prev, products: prev.products.filter((_, j) => j !== i) }));

  const trySave = () => {
    if (!r.name.trim()) { alert('Carrier name is required.'); return; }
    onSave({ ...r, name: r.name.trim() });
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onCancel}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl p-5 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg">{initial ? 'Edit carrier' : 'New carrier'}</h3>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-700"><X size={20}/></button>
        </div>

        {/* Identity */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <label className="block md:col-span-2">
            <span className="text-xs text-slate-500">Carrier name</span>
            <input value={r.name} onChange={e => set({ name: e.target.value })} placeholder="Mutual of Omaha" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
          </label>
          <label className="inline-flex items-center gap-2 text-sm mt-5">
            <input type="checkbox" checked={r.active} onChange={e => set({ active: e.target.checked })} className="rounded"/>
            Active
          </label>
        </div>

        {/* Matchers */}
        <div className="border border-slate-200 rounded-lg p-3 space-y-2">
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Detection — how do we know a transaction belongs to this carrier?</div>
          <div className="text-[11px] text-slate-500">Case-insensitive substring matches. Multiple values separated by commas. Either company OR MGA matching is enough.</div>
          <label className="block">
            <span className="text-xs text-slate-500">Company contains</span>
            <input value={matcherStr(r.companyMatchers)} onChange={e => set({ companyMatchers: parseMatchers(e.target.value) })} placeholder="UNITED, MUTUAL" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">MGA / agency contains</span>
            <input value={matcherStr(r.mgaMatchers)} onChange={e => set({ mgaMatchers: parseMatchers(e.target.value) })} placeholder="ETERNA, SENIOR ADVISOR" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
          </label>
        </div>

        {/* Products */}
        <div className="border border-slate-200 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Products</div>
            <button onClick={addProduct} className="text-xs text-slate-700 hover:text-slate-900 underline">+ Add product</button>
          </div>
          <div className="text-[11px] text-slate-500">Each product maps plan codes or policy number prefixes to a product type. Plan code prefix takes priority — fall back to policy prefix if plan code is blank.</div>
          {(r.products || []).length === 0 && (
            <div className="text-xs text-slate-500 italic">No product mappings yet — transactions for this carrier will resolve to product "Other".</div>
          )}
          {(r.products || []).map((p, i) => (
            <div key={i} className="border-t border-slate-100 pt-2 grid grid-cols-12 gap-2 items-center text-sm">
              <label className="col-span-3">
                <span className="text-[10px] text-slate-500">Name (display)</span>
                <input value={p.name} onChange={e => updateProduct(i, { name: e.target.value })} placeholder="IUL — Income Advantage" className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs"/>
              </label>
              <label className="col-span-2">
                <span className="text-[10px] text-slate-500">Product type</span>
                <select value={p.productType} onChange={e => updateProduct(i, { productType: e.target.value })} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-xs">
                  {PRODUCT_TYPES_FULL.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label className="col-span-2">
                <span className="text-[10px] text-slate-500">Plan code prefixes</span>
                <input value={(p.planCodePrefixes || []).join(', ')} onChange={e => updateProduct(i, { planCodePrefixes: parseMatchers(e.target.value) })} placeholder="FEPL, FEPG" className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs font-mono"/>
              </label>
              <label className="col-span-2">
                <span className="text-[10px] text-slate-500">Policy # prefixes</span>
                <input value={(p.policyPrefixes || []).join(', ')} onChange={e => updateProduct(i, { policyPrefixes: parseMatchers(e.target.value) })} placeholder="IULA, FEXB" className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs font-mono"/>
              </label>
              <label className="col-span-1">
                <span className="text-[10px] text-slate-500">Comm %</span>
                <input type="number" min="0" step="0.5" value={p.commissionPct || 0} onChange={e => updateProduct(i, { commissionPct: Number(e.target.value) || 0 })} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs"/>
              </label>
              <label className="col-span-1">
                <span className="text-[10px] text-slate-500">Adv mo</span>
                <input type="number" min="0" value={p.advanceMonths || 0} onChange={e => updateProduct(i, { advanceMonths: Number(e.target.value) || 0 })} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs"/>
              </label>
              <div className="col-span-1 flex items-end gap-1 mt-3">
                <input type="number" min="0" value={p.chargebackPeriodDays || 0} onChange={e => updateProduct(i, { chargebackPeriodDays: Number(e.target.value) || 0 })} title="Chargeback period (days)" className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs"/>
                <button onClick={() => removeProduct(i)} className="text-xs text-red-600 hover:text-red-800">×</button>
              </div>
            </div>
          ))}
        </div>

        <label className="block">
          <span className="text-xs text-slate-500">Notes (optional)</span>
          <textarea value={r.notes || ''} onChange={e => set({ notes: e.target.value })} rows={2} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
        </label>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
          <button onClick={onCancel} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button onClick={trySave} disabled={!r.name.trim()} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold">{initial ? 'Save changes' : 'Create carrier'}</button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// CommissionRulesPanel — Phase 2 of the multi-tenant SaaS refactor.
// Owners create/edit/delete payout rules. Each rule has matchers (carrier,
// product, agent), splits (role + target + percent), basis, advance, and
// chargeback behavior. The default rule (auto-seeded) reproduces today's
// "100% to producer of record" math so adopting custom rules is opt-in.
//
// PHASE 2 SCOPE — what's done here:
//   ✓ Rule storage, defaults, list / add / edit / delete UI
//   ✓ Test panel: pick a real transaction, see which rule matches and what
//     splits the engine would produce
//   ✓ Pure helpers (findApplicableRule, applyRule) ready for downstream
// PHASE 2 EXPLICITLY DEFERRED (so today's display math stays untouched):
//   ✗ Wiring the rule output into Dashboard / Teams / Agents aggregations.
//     A later phase replaces those aggregations with rule-driven ones,
//     enabled by an explicit "Apply rules to displays" toggle so owners
//     can verify their rule library before flipping the switch.
// ──────────────────────────────────────────────────────────────────────
function CommissionRulesPanel({ rules = [], setRules, transactions = [], producers = {}, getCarrier, getProduct, showToast = () => {} }) {
  const [editing, setEditing] = useState(null); // null | rule | { __new: true }
  const [testTxId, setTestTxId] = useState('');

  const carrierOptions = React.useMemo(() => {
    const s = new Set();
    transactions.forEach(t => { const c = getCarrier ? getCarrier(t) : t.company; if (c) s.add(c); });
    return Array.from(s).sort();
  }, [transactions, getCarrier]);
  const productOptions = ['IUL', 'Final Expense', 'Term', 'Other'];
  const agentOptions = React.useMemo(() => {
    const m = {};
    Object.entries(producers || {}).forEach(([pn, p]) => { if (pn) m[pn] = p?.name || pn; });
    transactions.forEach(t => { if (t.prodNum && !m[t.prodNum]) m[t.prodNum] = getProducerName(t.prodNum, producers); });
    return Object.entries(m).map(([prodNum, name]) => ({ prodNum, name })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [producers, transactions]);

  const upsert = (rule) => {
    const isNew = !rules.some(r => r.id === rule.id);
    if (isNew) setRules(prev => [...prev, rule]);
    else setRules(prev => prev.map(r => r.id === rule.id ? rule : r));
    showToast(isNew ? 'Rule added' : 'Rule saved');
    setEditing(null);
  };
  const remove = async (id) => {
    if (!(await hqConfirm({
      title: 'Delete this rule?',
      body: 'This cannot be undone.',
      confirmLabel: 'Delete rule', danger: true,
    }))) return;
    setRules(prev => prev.filter(r => r.id !== id));
    showToast('Rule deleted');
  };
  const toggleActive = (id) => {
    setRules(prev => prev.map(r => r.id === id ? { ...r, active: !r.active } : r));
  };
  const seedDefault = () => {
    if (rules.some(r => r.id === 'default-100-self')) { showToast('Default rule already exists'); return; }
    setRules(prev => [...prev, buildDefaultCommissionRule()]);
    showToast('Default rule restored');
  };

  const sortedRules = [...rules].sort((a, b) => (b.priority || 0) - (a.priority || 0) || a.name.localeCompare(b.name));

  // Test panel: which rule applies to this transaction, what splits are produced
  const testTx = transactions.find(t => t.id === testTxId);
  const testRule = testTx ? findApplicableRule(testTx, rules, getCarrier, getProduct) : null;
  const testSplits = testTx && testRule ? applyRule(testTx, testRule) : [];

  const summarizeSplits = (rule) => {
    const total = sumSplitPercent(rule.splits);
    const parts = (rule.splits || []).slice(0, 4).map(s => {
      const role = COMMISSION_SPLIT_ROLES.find(r => r.id === s.role)?.label.split(' ')[0] || s.role;
      return `${role} ${s.percent}%`;
    });
    return parts.join(' · ') + (rule.splits.length > 4 ? ` …(+${rule.splits.length - 4} more)` : '') + (total !== 100 ? ` (total ${total}%)` : '');
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="font-bold">Commission Rules Engine</h3>
          <div className="text-xs text-slate-500 mt-0.5">Configure payout splits by carrier / product / agent. The default rule keeps today's math (100% to producer) so this is opt-in.</div>
        </div>
        <div className="flex items-center gap-2">
          {rules.length === 0 && (
            <button onClick={seedDefault} className="text-xs text-slate-600 hover:text-slate-900 underline">Restore default rule</button>
          )}
          <button onClick={() => setEditing({ __new: true })} className="bg-slate-900 hover:bg-slate-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">+ New rule</button>
        </div>
      </div>

      {/* Phase 2 status banner */}
      <div className="rounded-lg p-2.5 mb-3 text-xs bg-amber-50 border border-amber-200 text-amber-900">
        <strong>Phase 2 status:</strong> rules can be created and tested here. Display math (Dashboard / Teams / Agents) still uses the legacy "100% to producer" path until a later phase explicitly switches it. This means you can build your rule library safely without disturbing current payouts.
      </div>

      {/* List */}
      <div className="space-y-2">
        {sortedRules.length === 0 && (
          <div className="text-sm text-slate-500 italic p-3 border border-dashed border-slate-200 rounded-lg text-center">No rules yet. Click <strong>Restore default rule</strong> to seed the "100% to producer" rule that matches today's math.</div>
        )}
        {sortedRules.map(r => (
          <div key={r.id} className={`border rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap ${r.active ? 'border-slate-200 bg-white' : 'border-slate-200 bg-slate-50 opacity-60'}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => toggleActive(r.id)} className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${r.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`} title="Click to toggle active">
                  {r.active ? '● Active' : '○ Inactive'}
                </button>
                <span className="font-semibold text-slate-900 truncate">{r.name}</span>
                <span className="text-[10px] text-slate-500">Priority {r.priority || 0}</span>
              </div>
              <div className="text-xs text-slate-600 mt-0.5 truncate">
                {(r.matchers?.carriers || []).length > 0 && <span className="mr-2">Carriers: <strong>{r.matchers.carriers.join(', ')}</strong></span>}
                {(r.matchers?.products || []).length > 0 && <span className="mr-2">Products: <strong>{r.matchers.products.join(', ')}</strong></span>}
                {(r.matchers?.agentProdNums || []).length > 0 && <span className="mr-2">Agents: <strong>{r.matchers.agentProdNums.length}</strong></span>}
                {((r.matchers?.carriers || []).length + (r.matchers?.products || []).length + (r.matchers?.agentProdNums || []).length) === 0 && <span className="italic text-slate-500">Matches all transactions</span>}
              </div>
              <div className="text-xs text-slate-700 mt-0.5">{summarizeSplits(r)}</div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setEditing(r)} className="text-xs text-slate-700 hover:text-slate-900 underline">Edit</button>
              <button onClick={() => remove(r.id)} className="text-xs text-red-600 hover:text-red-800 underline">Delete</button>
            </div>
          </div>
        ))}
      </div>

      {/* Test panel */}
      {transactions.length > 0 && (
        <div className="mt-4 border-t border-slate-200 pt-4">
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Test a rule against a transaction</div>
          <select value={testTxId} onChange={e => setTestTxId(e.target.value)} className="w-full md:w-auto border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white">
            <option value="">— Pick a transaction —</option>
            {transactions.slice(0, 200).map(t => (
              <option key={t.id} value={t.id}>
                {(getCarrier && getCarrier(t)) || t.company || '—'} · {t.policy || '?'} · {t.insured || '?'} · {fmt(Number(t.netToAccount) || 0)}
              </option>
            ))}
          </select>
          {testTx && (
            <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs">
              <div className="font-mono text-slate-600 mb-1">
                carrier=<strong>{getCarrier ? getCarrier(testTx) : testTx.company}</strong> · product=<strong>{getProduct ? getProduct(testTx) : '—'}</strong> · agent=<strong>{getProducerName(testTx.prodNum, producers)} ({testTx.prodNum})</strong> · net=<strong>{fmt(Number(testTx.netToAccount) || 0)}</strong>
              </div>
              {testRule ? (
                <>
                  <div className="text-slate-700 mt-2">Matched rule: <strong>{testRule.name}</strong> <span className="text-slate-500">(priority {testRule.priority || 0})</span></div>
                  <div className="text-slate-700">Basis: <strong>{COMMISSION_BASIS_OPTIONS.find(o => o.id === testRule.basis)?.label || testRule.basis}</strong> = <strong>{fmt(Number(testTx[testRule.basis]) || 0)}</strong></div>
                  <div className="mt-2 space-y-0.5">
                    {testSplits.map((s, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span>{COMMISSION_SPLIT_ROLES.find(r => r.id === s.role)?.label || s.role} → <strong>{s.target || '?'}</strong></span>
                        <span className="tabular-nums">{s.percent}% = <strong>{fmt(s.amount)}</strong></span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-amber-700">No active rule matches this transaction.</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Edit / new modal */}
      {editing && (
        <CommissionRuleModal
          initial={editing.__new ? null : editing}
          carrierOptions={carrierOptions}
          productOptions={productOptions}
          agentOptions={agentOptions}
          onCancel={() => setEditing(null)}
          onSave={upsert}
        />
      )}
    </div>
  );
}

function CommissionRuleModal({ initial, carrierOptions, productOptions, agentOptions, onCancel, onSave }) {
  const blank = {
    id: `rule_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    name: '',
    active: true,
    priority: 10,
    matchers: { carriers: [], products: [], agentProdNums: [] },
    splits: [{ role: 'agent', target: 'self', targetParam: '', percent: 100, note: '' }],
    basis: 'netToAccount',
    advanceMonths: 0,
    chargebackPeriodDays: 0,
    chargebackTreatment: 'reverse_split',
    notes: '',
    createdAt: new Date().toISOString(),
  };
  const [r, setR] = useState(() => initial ? JSON.parse(JSON.stringify(initial)) : blank);
  const set = (patch) => setR(prev => ({ ...prev, ...patch }));
  const setMatchers = (patch) => setR(prev => ({ ...prev, matchers: { ...(prev.matchers || {}), ...patch } }));

  const toggle = (arr, val) => arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val];
  const splitTotal = sumSplitPercent(r.splits);
  const splitOver = splitTotal > 100.0001;

  const updateSplit = (i, patch) => setR(prev => ({ ...prev, splits: prev.splits.map((s, j) => j === i ? { ...s, ...patch } : s) }));
  const addSplit = () => setR(prev => ({ ...prev, splits: [...prev.splits, { role: 'agency', target: 'agency', targetParam: '', percent: 0, note: '' }] }));
  const removeSplit = (i) => setR(prev => ({ ...prev, splits: prev.splits.filter((_, j) => j !== i) }));

  const trySave = () => {
    if (!r.name.trim()) { alert('Give this rule a name.'); return; }
    if (r.splits.length === 0) { alert('Add at least one split.'); return; }
    onSave({ ...r, name: r.name.trim() });
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onCancel}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl p-5 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg">{initial ? 'Edit commission rule' : 'New commission rule'}</h3>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-700"><X size={20}/></button>
        </div>

        {/* Identity */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <label className="block md:col-span-2">
            <span className="text-xs text-slate-500">Rule name</span>
            <input value={r.name} onChange={e => set({ name: e.target.value })} placeholder='e.g. "MOO IUL — Standard 65/10/15/10"' className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Priority (higher wins)</span>
            <input type="number" value={r.priority} onChange={e => set({ priority: Number(e.target.value) || 0 })} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={r.active} onChange={e => set({ active: e.target.checked })} className="rounded"/>
            Active
          </label>
        </div>

        {/* Matchers */}
        <div className="border border-slate-200 rounded-lg p-3 space-y-2">
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Matchers — when does this rule fire?</div>
          <div className="text-[11px] text-slate-500">Leave a row empty to mean "any". The rule fires when every populated row matches.</div>

          <div>
            <div className="text-xs font-semibold text-slate-600 mb-1">Carriers</div>
            <div className="flex flex-wrap gap-1">
              {carrierOptions.length === 0 ? <span className="text-xs italic text-slate-400">No transactions to derive carriers from yet</span> : carrierOptions.map(c => (
                <button key={c} onClick={() => setMatchers({ carriers: toggle(r.matchers.carriers || [], c) })} className={`px-2 py-1 text-xs font-medium rounded-lg border ${(r.matchers.carriers || []).includes(c) ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}>{c}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-600 mb-1">Products</div>
            <div className="flex flex-wrap gap-1">
              {productOptions.map(p => (
                <button key={p} onClick={() => setMatchers({ products: toggle(r.matchers.products || [], p) })} className={`px-2 py-1 text-xs font-medium rounded-lg border ${(r.matchers.products || []).includes(p) ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}>{p}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-600 mb-1">Specific agents (optional — leave empty for any)</div>
            <select multiple value={r.matchers.agentProdNums || []} onChange={e => setMatchers({ agentProdNums: Array.from(e.target.selectedOptions).map(o => o.value) })} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white" size={Math.min(5, agentOptions.length || 1)}>
              {agentOptions.map(a => <option key={a.prodNum} value={a.prodNum}>{a.name} ({a.prodNum})</option>)}
            </select>
          </div>
        </div>

        {/* Splits */}
        <div className="border border-slate-200 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Splits</div>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-bold ${splitOver ? 'text-red-700' : splitTotal === 100 ? 'text-emerald-700' : 'text-slate-500'}`}>Total {splitTotal}%</span>
              <button onClick={addSplit} className="text-xs text-slate-700 hover:text-slate-900 underline">+ Add split</button>
            </div>
          </div>
          {splitOver && <div className="text-xs text-red-700">Splits sum above 100% — adjust before saving.</div>}
          {(r.splits || []).map((s, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center text-sm border-t border-slate-100 pt-2">
              <select value={s.role} onChange={e => updateSplit(i, { role: e.target.value })} className="col-span-3 border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-xs">
                {COMMISSION_SPLIT_ROLES.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
              <select value={s.target} onChange={e => updateSplit(i, { target: e.target.value, targetParam: '' })} className="col-span-3 border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-xs">
                {COMMISSION_SPLIT_TARGETS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
              {s.target === 'specific_agent' ? (
                <select value={s.targetParam || ''} onChange={e => updateSplit(i, { targetParam: e.target.value })} className="col-span-3 border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-xs">
                  <option value="">— Pick agent —</option>
                  {agentOptions.map(a => <option key={a.prodNum} value={a.prodNum}>{a.name}</option>)}
                </select>
              ) : (
                <input value={s.targetParam || ''} onChange={e => updateSplit(i, { targetParam: e.target.value })} placeholder={s.target === 'partner' ? 'Partner id (free text)' : '—'} disabled={s.target === 'self' || s.target === 'agency' || s.target === 'team_leader'} className="col-span-3 border border-slate-200 rounded-lg px-2 py-1.5 text-xs disabled:bg-slate-100"/>
              )}
              <div className="col-span-2 flex items-center gap-1">
                <input type="number" min="0" max="100" step="0.5" value={s.percent} onChange={e => updateSplit(i, { percent: Number(e.target.value) || 0 })} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs"/>
                <span className="text-xs text-slate-500">%</span>
              </div>
              <button onClick={() => removeSplit(i)} className="col-span-1 text-xs text-red-600 hover:underline">Remove</button>
            </div>
          ))}
        </div>

        {/* Math behavior */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <label className="block">
            <span className="text-xs text-slate-500">Basis (what splits multiply against)</span>
            <select value={r.basis} onChange={e => set({ basis: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
              {COMMISSION_BASIS_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Chargeback treatment</span>
            <select value={r.chargebackTreatment} onChange={e => set({ chargebackTreatment: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
              {CHARGEBACK_TREATMENT_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Advance months (0 = paid as earned)</span>
            <input type="number" min="0" value={r.advanceMonths} onChange={e => set({ advanceMonths: Number(e.target.value) || 0 })} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Chargeback period (days, 0 = uses CHARGEBACK_TYPES list)</span>
            <input type="number" min="0" value={r.chargebackPeriodDays} onChange={e => set({ chargebackPeriodDays: Number(e.target.value) || 0 })} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
          </label>
        </div>

        <label className="block">
          <span className="text-xs text-slate-500">Notes (optional)</span>
          <textarea value={r.notes || ''} onChange={e => set({ notes: e.target.value })} rows={2} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
        </label>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
          <button onClick={onCancel} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button onClick={trySave} disabled={splitOver || !r.name.trim()} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold">{initial ? 'Save changes' : 'Create rule'}</button>
        </div>
      </div>
    </div>
  );
}

// Admin-only customers roster. Lists every row in public.users so the
// SaaS operator can see who's signed up, who's admin, and (eventually)
// drill into a tenant's CRM. Phase 1 is read-only — promotion / demotion
// runs through SQL until a server-side /api/admin/grant lands.
// Admin operator console. Live roster of every signed-up tenant with
// the data the operator actually needs:
//   - role (admin / client)
//   - subscription status (—, active, past_due, canceled, …)
//   - signup date
//   - "has data?" indicator (does user_state have any rows for them)
// Plus inline actions: promote, demote, delete. Each action hits a
// server endpoint under /api/admin/* that re-verifies the caller is
// an admin (the browser-side gate hiding this view is convenience,
// not security — the server is the source of truth).
function AdminCustomersView({ currentUser, showToast = () => {}, onViewAs = () => {} }) {
  const [rows, setRows] = React.useState(null); // null = loading, [] = empty
  const [error, setError] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [roleFilter, setRoleFilter] = React.useState('all'); // all|admin|client
  const [statusFilter, setStatusFilter] = React.useState('all'); // all|active|past_due|none
  const [refreshTick, setRefreshTick] = React.useState(0);
  const [pendingUserId, setPendingUserId] = React.useState(null); // userId currently being mutated
  const [confirmDelete, setConfirmDelete] = React.useState(null); // user obj awaiting "yes really delete"

  React.useEffect(() => {
    let cancelled = false;
    setError('');
    setRows(null);
    (async () => {
      try {
        const { supabase } = await import('./lib/supabase');

        // 1. Fetch every user row.
        const { data: users, error: usersErr } = await supabase
          .from('users')
          .select('id, email, role, created_at, updated_at')
          .order('created_at', { ascending: false });
        if (cancelled) return;
        if (usersErr) {
          setError(usersErr.message || 'Could not load users.');
          setRows([]);
          return;
        }

        // 2. Fetch every subscription row — joined client-side by user_id
        // because Supabase JS embeds need an explicit FK relation and
        // the table layout we have doesn't expose it. Two parallel
        // queries is cheaper than one with embedded selects anyway.
        const { data: subs } = await supabase
          .from('subscriptions')
          .select('user_id, status, plan, current_period_end');
        const subByUser = {};
        (subs || []).forEach(s => { subByUser[s.user_id] = s; });

        // 3. Cloud-data presence. SELECT-with-admin-policy in
        // migration 004 lets us see every row, but we just need a
        // per-user count — small payload via aggregation. Note: this
        // returns ONE row per user_id with the key count, not the keys
        // themselves.
        const { data: stateRows } = await supabase
          .from('user_state')
          .select('user_id');
        const stateCounts = {};
        (stateRows || []).forEach(r => { stateCounts[r.user_id] = (stateCounts[r.user_id] || 0) + 1; });

        const merged = (users || []).map(u => ({
          ...u,
          subStatus: subByUser[u.id]?.status || null,
          subPlan:   subByUser[u.id]?.plan   || null,
          cloudKeys: stateCounts[u.id] || 0,
        }));
        setRows(merged);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || String(err));
          setRows([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [refreshTick]);

  // Action helper. Hits /api/admin/<endpoint> with the user's JWT and
  // surfaces toast on success/error. Refreshes the list on success so
  // the row reflects the new state immediately.
  const callAdmin = React.useCallback(async (endpoint, body, successMsg) => {
    setPendingUserId(body.userId);
    try {
      const { supabase } = await import('./lib/supabase');
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        showToast('You need to be signed in.', 'error');
        return;
      }
      const res = await fetch(`/api/admin/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(json.error || `HTTP ${res.status}`, 'error');
        return false;
      }
      showToast(successMsg || 'Done', 'success');
      setRefreshTick(t => t + 1);
      return true;
    } catch (err) {
      showToast(err.message || String(err), 'error');
      return false;
    } finally {
      setPendingUserId(null);
    }
  }, [showToast]);

  const lc = search.trim().toLowerCase();
  const filtered = (rows || []).filter(r => {
    if (lc && !(r.email || '').toLowerCase().includes(lc)) return false;
    if (roleFilter !== 'all' && r.role !== roleFilter) return false;
    if (statusFilter === 'active'   && r.subStatus !== 'active' && r.subStatus !== 'trialing') return false;
    if (statusFilter === 'past_due' && r.subStatus !== 'past_due') return false;
    if (statusFilter === 'none'     && r.subStatus) return false;
    return true;
  });
  const totalUsers   = rows?.length ?? 0;
  const adminCount   = (rows || []).filter(r => r.role === 'admin').length;
  const clientCount  = (rows || []).filter(r => r.role === 'client').length;
  const payingCount  = (rows || []).filter(r => r.subStatus === 'active' || r.subStatus === 'trialing').length;
  const withDataCount = (rows || []).filter(r => r.cloudKeys > 0).length;

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-violet-900 to-violet-700 text-white rounded-2xl p-5">
        <div className="text-xs text-violet-200 uppercase tracking-wider font-semibold">Admin · Operator console</div>
        <h2 className="text-2xl font-bold mt-1">Customers</h2>
        <p className="text-sm text-violet-100 mt-1">Signed in as <strong>{currentUser?.name}</strong>. Server endpoints re-verify your admin role on every action.</p>
      </div>

      {error ? (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5">
          <div className="font-bold text-amber-900">Couldn't load the users table</div>
          <p className="text-sm text-amber-800 mt-1">{error}</p>
          <p className="text-sm text-amber-800 mt-2">
            If you haven't yet, apply <code className="bg-amber-100 px-1 rounded">supabase_migrations/004_users_roles.sql</code> in your Supabase SQL editor. The migration is idempotent.
          </p>
        </div>
      ) : null}

      {/* Top-line stats. Five tiles — total / admins / clients / paying / has-data. */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Total</div>
          <div className="text-3xl font-bold mt-1">{rows == null ? '—' : totalUsers}</div>
        </div>
        <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
          <div className="text-xs text-violet-700 font-semibold uppercase tracking-wide">Admins</div>
          <div className="text-3xl font-bold mt-1 text-violet-900">{rows == null ? '—' : adminCount}</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <div className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">Clients</div>
          <div className="text-3xl font-bold mt-1 text-emerald-900">{rows == null ? '—' : clientCount}</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="text-xs text-blue-700 font-semibold uppercase tracking-wide">Paying</div>
          <div className="text-3xl font-bold mt-1 text-blue-900">{rows == null ? '—' : payingCount}</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="text-xs text-amber-700 font-semibold uppercase tracking-wide">Has data</div>
          <div className="text-3xl font-bold mt-1 text-amber-900">{rows == null ? '—' : withDataCount}</div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold">All users</h3>
            <p className="text-xs text-slate-500 mt-0.5">{filtered.length} of {totalUsers} shown.</p>
          </div>
          <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-2 text-sm bg-white">
            <option value="all">All roles</option>
            <option value="admin">Admins</option>
            <option value="client">Clients</option>
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-2 text-sm bg-white">
            <option value="all">Any subscription</option>
            <option value="active">Active / trialing</option>
            <option value="past_due">Past due</option>
            <option value="none">No subscription</option>
          </select>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Filter by email…"
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
          />
          <button
            onClick={() => setRefreshTick(t => t + 1)}
            className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-2 rounded-lg"
          >
            Refresh
          </button>
        </div>
        {rows == null ? (
          <div className="p-8 text-center text-slate-400 text-sm">Loading users…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">{totalUsers === 0 ? 'No users in the table yet.' : 'No matches for that filter.'}</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map(u => {
              const isSelf = u.id === currentUser?.prodNum;
              const isPending = pendingUserId === u.id;
              return (
                <div key={u.id} className="p-3 flex items-center gap-3 flex-wrap hover:bg-slate-50">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm flex items-center gap-2">
                      {u.email || <span className="text-slate-400 italic">(no email)</span>}
                      {isSelf && <span className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">you</span>}
                    </div>
                    <div className="text-xs text-slate-500 font-mono mt-0.5 truncate">{u.id}</div>
                  </div>

                  <div className="text-xs text-slate-400 text-right hidden md:block">
                    <div>Joined</div>
                    <div className="font-semibold text-slate-700">{u.created_at ? fmtDateUS(u.created_at) : '—'}</div>
                  </div>

                  <div className="text-xs text-right">
                    <div className="text-slate-400">Subscription</div>
                    <div className={`font-semibold ${u.subStatus === 'active' || u.subStatus === 'trialing' ? 'text-emerald-700' : u.subStatus === 'past_due' ? 'text-amber-700' : u.subStatus === 'canceled' ? 'text-rose-700' : 'text-slate-400'}`}>
                      {u.subStatus || '—'}{u.subPlan ? ` · ${u.subPlan}` : ''}
                    </div>
                  </div>

                  <div className="text-xs text-right hidden sm:block">
                    <div className="text-slate-400">Cloud keys</div>
                    <div className={`font-semibold ${u.cloudKeys > 0 ? 'text-amber-700' : 'text-slate-400'}`}>{u.cloudKeys}</div>
                  </div>

                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${u.role === 'admin' ? 'bg-violet-100 text-violet-700' : 'bg-emerald-100 text-emerald-700'}`}>
                    {u.role}
                  </span>

                  {/* View-as: open this customer's CRM read-only.
                      Disabled for the caller (no point viewing as
                      yourself) and while a mutation is in flight. */}
                  <button
                    disabled={isPending || isSelf}
                    onClick={() => onViewAs(u)}
                    title={isSelf ? "Can't view as yourself" : `Open ${u.email}'s CRM in read-only mode`}
                    className="text-xs bg-violet-50 hover:bg-violet-100 text-violet-800 border border-violet-200 disabled:opacity-40 disabled:cursor-not-allowed px-2.5 py-1 rounded font-semibold"
                  >
                    View as
                  </button>

                  {/* Action buttons. Disabled while a mutation is
                      in flight on this row OR when the action would
                      target the caller themselves (server enforces
                      the same rules — these are UX guards). */}
                  {u.role === 'client' ? (
                    <button
                      disabled={isPending}
                      onClick={() => callAdmin('set-role', { userId: u.id, role: 'admin' }, `Promoted ${u.email} to admin`)}
                      className="text-xs bg-violet-600 hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-2.5 py-1 rounded font-semibold"
                    >
                      {isPending ? '…' : 'Promote'}
                    </button>
                  ) : (
                    <button
                      disabled={isPending}
                      onClick={() => callAdmin('set-role', { userId: u.id, role: 'client' }, `Demoted ${u.email} to client`)}
                      className="text-xs bg-slate-200 hover:bg-slate-300 disabled:opacity-40 disabled:cursor-not-allowed text-slate-800 px-2.5 py-1 rounded font-semibold"
                    >
                      {isPending ? '…' : 'Demote'}
                    </button>
                  )}

                  <button
                    disabled={isPending || isSelf}
                    onClick={() => setConfirmDelete(u)}
                    title={isSelf ? "Can't delete your own account" : 'Delete user'}
                    className="text-xs bg-white border border-rose-200 hover:bg-rose-50 text-rose-700 disabled:opacity-40 disabled:cursor-not-allowed px-2.5 py-1 rounded font-semibold"
                  >
                    Delete
                  </button>

                  <button
                    onClick={() => {
                      if (typeof navigator !== 'undefined' && navigator.clipboard) {
                        navigator.clipboard.writeText(u.id)
                          .then(() => showToast(`Copied user id for ${u.email || u.id.slice(0, 8)}`))
                          .catch(() => showToast('Browser blocked clipboard — copy manually.', 'amber'));
                      } else {
                        showToast('Clipboard not available in this browser — copy manually.', 'amber');
                      }
                    }}
                    className="text-xs bg-white border border-slate-200 hover:bg-slate-50 px-2 py-1 rounded"
                  >
                    Copy id
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete confirm dialog. Hard-delete is irreversible so we
          force a typed-email confirmation, the same pattern GitHub
          uses for repo deletion. */}
      {confirmDelete && (
        <ConfirmDeleteUserDialog
          user={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={async () => {
            const ok = await callAdmin('delete-user', { userId: confirmDelete.id }, `Deleted ${confirmDelete.email}`);
            if (ok) setConfirmDelete(null);
          }}
        />
      )}
    </div>
  );
}

// Delete confirmation modal. Requires the operator to type the
// target email to enable the destructive button.
function ConfirmDeleteUserDialog({ user, onCancel, onConfirm }) {
  const [typed, setTyped] = React.useState('');
  const armed = typed.trim().toLowerCase() === (user.email || '').toLowerCase();
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5">
        <div className="text-rose-700 font-bold text-lg">Delete this user?</div>
        <p className="text-sm text-slate-600 mt-2">
          This permanently removes <strong>{user.email}</strong> from auth.users, public.users, and every cascade target (orgs, memberships, subscriptions, user_state). The user will lose access immediately and their data cannot be recovered.
        </p>
        <label className="block mt-4">
          <span className="text-xs uppercase tracking-wide font-semibold text-slate-600">Type the email to confirm</span>
          <input
            autoFocus
            value={typed}
            onChange={e => setTyped(e.target.value)}
            placeholder={user.email}
            className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono"
          />
        </label>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-semibold bg-white border border-slate-200 hover:bg-slate-50">Cancel</button>
          <button
            disabled={!armed}
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Delete user
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Connect Discord — one-click join the Commission Command Discord
// server with a private support channel auto-provisioned. OAuth start
// at /api/discord/oauth-start; callback handles the rest.
// ────────────────────────────────────────────────────────────────
function ConnectDiscordCard({ currentUser, showToast = () => {} }) {
  const [link, setLink]       = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy]       = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('discord_links')
      .select('discord_user_id, discord_username, discord_channel_id, connected_at')
      .maybeSingle();
    setLink(data || null);
    setLoading(false);
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  // Surface ?discord=connected / =error after the redirect lands.
  React.useEffect(() => {
    const url = new URL(window.location.href);
    const status = url.searchParams.get('discord');
    if (!status) return;
    if (status === 'connected') {
      showToast('Discord connected — private channel created.');
      refresh();
    } else if (status === 'denied') {
      showToast('Discord connection cancelled.');
    } else if (status === 'error') {
      const reason = url.searchParams.get('reason') || 'unknown';
      showToast(`Discord error: ${reason}`);
    }
    // Strip the query so reloads don't re-toast.
    url.searchParams.delete('discord');
    url.searchParams.delete('reason');
    window.history.replaceState({}, '', url.toString());
  }, [refresh, showToast]);

  async function startOAuth() {
    setBusy(true);
    try {
      const { data: sessionResp } = await supabase.auth.getSession();
      const token = sessionResp?.session?.access_token;
      if (!token) { showToast('Sign in first.'); return; }
      const res = await fetch('/api/discord/oauth-start', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.url) {
        showToast(`Discord error: ${body.error || res.status}`);
        return;
      }
      window.location.href = body.url;
    } finally {
      setBusy(false);
    }
  }

  async function disconnect() {
    if (!(await hqConfirm({
      title: 'Disconnect Discord?',
      body: 'Your private support channel will be archived (we keep the history). You can reconnect anytime.',
      confirmLabel: 'Disconnect', danger: true,
    }))) return;
    setBusy(true);
    const { error } = await supabase.from('discord_links').delete().eq('owner_id', currentUser?.id);
    setBusy(false);
    if (error) { showToast(`Disconnect failed: ${error.message}`); return; }
    setLink(null);
    showToast('Discord disconnected.');
  }

  const channelDeepLink = link?.discord_channel_id
    ? `https://discord.com/channels/${process.env.REACT_APP_DISCORD_GUILD_ID || '@me'}/${link.discord_channel_id}`
    : null;

  return (
    <div className="bg-gradient-to-br from-[#5865F2] to-[#404EED] rounded-xl p-5 text-white shadow">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wider text-indigo-100">Discord Community</div>
          <div className="text-lg font-bold mt-1">
            {loading ? 'Checking connection…' : link ? `Connected as ${link.discord_username || 'Discord user'}` : 'Connect Discord'}
          </div>
          <div className="text-sm text-indigo-100 mt-1 max-w-xl">
            {link
              ? 'Your private support channel is live in the Commission Command server. Drop questions there anytime — the team is responsive within a few hours during US business days.'
              : 'One click joins you to the Commission Command Discord and auto-creates your private support channel. Only you and the team can see it.'}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!loading && !link && (
            <button
              type="button"
              onClick={startOAuth}
              disabled={busy}
              className="bg-white text-[#404EED] hover:bg-indigo-50 disabled:opacity-60 font-bold rounded-lg px-4 py-2 text-sm shadow"
            >
              {busy ? 'Connecting…' : 'Connect Discord'}
            </button>
          )}
          {!loading && link && channelDeepLink && (
            <a
              href={channelDeepLink}
              target="_blank"
              rel="noreferrer"
              className="bg-white text-[#404EED] hover:bg-indigo-50 font-bold rounded-lg px-4 py-2 text-sm shadow"
            >
              Open your channel ↗
            </a>
          )}
          {!loading && link && (
            <button
              type="button"
              onClick={disconnect}
              disabled={busy}
              className="bg-transparent border border-white/40 text-white hover:bg-white/10 disabled:opacity-60 font-semibold rounded-lg px-3 py-2 text-xs"
            >
              Disconnect
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// AI Voice picker — operator-selectable Polly voice used by every
// TwiML <Say> verb (inbound greeting, voicemail prompt, outbound
// reject messages). Backed by GET/POST /api/settings/voice; catalog
// of allowed voices lives server-side in api/_voices.js so adding
// or deprecating a voice doesn't require a UI rebuild.
// ── A2P 10DLC status card ─────────────────────────────────────────
// Surfaces the operator's brand + campaign registration posture:
// what's pending, what's approved, what failed and why, and whether
// they can hit Resubmit. The platform-shared-brand fallback note is
// always visible — operators should understand sends are working
// even while their own brand is in review.
function A2pStatusCard({ currentUser, showToast = () => {} }) {
  const [loading, setLoading]   = React.useState(true);
  const [data, setData]         = React.useState(null);
  const [resubmitting, setResubmitting] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const json = await apiFetch('/api/twilio/a2p-status');
      setData(json);
    } catch (err) {
      showToast(`A2P status load failed: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id, showToast]);

  React.useEffect(() => { load(); }, [load]);

  const resubmit = async () => {
    if (resubmitting) return;
    setResubmitting(true);
    try {
      await apiFetch('/api/twilio/a2p-status', { method: 'POST' });
      showToast('Brand resubmitted. Polling will reflect the new status within 15 minutes.');
      load();
    } catch (err) {
      showToast(`Resubmit failed: ${err.message || err}`);
    } finally {
      setResubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="text-sm text-slate-400">Loading A2P status…</div>
      </div>
    );
  }

  const posture  = data?.posture;
  const business = data?.business;
  const next     = data?.next || {};

  // No subscription yet → don't surface the card at all; nothing to show.
  if (!posture) return null;

  const StatusChip = ({ value }) => {
    const tone = value === 'approved' ? 'emerald'
              : value === 'pending'  ? 'amber'
              : value === 'failed'   ? 'red'
              : value === 'manual'   ? 'indigo'
              : 'slate';
    const label = value || 'not submitted';
    return (
      <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-${tone}-50 text-${tone}-700 border border-${tone}-200`}>{label}</span>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div>
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck size={16} className="text-slate-500"/> SMS verification (A2P 10DLC)
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Federal carriers require every business that sends SMS to register their brand and use case.
            We submitted yours automatically using the business details from onboarding — review typically takes 1–5 business days.
            Your SMS works the whole time via our pre-approved platform brand, so you can start working immediately.
            When your own brand approves, your number migrates over — no action needed from you.
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-md ${posture.sms_ready ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            <span className={`inline-block h-2 w-2 rounded-full ${posture.sms_ready ? 'bg-emerald-500' : 'bg-red-500'}`}/>
            {posture.sms_ready ? 'SMS ready to send' : 'SMS sends blocked'}
          </div>
        </div>
      </div>

      {/* 4-step wizard progress — per audit response §14 Phase 7 OPS
          "No A2P 10DLC onboarding wizard." We don't add a separate
          modal flow because the platform auto-submits during onboarding
          (provision.js); the operator's real need is TRANSPARENCY into
          what step they're on + how long each step takes. The phase
          enum from a2p-status.js next.stage drives the visual state. */}
      <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">A2P 10DLC registration · 4 phases</div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-1.5">
          {[
            {
              key: 'brand_submit',
              label: '1. Brand registered',
              detail: 'EIN + business details submitted for A2P registration',
              done: Boolean(posture.a2p_brand_sid),
              failed: posture.a2p_brand_status === 'failed',
              active: !posture.a2p_brand_sid && next.stage === 'platform_only',
            },
            {
              key: 'brand_approved',
              label: '2. Brand approved',
              detail: 'Carriers approve the brand · 1-5 business days',
              done: posture.a2p_brand_status === 'approved',
              failed: posture.a2p_brand_status === 'failed',
              active: posture.a2p_brand_status === 'pending',
            },
            {
              key: 'campaign_submit',
              label: '3. Campaign submitted',
              detail: 'Use case + sample messages auto-created',
              done: Boolean(posture.a2p_campaign_sid),
              failed: posture.a2p_campaign_status === 'failed',
              active: posture.a2p_brand_status === 'approved' && !posture.a2p_campaign_sid,
            },
            {
              key: 'campaign_approved',
              label: '4. Campaign approved',
              detail: 'SMS sends fire on your own brand · 1-3 days',
              done: posture.a2p_campaign_status === 'approved',
              failed: posture.a2p_campaign_status === 'failed',
              active: posture.a2p_campaign_status === 'pending',
            },
          ].map((s) => {
            const tone =
              s.failed ? 'border-red-300 bg-red-50 text-red-900'
              : s.done   ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
              : s.active ? 'border-amber-300 bg-amber-50 text-amber-900 ring-2 ring-amber-200'
              :            'border-slate-200 bg-white text-slate-500';
            const icon = s.failed ? '✕' : s.done ? '✓' : s.active ? '…' : '';
            return (
              <div key={s.key} className={`border rounded-md p-2 ${tone}`} title={s.detail}>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-bold">{icon}</span>
                  <span className="text-[11px] font-bold">{s.label}</span>
                </div>
                <div className="text-[10px] mt-0.5 opacity-80 leading-snug">{s.detail}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className="bg-slate-50/60 border border-slate-200 rounded-lg p-3 text-xs">
          <div className="flex items-center justify-between mb-1">
            <span className="font-semibold text-slate-700">Brand</span>
            <StatusChip value={posture.a2p_brand_status}/>
          </div>
          <div className="text-slate-500 break-all" title={posture.a2p_brand_sid || ''}>
            {/* Mask the raw Twilio brand UUID — only show the prefix +
                last 4 chars to confirm identity without exposing the
                full ID for enumeration. Title attribute keeps the
                full value available on hover for the operator who
                actually needs it. */}
            {posture.a2p_brand_sid
              ? `${posture.a2p_brand_sid.slice(0, 4)}…${posture.a2p_brand_sid.slice(-4)}`
              : <span className="italic">not submitted</span>}
          </div>
          {posture.a2p_submitted_at && (
            <div className="text-slate-400 mt-0.5">Submitted {new Date(posture.a2p_submitted_at).toLocaleString()}</div>
          )}
        </div>
        <div className="bg-slate-50/60 border border-slate-200 rounded-lg p-3 text-xs">
          <div className="flex items-center justify-between mb-1">
            <span className="font-semibold text-slate-700">Campaign</span>
            <StatusChip value={posture.a2p_campaign_status}/>
          </div>
          <div className="text-slate-500 break-all" title={posture.a2p_campaign_sid || ''}>
            {posture.a2p_campaign_sid
              ? `${posture.a2p_campaign_sid.slice(0, 4)}…${posture.a2p_campaign_sid.slice(-4)}`
              : <span className="italic">created automatically after brand approves</span>}
          </div>
          {posture.a2p_campaign_submitted_at && (
            <div className="text-slate-400 mt-0.5">Submitted {new Date(posture.a2p_campaign_submitted_at).toLocaleString()}</div>
          )}
        </div>
      </div>

      <div className="mt-3 text-xs text-slate-600">
        <span className="font-semibold text-slate-900">{next.label}</span>
      </div>

      {posture.a2p_last_error && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-xs">
          <div className="font-semibold text-red-800 mb-1">Last reported issue</div>
          <div className="text-red-700 break-all">{posture.a2p_last_error}</div>
          <div className="text-red-600 mt-2">
            Common fixes: confirm the business EIN exactly matches IRS records (no transposed digits, hyphen formatted as <code className="bg-red-100 px-1 rounded">12-3456789</code>), the business address matches the registered address on the EIN filing, and the website is a working public URL.
          </div>
        </div>
      )}

      {posture.a2p_last_polled_at && (
        <div className="mt-3 text-[11px] text-slate-400">
          Last polled: {new Date(posture.a2p_last_polled_at).toLocaleString()} · We poll every 15 minutes automatically.
        </div>
      )}

      {data?.can_resubmit && (
        <div className="mt-3">
          <button
            onClick={resubmit}
            disabled={resubmitting}
            className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            {resubmitting ? 'Resubmitting…' : 'Resubmit brand registration'}
          </button>
        </div>
      )}

      {/* Operator's submitted business details — surface so they can
          sanity-check what's in the registration on file. */}
      {business && (
        <details className="mt-4 text-xs">
          <summary className="cursor-pointer text-slate-500 hover:text-slate-900 font-medium">View business details on file</summary>
          <div className="mt-2 bg-slate-50/60 border border-slate-200 rounded-lg p-3 space-y-1 text-slate-700">
            <div><span className="text-slate-500">Legal name:</span> {business.business_name || <span className="italic text-amber-700">missing</span>}</div>
            <div><span className="text-slate-500">EIN:</span> <span className="font-mono">{business.business_ein || <span className="italic text-amber-700 font-sans">missing</span>}</span></div>
            <div><span className="text-slate-500">Address:</span> {business.business_address || <span className="italic text-amber-700">missing</span>}</div>
            <div><span className="text-slate-500">Website:</span> {business.business_website || <span className="italic text-amber-700">missing</span>}</div>
            <div className="text-slate-400 mt-1">To change any of these, update your business details in onboarding.</div>
          </div>
        </details>
      )}
    </div>
  );
}

// Voice device can fail to register (mic permission denied, ad-blocker
// killing Twilio's WebRTC, no microphone hardware in browser). Show
// the standard "connecting" message for 8s then swap to a graceful
// fallback that points to the alternative phone-call preview below
// instead of leaving a spinner forever.
function VoiceConnectingMessage({ voiceStatus }) {
  const [timedOut, setTimedOut] = React.useState(false);
  React.useEffect(() => {
    const t = setTimeout(() => setTimedOut(true), 8000);
    return () => clearTimeout(t);
  }, []);
  if (timedOut) {
    return (
      <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
        <div className="font-bold mb-1">In-browser voice preview isn't available right now.</div>
        <div>Status: <code className="text-[11px]">{voiceStatus || 'unknown'}</code>. Usually a microphone-permission prompt was denied, or an extension is blocking WebRTC.</div>
        <div className="mt-1">Use the <strong>phone-call preview</strong> below — it calls your real phone instead so you can still audition every voice.</div>
      </div>
    );
  }
  return (
    <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
      Voice preview connecting… (~5 seconds)
    </div>
  );
}

function AiVoiceCard({ showToast = () => {} }) {
  const PREVIEW_PHONE_LS = 'crm:voicePreview:phone';
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving]   = React.useState(false);
  const [catalog, setCatalog] = React.useState([]);
  const [current, setCurrent] = React.useState(null);
  const [pending, setPending] = React.useState(null);
  // Voice-preview state — operator's phone (cached per-browser so they
  // don't retype it for every audition) + in-flight flag.
  const [previewPhone, setPreviewPhone] = React.useState(() => {
    try { return localStorage.getItem(PREVIEW_PHONE_LS) || ''; } catch { return ''; }
  });
  const [previewing, setPreviewing] = React.useState(false);
  // In-browser preview — uses the existing Twilio Voice SDK Device
  // that TwilioVoiceProvider already registered for inbound calls.
  // Device.connect({ params: { previewVoice } }) triggers the outgoing
  // TwiML endpoint, which detects the previewVoice param and returns
  // <Say voice="..."><Hangup/> — the operator hears the voice straight
  // through their browser audio. No PSTN, no AWS, no incoming-call
  // modal.
  const { device: voiceDevice, status: voiceStatus } = React.useContext(TwilioVoiceContext);
  const [playingVoice, setPlayingVoice] = React.useState(null);
  const activeCallRef = React.useRef(null);

  // In-browser preview via the browser's built-in speech synthesizer —
  // instant, NO microphone, NO WebRTC, NO PSTN cost. Works even when the
  // Twilio Voice SDK device fails to register (mic denied, ad-blocker, no
  // mic hardware) — the failure mode that left the old WebRTC preview dead.
  //
  // It's an APPROXIMATION: the browser's local voice, mapped to the chosen
  // Polly voice's accent + gender, so the operator hears the SCRIPT and the
  // rough voice character immediately. The phone-call preview below plays the
  // EXACT Polly voice a caller hears.
  const playInBrowser = React.useCallback((voiceId) => {
    const meta = catalog.find(v => v.id === voiceId);
    if (!meta) return;
    const synth = typeof window !== 'undefined' ? window.speechSynthesis : null;
    if (!synth) { showToast('This browser has no speech preview — use the phone-call preview below.'); return; }
    try { synth.cancel(); } catch { /* ignore */ }

    const lang = meta.accent === 'uk' ? 'en-GB' : meta.accent === 'au' ? 'en-AU' : 'en-US';
    const text = `Hi! This is your A-I assistant from your insurance agency. This is how I'll sound ` +
                 `when I answer your missed calls, confirm appointments, and follow up with your clients.`;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang;
    u.rate = 1;
    u.pitch = meta.gender === 'male' ? 0.9 : 1.05;

    // Best-effort match to a local browser voice by language, then a gender
    // name-hint. If none match, the utterance's lang alone still yields a
    // reasonable default voice for that accent.
    const voices = synth.getVoices() || [];
    const langLc = lang.toLowerCase();
    const pool = voices.filter(v => (v.lang || '').toLowerCase() === langLc);
    const enPool = pool.length ? pool : voices.filter(v => (v.lang || '').toLowerCase().startsWith('en'));
    const femaleHint = /female|woman|samantha|victoria|karen|moira|tessa|fiona|zira|susan|linda|joanna|salli|kendra|ivy|amy|olivia/i;
    const maleHint   = /\bmale\b|\bman\b|daniel|alex|fred|george|david|mark|matthew|joey|stephen|brian/i;
    const want = meta.gender === 'male' ? maleHint : femaleHint;
    const match = enPool.find(v => want.test(v.name)) || enPool[0];
    if (match) u.voice = match;

    setPlayingVoice(voiceId);
    u.onend   = () => setPlayingVoice(p => (p === voiceId ? null : p));
    u.onerror = () => setPlayingVoice(p => (p === voiceId ? null : p));
    try { synth.speak(u); } catch { setPlayingVoice(null); }
  }, [catalog, showToast]);

  React.useEffect(() => {
    // Hang up any in-flight preview when this card unmounts.
    return () => {
      try { activeCallRef.current?.disconnect(); } catch { /* ignore */ }
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const json = await apiFetch('/api/settings/voice');
        if (cancelled) return;
        setCatalog(json.catalog || []);
        setCurrent(json.voice_id || null);
        setPending(json.voice_id || null);
      } catch (err) {
        if (!cancelled) showToast(`Voice load failed: ${err.message || err}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showToast]);

  const dirty = pending && pending !== current;

  const save = async () => {
    if (!pending || !dirty || saving) return;
    setSaving(true);
    try {
      const json = await apiFetch('/api/settings/voice', {
        method: 'POST',
        body: JSON.stringify({ voice_id: pending }),
      });
      setCurrent(json.voice_id);
      showToast(`AI voice saved · next call uses ${catalog.find(v => v.id === json.voice_id)?.label || 'new voice'}`);
    } catch (err) {
      showToast(`Save failed: ${err.message || err}`);
    } finally {
      setSaving(false);
    }
  };

  const hearVoice = async () => {
    if (previewing) return;
    const phone = (previewPhone || '').trim();
    if (!phone) { showToast('Enter your phone first.'); return; }
    try { localStorage.setItem(PREVIEW_PHONE_LS, phone); } catch { /* ignore */ }
    setPreviewing(true);
    try {
      const json = await apiFetch('/api/settings/voice-preview', {
        method: 'POST',
        body: JSON.stringify({ voice_id: pending, to_phone: phone }),
      });
      const label = catalog.find(v => v.id === json.voice_id)?.label || json.voice_id;
      if (json.dry_run) {
        showToast(`Dry-run: would call ${json.to} with ${label}. Enable SMS_SEND_ENABLED in Vercel for real preview calls.`);
      } else {
        showToast(`${label} is calling ${json.to} now — answer to hear the voice.`);
      }
    } catch (err) {
      showToast(`Preview call failed: ${err.message || err}`);
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><Volume2 size={16} className="text-slate-500"/> AI voice</h3>
          <p className="text-sm text-slate-500 mt-1">Pick the voice your CRM uses when greeting inbound callers, leaving voicemail prompts, and any AI-spoken message. Takes effect on the next call — no redeploy needed.</p>
        </div>
        {dirty && (
          <button
            onClick={save}
            disabled={saving}
            className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            {saving ? 'Saving…' : 'Save voice'}
          </button>
        )}
      </div>
      {loading ? (
        <div className="text-sm text-slate-400 py-6 text-center">Loading voices…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {catalog.map(v => {
            const selected = pending === v.id;
            const isPlaying = playingVoice === v.id;
            return (
              <div
                key={v.id}
                className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${selected ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50/60'}`}
              >
                <label className="flex items-start gap-3 flex-1 min-w-0 cursor-pointer">
                  <input
                    type="radio"
                    name="ai-voice"
                    value={v.id}
                    checked={selected}
                    onChange={() => setPending(v.id)}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900">{v.label}</span>
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-md">{v.accent}</span>
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-md">{v.gender}</span>
                      {v.id === current && <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-md">Active</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{v.description}</div>
                  </div>
                </label>
                <button
                  onClick={(e) => { e.stopPropagation(); playInBrowser(v.id); }}
                  className={`h-8 w-8 inline-flex items-center justify-center rounded-md border transition-colors shrink-0 ${isPlaying ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:text-slate-900 hover:border-slate-300'}`}
                  title={`Preview ${v.label} in your browser (approximate)`}
                  aria-label={`Preview ${v.label}`}
                >
                  <Play size={14}/>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {!loading && (
        <p className="mt-3 text-[11px] text-slate-400">
          ▸ The play button previews the script with your browser's voice (approximate). To hear the <strong className="text-slate-500">exact</strong> voice a caller hears, use the phone-call preview below.
        </p>
      )}

      {/* Phone preview — plays the EXACT Polly voice through a real call
          (validates the full Twilio stack: number, codec, audio level). */}
      {!loading && (
        <div className="mt-4 border-t border-slate-100 pt-4">
          <div className="text-xs font-semibold text-slate-700 mb-1">Hear the exact voice — call my phone</div>
          <p className="text-xs text-slate-500 mb-2">
            Plays the real Polly voice a caller hears, through the full Relay voice stack. ~3¢ from your wallet per preview.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="tel"
              value={previewPhone}
              onChange={e => setPreviewPhone(e.target.value)}
              placeholder="(512) 555-1234"
              autoComplete="tel"
              className="flex-1 min-w-[200px] border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-slate-500"
            />
            <button
              onClick={hearVoice}
              disabled={previewing || !previewPhone.trim() || !pending}
              className="inline-flex items-center gap-1.5 bg-white hover:bg-slate-50 disabled:opacity-40 text-slate-700 border border-slate-200 hover:border-slate-300 text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
            >
              <Phone size={14}/>
              {previewing ? 'Calling…' : 'Call my phone'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── AI Agents ─────────────────────────────────────────────────────
// Four agents per operator: Setter, Policy Manager, Agency Manager
// (premium), Custom. Each one is an editable system prompt + goal
// + channel mix + daily limit. Built-ins auto-seed on first read.
//
// Backed by /api/ai-agents/* — see api/_aiAgents.js for the runtime
// and supabase_migrations/057_ai_agents.sql for the schema.
const AI_AGENT_TYPE_META = {
  setter:          { icon: Bot,         tone: 'slate',   blurb: 'Books appointments off cold + new leads.' },
  policy_manager:  { icon: ShieldCheck, tone: 'slate',   blurb: 'Saves lapsed policies + chases UW requirements.' },
  agency_manager:  { icon: Briefcase,   tone: 'indigo',  blurb: 'Daily executive digest of every agent + the one thing to do today.' },
  dialer:          { icon: Phone,       tone: 'slate',   blurb: 'Outbound AI voice agent that places calls via Relay, runs multi-turn conversations, and logs outcomes.' },
  atlas:           { icon: Briefcase,   tone: 'amber',   blurb: 'Book-of-business steward. Scores client health, surfaces cross-sell, drives NPS + retention loops. Spans success + upsell.' },
  uplift:          { icon: TrendingUp,  tone: 'emerald', blurb: 'Cross-sell strategist. Finds the highest-value next policy per client, gates Medicare correctly, drafts compliant talk tracks.' },
  registrar:       { icon: ShieldCheck,  tone: 'emerald', blurb: 'A2P compliance setup assistant. Walks the agency through SMS registration: brand info, copy generation, URL audit, pre-submit checklist.' },
  custom:          { icon: Wand2,       tone: 'slate',   blurb: 'Operator-built automation. Define the role, tone, and triggers.' },
};

// Operator-facing agent NAMES — per the build-order doc the brains
// already have real names (NOVA / RETAIN / SCOUT). The seed in
// migration 057 stored them as "AI Appointment Setter" / "AI Policy
// Manager" / "Manager AI" which made the FAB pill labels SCOUT/NOVA
// look disconnected from the settings panel. Display-side override
// keeps existing DB rows untouched + works for both seeded and
// future-renamed accounts. Falls back to agent.name when no
// canonical name is known.
const AI_AGENT_DISPLAY_NAMES = {
  setter:          'NOVA',
  policy_manager:  'RETAIN',
  agency_manager:  'SCOUT',
  dialer:          'VOX',
  // ATLAS — book-of-business / customer success umbrella. Phase 2.4
  // in /docs/agent_build_order.md. Picked by the operator 2026-06-26
  // ("strongest brand, spans the full job — success + retention +
  // upsell"). Routing target reserved via migration 113; full code
  // surface (per-client health score / cross-sell queue / NPS hook)
  // TBD. When the seed_ai_agents_for_owner RPC is extended to seed
  // an 'atlas' row, this label takes over automatically.
  atlas:           'ATLAS',
  uplift:          'UPLIFT',
  registrar:       'REGISTRAR',
};
function displayAgentName(agent) {
  if (!agent) return '';
  return AI_AGENT_DISPLAY_NAMES[agent.agent_type] || agent.name || agent.agent_type || 'Agent';
}

function AiAgentsCard({ currentUser, showToast = () => {} }) {
  const [loading, setLoading]   = React.useState(true);
  const [agents, setAgents]     = React.useState([]);
  const [editing, setEditing]   = React.useState(null);   // agent object being edited
  const [creating, setCreating] = React.useState(false);
  const [activity, setActivity] = React.useState({ runs: [], spend_by_agent: {}, totals: { runs: 0, cost_cents: 0, window_hours: 24 } });
  const [running, setRunning]   = React.useState({});      // agent_type → bool
  const [loadError, setLoadError] = React.useState(null);
  // Circuit breaker — after 3 consecutive failures the polling stops
  // and the panel shows a one-time error banner with a Retry button.
  // Prevents the "agent backend 500s + UI keeps hitting it 100x/sec"
  // pattern we hit when SUPABASE_SERVICE_ROLE_KEY was unset in Vercel.
  const failCountRef = React.useRef(0);
  const [breakerOpen, setBreakerOpen] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!currentUser?.id) return;
    if (breakerOpen) return;
    try {
      const json = await apiFetch('/api/ai-agents');
      setAgents(json.agents || []);
      failCountRef.current = 0;
      setLoadError(null);
    } catch (err) {
      const msg = String(err.message || err);
      setLoadError(msg);
      failCountRef.current += 1;
      if (failCountRef.current >= 3) {
        setBreakerOpen(true);
        showToast(`AI agents disabled after 3 failed loads: ${msg}`);
      }
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id, breakerOpen, showToast]);

  const loadActivity = React.useCallback(async () => {
    if (!currentUser?.id) return;
    if (breakerOpen) return;
    try {
      const json = await apiFetch('/api/ai-agents/activity?limit=20&window_hours=24');
      setActivity(json);
    } catch { /* silent — best-effort, errors counted by load() */ }
  }, [currentUser?.id, breakerOpen]);

  const retry = React.useCallback(() => {
    failCountRef.current = 0;
    setBreakerOpen(false);
    setLoadError(null);
    setLoading(true);
  }, []);

  React.useEffect(() => { load(); loadActivity(); }, [load, loadActivity]);
  // Light polling so the operator sees agent activity land without
  // a hard refresh. 25 seconds is well inside the prompt-cache window.
  React.useEffect(() => {
    if (!currentUser?.id) return;
    if (breakerOpen) return;
    const t = setInterval(loadActivity, 25000);
    return () => clearInterval(t);
  }, [currentUser?.id, loadActivity, breakerOpen]);

  const refreshAgent = (next) => {
    setAgents(prev => prev.map(a => a.id === next.id ? { ...a, ...next } : a));
  };

  const runNow = async (agent) => {
    if (!['setter','policy_manager','agency_manager'].includes(agent.agent_type)) {
      showToast('Run now is available for Setter, Policy Manager, and Manager AI.');
      return;
    }
    setRunning(r => ({ ...r, [agent.agent_type]: true }));
    try {
      const json = await apiFetch('/api/ai-agents/run-now', {
        method: 'POST',
        body: JSON.stringify({ agent_type: agent.agent_type }),
      });
      const s = json.summary || {};
      if (agent.agent_type === 'agency_manager') {
        showToast(`Manager AI published a fresh digest.`);
      } else {
        showToast(`${agent.name}: ${s.sends || 0} sent, ${s.skipped || 0} skipped, ${s.errors || 0} errors.`);
      }
      // Refresh activity + per-agent counters.
      loadActivity();
      load();
    } catch (err) {
      showToast(`Run failed: ${err.message || err}`);
    } finally {
      setRunning(r => ({ ...r, [agent.agent_type]: false }));
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><Sparkles size={16} className="text-slate-500"/> AI agents</h3>
          <p className="text-sm text-slate-500 mt-1">
            Delegate the repetitive work. Each agent uses your credits wallet for SMS, voice, and AI tokens — so they only spend when they help.
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 text-sm font-semibold text-slate-700 transition-colors"
        >
          <Plus size={14}/> Custom agent
        </button>
      </div>
      {loading ? (
        <div className="text-sm text-slate-400 py-6 text-center">Loading agents…</div>
      ) : breakerOpen ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-sm font-semibold text-red-800 mb-1">AI agents unavailable</div>
          <div className="text-xs text-red-700 mb-3">
            The <code className="bg-red-100 px-1 rounded">/api/ai-agents</code> endpoint returned an error 3 times in a row. Polling is paused so it doesn't keep hammering the backend.
            {loadError && <div className="mt-2 font-mono text-[11px] bg-white border border-red-200 rounded p-2 break-all">{loadError}</div>}
            <div className="mt-2">
              Most likely: <code className="bg-red-100 px-1 rounded">SUPABASE_SERVICE_ROLE_KEY</code> or <code className="bg-red-100 px-1 rounded">SUPABASE_URL</code> isn't set in Vercel → Project Settings → Environment Variables. Set both for the Production environment and redeploy.
            </div>
          </div>
          <button
            onClick={retry}
            className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors"
          >
            Retry now
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {agents.map(a => (
            <AiAgentTile
              key={a.id}
              agent={a}
              spend={activity.spend_by_agent?.[a.id]}
              running={!!running[a.agent_type]}
              onConfigure={() => setEditing(a)}
              onRunNow={() => runNow(a)}
            />
          ))}
          {agents.length === 0 && (
            <div className="col-span-full text-sm text-slate-400 italic text-center py-6">No agents yet — built-ins seed automatically on next load.</div>
          )}
        </div>
      )}

      <AgentActivityFeed activity={activity}/>

      {editing && (
        <AiAgentEditor
          agent={editing}
          onClose={() => setEditing(null)}
          onSaved={(next) => { refreshAgent(next); setEditing(next); }}
          onDeleted={() => { setAgents(prev => prev.filter(a => a.id !== editing.id)); setEditing(null); }}
          showToast={showToast}
        />
      )}
      {creating && (
        <AiAgentCreator
          onClose={() => setCreating(false)}
          onCreated={(agent) => { setAgents(prev => [...prev, { ...agent, stats_7d: { runs: 0, sent: 0, booked: 0, errors: 0 } }]); setCreating(false); setEditing(agent); }}
          showToast={showToast}
        />
      )}
    </div>
  );
}

// Live activity feed across every agent — refreshes every 25s so the
// operator sees agent work land without a hard refresh.
function AgentActivityFeed({ activity }) {
  const runs = activity?.runs || [];
  const total = activity?.totals?.cost_cents || 0;
  const window = activity?.totals?.window_hours || 24;
  if (runs.length === 0) return null;
  return (
    <div className="mt-4 border-t border-slate-100 pt-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold text-slate-700">Recent agent activity</div>
        <div className="text-[11px] text-slate-500 tabular-nums">{activity.totals.runs} runs · ${(total / 100).toFixed(2)} · last {window}h</div>
      </div>
      <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-56 overflow-y-auto">
        {runs.map(r => (
          <div key={r.id} className="px-3 py-2 text-xs flex items-start gap-3">
            <div className="text-slate-500 tabular-nums shrink-0 w-28">{new Date(r.created_at).toLocaleString()}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-900 truncate">{r.agent_name || r.agent_type || 'Agent'}</span>
                <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{r.action}</span>
                {r.outcome && (
                  <span className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-md ${
                    r.outcome === 'success' ? 'text-emerald-700 bg-emerald-50 border border-emerald-200'
                    : r.outcome === 'dry_run' ? 'text-slate-700 bg-slate-100 border border-slate-200'
                    : r.outcome === 'error' || r.outcome === 'sms_failed' ? 'text-red-700 bg-red-50 border border-red-200'
                    : 'text-slate-600 bg-slate-100 border border-slate-200'
                  }`}>{r.outcome}</span>
                )}
              </div>
              {r.message && <div className="text-slate-600 mt-0.5 line-clamp-2">{r.message}</div>}
            </div>
            {r.cost_cents > 0 && <div className="text-slate-400 tabular-nums shrink-0 self-center">{(r.cost_cents / 100).toFixed(2)}¢</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function AiAgentTile({ agent, onConfigure, onRunNow, running = false, spend = null }) {
  const meta = AI_AGENT_TYPE_META[agent.agent_type] || AI_AGENT_TYPE_META.custom;
  const Icon = meta.icon;
  const s = agent.stats_7d || { runs: 0, sent: 0, booked: 0, errors: 0 };
  const tone = meta.tone;
  return (
    <div className="border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors">
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-lg bg-${tone}-50 text-${tone}-700 border border-${tone}-200 inline-flex items-center justify-center shrink-0`}>
          <Icon size={18}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-slate-900 tracking-wide uppercase text-sm">{displayAgentName(agent)}</span>
            <span className="text-[10px] text-slate-400 truncate">· {agent.name}</span>
            {agent.pricing_tier === 'premium' && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded-md">Premium</span>
            )}
            {agent.enabled ? (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-md">On</span>
            ) : (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-md">Off</span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{agent.description || meta.blurb}</p>
          <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500 tabular-nums flex-wrap">
            <span><span className="font-semibold text-slate-700">{s.sent}</span> sends · 7d</span>
            <span><span className="font-semibold text-slate-700">{s.booked}</span> booked</span>
            {s.errors > 0 && <span className="text-red-600"><span className="font-semibold">{s.errors}</span> errors</span>}
            {spend?.total_cents > 0 && (
              <span title={`${spend.run_count} runs in the last 24h`}>· <span className="font-semibold text-slate-700">${(spend.total_cents / 100).toFixed(2)}</span> spent 24h</span>
            )}
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <button
          onClick={onConfigure}
          className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors"
        >
          Configure
        </button>
        {agent.enabled && onRunNow && ['setter','policy_manager','agency_manager'].includes(agent.agent_type) && (
          <button
            onClick={onRunNow}
            disabled={running}
            className="inline-flex items-center gap-1.5 bg-white hover:bg-slate-50 disabled:opacity-50 text-slate-700 border border-slate-200 hover:border-slate-300 text-xs font-semibold px-3 py-1.5 rounded-md transition-colors"
            title="Trigger this agent immediately, scoped to your account."
          >
            <Play size={12}/>
            {running ? 'Running…' : 'Run now'}
          </button>
        )}
        {agent.last_run_at && (
          <span className="text-[11px] text-slate-400">Last · {new Date(agent.last_run_at).toLocaleString()}</span>
        )}
      </div>
    </div>
  );
}

function AiAgentEditor({ agent: initial, onClose, onSaved, onDeleted, showToast }) {
  const [agent, setAgent] = React.useState(initial);
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [preview, setPreview] = React.useState(null);
  const [runs, setRuns]       = React.useState([]);
  // Voice catalog + operator-level default. Loaded lazily so the editor
  // doesn't slow the initial mount; if the operator never expands the
  // Voice section we never fetch.
  const [voiceCatalog, setVoiceCatalog] = React.useState([]);
  const [operatorVoice, setOperatorVoice] = React.useState(null);
  const [voiceLoaded, setVoiceLoaded] = React.useState(false);
  // In-modal preview state (mirrors AiVoiceCard).
  const { device: voiceDevice, status: voiceStatus } = React.useContext(TwilioVoiceContext);
  const [playingVoice, setPlayingVoice] = React.useState(null);
  const activeCallRef = React.useRef(null);

  React.useEffect(() => {
    (async () => {
      try {
        const json = await apiFetch(`/api/ai-agents/one?id=${encodeURIComponent(initial.id)}`);
        if (json.agent) setAgent(json.agent);
        if (json.runs)  setRuns(json.runs);
      } catch { /* ignore */ }
    })();
  }, [initial.id]);

  React.useEffect(() => {
    (async () => {
      try {
        const json = await apiFetch('/api/settings/voice');
        setVoiceCatalog(json.catalog || []);
        setOperatorVoice(json.voice_id || null);
      } catch { /* ignore */ } finally {
        setVoiceLoaded(true);
      }
    })();
    return () => {
      try { activeCallRef.current?.disconnect(); } catch { /* ignore */ }
    };
  }, []);

  const update = (k, v) => setAgent(a => ({ ...a, [k]: v }));

  const previewAgentVoice = async (voiceId) => {
    if (!voiceId) return;
    if (!voiceDevice || voiceStatus !== 'ready') {
      showToast('Voice device still connecting — try again in a moment.');
      return;
    }
    try { activeCallRef.current?.disconnect(); } catch { /* ignore */ }
    try {
      const call = await voiceDevice.connect({ params: { previewVoice: voiceId } });
      activeCallRef.current = call;
      setPlayingVoice(voiceId);
      const clear = () => {
        if (activeCallRef.current === call) {
          activeCallRef.current = null;
          setPlayingVoice(null);
        }
      };
      call.on('disconnect', clear);
      call.on('cancel', clear);
      call.on('error', clear);
    } catch (err) {
      setPlayingVoice(null);
      showToast(`Preview failed: ${err.message || err}`);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const json = await apiFetch(`/api/ai-agents/one?id=${encodeURIComponent(agent.id)}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: agent.name, description: agent.description, enabled: agent.enabled,
          goal: agent.goal, role_summary: agent.role_summary, tone: agent.tone,
          channel_sms: agent.channel_sms, channel_voice: agent.channel_voice, channel_email: agent.channel_email,
          opening_script: agent.opening_script, followup_script: agent.followup_script,
          daily_limit_runs: agent.daily_limit_runs,
          tts_voice: agent.tts_voice ?? null,
        }),
      });
      onSaved(json.agent);
      showToast(`${json.agent.name} saved`);
    } catch (err) {
      showToast(`Save failed: ${err.message || err}`);
    } finally {
      setSaving(false);
    }
  };

  const previewMessage = async () => {
    setTesting(true);
    setPreview(null);
    try {
      const json = await apiFetch('/api/ai-agents/test', {
        method: 'POST',
        body: JSON.stringify({ agent_id: agent.id, preview_only: true }),
      });
      setPreview(json.body);
    } catch (err) {
      showToast(`Preview failed: ${err.message || err}`);
    } finally {
      setTesting(false);
    }
  };

  const del = async () => {
    if (!(await hqConfirm({
      title: 'Delete this custom agent permanently?',
      confirmLabel: 'Delete agent', danger: true,
    }))) return;
    try {
      await apiFetch(`/api/ai-agents/one?id=${encodeURIComponent(agent.id)}`, { method: 'DELETE' });
      onDeleted();
      showToast('Agent deleted');
    } catch (err) {
      showToast(`Delete failed: ${err.message || err}`);
    }
  };

  const meta = AI_AGENT_TYPE_META[agent.agent_type] || AI_AGENT_TYPE_META.custom;
  const Icon = meta.icon;
  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full my-8" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-slate-50 border border-slate-200 text-slate-700 inline-flex items-center justify-center"><Icon size={16}/></div>
          <div className="flex-1 min-w-0">
            <div className="text-lg font-semibold text-slate-900 truncate">{agent.name}</div>
            <div className="text-xs text-slate-500">{agent.agent_type === 'custom' ? 'Custom agent' : 'Built-in agent'} · {agent.pricing_tier === 'premium' ? 'Premium tier' : 'Standard tier'}</div>
          </div>
          <button aria-label="Close" onClick={onClose} className="text-slate-400 hover:text-slate-900 p-1" aria-label="Close"><X size={18}/></button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Master toggle */}
          <label className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200">
            <div>
              <div className="text-sm font-semibold text-slate-900">Enabled</div>
              <div className="text-xs text-slate-500">Runs automatically on the next tick (every 10 min). Toggle off to pause without losing config.</div>
            </div>
            <input type="checkbox" checked={!!agent.enabled} onChange={e => update('enabled', e.target.checked)} className="h-5 w-5 rounded"/>
          </label>

          {/* Name (custom only) */}
          {agent.agent_type === 'custom' && (
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">Name</span>
              <input value={agent.name || ''} onChange={e => update('name', e.target.value)} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-slate-500"/>
            </label>
          )}

          {/* Goal */}
          <label className="block">
            <span className="text-xs font-semibold text-slate-700">Goal</span>
            <input value={agent.goal || ''} onChange={e => update('goal', e.target.value)} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-slate-500"/>
          </label>

          {/* Role / system prompt */}
          <label className="block">
            <span className="text-xs font-semibold text-slate-700">Role &amp; instructions <span className="text-slate-400 font-normal">(this is what the agent reads as its system prompt)</span></span>
            <textarea
              value={agent.role_summary || ''}
              onChange={e => update('role_summary', e.target.value)}
              rows={10}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-slate-500"
            />
          </label>

          {/* Tone */}
          <label className="block">
            <span className="text-xs font-semibold text-slate-700">Tone</span>
            <input value={agent.tone || ''} onChange={e => update('tone', e.target.value)} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-slate-500"/>
          </label>

          {/* TRAINING — knowledge base + examples + forbidden + test sandbox */}
          <div className="pt-3 border-t-2 border-slate-200">
            <div className="text-[10px] font-black uppercase tracking-[0.3em] text-emerald-700 mb-2">Train this AI · Teach it your knowledge</div>
            <AgentTrainingPanel
              agent={agent}
              onSaved={(next) => { setAgent(next); onSaved?.(next); }}
              showToast={showToast}
            />
          </div>

          {/* Voice */}
          <div>
            <div className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-2">
              <Volume2 size={12} className="text-slate-500"/> Voice
            </div>
            <p className="text-xs text-slate-500 mb-2">
              The voice this agent uses when it speaks (voicemail prompts, future outbound calls). Click ▶ to hear any voice through your browser.
              {agent.tts_voice == null && (
                <span> Currently inheriting the operator default
                  {operatorVoice && <span className="text-slate-700 font-medium"> ({voiceCatalog.find(v => v.id === operatorVoice)?.label || 'default'})</span>}.</span>
              )}
            </p>
            {!voiceLoaded ? (
              <div className="text-xs text-slate-400 py-2">Loading voices…</div>
            ) : (
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-56 overflow-y-auto">
                <label className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${!agent.tts_voice ? 'bg-slate-50' : 'hover:bg-slate-50/60'}`}>
                  <input
                    type="radio"
                    name={`agent-voice-${agent.id}`}
                    checked={!agent.tts_voice}
                    onChange={() => update('tts_voice', null)}
                  />
                  <div className="flex-1 text-xs">
                    <div className="font-semibold text-slate-900">Use operator default</div>
                    <div className="text-slate-500">Inherits whatever the operator set in Settings → AI voice.</div>
                  </div>
                </label>
                {voiceCatalog.map(v => {
                  const selected = agent.tts_voice === v.id;
                  const isPlaying = playingVoice === v.id;
                  return (
                    <div key={v.id} className={`flex items-center gap-3 px-3 py-2 ${selected ? 'bg-slate-50' : 'hover:bg-slate-50/60'}`}>
                      <label className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer">
                        <input
                          type="radio"
                          name={`agent-voice-${agent.id}`}
                          checked={selected}
                          onChange={() => update('tts_voice', v.id)}
                        />
                        <div className="flex-1 min-w-0 text-xs">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-slate-900">{v.label}</span>
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-md">{v.accent}</span>
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-md">{v.gender}</span>
                          </div>
                          <div className="text-slate-500 mt-0.5">{v.description}</div>
                        </div>
                      </label>
                      <button
                        onClick={(e) => { e.stopPropagation(); previewAgentVoice(v.id); }}
                        disabled={voiceStatus !== 'ready'}
                        className={`h-7 w-7 inline-flex items-center justify-center rounded-md border transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed ${isPlaying ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:text-slate-900 hover:border-slate-300'}`}
                        title={voiceStatus === 'ready' ? `Play ${v.label} sample` : 'Voice device still connecting…'}
                        aria-label={`Play ${v.label} sample`}
                      >
                        <Play size={12}/>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Channels */}
          <div>
            <div className="text-xs font-semibold text-slate-700 mb-2">Channels</div>
            <div className="flex items-center gap-4 flex-wrap">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={!!agent.channel_sms} onChange={e => update('channel_sms', e.target.checked)}/> SMS
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={!!agent.channel_voice} onChange={e => update('channel_voice', e.target.checked)}/> Voice <span className="text-[10px] text-amber-700 font-semibold uppercase">soon</span>
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={!!agent.channel_email} onChange={e => update('channel_email', e.target.checked)}/> Email
              </label>
            </div>
          </div>

          {/* Daily limit */}
          <label className="block">
            <span className="text-xs font-semibold text-slate-700">Daily run limit · {agent.daily_limit_runs}</span>
            <input type="range" min={1} max={200} value={agent.daily_limit_runs || 25} onChange={e => update('daily_limit_runs', Number(e.target.value))} className="mt-1 w-full"/>
            <span className="text-xs text-slate-500">Caps how many touches this agent can dispatch per rolling 24h — protects your wallet from a runaway loop.</span>
          </label>

          {/* Preview */}
          <div className="border border-slate-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2 gap-2">
              <div className="text-xs font-semibold text-slate-700">Preview a message</div>
              <button
                onClick={previewMessage}
                disabled={testing}
                className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors"
              >
                {testing ? 'Generating…' : 'Generate sample SMS'}
              </button>
            </div>
            {preview ? (
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm text-slate-800 whitespace-pre-wrap">{preview}</div>
            ) : (
              <div className="text-xs text-slate-400 italic">Click to see what this agent would send a sample lead. Costs ~1¢ in AI credits per preview.</div>
            )}
          </div>

          {/* Policy Manager only: conservation report uploader */}
          {agent.agent_type === 'policy_manager' && (
            <ConservationReportUploader showToast={showToast}/>
          )}

          {/* Recent activity */}
          {runs.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-slate-700 mb-2">Recent activity</div>
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-48 overflow-y-auto">
                {runs.map(r => (
                  <div key={r.id} className="px-3 py-2 text-xs flex items-start gap-3">
                    <div className="text-slate-500 tabular-nums shrink-0">{new Date(r.created_at).toLocaleString()}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900">{r.action} · <span className={r.outcome === 'success' ? 'text-emerald-700' : r.outcome === 'error' ? 'text-red-700' : 'text-slate-500'}>{r.outcome || '—'}</span></div>
                      {r.message && <div className="text-slate-600 truncate">{r.message}</div>}
                    </div>
                    {r.cost_cents > 0 && <div className="text-slate-400 shrink-0">{(r.cost_cents / 100).toFixed(2)}¢</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-between">
          <div>
            {agent.agent_type === 'custom' && (
              <button onClick={del} className="text-sm text-red-600 hover:text-red-700 font-medium">Delete</button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button aria-label="Close" onClick={onClose} className="text-sm text-slate-500 hover:text-slate-900 font-medium px-3 py-2">Close</button>
            <button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AiAgentCreator({ onClose, onCreated, showToast }) {
  const [name, setName]             = React.useState('');
  const [goal, setGoal]             = React.useState('');
  const [role, setRole]             = React.useState('You are…\n\nYour rules:\n- …');
  const [tone, setTone]             = React.useState('warm, professional');
  const [saving, setSaving]         = React.useState(false);

  const create = async () => {
    if (!name.trim() || !goal.trim() || !role.trim()) {
      showToast('Name, goal, and role are required.');
      return;
    }
    setSaving(true);
    try {
      const json = await apiFetch('/api/ai-agents', {
        method: 'POST',
        body: JSON.stringify({ name, goal, role_summary: role, tone, enabled: false }),
      });
      onCreated(json.agent);
    } catch (err) {
      showToast(`Create failed: ${err.message || err}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full my-8" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-slate-50 border border-slate-200 text-slate-700 inline-flex items-center justify-center"><Wand2 size={16}/></div>
          <div className="flex-1">
            <div className="text-lg font-semibold text-slate-900">New custom agent</div>
            <div className="text-xs text-slate-500">Define the role, goal, and tone — the runtime will handle the rest.</div>
          </div>
          <button aria-label="Close" onClick={onClose} className="text-slate-400 hover:text-slate-900 p-1" aria-label="Close"><X size={18}/></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <label className="block">
            <span className="text-xs font-semibold text-slate-700">Name</span>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Birthday wisher, Referral asker"
              autoComplete="off"
              autoFocus
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-slate-500"
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-700">Goal</span>
            <input
              value={goal}
              onChange={e => setGoal(e.target.value)}
              placeholder="e.g. Ask each placed client for one referral"
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-slate-500"
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-700">Role &amp; instructions</span>
            <textarea
              value={role}
              onChange={e => setRole(e.target.value)}
              rows={8}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-slate-500"
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-700">Tone</span>
            <input value={tone} onChange={e => setTone(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-slate-500"/>
          </label>
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <button aria-label="Close" onClick={onClose} className="text-sm text-slate-500 hover:text-slate-900 font-medium px-3 py-2">Cancel</button>
          <button onClick={create} disabled={saving} className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">
            {saving ? 'Creating…' : 'Create agent'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Manager AI chat panel ─────────────────────────────────────────
// The operator's chief-of-staff. Talks plain English; routes the
// specialist agents under the hood. Conversation lives in client
// state (and localStorage) — refresh starts a fresh thread.
function ManagerChatPanel({ currentUser, showToast = () => {}, isDemo = false }) {
  const STORAGE_KEY = `crm:managerChat:${currentUser?.id || 'anon'}`;
  const [messages, setMessages] = React.useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) return arr;
      }
    } catch { /* ignore */ }
    return [];
  });
  const [input, setInput]     = React.useState('');
  const [busy, setBusy]       = React.useState(false);
  const [lastDispatch, setLastDispatch] = React.useState(null);
  const [expanded, setExpanded] = React.useState(false);
  // Show the freeform textarea? Hidden in empty-state until the
  // operator either fires a quick-prompt or explicitly asks to type
  // their own question. P2-7: keeps the panel prominent but compact —
  // 3 one-tap CTAs instead of a blank input box that some operators
  // don't know what to do with.
  const [showInput, setShowInput] = React.useState(false);
  // Manager AI feature gate. null = checking, true/false = known.
  // Surfaced as a friendly empty-state instead of letting the operator
  // type and then hit a server 412 — the auditor saw the gate text
  // appear on every input attempt with no clear next step.
  const [managerEnabled, setManagerEnabled] = React.useState(null);
  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch('/api/ai-agents');
        const list = Array.isArray(r?.agents) ? r.agents : [];
        const mgr  = list.find(a => a.agent_type === 'manager' || a.type === 'manager' || /manager/i.test(a.name || ''));
        if (!cancelled) setManagerEnabled(!!mgr?.enabled);
      } catch {
        if (!cancelled) setManagerEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-20))); } catch { /* ignore */ }
  }, [messages, STORAGE_KEY]);
  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  // Override lets a quick-prompt button fire a fixed message without
  // requiring the user to first populate the textarea + click Send.
  const send = async (override) => {
    const text = (override != null ? override : input).trim();
    if (!text || busy) return;
    const next = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setShowInput(true);   // once a conversation starts, keep the input visible
    setBusy(true);
    setLastDispatch(null);
    try {
      const json = await apiFetch('/api/ai-agents/manager/chat', {
        method: 'POST',
        body: JSON.stringify({ messages: next, auto_execute: true }),
      });
      setMessages(m => [...m, { role: 'assistant', content: json.user_reply || '(no reply)' }]);
      setLastDispatch({
        dispatch:         json.dispatch || [],
        dispatch_log:     json.dispatch_log || [],
        dispatch_results: json.dispatch_results || {},
      });
    } catch (err) {
      const msg = String(err.message || err);
      if (/Manager AI requires the Premium/i.test(msg)) {
        showToast('Manager AI requires the Premium or Enterprise plan.');
      } else if (/AI is disabled|Enable Manager AI|manager_ai_disabled/i.test(msg)) {
        showToast('AI is disabled. Enable Manager AI in Settings → AI agents.');
      } else {
        showToast(`Manager failed: ${msg}`);
      }
      setMessages(m => [...m, { role: 'assistant', content: `(${msg})` }]);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const reset = () => {
    setMessages([]);
    setLastDispatch(null);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Bot size={14} className="text-slate-400"/> Manager AI · chief of staff
          </h3>
          <div className="text-xs text-slate-500 mt-0.5">Tell it what you want in plain English. It routes the specialist team and reports back.</div>
        </div>
        {messages.length > 0 && (
          <button
            onClick={async () => {
              // Audit 2026-06-29 BUG-017: confirm before clearing
              // history. Was a one-click destructive action.
              if (await hqConfirm({
                title: 'Clear this conversation?',
                body: 'The history will be lost — Manager AI starts fresh next message.',
                confirmLabel: 'Clear', danger: true,
              })) {
                reset();
              }
            }}
            className="text-xs text-slate-500 hover:text-slate-900 font-medium"
          >
            Reset conversation
          </button>
        )}
      </div>

      {isDemo && managerEnabled === false ? (
        // Demo Mode: Manager AI ALWAYS appears active in the demo
        // (the real account may not have it on, but a prospect
        // walking through should see what it does, not an upsell).
        <div className="px-5 py-6">
          <div className="text-center mb-4">
            <Bot size={32} className="mx-auto text-violet-400 mb-2"/>
            <div className="text-sm font-semibold text-slate-900">Manager AI — your chief of staff</div>
            <div className="text-xs text-slate-500 mt-1 max-w-md mx-auto leading-relaxed">
              Ask plain English. Manager AI reads your book, finds the answer, and dispatches the specialist agents (NOVA, ENROLL, ANCHOR, RETAIN, LEDGER) under the hood.
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 max-w-none mx-auto">
            {[
              { q: 'How did I do yesterday?',         a: '8 dials, 3 contacts, 1 appointment booked for John Smith (IUL). 1 issue closed by ANCHOR — Robert Chen\'s income doc cleared. ENROLL queued 4 AEP reminders.' },
              { q: 'Who needs a follow-up call?',     a: 'Top 3: Tom Wilson (ACA, opt-in pending), Greg Anderson (no-show rescheduled for 2pm), Helen Park (dental cross-sell). Tap any name to dial.' },
              { q: 'Show at-risk renewals this month.', a: 'David Cherryholmes (FEX NSF — RETAIN running save cadence), Frank DeLuca (AEP plan change risk), 7 more in the at-risk bucket — open RETAIN for the full list.' },
            ].map(({ q, a }, i) => (
              <details key={i} className="bg-white border border-violet-200 rounded-lg p-2 text-xs cursor-pointer hover:border-violet-300">
                <summary className="font-semibold text-slate-900 hover:text-violet-700">{q}</summary>
                <div className="mt-2 text-slate-600 leading-relaxed">{a}</div>
              </details>
            ))}
          </div>
          <div className="text-[10px] text-slate-400 text-center mt-3 uppercase tracking-wider">Demo · presentation only</div>
        </div>
      ) : managerEnabled === false ? (
        // Friendly disabled state — replaces the per-input gate error
        // text the auditor saw. Tells the operator exactly what the
        // feature does + where to turn it on.
        <div className="px-5 py-6 text-center">
          <Bot size={32} className="mx-auto text-slate-300 mb-2"/>
          <div className="text-sm font-semibold text-slate-900">Manager AI is off for your account</div>
          <div className="text-xs text-slate-500 mt-1 max-w-md mx-auto leading-relaxed">
            When enabled, it answers questions like "How did I do yesterday?" or "Who needs a follow-up call?" and dispatches the specialist agents under the hood. Flip it on in Settings → AI agents.
          </div>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('crm-goto-tab', { detail: { tab: 'settings' } }))}
            className="mt-4 inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-1.5 rounded-lg"
          >
            Open Settings → AI agents
          </button>
        </div>
      ) : messages.length === 0 ? (
        // Compact CTA card (P2-7): three high-value one-tap prompts
        // instead of a blank chat input. Operators new to the product
        // don't always know what to type — these answer "what can this
        // thing actually do?" in three taps.
        <div className="px-5 py-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { q: 'How did I do yesterday?',                 sub: 'Daily rollup of calls, apps, and revenue' },
              { q: 'Who needs a follow-up call today?',       sub: 'The Policy Manager\'s ranked list' },
              { q: 'What\'s bleeding the most premium?',      sub: 'Top chargeback + retention risks' },
            ].map(p => (
              <button
                key={p.q}
                onClick={() => send(p.q)}
                disabled={busy}
                className="text-left bg-slate-50 hover:bg-slate-100 disabled:opacity-50 border border-slate-200 hover:border-slate-300 rounded-lg p-3 transition-colors"
              >
                <div className="text-sm font-semibold text-slate-900">{p.q}</div>
                <div className="text-[11px] text-slate-500 mt-1">{p.sub}</div>
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowInput(true)}
            className="mt-3 text-xs text-slate-500 hover:text-slate-900 font-medium"
          >
            Or ask something else →
          </button>
        </div>
      ) : (
        <div ref={scrollRef} className="px-5 py-3 max-h-96 overflow-y-auto space-y-3">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.role === 'user' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-900'}`}>
                <div className="whitespace-pre-wrap">{m.content}</div>
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="bg-slate-100 text-slate-500 text-sm px-3 py-2 rounded-lg">Manager is thinking…</div>
            </div>
          )}
        </div>
      )}

      {lastDispatch?.dispatch?.length > 0 && (
        <div className="px-5 pb-3 border-t border-slate-100">
          <button
            onClick={() => setExpanded(v => !v)}
            className="mt-2 text-xs text-slate-500 hover:text-slate-900 font-medium"
          >
            {expanded ? '▾' : '▸'} Dispatch trace ({lastDispatch.dispatch.length} {lastDispatch.dispatch.length === 1 ? 'specialist' : 'specialists'})
          </button>
          {expanded && (
            <div className="mt-2 space-y-1">
              {lastDispatch.dispatch.map((d, i) => {
                // Match the log entry by index when possible — multiple
                // dispatches can target the same agent type (e.g. two
                // SETTER calls in one turn), so .find(l => l.agent === d.agent)
                // would always return the first one for both rows.
                const logByIdx  = lastDispatch.dispatch_log?.[i];
                const logEntry  = (logByIdx && logByIdx.agent === d.agent)
                  ? logByIdx
                  : (lastDispatch.dispatch_log?.find(l => l.agent === d.agent) || {});
                const result = lastDispatch.dispatch_results?.[d.agent];
                return (
                  <div key={i} className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-xs">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-700 bg-white border border-slate-200 px-1.5 py-0.5 rounded-md">{d.agent}</span>
                      <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md ${logEntry.ok === false ? 'text-red-700 bg-red-50 border border-red-200' : 'text-emerald-700 bg-emerald-50 border border-emerald-200'}`}>
                        {logEntry.ok === false ? 'failed' : 'ok'}
                      </span>
                      {d.priority && <span className="text-[10px] text-slate-500">{d.priority}</span>}
                    </div>
                    <div className="text-slate-700 mt-1">{d.task}</div>
                    {result?.blocked && (
                      <div className="mt-1 text-amber-700">
                        Blocked by consent gate: {(result.blockers || []).join(', ')}
                      </div>
                    )}
                    {logEntry.error && (
                      <div className="mt-1 text-red-700">{logEntry.error}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Hide the textarea in the compact empty-state. Show as soon as
          there's an active conversation OR the operator clicks "Or ask
          something else" — first-time UX stays focused, repeat users
          get the full surface.
          Audit 2026-07-01: when managerEnabled === false, the friendly
          disabled card renders above but the textarea + Send button
          used to stay live (a stale conversation from before the flag
          flipped, or a prior click of "Or ask something else",
          left the input mounted). Gate the whole block on managerEnabled
          !== false AND disable both controls as belt-and-suspenders. */}
      {managerEnabled !== false && (messages.length > 0 || showInput) && (
        <div className="border-t border-slate-100 p-3 flex items-end gap-2">
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder='e.g. "Handle my book today" or "Who do I call first?"'
            rows={1}
            disabled={managerEnabled === false || busy}
            className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-slate-500 disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
            autoFocus={showInput && messages.length === 0}
          />
          <button
            onClick={() => send()}
            disabled={managerEnabled === false || busy || !input.trim()}
            className="bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors inline-flex items-center gap-1.5"
          >
            {busy ? '…' : <><Send size={14}/> Send</>}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Book of Business panel ────────────────────────────────────────
// Hits /api/ai-agents/book-report (Section 7 of the operating
// contract). Surfaces revenue at risk, chargeback risk, top 10 to
// call today, and aggregated data gaps. Designed to be the single
// thing a retention manager reads first thing every morning.
function BookOfBusinessPanel({ currentUser, showToast = () => {}, openClientProfile = () => {} }) {
  const [loading, setLoading] = React.useState(true);
  const [data, setData]       = React.useState(null);

  React.useEffect(() => {
    if (!currentUser?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const json = await apiFetch('/api/ai-agents/book-report');
        if (!cancelled) setData(json);
      } catch (err) {
        if (!cancelled) showToast(`Book report load failed: ${err.message || err}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.id, showToast]);

  if (loading) {
    return <SkeletonBlock lines={4}/>;
  }
  if (!data) return null;

  const total = data.policies_at_risk?.total || 0;
  // Empty-state used to be a full card with an instruction line; that
  // read as a "bare placeholder" alongside the equally-empty Daily
  // Action Queue. Render nothing instead — the Setup checklist already
  // points operators at the carrier-report upload flow. The panel
  // re-appears the moment there's at least one at-risk policy to show.
  if (total === 0) return null;

  const byState = data.policies_at_risk?.by_state || {};
  const STATE_LABEL = {
    LAPSE_PENDING:   { label: 'Lapse pending',   tone: 'red'    },
    PAYMENT_FAILURE: { label: 'Payment failure', tone: 'orange' },
    ISSUED_NOT_PAID: { label: 'Issued not paid', tone: 'amber'  },
    REQUIREMENTS:    { label: 'Requirements',    tone: 'blue'   },
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-slate-900 flex items-center gap-2"><ShieldCheck size={14} className="text-slate-400"/> Book of Business · revenue protection</h3>
          <div className="text-xs text-slate-500 mt-0.5">
            {total} policies at risk · {data.revenue_at_risk?.known_label} known premium at risk
            {data.revenue_at_risk?.unknown_row_count > 0 && (
              <span className="ml-1 text-amber-700">· {data.revenue_at_risk.unknown_row_count} rows with UNKNOWN premium</span>
            )}
          </div>
        </div>
        {data.chargeback_risk?.row_count > 0 && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Chargeback risk</div>
            <div className="text-sm font-semibold text-slate-900 tabular-nums">{data.chargeback_risk.known_label} known</div>
            <div className="text-[10px] text-slate-500">{data.chargeback_risk.row_count} rows</div>
          </div>
        )}
      </div>

      {/* State breakdown */}
      <div className="px-5 py-3 border-b border-slate-100 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {Object.entries(STATE_LABEL).map(([key, meta]) => {
          const n = byState[key] || 0;
          return (
            <div key={key} className={`bg-${meta.tone}-50 border border-${meta.tone}-200 rounded-lg p-3`}>
              <div className={`text-[10px] uppercase tracking-wide font-semibold text-${meta.tone}-700`}>{meta.label}</div>
              <div className={`text-2xl font-bold text-${meta.tone}-700 tabular-nums mt-0.5`}>{n}</div>
            </div>
          );
        })}
      </div>

      {/* Next best action */}
      {data.next_best_action && (
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/60">
          <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Next best action</div>
          <div className="mt-1 text-sm text-slate-900">
            <span className="font-semibold">{data.next_best_action.insured}</span>
            <span className="text-slate-500"> · {data.next_best_action.carrier} #{data.next_best_action.policy_number}</span>
            <div className="text-slate-600 mt-0.5">{data.next_best_action.rationale}</div>
          </div>
        </div>
      )}

      {/* Top to call today */}
      {data.top_to_call_today?.length > 0 && (
        <div className="divide-y divide-slate-100">
          <div className="px-5 py-2 text-[10px] uppercase tracking-wide font-semibold text-slate-500">Top to call today</div>
          {data.top_to_call_today.slice(0, 8).map(r => {
            const meta = STATE_LABEL[r.state] || { tone: 'slate', label: r.state };
            return (
              <div key={r.id} className="px-5 py-2.5 flex items-center gap-3">
                <div className="flex items-center justify-center h-9 w-12 rounded-md bg-slate-100 text-slate-700 font-semibold text-sm shrink-0 tabular-nums">
                  {r.priority_score}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <ClientNameLink name={r.client_name} openClientProfile={openClientProfile} className="font-semibold text-slate-900 hover:underline"/>
                    <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-${meta.tone}-50 text-${meta.tone}-700 border border-${meta.tone}-200`}>{meta.label}</span>
                    {r.save_probability && <span className="text-[10px] text-slate-500">Save: {r.save_probability}</span>}
                  </div>
                  <div className="text-xs text-slate-500 truncate mt-0.5">
                    {r.carrier} #{r.policy_number} · {r.writing_agent_name || 'unassigned'}
                    {r.days_since_last_transaction != null && <span> · {r.days_since_last_transaction}d since</span>}
                  </div>
                </div>
                {r.client_phone && (
                  <button
                    onClick={() => requestPhoneCall({ phone: r.client_phone, name: r.client_name })}
                    className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 hover:border-slate-300 transition-colors shrink-0"
                    title={`Call ${r.client_phone}`}
                    aria-label="Call"
                  >
                    <Phone size={14}/>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Data gaps */}
      {data.data_gaps?.length > 0 && (
        <div className="px-5 py-3 border-t border-slate-100 bg-amber-50/40">
          <div className="text-[10px] uppercase tracking-wide font-semibold text-amber-800 mb-1">Data gaps blocking complete scoring</div>
          <div className="text-xs text-slate-600 flex flex-wrap gap-x-3">
            {data.data_gaps.slice(0, 8).map(g => (
              <span key={g.field}><span className="font-semibold text-slate-900">{g.count}</span> need {g.field.replace(/_/g, ' ')}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Conservation report uploader ──────────────────────────────────
// Paste a carrier's "Policy Conservation Opportunities" report
// (Mutual of Omaha sample shipped with the build), the parser
// extracts every row, upserts into life_policies, creates a
// lead_profile per client tagged FOLLOW_UP_AI + ai_status=lapse_risk
// so the AI Policy Manager processes them on the next tick.
function ConservationReportUploader({ showToast }) {
  const [text, setText] = React.useState('');
  const [filename, setFilename] = React.useState('');
  const [parsing, setParsing] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const fileInputRef = React.useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFilename(file.name);
    // Plain-text files: read directly. PDFs: tell the operator to
    // paste the text (we don't ship a pdf.js extractor in the bundle).
    if (/\.txt$|\.csv$/i.test(file.name)) {
      const reader = new FileReader();
      reader.onload = () => setText(String(reader.result || ''));
      reader.readAsText(file);
    } else {
      showToast('PDF detected — open it in your viewer, select-all, and paste the text below.');
    }
  };

  const submit = async () => {
    const t = text.trim();
    if (!t) { showToast('Paste the report text first.'); return; }
    setParsing(true);
    setResult(null);
    try {
      const json = await apiFetch('/api/ai-agents/parse-report', {
        method: 'POST',
        body: JSON.stringify({ text: t, filename }),
      });
      setResult(json);
      const knownCents = json.known_premium_at_risk_cents || json.total_premium_at_risk_cents || 0;
      showToast(`Parsed ${json.unique_rows || json.rows_extracted} rows · ${json.policies_at_risk} at risk · $${(knownCents / 100).toLocaleString()} known premium at risk`);
    } catch (err) {
      showToast(`Parse failed: ${err.message || err}`);
    } finally {
      setParsing(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/40">
      <div className="text-xs font-semibold text-slate-700 mb-2">Upload conservation / lapse report</div>
      <p className="text-xs text-slate-500 mb-2">
        Paste the text from any MOO, Americo, Transamerica, or similar conservation report. The parser extracts every policy and queues the Policy Manager AI to reach out before the lapse date.
      </p>
      <div className="flex items-center gap-2 mb-2">
        <input ref={fileInputRef} type="file" accept=".txt,.csv,.pdf" onChange={onFile} className="hidden"/>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="text-xs font-medium px-3 py-1.5 rounded-md border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 text-slate-700"
        >
          Choose file…
        </button>
        {filename && <span className="text-xs text-slate-500 truncate">{filename}</span>}
      </div>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        rows={5}
        placeholder="Paste the raw report text here…"
        className="w-full border border-slate-300 rounded-md px-3 py-2 text-xs font-mono focus:outline-none focus:border-slate-500"
      />
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-[11px] text-slate-400">{text.length.toLocaleString()} chars</span>
        <button
          onClick={submit}
          disabled={parsing || !text.trim()}
          className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors"
        >
          {parsing ? 'Parsing…' : 'Parse + queue agent'}
        </button>
      </div>
      {result && <ConservationParseResult result={result}/>}
    </div>
  );
}

// Inline render of /api/ai-agents/parse-report response. Shows the
// contract-shape per-policy records the parser produced (state,
// priority, save probability, revenue at risk, data gaps) so the
// operator can verify classification immediately rather than waiting
// for the Book of Business panel to refresh.
function ConservationParseResult({ result }) {
  if (!result) return null;
  const records = result.contract_records || [];
  const STATE_TONE = {
    LAPSE_PENDING:   { bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200',    label: 'Lapse pending' },
    ISSUED_NOT_PAID: { bg: 'bg-amber-50',  text: 'text-amber-800',  border: 'border-amber-200',  label: 'Issued not paid' },
    PAYMENT_FAILURE: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', label: 'Payment failure' },
    REQUIREMENTS:    { bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200',   label: 'Requirements' },
  };
  return (
    <div className="mt-3 space-y-3">
      <div className="bg-white border border-slate-200 rounded-md p-3 text-xs text-slate-700">
        <div className="font-semibold text-slate-900 mb-1">
          {result.carrier || 'Carrier'} · {result.agent || 'Agent'} · {result.report_date || 'recent'}
          <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{result.format}</span>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <div><span className="font-semibold text-slate-900">{result.unique_rows}</span> unique rows ({result.duplicates_collapsed} duplicates collapsed)</div>
          <div><span className="font-semibold text-slate-900">{result.policies_at_risk}</span> at risk</div>
          <div><span className="font-semibold text-slate-900">{result.profiles_upserted}</span> queued for AI follow-up</div>
        </div>
        <div className="mt-1">
          <span className="font-semibold text-slate-900">${(result.known_premium_at_risk_cents / 100).toLocaleString()}</span> known premium at risk
          {result.unknown_premium_row_count > 0 && (
            <span className="text-amber-700 ml-2">· {result.unknown_premium_row_count} rows with UNKNOWN premium (cross-reference your commission ledger to fill)</span>
          )}
        </div>
      </div>
      {records.filter(r => r.state).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-100 text-xs font-semibold text-slate-700">Classified records</div>
          <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
            {records.filter(r => r.state).map((r, i) => {
              const tone = STATE_TONE[r.state] || STATE_TONE.REQUIREMENTS;
              return (
                <div key={`${r.policy_number}-${i}`} className="px-3 py-2 text-xs flex items-start gap-3">
                  <div className="flex items-center justify-center h-9 w-12 rounded-md bg-slate-100 text-slate-700 font-semibold tabular-nums shrink-0">
                    {r.priority_score != null ? r.priority_score : '—'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md ${tone.bg} ${tone.text} border ${tone.border}`}>{tone.label}</span>
                      {r.save_probability && <span className="text-[10px] font-semibold text-slate-600">Save: {r.save_probability}</span>}
                      <span className="font-semibold text-slate-900 truncate">{r.insured || '(no name)'}</span>
                      <span className="text-slate-400 tabular-nums">#{r.policy_number}</span>
                      {r.duplicate_count > 1 && <span className="text-[10px] text-slate-500">×{r.duplicate_count}</span>}
                      {r.state_conflict && <span className="text-[10px] text-amber-700 font-semibold">CONFLICT</span>}
                    </div>
                    <div className="text-slate-500 mt-0.5">
                      {r.carrier} · {r.agent || 'no agent on file'} · {r.days_since_last_transaction != null ? `${r.days_since_last_transaction}d since last transaction` : 'no transaction date'}
                    </div>
                    <div className="text-slate-500 mt-0.5">
                      Revenue at risk: <span className={r.revenue_at_risk_cents != null ? 'text-slate-900 font-semibold' : 'text-amber-700'}>{r.revenue_at_risk_label}</span>
                      {r.data_needed?.length > 0 && (
                        <span className="ml-2 text-amber-700">· Needs: {r.data_needed.join(', ')}</span>
                      )}
                    </div>
                    {r.notes && <div className="text-amber-700 mt-0.5">{r.notes}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Daily Action Queue ────────────────────────────────────────────
// The operator's "money list" for today. Reads /api/ai-agents/
// daily-queue and renders the top opportunities ranked by AI close-
// probability + expected revenue + urgency. Each row has a one-tap
// Call + Text + Hot button so the operator can fire without leaving
// the dashboard.
function DailyActionQueue({ currentUser, showToast = () => {}, onOpenLead = () => {} }) {
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState({ items: [], summary: {} });

  React.useEffect(() => {
    if (!currentUser?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const json = await apiFetch('/api/ai-agents/daily-queue');
        if (!cancelled) setData(json);
      } catch (err) {
        if (!cancelled) showToast(`Queue load failed: ${err.message || err}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.id, showToast]);

  const toggleHot = async (item) => {
    try {
      await apiFetch(`/api/ai-agents/profile?lead_key=${encodeURIComponent(item.lead_key)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tag_hot: !item.tag_hot }),
      });
      setData(d => ({
        ...d,
        items: d.items.map(i => i.lead_key === item.lead_key ? { ...i, tag_hot: !i.tag_hot } : i),
      }));
      showToast(item.tag_hot ? 'Removed HOT tag' : 'Tagged HOT_LEAD');
    } catch (err) {
      showToast(`Tag failed: ${err.message || err}`);
    }
  };

  if (loading) {
    return <SkeletonBlock lines={5}/>;
  }

  const items = data.items || [];
  const summary = data.summary || {};
  // Empty-state used to be a full instruction card; that read as a
  // bare placeholder next to the equally-empty Book of Business panel.
  // Render nothing instead — Setup checklist already covers next-steps
  // guidance. The queue re-appears as soon as the AI profiles at least
  // one lead.
  if (items.length === 0) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Sparkles size={14} className="text-slate-400"/> Today's action queue</h3>
          <div className="text-xs text-slate-500 mt-0.5">
            {items.length} of {summary.total_in_pipeline || 0} opportunities · {summary.hot_count || 0} hot · {summary.lapse_risk_count || 0} lapse-risk
          </div>
        </div>
        {summary.total_revenue_cents > 0 && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Potential</div>
            <div className="text-lg font-semibold text-slate-900 tabular-nums">${(summary.total_revenue_cents / 100).toLocaleString()}</div>
          </div>
        )}
      </div>
      <div className="divide-y divide-slate-100">
        {items.map(i => (
          <div key={i.lead_key} className="px-5 py-3 flex items-center gap-3 hover:bg-slate-50 cursor-pointer" onClick={() => onOpenLead(i.lead_key)}>
            <div className="flex items-center justify-center h-10 w-10 rounded-md bg-slate-100 text-slate-700 font-semibold text-sm shrink-0 tabular-nums">
              {i.close_probability != null ? `${i.close_probability}%` : '—'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-900 truncate">{i.name}</span>
                {i.tag_hot && <span className="text-[10px] font-semibold uppercase tracking-wide text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-md">Hot</span>}
                {i.ai_status === 'lapse_risk' && <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-md">Lapse risk</span>}
                {i.ai_status === 'payday_followup' && <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-700 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-md">Payday f/u</span>}
                {i.ai_status === 'spouse_review' && <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-700 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-md">Spouse review</span>}
              </div>
              <div className="text-xs text-slate-500 truncate mt-0.5">
                {(i.why || []).join(' · ')}
                {i.contact_time_hint && <span className="ml-1 text-slate-400">· {i.contact_time_hint}</span>}
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
              {i.expected_revenue_cents > 0 && (
                <div className="text-xs text-slate-500 tabular-nums hidden md:block">${(i.expected_revenue_cents / 100).toLocaleString()}</div>
              )}
              {i.phone && (
                <button
                  onClick={() => requestPhoneCall({ phone: i.phone, name: i.name })}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 hover:border-slate-300 transition-colors"
                  title={`Call ${i.phone}`}
                  aria-label="Call"
                >
                  <Phone size={14}/>
                </button>
              )}
              <button
                onClick={() => toggleHot(i)}
                className={`h-8 px-2 inline-flex items-center justify-center rounded-md border text-xs font-semibold transition-colors ${i.tag_hot ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:text-slate-900 hover:border-slate-300'}`}
                title="Toggle HOT_LEAD tag"
              >
                <Flame size={12}/>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Agent photo upload + URL field. Uses Vercel Blob via the existing
// /api/uploads/sign endpoint (same flow as ACA document uploads — image/*
// is on the allowedContentTypes list there). On success, writes the
// public blob URL straight into settings.agentPhotoUrl, which the
// public /api/public/agent-profile endpoint serves to the referral
// landing page. Drag-and-drop OR click-to-pick; both routes share the
// same handler.
function AgentPhotoField({ settings, setSettings, currentUser, showToast = () => {} }) {
  const [uploading, setUploading] = React.useState(false);
  const [dragOver, setDragOver]   = React.useState(false);
  // Sticky failure hint — when the upload fails we keep the message
  // visible next to the URL-paste fallback (so the operator sees the
  // workaround instead of dismissing the toast and giving up).
  const [lastError, setLastError] = React.useState(null);
  const inputRef = React.useRef(null);

  const handleFile = async (file) => {
    if (!file || uploading) return;
    if (!/^image\//.test(file.type)) {
      showToast('Pick an image file (JPG / PNG / WebP).', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('Image too big — keep it under 5 MB.', 'error');
      return;
    }
    setUploading(true);
    setLastError(null);
    try {
      // Supabase Storage upload (bucket: agent-photos, public read,
      // per-owner write via RLS — see migration 097).
      //
      // Path layout: <owner_id>/<timestamp>-<safe_filename>
      // The first segment MUST be the authed user's UUID — the
      // bucket's RLS policy compares (storage.foldername(name))[1]
      // against auth.uid(). Any mismatch → 403, no write.
      const ownerId = currentUser?.id;
      if (!ownerId) throw new Error('Not signed in — refresh the page and try again.');
      const ext = (file.name.match(/\.(jpe?g|png|webp)$/i)?.[1] || 'jpg').toLowerCase();
      const stamp = Date.now().toString(36);
      const path = `${ownerId}/${stamp}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('agent-photos')
        .upload(path, file, {
          contentType: file.type,
          upsert: false, // never overwrite — stamp varies, so each upload is new
          cacheControl: '604800', // 7 days — public URL changes per stamp anyway
        });
      if (upErr) throw upErr;
      // Public bucket → getPublicUrl works without signing. The URL
      // is stable as long as the object exists.
      const { data: pubData } = supabase.storage.from('agent-photos').getPublicUrl(path);
      const publicUrl = pubData?.publicUrl;
      if (!publicUrl) throw new Error('Upload succeeded but URL is empty — please try again.');
      setSettings(s => ({ ...s, agentPhotoUrl: publicUrl }));
      showToast('Photo uploaded — preview updated.');
    } catch (err) {
      // Surface the most useful piece. Supabase Storage errors carry
      // a .message + sometimes a .statusCode. The most common failure
      // mode after this migration runs is the bucket not existing
      // (operator's project is behind on migrations) — message there
      // reads "Bucket not found".
      const raw = String(err?.message || err);
      let friendly = raw;
      if (/bucket.not.found/i.test(raw)) {
        friendly = 'Photo storage is not set up yet on this Supabase project. An admin needs to run migration 097_agent_photos_bucket.sql. Meanwhile you can paste a hosted image URL below — it works the same.';
      } else if (/row.level.security|permission.denied|new.row.violates/i.test(raw)) {
        friendly = 'Sign-in expired or session mismatch — refresh the page and try the upload again.';
      } else if (/payload.too.large|too.big|file.size/i.test(raw)) {
        friendly = 'Image is too large. Keep it under 5 MB or paste a hosted URL instead.';
      } else if (/network|failed.to.fetch/i.test(raw)) {
        friendly = 'Network blip — check your connection and try again. Or paste a hosted URL below.';
      }
      setLastError(friendly);
      showToast(`Upload failed: ${friendly.slice(0, 140)}`, 'error');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <>
      <span className="text-sm text-slate-600">Headshot</span>
      <div className="mt-1 flex items-stretch gap-3 flex-wrap">
        {/* Current photo preview — anchors the field visually */}
        <div className={`h-20 w-20 shrink-0 rounded-full border-2 ${
          dragOver ? 'border-violet-400 bg-violet-50' : 'border-slate-200 bg-slate-100'
        } overflow-hidden flex items-center justify-center`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer?.files?.[0]; if (f) handleFile(f); }}
        >
          {settings.agentPhotoUrl ? (
            <img
              src={settings.agentPhotoUrl}
              alt="Headshot"
              className="h-full w-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <span className="text-2xl text-slate-400">📷</span>
          )}
        </div>
        <div className="flex-1 min-w-[240px] flex flex-col gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-xs font-bold uppercase tracking-tight px-3 py-2 rounded-md"
            >
              {uploading ? 'Uploading…' : (settings.agentPhotoUrl ? 'Replace photo' : 'Upload photo')}
            </button>
            {settings.agentPhotoUrl && (
              <button
                type="button"
                onClick={() => setSettings(s => ({ ...s, agentPhotoUrl: '' }))}
                disabled={uploading}
                className="text-xs text-slate-500 hover:text-red-700 font-medium px-2 py-2"
              >
                Remove
              </button>
            )}
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
          </div>
          {lastError && (
            <div className="bg-amber-50 border border-amber-300 rounded-md px-3 py-2 text-xs text-amber-900 leading-relaxed">
              <strong>Upload failed.</strong> {lastError}
            </div>
          )}
          <input
            value={settings.agentPhotoUrl || ''}
            onChange={e=>{ setSettings(s=>({...s, agentPhotoUrl: e.target.value.trim()})); setLastError(null); }}
            placeholder={lastError ? 'Paste a hosted image URL here ←' : '…or paste a hosted URL (LinkedIn, Gravatar, your own site)'}
            className={`w-full border rounded-lg px-3 py-2 font-mono text-xs ${
              lastError ? 'border-amber-400 ring-2 ring-amber-200' : 'border-slate-200'
            }`}
          />
          <p className="text-xs text-slate-500">
            Drag an image onto the circle, click Upload, or paste a URL. JPG / PNG / WebP, up to 5 MB.
          </p>
        </div>
      </div>
    </>
  );
}

// Small inline "Saved · just now" indicator next to the niche toggle
// row. Watches the niche array for changes — fades in for ~2.5 seconds
// after each save, then disappears. Persistence itself is handled by
// the existing settings useEffect; this only surfaces confirmation
// so the operator isn't left wondering whether the click took.
function NicheSavedIndicator({ value }) {
  const [shownAt, setShownAt] = React.useState(0);
  // useRef holds the previous value so we can detect a real change
  // (initial render shouldn't trigger the indicator).
  const prevRef = React.useRef();
  React.useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = value;
    if (prev === undefined) return; // first render
    const a = JSON.stringify(Array.isArray(prev) ? prev : []);
    const b = JSON.stringify(Array.isArray(value) ? value : []);
    if (a !== b) setShownAt(Date.now());
  }, [value]);
  // Auto-hide after 2.5s.
  const [, force] = React.useState(0);
  React.useEffect(() => {
    if (!shownAt) return;
    const t = setTimeout(() => force(x => x + 1), 2600);
    return () => clearTimeout(t);
  }, [shownAt]);
  const visible = shownAt && Date.now() - shownAt < 2500;
  if (!visible) return null;
  return (
    <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 inline-flex items-center gap-1 transition-opacity">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500"/>
      Saved
    </span>
  );
}

// "Replay value tour" card — clears the localStorage flag so the
// pain-point tour fires on the next render. Lives in Settings →
// Account so it's reachable but doesn't clutter the main panels.
function ReplayValueTourCard({ showToast = () => {} }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-semibold text-slate-900">Replay value tour</h3>
          <p className="text-xs text-slate-500 mt-0.5">2-minute interactive walkthrough of ANCHOR + the workflow engine + cross-sell scanner. Show it to a new agent on your team, or refresh on what's new.</p>
        </div>
        <button
          onClick={() => {
            try { localStorage.removeItem('crm:value_tour:v1'); } catch {}
            // Refresh re-mounts CommissionTracker which re-reads the flag.
            window.location.reload();
          }}
          className="bg-violet-600 hover:bg-violet-700 text-white font-semibold text-sm px-3 py-1.5 rounded whitespace-nowrap"
        >
          Replay tour
        </button>
      </div>
    </div>
  );
}

function SettingsView({ settings, setSettings, uploads, onDeleteSource, onClearAll, clientProfiles, setClientProfiles, lobConfig, setLobConfig, showToast, currentUser, credentialsBump, onResetAgentPassword, commissionRules = [], setCommissionRules = () => {}, transactions = [], producers = {}, getCarrier, getProduct, carrierCatalog = [], setCarrierCatalog = () => {}, onGotoTab = () => {} }) {
  // Settings page reorganized into 6 categories so it stops being a
  // 30-card scroll. Active category persists to localStorage so a
  // refresh (or revisiting Settings later) lands the operator back
  // where they were.
  const [activeCategory, setActiveCategory] = React.useState(() => {
    try { return localStorage.getItem('settingsCategory') || 'ai'; } catch { return 'ai'; }
  });
  const setCat = (c) => {
    setActiveCategory(c);
    try { localStorage.setItem('settingsCategory', c); } catch {}
  };
  const SETTINGS_CATEGORIES = [
    { key: 'ai',      label: 'AI & Automation', icon: '🤖' },
    { key: 'comms',   label: 'Comms & Calendar', icon: '📞' },
    { key: 'profile', label: 'Profile & Brand',  icon: '🎨' },
    { key: 'money',   label: 'Money',            icon: '💰' },
    { key: 'data',    label: 'Data & Imports',   icon: '📁' },
    { key: 'account', label: 'Account',          icon: '🔐' },
  ];

  return (
    <div className="space-y-4">
      {/* Top tab nav — pill row, sticky on scroll so the operator can
          jump between categories mid-page without losing context. */}
      <div className="bg-white border border-slate-200 rounded-xl p-2 sticky top-0 z-10 flex items-center gap-1 flex-wrap shadow-sm">
        {SETTINGS_CATEGORIES.map(c => (
          <button
            key={c.key}
            onClick={() => setCat(c.key)}
            className={`px-3 py-2 text-sm font-semibold rounded-lg inline-flex items-center gap-1.5 transition-colors ${
              activeCategory === c.key
                ? 'bg-slate-900 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <span>{c.icon}</span>
            {c.label}
          </button>
        ))}
      </div>

      {/* ── AI & AUTOMATION ────────────────────────────────────── */}
      {activeCategory === 'ai' && (
        <>
          <AiWalletView currentUser={currentUser} showToast={showToast}/>
          <AutomationControlPanel
            currentUser={currentUser}
            onGotoTab={onGotoTab}
            showToast={showToast}
            settings={settings}
            setSettings={setSettings}
          />
          <AgentToolApprovalsPanel currentUser={currentUser} showToast={showToast}/>
          <AiVoiceCard showToast={showToast}/>
          <AiAgentsCard currentUser={currentUser} showToast={showToast}/>
          <AiUsageCard currentUser={currentUser}/>
          <DeliveryAuditPanel currentUser={currentUser}/>
          <RequirementsAuditPanel currentUser={currentUser}/>
          <AddressBackfillPanel currentUser={currentUser} showToast={showToast}/>
        </>
      )}

      {/* ── COMMUNICATIONS & CALENDAR ──────────────────────────── */}
      {activeCategory === 'comms' && (
        <>
          <QuickStartAutomationsCard settings={settings} showToast={showToast}/>
          <TwilioHealthPanel />
          <A2pStatusCard currentUser={currentUser} showToast={showToast}/>
          <OperatorTextAlertsCard currentUser={currentUser} showToast={showToast}/>
          <GmailConnectionPanel showToast={showToast} />
          <AgentDigestPanel currentUser={currentUser} showToast={showToast}/>
          <ConnectDiscordCard currentUser={currentUser} showToast={showToast}/>
          <GoogleCalendarPanel currentUser={currentUser} showToast={showToast}/>
          <A2pSetupPanel />
          <KixiePowerDialPanel showToast={showToast}/>
          <ImessagePanel />

          <SelfReminderPanel currentUser={currentUser} showToast={showToast}/>
          <AvailabilityPanel currentUser={currentUser} showToast={showToast}/>
          <AppointmentRemindersPanel currentUser={currentUser} showToast={showToast}/>
          <LifecycleTemplatesPanel currentUser={currentUser} settings={settings} setSettings={setSettings} showToast={showToast}/>
          <CertTrackerCard currentUser={currentUser} showToast={showToast}/>
          <ReferralAutoTouchPanel
            settings={settings}
            setSettings={setSettings}
            showToast={showToast}
          />
        </>
      )}

      {/* ── PROFILE & BRANDING ─────────────────────────────────── */}
      {activeCategory === 'profile' && (
        <>
          <BrandingPanel settings={settings} setSettings={setSettings} showToast={showToast}/>
        </>
      )}

      {/* ── MONEY (commission settings + carrier/product catalog) */}
      {activeCategory === 'money' && (
        <>
          <CarrierCatalogPanel
            catalog={carrierCatalog}
            setCatalog={setCarrierCatalog}
            transactions={transactions}
            getCarrier={getCarrier}
            getProduct={getProduct}
            showToast={showToast}
          />
          <CommissionRulesPanel
            rules={commissionRules}
            setRules={setCommissionRules}
            transactions={transactions}
            producers={producers}
            getCarrier={getCarrier}
            getProduct={getProduct}
            showToast={showToast}
          />
        </>
      )}

      {/* ── DATA & IMPORTS ─────────────────────────────────────── */}
      {activeCategory === 'data' && (
        <>
          <ClientCsvImportCard profiles={clientProfiles} setProfiles={setClientProfiles} showToast={showToast}/>
          <LobConfigCard lobConfig={lobConfig} setLobConfig={setLobConfig}/>
        </>
      )}

      {/* ── ACCOUNT (team, password, billing, danger) ──────────── */}
      {activeCategory === 'account' && (
        <>
          {currentUser?.role === 'admin' && (
            <ManageAgentsPanel currentUser={currentUser} producers={producers}/>
          )}
          <TeamInvitePanel currentUser={currentUser} showToast={showToast}/>
          <BillingCard currentUser={currentUser} showToast={showToast}/>
          <ChangePasswordCard showToast={showToast}/>
          <MfaEnrollmentCard showToast={showToast}/>
          <ReplayValueTourCard showToast={showToast}/>
          <DemoLaunchAdminCard currentUser={currentUser} showToast={showToast}/>
        </>
      )}
      {/* Inline blocks that didn't have their own component — wrapped
          per-category below. Original ordering preserved within each
          category for visual continuity. */}

      {activeCategory === 'money' && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-bold mb-4">Commission settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm text-slate-600">Your name/label</span>
              <input value={settings.ownerName} onChange={e=>setSettings(s=>({...s, ownerName:e.target.value}))} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2"/>
            </label>
            <label className="block">
              <span className="text-sm text-slate-600">Your phone</span>
              <input value={settings.ownerPhone || ''} onChange={e=>setSettings(s=>({...s, ownerPhone:e.target.value}))} placeholder="(555) 123-4567" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2"/>
              <p className="text-xs text-slate-500 mt-1">Used as the [Agent Phone] token in Leads SMS templates.</p>
            </label>
            <label className="block">
              <span className="text-sm text-slate-600">Owner Producer #</span>
              <input value={settings.ownerProdNum || ''} onChange={e=>setSettings(s=>({...s, ownerProdNum:e.target.value.trim()}))} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 font-mono"/>
              <p className="text-xs text-slate-500 mt-1">Your personal producer #. Used to distinguish your own production from your team's commissions.</p>
            </label>
            <label className="block">
              <span className="text-sm text-slate-600">Agent split % of premium</span>
              <input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={settings.camPercent ?? 0}
                onChange={e=>setSettings(s=>({...s, camPercent: Math.max(0, Math.min(100, Number(e.target.value) || 0))}))}
                className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 font-mono"
              />
              <p className="text-xs text-slate-500 mt-1">The % of premium your producing agents take. Your override = team net commission − (premium × this %). Set to 0 if you haven't configured a split yet.</p>
            </label>
          </div>
        </div>
      )}

      {activeCategory === 'ai' && (
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-bold mb-1">AI auto-delivery</h3>
        <p className="text-sm text-slate-500 mb-3">
          If you don't mark a Policy Delivery Summary as sent within 3 days of issue, the AI emails it to the client on your behalf. Cleans up the "I forgot" gap and keeps clients from sitting policy-in-hand without paperwork.
        </p>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!settings.aiAutoSendDelivery}
            onChange={e => setSettings(s => ({ ...s, aiAutoSendDelivery: e.target.checked }))}
            className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
          />
          <span className="text-sm text-slate-700 leading-relaxed">
            <span className="font-semibold text-slate-900">Auto-send pending deliveries on day 3</span>
            <br/>
            <span className="text-xs text-slate-500">
              Gates: client must have email, policy issued 3+ days ago, no manual delivery stamp. Email comes from your reply-to address; you'll be CC'd on every send via the email_digest_log audit trail.
            </span>
          </span>
        </label>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm text-slate-600">Default brand for auto-sends</span>
            <select
              value={settings.deliveryDefaultBrand || 'custom'}
              onChange={e=>setSettings(s=>({...s, deliveryDefaultBrand: e.target.value}))}
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="for_the_people">For The People</option>
              <option value="legacy_wealth">Legacy Wealth</option>
              <option value="shark_financial">Shark Financial</option>
              <option value="custom">Custom (use agency name)</option>
            </select>
          </label>
          <label className="block">
            <span className="text-sm text-slate-600">License # (optional, shown on delivery)</span>
            <input
              value={settings.agentLicense || ''}
              onChange={e=>setSettings(s=>({...s, agentLicense: e.target.value}))}
              placeholder="TX-1234567"
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono"
            />
          </label>
        </div>
        <label className="block mt-4">
          <span className="text-sm text-slate-600">Default "why" line (optional — used in auto-sends)</span>
          <textarea
            value={settings.deliveryWhyDefault || ''}
            onChange={e=>setSettings(s=>({...s, deliveryWhyDefault: e.target.value.slice(0, 400)}))}
            rows={2}
            placeholder='e.g. "Built to protect what matters most to your family — your home, your income, your peace of mind."'
            className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
          />
          <p className="text-xs text-slate-500 mt-1">If blank, auto-sends skip the "why" section. Personalized notes always win — use the modal in Clients to write a per-client one.</p>
        </label>

        {/* AI cron behavior — payment reminder intelligence toggles.
            Both default OFF for cost + behavior safety. */}
        <div className="mt-5 pt-4 border-t border-slate-100">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Payment reminder AI</div>
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!settings.aiChannelSelection}
                onChange={e => setSettings(s => ({ ...s, aiChannelSelection: e.target.checked }))}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-sm text-slate-700 leading-relaxed">
                <span className="font-semibold text-slate-900">AI channel selection</span>
                <br/>
                <span className="text-xs text-slate-500">
                  When a client has 2+ inbound replies on a specific channel (SMS or email), the cron switches to that channel for them, overriding the step's configured channel. Never overrides call-task steps. No extra API cost — pure DB heuristic.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!settings.aiAdaptiveTone}
                onChange={e => setSettings(s => ({ ...s, aiAdaptiveTone: e.target.checked }))}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-sm text-slate-700 leading-relaxed">
                <span className="font-semibold text-slate-900">AI-adaptive tone for at-risk clients</span>
                <br/>
                <span className="text-xs text-slate-500">
                  For medium / high-risk clients (1+ NSF in 90 days), rewrites the base template through AI in a tone calibrated to the risk level. <strong>Costs ~1 AI call per medium+ client send.</strong> Capped at 50 AI renders per cron run as a safety stop. Low-risk clients always use the standard template — no extra cost.
                </span>
              </span>
            </label>
          </div>
        </div>
      </div>
      )}

      {/* Duplicate panel renders (DeliveryAudit, RequirementsAudit,
          AddressBackfill, TwilioHealth, Gmail, AutomationControl,
          ReferralAutoTouch) were removed here — they now live inside
          the activeCategory='ai' / 'comms' blocks at the top of this
          function. */}

      {activeCategory === 'profile' && (
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-bold mb-1">Public referral profile</h3>
        <p className="text-sm text-slate-500 mb-4">
          What people see on your referral page. Empty fields fall back to defaults.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <AgentPhotoField
              settings={settings}
              setSettings={setSettings}
              currentUser={currentUser}
              showToast={showToast}
            />
          </div>
          <label className="block">
            <span className="text-sm text-slate-600">Title</span>
            <input
              value={settings.agentTitle || ''}
              onChange={e=>setSettings(s=>({...s, agentTitle: e.target.value}))}
              placeholder="Licensed Insurance Agent"
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2"
            />
            <p className="text-xs text-slate-500 mt-1">
              Defaults to "Licensed Insurance Agent" if blank.
            </p>
          </label>
          <label className="block">
            <span className="text-sm text-slate-600">Agency name</span>
            <input
              value={settings.agencyName || ''}
              onChange={e=>setSettings(s=>({...s, agencyName: e.target.value}))}
              placeholder="Pulls from your subscription if blank"
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2"
            />
          </label>
          <label className="block md:col-span-2">
            <span className="text-sm text-slate-600">Bio / fun facts</span>
            <textarea
              value={settings.agentBio || ''}
              onChange={e=>setSettings(s=>({...s, agentBio: e.target.value.slice(0, 400)}))}
              placeholder="Licensed in TX, FL, GA · Specializing in final expense + Medicare · Dad of 2 + dog dad · Reach me anytime."
              rows={3}
              maxLength={400}
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
            />
            <p className="text-xs text-slate-500 mt-1">
              Short blurb shown on your referral page. {(settings.agentBio || '').length}/400 chars.
            </p>
          </label>

          {/* ── Headline + CTA ─────────────────────────────────── */}
          <label className="block md:col-span-2">
            <span className="text-sm text-slate-600">Headline</span>
            <input
              value={settings.agentHeadline || ''}
              onChange={e=>setSettings(s=>({...s, agentHeadline: e.target.value.slice(0, 90)}))}
              placeholder='e.g. "Your family deserves coverage that picks up the phone."'
              maxLength={90}
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2"
            />
            <p className="text-xs text-slate-500 mt-1">
              Short tagline above your bio. {(settings.agentHeadline || '').length}/90 chars.
            </p>
          </label>
          <label className="block">
            <span className="text-sm text-slate-600">Call-to-action button text</span>
            <input
              value={settings.referralCtaText || ''}
              onChange={e=>setSettings(s=>({...s, referralCtaText: e.target.value.slice(0, 40)}))}
              placeholder='Defaults to "Get a quote"'
              maxLength={40}
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2"
            />
            <p className="text-xs text-slate-500 mt-1">
              Button text on the form. e.g. "Get a quote", "Book a call", "Get started"
            </p>
          </label>
          <label className="block">
            <span className="text-sm text-slate-600">States licensed</span>
            <input
              value={settings.statesLicensed || ''}
              onChange={e=>setSettings(s=>({...s, statesLicensed: e.target.value.slice(0, 120)}))}
              placeholder="TX, FL, GA, AZ"
              maxLength={120}
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2"
            />
            <p className="text-xs text-slate-500 mt-1">
              Comma-separated 2-letter codes. Shown as credibility badges.
            </p>
          </label>

          {/* ── Colors & branding ───────────────────────────────── */}
          <div className="md:col-span-2 border border-slate-200 rounded-lg p-4 bg-slate-50">
            <div className="text-sm font-bold text-slate-900 mb-1">🎨 Colors</div>
            <p className="text-xs text-slate-500 mb-3">
              Customize how your referral page looks. Empty fields fall back to platform defaults
              (emerald primary, slate text).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-semibold text-slate-700">Brand color</span>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.brandColor || '#10b981'}
                    onChange={e=>setSettings(s=>({...s, brandColor: e.target.value}))}
                    className="h-10 w-12 border border-slate-200 rounded-md cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.brandColor || ''}
                    onChange={e=>{
                      const v = e.target.value.trim();
                      if (v === '' || /^#[0-9a-fA-F]{0,6}$/.test(v)) setSettings(s=>({...s, brandColor: v}));
                    }}
                    placeholder="#10b981"
                    className="flex-1 border border-slate-200 rounded-md px-2 py-1.5 text-sm font-mono"
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Primary — buttons, links, headers.</p>
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-700">Accent color</span>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.accentColor || '#0ea5e9'}
                    onChange={e=>setSettings(s=>({...s, accentColor: e.target.value}))}
                    className="h-10 w-12 border border-slate-200 rounded-md cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.accentColor || ''}
                    onChange={e=>{
                      const v = e.target.value.trim();
                      if (v === '' || /^#[0-9a-fA-F]{0,6}$/.test(v)) setSettings(s=>({...s, accentColor: v}));
                    }}
                    placeholder="#0ea5e9"
                    className="flex-1 border border-slate-200 rounded-md px-2 py-1.5 text-sm font-mono"
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Secondary — chips, hover, accents.</p>
              </label>
            </div>
            {/* Color preview strip */}
            <div className="mt-3 flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Preview</span>
              <div
                className="px-3 py-1.5 rounded-md text-xs font-bold text-white"
                style={{ background: settings.brandColor || '#10b981' }}
              >
                {settings.referralCtaText || 'Get a quote'}
              </div>
              <div
                className="px-2 py-1 rounded-md text-xs font-bold"
                style={{ background: (settings.accentColor || '#0ea5e9') + '20', color: settings.accentColor || '#0ea5e9' }}
              >
                accent chip
              </div>
            </div>
          </div>

          {/* ── Direct contact ──────────────────────────────────── */}
          <label className="block">
            <span className="text-sm text-slate-600">Direct phone</span>
            <input
              type="tel"
              value={settings.contactPhone || ''}
              onChange={e=>setSettings(s=>({...s, contactPhone: e.target.value.slice(0, 24)}))}
              placeholder="+1 555 123 4567"
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2"
            />
            <p className="text-xs text-slate-500 mt-1">
              Optional — surfaces a "Call now" button on your page.
            </p>
          </label>
          <label className="block">
            <span className="text-sm text-slate-600">Booking link</span>
            <input
              type="url"
              value={settings.bookingUrl || ''}
              onChange={e=>setSettings(s=>({...s, bookingUrl: e.target.value.slice(0, 240)}))}
              placeholder="https://calendly.com/your-name/15min"
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono"
            />
            <p className="text-xs text-slate-500 mt-1">
              Calendly / Cal.com URL — surfaces a "Book a call" button.
            </p>
          </label>

          {/* ── Niches you sell ──────────────────────────────────────
              Drives the per-niche landing page engine. One niche → that
              niche's full dedicated page (IUL = tayloriul-style, Health
              = subsidy estimator, Medicare = plan-types + windows).
              Two or three → chooser page where each card reveals the
              FULL niche section. Zero niches selected → generic referral
              form (the legacy default). */}
          <div className="block md:col-span-2">
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <span className="text-sm text-slate-600 font-semibold">What lines do you sell?</span>
              <NicheSavedIndicator value={settings.referralNiches}/>
            </div>
            <p className="text-xs text-slate-500 mt-0.5 mb-2">
              Toggle the products you write. Your referral page adapts: one toggle on shows
              that niche's full landing page; two or three shows a chooser so prospects pick.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {[
                { key: 'life',     label: 'Life / IUL',     sub: 'IUL · Term · FEX · Whole Life'      },
                { key: 'health',   label: 'Health / ACA',   sub: 'Marketplace · Short-term · Ancillary'},
                { key: 'medicare', label: 'Medicare',       sub: 'Advantage · Supplement · PDP'        },
              ].map(opt => {
                const enabled = Array.isArray(settings.referralNiches)
                  && settings.referralNiches.includes(opt.key);
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setSettings(s => {
                      const current = Array.isArray(s.referralNiches) ? s.referralNiches : [];
                      const next = enabled
                        ? current.filter(n => n !== opt.key)
                        : [...current, opt.key];
                      return { ...s, referralNiches: next };
                    })}
                    className={`text-left rounded-lg border-2 p-3 transition-colors ${
                      enabled
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-sm font-bold ${enabled ? 'text-emerald-900' : 'text-slate-700'}`}>
                        {opt.label}
                      </span>
                      <span className={`text-[10px] font-black uppercase tracking-wider rounded-full px-2 py-0.5 ${
                        enabled ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {enabled ? 'ON' : 'OFF'}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{opt.sub}</div>
                  </button>
                );
              })}
            </div>
            {(!Array.isArray(settings.referralNiches) || settings.referralNiches.length === 0) && (
              <p className="text-[11px] text-amber-700 mt-2">
                ⚠ No niches selected — your referral page will show the legacy generic form.
                Turn on at least one for the niche-specific landing experience.
              </p>
            )}
          </div>
        </div>
        {/* Live preview — the top of the referral page renders these. */}
        <div className="mt-4 border-t border-slate-200 pt-4">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold mb-2">Live preview</div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-center gap-3">
            {settings.agentPhotoUrl ? (
              <img
                src={settings.agentPhotoUrl}
                alt={settings.ownerName || 'Agent'}
                className="h-14 w-14 rounded-full object-cover border-2 border-white shadow"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <div className="h-14 w-14 rounded-full bg-slate-300 flex items-center justify-center text-slate-600 font-bold text-lg">
                {(settings.ownerName || 'A').slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-bold text-slate-900 truncate">{settings.ownerName || 'Your name'}</div>
              <div className="text-xs text-slate-600 truncate">{settings.agentTitle || 'Licensed Insurance Agent'}</div>
              {settings.agencyName && <div className="text-xs text-slate-500 truncate">{settings.agencyName}</div>}
            </div>
          </div>
          {settings.agentHeadline && (
            <div className="mt-2 text-sm font-bold leading-snug px-3" style={{ color: settings.brandColor || '#0f172a' }}>
              {settings.agentHeadline}
            </div>
          )}
          {settings.agentBio && (
            <div className="mt-2 text-xs text-slate-600 leading-relaxed bg-slate-50 border border-slate-200 rounded-md p-3">
              {settings.agentBio}
            </div>
          )}
          {(settings.contactPhone || settings.bookingUrl) && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {settings.contactPhone && (
                <div className="text-[11px] font-bold px-2 py-1 rounded-md text-white" style={{ background: settings.brandColor || '#10b981' }}>
                  📞 Call now
                </div>
              )}
              {settings.bookingUrl && (
                <div className="text-[11px] font-bold px-2 py-1 rounded-md border-2" style={{ borderColor: settings.brandColor || '#10b981', color: settings.brandColor || '#10b981' }}>
                  📅 Book a call
                </div>
              )}
            </div>
          )}
          {settings.statesLicensed && (
            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Licensed in</span>
              {String(settings.statesLicensed).split(',').slice(0, 12).map((s, i) => (
                <span key={i} className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border" style={{ background: (settings.accentColor || '#0ea5e9') + '15', color: settings.accentColor || '#0ea5e9', borderColor: (settings.accentColor || '#0ea5e9') + '40' }}>
                  {String(s).trim().toUpperCase()}
                </span>
              ))}
            </div>
          )}
          {Array.isArray(settings.referralNiches) && settings.referralNiches.length > 0 && (
            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Page renders</span>
              {settings.referralNiches.map(n => (
                <span
                  key={n}
                  className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200"
                >
                  {n === 'life' ? 'Life / IUL' : n === 'health' ? 'Health / ACA' : 'Medicare'}
                </span>
              ))}
              <span className="text-[10px] text-slate-500">
                {settings.referralNiches.length === 1
                  ? '· dedicated landing page'
                  : '· chooser with both sections inline'}
              </span>
            </div>
          )}
        </div>
      </div>
      )}

      {activeCategory === 'data' && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-bold mb-1">Uploaded files</h3>
          <p className="text-sm text-slate-500 mb-3">Delete a file to remove all its transactions from the system.</p>
          <div className="space-y-1">
            {uploads.length === 0 && <div className="text-sm text-slate-500">No uploads yet</div>}
            {uploads.map(u => (
              <div key={u.name} className="flex items-center justify-between border border-slate-100 rounded-lg p-2">
                <div className="min-w-0 flex-1 flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-bold flex-shrink-0 ${u.type==='pdf'?'bg-red-100 text-red-700':'bg-emerald-100 text-emerald-700'}`}>
                    {u.type==='pdf'?'PDF':'XLS'}
                  </span>
                  <div className="min-w-0">
                    <div className="font-mono text-xs truncate">{u.name}</div>
                    <div className="text-xs text-slate-500">{u.rows} rows · {new Date(u.at).toLocaleString()}</div>
                  </div>
                </div>
                <button onClick={()=>onDeleteSource(u.name)} className="text-red-600 hover:bg-red-50 p-2 rounded-lg flex-shrink-0"><Trash2 size={14}/></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeCategory === 'account' && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5">
          <h3 className="font-bold text-red-900 mb-2">Danger zone</h3>
          <button onClick={onClearAll} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold">Clear all data</button>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// SMS workflow engine — Phase 1 read-only shell.
//
// Three sub-tabs all reading directly from Supabase via RLS-scoped
// browser client:
//   • Workflows     — workflows + steps + templates, edit comes Phase 2
//   • Conversations — messages grouped by lead_id
//   • Appointments  — appointments sorted by scheduled_for
//
// Phase 1 is read-only on purpose: until A2P 10DLC + SMS_SEND_ENABLED
// are turned on, the cron is in dry-run mode and these panels exist so
// the operator can verify the engine is running, see what would have
// been sent, and watch the inbox + appointments populate as drips fire
// against test leads.
// ────────────────────────────────────────────────────────────────────

function SmsEngineView({ currentUser, transactions = [], leads = [], setLeads = () => {}, showToast = () => {} }) {
  const [sub, setSub] = useState('workflows'); // workflows | conversations | appointments | coverage

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl p-1 inline-flex items-center gap-1">
        {[
          ['workflows',     'Workflows'],
          ['conversations', 'Conversations'],
          ['appointments',  'Appointments'],
          ['coverage',      'Coverage'],
        ].map(([id, label]) => (
          <button key={id} onClick={() => setSub(id)} className={`px-3 py-1.5 text-sm font-medium rounded-lg ${sub === id ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
            {label}
          </button>
        ))}
      </div>

      <SmsDryRunBanner/>

      {sub === 'workflows'     && <SmsWorkflowsPanel currentUser={currentUser}/>}
      {sub === 'conversations' && <SmsConversationsPanel currentUser={currentUser}/>}
      {sub === 'appointments'  && <SmsAppointmentsPanel currentUser={currentUser}/>}
      {sub === 'coverage'      && <SmsCoveragePanel transactions={transactions} leads={leads} setLeads={setLeads} showToast={showToast}/>}
    </div>
  );
}

function SmsDryRunBanner() {
  // The flag lives on the server (SMS_SEND_ENABLED) — the browser can't
  // read it directly. We show a generic safety banner here; once an
  // operator turns the flag on, they should update this copy or hide
  // the banner from settings.
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900 flex items-start gap-2">
      <AlertTriangle size={16} className="mt-0.5 flex-shrink-0"/>
      <div>
        <div className="font-semibold">Dry-run mode (no real SMS will send)</div>
        <div className="text-amber-800/80 text-xs mt-0.5">
          Texts are written to the messages log but not delivered until <code className="bg-amber-100 px-1 rounded">SMS_SEND_ENABLED=true</code> is set on Vercel and your Relay A2P 10DLC campaign is approved. See <code className="bg-amber-100 px-1 rounded">SMS_SETUP.md</code> for the checklist.
        </div>
      </div>
    </div>
  );
}

// ── Preset workflow definitions for the "+ New workflow" picker ────
// Operators can pick a preset to spin up a new workflow without having
// to copy-paste step bodies. Edit these in lockstep with the seed
// migrations (007 for MP, 008 for IUL) — drift between the in-app
// preset and the migration only shows up on new-operator signup but
// it'll confuse anyone comparing the two sources.

const PRESET_MP = {
  name: 'MP Aged Lead 10-Touch',
  trigger: 'lead_imported',
  leadSourceFilter: 'mortgage_protection',
  steps: [
    { waitDays:  0, name: 'MP-01 Welcome ping',          body: 'Hi {first}, this is {agent}, the licensed field underwriter assigned to the mortgage protection request for your home in {state}. Still something you want squared away, or did you get it handled? A quick yes or no is fine. Reply STOP to opt out' },
    { waitDays:  2, name: 'MP-02 Day 2 nudge',           body: "Hey {first}, {agent} again — following up so your request doesn't sit in my file unanswered. Most folks just never got shown their options. Want me to take 10 minutes by phone and walk you through them?" },
    { waitDays:  3, name: 'MP-03 Day 3 pain point',      body: "{first}, quick one — if something happened to you tomorrow, would the mortgage be paid off for your family, or would they have to figure it out? That's the gap this covers. Worth a 10-minute call?" },
    { waitDays:  4, name: 'MP-04 Day 4 new programs',    body: "Hi {first} — some newer programs came out for homeowners in {state} since you sent your request: no-exam options and lower rates at certain ages. Can I check what you'd qualify for? Takes 10 min." },
    { waitDays:  5, name: 'MP-05 Day 5 spouse loop-in',  body: "{first}, does your spouse know about the mortgage protection request you put in? This is usually a both-of-you decision — happy to do the call when you're both home. Evenings work?" },
    { waitDays:  6, name: 'MP-06 Day 6 alt-time ask',    body: "Just tried to reach you about the mortgage protection file, {first}. I've got {time_1} or {time_2} open tonight — which is better?" },
    { waitDays:  7, name: 'MP-07 Day 7 sort-the-pile',   body: "{first}, honest question — is this a 'not interested' or a 'life got busy'? No pressure either way, I just need to know which pile to put your file in." },
    { waitDays: 10, name: 'MP-08 Day 10 closing the file', body: "{first}, I'm required to close out your mortgage protection file this week if I don't hear back. Before I mark it 'not interested' — was that your decision, or did it just slip?" },
    { waitDays: 20, name: 'MP-09 Day 20 reopen',         body: 'Hi {first} — reopening files this month for {state} homeowners who never finished their mortgage protection request. Rates are based on age, so waiting only ever costs more. 10 minutes this week?' },
    { waitDays: 30, name: 'MP-10 Day 30 sign-off',       body: "Last one from me, {first}. If anything changes — refinance, new home, health scare in the family — text me here and I'll pull your file back up. Take care." },
  ],
};

const PRESET_IUL = {
  name: 'IUL Aged Lead 8-Touch',
  trigger: 'lead_imported',
  leadSourceFilter: 'iul',
  steps: [
    { waitDays:  0, name: 'IUL-01 Welcome',              body: 'Hi {first}, this is {agent}, the licensed advisor following up on the IUL request from your home in {state}. Still looking at building tax-free retirement income, or did the timing change? Reply STOP to opt out' },
    { waitDays:  3, name: 'IUL-02 Day 3 tax angle',      body: '{first}, quick thought — most folks save in 401(k)s where Uncle Sam takes a cut in retirement. IUL is one of the few accounts where the income comes out tax-free. Worth a 10-min look?' },
    { waitDays:  6, name: 'IUL-03 Day 6 numbers',        body: 'Hi {first}, ran a quick projection — for someone your age contributing what you mentioned, an IUL can throw off six figures of tax-free retirement income. Want me to walk through the numbers? 10 minutes.' },
    { waitDays: 10, name: 'IUL-04 Day 10 downside floor', body: "{first}, the part most people miss about IUL — when the market drops, you're at zero, not negative. That downside floor is why some advisors call it the 'sleep at night' account. Couple minutes to compare to what you've got?" },
    { waitDays: 15, name: 'IUL-05 Day 15 tax bracket',   body: 'Hi {first} — with where tax rates are heading, paying tax now on a smaller balance and pulling it tax-free later is usually the better math. Open to a 10-min conversation about whether IUL fits your situation?' },
    { waitDays: 21, name: 'IUL-06 Day 21 living benefits', body: "{first}, one underrated piece of IUL: you can access cash value while you're alive, tax-free, via policy loans. So it doubles as an emergency fund, college fund, or business capital line. Worth comparing to your current setup?" },
    { waitDays: 30, name: 'IUL-07 Day 30 sort-the-pile', body: "{first}, honest question — is this a 'not for me' or a 'never got around to it'? Either is fine, I just need to know which pile to put your file in before I close it out this week." },
    { waitDays: 45, name: 'IUL-08 Day 45 sign-off',      body: "Last one from me, {first}. If your tax situation, income, or retirement plan shifts — text me here and I'll pull your file back up. Take care." },
  ],
};

// Policy milestone presets. Each is a single-step workflow that fires
// when a client's policy hits the configured age. The body is an
// `[AI: ...]` instruction — the engine generates the actual SMS at
// send time via Anthropic, so every message is tailored to the
// client's carrier/issue date/premium.
const PRESET_PM_30 = {
  name: 'Policy Milestone — 30-Day Welcome',
  trigger: 'policy_milestone',
  leadSourceFilter: null,
  milestoneDays: 30,
  steps: [
    { waitDays: 0, name: 'PM-30 Welcome', body: '[AI: Write a short, warm 30-day welcome text from {agent} to {first} who took out a {carrier} policy on {issue_date} at {premium}/mo. Single message, 1-2 sentences max. Thank them for the business, confirm everything looks good on the carrier\'s side, and offer to answer any questions. End with "Reply STOP to opt out".]' },
  ],
};
const PRESET_PM_90 = {
  name: 'Policy Milestone — 90-Day Check-In',
  trigger: 'policy_milestone',
  leadSourceFilter: null,
  milestoneDays: 90,
  steps: [
    { waitDays: 0, name: 'PM-90 Check-in', body: '[AI: Write a brief 90-day check-in text from {agent} to {first}, who has a {carrier} policy issued {issue_date} at {premium}/mo. 1-2 sentences. Ask if anything has changed in their life since taking out the policy (new home, new baby, job change) since that often means coverage needs updating. End with "let me know" rather than asking for a call.]' },
  ],
};
const PRESET_PM_180 = {
  name: 'Policy Milestone — 6-Month Review',
  trigger: 'policy_milestone',
  leadSourceFilter: null,
  milestoneDays: 180,
  steps: [
    { waitDays: 0, name: 'PM-180 Review', body: '[AI: Write a 6-month review text from {agent} to {first}, who has a {carrier} policy issued {issue_date} at {premium}/mo. Single message, 1-2 sentences max. Tone: friendly, professional, not salesy. Mention you\'re doing a quick 6-month check-in, ask if everything\'s still working for them, and offer a 10-minute call if anything\'s changed.]' },
  ],
};
const PRESET_PM_365 = {
  name: 'Policy Milestone — 12-Month Anniversary',
  trigger: 'policy_milestone',
  leadSourceFilter: null,
  milestoneDays: 365,
  steps: [
    { waitDays: 0, name: 'PM-365 Anniversary', body: '[AI: Write a 1-year anniversary text from {agent} to {first}, who has a {carrier} policy issued {issue_date} at {premium}/mo. 1-2 sentences. Congratulate them on a year, mention you\'re doing annual reviews this month, and offer a 10-min call to make sure the coverage still fits where their life is now. Friendly, not salesy.]' },
  ],
};

// Payment reminder — N days before each policy's monthly draft. Pure
// chargeback prevention: NSF and "I forgot today was the draft" are
// the two leading lapse causes in MP, and a 3-day heads-up text fixes
// both. Uses {premium} + {carrier} from the lead's closed-policy
// fields; no policy_id needed.
const PRESET_PAYMENT = {
  name: 'Payment Reminder — 3 Days Before Draft',
  trigger: 'payment_reminder',
  leadSourceFilter: null,
  leadDaysBefore: 3,
  steps: [
    { waitDays: 0, name: 'PR-3 Payment heads-up', body: '[AI: Write a friendly heads-up text from {agent} to {first}, reminding them their {carrier} policy draft for {premium} is coming up in 3 days. 1-2 sentences, calm and non-alarmist. The goal is to give them time to confirm funds are available. End with "let me know if anything\'s changed".]' },
  ],
};

// Birthday — fires on the lead's birthday (or N days before via
// lead_days_before). Relationship touch, not a sales touch.
const PRESET_BIRTHDAY = {
  name: 'Birthday Wishes — AI personalized',
  trigger: 'birthday',
  leadSourceFilter: null,
  leadDaysBefore: 0,
  steps: [
    { waitDays: 0, name: 'BD-1 Birthday text', body: '[AI: Write a brief, warm birthday text from {agent} to {first}. 1-2 sentences, genuine, NOT salesy. Mention you hope they have a great day and that you\'re glad to be their agent. Do NOT pitch additional coverage or mention reviews. End with a simple "happy birthday".]' },
  ],
};

// Conservation save — fires when a new lapse-warning event lands from
// a parsed conservation report. The browser auto-enrolls matched
// leads after each report parse; the engine sends and the AI handles
// any reply.
const PRESET_CONSERVATION = {
  name: 'Conservation Save — Auto Text',
  trigger: 'conservation_event',
  leadSourceFilter: null,
  steps: [
    { waitDays: 0, name: 'CS-1 Save the policy', body: '[AI: Write a warm save-the-policy text from {agent} to {first} whose {carrier} policy just had a draft issue. 1-2 sentences. Acknowledge it happens, offer to help fix it (update payment method, change draft date, etc.), and make it easy to reply — do not pressure. End with "happy to take care of it for you".]' },
  ],
};

const PRESETS_BY_KEY = {
  mp: PRESET_MP,
  iul: PRESET_IUL,
  pm30: PRESET_PM_30,
  pm90: PRESET_PM_90,
  pm180: PRESET_PM_180,
  pm365: PRESET_PM_365,
  payment: PRESET_PAYMENT,
  conservation: PRESET_CONSERVATION,
  birthday: PRESET_BIRTHDAY,
};

const LEAD_FILTER_OPTIONS = [
  { value: '',                      label: 'Any lead source' },
  { value: 'mortgage_protection',   label: 'Mortgage Protection leads only' },
  { value: 'iul',                   label: 'IUL leads only' },
  { value: 'final_expense',         label: 'Final Expense (FEX) leads only' },
  { value: 'general',               label: 'General leads only' },
];

function SmsWorkflowsPanel({ currentUser }) {
  const [workflows, setWorkflows] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [expanded, setExpanded] = useState({}); // { [workflowId]: true }

  const refresh = React.useCallback(async () => {
    try {
      const { data: wfs, error: e1 } = await supabase
        .from('workflows')
        .select('id, name, active, trigger, lead_source_filter, milestone_days, lead_days_before, created_at')
        .order('created_at', { ascending: true });
      if (e1) throw e1;

      const ids = (wfs || []).map(w => w.id);
      const stepsByWf = {};
      if (ids.length > 0) {
        const { data: steps } = await supabase
          .from('workflow_steps')
          .select('id, workflow_id, position, type, template_id, wait_minutes')
          .in('workflow_id', ids)
          .order('position', { ascending: true });
        (steps || []).forEach(s => {
          (stepsByWf[s.workflow_id] = stepsByWf[s.workflow_id] || []).push(s);
        });
      }

      const templateIds = Array.from(new Set(Object.values(stepsByWf).flat().map(s => s.template_id).filter(Boolean)));
      const templatesById = {};
      if (templateIds.length > 0) {
        const { data: tpls } = await supabase
          .from('templates')
          .select('id, name, body')
          .in('id', templateIds);
        (tpls || []).forEach(t => { templatesById[t.id] = t; });
      }

      setWorkflows((wfs || []).map(w => ({
        ...w,
        steps: (stepsByWf[w.id] || []).map(s => ({ ...s, template: templatesById[s.template_id] || null })),
      })));
    } catch (err) {
      setError(err.message || String(err));
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh, currentUser?.id]);

  // ── Mutations. All RLS-scoped via the per-user supabase client. ──

  async function toggleActive(w) {
    const next = !w.active;
    setWorkflows(ws => ws.map(x => x.id === w.id ? { ...x, active: next } : x));
    const { error } = await supabase.from('workflows').update({ active: next }).eq('id', w.id);
    if (error) { alert(`Couldn't toggle: ${error.message}`); refresh(); }
  }

  async function renameWorkflow(w, name) {
    const trimmed = (name || '').trim();
    if (!trimmed || trimmed === w.name) return;
    setWorkflows(ws => ws.map(x => x.id === w.id ? { ...x, name: trimmed } : x));
    const { error } = await supabase.from('workflows').update({ name: trimmed }).eq('id', w.id);
    if (error) { alert(`Couldn't rename: ${error.message}`); refresh(); }
  }

  async function setLeadFilter(w, value) {
    const v = value || null;
    setWorkflows(ws => ws.map(x => x.id === w.id ? { ...x, lead_source_filter: v } : x));
    const { error } = await supabase.from('workflows').update({ lead_source_filter: v }).eq('id', w.id);
    if (error) { alert(`Couldn't save filter: ${error.message}`); refresh(); }
  }

  async function setMilestoneDays(w, days) {
    const n = Number(days);
    const v = Number.isFinite(n) && n > 0 ? Math.round(n) : null;
    setWorkflows(ws => ws.map(x => x.id === w.id ? { ...x, milestone_days: v } : x));
    const { error } = await supabase.from('workflows').update({ milestone_days: v }).eq('id', w.id);
    if (error) { alert(`Couldn't save milestone: ${error.message}`); refresh(); }
  }

  async function setLeadDaysBefore(w, days) {
    const n = Number(days);
    const v = Number.isFinite(n) && n > 0 ? Math.round(n) : null;
    setWorkflows(ws => ws.map(x => x.id === w.id ? { ...x, lead_days_before: v } : x));
    const { error } = await supabase.from('workflows').update({ lead_days_before: v }).eq('id', w.id);
    if (error) { alert(`Couldn't save lead days: ${error.message}`); refresh(); }
  }

  async function setStepBody(step, body) {
    if (!step.template_id) {
      // Step had no template — create one.
      const { data: tpl, error: e1 } = await supabase
        .from('templates')
        .insert({ owner_id: currentUser.id, name: `Step ${step.position} template`, body })
        .select('id, name, body').single();
      if (e1) { alert(`Couldn't save: ${e1.message}`); return; }
      await supabase.from('workflow_steps').update({ template_id: tpl.id }).eq('id', step.id);
    } else {
      const { error } = await supabase.from('templates').update({ body }).eq('id', step.template_id);
      if (error) { alert(`Couldn't save body: ${error.message}`); refresh(); return; }
    }
    refresh();
  }

  async function setStepWaitDays(step, days) {
    const minutes = Math.max(0, Math.round(Number(days || 0) * 24 * 60));
    const { error } = await supabase.from('workflow_steps').update({ wait_minutes: minutes }).eq('id', step.id);
    if (error) { alert(`Couldn't save wait: ${error.message}`); refresh(); }
    else refresh();
  }

  // Step type (channel) — 'send_sms' | 'send_email' | 'create_call_task'.
  // Drives which dispatcher the cron picks for this step. SMS uses
  // template.body, email uses template.email_subject + email_body
  // (falls back to body), task uses body as the title.
  async function setStepType(step, type) {
    if (!['send_sms','send_email','create_call_task'].includes(type)) return;
    const { error } = await supabase.from('workflow_steps').update({ type }).eq('id', step.id);
    if (error) { alert(`Couldn't save type: ${error.message}`); refresh(); }
    else refresh();
  }

  async function deleteStep(step) {
    if (!(await hqConfirm({
      title: 'Delete this step?',
      body: 'The template will stay in your library.',
      confirmLabel: 'Delete step', danger: true,
    }))) return;
    const { error } = await supabase.from('workflow_steps').delete().eq('id', step.id);
    if (error) { alert(`Couldn't delete: ${error.message}`); return; }
    refresh();
  }

  async function addStep(w) {
    setBusy(true);
    try {
      const nextPos = (w.steps[w.steps.length - 1]?.position || 0) + 1;
      const defaultName = `Step ${nextPos} template`;
      const defaultBody = 'Hi {first}, …';
      const { data: tpl, error: e1 } = await supabase
        .from('templates').insert({ owner_id: currentUser.id, name: defaultName, body: defaultBody })
        .select('id, name, body').single();
      if (e1) throw e1;
      const { error: e2 } = await supabase.from('workflow_steps').insert({
        workflow_id: w.id, position: nextPos, type: 'send_sms', template_id: tpl.id, wait_minutes: 24 * 60,
      });
      if (e2) throw e2;
      await refresh();
    } catch (err) { alert(`Couldn't add step: ${err.message || err}`); }
    finally { setBusy(false); }
  }

  async function deleteWorkflow(w) {
    if (!(await hqConfirm({
      title: `Delete "${w.name}"?`,
      body: 'Active enrollments will keep firing until their drip is complete.',
      confirmLabel: 'Delete workflow', danger: true,
    }))) return;
    const { error } = await supabase.from('workflows').delete().eq('id', w.id);
    if (error) { alert(`Couldn't delete: ${error.message}`); return; }
    refresh();
  }

  async function createFromPreset(preset) {
    if (!currentUser?.id) { alert('Not logged in.'); return; }
    setBusy(true);
    try {
      const cloneName = await uniqueName(preset.name);
      const { data: wf, error: e1 } = await supabase
        .from('workflows')
        .insert({
          name: cloneName,
          active: false,
          created_by: currentUser.id,
          trigger: preset.trigger,
          lead_source_filter: preset.leadSourceFilter || null,
          milestone_days: preset.milestoneDays || null,
          lead_days_before: preset.leadDaysBefore || null,
        })
        .select('id').single();
      if (e1) throw e1;

      // Insert templates + steps sequentially. Sequential is fine —
      // operator action, ~20 round-trips per preset, runs in <2s.
      let pos = 0;
      for (const s of preset.steps) {
        pos += 1;
        const { data: tpl, error: e2 } = await supabase
          .from('templates')
          .insert({ owner_id: currentUser.id, name: s.name, body: s.body })
          .select('id').single();
        if (e2) throw e2;
        const { error: e3 } = await supabase.from('workflow_steps').insert({
          workflow_id: wf.id, position: pos, type: 'send_sms',
          template_id: tpl.id, wait_minutes: (s.waitDays || 0) * 24 * 60,
        });
        if (e3) throw e3;
      }

      setExpanded(x => ({ ...x, [wf.id]: true }));
      await refresh();
    } catch (err) { alert(`Couldn't create: ${err.message || err}`); }
    finally { setBusy(false); setShowNewMenu(false); }
  }

  async function createBlank() {
    if (!currentUser?.id) { alert('Not logged in.'); return; }
    setBusy(true);
    try {
      const cloneName = await uniqueName('New workflow');
      const { data: wf, error: e1 } = await supabase
        .from('workflows')
        .insert({
          name: cloneName, active: false, created_by: currentUser.id,
          trigger: 'manual', lead_source_filter: null,
        })
        .select('id').single();
      if (e1) throw e1;
      setExpanded(x => ({ ...x, [wf.id]: true }));
      await refresh();
    } catch (err) { alert(`Couldn't create: ${err.message || err}`); }
    finally { setBusy(false); setShowNewMenu(false); }
  }

  async function uniqueName(base) {
    // Avoid colliding with an existing name — append " (2)", " (3)", etc.
    const { data } = await supabase.from('workflows').select('name');
    const taken = new Set((data || []).map(r => r.name));
    if (!taken.has(base)) return base;
    let n = 2;
    while (taken.has(`${base} (${n})`)) n++;
    return `${base} (${n})`;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
        Failed to load workflows: {error}
      </div>
    );
  }
  if (workflows == null) return <div className="text-sm text-slate-500">Loading workflows…</div>;

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-slate-600">
          {workflows.length} workflow{workflows.length === 1 ? '' : 's'} ·{' '}
          {workflows.filter(w => w.active).length} active
        </div>
        <div className="relative">
          <button
            disabled={busy}
            onClick={() => setShowNewMenu(o => !o)}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5">
            + New workflow <ChevronRight size={14} className={`transition-transform ${showNewMenu ? 'rotate-90' : ''}`}/>
          </button>
          {showNewMenu && (
            <div className="absolute right-0 mt-1 w-80 bg-white border border-slate-200 rounded-xl shadow-lg z-10 overflow-hidden max-h-[28rem] overflow-y-auto">
              <div className="px-4 py-2 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50 border-b border-slate-100">Lead drips</div>
              <button onClick={() => createFromPreset(PRESETS_BY_KEY.mp)} className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100">
                <div className="font-semibold text-sm text-slate-800">Mortgage Protection — 10-touch</div>
                <div className="text-xs text-slate-500 mt-0.5">Auto-enrolls MP leads. 30 days, urgent tone.</div>
              </button>
              <button onClick={() => createFromPreset(PRESETS_BY_KEY.iul)} className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100">
                <div className="font-semibold text-sm text-slate-800">IUL — 8-touch</div>
                <div className="text-xs text-slate-500 mt-0.5">Auto-enrolls IUL leads. 45 days, consultative tone.</div>
              </button>
              <div className="px-4 py-2 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50 border-b border-slate-100">Policy milestones (AI check-ins)</div>
              <button onClick={() => createFromPreset(PRESETS_BY_KEY.pm30)} className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100">
                <div className="font-semibold text-sm text-slate-800">30-Day Welcome</div>
                <div className="text-xs text-slate-500 mt-0.5">AI texts every new client 30 days after their policy issues.</div>
              </button>
              <button onClick={() => createFromPreset(PRESETS_BY_KEY.pm90)} className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100">
                <div className="font-semibold text-sm text-slate-800">90-Day Check-In</div>
                <div className="text-xs text-slate-500 mt-0.5">Quarterly nudge — catches life changes that need coverage updates.</div>
              </button>
              <button onClick={() => createFromPreset(PRESETS_BY_KEY.pm180)} className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100">
                <div className="font-semibold text-sm text-slate-800">6-Month Review</div>
                <div className="text-xs text-slate-500 mt-0.5">The retention touch most VAs skip. AI-personalized to each policy.</div>
              </button>
              <button onClick={() => createFromPreset(PRESETS_BY_KEY.pm365)} className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100">
                <div className="font-semibold text-sm text-slate-800">12-Month Anniversary</div>
                <div className="text-xs text-slate-500 mt-0.5">Annual review pitch. Drives upsell conversations into year 2.</div>
              </button>
              <div className="px-4 py-2 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50 border-b border-slate-100">Chargeback prevention</div>
              <button onClick={() => createFromPreset(PRESETS_BY_KEY.payment)} className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100">
                <div className="font-semibold text-sm text-slate-800">Payment Reminder — 3 days before draft</div>
                <div className="text-xs text-slate-500 mt-0.5">Heads-up text before each monthly bank draft. NSF prevention.</div>
              </button>
              <button onClick={() => createFromPreset(PRESETS_BY_KEY.conservation)} className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100">
                <div className="font-semibold text-sm text-slate-800">Conservation Save — Auto Text</div>
                <div className="text-xs text-slate-500 mt-0.5">Auto-fires when a lapse warning lands from a parsed conservation report.</div>
              </button>
              <div className="px-4 py-2 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50 border-b border-slate-100">Relationship</div>
              <button onClick={() => createFromPreset(PRESETS_BY_KEY.birthday)} className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100">
                <div className="font-semibold text-sm text-slate-800">Birthday Wishes — AI personalized</div>
                <div className="text-xs text-slate-500 mt-0.5">Warm birthday text per client. No sales pitch. Builds retention.</div>
              </button>
              <div className="px-4 py-2 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50 border-b border-slate-100">Custom</div>
              <button onClick={createBlank} className="w-full text-left px-4 py-3 hover:bg-slate-50">
                <div className="font-semibold text-sm text-slate-800">Blank</div>
                <div className="text-xs text-slate-500 mt-0.5">Build your own steps from scratch.</div>
              </button>
            </div>
          )}
        </div>
      </div>

      {workflows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-6 text-sm text-slate-500 text-center">
          No workflows yet. Click "+ New workflow" to create one from a preset or from scratch.
        </div>
      ) : (
        <div className="space-y-3">
          {workflows.map(w => (
            <WorkflowCard
              key={w.id}
              workflow={w}
              expanded={!!expanded[w.id]}
              busy={busy}
              onToggleExpand={() => setExpanded(x => ({ ...x, [w.id]: !x[w.id] }))}
              onToggleActive={() => toggleActive(w)}
              onRename={(name) => renameWorkflow(w, name)}
              onSetFilter={(v) => setLeadFilter(w, v)}
              onSetMilestoneDays={(d) => setMilestoneDays(w, d)}
              onSetLeadDaysBefore={(d) => setLeadDaysBefore(w, d)}
              onSetStepBody={setStepBody}
              onSetStepWaitDays={setStepWaitDays}
              onSetStepType={setStepType}
              onDeleteStep={deleteStep}
              onAddStep={() => addStep(w)}
              onDeleteWorkflow={() => deleteWorkflow(w)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function WorkflowCard({
  workflow: w, expanded, busy, onToggleExpand, onToggleActive, onRename, onSetFilter,
  onSetMilestoneDays = () => {},
  onSetLeadDaysBefore = () => {},
  onSetStepBody, onSetStepWaitDays, onSetStepType, onDeleteStep, onAddStep, onDeleteWorkflow,
}) {
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(w.name);
  React.useEffect(() => { setNameDraft(w.name); }, [w.name]);

  const isMilestone    = w.trigger === 'policy_milestone';
  const isPayment      = w.trigger === 'payment_reminder';
  const isConservation = w.trigger === 'conservation_event';
  const isBirthday     = w.trigger === 'birthday';
  const stepsUseAi = (w.steps || []).some(s => /^\s*\[AI:/.test(s.template?.body || ''));
  const [milestoneDraft, setMilestoneDraft] = useState(w.milestone_days ?? '');
  React.useEffect(() => { setMilestoneDraft(w.milestone_days ?? ''); }, [w.milestone_days]);
  const [leadDaysDraft, setLeadDaysDraft] = useState(w.lead_days_before ?? '');
  React.useEffect(() => { setLeadDaysDraft(w.lead_days_before ?? ''); }, [w.lead_days_before]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between gap-3 border-b border-slate-100">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {editingName ? (
              <input
                autoFocus
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onBlur={() => { setEditingName(false); onRename(nameDraft); }}
                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setNameDraft(w.name); setEditingName(false); } }}
                className="font-semibold text-slate-800 border border-emerald-400 rounded px-2 py-1 text-sm w-full"
              />
            ) : (
              <button onClick={() => setEditingName(true)} className="text-left font-semibold text-slate-800 hover:text-emerald-700">
                {w.name}
              </button>
            )}
            {isMilestone    && <span className="text-[10px] font-bold uppercase tracking-wider bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">policy milestone</span>}
            {isPayment      && <span className="text-[10px] font-bold uppercase tracking-wider bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full">payment reminder</span>}
            {isConservation && <span className="text-[10px] font-bold uppercase tracking-wider bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">conservation save</span>}
            {isBirthday     && <span className="text-[10px] font-bold uppercase tracking-wider bg-pink-100 text-pink-700 px-2 py-0.5 rounded-full">birthday</span>}
            {stepsUseAi && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full" title="One or more steps are AI-generated at send time — the body changes per client based on their policy details.">AI-written</span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <span>{w.steps.length} step{w.steps.length === 1 ? '' : 's'}</span>
            {isMilestone && (
              <>
                <span className="text-slate-300">·</span>
                <label className="inline-flex items-center gap-1">
                  <span>Fires after</span>
                  <input
                    type="number" min="1" max="3650"
                    value={milestoneDraft}
                    onChange={(e) => setMilestoneDraft(e.target.value)}
                    onBlur={() => { if (Number(milestoneDraft) !== (w.milestone_days || 0)) onSetMilestoneDays(milestoneDraft); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    className="w-16 border border-slate-200 rounded px-1.5 py-0.5 text-xs text-right"
                  />
                  <span>days on book</span>
                </label>
              </>
            )}
            {isPayment && (
              <>
                <span className="text-slate-300">·</span>
                <label className="inline-flex items-center gap-1">
                  <span>Fires</span>
                  <input
                    type="number" min="1" max="30"
                    value={leadDaysDraft}
                    onChange={(e) => setLeadDaysDraft(e.target.value)}
                    onBlur={() => { if (Number(leadDaysDraft) !== (w.lead_days_before || 0)) onSetLeadDaysBefore(leadDaysDraft); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    className="w-14 border border-slate-200 rounded px-1.5 py-0.5 text-xs text-right"
                  />
                  <span>days before draft</span>
                </label>
              </>
            )}
            {isConservation && (
              <>
                <span className="text-slate-300">·</span>
                <span className="text-slate-600">fires on lapse warning from a parsed conservation report</span>
              </>
            )}
            {isBirthday && (
              <>
                <span className="text-slate-300">·</span>
                <label className="inline-flex items-center gap-1">
                  <span>Fires</span>
                  <input
                    type="number" min="0" max="30"
                    value={leadDaysDraft}
                    onChange={(e) => setLeadDaysDraft(e.target.value)}
                    onBlur={() => { if (Number(leadDaysDraft) !== (w.lead_days_before || 0)) onSetLeadDaysBefore(leadDaysDraft); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    className="w-14 border border-slate-200 rounded px-1.5 py-0.5 text-xs text-right"
                  />
                  <span>days before birthday (0 = on the day)</span>
                </label>
              </>
            )}
            {!isMilestone && !isPayment && !isConservation && !isBirthday && (
              <>
                <span className="text-slate-300">·</span>
                <label className="inline-flex items-center gap-1">
                  <span>Auto-enroll:</span>
                  <select
                    value={w.lead_source_filter || ''}
                    onChange={(e) => onSetFilter(e.target.value)}
                    className="text-xs border border-slate-200 rounded px-1.5 py-0.5 bg-white">
                    {LEAD_FILTER_OPTIONS.map(o => <option key={o.value || 'any'} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
              </>
            )}
          </div>
        </div>
        <button
          onClick={onToggleActive}
          title={w.active ? 'Click to pause' : 'Click to activate'}
          className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors ${w.active ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
          {w.active ? 'Active' : 'Inactive'}
        </button>
        <button onClick={onToggleExpand} className="text-slate-400 hover:text-slate-700" title={expanded ? 'Collapse' : 'Expand to edit'}>
          {expanded ? <ChevronLeft size={18} className="rotate-90"/> : <ChevronRight size={18} className="rotate-90"/>}
        </button>
      </div>

      {expanded && (
        <>
          {w.active && (
            <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-2 text-xs text-emerald-800">
              This workflow is active. New leads matching the auto-enroll filter will receive these texts — confirm consent before activating real campaigns.
            </div>
          )}
          <div className="divide-y divide-slate-100">
            {w.steps.length === 0 ? (
              <div className="px-4 py-6 text-sm text-slate-500 text-center">
                No steps yet. Click "+ Add step" below to add the first one.
              </div>
            ) : w.steps.map((s) => (
              <StepEditorRow
                key={s.id}
                step={s}
                onSaveBody={(body) => onSetStepBody(s, body)}
                onSaveWaitDays={(d) => onSetStepWaitDays(s, d)}
                onSaveType={(t) => onSetStepType(s, t)}
                onDelete={() => onDeleteStep(s)}
              />
            ))}
          </div>
          <div className="px-4 py-3 flex items-center justify-between border-t border-slate-100 bg-slate-50">
            <button
              disabled={busy}
              onClick={onAddStep}
              className="text-sm font-semibold text-emerald-700 hover:text-emerald-800 disabled:opacity-50">
              + Add step
            </button>
            <button onClick={onDeleteWorkflow} className="text-xs text-red-600 hover:text-red-700">
              Delete workflow
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function StepEditorRow({ step, onSaveBody, onSaveWaitDays, onSaveType = () => {}, onDelete }) {
  const initialDays = Math.round((step.wait_minutes || 0) / (24 * 60));
  const [daysDraft, setDaysDraft] = useState(initialDays);
  const [bodyDraft, setBodyDraft] = useState(step.template?.body || '');
  React.useEffect(() => { setDaysDraft(initialDays); }, [initialDays]);
  React.useEffect(() => { setBodyDraft(step.template?.body || ''); }, [step.template?.body]);

  const dirtyBody = bodyDraft !== (step.template?.body || '');
  const dirtyDays = Number(daysDraft) !== initialDays;

  // Compliance pre-flight. Runs on demand; the agent clicks the
  // shield to scan a body before saving. Kept on-demand (not auto-
  // on-blur) so we don't bill an Anthropic call for every keystroke.
  const [complianceBusy, setComplianceBusy] = React.useState(false);
  const [compliance, setCompliance] = React.useState(null);
  const [complianceErr, setComplianceErr] = React.useState(null);

  async function runCompliance() {
    setComplianceBusy(true); setComplianceErr(null);
    try {
      const out = await reviewMessageCompliance({
        body: bodyDraft,
        context: { channel: 'SMS', firstTouch: step.position === 1 },
      });
      if (!out) { setComplianceErr('No response.'); return; }
      setCompliance(out);
    } catch (e) {
      setComplianceErr(e.message || 'Failed.');
    } finally { setComplianceBusy(false); }
  }

  const riskTone = compliance?.riskLevel === 'high'   ? 'red'
                : compliance?.riskLevel === 'medium' ? 'amber'
                : compliance?.riskLevel === 'low'    ? 'amber'
                : compliance?.riskLevel === 'none'   ? 'emerald'
                : 'slate';

  return (
    <div className="px-4 py-3 text-sm">
      <div className="flex items-center gap-3 mb-2">
        <span className="text-xs font-mono text-slate-400 w-12">#{step.position}</span>
        {/* Channel selector — drives which dispatcher the cron picks
            for this step. SMS uses template.body. Email uses
            template.email_subject + email_body (falls back to body).
            "Both" fires SMS AND email back-to-back (separate dedup,
            so a delivery failure on one channel doesn't block the
            other). Task creates a row in public.tasks using body as
            title. */}
        <select
          value={step.type || 'send_sms'}
          onChange={(e) => onSaveType(e.target.value)}
          className="text-[0.65rem] font-bold uppercase tracking-wider border border-slate-200 rounded px-1 py-0.5 bg-white text-slate-700"
          title="Channel — SMS / email / both / call task"
        >
          <option value="send_sms">SMS</option>
          <option value="send_email">Email</option>
          <option value="send_both">SMS + Email</option>
          <option value="create_call_task">Call task</option>
        </select>
        <label className="inline-flex items-center gap-1 text-xs text-slate-500">
          <span>Wait</span>
          <input
            type="number" min="0" max="365"
            value={daysDraft}
            onChange={(e) => setDaysDraft(e.target.value)}
            onBlur={() => { if (dirtyDays) onSaveWaitDays(daysDraft); }}
            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
            className="w-14 border border-slate-200 rounded px-1.5 py-0.5 text-xs text-right"/>
          <span>days</span>
        </label>
        <span className="flex-1 text-slate-400 truncate text-xs italic">{step.template?.name || '(no template)'}</span>
        <button
          onClick={runCompliance}
          disabled={complianceBusy || !bodyDraft.trim()}
          title="AI compliance pre-flight check (TCPA / carrier / state regulations)"
          className="text-xs text-indigo-600 hover:text-indigo-800 disabled:opacity-50 font-semibold"
        >
          {complianceBusy ? '…' : 'Check'}
        </button>
        <button onClick={onDelete} className="text-slate-400 hover:text-red-600" title="Delete step">
          <Trash2 size={14}/>
        </button>
      </div>
      <textarea
        value={bodyDraft}
        onChange={(e) => { setBodyDraft(e.target.value); setCompliance(null); }}
        onBlur={() => { if (dirtyBody) onSaveBody(bodyDraft); }}
        rows={2}
        placeholder="Message body — supports {first} {last} {state} {agent} {time_1} {time_2}"
        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 resize-y focus:border-emerald-400 focus:outline-none"
      />
      {dirtyBody && <div className="text-xs text-amber-600 mt-1">Click outside to save</div>}
      {complianceErr && <div className="text-xs text-red-600 mt-1">{complianceErr}</div>}
      {compliance && (
        <div className={`mt-2 border rounded-lg p-2 bg-${riskTone}-50 border-${riskTone}-200`}>
          <div className={`text-xs font-bold uppercase tracking-wider text-${riskTone}-800 flex items-center gap-1.5`}>
            Compliance: {compliance.riskLevel}
          </div>
          {Array.isArray(compliance.issues) && compliance.issues.length > 0 ? (
            <ul className="mt-1 space-y-1 text-xs">
              {compliance.issues.map((iss, i) => (
                <li key={i} className="text-slate-700">
                  <span className={`font-semibold text-${iss.severity === 'high' ? 'red' : iss.severity === 'medium' ? 'amber' : 'slate'}-700`}>[{iss.severity}]</span> {iss.what} — <em>{iss.fix}</em>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-xs text-slate-700 mt-1">No issues flagged.</div>
          )}
          {compliance.cleanedSuggestion && (
            <div className="mt-2 text-xs">
              <div className="font-bold text-slate-600 mb-0.5">Suggested rewrite:</div>
              <div className="bg-white border border-slate-200 rounded p-1.5 text-slate-800 whitespace-pre-wrap">{compliance.cleanedSuggestion}</div>
              <button
                onClick={() => { setBodyDraft(compliance.cleanedSuggestion); setCompliance(null); }}
                className="mt-1 text-indigo-700 hover:text-indigo-900 font-semibold text-xs"
              >
                Apply rewrite →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Deprecated 2026-06-26 — this panel used to render its own
// duplicate conversation list pulling from the same `messages`
// table as SmsInboxView. Operators complained "I can't tell which
// inbox to look at." Now it points the operator at the single
// canonical Inbox (which also has the 📣 Bulk message button that
// used to be the only reason to come here). The SMS Engine tab
// still owns Workflows / Appointments / Coverage — those are
// distinct surfaces, not duplicates.
function SmsConversationsPanel({ currentUser }) {
  void currentUser;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-8 text-center max-w-none mx-auto">
      <div className="text-5xl mb-3">📥</div>
      <div className="text-lg font-bold text-slate-900 mb-1">Conversations moved into the Inbox</div>
      <p className="text-sm text-slate-600 leading-relaxed mb-4">
        We unified the two inboxes. Every reply lands in the main <strong>Inbox</strong> now — with lead context, smart filters, one-click actions, and a 📣 <strong>Bulk message</strong> button that does what this view used to.
      </p>
      <button
        onClick={() => window.dispatchEvent(new CustomEvent('crm-goto-tab', { detail: { tab: 'inbox' } }))}
        className="bg-slate-900 hover:bg-black text-white text-sm font-bold px-4 py-2 rounded-lg"
      >
        Open Inbox →
      </button>
      <div className="text-[11px] text-slate-400 mt-4">SMS Engine still owns Workflows / Appointments / Coverage — those tabs are unchanged.</div>
    </div>
  );
}

function SmsAppointmentsPanel({ currentUser }) {
  const [appts, setAppts] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error: e } = await supabase
          .from('appointments')
          .select('id, lead_id, scheduled_for, status, booked_by, policy_number, policy_premium, notes, created_at')
          .order('scheduled_for', { ascending: false })
          .limit(200);
        if (e) throw e;
        if (!cancelled) setAppts(data || []);
      } catch (err) {
        if (!cancelled) setError(err.message || String(err));
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.id]);

  if (error) return <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">Failed to load appointments: {error}</div>;
  if (appts == null) return <div className="text-sm text-slate-500">Loading appointments…</div>;
  if (appts.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 text-sm text-slate-500 text-center">
        No appointments yet. The AI appointment setter will book here once a lead replies and agrees to a time.
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left px-3 py-2">When</th>
            <th className="text-left px-3 py-2">Lead</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-left px-3 py-2">Booked by</th>
            <th className="text-left px-3 py-2">Policy</th>
            <th className="text-left px-3 py-2">Notes</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {appts.map(a => (
            <tr key={a.id}>
              <td className="px-3 py-2 whitespace-nowrap">
                {a.scheduled_for ? new Date(a.scheduled_for).toLocaleString() : '—'}
              </td>
              <td className="px-3 py-2 font-mono text-xs text-slate-600">{a.lead_id}</td>
              <td className="px-3 py-2">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${apptStatusClass(a.status)}`}>{a.status}</span>
              </td>
              <td className="px-3 py-2 text-xs text-slate-500">{a.booked_by}</td>
              <td className="px-3 py-2 text-xs text-slate-700">
                {a.policy_number ? <>{a.policy_number}{a.policy_premium ? ` · ${fmt(a.policy_premium)}` : ''}</> : '—'}
              </td>
              <td className="px-3 py-2 text-xs text-slate-500 max-w-md truncate">{a.notes || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Coverage panel: policies missing a lead match ───────────────────
//
// Policy-milestone workflows fire by matching policy.insured to
// lead.name. If the operator's commission book has 200 active
// policies but the CRM only has 80 leads — common, because aged-lead
// leads were imported but existing clients were not — the engine is
// only covering 40% of the book. This panel surfaces every active
// policy with no matched lead and lets the operator backfill a lead
// in a single click + phone entry.
//
// "Covered" criteria: lead must have phone_e164 AND consent === true
// AND opted_out === false. A lead with a matching name but no phone
// is still unmatched as far as the engine is concerned, so it shows
// up here with an action of "Add phone" instead of "Add lead."

const SMS_TZ_OPTIONS = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];

function smsCoverageNormalizeName(s) {
  const cleaned = String(s || '').toUpperCase().replace(/[^A-Z\s,]/g, '').replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';
  return cleaned.split(/[\s,]+/).filter(Boolean).sort().join(' ');
}

function smsCoverageToE164(raw) {
  // Naive but workable US-default E.164 formatter. Strip everything
  // except digits, prepend +1 if it looks like a 10-digit US number,
  // otherwise prepend + if it doesn't already have one.
  const digits = String(raw || '').replace(/[^\d]/g, '');
  if (!digits) return '';
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return `+${digits}`;
}

function SmsCoveragePanel({ transactions = [], leads = [], setLeads = () => {}, showToast = () => {} }) {
  const [filter, setFilter] = useState('upcoming'); // 'upcoming' | 'all'
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState({ phone: '', timezone: 'America/Chicago', consent: false, draftDay: '' });

  // Derive active policies the same way PersistencyView does.
  const policies = React.useMemo(() => {
    const map = {};
    (transactions || []).forEach(tx => {
      if (!tx.policy) return;
      const k = `${tx.prodNum || ''}|${tx.mga || ''}|${tx.policy}`;
      if (!map[k]) {
        map[k] = { key: k, policy: tx.policy, insured: tx.insured || '', prodNum: tx.prodNum || '', mga: tx.mga || '', carrier: tx.carrier || tx.mga || '', issueDate: tx.issueDate || '', premium: 0, lapsed: false };
      }
      const p = map[k];
      if (!p.insured && tx.insured) p.insured = tx.insured;
      if (!p.issueDate && tx.issueDate) p.issueDate = tx.issueDate;
      if (!p.carrier && (tx.carrier || tx.mga)) p.carrier = tx.carrier || tx.mga;
      if (tx.commPremium && Math.abs(tx.commPremium) > p.premium) p.premium = Math.abs(tx.commPremium);
      const amt = Number(tx.netToAccount) || 0;
      if (CHARGEBACK_TYPES.has(tx.activityType) || amt < 0) p.lapsed = true;
    });
    const today = Date.now();
    const DAY = 1000 * 60 * 60 * 24;
    const MILESTONES = [30, 90, 180, 365];
    return Object.values(map)
      .filter(p => !p.lapsed && p.insured && p.issueDate)
      .map(p => {
        const issued = new Date(p.issueDate);
        const ageDays = Number.isFinite(issued.getTime()) ? Math.floor((today - issued.getTime()) / DAY) : null;
        let nextMilestone = null, daysToNext = null;
        if (ageDays != null) {
          for (const m of MILESTONES) {
            if (m >= ageDays) { nextMilestone = m; daysToNext = m - ageDays; break; }
          }
        }
        return { ...p, ageDays, nextMilestone, daysToNext };
      });
  }, [transactions]);

  // Index leads by normalized name → first matching lead.
  const leadByName = React.useMemo(() => {
    const m = new Map();
    leads.forEach(l => {
      const k = smsCoverageNormalizeName(l.name);
      if (k && !m.has(k)) m.set(k, l);
    });
    return m;
  }, [leads]);

  // Classify each policy: covered / no_phone / no_consent / no_lead.
  const rows = React.useMemo(() => policies.map(p => {
    const lead = leadByName.get(smsCoverageNormalizeName(p.insured)) || null;
    let issue;
    if (!lead) issue = 'no_lead';
    else if (!lead.phone_e164 && !lead.phone) issue = 'no_phone';
    else if (lead.consent !== true) issue = 'no_consent';
    else if (lead.opted_out === true) issue = 'opted_out';
    else issue = 'covered';
    return { ...p, lead, issue };
  }), [policies, leadByName]);

  const uncovered = rows.filter(r => r.issue !== 'covered');
  const covered = rows.length - uncovered.length;
  const coveragePct = rows.length > 0 ? (covered / rows.length) * 100 : 0;

  // Visible set respects the upcoming/all filter.
  const visible = React.useMemo(() => {
    const list = filter === 'upcoming'
      ? uncovered.filter(r => r.daysToNext != null && r.daysToNext <= 30)
      : uncovered;
    // Sort: smallest daysToNext first, then by issue date ascending.
    return list.slice().sort((a, b) => {
      const ad = a.daysToNext == null ? 99999 : a.daysToNext;
      const bd = b.daysToNext == null ? 99999 : b.daysToNext;
      if (ad !== bd) return ad - bd;
      return (a.issueDate || '').localeCompare(b.issueDate || '');
    });
  }, [uncovered, filter]);

  function startEdit(row) {
    setEditingKey(row.key);
    setDraft({
      phone: row.lead?.phone_e164 || row.lead?.phone || '',
      timezone: row.lead?.timezone || 'America/Chicago',
      consent: false,
      draftDay: row.lead?.draftDay || '',
    });
  }
  function cancelEdit() { setEditingKey(null); }

  function saveEdit(row) {
    const phoneE164 = smsCoverageToE164(draft.phone);
    if (!phoneE164) { alert('Phone number required.'); return; }
    if (!draft.consent) { alert('Confirm consent before enrolling this client.'); return; }

    const draftDayNum = Number(draft.draftDay);
    const updatePatch = {
      phone: draft.phone,
      phone_e164: phoneE164,
      timezone: draft.timezone,
      consent: true,
      opted_out: false,
      ...(Number.isFinite(draftDayNum) && draftDayNum >= 1 && draftDayNum <= 31 ? { draftDay: draftDayNum } : {}),
    };

    setLeads(prev => {
      // If the lead exists, update it. Otherwise create a minimal record.
      if (row.lead) {
        return prev.map(l => l.id === row.lead.id ? { ...l, ...updatePatch } : l);
      }
      const newLead = {
        id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `lead_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        name: row.insured,
        ...updatePatch,
        status: 'closed',
        source: 'Imported from policy',
        coverageType: row.carrier || '',
        notes: `Auto-created from active policy ${row.policy} (${row.carrier}) issued ${row.issueDate}.`,
        createdAt: new Date().toISOString(),
      };
      return [...prev, newLead];
    });

    showToast(row.lead ? 'Lead updated — engine will pick up at next milestone.' : 'Lead created — engine will pick up at next milestone.');
    setEditingKey(null);
  }

  if (rows.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 text-sm text-slate-500 text-center">
        No active policies yet. Once a commission statement is uploaded, the engine will list every active policy here and flag any that don't have a textable lead linked.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Coverage summary */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-semibold text-slate-900">Engine coverage</h3>
            <p className="text-sm text-slate-500 mt-0.5">Active policies the SMS engine can text. Anything below 100% is a client the milestone workflows skip.</p>
          </div>
          <div className="text-right">
            <div className={`text-3xl font-bold tabular-nums ${coveragePct >= 90 ? 'text-emerald-700' : coveragePct >= 70 ? 'text-amber-700' : 'text-red-700'}`}>{coveragePct.toFixed(0)}%</div>
            <div className="text-xs text-slate-500 mt-0.5">{covered.toLocaleString()} of {rows.length.toLocaleString()} active policies</div>
          </div>
        </div>
        {uncovered.length > 0 && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <SmsCoverageStatPill tone="red"    label="No lead"     count={uncovered.filter(r => r.issue === 'no_lead').length}/>
            <SmsCoverageStatPill tone="amber"  label="No phone"    count={uncovered.filter(r => r.issue === 'no_phone').length}/>
            <SmsCoverageStatPill tone="amber"  label="No consent"  count={uncovered.filter(r => r.issue === 'no_consent').length}/>
            <SmsCoverageStatPill tone="slate"  label="Opted out"   count={uncovered.filter(r => r.issue === 'opted_out').length}/>
          </div>
        )}
      </div>

      {uncovered.length === 0 ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-sm text-emerald-900 text-center">
          Every active policy is textable. 
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
            <div className="text-sm font-semibold text-slate-700">{visible.length} policy {visible.length === 1 ? 'needs' : 'need'} attention</div>
            <div className="bg-slate-100 rounded-lg p-0.5 inline-flex text-xs">
              <button onClick={() => setFilter('upcoming')} className={`px-2.5 py-1 rounded ${filter === 'upcoming' ? 'bg-white text-slate-900 shadow-sm font-semibold' : 'text-slate-600'}`}>Next 30 days</button>
              <button onClick={() => setFilter('all')} className={`px-2.5 py-1 rounded ${filter === 'all' ? 'bg-white text-slate-900 shadow-sm font-semibold' : 'text-slate-600'}`}>All</button>
            </div>
          </div>

          {visible.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 text-center">No uncovered policies hit a milestone in the next 30 days. Switch to "All" to see the full backlog.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {visible.map(row => (
                <div key={row.key} className="p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-slate-800">{row.insured || '(no name)'}</div>
                        <SmsCoverageIssueBadge issue={row.issue}/>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {row.carrier || '—'} · policy <code>{row.policy}</code>
                        {row.issueDate && <> · issued {row.issueDate}</>}
                        {row.premium > 0 && <> · ${row.premium.toFixed(0)}/mo</>}
                      </div>
                      <div className="text-xs mt-1">
                        {row.ageDays != null ? (
                          <span className="text-slate-600">
                            <strong>{row.ageDays}</strong> day{row.ageDays === 1 ? '' : 's'} on book
                            {row.nextMilestone != null && (
                              <span className="ml-2 text-violet-700">
                                · next milestone: {row.nextMilestone}-day in {row.daysToNext} day{row.daysToNext === 1 ? '' : 's'}
                              </span>
                            )}
                          </span>
                        ) : <span className="text-slate-400">no issue date</span>}
                      </div>
                    </div>
                    {editingKey !== row.key && (
                      <button onClick={() => startEdit(row)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap">
                        {row.lead ? (row.issue === 'no_phone' ? 'Add phone' : row.issue === 'no_consent' ? 'Confirm consent' : 'Reactivate') : 'Add as lead'}
                      </button>
                    )}
                  </div>
                  {editingKey === row.key && (
                    <SmsCoverageEditor
                      draft={draft}
                      setDraft={setDraft}
                      onCancel={cancelEdit}
                      onSave={() => saveEdit(row)}
                      hasExistingLead={!!row.lead}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SmsCoverageStatPill({ tone, label, count }) {
  if (count === 0) return null;
  return (
    <div className={`bg-${tone}-50 border border-${tone}-200 rounded-lg px-3 py-2`}>
      <div className={`text-${tone}-700 font-semibold tabular-nums`}>{count}</div>
      <div className={`text-${tone}-700/70 text-[10px] uppercase tracking-wide mt-0.5`}>{label}</div>
    </div>
  );
}

function SmsCoverageIssueBadge({ issue }) {
  const map = {
    no_lead:    { tone: 'red',    label: 'No lead'    },
    no_phone:   { tone: 'amber',  label: 'No phone'   },
    no_consent: { tone: 'amber',  label: 'No consent' },
    opted_out:  { tone: 'slate',  label: 'Opted out'  },
  };
  const m = map[issue] || { tone: 'slate', label: issue };
  return <span className={`text-[10px] font-bold uppercase tracking-wider bg-${m.tone}-100 text-${m.tone}-700 px-1.5 py-0.5 rounded-full`}>{m.label}</span>;
}

function SmsCoverageEditor({ draft, setDraft, onCancel, onSave, hasExistingLead }) {
  return (
    <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <label className="block">
          <span className="text-xs text-slate-500">Phone</span>
          <input
            type="tel"
            value={draft.phone}
            onChange={e => setDraft({ ...draft, phone: e.target.value })}
            placeholder="(555) 123-4567"
            className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
            autoFocus
          />
        </label>
        <label className="block">
          <span className="text-xs text-slate-500">Timezone</span>
          <select
            value={draft.timezone}
            onChange={e => setDraft({ ...draft, timezone: e.target.value })}
            className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
          >
            {SMS_TZ_OPTIONS.map(tz => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-slate-500">Draft day <span className="text-slate-400">(optional)</span></span>
          <input
            type="number" min="1" max="31"
            value={draft.draftDay}
            onChange={e => setDraft({ ...draft, draftDay: e.target.value })}
            placeholder="15"
            className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
          />
        </label>
      </div>
      <label className="flex items-start gap-2 text-xs text-slate-700">
        <input
          type="checkbox"
          checked={draft.consent}
          onChange={e => setDraft({ ...draft, consent: e.target.checked })}
          className="mt-0.5"
        />
        <span>
          <strong>I confirm this client gave consent</strong> to receive automated text messages (TCPA / prior express written consent). Required before the engine can text them.
        </span>
      </label>
      <div className="flex items-center justify-end gap-2 pt-1">
        <button onClick={onCancel} className="text-xs text-slate-600 hover:text-slate-900 px-2 py-1">Cancel</button>
        <button onClick={onSave} disabled={!draft.consent} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold px-3 py-1.5 rounded-lg">
          {hasExistingLead ? 'Save' : 'Create lead'}
        </button>
      </div>
    </div>
  );
}

function apptStatusClass(s) {
  switch (s) {
    case 'booked':      return 'bg-blue-100 text-blue-700';
    case 'confirmed':   return 'bg-violet-100 text-violet-700';
    case 'showed':      return 'bg-emerald-100 text-emerald-700';
    case 'no_show':     return 'bg-red-100 text-red-700';
    case 'closed_won':  return 'bg-emerald-600 text-white';
    case 'closed_lost': return 'bg-slate-200 text-slate-600';
    default:            return 'bg-slate-100 text-slate-600';
  }
}

function fmtRel(iso) {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const diffSec = Math.round((Date.now() - t) / 1000);
  if (diffSec < 60)      return `${diffSec}s ago`;
  if (diffSec < 3600)    return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86400)   return `${Math.round(diffSec / 3600)}h ago`;
  if (diffSec < 86400*7) return `${Math.round(diffSec / 86400)}d ago`;
  return new Date(t).toLocaleDateString();
}

// ────────────────────────────────────────────────────────────────────
// FEX Goal Engine — the $50k/month machine
//
// Operator enters their monthly commission target + funnel assumptions.
// The engine reverse-engineers: deals needed → shows → appts → contacts
// → dials → leads → lead spend → VAs required → daily non-negotiables.
//
// Then it pulls THIS MONTH's actual activity from the CRM (outbound
// calls, appointments, closes) and shows pace: are you on track to hit
// the target, or behind?
//
// Designed primarily for FEX shops but works for MP/IUL by tweaking
// the funnel %s and avg AP. Defaults from FEX_CRM_KPI_Engine.pdf.
// ────────────────────────────────────────────────────────────────────

const DEFAULT_GOAL_CONFIG = {
  monthlyTarget:      50000,
  avgAnnualPremium:   1000,
  commissionRate:     1.00,
  lapseRate:          0.25,
  workingDays:        22,
  costPerLead:        0.75,
  vaBasePay:          1200,
  vaBonusPerSale:     50,
  dialToContact:      0.15,
  contactToAppt:      0.20,
  apptToShow:         0.65,
  showToSale:         0.25,
  dialsPerLead:       5,
  dialsPerVaPerDay:   350,
};

function GoalEngineView({ goalConfig, setGoalConfig, calls = [], leads = [], settings = {}, transactions = [], producers = {} }) {
  const cfg = { ...DEFAULT_GOAL_CONFIG, ...(goalConfig || {}) };

  // Setter that coerces numerics. Percent fields are entered as 0-100
  // in the UI but stored as 0-1 ratios, so we divide-by-100 on write
  // and multiply-by-100 on display.
  const set = (key, raw, kind = 'num') => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    const value = kind === 'pct' ? n / 100 : n;
    setGoalConfig({ ...cfg, [key]: value });
  };
  const resetDefaults = () => setGoalConfig({ ...DEFAULT_GOAL_CONFIG });

  // ── Required production per month ─────────────────────────────────
  // Math matches the FEX KPI engine PDF (verified against the $50k
  // scenario — 67 deals, 13,744 dials, 625 dials/day, 2 VAs).
  const realizedCommPerDeal = cfg.avgAnnualPremium * cfg.commissionRate * (1 - cfg.lapseRate);
  const dealsNeeded     = realizedCommPerDeal > 0 ? Math.ceil(cfg.monthlyTarget / realizedCommPerDeal) : 0;
  const grossAP         = dealsNeeded * cfg.avgAnnualPremium;
  const showsNeeded     = cfg.showToSale > 0     ? Math.ceil(dealsNeeded / cfg.showToSale)    : 0;
  const apptsNeeded     = cfg.apptToShow > 0     ? Math.ceil(showsNeeded / cfg.apptToShow)    : 0;
  const contactsNeeded  = cfg.contactToAppt > 0  ? Math.ceil(apptsNeeded / cfg.contactToAppt) : 0;
  const dialsNeeded     = cfg.dialToContact > 0  ? Math.ceil(contactsNeeded / cfg.dialToContact) : 0;
  const leadsNeeded     = cfg.dialsPerLead > 0   ? Math.ceil(dialsNeeded / cfg.dialsPerLead)  : 0;
  const leadSpend       = leadsNeeded * cfg.costPerLead;
  const dailyCapacity   = cfg.dialsPerVaPerDay * cfg.workingDays;
  const vasNeeded       = dailyCapacity > 0 ? Math.max(1, Math.ceil(dialsNeeded / dailyCapacity)) : 0;

  // ── Daily non-negotiables ─────────────────────────────────────────
  const dailySales      = cfg.workingDays > 0 ? dealsNeeded   / cfg.workingDays : 0;
  const dailyAppts      = cfg.workingDays > 0 ? apptsNeeded   / cfg.workingDays : 0;
  const dailyDials      = cfg.workingDays > 0 ? dialsNeeded   / cfg.workingDays : 0;
  const apptsPerVaPerDay = (vasNeeded > 0 && cfg.workingDays > 0) ? apptsNeeded / (vasNeeded * cfg.workingDays) : 0;

  // ── Cost & ROI ────────────────────────────────────────────────────
  const vaCost          = vasNeeded * cfg.vaBasePay + dealsNeeded * cfg.vaBonusPerSale;
  const totalCost       = leadSpend + vaCost;
  const cpa             = dealsNeeded > 0 ? totalCost / dealsNeeded : 0;
  const netAfterCost    = cfg.monthlyTarget - totalCost;
  const returnPerLeadDollar = leadSpend > 0 ? cfg.monthlyTarget / leadSpend : 0;

  // ── This month's actuals ──────────────────────────────────────────
  const monthBounds = React.useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0); // last day of month
    return { startMs: start.getTime(), endMs: end.getTime() + 24 * 60 * 60 * 1000 - 1, now, start, end };
  }, []);

  const monthActuals = React.useMemo(() => {
    const startStr = new Date(monthBounds.startMs).toISOString().slice(0, 10);
    const monthOutbound = (calls || []).filter(c => c.direction === 'outbound' && (c.date || '') >= startStr);
    const dials = monthOutbound.length;
    const contacts = monthOutbound.filter(c => isContactOutcome(c.outcome)).length;
    const apptsLogged = monthOutbound.filter(c => c.outcome === 'appt_set').length;

    // Sales: leads that closed this month. We don't have a closedAt
    // field, so use the most recent timestamp we can find on the lead
    // (updatedAt, then createdAt, then appointmentDate).
    const closedThisMonth = (leads || []).filter(l => {
      if (l.status !== 'closed') return false;
      const stamp = l.closedAt || l.updatedAt || l.appointmentDate || l.createdAt || '';
      if (!stamp) return false;
      const t = Date.parse(stamp);
      return Number.isFinite(t) && t >= monthBounds.startMs && t <= monthBounds.endMs;
    });
    const sales = closedThisMonth.length;
    // Realized commission per closed deal uses the operator's assumptions
    // (premium × 12 × comm rate × (1 - lapse)) so the math is consistent
    // with the targets.
    const commissionEarned = closedThisMonth.reduce((s, l) => {
      const monthly = Number(l.premium) || 0;
      return s + monthly * 12 * cfg.commissionRate * (1 - cfg.lapseRate);
    }, 0);

    // Working days elapsed (Mon-Fri, capped at cfg.workingDays). Used
    // to compute "where you should be by now" pace targets.
    let workdaysElapsed = 0;
    for (let d = new Date(monthBounds.startMs); d <= monthBounds.now && workdaysElapsed < cfg.workingDays; d.setDate(d.getDate() + 1)) {
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6) workdaysElapsed++;
    }
    return { dials, contacts, apptsLogged, sales, commissionEarned, workdaysElapsed };
  }, [calls, leads, monthBounds, cfg.commissionRate, cfg.lapseRate, cfg.workingDays]);

  const paceFraction = cfg.workingDays > 0 ? monthActuals.workdaysElapsed / cfg.workingDays : 0;
  const expectedByNow = {
    dials:      dialsNeeded     * paceFraction,
    contacts:   contactsNeeded  * paceFraction,
    appts:      apptsNeeded     * paceFraction,
    sales:      dealsNeeded     * paceFraction,
    commission: cfg.monthlyTarget * paceFraction,
  };
  const paceVs = (actual, expected) => expected > 0 ? (actual / expected) * 100 : null;

  // ── UI ────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-emerald-700 via-emerald-800 to-slate-900 text-white rounded-2xl p-6">
        <div className="text-emerald-200 text-xs font-semibold uppercase tracking-wider">Goal Engine</div>
        <h2 className="text-3xl font-bold mt-1">The {fmt(cfg.monthlyTarget)}/month machine</h2>
        <p className="text-emerald-100 text-sm mt-2 max-w-3xl">
          Reverse-engineered from your target. Hit the daily dial number → make the income. Tweak the levers below to match
          your actual funnel — the daily non-negotiables update live.
        </p>
      </div>

      {/* Assumptions panel */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
          <div>
            <h3 className="font-semibold text-slate-900">Your levers</h3>
            <p className="text-xs text-slate-500 mt-0.5">Numbers from your real funnel. Defaults match the FEX playbook ($50k / $1k AP / 25% lapse).</p>
          </div>
          <button onClick={resetDefaults} className="text-xs text-slate-500 hover:text-slate-700 underline">Reset to FEX defaults</button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <GoalField label="Monthly commission target" prefix="$" value={cfg.monthlyTarget} onChange={v => set('monthlyTarget', v)}/>
          <GoalField label="Avg annual premium / deal" prefix="$" value={cfg.avgAnnualPremium} onChange={v => set('avgAnnualPremium', v)}/>
          <GoalField label="Commission rate" suffix="%" value={cfg.commissionRate * 100} onChange={v => set('commissionRate', v, 'pct')}/>
          <GoalField label="Lapse / chargeback rate" suffix="%" value={cfg.lapseRate * 100} onChange={v => set('lapseRate', v, 'pct')}/>
          <GoalField label="Working days / month" value={cfg.workingDays} onChange={v => set('workingDays', v)}/>
          <GoalField label="Cost per aged lead" prefix="$" value={cfg.costPerLead} onChange={v => set('costPerLead', v)} step="0.01"/>
          <GoalField label="VA base pay / month" prefix="$" value={cfg.vaBasePay} onChange={v => set('vaBasePay', v)}/>
          <GoalField label="VA bonus / sale" prefix="$" value={cfg.vaBonusPerSale} onChange={v => set('vaBonusPerSale', v)}/>
          <GoalField label="Dial → Contact" suffix="%" value={cfg.dialToContact * 100} onChange={v => set('dialToContact', v, 'pct')}/>
          <GoalField label="Contact → Appt set" suffix="%" value={cfg.contactToAppt * 100} onChange={v => set('contactToAppt', v, 'pct')}/>
          <GoalField label="Appt → Show" suffix="%" value={cfg.apptToShow * 100} onChange={v => set('apptToShow', v, 'pct')}/>
          <GoalField label="Show → Sale" suffix="%" value={cfg.showToSale * 100} onChange={v => set('showToSale', v, 'pct')}/>
          <GoalField label="Dial attempts / lead" value={cfg.dialsPerLead} onChange={v => set('dialsPerLead', v)}/>
          <GoalField label="Dials / VA / day capacity" value={cfg.dialsPerVaPerDay} onChange={v => set('dialsPerVaPerDay', v)}/>
        </div>
      </div>

      {/* Daily non-negotiables — the headline */}
      <div className="bg-white border-2 border-emerald-200 rounded-2xl p-6">
        <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-1">Daily non-negotiables</div>
        <p className="text-sm text-slate-500 mb-4">Hit these every working day for {cfg.workingDays} days. That's the whole game.</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <DailyTile label="Sales / day"           value={fmtRound(dailySales, 1)}             tone="emerald"/>
          <DailyTile label="Appts set / day (team)" value={fmtRound(dailyAppts, 1)}             tone="emerald"/>
          <DailyTile label="Dials / day (team)"    value={fmtRound(dailyDials, 0)}             tone="emerald"/>
          <DailyTile label="VAs needed"            value={String(vasNeeded)}                   tone="slate"/>
          <DailyTile label="Appts / VA / day"      value={fmtRound(apptsPerVaPerDay, 1)}       tone="slate"/>
        </div>
      </div>

      {/* This month's pace */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
          <div>
            <h3 className="font-semibold text-slate-900">This month's pace</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {monthActuals.workdaysElapsed} of {cfg.workingDays} working days elapsed ({(paceFraction * 100).toFixed(0)}% of the month).
              Bars show where you should be by now.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <PaceTile label="Dials"      actual={monthActuals.dials}             expected={expectedByNow.dials}      total={dialsNeeded}/>
          <PaceTile label="Contacts"   actual={monthActuals.contacts}          expected={expectedByNow.contacts}   total={contactsNeeded}/>
          <PaceTile label="Appts set"  actual={monthActuals.apptsLogged}       expected={expectedByNow.appts}      total={apptsNeeded}/>
          <PaceTile label="Sales"      actual={monthActuals.sales}             expected={expectedByNow.sales}      total={dealsNeeded}/>
          <PaceTile label="Commission" actual={monthActuals.commissionEarned}  expected={expectedByNow.commission} total={cfg.monthlyTarget} isMoney/>
        </div>
      </div>

      {/* What you must produce per month */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h3 className="font-semibold text-slate-900 mb-1">What you must produce per month</h3>
        <p className="text-xs text-slate-500 mb-4">Calculated from your levers. You write <strong>{fmt(grossAP)}</strong> of AP to keep <strong>{fmt(cfg.monthlyTarget)}</strong> — the gap is the {(cfg.lapseRate * 100).toFixed(0)}% lapse, which is why persistency is everything.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <ProductionTile label="Realized commission / deal" value={fmt(realizedCommPerDeal)} sub={`AP × ${(cfg.commissionRate * 100).toFixed(0)}% × (1 − lapse)`}/>
          <ProductionTile label="Deals needed"               value={dealsNeeded.toLocaleString()} tip={`monthly target ${fmt(cfg.monthlyTarget)} ÷ realized commission per deal (${fmt(realizedCommPerDeal)})`}/>
          <ProductionTile label="Gross AP to write"          value={fmt(grossAP)} sub="before lapse"/>
          <ProductionTile label="Shows needed"               value={showsNeeded.toLocaleString()} tip={`deals ÷ close rate (${(cfg.closeRate * 100).toFixed(0)}%) — i.e. how many in-person/phone presentations are needed to land the deal count above`}/>
          <ProductionTile label="Appointments needed"        value={apptsNeeded.toLocaleString()} tip={`shows ÷ show rate (${(cfg.showRate * 100).toFixed(0)}%) — books shows accounting for no-shows`}/>
          <ProductionTile label="Contacts needed"            value={contactsNeeded.toLocaleString()} tip={`appointments ÷ contact-to-appt rate (${(cfg.contactToAppt * 100).toFixed(0)}%)`}/>
          <ProductionTile label="Dials needed"               value={dialsNeeded.toLocaleString()} highlight tip={`contacts ÷ dial-to-contact rate (${(cfg.dialToContact * 100).toFixed(0)}%). Working backward from your monthly target through every funnel stage: deals → shows → appts → contacts → dials. Tune the conversion levers below if your real numbers don't match these defaults.`}/>
          <ProductionTile label="Leads needed"               value={leadsNeeded.toLocaleString()} tip={`dials ÷ dials-per-lead (${cfg.dialsPerLead}) — how many lead records you need to feed the dial engine for the month`}/>
        </div>
      </div>

      {/* Cost & ROI */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h3 className="font-semibold text-slate-900 mb-1">Cost & ROI</h3>
        <p className="text-xs text-slate-500 mb-4">Net is pre-tax, before E&O, dialer, CRM and overhead.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          <ProductionTile label="Lead spend / month"          value={fmt(leadSpend)}/>
          <ProductionTile label="VA cost / month"             value={fmt(vaCost)} sub={`${vasNeeded} VA${vasNeeded === 1 ? '' : 's'} + sale bonuses`}/>
          <ProductionTile label="Total lead + VA cost"        value={fmt(totalCost)}/>
          <ProductionTile label="Cost per acquisition (CPA)"  value={fmt(cpa)}/>
          <ProductionTile label="Net after lead + VA cost"    value={fmt(netAfterCost)} highlight/>
          <ProductionTile label="Return per $1 of lead spend" value={`${returnPerLeadDollar.toFixed(2)}×`}/>
        </div>
      </div>
    </div>
  );
}

function GoalField({ label, value, onChange, prefix, suffix, step = '1' }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-500 block mb-1">{label}</span>
      <div className="relative">
        {prefix && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{prefix}</span>}
        <input
          type="number"
          step={step}
          value={Number.isFinite(value) ? value : 0}
          onChange={e => onChange(e.target.value)}
          className={`w-full border border-slate-200 rounded-lg py-1.5 ${prefix ? 'pl-5' : 'pl-2'} ${suffix ? 'pr-7' : 'pr-2'} text-sm text-right tabular-nums focus:border-emerald-400 focus:outline-none`}
        />
        {suffix && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{suffix}</span>}
      </div>
    </label>
  );
}

function DailyTile({ label, value, tone }) {
  return (
    <div className={`bg-${tone}-50 border border-${tone}-200 rounded-xl p-4 text-center`}>
      <div className={`text-3xl md:text-4xl font-bold text-${tone}-700 tabular-nums`}>{value}</div>
      <div className={`text-[10px] font-bold uppercase tracking-wider text-${tone}-700 mt-1`}>{label}</div>
    </div>
  );
}

function ProductionTile({ label, value, sub, highlight = false, tip = null }) {
  return (
    <div className={`rounded-xl p-3 ${highlight ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50'}`} title={tip || undefined}>
      <div className="text-xs text-slate-500 inline-flex items-center gap-1">
        {label}
        {tip && <span className="text-slate-400 cursor-help" aria-label={tip}>ⓘ</span>}
      </div>
      <div className={`text-xl font-bold tabular-nums mt-1 ${highlight ? 'text-emerald-700' : 'text-slate-900'}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function PaceTile({ label, actual, expected, total, isMoney = false }) {
  const pct = expected > 0 ? Math.min(200, (actual / expected) * 100) : null;
  const tone = pct == null ? 'slate' : pct >= 100 ? 'emerald' : pct >= 75 ? 'amber' : 'red';
  const totalPct = total > 0 ? Math.min(100, (actual / total) * 100) : 0;
  const f = (n) => isMoney ? fmt(n) : Math.round(n).toLocaleString();
  return (
    <div className={`bg-${tone}-50 border border-${tone}-200 rounded-xl p-3`}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600">{label}</div>
      <div className={`text-2xl font-bold tabular-nums mt-1 text-${tone}-700`}>{f(actual)}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">
        of {f(expected)} expected · {pct == null ? '—' : `${pct.toFixed(0)}%`}
      </div>
      <div className="h-1.5 bg-white rounded-full overflow-hidden mt-2">
        <div className={`h-full bg-${tone}-500`} style={{ width: `${totalPct}%` }}/>
      </div>
      <div className="text-[10px] text-slate-400 mt-0.5">monthly goal: {f(total)}</div>
    </div>
  );
}

function fmtRound(n, decimals = 0) {
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(decimals);
}

// ════════════════════════════════════════════════════════════════════
// FEX onboarding suite — content lifted from the
// New_Agent_Onboarding_Packet.pdf. Sub-components below render the
// SOP / script / station map / automation map / QC reference into the
// FEX hub so a new agent + their VA can run the system without
// switching to a PDF.
// ════════════════════════════════════════════════════════════════════

// ── Reusable: collapsible card ────────────────────────────────────
function FexAccordion({ title, defaultOpen = false, children, subtitle = '', accent = 'red' }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`bg-white border border-slate-200 rounded-xl overflow-hidden`}>
      <button onClick={() => setOpen(o => !o)} className={`w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 text-left`}>
        <div>
          <div className={`font-semibold text-slate-900 flex items-center gap-2`}>
            <span className={`text-xs font-bold uppercase tracking-wider text-${accent}-700`}>{open ? '▾' : '▸'}</span>
            {title}
          </div>
          {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
        </div>
      </button>
      {open && <div className="px-4 pb-4 border-t border-slate-100">{children}</div>}
    </div>
  );
}

// ── Reusable: a script "what the VA literally says" block ─────────
function ScriptBlock({ children }) {
  return (
    <div className="bg-emerald-50 border-l-4 border-emerald-500 px-4 py-3 my-2 text-sm text-slate-800 italic leading-relaxed">
      "{children}"
    </div>
  );
}

// ── Reusable: a checkbox row that persists into a record ──────────
function CheckRow({ checked, onChange, children }) {
  return (
    <label className="flex items-start gap-2 py-1.5 text-sm cursor-pointer hover:bg-slate-50 -mx-2 px-2 rounded">
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} className="mt-1 rounded"/>
      <span className={checked ? 'text-slate-500 line-through' : 'text-slate-700'}>{children}</span>
    </label>
  );
}

// ─── Onboarding — Phase 0 → Phase 4 ────────────────────────────────
// Each phase has a checklist; state persists to user_state via the
// fexOnboarding key. Visual phase progression bar at top.

const FEX_PHASES = [
  {
    id: 'p0',
    label: 'Phase 0 · Day 1',
    title: 'Stand up the machine',
    when: 'Day 1',
    items: [
      'Buy the CRM (Six Figure Agent Basic $100/mo) — get GHL login',
      'Register A2P 10DLC, connect calendar + number',
      'Set up WAVV dialer (your seat) + add your VA\'s seat',
      'Add VA as a CRM user (My Staff → Add Employee → User role)',
    ],
  },
  {
    id: 'p1',
    label: 'Phase 1 · Days 1-2',
    title: 'Learn your station',
    when: 'Days 1-2',
    items: [
      'Agent: learn the close script + the quoting tool',
      'VA: learn the setter script + the daily task flow',
      'Confirm the 9 workflows are live (drips, reminders, retention)',
      'Import + tag your first aged-lead list; build a dial Smart List',
    ],
  },
  {
    id: 'p2',
    label: 'Phase 2 · Week 1',
    title: 'Go live & dial',
    when: 'Week 1',
    items: [
      'VA dials ~250/day and books 7 appointments/day',
      'Agent sits every appointment, closes, moves the card',
      'Booking drip + confirmation + no-show recovery run automatically',
      'Disposition every call — the dashboard builds your real numbers',
    ],
  },
  {
    id: 'p3',
    label: 'Phase 3 · Weeks 2-4',
    title: 'Hit the standard',
    when: 'Weeks 2-4',
    items: [
      'Lock the daily standard: ~252 dials · 7 booked · ~1-2 closes',
      'Watch close %, show %, lapse % on the dashboard; fix the weak number',
      'Protect persistency — retention drip + draft-date reminders catch lapses in 24h',
      'First deals land; funnel data settles toward $20k pace',
    ],
  },
  {
    id: 'p4',
    label: 'Phase 4 · Day 31+',
    title: 'Optimize & duplicate',
    when: 'Day 31+',
    items: [
      'Cut low-ROI lead sources; double the winners (see Source ROI tab)',
      'Coach the VA from call recordings; tune the script',
      'When ready to add an agent: load the Snapshot, repeat this path',
    ],
  },
];

function FexOnboarding({ state = {}, setState }) {
  const get = (phaseId, idx) => !!(state[phaseId] && state[phaseId][idx]);
  const set = (phaseId, idx, val) => {
    setState({
      ...state,
      [phaseId]: { ...(state[phaseId] || {}), [idx]: val },
    });
  };
  const phasePct = (p) => {
    const done = p.items.filter((_, i) => get(p.id, i)).length;
    return Math.round((done / p.items.length) * 100);
  };
  const currentPhase = FEX_PHASES.findIndex(p => phasePct(p) < 100);
  const overallPct = Math.round(
    FEX_PHASES.reduce((s, p) => s + phasePct(p), 0) / FEX_PHASES.length
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <h3 className="font-semibold text-slate-900">Zero → $20k · The Critical Path</h3>
            <p className="text-xs text-slate-500 mt-0.5">Follow the phases in order. Don't read ahead — execute the current phase and move.</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-red-700 tabular-nums">{overallPct}%</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">complete</div>
          </div>
        </div>
        {/* Phase progression bar */}
        <div className="flex items-center gap-1">
          {FEX_PHASES.map((p, i) => {
            const pct = phasePct(p);
            const isActive = i === currentPhase;
            const isDone = pct === 100;
            return (
              <div key={p.id} className="flex-1">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 truncate">{p.label}</div>
                <div className={`h-2 rounded-full overflow-hidden ${isDone ? 'bg-emerald-200' : isActive ? 'bg-red-100' : 'bg-slate-100'}`}>
                  <div className={`h-full ${isDone ? 'bg-emerald-600' : 'bg-red-500'}`} style={{ width: `${pct}%` }}/>
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">{p.title}</div>
              </div>
            );
          })}
        </div>
      </div>

      {FEX_PHASES.map((p, i) => {
        const pct = phasePct(p);
        const tone = pct === 100 ? 'emerald' : i === currentPhase ? 'red' : 'slate';
        return (
          <div key={p.id} className={`bg-white border-2 border-${tone}-200 rounded-xl overflow-hidden`}>
            <div className={`px-4 py-3 bg-${tone}-50 border-b border-${tone}-100 flex items-baseline justify-between`}>
              <div>
                <div className={`text-xs font-bold uppercase tracking-wider text-${tone}-700`}>{p.label}</div>
                <div className="font-semibold text-slate-900 mt-0.5">{p.title}</div>
              </div>
              <div className={`text-sm font-semibold text-${tone}-700`}>{pct === 100 ? '✓ Done' : `${pct}%`}</div>
            </div>
            <div className="px-4 py-3">
              {p.items.map((it, idx) => (
                <CheckRow key={idx} checked={get(p.id, idx)} onChange={(v) => set(p.id, idx, v)}>
                  {it}
                </CheckRow>
              ))}
            </div>
          </div>
        );
      })}

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
        <strong>The one question that runs everything:</strong> Are we making ~252 dials and booking 7 appointments a day? If yes, $20k follows.
        Protect the dials, protect the show, protect the net.
      </div>
    </div>
  );
}

// ─── 4 Stations ───────────────────────────────────────────────────
function FexStations() {
  const stations = [
    { n: 1, name: 'Lead Intake',           who: 'Ops / VA',           inOut: 'Aged CSV → clean, scored, tagged dial list',  std: '100% scrubbed + tagged before any dial', accent: 'emerald' },
    { n: 2, name: 'The Dial Floor',        who: 'VA',                 inOut: 'Dial list → booked appointments',              std: '~250 dials · 7 booked/day · every call dispositioned', accent: 'emerald' },
    { n: 3, name: 'The Close',             who: 'Agent (licensed)',   inOut: 'Booked appt → issued policy',                  std: 'Sit every appt · close · move the card', accent: 'amber' },
    { n: 4, name: 'Retention / Back Office', who: 'Automated (CRM)', inOut: 'Issued policy → persisting client + referrals', std: 'Every policy in retention · lapses caught in 24h', accent: 'emerald' },
  ];
  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="font-semibold text-slate-900">The 4 Stations</h3>
        <p className="text-xs text-slate-500 mt-0.5">Like any franchise, the work is broken into stations. Each has one owner, one input, one output, one standard.</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-600">
              <th className="px-4 py-2">Station</th>
              <th className="px-4 py-2">Runs it</th>
              <th className="px-4 py-2">In → Out</th>
              <th className="px-4 py-2">The standard</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {stations.map(s => (
              <tr key={s.n}>
                <td className="px-4 py-3">
                  <div className={`text-[10px] font-bold uppercase tracking-wider text-${s.accent}-700`}>Station {s.n}</div>
                  <div className="font-semibold text-slate-900">{s.name}</div>
                </td>
                <td className="px-4 py-3 text-slate-700">{s.who}</td>
                <td className="px-4 py-3 text-slate-700">{s.inOut}</td>
                <td className="px-4 py-3 text-slate-700">{s.std}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 text-sm text-emerald-900">
        <strong>The rule that runs the franchise:</strong> Stations 1, 2 and 4 are run by the VA and the automated system.
        <strong> Station 3 — the close — is the only place the agent works,</strong> and the only place that legally requires a licensed human.
        Everything is built to deliver the agent a full calendar and get out of their way.
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h4 className="font-semibold text-slate-900 mb-3">Your daily math — per month → $20k</h4>
        <div className="grid grid-cols-5 gap-2 text-center">
          {[
            { v: '5,538', l: 'DIALS / MO', tone: 'red' },
            { v: '831',   l: 'CONTACTS',   tone: 'red' },
            { v: '166',   l: 'APPOINTMENTS', tone: 'amber' },
            { v: '108',   l: 'SHOWS',      tone: 'amber' },
            { v: '27',    l: 'DEALS → $20K', tone: 'emerald' },
          ].map((s, i) => (
            <div key={i} className={`bg-${s.tone}-50 border border-${s.tone}-200 rounded-lg p-3`}>
              <div className={`text-2xl md:text-3xl font-bold text-${s.tone}-700 tabular-nums`}>{s.v}</div>
              <div className={`text-[10px] font-bold uppercase tracking-wider text-${s.tone}-700 mt-1`}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── VA Playbook — Setter Script + Daily Schedule + EOD Report ─────
function FexVaPlaybook({ settings = {} }) {
  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="font-semibold text-slate-900">VA Operations Playbook</h3>
        <p className="text-xs text-slate-500 mt-1">
          FEX Appointment Setter — your role, rules, and hour-by-hour day. One standard: <strong>7 appointments
          booked on the agent's calendar, every day.</strong>
        </p>
      </div>

      {/* Role one-liner */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-900">
        <strong>Your role in one line:</strong> You connect interested leads to the licensed underwriter — by live transfer
        when the agent is free, by a booked appointment always. You verify and schedule.
        <strong> You do not sell, quote, or advise.</strong>
      </div>

      {/* Do / Don't grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white border border-emerald-200 rounded-xl p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">What you do</div>
          <ul className="text-sm space-y-1.5 text-slate-700">
            <li>✓ Dial the aged-lead list through the dialer</li>
            <li>✓ Run the setter script on every pickup</li>
            <li>✓ Verify identity + interest, then lightly qualify</li>
            <li>✓ Live-transfer OR book the appointment</li>
            <li>✓ Lock it: confirm number, set expectations, get the "yes"</li>
            <li>✓ Trigger the confirmation text + reminders</li>
            <li>✓ Log a disposition on EVERY call</li>
            <li>✓ Rebook no-shows; report your numbers daily</li>
          </ul>
        </div>
        <div className="bg-white border border-red-200 rounded-xl p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-red-700 mb-2">What you never do</div>
          <ul className="text-sm space-y-1.5 text-slate-700">
            <li>✕ Quote a price or coverage amount — ever</li>
            <li>✕ Say or imply you're licensed / an agent</li>
            <li>✕ Give insurance or medical advice</li>
            <li>✕ Argue with a "remove me" — mark DNC, move on</li>
            <li>✕ Call outside 9 AM – 7 PM the lead's local time</li>
            <li>✕ Skip logging a disposition</li>
          </ul>
        </div>
      </div>

      {/* Setter Script — collapsible by section */}
      <div className="space-y-2">
        <h4 className="font-semibold text-slate-900 mt-4">Setter Script — by section</h4>

        <FexAccordion title="1 · The Open (first 10 seconds — this is everything)" subtitle="Slow, calm, familiar — like you already know them" defaultOpen>
          <ScriptBlock>
            Hi, is this [Client first name]? (pause) Hey [Client name], it's [VA first name] — I'm with [Agency],
            calling for the licensed underwriter on your file. I've actually got your file pulled up here, I just need
            to verify one quick thing before I get you over to them. I have your date of birth as [DOB] — is that correct?
          </ScriptBlock>
          <p className="text-xs text-slate-500">The DOB confirm is your identity check and the pattern-interrupt that stops the hang-up.</p>
        </FexAccordion>

        <FexAccordion title="2 · Reason for the call (re-engage)">
          <ScriptBlock>
            Perfect, thank you. So the reason I'm reaching out — you'd requested information on your life insurance /
            final expense options here in [state], and [Agent], the licensed underwriter assigned to your file, asked me
            to get you connected so they can go over your options. Are you still looking to get that taken care of?
          </ScriptBlock>
        </FexAccordion>

        <FexAccordion title="3 · Quick qualify (3-4 questions — you're confirming, not closing)">
          <ul className="text-sm text-slate-700 space-y-1 ml-4 list-disc">
            <li>Were you looking to cover just yourself, or you and a spouse?</li>
            <li>Mainly to cover final expenses — funeral and burial — or to leave a little extra behind too?</li>
            <li>And are you the one who'd be making the decision on this?</li>
            <li>Last one, just for my notes — overall, would you say your health is good, fair, or poor?</li>
          </ul>
          <p className="text-xs text-slate-500 mt-2">Keep health to ONE soft question — the agent does the real medical.</p>
        </FexAccordion>

        <FexAccordion title="4 · The transition (always try the live transfer first)">
          <div className="text-xs font-bold text-emerald-700 uppercase tracking-wider mt-1">Path A — Live Transfer</div>
          <ScriptBlock>
            You know what, [Client] — good news. [Agent], your licensed underwriter, actually has a couple minutes free
            right now. Instead of waiting on a callback, let me walk you right over so they can pull your exact numbers
            up live. Give me one second, don't go anywhere — okay?
          </ScriptBlock>
          <div className="text-xs font-bold text-amber-700 uppercase tracking-wider mt-2">Path B — Set the Appointment</div>
          <ScriptBlock>
            Here's what I'll do then — I'm going to lock you in a quick 15-minute call with [Agent], the licensed
            underwriter, so they can pull your exact options and pricing. They've got two openings — [today at TIME A]
            or [tomorrow at TIME B]. Which one works better for you?
          </ScriptBlock>
          <p className="text-xs text-slate-500">Pick-one beats "want to book a call?" every time.</p>
        </FexAccordion>

        <FexAccordion title="5 · Lock it down & confirm">
          <ScriptBlock>
            Perfect — I've got you down for [DAY at TIME, time zone]. I'm putting it on [Agent]'s calendar right now
            under your name. They'll call you from [NUMBER] — go ahead and save that so you know it's them. It runs
            about 15 minutes, and it helps to have a pen and paper handy. Can I count on you to be by your phone at [TIME]?
          </ScriptBlock>
          <p className="text-xs text-slate-500">That last question is the micro-commitment — get the verbal "yes."</p>
        </FexAccordion>

        <FexAccordion title="6 · Warm-transfer handoff (10 seconds, OFF the client's line)">
          <ScriptBlock>
            Hey [Agent], warm transfer — this is [Client first/last], age [XX], in [state]. Requested final expense info.
            Wants to cover [their goal], it's for [self / + spouse], they're the decision-maker, health is [good/fair/poor].
            Reason now: [their why]. They're ready — go.
          </ScriptBlock>
        </FexAccordion>

        <FexAccordion title="7 · Objection handling (setter level)">
          <div className="space-y-3 mt-1">
            {[
              ['"I\'m not interested / don\'t remember filling anything out."', 'Totally understand — a lot of folks forget, it may have been a little while back. You\'d put in a request about covering final expenses so your family isn\'t stuck with the bill. Since I\'ve got your file open — is that still something you\'d want handled?'],
              ['"Who are you / what is this?"', 'Fair question — my name\'s [VA] with [Agency]. You requested info on life insurance options in [state], and I\'m just the person who connects you with the licensed underwriter, [Agent]. I don\'t sell anything — I just get you the right info.'],
              ['"How much is it? Just give me a price."', 'Great question — that\'s exactly what [Agent] pulls up for you. They\'re the licensed underwriter, so they shop 10+ carriers for your best rate. Pricing\'s based on your age and health, so they\'ll have your exact number on the call. Let\'s grab you a time…'],
              ['"Just email or mail me the info."', 'I hear you — the thing is, your rate depends on your age and a couple health details, so a generic flyer wouldn\'t be accurate. [Agent] pulls your actual numbers live in about 15 minutes. Would [TIME A] or [TIME B] be easier?'],
              ['"I already have insurance."', 'That\'s great you\'ve got something in place. A lot of [Agent]\'s clients did too — they just wanted a quick second opinion to be sure they\'re not overpaying or under-covered. No obligation. Worth a 15-minute look — [TIME A] or [TIME B]?'],
              ['"I\'m busy right now."', 'No problem at all — that\'s exactly why I\'d rather grab you a set time. Does later today at [TIME] work, or is tomorrow morning better?'],
              ['"Is this a sales call?"', 'I\'m not selling you anything — I just schedule you with the licensed underwriter who has your file. Whether you move forward is 100% up to you after you see your options.'],
              ['"Take me off your list."', 'Absolutely, I\'ll take care of that right now — you won\'t be contacted again. Take care. (Mark DNC immediately. Honor it — no rebuttal.)'],
            ].map(([objection, reply], i) => (
              <div key={i}>
                <div className="text-sm font-semibold text-red-700">{objection}</div>
                <ScriptBlock>{reply}</ScriptBlock>
              </div>
            ))}
          </div>
        </FexAccordion>

        <FexAccordion title="8 · Voicemail (short — curiosity + callback)">
          <ScriptBlock>
            Hi [Client], this is [VA] about the final expense information you requested — I have your file here and just
            need to confirm a couple details before your underwriter closes it out. Give me a call back at [NUMBER].
            Again that's [NUMBER]. Thanks [Client].
          </ScriptBlock>
        </FexAccordion>

        <FexAccordion title="9 · After you book (protect the show)">
          <ul className="text-sm text-slate-700 space-y-1 ml-4 list-disc">
            <li><strong>Immediately:</strong> fire the text confirmation (time + agent's number); have them reply YES.</li>
            <li><strong>Reminder 1:</strong> night-before or morning-of.</li>
            <li><strong>Reminder 2:</strong> ~1 hour before the call.</li>
            <li><strong>No-show:</strong> call back within 5 minutes to re-book; fire the no-show recovery drip.</li>
            <li><strong>Log it:</strong> move the contact to "Appointment Set" in the CRM so the reminder sequence runs.</li>
          </ul>
        </FexAccordion>
      </div>

      {/* Daily schedule */}
      <h4 className="font-semibold text-slate-900 mt-6">Your Daily Schedule</h4>
      <p className="text-xs text-slate-500">Time-blocked around the US calling window. Seniors answer best mid-morning and late afternoon / early evening — avoid the dinner hour.</p>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
        {[
          { label: 'START OF SHIFT · 15 min · Setup', tone: 'emerald', items: [
            'Log into the dialer, CRM and calendar',
            'Text a reminder to every appointment booked for today',
            'Pull yesterday\'s no-shows into your rebook list',
            'Review today\'s scheduled callbacks',
            'Load a fresh aged-lead list into the dialer',
            'Open the setter script + KPI tracker',
          ]},
          { label: 'BLOCK 1 · ~90 min · Power Dial', tone: 'red', items: [
            'Dial the list, run the script, transfer or book',
            'Log a disposition on EVERY call',
            'Block target: ~80 dials · ~2 appointments',
          ]},
          { label: 'BREAK · 10 min · MIDDAY ADMIN · 20 min', tone: 'slate', items: [
            'Confirm this afternoon\'s appointments',
            'Work your rebook + callback lists',
            'Remove DNCs and bad numbers from the dialer',
          ]},
          { label: 'BLOCK 2 · ~90 min · Power Dial', tone: 'red', items: [
            'Same as Block 1 — keep pushing toward 7',
            'Block target: ~80 dials · ~2-3 appointments',
          ]},
          { label: 'LUNCH · 15 min · BLOCK 3 · ~90 min · Prime Time', tone: 'amber', items: [
            'Your hottest window (late afternoon / early evening local)',
            'Heaviest dialing — close out the day\'s target',
            'Block target: ~90 dials · ~3 appointments',
          ]},
          { label: 'END OF SHIFT · 20 min · Wrap-Up', tone: 'emerald', items: [
            'Confirm tomorrow\'s appointments + send confirmations',
            'Check every contact is in the right CRM stage',
            'Fill in the KPI tracker (dials, contacts, appointments)',
            'Send the EOD report to your agent',
            'Flag hot leads / callbacks for tomorrow',
          ]},
        ].map((block, i) => (
          <div key={i} className="p-4">
            <div className={`text-xs font-bold uppercase tracking-wider text-${block.tone}-700 mb-2`}>{block.label}</div>
            <ul className="text-sm text-slate-700 space-y-1 ml-2">
              {block.items.map((it, j) => <li key={j}>• {it}</li>)}
            </ul>
          </div>
        ))}
      </div>

      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-900">
        <strong>Daily targets:</strong> ~250 dials · ~35-40 contacts · <strong>7 appointments booked</strong> · 100% of calls dispositioned.
        The appointment number is the one that counts — the rest are just how you get there.
      </div>

      {/* EOD Report template — printable */}
      <h4 className="font-semibold text-slate-900 mt-6">End-of-Day Report (send to your agent every day)</h4>
      <FexEodReport/>
    </div>
  );
}

function FexEodReport() {
  // Light-weight editable form — operator can fill, print (window.print)
  // or screenshot/copy. Not persisted; this is the daily disposable.
  const [r, setR] = useState({
    date: new Date().toISOString().slice(0, 10),
    name: '', dials: '', contacts: '', appts: '', transfers: '', callbacks: '', rebooks: '', notes: '',
  });
  const set = (k, v) => setR({ ...r, [k]: v });
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-600">End-of-day report</div>
        <button onClick={() => window.print()} className="bg-slate-900 hover:bg-slate-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">Print</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
        {[
          ['date', 'Date', 'date'],
          ['name', 'Your name', 'text'],
          ['dials', 'Dials', 'number'],
          ['contacts', 'Contacts', 'number'],
          ['appts', 'Appointments booked', 'number'],
          ['transfers', 'Live transfers', 'number'],
          ['callbacks', 'Callbacks scheduled', 'number'],
          ['rebooks', 'No-shows rebooked', 'number'],
        ].map(([k, label, type]) => (
          <label key={k} className="block">
            <span className="text-xs text-slate-500 block mb-1">{label}</span>
            <input
              type={type}
              value={r[k]}
              onChange={(e) => set(k, e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"
            />
          </label>
        ))}
      </div>
      <label className="block">
        <span className="text-xs text-slate-500 block mb-1">Hot leads / notes (names + times below)</span>
        <textarea
          value={r.notes}
          onChange={(e) => set('notes', e.target.value)}
          rows={4}
          className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"
        />
      </label>
    </div>
  );
}

// ─── Agent SOP — The Closer's Day ──────────────────────────────────
function FexAgentSOP() {
  const blocks = [
    { label: '1 · Morning Prep · ~25 min', tone: 'amber', items: [
      'Log into CRM, softphone/dialer, your quote tool and carrier portals',
      'Review today\'s calendar — how many appointments and when',
      'Read each VA brief (name, age, goal, why-now, health, beneficiary) — pre-load every close so you walk in warm',
      'Check the dashboard: pace to your monthly goal, yesterday\'s results',
      'Quick VA check-in: today\'s lead list + any hot live-transfers coming your way',
    ]},
    { label: '2 · Appointment Blocks · the core', tone: 'red', items: [
      'Sit every booked appointment ON TIME; stay available for live transfers in between',
      'Run the script: Intro → Why → Health → Quote (Bronze/Silver/Gold) → Close → Application → Banking → lock the close',
      'Quote tool open during the health questions; hard-close — don\'t leave without attempting',
      'Submit the application before the next appointment starts',
    ]},
    { label: '3 · Pipeline Follow-Up (between appts) · ongoing', tone: 'amber', items: [
      'No-show → quick attempt to reach, or hand to the VA to rebook',
      'Work "think about it" / not-closed leads from prior days',
      'Take live transfers as they come',
    ]},
    { label: '4 · Midday Review · ~15 min', tone: 'slate', items: [
      'Pace check: sat / closed / remaining today',
      'Chase any pending apps (approvals, banking issues)',
      'Quick VA sync',
    ]},
    { label: '5 · Prime-Time Block · late afternoon / early evening', tone: 'red', items: [
      'Your best FEX answer + close window — protect it; this is where the heaviest appointment load sits',
    ]},
    { label: '6 · Wrap-Up + App QA · ~30 min', tone: 'emerald', items: [
      'Finalize any pending applications',
      'QA every app today: no missing info that causes NSF/lapse; banking correct; draft + effective dates set',
      'Move every card to its stage; handle any payment failures (save-the-sale)',
      'Review the VA\'s EOD report; note tomorrow\'s appointments; one coaching note if needed',
    ]},
  ];
  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="font-semibold text-slate-900">Agent Daily SOP — The Closer's Day</h3>
        <p className="text-xs text-slate-500 mt-1">
          You're the closer. The system and the VA hand you booked, qualified, pre-briefed appointments.
          Your day is four things: <strong>prep, close, submit, protect.</strong> No prospecting — you harvest, you don't hunt.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
        {blocks.map((b, i) => (
          <div key={i} className="p-4">
            <div className={`text-xs font-bold uppercase tracking-wider text-${b.tone}-700 mb-2`}>{b.label}</div>
            <ul className="text-sm text-slate-700 space-y-1 ml-2">
              {b.items.map((it, j) => <li key={j}>• {it}</li>)}
            </ul>
          </div>
        ))}
      </div>

      <div className="bg-red-50 border-l-4 border-red-500 p-4 text-sm text-red-900">
        <strong>After every close — the non-negotiable admin.</strong> Skip this and retention never fires:
        <ul className="mt-2 ml-4 space-y-1 list-disc">
          <li>Move the opportunity: App Submitted → Issued & Paid</li>
          <li>Set the custom fields: carrier, coverage, premium, effective_date, draft_date, beneficiary, policy #</li>
          <li>Send the after-close "save my number" text</li>
          <li>That fires the post-sale retention workflow (W5) automatically — every time, no exceptions</li>
        </ul>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="bg-slate-50 px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-600">Daily standards (non-negotiables)</div>
        <table className="w-full text-sm">
          <tbody className="divide-y divide-slate-100">
            {[
              ['Sit 100% of booked appointments', 'On time, every one'],
              ['Attempt the close on every appt', 'Hard close — don\'t leave without it'],
              ['Submit apps same day', 'Accurate banking + dates set'],
              ['Move every card + set dates', 'So retention (W5) fires'],
              ['Stay compliant', 'Only show ≤ 10% of income; licensed quoting only'],
              ['~1-2 closes / day', 'Keeps you on $20k pace (27/mo)'],
            ].map(([std, mean], i) => (
              <tr key={i}>
                <td className="px-4 py-2 font-semibold text-slate-800 w-1/3">{std}</td>
                <td className="px-4 py-2 text-slate-600">{mean}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-900">
        <strong>The closer's mindset:</strong> You're not hunting — you're harvesting. The machine fills your calendar;
        your job is to convert and protect. Show up sharp, close clean, submit accurate, and let retention keep it on
        the books. Do that ~1-2 times a day and $20k takes care of itself.
      </div>
    </div>
  );
}

// ─── Automations Map — the 9 workflows + live status from Supabase ─
function FexAutomationsMap({ currentUser = null }) {
  const [workflows, setWorkflows] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase.from('workflows').select('id, name, active, trigger');
        setWorkflows(data || []);
      } catch { setWorkflows([]); }
    })();
  }, [currentUser?.id]);

  // The 9-workflow spec mapped to triggers in our engine. The
  // detect() function looks for a matching trigger type in the live
  // workflows list — green if at least one active, amber if seeded
  // but inactive, grey if not seeded at all.
  const SPEC = [
    { id: 'W1', name: 'Speed-to-Lead',         what: 'New lead → first text fires in seconds',                trigger: 'lead_imported',     map: 'Lead-imported triggered workflows like the MP/IUL/FEX drips' },
    { id: 'W2', name: 'Reply Drip',            what: '11-touch SMS sequence for un-booked leads',             trigger: 'lead_imported',     map: 'MP 10-Touch / IUL 8-Touch / FEX equivalents' },
    { id: 'W3', name: 'Confirm + Remind',      what: '3-touch: confirmation, night-before, 1-hour',           trigger: 'appointment_set',   map: 'Appointment Booked — Confirmation + Reminders' },
    { id: 'W4', name: 'No-Show Recovery',      what: 'Auto re-engage + re-book the moment they miss',         trigger: 'no_show',           map: 'No-Show Recovery (3-touch drip)' },
    { id: 'W5', name: 'Post-Sale Retention',   what: 'Welcome + draft-date + 30-day + lifetime loop',         trigger: 'policy_milestone',  map: 'Policy Milestone workflows (30/90/180/365 day)' },
    { id: 'W6', name: 'Reactivation',          what: 'Cold → breakup + monthly nurture',                       trigger: 'inactive_30d',      map: 'Lead Reactivation — Breakup + Monthly Nurture' },
    { id: 'W7', name: 'Stop-on-Reply',         what: 'Any reply → exit drip, route to human',                  trigger: 'lead_imported',     map: 'Engine-wide: inbound webhook pauses enrollment' },
    { id: 'W8', name: 'Warm-Lead Alert',       what: 'Interested lead → instant ping to agent',               trigger: 'tag_added',         map: 'Phase 2 (not yet wired)' },
    { id: 'W9', name: 'Disposition → Stage',   what: 'Call outcome auto-moves the contact',                   trigger: null,                map: 'Phase 2 (not yet wired)' },
  ];

  const detect = (sw) => {
    if (workflows == null) return { status: 'loading' };
    if (!sw.trigger) return { status: 'pending', note: sw.map };
    const matches = workflows.filter(w => w.trigger === sw.trigger);
    if (matches.length === 0) return { status: 'missing', note: 'No workflow with this trigger in your account' };
    const active = matches.filter(w => w.active).length;
    return active > 0
      ? { status: 'active',  note: `${active} of ${matches.length} active · ${sw.map}` }
      : { status: 'inactive', note: `${matches.length} seeded but inactive · ${sw.map}` };
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="font-semibold text-slate-900">Automation Map</h3>
        <p className="text-xs text-slate-500 mt-1">
          Nine workflows do the busywork so humans only do their station. Status shown against your actual
          Workflows table — flip any inactive workflow to active in the Workflows tab when you're ready.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {SPEC.map(sw => {
          const d = detect(sw);
          const tone =
            d.status === 'active'   ? 'emerald' :
            d.status === 'inactive' ? 'amber'   :
            d.status === 'pending'  ? 'slate'   :
            d.status === 'missing'  ? 'red'     : 'slate';
          const badge =
            d.status === 'active'   ? '● Active'    :
            d.status === 'inactive' ? '○ Inactive'  :
            d.status === 'pending'  ? '… Phase 2'   :
            d.status === 'missing'  ? '✕ Missing'   : 'Loading…';
          return (
            <div key={sw.id} className={`bg-white border-2 border-${tone}-200 rounded-xl p-4`}>
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <div className="flex items-baseline gap-2">
                  <span className={`text-xs font-bold uppercase tracking-wider text-${tone}-700`}>{sw.id}</span>
                  <span className="font-semibold text-slate-900">{sw.name}</span>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider text-${tone}-700`}>{badge}</span>
              </div>
              <div className="text-sm text-slate-600 mb-2">{sw.what}</div>
              <div className="text-xs text-slate-500">{d.note}</div>
            </div>
          );
        })}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
        <strong>The disposition loop is the heartbeat:</strong> every dialer outcome auto-moves the contact to the right
        stage, which fires the right workflow. The VA just dials and dispositions — the pipeline and reporting update
        themselves. <em>W9 is Phase 2 — wire your dialer's webhook to /api/twilio/inbound for full automation.</em>
      </div>
    </div>
  );
}

// ─── QC — live dial-pace + troubleshooting matrix ──────────────────
function FexQc({ calls = [], leads = [], transactions = [], goalConfig = {} }) {
  const cfg = { ...DEFAULT_GOAL_CONFIG, ...(goalConfig || {}) };

  const live = React.useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const monthStart = new Date(); monthStart.setDate(1);
    const monthStartStr = monthStart.toISOString().slice(0, 10);

    const todayOutbound = (calls || []).filter(c => c.direction === 'outbound' && c.date === today);
    const dialsToday    = todayOutbound.length;
    const apptsToday    = todayOutbound.filter(c => c.outcome === 'appt_set').length;

    const monthOutbound = (calls || []).filter(c => c.direction === 'outbound' && (c.date || '') >= monthStartStr);
    const dialsMtd      = monthOutbound.length;
    const apptsMtd      = monthOutbound.filter(c => c.outcome === 'appt_set').length;

    // Show rate — proxy: appointments booked this month that
    // subsequently moved to status='showed' OR 'closed'. Looking at
    // lead.status across the leads array.
    const apptLeadIds = new Set(monthOutbound.filter(c => c.outcome === 'appt_set').map(c => c.leadId).filter(Boolean));
    const apptLeads = (leads || []).filter(l => apptLeadIds.has(l.id));
    const showed = apptLeads.filter(l => l.status === 'showed' || l.status === 'closed').length;
    const showRate = apptLeadIds.size > 0 ? (showed / apptLeadIds.size) * 100 : null;

    // Sales MTD = closed leads with status='closed' this month.
    const closedThisMonth = (leads || []).filter(l => {
      if (l.status !== 'closed') return false;
      const stamp = l.closedAt || l.updatedAt || l.appointmentDate || l.createdAt || '';
      return stamp && stamp >= monthStartStr;
    });

    // Lapse % from transactions: chargebacks / gross commission.
    const totalGross = (transactions || []).reduce((s, tx) => s + Math.max(0, tx.netToAccount || 0), 0);
    const totalCb    = Math.abs((transactions || []).reduce((s, tx) => s + Math.min(0, tx.netToAccount || 0), 0));
    const lapsePct   = totalGross > 0 ? (totalCb / totalGross) * 100 : null;

    // Realized commission MTD using cfg.commissionRate × (1 − lapse)
    const commMtd = closedThisMonth.reduce((s, l) => s + (Number(l.premium) || 0) * 12 * cfg.commissionRate * (1 - cfg.lapseRate), 0);

    // Pace label: vs working days elapsed this month
    const wkdaysInMonth = cfg.workingDays;
    let elapsed = 0;
    const now = new Date();
    for (let d = new Date(monthStart); d <= now && elapsed < wkdaysInMonth; d.setDate(d.getDate() + 1)) {
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6) elapsed++;
    }
    const expected = (cfg.monthlyTarget * elapsed) / wkdaysInMonth;
    const pace = commMtd >= expected ? 'ON TRACK' : commMtd >= expected * 0.75 ? 'WATCH' : 'BEHIND';

    // Daily dial target derived from goalConfig
    const realizedCommPerDeal = cfg.avgAnnualPremium * cfg.commissionRate * (1 - cfg.lapseRate);
    const dealsNeeded     = realizedCommPerDeal > 0 ? Math.ceil(cfg.monthlyTarget / realizedCommPerDeal) : 0;
    const showsNeeded     = cfg.showToSale > 0    ? Math.ceil(dealsNeeded / cfg.showToSale)    : 0;
    const apptsNeeded     = cfg.apptToShow > 0    ? Math.ceil(showsNeeded / cfg.apptToShow)    : 0;
    const contactsNeeded  = cfg.contactToAppt > 0 ? Math.ceil(apptsNeeded / cfg.contactToAppt) : 0;
    const dialsNeeded     = cfg.dialToContact > 0 ? Math.ceil(contactsNeeded / cfg.dialToContact) : 0;
    const dailyDialTarget = cfg.workingDays > 0 ? Math.round(dialsNeeded / cfg.workingDays) : 0;
    const dailyApptTarget = cfg.workingDays > 0 ? (apptsNeeded / cfg.workingDays).toFixed(1) : 0;

    return {
      dialsToday, apptsToday, dialsMtd, apptsMtd,
      showRate, sales: closedThisMonth.length, dealsNeeded,
      lapsePct, pace, dailyDialTarget, dailyApptTarget,
    };
  }, [calls, leads, transactions, cfg]);

  const paceTone = live.pace === 'ON TRACK' ? 'emerald' : live.pace === 'WATCH' ? 'amber' : 'red';

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="font-semibold text-slate-900">Quality Control · The Dashboard</h3>
        <p className="text-xs text-slate-500 mt-1">
          One glance tells you if the store is on pace. Reverse-engineered to today's dial number from your Goal Engine config.
        </p>
      </div>

      {/* Live tiles */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <QcTile label="Dials today"   value={`${live.dialsToday} / ${live.dailyDialTarget}`}
          tone={live.dialsToday >= live.dailyDialTarget ? 'emerald' : live.dialsToday >= live.dailyDialTarget * 0.75 ? 'amber' : 'red'}/>
        <QcTile label="Appts today"   value={`${live.apptsToday} / ${live.dailyApptTarget}`} tone={live.apptsToday >= Number(live.dailyApptTarget) ? 'emerald' : 'amber'}/>
        <QcTile label="Show rate"     value={live.showRate == null ? '—' : `${live.showRate.toFixed(0)}%`} tone={live.showRate == null ? 'slate' : live.showRate >= 65 ? 'emerald' : live.showRate >= 50 ? 'amber' : 'red'}/>
        <QcTile label="Deals MTD"     value={`${live.sales} / ${live.dealsNeeded}`} tone={live.sales >= live.dealsNeeded ? 'emerald' : 'slate'}/>
        <QcTile label="Lapse %"       value={live.lapsePct == null ? '—' : `${live.lapsePct.toFixed(0)}%`} tone={live.lapsePct == null ? 'slate' : live.lapsePct <= 15 ? 'emerald' : live.lapsePct <= 25 ? 'amber' : 'red'}/>
        <QcTile label="Pace"          value={live.pace} tone={paceTone}/>
      </div>

      {/* Reverse-engineered card */}
      <div className="bg-slate-900 text-white rounded-xl p-5 text-center">
        <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Reverse-engineered from your target</div>
        <div className="text-2xl font-bold">
          {fmt(cfg.monthlyTarget)} → {live.dealsNeeded} deals → {live.dailyApptTarget} appts/day → ~{live.dailyDialTarget.toLocaleString()} dials/day
        </div>
      </div>

      {/* If a number's off — fix this */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="bg-slate-50 px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-600">If a number's off — the fix</div>
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 uppercase tracking-wide">
            <tr className="border-b border-slate-100">
              <th className="px-4 py-2 text-left w-1/3">If this is off…</th>
              <th className="px-4 py-2 text-left">Fix this</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {[
              ['Contact rate low',  'Number health / rotation; add local presence; verify 10DLC; dial more'],
              ['Set rate low',      'VA script & tonality; test a different lead source or vintage'],
              ['Show rate low',     'Confirmation + reminder workflow (W3); confirm same-day'],
              ['Close rate low',    'Agent skill & appointment quality; product fit for their health'],
              ['Lapse high',        'Retention workflow (W5); better qualification; draft-date timing'],
              ['CPA high',          'Cut low-ROI lead sources; double the winners (Source ROI tab)'],
            ].map(([sym, fix], i) => (
              <tr key={i}>
                <td className="px-4 py-2 font-semibold text-slate-800">{sym}</td>
                <td className="px-4 py-2 text-slate-600">{fix}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-900">
        <strong>The one question that runs the whole business:</strong> Are we making ~{live.dailyDialTarget} dials and booking
        {' '}{live.dailyApptTarget} appointments a day? If yes, your target follows.
      </div>
    </div>
  );
}

function QcTile({ label, value, tone = 'slate' }) {
  return (
    <div className={`bg-${tone}-50 border border-${tone}-200 rounded-xl p-3 text-center`}>
      <div className={`text-2xl md:text-3xl font-bold text-${tone}-700 tabular-nums`}>{value}</div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600 mt-1">{label}</div>
    </div>
  );
}

// ─── FEX Carrier Placement — point-of-sale carrier picker ──────────
// Mirrors the Eagle Premier / Accendo / New Vista / Senior Choice /
// Plan Right / Living Promise underwriting grid. The agent taps the
// client's conditions; the tool ranks the 6 carriers by their worst
// verdict across the selected conditions and surfaces the BEST FIT.
// Every detail row is preserved so the agent can read the carrier's
// exact timeframe rule. The carrier's e-application is always the
// final authority.

// ════════════════════════════════════════════════════════════════════
// TERM CARRIER PLACEMENT — same UX as FEX, term carrier set.
//
// Five term carriers (Americo HMS, Ethos TruStage, Prosperity Family
// Freedom, MOO TLE/IULE, AmAm Term Made Simple) × ~75 conditions.
// Shares the phrase-alias resolver + medication lookup + AI parser.
// ════════════════════════════════════════════════════════════════════

function TermCarrierPlacement() {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [aiInput, setAiInput] = React.useState('');
  const [aiBusy, setAiBusy] = React.useState(false);
  const [aiNote, setAiNote] = React.useState('');
  // Last parse session — used by the training feedback button to
  // POST { raw_text, ai_predicted, operator_corrected } once the
  // operator has accepted / corrected the chips.
  const [lastParse, setLastParse] = React.useState({ rawText: null, aiPredictedNames: [], source: 'agent_text' });
  // Doc upload state
  const [docBusy, setDocBusy] = React.useState(false);
  const fileInputRef = React.useRef(null);

  // Voice dictation state. Uses browser Web Speech API (Chrome/Edge/Safari
  // on modern iOS). We stream interim transcripts into the textarea so the
  // agent can dictate the client's story while looking at the client, then
  // hit PARSE WITH AI without touching the keyboard.
  const recognitionRef = React.useRef(null);
  const voiceBaseRef   = React.useRef('');
  const [voiceOn, setVoiceOn] = React.useState(false);
  const voiceSupported = typeof window !== 'undefined' &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);

  const stopVoice = React.useCallback(() => {
    try { recognitionRef.current?.stop(); } catch {}
    setVoiceOn(false);
  }, []);

  const startVoice = React.useCallback(() => {
    if (!voiceSupported) {
      setAiNote('Voice input needs Chrome, Edge, or Safari with mic permission.');
      return;
    }
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new Rec();
    rec.lang = 'en-US';
    rec.continuous = true;
    rec.interimResults = true;
    voiceBaseRef.current = aiInput ? (aiInput.trimEnd() + ' ') : '';
    rec.onresult = (evt) => {
      let final = '';
      let interim = '';
      for (let i = evt.resultIndex; i < evt.results.length; i++) {
        const t = evt.results[i][0].transcript;
        if (evt.results[i].isFinal) final += t;
        else interim += t;
      }
      if (final) voiceBaseRef.current += final + ' ';
      setAiInput(voiceBaseRef.current + interim);
    };
    rec.onerror = (e) => {
      setAiNote(e.error === 'not-allowed'
        ? 'Mic blocked. Allow microphone access in your browser, then try again.'
        : `Voice error: ${e.error || 'unknown'}. Type instead.`);
      setVoiceOn(false);
    };
    rec.onend = () => setVoiceOn(false);
    recognitionRef.current = rec;
    try {
      rec.start();
      setVoiceOn(true);
      setAiNote('Listening… speak the client\'s story, then tap the mic to stop.');
    } catch (err) {
      setAiNote(`Couldn't start mic: ${err.message || 'unknown'}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiInput, voiceSupported]);

  React.useEffect(() => () => {
    try { recognitionRef.current?.stop(); } catch {}
  }, []);

  const toggle = (idx) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };
  const clearAll = () => setSelected(new Set());

  const visibleChips = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return TERM_CONDITIONS.map((c, i) => ({ name: c.name, idx: i }));
    const medHits = medicationMatches(q);
    if (medHits && medHits.length > 0) {
      const seen = new Set();
      const out = [];
      for (const frag of medHits) {
        const f = frag.toLowerCase();
        TERM_CONDITIONS.forEach((c, i) => {
          if (c.name.toLowerCase().includes(f) && !seen.has(i)) {
            seen.add(i);
            out.push({ name: c.name, idx: i, viaMed: q });
          }
        });
      }
      if (out.length > 0) return out;
    }
    return TERM_CONDITIONS
      .map((c, i) => ({ name: c.name, idx: i }))
      .filter(c => c.name.toLowerCase().includes(q));
  }, [query]);

  const aliasHits = React.useMemo(() => {
    const matches = resolvePhraseAliases(aiInput);
    const names = TERM_CONDITIONS.map(c => c.name);
    return matches
      .map(m => ({ matched: m.matched, indices: indicesForFragments(m.conditionFragments, names) }))
      .filter(m => m.indices.length > 0);
  }, [aiInput]);

  async function parseFromText() {
    const text = aiInput.trim();
    if (!text) return;
    setAiBusy(true);
    setAiNote('');
    try {
      const fromAliases = new Set();
      for (const hit of aliasHits) hit.indices.forEach(i => fromAliases.add(i));
      if (fromAliases.size > 0) {
        setSelected(prev => {
          const next = new Set(prev);
          fromAliases.forEach(i => next.add(i));
          return next;
        });
      }
      // Phase B: route through /api/underwriting/parse-text which
      // prepends this operator's prior corrections as few-shot
      // examples in the system prompt — making the parser learn
      // their phrasing over time without fine-tuning. The endpoint
      // owns the prompt + model choice + JSON validation now, so
      // the client only has to hand it text + the condition list.
      const conditionNames = TERM_CONDITIONS.map(c => c.name);
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/underwriting/parse-text', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', authorization: `Bearer ${session?.access_token || ''}` },
        body:    JSON.stringify({ text, conditions: conditionNames }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const matched = Array.isArray(data?.matched) ? data.matched : [];
      const examplesUsed = Number(data?.examples_used) || 0;
      const newIdxs = [];
      matched.forEach(name => {
        const i = TERM_CONDITIONS.findIndex(c => c.name === name);
        if (i >= 0) newIdxs.push(i);
      });
      const totalAdded = newIdxs.length + fromAliases.size;
      // Remember this parse session so the operator can train on it
      // after correcting the chip selections. The aiPredictedNames
      // list is the UNION of the AI's matches + the deterministic
      // alias hits — anything the system ADDED to the selection.
      const aliasNames = Array.from(fromAliases).map(i => TERM_CONDITIONS[i]?.name).filter(Boolean);
      const aiNames    = newIdxs.map(i => TERM_CONDITIONS[i]?.name).filter(Boolean);
      const allPredicted = Array.from(new Set([...aliasNames, ...aiNames]));
      setLastParse({ rawText: text, aiPredictedNames: allPredicted, source: 'agent_text' });
      if (totalAdded === 0) {
        setAiNote('Nothing on the grid matched. Rephrase or pick chips manually.');
      } else {
        setSelected(prev => {
          const next = new Set(prev);
          newIdxs.forEach(i => next.add(i));
          return next;
        });
        const exampleSuffix = examplesUsed > 0 ? ` · using ${examplesUsed} of your prior corrections` : '';
        setAiNote(`Added ${totalAdded} condition${totalAdded === 1 ? '' : 's'} (${fromAliases.size} from phrasing, ${newIdxs.length} from AI${exampleSuffix}). Correct the chips and tap "Save training" so the AI learns your phrasing.`);
      }
    } catch (err) {
      setAiNote(`Parse failed: ${err.message || 'unknown'}. Pick chips manually below.`);
    } finally {
      setAiBusy(false);
    }
  }

  // Upload a client document (image of a meds list / prior carrier
  // underwriting summary / discharge sheet / etc.) and let Claude
  // vision extract the medical history. The returned text gets
  // dropped into the AI parse textarea so the operator can review
  // and run the standard parse pipeline.
  async function uploadDoc(file) {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setAiNote('File too big — keep it under 8MB.');
      return;
    }
    setDocBusy(true);
    setAiNote('Reading the document…');
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload  = () => resolve(String(r.result || '').replace(/^data:[^,]+,/, ''));
        r.onerror = () => reject(new Error('file_read_failed'));
        r.readAsDataURL(file);
      });
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/underwriting/upload-doc', {
        method:  'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${session?.access_token || ''}` },
        body:    JSON.stringify({ imageBase64: b64, mimeType: file.type || 'image/png' }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.detail || j?.error || `HTTP ${res.status}`);
      const text = String(j.text || '').trim();
      if (!text) {
        setAiNote('Document read but no medical history extracted. Try a clearer image.');
        return;
      }
      setAiInput(prev => prev ? `${prev}\n\n${text}` : text);
      setLastParse({ rawText: text, aiPredictedNames: [], source: 'doc_upload' });
      setAiNote('Extracted from document. Click PARSE WITH AI to add chips.');
    } catch (err) {
      setAiNote(`Doc upload failed: ${err.message || 'unknown'}`);
    } finally {
      setDocBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  // After the operator has reviewed + corrected the chip selection,
  // POST the training example so the parser can use it as a few-shot
  // on subsequent calls (Phase B).
  async function saveTraining() {
    if (!lastParse.rawText) {
      setAiNote('No parse session to save yet — parse a description first.');
      return;
    }
    try {
      const correctedNames = Array.from(selected).map(i => TERM_CONDITIONS[i]?.name).filter(Boolean);
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/underwriting/train', {
        method:  'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${session?.access_token || ''}` },
        body:    JSON.stringify({
          raw_text:           lastParse.rawText,
          ai_predicted:       lastParse.aiPredictedNames,
          operator_corrected: correctedNames,
          source:             lastParse.source,
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.detail || j?.error || `HTTP ${res.status}`);
      setAiNote(`✓ Training saved (${correctedNames.length} conditions). The AI will use this next time.`);
    } catch (err) {
      setAiNote(`Couldn't save training: ${err.message || 'unknown'}`);
    }
  }

  const indices = React.useMemo(() => Array.from(selected).sort((a, b) => a - b), [selected]);
  const ranked = React.useMemo(() => rankTermCarriers(indices), [indices]);

  const headline = React.useMemo(() => {
    if (!ranked) return null;
    const bestWorst = ranked[0].worstR;
    if (bestWorst >= 5)   return { tone: 'red',     text: 'NO CLEAN FIT — every term carrier declines on at least one condition. Look at FEX (whole life) or guaranteed-issue alternatives.' };
    if (bestWorst >= 2.5) return { tone: 'slate',   text: 'CHECK / ROUTE — the best fit requires reading the detail row. Likely route to Ethos Select or check med list.' };
    if (bestWorst >= 2)   return { tone: 'amber',   text: 'CONDITIONAL — best fit accepts but with timeframe rules. Read the detail before quoting.' };
    return { tone: 'emerald', text: 'CLEAN ACCEPT — top carrier writes day-one with no conditional flags. Quote it.' };
  }, [ranked]);

  return (
    <div className="space-y-4">
      <div className="bg-black border-l-4 border-blue-500 px-6 py-5 shadow-2xl">
        <div className="text-blue-400 text-[0.6rem] font-black uppercase tracking-[0.3em]">Life Underwriting</div>
        <div className="text-white text-xl md:text-2xl font-black mt-1.5 uppercase tracking-tight">Find the Right Life Carrier</div>
        <div className="text-slate-400 text-xs mt-1.5">
          Five carriers · Americo HMS · Ethos TruStage · Prosperity Family Freedom · MOO TLE/IULE · AmAm Term Made Simple. Toggle conditions, see best fit. The AI learns your phrasing every time you save training.
        </div>
      </div>

      {/* AI Parse */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 border-l-4 border-blue-500 p-4 shadow-lg">
        <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
          <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-blue-400">AI Parse Medical History</div>
          <div className="text-[0.55rem] uppercase tracking-wider text-slate-400">Natural language · meds · uploaded docs → grid chips</div>
        </div>
        <div className="text-xs text-slate-300 mb-2">
          Type the client's story in plain English, or upload a doc (image / screenshot of meds list / prior carrier summary). The tool resolves common phrasings ("lost a leg", "on oxygen", "memory loss") AND parses medications via AI. Correct the chips after a parse and tap "Save training" so the AI learns YOUR phrasing.
        </div>
        <textarea
          value={aiInput}
          onChange={e => setAiInput(e.target.value)}
          placeholder={'e.g. "55, has memory loss, on metformin, mini stroke last year"'}
          rows={3}
          className="w-full bg-slate-950 border border-slate-700 text-white text-sm px-3 py-2 focus:border-blue-500 focus:outline-none placeholder-slate-500"
        />
        <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={parseFromText}
              disabled={aiBusy || !aiInput.trim()}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-black uppercase tracking-tight px-4 py-2 shadow"
            >
              {aiBusy ? 'Parsing…' : 'PARSE WITH AI'}
            </button>
            {voiceSupported && (
              <button
                onClick={voiceOn ? stopVoice : startVoice}
                title={voiceOn
                  ? 'Tap to stop dictating'
                  : 'Tap and speak — dictate the client\'s medical history hands-free'}
                aria-label={voiceOn ? 'Stop voice input' : 'Start voice input'}
                className={`text-xs font-bold uppercase tracking-tight px-3 py-2 shadow border inline-flex items-center gap-1.5 ${
                  voiceOn
                    ? 'bg-rose-600 border-rose-700 text-white animate-pulse'
                    : 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600'
                }`}
              >
                <span aria-hidden="true">{voiceOn ? '⏹' : '🎙'}</span>
                {voiceOn ? 'Stop' : 'Speak'}
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={e => uploadDoc(e.target.files?.[0])}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={docBusy}
              title="Upload a photo / screenshot of a meds list, prior carrier summary, etc. AI vision extracts the history."
              className="bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white text-xs font-bold uppercase tracking-tight px-3 py-2 shadow border border-slate-600"
            >
              {docBusy ? 'Reading…' : '📎 Upload doc'}
            </button>
            {lastParse.rawText && (
              <button
                onClick={saveTraining}
                title="Save the current chip selection as training data. The AI uses this as a few-shot example next time."
                className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold uppercase tracking-tight px-3 py-2 shadow border border-amber-700"
              >
                📝 Save training
              </button>
            )}
          </div>
          {aiNote && <div className="text-xs text-blue-300 flex-1 min-w-0">{aiNote}</div>}
        </div>
        {aliasHits.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-700 space-y-1">
            <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-blue-400 mb-1">Interpreted phrases</div>
            {aliasHits.map((h, i) => (
              <div key={i} className="text-xs text-slate-200">
                <span className="text-amber-300">Reading "{h.matched}"</span> as:{' '}
                <span className="text-blue-300 font-bold">{h.indices.map(idx => TERM_CONDITIONS[idx].name).join(' · ')}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Search + chips */}
      <div className="bg-white border border-slate-200 p-4">
        <div className="text-xs font-bold uppercase tracking-wider text-blue-700 mb-2">1 · Find &amp; add conditions</div>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search a condition OR medication (eliquis, metformin, keppra, plavix…)"
          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:border-blue-400 focus:outline-none"
          autoComplete="off"
        />
        <div className="flex flex-wrap gap-2 mt-3 max-h-72 overflow-auto">
          {visibleChips.length === 0 && (
            <div className="text-sm text-slate-400 italic py-2">No condition matches "{query}".</div>
          )}
          {visibleChips.map(c => {
            const sel = selected.has(c.idx);
            return (
              <button
                key={c.idx}
                onClick={() => toggle(c.idx)}
                className={`text-sm rounded-full px-3 py-1.5 border transition-colors ${
                  sel
                    ? 'bg-blue-600 text-white border-blue-600'
                    : c.viaMed
                      ? 'bg-violet-50 text-violet-800 border-violet-300 hover:border-violet-500'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-blue-400'
                }`}
                title={c.viaMed ? `Matched from medication "${c.viaMed}"` : undefined}
              >
                {c.viaMed && ''}{c.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected */}
      <div className="bg-white border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-bold uppercase tracking-wider text-blue-700">2 · This client's profile</div>
          <button onClick={clearAll} className="text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded px-2 py-1">Clear</button>
        </div>
        {selected.size === 0 ? (
          <div className="text-sm text-slate-400 italic py-2">No conditions added yet. Tap conditions above. A clean-health client → write the cheapest day-one carrier.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {indices.map(i => (
              <span key={i} className="bg-slate-900 text-white rounded-full pl-3 pr-2 py-1 text-xs font-semibold inline-flex items-center gap-2">
                {TERM_CONDITIONS[i].name}
                <button onClick={() => toggle(i)} className="text-white/80 hover:text-white text-base leading-none">×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Results */}
      {ranked && (
        <>
          <div className="bg-white border border-slate-200 p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-blue-700 mb-2">3 · Best carrier fit</div>
            {headline && (
              <div className={`text-sm bg-${headline.tone}-50 border border-${headline.tone}-200 text-${headline.tone}-900 px-3 py-2 mb-3`}>
                {headline.text}
              </div>
            )}
            <div className="space-y-2">
              {ranked.map((r, k) => {
                const tier = Object.values(TERM_TIERS).find(t => t.r === r.worstR) || TERM_TIERS.check;
                const bestWorst = ranked[0].worstR;
                const isBest = r.worstR === bestWorst && r.worstR < 5;
                return (
                  <div key={r.ci} className={`flex items-center gap-3 border px-3 py-3 ${isBest ? `border-amber-400 bg-amber-50/40 ring-2 ring-amber-100` : 'border-slate-200 bg-white'}`}>
                    <div className={`text-[10px] font-bold uppercase tracking-wider text-white px-2 py-1.5 min-w-[78px] text-center bg-${tier.tone}-600`}>
                      {tier.label}
                    </div>
                    <div className="flex-1 min-w-0">
                      {isBest && <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Best fit</div>}
                      <div className="font-semibold text-slate-900 text-sm">{r.name}</div>
                      <div className="text-xs text-slate-500">{r.product}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        Limited by: <span className="text-slate-700">{r.limitingCondition || '—'}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Carrier-by-carrier detail */}
          <div className="bg-white border border-slate-200 p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-blue-700 mb-2">Carrier-by-carrier detail</div>
            <div className="space-y-3">
              {indices.map(i => (
                <div key={i} className="border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-3 py-2 font-semibold text-slate-800 text-sm">{TERM_CONDITIONS[i].name}</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-slate-200">
                    {TERM_CONDITIONS[i].verdicts.map((cell, ci) => {
                      const tier = TERM_TIERS[cell.tier] || TERM_TIERS.check;
                      return (
                        <div key={ci} className="bg-white px-3 py-2">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{TERM_CARRIERS[ci].name}</div>
                          <span className={`inline-block text-[10px] font-bold uppercase tracking-wider text-white px-1.5 py-0.5 my-1 bg-${tier.tone}-600`}>
                            {tier.label}
                          </span>
                          <div className="text-xs text-slate-700 leading-snug">{cell.detail}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function FexCarrierPlacement() {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  // AI parser state — operator types client's medical history in
  // plain English ("62, wheelchair, type 2 diabetes 10yrs, eliquis,
  // cancer 2 yrs ago"), Claude extracts the matching FEX conditions
  // and auto-toggles the chips.
  const [aiInput, setAiInput] = React.useState('');
  const [aiBusy, setAiBusy] = React.useState(false);
  const [aiNote, setAiNote] = React.useState('');

  const toggle = (idx) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };
  const clearAll = () => setSelected(new Set());

  // Filter the chip pool against the search query. If the query
  // matches a medication (e.g. "eliquis", "metformin", "keppra"),
  // surface the conditions that medication points to instead of
  // doing a literal name match. Direct condition-name match still
  // works as a fallback.
  const visibleChips = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FEX_CONDITIONS.map((c, i) => ({ name: c.name, idx: i }));
    const medHits = medicationMatches(q);
    if (medHits && medHits.length > 0) {
      // Match each fragment against condition name (case-insensitive
      // substring). Dedupe across fragments.
      const seen = new Set();
      const out = [];
      for (const frag of medHits) {
        const f = frag.toLowerCase();
        FEX_CONDITIONS.forEach((c, i) => {
          if (c.name.toLowerCase().includes(f) && !seen.has(i)) {
            seen.add(i);
            out.push({ name: c.name, idx: i, viaMed: q });
          }
        });
      }
      if (out.length > 0) return out;
    }
    return FEX_CONDITIONS
      .map((c, i) => ({ name: c.name, idx: i }))
      .filter(c => c.name.toLowerCase().includes(q));
  }, [query]);

  // Phrase-alias interpretation hints. Recomputed live as the agent
  // types so they SEE how their words get read.
  const aliasHits = React.useMemo(() => {
    const matches = resolvePhraseAliases(aiInput);
    const conditionNames = FEX_CONDITIONS.map(c => c.name);
    return matches
      .map(m => ({ matched: m.matched, indices: indicesForFragments(m.conditionFragments, conditionNames) }))
      .filter(m => m.indices.length > 0);
  }, [aiInput]);

  // Ask the AI to extract conditions from the agent's free-form
  // description. Reuses the /api/ai proxy that the rest of
  // the app uses; runs server-side so the API key never leaks.
  async function parseFromText() {
    const text = aiInput.trim();
    if (!text) return;
    setAiBusy(true);
    setAiNote('');
    try {
      // First: deterministic phrase aliases. Add every matched
      // condition immediately so the agent sees instant progress even
      // before Claude responds.
      const fromAliases = new Set();
      for (const hit of aliasHits) hit.indices.forEach(i => fromAliases.add(i));
      if (fromAliases.size > 0) {
        setSelected(prev => {
          const next = new Set(prev);
          fromAliases.forEach(i => next.add(i));
          return next;
        });
      }
      const conditionList = FEX_CONDITIONS.map(c => c.name).join('\n');
      const system = `You are an FEX underwriting assistant. Given a free-form description of a client's medical history, identify which of these underwriting conditions apply:

${conditionList}

RULES:
- Match condition names EXACTLY as written above (preserve capitalization, punctuation, apostrophes).
- If a MEDICATION is mentioned (eliquis, metformin, keppra, lisinopril, plavix, prednisone, etc.) infer the underlying condition AND include it.
- A "wheelchair", "cane", "walker" mention → match the corresponding condition.
- Cancer history matters: include it even if the agent says "X years ago" — the carriers ask about it.
- If the description doesn't clearly imply a condition on the list, omit it. Don't guess.
- Return ONLY a JSON object: { "matched": ["condition exact name 1", "condition exact name 2"] }`;
      // AUDIT prod-401 fix: attach the Supabase JWT so /api/ai's checkCap
      // gate can identify the caller; without it the endpoint 401s.
      const res = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({
          max_tokens: 800,
          system,
          messages: [{ role: 'user', content: text }],
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const reply = data?.content?.[0]?.text || '';
      const m = reply.match(/\{[\s\S]*\}/);
      if (!m) throw new Error('No JSON in reply');
      const parsed = JSON.parse(m[0]);
      const matched = Array.isArray(parsed.matched) ? parsed.matched : [];
      const newIdxs = [];
      matched.forEach(name => {
        const i = FEX_CONDITIONS.findIndex(c => c.name === name);
        if (i >= 0) newIdxs.push(i);
      });
      if (newIdxs.length === 0) {
        setAiNote('Nothing on the underwriting list matched. Rephrase or pick chips manually.');
      } else {
        setSelected(prev => {
          const next = new Set(prev);
          newIdxs.forEach(i => next.add(i));
          return next;
        });
        setAiNote(`Added ${newIdxs.length} condition${newIdxs.length === 1 ? '' : 's'}: ${newIdxs.map(i => FEX_CONDITIONS[i].name).join(' · ')}`);
      }
    } catch (err) {
      setAiNote(`Parse failed: ${err.message || 'unknown'}. You can add chips manually below.`);
    } finally {
      setAiBusy(false);
    }
  }

  // Rank carriers + compute the headline.
  const indices = React.useMemo(() => Array.from(selected).sort((a, b) => a - b), [selected]);
  const ranked = React.useMemo(() => rankFexCarriers(indices), [indices]);

  const headline = React.useMemo(() => {
    if (!ranked) return null;
    const bestWorst = ranked[0].worstR;
    if (bestWorst >= 5) return { tone: 'red',     text: 'NO CLEAN FIT — every carrier declines or needs verification on at least one condition. Americo Eagle Guaranteed is your GI safety net; otherwise look outside this grid.' };
    if (bestWorst >= 4) return { tone: 'blue',    text: 'GUARANTEED-ISSUE TERRITORY — the strongest realistic offer is GI (Americo Eagle Guaranteed). Position it as approval-first.' };
    if (bestWorst >= 3) return { tone: 'orange',  text: 'MODIFIED / ROP territory — the best fit gets the client covered, but the first year(s) carry waiting periods. Read the detail below for the exact timeframe.' };
    if (bestWorst >= 2) return { tone: 'amber',   text: 'GRADED is the best you can get — partial benefit early years, full benefit later. Set client expectations on the close.' };
    return { tone: 'emerald', text: 'DAY-ONE territory — full benefit out the gate. Quote the best-fit carrier and write it.' };
  }, [ranked]);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="font-semibold text-slate-900">FEX Carrier Placement</h3>
        <p className="text-xs text-slate-500 mt-1">
          Tap the client's conditions → see the best carrier. Ranks the 6 FEX carriers by their <strong>worst</strong> decision
          across the selected conditions (the limiting condition drives placement). The carrier's e-application is always the
          final authority — read the detail row for exact timeframes and confirm height/weight on the app.
        </p>
      </div>

      {/* AI Parse Medical History — free-form input, Claude extracts conditions */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 border-l-4 border-emerald-500 p-4 shadow-lg">
        <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
          <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-emerald-400">AI Parse Medical History</div>
          <div className="text-[0.55rem] uppercase tracking-wider text-slate-400">Plain English → underwriting chips</div>
        </div>
        <div className="text-xs text-slate-300 mb-2">
          Type or paste the client's history — natural language is fine. The AI extracts every condition + medication that matches the underwriting grid and adds them as chips below.
        </div>
        <textarea
          value={aiInput}
          onChange={e => setAiInput(e.target.value)}
          placeholder={'e.g. "62, in a wheelchair, type 2 diabetes 10 years, eliquis, cancer 2 yrs ago" or "32, COPD, on prednisone and spiriva, smoker"'}
          rows={3}
          className="w-full bg-slate-950 border border-slate-700 text-white text-sm px-3 py-2 focus:border-emerald-500 focus:outline-none placeholder-slate-500"
        />
        <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
          <button
            onClick={parseFromText}
            disabled={aiBusy || !aiInput.trim()}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-black uppercase tracking-tight px-4 py-2 shadow"
          >
            {aiBusy ? 'Parsing…' : 'PARSE WITH AI'}
          </button>
          {aiNote && <div className="text-xs text-emerald-300 flex-1 min-w-0">{aiNote}</div>}
        </div>
        {/* Phrase interpretation hints — show as the agent types so they
            see how natural language gets resolved into grid conditions. */}
        {aliasHits.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-700 space-y-1">
            <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-emerald-400 mb-1">Interpreted phrases</div>
            {aliasHits.map((h, i) => (
              <div key={i} className="text-xs text-slate-200">
                <span className="text-amber-300">Reading "{h.matched}"</span> as:{' '}
                <span className="text-emerald-300 font-bold">{h.indices.map(idx => FEX_CONDITIONS[idx].name).join(' · ')}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 1. Search + chip grid */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">1 · Find &amp; add conditions</div>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search a condition OR medication (eliquis, metformin, keppra, plavix…)"
          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:border-emerald-400 focus:outline-none"
          autoComplete="off"
        />
        <div className="flex flex-wrap gap-2 mt-3 max-h-72 overflow-auto">
          {visibleChips.length === 0 && (
            <div className="text-sm text-slate-400 italic py-2">No condition matches "{query}".</div>
          )}
          {visibleChips.map(c => {
            const sel = selected.has(c.idx);
            return (
              <button
                key={c.idx}
                onClick={() => toggle(c.idx)}
                className={`text-sm rounded-full px-3 py-1.5 border transition-colors ${
                  sel
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : c.viaMed
                      ? 'bg-violet-50 text-violet-800 border-violet-300 hover:border-violet-500'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-400'
                }`}
                title={c.viaMed ? `Matched from medication "${c.viaMed}"` : undefined}
              >
                {c.viaMed && ''}{c.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. Selected bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">2 · This client's profile</div>
          <button onClick={clearAll} className="text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg px-2 py-1">
            Clear
          </button>
        </div>
        {selected.size === 0 ? (
          <div className="text-sm text-slate-400 italic py-2">
            No conditions added yet. Tap conditions above. A clean-health client → chase the cheapest Day-One carrier.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {indices.map(i => (
              <span key={i} className="bg-slate-900 text-white rounded-full pl-3 pr-2 py-1 text-xs font-semibold inline-flex items-center gap-2">
                {FEX_CONDITIONS[i].name}
                <button onClick={() => toggle(i)} className="text-white/80 hover:text-white text-base leading-none">×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* 3. Results */}
      {ranked && (
        <>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">3 · Best carrier fit</div>
            {headline && (
              <div className={`text-sm bg-${headline.tone}-50 border border-${headline.tone}-200 text-${headline.tone}-900 rounded-lg px-3 py-2 mb-3`}>
                {headline.text}
              </div>
            )}
            <div className="space-y-2">
              {ranked.map((r, k) => {
                const tier = Object.values(FEX_TIERS).find(t => t.r === r.worstR) || FEX_TIERS.check;
                const bestWorst = ranked[0].worstR;
                const isBest = r.worstR === bestWorst && r.worstR < 5;
                return (
                  <div key={r.ci} className={`flex items-center gap-3 border rounded-xl px-3 py-3 ${isBest ? `border-amber-400 bg-amber-50/40 ring-2 ring-amber-100` : 'border-slate-200 bg-white'}`}>
                    <div className={`text-[10px] font-bold uppercase tracking-wider text-white rounded-md px-2 py-1.5 min-w-[78px] text-center bg-${tier.tone}-600`}>
                      {tier.label}
                    </div>
                    <div className="flex-1 min-w-0">
                      {isBest && <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Best fit</div>}
                      <div className="font-semibold text-slate-900 text-sm">{r.name}</div>
                      <div className="text-xs text-slate-500">{r.product}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        Limited by: <span className="text-slate-700">{r.limitingCondition || '—'}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Carrier-by-carrier detail per selected condition */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">Carrier-by-carrier detail</div>
            <div className="space-y-3">
              {indices.map(i => (
                <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                  <div className="bg-slate-50 px-3 py-2 font-semibold text-slate-800 text-sm">{FEX_CONDITIONS[i].name}</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-slate-200">
                    {FEX_CONDITIONS[i].verdicts.map((cell, ci) => {
                      const tier = FEX_TIERS[cell.tier] || FEX_TIERS.check;
                      return (
                        <div key={ci} className="bg-white px-3 py-2">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{FEX_CARRIERS[ci].name}</div>
                          <span className={`inline-block text-[10px] font-bold uppercase tracking-wider text-white rounded px-1.5 py-0.5 my-1 bg-${tier.tone}-600`}>
                            {tier.label}
                          </span>
                          <div className="text-xs text-slate-700 leading-snug">{cell.detail}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Legend + caveat */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Legend</div>
        <div className="flex flex-wrap gap-3 text-xs">
          {[
            ['emerald', 'Day-One (full benefit)'],
            ['amber',   'Graded'],
            ['orange',  'Modified / ROP / Basic'],
            ['blue',    'Guaranteed Issue'],
            ['slate',   'Verify (meds / see note)'],
            ['red',     'Decline'],
          ].map(([tone, label]) => (
            <span key={tone} className="inline-flex items-center gap-1.5">
              <span className={`inline-block w-3 h-3 rounded bg-${tone}-600`}/>
              <span className="text-slate-600">{label}</span>
            </span>
          ))}
        </div>
        <div className="text-[11px] text-slate-500 italic mt-3 leading-relaxed">
          Built from the FEX underwriting grid as a fast point-of-sale reference. It ranks carriers by their <strong>worst</strong>
          decision across the client's conditions (the limiting condition drives placement). Time-based outcomes show the
          full sliding scale in the detail — read it for the client's exact timeframe.
          <strong> The carrier's actual e-application is the final authority;</strong> guidelines change and vary by state.
          Always confirm height/weight on the carrier's build chart and verify on the app.
        </div>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════
// FEX Hub — one tab for everything Final Expense
//
// Replaces the standalone Goal Engine tab. Three sub-pills:
//   Engine        — the Goal Engine (target → daily dial number → pace)
//   Daily KPI Log — printable per-VA daily log from page 2 of the
//                   FEX KPI Engine PDF
//   Source ROI    — CPA + ROI by lead source, scoped to FEX leads only
//
// Everything FEX-specific lands here so the operator (and their VA)
// have one screen to run the FEX side of the business.
// ────────────────────────────────────────────────────────────────────

function FexHubView({ goalConfig, setGoalConfig, calls = [], leads = [], settings = {}, transactions = [], producers = {}, currentUser = null, onGotoTab = () => {}, fexOnboarding = {}, setFexOnboarding = () => {}, clientProfiles = {}, conservation = [], saveTasks = {}, setSaveTasks = () => {} }) {
  const [sub, setSub] = useState('engine');

  // How much FEX activity does this operator actually have? Used to
  // surface a friendly "you don't have any FEX leads yet" hint when
  // the operator visits the tab cold.
  const fexCounts = React.useMemo(() => {
    const fexLeads = (leads || []).filter(l => (l.leadType || 'general') === 'final_expense');
    return {
      leads: fexLeads.length,
      closed: fexLeads.filter(l => l.status === 'closed').length,
    };
  }, [leads]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-br from-red-700 via-red-800 to-slate-900 text-white rounded-2xl p-6">
        <div className="text-red-200 text-xs font-semibold uppercase tracking-wider">FEX Hub</div>
        <h2 className="text-3xl font-bold mt-1">Final Expense — the $20k machine</h2>
        <p className="text-red-100 text-sm mt-2 max-w-3xl">
          Everything FEX in one place. Reverse-engineer your monthly target into a daily dial number,
          give your VA a printable log to fill in, and watch CPA by source so you can kill what doesn't
          produce and double what does.
        </p>
        <div className="mt-3 text-xs text-red-200/80 flex items-center gap-4">
          <span>{fexCounts.leads.toLocaleString()} FEX lead{fexCounts.leads === 1 ? '' : 's'}</span>
          <span className="text-red-300/50">·</span>
          <span>{fexCounts.closed.toLocaleString()} closed</span>
        </div>
      </div>

      {/* Sub-pills — flex-wrap so 9 fit gracefully */}
      <div className="bg-white border border-slate-200 rounded-xl p-1 flex flex-wrap items-center gap-1">
        {[
          ['engine',    'Engine'],
          ['qc',        'QC'],
          ['placement', 'Placement'],
          // Saves moved out — chargeback/lapse save work is book-of-
          // business wide, lives in the Retention tab now.
          ['kpi',       'KPI Log'],
          ['source',    'ROI'],
          ['onboard',   'Onboard'],
          ['stations',  'Stations'],
          ['va',        'VA SOP'],
          ['agent',     'Agent SOP'],
          ['workflows', 'Workflows'],
        ].map(([id, label]) => (
          <button key={id} onClick={() => setSub(id)} className={`px-3 py-1.5 text-sm font-medium rounded-lg whitespace-nowrap ${sub === id ? 'bg-red-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
            {label}
          </button>
        ))}
      </div>

      {sub === 'engine' && (
        <GoalEngineView
          goalConfig={goalConfig} setGoalConfig={setGoalConfig}
          calls={calls} leads={leads} settings={settings}
          transactions={transactions} producers={producers}
        />
      )}
      {sub === 'qc'        && <FexQc calls={calls} leads={leads} transactions={transactions} goalConfig={goalConfig}/>}
      {sub === 'placement' && <FexCarrierPlacement/>}
      {/* sub === 'saves' removed — saves are book-of-business, see Retention */}
      {sub === 'kpi'       && <FexKpiTracker calls={calls} producers={producers}/>}
      {sub === 'source'    && <FexSourceRoi leads={leads} goalConfig={goalConfig} onGotoLeads={() => onGotoTab('leads')}/>}
      {sub === 'onboard'   && <FexOnboarding state={fexOnboarding} setState={setFexOnboarding}/>}
      {sub === 'stations'  && <FexStations/>}
      {sub === 'va'        && <FexVaPlaybook settings={settings}/>}
      {sub === 'agent'     && <FexAgentSOP/>}
      {sub === 'workflows' && <FexAutomationsMap currentUser={currentUser}/>}
    </div>
  );
}

// ─── Daily KPI Log ─────────────────────────────────────────────────
// One row per (date, agent) for the last 14 working days. Pulls from
// the calls log so the VA doesn't have to log anything by hand. Built
// to be printed — the print button uses window.print() and the table
// styles are deliberately desaturated for ink economy.

function FexKpiTracker({ calls = [], producers = {} }) {
  const rows = React.useMemo(() => {
    // Build the (date, agentProdNum) buckets for the last 14 days.
    const buckets = {};
    const today = new Date();
    const cutoff = new Date(today); cutoff.setDate(cutoff.getDate() - 14);
    const cutoffStr = cutoff.toISOString().slice(0, 10);

    (calls || []).forEach(c => {
      if (!c.date || c.date < cutoffStr) return;
      if (c.direction && c.direction !== 'outbound') return;
      const k = `${c.date}|${c.agentProdNum || ''}`;
      if (!buckets[k]) buckets[k] = {
        date: c.date,
        agentProdNum: c.agentProdNum || '',
        dials: 0, contacts: 0, appts: 0, sales: 0,
      };
      const r = buckets[k];
      r.dials++;
      if (isContactOutcome(c.outcome)) r.contacts++;
      if (c.outcome === 'appt_set') r.appts++;
      // "Sales" inferred from a call outcome of appt_set + subsequent
      // close — we don't have that join on the call row. For now, use
      // a heuristic: app_submitted or sale outcomes would be tracked
      // in lead.status — caller logs sales in the close action. We
      // leave this column blank for the VA to fill in manually on the
      // print sheet. (Phase 2: wire close-the-loop here.)
    });

    return Object.values(buckets).map(r => ({
      ...r,
      contactPct: r.dials > 0 ? (r.contacts / r.dials) * 100 : 0,
      closePct:   r.contacts > 0 ? (r.appts / r.contacts) * 100 : 0,
      dialsPerSale: r.sales > 0 ? r.dials / r.sales : null,
    })).sort((a, b) => (b.date.localeCompare(a.date)) || (a.agentProdNum.localeCompare(b.agentProdNum)));
  }, [calls]);

  const agentLabel = (pn) => pn ? (getProducerName(pn, producers) || `#${pn}`) : '—';

  // Append empty rows for hand-fill if there's less than the typical
  // workweek. Keeps the printable log useful day-1 before any data
  // has been logged through the dialer.
  const blankRowsNeeded = Math.max(0, 10 - rows.length);

  return (
    <div className="space-y-3">
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
          <div>
            <h3 className="font-semibold text-slate-900">Daily KPI Log</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Last 14 days of outbound dial activity, one row per (date, agent). Auto-populated from
              the calls log. Print this for your VA — they fill in Apps and Sales, you compute
              Dials/Sale to feed back into the engine.
            </p>
          </div>
          <button onClick={() => window.print()} className="bg-slate-900 hover:bg-slate-700 text-white text-xs font-semibold px-3 py-2 rounded-lg">
            Print log
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
                <th className="py-2 pr-3">Date</th>
                <th className="py-2 px-2">Rep / VA</th>
                <th className="py-2 px-2 text-right">Dials</th>
                <th className="py-2 px-2 text-right">Contacts</th>
                <th className="py-2 px-2 text-right">Appts</th>
                <th className="py-2 px-2 text-right">Apps</th>
                <th className="py-2 px-2 text-right">Sales</th>
                <th className="py-2 px-2 text-right">Contact %</th>
                <th className="py-2 px-2 text-right">Close %</th>
                <th className="py-2 px-2 text-right">Dials/Sale</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && blankRowsNeeded === 10 && (
                <tr><td colSpan={10} className="py-6 text-center text-slate-400 text-xs">No outbound calls logged in the last 14 days.</td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-2 pr-3 tabular-nums">{fmtDateUS(r.date)}</td>
                  <td className="py-2 px-2 text-slate-700">{agentLabel(r.agentProdNum)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{r.dials}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{r.contacts}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{r.appts}</td>
                  <td className="py-2 px-2 text-right tabular-nums text-slate-300">—</td>
                  <td className="py-2 px-2 text-right tabular-nums text-slate-300">—</td>
                  <td className="py-2 px-2 text-right tabular-nums">{r.contactPct.toFixed(0)}%</td>
                  <td className="py-2 px-2 text-right tabular-nums">{r.closePct.toFixed(0)}%</td>
                  <td className="py-2 px-2 text-right tabular-nums text-slate-300">—</td>
                </tr>
              ))}
              {Array.from({ length: blankRowsNeeded }, (_, i) => (
                <tr key={`blank-${i}`} className="border-b border-slate-100">
                  {Array.from({ length: 10 }, (_, j) => (
                    <td key={j} className="py-2 px-2 text-slate-200">_______</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-[11px] text-slate-400 mt-2">
          <strong>Dials/Sale</strong> is the master number — feed it back into the Engine's
          "Dial → Contact / Contact → Appt set" levers to make tomorrow's daily dial target exact.
        </div>
      </div>
    </div>
  );
}

// ─── FEX Source ROI ─────────────────────────────────────────────────
// CPA + ROI per lead source, scoped to leadType=final_expense. Matches
// the table on page 2 of the FEX KPI Engine PDF.

function FexSourceRoi({ leads = [], goalConfig = {}, onGotoLeads = () => {} }) {
  const cfg = { ...DEFAULT_GOAL_CONFIG, ...(goalConfig || {}) };

  const rows = React.useMemo(() => {
    const fex = (leads || []).filter(l => (l.leadType || 'general') === 'final_expense');
    if (fex.length === 0) return [];
    const m = {};
    fex.forEach(l => {
      const src = (l.source || '—').trim() || '—';
      if (!m[src]) m[src] = { source: src, count: 0, spend: 0, deals: 0, premium: 0 };
      m[src].count++;
      m[src].spend += Number(l.cost) || 0;
      if (l.status === 'closed') {
        m[src].deals++;
        m[src].premium += Number(l.premium) || 0;
      }
    });
    return Object.values(m).map(r => {
      const dollarsPerLead = r.count > 0 ? r.spend / r.count : 0;
      const apWritten = r.premium * 12;
      const estComm = apWritten * cfg.commissionRate * (1 - cfg.lapseRate);
      const cpa = r.deals > 0 ? r.spend / r.deals : null;
      const roi = r.spend > 0 ? estComm / r.spend : null;
      return { ...r, dollarsPerLead, apWritten, estComm, cpa, roi };
    }).sort((a, b) => b.count - a.count);
  }, [leads, cfg.commissionRate, cfg.lapseRate]);

  // Totals row matches the PDF — totals + blended $/Lead.
  const tot = rows.reduce((acc, r) => ({
    count:     acc.count + r.count,
    spend:     acc.spend + r.spend,
    deals:     acc.deals + r.deals,
    apWritten: acc.apWritten + r.apWritten,
    estComm:   acc.estComm + r.estComm,
  }), { count: 0, spend: 0, deals: 0, apWritten: 0, estComm: 0 });

  if (rows.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
        <h3 className="font-semibold text-slate-900 mb-1">No FEX leads yet</h3>
        <p className="text-sm text-slate-500 mb-4">Import a list (Grand Capital, Silver, Bronze, etc.) or set existing leads' Lead type to "Final Expense" — then this view fills in.</p>
        <button onClick={onGotoLeads} className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-4 py-2 rounded-lg">Open Leads</button>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <h3 className="font-semibold text-slate-900 mb-1">Lead Source ROI — FEX only</h3>
      <p className="text-xs text-slate-500 mb-3">
        Track CPA by source. Kill what doesn't produce, double what does. Est. commission =
        AP × {(cfg.commissionRate * 100).toFixed(0)}% × (1 − {(cfg.lapseRate * 100).toFixed(0)}% lapse).
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
              <th className="py-2 pr-3">Source</th>
              <th className="py-2 px-2 text-right">Leads</th>
              <th className="py-2 px-2 text-right">Cost</th>
              <th className="py-2 px-2 text-right">$/Lead</th>
              <th className="py-2 px-2 text-right">Deals</th>
              <th className="py-2 px-2 text-right">AP Written</th>
              <th className="py-2 px-2 text-right">Est. Comm.</th>
              <th className="py-2 px-2 text-right">CPA</th>
              <th className="py-2 px-2 text-right">ROI</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const roiTone = r.roi == null ? 'slate' : r.roi >= 5 ? 'emerald' : r.roi >= 2 ? 'amber' : 'red';
              return (
                <tr key={r.source} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 pr-3 font-medium text-slate-800">{r.source}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{r.count.toLocaleString()}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{fmt(r.spend)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{r.dollarsPerLead > 0 ? fmt(r.dollarsPerLead) : '—'}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{r.deals}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{fmt(r.apWritten)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{fmt(r.estComm)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{r.cpa == null ? '—' : fmt(r.cpa)}</td>
                  <td className={`py-2 px-2 text-right tabular-nums font-semibold text-${roiTone}-700`}>{r.roi == null ? '—' : `${r.roi.toFixed(1)}×`}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-300 text-sm font-semibold text-slate-900">
              <td className="py-2 pr-3">Totals</td>
              <td className="py-2 px-2 text-right tabular-nums">{tot.count.toLocaleString()}</td>
              <td className="py-2 px-2 text-right tabular-nums">{fmt(tot.spend)}</td>
              <td className="py-2 px-2 text-right tabular-nums">{tot.count > 0 ? fmt(tot.spend / tot.count) : '—'}</td>
              <td className="py-2 px-2 text-right tabular-nums">{tot.deals}</td>
              <td className="py-2 px-2 text-right tabular-nums">{fmt(tot.apWritten)}</td>
              <td className="py-2 px-2 text-right tabular-nums">{fmt(tot.estComm)}</td>
              <td className="py-2 px-2 text-right tabular-nums">{tot.deals > 0 ? fmt(tot.spend / tot.deals) : '—'}</td>
              <td className="py-2 px-2 text-right tabular-nums">{tot.spend > 0 ? `${(tot.estComm / tot.spend).toFixed(1)}×` : '—'}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// OnboardingAgent — AI assistant that DOES the setup, not explains it.
//
// Architecture:
//   • Tools are defined server-side in /api/onboarding/chat.js
//   • The agent (Claude Sonnet 4.6) emits tool_use blocks in its
//     response; we execute them HERE in the browser where the React
//     state lives, then POST tool_result blocks back to continue the
//     conversation.
//   • Auto-launches for fresh operators (no ownerPhone, no leads, no
//     monthlyTarget). Once dismissed, stays dismissed across reloads.
//   • Conversation transcript persists in cloudBackup so a half-done
//     setup can resume.
// ════════════════════════════════════════════════════════════════════

// Tiny helper — toggles a class on document.body while mounted so the
// fixed setup banner can claim 44px at the top of every page.
function SetupBannerBodyClass({ active }) {
  React.useEffect(() => {
    if (!active) return undefined;
    document.body.classList.add('setup-banner-active');
    return () => document.body.classList.remove('setup-banner-active');
  }, [active]);
  return null;
}

function OnboardingAgent({
  loading,
  currentUser,
  settings = {}, setSettings,
  goalConfig, setGoalConfig,
  leads = [], setLeads,
  fexOnboarding = {}, setFexOnboarding,
  chat = [], setChat,
  dismissed = false, setDismissed,
  onGotoTab = () => {},
  showToast = () => {},
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState('');
  const [role, setRole] = useState(null);
  const [dryRunPreview, setDryRunPreview] = useState(null);
  const [autoLaunched, setAutoLaunched] = useState(false);
  const scrollRef = React.useRef(null);

  // Best-effort trigger inference for auto-seeded workflows. The
  // real seeded rows come from the 007-series migrations; when we
  // create one on the fly (because the seed never ran for this
  // user), we pick a sensible default trigger from the name so
  // downstream lifecycle scans have something to key off.
  function inferTriggerFromName(name) {
    const s = String(name || '').toLowerCase();
    if (s.includes('appointment') && s.includes('booked'))     return 'appointment_booked';
    if (s.includes('no-show') || s.includes('no show'))        return 'appointment_no_show';
    if (s.includes('mp') && s.includes('aged'))                return 'lead_status_change';
    if (s.includes('welcome'))                                 return 'lead_created';
    if (s.includes('birthday'))                                return 'birthday_cron';
    if (s.includes('winback'))                                 return 'lead_inactive';
    return 'manual';
  }

  // Tool handler map. Each handler returns the result that becomes
  // the tool_result content sent back to the model. Errors propagate
  // as { error } so the model can read them and recover.
  const tools = React.useMemo(() => ({
    readState: () => {
      const phaseProgress = Object.fromEntries(
        ['p0','p1','p2','p3','p4'].map(pid => {
          const done = Object.values(fexOnboarding[pid] || {}).filter(Boolean).length;
          return [pid, { done }];
        })
      );
      return {
        owner: {
          name:  settings.ownerName || '',
          phone: settings.ownerPhone || '',
          alertPhone: settings.alertPhone || '',
          agencyName: settings.agencyName || '',
        },
        goalConfig: goalConfig || {},
        leadCount: (leads || []).length,
        leadTypeBreakdown: countByLeadType(leads || []),
        role,
        fexOnboardingProgress: phaseProgress,
        userEmail: currentUser?.email || '',
        smsSendEnabled: 'set on server — assume false in dry-run',
      };
    },
    setRole: ({ role: r }) => { setRole(r); return { ok: true, role: r }; },
    updateSettings: ({ patch }) => {
      if (!patch || typeof patch !== 'object') return { error: 'patch must be an object' };
      setSettings(s => ({ ...s, ...patch }));
      showToast(`✓ Settings updated: ${Object.keys(patch).join(', ')}`);
      return { ok: true, applied: Object.keys(patch) };
    },
    updateGoalConfig: ({ patch }) => {
      if (!patch || typeof patch !== 'object') return { error: 'patch must be an object' };
      setGoalConfig(cfg => ({ ...(cfg || {}), ...patch }));
      showToast(`✓ Goal Engine updated: ${Object.keys(patch).join(', ')}`);
      return { ok: true, applied: Object.keys(patch) };
    },
    activateWorkflow: async ({ name }) => {
      if (!name) return { error: 'name is required' };
      // Self-healing: the seed migration (007_seed_mp_workflow.sql)
      // only ran ONCE against auth.users at migration time. Every
      // subsequent signup gets zero workflow rows, so the AI tool
      // was previously returning "no workflow named X found" and
      // telling the client to open a support ticket for what is
      // really a backend gap. Now: if the update matches zero rows,
      // we create the workflow row on the fly and hand the AI a
      // clean success. Idempotent — a subsequent call flips active
      // on the existing row.
      try {
        const { data: { user } } = await supabase.auth.getUser();
        const ownerId = user?.id;
        if (!ownerId) return { error: 'must be signed in' };

        // 1. Try activating an existing row scoped to this user.
        const upd = await supabase
          .from('workflows')
          .update({ active: true })
          .eq('name', name)
          .eq('created_by', ownerId)
          .select('id, name');
        if (upd.error) return { error: upd.error.message };
        if (upd.data && upd.data.length > 0) {
          showToast(`✓ Workflow activated: ${name}`);
          return { ok: true, activated: upd.data.length, name };
        }

        // 2. No row — create one for this user. Minimal shape so
        //    the workflow tick engine sees it and honors the active
        //    flag; steps can be templated later.
        const ins = await supabase
          .from('workflows')
          .insert({
            name,
            active: true,
            created_by: ownerId,
            // Reasonable defaults so lifecycle enrolment doesn't
            // crash on missing fields. Real templates ship via
            // the seed migration when we backfill.
            trigger:    inferTriggerFromName(name),
            description: `Auto-seeded for ${name} (backend seed was missing)`,
          })
          .select('id, name');
        if (ins.error) {
          // Surface the real error; don't tell the client to file
          // a ticket — signal the admin panel to fix it for them.
          try {
            askAdminForHelp({
              tag: 'onboarding_stall',
              owner_id: ownerId,
              email: user?.email,
              severity: 'error',
              shape: {
                where: 'activateWorkflow.insert',
                workflow_name: String(name).slice(0, 100),
                error: String(ins.error.message || 'unknown').slice(0, 200),
              },
            });
          } catch { /* fire-and-forget */ }
          return { error: `Could not create workflow "${name}" — I've flagged this for our team to handle in the background. You can keep going with the next step.` };
        }
        showToast(`✓ Workflow activated: ${name}`);
        return { ok: true, activated: 1, name, note: 'seeded_on_demand' };
      } catch (e) { return { error: e.message }; }
    },
    markPhaseItem: ({ phaseId, idx, done }) => {
      setFexOnboarding(s => ({
        ...s,
        [phaseId]: { ...(s[phaseId] || {}), [idx]: !!done },
      }));
      return { ok: true, phaseId, idx, done: !!done };
    },
    createTestLead: ({ patch }) => {
      const newLead = {
        id: `lead_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        name: '(test lead)',
        status: 'new',
        leadType: 'final_expense',
        createdAt: new Date().toISOString(),
        ...(patch || {}),
      };
      setLeads(prev => [...prev, newLead]);
      showToast(`✓ Test lead created: ${newLead.name}`);
      return { ok: true, leadId: newLead.id, name: newLead.name };
    },
    dryRunSms: ({ body }) => {
      // Show an on-screen preview "as if" the engine sent it. Logs
      // only; never calls Twilio from here.
      setDryRunPreview({ body, ts: Date.now() });
      // Auto-clear preview after 8s so it doesn't pile up
      setTimeout(() => setDryRunPreview(p => p && Date.now() - p.ts >= 7900 ? null : p), 8000);
      return { ok: true, preview: body, deliveredVia: 'dry-run (no Relay send)' };
    },
    gotoTab: ({ tabId }) => {
      onGotoTab(tabId);
      return { ok: true, navigatedTo: tabId };
    },
    dismiss: ({ reason }) => {
      setDismissed(true);
      setOpen(false);
      return { ok: true, reason: reason || 'user requested' };
    },
  }), [settings, goalConfig, leads, role, fexOnboarding, currentUser, setSettings, setGoalConfig, setLeads, setFexOnboarding, setDismissed, onGotoTab, showToast]);

  // Auto-launch detection. Only fires once per session for fresh
  // operators. Won't re-open after dismissal or if the user has
  // already started a conversation in a prior session.
  React.useEffect(() => {
    if (loading) return;
    if (dismissed) return;
    if (autoLaunched) return;
    if ((chat || []).length > 0) return; // resume mode — wait for user click
    const fresh =
      !settings.ownerPhone &&
      (leads || []).length === 0 &&
      !(goalConfig && goalConfig.monthlyTarget);
    if (!fresh) return;
    setAutoLaunched(true);
    setOpen(true);
    // Kick off the conversation with a synthetic user message.
    sendUserMessage('Hi — I just logged in. Get me set up.');
  }, [loading, dismissed, chat.length, settings.ownerPhone, leads.length, goalConfig?.monthlyTarget]);

  // Auto-scroll to bottom on new messages.
  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [chat, busy]);

  async function sendUserMessage(text) {
    const trimmed = (text || '').trim();
    if (!trimmed) return;
    const userMsg = { role: 'user', content: trimmed };
    const next = [...(chat || []), userMsg];
    setChat(next);
    setInput('');
    await runConversation(next);
  }

  async function runConversation(currentMessages, depth = 0) {
    if (depth > 8) {
      // Safety: cap tool-use loops so a misbehaving model can't burn
      // tokens forever.
      setChat(m => [...m, { role: 'assistant', content: [{ type: 'text', text: '(stopped — too many tool calls in a row)' }] }]);
      return;
    }
    setBusy(true);
    try {
      const res = await fetch('/api/onboarding/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: currentMessages,
          systemContext: {
            ownerName: settings.ownerName || '',
            role: role || '',
          },
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        setChat(m => [...m, { role: 'assistant', content: [{ type: 'text', text: `(error: ${json.error || res.statusText})` }] }]);
        return;
      }
      const assistantBlocks = Array.isArray(json.content) ? json.content : [];
      const withAssistant = [...currentMessages, { role: 'assistant', content: assistantBlocks }];
      setChat(withAssistant);

      // Execute any tool_use blocks; collect tool_result blocks; if any
      // were emitted, continue the conversation with a user-role turn
      // containing the results.
      const toolUses = assistantBlocks.filter(b => b && b.type === 'tool_use');
      if (toolUses.length === 0) return;

      const toolResults = await Promise.all(toolUses.map(async (tu) => {
        const handler = tools[tu.name];
        let result;
        try {
          result = handler ? await handler(tu.input || {}) : { error: `unknown tool: ${tu.name}` };
        } catch (e) {
          result = { error: e.message || String(e) };
        }
        return {
          type: 'tool_result',
          tool_use_id: tu.id,
          content: JSON.stringify(result),
        };
      }));

      const withResults = [...withAssistant, { role: 'user', content: toolResults }];
      setChat(withResults);
      await runConversation(withResults, depth + 1);
    } catch (err) {
      setChat(m => [...m, { role: 'assistant', content: [{ type: 'text', text: `(network error: ${err.message || err})` }] }]);
    } finally {
      setBusy(false);
    }
  }

  function resetConversation() {
    setChat([]);
    setRole(null);
    setBusy(false);
    setInput('');
    sendUserMessage('Hi — restart the setup from the beginning.');
  }

  // Don't render anything while the parent is still hydrating state.
  if (loading) return null;

  // Compute notification badge — show "Resume" if there's a half-done
  // conversation, or "Setup" if the operator hasn't started yet.
  const hasConversation = (chat || []).length > 0;
  const buttonLabel = hasConversation ? 'Resume setup' : 'Setup Assistant';
  const buttonEmoji = hasConversation ? '' : '';

  // The launcher used to live as a floating green pill bottom-right
  // which overlapped charts on scroll and competed visually with the
  // ⌘K palette pill stacked nearby. It now renders as a slim top
  // banner that the operator can dismiss (per-tab, persisted to
  // localStorage). When dismissed, the only way back in is the user
  // menu or the cmd-K palette → Setup Assistant.
  const dismissedKey = 'setup_assistant:banner_dismissed';
  const [bannerDismissed, setBannerDismissed] = React.useState(() => {
    try { return localStorage.getItem(dismissedKey) === '1'; } catch { return false; }
  });
  const handleDismissBanner = () => {
    setBannerDismissed(true);
    try { localStorage.setItem(dismissedKey, '1'); } catch {}
  };

  return (
    <>
      {/* Dismissible top banner — replaces the floating launcher.
          Fixed positioning so it sits at the very top of the viewport
          regardless of where in the DOM the OnboardingAgent component
          is mounted. Body gets compensating padding via the CSS rule
          injected below. */}
      {!open && !bannerDismissed && (
        <>
          <style>{`body.setup-banner-active { padding-top: 44px; }`}</style>
          <SetupBannerBodyClass active/>
          <div className="fixed top-0 left-0 lg:left-[232px] right-0 z-40 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-4 py-2 flex items-center justify-between gap-3 flex-wrap shadow-md">
            <div className="flex items-center gap-3 flex-wrap min-w-0">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] bg-white/20 px-2 py-0.5 rounded-full whitespace-nowrap">{hasConversation ? 'In Progress' : 'New'}</span>
              <span className="text-sm font-semibold truncate">
                {hasConversation
                  ? "Your AI setup is paused — finish it to unlock automated lifecycle SMS, lead routing, and the Health portal."
                  : "Set up your CRM with the AI Onboarding Assistant. Takes ~5 minutes."}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setOpen(true)}
                className="bg-white text-emerald-700 hover:bg-emerald-50 text-xs font-black uppercase tracking-tight px-3 py-1.5 rounded-md transition"
              >
                {buttonLabel} →
              </button>
              <button
                onClick={handleDismissBanner}
                title="Dismiss — reopen via the cmd-K palette → Setup Assistant."
                className="text-white/80 hover:text-white text-xl leading-none px-2"
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          </div>
        </>
      )}

      {/* Dry-run SMS preview toast — shows above the panel when the
          agent calls dryRunSms */}
      {dryRunPreview && (
        <div className="fixed bottom-44 right-6 z-50 max-w-sm bg-slate-900 text-white rounded-2xl shadow-xl p-4 animate-pulse-once">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Dry-run SMS preview</div>
          <div className="text-sm">{dryRunPreview.body}</div>
          <div className="text-[10px] text-slate-500 mt-2">No Relay send — this is a sample of what an outbound text would look like.</div>
        </div>
      )}

      {/* Slide-in panel — HQ dark + amber (fixed overlay, so it needs
          explicit dark styles; it lives outside the .classic-hq scope). */}
      {open && (
        <div className="fixed inset-y-0 right-0 z-40 w-full sm:w-[420px] shadow-2xl flex flex-col" style={{ background: '#080b0f', borderLeft: '1px solid rgba(245,166,35,0.20)' }}>
          {/* Header */}
          <div className="px-4 py-3 flex items-center justify-between gap-2" style={{ background: 'linear-gradient(135deg, rgba(24,20,10,0.96), rgba(8,11,15,0.98))', borderBottom: '1px solid rgba(245,166,35,0.22)' }}>
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: '#F5A623', fontFamily: 'ui-monospace, monospace' }}>AI Onboarding Assistant</div>
              <div className="font-semibold" style={{ color: '#F4F4F2' }}>{settings.ownerName ? `Hey ${settings.ownerName.split(' ')[0]}` : "Let's get you set up"}</div>
              {role && <div className="text-[10px] mt-0.5" style={{ color: '#7C838C' }}>role: {role}</div>}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={resetConversation} title="Restart" className="p-1.5 rounded text-xs hover:brightness-125" style={{ color: '#B9BEC6' }}>↺</button>
              <button onClick={() => setOpen(false)} title="Close" className="p-1.5 rounded hover:brightness-125" style={{ color: '#B9BEC6' }}>
                <X size={18}/>
              </button>
            </div>
          </div>

          {/* Message list */}
          <div ref={scrollRef} className="flex-1 overflow-auto px-3 py-3 space-y-2" style={{ background: '#050709' }}>
            {chat.length === 0 && (
              <div className="text-center text-sm italic py-12" style={{ color: '#7C838C' }}>
                Say hi or ask "set me up" to start.
              </div>
            )}
            {chat.map((m, i) => <OnboardingMessage key={i} msg={m}/>)}
            {busy && (
              <div className="flex items-center gap-2 text-xs italic px-3 py-2" style={{ color: '#7C838C' }}>
                <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#F5A623' }}/>
                <span>thinking…</span>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="p-3" style={{ borderTop: '1px solid rgba(245,166,35,0.18)', background: '#080b0f' }}>
            <form
              onSubmit={(e) => { e.preventDefault(); sendUserMessage(input); }}
              className="flex items-center gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={busy ? 'wait…' : 'type a message…'}
                disabled={busy}
                className="flex-1 rounded-lg px-3 py-2 text-sm focus:outline-none"
                style={{ background: 'rgba(9,12,16,0.7)', border: '1px solid rgba(245,166,35,0.20)', color: '#F4F4F2' }}
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                className="disabled:opacity-50 text-sm font-bold rounded-lg px-3 py-2"
                style={{ background: 'linear-gradient(180deg,#FFC457,#F5A623)', color: '#0b0a05' }}
              >Send</button>
            </form>
            <div className="text-[10px] mt-1.5" style={{ color: '#7C838C' }}>
              Actions: configure settings · activate workflows · check off Phase items · navigate the app.
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Render one message from the transcript. The user role messages can
// be either a plain string (typed input) OR an array of tool_result
// blocks (when we're feeding tool execution results back to the
// model). Assistant messages are always content-block arrays.
function OnboardingMessage({ msg }) {
  if (!msg) return null;
  const isUser = msg.role === 'user';

  // Tool-result turns: we surface a tiny "✓ tool_result" line so the
  // user can see the agent did something, without dumping JSON.
  if (isUser && Array.isArray(msg.content) && msg.content.length > 0 && msg.content[0].type === 'tool_result') {
    return (
      <div className="text-[10px] text-center my-1" style={{ color: '#7C838C' }}>
        ✓ {msg.content.length} tool result{msg.content.length === 1 ? '' : 's'} returned to the agent
      </div>
    );
  }

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-sm px-3 py-2 text-sm" style={{ background: 'linear-gradient(180deg,#FFC457,#F5A623)', color: '#0b0a05', fontWeight: 500 }}>
          {typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content)}
        </div>
      </div>
    );
  }

  // Assistant: render text blocks + a small chip per tool_use
  const blocks = Array.isArray(msg.content) ? msg.content : [];
  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] rounded-2xl rounded-bl-sm px-3 py-2 text-sm space-y-1.5" style={{ background: 'rgba(14,18,23,0.86)', border: '1px solid rgba(245,166,35,0.18)' }}>
        {blocks.map((b, i) => {
          if (!b) return null;
          if (b.type === 'text') {
            return <div key={i} className="whitespace-pre-wrap" style={{ color: '#E4E7EC' }}>{b.text}</div>;
          }
          if (b.type === 'tool_use') {
            const summary = summarizeToolUse(b);
            return (
              <div key={i} className="text-[11px] rounded-md px-2 py-1 font-mono" style={{ background: 'rgba(9,12,16,0.7)', border: '1px solid rgba(245,166,35,0.16)', color: '#B9BEC6' }}>
                {b.name}{summary ? ` · ${summary}` : ''}
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}

function summarizeToolUse(tu) {
  const i = tu.input || {};
  switch (tu.name) {
    case 'readState':       return null;
    case 'setRole':         return i.role;
    case 'updateSettings':  return Object.keys(i.patch || {}).join(', ');
    case 'updateGoalConfig': return Object.keys(i.patch || {}).join(', ');
    case 'activateWorkflow': return i.name;
    case 'markPhaseItem':   return `${i.phaseId} #${i.idx} = ${i.done ? '✓' : '✗'}`;
    case 'createTestLead':  return (i.patch?.name || 'sample');
    case 'dryRunSms':       return (i.body || '').slice(0, 40) + ((i.body || '').length > 40 ? '…' : '');
    case 'gotoTab':         return i.tabId;
    case 'dismiss':         return i.reason;
    default:                return JSON.stringify(i).slice(0, 60);
  }
}

function countByLeadType(leads) {
  const out = { final_expense: 0, mortgage_protection: 0, iul: 0, general: 0 };
  for (const l of leads) {
    const t = l.leadType || 'general';
    if (out[t] != null) out[t]++;
    else out.general++;
  }
  return out;
}

// ════════════════════════════════════════════════════════════════════
// Mission view + Power Dialer
//
// The "lazy agent runs 2000 leads/month" play. The system surfaces a
// prioritized dial queue (hot first → fresh imports → going-cold) and
// the Power Dialer takes over the screen for focused volume dialing:
// one big call button + 7 single-click outcome buttons + AI pre-call
// brief + auto-advance.
//
// The agent doesn't decide what to do — the queue tells them. They
// don't have to remember to log calls — every outcome button logs
// the call AND updates the lead's stage AND advances to the next.
//
// Designed for ~250 dials/day. With a Twilio-style softphone or
// WAVV-style dialer integration the same UI handles the real dial.
// ════════════════════════════════════════════════════════════════════

// ─── Power Dialer queue computation ────────────────────────────────
//
// Returns an array of { lead, priority, ... } sorted best-first.
// Priority signals (additive):
//   +100 per "hot" tag (Hot , Warm, Interested...)
//   +10 × leadScore (1-5)
//   +80 if status === appt_set, +50 if interested
//   +30 for never-called fresh imports (speed-to-lead)
//   −10 if >7 days since last contact (cold decay)
//   −20 if >14 days since last contact
function buildPowerDialQueue({ leads = [], calls = [], outreach = [], lens = 'all' }) {
  const now = Date.now();
  const DAY = 1000 * 60 * 60 * 24;

  // Index last-touch per lead from calls + outreach.
  const lastTouch = {};
  for (const c of (calls || [])) {
    if (!c.leadId) continue;
    const ts = Date.parse(`${c.date || ''}T${c.startTime || '00:00'}:00`);
    if (Number.isFinite(ts)) lastTouch[c.leadId] = Math.max(lastTouch[c.leadId] || 0, ts);
  }
  for (const o of (outreach || [])) {
    if (!o.leadId) continue;
    const ts = Date.parse(o.createdAt || '');
    if (Number.isFinite(ts)) lastTouch[o.leadId] = Math.max(lastTouch[o.leadId] || 0, ts);
  }

  const TERMINAL = new Set(['closed', 'dnc', 'recycled', 'no_show']);
  const HOT_TAG_RE = /hot|fire|🔥|warm|interested|ready|callback/i;

  return leads
    .filter(l => {
      if (TERMINAL.has(l.status)) return false;
      if (l.optOut || l.opted_out) return false;
      if (!l.phone && !l.phone_e164) return false;
      if (lens === 'fex'   && (l.leadType || 'general') !== 'final_expense')      return false;
      if (lens === 'mp'    && l.leadType !== 'mortgage_protection')                return false;
      if (lens === 'iul'   && l.leadType !== 'iul')                                return false;
      if (lens === 'hot') {
        // hot tag OR score >= 4 OR status interested/appt_set
        const tags = (l.tags || []);
        const hasHotTag = tags.some(t => HOT_TAG_RE.test(t));
        const score = calcLeadScore ? calcLeadScore(l) : 0;
        if (!hasHotTag && score < 4 && !['interested','appt_set'].includes(l.status)) return false;
      }
      if (lens === 'cold') {
        const touched = lastTouch[l.id] || 0;
        const daysSince = touched ? (now - touched) / DAY : Infinity;
        if (daysSince < 7) return false;
      }
      return true;
    })
    .map(l => {
      const touched   = lastTouch[l.id] || 0;
      const daysSince = touched ? Math.floor((now - touched) / DAY) : Infinity;
      const created   = Date.parse(l.createdAt || '');
      const daysSinceImport = Number.isFinite(created) ? Math.floor((now - created) / DAY) : 999;
      const hotTags   = (l.tags || []).filter(t => HOT_TAG_RE.test(t)).length;
      const leadScore = calcLeadScore ? (calcLeadScore(l) || 0) : 0;
      const status    = l.status || 'new';

      let priority = 0;
      priority += 100 * hotTags;
      priority += 10 * leadScore;
      if (status === 'appt_set')   priority += 80;
      if (status === 'interested') priority += 50;
      if (status === 'contacted')  priority += 20;
      if (daysSince === Infinity && daysSinceImport <= 1) priority += 30; // speed-to-lead
      if (daysSince > 7)  priority -= 10;
      if (daysSince > 14) priority -= 20;

      return { lead: l, priority, hotTags, leadScore, daysSince, daysSinceImport };
    })
    .sort((a, b) => b.priority - a.priority);
}

// ─── Mission view — daily mission control ──────────────────────────
function MissionView({ goalConfig, calls = [], leads = [], settings = {}, transactions = [], producers = {}, currentUser = null, onGotoTab = () => {}, onOpenPowerDialer = () => {} }) {
  const cfg = { ...DEFAULT_GOAL_CONFIG, ...(goalConfig || {}) };

  const todayStr = new Date().toISOString().slice(0, 10);

  const stats = React.useMemo(() => {
    // Reverse-engineer daily targets from goalConfig (same math as Goal Engine)
    const realizedComm = cfg.avgAnnualPremium * cfg.commissionRate * (1 - cfg.lapseRate);
    const dealsNeeded     = realizedComm > 0 ? Math.ceil(cfg.monthlyTarget / realizedComm) : 0;
    const showsNeeded     = cfg.showToSale > 0    ? Math.ceil(dealsNeeded / cfg.showToSale)    : 0;
    const apptsNeeded     = cfg.apptToShow > 0    ? Math.ceil(showsNeeded / cfg.apptToShow)    : 0;
    const contactsNeeded  = cfg.contactToAppt > 0 ? Math.ceil(apptsNeeded / cfg.contactToAppt) : 0;
    const dialsNeeded     = cfg.dialToContact > 0 ? Math.ceil(contactsNeeded / cfg.dialToContact) : 0;
    const dailyDialTarget = cfg.workingDays > 0 ? Math.round(dialsNeeded / cfg.workingDays) : 0;
    const dailyApptTarget = cfg.workingDays > 0 ? Math.ceil(apptsNeeded / cfg.workingDays) : 0;

    const todayCalls = (calls || []).filter(c => c.direction === 'outbound' && c.date === todayStr);
    const dialsToday = todayCalls.length;
    const apptsBookedToday = todayCalls.filter(c => c.outcome === 'appt_set').length;
    const dialsRemaining = Math.max(0, dailyDialTarget - dialsToday);

    const apptsToday = (leads || []).filter(l => l.appointmentDate === todayStr && !['closed','dnc','no_show'].includes(l.status));
    const overdueFollowUps = (leads || []).filter(l => {
      if (!l.followUpDate) return false;
      if (['closed','dnc','no_show','recycled'].includes(l.status)) return false;
      return l.followUpDate < todayStr;
    });

    return {
      dialsToday, apptsBookedToday, dialsRemaining,
      dailyDialTarget, dailyApptTarget,
      apptsToday, overdueFollowUps,
    };
  }, [cfg, calls, leads, todayStr]);

  // Build the prioritized dial queue (capped at 50 for visual)
  const queue = React.useMemo(() => buildPowerDialQueue({ leads, calls, lens: 'all' }).slice(0, 50), [leads, calls]);
  const queueByLens = React.useMemo(() => ({
    all: queue.length,
    fex: buildPowerDialQueue({ leads, calls, lens: 'fex' }).length,
    hot: buildPowerDialQueue({ leads, calls, lens: 'hot' }).length,
    cold: buildPowerDialQueue({ leads, calls, lens: 'cold' }).length,
  }), [leads, calls]);

  const date = new Date();
  const dateStr = date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const dialPct = stats.dailyDialTarget > 0 ? Math.min(100, (stats.dialsToday / stats.dailyDialTarget) * 100) : 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 text-white rounded-2xl p-6">
        <div className="text-emerald-300 text-xs font-semibold uppercase tracking-wider">Today's Mission</div>
        <h1 className="text-3xl font-bold mt-1">{dateStr}</h1>
        <p className="text-slate-300 text-sm mt-2">
          One screen. Three numbers. Hit the dial target → sit the appointments → close.
          The system handles the rest.
        </p>
      </div>

      {/* 3 big tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Dials. Empty-book guard: when there are zero leads on the
            book, the Goal Engine's "318 dials remaining" reads as a
            confusing hill to climb with no one to call. Surface an
            "Import leads to start dialing" prompt instead and keep
            the target text muted. */}
        <div className="bg-white border-2 border-emerald-200 rounded-2xl p-5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Power Dial</div>
          {leads.length === 0 ? (
            <>
              <div className="text-4xl font-bold text-slate-300 tabular-nums mt-1">—</div>
              <div className="text-xs text-slate-500">
                No leads yet. Import a list or add one manually to start dialing.
              </div>
              <div className="text-[10px] text-slate-400 mt-3 italic">Daily target {stats.dailyDialTarget} dials · activates once leads land.</div>
            </>
          ) : (
            <>
              <div className="text-4xl font-bold text-slate-900 tabular-nums mt-1">
                {stats.dialsRemaining}
              </div>
              <div className="text-xs text-slate-500">
                dials remaining to hit today's <strong>{stats.dailyDialTarget}</strong> target
              </div>
              <div className="h-2 bg-slate-100 rounded-full mt-3 overflow-hidden">
                <div className="h-full bg-emerald-600 transition-all" style={{ width: `${dialPct}%` }}/>
              </div>
              <div className="text-[10px] text-slate-500 mt-1 tabular-nums">{stats.dialsToday} / {stats.dailyDialTarget}</div>
            </>
          )}
          <button
            onClick={() => leads.length === 0 ? onGotoTab('leads') : onOpenPowerDialer('hot')}
            className="mt-4 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg py-3 text-sm flex items-center justify-center gap-2"
          >
            {leads.length === 0 ? 'Add Leads' : 'Start Power Dialing'}
          </button>
        </div>

        {/* Appointments */}
        <div className="bg-white border-2 border-amber-200 rounded-2xl p-5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Appointments today</div>
          <div className="text-4xl font-bold text-slate-900 tabular-nums mt-1">
            {stats.apptsToday.length}
          </div>
          <div className="text-xs text-slate-500">
            booked appointments to sit
          </div>
          <div className="mt-3 space-y-1 max-h-24 overflow-auto">
            {stats.apptsToday.length === 0 && (
              <div className="text-xs text-slate-400 italic">No appointments today.</div>
            )}
            {stats.apptsToday.slice(0, 4).map(l => (
              <div key={l.id} className="text-xs text-slate-700 flex items-center gap-2">
                <span className="text-amber-700 font-mono tabular-nums">{l.appointmentTime || '?'}</span>
                <span className="truncate">{l.name}</span>
              </div>
            ))}
          </div>
          <button
            onClick={() => onGotoTab('calendar')}
            className="mt-4 w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg py-3 text-sm"
          >
            Open Calendar
          </button>
        </div>

        {/* Hot replies / overdue. Tone follows the count — neutral when
            there's nothing to chase, red only when there's real work. */}
        {(() => {
          const overdue = stats.overdueFollowUps.length;
          const hasOverdue = overdue > 0;
          return (
            <div className={`bg-white border-2 rounded-2xl p-5 ${hasOverdue ? 'border-red-200' : 'border-slate-200'}`}>
              <div className={`text-[10px] font-bold uppercase tracking-wider ${hasOverdue ? 'text-red-700' : 'text-slate-500'}`}>
                {hasOverdue ? 'Needs you now' : 'Follow-up queue'}
              </div>
              <div className={`text-4xl font-bold tabular-nums mt-1 ${hasOverdue ? 'text-slate-900' : 'text-slate-300'}`}>
                {overdue}
              </div>
              <div className="text-xs text-slate-500">
                overdue follow-ups
              </div>
              <div className="mt-3 space-y-1 max-h-24 overflow-auto">
                {!hasOverdue && (
                  <div className="text-xs text-slate-400 italic">All caught up.</div>
                )}
                {stats.overdueFollowUps.slice(0, 4).map(l => (
                  <div key={l.id} className="text-xs text-slate-700 truncate">
                    ⚠ {l.name} · due {l.followUpDate}
                  </div>
                ))}
              </div>
              <button
                onClick={() => onGotoTab('leads')}
                disabled={!hasOverdue}
                className={`mt-4 w-full font-semibold rounded-lg py-3 text-sm transition-colors ${
                  hasOverdue
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                }`}
              >
                {hasOverdue ? 'Work Overdue' : 'No overdue work'}
              </button>
            </div>
          );
        })()}
      </div>

      {/* Lens selector for the power dial queue */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <h3 className="font-semibold text-slate-900">Power Dial Queue</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Prioritized by hot tags → lead score → speed-to-lead → cold decay.
              Pick a lens and start dialing.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          {[
            ['hot',  'Hot only',   queueByLens.hot,  'red',     'Leads tagged hot OR with a 5-star lead score. Dial these first.'],
            ['all',  'All ready',  queueByLens.all,  'emerald', 'Every lead eligible to dial right now (not opted-out, not in a stage that suppresses outreach).'],
            ['fex',  'FEX',        queueByLens.fex,  'slate',   'Final Expense leads only (vertical=fex). Use for FEX-only dial sessions.'],
            ['cold', 'Going cold', queueByLens.cold, 'amber',   'Leads with no contact in 7+ days — falling out of the active pipeline. Revive or move to win-back.'],
          ].map(([id, label, count, tone, tip]) => (
            <button
              key={id}
              onClick={() => onOpenPowerDialer(id)}
              className={`bg-${tone}-50 hover:bg-${tone}-100 border border-${tone}-200 text-${tone}-800 px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed`}
              disabled={count === 0}
              title={tip}
            >
              {label}
              <span className={`text-xs bg-${tone}-100 text-${tone}-800 border border-${tone}-200 px-1.5 py-0.5 rounded-md tabular-nums`}>{count}</span>
            </button>
          ))}
        </div>

        {/* Queue preview */}
        {queue.length === 0 ? (
          <div className="text-sm text-slate-500 italic py-6 text-center">
            No leads in the dial queue. Import a CSV or check your filters.
          </div>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Lead</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Score</th>
                  <th className="text-right px-3 py-2">Days</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {queue.slice(0, 12).map((q, i) => (
                  <tr key={q.lead.id} className={i === 0 ? 'bg-emerald-50' : ''}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-800">{q.lead.name || '—'}</div>
                      <div className="text-xs text-slate-500">{q.lead.phone || '—'}</div>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">{(q.lead.leadType || 'general').replace(/_/g, ' ')}</td>
                    <td className="px-3 py-2 text-xs">{q.lead.status || 'new'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      <span
                        className="text-amber-700 font-semibold cursor-help"
                        title={`Lead score: ${q.leadScore}/5\n\n${explainLeadScore(q.lead).join('\n')}`}
                      >
                        {q.leadScore}
                      </span>
                      {q.hotTags > 0 && (
                        <span
                          className="ml-1 text-[10px] cursor-help"
                          title={`${q.hotTags} hot tag(s) detected (recent contact, intent keyword, etc.) — see notes`}
                        >
                          ×{q.hotTags}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs text-slate-500">
                      {q.daysSince === Infinity ? 'never' : `${q.daysSince}d`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {queue.length > 12 && (
              <div className="px-3 py-2 text-xs text-slate-500 bg-slate-50 border-t border-slate-200">
                +{queue.length - 12} more in queue
              </div>
            )}
          </div>
        )}
      </div>

      {/* The one rule */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
        <strong>The one rule:</strong> Hit the dials. The system runs the drips, the reminders, the no-show recovery,
        the conservation save. You sit appointments and close. Everything else is automated.
      </div>
    </div>
  );
}

// ─── Power Dialer — full-screen takeover for high-volume dialing ───
function PowerDialerOverlay({ leads = [], setLeads, calls = [], setCalls, outreach = [], setOutreach, producers = {}, settings = {}, currentUser = null, initialLens = 'all', onClose }) {
  const [lens, setLens] = useState(initialLens);
  const [dialedThisSession, setDialedThisSession] = useState(() => new Set());
  const [brief, setBrief] = useState(null);
  const [loadingBrief, setLoadingBrief] = useState(false);
  const [notes, setNotes] = useState('');
  const [showOutcomes, setShowOutcomes] = useState(false);

  // The queue updates whenever leads/calls change (so completed leads
  // disappear) AND we filter out ones disposed in THIS session.
  const queue = React.useMemo(() => {
    const all = buildPowerDialQueue({ leads, calls, outreach, lens });
    return all.filter(q => !dialedThisSession.has(q.lead.id));
  }, [leads, calls, outreach, lens, dialedThisSession]);

  const current = queue[0];
  const dialedCount = dialedThisSession.size;

  // Load AI brief when the current lead changes. Cached per lead-id
  // for the session so navigating doesn't re-spend tokens.
  const briefCache = React.useRef({});
  React.useEffect(() => {
    setNotes('');
    setShowOutcomes(false);
    setBrief(null);
    if (!current) return;
    if (briefCache.current[current.lead.id]) {
      setBrief(briefCache.current[current.lead.id]);
      return;
    }
    let cancelled = false;
    setLoadingBrief(true);
    const recentCalls = (calls || []).filter(c => c.leadId === current.lead.id).slice(-5);
    const recentOutreach = (outreach || []).filter(o => o.leadId === current.lead.id).slice(-5);
    generateLeadBrief({ lead: current.lead, recentCalls, recentOutreach })
      .then(b => {
        if (cancelled) return;
        briefCache.current[current.lead.id] = b;
        setBrief(b);
      })
      .catch(err => {
        // Brief generation can fail — log so the operator can report,
        // but don't blow up the inbox row. The empty brief state is
        // the visible signal something went wrong.
        if (!cancelled) console.warn('[lead-brief] generation failed:', err?.message || err);
      })
      .finally(() => !cancelled && setLoadingBrief(false));
    return () => { cancelled = true; };
  }, [current?.lead?.id, calls, outreach]);

  function logOutcome(outcome) {
    if (!current) return;
    const now = new Date();
    const isoNow = now.toISOString();
    const dateStr = isoNow.slice(0, 10);
    const timeStr = isoNow.slice(11, 16);
    const callId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `call_${Date.now()}`;
    const agentName = current.lead.agentProdNum || currentUser?.prodNum || '';

    setCalls(prev => [...prev, {
      id: callId,
      agentProdNum: agentName,
      leadId: current.lead.id,
      prospectName: current.lead.name,
      prospectPhone: current.lead.phone,
      date: dateStr,
      startTime: timeStr,
      direction: 'outbound',
      outcome,
      duration: 0,
      notes,
      createdAt: isoNow,
      updatedAt: isoNow,
    }]);

    // W9 disposition→stage (same mappings as the Calls tab's saveCall)
    const TERMINAL = new Set(['closed', 'dnc']);
    let nextStatus = null;
    if (outcome === 'appt_set'         && current.lead.status !== 'appt_set' && current.lead.status !== 'closed') nextStatus = 'appt_set';
    else if (outcome === 'not_interested' && !TERMINAL.has(current.lead.status))                                    nextStatus = 'recycled';
    else if (outcome === 'dnc'            && !TERMINAL.has(current.lead.status))                                    nextStatus = 'dnc';
    else if (outcome === 'pickup'         && current.lead.status === 'new')                                         nextStatus = 'contacted';
    else if ((outcome === 'callback' || outcome === 'voicemail') && current.lead.status === 'new')                  nextStatus = 'contacted';

    if (nextStatus) {
      setLeads(prev => prev.map(l => l.id === current.lead.id ? {
        ...l,
        status: nextStatus,
        statusChangedAt: isoNow,
        updatedAt: isoNow,
        firstContactedAt: l.firstContactedAt || isoNow,
        activity: [...(l.activity || []), { at: isoNow, type: 'power_dial', body: `Power dial outcome: ${outcome}${notes ? ' — ' + notes : ''}` }],
      } : l));
    }

    // Mark this lead as disposed this session → drops out of the queue
    setDialedThisSession(s => new Set([...s, current.lead.id]));
    setShowOutcomes(false);
    setNotes('');
  }

  function skip() {
    if (!current) return;
    setDialedThisSession(s => new Set([...s, current.lead.id]));
    setShowOutcomes(false);
    setNotes('');
  }

  function dial() {
    if (!current?.lead?.phone) return;
    setShowOutcomes(true);
    // Route through the global dial dispatcher so the Twilio Voice SDK
    // softphone opens on desktop instead of falling through to the OS
    // tel: handler (which on most desktops navigates the tab to Google
    // Voice / FaceTime / nothing).
    requestPhoneCall({ lead: current.lead });
  }

  // Keyboard shortcuts. Letters > numbers because numbers might trigger
  // unintended inputs in form fields.
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA') return;
      const key = e.key.toLowerCase();
      if (key === 'escape') { onClose(); return; }
      if (key === 'c' || key === ' ') { e.preventDefault(); dial(); return; }
      if (key === 's' || key === 'n') { e.preventDefault(); skip(); return; }
      if (!showOutcomes) return;
      if (key === '1') logOutcome('appt_set');
      else if (key === '2') logOutcome('pickup');
      else if (key === '3') logOutcome('callback');
      else if (key === '4') logOutcome('not_interested');
      else if (key === '5') logOutcome('voicemail');
      else if (key === '6') logOutcome('no_answer');
      else if (key === '7') logOutcome('dnc');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current?.lead?.id, showOutcomes, notes]);

  const agentName = current?.lead ? getProducerName(current.lead.agentProdNum, producers) : '';

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/95 backdrop-blur-sm overflow-auto">
      <div className="max-w-none mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button aria-label="Close" onClick={onClose} className="text-slate-400 hover:text-white">
              <X size={24}/>
            </button>
            <div>
              <div className="text-xs uppercase tracking-wider text-emerald-400 font-semibold">Power Dialer</div>
              <div className="text-white text-sm">Dialed this session: <span className="text-emerald-400 font-bold tabular-nums">{dialedCount}</span> · Queue: <span className="text-amber-400 font-bold tabular-nums">{queue.length}</span></div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={lens}
              onChange={(e) => { setLens(e.target.value); setDialedThisSession(new Set()); }}
              className="bg-slate-800 text-white border border-slate-700 rounded-lg px-3 py-1.5 text-sm"
            >
              <option value="all">All ready</option>
              <option value="hot">Hot only</option>
              <option value="fex">FEX</option>
              <option value="mp">MP</option>
              <option value="iul">IUL</option>
              <option value="cold">Going cold</option>
            </select>
          </div>
        </div>

        {!current ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <div className="text-4xl mb-3"></div>
            <div className="text-xl font-bold text-slate-900">Queue empty</div>
            <div className="text-sm text-slate-500 mt-2">
              No more leads in this lens. Try a different lens or import more leads.
            </div>
            <button aria-label="Close" onClick={onClose} className="mt-4 bg-slate-900 hover:bg-slate-700 text-white px-4 py-2 rounded-lg font-semibold">Close</button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl overflow-hidden shadow-2xl">
            {/* Lead card */}
            <div className="bg-gradient-to-br from-emerald-50 to-white p-6 border-b border-slate-100">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-2xl font-bold text-slate-900">{current.lead.name || 'Unknown'}</h2>
                    {current.hotTags > 0 && <span className="text-lg">{''.repeat(Math.min(5, current.hotTags))}</span>}
                    {current.leadScore > 0 && (
                      <span className="bg-amber-100 text-amber-800 text-xs font-bold px-2 py-0.5 rounded-full">
                        Score {current.leadScore}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-slate-600 mt-1">
                    {current.lead.phone || '(no phone)'} ·{' '}
                    {(current.lead.leadType || 'general').replace(/_/g, ' ')} ·{' '}
                    {current.lead.state || ''} ·{' '}
                    status: {current.lead.status || 'new'}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {current.lead.source || 'no source'} ·{' '}
                    {current.daysSinceImport === 999 ? 'imported ?' : `imported ${current.daysSinceImport}d ago`} ·{' '}
                    {current.daysSince === Infinity ? 'never contacted' : `last contact ${current.daysSince}d ago`}
                    {agentName && <> · agent: {agentName}</>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Priority</div>
                  <div className="text-2xl font-bold text-emerald-700 tabular-nums">{current.priority}</div>
                </div>
              </div>
            </div>

            {/* AI brief */}
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-2">
                AI Pre-Call Brief
              </div>
              {loadingBrief ? (
                <div className="text-xs text-slate-500 italic">generating brief…</div>
              ) : brief ? (
                <div className="space-y-1.5 text-sm">
                  {brief.brief && <div><span className="text-[10px] uppercase font-bold text-slate-500 block">Who they are</span>{brief.brief}</div>}
                  {brief.angle && <div><span className="text-[10px] uppercase font-bold text-emerald-700 block">Lead with</span>{brief.angle}</div>}
                  {brief.risk && <div><span className="text-[10px] uppercase font-bold text-red-700 block">Watch for</span>{brief.risk}</div>}
                </div>
              ) : (
                <div className="text-xs text-slate-400 italic">No brief available.</div>
              )}
            </div>

            {/* Notes */}
            <div className="px-6 py-3 border-b border-slate-100">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Quick notes (auto-saved with the call outcome)…"
                rows={2}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-emerald-400 focus:outline-none"
              />
            </div>

            {/* Action row */}
            <div className="p-6 space-y-3 bg-slate-50">
              {!showOutcomes ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={dial}
                    disabled={!current.lead.phone}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white text-lg font-bold rounded-xl px-5 py-4 flex items-center justify-center gap-2"
                  >
                    Call {current.lead.phone || '(no phone)'}
                  </button>
                  <button onClick={skip} className="bg-slate-200 hover:bg-slate-300 text-slate-800 font-semibold rounded-xl px-5 py-4">
                    Skip ↺
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-600">After the call — pick one:</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <OutcomeButton k="1" label="✓ Appt Set"      tone="emerald" onClick={() => logOutcome('appt_set')}/>
                    <OutcomeButton k="2" label="Talked"        tone="emerald" onClick={() => logOutcome('pickup')}/>
                    <OutcomeButton k="3" label="Callback"      tone="amber"   onClick={() => logOutcome('callback')}/>
                    <OutcomeButton k="4" label="✕ Not Interested" tone="slate"   onClick={() => logOutcome('not_interested')}/>
                    <OutcomeButton k="5" label="Left VM"        tone="blue"    onClick={() => logOutcome('voicemail')}/>
                    <OutcomeButton k="6" label="—  No Answer"     tone="slate"   onClick={() => logOutcome('no_answer')}/>
                    <OutcomeButton k="7" label="DNC"            tone="red"     onClick={() => logOutcome('dnc')}/>
                    <button onClick={() => setShowOutcomes(false)} className="bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-lg px-3 py-3 hover:bg-slate-50">
                      ↶ Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Footer hint */}
            <div className="px-6 py-2 text-[11px] text-slate-500 bg-white border-t border-slate-100">
              <strong>Keyboard:</strong> C/space = call · S/N = skip · 1-7 = outcome · Esc = close
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function OutcomeButton({ k, label, tone, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`bg-${tone}-50 hover:bg-${tone}-100 border border-${tone}-200 text-${tone}-800 text-sm font-bold rounded-lg px-3 py-3 text-left`}
    >
      <span className="inline-block w-5 h-5 bg-white border border-slate-300 rounded text-[10px] text-slate-600 text-center mr-1 leading-5">{k}</span>
      {label}
    </button>
  );
}

// ════════════════════════════════════════════════════════════════════
// Retention Hub — keep 90% on the books
//
// The retention play, automated for a lazy agent. Lapse-risk scores
// every active client, surfaces the critical-risk + high-risk lists
// as the agent's daily worklist, tracks recently-lapsed for the
// reinstatement window, and gives one-click AI scripts + dial links.
//
// The underlying engine is already automated:
//   - Policy milestone workflows (30/90/180/365 day check-ins)
//   - Payment reminder workflow (3 days before draft)
//   - Chargeback Save 3-Touch Drip (fires on lapse, auto-enrolled)
//   - Win-Back 30/60/90 Drip (this ship, for clients who didn't
//     come back from the immediate save)
//
// This view is the operator's command center for everything the
// engine flags + the manual saves the engine can't make alone.
// ════════════════════════════════════════════════════════════════════

const REINSTATEMENT_WINDOW_DAYS = 60;

// Lapse risk score 0-100 for an active client. Higher = more at risk.
// Returns { score, tier, reasons, ... }.
function computeLapseRisk(client, ctx) {
  const { calls, outreach, transactions, leads } = ctx;
  const now = Date.now();
  const DAY = 1000 * 60 * 60 * 24;
  const reasons = [];
  let score = 0;

  const issueTs = parseDate(client.issueDate)?.getTime();
  const policyAgeDays = issueTs ? Math.floor((now - issueTs) / DAY) : null;

  // Last contact across calls + outreach for matched lead
  const matched = leads.find(l => String(l.name || '').toUpperCase().trim() === String(client.insured || '').toUpperCase().trim());
  let lastContactTs = 0;
  if (matched) {
    for (const c of calls || []) {
      if (c.leadId !== matched.id) continue;
      const ts = Date.parse(`${c.date || ''}T${c.startTime || '00:00'}:00`);
      if (Number.isFinite(ts)) lastContactTs = Math.max(lastContactTs, ts);
    }
    for (const o of outreach || []) {
      if (o.leadId !== matched.id) continue;
      const ts = Date.parse(o.createdAt || '');
      if (Number.isFinite(ts)) lastContactTs = Math.max(lastContactTs, ts);
    }
  }
  const daysSinceContact = lastContactTs ? Math.floor((now - lastContactTs) / DAY) : 999;

  // Recent chargebacks on this exact (insured, policy)
  const recentCb = (transactions || []).filter(tx => {
    if (String(tx.insured || '').toUpperCase().trim() !== String(client.insured || '').toUpperCase().trim()) return false;
    if (tx.policy !== client.policy) return false;
    if ((tx.netToAccount || 0) >= 0) return false;
    const t = parseDate(tx.activityDate)?.getTime();
    return t && (now - t) <= 90 * DAY;
  });

  if (recentCb.length > 0) {
    score += 30;
    reasons.push(`${recentCb.length} chargeback${recentCb.length === 1 ? '' : 's'} in last 90 days`);
  }
  if (daysSinceContact > 60) {
    score += 20;
    reasons.push(`no contact in ${daysSinceContact} days`);
  } else if (daysSinceContact > 30) {
    score += 10;
    reasons.push(`no contact in ${daysSinceContact} days`);
  }
  if (policyAgeDays != null && policyAgeDays < 30) {
    score += 25;
    reasons.push(`policy under 30 days (highest NSF window)`);
  } else if (policyAgeDays != null && policyAgeDays < 90) {
    score += 15;
    reasons.push(`in month 1-3 (early lapse window)`);
  }
  if (client.monthlyPremium > 80) {
    score += 10;
    reasons.push(`premium $${Math.round(client.monthlyPremium)} above $80 risk threshold`);
  }
  if (matched && matched.consent !== true) {
    score += 10;
    reasons.push('no TCPA consent on file (can\'t text reminders)');
  }
  if (!matched) {
    score += 5;
    reasons.push('not matched to a lead — no contact history visible');
  }

  return {
    score: Math.min(100, score),
    tier: score >= 76 ? 'critical' : score >= 51 ? 'high' : score >= 26 ? 'medium' : 'low',
    reasons,
    daysSinceContact: lastContactTs ? daysSinceContact : null,
    policyAgeDays,
    recentChargebacks: recentCb.length,
    matchedLeadId: matched?.id || null,
    matchedLeadPhone: matched?.phone || matched?.phone_e164 || null,
  };
}

// ════════════════════════════════════════════════════════════════════
// RETENTION & CHARGEBACK COMMAND CENTER — Phase 1.
//
// The owner's #1 competitive-moat page: one place that shows every
// life policy's retention/chargeback exposure with real numbers, and
// composes the existing retention surfaces (RetentionHub, RetentionView,
// WinbackView, ChargebacksView, PersistencyView, RetainAgentView) as
// tabs instead of re-implementing any of their money math. The top
// metrics strip is built ONLY from src/lib/lifeExposure.js — verbatim
// extractions of those components' own formulas (see that file's
// header) — plus the same `conservation` (paste/upload-ingested
// carrier report) state RetentionView already uses. No number here is
// re-derived independently.
//
// Guardian AI retention workflow + the cinematic 3D room are Phase
// 2/3 — this is the flat, real-data working page only.
// ════════════════════════════════════════════════════════════════════

const RCC_TABS = [
  ['overview',    'Overview'],
  ['at_risk',     'At Risk'],
  ['failed_drafts','Failed Drafts'],
  ['grace_period','Grace Period'],
  ['lapsed',      'Lapsed'],
  ['chargebacks', 'Chargebacks'],
  ['recovery',    'Recovery Campaigns'],
  ['saved',       'Saved Policies'],
  ['persistency', 'Persistency'],
  ['analysis',    'Carrier / Agent / Lead Source'],
  ['ai_activity', 'AI Activity'],
  ['reports',     'Reports'],
];

function RccMetricCard({ label, value, sub, tone = 'slate', onClick }) {
  const toneClass = {
    slate:   'border-white/10 text-white',
    emerald: 'border-emerald-400/30 text-emerald-300',
    amber:   'border-amber-400/30 text-amber-300',
    red:     'border-rose-400/30 text-rose-300',
    gold:    'border-[#C9A227]/40 text-[#C9A227]',
  }[tone] || 'border-white/10 text-white';
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-xl border ${toneClass} bg-black/30 backdrop-blur px-3 py-2.5 hover:bg-black/45 transition-colors min-w-0`}
      style={{ minWidth: 0 }}
    >
      <div className="text-[9px] uppercase tracking-wider font-bold text-slate-400 truncate">{label}</div>
      <div className="text-xl font-black tabular-nums mt-0.5 truncate">{value}</div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5 truncate">{sub}</div>}
    </button>
  );
}

function RetentionCommandCenterView({
  transactions = [], allTransactions = [], filteredTx = [], periodMode = 'month',
  conservation = [], setConservation = () => {},
  producers = {}, setProducers = () => {},
  settings = {}, clientProfiles = {}, setClientProfiles = () => {},
  calendar = [], setCalendar = () => {}, leadTemplates = [],
  leads = [], setLeads = () => {}, calls = [], outreach = [],
  policyTracking = {}, setPolicyTracking = () => {},
  clientNotes = {}, setClientNotes = () => {},
  currentUser = null, showToast = () => {}, openClientProfile = () => {},
  saveTasks = {}, setSaveTasks = () => {}, onGotoTab = () => {},
}) {
  const [subTab, setSubTab] = React.useState('overview');
  const today = React.useMemo(() => new Date(), []);

  // ── Top-metrics strip — ALL from src/lib/lifeExposure.js, zero
  // recomputation of any component's own money math. ──────────────
  const book       = React.useMemo(() => classifyLifeBook(allTransactions), [allTransactions]);
  const persist    = React.useMemo(() => computePersistencyWindows(allTransactions, [30, 60, 90, 180, 365], today), [allTransactions, today]);
  const cbGroup    = React.useMemo(() => groupChargebackPolicies(filteredTx.length ? filteredTx : allTransactions), [filteredTx, allTransactions]);
  const exposure   = React.useMemo(() => computeConservationExposure(conservation, allTransactions, today), [conservation, allTransactions, today]);

  const goTab = (t) => setSubTab(t);

  return (
    <div className="space-y-4">
      {/* Top metrics strip */}
      <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-black/50 to-black/20 backdrop-blur p-4">
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#C9A227]">Retention &amp; Chargeback Command</div>
            <div className="text-white text-sm mt-0.5">Every life policy's retention exposure, from real transactions and carrier conservation reports.</div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
          <RccMetricCard label="Active policies" value={book.activeCount} tone="emerald" onClick={() => goTab('at_risk')}/>
          <RccMetricCard label="Monthly premium on book" value={fmt(book.monthlyPremiumOnBook)} sub={`${fmt(book.annualPremiumOnBook)}/yr`} tone="gold" onClick={() => goTab('overview')}/>
          <RccMetricCard label="Advance outstanding" value={fmt(exposure.totalAdvancedOpen)} sub="open cases, advanced $" tone="amber" onClick={() => goTab('at_risk')}/>
          <RccMetricCard label="Revenue at risk" value={fmt(exposure.dollarsAtRisk)} sub="open + in chargeback window" tone="red" onClick={() => goTab('at_risk')}/>
          <RccMetricCard label="Failed drafts" value={exposure.failedDrafts.length} tone="red" onClick={() => goTab('failed_drafts')}/>
          <RccMetricCard label="Pending lapses" value={exposure.pendingLapses.length} tone="amber" onClick={() => goTab('grace_period')}/>
          <RccMetricCard label="Lapsed" value={book.lapsedCount} tone="red" onClick={() => goTab('lapsed')}/>
          <RccMetricCard label="Chargebacks this period" value={cbGroup.count} sub={fmt(cbGroup.totalLoss)} tone="red" onClick={() => goTab('chargebacks')}/>
          <RccMetricCard label="Policies saved" value={exposure.cured.length} tone="emerald" onClick={() => goTab('saved')}/>
          <RccMetricCard label="Recovery rate" value={exposure.recoveryRatePct == null ? '—' : `${exposure.recoveryRatePct.toFixed(0)}%`} sub="cured ÷ (cured+dismissed)" tone="emerald" onClick={() => goTab('recovery')}/>
          <RccMetricCard label="Persistency 30d" value={persist.windows[30]?.pct == null ? '—' : `${persist.windows[30].pct.toFixed(0)}%`} tone="slate" onClick={() => goTab('persistency')}/>
          <RccMetricCard label="Persistency 365d" value={persist.windows[365]?.pct == null ? '—' : `${persist.windows[365].pct.toFixed(0)}%`} tone="slate" onClick={() => goTab('persistency')}/>
        </div>
      </div>

      {/* 12-tab router */}
      <div className="rounded-xl border border-white/10 bg-black/30 backdrop-blur p-1 flex flex-wrap gap-1">
        {RCC_TABS.map(([id, label]) => (
          <button
            key={id}
            onClick={() => setSubTab(id)}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg whitespace-nowrap transition-colors ${
              subTab === id ? 'bg-[#C9A227] text-black' : 'text-slate-300 hover:bg-white/10'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="rounded-xl bg-white/95 backdrop-blur p-3 sm:p-4">
        {subTab === 'overview' && (
          <RetentionHub transactions={transactions} leads={leads} setLeads={setLeads} clientProfiles={clientProfiles} conservation={conservation} calls={calls} outreach={outreach} settings={settings} producers={producers} currentUser={currentUser} saveTasks={saveTasks} setSaveTasks={setSaveTasks} onGotoTab={onGotoTab}/>
        )}
        {subTab === 'at_risk' && (
          <RetentionView conservation={conservation} setConservation={setConservation} transactions={transactions} producers={producers} setProducers={setProducers} settings={settings} clientProfiles={clientProfiles} setClientProfiles={setClientProfiles} calendar={calendar} setCalendar={setCalendar} leadTemplates={leadTemplates} leads={leads} currentUser={currentUser} showToast={showToast} openClientProfile={openClientProfile}/>
        )}
        {subTab === 'failed_drafts' && (
          <ConservationEventMiniList
            title="Failed drafts (NSF)"
            subtitle="Open conservation events where the carrier reported a failed/NSF draft."
            events={exposure.failedDrafts}
            emptyMsg="No open failed-draft events. Import a carrier conservation report from the At Risk tab to populate this."
            openClientProfile={openClientProfile}
          />
        )}
        {subTab === 'grace_period' && (
          <ConservationEventMiniList
            title="Grace period + pending lapse"
            subtitle="Open events in a carrier grace period or flagged as a pending lapse warning."
            events={[...exposure.gracePeriod, ...exposure.pendingLapses]}
            emptyMsg="No open grace-period or lapse-warning events."
            openClientProfile={openClientProfile}
          />
        )}
        {subTab === 'lapsed' && (
          <WinbackView allTransactions={transactions} producers={producers} settings={settings} policyTracking={policyTracking} setPolicyTracking={setPolicyTracking} clientNotes={clientNotes} setClientNotes={setClientNotes} openClientProfile={openClientProfile}/>
        )}
        {subTab === 'chargebacks' && (
          <div className="space-y-4">
            <ChargebacksView transactions={filteredTx.length ? filteredTx : transactions} allTransactions={allTransactions} producers={producers} settings={settings} periodMode={periodMode} openClientProfile={openClientProfile}/>
            <ChargebackExplanationPanel currentUser={currentUser} openClientProfile={openClientProfile}/>
          </div>
        )}
        {subTab === 'recovery' && (
          <ConservationEventMiniList
            title="Recovery campaigns — in play"
            subtitle="Conservation events currently under active contact (status: contacted)."
            events={exposure.decorated.filter(e => e.status === 'contacted')}
            emptyMsg="No conservation cases currently under active contact."
            openClientProfile={openClientProfile}
          />
        )}
        {subTab === 'saved' && (
          <ConservationEventMiniList
            title="Saved policies"
            subtitle="Conservation events resolved as cured — the policy stayed on the books."
            events={exposure.cured}
            emptyMsg="No cured conservation events yet."
            openClientProfile={openClientProfile}
            tone="emerald"
          />
        )}
        {subTab === 'persistency' && (
          <PersistencyView transactions={transactions} producers={producers} settings={settings} leads={leads} calls={calls} outreach={outreach} openClientProfile={openClientProfile}/>
        )}
        {subTab === 'analysis' && (
          <RccAnalysisTab transactions={allTransactions} producers={producers} leads={leads}/>
        )}
        {subTab === 'ai_activity' && (
          <RetainAgentView currentUser={currentUser} showToast={showToast}/>
        )}
        {subTab === 'reports' && (
          <RccReportsTab book={book} persist={persist} cbGroup={cbGroup} exposure={exposure}/>
        )}
      </div>
    </div>
  );
}

// Compact real-data list for the tabs that don't have a dedicated
// existing component (Failed Drafts, Grace Period, Recovery
// Campaigns, Saved Policies). Reads from the SAME decorated
// conservation-events array computeConservationExposure already
// built — no separate data source, no new money math.
function ConservationEventMiniList({ title, subtitle, events = [], emptyMsg, openClientProfile = () => {}, tone = 'slate' }) {
  const toneBorder = tone === 'emerald' ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200';
  return (
    <div>
      <h3 className="font-semibold text-slate-900">{title}</h3>
      <p className="text-xs text-slate-500 mt-0.5 mb-3">{subtitle}</p>
      {events.length === 0 ? (
        <div className="text-sm text-slate-400 italic py-6 text-center border border-dashed border-slate-200 rounded-lg">{emptyMsg}</div>
      ) : (
        <div className="space-y-2">
          {events.slice(0, 50).map(ev => (
            <div key={ev.id} className={`border ${toneBorder} rounded-lg px-3 py-2 flex items-center gap-3 flex-wrap`}>
              <div className="flex-1 min-w-0">
                <button
                  onClick={() => ev.clientName && openClientProfile(ev.clientName)}
                  className="font-medium text-slate-800 hover:text-emerald-700 hover:underline text-left"
                  disabled={!ev.clientName}
                >
                  {ev.clientName || '(name not parsed)'}
                </button>
                <div className="text-xs text-slate-500 truncate">
                  {ev.carrier || 'Carrier'} · Policy {ev.policyNum || '—'} · {ev.eventTypeLabel || ev.eventType}
                  {ev.advancedPremium ? ` · ${fmt(ev.advancedPremium)} advanced` : ''}
                </div>
              </div>
              <div className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                {ev.status}
              </div>
              {ev.phone && (
                <button onClick={() => requestPhoneCall({ phone: ev.phone, name: ev.clientName })} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded px-2 py-1.5 inline-flex items-center gap-1">
                  <Phone size={12}/> Call
                </button>
              )}
            </div>
          ))}
          {events.length > 50 && <div className="text-xs text-slate-500 italic text-center pt-2">+{events.length - 50} more</div>}
        </div>
      )}
    </div>
  );
}

// Carrier / Agent / Lead-Source breakdown — built from the SAME
// groupChargebackPolicies() output as the top-metrics strip and the
// Chargebacks tab. Lead-source is a best-effort name join against
// `leads` (same normalizeInsuredName pattern as HardToCloseReport);
// unmatched rows are reported honestly rather than guessed.
function RccAnalysisTab({ transactions = [], producers = {}, leads = [] }) {
  const { policies } = React.useMemo(() => groupChargebackPolicies(transactions), [transactions]);

  const byCarrier = React.useMemo(() => {
    const m = {};
    policies.forEach(p => {
      const k = p.company || 'Unknown carrier';
      if (!m[k]) m[k] = { key: k, count: 0, totalLoss: 0 };
      m[k].count++; m[k].totalLoss += p.totalLoss;
    });
    return Object.values(m).sort((a, b) => a.totalLoss - b.totalLoss);
  }, [policies]);

  const byAgent = React.useMemo(() => {
    const m = {};
    policies.forEach(p => {
      const k = p.prodNum || 'unassigned';
      if (!m[k]) m[k] = { key: k, name: getProducerName(p.prodNum, producers), count: 0, totalLoss: 0 };
      m[k].count++; m[k].totalLoss += p.totalLoss;
    });
    return Object.values(m).sort((a, b) => a.totalLoss - b.totalLoss);
  }, [policies, producers]);

  const bySource = React.useMemo(() => {
    const leadByName = new Map();
    (leads || []).forEach(l => {
      const k = normalizeInsuredName(l.name);
      if (k && !leadByName.has(k)) leadByName.set(k, l);
    });
    const m = {};
    let unmatched = 0;
    policies.forEach(p => {
      const lead = leadByName.get(normalizeInsuredName(p.insured));
      if (!lead) { unmatched++; return; }
      const k = lead.source || 'Unknown source';
      if (!m[k]) m[k] = { key: k, count: 0, totalLoss: 0 };
      m[k].count++; m[k].totalLoss += p.totalLoss;
    });
    return { rows: Object.values(m).sort((a, b) => a.totalLoss - b.totalLoss), unmatched };
  }, [policies, leads]);

  const Table = ({ title, rows, nameKey = 'key' }) => (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <h4 className="font-semibold text-slate-900 text-sm mb-2">{title}</h4>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400 italic py-3 text-center">No chargeback data yet.</div>
      ) : (
        <table className="w-full text-xs">
          <thead><tr className="text-slate-500 border-b border-slate-100"><th className="text-left py-1.5">Name</th><th className="text-right py-1.5">Count</th><th className="text-right py-1.5">$ loss</th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.key} className="border-b border-slate-50 last:border-0">
                <td className="py-1.5 text-slate-800">{r.name || r[nameKey]}</td>
                <td className="py-1.5 text-right tabular-nums">{r.count}</td>
                <td className="py-1.5 text-right tabular-nums text-rose-700 font-semibold">{fmt(r.totalLoss)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">Chargeback breakdown by carrier, writing agent, and lead source — built from the same chargeback grouping as the Chargebacks tab.</p>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Table title="By carrier" rows={byCarrier}/>
        <Table title="By agent" rows={byAgent}/>
        <div>
          <Table title="By lead source" rows={bySource.rows}/>
          {bySource.unmatched > 0 && (
            <div className="text-[11px] text-slate-500 mt-1.5">{bySource.unmatched} chargeback{bySource.unmatched === 1 ? '' : 's'} couldn't be matched to a lead record (name didn't match) — excluded rather than guessed.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function downloadCsvFile(filename, rows) {
  const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function RccReportsTab({ book, persist, cbGroup, exposure }) {
  const rows = [
    ['Metric', 'Value'],
    ['Active policies', book.activeCount],
    ['Lapsed policies', book.lapsedCount],
    ['Monthly premium on book', book.monthlyPremiumOnBook.toFixed(2)],
    ['Annual premium on book', book.annualPremiumOnBook.toFixed(2)],
    ['Advance commission outstanding (open cases)', exposure.totalAdvancedOpen.toFixed(2)],
    ['Revenue at risk (open + in chargeback window)', exposure.dollarsAtRisk.toFixed(2)],
    ['Failed drafts (open)', exposure.failedDrafts.length],
    ['Pending lapses (open)', exposure.pendingLapses.length],
    ['Chargebacks this period', cbGroup.count],
    ['Chargeback $ loss this period', cbGroup.totalLoss.toFixed(2)],
    ['Policies saved (cured)', exposure.cured.length],
    ['Recovery rate %', exposure.recoveryRatePct == null ? '' : exposure.recoveryRatePct.toFixed(1)],
    ...[30, 60, 90, 180, 365].map(n => [`Persistency ${n}d %`, persist.windows[n]?.pct == null ? '' : persist.windows[n].pct.toFixed(1)]),
  ];
  return (
    <div>
      <h3 className="font-semibold text-slate-900">Reports</h3>
      <p className="text-xs text-slate-500 mt-0.5 mb-3">Every top-metric on this page in one exportable table. Numbers are the same ones shown above — nothing recomputed for the export.</p>
      <button
        onClick={() => downloadCsvFile(`retention-command-report-${new Date().toISOString().slice(0, 10)}.csv`, rows)}
        className="bg-slate-900 hover:bg-black text-white text-xs font-semibold px-3 py-2 rounded-lg mb-3"
      >
        Download CSV
      </button>
      <table className="w-full text-xs border border-slate-200 rounded-lg overflow-hidden">
        <tbody>
          {rows.slice(1).map(([label, value]) => (
            <tr key={label} className="border-b border-slate-100 last:border-0 odd:bg-slate-50/50">
              <td className="py-1.5 px-3 text-slate-600">{label}</td>
              <td className="py-1.5 px-3 text-right font-semibold text-slate-900 tabular-nums">{value === '' ? '—' : value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Chargeback explanation panel ───────────────────────────────────
// The NEW surface this phase adds: for every chargeback CASE
// (conservation_cases where issue_type='chargeback' — real table,
// RLS-scoped by owner_id), show three SEPARATE fields:
//   1. Carrier reason — event_description, verbatim from the carrier
//      report (same field ConservationCaseDetail already labels
//      "Carrier note (verbatim)").
//   2. Client-reported reason — the most recent human-authored (not
//      AI) note/call log on the case's conservation_actions timeline.
//      Labeled honestly as what it is: the agent's logged account of
//      what the client said, not a client-authored field.
//   3. AI root-cause — deriveChargebackRootCause() from
//      lifeExposure.js. HARD RULE enforced structurally: it can only
//      ever be a verbatim echo of #1/#2 or a keyword-matched label
//      whose keyword is present in #1/#2. If both are empty it must
//      say "Not determined" — never a guess.
function ChargebackExplanationPanel({ currentUser, openClientProfile = () => {} }) {
  const [cases, setCases] = React.useState(null);
  const [actionsByCase, setActionsByCase] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [openId, setOpenId] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (!currentUser?.id) { setLoading(false); return; }
      setLoading(true); setError(null);
      try {
        const { data: caseRows, error: caseErr } = await supabase
          .from('conservation_cases')
          .select('id, client_name, client_phone, carrier, policy_number, event_description, resolution_note, status, priority, amount_due, premium, created_at')
          .eq('issue_type', 'chargeback')
          .order('created_at', { ascending: false })
          .limit(50);
        if (caseErr) throw caseErr;
        if (!alive) return;
        const rows = caseRows || [];
        setCases(rows);
        if (rows.length > 0) {
          const { data: actionRows } = await supabase
            .from('conservation_actions')
            .select('id, case_id, kind, message, is_ai, created_at')
            .in('case_id', rows.map(r => r.id))
            .in('kind', ['note', 'call'])
            .order('created_at', { ascending: false });
          if (!alive) return;
          const byCase = {};
          (actionRows || []).forEach(a => {
            if (a.is_ai) return; // client-reported reason must be human-logged
            if (!a.message || !a.message.trim()) return;
            if (!byCase[a.case_id]) byCase[a.case_id] = a.message.trim(); // first = most recent (desc order)
          });
          setActionsByCase(byCase);
        }
      } catch (err) {
        setError(err?.message || 'load_failed');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [currentUser?.id]);

  if (loading) return <div className="text-xs text-slate-500 py-3">Loading chargeback case explanations…</div>;
  if (error) return <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Chargeback case explanations unavailable ({error}). The conservation_cases table may not have any chargeback-issue rows yet.</div>;
  if (!cases || cases.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <h4 className="font-semibold text-slate-900 text-sm">Chargeback case explanations</h4>
        <p className="text-xs text-slate-500 mt-2">No conservation_cases rows with issue_type=&quot;chargeback&quot; yet. This populates once a carrier conservation report with chargeback events has been parsed via Conservation → parse-report / commit-cases.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <h4 className="font-semibold text-slate-900 text-sm">Chargeback case explanations</h4>
      <p className="text-xs text-slate-500 mt-0.5 mb-3">Carrier reason, client-reported reason, and AI root-cause — shown separately. The AI root-cause never asserts anything the other two don't support.</p>
      <div className="space-y-2">
        {cases.map(c => {
          const carrierReason = c.event_description || '';
          const clientReason = actionsByCase[c.id] || c.resolution_note || '';
          const rootCause = deriveChargebackRootCause({ carrierReason, clientReason });
          const isOpen = openId === c.id;
          return (
            <div key={c.id} className="border border-slate-200 rounded-lg">
              <button onClick={() => setOpenId(isOpen ? null : c.id)} className="w-full text-left px-3 py-2 flex items-center gap-3 flex-wrap hover:bg-slate-50">
                <span className="font-medium text-slate-800 flex-1 min-w-0 truncate">{c.client_name || '(unnamed)'}</span>
                <span className="text-xs text-slate-500">{c.carrier || '—'} · {c.policy_number || '—'}</span>
                <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${rootCause.determined ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'}`}>
                  {rootCause.determined ? 'root cause found' : 'not determined'}
                </span>
              </button>
              {isOpen && (
                <div className="px-3 pb-3 pt-1 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">
                    <div className="text-[9px] uppercase tracking-wider font-bold text-slate-500 mb-1">Carrier reason (verbatim)</div>
                    <div className="text-slate-800 italic">{carrierReason || '— none on file —'}</div>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">
                    <div className="text-[9px] uppercase tracking-wider font-bold text-slate-500 mb-1">Client-reported reason (agent's logged note)</div>
                    <div className="text-slate-800 italic">{clientReason || '— none logged —'}</div>
                  </div>
                  <div className={`border rounded-lg p-2 ${rootCause.determined ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="text-[9px] uppercase tracking-wider font-bold text-slate-500 mb-1">AI root-cause analysis</div>
                    <div className="text-slate-800">{rootCause.text}</div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RetentionHub({ transactions = [], leads = [], setLeads, clientProfiles = {}, conservation = [], calls = [], outreach = [], settings = {}, producers = {}, currentUser = null, saveTasks = {}, setSaveTasks = () => {}, onGotoTab = () => {} }) {
  const [busyKey, setBusyKey] = useState(null);

  // Build active clients + lapsed clients from transactions. Same
  // grouping logic the Persistency view uses: one row per
  // (prodNum, mga, policy). A policy with any chargeback OR
  // negative net commission counts as lapsed.
  const { activeClients, lapsedClients } = React.useMemo(() => {
    const policies = {};
    for (const tx of (transactions || [])) {
      if (!tx.policy) continue;
      const k = `${tx.prodNum || ''}|${tx.mga || ''}|${tx.policy}`;
      if (!policies[k]) policies[k] = {
        key: k,
        prodNum: tx.prodNum,
        policy: tx.policy,
        insured: tx.insured || '',
        carrier: (typeof deriveCarrier === 'function' ? deriveCarrier(tx) : (tx.carrier || '')),
        issueDate: tx.issueDate || '',
        firstLapseDate: '',
        chargebackCount: 0,
        totalCommission: 0,
        monthlyPremium: 0,
      };
      const p = policies[k];
      if (!p.insured && tx.insured) p.insured = tx.insured;
      if (!p.issueDate && tx.issueDate) p.issueDate = tx.issueDate;
      if (tx.commPremium && Math.abs(tx.commPremium) > p.monthlyPremium) p.monthlyPremium = Math.abs(tx.commPremium);
      p.totalCommission += Number(tx.netToAccount) || 0;
      const isCb = (typeof CHARGEBACK_TYPES !== 'undefined' && CHARGEBACK_TYPES?.has?.(tx.activityType))
                || ((tx.netToAccount || 0) < 0);
      if (isCb && tx.activityDate) {
        p.chargebackCount++;
        if (!p.firstLapseDate || (parseDate(tx.activityDate)?.getTime() || 0) < (parseDate(p.firstLapseDate)?.getTime() || 0)) {
          p.firstLapseDate = tx.activityDate;
        }
      }
    }
    const all = Object.values(policies);
    const lapsed = all.filter(p => p.chargebackCount > 0 || p.totalCommission < 0);
    const active = all.filter(p => p.chargebackCount === 0 && p.totalCommission >= 0);
    return { activeClients: active, lapsedClients: lapsed };
  }, [transactions]);

  // Score every active client.
  const scored = React.useMemo(() => {
    const ctx = { calls, outreach, transactions, leads };
    return activeClients
      .map(c => ({ ...c, risk: computeLapseRisk(c, ctx) }))
      .sort((a, b) => b.risk.score - a.risk.score);
  }, [activeClients, calls, outreach, transactions, leads]);

  const critical = scored.filter(c => c.risk.tier === 'critical');
  const high     = scored.filter(c => c.risk.tier === 'high');
  const medium   = scored.filter(c => c.risk.tier === 'medium');
  const low      = scored.filter(c => c.risk.tier === 'low');

  // Recently lapsed = last 90 days, sorted by lapse date desc. Mark
  // each with the reinstatement-window remaining.
  const recentLapsed = React.useMemo(() => {
    const now = Date.now();
    const DAY = 1000 * 60 * 60 * 24;
    return lapsedClients
      .map(c => {
        const lapseTs = parseDate(c.firstLapseDate)?.getTime();
        if (!lapseTs || (now - lapseTs) > 90 * DAY) return null;
        const daysSinceLapse = Math.floor((now - lapseTs) / DAY);
        const reinstateLeft  = Math.max(0, REINSTATEMENT_WINDOW_DAYS - daysSinceLapse);
        const matched = leads.find(l => String(l.name || '').toUpperCase().trim() === String(c.insured || '').toUpperCase().trim());
        return { ...c, daysSinceLapse, reinstateLeft, matched };
      })
      .filter(Boolean)
      .sort((a, b) => a.daysSinceLapse - b.daysSinceLapse);
  }, [lapsedClients, leads]);

  // Persistency snapshot — % of total written that are still active
  const total = activeClients.length + lapsedClients.length;
  const persistencyRate = total > 0 ? (activeClients.length / total) * 100 : 0;

  // AI script regenerator helper for at-risk clients
  const taskKey = (c) => `risk_${c.key}`;
  const taskOf = (c) => saveTasks[taskKey(c)] || {};
  const setTask = (c, patch) => setSaveTasks({ ...saveTasks, [taskKey(c)]: { ...taskOf(c), ...patch, updatedAt: new Date().toISOString() } });

  async function regenRetentionScript(c) {
    setBusyKey(taskKey(c));
    try {
      const result = await generateRetentionCheckIn({
        clientName: c.insured,
        carrier: c.carrier,
        monthlyPremium: c.monthlyPremium,
        policyAgeDays: c.risk.policyAgeDays || 0,
        daysSinceContact: c.risk.daysSinceContact || 0,
        riskReasons: c.risk.reasons,
        agentName: settings.ownerName || 'your agent',
      });
      setTask(c, { aiScript: result });
    } catch (err) {
      setTask(c, { aiScript: { error: err.message } });
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-900 via-emerald-700 to-slate-900 text-white rounded-2xl p-6">
        <div className="text-emerald-200 text-xs font-semibold uppercase tracking-wider">Retention Hub</div>
        <h2 className="text-3xl font-bold mt-1">Keep 90% on the books</h2>
        <p className="text-emerald-100 text-sm mt-2 max-w-3xl">
          Every active policy scored for lapse risk. Critical-risk clients surface as today's worklist.
          The Chargeback Save 3-Touch + Win-Back 30/60/90 + Payment Reminder workflows handle the long tail —
          this is where you make the calls the engine can't.
        </p>
      </div>

      {/* Three big tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <SaveStat label="13-mo Persistency" value={`${persistencyRate.toFixed(0)}%`} tone={persistencyRate >= 85 ? 'emerald' : persistencyRate >= 70 ? 'amber' : 'red'}/>
        <SaveStat label="Critical risk · act now" value={critical.length} tone={critical.length > 0 ? 'red' : 'emerald'}/>
        <SaveStat label="High risk" value={high.length} tone={high.length > 0 ? 'amber' : 'emerald'}/>
        <SaveStat label="Lapsed last 90d" value={recentLapsed.length} tone={recentLapsed.length > 0 ? 'red' : 'emerald'}/>
      </div>

      {/* Critical risk */}
      <RiskSection
        title="Critical risk — act today"
        subtitle="Score 76-100. Call these now; the engine can't save without a human voice."
        tone="red"
        clients={critical}
        emptyMsg="No critical-risk clients. The engine is keeping up."
        taskOf={taskOf}
        setTask={setTask}
        regenRetentionScript={regenRetentionScript}
        busyKey={busyKey}
      />

      {/* High risk */}
      <RiskSection
        title="⚠ High risk — intervene this week"
        subtitle="Score 51-75. Send a check-in text or schedule a call."
        tone="amber"
        clients={high}
        emptyMsg="No high-risk clients."
        taskOf={taskOf}
        setTask={setTask}
        regenRetentionScript={regenRetentionScript}
        busyKey={busyKey}
      />

      {/* Recently lapsed — win-back / reinstatement */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="font-semibold text-slate-900">Recently lapsed — reinstatement window + win-back</h3>
        <p className="text-xs text-slate-500 mt-0.5 mb-3">
          {recentLapsed.length} lapsed in the last 90 days. Reinstatement window is {REINSTATEMENT_WINDOW_DAYS} days (default; carrier-specific).
          Inside the window = call to reinstate. Outside = enroll in Win-Back.
        </p>
        {recentLapsed.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-6 text-center">No lapses in the last 90 days.</div>
        ) : (
          <div className="space-y-2">
            {recentLapsed.slice(0, 15).map(c => {
              const phone = c.matched?.phone || c.matched?.phone_e164;
              const inWindow = c.reinstateLeft > 0;
              return (
                <div key={c.key} className={`border ${inWindow ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200'} rounded-lg px-3 py-2 flex items-center gap-3 flex-wrap`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800">{titleCase ? titleCase(c.insured) : c.insured}</div>
                    <div className="text-xs text-slate-500">
                      {c.carrier} · Policy {c.policy || '—'} · {c.monthlyPremium ? `${fmt(c.monthlyPremium)}/mo` : 'premium ?'} · lapsed {c.daysSinceLapse}d ago
                    </div>
                  </div>
                  <div className="text-right">
                    {inWindow ? (
                      <div className="text-xs font-semibold text-amber-700">
                        {c.reinstateLeft}d reinstate window
                      </div>
                    ) : (
                      <div className="text-xs text-slate-500">past reinstate window</div>
                    )}
                  </div>
                  {phone && (
                    <button onClick={() => requestPhoneCall({ phone, name: c.insured })} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded px-2 py-1.5 inline-flex items-center gap-1">
                      <Phone size={12}/> Call
                    </button>
                  )}
                </div>
              );
            })}
            {recentLapsed.length > 15 && (
              <div className="text-xs text-slate-500 italic text-center pt-2">+{recentLapsed.length - 15} more</div>
            )}
          </div>
        )}
      </div>

      {/* Active book tier breakdown */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="font-semibold text-slate-900 mb-3">Active book by risk tier</h3>
        {[
          ['Low (0-25)',       low.length,      'emerald'],
          ['Medium (26-50)',   medium.length,   'amber'],
          ['High (51-75)',     high.length,     'orange'],
          ['Critical (76+)',   critical.length, 'red'],
        ].map(([label, count, tone]) => {
          const pct = activeClients.length > 0 ? (count / activeClients.length) * 100 : 0;
          return (
            <div key={label} className="mb-2 last:mb-0">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className={`font-semibold text-${tone}-700`}>{label}</span>
                <span className="tabular-nums text-slate-600">{count} clients · {pct.toFixed(0)}%</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full bg-${tone}-500`} style={{ width: `${pct}%` }}/>
              </div>
            </div>
          );
        })}
        <div className="text-xs text-slate-500 mt-3">
          Across {activeClients.length} active policies. Goal: keep critical + high under 10% of book.
        </div>
      </div>

      {/* Footer rule */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-900">
        <strong>The retention rule:</strong> The engine handles every milestone + payment + chargeback + win-back text automatically.
        Your job is to call the ~10 critical-risk clients per week and reinstate the lapsed within {REINSTATEMENT_WINDOW_DAYS} days.
        Do that and 90% persistency is arithmetic.
      </div>

      {/* Deep links to the older absorbed views — same retention domain,
          different data sources. Kept reachable but tucked at the bottom so
          they don't compete with the daily worklist above. */}
      <div className="flex flex-wrap gap-2 text-xs">
        <button onClick={() => onGotoTab('conservation_old')} className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg px-3 py-1.5 font-semibold">
          Carrier Conservation Reports →
        </button>
        <button onClick={() => onGotoTab('chargebacks')} className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg px-3 py-1.5 font-semibold">
          Chargeback breakdown →
        </button>
        <button onClick={() => onGotoTab('winback')} className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg px-3 py-1.5 font-semibold">
          Legacy Winback list →
        </button>
      </div>
    </div>
  );
}

function RiskSection({ title, subtitle, tone, clients, emptyMsg, taskOf, setTask, regenRetentionScript, busyKey }) {
  if (clients.length === 0) {
    return (
      <div className={`bg-white border border-slate-200 rounded-xl p-5`}>
        <h3 className="font-semibold text-slate-900">{title}</h3>
        <p className="text-xs text-slate-500 mt-0.5 mb-2">{subtitle}</p>
        <div className="text-sm text-emerald-700 italic py-4 text-center bg-emerald-50 rounded">{emptyMsg}</div>
      </div>
    );
  }
  return (
    <div className={`bg-white border-2 border-${tone}-200 rounded-xl p-5`}>
      <h3 className="font-semibold text-slate-900">{title}</h3>
      <p className="text-xs text-slate-500 mt-0.5 mb-3">{subtitle}</p>
      <div className="space-y-3">
        {clients.slice(0, 10).map(c => {
          const t = taskOf(c);
          const isBusy = busyKey === `risk_${c.key}`;
          const phone = c.risk.matchedLeadPhone;
          return (
            <div key={c.key} className={`border border-${tone}-200 rounded-lg overflow-hidden`}>
              <div className="px-3 py-2 flex items-start justify-between gap-3 flex-wrap bg-white">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-slate-900">{titleCase ? titleCase(c.insured) : c.insured}</div>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-${tone}-100 text-${tone}-700`}>
                      Risk {c.risk.score}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {c.carrier && <>{c.carrier} · </>}
                    Policy {c.policy || '—'} ·{' '}
                    {c.monthlyPremium ? `${fmt(c.monthlyPremium)}/mo` : 'premium ?'} ·{' '}
                    {c.risk.policyAgeDays != null ? `${c.risk.policyAgeDays}d on book` : 'age ?'} ·{' '}
                    {c.risk.daysSinceContact != null ? `last contact ${c.risk.daysSinceContact}d` : 'never contacted'}
                  </div>
                  <div className="text-[11px] text-slate-600 mt-1 italic">
                    Risk factors: {c.risk.reasons.slice(0, 3).join(' · ') || '—'}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {phone && (
                    <button onClick={() => requestPhoneCall({ phone, name: c.insured })} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded px-2 py-1.5 inline-flex items-center gap-1">
                      <Phone size={12}/> Call
                    </button>
                  )}
                </div>
              </div>
              {/* AI retention check-in */}
              <div className="px-3 py-2 border-t border-slate-100 bg-slate-50">
                {t.aiScript && !t.aiScript.error ? (
                  <div className="space-y-1.5 text-sm">
                    {t.aiScript.smsBody && (
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 block">SMS body</span>
                        {t.aiScript.smsBody}
                      </div>
                    )}
                    {t.aiScript.callAngle && (
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 block">Call angle</span>
                        {t.aiScript.callAngle}
                      </div>
                    )}
                    {t.aiScript.watchFor && (
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 block">⚠ Watch for</span>
                        {t.aiScript.watchFor}
                      </div>
                    )}
                    <button onClick={() => regenRetentionScript(c)} disabled={isBusy} className="text-xs text-slate-500 hover:text-slate-700 underline mt-1">
                      {isBusy ? 'regenerating…' : 'regenerate'}
                    </button>
                  </div>
                ) : t.aiScript?.error ? (
                  <div className="text-xs text-red-600">
                    AI script failed: {t.aiScript.error}
                    <button onClick={() => regenRetentionScript(c)} className="ml-2 underline">retry</button>
                  </div>
                ) : (
                  <button onClick={() => regenRetentionScript(c)} disabled={isBusy} className="bg-slate-900 hover:bg-slate-700 text-white text-xs font-semibold px-3 py-1.5 rounded disabled:opacity-50">
                    {isBusy ? 'generating…' : 'Generate retention check-in'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {clients.length > 10 && (
          <div className="text-xs text-slate-500 italic text-center pt-2">+{clients.length - 10} more at this risk tier</div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// SMS Inbox — every conversation with every lead in one place.
//
// Two-pane (desktop) / stack (mobile) layout:
//   LEFT  : conversation list, sorted by last message desc, with
//           unread dots, filter pills (All/Unread/Replied), search
//   RIGHT : selected conversation thread + composer + lead actions
//
// Data:
//   - Polls public.messages (RLS-scoped to operator) every 20s
//   - Joins to local leads array by lead_id for the lead's real name
//   - Tracks per-lead lastReadAt in K.inboxReadState
//
// Sending:
//   - POSTs to /api/sms/send-manual which validates session JWT,
//     enforces consent + opt-out + phone gates, calls Twilio (gated
//     by SMS_SEND_ENABLED), persists the outbound message row,
//     returns the SID. Optimistically renders the sent bubble.
//
// Features:
//   - "AI" → calls generateFollowUpDraft for a suggested reply
//   - One-click Call (tel: link)
//   - One-click Hot toggle (adds Hot tag to lead)
//   - "profile" link opens the client profile drawer
//   - Composer: Enter to send, Shift+Enter for newline
//   - Compliance warnings inline if lead has no consent or opted out
// ════════════════════════════════════════════════════════════════════

// PERF FIX 2026-07-14: was 20_000 (3 requests/min per open tab). Raised
// to 120s as a safety-net poll — Realtime subscription on `messages`
// via the AI activity view + Ops Floor delivers events without the poll.
// If Realtime is confirmed live-pushing everywhere, this can drop
// further or become event-driven-only.
const INBOX_POLL_MS = 120_000;

// ── Bulk SMS modal ─────────────────────────────────────────────
// One body → many recipients. Replaces the old "mass SMS lives in
// Leads > SMS Engine while responses land in the Inbox" split that
// forced the high-volume operator to bounce between two surfaces.
// Defaults to every conversation matching the current inbox filter
// (so "send to all the hot replied prospects" is two clicks: filter
// to Hot, open Bulk, hit Send). Sequential sends through the existing
// /api/sms/send-manual so consent + quiet hours + A2P gating runs
// per recipient — no bulk bypass.
function BulkSmsModal({ initialConversations = [], onClose, onSent }) {
  const [selected, setSelected]     = React.useState(() => new Set((initialConversations || []).map(c => String(c.leadId))));
  const [body, setBody]             = React.useState('');
  const [sending, setSending]       = React.useState(false);
  const [progress, setProgress]     = React.useState({ done: 0, ok: 0, failed: 0 });
  const [results, setResults]       = React.useState(null);

  const recipients = (initialConversations || []).filter(c => selected.has(String(c.leadId)));
  const canSend    = !sending && body.trim().length > 0 && recipients.length > 0;

  function toggle(leadId) {
    setSelected(prev => {
      const next = new Set(prev);
      const k = String(leadId);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  }
  function selectAll()  { setSelected(new Set((initialConversations || []).map(c => String(c.leadId)))); }
  function selectNone() { setSelected(new Set()); }

  async function send() {
    if (!canSend) return;
    setSending(true);
    setResults(null);
    setProgress({ done: 0, ok: 0, failed: 0 });
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { setSending(false); alert('Not signed in.'); return; }
    const out = [];
    for (const c of recipients) {
      try {
        const res = await fetch('/api/sms/send-manual', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body:    JSON.stringify({ leadId: c.leadId, body: body.trim() }),
        });
        const j = await res.json().catch(() => ({}));
        const okSent = res.ok;
        out.push({ leadId: c.leadId, name: c.lead?.name || c.leadId, ok: okSent, status: res.status, detail: j?.error || j?.detail || null });
        setProgress(p => ({ done: p.done + 1, ok: p.ok + (okSent ? 1 : 0), failed: p.failed + (okSent ? 0 : 1) }));
      } catch (err) {
        out.push({ leadId: c.leadId, name: c.lead?.name || c.leadId, ok: false, status: 0, detail: String(err.message || err) });
        setProgress(p => ({ done: p.done + 1, ok: p.ok, failed: p.failed + 1 }));
      }
    }
    setResults(out);
    setSending(false);
    onSent && onSent(out);
  }

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-lg font-bold text-slate-900">📣 Bulk message</div>
            <div className="text-xs text-slate-500">One SMS, many recipients. Each send runs through SHIELD (consent + quiet hours + A2P) per recipient.</div>
          </div>
          <button aria-label="Close" onClick={onClose} disabled={sending} className="text-slate-400 hover:text-slate-900 disabled:opacity-30 text-xl leading-none">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Composer */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Message body</label>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              disabled={sending}
              placeholder="Hi {first}, quick follow-up — …"
              className="w-full min-h-[100px] border border-slate-300 rounded-lg px-3 py-2 text-sm leading-relaxed focus:outline-none focus:border-emerald-500 disabled:bg-slate-50"
            />
            <div className="text-[11px] text-slate-500 mt-1 flex justify-between gap-2 flex-wrap">
              <span>{body.length} chars · {Math.ceil(body.length / 160) || 1} segment{Math.ceil(body.length / 160) > 1 ? 's' : ''}</span>
              <span className="text-slate-400">Personalize with {'{first}'} / {'{agent}'} / {'{state}'} where the lead has the data.</span>
            </div>
          </div>

          {/* Recipients */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold text-slate-700">Recipients ({recipients.length} / {initialConversations.length})</label>
              <div className="flex gap-2">
                <button onClick={selectAll}  disabled={sending} className="text-[11px] text-slate-600 hover:text-slate-900 underline disabled:opacity-30">All</button>
                <button onClick={selectNone} disabled={sending} className="text-[11px] text-slate-600 hover:text-slate-900 underline disabled:opacity-30">None</button>
              </div>
            </div>
            <div className="border border-slate-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-slate-100">
              {(initialConversations || []).length === 0 ? (
                <div className="p-4 text-xs text-slate-500 text-center">No conversations in the current filter.</div>
              ) : (initialConversations || []).map(c => {
                const isSel = selected.has(String(c.leadId));
                return (
                  <label key={c.leadId} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggle(c.leadId)}
                      disabled={sending}
                      className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{c.lead?.name || `Lead ${String(c.leadId).slice(0, 8)}`}</div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {c.lead?.phone || 'no phone'}
                        {c.lead?.status && <> · {c.lead.status}</>}
                        {c.unreadCount > 0 && <> · <span className="text-red-700 font-bold">{c.unreadCount} unread</span></>}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Results */}
          {sending && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700">
              Sending… <strong>{progress.done}</strong> / {recipients.length} ({progress.ok} ok · {progress.failed} failed)
            </div>
          )}
          {results && !sending && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs text-emerald-900 space-y-1">
              <div><strong>Done.</strong> {progress.ok} sent · {progress.failed} failed.</div>
              {progress.failed > 0 && (
                <details className="text-emerald-800">
                  <summary className="cursor-pointer">Show failures</summary>
                  <ul className="mt-1 list-disc list-inside space-y-0.5">
                    {results.filter(r => !r.ok).slice(0, 30).map(r => (
                      <li key={r.leadId}><strong>{r.name}</strong> · {r.status || 'err'} · {r.detail || 'failed'}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button aria-label="Close" onClick={onClose} disabled={sending} className="text-sm text-slate-600 hover:text-slate-900 disabled:opacity-30 px-3 py-1.5">{results ? 'Close' : 'Cancel'}</button>
          {!results && (
            <button
              onClick={send}
              disabled={!canSend}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white font-bold text-sm px-4 py-2 rounded-lg"
            >
              {sending ? `Sending… (${progress.done}/${recipients.length})` : `Send to ${recipients.length}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Inbox smart-filter + lead-context helpers ──────────────────
// Used by SmsInboxView's filter chips, conversation row chips, and
// the inline action bar in the thread header. Pure functions so
// they're easy to unit-test if we ever extract them.
function isHotLead(lead) {
  if (!lead) return false;
  if (lead.status === 'hot') return true;
  const tags = Array.isArray(lead.tags) ? lead.tags : [];
  if (tags.some(t => /\bhot\b/i.test(String(t)))) return true;
  if (Number(lead.score) >= 8) return true;
  return false;
}
function needsFollowUp(conv) {
  if (!conv || !conv.last) return false;
  // Last message is INBOUND and older than 24 hours → waiting on us.
  if (conv.last.direction !== 'in') return false;
  const t = Date.parse(conv.last.sent_at || '');
  if (!Number.isFinite(t)) return false;
  return (Date.now() - t) > 24 * 3600_000;
}
// Tailwind color tokens per lead status — same family the kanban uses.
const STATUS_PILL = {
  new:           'bg-sky-100 text-sky-800 border-sky-200',
  contacted:     'bg-sky-100 text-sky-800 border-sky-200',
  qualified:     'bg-violet-100 text-violet-800 border-violet-200',
  appt_set:      'bg-amber-100 text-amber-800 border-amber-200',
  showed:        'bg-amber-100 text-amber-800 border-amber-200',
  quoted:        'bg-emerald-100 text-emerald-800 border-emerald-200',
  pending_uw:    'bg-violet-100 text-violet-800 border-violet-200',
  closed_won:    'bg-emerald-100 text-emerald-800 border-emerald-200',
  closed:        'bg-slate-100 text-slate-700 border-slate-200',
  not_interested:'bg-slate-100 text-slate-700 border-slate-200',
  no_show:       'bg-red-100 text-red-800 border-red-200',
  dnc:           'bg-red-100 text-red-800 border-red-200',
  hot:           'bg-red-100 text-red-800 border-red-200',
};

function SmsInboxView({ leads = [], setLeads, calls = [], settings = {}, currentUser = null, inboxReadState = {}, setInboxReadState = () => {}, onGotoTab = () => {}, onOpenLeadProfile = () => {}, isDemo = false }) {
  const [messages, setMessages] = useState(null);
  const [error, setError] = useState(null);
  // Inbox-selected conversation persists to URL hash (#lead=...) so
  // refreshing on an open thread restores it. Not localStorage —
  // selection is a "current view" thing, not a saved preference, and
  // the URL is the right surface (operator can share a deep-link to
  // a specific conversation with another teammate).
  const [selectedLeadId, setSelectedLeadId] = usePersistentState('lead', null, {
    persistToLocalStorage: false,
    validate: (v) => (typeof v === 'string' && v.length > 0 && v.length < 128) ? v : null,
  });
  const [filter, setFilter] = useState('all');
  // Vertical filter — single inbox for life + health (operator ask
  // 2026-06-26: "Agents who are doing both life and health only need
  // to be reaching out to clients in 1 inbox … need to be able to
  // filter life and health, AI Agents needs to know the different,
  // agent should be able to tag the client if they are life or
  // health"). Values: 'all' | 'life' | 'health' | 'untagged'.
  // Persisted to localStorage so the next session lands on the same
  // filter the operator left on.
  const [ibVertical, setIbVerticalRaw] = useState(() => {
    try { return localStorage.getItem('inbox.verticalFilter') || 'all'; } catch { return 'all'; }
  });
  const setIbVertical = (v) => {
    setIbVerticalRaw(v);
    try { localStorage.setItem('inbox.verticalFilter', v); } catch {}
  };
  const [search, setSearch] = useState('');
  const [composer, setComposer] = useState('');
  const [sending, setSending] = useState(false);
  const [suggestBusy, setSuggestBusy] = useState(false);
  // Dark-theme inbox — matches the operator-provided mock literally.
  // Theme picker lets the operator choose the bubble colors; persisted
  // to localStorage per-user so the choice sticks across sessions.
  const [themePopOpen, setThemePopOpen] = useState(false);
  const [composerChannel, setComposerChannel] = useState('sms'); // 'sms' | 'email' | 'template'
  const [msgColor, setMsgColor] = useState(() => {
    try {
      const raw = localStorage.getItem('inbox.msgColor');
      if (raw) return JSON.parse(raw);
    } catch {}
    return { you: '#0A84FF', ai: '#7C5CFF' };
  });
  React.useEffect(() => {
    try { localStorage.setItem('inbox.msgColor', JSON.stringify(msgColor)); } catch {}
  }, [msgColor]);
  const darkenHex = (hex) => {
    const n = parseInt(String(hex || '#0A84FF').slice(1), 16);
    const r = Math.round(((n >> 16) & 255) * 0.8);
    const g = Math.round(((n >>  8) & 255) * 0.8);
    const b = Math.round((n & 255) * 0.8);
    return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
  };
  const lightenHex = (hex) => {
    const n = parseInt(String(hex || '#0A84FF').slice(1), 16);
    const r = Math.round(((n >> 16) & 255) + (255 - ((n >> 16) & 255)) * 0.45);
    const g = Math.round(((n >>  8) & 255) + (255 - ((n >>  8) & 255)) * 0.45);
    const b = Math.round((n & 255) + (255 - (n & 255)) * 0.45);
    return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
  };
  // composeIntent — null = closed, 'text' = open the SMS composer,
  // 'call' = open the contact-picker for dialing. Operator ask
  // 2026-06-27: "need to be a drop down on which contact you want to
  // call when you select new." Replaced the single "+ New" button
  // with a dropdown menu (📞 Call / 💬 Text) that opens the same
  // contact picker in the right intent.
  const [composeIntent, setComposeIntent] = useState(null);
  const [composeMenuOpen, setComposeMenuOpen] = useState(false);

  // Tag a lead with a vertical. AI agents (RETAIN, ATLAS, NOVA) and
  // the analytics splits already key off lead.vertical / line_of_business
  // — once tagged, every downstream surface respects the tag.
  const tagLeadVertical = React.useCallback((leadId, vertical) => {
    if (!setLeads) return;
    setLeads(prev => prev.map(l => String(l.id) === String(leadId) ? { ...l, vertical } : l));
  }, [setLeads]);
  // Bulk-message modal — operator opens it from the inbox header,
  // picks N recipients (defaults to every conversation matching the
  // current filter), writes one body, sends to all. Replaces the old
  // split workflow where mass SMS lived in Leads > SMS Engine and
  // responses landed in the Inbox.
  const [bulkOpen, setBulkOpen] = useState(false);
  // Cursor for incremental fetch. Initial load grabs the last 1000
  // rows; subsequent polls only pull messages with sent_at >= cursor,
  // then merge by id. This drops the 20s poll from 1000 rows/tick to
  // typically zero rows on a quiet inbox and only the new ones when
  // a reply lands. Cuts Supabase row-read quota by ~100x at scale.
  const cursorRef = React.useRef(null);

  const fetchMessages = React.useCallback(async () => {
    // Demo Mode: synthesize the flat `messages` list from
    // seed.inbox_threads instead of polling Supabase. The view's
    // grouping + sorting logic runs unchanged on top.
    if (isDemo) {
      try {
        const mod = await import('./demo/liveAdapters');
        const flat = mod.flattenSeedMessages();
        setMessages(flat);
        setError(null);
      } catch (e) {
        setError(e?.message || String(e));
      }
      return;
    }
    try {
      const isInitial = cursorRef.current == null;
      let q = supabase
        .from('messages')
        .select('id, lead_id, direction, body, sent_at, delivery_status, source, twilio_sid')
        .order('sent_at', { ascending: false });
      if (isInitial) {
        q = q.limit(1000);
      } else {
        // Subtract 1 second from the cursor to tolerate Supabase
        // timestamp ordering edge cases (rows with identical sent_at).
        // Cap to 500 so a long pause doesn't suddenly pull the wall.
        const since = new Date(Date.parse(cursorRef.current) - 1000).toISOString();
        q = q.gte('sent_at', since).limit(500);
      }
      const { data, error: e } = await q;
      if (e) throw e;
      const incoming = data || [];
      if (isInitial) {
        setMessages(incoming);
      } else if (incoming.length > 0) {
        setMessages(prev => {
          const byId = new Map((prev || []).map(m => [m.id, m]));
          for (const m of incoming) byId.set(m.id, m); // newer overwrite
          return Array.from(byId.values())
            .sort((a, b) => (b.sent_at || '').localeCompare(a.sent_at || ''));
        });
      }
      // Advance the cursor to the most recent sent_at we've seen.
      const newest = incoming[0]?.sent_at;
      if (newest && (!cursorRef.current || newest > cursorRef.current)) {
        cursorRef.current = newest;
      } else if (isInitial && incoming.length === 0) {
        // Empty initial load — set cursor to NOW so the next poll
        // doesn't re-pull the entire (still-empty) history.
        cursorRef.current = new Date().toISOString();
      }
      setError(null);
    } catch (err) {
      setError(err.message || String(err));
    }
  }, []);

  useEffect(() => {
    // Reset cursor on user change — never leak one operator's messages
    // into another's view across a session switch.
    cursorRef.current = null;
    fetchMessages();
    const t = setInterval(fetchMessages, INBOX_POLL_MS);
    return () => clearInterval(t);
  }, [fetchMessages, currentUser?.id]);

  const conversations = React.useMemo(() => {
    if (!messages) return null;
    // Pre-compute a Map for O(1) lead lookup by id. Previously this
    // grouping ran `leads.find(l => String(l.id) === String(leadId))`
    // per group, which is O(n) per group — at 500 leads + 5000 messages
    // grouped into 500 conversations that's 250k linear scans on every
    // re-render of the inbox. Map.get() drops it to 500.
    const leadById = new Map((leads || []).map(l => [String(l.id), l]));
    const groups = {};
    for (const m of messages) {
      if (!groups[m.lead_id]) groups[m.lead_id] = [];
      groups[m.lead_id].push(m);
    }
    return Object.entries(groups).map(([leadId, items]) => {
      items.sort((a, b) => (a.sent_at || '').localeCompare(b.sent_at || ''));
      const last = items[items.length - 1];
      const lead = leadById.get(String(leadId));
      const lastReadAt = inboxReadState[leadId]?.lastReadAt || '';
      const unreadCount = items.filter(m =>
        m.direction === 'in' && (m.sent_at || '') > lastReadAt
      ).length;
      return {
        leadId, lead, items, last, unreadCount,
        hasInbound: items.some(m => m.direction === 'in'),
      };
    }).sort((a, b) => (b.last.sent_at || '').localeCompare(a.last.sent_at || ''));
  }, [messages, leads, inboxReadState]);

  const verticalCounts = React.useMemo(() => {
    const c = { all: 0, life: 0, health: 0, untagged: 0 };
    (conversations || []).forEach(conv => {
      c.all++;
      const v = (conv.lead?.vertical || '').toLowerCase();
      if (v === 'life')        c.life++;
      else if (v === 'health') c.health++;
      else                     c.untagged++;
    });
    return c;
  }, [conversations]);

  const visibleConversations = React.useMemo(() => {
    if (!conversations) return [];
    const q = search.trim().toLowerCase();
    return conversations.filter(c => {
      if (filter === 'unread'    && c.unreadCount === 0) return false;
      if (filter === 'inbound'   && !c.hasInbound)       return false;
      // "Awaiting you" — thread with an inbound reply that's the
      // MOST RECENT message. The operator owes them a response.
      if (filter === 'awaiting' && !(c.hasInbound && (!c.last || c.last.direction === 'in'))) return false;
      // "AI" — thread where the operator has handed autopilot to the AI
      if (filter === 'ai' && c.lead?.aiAutopilot !== true) return false;
      // ── Smart filters (high-volume agent) ─────────────────
      // hot = lead tagged Hot / status=hot / has Hot tag
      if (filter === 'hot' && !isHotLead(c.lead))        return false;
      // Legacy filter keys kept so deep-links from other views + old
      // localStorage values still filter as expected.
      if (filter === 'no_appt' && (!c.hasInbound || c.lead?.appointmentDate)) return false;
      if (filter === 'fu_needed' && !needsFollowUp(c))   return false;
      // Vertical filter — single unified inbox; pills slice it.
      if (ibVertical !== 'all') {
        const v = (c.lead?.vertical || '').toLowerCase();
        if (ibVertical === 'life'     && v !== 'life')   return false;
        if (ibVertical === 'health'   && v !== 'health') return false;
        if (ibVertical === 'untagged' && (v === 'life' || v === 'health')) return false;
      }
      if (q) {
        const name = (c.lead?.name || '').toLowerCase();
        const bodyMatch = c.items.some(m => (m.body || '').toLowerCase().includes(q));
        if (!name.includes(q) && !bodyMatch) return false;
      }
      return true;
    });
  }, [conversations, filter, search, ibVertical]);

  // Publish total unread to the root nav so the operator sees a
  // pip on the Inbox tab from any screen. Debounce-free — the count
  // only changes when a new inbound lands or the operator opens an
  // unread thread.
  const totalUnreadForBadge = React.useMemo(() => {
    return (conversations || []).reduce((s, c) => s + (c.unreadCount || 0), 0);
  }, [conversations]);
  React.useEffect(() => {
    window.dispatchEvent(new CustomEvent('inbox:unread', { detail: { total: totalUnreadForBadge } }));
  }, [totalUnreadForBadge]);

  const selectedConv = React.useMemo(() => {
    if (!selectedLeadId || !conversations) return null;
    return conversations.find(c => String(c.leadId) === String(selectedLeadId));
  }, [selectedLeadId, conversations]);

  // Stale-id cleanup — if the URL hash restored a lead id that no
  // longer matches any conversation (lead deleted, filter excluded
  // them, etc.), clear it so the auto-pick below takes over.
  React.useEffect(() => {
    if (!selectedLeadId) return;
    if (!conversations) return;             // still loading
    const exists = conversations.some(c => String(c.leadId) === String(selectedLeadId));
    if (!exists) setSelectedLeadId(null);
  }, [selectedLeadId, conversations, setSelectedLeadId]);

  React.useEffect(() => {
    if (selectedLeadId) return;
    if (!visibleConversations || visibleConversations.length === 0) return;
    setSelectedLeadId(visibleConversations[0].leadId);
  }, [visibleConversations, selectedLeadId, setSelectedLeadId]);

  // Mark as read when conversation is selected + has unread.
  useEffect(() => {
    if (!selectedConv) return;
    if (selectedConv.unreadCount === 0) return;
    setInboxReadState({
      ...inboxReadState,
      [selectedConv.leadId]: { lastReadAt: new Date().toISOString() },
    });
  }, [selectedConv?.leadId, selectedConv?.last?.sent_at]);

  // Auto-scroll thread to bottom.
  const threadRef = React.useRef(null);
  React.useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [selectedConv?.leadId, selectedConv?.items?.length]);

  async function sendReply() {
    const text = composer.trim();
    if (!text || !selectedConv || sending) return;
    setSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('not signed in');
      const res = await fetch('/api/sms/send-manual', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ leadId: selectedConv.leadId, body: text }),
      });
      const json = await res.json();
      if (res.status === 402) {
        alert(`Out of credits — top up your AI Wallet to keep texting.\n\n${json.detail || ''}`);
        return;
      }
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setComposer('');
      // Optimistic append so the bubble shows up instantly.
      setMessages(m => [
        ...(m || []),
        {
          id: `optimistic_${Date.now()}`,
          lead_id: selectedConv.leadId,
          direction: 'out',
          body: text,
          sent_at: new Date().toISOString(),
          delivery_status: json.deliveryStatus,
          source: 'manual',
        },
      ]);
      setTimeout(fetchMessages, 800);
    } catch (err) {
      alert(`Send failed: ${err.message}`);
    } finally {
      setSending(false);
    }
  }

  async function suggestReply() {
    if (!selectedConv || !selectedConv.lead || suggestBusy) return;
    setSuggestBusy(true);
    try {
      // Map message history into the recentOutreach shape that
      // generateFollowUpDraft consumes (type + note). Direction
      // becomes the type so the AI can tell who said what.
      const recentOutreach = selectedConv.items.slice(-10).map(m => ({
        createdAt: m.sent_at,
        type: m.direction === 'in' ? 'sms_received' : 'sms_sent',
        note: m.body,
      }));
      const draft = await generateFollowUpDraft({
        lead: selectedConv.lead,
        recentCalls: [],
        recentOutreach,
      });
      if (draft?.body) setComposer(draft.body);
      else if (typeof draft === 'string') setComposer(draft);
    } catch (err) {
      alert(`AI suggest failed: ${err.message}`);
    } finally {
      setSuggestBusy(false);
    }
  }

  function toggleHotTag(lead) {
    if (!lead) return;
    const HOT = 'Hot ';
    const tags = lead.tags || [];
    const has = tags.includes(HOT);
    const nextTags = has ? tags.filter(t => t !== HOT) : [...tags, HOT];
    setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, tags: nextTags, updatedAt: new Date().toISOString() } : l));
  }

  // ── Inline action handlers (high-volume agent) ─────────────
  // Each mutates the lead through setLeads so persistence runs on
  // the next CommissionTracker tick. The thread header surfaces
  // these as one-tap buttons so the operator never bounces to the
  // pipeline tab to change a status.
  function setLeadFields(leadId, patch) {
    if (!leadId) return;
    setLeads(prev => prev.map(l => String(l.id) === String(leadId)
      ? { ...l, ...patch, statusChangedAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
      : l));
  }
  function quickSetAppt(lead) {
    if (!lead) return;
    // Default: today's date — operator can change it on the lead
    // profile if a specific time is needed. The kanban shows it
    // immediately in the "Appt set" column.
    const today = new Date().toISOString().slice(0, 10);
    setLeadFields(lead.id, { status: 'appt_set', appointmentDate: today });
  }
  async function quickDnc(lead) {
    if (!lead) return;
    if (!(await hqConfirm({
      title: `Mark ${lead.name || 'this lead'} as Do Not Contact?`,
      body: 'This stops every future drip and SMS.',
      confirmLabel: 'Mark DNC', danger: true,
    }))) return;
    setLeadFields(lead.id, { status: 'dnc', opted_out: true });
  }
  function quickStatus(lead, status) {
    if (!lead || !status) return;
    setLeadFields(lead.id, { status });
  }

  const totalUnread = (conversations || []).reduce((s, c) => s + c.unreadCount, 0);

  if (error) {
    return <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">Failed to load conversations: {error}</div>;
  }
  if (messages == null) {
    return <ConversationSkeleton/>;
  }

  // Dark-theme inbox — three panes, iMessage-style bubbles, colored
  // temperature avatars, right-side lead intel with metrics + consent
  // card. Full-height inside the tab area; the wrapping container
  // above (max-w-7xl + p-4) constrains us, so we set our own inner
  // grid to fill it. Every color routes through the operator-chosen
  // msgColor state so the theme picker in the thread header lets them
  // customize You/AI bubble colors without touching the labels.
  const HOT_AVA = '#b5652f', WARM_AVA = '#a8842f', COLD_AVA = '#5a5a52', LIVE_AVA = '#2f8f4f';
  const HOT_DOT = '#C0463B', WARM_DOT = '#D8973A', COLD_DOT = '#67675f', LIVE_DOT = '#34C759';
  const isAiSource = (src) => {
    const s = String(src || '').toLowerCase();
    return ['ai', 'nova', 'anchor', 'retain', 'consent', 'enroll', 'workflow'].includes(s) || s.startsWith('nova_') || s.startsWith('ai_');
  };
  const SWATCH_COLORS = ['#0A84FF','#34C759','#C0463B','#7C5CFF','#C9A227','#FF6B35','#E0457B','#3AAFA9','#a06bd8','#5856D6'];
  const PRESETS = [
    ['Classic', '#0A84FF', '#7C5CFF'],
    ['Emerald', '#34C759', '#0A84FF'],
    ['Sunset',  '#FF6B35', '#C0463B'],
    ['Royal',   '#7C5CFF', '#a06bd8'],
    ['Gold',    '#C9A227', '#3AAFA9'],
    ['Rose',    '#E0457B', '#7C5CFF'],
  ];
  const youAccent = lightenHex(msgColor.you);
  const aiAccent  = lightenHex(msgColor.ai);

  return (
    <div className="bg-[#0a0a0a] text-[#ECECEC] rounded-2xl overflow-hidden border border-[#262626]" style={{ height: 'calc(100vh - 180px)', minHeight: 620, fontFamily: 'Inter, -apple-system, system-ui, sans-serif' }}>
      <div className="grid h-full" style={{ gridTemplateColumns: 'minmax(260px, 310px) 1fr minmax(0, 316px)' }}>
        {/* LEFT — conversation list (dark) */}
        <div className={`border-r border-[#262626] bg-[#0c0c0c] flex flex-col h-full overflow-hidden ${selectedLeadId ? 'hidden md:flex' : 'flex'}`}>
          <div className="px-4 pt-4 pb-3 flex items-center justify-between">
            <h1 style={{ fontFamily: 'Georgia, "Times New Roman", serif' }} className="text-[19px] font-semibold text-[#ECECEC]">Messages</h1>
            {totalUnread > 0 && (
              <span className="text-[10px] font-black rounded-full px-2 py-0.5 tabular-nums text-white" style={{ background: '#C0463B' }}>{totalUnread}</span>
            )}
          </div>
          <div className="px-4 pb-3">
            <input
              type="text"
              placeholder="🔍  Search leads &amp; messages"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-[#181818] border border-[#262626] rounded-[10px] px-3 py-2 text-[12.5px] text-[#ECECEC] placeholder:text-[#67675f] focus:border-[#C9A227] focus:outline-none"
            />
          </div>
          {/* Filter chips */}
          <div className="flex gap-1.5 px-4 pb-3 flex-wrap">
            {(() => {
              const all      = conversations || [];
              const awaiting = all.filter(c => c.hasInbound && (!c.last || c.last.direction === 'in')).length;
              const aiCount  = all.filter(c => c.lead?.aiAutopilot === true).length;
              const hotCount = all.filter(c => isHotLead(c.lead)).length;
              return [
                ['all',      'All',        all.length],
                ['unread',   'Unread',     totalUnread],
                ['hot',      '🔴 Hot',     hotCount],
                ['ai',       '🤖 AI',      aiCount],
                ['awaiting', 'Needs me',   awaiting],
              ];
            })().map(([id, label, count]) => {
              const active = filter === id;
              return (
                <button
                  key={id}
                  onClick={() => setFilter(id)}
                  className={`text-[10.5px] font-semibold px-2.5 py-1.5 rounded-full border whitespace-nowrap transition-colors ${
                    active
                      ? 'text-[#C9A227]'
                      : 'text-[#9A9A96] hover:text-[#ECECEC]'
                  }`}
                  style={active
                    ? { borderColor: '#C9A227', background: 'rgba(201,162,39,0.08)' }
                    : { borderColor: '#262626' }
                  }
                >
                  {label}{count > 0 && <span className="ml-1 font-mono opacity-70">{count}</span>}
                </button>
              );
            })}
          </div>

          {/* Book selector — subtle segmented pill in dark theme */}
          <div className="px-4 pb-3 flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center bg-[#141414] border border-[#262626] rounded-md p-0.5 text-[10px] font-bold">
              {[
                ['all',    'Both',   verticalCounts.all],
                ['life',   'Life',   verticalCounts.life],
                ['health', 'Health', verticalCounts.health],
              ].map(([id, label, count]) => {
                const active = ibVertical === id;
                return (
                  <button
                    key={id}
                    onClick={() => setIbVertical(id)}
                    className={`px-2 py-1 rounded-[5px] transition-colors ${active ? 'bg-[#1e1e1e] text-[#ECECEC]' : 'text-[#67675f] hover:text-[#9A9A96]'}`}
                  >
                    {label}<span className="ml-1 text-[9px] font-mono opacity-70">{count}</span>
                  </button>
                );
              })}
            </div>
            {verticalCounts.untagged > 0 && (
              <button
                onClick={() => setIbVertical('untagged')}
                className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${ibVertical === 'untagged' ? 'bg-[#D8973A26] text-[#f0c67a] border-[#D8973A73]' : 'text-[#D8973A] border-[#D8973A40] hover:border-[#D8973A]'}`}
                title="Leads with no vertical yet — tag them so the AI agents know which playbook to run"
              >
                Untagged {verticalCounts.untagged}
              </button>
            )}
          </div>

          {/* Conversation rows */}
          <div className="flex-1 overflow-y-auto">
            {visibleConversations.length === 0 ? (
              (conversations?.length || 0) === 0 ? (
                <div className="px-6 py-8 text-center">
                  <div className="text-5xl mb-3">💬</div>
                  <div className="text-base font-bold text-[#ECECEC] mb-1">Your inbox is empty</div>
                  <div className="text-xs text-[#9A9A96] max-w-xs mx-auto mb-4">
                    Start a conversation, or wait for drips + lead replies to populate this list.
                  </div>
                  <div className="flex flex-col gap-2 max-w-xs mx-auto">
                    <button
                      onClick={() => setComposeIntent('text')}
                      className="text-[13px] font-bold rounded-lg px-3 py-2 inline-flex items-center justify-center gap-1.5"
                      style={{ background: 'linear-gradient(180deg,#C9A84C,#C9A227)', color: '#1a1500' }}
                    >
                      <MessageSquare size={14}/> Start a new text
                    </button>
                    <button
                      onClick={() => setComposeIntent('call')}
                      className="bg-[#181818] border border-[#262626] hover:border-[#C9A227] text-[#9A9A96] hover:text-[#C9A227] text-[13px] font-semibold rounded-lg px-3 py-2 inline-flex items-center justify-center gap-1.5"
                    >
                      <Phone size={14}/> Start a new call
                    </button>
                    <button
                      onClick={() => onGotoTab('leads')}
                      className="text-xs text-[#67675f] hover:text-[#C9A227] underline mt-2"
                    >
                      Or import leads to start drips →
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-6 text-center">
                  <div className="text-sm text-[#ECECEC] font-semibold mb-1">No conversations match this filter</div>
                  <div className="text-xs text-[#9A9A96] mb-3">Try a different filter or clear the search.</div>
                  <button
                    onClick={() => { setFilter('all'); setIbVertical('all'); setSearch(''); }}
                    className="text-xs text-[#C9A227] font-semibold hover:underline"
                  >
                    Clear all filters
                  </button>
                </div>
              )
            ) : (
              visibleConversations.map(c => {
                const isSel = String(c.leadId) === String(selectedLeadId);
                const name = c.lead?.name || `Lead ${String(c.leadId).slice(0, 8)}`;
                const initials = name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || '?';
                const lastBody = c.last?.body || '';
                const lastDir = c.last?.direction;
                const lastIsAi = lastDir === 'out' && isAiSource(c.last?.source);
                const aiOn  = c.lead?.aiAutopilot === true;
                const isHot = isHotLead(c.lead);
                // Temperature drives avatar color.
                const tempBg = aiOn ? LIVE_AVA : isHot ? HOT_AVA : c.hasInbound ? WARM_AVA : COLD_AVA;
                const tempDot = aiOn ? LIVE_DOT : isHot ? HOT_DOT : c.hasInbound ? WARM_DOT : COLD_DOT;
                const vertical = String(c.lead?.vertical || '').toLowerCase();
                const consentOK = c.lead?.consent === true || c.lead?.consentAt || c.lead?.opt_in_sms === true;
                const optedOut = c.lead?.opted_out === true || c.lead?.optOut === true;
                return (
                  <button
                    key={c.leadId}
                    onClick={() => setSelectedLeadId(c.leadId)}
                    className={`w-full text-left px-4 py-3 border-b border-[#171717] hover:bg-[#131313] transition-colors relative ${isSel ? 'pl-[14px]' : ''}`}
                    style={isSel ? { background: 'linear-gradient(90deg, rgba(201,162,39,0.10), transparent)', borderLeft: '2px solid #C9A227' } : undefined}
                  >
                    <div className="grid grid-cols-[44px_1fr] gap-2.5">
                      <div className="relative w-11 h-11 rounded-full grid place-items-center font-bold text-[15px] text-white" style={{ background: tempBg }}>
                        {initials}
                        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2" style={{ background: tempDot, borderColor: '#0c0c0c' }}/>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className={`flex-1 truncate text-[13.5px] ${c.unreadCount > 0 ? 'font-bold text-[#ECECEC]' : 'font-semibold text-[#ECECEC]'}`}>{name}</span>
                          <span className="text-[10.5px] text-[#67675f] font-mono">{fmtRel(c.last?.sent_at)}</span>
                        </div>
                        <div className="text-[12px] text-[#9A9A96] mt-0.5 flex items-center gap-1.5 truncate">
                          {lastDir === 'out' && (
                            lastIsAi
                              ? <span className="text-[8.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded flex-shrink-0" style={{ background: 'rgba(124,92,255,0.18)', color: '#b3a0ff' }}>AI</span>
                              : <span className="text-[8.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded flex-shrink-0" style={{ background: 'rgba(10,132,255,0.16)', color: '#6db3ff' }}>YOU</span>
                          )}
                          {lastDir === 'in' && (
                            <span className="text-[8.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#1e1e1e] text-[#9A9A96] flex-shrink-0">THEM</span>
                          )}
                          <span className="truncate">{lastBody.slice(0, 80)}{lastBody.length > 80 ? '…' : ''}</span>
                        </div>
                        <div className="flex gap-1 mt-1.5 flex-wrap">
                          {vertical === 'life'   && <span className="text-[8.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: 'rgba(10,132,255,0.14)', color: '#6db3ff' }}>LIFE</span>}
                          {vertical === 'health' && <span className="text-[8.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: 'rgba(52,199,89,0.14)', color: '#5fd47f' }}>HEALTH</span>}
                          {optedOut
                            ? <span className="text-[8.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: 'rgba(192,70,59,0.14)', color: '#e08a80' }}>OPT-OUT</span>
                            : consentOK
                              ? <span className="text-[8.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: 'rgba(52,199,89,0.12)', color: '#5fd47f' }}>SMS ✓</span>
                              : <span className="text-[8.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: 'rgba(192,70,59,0.14)', color: '#e08a80' }}>EMAIL ONLY</span>
                          }
                          {isHot && <span className="text-[8.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: 'rgba(192,70,59,0.14)', color: '#e08a80' }}>🔴 HOT</span>}
                          {aiOn && !isHot && <span className="text-[8.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: 'rgba(124,92,255,0.18)', color: '#b3a0ff' }}>🤖 AI</span>}
                        </div>
                      </div>
                    </div>
                    {c.unreadCount > 0 && (
                      <div className="absolute top-3.5 right-3.5 w-2.5 h-2.5 rounded-full" style={{ background: '#0A84FF' }}/>
                    )}
                  </button>
                );
              })
            )}
          </div>

          <div className="p-3 border-t border-[#262626] bg-[#0c0c0c]">
            <button
              onClick={() => setComposeIntent('text')}
              className="w-full text-[13px] font-bold rounded-lg py-2.5"
              style={{ background: 'linear-gradient(180deg,#C9A84C,#C9A227)', color: '#1a1500' }}
            >
              + New conversation
            </button>
          </div>
        </div>

        {/* CENTER — thread (dark, iMessage bubbles, theme picker) */}
        <div className={`h-full overflow-hidden relative flex flex-col ${!selectedLeadId ? 'hidden md:flex' : 'flex'}`} style={{ background: 'linear-gradient(180deg,#0b0b0b,#0e0e0e)' }}>
          {!selectedConv ? (
            <div className="flex-1 flex items-center justify-center text-[#67675f] text-sm">
              <div className="text-center">
                <div className="text-5xl mb-3">💬</div>
                <div>Pick a conversation on the left to view the thread.</div>
              </div>
            </div>
          ) : (
            <>
              {/* Theme picker popup */}
              {themePopOpen && (
                <div className="absolute top-16 right-5 w-[300px] rounded-[14px] p-4 z-50 shadow-2xl" style={{ background: '#141414', border: '1px solid #303030' }}>
                  <div className="text-[11px] uppercase tracking-widest text-[#9A9A96] font-bold">Message colors</div>
                  <div className="text-[10.5px] text-[#67675f] leading-relaxed mt-1 mb-3">
                    Make the inbox yours — pick a preset or set exact colors. Cosmetic only; the AI vs YOU vs customer labels never change.
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-[#67675f] font-bold">Your messages</div>
                  <div className="flex gap-2 flex-wrap mt-2">
                    {SWATCH_COLORS.map(c => (
                      <button key={`you-${c}`} onClick={() => setMsgColor(m => ({ ...m, you: c }))}
                        className="w-[34px] h-[34px] rounded-[9px] relative"
                        style={{ background: c, boxShadow: msgColor.you === c ? '0 0 0 2px #ECECEC inset' : 'none' }}
                      >{msgColor.you === c && <span className="absolute inset-0 grid place-items-center text-white font-bold text-[13px]">✓</span>}</button>
                    ))}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-[#67675f] font-bold mt-3">AI assistant messages</div>
                  <div className="flex gap-2 flex-wrap mt-2">
                    {SWATCH_COLORS.map(c => (
                      <button key={`ai-${c}`} onClick={() => setMsgColor(m => ({ ...m, ai: c }))}
                        className="w-[34px] h-[34px] rounded-[9px] relative"
                        style={{ background: c, boxShadow: msgColor.ai === c ? '0 0 0 2px #ECECEC inset' : 'none' }}
                      >{msgColor.ai === c && <span className="absolute inset-0 grid place-items-center text-white font-bold text-[13px]">✓</span>}</button>
                    ))}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-[#67675f] font-bold mt-4 mb-2">Quick presets</div>
                  <div className="grid grid-cols-2 gap-2">
                    {PRESETS.map(([label, y, a]) => (
                      <button key={label} onClick={() => setMsgColor({ you: y, ai: a })}
                        className="flex items-center gap-2 border rounded-[9px] px-2 py-1.5 text-[11px] text-[#9A9A96] hover:text-[#ECECEC]"
                        style={{ background: '#181818', borderColor: '#262626' }}
                      >
                        <span className="flex gap-1">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: y }}/>
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: a }}/>
                        </span>
                        {label}
                      </button>
                    ))}
                  </div>
                  <button onClick={() => setThemePopOpen(false)}
                    className="w-full mt-3 font-bold text-[12.5px] rounded-[9px] py-2.5"
                    style={{ background: 'linear-gradient(180deg,#C9A84C,#C9A227)', color: '#1a1500' }}
                  >Save theme</button>
                </div>
              )}

              {/* Header */}
              <div className="flex items-center gap-3 px-5 py-3 border-b border-[#262626] backdrop-blur" style={{ background: 'rgba(12,12,12,0.9)' }}>
                <button onClick={() => setSelectedLeadId(null)} className="md:hidden text-[#9A9A96] hover:text-white mr-1">←</button>
                <button
                  onClick={() => onGotoTab('deal_room')}
                  className="text-[10px] font-black uppercase tracking-[0.15em] bg-[#C9A227] text-black hover:bg-[#e0b83e] px-2.5 py-1.5 rounded"
                  title="Open Deal Room for this lead"
                >Deal Room</button>
                {(() => {
                  const isHot = isHotLead(selectedConv.lead);
                  const aiOn = selectedConv.lead?.aiAutopilot === true;
                  const bg = aiOn ? LIVE_AVA : isHot ? HOT_AVA : selectedConv.hasInbound ? WARM_AVA : COLD_AVA;
                  const initials = (selectedConv.lead?.name || 'L').split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || 'L';
                  return (
                    <div className="w-[38px] h-[38px] rounded-full grid place-items-center font-bold text-white text-[13px]" style={{ background: bg }}>{initials}</div>
                  );
                })()}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-bold text-[14.5px] truncate text-[#ECECEC]">{selectedConv.lead?.name || 'Unknown lead'}</div>
                    {selectedConv.lead?.status && (
                      <span className="text-[10px] uppercase tracking-wider bg-[#1e1e1e] text-[#9A9A96] px-1.5 py-0.5 rounded font-semibold">
                        {String(selectedConv.lead.status).replace(/_/g, ' ')}
                      </span>
                    )}
                    {(() => {
                      const v = (selectedConv.lead?.vertical || '').toLowerCase();
                      const Pill = ({ id, label, active, activeBg, activeText }) => (
                        <button
                          onClick={() => tagLeadVertical(selectedConv.leadId, active ? '' : id)}
                          className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-bold border"
                          style={active
                            ? { background: activeBg, color: activeText, borderColor: activeBg }
                            : { background: 'transparent', color: '#9A9A96', borderColor: '#262626' }}
                          title={active ? `Click to untag (currently ${label})` : `Tag as ${label}`}
                        >
                          {active ? '✓ ' : ''}{label}
                        </button>
                      );
                      return (
                        <span className="inline-flex items-center gap-1">
                          <Pill id="life"   label="Life"   active={v === 'life'}   activeBg="#6D28D9" activeText="#fff"/>
                          <Pill id="health" label="Health" active={v === 'health'} activeBg="#0E7C6B" activeText="#fff"/>
                        </span>
                      );
                    })()}
                  </div>
                  <div className="text-[11px] text-[#9A9A96] mt-0.5 flex items-center gap-1.5 truncate">
                    <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: '#34C759' }}/>
                    {selectedConv.lead?.phone || '—'}
                    {selectedConv.lead?.source && <> · {selectedConv.lead.source}</>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {selectedConv.lead?.phone && (
                    <button onClick={() => requestPhoneCall({ lead: selectedConv.lead })} title={`Call ${selectedConv.lead?.phone}`}
                      className="w-[34px] h-[34px] rounded-[9px] border grid place-items-center text-[#9A9A96] hover:text-[#C9A227]"
                      style={{ background: '#181818', borderColor: '#262626' }}
                    >📞</button>
                  )}
                  {selectedConv.lead && (() => {
                    const on = selectedConv.lead.aiAutopilot === true;
                    return (
                      <button
                        onClick={() => setLeads && setLeads(prev => prev.map(l => String(l.id) === String(selectedConv.leadId) ? { ...l, aiAutopilot: !on } : l))}
                        className="w-[34px] h-[34px] rounded-[9px] border grid place-items-center"
                        style={on
                          ? { background: 'rgba(124,92,255,0.16)', color: '#b3a0ff', borderColor: '#7C5CFF' }
                          : { background: '#181818', color: '#9A9A96', borderColor: '#262626' }}
                        title={on ? 'AI is handling replies — click to take back' : 'Hand this thread to the AI'}
                      >🤖</button>
                    );
                  })()}
                  <button onClick={() => setThemePopOpen(o => !o)} title="Customize message colors"
                    className="w-[34px] h-[34px] rounded-[9px] border grid place-items-center text-[#9A9A96] hover:text-[#C9A227]"
                    style={{ background: '#181818', borderColor: '#262626' }}
                  >🎨</button>
                  <button
                    onClick={() => quickSetAppt(selectedConv.lead)}
                    disabled={selectedConv.lead?.status === 'appt_set'}
                    title="Set appointment"
                    className="w-[34px] h-[34px] rounded-[9px] border grid place-items-center text-[#9A9A96] hover:text-[#D8973A] disabled:opacity-40"
                    style={{ background: '#181818', borderColor: '#262626' }}
                  >📅</button>
                  <button
                    onClick={() => toggleHotTag(selectedConv.lead)}
                    title="Toggle Hot"
                    className="w-[34px] h-[34px] rounded-[9px] border grid place-items-center text-[#9A9A96] hover:text-[#e08a80]"
                    style={{
                      background: selectedConv.lead?.tags?.includes('Hot ') ? 'rgba(192,70,59,0.14)' : '#181818',
                      borderColor: selectedConv.lead?.tags?.includes('Hot ') ? 'rgba(192,70,59,0.6)' : '#262626',
                      color: selectedConv.lead?.tags?.includes('Hot ') ? '#e08a80' : undefined,
                    }}
                  >🔥</button>
                  {selectedConv.lead && (
                    <button onClick={() => onOpenLeadProfile(selectedConv.leadId)} title="Open lead profile"
                      className="w-[34px] h-[34px] rounded-[9px] border grid place-items-center text-[#9A9A96] hover:text-[#C9A227]"
                      style={{ background: '#181818', borderColor: '#262626' }}
                    >ⓘ</button>
                  )}
                </div>
              </div>

              {/* AI Sales Command briefing above the thread — compact
                  bar (Deal Score, journey stage, intent chips, close/
                  momentum/referral predictions) that expands on click
                  to reveal the executive summary + Next Best Action.
                  Lazy-loaded — first inbox-open pays the ~5KB chunk
                  fetch, then cached. Fail-soft: if the endpoint 404s
                  or the lead has no data, the card renders a quiet
                  hint and never blocks the thread below it. */}
              <React.Suspense fallback={<div className="h-10 border-b border-[#262626]" style={{ background: 'rgba(20,20,20,0.6)' }}/>}>
                <InboxBriefingCard
                  leadId={selectedLeadId}
                  onGotoDealRoom={() => onGotoTab('deal_room')}
                  lastInboundMessage={(() => {
                    // Find the most recent inbound message so the
                    // response-variants panel has something to reply
                    // to. Null if the conversation has only outbounds
                    // (Response Variants renders a hint in that case).
                    const items = selectedConv?.items || [];
                    for (let i = items.length - 1; i >= 0; i--) {
                      if (items[i]?.direction === 'in' && items[i]?.body) return items[i].body;
                    }
                    return null;
                  })()}
                />
              </React.Suspense>

              {/* Thread — iMessage-style bubbles with sender labels */}
              <div ref={threadRef} className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-0.5">
                {selectedConv.items.map((m, i, arr) => {
                  const isAi   = m.direction === 'out' && isAiSource(m.source);
                  const isYou  = m.direction === 'out' && !isAi;
                  const prev = arr[i - 1];
                  const prevIsAi  = prev && prev.direction === 'out' && isAiSource(prev.source);
                  const prevIsYou = prev && prev.direction === 'out' && !prevIsAi;
                  const prevIsIn  = prev && prev.direction === 'in';
                  const sameSender = (isAi && prevIsAi) || (isYou && prevIsYou) || (m.direction === 'in' && prevIsIn);
                  const bubbleStyle = isAi
                    ? { background: `linear-gradient(135deg, ${msgColor.ai}, ${darkenHex(msgColor.ai)})`, color: '#fff' }
                    : isYou
                      ? { background: msgColor.you, color: '#fff' }
                      : { background: '#262628', color: '#ECECEC' };
                  const bubbleRounding = m.direction === 'out'
                    ? 'rounded-[19px] rounded-br-[6px]'
                    : 'rounded-[19px] rounded-bl-[6px]';
                  const labelStyle = isAi
                    ? { color: aiAccent }
                    : isYou
                      ? { color: youAccent }
                      : undefined;
                  const senderLabel = isAi
                    ? '🤖 AI Assistant'
                    : isYou
                      ? '👤 You'
                      : `${(selectedConv.lead?.name || 'Lead').split(' ')[0]} · inbound`;
                  return (
                    <div key={m.id} className={`flex flex-col ${m.direction === 'out' ? 'items-end' : 'items-start'} ${sameSender ? 'mt-0.5' : 'mt-3'}`}>
                      {!sameSender && (
                        <div className="text-[9.5px] font-bold uppercase tracking-wide px-1.5 mb-1" style={labelStyle || { color: '#9A9A96' }}>
                          {senderLabel}
                        </div>
                      )}
                      <div className={`max-w-[76%] px-3.5 py-2 text-[13.5px] leading-snug ${bubbleRounding}`} style={bubbleStyle}>
                        <div className="whitespace-pre-wrap break-words">{m.body}</div>
                      </div>
                      <div className="text-[9.5px] text-[#67675f] mt-0.5 px-1.5 flex items-center gap-1.5">
                        {m.direction === 'out' && (
                          <>
                            <span style={{ color: '#5fd47f' }}>consent ✓ · quiet hours ✓</span>
                            <span>·</span>
                          </>
                        )}
                        <span>{fmtRel(m.sent_at)}</span>
                        {m.direction === 'out' && (m.delivery_status === 'delivered' || m.delivery_status === 'read') && (
                          <><span>·</span><span className="font-semibold" style={{ color: youAccent }}>Read</span></>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Composer */}
              <div className="border-t border-[#262626] px-5 py-3" style={{ background: '#0c0c0c' }}>
                <div className="flex gap-2 mb-2 items-center flex-wrap">
                  {[
                    ['sms', '💬 SMS'],
                    ['email', '✉ Email'],
                    ['template', '📝 Template'],
                  ].map(([id, label]) => (
                    <button key={id} onClick={() => setComposerChannel(id)}
                      className="text-[11px] font-semibold px-3 py-1 rounded-lg border"
                      style={composerChannel === id
                        ? { background: '#181818', borderColor: '#262626', color: '#ECECEC' }
                        : { background: 'transparent', borderColor: 'transparent', color: '#9A9A96' }}
                    >{label}</button>
                  ))}
                  {selectedConv.lead?.aiAutopilot === true && (
                    <span className="ml-auto text-[10.5px] flex items-center gap-1.5" style={{ color: aiAccent }}>
                      🤖 AI is handling this thread — type to take over
                    </span>
                  )}
                </div>
                {selectedConv.lead?.opted_out === true && (
                  <div className="text-[11px] rounded-[10px] px-3 py-2 mb-2" style={{ background: 'rgba(192,70,59,0.14)', color: '#e08a80', border: '1px solid rgba(192,70,59,0.6)' }}>
                    Lead has opted out. Cannot send.
                  </div>
                )}
                <form onSubmit={(e) => { e.preventDefault(); sendReply(); }} className="flex items-end gap-2 rounded-[22px] px-4 py-2" style={{ background: '#181818', border: '1px solid #262626' }}>
                  <textarea
                    value={composer}
                    onChange={(e) => {
                      const val = e.target.value;
                      setComposer(val);
                      if (val.length === 1 && setLeads && selectedConv?.lead?.aiAutopilot === true) {
                        setLeads(prev => prev.map(l => String(l.id) === String(selectedConv.leadId) ? { ...l, aiAutopilot: false } : l));
                      }
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                    placeholder={selectedConv.lead?.opted_out ? 'Lead opted out' : `Message ${(selectedConv.lead?.name || 'lead').split(' ')[0]}…`}
                    rows={1}
                    disabled={selectedConv.lead?.opted_out === true || sending}
                    className="flex-1 bg-transparent text-[13px] text-[#ECECEC] placeholder:text-[#67675f] resize-none focus:outline-none py-1"
                    style={{ minHeight: 24, maxHeight: 120 }}
                  />
                  <button type="button" onClick={suggestReply} disabled={suggestBusy || sending}
                    className="text-[11px] font-bold px-2 py-1 rounded-lg border disabled:opacity-50"
                    style={{ borderColor: '#7C5CFF', color: '#b3a0ff', background: 'rgba(124,92,255,0.10)' }}
                    title="AI suggest reply"
                  >{suggestBusy ? '…' : 'AI'}</button>
                  <button type="submit" disabled={sending || !composer.trim() || selectedConv.lead?.opted_out === true}
                    className="w-[34px] h-[34px] rounded-full font-bold text-white text-[15px] disabled:opacity-40 grid place-items-center"
                    style={{ background: msgColor.you }}
                  >{sending ? '…' : '↑'}</button>
                </form>
                {/* Segment counter + live gate readout */}
                {(() => {
                  const len = composer.length;
                  const segs = len === 0 ? 0 : Math.ceil(len / 160);
                  const optedOut = selectedConv.lead?.opted_out === true || selectedConv.lead?.optOut === true;
                  const consentOK = selectedConv.lead?.consent === true || selectedConv.lead?.consentAt || selectedConv.lead?.opt_in_sms === true;
                  const gatePass = consentOK && !optedOut;
                  return (
                    <div className="flex items-center justify-between gap-2 text-[10px] font-mono mt-1.5 px-1" style={{ color: gatePass ? '#67675f' : '#e08a80' }}>
                      <span>🔒 {segs} segment{segs === 1 ? '' : 's'} · {len}/160 · STOP honored carrier-level</span>
                      <span>
                        gates:
                        <span className="ml-1" style={{ color: consentOK && !optedOut ? '#5fd47f' : '#e08a80' }}>consent {consentOK && !optedOut ? '✓' : '✕'}</span>
                        <span className="ml-1" style={{ color: '#5fd47f' }}>· quiet hours ✓</span>
                        <span className="ml-1" style={{ color: '#5fd47f' }}>· A2P ✓</span>
                      </span>
                    </div>
                  );
                })()}
                {selectedConv.lead && (
                  <div className="flex items-center gap-1 mt-2 flex-wrap">
                    <button
                      onClick={() => quickDnc(selectedConv.lead)}
                      className="text-[10.5px] font-semibold rounded-md px-2 py-1 border text-[#9A9A96] hover:text-[#e08a80]"
                      style={{ background: '#141414', borderColor: '#262626' }}
                      title="Do Not Contact — stops every drip + SMS"
                    >🚫 DNC</button>
                    <select
                      value={selectedConv.lead?.status || ''}
                      onChange={e => quickStatus(selectedConv.lead, e.target.value)}
                      className="text-[10.5px] font-semibold rounded-md px-2 py-1 border text-[#9A9A96]"
                      style={{ background: '#141414', borderColor: '#262626' }}
                      title="Update lead status"
                    >
                      <option value="" disabled>Update status…</option>
                      <option value="new">new</option>
                      <option value="contacted">contacted</option>
                      <option value="qualified">qualified</option>
                      <option value="appt_set">appt set</option>
                      <option value="showed">showed</option>
                      <option value="quoted">quoted</option>
                      <option value="pending_uw">pending uw</option>
                      <option value="closed_won">closed won</option>
                      <option value="no_show">no show</option>
                      <option value="not_interested">not interested</option>
                    </select>
                    <button onClick={() => setBulkOpen(true)}
                      disabled={(visibleConversations || []).length === 0}
                      className="text-[10.5px] font-semibold rounded-md px-2 py-1 border text-[#9A9A96] hover:text-[#C9A227] disabled:opacity-40 ml-auto"
                      style={{ background: '#141414', borderColor: '#262626' }}
                      title="Send the same SMS to every conversation in the current filter"
                    >Bulk to filter</button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* RIGHT — lead intel (dark). Hero + Lead status + Consent +
            Metrics + Actions. Mirrors the mock's right pane exactly. */}
        <div className="border-l border-[#262626] overflow-y-auto hidden lg:block" style={{ background: '#0c0c0c' }}>
          {!selectedConv ? (
            <div className="p-5 text-[11px] text-[#67675f] italic">Select a conversation to see the lead's intel.</div>
          ) : (() => {
            const lead = selectedConv.lead || {};
            const items = selectedConv.items || [];
            const isHot = isHotLead(lead);
            const aiOn  = lead.aiAutopilot === true;
            const consentOK = lead.consent === true || lead.consentAt || lead.opt_in_sms === true;
            const optedOut = lead.opted_out === true || lead.optOut === true;
            const heroBg = aiOn ? LIVE_AVA : isHot ? HOT_AVA : selectedConv.hasInbound ? WARM_AVA : COLD_AVA;
            const initials = (lead.name || 'L').split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || 'L';
            const total = items.length;
            const inbound = items.filter(m => m.direction === 'in').length;
            const outbound = total - inbound;
            const aiSent = items.filter(m => m.direction === 'out' && isAiSource(m.source)).length;
            const youSent = outbound - aiSent;
            const delivered = items.filter(m => m.direction === 'out' && (m.delivery_status === 'delivered' || m.delivery_status === 'sent' || m.delivery_status === 'read')).length;
            const deliveryPct = outbound > 0 ? Math.round((delivered / outbound) * 100) : 0;
            const lastInbound = [...items].reverse().find(m => m.direction === 'in');
            const lastOutbound = [...items].reverse().find(m => m.direction === 'out');
            const v = String(lead.vertical || '').toLowerCase();
            return (
              <>
                {/* Hero */}
                <div className="p-5 text-center border-b border-[#262626]">
                  <div className="w-[66px] h-[66px] rounded-full grid place-items-center font-bold text-[23px] text-white mx-auto mb-2.5" style={{ background: heroBg }}>{initials}</div>
                  <div style={{ fontFamily: 'Georgia, "Times New Roman", serif' }} className="text-lg font-semibold text-[#ECECEC]">{lead.name || 'Unknown lead'}</div>
                  <div className="text-[11.5px] text-[#67675f] mt-0.5">{lead.source || '—'}{lead.leadType ? ` · ${String(lead.leadType).replace(/_/g, ' ')}` : ''}</div>
                  {isHot && (
                    <div className="mt-2.5 inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-full" style={{ background: 'rgba(192,70,59,0.15)', color: '#e08a80' }}>
                      🔴 HOT LEAD
                    </div>
                  )}
                  <div className="flex gap-1.5 flex-wrap justify-center mt-2.5">
                    {v === 'life'   && <span className="text-[8.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: 'rgba(10,132,255,0.14)', color: '#6db3ff' }}>LIFE</span>}
                    {v === 'health' && <span className="text-[8.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: 'rgba(52,199,89,0.14)', color: '#5fd47f' }}>HEALTH</span>}
                    {optedOut
                      ? <span className="text-[8.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: 'rgba(192,70,59,0.14)', color: '#e08a80' }}>OPT-OUT</span>
                      : consentOK && <span className="text-[8.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: 'rgba(52,199,89,0.12)', color: '#5fd47f' }}>SMS ✓</span>}
                    {aiOn && <span className="text-[8.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: 'rgba(124,92,255,0.18)', color: '#b3a0ff' }}>AI-HANDLED</span>}
                  </div>
                </div>

                {/* Lead status */}
                <div className="p-4 border-b border-[#262626]">
                  <div className="text-[9.5px] tracking-widest uppercase text-[#67675f] mb-2.5 font-bold">Lead status</div>
                  <div className="space-y-1.5 text-[12.5px]">
                    <div className="flex justify-between"><span className="text-[#9A9A96]">Stage</span><span className="font-semibold" style={{ color: '#C9A227' }}>{String(lead.status || '—').replace(/_/g, ' ')}</span></div>
                    {lead.agentProdNum && <div className="flex justify-between"><span className="text-[#9A9A96]">Owner</span><span className="font-semibold font-mono text-[#ECECEC]">{lead.agentProdNum}</span></div>}
                    {lead.source && <div className="flex justify-between"><span className="text-[#9A9A96]">Source</span><span className="font-semibold truncate max-w-[140px] text-[#ECECEC]" title={lead.source}>{lead.source}</span></div>}
                    <div className="flex justify-between"><span className="text-[#9A9A96]">Assigned AI</span><span className="font-semibold" style={{ color: aiOn ? aiAccent : '#67675f' }}>{aiOn ? 'AI Assistant · ON' : 'Idle'}</span></div>
                    {lastInbound && <div className="flex justify-between"><span className="text-[#9A9A96]">Last inbound</span><span className="font-semibold text-[#ECECEC]">{fmtRel(lastInbound.sent_at)}</span></div>}
                    {lastOutbound && <div className="flex justify-between"><span className="text-[#9A9A96]">Last outbound</span><span className="font-semibold text-[#ECECEC]">{fmtRel(lastOutbound.sent_at)}</span></div>}
                  </div>
                </div>

                {/* Consent & compliance */}
                <div className="p-4 border-b border-[#262626]">
                  <div className="text-[9.5px] tracking-widest uppercase text-[#67675f] mb-2.5 font-bold">Consent &amp; compliance</div>
                  <div
                    className="rounded-[11px] p-3.5"
                    style={{
                      background: optedOut
                        ? 'linear-gradient(180deg, rgba(192,70,59,0.10), #131313)'
                        : consentOK
                          ? 'linear-gradient(180deg, rgba(52,199,89,0.10), #131313)'
                          : 'linear-gradient(180deg, rgba(216,151,58,0.10), #131313)',
                      border: `1px solid ${optedOut ? 'rgba(192,70,59,0.35)' : consentOK ? 'rgba(52,199,89,0.35)' : 'rgba(216,151,58,0.35)'}`,
                    }}
                  >
                    <div className="text-[10px] tracking-widest uppercase font-bold flex items-center gap-1.5"
                      style={{ color: optedOut ? '#e08a80' : consentOK ? '#5fd47f' : '#f0c67a' }}>
                      🔒 {optedOut ? 'Opted out' : consentOK ? 'SMS consent on file' : 'No consent captured'}
                    </div>
                    <div className="text-[11.5px] mt-2 leading-relaxed" style={{ color: '#cfcfcf' }}>
                      {optedOut
                        ? 'Lead sent STOP or was flagged for opt-out. All sends blocked at the server; DNC status enforced.'
                        : consentOK
                          ? 'Express consent on file · quiet hours 9a–9p local · A2P campaign approved. Gates re-check at fire time — never cached.'
                          : 'Server will refuse any send until consent is captured (verbal or written) on the lead record.'}
                    </div>
                  </div>
                  <div className="flex justify-between text-[12.5px] mt-3"><span className="text-[#9A9A96]">Email (CAN-SPAM)</span><span className="font-semibold" style={{ color: lead.email ? '#5fd47f' : '#67675f' }}>{lead.email ? '✓ unsub present' : '—'}</span></div>
                  <div className="flex justify-between text-[12.5px] mt-1.5"><span className="text-[#9A9A96]">AI voice consent</span><span className="font-semibold text-[#67675f]">not captured</span></div>
                  <div className="flex justify-between text-[12.5px] mt-1.5"><span className="text-[#9A9A96]">DNC status</span><span className="font-semibold" style={{ color: optedOut ? '#e08a80' : '#5fd47f' }}>{optedOut ? '✕ opted out' : '✓ clear'}</span></div>
                </div>

                {/* Messaging metrics */}
                <div className="p-4 border-b border-[#262626]">
                  <div className="text-[9.5px] tracking-widest uppercase text-[#67675f] mb-2.5 font-bold">Messaging metrics</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-[10px] px-3 py-2.5" style={{ background: '#181818', border: '1px solid #262626' }}>
                      <div className="font-mono font-bold text-[17px] text-[#ECECEC] tabular-nums">{total}</div>
                      <div className="text-[9.5px] text-[#67675f] uppercase tracking-wide mt-0.5">Total msgs</div>
                    </div>
                    <div className="rounded-[10px] px-3 py-2.5" style={{ background: '#181818', border: '1px solid #262626' }}>
                      <div className="font-mono font-bold text-[17px] tabular-nums" style={{ color: '#5fd47f' }}>{inbound}</div>
                      <div className="text-[9.5px] text-[#67675f] uppercase tracking-wide mt-0.5">Inbound</div>
                    </div>
                    <div className="rounded-[10px] px-3 py-2.5" style={{ background: '#181818', border: '1px solid #262626' }}>
                      <div className="font-mono font-bold text-[17px] tabular-nums" style={{ color: aiAccent }}>{aiSent}</div>
                      <div className="text-[9.5px] text-[#67675f] uppercase tracking-wide mt-0.5">AI sent</div>
                    </div>
                    <div className="rounded-[10px] px-3 py-2.5" style={{ background: '#181818', border: '1px solid #262626' }}>
                      <div className="font-mono font-bold text-[17px] tabular-nums" style={{ color: youAccent }}>{youSent}</div>
                      <div className="text-[9.5px] text-[#67675f] uppercase tracking-wide mt-0.5">You sent</div>
                    </div>
                    <div className="rounded-[10px] px-3 py-2.5 col-span-2" style={{ background: '#181818', border: '1px solid #262626' }}>
                      <div className="font-mono font-bold text-[17px] tabular-nums" style={{ color: '#5fd47f' }}>{deliveryPct}%</div>
                      <div className="text-[9.5px] text-[#67675f] uppercase tracking-wide mt-0.5">Delivered</div>
                    </div>
                  </div>
                </div>

                {/* All channels */}
                <div className="p-4 border-b border-[#262626]">
                  <div className="text-[9.5px] tracking-widest uppercase text-[#67675f] mb-2.5 font-bold">All channels</div>
                  <div className="space-y-1.5 text-[12.5px]">
                    <div className="flex justify-between"><span className="text-[#9A9A96]">📱 SMS</span><span className="font-semibold text-[#ECECEC]">{lead.phone || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-[#9A9A96]">✉ Email</span><span className="font-semibold text-[#ECECEC] truncate max-w-[160px]" title={lead.email || ''}>{lead.email || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-[#9A9A96]">📞 Voice</span><span className="font-semibold text-[#ECECEC]">{lead.phone_e164 || lead.phone || '—'}</span></div>
                  </div>
                </div>

                {/* Actions */}
                <div className="p-4">
                  <div className="text-[9.5px] tracking-widest uppercase text-[#67675f] mb-2.5 font-bold">Actions</div>
                  <div className="grid grid-cols-2 gap-2">
                    {lead.phone && (
                      <button onClick={() => requestPhoneCall({ lead })}
                        className="col-span-2 text-[11.5px] font-semibold px-2 py-2.5 rounded-[9px]"
                        style={{ background: 'rgba(201,162,39,0.10)', border: '1px solid rgba(201,162,39,0.45)', color: '#C9A227' }}
                      >📞 Call {lead.phone}</button>
                    )}
                    <button onClick={() => onOpenLeadProfile(selectedConv.leadId)}
                      className="text-[11.5px] font-semibold px-2 py-2.5 rounded-[9px] text-[#9A9A96] hover:text-[#C9A227]"
                      style={{ background: '#181818', border: '1px solid #262626' }}
                    >View full record</button>
                    {(() => {
                      const on = aiOn;
                      return (
                        <button onClick={() => setLeads && setLeads(prev => prev.map(l => String(l.id) === String(selectedConv.leadId) ? { ...l, aiAutopilot: !on } : l))}
                          className="text-[11.5px] font-semibold px-2 py-2.5 rounded-[9px] text-[#9A9A96] hover:text-[#C9A227]"
                          style={{ background: '#181818', border: '1px solid #262626' }}
                        >{on ? 'Take over from AI' : 'Hand to AI'}</button>
                      );
                    })()}
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {bulkOpen && (
        <BulkSmsModal
          initialConversations={visibleConversations}
          onClose={() => setBulkOpen(false)}
          onSent={() => {
            // Refresh after the loop so the operator sees the new
            // outbound bubbles. Modal stays open until the operator
            // clicks Close so they can review pass/fail counts.
            setTimeout(fetchMessages, 800);
          }}
        />
      )}

      {composeIntent && (
        <ComposeNewMessageModal
          intent={composeIntent}
          leads={leads}
          setLeads={setLeads}
          conversations={conversations || []}
          onGotoTab={onGotoTab}
          onClose={() => setComposeIntent(null)}
          onSent={(leadId) => {
            setComposeIntent(null);
            setSelectedLeadId(leadId);
            // Re-fetch so the new conversation shows up in the list.
            setTimeout(fetchMessages, 800);
          }}
          onCallPicked={(lead) => {
            setComposeIntent(null);
            // Fire the existing dial-request event that CallWidget
            // listens for at the app-root (same channel DIAL, the
            // inbox header, and the Territory drill-down already use).
            window.dispatchEvent(new CustomEvent('crm-dial-request', {
              detail: {
                lead: {
                  id:    lead.id,
                  name:  lead.name,
                  phone: lead.phone_e164 || lead.phone,
                  email: lead.email,
                },
              },
            }));
          }}
        />
      )}
    </div>
  );
}

// Modal for starting a new SMS conversation with any lead. The
// operator picks a lead from the searchable list, types a message,
// and sends. Reuses the /api/sms/send-manual endpoint that powers
// thread replies — so compliance gates (consent, opt-out, phone) are
// enforced identically and a new row in public.messages causes the
// inbox to auto-pick up the new conversation on next poll.
function ComposeNewMessageModal({ intent = 'text', leads = [], setLeads = null, conversations = [], onClose, onSent = () => {}, onCallPicked = () => {}, onGotoTab = () => {} }) {
  const [query, setQuery] = useState('');
  const [pickedLeadId, setPickedLeadId] = useState(null);
  const [pickedClient, setPickedClient] = useState(null);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [clients, setClients] = useState([]);
  const isCall = intent === 'call';

  // Fetch clients on mount so the picker shows BOTH leads + clients.
  // Operator ask 2026-06-27: "this needs to be way better" — clients
  // were missing from the picker so when the leads list is empty the
  // modal felt useless even though the operator had clients to text.
  React.useEffect(() => {
    let alive = true;
    supabase.from('clients')
      .select('id, name, phone, phone_e164, email, line_of_business, vertical, status, consent, opt_in_sms')
      .limit(500)
      .then(({ data }) => {
        if (alive) setClients(Array.isArray(data) ? data : []);
      });
    return () => { alive = false; };
  }, []);

  // Email-shape detection — a valid-ish email in the query triggers
  // the ad-hoc email row that routes to the email composer with the
  // address prefilled. Matches "user@host.tld"; broad enough for
  // sub-domains, tight enough to not fire on partial names.
  const trimmed = String(query || '').trim();
  const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  const adHocEmail = looksLikeEmail ? trimmed.toLowerCase() : null;

  // Phone-shape detection — 7+ digits triggers the ad-hoc row.
  // Mirrors smsCoverageToE164 (US-default).
  const digits = (query || '').replace(/[^\d]/g, '');
  const looksLikePhone = !looksLikeEmail && digits.length >= 7;
  const adHocPhone = React.useMemo(() => {
    if (!looksLikePhone) return null;
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return `+${digits}`;
  }, [digits, looksLikePhone]);
  // Pretty preview of what they're typing — "+1 (555) 123-4567".
  const adHocPretty = React.useMemo(() => {
    if (!adHocPhone) return null;
    const d = adHocPhone.replace(/[^\d]/g, '');
    if (d.length === 11 && d.startsWith('1')) {
      return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7, 11)}`;
    }
    return adHocPhone;
  }, [adHocPhone]);
  const phoneAlreadyExists = React.useMemo(() => {
    if (!adHocPhone) return null;
    const lastTen = adHocPhone.replace(/[^\d]/g, '').slice(-10);
    if (lastTen.length !== 10) return null;
    const inLeads = (leads || []).find(l => {
      const p = String(l.phone_e164 || l.phone || '').replace(/[^\d]/g, '');
      return p && p.endsWith(lastTen);
    });
    if (inLeads) return { kind: 'lead', record: inLeads };
    const inClients = (clients || []).find(c => {
      const p = String(c.phone_e164 || c.phone || '').replace(/[^\d]/g, '');
      return p && p.endsWith(lastTen);
    });
    if (inClients) return { kind: 'client', record: inClients };
    return null;
  }, [adHocPhone, leads, clients]);

  // Stub-lead creator for ad-hoc numbers (text intent only).
  const createAdHocLead = React.useCallback(() => {
    if (!adHocPhone || !setLeads) return null;
    const id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `lead_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const newLead = {
      id, name: adHocPhone, phone: adHocPhone, phone_e164: adHocPhone,
      status: 'new', source: 'manual_inbox', consent: true,
      createdAt: new Date().toISOString(),
    };
    setLeads(prev => [...prev, newLead]);
    return newLead;
  }, [adHocPhone, setLeads]);

  // Phone normalize for client rows (clients table has phone but no
  // phone_e164 on older rows). Falls back to raw.
  function phoneOf(r) { return r.phone_e164 || r.phone || ''; }
  function hasPhone(r) { return /\d{7,}/.test(phoneOf(r)); }
  function fmtPhone(raw) {
    const d = String(raw || '').replace(/[^\d]/g, '');
    if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
    if (d.length === 11 && d.startsWith('1')) return `+1 (${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`;
    return raw;
  }
  function verticalChip(v) {
    const vl = String(v || '').toLowerCase();
    if (vl === 'life')   return <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700">Life</span>;
    if (vl === 'health') return <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-700">Health</span>;
    return null;
  }

  // Recents — leads with an existing conversation, sorted by most
  // recent message. Pulled from the `conversations` prop which already
  // sorted them. Top 5 only.
  const recents = React.useMemo(() => {
    const existingIds = new Set();
    const out = [];
    for (const c of conversations) {
      if (!c.lead || !hasPhone(c.lead)) continue;
      if (existingIds.has(String(c.lead.id))) continue;
      existingIds.add(String(c.lead.id));
      out.push({ kind: 'lead', record: c.lead, lastAt: c.last?.sent_at });
      if (out.length >= 5) break;
    }
    return out;
  }, [conversations]);

  // Apply search query to leads + clients
  const q = query.trim().toLowerCase();
  function matchesQuery(r) {
    if (!q) return true;
    const name = (r.name || '').toLowerCase();
    const ph = String(phoneOf(r)).toLowerCase().replace(/[^\d]/g, '');
    const qDigits = q.replace(/[^\d]/g, '');
    if (name.includes(q)) return true;
    if (qDigits && ph.includes(qDigits)) return true;
    return false;
  }

  const visibleLeads = React.useMemo(() => {
    const recentIds = new Set(recents.map(r => String(r.record.id)));
    return (leads || [])
      .filter(hasPhone)
      .filter(l => !recentIds.has(String(l.id)))
      .filter(matchesQuery)
      .sort((a, b) => (b.updatedAt || b.createdAt || '').localeCompare(a.updatedAt || a.createdAt || ''))
      .slice(0, 40);
  }, [leads, recents, q]);

  const visibleClients = React.useMemo(() => {
    return (clients || [])
      .filter(hasPhone)
      .filter(matchesQuery)
      .slice(0, 40);
  }, [clients, q]);

  // What's the active recipient — could be a lead, client, or ad-hoc
  // number. Always show this in the body composer header.
  const pickedLead   = pickedLeadId ? (leads || []).find(l => l.id === pickedLeadId) : null;
  const activeRecipient = pickedLead
    ? { kind: 'lead', record: pickedLead }
    : pickedClient
      ? { kind: 'client', record: pickedClient }
      : null;
  const recipientBlockReason = (() => {
    if (!activeRecipient) return null;
    const r = activeRecipient.record;
    if (r.opted_out === true) return 'opted out';
    if (r.opt_in_sms === false && r.consent !== true) return 'no SMS consent';
    return null;
  })();
  const canText = !!activeRecipient && !recipientBlockReason;

  // Templates — quick chips that populate the body with {name} substitution.
  const TEMPLATES = [
    { id: 'follow', label: 'Follow up', body: `Hi {name}, just following up — let me know if you have any questions on what we discussed.` },
    { id: 'sched',  label: 'Reschedule', body: `Hi {name}, looks like we missed each other. When's a good time today or tomorrow for a quick call?` },
    { id: 'quick',  label: 'Quick Q',  body: `Hi {name}, quick question for you when you get a sec — got a few minutes today?` },
    { id: 'thx',    label: 'Thanks',  body: `Thanks {name}! Appreciate you taking the time. I'll follow up with the next steps shortly.` },
  ];
  function applyTemplate(t) {
    const name = (activeRecipient?.record?.name || '').split(' ')[0] || 'there';
    setBody(t.body.replace(/\{name\}/g, name));
  }

  // Esc closes
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function send() {
    if (!activeRecipient || !body.trim() || sending) return;
    if (!canText) {
      alert(`Can't text — ${recipientBlockReason}.`);
      return;
    }
    setSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('not signed in');
      // Clients don't yet have a thread; send via send-manual with the
      // client's phone as a quick lead bootstrap. The endpoint resolves
      // by leadId — if we're sending to a client, create a quick lead.
      let leadId = activeRecipient.record.id;
      if (activeRecipient.kind === 'client' && setLeads) {
        const quickId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `lead_${Date.now()}`;
        const c = activeRecipient.record;
        const quick = {
          id: quickId,
          name: c.name || phoneOf(c),
          phone: phoneOf(c),
          phone_e164: phoneOf(c),
          email: c.email || '',
          status: 'client_outreach',
          source: 'client_thread',
          consent: true,
          vertical: c.vertical || (String(c.line_of_business || '').toLowerCase().match(/aca|medicare|dental|vision|supplemental|health/) ? 'health' : 'life'),
          createdAt: new Date().toISOString(),
          metadata: { client_id: c.id },
        };
        setLeads(prev => [...prev, quick]);
        leadId = quickId;
      }
      // Race-fix: when the picked recipient is an ad-hoc phone the
      // client-side `setLeads` may not have persisted yet, so the
      // server would 404. Pass the phone alongside; the server
      // materializes the stub lead atomically before send.
      const isAdHoc = activeRecipient.record.source === 'manual_inbox' && !!activeRecipient.record.phone_e164;
      const payload = { leadId, body: body.trim() };
      if (isAdHoc) payload.adhoc_phone = activeRecipient.record.phone_e164;
      const res = await fetch('/api/sms/send-manual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      // Server returns the canonical lead id (may differ from ours
      // for ad-hoc where an existing phone-match was reused).
      onSent(json.leadId || leadId);
    } catch (err) {
      alert(`Send failed: ${err.message}`);
    } finally {
      setSending(false);
    }
  }

  const charCount = body.length;
  const segments  = Math.max(1, Math.ceil(charCount / 160));

  function Row({ r, isExisting }) {
    return (
      <button
        onClick={() => {
          if (isCall) {
            onCallPicked(r);
            return;
          }
          // Text intent — set as active recipient.
          if (clients.some(c => c.id === r.id)) {
            setPickedClient(r);
            setPickedLeadId(null);
          } else {
            setPickedLeadId(r.id);
            setPickedClient(null);
          }
        }}
        className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2.5 border-b border-slate-100 last:border-b-0"
      >
        <div className="flex-shrink-0 w-8 h-8 bg-slate-200 text-slate-700 rounded-full flex items-center justify-center text-sm font-bold">
          {(r.name || '?')[0]?.toUpperCase() || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <div className="text-sm font-semibold text-slate-900 truncate">{r.name || '(no name)'}</div>
            {verticalChip(r.vertical)}
            {isExisting && <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1 py-0.5 rounded">EXISTING</span>}
          </div>
          <div className="text-[11px] text-slate-500 truncate">
            <span className="font-mono">{fmtPhone(phoneOf(r))}</span>
            {r.line_of_business && <> · {r.line_of_business}</>}
            {r.status && <> · {r.status}</>}
          </div>
        </div>
        {isCall && (
          <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 flex-shrink-0">📞 Dial</div>
        )}
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white flex items-center justify-between">
          <div className="font-semibold text-slate-900 flex items-center gap-2">
            {isCall ? <Phone size={16} className="text-emerald-600"/> : <MessageSquare size={16} className="text-emerald-600"/>}
            {isCall ? 'New call' : 'New text'}
          </div>
          <button aria-label="Close" onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={18}/>
          </button>
        </div>

        {/* Active recipient pill — collapses the picker to a chip */}
        {activeRecipient && !isCall && (
          <div className="px-4 py-2.5 border-b border-slate-100 bg-emerald-50/40">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">To</div>
            <div className="flex items-center gap-2">
              <div className="flex-shrink-0 w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                {(activeRecipient.record.name || '?')[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <div className="text-sm font-bold text-slate-900 truncate">{activeRecipient.record.name || '(no name)'}</div>
                  {verticalChip(activeRecipient.record.vertical)}
                  <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                    {activeRecipient.kind}
                  </span>
                </div>
                <div className="text-xs text-slate-600 font-mono">{fmtPhone(phoneOf(activeRecipient.record))}</div>
              </div>
              {recipientBlockReason && (
                <div className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded shrink-0">
                  ⚠ {recipientBlockReason}
                </div>
              )}
              <button
                onClick={() => { setPickedLeadId(null); setPickedClient(null); }}
                className="text-xs text-slate-500 hover:text-slate-700 underline ml-1 shrink-0"
              >
                change
              </button>
            </div>
          </div>
        )}

        {/* Picker — only shown when no recipient is selected */}
        {!activeRecipient && (
          <>
            <div className="px-4 py-3 border-b border-slate-100">
              <input
                type="text"
                placeholder={isCall ? 'Search a name, or type any phone number…' : 'Search a name · type any phone number · paste an email address…'}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-emerald-400 focus:outline-none"
              />
              {looksLikePhone && (
                <div className="mt-1.5 text-[11px] text-slate-500">
                  Parses to: <span className="font-mono font-semibold text-slate-700">{adHocPretty}</span>
                  {phoneAlreadyExists && (
                    <span className="text-amber-700 ml-2">· already a {phoneAlreadyExists.kind}</span>
                  )}
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto" style={{ maxHeight: 380 }}>
              {/* Ad-hoc email — top affordance when the query is an
                  email address. Routes to the email composer with the
                  address pre-filled via sessionStorage handoff. */}
              {adHocEmail && !isCall && (
                <div className="px-3 py-2 border-b-2 border-sky-200 bg-sky-50/40">
                  <button
                    onClick={() => {
                      try { sessionStorage.setItem('email.prefillTo', adHocEmail); } catch {}
                      onClose();
                      onGotoTab('email');
                    }}
                    className="w-full text-left flex items-center gap-2.5 hover:bg-sky-50 rounded px-1.5 py-1"
                  >
                    <div className="flex-shrink-0 w-8 h-8 bg-sky-100 text-sky-700 rounded-full flex items-center justify-center text-base font-bold">
                      ✉
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-slate-900">
                        Email <span className="font-mono">{adHocEmail}</span>
                      </div>
                      <div className="text-[11px] text-slate-600">
                        Opens the email composer with this address pre-filled.
                      </div>
                    </div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-sky-700 shrink-0">
                      Compose →
                    </div>
                  </button>
                </div>
              )}
              {/* Ad-hoc number — top affordance when typing a number */}
              {adHocPhone && !phoneAlreadyExists && (
                <div className="px-3 py-2 border-b-2 border-emerald-200 bg-emerald-50/40">
                  <button
                    onClick={() => {
                      if (isCall) {
                        onCallPicked({ id: null, name: adHocPhone, phone: adHocPhone, phone_e164: adHocPhone });
                        return;
                      }
                      const lead = createAdHocLead();
                      if (lead) setPickedLeadId(lead.id);
                    }}
                    className="w-full text-left flex items-center gap-2.5 hover:bg-emerald-50 rounded px-1.5 py-1"
                  >
                    <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-base font-bold">
                      {isCall ? '📞' : '＋'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-slate-900">
                        {isCall ? 'Dial' : 'Text'} <span className="font-mono">{adHocPretty}</span>
                      </div>
                      <div className="text-[11px] text-slate-600">
                        {isCall ? 'Direct dial — no lead created.' : 'Creates a stub lead so the reply lands here.'}
                      </div>
                    </div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 shrink-0">
                      {isCall ? 'Dial' : 'Start'}
                    </div>
                  </button>
                </div>
              )}

              {/* Recents */}
              {recents.length > 0 && (
                <Section title={`Recent (${recents.length})`}>
                  {recents.map(r => <Row key={`recent-${r.record.id}`} r={r.record} isExisting/>)}
                </Section>
              )}

              {/* Clients */}
              {visibleClients.length > 0 && (
                <Section title={`Clients (${visibleClients.length})`}>
                  {visibleClients.map(c => <Row key={`c-${c.id}`} r={c}/>)}
                </Section>
              )}

              {/* Leads */}
              {visibleLeads.length > 0 && (
                <Section title={`Leads (${visibleLeads.length})`}>
                  {visibleLeads.map(l => <Row key={`l-${l.id}`} r={l}/>)}
                </Section>
              )}

              {/* Empty state — actionable, not passive */}
              {recents.length === 0 && visibleLeads.length === 0 && visibleClients.length === 0 && !adHocPhone && !adHocEmail && (
                <div className="p-8 text-center">
                  <div className="text-4xl mb-2">📱</div>
                  <div className="text-sm font-semibold text-slate-800 mb-1">
                    {q ? 'No contacts match' : 'No contacts yet'}
                  </div>
                  <div className="text-xs text-slate-500 max-w-xs mx-auto">
                    Type a client's name to search, {isCall ? 'or any phone number to dial it directly' : 'or paste a phone number to text it, or an email address to compose an email — a new lead is created automatically for phone sends'}.
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Body composer — text only, always visible once a recipient is set */}
        {activeRecipient && !isCall && (
          <div className="px-4 py-3 flex-1 flex flex-col gap-2 overflow-y-auto">
            {/* Template chips */}
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Templates:</span>
              {TEMPLATES.map(t => (
                <button
                  key={t.id}
                  onClick={() => applyTemplate(t)}
                  className="text-[11px] font-semibold text-slate-700 bg-white border border-slate-200 hover:border-emerald-400 hover:text-emerald-700 rounded-full px-2.5 py-0.5"
                  title={t.body.replace(/\{name\}/g, (activeRecipient?.record?.name || 'there').split(' ')[0])}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {recipientBlockReason && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                ⚠ This contact is {recipientBlockReason} — sending will be blocked at the server.
              </div>
            )}

            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={`Hi ${(activeRecipient?.record?.name || 'there').split(' ')[0]}…`}
              rows={6}
              autoFocus
              className="flex-1 w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-emerald-400 focus:outline-none resize-none min-h-[120px]"
            />
            <div className="flex items-center justify-between text-[10px] text-slate-400">
              <span>{charCount} chars · {segments} segment{segments === 1 ? '' : 's'}</span>
              <span>⌘/Ctrl+Enter to send</span>
            </div>
          </div>
        )}

        {/* Footer actions */}
        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button aria-label="Close" onClick={onClose} className="text-sm text-slate-600 hover:text-slate-800 font-semibold px-3 py-1.5">
            Cancel
          </button>
          {!isCall && (
            <button
              onClick={send}
              disabled={!activeRecipient || !body.trim() || sending || !canText}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg px-4 py-1.5 inline-flex items-center gap-1.5"
            >
              {sending ? 'Sending…' : <>Send <Send size={12}/></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Section header for the contact picker.
function Section({ title, children }) {
  return (
    <div>
      <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-wider text-slate-500 sticky top-0">
        {title}
      </div>
      {children}
    </div>
  );
}

// SMS bubble with source icon + delivery status checkmarks
// Right-pane context for the selected thread. Reads composition of
// data the inbox already has — no new fetches. Same last-contacted
// pattern as the lead drawer so the operator sees consistent info
// wherever they open a lead.
function ThreadContextPane({ conv, onOpenLeadProfile = () => {}, onGotoTab = () => {} }) {
  const lead        = conv?.lead || {};
  const items       = conv?.items || [];
  const outbound    = items.find(m => m.direction === 'outbound');
  const inbound     = items.find(m => m.direction === 'inbound');
  const lastAt      = outbound?.sent_at || conv?.lastAt;
  const consentOK   = lead.consent === true || lead.consentAt || lead.opt_in_sms === true;
  const optedOut    = lead.opted_out === true || lead.optOut === true;
  const isHot       = typeof isHotLead === 'function' ? isHotLead(lead) : false;

  const stage = lead.status || 'new';
  const stageLabel = String(stage).replace(/_/g, ' ');

  const fmtWhen = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (sameDay) return `Today · ${time}`;
    const yest = new Date(now.getTime() - 86400000).toDateString() === d.toDateString();
    if (yest) return `Yesterday · ${time}`;
    return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} · ${time}`;
  };

  const totalTouches = items.length;
  const inboundCount = items.filter(m => m.direction === 'inbound').length;

  return (
    <div className="p-3 space-y-3">
      {/* Last-contacted banner — same treatment as the lead drawer */}
      <div className="border-2 border-amber-400 bg-gradient-to-b from-amber-50 to-white p-3 rounded-md">
        <div className="text-[9px] font-black uppercase tracking-[0.16em] text-amber-700">Last contacted</div>
        {outbound ? (
          <>
            <div className="text-sm font-black text-slate-900 mt-1">{fmtWhen(lastAt)}</div>
            <div className="text-[11px] text-slate-700 mt-0.5">
              SMS · {outbound.source === 'ai' ? 'AI assistant' : 'you'} · {outbound.delivery_status || 'delivered'}
            </div>
          </>
        ) : (
          <div className="text-sm font-bold text-rose-700 mt-1">Never contacted</div>
        )}
      </div>

      {/* Lead facts */}
      <div className="bg-white border border-slate-200 rounded-md p-3">
        <div className="text-[9px] font-black uppercase tracking-[0.14em] text-slate-500 mb-2">Lead</div>
        <dl className="text-xs space-y-1">
          <div className="flex justify-between gap-2"><dt className="text-slate-500">Stage</dt><dd className={`font-bold ${isHot ? 'text-rose-700' : 'text-slate-900'}`}>{stageLabel}{isHot && <span className="ml-1 text-[9px] bg-rose-100 text-rose-700 px-1 rounded">HOT</span>}</dd></div>
          {lead.agentProdNum && <div className="flex justify-between gap-2"><dt className="text-slate-500">Owner</dt><dd className="font-mono text-slate-900">{lead.agentProdNum}</dd></div>}
          {lead.vertical    && <div className="flex justify-between gap-2"><dt className="text-slate-500">Vertical</dt><dd className="font-bold text-slate-900 capitalize">{lead.vertical}</dd></div>}
          {lead.leadSource  && <div className="flex justify-between gap-2"><dt className="text-slate-500">Source</dt><dd className="font-bold text-slate-900 truncate max-w-[140px]" title={lead.leadSource}>{lead.leadSource}</dd></div>}
          <div className="flex justify-between gap-2">
            <dt className="text-slate-500">Consent</dt>
            <dd className={`font-bold ${optedOut ? 'text-rose-700' : consentOK ? 'text-emerald-700' : 'text-amber-700'}`}>
              {optedOut ? '✗ Opted out' : consentOK ? '✓ SMS OK' : '⚠ Not captured'}
            </dd>
          </div>
          <div className="flex justify-between gap-2"><dt className="text-slate-500">Total touches</dt><dd className="font-mono text-slate-900">{totalTouches} · {inboundCount} in</dd></div>
          {lead.followUpDate && <div className="flex justify-between gap-2"><dt className="text-slate-500">Next scheduled</dt><dd className="font-bold text-slate-900">{new Date(lead.followUpDate).toLocaleDateString([], { month: 'short', day: 'numeric' })}</dd></div>}
        </dl>
      </div>

      {/* Quick actions */}
      <div className="bg-white border border-slate-200 rounded-md p-3">
        <div className="text-[9px] font-black uppercase tracking-[0.14em] text-slate-500 mb-2">Quick actions</div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => lead.id && onOpenLeadProfile(String(lead.id))}
            className="col-span-2 text-[11px] font-bold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 px-2 py-1.5 rounded"
          >
            Open full history
          </button>
          {lead.phone && (
            <a
              href={`tel:${String(lead.phone).replace(/\D/g, '')}`}
              className="text-[11px] font-bold text-slate-700 border border-slate-200 bg-white hover:bg-slate-50 px-2 py-1.5 rounded text-center"
            >
              Call
            </a>
          )}
          <button
            onClick={() => onGotoTab('calendar')}
            className="text-[11px] font-bold text-slate-700 border border-slate-200 bg-white hover:bg-slate-50 px-2 py-1.5 rounded"
          >
            Book appt
          </button>
          {!optedOut && (
            <button
              onClick={async () => {
                if (await hqConfirm({
                  title: `Mark ${lead.name || 'this lead'} as not interested?`,
                  confirmLabel: 'Mark not interested',
                })) {/* left as no-op UI — parent state update out of scope */}
              }}
              className="col-span-2 text-[11px] font-bold text-rose-700 border border-rose-200 bg-white hover:bg-rose-50 px-2 py-1.5 rounded"
            >
              Not interested — stop
            </button>
          )}
        </div>
      </div>

      {/* Rails footer — the compliance principle behind every send */}
      <div className="text-[10px] text-slate-500 leading-relaxed border-t border-slate-200 pt-2">
        <span className="font-bold text-amber-700">Rails:</span> composer disables for non-opted-in contacts · STOP suppresses instantly, carrier-mirrored · every send passes consent + quiet hours + A2P at fire time.
      </div>
    </div>
  );
}

function SmsBubble({ msg }) {
  const isOut = msg.direction === 'out';
  const statusIcon =
    msg.delivery_status === 'delivered' ? '✓✓' :
    msg.delivery_status === 'sent'      ? '✓'  :
    msg.delivery_status === 'failed'    ? '✕'  :
    msg.delivery_status === 'dry_run'   ? '◯'  : null;
  // AI vs YOU classification — makes it obvious who sent every
  // outbound message. Maps every source string the CRM currently
  // writes (nova · anchor · retain · consent · enroll · workflow ·
  // ai · manual · system) into a clear operator-facing label +
  // color. New sources default to "YOU" so an unknown label doesn't
  // silently claim AI provenance.
  const src = String(msg.source || '').toLowerCase();
  const isAi = src === 'ai' || src === 'nova' || src === 'anchor' || src === 'retain' || src === 'consent' || src === 'enroll' || src === 'workflow' || src.startsWith('nova_') || src.startsWith('ai_');
  const isSystem = src === 'system';
  const sourceLabel = isAi
    ? (src === 'ai' || src === 'workflow' ? 'AI' : `AI · ${src.toUpperCase().replace(/_.*/, '')}`)
    : isSystem
      ? 'SYSTEM'
      : 'YOU';
  const sourceChipTone = isAi
    ? 'bg-teal-600 text-white'
    : isSystem
      ? 'bg-slate-500 text-white'
      : 'bg-amber-500 text-white';
  return (
    <div className={`flex ${isOut ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[78%] flex flex-col ${isOut ? 'items-end' : 'items-start'} gap-0.5`}>
        {/* Source pill above outbound bubbles — instantly answers
            "who sent this" before the operator reads the copy. */}
        {isOut && (
          <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${sourceChipTone}`}>
            {sourceLabel}
          </span>
        )}
        <div className={`rounded-2xl px-3 py-2 text-sm ${isOut ? 'bg-emerald-600 text-white rounded-br-sm' : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm'}`}>
          <div className="whitespace-pre-wrap leading-snug">{msg.body}</div>
          <div className={`text-[10px] mt-1 flex items-center gap-1 ${isOut ? 'text-emerald-100/80 justify-end' : 'text-slate-400'}`}>
            <span>{fmtRel(msg.sent_at)}</span>
            {isOut && statusIcon && <span title={`status: ${msg.delivery_status}`}>{statusIcon}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// CFO Dashboard — Real money math, not vanity stats.
//
// The complaint that drove this build: existing money views were
// undershooting. They showed gross commission but no LTV/CAC, no
// per-producer contribution margin, no cash flow projection, no
// proper override accounting, no enterprise value. This view fixes
// all of that with first-principles math built from the actual
// transactions, leads, calls, producers, and operator-tunable
// financial assumptions.
//
// Math is conservative and explicit — every section shows the
// formula so the operator can audit it. Where the data is missing
// (e.g. carrier advance %, overhead), we expose it as an input at
// the top of the view rather than fudging numbers behind the scenes.
// ════════════════════════════════════════════════════════════════════

const DEFAULT_FINANCIAL_CONFIG = {
  overheadMonthly:           500,    // rent, software, E&O, dialer — fixed
  advanceRate:               0.75,   // % of first-year commission advanced (typical FEX/MP)
  monthsAdvanced:            9,      // duration of advance window (typical)
  renewalRate:               0.08,   // renewal commission as % of annual premium per renewal year
  expectedClientYears:       6.5,    // average expected client life for LTV (FEX final-expense industry avg)
  enterpriseValueMultiple:   2.0,    // typical FEX/MP agency multiple on annualized in-force AP
  // Producer payout shape — used to estimate overrides when actual contract
  // levels per-producer aren't configured. 1.0 = producer keeps everything,
  // 0.7 = producer at 70% (agency overrides 30%).
  avgProducerContractLevel:  0.85,
};

function CFODashboard({
  transactions = [], producers = {}, producerList = [], settings = {}, goalConfig = {},
  leads = [], calls = [], financialConfig, setFinancialConfig,
  openClientProfile = () => {}, onGotoTab = () => {}, openAccountant = () => {},
}) {
  const cfg = { ...DEFAULT_FINANCIAL_CONFIG, ...(financialConfig || {}) };
  const goal = { ...DEFAULT_GOAL_CONFIG, ...(goalConfig || {}) };
  const set = (k, raw, kind = 'num') => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    setFinancialConfig({ ...cfg, [k]: kind === 'pct' ? n / 100 : n });
  };

  // ── Period windows ─────────────────────────────────────────────────
  const now = new Date();
  const DAY = 1000 * 60 * 60 * 24;
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const ytdStart   = new Date(now.getFullYear(), 0, 1);
  const ninetyDayStart = new Date(now.getTime() - 90 * DAY);

  // Helpers — every transaction has activityDate + netToAccount.
  function tsOf(tx) {
    const d = parseDate(tx.activityDate);
    return d ? d.getTime() : null;
  }
  function within(tx, fromTs) {
    const t = tsOf(tx);
    return t != null && t >= fromTs && t <= now.getTime();
  }

  // ── Aggregates per period ──────────────────────────────────────────
  const aggregate = (txs) => {
    let gross = 0, chargebacks = 0, polCount = new Set(), apIssued = 0;
    for (const tx of txs) {
      const v = Number(tx.netToAccount) || 0;
      if (v >= 0) gross += v; else chargebacks += Math.abs(v);
      if (tx.policy) polCount.add(`${tx.prodNum || ''}|${tx.mga || ''}|${tx.policy}`);
      // Estimate AP from commPremium when the activity is a new-issue
      // (positive amount + has commPremium).
      if (v > 0 && tx.commPremium && Number(tx.commPremium) > 0) {
        apIssued += Math.abs(Number(tx.commPremium)) * 12; // monthly × 12
      }
    }
    return {
      gross,
      chargebacks,
      netCommission: gross - chargebacks,
      chargebackRate: gross > 0 ? chargebacks / gross : 0,
      policyCount: polCount.size,
      apIssued,
    };
  };

  const mtdTx  = (transactions || []).filter(tx => within(tx, monthStart.getTime()));
  const t90Tx  = (transactions || []).filter(tx => within(tx, ninetyDayStart.getTime()));
  const ytdTx  = (transactions || []).filter(tx => within(tx, ytdStart.getTime()));

  const mtd  = aggregate(mtdTx);
  const t90  = aggregate(t90Tx);
  const ytd  = aggregate(ytdTx);

  // ── Deal economics ─────────────────────────────────────────────────
  // We weight LTV by persistency. Renewal commissions add value beyond
  // the first-year advance:
  //   LTV per deal = first-year commission × (1 - lapse_rate)
  //                + (renewalRate × AP × expectedClientYears) × (1 - lapse_rate)
  const realizedFirstYearComm = goal.avgAnnualPremium * goal.commissionRate * (1 - goal.lapseRate);
  const renewalLifetimeValue  = goal.avgAnnualPremium * cfg.renewalRate * cfg.expectedClientYears * (1 - goal.lapseRate);
  const ltvPerDeal            = realizedFirstYearComm + renewalLifetimeValue;

  // CAC: blended cost per closed deal — leads + VA + overhead allocated
  // across the deals in the trailing 90 days.
  const leadSpend90  = (leads || [])
    .filter(l => {
      const ca = Date.parse(l.createdAt || '');
      return Number.isFinite(ca) && ca >= ninetyDayStart.getTime();
    })
    .reduce((s, l) => s + (Number(l.cost) || 0), 0);

  // Daily VA cost from goalConfig × 3 months. Bonus from closed leads in window.
  const vasNeededForGoal = Math.max(1, Math.ceil((goal.monthlyTarget / realizedFirstYearComm) / goal.workingDays / goal.dialsPerVaPerDay));
  const closedIn90 = (leads || []).filter(l => {
    if (l.status !== 'closed') return false;
    const stamp = l.closedAt || l.updatedAt || l.appointmentDate || l.createdAt || '';
    const t = Date.parse(stamp);
    return Number.isFinite(t) && t >= ninetyDayStart.getTime();
  });
  const vaCost90 = vasNeededForGoal * goal.vaBasePay * 3 + closedIn90.length * goal.vaBonusPerSale;
  const overhead90 = cfg.overheadMonthly * 3;

  const dealsClosed90 = closedIn90.length;
  const acquisitionCost90 = leadSpend90 + vaCost90 + overhead90;
  const blendedCAC = dealsClosed90 > 0 ? acquisitionCost90 / dealsClosed90 : null;

  const ltvCacRatio = blendedCAC && blendedCAC > 0 ? ltvPerDeal / blendedCAC : null;

  // Contribution margin per deal
  const contributionPerDeal = blendedCAC != null ? realizedFirstYearComm - blendedCAC : null;
  const contributionMargin  = blendedCAC != null && realizedFirstYearComm > 0 ? contributionPerDeal / realizedFirstYearComm : null;

  // ── True P&L per period ────────────────────────────────────────────
  // For MTD: actual gross/chargebacks from data + apportioned lead/VA/overhead.
  // Working days elapsed this month
  let mtdWorkdays = 0;
  for (let d = new Date(monthStart); d <= now && mtdWorkdays < goal.workingDays; d.setDate(d.getDate() + 1)) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) mtdWorkdays++;
  }
  const mtdFraction = goal.workingDays > 0 ? mtdWorkdays / goal.workingDays : 0;
  const leadSpendMtd = (leads || [])
    .filter(l => {
      const ca = Date.parse(l.createdAt || '');
      return Number.isFinite(ca) && ca >= monthStart.getTime();
    })
    .reduce((s, l) => s + (Number(l.cost) || 0), 0);
  const vaCostMtd = vasNeededForGoal * goal.vaBasePay * mtdFraction +
    (leads || []).filter(l => l.status === 'closed' && Date.parse(l.closedAt || l.updatedAt || '') >= monthStart.getTime()).length * goal.vaBonusPerSale;
  const overheadMtd = cfg.overheadMonthly * mtdFraction;

  // Estimated override paid out from gross. Producer keeps avgContract%; agency keeps (1 - avgContract%) as override.
  // For MTD we approximate override as the spread — this is a starting
  // model; per-producer rates configured on the producer object would
  // refine it further.
  const overrideMtd = mtd.gross * (1 - cfg.avgProducerContractLevel);
  const producerPaidMtd = mtd.gross - overrideMtd;

  const pnl = (gross, cb, leadSp, vaCost, overhead) => {
    const netCommission = gross - cb;
    const overridePaid = netCommission * (1 - cfg.avgProducerContractLevel);
    const producerPaid = netCommission - overridePaid;
    const allCosts = overridePaid + leadSp + vaCost + overhead;
    const netContribution = netCommission - leadSp - vaCost - overhead - overridePaid;
    return { gross, cb, netCommission, overridePaid, producerPaid, leadSp, vaCost, overhead, netContribution };
  };
  const pnlMtd = pnl(mtd.gross, mtd.chargebacks, leadSpendMtd, vaCostMtd, overheadMtd);
  const pnl90  = pnl(t90.gross, t90.chargebacks, leadSpend90, vaCost90, overhead90);
  const pnlYtd = pnl(
    ytd.gross,
    ytd.chargebacks,
    (leads || []).filter(l => Date.parse(l.createdAt || '') >= ytdStart.getTime()).reduce((s, l) => s + (Number(l.cost) || 0), 0),
    vasNeededForGoal * goal.vaBasePay * ((now.getTime() - ytdStart.getTime()) / DAY / 30) + (leads || []).filter(l => l.status === 'closed' && Date.parse(l.closedAt || l.updatedAt || '') >= ytdStart.getTime()).length * goal.vaBonusPerSale,
    cfg.overheadMonthly * ((now.getTime() - ytdStart.getTime()) / DAY / 30),
  );

  // ── Cash flow projection (next 30 days) ────────────────────────────
  // Cash IN: expected advance commissions on next-30-day deals
  const nextMonthDealForecast = Math.ceil(goal.monthlyTarget / realizedFirstYearComm);
  const expectedCashIn30 = nextMonthDealForecast * goal.avgAnnualPremium * goal.commissionRate * cfg.advanceRate;
  // Cash OUT
  const expectedLeadSpend30 = ((leads || []).filter(l => Date.parse(l.createdAt || '') >= monthStart.getTime()).reduce((s,l) => s + (Number(l.cost)||0), 0)) / Math.max(1, mtdWorkdays) * goal.workingDays;
  const expectedVaCost30 = vasNeededForGoal * goal.vaBasePay + nextMonthDealForecast * goal.vaBonusPerSale;
  const expectedOverhead30 = cfg.overheadMonthly;
  const expectedOverridePaid30 = expectedCashIn30 * (1 - cfg.avgProducerContractLevel);
  const expectedCashOut30 = expectedLeadSpend30 + expectedVaCost30 + expectedOverhead30 + expectedOverridePaid30;
  const netCash30 = expectedCashIn30 - expectedCashOut30;
  // Chargeback reserve: hold 25% of trailing 90-day gross to cover lapses
  const chargebackReserveNeeded = t90.gross * goal.lapseRate;

  // ── Producer contribution margins ──────────────────────────────────
  const producerContrib = React.useMemo(() => {
    // Group transactions by prodNum for trailing 90 days.
    const byProd = {};
    for (const tx of t90Tx) {
      const k = tx.prodNum || '(unassigned)';
      if (!byProd[k]) byProd[k] = { prodNum: k, gross: 0, chargebacks: 0, policies: new Set() };
      const v = Number(tx.netToAccount) || 0;
      if (v >= 0) byProd[k].gross += v; else byProd[k].chargebacks += Math.abs(v);
      if (tx.policy) byProd[k].policies.add(`${tx.mga || ''}|${tx.policy}`);
    }
    return Object.values(byProd)
      .map(r => {
        const net = r.gross - r.chargebacks;
        const override = net * (1 - cfg.avgProducerContractLevel);
        const producerNetTake = net - override;
        const name = getProducerName ? getProducerName(r.prodNum, producers) : r.prodNum;
        return {
          ...r,
          name,
          policyCount: r.policies.size,
          net,
          override,
          producerNetTake,
          chargebackRate: r.gross > 0 ? r.chargebacks / r.gross : 0,
        };
      })
      .filter(r => r.gross > 0 || r.chargebacks > 0)
      .sort((a, b) => b.override - a.override);
  }, [t90Tx, cfg.avgProducerContractLevel, producers]);

  // ── Override breakdown by carrier ──────────────────────────────────
  const overrideByCarrier = React.useMemo(() => {
    const byCarrier = {};
    for (const tx of t90Tx) {
      const c = (typeof deriveCarrier === 'function' ? deriveCarrier(tx) : (tx.carrier || 'Other')) || 'Other';
      if (!byCarrier[c]) byCarrier[c] = { carrier: c, gross: 0, chargebacks: 0, policyCount: 0 };
      const v = Number(tx.netToAccount) || 0;
      if (v >= 0) byCarrier[c].gross += v; else byCarrier[c].chargebacks += Math.abs(v);
      if (tx.policy && v > 0) byCarrier[c].policyCount++;
    }
    return Object.values(byCarrier)
      .map(r => {
        const net = r.gross - r.chargebacks;
        const override = net * (1 - cfg.avgProducerContractLevel);
        return { ...r, net, override, chargebackRate: r.gross > 0 ? r.chargebacks / r.gross : 0 };
      })
      .sort((a, b) => b.gross - a.gross);
  }, [t90Tx, cfg.avgProducerContractLevel]);

  // ── Enterprise value ───────────────────────────────────────────────
  // Industry standard: 1.5-3.0× annualized in-force AP for FEX/MP agencies.
  // Persistency-adjusted: discount the multiple by lapse-rate exposure.
  // Active AP = sum of premium on policies that haven't lapsed.
  const policyMap = React.useMemo(() => {
    const m = {};
    for (const tx of transactions) {
      if (!tx.policy) continue;
      const k = `${tx.prodNum || ''}|${tx.mga || ''}|${tx.policy}`;
      if (!m[k]) m[k] = { policy: tx.policy, premium: 0, lapsed: false, totalCommission: 0 };
      const v = Number(tx.netToAccount) || 0;
      m[k].totalCommission += v;
      if (Number(tx.commPremium) && Math.abs(Number(tx.commPremium)) > m[k].premium) m[k].premium = Math.abs(Number(tx.commPremium));
      if (v < 0) m[k].lapsed = true;
    }
    return Object.values(m);
  }, [transactions]);

  const activePolicies = policyMap.filter(p => !p.lapsed && p.totalCommission > 0);
  const inForceMonthlyAP = activePolicies.reduce((s, p) => s + p.premium, 0);
  const inForceAnnualAP = inForceMonthlyAP * 12;
  const persistencyRate = policyMap.length > 0 ? activePolicies.length / policyMap.length : 0;
  // Adjust the multiple by persistency strength
  const adjustedMultiple = cfg.enterpriseValueMultiple * Math.max(0.5, Math.min(1.3, persistencyRate / 0.75));
  const enterpriseValue = inForceAnnualAP * adjustedMultiple;

  // ── Health flags ───────────────────────────────────────────────────
  // EMPTY-BOOK GUARD: every flag here compares against data the
  // operator hasn't entered yet. On day one the defaults (overhead +
  // VA + zero-policy persistency) fire as red alarms even though
  // nothing is wrong. Skip all flags until there's a real signal —
  // a real policy on book, or real chargebacks on the ledger.
  const hasRealData = policyMap.length > 0 || mtd.chargebackRate > 0;
  const flags = [];
  if (hasRealData) {
    if (ltvCacRatio != null && ltvCacRatio < 3)  flags.push({ tone: 'red',    label: `LTV/CAC at ${ltvCacRatio.toFixed(1)}× — below the 3× healthy threshold. Cut low-ROI lead sources.` });
    if (pnlMtd.netContribution < 0)               flags.push({ tone: 'red',    label: `Net contribution NEGATIVE this month (${fmt(pnlMtd.netContribution)}). Lead spend or VA cost is outpacing commission.` });
    if (mtd.chargebackRate > 0.25)                flags.push({ tone: 'amber',  label: `Chargeback rate ${(mtd.chargebackRate*100).toFixed(0)}% this month — above 25% healthy ceiling. Look at carrier mix + retention.` });
    if (persistencyRate < 0.70)                   flags.push({ tone: 'amber',  label: `Lifetime persistency ${(persistencyRate*100).toFixed(0)}% — below 70% threshold. Enterprise value is depressed.` });
    if (chargebackReserveNeeded > netCash30 + pnlMtd.netContribution) flags.push({ tone: 'amber', label: `Chargeback reserve (${fmt(chargebackReserveNeeded)}) exceeds projected 30d net cash. Build reserve before scaling spend.` });
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 via-emerald-900 to-emerald-700 text-white rounded-2xl p-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-emerald-200 text-xs font-semibold uppercase tracking-wider">CFO Dashboard</div>
          <h2 className="text-3xl font-bold mt-1">The actual money math</h2>
          <p className="text-emerald-100 text-sm mt-2 max-w-3xl">
            Unit economics. Real P&L. LTV vs CAC. 30-day cash projection. Per-producer contribution margins.
            Override accounting by carrier. Enterprise value. Built from transactions + leads + calls; tunable
            for the inputs the data can't supply.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => onGotoTab('ledger')}
            className="bg-white text-violet-700 hover:bg-violet-50 px-4 py-2.5 rounded-lg text-sm font-bold flex items-center gap-2 border-2 border-white shadow-lg"
            title="LEDGER reconciles every policy's expected vs received commission penny-exact + projects 12-mo cash flow"
          >
            📊 Open LEDGER →
          </button>
          <button
            onClick={openAccountant}
            className="bg-white/15 hover:bg-white/25 backdrop-blur text-white px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 border border-white/20"
            title="Ask the AI Accountant"
          >
            Ask Accountant
          </button>
        </div>
      </div>

      {/* LEDGER cross-link banner — surfaces the reconciliation +
          12-month cash flow forecast that already exist on the LEDGER
          tab (Ships 2 + 5 of the LEDGER engine). Per audit response
          §13 Phase 6 "no reconciliation workflow" + "no cash flow
          projection" — both shipped, the P&L user just couldn't
          discover them. */}
      <div className="bg-gradient-to-r from-violet-50 to-emerald-50 border-2 border-violet-200 rounded-xl p-4 flex items-start gap-3 flex-wrap">
        <div className="text-2xl">📊</div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-slate-900">Want penny-exact reconciliation + 12-month cash flow forecast?</div>
          <div className="text-sm text-slate-700 mt-0.5">
            LEDGER reconciles every policy's expected commission against carrier statements and projects 12 months of cash flow persistency-adjusted by product family. Both shipped. <strong>This dashboard is the strategic P&L; LEDGER is the operational reconciliation engine.</strong>
          </div>
        </div>
        <button
          onClick={() => onGotoTab('ledger')}
          className="bg-violet-600 hover:bg-violet-700 text-white font-semibold text-sm px-3 py-1.5 rounded shrink-0"
        >
          Open LEDGER →
        </button>
      </div>

      {/* Health flags up top */}
      {flags.length > 0 && (
        <div className="space-y-2">
          {flags.map((f, i) => (
            <div key={i} className={`bg-${f.tone}-50 border border-${f.tone}-200 text-${f.tone}-900 rounded-xl p-3 text-sm`}>
              ⚠ {f.label}
            </div>
          ))}
        </div>
      )}

      {/* Assumption inputs */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
          <div>
            <h3 className="font-semibold text-slate-900">Financial assumptions</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Tune these to your shop's actual rates. Defaults match FEX/MP industry standards.
              These drive every calculation below.
            </p>
          </div>
          <button onClick={() => setFinancialConfig({ ...DEFAULT_FINANCIAL_CONFIG })} className="text-xs text-slate-500 hover:text-slate-700 underline">
            Reset to defaults
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <GoalField label="Overhead / mo (rent, software, E&O)" prefix="$" value={cfg.overheadMonthly} onChange={v => set('overheadMonthly', v)}/>
          <GoalField label="Advance % (first-year commission)"   suffix="%" value={cfg.advanceRate * 100} onChange={v => set('advanceRate', v, 'pct')}/>
          <GoalField label="Months advanced"                     value={cfg.monthsAdvanced} onChange={v => set('monthsAdvanced', v)}/>
          <GoalField label="Renewal rate / year"                 suffix="%" value={cfg.renewalRate * 100} onChange={v => set('renewalRate', v, 'pct')}/>
          <GoalField label="Expected client lifetime (yrs)"      value={cfg.expectedClientYears} onChange={v => set('expectedClientYears', v)} step="0.5"/>
          <GoalField label="Avg producer contract level"         suffix="%" value={cfg.avgProducerContractLevel * 100} onChange={v => set('avgProducerContractLevel', v, 'pct')}/>
          <GoalField label="Enterprise value multiple"           value={cfg.enterpriseValueMultiple} onChange={v => set('enterpriseValueMultiple', v)} step="0.1"/>
          <button onClick={() => onGotoTab('fex')} className="self-end bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg py-2.5">
            More levers in FEX → Engine →
          </button>
        </div>
      </div>

      {/* Unit Economics — the punch line */}
      <div className="bg-white border-2 border-emerald-300 rounded-2xl p-5">
        <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Unit economics per deal</div>
        <p className="text-xs text-slate-500 mt-0.5 mb-3">The math behind whether your business model works.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <BigStatTile label="LTV per deal"      value={fmt(ltvPerDeal)}     sub={`${fmt(realizedFirstYearComm)} yr-1 + ${fmt(renewalLifetimeValue)} renewals`} tone="emerald"/>
          <BigStatTile label="Blended CAC"       value={blendedCAC == null ? '—' : fmt(blendedCAC)}     sub={dealsClosed90 > 0 ? `${fmt(acquisitionCost90)} ÷ ${dealsClosed90} closed` : 'no closes in 90d'} tone="amber"/>
          <BigStatTile label="LTV / CAC ratio"   value={ltvCacRatio == null ? '—' : `${ltvCacRatio.toFixed(1)}×`} sub={ltvCacRatio == null ? 'need closes' : ltvCacRatio >= 3 ? 'healthy ≥3×' : 'below 3× — fix CAC or LTV'} tone={ltvCacRatio == null ? 'slate' : ltvCacRatio >= 5 ? 'emerald' : ltvCacRatio >= 3 ? 'amber' : 'red'}/>
          <BigStatTile label="Contribution margin / deal"  value={contributionMargin == null ? '—' : `${(contributionMargin * 100).toFixed(0)}%`} sub={contributionPerDeal == null ? '—' : `${fmt(contributionPerDeal)} per close`} tone={contributionMargin == null ? 'slate' : contributionMargin > 0.5 ? 'emerald' : 'amber'}/>
        </div>
      </div>

      {/* P&L — three periods side by side */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h3 className="font-semibold text-slate-900">Profit & Loss</h3>
        <p className="text-xs text-slate-500 mt-0.5 mb-3">
          Gross commission, chargebacks, costs, and net contribution.
          MTD uses elapsed working days for cost proration so partial months don't look better than they are.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Line</th>
                <th className="px-3 py-2 text-right">Month-to-Date</th>
                <th className="px-3 py-2 text-right">Trailing 90 Days</th>
                <th className="px-3 py-2 text-right">Year-to-Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <PnlRow label="Gross commission written"       a={pnlMtd.gross} b={pnl90.gross} c={pnlYtd.gross}/>
              <PnlRow label="− Chargebacks"                  a={-pnlMtd.cb} b={-pnl90.cb} c={-pnlYtd.cb}/>
              <PnlRow label="= Net commission"               a={pnlMtd.netCommission} b={pnl90.netCommission} c={pnlYtd.netCommission} bold/>
              <PnlRow label="− Override paid (producer cut)" a={-pnlMtd.overridePaid} b={-pnl90.overridePaid} c={-pnlYtd.overridePaid} dim/>
              <PnlRow label="= Producer net take"            a={pnlMtd.producerPaid} b={pnl90.producerPaid} c={pnlYtd.producerPaid} dim/>
              <PnlRow label="− Lead spend"                   a={-pnlMtd.leadSp} b={-pnl90.leadSp} c={-pnlYtd.leadSp}/>
              <PnlRow label="− VA cost"                      a={-pnlMtd.vaCost} b={-pnl90.vaCost} c={-pnlYtd.vaCost}/>
              <PnlRow label="− Overhead"                     a={-pnlMtd.overhead} b={-pnl90.overhead} c={-pnlYtd.overhead}/>
              <tr className="border-t-2 border-slate-300 font-bold">
                <td className="px-3 py-2 text-slate-900">Net contribution (agency take-home)</td>
                <td className={`px-3 py-2 text-right tabular-nums ${pnlMtd.netContribution >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(pnlMtd.netContribution)}</td>
                <td className={`px-3 py-2 text-right tabular-nums ${pnl90.netContribution >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(pnl90.netContribution)}</td>
                <td className={`px-3 py-2 text-right tabular-nums ${pnlYtd.netContribution >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(pnlYtd.netContribution)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Cash flow projection. The deal forecast comes from the Goal
          Engine targets, NOT real pipeline data. We label it that way
          so an empty-pipeline operator reading "$20,400 cash in" knows
          they're seeing a target projection, not a guaranteed result. */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h3 className="font-semibold text-slate-900">30-day cash flow projection · target-based</h3>
        <p className="text-xs text-slate-500 mt-0.5 mb-3">
          Cash math at the operator's bank — advance commissions in, costs + override out.
          Plus the chargeback reserve you need to hold to survive a bad month.
          <span className="block mt-1 italic text-slate-400">
            Forecast deals come from your Goal Engine targets, not your live pipeline. Treat this as a model — adjust targets in Settings if it feels off.
          </span>
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <BigStatTile label="Cash in (advances · target)" value={fmt(expectedCashIn30)}    sub={`${nextMonthDealForecast} forecast deals × ${(cfg.advanceRate * 100).toFixed(0)}% advance`} tone="emerald"/>
          <BigStatTile label="Cash out"                  value={fmt(expectedCashOut30)}  sub="overrides + leads + VA + overhead" tone="amber"/>
          <BigStatTile label="Net 30-day cash"           value={fmt(netCash30)}          sub={netCash30 >= 0 ? 'positive' : 'NEGATIVE'} tone={netCash30 >= 0 ? 'emerald' : 'red'}/>
          <BigStatTile label="Chargeback reserve needed" value={fmt(chargebackReserveNeeded)} sub={`${(goal.lapseRate * 100).toFixed(0)}% × 90d gross`} tone="red"/>
        </div>
        <div className="mt-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3 text-xs">
          <strong>Working capital floor:</strong> hold at least <strong>{fmt(chargebackReserveNeeded + expectedCashOut30)}</strong> in cash
          (90-day chargeback reserve + 30-day operating costs) before scaling lead spend.
        </div>
      </div>

      {/* Producer contribution */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h3 className="font-semibold text-slate-900">Producer contribution (trailing 90 days)</h3>
        <p className="text-xs text-slate-500 mt-0.5 mb-3">
          Override paid per producer = the agency's cut. Sorted by override value desc — that's who's funding the agency right now.
        </p>
        {producerContrib.length === 0 ? (
          <div className="text-sm text-slate-500 italic py-6 text-center">No producer activity in the last 90 days.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Producer</th>
                  <th className="px-3 py-2 text-right">Policies</th>
                  <th className="px-3 py-2 text-right">Gross</th>
                  <th className="px-3 py-2 text-right">Chargebacks</th>
                  <th className="px-3 py-2 text-right">CB Rate</th>
                  <th className="px-3 py-2 text-right">Net</th>
                  <th className="px-3 py-2 text-right">Producer Take</th>
                  <th className="px-3 py-2 text-right">Override (agency)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {producerContrib.slice(0, 20).map(r => (
                  <tr key={r.prodNum}>
                    <td className="px-3 py-2 font-medium text-slate-800">{r.name}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.policyCount}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(r.gross)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-red-700">{r.chargebacks > 0 ? `(${fmt(r.chargebacks)})` : '—'}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${r.chargebackRate > 0.25 ? 'text-red-700 font-semibold' : 'text-slate-600'}`}>{(r.chargebackRate * 100).toFixed(0)}%</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmt(r.net)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">{fmt(r.producerNetTake)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-emerald-700">{fmt(r.override)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Override by carrier */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h3 className="font-semibold text-slate-900">Override by carrier (trailing 90 days)</h3>
        <p className="text-xs text-slate-500 mt-0.5 mb-3">
          Where the agency override is coming from. High chargeback rates per carrier = renegotiate or reduce share.
        </p>
        {overrideByCarrier.length === 0 ? (
          <div className="text-sm text-slate-500 italic py-6 text-center">No carrier activity in the last 90 days.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Carrier</th>
                  <th className="px-3 py-2 text-right">Policies</th>
                  <th className="px-3 py-2 text-right">Gross</th>
                  <th className="px-3 py-2 text-right">CB Rate</th>
                  <th className="px-3 py-2 text-right">Net</th>
                  <th className="px-3 py-2 text-right">Override</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {overrideByCarrier.map(r => (
                  <tr key={r.carrier}>
                    <td className="px-3 py-2 font-medium text-slate-800">{r.carrier}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.policyCount}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(r.gross)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${r.chargebackRate > 0.25 ? 'text-red-700 font-semibold' : 'text-slate-600'}`}>{(r.chargebackRate * 100).toFixed(0)}%</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmt(r.net)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-emerald-700">{fmt(r.override)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Enterprise value */}
      <div className="bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 text-white rounded-2xl p-6">
        <div className="text-emerald-300 text-xs font-semibold uppercase tracking-wider">Estimated enterprise value</div>
        <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-300">In-force monthly AP</div>
            <div className="text-2xl font-bold tabular-nums">{fmt(inForceMonthlyAP)}</div>
            <div className="text-[10px] text-emerald-200 mt-0.5">{activePolicies.length} active policies</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-300">Annualized AP</div>
            <div className="text-2xl font-bold tabular-nums">{fmt(inForceAnnualAP)}</div>
            <div className="text-[10px] text-emerald-200 mt-0.5">× 12 months</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-300">Persistency-adjusted multiple</div>
            <div className="text-2xl font-bold tabular-nums">{adjustedMultiple.toFixed(1)}×</div>
            <div className="text-[10px] text-emerald-200 mt-0.5">{cfg.enterpriseValueMultiple}× × {(persistencyRate * 100).toFixed(0)}% persist / 75% baseline</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-300">Sellable enterprise value</div>
            <div className="text-3xl font-bold tabular-nums text-emerald-300">{fmt(enterpriseValue)}</div>
            <div className="text-[10px] text-emerald-200 mt-0.5">indicative range for FEX/MP book sale</div>
          </div>
        </div>
        <div className="mt-4 text-xs text-emerald-200/80">
          Industry multiples for FEX/MP agency books typically run 1.5–3.0× annualized in-force AP.
          Persistency adjustment caps at 1.3× (excellent book) and floors at 0.5× (high-lapse book).
          A clean 80%+ persistency book gets the top multiple; chargeback-heavy books trade at a discount.
        </div>
      </div>

      {/* Methodology footer */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600">
        <strong>Methodology:</strong> Gross commission and chargebacks pulled from your transactions array directly.
        Lead spend from <code>leads[].cost</code> in the period. VA cost = vasNeeded × base pay × period fraction + bonus × closes.
        Overhead is a flat monthly input you set above. Override paid out estimated as net commission × (1 − producer contract %)
        — refine by setting actual per-producer contract levels in Producers tab (Phase 2). LTV includes first-year realized commission + renewal stream × expected client lifetime. CAC is blended across all costs.
        Cash flow uses your Goal Engine forecast for next-month deals × advance %.
      </div>
    </div>
  );
}

// Reusable P&L row — 3 numeric columns with proper neg / pos formatting
function PnlRow({ label, a, b, c, bold = false, dim = false }) {
  const cls = (v) =>
    `px-3 py-2 text-right tabular-nums ${bold ? 'font-semibold' : ''} ${dim ? 'text-slate-500' : ''}`;
  const fmtVal = (v) => v < 0 ? `(${fmt(Math.abs(v))})` : fmt(v);
  return (
    <tr>
      <td className={`px-3 py-2 ${bold ? 'font-semibold text-slate-900' : 'text-slate-700'} ${dim ? 'text-slate-500 pl-6' : ''}`}>{label}</td>
      <td className={cls(a)}>{fmtVal(a)}</td>
      <td className={cls(b)}>{fmtVal(b)}</td>
      <td className={cls(c)}>{fmtVal(c)}</td>
    </tr>
  );
}

function BigStatTile({ label, value, sub, tone = 'slate', tooltip = null }) {
  return (
    <div
      className={`bg-${tone}-50 border border-${tone}-200 rounded-xl p-3 ${tooltip ? 'cursor-help' : ''}`}
      title={tooltip || undefined}
    >
      <div className={`text-[10px] font-bold uppercase tracking-wider text-${tone}-700`}>{label}</div>
      <div className={`text-2xl md:text-3xl font-bold tabular-nums mt-1 text-${tone}-700`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Apps — Underwriting Pipeline
//
// Closes the placement-rate gap. The single biggest missing metric in
// life-insurance distribution: applications submitted → applications
// placed. Top performers track this weekly; the framework says 70%+
// healthy, <55% distressed. Lazy agents submit apps then forget about
// them — carrier closes the file after 14-30 days inactive, lead cost
// is gone, and the chargeback might still hit.
//
// Kanban layout — 4 columns matching the life-UW funnel:
//   Submitted          (carrier hasn't started UW yet)
//   Pending UW         (UW reviewing)
//   Pending Reqs       (carrier needs paramed / APS / MIB / inspection)
//   Approved / Placed  (UW done; first draft may have cleared)
//
// Decisions stay reachable via the status select (declined / withdrawn).
//
// Each card shows: client name, carrier+product, monthly premium,
// days in current stage, days since submitted, pending requirements,
// agent on file. Stuck cards (>7d no movement) get a red border —
// the lazy-agent worklist.
// ════════════════════════════════════════════════════════════════════

const APP_STATUSES = [
  { id: 'submitted',    label: 'Submitted',       tone: 'slate'   },
  { id: 'pending_uw',   label: 'Pending UW',      tone: 'amber'   },
  { id: 'pending_reqs', label: 'Pending Reqs',    tone: 'amber'   },
  { id: 'approved',     label: '✓ Approved',         tone: 'emerald' },
  { id: 'placed',       label: 'Placed',          tone: 'emerald' },
  { id: 'declined',     label: '✕ Declined',         tone: 'red'     },
  { id: 'withdrawn',    label: '↩ Withdrawn',        tone: 'slate'   },
];
const APP_STATUS_BY_ID = Object.fromEntries(APP_STATUSES.map(s => [s.id, s]));
const KANBAN_COLUMNS = ['submitted','pending_uw','pending_reqs','approved'];
const PRODUCT_TYPES = [
  { id: 'final_expense',       label: 'Final Expense' },
  { id: 'mortgage_protection', label: 'Mortgage Protection' },
  { id: 'iul',                 label: 'IUL' },
  { id: 'term',                label: 'Term' },
  { id: 'whole_life',          label: 'Whole Life' },
  { id: 'annuity',             label: 'Annuity' },
  { id: 'other',               label: 'Other' },
];
const COMMON_REQUIREMENTS = ['Paramed', 'APS', 'MIB', 'Phone interview', 'Inspection', 'EFT form', 'HIPAA auth', 'ID copy', 'Beneficiary form'];

// CarrierPortalLink — shared reusable component that maps an operator's
// free-text carrier name to the carriers catalog (migration 099) and
// renders a small portal/autopay link when there's a match. Used across
// AppsPipeline + retention surfaces + book-of-business to resolve the
// audit's recurring "operator has to Google the carrier name to find
// their portal" complaint (§11 + §15 + §16 of audit_response.md).
//
// We fetch the catalog ONCE per browser session and memoize it on
// window so every CarrierPortalLink instance reuses the same map.
// Initial render is silent (returns null) while the fetch resolves —
// avoids a flicker of "—" then "Portal ↗".
function useCarriersCatalog() {
  const [map, setMap] = React.useState(() => (typeof window !== 'undefined' && window.__crm_carriers_catalog) || null);
  React.useEffect(() => {
    if (map || typeof window === 'undefined') return;
    let alive = true;
    (async () => {
      try {
        const { data: rows } = await supabase
          .from('carriers')
          .select('display_name, portal_url, autopay_url, customer_service_phone')
          .eq('is_active', true);
        if (!alive) return;
        const m = {};
        for (const r of (rows || [])) {
          const key = String(r.display_name || '').toLowerCase().trim();
          if (key) m[key] = r;
          const first = key.split(/\s+/)[0];
          if (first && !m[first]) m[first] = r;
        }
        window.__crm_carriers_catalog = m;
        setMap(m);
      } catch {}
    })();
    return () => { alive = false; };
  }, [map]);
  return map;
}

function CarrierPortalLink({ carrierName, mode = 'app' }) {
  const catalog = useCarriersCatalog();
  if (!catalog || !carrierName) return null;
  const key = String(carrierName).toLowerCase().trim();
  const hit = catalog[key] || catalog[key.split(/\s+/)[0]] || null;
  const href = (mode === 'pay' && hit?.autopay_url) || hit?.portal_url;
  if (!href) return null;
  const label = mode === 'pay' ? 'Pay ↗' : 'Portal ↗';
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={`Open ${carrierName} ${mode === 'pay' ? 'payment portal' : 'agent/member portal'} in a new tab`}
      className="text-[10px] font-bold uppercase tracking-tight text-violet-700 bg-violet-50 border border-violet-200 hover:bg-violet-100 px-1.5 py-0.5 rounded shrink-0"
    >
      {label}
    </a>
  );
}

function AppsPipeline({ apps = [], setApps, leads = [], producers = {}, producerList = [], settings = {}, currentUser = null, onGotoTab = () => {} }) {
  const [editingApp, setEditingApp] = useState(null);
  const [filterAgent, setFilterAgent] = useState('all');
  const [filterCarrier, setFilterCarrier] = useState('all');

  // Aging math — runs every render but cheap.
  const enriched = React.useMemo(() => {
    const now = Date.now();
    const DAY = 1000 * 60 * 60 * 24;
    return (apps || []).map(a => {
      const subTs    = Date.parse(a.submittedDate || '');
      const touchTs  = Date.parse(a.lastTouchDate || a.updatedAt || a.createdAt || '');
      const daysSubmitted = Number.isFinite(subTs)   ? Math.floor((now - subTs)   / DAY) : null;
      const daysSinceTouch = Number.isFinite(touchTs) ? Math.floor((now - touchTs) / DAY) : null;
      const stuck = daysSinceTouch != null && daysSinceTouch >= 7 && !['approved','placed','declined','withdrawn'].includes(a.status);
      return { ...a, daysSubmitted, daysSinceTouch, stuck };
    });
  }, [apps]);

  // Apply filters
  const visible = enriched.filter(a => {
    if (filterAgent !== 'all' && a.agentProdNum !== filterAgent) return false;
    if (filterCarrier !== 'all' && a.carrier !== filterCarrier) return false;
    return true;
  });

  // Agent dropdown options
  const agentChoices = React.useMemo(() => {
    const seen = new Set();
    const opts = [];
    enriched.forEach(a => {
      if (a.agentProdNum && !seen.has(a.agentProdNum)) {
        seen.add(a.agentProdNum);
        opts.push({ prodNum: a.agentProdNum, name: getProducerName ? getProducerName(a.agentProdNum, producers) : a.agentProdNum });
      }
    });
    return opts.sort((a,b) => (a.name || '').localeCompare(b.name || ''));
  }, [enriched, producers]);

  const carrierChoices = React.useMemo(() => {
    const set = new Set();
    enriched.forEach(a => { if (a.carrier) set.add(a.carrier); });
    return Array.from(set).sort();
  }, [enriched]);

  // Placement rate calculations (trailing 90 days)
  const stats = React.useMemo(() => {
    const now = Date.now();
    const DAY = 1000 * 60 * 60 * 24;
    const cutoff = now - 90 * DAY;
    const recent = enriched.filter(a => {
      const ts = Date.parse(a.submittedDate || '');
      return Number.isFinite(ts) && ts >= cutoff;
    });
    const issued = recent.filter(a => ['approved','placed'].includes(a.status));
    const decided = recent.filter(a => ['approved','placed','declined','withdrawn'].includes(a.status));
    const placementRate = decided.length > 0 ? (issued.length / decided.length) * 100 : null;
    const pendingAP = enriched
      .filter(a => ['submitted','pending_uw','pending_reqs'].includes(a.status))
      .reduce((s, a) => s + (Number(a.monthlyPremium) || 0) * 12, 0);
    const stuckCount = enriched.filter(a => a.stuck).length;
    return { totalRecent: recent.length, issued: issued.length, declined: recent.filter(a => a.status === 'declined').length, placementRate, pendingAP, stuckCount, pendingCount: enriched.filter(a => !['approved','placed','declined','withdrawn'].includes(a.status)).length };
  }, [enriched]);

  function newApp() {
    setEditingApp({
      id: `app_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      leadId: '',
      clientName: '',
      agentProdNum: '',
      carrier: '',
      product: '',
      productType: 'final_expense',
      monthlyPremium: '',
      annualPremium: '',
      submittedDate: new Date().toISOString().slice(0, 10),
      effectiveDate: '',
      status: 'submitted',
      pendingRequirements: [],
      lastTouchDate: new Date().toISOString().slice(0, 10),
      notes: '',
      applicationNumber: '',
      policyNumber: '',
      createdAt: new Date().toISOString(),
      __new: true,
    });
  }

  function saveApp(rec) {
    const now = new Date().toISOString();
    if (rec.__new) {
      const { __new, ...clean } = rec;
      setApps(prev => [...prev, { ...clean, createdAt: now, updatedAt: now }]);
    } else {
      setApps(prev => prev.map(a => a.id === rec.id ? { ...a, ...rec, updatedAt: now } : a));
    }
    setEditingApp(null);
  }
  async function deleteApp(id) {
    if (!(await hqConfirm({
      title: 'Delete this app?',
      body: 'This cannot be undone.',
      confirmLabel: 'Delete app', danger: true,
    }))) return;
    setApps(prev => prev.filter(a => a.id !== id));
    setEditingApp(null);
  }
  function updateStatus(id, newStatus) {
    const now = new Date().toISOString();
    setApps(prev => prev.map(a => a.id === id ? { ...a, status: newStatus, lastTouchDate: now.slice(0, 10), updatedAt: now } : a));
  }

  const groupedByStatus = KANBAN_COLUMNS.map(s => ({
    status: s,
    apps: visible.filter(a => a.status === s),
  }));
  const decided = visible.filter(a => ['placed','declined','withdrawn'].includes(a.status));

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">Underwriting Pipeline</h2>
          <p className="text-sm text-slate-500 mt-1">
            Every application from submitted → placed. Closes the placement-rate visibility gap that costs lazy agents ~$4k/mo at $20k targets.
          </p>
        </div>
        <button onClick={newApp} className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-semibold">+ Submit App</button>
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <BigStatTile label="Pending apps" value={stats.pendingCount} sub={`${fmt(stats.pendingAP)} AP in pipeline`} tone="amber"/>
        <BigStatTile
          label="Placement rate (90d)"
          value={stats.placementRate == null ? '0%' : `${stats.placementRate.toFixed(0)}%`}
          sub={stats.placementRate == null ? 'No decisions yet in this window' : `${stats.issued}/${stats.issued + stats.declined} issued ÷ decided`}
          tone={stats.placementRate == null ? 'slate' : stats.placementRate >= 70 ? 'emerald' : stats.placementRate >= 55 ? 'amber' : 'red'}
          tooltip={stats.placementRate == null ? 'Placement rate = issued ÷ (issued + declined). It will populate once carriers start returning decisions on your pending apps.' : null}
        />
        <BigStatTile label="Issued (90d)" value={stats.issued} sub="approved or placed" tone="emerald"/>
        <BigStatTile label="Declined (90d)" value={stats.declined} sub="UW reject" tone="red"/>
        <BigStatTile label="Stuck (>7d no touch)" value={stats.stuckCount} sub="lazy-agent worklist" tone={stats.stuckCount > 0 ? 'red' : 'emerald'}/>
      </div>

      {/* Benchmark callout */}
      <div className={`border rounded-xl p-4 text-sm ${stats.placementRate == null ? 'bg-slate-50 border-slate-200 text-slate-700' : stats.placementRate >= 70 ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : stats.placementRate >= 55 ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-red-50 border-red-200 text-red-900'}`}>
        <strong>Benchmark:</strong> Elite distribution runs 70%+ placement rate. Healthy 55-70%. Distressed below 55%.
        Each 10pt improvement on $20k target ≈ <strong>$4k/mo</strong> recovered FYC. Top lever: chase pending requirements within 48h of carrier request.
      </div>

      {/* Filters */}
      {(agentChoices.length > 1 || carrierChoices.length > 1) && (
        <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-2 flex-wrap text-sm">
          {agentChoices.length > 1 && (
            <select value={filterAgent} onChange={e => setFilterAgent(e.target.value)} className="border border-slate-200 rounded px-2 py-1 text-xs bg-white">
              <option value="all">All agents</option>
              {agentChoices.map(a => <option key={a.prodNum} value={a.prodNum}>{a.name}</option>)}
            </select>
          )}
          {carrierChoices.length > 1 && (
            <select value={filterCarrier} onChange={e => setFilterCarrier(e.target.value)} className="border border-slate-200 rounded px-2 py-1 text-xs bg-white">
              <option value="all">All carriers</option>
              {carrierChoices.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          {(filterAgent !== 'all' || filterCarrier !== 'all') && (
            <button onClick={() => { setFilterAgent('all'); setFilterCarrier('all'); }} className="text-xs text-slate-500 hover:text-slate-700 underline">Reset filters</button>
          )}
        </div>
      )}

      {/* Kanban */}
      {visible.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-12 text-center">
          <div className="text-4xl mb-3"></div>
          <div className="text-lg font-semibold text-slate-700">No apps in your pipeline yet</div>
          <div className="text-sm text-slate-500 mt-1 mb-4">
            Track every submitted app from carrier intake through placement. Surfaces what's stuck.
          </div>
          <button onClick={newApp} className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold">
            + Submit your first app
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {groupedByStatus.map(col => {
            const s = APP_STATUS_BY_ID[col.status];
            return (
              <div key={col.status} className={`bg-${s.tone}-50 border border-${s.tone}-200 rounded-xl overflow-hidden`}>
                <div className={`bg-${s.tone}-100 px-3 py-2 flex items-center justify-between`}>
                  <div className={`text-xs font-bold uppercase tracking-wider text-${s.tone}-800`}>{s.label}</div>
                  <div className={`text-xs font-bold tabular-nums text-${s.tone}-800`}>{col.apps.length}</div>
                </div>
                <div className="p-2 space-y-2 min-h-[100px]">
                  {col.apps.length === 0 ? (
                    <div className="text-xs text-slate-400 italic text-center py-4">none</div>
                  ) : col.apps.map(a => (
                    <AppCard key={a.id} app={a} producers={producers} onClick={() => setEditingApp(a)} onStatusChange={(ns) => updateStatus(a.id, ns)}/>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Decided pile (collapsed) */}
      {decided.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <details>
            <summary className="cursor-pointer text-sm font-semibold text-slate-700">
              Decided ({decided.length}) — placed · declined · withdrawn
            </summary>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
              {decided.map(a => <AppCard key={a.id} app={a} producers={producers} onClick={() => setEditingApp(a)} onStatusChange={(ns) => updateStatus(a.id, ns)} compact/>)}
            </div>
          </details>
        </div>
      )}

      {/* Edit modal */}
      {editingApp && (
        <AppEditModal
          app={editingApp}
          leads={leads}
          producerList={producerList}
          producers={producers}
          onSave={saveApp}
          onDelete={() => deleteApp(editingApp.id)}
          onClose={() => setEditingApp(null)}
        />
      )}
    </div>
  );
}

function AppCard({ app, producers = {}, onClick, onStatusChange, compact = false }) {
  const s = APP_STATUS_BY_ID[app.status] || APP_STATUSES[0];
  const agent = app.agentProdNum && getProducerName ? getProducerName(app.agentProdNum, producers) : '';
  return (
    <div
      className={`bg-white rounded-lg shadow-sm border ${app.stuck ? 'border-red-400 ring-2 ring-red-100' : 'border-slate-200'} hover:shadow-md cursor-pointer p-2.5`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="font-semibold text-slate-900 text-sm truncate">{app.clientName || '(no name)'}</div>
        {app.stuck && <span className="text-[9px] font-bold text-red-600 bg-red-50 px-1 rounded shrink-0">STUCK</span>}
      </div>
      <div className="text-xs text-slate-500 truncate flex items-center gap-1">
        <span>{app.carrier || '—'}{app.product ? ` · ${app.product}` : ''}</span>
        {/* Carrier portal deep link — pulled from the catalog when
            the operator's free-text carrier name matches. Per audit
            §15 Phase 5b UNDERWRITING "carrier portal deep links
            from each pending application." */}
        <CarrierPortalLink carrierName={app.carrier} mode="app"/>
      </div>
      {app.monthlyPremium > 0 && (
        <div className="text-xs text-slate-600 tabular-nums">{fmt(app.monthlyPremium)}/mo · {fmt(app.monthlyPremium * 12)}/yr AP</div>
      )}
      {!compact && (
        <div className="flex items-center justify-between mt-1.5 text-[10px] text-slate-500">
          <span>{app.daysSubmitted != null ? `${app.daysSubmitted}d in pipeline` : ''}</span>
          {app.daysSinceTouch != null && <span className={app.stuck ? 'text-red-600 font-semibold' : ''}>{app.daysSinceTouch}d last touch</span>}
        </div>
      )}
      {app.pendingRequirements?.length > 0 && !compact && (
        <div className="text-[10px] text-amber-700 mt-1 truncate">
          {app.pendingRequirements.join(' · ')}
        </div>
      )}
      {agent && !compact && <div className="text-[10px] text-slate-400 mt-0.5">agent: {agent}</div>}
      {!compact && (
        <select
          value={app.status}
          onChange={(e) => { e.stopPropagation(); onStatusChange(e.target.value); }}
          onClick={e => e.stopPropagation()}
          className={`mt-2 w-full text-xs border border-${s.tone}-200 bg-${s.tone}-50 text-${s.tone}-800 rounded px-1 py-0.5 cursor-pointer`}
        >
          {APP_STATUSES.map(st => <option key={st.id} value={st.id}>{st.label}</option>)}
        </select>
      )}
    </div>
  );
}

function AppEditModal({ app, leads = [], producerList = [], producers = {}, onSave, onDelete, onClose }) {
  const [r, setR] = useState({ ...app });
  const set = (k, v) => setR(prev => ({ ...prev, [k]: v }));
  // Premium input mode toggle ('monthly' vs 'annual'). Carriers quote
  // year-1 comp on annual premium; leads talk in monthly. Letting the
  // agent enter either avoids ×12 / ÷12 typos and matches what the
  // /paycalc tab does.
  const [premiumMode, setPremiumMode] = React.useState('monthly');
  // Use a string staging value for the input so partial entries like
  // "12." don't get coerced to NaN/0 mid-keystroke. The numeric
  // monthlyPremium/annualPremium fields on `r` track the canonical
  // values for downstream math.
  const [premiumInput, setPremiumInput] = React.useState(() => String(r.monthlyPremium ?? ''));
  // Keep input synced if r.monthlyPremium changes via prop / reset.
  React.useEffect(() => {
    const expected = premiumMode === 'monthly' ? r.monthlyPremium : r.annualPremium;
    const expectedStr = expected == null ? '' : String(expected);
    if (premiumInput === '' && expectedStr) setPremiumInput(expectedStr);
  }, [r.monthlyPremium, r.annualPremium, premiumMode, premiumInput]);

  const setPremium = (rawTyped) => {
    const cleaned = sanitizeMoneyInput(rawTyped);
    setPremiumInput(cleaned);
    const n = Number(cleaned);
    const finite = Number.isFinite(n) && cleaned !== '' && cleaned !== '.';
    if (premiumMode === 'monthly') {
      setR(prev => ({
        ...prev,
        monthlyPremium: finite ? n : 0,
        annualPremium: finite ? Math.round(n * 12 * 100) / 100 : prev.annualPremium,
      }));
    } else {
      setR(prev => ({
        ...prev,
        annualPremium: finite ? n : 0,
        monthlyPremium: finite ? Math.round((n / 12) * 100) / 100 : prev.monthlyPremium,
      }));
    }
  };
  const switchPremiumMode = (mode) => {
    if (mode === premiumMode) return;
    setPremiumMode(mode);
    setPremiumInput(mode === 'monthly' ? String(r.monthlyPremium ?? '') : String(r.annualPremium ?? ''));
  };
  const matchedLead = leads.find(l => String(l.id) === String(r.leadId));

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="font-semibold text-slate-900">{r.__new ? '+ Submit App' : `Edit App · ${r.clientName || ''}`}</div>
          <button aria-label="Close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Client + agent */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <label className="block">
              <span className="text-xs text-slate-500">Client name</span>
              <input value={r.clientName} onChange={e => set('clientName', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">Link to lead (optional)</span>
              <select value={r.leadId || ''} onChange={e => {
                const id = e.target.value;
                const lead = leads.find(l => String(l.id) === id);
                setR(prev => ({ ...prev, leadId: id, clientName: lead?.name || prev.clientName, agentProdNum: lead?.agentProdNum || prev.agentProdNum }));
              }} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 bg-white">
                <option value="">— no lead match —</option>
                {leads.filter(l => l.name).slice(0, 100).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">Agent</span>
              <select value={r.agentProdNum || ''} onChange={e => set('agentProdNum', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 bg-white">
                <option value="">—</option>
                {producerList.map(p => <option key={p.prodNum} value={p.prodNum}>{getProducerName ? getProducerName(p.prodNum, producers) : p.prodNum}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">Product type</span>
              <select value={r.productType} onChange={e => set('productType', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 bg-white">
                {PRODUCT_TYPES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </label>
          </div>

          {/* Carrier + product + AP */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <label className="block">
              <span className="text-xs text-slate-500">Carrier</span>
              <input value={r.carrier} onChange={e => set('carrier', e.target.value)} placeholder="e.g. Mutual of Omaha" className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">Product / plan</span>
              <input value={r.product} onChange={e => set('product', e.target.value)} placeholder="e.g. Living Promise" className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
            </label>
            <label className="block">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-slate-500">
                  {premiumMode === 'monthly' ? 'Monthly premium ($)' : 'Annual premium ($)'}
                </span>
                <div className="inline-flex border border-slate-200 rounded overflow-hidden text-[10px]">
                  <button
                    type="button"
                    onClick={() => switchPremiumMode('monthly')}
                    className={`px-1.5 py-0.5 font-semibold ${premiumMode === 'monthly' ? 'bg-slate-800 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    onClick={() => switchPremiumMode('annual')}
                    className={`px-1.5 py-0.5 font-semibold ${premiumMode === 'annual' ? 'bg-slate-800 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                  >
                    Annual
                  </button>
                </div>
              </div>
              <input
                type="text"
                inputMode="decimal"
                value={premiumInput}
                onChange={e => setPremium(e.target.value)}
                placeholder="0.00"
                className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"
              />
              {Number(premiumInput) > 0 && (
                <div className="text-[10px] text-slate-400 mt-0.5">
                  {premiumMode === 'monthly'
                    ? `Annual: $${(r.annualPremium ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : `Monthly: $${(r.monthlyPremium ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                </div>
              )}
            </label>
          </div>

          {/* Status + dates + numbers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <label className="block">
              <span className="text-xs text-slate-500">Status</span>
              <select value={r.status} onChange={e => set('status', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 bg-white">
                {APP_STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">Submitted date</span>
              <input type="date" value={r.submittedDate} onChange={e => set('submittedDate', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">Last touch (carrier comm)</span>
              <input type="date" value={r.lastTouchDate} onChange={e => set('lastTouchDate', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">App # (optional)</span>
              <input
                value={r.applicationNumber}
                onChange={e => set('applicationNumber', e.target.value)}
                autoComplete="off" data-1p-ignore data-lpignore="true"
                name="app_number_no_autofill"
                className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"
              />
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">Policy # (when issued)</span>
              <input
                value={r.policyNumber}
                onChange={e => set('policyNumber', e.target.value)}
                autoComplete="off" data-1p-ignore data-lpignore="true"
                name="policy_number_no_autofill"
                className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"
              />
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">Effective date</span>
              <input type="date" value={r.effectiveDate} onChange={e => set('effectiveDate', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
            </label>
          </div>

          {/* Pending requirements — multi-select chips */}
          <div>
            <div className="text-xs text-slate-500 mb-1">Pending requirements</div>
            <div className="flex flex-wrap gap-1.5">
              {COMMON_REQUIREMENTS.map(req => {
                const has = (r.pendingRequirements || []).includes(req);
                return (
                  <button
                    key={req}
                    type="button"
                    onClick={() => set('pendingRequirements', has ? r.pendingRequirements.filter(x => x !== req) : [...(r.pendingRequirements || []), req])}
                    className={`text-xs rounded-full px-3 py-1 border ${has ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                  >
                    {has && '✓ '}{req}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <label className="block">
            <span className="text-xs text-slate-500">Notes</span>
            <textarea rows={3} value={r.notes} onChange={e => set('notes', e.target.value)} className="mt-1 w-full text-sm border border-slate-200 rounded px-2 py-1.5"/>
          </label>

          {/* ── Rich client profile sections ─────────────────────────
              Same fields the Client Profile view surfaces — captured
              once at submission so the data flows to underwriting +
              issue + retention without re-entry. Each section is
              collapsed by default so the modal stays scannable. */}

          {/* Contact & Address */}
          <details className="border border-slate-200 rounded">
            <summary className="cursor-pointer bg-slate-50 px-3 py-2 text-[0.65rem] font-black uppercase tracking-[0.12em] text-slate-700">Contact &amp; Address</summary>
            <div className="p-3 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <label className="block md:col-span-2">
                  <span className="text-xs text-slate-500">Address line 1</span>
                  <input value={r.address1 || ''} onChange={e => set('address1', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
                </label>
                <label className="block md:col-span-2">
                  <span className="text-xs text-slate-500">Address line 2</span>
                  <input value={r.address2 || ''} onChange={e => set('address2', e.target.value)} placeholder="Apt, suite, unit (optional)" className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">City</span>
                  <input value={r.city || ''} onChange={e => set('city', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label className="block">
                    <span className="text-xs text-slate-500">State</span>
                    <input value={r.stateAbbr || ''} onChange={e => set('stateAbbr', e.target.value.toUpperCase().slice(0, 2))} placeholder="TX" maxLength={2} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 uppercase font-mono"/>
                  </label>
                  <label className="block">
                    <span className="text-xs text-slate-500">ZIP</span>
                    <input value={r.zip || ''} onChange={e => set('zip', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 font-mono"/>
                  </label>
                </div>
                <label className="block">
                  <span className="text-xs text-slate-500">Phone</span>
                  <input value={r.phone || ''} onChange={e => set('phone', e.target.value)} placeholder="(xxx) xxx-xxxx" className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">Email</span>
                  <input type="email" value={r.email || ''} onChange={e => set('email', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
                </label>
              </div>
            </div>
          </details>

          {/* Demographics */}
          <details className="border border-slate-200 rounded">
            <summary className="cursor-pointer bg-slate-50 px-3 py-2 text-[0.65rem] font-black uppercase tracking-[0.12em] text-slate-700">Demographics</summary>
            <div className="p-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <label className="block">
                  <span className="text-xs text-slate-500">Date of birth</span>
                  <input type="date" value={r.dob || ''} onChange={e => set('dob', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">Gender</span>
                  <select value={r.gender || ''} onChange={e => set('gender', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 bg-white">
                    <option value="">—</option>
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                    <option value="X">Other</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">Age at issue</span>
                  <input type="number" value={r.ageAtIssue || ''} onChange={e => set('ageAtIssue', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 font-mono"/>
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">SSN</span>
                  <input
                    type={r._showSsn ? 'text' : 'password'}
                    value={r.ssn || ''}
                    onChange={e => set('ssn', e.target.value)}
                    placeholder="XXX-XX-XXXX"
                    className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 font-mono"
                  />
                  <button type="button" onClick={() => set('_showSsn', !r._showSsn)} className="text-[10px] text-slate-500 hover:text-slate-800 mt-0.5">{r._showSsn ? 'Hide' : 'Show'}</button>
                </label>
              </div>
            </div>
          </details>

          {/* Policy Owner / Secondary Insured */}
          <details className="border border-slate-200 rounded">
            <summary className="cursor-pointer bg-slate-50 px-3 py-2 text-[0.65rem] font-black uppercase tracking-[0.12em] text-slate-700">Policy Owner / Secondary Insured</summary>
            <div className="p-3 space-y-4">
              <div>
                <div className="text-[0.6rem] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Owner (if different from insured)</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <label className="block">
                    <span className="text-xs text-slate-500">Owner name</span>
                    <input value={r.ownerName || ''} onChange={e => set('ownerName', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
                  </label>
                  <label className="block">
                    <span className="text-xs text-slate-500">Owner DOB</span>
                    <input type="date" value={r.ownerDob || ''} onChange={e => set('ownerDob', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
                  </label>
                  <label className="block">
                    <span className="text-xs text-slate-500">Owner gender</span>
                    <select value={r.ownerGender || ''} onChange={e => set('ownerGender', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 bg-white">
                      <option value="">—</option>
                      <option value="M">Male</option>
                      <option value="F">Female</option>
                      <option value="X">Other</option>
                    </select>
                  </label>
                </div>
              </div>
              <div>
                <div className="text-[0.6rem] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Secondary insured (joint policies)</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <label className="block">
                    <span className="text-xs text-slate-500">Insured 2 name</span>
                    <input value={r.insured2Name || ''} onChange={e => set('insured2Name', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
                  </label>
                  <label className="block">
                    <span className="text-xs text-slate-500">Insured 2 DOB</span>
                    <input type="date" value={r.insured2Dob || ''} onChange={e => set('insured2Dob', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
                  </label>
                  <label className="block">
                    <span className="text-xs text-slate-500">Insured 2 gender</span>
                    <select value={r.insured2Gender || ''} onChange={e => set('insured2Gender', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 bg-white">
                      <option value="">—</option>
                      <option value="M">Male</option>
                      <option value="F">Female</option>
                      <option value="X">Other</option>
                    </select>
                  </label>
                </div>
              </div>
            </div>
          </details>

          {/* Banking info */}
          <details className="border border-slate-200 rounded">
            <summary className="cursor-pointer bg-slate-50 px-3 py-2 text-[0.65rem] font-black uppercase tracking-[0.12em] text-slate-700">Banking Info <span className="text-[0.55rem] font-normal text-slate-500 ml-2 normal-case">For premium draft</span></summary>
            <div className="p-3 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <label className="block">
                  <span className="text-xs text-slate-500">Bank name</span>
                  <input value={r.bankName || ''} onChange={e => set('bankName', e.target.value)} placeholder="Wells Fargo, Chase, etc." className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">Account holder name</span>
                  <input value={r.accountHolderName || ''} onChange={e => set('accountHolderName', e.target.value)} placeholder="If different from insured" className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">Account type</span>
                  <select value={r.accountType || ''} onChange={e => set('accountType', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 bg-white">
                    <option value="">— Select —</option>
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">Draft date</span>
                  <input value={r.draftDate || ''} onChange={e => set('draftDate', e.target.value)} placeholder="e.g. 15th, Last day, 1st Friday" className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">Routing number</span>
                  <input
                    type={r._showRouting ? 'text' : 'password'}
                    value={r.routingNumber || ''}
                    onChange={e => set('routingNumber', e.target.value.replace(/\D/g, '').slice(0, 9))}
                    placeholder="9 digits"
                    className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 font-mono"
                  />
                  <button type="button" onClick={() => set('_showRouting', !r._showRouting)} className="text-[10px] text-slate-500 hover:text-slate-800 mt-0.5">{r._showRouting ? 'Hide' : 'Show'}</button>
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">Account number</span>
                  <input
                    type={r._showAccount ? 'text' : 'password'}
                    value={r.accountNumber || ''}
                    onChange={e => set('accountNumber', e.target.value.replace(/\D/g, ''))}
                    className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 font-mono"
                  />
                  <button type="button" onClick={() => set('_showAccount', !r._showAccount)} className="text-[10px] text-slate-500 hover:text-slate-800 mt-0.5">{r._showAccount ? 'Hide' : 'Show'}</button>
                </label>
              </div>
              <label className="block">
                <span className="text-xs text-slate-500">Banking notes</span>
                <textarea rows={2} value={r.bankingNotes || ''} onChange={e => set('bankingNotes', e.target.value)} placeholder="e.g. account changed 03/14, switched from Chase to Wells, overdraft history, etc." className="mt-1 w-full text-sm border border-slate-200 rounded px-2 py-1.5"/>
              </label>
            </div>
          </details>

          {/* Beneficiaries */}
          <details className="border border-slate-200 rounded">
            <summary className="cursor-pointer bg-slate-50 px-3 py-2 text-[0.65rem] font-black uppercase tracking-[0.12em] text-slate-700">Beneficiaries</summary>
            <div className="p-3">
              {(r.beneficiaries || []).length === 0 && (
                <div className="text-xs text-slate-400 italic mb-3">No beneficiaries on file.</div>
              )}
              <div className="space-y-2">
                {(r.beneficiaries || []).map((b, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input
                      value={b.name || ''}
                      onChange={e => set('beneficiaries', (r.beneficiaries || []).map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                      placeholder="Name"
                      className="col-span-5 border border-slate-200 rounded px-2 py-1.5 text-sm"
                    />
                    <input
                      value={b.relationship || ''}
                      onChange={e => set('beneficiaries', (r.beneficiaries || []).map((x, j) => j === i ? { ...x, relationship: e.target.value } : x))}
                      placeholder="Relationship (spouse, daughter…)"
                      className="col-span-4 border border-slate-200 rounded px-2 py-1.5 text-sm"
                    />
                    <input
                      type="number"
                      value={b.percentage || ''}
                      onChange={e => set('beneficiaries', (r.beneficiaries || []).map((x, j) => j === i ? { ...x, percentage: e.target.value } : x))}
                      placeholder="%"
                      max={100}
                      className="col-span-2 border border-slate-200 rounded px-2 py-1.5 text-sm font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => set('beneficiaries', (r.beneficiaries || []).filter((_, j) => j !== i))}
                      className="col-span-1 text-xs text-red-600 hover:text-red-800"
                      title="Remove"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => set('beneficiaries', [...(r.beneficiaries || []), { name: '', relationship: '', percentage: '' }])}
                className="mt-3 text-[0.6rem] font-black uppercase tracking-tight bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5"
              >
                + Add Beneficiary
              </button>
            </div>
          </details>

          {/* Medical conditions */}
          <details className="border border-slate-200 rounded">
            <summary className="cursor-pointer bg-slate-50 px-3 py-2 text-[0.65rem] font-black uppercase tracking-[0.12em] text-slate-700">Medical Conditions <span className="text-[0.55rem] font-normal text-slate-500 ml-2 normal-case">Diagnoses, medications, height/weight, smoker status</span></summary>
            <div className="p-3">
              <textarea
                rows={3}
                value={r.medicalConditions || ''}
                onChange={e => set('medicalConditions', e.target.value)}
                placeholder="e.g. Type 2 diabetes (controlled, metformin) · former smoker (quit 2018) · BMI 27 · no other conditions"
                className="w-full text-sm border border-slate-200 rounded px-2 py-1.5"
              />
            </div>
          </details>

          {matchedLead && (
            <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs text-slate-600">
              Linked to lead: <strong>{matchedLead.name}</strong> · {matchedLead.phone || '—'} · status: {matchedLead.status || 'new'}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          {!r.__new && (
            <button onClick={onDelete} className="text-xs text-red-600 hover:text-red-700 underline">Delete</button>
          )}
          <div className="flex items-center gap-2 ml-auto">
            <button aria-label="Close" onClick={onClose} className="text-sm text-slate-600 hover:text-slate-800 font-semibold px-3 py-1.5">Cancel</button>
            <button onClick={() => onSave(r)} disabled={!r.clientName?.trim()} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg px-4 py-1.5">
              {r.__new ? 'Submit App' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// HOME — single-screen KPI dashboard
//
// The new default landing tab. Replaces "Dashboard shows onboarding
// cards even for existing users." Six numbers that answer "how am I
// doing right now?" without scrolling, each clickable to drill in.
//
// Operator-scoped: everything filters to the current user's own
// production via prodNum (settings.ownerProdNum for admin role;
// currentUser.prodNum for agent role). Same rule the rest of the app
// uses for "mine vs downline."
// ════════════════════════════════════════════════════════════════════

function KpiTile({ label, value, suffix = '', subtext, color = 'slate', onClick }) {
  // Numbers are the dashboard. 3.4rem / 900-weight so they read across
  // the room. Label + subtext shrink to micro-uppercase so they serve
  // the number, never compete with it.
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      onClick={onClick}
      className={`bg-white border-2 border-slate-200 ${onClick ? `hover:border-${color}-500 cursor-pointer hover:shadow-lg` : ''} p-5 text-left transition-all shadow-sm w-full`}
    >
      <div className="text-[0.58rem] font-bold uppercase tracking-[0.12em] text-slate-500">{label}</div>
      <div className={`text-[3.4rem] font-black text-${color}-700 mt-2 tabular-nums leading-none`}>
        {value}{suffix && <span className="text-lg font-bold text-slate-400 ml-1.5 align-baseline">{suffix}</span>}
      </div>
      {subtext && <div className="text-[0.58rem] uppercase tracking-[0.1em] text-slate-500 mt-4">{subtext}</div>}
    </Tag>
  );
}

// ────────────────────────────────────────────────────────────────────
// CRITICAL METRIC CARD — accountability tile with a progress bar.
// Used by the "Today's Critical Metrics" panel on the Home dashboard.
// Three of these: Dial Target, Placement Rate, Revenue Velocity.
// ────────────────────────────────────────────────────────────────────
function CriticalMetricCard({ label, current, target, displayCurrent, displayTarget, color = 'emerald', benchmark }) {
  const targetNum = Number(target) || 0;
  const pct = targetNum > 0 ? Math.min(100, Math.max(0, (Number(current) / targetNum) * 100)) : 0;
  const onTrack = pct >= 100;
  return (
    <div className={`bg-slate-50 border-2 border-slate-200 p-4 ${onTrack ? `border-${color}-400` : ''}`}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[0.58rem] font-bold uppercase tracking-[0.15em] text-slate-500">{label}</div>
        {benchmark && <div className={`text-[0.55rem] font-black uppercase tracking-[0.15em] text-${color}-700 bg-${color}-50 px-1.5 py-0.5`}>{benchmark}</div>}
      </div>
      <div className="flex items-baseline gap-2 mt-2">
        <div className={`text-[2.4rem] font-black text-${color}-700 tabular-nums leading-none`}>{displayCurrent}</div>
        <div className="text-[0.65rem] text-slate-400 uppercase tracking-wider font-bold">/ {displayTarget}</div>
      </div>
      <div className="h-2 bg-slate-200 mt-3 overflow-hidden">
        <div className={`h-full bg-${color}-500 transition-all`} style={{ width: `${pct}%` }}/>
      </div>
      <div className="flex justify-between mt-1.5 text-[0.55rem] uppercase tracking-[0.1em] font-bold">
        <span className="text-slate-500">{pct.toFixed(0)}% to target</span>
        <span className={onTrack ? `text-${color}-700` : 'text-slate-400'}>{onTrack ? '✓ ON TRACK' : 'PUSH'}</span>
      </div>
    </div>
  );
}

// New-Client Welcome panel — shows recently-issued policies (last 14
// days) that haven't been welcomed yet, with per-row actions to send
// a templated welcome SMS, complete the lead's profile, or add the
// policyholder as a new lead. Operator confirms each action — no
// auto-fire until A2P is approved AND the operator opts into auto-send
// (future toggle). Auto-hides when the queue is empty.
function NewClientWelcomePanel({
  transactions = [],
  leads = [],
  setLeads = () => {},
  settings = {},
  acknowledgedWelcomes = [],
  setAcknowledgedWelcomes = () => {},
  onGotoTab = () => {},
  showToast = () => {},
}) {
  const [sendingKey, setSendingKey] = React.useState(null);
  const WINDOW_DAYS = 14;
  const ackSet = React.useMemo(() => new Set(acknowledgedWelcomes || []), [acknowledgedWelcomes]);

  // Normalize a name for fuzzy matching. Same approach as the
  // conservation enrollment + persistency views.
  const normName = (s) => {
    const cleaned = String(s || '').toUpperCase().replace(/[^A-Z\s,]/g, '').replace(/\s+/g, ' ').trim();
    if (!cleaned) return '';
    return cleaned.split(/[\s,]+/).filter(Boolean).sort().join(' ');
  };

  // Build a name → lead map. First match wins.
  const leadByName = React.useMemo(() => {
    const m = new Map();
    (leads || []).forEach(l => {
      const k = normName(l.name);
      if (k && !m.has(k)) m.set(k, l);
    });
    return m;
  }, [leads]);

  // Derive the queue: unique (prod_num | policy) keys with an issueDate
  // within the last WINDOW_DAYS, excluding chargebacks and already-
  // acknowledged keys.
  const pending = React.useMemo(() => {
    const now = Date.now();
    const cutoff = now - WINDOW_DAYS * 24 * 60 * 60 * 1000;
    const seen = new Map();   // policyKey → first matching tx (for client-name display)
    for (const tx of (transactions || [])) {
      if (tx.isChargeback) continue;
      if (!tx.issueDate) continue;
      const d = Date.parse(tx.issueDate + 'T00:00:00');
      if (!Number.isFinite(d) || d < cutoff) continue;
      const key = `${tx.prodNum || ''}|${tx.policy || ''}`;
      if (!key.trim() || key === '|') continue;
      if (ackSet.has(key)) continue;
      if (seen.has(key)) continue;
      seen.set(key, tx);
    }
    // Build rows.
    const rows = [];
    for (const [key, tx] of seen) {
      const lead = leadByName.get(normName(tx.insured));
      let status;
      if (!lead)                                                          status = 'no_lead';
      else if (!lead.phone_e164 && !lead.phone)                           status = 'missing_phone';
      else if (lead.consent !== true)                                     status = 'missing_consent';
      else if (lead.opted_out === true || lead.optOut === true)           status = 'opted_out';
      else                                                                status = 'ready';
      rows.push({
        key,
        prodNum: tx.prodNum || '',
        policy: tx.policy || '',
        clientName: tx.insured || '(unknown)',
        carrier: tx.company || tx.mga || '',
        issueDate: tx.issueDate,
        lead,
        status,
      });
    }
    // Most recent issue dates first.
    rows.sort((a, b) => (b.issueDate || '').localeCompare(a.issueDate || ''));
    return rows;
  }, [transactions, ackSet, leadByName]);

  if (pending.length === 0) return null;

  const counts = {
    ready:           pending.filter(r => r.status === 'ready').length,
    missing_phone:   pending.filter(r => r.status === 'missing_phone').length,
    missing_consent: pending.filter(r => r.status === 'missing_consent').length,
    opted_out:       pending.filter(r => r.status === 'opted_out').length,
    no_lead:         pending.filter(r => r.status === 'no_lead').length,
  };

  const agentName = settings?.ownerName || 'your agent';
  const renderWelcomeBody = (row) => {
    const first = String(row.clientName || '').split(/[\s,]+/)[0] || 'there';
    const carrier = row.carrier ? ` your ${row.carrier} policy` : ' your new policy';
    const issueBit = row.issueDate ? ` (effective ${row.issueDate})` : '';
    return `Hey ${first}, this is ${agentName} — wanted to personally welcome you and confirm${carrier} is set up${issueBit}. I'm your point of contact for anything. Reach out anytime. Reply STOP to opt out.`;
  };

  const acknowledge = (key) => {
    setAcknowledgedWelcomes(prev => Array.from(new Set([...(prev || []), key])));
  };

  const sendWelcome = async (row) => {
    if (!row.lead) return;
    setSendingKey(row.key);
    try {
      const body = renderWelcomeBody(row);
      const r = await apiFetch('/api/sms/send-manual', {
        method: 'POST',
        body: JSON.stringify({ leadId: row.lead.id, body }),
      });
      if (r?.ok) {
        showToast(r.dryRun ? `Dry-run logged (SMS_SEND_ENABLED=false)` : `Welcome sent to ${row.clientName}`);
        acknowledge(row.key);
      } else {
        showToast(`Send failed: ${r?.error || 'unknown'}`, 'error');
      }
    } catch (err) {
      const msg = String(err.message || err);
      if (/consent missing/i.test(msg))   showToast(`${row.clientName}: needs consent before sending.`, 'error');
      else if (/opted out/i.test(msg))    showToast(`${row.clientName}: opted out.`, 'error');
      else if (/no E\.164/i.test(msg))    showToast(`${row.clientName}: phone not in E.164.`, 'error');
      else                                showToast(`Send failed: ${msg.slice(0, 120)}`, 'error');
    } finally {
      setSendingKey(null);
    }
  };

  const addAsLead = (row) => {
    const id = `lead_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    const now = new Date().toISOString();
    setLeads(prev => [...(prev || []), {
      id,
      name: row.clientName,
      phone: '',
      phone_e164: '',
      email: '',
      status: 'new',
      source: row.carrier || 'Imported from statement',
      vertical: 'life',
      sector: 'aca_private',
      createdAt: now,
      updatedAt: now,
      consent: false,
      activity: [{ at: now, type: 'created', body: `Added from new-policy statement (${row.carrier} ${row.policy})` }],
    }]);
    showToast(`Added ${row.clientName} as a lead — complete profile + capture TCPA consent next.`);
    onGotoTab('leads');
  };

  const STATUS_LABEL = {
    ready:           { text: 'Ready',                 color: 'emerald' },
    missing_phone:   { text: 'No phone',              color: 'amber'   },
    missing_consent: { text: 'No TCPA consent',       color: 'amber'   },
    opted_out:       { text: 'Opted out',             color: 'slate'   },
    no_lead:         { text: 'Not in pipeline',       color: 'slate'   },
  };

  return (
    <div className="bg-white border-2 border-emerald-300 ring-1 ring-emerald-100 rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-emerald-100 bg-emerald-50">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-sm font-bold text-emerald-900 uppercase tracking-tight">📨 New clients — welcome queue</h3>
            <div className="text-xs text-emerald-800 mt-0.5">
              {pending.length} polic{pending.length === 1 ? 'y' : 'ies'} issued in the last {WINDOW_DAYS} days · {counts.ready} ready to text · {counts.missing_phone + counts.missing_consent} need profile completion · {counts.no_lead} not in pipeline
            </div>
          </div>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {pending.slice(0, 25).map(row => {
          const meta = STATUS_LABEL[row.status];
          const busy = sendingKey === row.key;
          return (
            <div key={row.key} className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900 truncate">{row.clientName}</div>
                <div className="text-xs text-slate-500 truncate">
                  {row.carrier} · policy {row.policy} · issued {row.issueDate}
                  {' · '}
                  <span className={`font-semibold text-${meta.color}-700`}>{meta.text}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {row.status === 'ready' && (
                  <button
                    onClick={() => sendWelcome(row)}
                    disabled={busy}
                    className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold px-3 py-1.5 rounded-md"
                  >
                    {busy ? 'Sending…' : 'Send welcome'}
                  </button>
                )}
                {(row.status === 'missing_phone' || row.status === 'missing_consent') && (
                  <button
                    onClick={() => onGotoTab('leads')}
                    className="bg-amber-500 hover:bg-amber-600 text-slate-900 text-xs font-bold px-3 py-1.5 rounded-md"
                  >
                    Complete profile
                  </button>
                )}
                {row.status === 'no_lead' && (
                  <button
                    onClick={() => addAsLead(row)}
                    className="bg-sky-600 hover:bg-sky-700 text-white text-xs font-bold px-3 py-1.5 rounded-md"
                  >
                    Add as lead
                  </button>
                )}
                <button
                  onClick={() => acknowledge(row.key)}
                  className="text-xs text-slate-500 hover:text-slate-900 font-medium px-2 py-1"
                  title="Dismiss — hides this policy from the queue without sending"
                >
                  Dismiss
                </button>
              </div>
            </div>
          );
        })}
        {pending.length > 25 && (
          <div className="px-5 py-2 text-xs text-slate-500 text-center">
            …and {pending.length - 25} more. Welcome or dismiss the top batch to surface the rest.
          </div>
        )}
      </div>
    </div>
  );
}

// ReferralLinkShareCard — surfaces the operator's public landing
// page URL on Home so they share + use it day 1. Per audit response
// §9 (no consumer-facing booking link) + §10 + §15: the page is
// shipped with niche-aware sections + inline calendar booking;
// surfacing the URL is pure activation work. URL format is
// /p/<slug-from-ownerName> to match the marketing site's landing
// route. Dismissable per-session.
function ReferralLinkShareCard({ currentUser, settings = {}, showToast, onGotoTab }) {
  const [hidden, setHidden] = React.useState(() => {
    try { return sessionStorage.getItem('crm:home_referral_card_hidden') === '1'; } catch { return false; }
  });
  // Audit 2026-06-29 BUG-016: link was shown even when the operator
  // hadn't connected a Google Calendar — prospects clicking it would
  // land on a booking page with no slots. We don't hide the link
  // (operator still wants the URL for prep), but we surface a
  // warning + a one-click jump to Settings if calendar isn't linked.
  const [calLinked, setCalLinked] = React.useState(null);   // null = loading, true/false = known
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const jwt = session?.access_token;
        const r = await fetch('/api/google-calendar/status', { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} });
        const body = await r.json().catch(() => ({}));
        if (!alive) return;
        setCalLinked(Boolean(body?.linked || body?.connected || body?.refresh_token_present));
      } catch { if (alive) setCalLinked(false); }
    })();
    return () => { alive = false; };
  }, []);
  if (hidden) return null;
  const link = referralLandingUrl({ ownerName: settings?.ownerName, fallbackId: currentUser?.id });
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      showToast('Referral + booking link copied. Drop it anywhere — texts, email signature, biz card QR.');
    } catch {
      showToast('Copy failed — long-press the URL to copy manually.', 'error');
    }
  };
  const dismiss = () => {
    try { sessionStorage.setItem('crm:home_referral_card_hidden', '1'); } catch {}
    setHidden(true);
  };
  return (
    <div className="bg-gradient-to-br from-violet-50 to-violet-100 border-2 border-violet-200 rounded-xl p-4 flex items-start gap-3 flex-wrap">
      <div className="text-2xl">🔗</div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-violet-900">Your booking + referral link</div>
        <div className="text-xs text-violet-800 mt-0.5 leading-snug">
          Niche-aware landing page that lets prospects pick a time on your calendar directly. Drop it in your email signature, your biz card QR, or texts. Every booking shows up in your calendar automatically.
        </div>
        {calLinked === false && (
          <div className="mt-2 bg-amber-50 border border-amber-300 rounded px-2 py-1.5 text-[11px] text-amber-900 flex items-center gap-2 flex-wrap">
            <span>⚠ Google Calendar isn't connected yet — prospects who click the link see no available slots.</span>
            <button
              onClick={() => onGotoTab && onGotoTab('settings')}
              className="bg-amber-700 hover:bg-amber-800 text-white text-[10px] font-bold uppercase tracking-tight px-2 py-0.5 rounded"
            >
              Connect now
            </button>
          </div>
        )}
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <code className="text-xs bg-white border border-violet-200 rounded px-2 py-1 font-mono text-violet-900 max-w-full overflow-x-auto whitespace-nowrap">{link}</code>
          <button
            onClick={copy}
            className="bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold px-3 py-1.5 rounded"
          >
            Copy
          </button>
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white border border-violet-300 hover:bg-violet-50 text-violet-700 text-xs font-semibold px-3 py-1.5 rounded"
          >
            Preview ↗
          </a>
          <button
            onClick={() => onGotoTab && onGotoTab('referrals')}
            className="text-xs text-violet-700 hover:text-violet-900 font-semibold underline"
          >
            Customize
          </button>
        </div>
      </div>
      <button
        onClick={dismiss}
        className="text-violet-400 hover:text-violet-700 text-sm"
        title="Hide for this session"
      >
        ✕
      </button>
    </div>
  );
}

// NewUserWelcomeHero — the first thing a brand-new operator sees on
// the Home dashboard. Replaces the wall of zero-valued KPI tiles and
// dark "Critical Metrics" panel that made a fresh account feel
// broken. Focus is on tone (time-of-day greeting, first name) + three
// concrete "your next action is one of these" choices. Auto-hides
// the moment the user has any real data (transactions/leads/apps),
// so it's invisible to activated operators.
function NewUserWelcomeHero({ currentUser, onGotoTab, onLoadDemoData }) {
  const now = new Date();
  const hour = now.getHours();
  const partOfDay = hour < 5 ? 'evening' : hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
  const rawName = String(currentUser?.name || currentUser?.full_name || currentUser?.email || '').trim();
  const firstName = rawName.includes('@') ? rawName.split('@')[0] : rawName.split(/\s+/)[0];
  const greetingName = firstName ? `, ${firstName.charAt(0).toUpperCase()}${firstName.slice(1)}` : '';

  const Tile = ({ eyebrow, title, blurb, cta, onClick, primary = false }) => (
    <button
      onClick={onClick}
      className={`text-left rounded-xl border p-4 transition-shadow ${
        primary
          ? 'bg-slate-900 text-white border-slate-900 hover:shadow-lg'
          : 'bg-white border-slate-200 hover:border-slate-400 hover:shadow-md'
      }`}
    >
      <div className={`text-[10px] font-bold uppercase tracking-[0.18em] ${primary ? 'text-emerald-300' : 'text-emerald-700'}`}>
        {eyebrow}
      </div>
      <div className={`font-bold text-base mt-1 ${primary ? 'text-white' : 'text-slate-900'}`}>
        {title}
      </div>
      <div className={`text-xs mt-1.5 leading-snug ${primary ? 'text-slate-300' : 'text-slate-500'}`}>
        {blurb}
      </div>
      <div className={`text-xs font-bold mt-3 inline-flex items-center gap-1 ${primary ? 'text-white' : 'text-slate-900'}`}>
        {cta} <span aria-hidden>→</span>
      </div>
    </button>
  );

  return (
    <div className="bg-gradient-to-br from-white via-emerald-50/40 to-white border border-slate-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700">
            Fresh start
          </div>
          <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mt-1" style={{ fontFamily: 'Georgia, serif', letterSpacing: '-0.02em' }}>
            Good {partOfDay}{greetingName} — welcome to your CRM.
          </h2>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Nothing is loaded yet, so the dashboard is quiet. Pick one of the three below and this page starts filling in with real activity, revenue, and follow-ups.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Tile
          eyebrow="Path 1 · Fastest"
          title="Drop a commission statement"
          blurb="Paste or upload a carrier statement. We auto-parse Americo, Transamerica, BCBS, UHC, HealthSherpa, Plan Advisors — persistency, chargeback, and per-agent KPIs populate within seconds."
          cta="Paste a statement"
          primary
          onClick={() => onGotoTab?.('briefing')}
        />
        <Tile
          eyebrow="Path 2 · Prospecting"
          title="Import a lead list"
          blurb="CSV or paste — get contacts into the CRM so you can start texting or dialing today. First-touch metrics start tracking automatically."
          cta="Import leads"
          onClick={() => onGotoTab?.('leads')}
        />
        <Tile
          eyebrow="Path 3 · Explore"
          title="Load demo data"
          blurb="Loads sample transactions, leads, and policies so you can see the product working end-to-end. Clearable anytime from Settings."
          cta="Try the demo"
          onClick={onLoadDemoData}
        />
      </div>
    </div>
  );
}

function DashboardHome({ calls = [], leads = [], setLeads = () => {}, apps = [], transactions = [], calendar = [], settings = {}, goalConfig = {}, currentUser, onGotoTab = () => {}, showToast = () => {}, acknowledgedWelcomes = [], setAcknowledgedWelcomes = () => {}, isDemo = false, nudgeCount = { need: 0 }, openClientProfile = () => {} }) {
  // ── Operator self-identification ────────────────────────────────
  const myProdNum = String(
    (currentUser?.role !== 'admin' && currentUser?.prodNum) ||
    settings?.ownerProdNum || ''
  ).trim();
  const mine = (val) => !myProdNum || String(val || '').trim() === myProdNum;

  // ── Date windows ────────────────────────────────────────────────
  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  // Start of week (Sunday)
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay());
  weekStart.setHours(0, 0, 0, 0);

  // ── Dials today ─────────────────────────────────────────────────
  const dialsToday = (calls || []).filter(c => {
    const d = String(c.date || c.calledAt || c.created_at || '').slice(0, 10);
    return d === todayStr && mine(c.agentProdNum);
  }).length;
  const dialsYesterday = (calls || []).filter(c => {
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    const d = String(c.date || c.calledAt || c.created_at || '').slice(0, 10);
    return d === yesterday && mine(c.agentProdNum);
  }).length;

  // ── Appointments set this week ──────────────────────────────────
  const apptsSetThisWeek = (calendar || []).filter(a => {
    const dateStr = a.date || a.scheduled_for || a.scheduledFor || a.createdAt;
    if (!dateStr) return false;
    const d = new Date(dateStr);
    return d >= weekStart && mine(a.agentProdNum);
  }).length;

  // ── Apps submitted MTD ──────────────────────────────────────────
  const appsSubmittedMTD = (apps || []).filter(a => {
    if (!a.submittedDate) return false;
    return new Date(a.submittedDate + 'T00:00:00') >= monthStart && mine(a.agentProdNum);
  }).length;

  // ── Active premium (sum of monthly premium on active policies) ──
  // Dedupe by policy number so multi-row commission entries don't
  // double-count premium.
  const seenPolicy = new Set();
  let activePremium = 0;
  for (const t of (transactions || [])) {
    if (!mine(t.prodNum)) continue;
    if (t.chargedBack) continue;
    const pol = String(t.policy || '').trim();
    if (!pol || seenPolicy.has(pol)) continue;
    seenPolicy.add(pol);
    activePremium += Number(t.monthlyPremium) || 0;
  }

  // ── Persistency (simple) ────────────────────────────────────────
  // 1 - (lapsed unique policies / total unique policies). Same dedupe.
  const allPolicies = new Set();
  const lapsedPolicies = new Set();
  for (const t of (transactions || [])) {
    if (!mine(t.prodNum)) continue;
    const pol = String(t.policy || '').trim();
    if (!pol) continue;
    allPolicies.add(pol);
    if (t.chargedBack) lapsedPolicies.add(pol);
  }
  const persistencyPct = allPolicies.size > 0
    ? ((allPolicies.size - lapsedPolicies.size) / allPolicies.size) * 100
    : 0;
  const persistencyColor = persistencyPct >= 80 ? 'emerald'
                        : persistencyPct >= 60 ? 'amber'
                        : 'red';

  // ── Net commission MTD ──────────────────────────────────────────
  const netMTD = (transactions || []).filter(t => {
    if (!mine(t.prodNum)) return false;
    if (!t.activityDate) return false;
    return new Date(t.activityDate + 'T00:00:00') >= monthStart;
  }).reduce((sum, t) => sum + (Number(t.amount) || 0), 0);

  const fmt$ = (n) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

  // ── Live status bar data ────────────────────────────────────────
  // Time updates every 30s. Date doesn't change in a session so it's
  // fine to compute it once.
  const [tickTime, setTickTime] = React.useState(new Date());
  React.useEffect(() => {
    const id = setInterval(() => setTickTime(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  const dateStr = tickTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase();
  const timeStr = tickTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  const txnCount = (transactions || []).length;
  const filesLoaded = new Set((transactions || []).map(t => t.source).filter(Boolean)).size;

  // ── Critical metrics math ───────────────────────────────────────
  // Dial Target — pull from goalConfig.dailyDials if set, else 626 default.
  const dialTarget = Number(goalConfig?.dailyDials) || Number(goalConfig?.dials) || 626;

  // Placement Rate — issued / (issued + declined + withdrawn).
  // Only counts apps whose lifecycle has resolved one way or the other.
  const closedApps = (apps || []).filter(a => {
    if (!mine(a.agentProdNum)) return false;
    return ['issued', 'declined', 'withdrawn'].includes(a.status);
  });
  const issuedApps = closedApps.filter(a => a.status === 'issued').length;
  const placementRate = closedApps.length > 0 ? (issuedApps / closedApps.length) * 100 : 0;

  // Revenue Velocity — MTD net commission against the operator's monthly
  // target (goalConfig.monthlyCommissionTarget). Default $10k if unset.
  const monthlyCommTarget = Number(goalConfig?.monthlyCommissionTarget) || Number(goalConfig?.monthlyTarget) || 10000;

  // Empty-state detection — agency has data once a commission statement
  // has been parsed (transactions table) OR a health BoB has been
  // imported (apps + leads aren't enough since those exist by default
  // on new accounts via inline creation). When everything is bare we
  // surface a prominent Upload CTA instead of empty KPI tiles.
  const hasAnyImportedData = (transactions || []).length > 0;

  // ── Threshold-alert banner inputs ───────────────────────────────
  // Per audit response §15 Phase 5b HOME: "Add: Red alert banner when
  // any metric crosses a threshold (persistency < 80%, chargeback >
  // 25%, etc.)" We compute a single chargeback rate here so the banner
  // doesn't need to do its own scan of transactions.
  let _gross = 0, _cbAbs = 0;
  for (const t of (transactions || [])) {
    if (!mine(t.prodNum)) continue;
    const v = Number(t.netToAccount || t.amount || 0);
    if (v > 0) _gross += v;
    else _cbAbs += Math.abs(v);
  }
  const chargebackRate = _gross > 0 ? (_cbAbs / _gross) * 100 : 0;
  const lowPersistency = hasAnyImportedData && persistencyPct < 80;
  const highChargeback = hasAnyImportedData && chargebackRate > 25;
  const showThresholdAlert = lowPersistency || highChargeback;

  // "Brand-new" detection — no transactions parsed AND no leads/apps
  // in the system yet. When true the dashboard's KPI tiles and
  // aggregate panels would all render zeros; instead we open with a
  // warm hero and skip the wall of empty grids until real data lands.
  const brandNew = !hasAnyImportedData
    && (leads || []).length === 0
    && (apps || []).length === 0;

  return (
    <div className="space-y-5">
      {brandNew && !isDemo && (
        <NewUserWelcomeHero
          currentUser={currentUser}
          onGotoTab={onGotoTab}
          onLoadDemoData={async () => {
            const ok = await hqConfirm({
              title: 'Load a small demo data set?',
              body: 'Sample transactions, leads, and policies so you can see the product working. This won\'t touch any real data, and you can clear it anytime from Settings.',
              confirmLabel: 'Load demo data',
            });
            if (!ok) return;
            seedDemoData();
            showToast('Demo data loaded — refreshing.');
            setTimeout(() => window.location.reload(), 600);
          }}
        />
      )}

      {/* ASCEND autonomous-onboarding status — visible only while a
          run is queued / in_progress / needs_human. Hides itself
          once the operator's onboarding pipeline reaches ready. */}
      <AscendStatusCard onGotoTab={onGotoTab}/>

      {/* Threshold-alert banner — surfaces when key health metrics
          cross unhealthy thresholds. Per audit response §15 Phase 5b
          HOME row 5. We treat persistency < 80% and chargeback > 25%
          as the two non-negotiable signals; both compounds enterprise
          value the way the CFODashboard P&L flags say they do. */}
      {showThresholdAlert && (
        <div className="bg-gradient-to-r from-red-600 to-rose-700 text-white rounded-xl px-5 py-4 shadow-lg border-2 border-red-300 flex items-start gap-3 flex-wrap">
          <div className="text-3xl leading-none">⚠</div>
          <div className="flex-1 min-w-0">
            <div className="font-bold uppercase tracking-wider text-sm">
              Book health alert
            </div>
            <div className="text-xs text-red-100 mt-1 space-y-0.5">
              {lowPersistency && (
                <div>• Persistency at <strong>{persistencyPct.toFixed(0)}%</strong> — below the 80% threshold. Enterprise value is depressed; retention work is the highest-leverage action.</div>
              )}
              {highChargeback && (
                <div>• Chargeback rate at <strong>{chargebackRate.toFixed(0)}%</strong> — above the 25% healthy ceiling. Build reserves before scaling spend.</div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => onGotoTab('retain')}
              className="bg-white text-red-700 hover:bg-red-50 font-bold text-xs px-3 py-1.5 rounded shadow"
            >
              Open RETAIN →
            </button>
            <button
              onClick={() => onGotoTab('pnl')}
              className="bg-white/20 hover:bg-white/30 text-white font-semibold text-xs px-3 py-1.5 rounded border border-white/30"
            >
              P&L →
            </button>
          </div>
        </div>
      )}

      {/* NUDGE login burden — operator-stated requirement: "they'll
          burden me the agent every time I log in." When prospects need
          follow-up today, surface a sticky violet banner above every
          Home panel until the count is zero. Click jumps straight
          into NUDGE so the agent's first action of the day is to clear
          this queue. */}
      {nudgeCount.need > 0 && (
        <div className="bg-gradient-to-r from-violet-600 via-violet-700 to-fuchsia-700 text-white rounded-xl px-5 py-3 mb-4 flex items-center justify-between gap-3 flex-wrap shadow-md">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-2xl">✦</span>
            <div>
              <div className="font-bold text-base">{nudgeCount.need} {nudgeCount.need === 1 ? 'person needs' : 'people need'} follow-up today</div>
              <div className="text-xs text-violet-100/90">
                {nudgeCount.hot > 0 && <span><strong>{nudgeCount.hot}</strong> hot</span>}
                {nudgeCount.hot > 0 && (nudgeCount.due > 0 || nudgeCount.slip > 0) && ' · '}
                {nudgeCount.due > 0 && <span><strong>{nudgeCount.due}</strong> due</span>}
                {nudgeCount.due > 0 && nudgeCount.slip > 0 && ' · '}
                {nudgeCount.slip > 0 && <span><strong>{nudgeCount.slip}</strong> slipping away</span>}
                {' — knock out the red ones first'}
              </div>
            </div>
          </div>
          <button
            onClick={() => onGotoTab('nudge')}
            className="bg-white text-violet-700 hover:bg-violet-50 font-bold text-sm px-4 py-2 rounded-lg whitespace-nowrap"
          >
            Open NUDGE →
          </button>
        </div>
      )}

      {/* §11c — Quickstart task list. Auto-hides at 6/6 done or when
          the operator dismisses it for the session. Surfaces only the
          tasks not yet complete; helps tenants stay productive while
          A2P pends. Hidden in Demo Mode — the demo represents a
          fully-onboarded agency, not a setup-in-progress one. */}
      {!isDemo && <QuickstartPanel currentUser={currentUser} onGotoTab={onGotoTab}/>}

      {/* Setup Command Center — auto-hides once 100% complete.
          Also hidden in Demo Mode for the same reason as Quickstart. */}
      {!isDemo && <SetupProgressPanel currentUser={currentUser} leads={leads} onGotoTab={onGotoTab} showToast={showToast}/>}

      {/* New-Client Welcome queue — surfaces recently-issued policies
          (last 14 days) that haven't been welcomed yet. Each row shows
          one of three actions: Send welcome (if matching lead has phone
          + consent), Complete profile (lead exists but missing data),
          or Add as lead (no matching lead). Operator confirms each
          send — no auto-fire until A2P approves + you flip the
          auto-send toggle (future). */}
      <NewClientWelcomePanel
        transactions={transactions}
        leads={leads}
        setLeads={setLeads}
        settings={settings}
        acknowledgedWelcomes={acknowledgedWelcomes}
        setAcknowledgedWelcomes={setAcknowledgedWelcomes}
        onGotoTab={onGotoTab}
        showToast={showToast}
      />

      {/* Pending policy deliveries — clients whose policy issued in
          the last 60 days but no Policy Delivery Summary has been
          marked sent yet. Color-coded by age (fresh / aging / overdue);
          opens the same modal used in ClientsView. Auto-hides when
          the queue is empty. */}
      <PendingDeliveriesPanel
        currentUser={currentUser}
        settings={settings}
        showToast={showToast}
      />

      {/* Outstanding requirements — every client with pending items on
          file. Driven by the new client_requirements table; daily cron
          texts + emails clients automatically. Open client → operator
          can edit that client's checklist in the modal. */}
      <PendingRequirementsPanel
        currentUser={currentUser}
        onOpenClient={() => onGotoTab('clients')}
      />

      {/* Manager AI — the operator's single conversational entry
          point. Dispatches the specialist team and synthesizes the
          result. */}
      <ManagerChatPanel currentUser={currentUser} showToast={showToast} isDemo={isDemo}/>

      {/* Daily action queue — the operator's money list for today,
          ranked by the Policy Manager's close-probability model. */}
      <DailyActionQueue currentUser={currentUser} showToast={showToast} onOpenLead={() => onGotoTab('leads')}/>

      {/* Book of Business — revenue protection + chargeback risk
          aggregated from classified life_policies (lives across all
          uploaded carrier conservation reports). */}
      <BookOfBusinessPanel currentUser={currentUser} showToast={showToast} openClientProfile={openClientProfile}/>

      {/* Performance overview header — calm white card. Replaces the
          two stacked "Live KPIs" / "Performance Dashboard" panels
          (one pitch-black with a STREAMING badge, one slate-950 with
          a retro terminal status line). Same date / time / counts
          info in one quieter row. */}
      <div className="bg-white border border-slate-200 rounded-xl px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Performance overview</h2>
          <div className="text-sm text-slate-500 mt-0.5">Tap any KPI tile to drill into the source data.</div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-4 text-xs text-slate-500 tabular-nums">
            <div className="hidden sm:block"><span className="text-slate-400">Updated</span> <span className="text-slate-700 font-semibold">{dateStr} · {timeStr}</span></div>
            <div><span className="text-slate-400">Loaded</span> <span className="text-slate-700 font-semibold">{txnCount.toLocaleString()} txns · {filesLoaded} {filesLoaded === 1 ? 'file' : 'files'}</span></div>
          </div>
          {/* Contextual Import button (P1-1): operators on Home shouldn't
              have to reach up to the global top bar to drop a fresh
              statement. The top-bar import stays in place for the
              other tabs that still need it. */}
          {hasAnyImportedData && (
            <button
              onClick={() => onGotoTab('briefing')}
              className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors inline-flex items-center gap-1.5"
            >
              <Upload size={12}/> Import statement
            </button>
          )}
        </div>
      </div>

      {/* Referral / booking link share card — surfaces the operator's
          public /p/<slug> landing-page URL on home so they actually
          USE it. Per audit response §9 Phase 3 SALES "no consumer-
          facing booking link" + §15 Phase 5b CLIENT "one-click
          'send referral ask' button" + §10 Phase 4 CUSTOMER SERVICE
          on activation paths. The marketing site owns the
          /p/<slug> route; we just emit the URL here. */}
      {currentUser?.id && (
        <ReferralLinkShareCard currentUser={currentUser} settings={settings} showToast={showToast} onGotoTab={onGotoTab}/>
      )}

      {/* Empty-state import CTA — calm white card, only shown when no
          transactions have landed yet. Replaces a bright emerald
          gradient with a 2px emerald border. */}
      {!hasAnyImportedData && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Step 1 · Load your book</div>
              <h3 className="text-slate-900 text-lg font-semibold mt-1">Drop your first commission statement to bring the dashboard to life</h3>
              <p className="text-slate-600 text-sm mt-2 leading-snug">
                We auto-parse Americo, Transamerica, BCBS / HCSC, UHC / Golden Rule, HealthSherpa, and Plan Advisors statements. Paste from your carrier portal or upload the PDF / CSV — within seconds you'll have persistency, chargeback, and per-agent leaderboards populated.
              </p>
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                <button
                  onClick={() => onGotoTab('briefing')}
                  className="bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                >
                  Paste a carrier statement
                </button>
                <button
                  onClick={() => onGotoTab('health_policies')}
                  className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 hover:border-slate-300 text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                >
                  Upload Health book
                </button>
                {/* Demo data seeder — pre-populates synthetic transactions /
                    leads / apps / calls so a brand-new operator can EXPLORE
                    the product before committing to import real data.
                    Writes to localStorage under the standard K keys so the
                    next page load (forced via window.location.reload)
                    hydrates everything cleanly. */}
                <button
                  onClick={async () => {
                    const ok = await hqConfirm({
                      title: 'Load a small demo data set?',
                      body: 'Sample transactions, leads, and policies so you can see the product working. This won\'t touch any real data, and you can clear it anytime from Settings.',
                      confirmLabel: 'Load demo data',
                    });
                    if (!ok) return;
                    seedDemoData();
                    showToast('Demo data loaded — refreshing.');
                    setTimeout(() => window.location.reload(), 600);
                  }}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900 text-sm font-bold px-4 py-2 rounded-lg transition-colors"
                >
                  Load demo data
                </button>
                <button
                  onClick={() => onGotoTab('underwriting')}
                  className="text-slate-500 hover:text-slate-900 text-sm font-medium px-2 py-2"
                >
                  Or start with UW Lookup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KPI tiles — split into two banded sections so the eye scans
          activity vs. financial separately instead of treating all six
          as one undifferentiated grid. Hidden for brand-new users
          because a wall of zeros makes the dashboard feel broken;
          NewUserWelcomeHero at the top of this view already directs
          them to the actions that will populate these tiles. */}
      {!brandNew && (
      <div className="space-y-5">
        <section>
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-[0.15em] mb-3 px-1">Activity</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KpiTile
              label="Dials Today"
              value={dialsToday}
              suffix="calls"
              subtext={dialsYesterday > 0 ? `${dialsYesterday} yesterday` : 'New day — get on the phones'}
              color="emerald"
              onClick={() => onGotoTab('calls')}
            />
            <KpiTile
              label="Appts Set (Week)"
              value={apptsSetThisWeek}
              suffix={apptsSetThisWeek === 1 ? 'appt' : 'appts'}
              subtext="Open Calendar →"
              color="sky"
              onClick={() => onGotoTab('calendar')}
            />
            <KpiTile
              label="Apps Submitted (MTD)"
              value={appsSubmittedMTD}
              suffix={appsSubmittedMTD === 1 ? 'app' : 'apps'}
              subtext="In underwriting"
              color="violet"
              onClick={() => onGotoTab('apps')}
            />
          </div>
        </section>
        <section>
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-[0.15em] mb-3 px-1">Financial</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KpiTile
              label="Active Premium"
              value={fmt$(activePremium)}
              subtext={`${allPolicies.size - lapsedPolicies.size} policies on book`}
              color="amber"
              onClick={() => onGotoTab('book')}
            />
            <KpiTile
              label="Persistency"
              value={`${persistencyPct.toFixed(0)}%`}
              subtext={persistencyPct >= 80 ? 'Elite-tier ' : persistencyPct >= 60 ? 'Healthy — keep pushing' : 'Needs attention'}
              color={persistencyColor}
              onClick={() => onGotoTab('persistency')}
            />
            <KpiTile
              label="Net Commission (MTD)"
              value={fmt$(netMTD)}
              subtext="Owner net after override paid"
              color="emerald"
              onClick={() => onGotoTab('pnl')}
            />
          </div>
        </section>
      </div>
      )}

      {/* Today's Critical Metrics — heaviest panel on the page (P1-3
          visual hierarchy). Unlike the 6 KPI tiles above which are
          SUMMARIES, these are TARGETS the operator is being held
          against today. Promoted to slate-900 frame + larger title so
          the eye lands here first after the KPI bands. Hidden for
          brand-new users — a black hero showing zero-of-zero targets
          feels punitive. */}
      {!brandNew && (
      <div className="bg-slate-900 text-white rounded-xl border-2 border-slate-900 p-6 shadow-lg">
        <div className="flex items-baseline justify-between mb-1">
          <div className="text-lg font-black uppercase tracking-tight text-white">Today's Critical Metrics</div>
          <div className="text-[0.55rem] font-bold uppercase tracking-[0.2em] text-slate-400">Goal Engine</div>
        </div>
        <div className="text-[0.6rem] font-bold uppercase tracking-[0.2em] text-emerald-400 mb-4">What Moves the Needle</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <CriticalMetricCard
            label="Dial Target"
            current={dialsToday}
            target={dialTarget}
            displayCurrent={dialsToday.toLocaleString()}
            displayTarget={dialTarget.toLocaleString() + ' dials'}
            color="emerald"
          />
          <CriticalMetricCard
            label="Placement Rate"
            current={placementRate}
            target={70}
            displayCurrent={placementRate.toFixed(0) + '%'}
            displayTarget="70%+ elite"
            color="violet"
            benchmark="ELITE 70%+"
          />
          <CriticalMetricCard
            label="Revenue Velocity"
            current={netMTD}
            target={monthlyCommTarget}
            displayCurrent={fmt$(netMTD)}
            displayTarget={fmt$(monthlyCommTarget) + ' MTD'}
            color="amber"
          />
        </div>
      </div>
      )}

      {/* Quick actions — moved to page footer (P1-2). Was interrupting
          the scan flow between Financial KPIs and Critical Metrics.
          As a footer action bar, it's still one click away but doesn't
          fragment the main reading order. Also rendered at lower visual
          weight (lighter title, smaller padding) per visual hierarchy
          pass (P1-3). */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
        <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.15em] mb-3 px-1">Quick actions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {[
            { tab: 'leads',        label: 'Leads',         icon: Users,        desc: 'Pipeline + search' },
            { tab: 'inbox',        label: 'SMS Inbox',     icon: MessageSquare, desc: 'Reply to drips' },
            { tab: 'mission',      label: 'Mission',       icon: Activity,     desc: 'Today\'s brief' },
            { tab: 'retention',    label: 'Retention',     icon: AlertTriangle, desc: 'Save the policy' },
            { tab: 'paycalc',      label: 'My Pay',        icon: DollarSign,   desc: 'Earnings & comp' },
            { tab: 'overrides_iq', label: 'Overrides',     icon: TrendingUp,   desc: 'Hierarchy override' },
          ].map(a => {
            const Icon = a.icon;
            return (
              <button
                key={a.tab}
                onClick={() => onGotoTab(a.tab)}
                className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-100 transition-colors text-left"
              >
                <div className="h-8 w-8 rounded-md bg-slate-100 text-slate-600 inline-flex items-center justify-center shrink-0">
                  <Icon size={16}/>
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{a.label}</div>
                  <div className="text-[11px] text-slate-500 truncate">{a.desc}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// PAY CALCULATOR — "What will I get paid on this policy?"
//
// Agent-facing answer to the single most-asked question on every call:
// "If I write this for $X premium, what hits my deposit?"
//
// Inputs: carrier × product × FFL contract level × premium.
// Premium accepts MONTHLY or ANNUAL via toggle (most leads talk monthly,
// the comp rate is on annual — easy to mis-key).
//
// Output: annual commission, the 9-month advance (75% upfront, split
// monthly), and months 10/11/12 earned (25% remainder, split). Also
// shows max chargeback exposure if the policy lapses early — keeps the
// agent honest about what they're really keeping.
//
// Rate tables live in /lib/fflCommissionRates.js — sourced from the
// FFL commission charts (rev 12/21/2023). Internal use only; verify
// against the official comp guide before relying on these.
// ════════════════════════════════════════════════════════════════════

// ─────────────────────────────────────────────────────────────────────
// HEALTH PAY COMMISSION MODEL
// ─────────────────────────────────────────────────────────────────────
// Health comp doesn't fit the FFL life model (single annual premium ×
// rate × 9-month advance). It comes in four distinct shapes:
//
//   1. PMPM (per-member-per-month) — ACA Marketplace, Dental/Vision.
//      Paid monthly as the carrier receives premium. NO advance.
//   2. CMS-set per-enrollment — Medicare Advantage / Medicare Part D.
//      Federally capped first-year init + flat renewal years 2+.
//   3. % of annual premium — Medicare Supplement. First-year is the
//      headline number; renewals taper across years 2-6 then floor.
//   4. Per-policy flat + small PMPM — Hospital indemnity / supplemental.
//
// Rates here are conservative ballpark numbers an agent should validate
// against their actual carrier contracts. Federal Marketplace and CMS
// rates are the *floor*; agents with FMO override stacks pay more. The
// table is editable in-app via the rate inputs.
const HEALTH_PAY_PRODUCTS = {
  'ACA Marketplace':       { model: 'pmpm',    defaultRate: 20,  unit: '$/member/mo', ltvMonths: 36, advance: false,
                             note: 'PMPM = per-member-per-month. Paid as carrier collects premium; no advance.' },
  'Medicare Advantage':    { model: 'cms_init',defaultRate: 626, unit: '$ init',      renewalRate: 313, ltvYears: 6, advance: true,
                             note: 'CMS-set initial; renewal is half (years 2+). PR/HI/AK ~$695. Verify by ZIP.' },
  'Medicare Part D (PDP)': { model: 'cms_init',defaultRate: 109, unit: '$ init',      renewalRate: 55,  ltvYears: 6, advance: true,
                             note: 'CMS-set initial; renewal is half. Smaller payday than MA but easy to add to MA sales.' },
  'Medicare Supplement':   { model: 'pct_ap',  defaultRate: 0.20, unit: '% of annual premium', renewalSchedule: [0.10, 0.10, 0.10, 0.10, 0.10, 0.05], advance: true,
                             note: 'First-year %; renewals typically 10% years 2–6 then 5% lifetime.' },
  'Dental / Vision':       { model: 'pmpm',    defaultRate: 4,   unit: '$/member/mo', ltvMonths: 24, advance: false,
                             note: 'Sticky cross-sell. PMPM with no advance.' },
  'Hospital Indemnity':    { model: 'flat',    defaultRate: 30,  unit: '$/policy flat', pmpmAdd: 2, ltvMonths: 24, advance: true,
                             note: 'Per-policy bonus on issue + small PMPM. NOT MEC — stamp on every quote.' },
};

function fmtH$(n) {
  if (n == null || isNaN(n)) return '$0';
  return '$' + Math.round(n).toLocaleString();
}

function HealthPayPanel() {
  const [product, setProduct] = React.useState('ACA Marketplace');
  const cfg = HEALTH_PAY_PRODUCTS[product];
  const [rate, setRate] = React.useState(cfg.defaultRate);
  const [members, setMembers] = React.useState(1);
  const [annualPremium, setAnnualPremium] = React.useState(2400);

  React.useEffect(() => {
    setRate(HEALTH_PAY_PRODUCTS[product].defaultRate);
  }, [product]);

  const calc = React.useMemo(() => {
    if (cfg.model === 'pmpm') {
      const monthly = Number(rate) * Number(members);
      const annual  = monthly * 12;
      const ltvMonths = cfg.ltvMonths || 24;
      return {
        annual,
        monthly,
        advance: 0,
        ltv: monthly * ltvMonths,
        breakdown: [
          { label: 'Per member · per month', value: fmtH$(rate) },
          { label: `× ${members} member${members === 1 ? '' : 's'}`, value: fmtH$(monthly) + '/mo' },
          { label: '× 12 months', value: fmtH$(annual) + '/yr' },
          { label: `LTV over ${ltvMonths} mo (avg persistency)`, value: fmtH$(monthly * ltvMonths) },
        ],
      };
    }
    if (cfg.model === 'cms_init') {
      const init    = Number(rate);
      const renewal = Number(cfg.renewalRate);
      const ltv     = init + renewal * (cfg.ltvYears - 1);
      return {
        annual: init,
        monthly: init / 12,
        advance: init,
        ltv,
        breakdown: [
          { label: 'Year 1 initial (paid upfront)', value: fmtH$(init) },
          { label: `Year 2–${cfg.ltvYears} renewal`, value: fmtH$(renewal) + '/yr' },
          { label: `LTV over ${cfg.ltvYears} yrs`,  value: fmtH$(ltv) },
        ],
      };
    }
    if (cfg.model === 'pct_ap') {
      const ap     = Number(annualPremium);
      const first  = ap * Number(rate);
      const renewalTotal = (cfg.renewalSchedule || []).reduce((s, r) => s + ap * r, 0);
      const ltv    = first + renewalTotal;
      return {
        annual: first,
        monthly: first / 12,
        advance: first,
        ltv,
        breakdown: [
          { label: `Annual premium`, value: fmtH$(ap) },
          { label: `Year 1 (${(rate * 100).toFixed(0)}%)`, value: fmtH$(first) },
          { label: `Renewals Y2–Y${1 + (cfg.renewalSchedule?.length || 0)}`, value: fmtH$(renewalTotal) },
          { label: `LTV total`, value: fmtH$(ltv) },
        ],
      };
    }
    if (cfg.model === 'flat') {
      const flat    = Number(rate);
      const pmpmAdd = Number(cfg.pmpmAdd || 0);
      const annual  = flat + (pmpmAdd * 12);
      const ltv     = flat + (pmpmAdd * (cfg.ltvMonths || 24));
      return {
        annual,
        monthly: annual / 12,
        advance: flat,
        ltv,
        breakdown: [
          { label: 'Per-policy flat (upfront)', value: fmtH$(flat) },
          { label: `+ PMPM bonus`, value: fmtH$(pmpmAdd) + '/mo' },
          { label: `Year 1 total`, value: fmtH$(annual) },
          { label: `LTV ${cfg.ltvMonths || 24} mo`, value: fmtH$(ltv) },
        ],
      };
    }
    return { annual: 0, monthly: 0, advance: 0, ltv: 0, breakdown: [] };
  }, [cfg, rate, members, annualPremium]);

  return (
    <div className="space-y-6 w-full">
      {/* Hero — TEAL accent so the agent knows this is the Health pay
          path, not the Life FFL calculator. */}
      <div className="bg-gradient-to-r from-teal-700 via-teal-800 to-teal-900 rounded-2xl p-6 shadow-xl">
        <div>
          <div className="text-teal-200 text-xs font-semibold uppercase tracking-wider">Per-Enrollment Pay · Health</div>
          <div className="text-white text-2xl font-bold mt-1">What Will I Get Paid? · Health</div>
          <div className="text-teal-100 text-sm mt-1 max-w-2xl">
            Pick the product. Set members, premium, or rate. See your annual commission, what's paid upfront vs. PMPM, and the lifetime value of the case at typical persistency.
          </div>
        </div>
      </div>

      {/* Inputs */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="block md:col-span-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</span>
            <select
              value={product}
              onChange={e => setProduct(e.target.value)}
              className="mt-1.5 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {Object.keys(HEALTH_PAY_PRODUCTS).map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <span className="block text-[11px] text-slate-400 mt-1">{cfg.note}</span>
          </label>

          <label className="block">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{cfg.unit}</span>
            <input
              type="text"
              inputMode="decimal"
              value={rate}
              onChange={e => setRate(e.target.value.replace(/[^\d.]/g, ''))}
              className="mt-1.5 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </label>
        </div>

        {(cfg.model === 'pmpm') && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Members on plan</span>
              <input
                type="text"
                inputMode="numeric"
                value={members}
                onChange={e => setMembers(Number(e.target.value.replace(/[^\d]/g, '') || 0))}
                className="mt-1.5 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <span className="block text-[11px] text-slate-400 mt-1">Household size if ACA · single-cover if dental.</span>
            </label>
          </div>
        )}

        {(cfg.model === 'pct_ap') && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Annual premium</span>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={annualPremium}
                  onChange={e => setAnnualPremium(Number(e.target.value.replace(/[^\d.]/g, '') || 0))}
                  className="mt-1.5 w-full border border-slate-300 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <span className="block text-[11px] text-slate-400 mt-1">Typical Plan G: $2,000–$3,000 in IL/TX/FL at age 65.</span>
            </label>
          </div>
        )}
      </div>

      {/* Output */}
      <div className="space-y-3">
        <div className="bg-white border-2 border-slate-300 rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Year 1 Commission</div>
              <div className="text-xs text-slate-600 mt-0.5">{cfg.model === 'pmpm' ? 'Paid PMPM as carrier collects premium · no advance' : cfg.model === 'cms_init' ? 'CMS-set · paid upfront on enrollment' : cfg.model === 'pct_ap' ? `${(rate * 100).toFixed(0)}% of annual premium` : 'Flat + PMPM blend'}</div>
            </div>
            <div className="text-3xl font-bold text-slate-900">{fmtH$(calc.annual)}</div>
          </div>
        </div>

        {cfg.advance && (
          <div className="bg-teal-50 border-2 border-teal-300 rounded-xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-bold text-teal-800 uppercase tracking-wider">Paid Upfront</div>
                <div className="text-xs text-teal-700 mt-0.5">On enrollment / issue</div>
              </div>
              <div className="text-3xl font-bold text-teal-700">{fmtH$(calc.advance)}</div>
            </div>
          </div>
        )}

        {cfg.model === 'pmpm' && (
          <div className="bg-sky-50 border-2 border-sky-300 rounded-xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-bold text-sky-800 uppercase tracking-wider">Monthly recurring</div>
                <div className="text-xs text-sky-700 mt-0.5">Drops every month as carrier collects</div>
              </div>
              <div className="text-3xl font-bold text-sky-700">{fmtH$(calc.monthly)}</div>
            </div>
          </div>
        )}

        <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-bold text-emerald-800 uppercase tracking-wider">Lifetime Value</div>
              <div className="text-xs text-emerald-700 mt-0.5">At typical persistency · {cfg.ltvMonths ? `${cfg.ltvMonths} mo` : `${cfg.ltvYears} yrs`}</div>
            </div>
            <div className="text-3xl font-bold text-emerald-700">{fmtH$(calc.ltv)}</div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">The math</div>
          <div className="space-y-1.5">
            {calc.breakdown.map((row, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{row.label}</span>
                <span className="font-mono font-semibold text-slate-900">{row.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
          <div className="text-sm font-bold text-amber-900">Health-side compliance</div>
          <div className="text-xs text-amber-800 mt-1 leading-relaxed">
            CMS Medicare rates are federal caps — your FMO contract may pay more. ACA PMPM varies by carrier and state (CA/NY higher, TX/FL lower). Hospital Indemnity and Critical Illness are <strong>NOT MEC</strong> — disclose on every quote. Always verify against your actual carrier contracts before quoting an agent payout.
          </div>
        </div>
      </div>
    </div>
  );
}

function PayCalculatorView({ settings = {} }) {
  // ── Vertical toggle — life vs health pay model ───────────────────
  // Life uses FFL_COMMISSION_RATES (single AP × rate × advance/earned).
  // Health uses HEALTH_PAY_PRODUCTS (PMPM / CMS-set / % AP / flat).
  // Persisted to localStorage so the agent's preference sticks.
  const [vertical, setVertical] = React.useState(() => {
    try { return localStorage.getItem('paycalc:vertical') || 'life'; }
    catch { return 'life'; }
  });
  React.useEffect(() => {
    try { localStorage.setItem('paycalc:vertical', vertical); } catch {}
  }, [vertical]);

  // ── State ────────────────────────────────────────────────────────
  const [carrier, setCarrier] = React.useState('Mutual of Omaha');
  const carrierData = FFL_COMMISSION_RATES[carrier];
  const productOptions = carrierData ? Object.keys(carrierData.products) : [];

  const [product, setProduct] = React.useState(productOptions[0] || '');
  React.useEffect(() => {
    // When carrier changes, snap product to first available for that carrier.
    const opts = FFL_COMMISSION_RATES[carrier]?.products
      ? Object.keys(FFL_COMMISSION_RATES[carrier].products) : [];
    if (!opts.includes(product)) setProduct(opts[0] || '');
  }, [carrier, product]);

  const isAnnuity = carrierData?.type === 'annuity';
  const defaultLevel = isAnnuity ? 'silver' : 120;
  const [level, setLevel] = React.useState(defaultLevel);
  React.useEffect(() => {
    setLevel(carrierData?.type === 'annuity' ? 'silver' : 120);
  }, [carrierData]);

  // Premium mode toggle. Annuities only support a single "deposit"
  // amount so we hide the toggle for them.
  const [premiumMode, setPremiumMode] = React.useState('monthly');
  const [premiumInput, setPremiumInput] = React.useState('100');

  // ── Derived ──────────────────────────────────────────────────────
  const numericPremium = Number(premiumInput) || 0;
  const annualPremium = isAnnuity
    ? numericPremium              // annuity rate is on the deposit, not annual
    : (premiumMode === 'monthly' ? numericPremium * 12 : numericPremium);
  const monthlyPremium = isAnnuity ? null : annualPremium / 12;

  const result = calcCommission({ carrier, product, level, annualPremium });

  // ── Level options for the dropdown ───────────────────────────────
  const levelOptions = isAnnuity
    ? FFL_ANNUITY_LEVELS
    : FFL_LEVELS
        .filter(l => carrierData?.products?.[product]?.[l] != null)
        .map(l => ({ key: l, label: `FFL ${l}` }));

  // ── Render ───────────────────────────────────────────────────────
  // Top-level toggle picks LIFE (FFL model) vs HEALTH (PMPM / CMS-set
  // / % AP / flat). Health renders a dedicated panel.
  const VerticalToggle = (
    <div className="inline-flex bg-slate-100 border border-slate-200 rounded-lg p-0.5 text-xs font-bold uppercase tracking-tight">
      <button
        onClick={() => setVertical('life')}
        className={`px-4 py-1.5 rounded-md transition-colors ${vertical === 'life' ? 'bg-emerald-600 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
      >
        Life Pay
      </button>
      <button
        onClick={() => setVertical('health')}
        className={`px-4 py-1.5 rounded-md transition-colors ${vertical === 'health' ? 'bg-teal-600 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
      >
        Health Pay
      </button>
    </div>
  );

  if (vertical === 'health') {
    return (
      <div className="space-y-4 w-full">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Vertical</div>
          {VerticalToggle}
        </div>
        <HealthPayPanel/>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Vertical</div>
        {VerticalToggle}
      </div>
      {/* Hero */}
      <div className="bg-gradient-to-r from-emerald-700 via-emerald-800 to-emerald-900 rounded-2xl p-6 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-emerald-200 text-xs font-semibold uppercase tracking-wider">Per-Policy Pay · Life</div>
            <div className="text-white text-2xl font-bold mt-1">What Will I Get Paid? · Life</div>
            <div className="text-emerald-100 text-sm mt-1 max-w-2xl">
              Pick the carrier, product, your FFL contract level, and the premium. See the annual commission, the 9-month advance you'll get upfront, and what hits months 10-12 as earned.
            </div>
          </div>
        </div>
      </div>

      {/* Inputs */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="block">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Carrier</span>
            <select
              value={carrier}
              onChange={e => setCarrier(e.target.value)}
              className="mt-1.5 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {FFL_CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <span className="block text-[11px] text-slate-400 mt-1">{carrierData?.productLine || ''}</span>
          </label>

          <label className="block">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</span>
            <select
              value={product}
              onChange={e => setProduct(e.target.value)}
              className="mt-1.5 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {productOptions.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              {isAnnuity ? 'Producer Level' : 'Your FFL Level'}
            </span>
            <select
              value={level}
              onChange={e => setLevel(isAnnuity ? e.target.value : Number(e.target.value))}
              className="mt-1.5 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {levelOptions.map(opt => <option key={opt.key} value={opt.key}>{opt.label}</option>)}
            </select>
          </label>
        </div>

        {/* Premium row — toggle hidden for annuities */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                {isAnnuity ? 'Deposit Amount' : 'Premium'}
              </span>
              {!isAnnuity && (
                <div className="inline-flex border border-slate-300 rounded-md overflow-hidden text-xs">
                  <button
                    type="button"
                    onClick={() => setPremiumMode('monthly')}
                    className={`px-3 py-1 font-semibold transition-colors ${premiumMode === 'monthly' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    onClick={() => setPremiumMode('annual')}
                    className={`px-3 py-1 font-semibold transition-colors ${premiumMode === 'annual' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    Annual
                  </button>
                </div>
              )}
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">$</span>
              <input
                type="text"
                inputMode="decimal"
                value={premiumInput}
                onChange={e => setPremiumInput(sanitizeMoneyInput(e.target.value))}
                placeholder="0.00"
                className="w-full border border-slate-300 rounded-lg pl-7 pr-3 py-2 text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            {!isAnnuity && (
              <div className="text-xs text-slate-500 mt-1.5">
                {premiumMode === 'monthly'
                  ? <>Annual premium: <strong>{fmtMoneyFFL(annualPremium)}</strong></>
                  : <>Monthly premium: <strong>{fmtMoneyFFL(monthlyPremium)}</strong></>}
              </div>
            )}
          </div>

          {/* Rate display */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Your Comp Rate</div>
            <div className="text-2xl font-bold text-slate-900 mt-1">
              {result ? result.ratePct : '—'}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              of {isAnnuity ? 'deposit' : 'annual premium'}
            </div>
          </div>
        </div>
      </div>

      {/* Output */}
      {result ? (
        <div className="space-y-3">
          {/* Annual commission */}
          <div className="bg-white border-2 border-slate-300 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Annual Commission</div>
                <div className="text-xs text-slate-600 mt-0.5">
                  {fmtMoneyFFL(annualPremium)} {isAnnuity ? 'deposit' : 'AP'} × {result.ratePct}
                </div>
              </div>
              <div className="text-3xl font-bold text-slate-900">{fmtMoneyFFL(result.annualCommission)}</div>
            </div>
          </div>

          {isAnnuity ? (
            // Annuities pay full commission upfront, no advance/earned split
            <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-bold text-emerald-800 uppercase tracking-wider">Paid Upfront (100%)</div>
                  <div className="text-xs text-emerald-700 mt-0.5">Annuity commissions are typically paid in full on issue</div>
                </div>
                <div className="text-3xl font-bold text-emerald-700">{fmtMoneyFFL(result.annualCommission)}</div>
              </div>
            </div>
          ) : (
            <>
              {/* 9-month advance */}
              <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-bold text-emerald-800 uppercase tracking-wider">9-Month Advance (75%)</div>
                    <div className="text-xs text-emerald-700 mt-0.5">Paid upfront when the policy issues</div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-emerald-700">{fmtMoneyFFL(result.advance9Month)}</div>
                    <div className="text-xs text-emerald-600 mt-0.5 font-semibold">{fmtMoneyFFL(result.advanceMonthly)} × 9 months</div>
                  </div>
                </div>
              </div>

              {/* Months 10-12 earned */}
              <div className="bg-sky-50 border-2 border-sky-300 rounded-xl p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-bold text-sky-800 uppercase tracking-wider">Months 10 / 11 / 12 — Earned (25%)</div>
                    <div className="text-xs text-sky-700 mt-0.5">The remaining 25%, paid as earned over months 10-12</div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-sky-700">{fmtMoneyFFL(result.earnedPerMonth)}</div>
                    <div className="text-xs text-sky-600 mt-0.5 font-semibold">× 3 months</div>
                  </div>
                </div>
              </div>

              {/* Chargeback warning */}
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="text-2xl flex-shrink-0">⚠</div>
                  <div>
                    <div className="text-sm font-bold text-amber-900">Max chargeback exposure</div>
                    <div className="text-xs text-amber-800 mt-1">
                      If this policy lapses before earning out, you can chargeback up to{' '}
                      <strong>{fmtMoneyFFL(result.chargebackFloor)}</strong> of the advance. Persistency-first selling protects the deposit.
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Cross-product comparison: same premium + same level, every
              other carrier × product that has a rate at this level.
              Sorted highest to lowest so the agent can see at a glance
              which product to write for this same client. */}
          {(() => {
            const rows = [];
            const isLifeMode = typeof level === 'number';
            for (const [carrierName, carrierData] of Object.entries(FFL_COMMISSION_RATES)) {
              if (isLifeMode && carrierData.type !== 'life') continue;
              if (!isLifeMode && carrierData.type !== 'annuity') continue;
              for (const [productName, rates] of Object.entries(carrierData.products)) {
                const rate = rates[level];
                if (rate == null) continue;
                const commission = annualPremium * rate;
                rows.push({
                  carrier: carrierName,
                  product: productName,
                  rate,
                  commission,
                  isCurrent: carrierName === carrier && productName === product,
                });
              }
            }
            rows.sort((a, b) => b.commission - a.commission);
            if (rows.length <= 1) return null;
            return (
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-baseline justify-between mb-3">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Other products at this premium &amp; level
                  </div>
                  <div className="text-[11px] text-slate-400">
                    Highest → lowest · same {fmtMoneyFFL(annualPremium)} {isAnnuity ? 'deposit' : 'AP'} · {isAnnuity ? 'at your producer level' : `at FFL ${level}`}
                  </div>
                </div>
                <div className="space-y-1.5 max-h-96 overflow-auto pr-1">
                  {rows.map((row, idx) => {
                    const delta = row.commission - result.annualCommission;
                    return (
                      <button
                        key={`${row.carrier}::${row.product}`}
                        type="button"
                        onClick={() => { setCarrier(row.carrier); setProduct(row.product); }}
                        className={`w-full flex items-center justify-between text-left rounded-lg border px-3 py-2 transition-colors ${
                          row.isCurrent
                            ? 'bg-emerald-50 border-emerald-400 ring-1 ring-emerald-400'
                            : 'bg-white border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`text-[10px] font-bold w-6 text-center rounded ${
                            idx === 0 ? 'bg-amber-100 text-amber-700' :
                            idx === 1 ? 'bg-slate-200 text-slate-700' :
                            idx === 2 ? 'bg-orange-100 text-orange-700' :
                            'bg-slate-100 text-slate-500'
                          }`}>#{idx + 1}</span>
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-slate-900 truncate">{row.carrier} · {row.product}</div>
                            <div className="text-[11px] text-slate-500">{(row.rate * 100).toFixed(1)}% comp</div>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-3">
                          <div className="text-sm font-bold text-slate-900">{fmtMoneyFFL(row.commission)}</div>
                          <div className={`text-[11px] font-semibold ${
                            row.isCurrent ? 'text-emerald-600' :
                            delta > 0 ? 'text-emerald-600' :
                            delta < 0 ? 'text-red-600' : 'text-slate-400'
                          }`}>
                            {row.isCurrent ? '— current' : `${delta > 0 ? '+' : ''}${fmtMoneyFFL(delta)}`}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-5 text-sm text-amber-900">
          No commission rate found for this combination — double-check carrier × product × level. If you know the right rate, verify against your official FFL comp guide.
        </div>
      )}

      {/* Disclaimer */}
      <div className="text-[11px] text-slate-400 leading-relaxed border-t border-slate-200 pt-3">
        <strong>Internal estimate only.</strong> Rates sourced from FFL commission charts (rev 12/21/2023, fflcalc.netlify.app) and may shift by client age, product variant, or carrier contract changes. Always verify against your official FFL comp guide and the carrier's commission schedule before promising a payout to anyone — including yourself.
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// OVERRIDE INTELLIGENCE CENTER
//
// The recurring revenue + enterprise value engine, treated like the
// strategic asset it actually is. Every override dollar compounds.
//
// What this view answers — the questions PE diligence opens with:
//
//   1. How much override am I making this month / quarter / year?
//   2. WHO is funding the agency right now (and by how much)?
//   3. What's my concentration risk if my top producer leaves?
//   4. What's the LIFETIME VALUE of each downline relationship?
//   5. Which producers are growing? Which are declining?
//   6. Is the carrier's override % drifting on me (silent comp cut)?
//   7. What's my recruiting ROI per producer?
//
// Decision-grade design — every section has a thesis and an action.
// ════════════════════════════════════════════════════════════════════

function OverrideIntelligence({
  transactions = [],
  producers = {},
  setProducers,
  producerList = [],
  settings = {},
  financialConfig,
  setFinancialConfig,
  getCarrier = () => 'Unknown',
  openClientProfile = () => {},
  onGotoTab = () => {},
}) {
  const cfg = { ...DEFAULT_FINANCIAL_CONFIG, ...(financialConfig || {}) };
  const [editingContractFor, setEditingContractFor] = React.useState(null);

  // Effective contract level for a producer:
  //   1. Producer-specific if explicitly set on the producer object
  //   2. Otherwise the agency default from financialConfig
  // Override % = (1 - producerLevel)  — assumes agency at 100% baseline
  // Refined math: if you've got non-100% agency contracts per carrier,
  // operator can configure via per-carrier overrides (Phase 2).
  const contractLevelFor = (prodNum) => {
    const p = producers?.[prodNum];
    if (p && typeof p.contractLevel === 'number') return p.contractLevel;
    return cfg.avgProducerContractLevel;
  };

  // ── Date range state (operator-driven) ────────────────────────────
  // Persisted to localStorage so the operator's view sticks between
  // sessions. Default 'last_90d' because the original page anchored
  // every chart on a trailing-90-day window — keep that as the home
  // view, but let the operator slice any custom period.
  const [preset, setPreset] = React.useState(() => {
    try { return localStorage.getItem('override_intel:preset') || 'last_90d'; }
    catch { return 'last_90d'; }
  });
  const [customFrom, setCustomFrom] = React.useState(() => {
    try { return localStorage.getItem('override_intel:from') || ''; } catch { return ''; }
  });
  const [customTo, setCustomTo] = React.useState(() => {
    try { return localStorage.getItem('override_intel:to') || ''; } catch { return ''; }
  });
  React.useEffect(() => {
    try {
      localStorage.setItem('override_intel:preset', preset);
      localStorage.setItem('override_intel:from',   customFrom);
      localStorage.setItem('override_intel:to',     customTo);
    } catch {}
  }, [preset, customFrom, customTo]);

  const now = new Date();
  const DAY = 1000 * 60 * 60 * 24;

  const { fromDate, toDate, rangeLabel, rangeDays } = React.useMemo(() => {
    const today = new Date();
    const dayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const iso = (d) => d.toISOString().slice(0, 10);
    const daysBetween = (a, b) => Math.max(1, Math.round((b.getTime() - a.getTime()) / DAY) + 1);
    if (preset === 'today') {
      return { fromDate: iso(dayStart), toDate: iso(dayStart), rangeLabel: 'Today', rangeDays: 1 };
    }
    if (preset === 'mtd') {
      const s = new Date(today.getFullYear(), today.getMonth(), 1);
      return { fromDate: iso(s), toDate: iso(dayStart), rangeLabel: 'Month-to-Date', rangeDays: daysBetween(s, dayStart) };
    }
    if (preset === 'last_month') {
      const s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const e = new Date(today.getFullYear(), today.getMonth(), 0);
      return { fromDate: iso(s), toDate: iso(e), rangeLabel: 'Last Month', rangeDays: daysBetween(s, e) };
    }
    if (preset === 'last_90d') {
      const s = new Date(dayStart.getTime() - 89 * DAY);
      return { fromDate: iso(s), toDate: iso(dayStart), rangeLabel: 'Last 90 Days', rangeDays: 90 };
    }
    if (preset === 'qtd') {
      const q = Math.floor(today.getMonth() / 3);
      const s = new Date(today.getFullYear(), q * 3, 1);
      return { fromDate: iso(s), toDate: iso(dayStart), rangeLabel: 'Quarter-to-Date', rangeDays: daysBetween(s, dayStart) };
    }
    if (preset === 'ytd') {
      const s = new Date(today.getFullYear(), 0, 1);
      return { fromDate: iso(s), toDate: iso(dayStart), rangeLabel: 'Year-to-Date', rangeDays: daysBetween(s, dayStart) };
    }
    if (preset === 'last_12mo') {
      const s = new Date(today.getFullYear(), today.getMonth() - 11, 1);
      return { fromDate: iso(s), toDate: iso(dayStart), rangeLabel: 'Last 12 Months', rangeDays: daysBetween(s, dayStart) };
    }
    if (preset === 'custom') {
      const s = customFrom ? new Date(customFrom + 'T00:00:00') : new Date(today.getFullYear(), 0, 1);
      const e = customTo   ? new Date(customTo   + 'T00:00:00') : dayStart;
      return {
        fromDate: customFrom || iso(new Date(today.getFullYear(), 0, 1)),
        toDate:   customTo   || iso(dayStart),
        rangeLabel: customFrom && customTo ? `${customFrom} → ${customTo}` : 'Custom',
        rangeDays: daysBetween(s, e),
      };
    }
    return { fromDate: iso(dayStart), toDate: iso(dayStart), rangeLabel: 'Today', rangeDays: 1 };
  }, [preset, customFrom, customTo]);

  const rangeFromMs = React.useMemo(() => new Date(fromDate + 'T00:00:00').getTime(), [fromDate]);
  const rangeToMs   = React.useMemo(() => new Date(toDate   + 'T23:59:59').getTime(), [toDate]);
  // Prior equal-length range — for trajectory delta vs prior period.
  const priorRange = React.useMemo(() => {
    const ms = Math.max(DAY, rangeToMs - rangeFromMs);
    return { from: rangeFromMs - ms - DAY, to: rangeFromMs - DAY };
  }, [rangeFromMs, rangeToMs]);

  // Wider 6-month window — used for the trajectory chart + carrier
  // drift detection. These are inherently 6-month concepts and stay
  // independent of the operator's selected range.
  const win6moStart = new Date(now.getFullYear(), now.getMonth() - 5, 1);

  function txTs(tx) {
    const d = parseDate(tx.activityDate);
    return d ? d.getTime() : null;
  }
  function withinMs(tx, fromMs, toMs) {
    const t = txTs(tx);
    return t != null && t >= fromMs && t <= toMs;
  }

  // ── Aggregate per producer × period ───────────────────────────────
  function aggregateByProducer(txList) {
    const byProd = {};
    for (const tx of txList) {
      const prod = tx.prodNum || '(unassigned)';
      if (!byProd[prod]) byProd[prod] = { prodNum: prod, gross: 0, chargebacks: 0, policies: new Set(), apEstimate: 0 };
      const r = byProd[prod];
      const v = Number(tx.netToAccount) || 0;
      if (v >= 0) r.gross += v; else r.chargebacks += Math.abs(v);
      if (tx.policy) r.policies.add(`${tx.mga || ''}|${tx.policy}`);
      if (v > 0 && tx.commPremium) r.apEstimate += Math.abs(Number(tx.commPremium)) * 12;
    }
    return Object.values(byProd);
  }

  // ── Windowed tx slices driven by the selected range ───────────────
  const txRange = (transactions || []).filter(tx => withinMs(tx, rangeFromMs, rangeToMs));
  const txPrior = (transactions || []).filter(tx => withinMs(tx, priorRange.from, priorRange.to));
  const tx6mo   = (transactions || []).filter(tx => withinMs(tx, win6moStart.getTime(), now.getTime()));

  const aggByProdRange = aggregateByProducer(txRange);
  const aggByProdPrior = aggregateByProducer(txPrior);

  // ── Headline override revenue (selected range) ────────────────────
  const totalOverride = (aggs) => aggs.reduce((s, r) => {
    const lvl = contractLevelFor(r.prodNum);
    const net = r.gross - r.chargebacks;
    return s + net * (1 - lvl);
  }, 0);
  const overrideRange = totalOverride(aggByProdRange);
  const overridePrior = totalOverride(aggByProdPrior);
  const trajectoryDeltaRange = overridePrior <= 0
    ? null
    : (overrideRange - overridePrior) / Math.abs(overridePrior);
  // Annualized run rate from the selected range, used in the headline tile.
  const annualMult = 365 / Math.max(1, rangeDays);
  const overrideAnnualRunRate = overrideRange * annualMult;
  // Equivalent monthly override (for LTV / payback math below).
  const monthsInRange = Math.max(1, rangeDays / 30);

  // ── Per-producer enriched rows for the big table ──────────────────
  const producerRows = React.useMemo(() => {
    return aggByProdRange
      .map(r => {
        const lvl = contractLevelFor(r.prodNum);
        const overrideSpread = 1 - lvl;
        const net = r.gross - r.chargebacks;
        const overrideEarned = net * overrideSpread;
        const chargebackRate = r.gross > 0 ? r.chargebacks / r.gross : 0;
        // Monthly-equivalent override rate, derived from the picked
        // range length — drives LTV and payback math below.
        const overrideRatePerMonth = overrideEarned / monthsInRange;
        // LTV: avg monthly override × expected tenure × persistency adjustment.
        // Tenure assumption: 24 months base for FEX/MP producer.
        // Persistency adj: 1 - chargebackRate (more chargebacks = shorter
        // economic life of the override stream).
        const expectedTenure = Number.isFinite(cfg.producerExpectedTenureMonths) ? cfg.producerExpectedTenureMonths : 24;
        const persistencyAdj = Math.max(0.4, 1 - chargebackRate);
        const ltv = overrideRatePerMonth * expectedTenure * persistencyAdj;
        // Trajectory: this producer's override in the selected range vs
        // the prior equal-length range.
        const prevAgg = aggByProdPrior.find(p => p.prodNum === r.prodNum) || { gross: 0, chargebacks: 0 };
        const prevOverride = (prevAgg.gross - prevAgg.chargebacks) * overrideSpread;
        const trajectory = prevOverride === 0 ? null : (overrideEarned - prevOverride) / Math.abs(prevOverride);
        const name = getProducerName ? getProducerName(r.prodNum, producers) : r.prodNum;
        return {
          prodNum: r.prodNum,
          name,
          policyCount: r.policies.size,
          gross: r.gross,
          chargebacks: r.chargebacks,
          net,
          chargebackRate,
          contractLevel: lvl,
          overrideSpread,
          overrideEarned,
          overrideRatePerMonth,
          // Back-compat alias — kept for any downstream reader that
          // still references the old field name (Recruiting ROI math).
          overrideRate90Avg: overrideRatePerMonth,
          ltv,
          trajectory,
          ltvTier: ltv >= 75000 ? 'critical' : ltv >= 25000 ? 'high' : ltv >= 10000 ? 'medium' : 'low',
        };
      })
      .filter(r => r.gross > 0 || r.chargebacks > 0)
      .sort((a, b) => b.overrideEarned - a.overrideEarned);
  }, [aggByProdRange, aggByProdPrior, producers, cfg.avgProducerContractLevel, cfg.producerExpectedTenureMonths, monthsInRange]);

  // ── Concentration risk (Herfindahl-Hirschman Index) ───────────────
  const concentrationStats = React.useMemo(() => {
    const total = producerRows.reduce((s, r) => s + Math.max(0, r.overrideEarned), 0);
    if (total <= 0) return { hhi: 0, top1Pct: 0, top3Pct: 0, top5Pct: 0, top1: null, top3: [], totalProducers: 0 };
    const shares = producerRows.map(r => Math.max(0, r.overrideEarned) / total);
    const hhi = Math.round(shares.reduce((s, x) => s + (x * 100) ** 2, 0));
    const top1Pct = (shares[0] || 0) * 100;
    const top3Pct = shares.slice(0, 3).reduce((s, x) => s + x, 0) * 100;
    const top5Pct = shares.slice(0, 5).reduce((s, x) => s + x, 0) * 100;
    return {
      hhi,
      top1Pct, top3Pct, top5Pct,
      top1: producerRows[0] || null,
      top3: producerRows.slice(0, 3),
      totalProducers: producerRows.length,
    };
  }, [producerRows]);

  // ── Per-carrier override breakdown + drift detection ──────────────
  // For each carrier × month bucket: gross, override $, chargebacks.
  // Drift detection is inherently a 6-month concept, so this section
  // stays anchored on tx6mo and does NOT honor the operator's range.
  const carrierRows = React.useMemo(() => {
    const byCarrier = {};
    for (const tx of tx6mo) {
      const c = (typeof getCarrier === 'function' ? getCarrier(tx) : (tx.carrier || 'Other')) || 'Other';
      if (!byCarrier[c]) byCarrier[c] = { carrier: c, gross: 0, chargebacks: 0, policies: new Set(), monthly: {} };
      const v = Number(tx.netToAccount) || 0;
      if (v >= 0) byCarrier[c].gross += v; else byCarrier[c].chargebacks += Math.abs(v);
      if (tx.policy && v > 0) byCarrier[c].policies.add(`${tx.mga || ''}|${tx.policy}`);
      const d = parseDate(tx.activityDate);
      if (d) {
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (!byCarrier[c].monthly[key]) byCarrier[c].monthly[key] = { gross: 0, override: 0 };
        const overrideEst = Math.max(0, v) * (1 - cfg.avgProducerContractLevel);
        byCarrier[c].monthly[key].gross += Math.max(0, v);
        byCarrier[c].monthly[key].override += overrideEst;
      }
    }
    return Object.values(byCarrier).map(r => {
      const net = r.gross - r.chargebacks;
      const overrideTotal = net * (1 - cfg.avgProducerContractLevel);
      // Drift: take last 6 monthly buckets, compute month-over-month
      // change in (override / gross). If max-min > 2pts of the spread,
      // flag.
      const buckets = Object.entries(r.monthly).sort((a, b) => a[0].localeCompare(b[0]));
      const ratios = buckets.map(([k, v]) => v.gross > 0 ? v.override / v.gross : null).filter(x => x != null);
      let driftPts = null;
      if (ratios.length >= 2) {
        driftPts = (Math.max(...ratios) - Math.min(...ratios)) * 100;
      }
      return {
        ...r,
        net,
        overrideTotal,
        policyCount: r.policies.size,
        chargebackRate: r.gross > 0 ? r.chargebacks / r.gross : 0,
        driftPts,
        monthlyTrend: buckets,
      };
    }).sort((a, b) => b.overrideTotal - a.overrideTotal);
  }, [tx6mo, cfg.avgProducerContractLevel]);

  // ── 6-month override trajectory (chart data) ──────────────────────
  const trajectoryData = React.useMemo(() => {
    // 6 monthly buckets, oldest first
    const buckets = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      buckets.push({ key, label: d.toLocaleDateString('en-US', { month: 'short' }) });
    }
    const totalsByMonth = {};
    for (const tx of (transactions || [])) {
      const d = parseDate(tx.activityDate);
      if (!d) continue;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!buckets.find(b => b.key === key)) continue;
      const v = Number(tx.netToAccount) || 0;
      const lvl = contractLevelFor(tx.prodNum);
      const override = v * (1 - lvl);
      totalsByMonth[key] = (totalsByMonth[key] || 0) + override;
    }
    return buckets.map(b => ({
      month: b.label,
      override: Math.round(totalsByMonth[b.key] || 0),
    }));
  }, [transactions]);

  // Use the range-based delta everywhere (range vs prior equal-length
  // range) so a single picked window drives every trend signal on the
  // page. The 6-month bar chart remains a static visual reference.
  const trajectoryDelta = trajectoryDeltaRange;

  // ── Recruiting ROI ────────────────────────────────────────────────
  const RECRUIT_COST_DEFAULT = 500; // ad spend / referral fee / time
  const recruitCost = Number.isFinite(cfg.producerRecruitCost) ? cfg.producerRecruitCost : RECRUIT_COST_DEFAULT;

  // Health flags up top
  const flags = [];
  if (concentrationStats.top1Pct > 40) {
    flags.push({ tone: 'red', label: `${(concentrationStats.top1Pct).toFixed(0)}% of override revenue concentrated in ${concentrationStats.top1?.name}. If they leave with downline: ~${fmt(concentrationStats.top1?.ltv || 0)} of enterprise value at risk.` });
  } else if (concentrationStats.top3Pct > 75) {
    flags.push({ tone: 'amber', label: `Top 3 producers = ${(concentrationStats.top3Pct).toFixed(0)}% of override revenue. Diversify by recruiting middle tier.` });
  }
  if (concentrationStats.hhi > 2500) {
    flags.push({ tone: 'amber', label: `Herfindahl concentration index ${concentrationStats.hhi} — highly concentrated portfolio. PE diligence will discount enterprise value.` });
  }
  if (trajectoryDelta != null && trajectoryDelta < -0.15) {
    flags.push({ tone: 'red', label: `Override revenue trajectory DOWN ${(trajectoryDelta * 100).toFixed(0)}% in ${rangeLabel} vs prior equal window. Producer churn or carrier comp cut?` });
  } else if (trajectoryDelta != null && trajectoryDelta > 0.25) {
    flags.push({ tone: 'emerald', label: `Override revenue trajectory UP ${(trajectoryDelta * 100).toFixed(0)}% in ${rangeLabel} vs prior equal window. Flywheel turning.` });
  }
  for (const c of carrierRows) {
    if (c.driftPts != null && c.driftPts > 4 && c.gross > 1000) {
      flags.push({ tone: 'amber', label: `${c.carrier} override rate has drifted ${c.driftPts.toFixed(1)}pts over the last 6 months. Possible comp change or producer-level error.` });
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 via-amber-900 to-amber-700 text-white rounded-2xl p-6">
        <div className="text-amber-200 text-xs font-semibold uppercase tracking-wider">Override Intelligence</div>
        <h2 className="text-3xl font-bold mt-1">The recurring revenue engine</h2>
        <p className="text-amber-100 text-sm mt-2 max-w-3xl">
          Override = your real margin on downline production. Every dollar compounds into enterprise value because the downline + their persistency is what a PE buyer pays for. This is the strategic asset.
        </p>
      </div>

      {/* Date-range picker — every metric on this page filters to this window */}
      <div className="bg-white border-2 border-amber-300 rounded-xl p-3 flex items-center gap-2 flex-wrap">
        <span className="text-[0.55rem] font-black uppercase tracking-wider text-amber-700">Date range</span>
        {[
          ['today',      'Today'],
          ['mtd',        'MTD'],
          ['last_month', 'Last Month'],
          ['last_90d',   'Last 90d'],
          ['qtd',        'QTD'],
          ['ytd',        'YTD'],
          ['last_12mo',  'Last 12 Mo'],
          ['custom',     'Custom'],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setPreset(k)}
            className={`text-xs font-bold uppercase tracking-tight px-3 py-1.5 rounded ${preset === k ? 'bg-amber-600 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
            {l}
          </button>
        ))}
        {preset === 'custom' && (
          <span className="flex items-center gap-2 ml-2">
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="text-xs border border-slate-300 rounded px-2 py-1"/>
            <span className="text-slate-500">→</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="text-xs border border-slate-300 rounded px-2 py-1"/>
          </span>
        )}
        <span className="ml-auto text-[0.65rem] uppercase tracking-wider text-slate-500">
          {rangeLabel} · <span className="font-mono">{fromDate}</span> → <span className="font-mono">{toDate}</span> · {rangeDays}d
        </span>
      </div>

      {/* Health flags */}
      {flags.length > 0 && (
        <div className="space-y-2">
          {flags.map((f, i) => (
            <div key={i} className={`bg-${f.tone}-50 border border-${f.tone}-200 text-${f.tone}-900 rounded-xl p-3 text-sm`}>
              {f.tone === 'red' ? '' : f.tone === 'amber' ? '⚠' : '✓'} {f.label}
            </div>
          ))}
        </div>
      )}

      {/* Headline tiles — every value reflects the picked range */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <BigStatTile
          label={`Override · ${rangeLabel}`}
          value={fmt(overrideRange)}
          sub={`${producerRows.length} producers contributing`}
          tone={overrideRange >= 0 ? 'emerald' : 'red'}/>
        <BigStatTile
          label="vs Prior Window"
          value={trajectoryDelta == null ? '—' : `${trajectoryDelta >= 0 ? '+' : ''}${(trajectoryDelta * 100).toFixed(0)}%`}
          sub={`prior ${fmt(overridePrior)}`}
          tone={trajectoryDelta == null ? 'slate' : trajectoryDelta >= 0 ? 'emerald' : 'red'}/>
        <BigStatTile
          label="Annualized Run-Rate"
          value={fmt(overrideAnnualRunRate)}
          sub={`from ${rangeDays}d × 365`}
          tone="emerald"/>
        <BigStatTile
          label="Active downline"
          value={producerRows.length}
          sub={producerRows.length === 0 ? 'recruit one' : `avg ${fmt(overrideRange / Math.max(1, producerRows.length))}/producer`}
          tone="slate"/>
        <BigStatTile
          label="Concentration (HHI)"
          value={concentrationStats.hhi.toLocaleString()}
          sub={concentrationStats.hhi > 2500 ? 'highly concentrated' : concentrationStats.hhi > 1500 ? 'moderate' : 'diversified'}
          tone={concentrationStats.hhi > 2500 ? 'red' : concentrationStats.hhi > 1500 ? 'amber' : 'emerald'}/>
      </div>

      {/* Concentration risk visualization */}
      {producerRows.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <h3 className="font-semibold text-slate-900">Concentration risk</h3>
          <p className="text-xs text-slate-500 mt-0.5 mb-3">
            How much of your override comes from your top producers. The closer to 100%, the more enterprise value is at risk if someone leaves.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Top 1 producer</div>
              <div className="text-2xl font-bold tabular-nums text-amber-700 mt-1">{concentrationStats.top1Pct.toFixed(0)}%</div>
              <div className="text-xs text-slate-500 mt-0.5">{concentrationStats.top1?.name || '—'}</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Top 3 producers</div>
              <div className="text-2xl font-bold tabular-nums text-amber-700 mt-1">{concentrationStats.top3Pct.toFixed(0)}%</div>
              <div className="text-xs text-slate-500 mt-0.5">{concentrationStats.top3.map(t => t.name?.split(' ')[0]).filter(Boolean).join(' · ') || '—'}</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Top 5 producers</div>
              <div className="text-2xl font-bold tabular-nums text-amber-700 mt-1">{concentrationStats.top5Pct.toFixed(0)}%</div>
              <div className="text-xs text-slate-500 mt-0.5">remaining {Math.max(0, producerRows.length - 5)} contribute {Math.max(0, 100 - concentrationStats.top5Pct).toFixed(0)}%</div>
            </div>
          </div>
          {concentrationStats.top1 && (
            <div className={`text-xs rounded-lg p-3 ${concentrationStats.top1Pct > 40 ? 'bg-red-50 border border-red-200 text-red-900' : 'bg-slate-50 border border-slate-200 text-slate-700'}`}>
              <strong>Loss scenario:</strong> If your top producer ({concentrationStats.top1.name}) leaves with their downline, you immediately lose <strong>{fmt(concentrationStats.top1.overrideEarned * annualMult)}/yr</strong> of override revenue and approximately <strong>{fmt(concentrationStats.top1.ltv)}</strong> of enterprise value (≈ {((concentrationStats.top1.ltv / Math.max(1, producerRows.reduce((s, r) => s + r.ltv, 0))) * 100).toFixed(0)}% of total).
            </div>
          )}
        </div>
      )}

      {/* 6-month trajectory chart */}
      {trajectoryData.some(d => d.override > 0) && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <h3 className="font-semibold text-slate-900">6-month override trajectory</h3>
          <p className="text-xs text-slate-500 mt-0.5 mb-3">
            Total override revenue by month. Trend line is the truth — recruiting drives growth, attrition drives decline.
          </p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={trajectoryData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
              <XAxis dataKey="month" tick={{ fontSize: 12 }}/>
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}/>
              <Tooltip formatter={(v) => fmt(v)}/>
              <Bar dataKey="override" name="Override" fill="#d97706"/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Per-producer override P&L */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
          <div>
            <h3 className="font-semibold text-slate-900">Producer override P&L · {rangeLabel}</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Per-producer override after chargebacks + LTV at current pace × {Number.isFinite(cfg.producerExpectedTenureMonths) ? cfg.producerExpectedTenureMonths : 24}-month tenure assumption. Trajectory = this window vs prior equal window.
              <strong> Click a producer's contract level to set their actual contract % — defaults to {(cfg.avgProducerContractLevel * 100).toFixed(0)}% agency avg.</strong>
            </p>
          </div>
        </div>

        {producerRows.length === 0 ? (
          <div className="text-sm text-slate-500 italic py-6 text-center">No producer activity in {rangeLabel.toLowerCase()}.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Producer</th>
                  <th className="px-3 py-2 text-right">Contract %</th>
                  <th className="px-3 py-2 text-right">Override Spread</th>
                  <th className="px-3 py-2 text-right">Policies</th>
                  <th className="px-3 py-2 text-right">Gross</th>
                  <th className="px-3 py-2 text-right">CB Rate</th>
                  <th className="px-3 py-2 text-right">Override · {rangeLabel}</th>
                  <th className="px-3 py-2 text-right">Trajectory</th>
                  <th className="px-3 py-2 text-right">LTV</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {producerRows.map(r => (
                  <tr key={r.prodNum}>
                    <td className="px-3 py-2 font-medium text-slate-800">{r.name}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setEditingContractFor(r.prodNum)} className="text-blue-600 hover:text-blue-800 underline tabular-nums">
                        {(r.contractLevel * 100).toFixed(0)}%
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-700 font-semibold">{(r.overrideSpread * 100).toFixed(0)}pts</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.policyCount}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(r.gross)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${r.chargebackRate > 0.25 ? 'text-red-700 font-semibold' : 'text-slate-600'}`}>{(r.chargebackRate * 100).toFixed(0)}%</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-emerald-700">{fmt(r.overrideEarned)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-semibold ${r.trajectory == null ? 'text-slate-400' : r.trajectory >= 0.15 ? 'text-emerald-700' : r.trajectory <= -0.15 ? 'text-red-700' : 'text-slate-600'}`}>
                      {r.trajectory == null ? '—' : `${r.trajectory >= 0 ? '↑' : '↓'} ${(Math.abs(r.trajectory) * 100).toFixed(0)}%`}
                    </td>
                    <td className={`px-3 py-2 text-right tabular-nums font-bold ${r.ltvTier === 'critical' ? 'text-amber-700' : r.ltvTier === 'high' ? 'text-emerald-700' : r.ltvTier === 'medium' ? 'text-slate-700' : 'text-slate-500'}`}>{fmt(r.ltv)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-300 font-bold text-slate-900">
                  <td className="px-3 py-2">Totals</td>
                  <td className="px-3 py-2"/>
                  <td className="px-3 py-2"/>
                  <td className="px-3 py-2 text-right tabular-nums">{producerRows.reduce((s, r) => s + r.policyCount, 0)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(producerRows.reduce((s, r) => s + r.gross, 0))}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{(producerRows.reduce((s, r) => s + r.chargebacks, 0) / Math.max(1, producerRows.reduce((s, r) => s + r.gross, 0)) * 100).toFixed(0)}%</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{fmt(overrideRange)}</td>
                  <td className="px-3 py-2"/>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{fmt(producerRows.reduce((s, r) => s + r.ltv, 0))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Carrier breakdown + drift */}
      {carrierRows.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <h3 className="font-semibold text-slate-900">Override by carrier · drift detection</h3>
          <p className="text-xs text-slate-500 mt-0.5 mb-3">
            Which carriers fund your override. Drift &gt; 2pts in 6 months = comp change or attribution error — investigate.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Carrier</th>
                  <th className="px-3 py-2 text-right">Policies</th>
                  <th className="px-3 py-2 text-right">Gross</th>
                  <th className="px-3 py-2 text-right">CB Rate</th>
                  <th className="px-3 py-2 text-right">Override 6mo</th>
                  <th className="px-3 py-2 text-right">Drift</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {carrierRows.map(r => (
                  <tr key={r.carrier}>
                    <td className="px-3 py-2 font-medium text-slate-800">{r.carrier}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.policyCount}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(r.gross)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${r.chargebackRate > 0.25 ? 'text-red-700 font-semibold' : 'text-slate-600'}`}>{(r.chargebackRate * 100).toFixed(0)}%</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-emerald-700">{fmt(r.overrideTotal)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${r.driftPts == null ? 'text-slate-400' : r.driftPts > 4 ? 'text-red-700 font-bold' : r.driftPts > 2 ? 'text-amber-700' : 'text-slate-500'}`}>
                      {r.driftPts == null ? '—' : `${r.driftPts > 2 ? '⚠ ' : ''}${r.driftPts.toFixed(1)}pts`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recruiting ROI */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
          <div>
            <h3 className="font-semibold text-slate-900">Recruiting ROI</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              How fast each producer pays back recruiting cost. Best performers become the template for the next hire.
            </p>
          </div>
          <label className="text-xs text-slate-500 flex items-center gap-2">
            Recruit cost:
            <input type="number" value={recruitCost} onChange={(e) => setFinancialConfig({ ...cfg, producerRecruitCost: Number(e.target.value) || 0 })} className="border border-slate-200 rounded px-2 py-1 w-20 text-right"/>
          </label>
        </div>
        {producerRows.length === 0 ? (
          <div className="text-sm text-slate-500 italic py-6 text-center">No producers yet. Override compounds — start with one.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Producer</th>
                  <th className="px-3 py-2 text-right">Avg Override / mo</th>
                  <th className="px-3 py-2 text-right">Payback</th>
                  <th className="px-3 py-2 text-right">LTV / CAC</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {producerRows.map(r => {
                  const payback = r.overrideRate90Avg > 0 ? recruitCost / r.overrideRate90Avg : null;
                  const ltvCac = recruitCost > 0 ? r.ltv / recruitCost : null;
                  return (
                    <tr key={r.prodNum}>
                      <td className="px-3 py-2 font-medium text-slate-800">{r.name}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt(r.overrideRate90Avg)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${payback == null ? 'text-slate-400' : payback <= 3 ? 'text-emerald-700 font-bold' : payback <= 6 ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {payback == null ? '—' : `${payback.toFixed(1)} mo`}
                      </td>
                      <td className={`px-3 py-2 text-right tabular-nums ${ltvCac == null ? 'text-slate-400' : ltvCac >= 20 ? 'text-emerald-700 font-bold' : ltvCac >= 5 ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {ltvCac == null ? '—' : `${ltvCac.toFixed(0)}×`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3 text-[11px] text-slate-500 italic">
          Best benchmark: a producer paying back &lt;3 months with 20×+ LTV/CAC is your template. Hire more like them.
        </div>
      </div>

      {/* Drill into policy detail */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs">
        <strong>Need per-policy detail?</strong> The legacy <button onClick={() => onGotoTab('overrides')} className="text-blue-600 hover:text-blue-800 underline">"My Overrides" view</button> drills into every override transaction by policy.
        Use it when you need to audit a specific carrier statement.
      </div>

      {/* Methodology */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600">
        <strong>Methodology:</strong> Override per producer = (gross − chargebacks) × (1 − producer contract level).
        Every figure above honors the picked Date Range; trajectory compares the selected window to the prior equal-length window. Annualized run-rate = override in window × (365 / window days).
        LTV = avg monthly override × {Number.isFinite(cfg.producerExpectedTenureMonths) ? cfg.producerExpectedTenureMonths : 24}-month tenure assumption × (1 − chargeback rate) persistency adjustment.
        Concentration uses Herfindahl-Hirschman Index — sum of squared market shares × 10,000. HHI &gt; 2500 = highly concentrated. The 6-month trajectory chart and Drift detection are 6-month concepts and remain fixed to the trailing 6 months regardless of picked range.
      </div>

      {/* Contract level edit modal */}
      {editingContractFor && (
        <ContractLevelEditModal
          prodNum={editingContractFor}
          producers={producers}
          setProducers={setProducers}
          defaultLevel={cfg.avgProducerContractLevel}
          onClose={() => setEditingContractFor(null)}
        />
      )}
    </div>
  );
}

function ContractLevelEditModal({ prodNum, producers, setProducers, defaultLevel, onClose }) {
  const current = producers?.[prodNum];
  const [level, setLevel] = React.useState(current?.contractLevel != null ? current.contractLevel : defaultLevel);
  const name = getProducerName ? getProducerName(prodNum, producers) : prodNum;

  function save() {
    const n = Number(level);
    if (!Number.isFinite(n) || n < 0 || n > 2) {
      alert('Contract level must be a decimal between 0 and 2 (e.g. 0.80 for 80%)');
      return;
    }
    setProducers(prev => ({
      ...prev,
      [prodNum]: { ...(prev[prodNum] || { prodNum }), contractLevel: n },
    }));
    onClose();
  }
  function clear() {
    setProducers(prev => {
      const next = { ...prev };
      if (next[prodNum]) {
        const { contractLevel, ...rest } = next[prodNum];
        next[prodNum] = rest;
      }
      return next;
    });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 font-semibold text-slate-900">
          Contract Level · {name}
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Producer's contract level</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.01" min="0" max="2"
                value={level}
                onChange={(e) => setLevel(e.target.value)}
                autoFocus
                className="border border-slate-200 rounded-lg px-3 py-2 w-32 text-right tabular-nums"
              />
              <span className="text-sm text-slate-500">= {(Number(level) * 100).toFixed(0)}%</span>
            </div>
            <div className="text-xs text-slate-500 mt-2">
              Decimal between 0 and 2. Example: <code>0.80</code> = 80% contract. Override spread you keep = 100% − producer level (assumes agency at 100%).
            </div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-xs text-emerald-900">
            <strong>If set to {(Number(level) * 100).toFixed(0)}%</strong>, you keep <strong>{((1 - Number(level)) * 100).toFixed(0)}pts</strong> of override spread on this producer's AP.
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          {current?.contractLevel != null && (
            <button onClick={clear} className="text-xs text-slate-500 hover:text-slate-700 underline">Clear (use agency default)</button>
          )}
          <div className="flex items-center gap-2 ml-auto">
            <button aria-label="Close" onClick={onClose} className="text-sm text-slate-600 hover:text-slate-800 font-semibold px-3 py-1.5">Cancel</button>
            <button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg px-4 py-1.5">Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// CLIENTS — Client Lifecycle + CS/Billing Agent (Phase 1)
//
// Close-intake surface that feeds the lifecycle messaging agent.
// Carrier statements give us insured + policy but no phone, no draft
// date, no billing context. This tab is where that data gets entered
// at close (or synced from GHL later) so the welcome / first-draft /
// day-30 / month-3-6-9 / anniversary / NSF-recovery touchpoints can
// run.
//
// All data lives in public.clients + public.consent in Supabase
// (migration 019). RLS scopes every read/write to auth.uid() so we
// never need to filter by owner_id client-side.
//
// IMPORTANT — consent design:
//   • care_opted_in defaults to true (client just bought a policy).
//     Required for transactional touches: welcome, first-draft
//     reminder, day-30, NSF recovery.
//   • marketing_ok defaults to false. Required for referral-bearing
//     touches: month-3, 6, 9, anniversary. Operator must affirmatively
//     check this at intake (or capture YES later).
//   • Both checks happen in /api/lifecycle/send before any wire — never
//     rely on the UI alone to enforce.
// ════════════════════════════════════════════════════════════════════

const CLIENT_TZ_OPTIONS = [
  { value: 'America/New_York',    label: 'Eastern (NY)' },
  { value: 'America/Chicago',     label: 'Central (Chicago)' },
  { value: 'America/Denver',      label: 'Mountain (Denver)' },
  { value: 'America/Phoenix',     label: 'Arizona (no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific (LA)' },
  { value: 'America/Anchorage',   label: 'Alaska' },
  { value: 'Pacific/Honolulu',    label: 'Hawaii' },
  { value: 'America/Puerto_Rico', label: 'Atlantic (Puerto Rico)' },
];

const BILLING_MODE_OPTIONS = [
  { value: 'monthly',   label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual',    label: 'Annual' },
];

const BILLING_STATUS_OPTIONS = [
  { value: 'active',    label: 'Active',    color: 'emerald' },
  { value: 'nsf',       label: 'NSF',       color: 'amber'   },
  { value: 'lapsed',    label: 'Lapsed',    color: 'red'     },
  { value: 'cancelled', label: 'Cancelled', color: 'slate'   },
];

const DEFAULT_CLIENT = {
  name: '',
  phone: '',
  email: '',
  timezone: 'America/Chicago',
  policy: '',
  carrier: '',
  prod_num: '',
  close_date: '',
  issue_date: '',
  first_draft_date: '',
  draft_amount: '',
  billing_mode: 'monthly',
  billing_status: 'active',
  billing_schedule_mode: 'fixed_date',
  billing_day_of_month: null,
  billing_week_of_month: null,
  billing_day_of_week: null,
  lifecycle_paused: false,
  // Verbal verification — agent and client agree on these at close.
  // Used when the agent (or a teammate) calls the client later and
  // needs to confirm identity over the phone.
  security_code: '',
  security_question: '',
  consent: { care_opted_in: true, marketing_ok: false },
  // v2 SECTOR MODEL: spine of every downstream branch (Medicare gate,
  // calendar windows, pipeline, compliance). 'aca_private' = under-65;
  // 'medicare' = 65+ AEP/OEP/SOA workflows. Defaults to aca_private.
  sector: 'aca_private',
  // v2 VERTICAL: 'life' (FEX/Term/IUL — default) vs 'health'
  // (ACA/Medicare/PPO/dental). Drives the Health Portal isolation.
  vertical: 'life',
  // Demographics (used by sector switches + cross-sell simulator).
  dob: '',
  state_abbr: '',
  sex: '',
  height_in: '',
  weight_lb: '',
  // Medicare-only fields — gated in the form by sector === 'medicare'.
  mbi: '',                         // Treat like SSN: mask in UI; never log raw.
  part_a_effective: '',
  part_b_effective: '',
  turn65_date: '',                 // Auto-computed from DOB (DOB + 65y).
  current_plan_type: '',
  current_carrier: '',
  current_plan_id: '',
  lis_dual_status: '',
  chronic_conditions: [],
  // Compliance flags (BOTH sectors): SMS opt-in + PTC required for
  // any outbound, full stop. Medicare PTC is the stricter of the two.
  opt_in_sms: false,
  permission_to_contact: false,
};

// MBI masker — only show last 4 in UI. Pattern: 1EG4-TE5-MK73.
function maskMbi(v) {
  if (!v) return '';
  const s = String(v).trim();
  if (s.length <= 4) return s;
  return `••••-••-${s.slice(-4)}`;
}
// Derive turn65 from DOB. Returns yyyy-mm-dd or ''.
function deriveTurn65(dobStr) {
  if (!dobStr) return '';
  const d = new Date(dobStr + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getUTCFullYear() + 65;
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function normalizeClientPhone(raw) {
  if (!raw) return '';
  const digits = String(raw).replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (digits.length > 11) return `+${digits}`;
  return '';
}

function estimateNextDraft(client) {
  if (!client.first_draft_date) return null;
  const first = new Date(client.first_draft_date + 'T00:00:00');
  if (Number.isNaN(first.getTime())) return null;
  const now = new Date();
  if (now < first) return first;
  const stepMonths = client.billing_mode === 'annual' ? 12
                  : client.billing_mode === 'quarterly' ? 3 : 1;
  const next = new Date(first.getTime());
  let safety = 200;
  while (next <= now && safety-- > 0) next.setMonth(next.getMonth() + stepMonths);
  return next;
}

// ── Clients & Policies merged wrapper ──────────────────────────
// Operator IA cleanup 2026-06-26: the "Policies" nav tab was
// removed in favor of a single "Clients & Policies" tab. This
// wrapper renders a slim sub-tab toggle and switches between the
// existing ClientsView (default) and the existing MyPoliciesView.
//
// Both views are unchanged — they receive the same props they
// always did, so there's no risk of subtle behavior drift. The
// sub-tab choice persists in localStorage so refresh doesn't yank
// the operator off whichever surface they were just in.
const CP_SUBTAB_KEY = 'crm:clients_policies_subtab';

function ClientsAndPoliciesView(props) {
  const [subTab, setSubTab] = React.useState(() => {
    try {
      const v = localStorage.getItem(CP_SUBTAB_KEY);
      return v === 'policies' ? 'policies' : 'clients';
    } catch { return 'clients'; }
  });
  React.useEffect(() => {
    try { localStorage.setItem(CP_SUBTAB_KEY, subTab); } catch {}
  }, [subTab]);

  return (
    <div>
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8 pt-3">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
          <button
            onClick={() => setSubTab('clients')}
            className={`text-xs font-bold uppercase tracking-tight px-3 py-1.5 rounded-md transition-colors ${subTab === 'clients' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'}`}
          >
            👥 Clients
          </button>
          <button
            onClick={() => setSubTab('policies')}
            className={`text-xs font-bold uppercase tracking-tight px-3 py-1.5 rounded-md transition-colors ${subTab === 'policies' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'}`}
          >
            📄 Policies
          </button>
        </div>
      </div>
      {subTab === 'clients'
        ? <ClientsView {...props}/>
        : <MyPoliciesView
            transactions={props.transactions}
            producers={props.producers}
            settings={props.settings}
            currentUser={props.currentUser}
            getCarrier={props.getCarrier}
          />}
    </div>
  );
}

function ClientsView({
  currentUser,
  showToast = () => {},
  transactions = [],
  settings = {},
  getCarrier = () => '',
  producers = {},
  onGotoTab = () => {},
  // Subscription workspace — 'life' | 'health' | 'both'. In 'both' the
  // roster gets a product segmented control; otherwise it stays the
  // single-line book it has always been. Defaults to 'life' so nothing
  // changes for callers that don't pass it.
  workspaceMode = 'life',
  // Demo Mode — when non-null, CommissionTracker.jsx has already
  // adapted the seed book of business (src/demo/liveAdapters.js's
  // demoClients()) into this exact `clients` row shape. Skip the real
  // Supabase fetch entirely and render this instead. null in real mode
  // (the default) leaves every line below byte-for-byte unchanged.
  demoClients = null,
}) {
  const [clients, setClients] = React.useState(() => demoClients || []);
  const [loading, setLoading] = React.useState(!demoClients);
  const [loadError, setLoadError] = React.useState(null);
  const [search, setSearch] = React.useState('');
  const [editing, setEditing] = React.useState(null);
  const [syncing, setSyncing] = React.useState(false);
  // Open the Policy Delivery Summary modal for a specific client row.
  // null = closed. The modal renders out-of-flow at the end of this view.
  const [deliveryClient, setDeliveryClient] = React.useState(null);
  // Per-client NSF risk envelope keyed by client.id. Loaded alongside
  // clients via a single nsf_events query and computed in-memory using
  // the same model as the scan-client-payments cron — see
  // api/_paymentRisk.js.
  const [riskByClient, setRiskByClient] = React.useState({});

  // The operator's OWN writing producer #. Admins identify themselves
  // via settings.ownerProdNum; agent-role users carry it on
  // currentUser.prodNum. This drives the downline-exclusion rule below.
  //
  // CRITICAL — DOWNLINE RULE:
  //   If a client's prod_num doesn't equal this value, the lifecycle
  //   agent MUST NOT text them. Downline producers handle their own
  //   clients through their own seats; the agency owner texting on
  //   their behalf would impersonate, use the wrong voice, and confuse
  //   the relationship. Enforced in two places:
  //     1) UI badge ("Downline") so the operator sees it.
  //     2) Phase 2 enqueuer cron — server-side filter.
  const myProdNum = String(
    (currentUser?.role !== 'admin' && currentUser?.prodNum) ||
    settings?.ownerProdNum || ''
  ).trim();

  const loadClients = React.useCallback(async () => {
    // Demo Mode — render the pre-adapted seed book instead of hitting
    // Supabase. Covers every caller of loadClients (initial mount,
    // refreshClients, syncFromBook) with one guard.
    if (demoClients) {
      setClients(demoClients);
      setRiskByClient({});
      setLoadError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    // Two-step fetch instead of `select('*, consent(*)')` — PostgREST
    // sometimes can't resolve the implicit FK relationship if the
    // schema cache hasn't picked up migration 019's consent table,
    // which results in the production-killing "Could not find a
    // relationship between 'clients' and 'consent'" error on the
    // main screen. Fetching consent rows separately is one extra
    // round trip but cannot fail this way.
    const { data: rows, error } = await supabase
      .from('clients')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      setLoadError(error.message);
      console.error('[ClientsView] load failed:', error.message);
      setLoading(false);
      return;
    }
    const clientList = Array.isArray(rows) ? rows : [];
    // Best-effort consent join — if the consent table isn't there
    // (older Supabase project, migration 019 not run), we still
    // render the client list without consent badges.
    if (clientList.length > 0) {
      try {
        const ids = clientList.map(c => c.id).filter(Boolean);
        const { data: consents } = await supabase
          .from('consent')
          .select('*')
          .in('client_id', ids);
        if (Array.isArray(consents)) {
          const byClient = new Map();
          for (const c of consents) {
            if (!byClient.has(c.client_id)) byClient.set(c.client_id, []);
            byClient.get(c.client_id).push(c);
          }
          for (const c of clientList) c.consent = byClient.get(c.id) || [];
        }
      } catch { /* consent table absent → render without */ }
    }
    setClients(clientList);

    // NSF events for risk badges. One round trip for the whole book;
    // risk computed in memory. Mirrors api/_paymentRisk.js's model —
    // kept inline rather than imported so the browser bundle doesn't
    // need to require from api/. If the rules diverge, sync them.
    try {
      const { data: nsf } = await supabase
        .from('nsf_events')
        .select('client_id, occurred_at, created_at');
      const byClient = new Map();
      for (const r of (nsf || [])) {
        if (!r.client_id) continue;
        if (!byClient.has(r.client_id)) byClient.set(r.client_id, []);
        byClient.get(r.client_id).push(r);
      }
      const DAY_MS = 86_400_000;
      const out = {};
      for (const [cid, events] of byClient) {
        const now = Date.now();
        let count90 = 0, recent = 0;
        for (const e of events) {
          const t = Date.parse(e.occurred_at || e.created_at || '');
          if (!Number.isFinite(t)) continue;
          if (t >= now - 90 * DAY_MS) count90++;
          if (t >= now - 14 * DAY_MS) recent++;
        }
        let score = 0, level = 'low';
        if (count90 === 0)      { score = events.length > 5 ? 40 : Math.min(20, events.length * 5); level = events.length > 5 ? 'medium' : 'low'; }
        else if (count90 === 1) { score = 35; level = 'medium'; }
        else if (count90 === 2) { score = 65; level = 'high'; }
        else                    { score = Math.min(100, 80 + (count90 - 3) * 5); level = 'high'; }
        if (recent > 0) score = Math.min(100, score + 10);
        out[cid] = { level, score, count90 };
      }
      setRiskByClient(out);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[ClientsView] nsf_events load skipped:', err.message || err);
      setRiskByClient({});
    }
    setLoading(false);
  }, [demoClients]);

  React.useEffect(() => { loadClients(); }, [loadClients]);

  // Clients view is LIFE-ONLY. Health clients have their own dedicated
  // portal (HealthClientsView at tab='health_clients') with health-
  // specific status filters (NSF / lapsed / cancelled) and carrier
  // grids. Surfacing them here was bleeding two different products into
  // one mental model.
  // Product segment (Both-workspace only). Life-only agencies never see
  // health clients here; a Both agency slices its book by line. Defaults
  // to 'life' so single-line callers render exactly as before.
  const [productSegment, setProductSegment] = React.useState('life');
  const matchesBook = (c) => {
    const vert = c.vertical === 'health' ? 'health' : 'life';
    switch (productSegment) {
      case 'all':      return true;
      case 'aca':      return vert === 'health' && c.sector !== 'medicare';
      case 'medicare': return vert === 'health' && c.sector === 'medicare';
      case 'life':
      default:         return vert === 'life';
    }
  };

  // The life-only book BookOfBusinessBoard renders. It owns its own
  // status-tab / search / dropdown filtering internally (search state is
  // still lifted here so it round-trips with the rest of the view).
  const inBook = clients.filter(matchesBook);

  async function saveClient(payload) {
    const isNew = !!payload.__new;
    const ownerId = currentUser?.id;
    if (!ownerId) { showToast('Not signed in.'); return; }

    const clientRow = {
      name:             (payload.name || '').trim(),
      phone:            normalizeClientPhone(payload.phone),
      email:            (payload.email || '').trim() || null,
      timezone:         payload.timezone || 'America/Chicago',
      policy:           (payload.policy || '').trim() || null,
      carrier:          (payload.carrier || '').trim() || null,
      prod_num:         (payload.prod_num || '').trim() || null,
      close_date:       payload.close_date || null,
      issue_date:       payload.issue_date || null,
      first_draft_date: payload.first_draft_date || null,
      draft_amount:     payload.draft_amount === '' ? null : Number(payload.draft_amount) || null,
      billing_mode:           payload.billing_mode || 'monthly',
      billing_status:         payload.billing_status || 'active',
      billing_schedule_mode:  payload.billing_schedule_mode === 'week_based' ? 'week_based' : 'fixed_date',
      billing_day_of_month:   payload.billing_day_of_month === '' || payload.billing_day_of_month == null ? null : Number(payload.billing_day_of_month) || null,
      billing_week_of_month:  payload.billing_week_of_month === '' || payload.billing_week_of_month == null ? null : Number(payload.billing_week_of_month) || null,
      billing_day_of_week:    payload.billing_day_of_week === '' || payload.billing_day_of_week == null ? null : Number(payload.billing_day_of_week),
      lifecycle_paused: !!payload.lifecycle_paused,
      auto_delivery_opted_out: !!payload.auto_delivery_opted_out,
      security_code:     (payload.security_code     || '').trim() || null,
      security_question: (payload.security_question || '').trim() || null,
      // v2 — vertical + sector + demographics + Medicare-specific fields.
      // Vertical defaults to 'life'; flipping to 'health' moves the
      // client into the Health Portal book.
      vertical:          payload.vertical === 'health' ? 'health' : 'life',
      sector:            payload.sector === 'medicare' ? 'medicare' : 'aca_private',
      dob:               payload.dob || null,
      state_abbr:        (payload.state_abbr || '').trim().toUpperCase() || null,
      sex:               payload.sex || null,
      height_in:         payload.height_in === '' || payload.height_in == null ? null : Number(payload.height_in) || null,
      weight_lb:         payload.weight_lb === '' || payload.weight_lb == null ? null : Number(payload.weight_lb) || null,
      mbi:               (payload.mbi || '').trim() || null,
      part_a_effective:  payload.part_a_effective || null,
      part_b_effective:  payload.part_b_effective || null,
      turn65_date:       payload.turn65_date || (payload.dob ? deriveTurn65(payload.dob) : null) || null,
      current_plan_type: payload.current_plan_type || null,
      current_carrier:   (payload.current_carrier || '').trim() || null,
      current_plan_id:   (payload.current_plan_id || '').trim() || null,
      lis_dual_status:   payload.lis_dual_status || null,
      chronic_conditions: Array.isArray(payload.chronic_conditions)
                            ? payload.chronic_conditions
                            : ((payload.chronic_conditions || '').split(',').map(s => s.trim()).filter(Boolean)),
      opt_in_sms:           !!payload.opt_in_sms,
      permission_to_contact:!!payload.permission_to_contact,
    };
    if (!clientRow.name) { showToast('Name is required.'); return; }

    let clientId = payload.id;
    // Capture audit-sensitive fields BEFORE the write so we can detect
    // transitions + record them to audit_events afterward. Field set
    // is what an auditor will care about: TCPA/PTC, SMS opt-in, the
    // billing-status NSF transition (already tracked elsewhere), and
    // the policy + carrier identification.
    let prevAuditFields = null;
    if (!isNew && clientId) {
      try {
        const { data: existingForAudit } = await supabase
          .from('clients')
          .select('permission_to_contact, opt_in_sms, policy, carrier, prod_num, billing_status')
          .eq('id', clientId).maybeSingle();
        prevAuditFields = existingForAudit || null;
      } catch { /* best-effort */ }
    }

    // Resilient write: the clients table's columns drift per tenant (some lack
    // security_code / billing_schedule_mode / part_a_effective / etc.). A single
    // missing column used to 500 the whole insert and silently leave the modal
    // open ("won't let me add a client"). On a "column not found" error we drop
    // that column and retry, so the client is created with whatever columns the
    // tenant actually has. Dropped columns are logged, not fatal.
    const clientsWriteResilient = async (row, mode, id) => {
      let payload = { ...row }; const dropped = [];
      for (let i = 0; i < 20; i++) {
        const q = mode === 'insert'
          ? await supabase.from('clients').insert(payload).select().single()
          : await supabase.from('clients').update(payload).eq('id', id);
        if (!q.error) return { data: q.data, dropped };
        const m = /could not find the '([a-z0-9_]+)' column/i.exec(q.error.message || '');
        if (m && Object.prototype.hasOwnProperty.call(payload, m[1])) { delete payload[m[1]]; dropped.push(m[1]); continue; }
        return { error: q.error, dropped };
      }
      return { error: { message: 'too many unknown columns on clients' }, dropped };
    };

    if (isNew) {
      const { data, error, dropped } = await clientsWriteResilient({ ...clientRow, owner_id: ownerId }, 'insert');
      if (error) { showToast(`Save failed: ${error.message}`); return; }
      if (dropped?.length) console.warn('[addClient] clients table missing columns (saved without them):', dropped);
      clientId = data.id;
    } else {
      // Detect billing_status transition INTO 'nsf' so we can log a
      // nsf_events row after the update. Query the existing value
      // BEFORE writing so re-saves of an already-NSF client don't
      // double-log. Best-effort — failure to detect doesn't block the
      // save itself.
      const wasAlreadyNsf = String(prevAuditFields?.billing_status || '').toLowerCase() === 'nsf';

      const { error, dropped } = await clientsWriteResilient(clientRow, 'update', clientId);
      if (error) { showToast(`Save failed: ${error.message}`); return; }
      if (dropped?.length) console.warn('[editClient] clients table missing columns (saved without them):', dropped);

      const isNowNsf = String(clientRow.billing_status || '').toLowerCase() === 'nsf';
      if (isNowNsf && !wasAlreadyNsf) {
        const { error: nsfErr } = await supabase.from('nsf_events').insert({
          owner_id:    ownerId,
          client_id:   clientId,
          occurred_at: new Date().toISOString(),
          reason:      'manual',
          amount:      clientRow.draft_amount || null,
          source:      'manual',
          notes:       'Logged from client edit modal — billing_status set to NSF.',
        });
        if (nsfErr) console.warn('[ClientsView] nsf_events insert warning:', nsfErr.message);
      }
    }

    // ── Audit-event emission ───────────────────────────────────────
    // After a successful save, post to /api/audit/record so the chain
    // captures (a) PTC transitions specifically and (b) any other
    // changes to audit-sensitive fields as a single
    // client_record_edited event. Best-effort — audit failures never
    // block the primary save.
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const jwt = session?.access_token;
      const post = async (event_type, payloadBody) => {
        if (!jwt || !clientId) return;
        try {
          await fetch('/api/audit/record', {
            method: 'POST',
            headers: { 'content-type': 'application/json', Authorization: `Bearer ${jwt}` },
            body: JSON.stringify({ client_id: clientId, event_type, payload: payloadBody }),
          });
        } catch { /* swallow */ }
      };
      if (isNew) {
        // First-touch: any PTC=true on creation is an immediate capture event.
        if (clientRow.permission_to_contact) {
          await post('ptc_captured', { source: 'client_form_create', via: 'manual' });
        }
        await post('client_record_edited', { kind: 'create', vertical: clientRow.vertical, sector: clientRow.sector });
      } else if (prevAuditFields) {
        const ptcBefore = !!prevAuditFields.permission_to_contact;
        const ptcAfter  = !!clientRow.permission_to_contact;
        if (ptcAfter && !ptcBefore) {
          await post('ptc_captured', { source: 'client_form_edit', via: 'manual' });
        } else if (!ptcAfter && ptcBefore) {
          await post('ptc_captured', { source: 'client_form_edit', revoked: true });
        }
        // Single client_record_edited event with the diff of
        // audit-sensitive fields. Payload stays small + reviewable.
        const changed = {};
        const watch  = ['permission_to_contact', 'opt_in_sms', 'policy', 'carrier', 'prod_num', 'billing_status'];
        for (const k of watch) {
          const before = (prevAuditFields[k] === undefined || prevAuditFields[k] === null) ? '' : prevAuditFields[k];
          const after  = (clientRow[k]        === undefined || clientRow[k]        === null) ? '' : clientRow[k];
          if (String(before) !== String(after)) changed[k] = { from: before, to: after };
        }
        if (Object.keys(changed).length > 0) {
          await post('client_record_edited', { kind: 'edit', changed });
        }
      }
    } catch { /* never let an audit hiccup fail the save */ }

    // ── PHI segregation Phase 2a: DOB dual-write bridge ─────────────
    // clientRow.dob was just written directly to Supabase from the
    // browser (RLS-scoped) — MBI_ENC_KEY is a server-only secret and
    // can never reach this bundle, so encryption + the de-identified
    // birth_year/month/day derivation happen server-side via a
    // best-effort follow-up call. Never blocks the save.
    if (clientRow.dob && clientId) {
      try {
        await apiFetch('/api/clients/dob-sync', {
          method: 'POST',
          body: JSON.stringify({ client_id: clientId }),
        });
      } catch { /* best-effort — DOB encryption sync never blocks the save */ }
    }

    const consentPayload = {
      care_opted_in: payload.consent?.care_opted_in ?? true,
      marketing_ok:  payload.consent?.marketing_ok  ?? false,
    };
    const { error: cErr } = await supabase
      .from('consent')
      .update(consentPayload)
      .eq('client_id', clientId);
    if (cErr) console.warn('[ClientsView] consent update warning:', cErr.message);

    setEditing(null);
    await loadClients();
    showToast(isNew ? 'Client added' : 'Client updated');
  }

  async function deleteClient(client) {
    if (!(await hqConfirm({
      title: `Delete ${client.name || 'this client'}?`,
      body: 'This also removes consent + lifecycle history. Cannot be undone.',
      confirmLabel: 'Delete client', danger: true,
    }))) return;
    const { error } = await supabase.from('clients').delete().eq('id', client.id);
    if (error) { showToast(`Delete failed: ${error.message}`); return; }
    setEditing(null);
    await loadClients();
    showToast('Client deleted');
  }

  // Book-of-business sync. Builds client stubs for every (insured, policy)
  // the operator wrote that isn't already in the clients table.
  //
  // DOWNLINE EXCLUSION:
  //   Only transactions where tx.prodNum === myProdNum are imported.
  //   Downline writes are deliberately skipped — those clients get
  //   texted by the producer who actually wrote them.
  //
  // The stub has no phone, no draft date, no consent flip. That's by
  // design: the operator has to fill in the contact data the carrier
  // statement doesn't carry. The lifecycle sender cron will skip stubs
  // with missing phone/dates until they're filled.
  async function syncFromBook() {
    const ownerId = currentUser?.id;
    if (!ownerId) { showToast('Not signed in.'); return; }
    if (!myProdNum) {
      showToast('Set your producer # in Settings before syncing the book.');
      return;
    }
    setSyncing(true);
    try {
      const mine = (transactions || []).filter(t =>
        String(t.prodNum || '').trim() === myProdNum &&
        (t.insured || '').toString().trim() &&
        (t.policy  || '').toString().trim()
      );
      // Dedupe by (lower-cased insured + policy).
      const seen = new Set();
      const candidates = [];
      for (const tx of mine) {
        const insuredKey = String(tx.insured).trim().toLowerCase();
        const policyKey  = String(tx.policy).trim();
        const key = `${insuredKey}::${policyKey}`;
        if (seen.has(key)) continue;
        seen.add(key);
        candidates.push({
          name:     String(tx.insured).trim(),
          policy:   policyKey,
          carrier:  (getCarrier ? getCarrier(tx) : tx.carrier) || null,
          prod_num: myProdNum,
          issue_date: tx.effectiveDate || tx.issueDate || null,
          billing_mode:   'monthly',
          billing_status: tx.chargedBack ? 'lapsed' : 'active',
        });
      }
      // Skip anything already represented in the clients table — match
      // on policy first (most reliable), fall back to insured name.
      const havePolicy = new Set(
        clients.map(c => String(c.policy || '').trim()).filter(Boolean)
      );
      const haveName = new Set(
        clients.map(c => String(c.name || '').trim().toLowerCase()).filter(Boolean)
      );
      const toInsert = candidates.filter(c =>
        !havePolicy.has(c.policy) && !haveName.has(c.name.toLowerCase())
      );
      if (toInsert.length === 0) {
        showToast('Book is already in sync — no new policies to import.');
        return;
      }
      const rows = toInsert.map(c => ({
        ...c,
        owner_id: ownerId,
        timezone: 'America/Chicago',
      }));
      const { error } = await insertClientSafe(rows);
      if (error) { showToast(`Sync failed: ${error.message}`); return; }
      await loadClients();
      showToast(`Synced ${toInsert.length} client${toInsert.length === 1 ? '' : 's'} from your book. Add phones + first-draft dates to start texting.`);
    } finally {
      setSyncing(false);
    }
  }

  // Count of downline policies in the transactions feed — surfaced in
  // the header so the operator sees we deliberately excluded them.
  const downlinePolicyCount = React.useMemo(() => {
    if (!myProdNum) return 0;
    const set = new Set();
    for (const tx of (transactions || [])) {
      const pn = String(tx.prodNum || '').trim();
      const policy = String(tx.policy || '').trim();
      if (!pn || !policy) continue;
      if (pn === myProdNum) continue;
      set.add(`${pn}::${policy}`);
    }
    return set.size;
  }, [transactions, myProdNum]);

  // Helper for the row badge.
  const isDownlinePolicy = (c) => {
    if (!myProdNum) return false;
    const pn = String(c.prod_num || '').trim();
    if (!pn) return false;
    return pn !== myProdNum;
  };

  return (
    <div className="space-y-5">
      {/* Product segment — only a Both-workspace agency can slice the book
          by line. Life-only / Health-only never render this. */}
      {workspaceMode === 'both' && !loading && !loadError && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mr-1">Book</span>
          {[
            { id: 'life',     label: 'Life' },
            { id: 'aca',      label: 'ACA · Private' },
            { id: 'medicare', label: 'Medicare' },
            { id: 'all',      label: 'All Clients' },
          ].map(seg => (
            <button
              key={seg.id}
              onClick={() => setProductSegment(seg.id)}
              className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${
                productSegment === seg.id
                  ? 'bg-slate-900 text-white shadow'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >{seg.label}</button>
          ))}
        </div>
      )}
      {loading ? (
        <SkeletonBlock lines={6}/>
      ) : loadError ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-sm text-red-800">
          <div className="font-semibold">Couldn't load clients.</div>
          <div className="text-xs mt-1">{loadError}</div>
          <div className="text-xs mt-2 text-red-600">If you're seeing this, migration 019 might not be applied to your Supabase project yet. Run <code>supabase_migrations/019_client_lifecycle.sql</code> in the SQL editor.</div>
        </div>
      ) : (
        <BookOfBusinessBoard
          clients={inBook}
          riskByClient={riskByClient}
          getAgentName={(pn) => getProducerName(pn, producers)}
          search={search}
          setSearch={setSearch}
          onAddClient={() => setEditing({ ...DEFAULT_CLIENT, __new: true })}
          onEditClient={(c) => {
            const cons = Array.isArray(c.consent) ? c.consent[0] : c.consent;
            setEditing({ ...c, consent: cons || { care_opted_in: true, marketing_ok: false } });
          }}
          onDeliverySummary={(c) => setDeliveryClient(c)}
          onSyncBook={syncFromBook}
          syncing={syncing}
          myProdNum={myProdNum}
          downlinePolicyCount={downlinePolicyCount}
          isDownlinePolicy={isDownlinePolicy}
          showToast={showToast}
          refreshClients={loadClients}
          estimateNextDraft={estimateNextDraft}
          isDemo={!!demoClients}
          onGotoTab={onGotoTab}
        />
      )}

      {editing && (
        <ClientFormModal
          client={editing}
          onClose={() => setEditing(null)}
          onSave={saveClient}
          onDelete={() => deleteClient(editing)}
        />
      )}

      <PolicyDeliveryModal
        client={deliveryClient}
        settings={settings}
        open={!!deliveryClient}
        onClose={() => setDeliveryClient(null)}
        showToast={showToast}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Client add/edit modal — every field the lifecycle agent reads.
// ──────────────────────────────────────────────────────────────────
function ClientFormModal({ client, onClose, onSave, onDelete }) {
  const [c, setC] = React.useState(client);
  const set = (k, v) => setC(prev => ({ ...prev, [k]: v }));
  const setConsent = (k, v) => setC(prev => ({ ...prev, consent: { ...(prev.consent || {}), [k]: v } }));

  // When DOB changes, auto-compute turn65_date — the bridge field that
  // drives the Turning 65 pipeline for ACA/private clients. Keep it
  // editable so an agent can override (immigrant clients, etc.).
  const setDob = (v) => setC(prev => ({ ...prev, dob: v, turn65_date: prev.turn65_date || deriveTurn65(v) }));

  // MBI show/hide toggle — same UX pattern as SSN. Default masked.
  const [showMbi, setShowMbi] = React.useState(false);

  const isMedicare = c.sector === 'medicare';

  // Bridge flag: aca_private client within 180 days of turning 65.
  const turn65Bridge = (() => {
    if (isMedicare || !c.turn65_date) return null;
    const d = new Date(c.turn65_date + 'T00:00:00');
    if (Number.isNaN(d.getTime())) return null;
    const days = Math.ceil((d.getTime() - Date.now()) / 86400000);
    if (days <= 180 && days >= -30) return { days };
    return null;
  })();

  // Normalize phone preview as the user types so they see what's stored.
  const phonePreview = normalizeClientPhone(c.phone);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between sticky top-0 z-10">
          <div className="font-semibold text-slate-900">
            {c.__new ? '+ Add Client' : `Edit Client · ${c.name || ''}`}
          </div>
          <button aria-label="Close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Contact */}
          <section>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Contact</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <label className="block">
                <span className="text-xs text-slate-500">Name *</span>
                <input value={c.name || ''} onChange={e => set('name', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
              </label>
              <label className="block">
                <span className="text-xs text-slate-500">Email</span>
                <input type="email" value={c.email || ''} onChange={e => set('email', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
              </label>
              <label className="block">
                <span className="text-xs text-slate-500">Phone</span>
                <input value={c.phone || ''} onChange={e => set('phone', e.target.value)} placeholder="(512) 555-1234" className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
                {c.phone && (
                  <span className={`text-[10px] block mt-0.5 ${phonePreview ? 'text-slate-500' : 'text-red-600'}`}>
                    {phonePreview ? `Saved as ${phonePreview}` : 'Invalid format — agent cannot text this client'}
                  </span>
                )}
              </label>
              <label className="block">
                <span className="text-xs text-slate-500">Time zone</span>
                <select value={c.timezone || 'America/Chicago'} onChange={e => set('timezone', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 bg-white">
                  {CLIENT_TZ_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </label>
            </div>
          </section>

          {/* Vertical — what business book this client belongs in.
              Life-vertical agents and health-vertical agents keep
              their books cleanly separated in the Health Portal. */}
          <section>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Business Vertical</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              {[
                { id: 'life',   label: 'Life',   sub: 'FEX · Term · IUL · MP' },
                { id: 'health', label: 'Health', sub: 'ACA · Medicare · Supplemental' },
              ].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => set('vertical', opt.id)}
                  className={`border-2 p-2 text-left rounded ${c.vertical === opt.id
                    ? (opt.id === 'health' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-800 bg-slate-100')
                    : 'border-slate-200 hover:border-slate-300'}`}
                >
                  <div className="font-black text-sm">{opt.label}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{opt.sub}</div>
                </button>
              ))}
            </div>
          </section>

          {/* Sector — the v2 spine. Switches everything downstream:
              fields, pipeline, compliance gate, calendar windows.
              Only meaningful inside the health vertical. */}
          {c.vertical === 'health' && (
          <section>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Sector</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              {[
                { id: 'aca_private', label: 'ACA / Private', sub: 'Under-65 · year-round + SEPs' },
                { id: 'medicare',    label: 'Medicare',      sub: '65+ · AEP/OEP · SOA required' },
              ].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => set('sector', opt.id)}
                  className={`border-2 p-3 text-left rounded ${c.sector === opt.id
                    ? (opt.id === 'medicare' ? 'border-blue-500 bg-blue-50' : 'border-emerald-500 bg-emerald-50')
                    : 'border-slate-200 hover:border-slate-300'}`}
                >
                  <div className="font-black text-sm">{opt.label}</div>
                  <div className="text-[0.6rem] uppercase tracking-wider text-slate-500 mt-0.5">{opt.sub}</div>
                </button>
              ))}
            </div>
            {turn65Bridge && (
              <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-300 px-2 py-1.5 rounded">
                ⚠ Turning 65 in {turn65Bridge.days} days — flag this client for the Medicare bridge pipeline.
              </div>
            )}
          </section>
          )}

          {/* Demographics + compliance flags — both sectors. */}
          <section>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Demographics &amp; Compliance</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <label className="block">
                <span className="text-xs text-slate-500">DOB</span>
                <input type="date" value={c.dob || ''} onChange={e => setDob(e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
              </label>
              <label className="block">
                <span className="text-xs text-slate-500">State</span>
                <input value={c.state_abbr || ''} maxLength={2} onChange={e => set('state_abbr', e.target.value.toUpperCase())} placeholder="TX" className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 uppercase font-mono"/>
              </label>
              <label className="block">
                <span className="text-xs text-slate-500">Sex</span>
                <select value={c.sex || ''} onChange={e => set('sex', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 bg-white">
                  <option value="">—</option><option value="M">M</option><option value="F">F</option><option value="X">X</option>
                </select>
              </label>
              <label className="block">
                <span className="text-xs text-slate-500">Height (in)</span>
                <input type="number" value={c.height_in || ''} onChange={e => set('height_in', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
              </label>
              <label className="block">
                <span className="text-xs text-slate-500">Weight (lb)</span>
                <input type="number" value={c.weight_lb || ''} onChange={e => set('weight_lb', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
              </label>
              <label className="flex items-start gap-2 text-xs md:col-span-1.5 col-span-2">
                <input type="checkbox" className="mt-0.5" checked={!!c.opt_in_sms} onChange={e => set('opt_in_sms', e.target.checked)}/>
                <span>SMS opt-in (required before any text)</span>
              </label>
              <label className="flex items-start gap-2 text-xs col-span-2">
                <input type="checkbox" className="mt-0.5" checked={!!c.permission_to_contact} onChange={e => set('permission_to_contact', e.target.checked)}/>
                <span>Permission to Contact (PTC) — REQUIRED for Medicare outbound. No unsolicited Medicare SMS, full stop.</span>
              </label>
            </div>
          </section>

          {/* Medicare-only fields — only render when sector === medicare. */}
          {isMedicare && (
            <section className="border-2 border-blue-200 bg-blue-50/40 rounded p-3">
              <div className="text-xs font-black uppercase tracking-wider text-blue-700 mb-2">Medicare</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <label className="block md:col-span-2">
                  <span className="text-xs text-slate-500 flex items-center gap-2">
                    MBI (Medicare Beneficiary ID)
                    <button type="button" onClick={() => setShowMbi(s => !s)} className="text-[0.55rem] font-bold uppercase tracking-wider text-blue-700 hover:text-blue-900">
                      {showMbi ? 'Hide' : 'Show'}
                    </button>
                  </span>
                  {showMbi ? (
                    <input value={c.mbi || ''} onChange={e => set('mbi', e.target.value.toUpperCase())} placeholder="1EG4-TE5-MK73" className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 font-mono"/>
                  ) : (
                    <div className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 bg-white font-mono text-slate-600">{maskMbi(c.mbi) || <span className="text-slate-300">— not on file —</span>}</div>
                  )}
                  <div className="text-[0.55rem] uppercase tracking-wider text-slate-400 mt-0.5">Treat like SSN. Never logged in plaintext exports.</div>
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">Part A effective</span>
                  <input type="date" value={c.part_a_effective || ''} onChange={e => set('part_a_effective', e.target.value)} className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"/>
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">Part B effective</span>
                  <input type="date" value={c.part_b_effective || ''} onChange={e => set('part_b_effective', e.target.value)} className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"/>
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">Turn 65 date</span>
                  <input type="date" value={c.turn65_date || ''} onChange={e => set('turn65_date', e.target.value)} className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"/>
                  <div className="text-[0.55rem] uppercase tracking-wider text-slate-400 mt-0.5">Auto-derived from DOB; editable.</div>
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">LIS / Dual status</span>
                  <select value={c.lis_dual_status || ''} onChange={e => set('lis_dual_status', e.target.value)} className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 bg-white">
                    <option value="">— none / unknown —</option>
                    <option value="LIS_full">LIS — Full subsidy</option>
                    <option value="LIS_partial">LIS — Partial subsidy</option>
                    <option value="FBDE">FBDE — Full Benefit Dual Eligible</option>
                    <option value="QMB">QMB — Qualified Medicare Beneficiary</option>
                    <option value="SLMB">SLMB</option>
                    <option value="QI">QI</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">Current plan type</span>
                  <select value={c.current_plan_type || ''} onChange={e => set('current_plan_type', e.target.value)} className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 bg-white">
                    <option value="">—</option>
                    <option value="MA">MA</option><option value="MAPD">MAPD</option><option value="PDP">PDP</option><option value="Med Supp">Med Supp</option><option value="Original Medicare">Original Medicare only</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">Current carrier</span>
                  <input value={c.current_carrier || ''} onChange={e => set('current_carrier', e.target.value)} placeholder="UHC / Humana / Aetna…" className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"/>
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">Current plan ID</span>
                  <input value={c.current_plan_id || ''} onChange={e => set('current_plan_id', e.target.value)} placeholder="H1234-001-0" className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 font-mono"/>
                </label>
                <label className="block md:col-span-2">
                  <span className="text-xs text-slate-500">Chronic conditions (comma-separated)</span>
                  <input
                    value={Array.isArray(c.chronic_conditions) ? c.chronic_conditions.join(', ') : (c.chronic_conditions || '')}
                    onChange={e => set('chronic_conditions', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                    placeholder="CHF, COPD, Diabetes Type 2…"
                    className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"/>
                  <div className="text-[0.55rem] uppercase tracking-wider text-slate-400 mt-0.5">Drives C-SNP / D-SNP eligibility checks.</div>
                </label>
              </div>
              <div className="mt-3 text-[0.6rem] text-blue-900 bg-blue-100/60 border border-blue-200 rounded px-2 py-1.5 uppercase tracking-wider font-bold">
                ⚠ Medicare enrollment requires: SOA signed ≥48h before appt · recorded call · current AHIP + carrier certs. Indemnity ≠ MEC.
              </div>
            </section>
          )}

          {/* Policy */}
          <section>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Policy</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <label className="block">
                <span className="text-xs text-slate-500">Policy #</span>
                <input value={c.policy || ''} onChange={e => set('policy', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
              </label>
              <label className="block">
                <span className="text-xs text-slate-500">Carrier</span>
                <input value={c.carrier || ''} onChange={e => set('carrier', e.target.value)} placeholder="e.g. Mutual of Omaha" className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
              </label>
              <label className="block">
                <span className="text-xs text-slate-500">Writing agent #</span>
                <input value={c.prod_num || ''} onChange={e => set('prod_num', e.target.value)} placeholder="Producer code" className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
              </label>
            </div>
          </section>

          {/* Billing */}
          <section>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Billing</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <label className="block">
                <span className="text-xs text-slate-500">Close date</span>
                <input type="date" value={c.close_date || ''} onChange={e => set('close_date', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
              </label>
              <label className="block">
                <span className="text-xs text-slate-500">Issue date</span>
                <input type="date" value={c.issue_date || ''} onChange={e => set('issue_date', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
              </label>
              <label className="block">
                <span className="text-xs text-slate-500">First draft date</span>
                <input type="date" value={c.first_draft_date || ''} onChange={e => set('first_draft_date', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"/>
              </label>
              <label className="block">
                <span className="text-xs text-slate-500">Draft amount ($)</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={c.draft_amount ?? ''}
                  onChange={e => set('draft_amount', sanitizeMoneyInput(e.target.value))}
                  placeholder="0.00"
                  className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"
                />
              </label>
              <label className="block">
                <span className="text-xs text-slate-500">Billing mode</span>
                <select value={c.billing_mode || 'monthly'} onChange={e => set('billing_mode', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 bg-white">
                  {BILLING_MODE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs text-slate-500">Billing status</span>
                <select value={c.billing_status || 'active'} onChange={e => set('billing_status', e.target.value)} className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 bg-white">
                  {BILLING_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
            </div>
            {/* Recurring billing schedule — drives the draft date reminder
                workflow (migration 043). Two modes: fixed calendar day
                (1-31), or "Nth weekday of the month" for week-based
                billing (common with Social Security recipients + union
                payrolls). Preview line at the bottom describes the
                schedule in plain English. */}
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Recurring draft schedule</div>
              <div className="flex items-center gap-4 flex-wrap mb-3">
                <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="billing_schedule_mode"
                    checked={(c.billing_schedule_mode || 'fixed_date') === 'fixed_date'}
                    onChange={() => set('billing_schedule_mode', 'fixed_date')}
                  />
                  <span>Fixed calendar day (1-31)</span>
                </label>
                <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="billing_schedule_mode"
                    checked={c.billing_schedule_mode === 'week_based'}
                    onChange={() => set('billing_schedule_mode', 'week_based')}
                  />
                  <span>Week-based (e.g. 2nd Tuesday)</span>
                </label>
              </div>
              {(c.billing_schedule_mode || 'fixed_date') === 'fixed_date' ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <label className="block">
                    <span className="text-xs text-slate-500">Day of month</span>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={c.billing_day_of_month ?? ''}
                      onChange={e => set('billing_day_of_month', e.target.value === '' ? null : Number(e.target.value))}
                      placeholder="e.g. 15"
                      className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"
                    />
                    <div className="text-[11px] text-slate-500 mt-1">If &gt; days in month (e.g. 31 in Feb), the carrier draws on the last day.</div>
                  </label>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <label className="block">
                    <span className="text-xs text-slate-500">Week of month</span>
                    <select
                      value={c.billing_week_of_month ?? ''}
                      onChange={e => set('billing_week_of_month', e.target.value === '' ? null : Number(e.target.value))}
                      className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 bg-white"
                    >
                      <option value="">— select —</option>
                      <option value="1">1st</option>
                      <option value="2">2nd</option>
                      <option value="3">3rd</option>
                      <option value="4">4th</option>
                      <option value="5">5th (last)</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-xs text-slate-500">Day of week</span>
                    <select
                      value={c.billing_day_of_week ?? ''}
                      onChange={e => set('billing_day_of_week', e.target.value === '' ? null : Number(e.target.value))}
                      className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 bg-white"
                    >
                      <option value="">— select —</option>
                      <option value="1">Monday</option>
                      <option value="2">Tuesday</option>
                      <option value="3">Wednesday</option>
                      <option value="4">Thursday</option>
                      <option value="5">Friday</option>
                      <option value="6">Saturday</option>
                      <option value="0">Sunday</option>
                    </select>
                  </label>
                  <div className="md:col-span-3 text-[11px] text-slate-500">
                    5th-occurrence handling: if a given month doesn't have a 5th weekday (e.g. only four Wednesdays), the carrier draws on the 4th — most billing systems treat "5th" as "last."
                  </div>
                </div>
              )}
              {/* Plain-English preview — uses the same describeSchedule
                  helper the cron uses, kept in sync via api/_billingSchedule.js. */}
              <div className="mt-3 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
                <span className="font-bold uppercase tracking-wider text-[10px] mr-2">Schedule</span>
                {(() => {
                  const mode = c.billing_schedule_mode || 'fixed_date';
                  if (mode === 'fixed_date') {
                    const d = Number(c.billing_day_of_month);
                    if (!Number.isFinite(d) || d < 1 || d > 31) return '(set a day of the month to enable reminders)';
                    const ord = (n) => { const s=['th','st','nd','rd'], v=n%100; return n+(s[(v-20)%10]||s[v]||s[0]); };
                    return `Drafts on the ${ord(d)} of each month.`;
                  }
                  const wom = Number(c.billing_week_of_month);
                  const dow = Number(c.billing_day_of_week);
                  if (!Number.isFinite(wom) || wom < 1 || wom > 5) return '(pick a week + weekday)';
                  if (!Number.isFinite(dow) || dow < 0 || dow > 6) return '(pick a week + weekday)';
                  const WOM = ['1st','2nd','3rd','4th','5th'];
                  const DOW = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
                  return `Drafts on the ${WOM[wom-1]} ${DOW[dow]} of each month.`;
                })()}
              </div>
            </div>
          </section>

          {/* Verbal verification — the security code and security question
              the agent set up with the client at close. Used to verify
              the client's identity on any future inbound or outbound call. */}
          <section>
            <div className="flex items-baseline justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Verbal Verification</div>
              <div className="text-[10px] text-slate-400">For phone-call ID confirmation</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <label className="block">
                <span className="text-xs text-slate-500">Security code</span>
                <input
                  value={c.security_code || ''}
                  onChange={e => set('security_code', e.target.value)}
                  placeholder={'e.g. "BLUE OCEAN"'}
                  maxLength={120}
                  className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 font-mono tracking-wide"
                />
                <span className="text-[10px] block text-slate-400 mt-0.5">
                  The verbal password you gave the client. Ask for this before discussing the policy.
                </span>
              </label>
              <label className="block">
                <span className="text-xs text-slate-500">Security question</span>
                <input
                  value={c.security_question || ''}
                  onChange={e => set('security_question', e.target.value)}
                  placeholder={'e.g. "What was your first car?" — \'67 Chevy'}
                  maxLength={240}
                  className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5"
                />
                <span className="text-[10px] block text-slate-400 mt-0.5">
                  Question + answer the client agreed to remember. Free-form — include both Q and A here.
                </span>
              </label>
            </div>
          </section>

          {/* Lifecycle + Consent */}
          <section>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Lifecycle &amp; Consent</div>
            <div className="space-y-2">
              <label className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!c.consent?.care_opted_in}
                  onChange={e => setConsent('care_opted_in', e.target.checked)}
                  className="mt-0.5"
                />
                <div>
                  <div className="text-sm font-semibold text-slate-900">Care messages opted in</div>
                  <div className="text-xs text-slate-500">Welcome, first-draft reminder, 30-day check-in, NSF recovery. Required for any transactional touch.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!c.consent?.marketing_ok}
                  onChange={e => setConsent('marketing_ok', e.target.checked)}
                  className="mt-0.5"
                />
                <div>
                  <div className="text-sm font-semibold text-slate-900">Marketing messages opted in</div>
                  <div className="text-xs text-slate-500">Required for referral asks (month 3 / 6 / 9 / anniversary). Capture this affirmatively at close.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!c.lifecycle_paused}
                  onChange={e => set('lifecycle_paused', e.target.checked)}
                  className="mt-0.5"
                />
                <div>
                  <div className="text-sm font-semibold text-slate-900">Pause lifecycle sends</div>
                  <div className="text-xs text-slate-500">Suspends every scheduled touch for this client without revoking consent. Use during a complaint, post-loss period, or extended vacation.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!c.auto_delivery_opted_out}
                  onChange={e => set('auto_delivery_opted_out', e.target.checked)}
                  className="mt-0.5"
                />
                <div>
                  <div className="text-sm font-semibold text-slate-900">Skip AI auto-delivery</div>
                  <div className="text-xs text-slate-500">When checked, the AI won't auto-email this client their Policy Delivery Summary even if global auto-send is on. Use for in-person-only delivery, sensitive policies, pending disputes, or high-touch clients.</div>
                </div>
              </label>
            </div>
          </section>

          {/* Outstanding requirements checklist — AI auto-reminds the
              client every 3 days and creates a follow-up task for the
              agent if anything goes 7+ days unresolved. Only shows
              when the client has been saved (needs c.id for FK). */}
          {!c.__new && c.id && (
            <section className="border-t border-slate-200 pt-4">
              <ClientRequirementsSection
                clientId={c.id}
                ownerId={c.owner_id}
                showToast={(msg) => { /* surface via the parent if available */ try { window?.dispatchEvent?.(new CustomEvent('ct:toast', { detail: { msg } })); } catch {} }}
              />
            </section>
          )}

          {/* Forms & Documents — every compliance form tied to this
              client (SOA, ACA Consent, PTC, future forms) plus a
              one-click Audit Packet download. Hidden on a brand-new
              client because the audit endpoints need a saved client_id. */}
          {!c.__new && c.id && (
            <section>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center justify-between">
                <span>Forms &amp; Documents</span>
                <a
                  href={`/api/audit/packet?client_id=${encodeURIComponent(c.id)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-[10px] font-bold uppercase tracking-wider text-sky-700 hover:text-sky-900"
                  title="Open the full audit packet — every event, every signature, every server-captured timestamp"
                >
                  ⬇ Download audit packet (JSON)
                </a>
              </div>
              <ClientFormsPanel clientId={c.id} clientName={c.name}/>
            </section>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between sticky bottom-0">
          {!c.__new && (
            <button onClick={onDelete} className="text-xs text-red-600 hover:text-red-700 underline">Delete</button>
          )}
          <div className="flex items-center gap-2 ml-auto">
            <button aria-label="Close" onClick={onClose} className="text-sm text-slate-600 hover:text-slate-800 font-semibold px-3 py-1.5">Cancel</button>
            <button
              onClick={() => onSave(c)}
              disabled={!c.name?.trim()}
              className="bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg px-4 py-1.5"
            >
              {c.__new ? 'Add Client' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// CONVERSATIONS — threaded message_log per client.
//
// Source of truth = public.message_log. Every inbound + outbound text
// the lifecycle engine has touched, plus manual replies the operator
// types in this tab.
//
// Filters:
//   • All           — every conversation, most recent first
//   • Needs human   — only conversations with an unhandled needs_human
//                     message (escalations from the inbound AI)
//   • Unread        — conversations with inbound messages newer than
//                     the operator's last view
// ════════════════════════════════════════════════════════════════════
function ConversationsView({ currentUser, showToast = () => {} }) {
  const [clients, setClients] = React.useState([]);
  const [messages, setMessages] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState('all'); // all | needs_human
  const [activeClientId, setActiveClientId] = React.useState(null);
  const [replyDraft, setReplyDraft] = React.useState('');
  const [sending, setSending] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    const [c, m] = await Promise.all([
      supabase.from('clients').select('id, name, phone, billing_status').order('name'),
      supabase.from('message_log').select('id, client_id, direction, body, handled_by, needs_human, created_at, twilio_sid')
        .order('created_at', { ascending: false })
        .limit(500),
    ]);
    setClients(c.data || []);
    setMessages(m.data || []);
    setLoading(false);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  // Aggregate per-client message stats.
  const perClient = React.useMemo(() => {
    const map = {};
    for (const msg of messages) {
      if (!msg.client_id) continue;
      if (!map[msg.client_id]) map[msg.client_id] = { last: null, lastBody: '', needsHuman: 0, total: 0, lastDirection: 'in' };
      const e = map[msg.client_id];
      e.total++;
      if (!e.last || new Date(msg.created_at) > new Date(e.last)) {
        e.last = msg.created_at;
        e.lastBody = msg.body;
        e.lastDirection = msg.direction;
      }
      if (msg.needs_human) e.needsHuman++;
    }
    return map;
  }, [messages]);

  const visibleClients = React.useMemo(() => {
    const enriched = clients
      .map(c => ({ ...c, stats: perClient[c.id] || null }))
      .filter(c => c.stats);
    const filtered = filter === 'needs_human'
      ? enriched.filter(c => c.stats.needsHuman > 0)
      : enriched;
    return filtered.sort((a, b) => new Date(b.stats.last) - new Date(a.stats.last));
  }, [clients, perClient, filter]);

  const activeClient = clients.find(c => c.id === activeClientId) || null;
  const thread = React.useMemo(() =>
    messages
      .filter(m => m.client_id === activeClientId)
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at)),
    [messages, activeClientId]
  );

  async function sendReply() {
    if (!activeClient || !replyDraft.trim()) return;
    setSending(true);
    try {
      const body = replyDraft.trim();
      // Persist immediately (optimistic). Twilio send is gated by
      // SMS_SEND_ENABLED + the api/sms/send-manual route which already
      // exists — we hit it if we want the wire delivery.
      const { data: inserted, error } = await supabase.from('message_log').insert({
        owner_id: currentUser?.id,
        client_id: activeClient.id,
        direction: 'out',
        body,
        to_number: activeClient.phone,
        handled_by: 'human',
      }).select().single();
      if (error) {
        showToast(`Couldn't save: ${error.message}`);
        return;
      }
      // Attempt actual SMS send if the manual-send endpoint exists.
      // The endpoint is no-op in dry-run mode so this never accidentally
      // ships unconsented texts.
      try {
        const { data: sess } = await supabase.auth.getSession();
        if (sess?.session?.access_token) {
          await fetch('/api/sms/send-manual', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sess.session.access_token}` },
            body: JSON.stringify({ leadId: activeClient.id, body }),
          }).catch(() => {});
        }
      } catch { /* dry-run mode is fine */ }
      setMessages(prev => [inserted, ...prev]);
      setReplyDraft('');
      showToast('Reply sent');
    } finally {
      setSending(false);
    }
  }

  async function markHandled(msg) {
    const { error } = await supabase.from('message_log').update({ needs_human: false }).eq('id', msg.id);
    if (error) { showToast(`Couldn't mark: ${error.message}`); return; }
    setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, needs_human: false } : m));
  }

  const fmtTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-black border-l-4 border-emerald-500 px-6 py-5 shadow-2xl">
        <div className="text-emerald-400 text-[0.6rem] font-black uppercase tracking-[0.3em]">Conversations</div>
        <div className="text-white text-xl md:text-2xl font-black mt-1.5 uppercase tracking-tight">Client Threads — All Activity</div>
        <div className="text-slate-400 text-xs mt-1.5">Every outbound lifecycle text + every inbound reply. Filter to "Needs human" for AI escalations.</div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setFilter('all')}
          className={`text-[0.6rem] font-black uppercase tracking-tight px-3 py-2 border-2 ${filter === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
        >
          ALL · {Object.keys(perClient).length}
        </button>
        <button
          onClick={() => setFilter('needs_human')}
          title="Inbound replies the AI escalated to you — usually because it couldn't confidently identify the intent, the client asked something compliance-sensitive, or the client signaled they want a real person. These do NOT auto-resolve; reply or mark them handled."
          className={`text-[0.6rem] font-black uppercase tracking-tight px-3 py-2 border-2 ${filter === 'needs_human' ? 'bg-red-600 text-white border-red-700' : 'bg-white text-red-700 border-red-200 hover:border-red-400'}`}
        >
          ⚠ NEEDS HUMAN · {Object.values(perClient).filter(p => p.needsHuman > 0).length}
        </button>
      </div>
      {filter === 'needs_human' && (
        <div className="bg-rose-50 border border-rose-200 rounded-md px-3 py-2 text-xs text-rose-900 leading-relaxed">
          <strong>What this is:</strong> conversations where the inbound AI handed off to you. Triggers include unclear intent, a compliance-sensitive ask (cancel, rate, claim), or the client asking for a real person. <strong>What to do:</strong> reply directly from the thread on the right — sending a reply clears the needs-human flag automatically.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" style={{ minHeight: '60vh' }}>
        {/* Left: client list */}
        <div className="md:col-span-1 bg-white border border-slate-200 overflow-auto" style={{ maxHeight: '65vh' }}>
          {loading ? (
            <div className="p-6 text-center text-sm text-slate-500">Loading…</div>
          ) : visibleClients.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">
              {filter === 'needs_human' ? 'No escalations ' : 'No conversations yet. Lifecycle texts will populate here once they send.'}
            </div>
          ) : (
            visibleClients.map(c => (
              <button
                key={c.id}
                onClick={() => setActiveClientId(c.id)}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${activeClientId === c.id ? 'bg-emerald-50 border-l-4 border-l-emerald-500' : ''}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-bold text-slate-900 text-sm truncate">{c.name}</div>
                  <div className="text-[0.55rem] uppercase tracking-wider text-slate-400 flex-shrink-0">{fmtTime(c.stats.last)}</div>
                </div>
                <div className="text-xs text-slate-600 mt-0.5 truncate">
                  {c.stats.lastDirection === 'in' ? '↩ ' : '→ '}{c.stats.lastBody}
                </div>
                {c.stats.needsHuman > 0 && (
                  <div className="text-[0.55rem] font-black uppercase tracking-[0.15em] text-red-700 mt-1">⚠ {c.stats.needsHuman} ESCALATION{c.stats.needsHuman > 1 ? 'S' : ''}</div>
                )}
              </button>
            ))
          )}
        </div>

        {/* Right: thread */}
        <div className="md:col-span-2 bg-white border border-slate-200 flex flex-col" style={{ maxHeight: '65vh' }}>
          {!activeClient ? (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-400 p-6">
              Pick a client to see the thread.
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
                <div className="font-bold text-slate-900">{activeClient.name}</div>
                <div className="text-xs text-slate-500">{activeClient.phone || 'no phone'} · {activeClient.billing_status || 'active'}</div>
              </div>
              <div className="flex-1 overflow-auto p-4 space-y-2">
                {thread.length === 0 ? (
                  <div className="text-center text-sm text-slate-400 mt-8">No messages yet.</div>
                ) : (
                  thread.map(m => (
                    <div key={m.id} className={`flex ${m.direction === 'out' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] px-3 py-2 ${m.direction === 'out' ? 'bg-emerald-600 text-white' : m.needs_human ? 'bg-red-50 border border-red-300 text-slate-900' : 'bg-slate-100 text-slate-900'}`}>
                        <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                        <div className={`text-[0.5rem] mt-1 uppercase tracking-wider ${m.direction === 'out' ? 'text-emerald-100' : 'text-slate-500'}`}>
                          {fmtTime(m.created_at)}
                          {m.handled_by && ` · ${m.handled_by}`}
                          {m.needs_human && (
                            <button
                              onClick={() => markHandled(m)}
                              className="ml-2 text-[0.5rem] underline font-bold text-red-700"
                            >
                              MARK HANDLED
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="p-3 border-t border-slate-200 flex gap-2">
                <input
                  value={replyDraft}
                  onChange={e => setReplyDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                  placeholder={activeClient.phone ? 'Type a reply…' : 'No phone on file'}
                  disabled={!activeClient.phone || sending}
                  className="flex-1 border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                />
                <button
                  onClick={sendReply}
                  disabled={!activeClient.phone || !replyDraft.trim() || sending}
                  className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-black uppercase tracking-tight px-4"
                >
                  {sending ? 'Sending…' : 'Send'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// REFERRALS — pipeline with payout status
// ════════════════════════════════════════════════════════════════════
function ReferralsView({ currentUser, settings = {}, showToast = () => {}, onGotoTab = () => {} }) {
  const [referrals, setReferrals] = React.useState([]);
  const [clients, setClients] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [adding, setAdding] = React.useState(false);
  const [draft, setDraft] = React.useState({ referred_name: '', referred_phone: '', referrer_client_id: '', notes: '' });
  const [profileRef, setProfileRef] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    const [r, c] = await Promise.all([
      supabase.from('referrals').select('*').order('created_at', { ascending: false }),
      supabase.from('clients').select('id, name').order('name'),
    ]);
    setReferrals(r.data || []);
    setClients(c.data || []);
    setLoading(false);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const COLUMNS = [
    { key: 'new',       label: 'New',       color: 'sky' },
    { key: 'contacted', label: 'Contacted', color: 'amber' },
    { key: 'closed',    label: 'Closed',    color: 'emerald' },
    { key: 'dead',      label: 'Dead',      color: 'slate' },
  ];

  async function moveStatus(ref, status) {
    const { error } = await supabase.from('referrals').update({ status }).eq('id', ref.id);
    if (error) { showToast(`Move failed: ${error.message}`); return; }
    setReferrals(prev => prev.map(r => r.id === ref.id ? { ...r, status } : r));
    // Moving to "closed" → offer to spin up a client profile. They
    // closed; this is now an active customer who needs to land in the
    // Clients tab + the lifecycle engine.
    if (status === 'closed') {
      const ok = await hqConfirm({
        title: `Mark ${ref.referred_name} as closed and create a client profile?`,
        body: `They'll land in your Clients tab with their phone${ref.notes && /Email:/i.test(ref.notes) ? ' + email' : ''} pre-filled. You can complete the profile and schedule the first appointment from there.`,
        confirmLabel: 'Close and convert',
      });
      if (ok) await convertToClient({ ...ref, status });
    }
  }
  async function setPayoutStatus(ref, payout_status) {
    const { error } = await supabase.from('referrals').update({ payout_status }).eq('id', ref.id);
    if (error) { showToast(`Update failed: ${error.message}`); return; }
    setReferrals(prev => prev.map(r => r.id === ref.id ? { ...r, payout_status } : r));
  }

  // Convert a referral into a client row. Parses the email out of the
  // notes field (the intake endpoint composes it as "Email: foo@bar")
  // and inserts into public.clients with vertical=life so the new
  // client lands in the operator's Clients tab. Idempotent — if a
  // client with the same name already exists for this owner, we just
  // navigate there instead of duplicating.
  async function convertToClient(ref) {
    if (!currentUser?.id) return;
    if (ref.converted_at) {
      showToast(`${ref.referred_name} was already converted — opening Clients.`);
      onGotoTab('clients');
      return;
    }
    const emailMatch = String(ref.notes || '').match(/Email:\s*([^\s\n]+)/i);
    const email = emailMatch ? emailMatch[1] : null;

    // Check for an existing client with the same name (cheap dedup).
    const { data: existing } = await supabase
      .from('clients')
      .select('id')
      .eq('owner_id', currentUser.id)
      .ilike('name', ref.referred_name)
      .maybeSingle();
    if (existing) {
      showToast(`${ref.referred_name} is already in your Clients — opening the tab.`);
      // Stamp conversion on the referral even if the client already
      // existed, so the kanban can grey this row out next refresh.
      await markReferralConverted(ref.id, existing.id);
      onGotoTab('clients');
      return;
    }

    // No `notes` column on public.clients — the schema only carries
    // contact + policy + billing fields. We keep the original referral
    // notes in the referrals row (they stay viewable in the kanban
    // card) and drop them from the client insert.
    const { data: inserted, error } = await insertClientSafe({
      owner_id:  currentUser.id,
      name:      ref.referred_name,
      phone:     ref.referred_phone || null,
      email:     email,
      vertical:  'life',
      timezone:  'America/Chicago',
      billing_status: 'active',
    }, 'id');
    if (error) {
      showToast(`Convert failed: ${error.message}`, 'error');
      return;
    }
    await markReferralConverted(ref.id, inserted?.id);
    showToast(`${ref.referred_name} added to your Clients — complete their profile next.`);
    onGotoTab('clients');
  }

  // Best-effort stamp converted_at + client_id on the referral row.
  // The columns are added by migration 022b_referrals_conversion_tracking.sql;
  // until that runs, the update returns an "unknown column" error and
  // we silently skip (the conversion itself already succeeded). After
  // the migration runs, every conversion stamps cleanly and the kanban
  // greys converted rows.
  async function markReferralConverted(refId, clientId) {
    try {
      const { error } = await supabase
        .from('referrals')
        .update({ converted_at: new Date().toISOString(), client_id: clientId || null })
        .eq('id', refId);
      if (error) {
        // eslint-disable-next-line no-console
        console.warn('[referrals] converted_at stamp skipped:', error.message);
        return;
      }
      setReferrals(prev => prev.map(r => r.id === refId
        ? { ...r, converted_at: new Date().toISOString(), client_id: clientId || null }
        : r));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[referrals] converted_at stamp threw:', err.message || err);
    }
  }

  // Book an appointment from a referral row. Inserts into
  // appointment_reminders (the same table the AI booker writes to —
  // commit d52818a) and fires the Google Calendar sync best-effort so
  // the event lands in the operator's calendar within seconds.
  const [schedulingRef, setSchedulingRef] = React.useState(null);
  const [apptDateTime, setApptDateTime] = React.useState('');
  async function scheduleAppointment(ref) {
    if (!currentUser?.id || !apptDateTime) return;
    const parsed = new Date(apptDateTime);
    if (Number.isNaN(parsed.getTime())) {
      showToast('Pick a valid date and time first.', 'error');
      return;
    }
    const id = `appt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    const { error: insErr } = await supabase.from('appointment_reminders').insert({
      id,
      owner_id:             currentUser.id,
      appointment_at:       parsed.toISOString(),
      title:                'Referral call',
      client_name:          ref.referred_name,
      client_phone:         ref.referred_phone || '',
      notes:                `[Referral] ${ref.notes || ''}`.trim(),
      status:               'scheduled',
      reminder_24h_enabled: true,
      reminder_1h_enabled:  true,
    });
    if (insErr) {
      showToast(`Schedule failed: ${insErr.message}`, 'error');
      return;
    }
    // Best-effort calendar sync — never blocks the UX on it.
    try {
      await apiFetch('/api/google-calendar/upsert-event', {
        method: 'POST',
        body:   JSON.stringify({ appointment_id: id }),
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[referrals] calendar sync failed:', err.message || err);
    }
    showToast(`Booked: ${ref.referred_name} on ${parsed.toLocaleString()}.`);
    setSchedulingRef(null);
    setApptDateTime('');
  }
  async function addReferral() {
    const name = draft.referred_name.trim();
    if (!name) { showToast('Name required.'); return; }
    const { data, error } = await supabase.from('referrals').insert({
      owner_id: currentUser?.id,
      referrer_client_id: draft.referrer_client_id || null,
      referred_name: name,
      referred_phone: draft.referred_phone.trim() || null,
      notes: draft.notes.trim() || null,
      status: 'new',
      payout_status: 'owed',
    }).select().single();
    if (error) { showToast(`Add failed: ${error.message}`); return; }
    setReferrals(prev => [data, ...prev]);
    setDraft({ referred_name: '', referred_phone: '', referrer_client_id: '', notes: '' });
    setAdding(false);
    showToast('Referral added');
  }
  async function deleteReferral(ref) {
    if (!(await hqConfirm({
      title: `Delete referral for ${ref.referred_name}?`,
      confirmLabel: 'Delete referral', danger: true,
    }))) return;
    const { error } = await supabase.from('referrals').delete().eq('id', ref.id);
    if (error) { showToast(`Delete failed: ${error.message}`); return; }
    setReferrals(prev => prev.filter(r => r.id !== ref.id));
  }

  const referrerName = (id) => clients.find(c => c.id === id)?.name || null;
  const owedCount = referrals.filter(r => r.payout_status === 'owed' && r.status === 'closed').length;

  // Public landing-page URL for this agent. Format is /p/<slug>
  // where slug is kebab-case of the operator's ownerName. Shared
  // with clients who forward it to friends/family; anyone who fills
  // the form lands in this pipeline as a new referral row via
  // /api/public/referral-intake. The marketing site owns the route
  // and resolves slug → owner_id; old /refer/<uuid> links 301 over.
  const referralLink = currentUser?.id
    ? referralLandingUrl({ ownerName: settings?.ownerName, fallbackId: currentUser.id })
    : '';
  const [copiedLink, setCopiedLink] = React.useState(false);
  const copyReferralLink = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopiedLink(true);
      showToast('Referral link copied.');
      setTimeout(() => setCopiedLink(false), 2000);
    } catch {
      showToast('Copy failed — long-press the link to copy manually.', 'error');
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-xl px-6 py-5 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-slate-500 text-[10px] font-semibold uppercase tracking-[0.25em]">Pipeline</div>
          <h1 className="text-slate-900 text-2xl font-semibold mt-1 tracking-tight">Referrals</h1>
          <div className="text-slate-500 text-sm mt-1">
            {owedCount > 0
              ? <span><span className="font-semibold text-slate-900">{owedCount}</span> closed referral{owedCount === 1 ? '' : 's'} awaiting payout</span>
              : 'Captured automatically when a client texts a name + number.'}
          </div>
        </div>
        <button
          onClick={() => setAdding(true)}
          className="bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium tracking-tight px-4 py-2 rounded-lg"
        >
          Add referral
        </button>
      </div>

      {/* Public referral link — share with clients; anyone who fills out
          the form on the other end becomes a new referral in this
          pipeline, auto-attributed to this agent. */}
      {referralLink && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <h3 className="text-sm font-semibold text-slate-900 tracking-tight">Your referral link</h3>
              <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                Share with existing clients. Anyone they forward it to lands here as a new referral — already attributed to you.
              </p>
            </div>
            <button
              onClick={() => {
                onGotoTab && onGotoTab('settings');
                showToast('Scroll to "Public referral profile" in Settings to edit your photo, title, and bio.');
              }}
              className="text-xs font-medium text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-300 rounded-md px-3 py-1.5"
              title="Add a photo, title, and bio that show on your referral page"
            >
              Edit your profile
            </button>
          </div>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <code className="flex-1 min-w-[260px] bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-xs font-mono text-slate-700 break-all">
              {referralLink}
            </code>
            <button
              onClick={copyReferralLink}
              className={`text-sm font-medium tracking-tight px-4 py-2 rounded-md transition-colors ${
                copiedLink
                  ? 'bg-emerald-600 text-white'
                  : 'bg-slate-900 hover:bg-slate-800 text-white'
              }`}
            >
              {copiedLink ? 'Copied' : 'Copy link'}
            </button>
          </div>
          <div className="mt-3 text-xs text-slate-500 leading-relaxed">
            Suggested SMS: <span className="italic">"Hey — if you know anyone who'd benefit from coverage, here's a quick form: {referralLink}. Takes 30 seconds and I'll handle the rest."</span>
          </div>
        </div>
      )}

      {/* Pipeline */}
      {loading ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-sm text-slate-500">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {COLUMNS.map(col => {
            const items = referrals.filter(r => r.status === col.key);
            return (
              <div key={col.key} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 flex items-baseline justify-between">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{col.label}</div>
                  <div className="text-sm font-semibold tabular-nums text-slate-900">{items.length}</div>
                </div>
                <div className="p-3 space-y-2 min-h-[200px]">
                  {items.length === 0 ? (
                    <div className="text-center text-xs text-slate-400 py-6">Empty</div>
                  ) : (
                    items.map(r => (
                      <div
                        key={r.id}
                        className={`border rounded-lg p-3 text-sm transition-colors ${
                          r.converted_at
                            ? 'bg-emerald-50/30 border-emerald-200/60 opacity-80'
                            : 'bg-white border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={() => setProfileRef(r)}
                            className={`text-left font-semibold text-slate-900 hover:text-indigo-600 transition-colors ${r.converted_at ? 'line-through decoration-1' : ''}`}
                            title="View full profile"
                          >
                            {r.referred_name}
                          </button>
                          {(() => {
                            const m = String(r.notes || '').match(/\[NICHE:\s*(life|health|medicare)\s*\]/i);
                            if (!m) return null;
                            const k = m[1].toLowerCase();
                            const label = k === 'life' ? 'Life / IUL' : k === 'health' ? 'Health / ACA' : 'Medicare';
                            const tone  = k === 'life' ? 'amber' : k === 'health' ? 'sky' : 'emerald';
                            return (
                              <span className={`text-[10px] font-medium uppercase tracking-wider text-${tone}-800 bg-${tone}-50 border border-${tone}-200 px-1.5 py-0.5 rounded`}>
                                {label}
                              </span>
                            );
                          })()}
                          {r.converted_at && (
                            <span className="text-[10px] font-medium uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                              Client
                            </span>
                          )}
                        </div>
                        {r.referred_phone && <div className="text-slate-500 text-xs mt-1 tabular-nums">{fmtPhoneUS(r.referred_phone)}</div>}
                        {referrerName(r.referrer_client_id) && (
                          <div className="text-slate-500 mt-1">via {referrerName(r.referrer_client_id)}</div>
                        )}
                        {/* Strip the [NICHE: …] line + the verbatim "Consent shown
                            to prospect at submission:" block from the operator-
                            visible notes — both are surfaced elsewhere (chip
                            above, audit trail below) and would otherwise wall-of-text
                            every card. */}
                        {(() => {
                          const clean = String(r.notes || '')
                            .replace(/\[NICHE:\s*(life|health|medicare)\s*\]\s*\n?/i, '')
                            .replace(/---\nConsent shown to prospect at submission:[\s\S]*$/m, '')
                            .trim();
                          return clean ? <div className="text-slate-500 mt-1 italic">{clean}</div> : null;
                        })()}
                        <div className="flex items-center justify-between mt-3 gap-1.5 flex-wrap">
                          <select
                            value={r.status}
                            onChange={e => moveStatus(r, e.target.value)}
                            className="text-[11px] font-medium border border-slate-200 rounded px-1.5 py-0.5 bg-white text-slate-700"
                          >
                            {COLUMNS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                          </select>
                          <select
                            value={r.payout_status}
                            onChange={e => setPayoutStatus(r, e.target.value)}
                            className={`text-[11px] font-medium border rounded px-1.5 py-0.5 ${r.payout_status === 'owed' ? 'bg-amber-50 border-amber-200 text-amber-800' : r.payout_status === 'paid' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-slate-50 border-slate-200 text-slate-600'}`}
                          >
                            <option value="owed">Owed</option>
                            <option value="paid">Paid</option>
                            <option value="na">N/A</option>
                          </select>
                          <button onClick={() => deleteReferral(r)} className="text-[11px] text-slate-400 hover:text-rose-600 ml-auto">Delete</button>
                        </div>
                        {/* Convert + Schedule actions. Two routes for moving
                            the referral forward — turn them into a client
                            so the lifecycle engine picks them up, OR book
                            the first call right from this card (also
                            creates a Google Calendar event if linked). */}
                        {schedulingRef === r.id ? (
                          <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                            <input
                              type="datetime-local"
                              value={apptDateTime}
                              onChange={e => setApptDateTime(e.target.value)}
                              className="text-xs border border-slate-200 rounded px-2 py-1 flex-1 min-w-[140px]"
                              autoFocus
                            />
                            <button
                              onClick={() => scheduleAppointment(r)}
                              disabled={!apptDateTime}
                              className="text-xs font-medium bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white px-2.5 py-1 rounded"
                            >
                              Book
                            </button>
                            <button
                              onClick={() => { setSchedulingRef(null); setApptDateTime(''); }}
                              className="text-xs text-slate-500 hover:text-slate-900 px-1"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                            {!r.converted_at && (
                              <button
                                onClick={() => convertToClient(r)}
                                className="text-xs font-medium bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 px-2.5 py-1 rounded"
                                title="Create a client profile so the lifecycle engine picks them up"
                              >
                                Convert to client
                              </button>
                            )}
                            {r.converted_at && (
                              <button
                                onClick={() => onGotoTab('clients')}
                                className="text-xs font-medium bg-white border border-emerald-200 hover:border-emerald-300 text-emerald-700 px-2.5 py-1 rounded"
                                title="Open the Clients tab to view this person's full profile"
                              >
                                Open client
                              </button>
                            )}
                            <button
                              onClick={() => setSchedulingRef(r.id)}
                              className="text-xs font-medium bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 px-2.5 py-1 rounded"
                              title="Book a call"
                            >
                              Schedule
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add modal */}
      {adding && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setAdding(false)}>
          <div className="bg-white w-full max-w-md rounded-xl shadow-xl border border-slate-200" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-100">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">New referral</div>
              <h2 className="text-lg font-semibold text-slate-900 tracking-tight mt-0.5">Add referral</h2>
            </div>
            <div className="p-5 space-y-3">
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Name <span className="text-rose-500">*</span></span>
                <input value={draft.referred_name} onChange={e => setDraft(d => ({ ...d, referred_name: e.target.value }))} className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm focus:border-slate-400 focus:outline-none"/>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Phone</span>
                <input value={draft.referred_phone} onChange={e => setDraft(d => ({ ...d, referred_phone: e.target.value }))} placeholder="(xxx) xxx-xxxx" className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm focus:border-slate-400 focus:outline-none"/>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Referrer (existing client)</span>
                <select value={draft.referrer_client_id} onChange={e => setDraft(d => ({ ...d, referrer_client_id: e.target.value }))} className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white">
                  <option value="">— none —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Notes</span>
                <textarea rows={2} value={draft.notes} onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))} className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm focus:border-slate-400 focus:outline-none"/>
              </label>
            </div>
            <div className="px-5 py-3 flex justify-end gap-2 border-t border-slate-100">
              <button onClick={() => setAdding(false)} className="text-sm font-medium text-slate-600 hover:text-slate-900 px-3 py-1.5">Cancel</button>
              <button onClick={addReferral} disabled={!draft.referred_name.trim()} className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-medium tracking-tight px-4 py-1.5 rounded-md">Add referral</button>
            </div>
          </div>
        </div>
      )}

      {/* Profile modal — full referral details, opened by clicking a name in the kanban. */}
      {profileRef && (() => {
        const r = profileRef;
        const emailMatch = String(r.notes || '').match(/Email:\s*([^\s\n]+)/i);
        const email = emailMatch ? emailMatch[1] : null;
        const cleanNotes = String(r.notes || '')
          .replace(/\[NICHE:\s*(life|health|medicare)\s*\]\s*\n?/i, '')
          .replace(/Email:\s*[^\s\n]+\s*\n?/i, '')
          .replace(/---\nConsent shown to prospect at submission:[\s\S]*$/m, '')
          .trim();
        const stageCol = COLUMNS.find(c => c.key === r.status);
        const refrName = referrerName(r.referrer_client_id);
        return (
          <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setProfileRef(null)}>
            <div className="bg-white w-full max-w-lg rounded-xl shadow-xl border border-slate-200 overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="px-6 py-5 border-b border-slate-100 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Referral profile</div>
                  <h2 className="text-xl font-semibold text-slate-900 tracking-tight mt-0.5 truncate">{r.referred_name}</h2>
                  <div className="text-xs text-slate-500 mt-1">
                    Added {new Date(r.created_at).toLocaleDateString()}{refrName ? <> · via <span className="font-medium text-slate-700">{refrName}</span></> : null}
                  </div>
                </div>
                <button onClick={() => setProfileRef(null)} className="text-slate-400 hover:text-slate-900 text-sm">Close</button>
              </div>

              <div className="px-6 py-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">Stage</div>
                    <div className="mt-1 text-sm font-medium text-slate-900">{stageCol?.label || r.status}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">Payout</div>
                    <div className="mt-1 text-sm font-medium text-slate-900 capitalize">{r.payout_status || '—'}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">Phone</div>
                    <div className="mt-1 text-sm font-medium text-slate-900 tabular-nums">{r.referred_phone ? fmtPhoneUS(r.referred_phone) : <span className="text-slate-400">—</span>}</div>
                  </div>
                  {email && (
                    <div className="col-span-2">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">Email</div>
                      <div className="mt-1 text-sm font-medium text-slate-900">{email}</div>
                    </div>
                  )}
                  {cleanNotes && (
                    <div className="col-span-2">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">Notes</div>
                      <div className="mt-1 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{cleanNotes}</div>
                    </div>
                  )}
                  {r.converted_at && (
                    <div className="col-span-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-emerald-700">Converted to client</div>
                      <div className="mt-0.5 text-xs text-emerald-800">{new Date(r.converted_at).toLocaleString()}</div>
                    </div>
                  )}
                </div>
              </div>

              <div className="px-6 py-3 flex flex-wrap justify-end gap-2 border-t border-slate-100 bg-slate-50/50">
                {!r.converted_at && (
                  <button
                    onClick={() => { convertToClient(r); setProfileRef(null); }}
                    className="text-sm font-medium bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-1.5 rounded-md"
                  >
                    Convert to client
                  </button>
                )}
                {r.converted_at && (
                  <button
                    onClick={() => { onGotoTab('clients'); setProfileRef(null); }}
                    className="text-sm font-medium bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 rounded-md"
                  >
                    Open client
                  </button>
                )}
                <button
                  onClick={() => { setProfileRef(null); setSchedulingRef(r.id); }}
                  className="text-sm font-medium bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded-md"
                >
                  Schedule a call
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// SMS segment math — GSM-7 = 160 single / 153 concat, Unicode = 70
// single / 67 concat. We coarse-detect Unicode by scanning for any
// codepoint above 0x7F; that overcounts a bit (some Latin-1 chars
// are still GSM) but errs on the side of warning the operator.
function smsSegmentInfo(body = '') {
  const chars = body.length;
  const isUnicode = /[^\x00-\x7F]/.test(body);
  const single = isUnicode ? 70 : 160;
  const concat = isUnicode ? 67 : 153;
  let segments;
  if (chars === 0)          segments = 0;
  else if (chars <= single) segments = 1;
  else                      segments = Math.ceil(chars / concat);
  return {
    chars,
    segments,
    encoding: isUnicode ? 'ucs2' : 'gsm7',
    chars_per_segment: single,
  };
}

// ════════════════════════════════════════════════════════════════════
// LIFECYCLE TEMPLATES PANEL — embedded in Settings.
//
// One row per touchpoint. Operator edits the body inline; toggles
// active; sees the merge-field reference. Also captures referral
// payout amount + (future) quiet hours config.
// ════════════════════════════════════════════════════════════════════
function LifecycleTemplatesPanel({ currentUser, settings = {}, setSettings = () => {}, showToast = () => {} }) {
  const [templates, setTemplates] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [drafts, setDrafts] = React.useState({});  // touchpoint_key → body draft
  const [referralAmount, setReferralAmount] = React.useState(String(settings?.referralPayoutAmount ?? ''));

  const load = React.useCallback(async () => {
    // No user yet → render empty state, don't sit on a spinner.
    if (!currentUser?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      await seedDefaultTemplatesIfEmpty(supabase, currentUser.id);
      const { data } = await supabase.from('message_templates').select('*').eq('owner_id', currentUser.id);
      const rows = data || [];
      setTemplates(rows);
      const d = {};
      rows.forEach(t => { d[t.touchpoint_key] = t.body; });
      setDrafts(d);
    } catch (err) {
      // Seed or RLS failure — show the "no templates" state instead
      // of stalling forever. Bug report: stuck "Loading templates…".
      console.warn('[Templates] load threw:', err);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  React.useEffect(() => { load(); }, [load]);

  async function saveBody(touchpointKey) {
    const body = drafts[touchpointKey];
    if (body == null) return;
    const { error } = await supabase
      .from('message_templates')
      .update({ body })
      .eq('owner_id', currentUser?.id)
      .eq('touchpoint_key', touchpointKey);
    if (error) { showToast(`Save failed: ${error.message}`); return; }
    setTemplates(prev => prev.map(t => t.touchpoint_key === touchpointKey ? { ...t, body } : t));
    showToast(`Saved ${touchpointKey}`);
  }
  async function toggleActive(t) {
    const next = !t.active;
    const { error } = await supabase
      .from('message_templates')
      .update({ active: next })
      .eq('owner_id', currentUser?.id)
      .eq('touchpoint_key', t.touchpoint_key);
    if (error) { showToast(`Toggle failed: ${error.message}`); return; }
    setTemplates(prev => prev.map(x => x.touchpoint_key === t.touchpoint_key ? { ...x, active: next } : x));
  }
  function saveReferralAmount() {
    const n = Number(referralAmount);
    if (!Number.isFinite(n) || n < 0) { showToast('Enter a valid number.'); return; }
    setSettings(s => ({ ...s, referralPayoutAmount: n }));
    showToast(`Referral payout set to $${n.toFixed(0)}`);
  }

  const tpByKey = Object.fromEntries(templates.map(t => [t.touchpoint_key, t]));

  return (
    <div className="bg-white border-2 border-slate-200 p-5">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">Lifecycle SMS Engine</div>
          <div className="text-lg font-black text-slate-900 uppercase tracking-tight mt-1">Templates</div>
        </div>
        <div className="text-[0.6rem] uppercase tracking-wider text-slate-400">{templates.length} / {LIFECYCLE_TOUCHPOINTS.length} touchpoints</div>
      </div>

      {/* Referral payout amount */}
      <div className="bg-violet-50 border border-violet-200 p-3 mb-4 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-[0.6rem] font-black uppercase tracking-[0.15em] text-violet-800">Referral Payout Amount</div>
          <div className="text-xs text-violet-700 mt-0.5">Rendered in marketing templates as {'{referral_amount}'}. Used in month 3/6/9 and anniversary asks.</div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-500 font-mono">$</span>
          <input
            type="text"
            inputMode="decimal"
            value={referralAmount}
            onChange={e => setReferralAmount(e.target.value.replace(/[^0-9.]/g, ''))}
            placeholder="100"
            className="w-24 border border-slate-300 px-2 py-1 text-sm font-mono"
          />
          <button onClick={saveReferralAmount} className="bg-violet-600 hover:bg-violet-700 text-white text-[0.6rem] font-black uppercase tracking-tight px-3 py-1.5">Save</button>
        </div>
      </div>

      {/* Merge field reference */}
      <details className="mb-4 border border-slate-200 bg-slate-50 p-3">
        <summary className="text-[0.6rem] font-black uppercase tracking-[0.15em] text-slate-500 cursor-pointer">Merge Field Reference</summary>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3 text-xs font-mono">
          <span><span className="text-emerald-700">{'{first_name}'}</span> · client first name</span>
          <span><span className="text-emerald-700">{'{agent_name}'}</span> · your display name</span>
          <span><span className="text-emerald-700">{'{draft_date}'}</span> · first draft date</span>
          <span><span className="text-emerald-700">{'{draft_amount}'}</span> · draft $ formatted</span>
          <span><span className="text-emerald-700">{'{referral_amount}'}</span> · payout above</span>
          <span><span className="text-emerald-700">{'{carrier}'}</span> · policy carrier</span>
        </div>
      </details>

      {loading ? (
        <div className="text-center text-sm text-slate-500 py-8">Loading templates…</div>
      ) : (
        <div className="space-y-3">
          {LIFECYCLE_TOUCHPOINTS.map(tp => {
            const t = tpByKey[tp.key];
            if (!t) return null;
            const draft = drafts[tp.key] ?? t.body;
            const dirty = draft !== t.body;
            return (
              <div key={tp.key} className={`border-2 ${t.active ? 'border-slate-200' : 'border-slate-100 bg-slate-50'} p-3`}>
                <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
                  <div>
                    <div className="text-[0.65rem] font-black uppercase tracking-[0.12em] text-slate-900">
                      {tp.label}
                      {tp.isMarketing && <span className="ml-2 text-[0.55rem] font-black tracking-[0.15em] text-violet-700 bg-violet-50 border border-violet-200 px-1.5 py-0.5">MKTG</span>}
                    </div>
                    <div className="text-[0.6rem] text-slate-500 mt-0.5">
                      <span className="font-bold uppercase tracking-wider">{tp.anchor}</span> · {tp.offset} · {tp.description}
                    </div>
                  </div>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={t.active} onChange={() => toggleActive(t)} className="rounded"/>
                    <span className="text-[0.6rem] font-bold uppercase tracking-wider text-slate-700">{t.active ? 'Active' : 'Inactive'}</span>
                  </label>
                </div>
                <textarea
                  rows={3}
                  value={draft}
                  onChange={e => setDrafts(d => ({ ...d, [tp.key]: e.target.value }))}
                  className={`w-full border-2 px-2 py-1.5 text-sm font-mono focus:outline-none ${
                    draft.length > 160 ? 'border-amber-500 focus:border-amber-600' : 'border-slate-300 focus:border-emerald-500'
                  }`}
                />
                {/* SMS segment cost guard — audit bug #2: templates
                    over 160 chars silently cost 2 segments (~$0.06)
                    instead of 1 (~$0.03). Show segment count + cost
                    implication so the operator can make an informed
                    call. Not a hard block — some marketing copy
                    intentionally needs multi-segment length. */}
                {(() => {
                  const seg = smsSegmentInfo(draft);
                  const dollarsBase = 0.03; // per-segment SMS cost estimate
                  const costCents = Math.round(seg.segments * dollarsBase * 100);
                  const toneClass = seg.segments <= 1
                    ? 'text-slate-400'
                    : seg.segments === 2 ? 'text-amber-700 font-semibold'
                    :                       'text-red-700 font-bold';
                  return (
                    <div className="flex justify-between items-center mt-1.5">
                      <div className={`text-[0.55rem] uppercase tracking-wider ${toneClass}`}>
                        {draft.length} chars · {seg.segments} segment{seg.segments === 1 ? '' : 's'} · ~${(costCents / 100).toFixed(2)}/send
                        {seg.segments > 1 && (
                          <span className="ml-2 text-[0.5rem] normal-case tracking-normal">
                            (splits at {seg.chars_per_segment} chars — trim to save {seg.segments - 1} segment{seg.segments === 2 ? '' : 's'})
                          </span>
                        )}
                      </div>
                      {dirty && (
                        <button
                          onClick={() => saveBody(tp.key)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white text-[0.6rem] font-black uppercase tracking-tight px-3 py-1"
                        >
                          Save changes
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// SMS DRIP BUILDER + ENROLLMENT
//
// Lets agents create their OWN SMS drip campaigns (custom workflows),
// then drop a selected lead into one. Backed by the existing Supabase
// workflows + workflow_steps + enrollments tables (migration 006);
// the cron at /api/workflow/tick fires the scheduled sends.
//
// Each drip is a sequence of:
//   • send_sms(body, after_X_minutes)
// First step starts immediately on enroll; subsequent steps wait the
// configured minutes after the previous step.
//
// The drip BODY supports the same merge fields as the lifecycle
// templates: {first_name}, {agent_name}, {phone}, etc. Anything inside
// [AI: instruction] gets Claude-rendered at send time per existing
// tick.js logic.
// ════════════════════════════════════════════════════════════════════

function SmsDripBuilder({ currentUser, settings = {}, showToast = () => {} }) {
  const [drips, setDrips] = React.useState([]);
  const [steps, setSteps] = React.useState({}); // workflowId → step rows
  const [activeId, setActiveId] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [adding, setAdding] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [aiOpen, setAiOpen] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    const { data: wfs } = await supabase
      .from('workflows')
      .select('*')
      .eq('trigger', 'manual')
      .order('created_at');
    setDrips(wfs || []);
    if (wfs && wfs.length > 0 && !activeId) setActiveId(wfs[0].id);
    if (wfs && wfs.length > 0) {
      const ids = wfs.map(w => w.id);
      const { data: stps } = await supabase
        .from('workflow_steps')
        .select('*')
        .in('workflow_id', ids)
        .order('position');
      const byWf = {};
      (stps || []).forEach(s => { (byWf[s.workflow_id] ||= []).push(s); });
      setSteps(byWf);
    }
    setLoading(false);
  }, [activeId]);

  React.useEffect(() => { load(); }, [load]);

  async function createDrip() {
    const name = (newName || '').trim();
    if (!name) { showToast('Name required'); return; }

    // RLS on public.workflows requires created_by = auth.uid().
    // The currentUser prop can be null mid-load (race with AuthContext);
    // resolve uid from a live Supabase auth lookup as a fallback so we
    // never send a null/undefined created_by — that's what triggers
    // "new row violates row-level security policy".
    let uid = currentUser?.id;
    if (!uid) {
      try {
        const { data: u } = await supabase.auth.getUser();
        uid = u?.user?.id;
      } catch { /* keep uid null */ }
    }
    if (!uid) { showToast('Sign in to create a drip.'); return; }

    const { data, error } = await supabase.from('workflows').insert({
      name,
      trigger: 'manual',
      active: true,
      created_by: uid,
    }).select().single();
    if (error) { showToast(`Failed: ${error.message}`); return; }
    setDrips(prev => [...prev, data]);
    setActiveId(data.id);
    setSteps(prev => ({ ...prev, [data.id]: [] }));
    setNewName('');
    setAdding(false);
  }
  async function renameDrip(id, name) {
    if (!name.trim()) return;
    await supabase.from('workflows').update({ name: name.trim() }).eq('id', id);
    setDrips(prev => prev.map(d => d.id === id ? { ...d, name: name.trim() } : d));
  }
  async function toggleActive(d) {
    const next = !d.active;
    await supabase.from('workflows').update({ active: next }).eq('id', d.id);
    setDrips(prev => prev.map(x => x.id === d.id ? { ...x, active: next } : x));
  }
  async function deleteDrip(id) {
    if (!(await hqConfirm({
      title: 'Delete this drip?',
      body: 'Any active enrollments will keep firing through completion.',
      confirmLabel: 'Delete drip', danger: true,
    }))) return;
    await supabase.from('workflows').delete().eq('id', id);
    setDrips(prev => prev.filter(d => d.id !== id));
    if (activeId === id) setActiveId(null);
  }

  // ── Step CRUD ───────────────────────────────────────────────────
  async function addStep(workflowId) {
    const existing = steps[workflowId] || [];
    const position = existing.length + 1;
    const body = position === 1
      ? 'Hi {first_name}, this is {agent_name} — quick follow-up. Got a sec?'
      : 'Just checking in {first_name} — any thoughts? — {agent_name}';
    const waitMinutes = position === 1 ? 0 : 60 * 24; // step 1 immediate, others +1d default
    // Create template first.
    const { data: tpl, error: tErr } = await supabase.from('templates').insert({
      name: `Drip step ${position}`,
      body,
      owner_id: currentUser?.id,
    }).select().single();
    if (tErr) { showToast(`Template failed: ${tErr.message}`); return; }
    const { data: step, error: sErr } = await supabase.from('workflow_steps').insert({
      workflow_id: workflowId,
      position,
      kind: 'send_sms',
      template_id: tpl.id,
      wait_minutes: waitMinutes,
    }).select().single();
    if (sErr) { showToast(`Step failed: ${sErr.message}`); return; }
    setSteps(prev => ({ ...prev, [workflowId]: [...(prev[workflowId] || []), { ...step, _body: body }] }));
  }
  async function updateStepBody(step, body) {
    if (step.template_id) {
      await supabase.from('templates').update({ body }).eq('id', step.template_id);
    } else {
      const { data: tpl } = await supabase.from('templates').insert({
        name: `Drip step ${step.position}`,
        body,
        owner_id: currentUser?.id,
      }).select().single();
      await supabase.from('workflow_steps').update({ template_id: tpl.id }).eq('id', step.id);
    }
    setSteps(prev => ({
      ...prev,
      [step.workflow_id]: (prev[step.workflow_id] || []).map(s => s.id === step.id ? { ...s, _body: body } : s),
    }));
  }
  async function updateWait(step, minutes) {
    await supabase.from('workflow_steps').update({ wait_minutes: minutes }).eq('id', step.id);
    setSteps(prev => ({
      ...prev,
      [step.workflow_id]: (prev[step.workflow_id] || []).map(s => s.id === step.id ? { ...s, wait_minutes: minutes } : s),
    }));
  }
  async function deleteStep(step) {
    if (!(await hqConfirm({
      title: 'Delete this step?',
      confirmLabel: 'Delete step', danger: true,
    }))) return;
    await supabase.from('workflow_steps').delete().eq('id', step.id);
    const remaining = (steps[step.workflow_id] || []).filter(s => s.id !== step.id);
    // Renumber positions in DB to keep order tight.
    for (let i = 0; i < remaining.length; i++) {
      if (remaining[i].position !== i + 1) {
        await supabase.from('workflow_steps').update({ position: i + 1 }).eq('id', remaining[i].id);
        remaining[i].position = i + 1;
      }
    }
    setSteps(prev => ({ ...prev, [step.workflow_id]: remaining }));
  }

  // Load body text for each step on first render (templates table).
  React.useEffect(() => {
    (async () => {
      const stepsAll = Object.values(steps).flat();
      const need = stepsAll.filter(s => s.template_id && s._body == null);
      if (need.length === 0) return;
      const ids = need.map(s => s.template_id);
      const { data: tpls } = await supabase.from('templates').select('id, body').in('id', ids);
      if (!tpls) return;
      setSteps(prev => {
        const next = { ...prev };
        for (const wfId of Object.keys(next)) {
          next[wfId] = next[wfId].map(s => {
            if (s._body != null) return s;
            const tpl = tpls.find(t => t.id === s.template_id);
            return tpl ? { ...s, _body: tpl.body } : s;
          });
        }
        return next;
      });
    })();
  }, [steps]);

  const active = drips.find(d => d.id === activeId) || null;
  const activeSteps = active ? (steps[active.id] || []) : [];
  const fmtWait = (mins) => {
    if (!mins || mins === 0) return 'Immediate';
    if (mins < 60) return `+${mins}m`;
    if (mins < 1440) return `+${(mins / 60).toFixed(mins % 60 === 0 ? 0 : 1)}h`;
    return `+${(mins / 1440).toFixed(mins % 1440 === 0 ? 0 : 1)}d`;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-xl px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-slate-900">SMS Drip Campaigns</h2>
          <div className="text-sm text-slate-500 mt-1">Build follow-up sequences. Enroll any lead with one click from the Leads board.</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setAiOpen(true)}
            title="Describe the drip in plain English (or dictate it). AI drafts the full flow with wait times and message text — you review and save."
            className="bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold px-4 py-2 rounded-lg transition-colors inline-flex items-center gap-1.5 shadow-sm"
          >
            ✨ Build with AI
          </button>
          <button
            onClick={() => { setNewName(''); setAdding(true); }}
            className="bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            + New drip
          </button>
        </div>
      </div>

      <React.Suspense fallback={null}>
        <AiDripBuilderModal
          open={aiOpen}
          onClose={() => setAiOpen(false)}
          currentUser={currentUser}
          showToast={showToast}
          onSaved={() => load()}
        />
      </React.Suspense>

      {adding && (
        <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-2 flex-wrap">
          <input
            // Browser autofill was injecting random text ("yy7") into this
            // field on Drips load. The combination of off+name+1p-ignore
            // covers Chrome/Safari + 1Password/Bitwarden behaviors.
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Drip name — e.g. Aged 7-Touch, Voicemail Saturation, IUL Quote Follow-Up"
            autoFocus
            autoComplete="off"
            name="crm-drip-name"
            data-1p-ignore="true"
            data-lpignore="true"
            data-form-type="other"
            onKeyDown={e => { if (e.key === 'Enter') createDrip(); else if (e.key === 'Escape') { setAdding(false); setNewName(''); } }}
            className="flex-1 min-w-[260px] border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
          />
          <button
            onClick={createDrip}
            disabled={!newName.trim()}
            title={!newName.trim() ? 'Enter a drip name first' : 'Create drip'}
            className="bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-3 py-2 rounded-lg transition-colors"
          >
            Create
          </button>
          <button onClick={() => { setAdding(false); setNewName(''); }} className="text-sm font-medium text-slate-500 hover:text-slate-900 px-2 py-2">Cancel</button>
        </div>
      )}
      {/* Operator audit 2026-06-29 (BUG-007): the Create button was
          disabled when name was empty but offered no visible reason,
          so clicks read as "silently does nothing." Inline helper
          surfaces the gate explicitly. */}
      {adding && !newName.trim() && (
        <div className="text-xs text-amber-700 -mt-2 pl-1">Enter a drip name to enable Create.</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {/* Drip list */}
        <div className="md:col-span-1 bg-white border border-slate-200 overflow-auto" style={{ maxHeight: '70vh' }}>
          {loading ? (
            <div className="p-4 text-xs text-slate-400">Loading…</div>
          ) : drips.length === 0 ? (
            <div className="p-4 text-xs text-slate-400 italic text-center">No drips yet. Build your first one above.</div>
          ) : (
            drips.map(d => (
              <button
                key={d.id}
                onClick={() => setActiveId(d.id)}
                className={`w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 ${activeId === d.id ? 'bg-violet-50 border-l-4 border-l-violet-500' : ''}`}
              >
                <div className="text-sm font-bold text-slate-900 truncate">{d.name}</div>
                <div className="flex items-baseline gap-2 mt-0.5">
                  <span className={`text-[0.5rem] font-black uppercase tracking-wider ${d.active ? 'text-emerald-700' : 'text-slate-400'}`}>{d.active ? '● ACTIVE' : '○ PAUSED'}</span>
                  <span className="text-[0.55rem] text-slate-500">{(steps[d.id] || []).length} steps</span>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Step editor */}
        <div className="md:col-span-3 bg-white border border-slate-200 p-4">
          {!active ? (
            <div className="text-center text-sm text-slate-400 py-12">Pick a drip on the left, or create a new one above.</div>
          ) : (
            <>
              <div className="flex items-baseline justify-between gap-3 mb-4 pb-3 border-b border-slate-200">
                <input
                  value={active.name}
                  onChange={e => setDrips(prev => prev.map(d => d.id === active.id ? { ...d, name: e.target.value } : d))}
                  onBlur={e => renameDrip(active.id, e.target.value)}
                  className="text-lg font-black uppercase tracking-tight text-slate-900 border-0 focus:outline-none flex-1 min-w-0"
                />
                <label className="flex items-center gap-1.5 text-[0.6rem] font-bold uppercase cursor-pointer">
                  <input type="checkbox" checked={active.active} onChange={() => toggleActive(active)}/>
                  Active
                </label>
                <button onClick={() => deleteDrip(active.id)} className="text-[0.6rem] font-bold uppercase text-red-600 hover:text-red-800 underline">Delete</button>
              </div>

              {activeSteps.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-sm text-slate-400 mb-3">No steps yet — add one to start your drip.</div>
                  <button onClick={() => addStep(active.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase tracking-tight px-4 py-2">+ Add First Step</button>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {activeSteps.map((s, i) => (
                      <div key={s.id} className="border-2 border-slate-200 p-3">
                        <div className="flex items-baseline justify-between gap-2 mb-2">
                          <div className="text-[0.6rem] font-black uppercase tracking-[0.15em] text-slate-700">Step {s.position}</div>
                          <div className="flex items-center gap-2">
                            <label className="flex items-center gap-1 text-[0.6rem] font-bold uppercase text-slate-600">
                              Wait
                              <input
                                type="number"
                                value={Math.round((s.wait_minutes || 0) / 60)}
                                onChange={e => updateWait(s, Math.max(0, Number(e.target.value)) * 60)}
                                min={0}
                                className="w-14 border border-slate-300 px-1.5 py-0.5 text-xs font-mono"
                              />
                              hrs <span className="text-slate-400">({fmtWait(s.wait_minutes)})</span>
                            </label>
                            <button onClick={() => deleteStep(s)} className="text-[0.6rem] font-bold uppercase text-red-600 hover:text-red-800">✕</button>
                          </div>
                        </div>
                        <textarea
                          rows={3}
                          value={s._body || ''}
                          onChange={e => {
                            const v = e.target.value;
                            setSteps(prev => ({ ...prev, [active.id]: prev[active.id].map(x => x.id === s.id ? { ...x, _body: v } : x) }));
                          }}
                          onBlur={e => updateStepBody(s, e.target.value)}
                          placeholder="SMS body. Tokens: {first_name} {agent_name} {phone}. Wrap dynamic instructions in [AI: tell them the carrier called]."
                          className="w-full text-sm border border-slate-300 px-2 py-1.5 font-mono focus:border-violet-500 focus:outline-none"
                        />
                        <div className="text-[0.55rem] text-slate-400 mt-0.5">{(s._body || '').length} chars</div>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => addStep(active.id)} className="mt-3 w-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-[0.65rem] font-black uppercase tracking-tight py-2">+ Add Step</button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// ENROLL LEAD IN DRIP MODAL — pick a workflow, drop in.
// ════════════════════════════════════════════════════════════════════
function EnrollLeadModal({ lead, currentUser, onClose, showToast = () => {} }) {
  const [drips, setDrips] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [enrolling, setEnrolling] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('workflows')
        .select('id, name, trigger, active')
        .eq('active', true)
        .order('name');
      setDrips(data || []);
      setLoading(false);
    })();
  }, []);

  async function enroll(wf) {
    if (!lead?.id) return;
    setEnrolling(wf.id);
    try {
      // Find step 1 of this workflow.
      const { data: step1 } = await supabase
        .from('workflow_steps')
        .select('id, wait_minutes')
        .eq('workflow_id', wf.id)
        .eq('position', 1)
        .maybeSingle();
      if (!step1) { showToast('This drip has no steps yet.'); return; }
      const { data: enr, error: e1 } = await supabase.from('enrollments').insert({
        lead_id: String(lead.id),
        workflow_id: wf.id,
        current_step: 1,
        status: 'active',
        owner_id: currentUser?.id,
      }).select('id').single();
      if (e1) { showToast(`Failed: ${e1.message}`); return; }
      const runAt = new Date(Date.now() + (step1.wait_minutes || 0) * 60 * 1000).toISOString();
      await supabase.from('scheduled_actions').insert({
        enrollment_id: enr.id,
        step_id: step1.id,
        run_at: runAt,
      });
      showToast(`Enrolled ${lead.name || 'lead'} in "${wf.name}"`);
      onClose();
    } finally {
      setEnrolling(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="bg-black border-l-4 border-violet-500 px-5 py-4">
          <div className="text-violet-400 text-[0.6rem] font-black uppercase tracking-[0.2em]">Enroll in Drip</div>
          <div className="text-white text-lg font-black uppercase tracking-tight mt-1">{lead?.name || 'Lead'}</div>
          <div className="text-slate-400 text-xs mt-0.5">{lead?.phone || '— no phone —'}</div>
        </div>
        <div className="p-4 max-h-[60vh] overflow-auto">
          {loading ? (
            <div className="text-center py-6 text-sm text-slate-400">Loading drips…</div>
          ) : drips.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-400">
              No active drips yet. Build one in the <strong>Drips</strong> tab first.
            </div>
          ) : (
            <div className="space-y-2">
              {drips.map(d => (
                <button
                  key={d.id}
                  onClick={() => enroll(d)}
                  disabled={enrolling === d.id}
                  className="w-full text-left p-3 border-2 border-slate-200 hover:border-violet-500 hover:bg-violet-50 transition-colors disabled:opacity-50"
                >
                  <div className="flex items-baseline justify-between">
                    <div className="font-bold text-slate-900 text-sm">{d.name}</div>
                    <div className="text-[0.55rem] font-black uppercase tracking-wider text-violet-700">{enrolling === d.id ? 'Enrolling…' : 'Enroll →'}</div>
                  </div>
                  <div className="text-[0.6rem] text-slate-500 uppercase tracking-wider mt-0.5">Trigger: {d.trigger || 'manual'}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="bg-slate-50 px-5 py-3 border-t border-slate-200 flex justify-end">
          <button aria-label="Close" onClick={onClose} className="text-xs font-bold uppercase text-slate-600 hover:text-slate-900">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// CRITICAL ALERTS — Prioritized intervention queue
//
// "Working backwards from the fire." Surfaces every policy that needs
// a phone call TODAY, with the dollar value at stake and what happens
// if nothing is done. Sorted by $ at risk descending.
//
// Sources of alerts:
//   1. Apps stuck >7d in non-terminal status        ($ pending commission)
//   2. Apps missing critical requirements           ($ blocked)
//   3. Recent chargebacks (last 14 days)            ($ already lost / save window)
//   4. Recently lapsed policies (last 30 days)      ($ premium at risk)
//   5. Clients with billing_status = nsf or lapsed  ($ next chargeback brewing)
//
// Each row shows the agent assigned, so the operator can route the
// work or do it themselves.
// ════════════════════════════════════════════════════════════════════

function CriticalAlertsView({ apps = [], transactions = [], leads = [], producers = {}, settings = {}, currentUser, getCarrier = () => 'Unknown', onGotoTab = () => {}, openClientProfile = () => {} }) {
  const [clientsCache, setClientsCache] = React.useState([]);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('clients').select('id, name, phone, billing_status, draft_amount, billing_mode, prod_num').limit(2000);
      if (!cancelled) setClientsCache(data || []);
    })();
    return () => { cancelled = true; };
  }, []);

  const myProdNum = String(
    (currentUser?.role !== 'admin' && currentUser?.prodNum) ||
    settings?.ownerProdNum || ''
  ).trim();
  const isAdmin = currentUser?.role === 'admin';
  const mine = (val) => !myProdNum || isAdmin || String(val || '').trim() === myProdNum;

  const fmt$ = (n) => '$' + Math.round(Number(n) || 0).toLocaleString();
  const agentName = (pn) => producers?.[pn]?.name || (pn ? `Producer ${pn}` : '—');

  // Critical alerts feed
  const alerts = React.useMemo(() => {
    const out = [];
    const now = Date.now();
    const DAY = 86400000;

    // 1. Stuck apps — submitted >7d, no movement
    (apps || []).forEach(a => {
      if (!mine(a.agentProdNum)) return;
      if (!a.submittedDate || ['issued','declined','withdrawn'].includes(a.status)) return;
      const daysSince = Math.floor((now - new Date(a.submittedDate + 'T00:00:00').getTime()) / DAY);
      if (daysSince < 7) return;
      const annualPrem = (Number(a.monthlyPremium) || 0) * 12;
      // Estimated commission lost = annual premium × 1.0 (~100% rate avg) × 0.75 (advance)
      const dollarsAtRisk = annualPrem * 0.75;
      out.push({
        type: 'stuck_app',
        urgency: daysSince > 21 ? 'critical' : daysSince > 14 ? 'high' : 'medium',
        title: `App stuck ${daysSince} days — ${a.clientName || 'Unknown'}`,
        sub: `${a.carrier || ''} ${a.product || ''} · ${a.status || 'submitted'}`.trim(),
        reason: `No status movement since ${a.submittedDate}. ${a.pendingRequirements?.length ? 'Pending: ' + a.pendingRequirements.join(', ') : 'Nothing flagged as pending.'}`,
        consequence: daysSince > 21 ? `Auto-decline likely · ${fmt$(dollarsAtRisk)} commission lost` : `Risk of NIGO/decline · ${fmt$(dollarsAtRisk)} pending`,
        dollarsAtRisk,
        agent: agentName(a.agentProdNum),
        agentProdNum: a.agentProdNum,
        clientName: a.clientName,
        action: 'Open in Apps',
        onAction: () => onGotoTab('apps'),
      });
    });

    // 2. Apps missing critical requirements (EFT form, HIPAA auth)
    (apps || []).forEach(a => {
      if (!mine(a.agentProdNum)) return;
      if (['issued','declined','withdrawn'].includes(a.status)) return;
      const critical = (a.pendingRequirements || []).filter(r => ['EFT form','HIPAA auth','ID copy'].includes(r));
      if (critical.length === 0) return;
      const annualPrem = (Number(a.monthlyPremium) || 0) * 12;
      const dollarsAtRisk = annualPrem * 0.75;
      out.push({
        type: 'missing_req',
        urgency: 'high',
        title: `Missing ${critical[0]} — ${a.clientName || 'Unknown'}`,
        sub: `${a.carrier || ''} ${a.product || ''}`.trim(),
        reason: `Application blocked until ${critical.join(' + ')} received. Without these the carrier can't process.`,
        consequence: `${fmt$(dollarsAtRisk)} on hold · withdraw risk after 30 days`,
        dollarsAtRisk,
        agent: agentName(a.agentProdNum),
        agentProdNum: a.agentProdNum,
        clientName: a.clientName,
        action: 'Open in Apps',
        onAction: () => onGotoTab('apps'),
      });
    });

    // 3. Recent chargebacks (last 14 days) — recovery window
    const cb14 = (transactions || []).filter(t => {
      if (!mine(t.prodNum)) return false;
      if (!t.isChargeback && !(Number(t.commAmt) < 0)) return false;
      if (!t.activityDate) return false;
      const d = new Date(t.activityDate + 'T00:00:00').getTime();
      return d > now - 14 * DAY;
    });
    cb14.forEach(t => {
      const lost = Math.abs(Number(t.commAmt) || 0);
      if (lost < 25) return; // skip trivial
      out.push({
        type: 'chargeback',
        urgency: lost > 500 ? 'critical' : lost > 200 ? 'high' : 'medium',
        title: `Chargeback ${fmt$(-lost)} — ${t.insured || 'Unknown'}`,
        sub: `${t.company || 'Carrier'} · ${t.activityType || ''} · ${t.policy || ''}`.trim(),
        reason: `Carrier reversed ${fmt$(lost)} on ${t.activityDate}. Save window typically 30 days before policy hard-lapses.`,
        consequence: `If not saved: permanent loss + chargeback compounds + relationship damage`,
        dollarsAtRisk: lost,
        agent: agentName(t.prodNum),
        agentProdNum: t.prodNum,
        clientName: t.insured,
        action: 'Open Retention',
        onAction: () => onGotoTab('retention'),
      });
    });

    // 4. NSF clients — next chargeback brewing
    (clientsCache || []).forEach(c => {
      if (!mine(c.prod_num)) return;
      if (c.billing_status !== 'nsf') return;
      const monthlyExposure = Number(c.draft_amount) || 0;
      const annualPrem = monthlyExposure * 12;
      const dollarsAtRisk = annualPrem * 0.75; // unearned advance
      out.push({
        type: 'nsf_client',
        urgency: 'high',
        title: `NSF · ${c.name || 'Unknown'}`,
        sub: `${c.billing_mode || 'monthly'} · ${fmt$(monthlyExposure)}/mo draft`,
        reason: 'Payment did not clear. Standard sequence: re-try in 5 days, lapse in ~31. Phone call now usually saves it (new card, payment date shift).',
        consequence: `~${fmt$(dollarsAtRisk)} advance reclaim if it lapses · client relationship damaged`,
        dollarsAtRisk,
        agent: agentName(c.prod_num),
        agentProdNum: c.prod_num,
        clientName: c.name,
        action: 'Open client',
        onAction: () => openClientProfile(c.name),
      });
    });

    // Sort by urgency rank then $ at risk
    const rank = { critical: 3, high: 2, medium: 1 };
    return out.sort((a, b) => (rank[b.urgency] - rank[a.urgency]) || (b.dollarsAtRisk - a.dollarsAtRisk));
  }, [apps, transactions, clientsCache, myProdNum, isAdmin, agentName, onGotoTab, openClientProfile, mine]);

  const totalRisk = alerts.reduce((s, a) => s + a.dollarsAtRisk, 0);
  const urgencyCount = {
    critical: alerts.filter(a => a.urgency === 'critical').length,
    high:     alerts.filter(a => a.urgency === 'high').length,
    medium:   alerts.filter(a => a.urgency === 'medium').length,
  };
  const [filterUrgency, setFilterUrgency] = React.useState('all');
  const visible = alerts.filter(a => filterUrgency === 'all' || a.urgency === filterUrgency);

  return (
    <div className="space-y-5">
      {/* Executive bar */}
      <div className="bg-black border-l-4 border-red-500 px-6 py-5 shadow-2xl flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-red-400 text-[0.6rem] font-black uppercase tracking-[0.3em]">Critical Alerts</div>
          <div className="text-white text-xl md:text-2xl font-black mt-1.5 uppercase tracking-tight">Fires to Put Out Today</div>
          <div className="text-slate-400 text-xs mt-1.5">
            {alerts.length} active alert{alerts.length === 1 ? '' : 's'} · {fmt$(totalRisk)} at risk
          </div>
        </div>
        <div className="text-right">
          <div className="text-red-400 text-[0.55rem] font-black uppercase tracking-[0.25em]">$ Dollar at Risk</div>
          <div className="text-white text-3xl md:text-4xl font-black tabular-nums leading-none mt-1">{fmt$(totalRisk)}</div>
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          ['all', `All ${alerts.length}`, 'slate'],
          ['critical', `Critical ${urgencyCount.critical}`, 'red'],
          ['high', `High ${urgencyCount.high}`, 'orange'],
          ['medium', `Medium ${urgencyCount.medium}`, 'amber'],
        ].map(([k, label, color]) => (
          <button
            key={k}
            onClick={() => setFilterUrgency(k)}
            className={`text-[0.6rem] font-black uppercase tracking-tight px-3 py-2 border-2 ${
              filterUrgency === k
                ? `bg-${color}-600 text-white border-${color}-700`
                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Alert cards */}
      {visible.length === 0 ? (
        <div className="bg-white border-2 border-emerald-200 p-10 text-center">
          <div className="text-5xl mb-3">✓</div>
          <div className="text-lg font-black text-emerald-700 uppercase tracking-tight">No fires.</div>
          <div className="text-sm text-slate-500 mt-1">Everything stable. Use the freed-up time to dial more leads.</div>
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((a, i) => {
            const tone = a.urgency === 'critical' ? 'red' : a.urgency === 'high' ? 'orange' : 'amber';
            return (
              <div key={i} className={`bg-white border-l-4 border-${tone}-500 border-2 border-r-slate-200 border-t-slate-200 border-b-slate-200 shadow-sm`}>
                <div className="flex items-stretch">
                  {/* Urgency stripe */}
                  <div className={`bg-${tone}-50 px-3 py-3 flex flex-col items-center justify-center min-w-[88px] border-r-2 border-${tone}-200`}>
                    <div className={`text-[0.55rem] font-black uppercase tracking-[0.15em] text-${tone}-700`}>{a.urgency}</div>
                    <div className={`text-xl font-black text-${tone}-700 tabular-nums leading-none mt-1`}>{fmt$(a.dollarsAtRisk)}</div>
                    <div className={`text-[0.5rem] uppercase tracking-wider text-${tone}-600 mt-0.5`}>at risk</div>
                  </div>
                  {/* Body */}
                  <div className="flex-1 px-4 py-3 min-w-0">
                    <div className="font-black text-slate-900 text-sm uppercase tracking-tight truncate">{a.title}</div>
                    <div className="text-xs text-slate-500 truncate">{a.sub}</div>
                    <div className="text-xs text-slate-700 mt-1.5 leading-snug">{a.reason}</div>
                    <div className={`text-[0.6rem] font-bold uppercase tracking-wider text-${tone}-700 mt-1.5`}>
                      ⚠ {a.consequence}
                    </div>
                    <div className="text-[0.55rem] uppercase tracking-wider text-slate-400 mt-1">
                      Agent: <span className="text-slate-600">{a.agent}</span>
                    </div>
                  </div>
                  {/* Action */}
                  <div className="flex items-center px-3">
                    <button
                      onClick={a.onAction}
                      className={`bg-${tone}-600 hover:bg-${tone}-700 text-white text-[0.6rem] font-black uppercase tracking-tight px-3 py-2 whitespace-nowrap`}
                    >
                      {a.action} →
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// MY POLICIES — fully searchable, sortable policy table
//
// Source: transactions (every commission row). Dedupes by policy
// number, keeps the most-recent activity per policy as the canonical
// row. Computes annual premium from monthlyPremium × 12 OR uses the
// commPremium field where present. Status derived from the
// most-recent activity type (chargeback / lapse → lapsed; else active).
// ════════════════════════════════════════════════════════════════════
function MyPoliciesView({ transactions = [], producers = {}, settings = {}, currentUser, getCarrier = () => 'Unknown', openClientProfile = () => {} }) {
  const myProdNum = String(
    (currentUser?.role !== 'admin' && currentUser?.prodNum) ||
    settings?.ownerProdNum || ''
  ).trim();
  const isAdmin = currentUser?.role === 'admin';
  const agentName = (pn) => producers?.[pn]?.name || (pn ? `Producer ${pn}` : '—');

  // Dedupe to one row per policy. Latest activity wins for "date
  // reported"; status derives from any chargeback/lapse seen.
  const policies = React.useMemo(() => {
    const map = new Map();
    for (const tx of (transactions || [])) {
      const pol = String(tx.policy || '').trim();
      if (!pol) continue;
      if (!isAdmin && myProdNum && String(tx.prodNum || '').trim() !== myProdNum) continue;
      const existing = map.get(pol);
      const date = tx.activityDate || '';
      const isCb = !!tx.isChargeback || Number(tx.commAmt) < 0;
      if (!existing) {
        map.set(pol, {
          policy: pol,
          insured: tx.insured || '—',
          prodNum: tx.prodNum,
          agent: agentName(tx.prodNum),
          carrier: tx.company || (getCarrier ? getCarrier(tx) : '') || '—',
          dateReported: date,
          monthlyPremium: Number(tx.monthlyPremium) || Number(tx.commPremium) || 0,
          annualPremium: (Number(tx.monthlyPremium) || Number(tx.commPremium) || 0) * 12,
          everChargedBack: isCb,
          rowCount: 1,
          netCommission: Number(tx.commAmt) || 0,
        });
      } else {
        existing.rowCount++;
        existing.netCommission += Number(tx.commAmt) || 0;
        if (isCb) existing.everChargedBack = true;
        if (date > existing.dateReported) existing.dateReported = date;
        // Prefer non-zero premium when available
        const prem = Number(tx.monthlyPremium) || Number(tx.commPremium) || 0;
        if (prem > existing.monthlyPremium) {
          existing.monthlyPremium = prem;
          existing.annualPremium = prem * 12;
        }
      }
    }
    return Array.from(map.values()).map(p => ({
      ...p,
      status: p.everChargedBack ? 'lapsed' : 'active',
    }));
  }, [transactions, myProdNum, isAdmin, producers, getCarrier]);

  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [carrierFilter, setCarrierFilter] = React.useState('all');
  const [agentFilter, setAgentFilter] = React.useState('all');
  const [sortKey, setSortKey] = React.useState('dateReported');
  const [sortDir, setSortDir] = React.useState('desc');

  const carriers = Array.from(new Set(policies.map(p => p.carrier).filter(Boolean))).sort();
  const agents = Array.from(new Set(policies.map(p => p.agent).filter(Boolean))).sort();

  const filtered = policies.filter(p => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (carrierFilter !== 'all' && p.carrier !== carrierFilter) return false;
    if (agentFilter !== 'all' && p.agent !== agentFilter) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (p.policy || '').toLowerCase().includes(s)
      || (p.insured || '').toLowerCase().includes(s)
      || (p.agent || '').toLowerCase().includes(s)
      || (p.carrier || '').toLowerCase().includes(s);
  });

  const sorted = [...filtered].sort((a, b) => {
    let av = a[sortKey], bv = b[sortKey];
    if (typeof av === 'string') { av = av.toLowerCase(); bv = (bv || '').toLowerCase(); }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const totalAP = sorted.reduce((s, p) => s + p.annualPremium, 0);
  const fmt$ = (n) => '$' + Math.round(n).toLocaleString();
  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('desc'); }
  };
  const arrow = (k) => sortKey === k ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  const Th = ({ field, label, align = 'left' }) => (
    <th onClick={() => toggleSort(field)} className={`text-${align} px-3 py-2 cursor-pointer hover:bg-slate-100 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-600`}>
      {label}{arrow(field)}
    </th>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-black border-l-4 border-emerald-500 px-6 py-5 shadow-2xl flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-emerald-400 text-[0.6rem] font-black uppercase tracking-[0.3em]">My Policies</div>
          <div className="text-white text-xl md:text-2xl font-black mt-1.5 uppercase tracking-tight">Policy Search &amp; Roster</div>
          <div className="text-slate-400 text-xs mt-1.5">{sorted.length} of {policies.length} polic{sorted.length === 1 ? 'y' : 'ies'} · {fmt$(totalAP)} AP shown</div>
        </div>
        <div className="text-right">
          <div className="text-emerald-400 text-[0.55rem] font-black uppercase tracking-[0.25em]">Total AP</div>
          <div className="text-white text-3xl md:text-4xl font-black tabular-nums leading-none mt-1">{fmt$(totalAP)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 p-3 flex items-center gap-2 flex-wrap">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search policy #, insured, agent, or carrier…"
          className="flex-1 min-w-[220px] border border-slate-300 px-3 py-1.5 text-sm focus:border-emerald-500 focus:outline-none"
        />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-slate-300 px-2 py-1.5 text-sm bg-white">
          <option value="all">All status</option>
          <option value="active">Active</option>
          <option value="lapsed">Lapsed</option>
        </select>
        <select value={carrierFilter} onChange={e => setCarrierFilter(e.target.value)} className="border border-slate-300 px-2 py-1.5 text-sm bg-white">
          <option value="all">All carriers</option>
          {carriers.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={agentFilter} onChange={e => setAgentFilter(e.target.value)} className="border border-slate-300 px-2 py-1.5 text-sm bg-white">
          <option value="all">All agents</option>
          {agents.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 sticky top-0">
            <tr>
              <Th field="policy" label="Policy #"/>
              <Th field="insured" label="Insured"/>
              <Th field="agent" label="Agent"/>
              <Th field="carrier" label="Carrier"/>
              <Th field="dateReported" label="Date Reported"/>
              <Th field="annualPremium" label="Annual Premium" align="right"/>
              <Th field="netCommission" label="Net Comm" align="right"/>
              <Th field="status" label="Status"/>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sorted.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-400 text-sm">No policies match these filters.</td></tr>
            ) : sorted.slice(0, 500).map(p => {
              // Carrier statements arrive as "LAST FIRST" (all caps).
              // Render as human-readable "First Last" per the July 2026
              // roster-UX fix; the raw form is preserved on p.insured
              // so downstream matchers (clientKeyOf, dedup) are
              // unaffected.
              const displayName = formatInsuredFirstLast(p.insured);
              const clientKey = clientKeyOf(p.insured);
              const rowClickable = !!clientKey && typeof openClientProfile === 'function';
              return (
                <tr
                  key={p.policy}
                  onClick={rowClickable ? () => openClientProfile(clientKey) : undefined}
                  className={`hover:bg-slate-50 ${rowClickable ? 'cursor-pointer' : ''}`}
                  title={rowClickable ? `Open ${displayName}'s profile` : undefined}
                >
                  <td className="px-3 py-2 font-mono text-xs font-bold text-slate-900">{p.policy}</td>
                  <td className="px-3 py-2 text-sm text-slate-900">
                    {rowClickable ? (
                      <span className="hover:text-emerald-700 hover:underline">{displayName}</span>
                    ) : displayName}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{p.agent}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{p.carrier}</td>
                  <td className="px-3 py-2 text-xs text-slate-600 font-mono">{p.dateReported || '—'}</td>
                  <td className="px-3 py-2 text-right text-sm tabular-nums font-semibold">{fmt$(p.annualPremium)}</td>
                  <td className={`px-3 py-2 text-right text-sm tabular-nums font-bold ${p.netCommission < 0 ? 'text-red-700' : 'text-emerald-700'}`}>{fmt$(p.netCommission)}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[0.55rem] font-black uppercase tracking-wider px-2 py-0.5 ${p.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                      {p.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {sorted.length > 500 && (
          <div className="px-3 py-2 text-center text-[0.6rem] uppercase tracking-wider text-slate-400 border-t border-slate-100">
            Showing first 500 · refine filters to see more
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// TEAM LEADERBOARD — competition as a forcing function
//
// Aggregates producers by customTeam (or 'mine' / 'unassigned'). Top 3
// teams get gold/silver/bronze spotlight cards. Below: a full sortable
// leaderboard. Toggle between team-level and agent-level views; toggle
// the time window (this month / last 30d / YTD).
// ════════════════════════════════════════════════════════════════════
function TeamLeaderboardView({ transactions = [], producers = {}, customTeams = [], settings = {}, currentUser }) {
  const [scope, setScope] = React.useState('team');   // team | agent
  const [period, setPeriod] = React.useState('mtd');  // mtd | last30 | ytd

  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const ytdStart = new Date(now.getFullYear(), 0, 1);
  const last30Start = new Date(now.getTime() - 30 * 86400000);
  const periodStart = period === 'mtd' ? monthStart : period === 'last30' ? last30Start : ytdStart;

  // Filter transactions to period.
  const periodTx = React.useMemo(() => {
    return (transactions || []).filter(t => {
      if (!t.activityDate) return false;
      return new Date(t.activityDate + 'T00:00:00') >= periodStart;
    });
  }, [transactions, periodStart]);

  // Aggregate per-producer.
  const perAgent = React.useMemo(() => {
    const m = {};
    for (const tx of periodTx) {
      const pn = String(tx.prodNum || '').trim();
      if (!pn) continue;
      if (!m[pn]) m[pn] = {
        prodNum: pn,
        name: producers[pn]?.name || `Producer ${pn}`,
        team: producers[pn]?.customTeam || producers[pn]?.team || 'unassigned',
        netCommission: 0,
        policies: new Set(),
        premium: 0,
      };
      m[pn].netCommission += Number(tx.commAmt) || 0;
      if (tx.policy) m[pn].policies.add(tx.policy);
      m[pn].premium += Number(tx.commPremium) || 0;
    }
    return Object.values(m).map(a => ({
      ...a,
      policyCount: a.policies.size,
      avgPremium: a.policies.size > 0 ? a.premium / a.policies.size : 0,
    }));
  }, [periodTx, producers]);

  // Aggregate per-team.
  const perTeam = React.useMemo(() => {
    const m = {};
    for (const a of perAgent) {
      const t = a.team || 'unassigned';
      if (!m[t]) m[t] = {
        key: t,
        name: t === 'mine' ? (settings.ownerName || 'My team') : t === 'unassigned' ? 'Unassigned' : (customTeams.find(ct => ct.id === t)?.name || t),
        netCommission: 0,
        policyCount: 0,
        premium: 0,
        agents: 0,
      };
      m[t].netCommission += a.netCommission;
      m[t].policyCount   += a.policyCount;
      m[t].premium       += a.premium;
      m[t].agents++;
    }
    return Object.values(m).map(t => ({
      ...t,
      avgPremium: t.policyCount > 0 ? t.premium / t.policyCount : 0,
    })).sort((a, b) => b.netCommission - a.netCommission);
  }, [perAgent, customTeams, settings.ownerName]);

  const podiumColors = ['amber', 'slate', 'orange']; // gold / silver / bronze
  const top3 = perTeam.slice(0, 3);
  const rest = scope === 'team' ? perTeam.slice(3) : perAgent.slice().sort((a, b) => b.netCommission - a.netCommission);

  const fmt$ = (n) => '$' + Math.round(n).toLocaleString();
  const periodLabel = period === 'mtd' ? 'Month to date' : period === 'last30' ? 'Last 30 days' : 'Year to date';

  return (
    <div className="space-y-5">
      {/* Executive bar */}
      <div className="bg-black border-l-4 border-amber-500 px-6 py-5 shadow-2xl flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-amber-400 text-[0.6rem] font-black uppercase tracking-[0.3em]">Leaderboard</div>
          <div className="text-white text-xl md:text-2xl font-black mt-1.5 uppercase tracking-tight">Team &amp; Agent Rankings</div>
          <div className="text-slate-400 text-xs mt-1.5">{periodLabel} · {perAgent.length} active agents · {perTeam.length} teams</div>
        </div>
        <div className="flex items-center gap-2">
          {/* Period toggle */}
          <div className="inline-flex border border-slate-700">
            {[['mtd','MTD'],['last30','30D'],['ytd','YTD']].map(([k, label]) => (
              <button key={k} onClick={() => setPeriod(k)} className={`px-3 py-1.5 text-[0.6rem] font-black uppercase tracking-tight ${period === k ? 'bg-amber-500 text-black' : 'text-slate-400 hover:text-white'}`}>
                {label}
              </button>
            ))}
          </div>
          {/* Scope toggle */}
          <div className="inline-flex border border-slate-700">
            {[['team','TEAMS'],['agent','AGENTS']].map(([k, label]) => (
              <button key={k} onClick={() => setScope(k)} className={`px-3 py-1.5 text-[0.6rem] font-black uppercase tracking-tight ${scope === k ? 'bg-emerald-500 text-black' : 'text-slate-400 hover:text-white'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Top 3 spotlight cards */}
      {top3.length > 0 && scope === 'team' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {top3.map((t, i) => {
            const tone = podiumColors[i];
            const medal = ['', '', ''][i];
            return (
              <div key={t.key} className={`bg-white border-2 border-${tone}-400 p-5 relative overflow-hidden`}>
                <div className={`absolute top-0 right-0 bg-${tone}-500 text-white text-[0.6rem] font-black uppercase tracking-[0.15em] px-3 py-1`}>#{i + 1}</div>
                <div className="text-4xl mb-2">{medal}</div>
                <div className="text-[0.6rem] font-black uppercase tracking-[0.15em] text-slate-500">Team</div>
                <div className="text-lg font-black text-slate-900 uppercase tracking-tight mt-0.5 truncate">{t.name}</div>
                <div className={`text-3xl font-black text-${tone}-700 tabular-nums leading-none mt-3`}>{fmt$(t.netCommission)}</div>
                <div className="text-[0.55rem] uppercase tracking-wider text-slate-500 mt-1">Net commission</div>
                <div className="mt-4 pt-3 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="text-sm font-black text-slate-900 tabular-nums">{t.policyCount}</div>
                    <div className="text-[0.5rem] uppercase tracking-wider text-slate-500">Policies</div>
                  </div>
                  <div>
                    <div className="text-sm font-black text-slate-900 tabular-nums">{fmt$(t.avgPremium)}</div>
                    <div className="text-[0.5rem] uppercase tracking-wider text-slate-500">Avg AP</div>
                  </div>
                  <div>
                    <div className="text-sm font-black text-slate-900 tabular-nums">{t.agents}</div>
                    <div className="text-[0.5rem] uppercase tracking-wider text-slate-500">Agents</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Full leaderboard */}
      <div className="bg-white border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
          <div className="text-[0.6rem] font-black uppercase tracking-[0.15em] text-slate-700">
            {scope === 'team' ? 'Remaining teams' : 'Agent leaderboard'}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-white border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">Rank</th>
              <th className="text-left px-4 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">{scope === 'team' ? 'Team' : 'Agent'}</th>
              {scope === 'team' && <th className="text-left px-4 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">Agents</th>}
              <th className="text-right px-4 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">Policies</th>
              <th className="text-right px-4 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">Avg AP</th>
              <th className="text-right px-4 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">Net Comm</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rest.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400 text-sm">No additional {scope === 'team' ? 'teams' : 'agents'}.</td></tr>
            ) : rest.map((row, i) => {
              const rank = scope === 'team' ? i + 4 : i + 1;
              return (
                <tr key={row.key || row.prodNum} className="hover:bg-slate-50">
                  <td className="px-4 py-2 text-xs font-black text-slate-400 tabular-nums">#{rank}</td>
                  <td className="px-4 py-2">
                    <div className="font-bold text-slate-900 text-sm">{row.name}</div>
                    {scope === 'agent' && row.team && <div className="text-[0.6rem] uppercase tracking-wider text-slate-500">{row.team === 'mine' ? (settings.ownerName || 'My team') : row.team === 'unassigned' ? 'Unassigned' : row.team}</div>}
                  </td>
                  {scope === 'team' && <td className="px-4 py-2 text-xs text-slate-600 tabular-nums">{row.agents}</td>}
                  <td className="px-4 py-2 text-right text-sm tabular-nums font-semibold">{row.policyCount}</td>
                  <td className="px-4 py-2 text-right text-sm tabular-nums text-slate-700">{fmt$(row.avgPremium)}</td>
                  <td className={`px-4 py-2 text-right text-sm tabular-nums font-black ${row.netCommission < 0 ? 'text-red-700' : 'text-emerald-700'}`}>{fmt$(row.netCommission)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// AI USAGE CARD — Settings panel showing this month's AI chat usage
// against the operator's plan cap. Surfaces the cap-exceeded state
// with an "upgrade" prompt before they hit it.
// ════════════════════════════════════════════════════════════════════
function AiUsageCard({ currentUser }) {
  const [used, setUsed] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    if (!currentUser?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const now = new Date();
      const monthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString();
      const { count } = await supabase
        .from('ai_usage')
        .select('id', { count: 'exact', head: true })
        .eq('owner_id', currentUser.id)
        .gte('created_at', monthStart);
      setUsed(count ?? 0);
    } catch (err) {
      console.warn('[AiUsage] load threw:', err);
      setUsed(0);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  React.useEffect(() => { load(); }, [load]);

  // Mirror PLAN_CAPS in api/_aiUsage.js. If a tighter integration to
  // Stripe subscriptions ships later, plumb the real plan here.
  const plan = 'producer';       // default tier
  const PLAN_NAMES = { solo: 'Solo · $97/mo', producer: 'Producer · $147/mo', agency: 'Agency · $297/mo' };
  const PLAN_CAPS  = { solo: 50, producer: 200, agency: Infinity };
  const cap = PLAN_CAPS[plan];
  // Actual usage percentage (uncapped) — used in the LABEL so an
  // over-cap operator reads "265% used" not the misleading "100% used"
  // the previous math produced. Bar width still clamps at 100% because
  // a progress bar can't render past its container.
  const pctActual = cap === Infinity ? 0 : ((used || 0) / cap) * 100;
  const pctBarWidth = Math.min(100, pctActual);
  const overCap = pctActual > 100;
  const remaining = cap === Infinity ? Infinity : Math.max(0, cap - (used || 0));
  const overBy = overCap ? (used || 0) - cap : 0;
  const tone = pctActual >= 90 ? 'red' : pctActual >= 70 ? 'amber' : 'emerald';
  // pct is kept as a name for legacy branches below that read it —
  // now points to the uncapped value so labels are honest.
  const pct = pctActual;

  return (
    <div className="bg-white border-2 border-slate-200 p-5">
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
        <div>
          <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">AI Usage</div>
          <div className="text-lg font-black text-slate-900 uppercase tracking-tight mt-1">This Month</div>
        </div>
        <div className="text-[0.6rem] uppercase tracking-wider text-slate-400">{PLAN_NAMES[plan]}</div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 italic py-3 text-center">Loading…</div>
      ) : (
        <>
          <div className="flex items-baseline gap-3 mb-2">
            <div className={`text-[3rem] font-black text-${tone}-700 tabular-nums leading-none`}>{used ?? 0}</div>
            <div className="text-sm text-slate-500 tabular-nums">/ {cap === Infinity ? '∞' : cap} chats</div>
          </div>
          {cap !== Infinity && (
            <>
              <div className="h-3 bg-slate-100 overflow-hidden">
                <div className={`h-full bg-${tone}-500 transition-all`} style={{ width: `${pctBarWidth}%` }}/>
              </div>
              <div className="flex justify-between mt-1.5 text-[0.55rem] font-bold uppercase tracking-wider">
                <span className="text-slate-500">{pctActual.toFixed(0)}% used</span>
                <span className={`text-${tone}-700`}>
                  {overCap ? `OVER · +${overBy}` : `${remaining} remaining`}
                </span>
              </div>
              {pct >= 70 && (
                <div className={`mt-3 text-xs bg-${tone}-50 border border-${tone}-200 text-${tone}-900 px-3 py-2`}>
                  {pct >= 100
                    ? `You've hit your monthly limit. AI is paused until ${new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, or upgrade for more.`
                    : `You're approaching your monthly AI limit. Upgrade to ${plan === 'solo' ? 'Producer' : 'Agency'} for ${plan === 'solo' ? '4x' : 'unlimited'} chats.`}
                </div>
              )}
            </>
          )}
          <div className="text-[0.55rem] text-slate-400 mt-3 uppercase tracking-wider">
            Each AI chat (Briefing, Accountant, Underwriting parser, etc.) counts as one. Resets the 1st of each month.
          </div>
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// HEALTH INSURANCE VERTICAL — Phase 1
//
// Parallel to the life-side data model. Health agents sell MA / Medigap
// / PDP / ACA / PPO / hospital indemnity / dental & vision / STM. Each
// has its own commission shape (PMPM recurring vs life advance) and a
// hard annual renewal cycle anchored by:
//   AEP (Annual Election Period)         Oct 15 – Dec 7
//   OEP (Open Enrollment Period)         Jan 1 – Mar 31
//   ACA OEP                              Nov 1 – Jan 15
//
// This phase ships:
//   • HealthDashboardView — AEP/OEP countdown, hero KPIs, renewal queue
//   • HealthPoliciesView  — searchable roster + add/edit modal
//   • Add Health Policy modal — captures everything the renewal engine
//     needs (carrier, plan type, dates, member ID, premium, comm)
// ════════════════════════════════════════════════════════════════════

function HealthDashboardView({ currentUser, settings = {}, onGotoTab = () => {}, openClientProfile = () => {} }) {
  const [policies, setPolicies] = React.useState([]);
  const [loading, setLoading]   = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('health_policies').select('*').order('renewal_date', { ascending: true });
    setPolicies(data || []);
    setLoading(false);
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const now = new Date();
  const window = activeOrNextWindow(now);
  const active = policies.filter(p => p.status === 'active');
  const totalMembers = active.length;
  const totalMRR = active.reduce((s, p) => s + (Number(p.monthly_premium) || 0), 0);
  const totalCommMRR = active.reduce((s, p) => s + (Number(p.monthly_commission) || 0), 0);

  // Renewal queue — anything renewing in the next 90 days, active.
  const renewing = active
    .filter(p => p.renewal_date)
    .map(p => ({ ...p, daysToRenewal: Math.ceil((new Date(p.renewal_date + 'T00:00:00') - now) / 86400000) }))
    .filter(p => p.daysToRenewal >= -7 && p.daysToRenewal <= 90)
    .sort((a, b) => a.daysToRenewal - b.daysToRenewal);

  // Stats by plan type.
  const byType = {};
  for (const p of active) {
    const t = p.plan_type || 'other';
    if (!byType[t]) byType[t] = { count: 0, mrr: 0 };
    byType[t].count++;
    byType[t].mrr += Number(p.monthly_premium) || 0;
  }

  const fmt$ = (n) => '$' + Math.round(Number(n) || 0).toLocaleString();

  return (
    <div className="space-y-5">
      {/* Executive bar */}
      <div className="bg-black border-l-4 border-sky-500 px-6 py-5 shadow-2xl flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sky-400 text-[0.6rem] font-black uppercase tracking-[0.3em]">Health Dashboard</div>
          <div className="text-white text-xl md:text-2xl font-black mt-1.5 uppercase tracking-tight">Medicare · ACA · PPO · Dental · Hospital</div>
          <div className="text-slate-400 text-xs mt-1.5">{totalMembers} active polic{totalMembers === 1 ? 'y' : 'ies'} · {fmt$(totalMRR)} MRR · {fmt$(totalCommMRR)} commission MRR</div>
        </div>
        {window && (
          <div className={`${window.status === 'active' ? 'bg-emerald-500' : 'bg-amber-500'} text-black px-4 py-2 font-mono`}>
            <div className="text-[0.55rem] font-black uppercase tracking-[0.2em]">{window.status === 'active' ? 'NOW' : 'NEXT'}</div>
            <div className="text-lg font-black uppercase tracking-tight mt-0.5">{window.label}</div>
            {window.status === 'upcoming' && <div className="text-[0.55rem] uppercase tracking-wider mt-0.5">in {window.daysUntil} days</div>}
          </div>
        )}
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <KpiTile label="Active Policies" value={totalMembers} suffix="members" color="sky" onClick={() => onGotoTab('health_policies')}/>
        <KpiTile label="Monthly Premium (MRR)" value={fmt$(totalMRR)} color="emerald" onClick={() => onGotoTab('health_policies')}/>
        <KpiTile label="Monthly Commission" value={fmt$(totalCommMRR)} color="amber" onClick={() => onGotoTab('health_policies')}/>
        <KpiTile label="Renewals Next 90d" value={renewing.length} suffix={renewing.length === 1 ? 'policy' : 'policies'} color={renewing.length > 10 ? 'red' : renewing.length > 3 ? 'amber' : 'emerald'} onClick={() => onGotoTab('health_policies')}/>
      </div>

      {/* Renewal queue */}
      <div className="bg-white border-2 border-slate-200 p-5">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="text-[0.6rem] font-black uppercase tracking-[0.15em] text-slate-500">Renewal Engine</div>
            <div className="text-lg font-black text-slate-900 uppercase tracking-tight mt-1">Upcoming Renewals</div>
          </div>
          <div className="text-[0.6rem] uppercase tracking-wider text-slate-400">Next 90 days</div>
        </div>
        {loading ? (
          <div className="text-center py-6 text-sm text-slate-400">Loading…</div>
        ) : renewing.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-3xl mb-2"></div>
            <div className="text-sm font-bold text-slate-700 uppercase tracking-tight">No renewals coming up</div>
            <div className="text-xs text-slate-400 mt-1">Add health policies in the Policies tab — renewals auto-populate here.</div>
          </div>
        ) : (
          <div className="space-y-1.5">
            {renewing.slice(0, 50).map(p => {
              const tone = p.daysToRenewal < 0 ? 'red' : p.daysToRenewal <= 14 ? 'orange' : p.daysToRenewal <= 30 ? 'amber' : 'sky';
              const ptype = HEALTH_PLAN_TYPE_BY_ID[p.plan_type] || HEALTH_PLAN_TYPE_BY_ID.other;
              return (
                <div key={p.id} className={`flex items-center gap-3 border-l-4 border-${tone}-500 border-2 border-r-slate-200 border-t-slate-200 border-b-slate-200 p-2.5`}>
                  <div className={`bg-${tone}-50 text-${tone}-700 px-3 py-2 text-center min-w-[68px]`}>
                    <div className={`text-[0.55rem] font-black uppercase tracking-[0.15em] text-${tone}-700`}>
                      {p.daysToRenewal < 0 ? `${Math.abs(p.daysToRenewal)}d PAST` : p.daysToRenewal === 0 ? 'TODAY' : `${p.daysToRenewal}d`}
                    </div>
                    <div className={`text-[0.6rem] font-bold uppercase mt-0.5 text-${tone}-700`}>{p.renewal_date?.slice(5)}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-slate-900 truncate">
                      <ClientNameLink name={p.client_name} openClientProfile={openClientProfile} className="text-slate-900 font-bold hover:underline"/>
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      <span className={`text-${ptype.tone}-700 font-bold`}>{ptype.short}</span> · {p.carrier} · {p.plan_name || '—'}
                    </div>
                  </div>
                  <div className="text-right text-xs text-slate-600 font-mono whitespace-nowrap">
                    {p.monthly_premium ? `${fmt$(p.monthly_premium)}/mo` : ''}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* By plan type */}
      <div className="bg-white border border-slate-200 p-5">
        <div className="text-[0.6rem] font-black uppercase tracking-[0.15em] text-slate-500 mb-3">Book Composition</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {HEALTH_PLAN_TYPES.filter(t => (byType[t.id]?.count || 0) > 0).map(t => (
            <div key={t.id} className={`border-l-4 border-${t.tone}-500 border-2 border-r-slate-200 border-t-slate-200 border-b-slate-200 p-3`}>
              <div className={`text-[0.55rem] font-black uppercase tracking-[0.15em] text-${t.tone}-700`}>{t.label}</div>
              <div className={`text-2xl font-black text-${t.tone}-700 tabular-nums mt-1`}>{byType[t.id].count}</div>
              <div className="text-[0.6rem] uppercase tracking-wider text-slate-500">{fmt$(byType[t.id].mrr)}/mo</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── HEALTH POLICIES — searchable roster + add/edit ──────────────────
// ════════════════════════════════════════════════════════════════════
// HEALTH IMPORT — drag/drop carrier statements + client lists.
//
// Auto-detects format from header keywords:
//   HCSC      — BCBS IL/MT/NM/OK/TX consolidated commission statement
//   HealthSherpa — ACA marketplace client export
//   Generic CSV — best-effort column mapping
//
// Pasted text is parsed via the format-specific library, surfaced as a
// preview table with CPA-grade anomalies (zero-comm, cancellations,
// first-month watch, dropped policies), then bulk-upserted into
// public.health_policies. Existing policies are matched by member_id
// and updated in place; new ones inserted.
// ════════════════════════════════════════════════════════════════════

function detectImportFormat(text) {
  const t = String(text || '');
  if (/Acct\/Policy|MYBLUEHEALTH|HCSC|Calc Method|\$\/Member|Selling Producer/i.test(t)) return 'hcsc';
  // UHC commission statement — header signature 'Commission Action'
  // + UnitedHealthcare brand string. Check before Jarvis because both
  // carry the UHC brand word, but only the commission statement has
  // the action column.
  if (isUhcStatementFormat(t))                                                            return 'uhc_statement';
  // Jarvis (UHC Book of Business) — check before generic CSV so the
  // confidentiality banner / agentId+agentNpn header signature wins.
  if (isJarvisFormat(t))                                                                  return 'jarvis';
  // Plan Advisors aggregator (Molina / Centene / Oscar / etc.) —
  // header trio "Generated From + Payee NPN + Carrier + Insured"
  // is unique. Check before HealthSherpa so the broader keyword
  // overlap on "Plan" doesn't false-positive.
  if (isPlanAdvisorsFormat(t))                                                            return 'plan_advisors';
  // HealthSherpa agent application export — header signature trio
  // (csr_level + ffm_app_id + healthsherpa_id) is uniquely
  // HealthSherpa. Check before the loose regex below so we get the
  // RICH-field path (carrier IDs, lifecycle, AOR, DMI/SVI, etc.)
  // instead of the generic-CSV bare-bones path.
  if (isHealthSherpaFormat(t))                                                            return 'healthsherpa';
  if (/HealthSherpa|HIOS|APTC|Premium Tax Credit|Marketplace Plan|Plan ID/i.test(t))     return 'healthsherpa_legacy';
  return 'generic_csv';
}

// Generic CSV / HealthSherpa column-mapper. Looks for known aliases per
// destination field and returns null for any we couldn't resolve.
function mapCsvHeader(headerRow) {
  const norm = (s) => String(s || '').toLowerCase().replace(/[_\-\s]+/g, ' ').trim();
  const idx = headerRow.map(norm);
  const find = (...aliases) => {
    for (const a of aliases) {
      const i = idx.indexOf(a);
      if (i >= 0) return i;
    }
    return -1;
  };
  return {
    client_name:    find('subscriber name', 'member name', 'client name', 'name', 'full name', 'insured name'),
    client_phone:   find('phone', 'mobile', 'phone number'),
    client_email:   find('email', 'email address'),
    client_dob:     find('dob', 'date of birth', 'birthdate'),
    plan_name:      find('plan name', 'plan', 'product name'),
    member_id:      find('member id', 'subscriber id', 'hios id', 'plan id', 'policy number', 'acct/policy'),
    carrier:        find('carrier', 'issuer', 'insurer'),
    plan_type:      find('plan type', 'metal level', 'metal'),
    effective_date: find('effective date', 'coverage start', 'orig eff dt'),
    renewal_date:   find('renewal date', 'plan year end', 'coverage end'),
    termination_date:find('termination date', 'cancel date', 'cancel dt'),
    monthly_premium:find('monthly premium', 'total premium', 'premium', 'premium amount'),
    monthly_commission: find('monthly commission', 'commission', 'comm amt'),
    subsidy_amount: find('aptc', 'aptc amount', 'premium tax credit', 'subsidy'),
    status:         find('status', 'enrollment status'),
    lead_source:    find('lead source', 'source', 'vendor', 'lead vendor'),
    notes:          find('notes', 'comments'),
    agent_npn:      find('agent npn', 'producer npn', 'npn'),
  };
}

function parseCsvLine(line) {
  const out = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQ) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') inQ = false;
      else cur += ch;
    } else {
      if (ch === ',')      { out.push(cur); cur = ''; }
      else if (ch === '\t'){ out.push(cur); cur = ''; }
      else if (ch === '"') inQ = true;
      else cur += ch;
    }
  }
  out.push(cur);
  return out.map(s => s.trim());
}

function parseGenericCsv(text) {
  const lines = String(text || '').split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) return { rows: [], header: null };
  const header = parseCsvLine(lines[0]);
  const map = mapCsvHeader(header);
  const rows = lines.slice(1).map(line => {
    const cells = parseCsvLine(line);
    const get = (key) => map[key] >= 0 ? cells[map[key]] : '';
    return {
      client_name:    get('client_name'),
      client_phone:   get('client_phone'),
      client_email:   get('client_email'),
      client_dob:     get('client_dob'),
      plan_name:      get('plan_name'),
      member_id:      get('member_id'),
      carrier:        get('carrier'),
      plan_type:      get('plan_type'),
      effective_date: get('effective_date'),
      renewal_date:   get('renewal_date'),
      termination_date:get('termination_date'),
      monthly_premium:get('monthly_premium'),
      monthly_commission: get('monthly_commission'),
      subsidy_amount: get('subsidy_amount'),
      status:         (get('status') || '').toLowerCase() || 'active',
      lead_source:    get('lead_source'),
      notes:          get('notes'),
    };
  }).filter(r => r.client_name);
  return { rows, header };
}

// Small helper used by HealthImportCard's PDF path — converts a File
// to a raw base64 string (no data:URL prefix) so the server endpoint
// can hand it to Claude's PDF input cleanly.
function fileToBase64Inline(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const result = String(r.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

function HealthImportCard({ currentUser, settings = {}, onImported = () => {}, showToast = () => {}, openClientProfile = () => {} }) {
  // Duplicate Prevention Dashboard state. The planner runs on
  // the parsed rows, produces a plan { newClients, updates,
  // duplicates, failed, stats }, and the agent reviews + commits.
  // Per-row decisions on flagged duplicates: 'skip' | 'create_new' | 'merge'.
  const [plan, setPlan] = React.useState(null);
  const [planning, setPlanning] = React.useState(false);
  const [decisions, setDecisions] = React.useState({});
  const [fileName, setFileName] = React.useState(null);
  const [text, setText]     = React.useState('');
  const [dragOver, setDragOver] = React.useState(false);
  const [parsed, setParsed] = React.useState(null);
  const [format, setFormat] = React.useState(null);
  const [analyzed, setAnalyzed] = React.useState(null);
  const [importing, setImporting] = React.useState(false);

  async function ingestFiles(files) {
    if (!files || files.length === 0) return;
    // Snapshot the file name(s) for the import_runs row. Multi-file
    // drops join with "; ".
    const names = Array.from(files).map(f => f.name).filter(Boolean);
    setFileName(names.join('; ') || null);

    // Separate PDFs (read as base64, sent to Claude server-side) from
    // text files (read as text, parsed client-side). PDFs route to
    // the carrier-specific server endpoint that uses Claude's native
    // PDF input — currently UHC only; HCSC / Plan Advisors / etc. fall
    // back to the legacy paste-text flow.
    const fileArr = Array.from(files);
    const pdfs    = fileArr.filter(f => (f.type || '').includes('pdf') || /\.pdf$/i.test(f.name || ''));
    const others  = fileArr.filter(f => !pdfs.includes(f));

    // ── PDF path — free, in-browser text extraction ──
    // Uses pdf.js (already lazy-loaded for the conservation tools) to
    // pull the text content out of each page. The text then flows
    // through the SAME parseText() / detectImportFormat() pipeline as
    // a paste or CSV — no AI in the loop, no per-import cost. Carriers
    // whose PDFs have clean text columns (HCSC / BCBS, Plan Advisors,
    // most ACA-side rosters) parse end-to-end. PDFs with awkward
    // column reflow (some UHC layouts) may extract scrambled — when
    // that happens the operator can still paste the table text
    // directly into the textarea below.
    if (pdfs.length > 0) {
      let mergedPdfText = '';
      for (const f of pdfs) {
        try {
          setPlanning(true);
          const t = await extractPdfText(f);
          if (t && t.trim()) {
            mergedPdfText += (mergedPdfText ? '\n' : '') + t.trim();
            showToast(`Extracted text from ${f.name}.`, 'success');
          } else {
            showToast(`${f.name}: no readable text — image-only PDF. Paste the table into the box below.`, 'error');
          }
        } catch (err) {
          showToast(`${f.name}: PDF text extraction failed — ${err.message || err}`, 'error');
        } finally {
          setPlanning(false);
        }
      }
      // Route through the regular text pipeline so HCSC / Plan
      // Advisors / UHC / Jarvis / HealthSherpa detection + parsing
      // all kick in automatically.
      if (mergedPdfText) {
        const merged = [text, mergedPdfText].filter(Boolean).join('\n');
        setText(merged);
        parseText(merged);
        setPlan(null); setDecisions({});
      }
      return;
    }

    // ── Text path (CSV, pasted statements, etc.) ──
    const texts = await Promise.all(others.map(f => f.text()));
    const merged = [text, ...texts].filter(Boolean).join('\n');
    setText(merged);
    parseText(merged);
    setPlan(null); setDecisions({});
  }

  function parseText(t) {
    const f = detectImportFormat(t);
    setFormat(f);
    if (f === 'hcsc') {
      const rows = parseHcscStatement(t, { producerState: settings?.ownerState || 'TX' });
      const { summary, anomalies } = analyzeHcscRows(rows);
      // Page-2 summary parsing pulls bucket-level YTD with chargebacks
      // (the -$12 Group YTD class of finding that detail-only parsing
      // would miss). chargebackPayloads is what we'll write on commit.
      const summaryDoc = parseHcscSummary(t);
      const chargebackPayloads = summaryToChargebackUpserts(summaryDoc, {
        ownerId: currentUser?.id,
        carrier: 'BCBS (HCSC)',
      });
      // Hoist chargebacks into the anomaly drawer so the agent sees
      // them inline next to ZERO_COMMISSION / CANCELLATION / etc.
      chargebackPayloads.forEach(cb => {
        anomalies.push({
          kind: 'CHARGEBACK',
          severity: 'red',
          policy: null,
          insured: cb.bucket,
          message: `${cb.amount < 0 ? `-$${Math.abs(cb.amount).toFixed(2)}` : `$${cb.amount.toFixed(2)}`} YTD from ${cb.bucket}. ${cb.period_amount === 0 ? 'Inherited from a prior statement.' : `$${cb.period_amount.toFixed(2)} this period.`} Will write to health_chargebacks on import.`,
        });
      });
      setParsed({ format: f, rows, summaryDoc, chargebackPayloads });
      setAnalyzed({ summary, anomalies });
    } else if (f === 'healthsherpa') {
      // HealthSherpa agent application export — 188 columns of rich
      // policy / household / compliance / AOR data. The dedup matcher
      // uses issuer_assigned_policy_id as the carrier-side link so a
      // later commission-statement import (Plan Advisors / HCSC /
      // etc.) updates the SAME health_policies row instead of
      // creating a duplicate. That's the foundation of the BoB ↔
      // commission cross-reference for silent-disenrollment defense.
      const { rows, agent, summary } = parseHealthSherpaCsv(t);
      setParsed({ format: f, rows, agent, summary });
      setAnalyzed({
        summary: {
          rowCount:           summary.totalRows,
          totalCommThisPeriod:0,  // HealthSherpa export doesn't carry commission $
          totalYtdCommission: null,
          activeContracts:    summary.byStatus['Effectuated'] || 0,
          bySource:           { 'HealthSherpa': summary.totalRows },
          byPlan:             summary.byMetalLevel,
          byCarrier:          summary.byCarrier,
          // HealthSherpa-specific extras consumed by the confirmation strip:
          totalHealthMembers: summary.totalHealthMembers,
          totalGrossPremium:  summary.totalGrossPremium,
          totalSubsidy:       summary.totalSubsidy,
          totalNetPremium:    summary.totalNetPremium,
          pastDueRows:        summary.pastDueRows,
          pastDueAmount:      summary.pastDueAmount,
          gracePeriodRows:    summary.gracePeriodRows,
          dmiOutstanding:     summary.dmiOutstanding,
          sviOutstanding:     summary.sviOutstanding,
          consentMissing:     summary.consentMissing,
          nonAgentAorCount:   summary.nonAgentAorCount,
          cancellationReasons:summary.cancellationReasons,
          aepRollCount:       summary.aepRollCount,
        },
        anomalies: [
          ...rows.filter(r => r._gracePeriod).map(r => ({
            kind: 'GRACE_PERIOD', severity: 'amber',
            insured: r.client_name, policy: r._healthsherpaId,
            message: `In CMS grace period since ${r._gracePeriod}. Coverage will terminate if balance isn't cured. Past due: $${(r._pastDueAmt || 0).toFixed(2)}, current due: $${(r._currentDueAmt || 0).toFixed(2)}.`,
          })),
          ...rows.filter(r => r._pastDueAmt && r._pastDueAmt > 0 && !r._gracePeriod).map(r => ({
            kind: 'PAST_DUE', severity: 'amber',
            insured: r.client_name, policy: r._healthsherpaId,
            message: `Past due $${r._pastDueAmt.toFixed(2)}. Reach out before grace period starts.`,
          })),
          ...rows.filter(r => r._compliance.dmiOutstanding > 0 || r._compliance.sviOutstanding > 0).map(r => ({
            kind: 'CMS_DOCS', severity: 'amber',
            insured: r.client_name, policy: r._healthsherpaId,
            message: `${r._compliance.dmiOutstanding} DMI · ${r._compliance.sviOutstanding} SVI outstanding. Subsidy at risk if expired (${r._compliance.dmiExpired} DMI / ${r._compliance.sviExpired} SVI already expired).`,
          })),
          ...rows.filter(r => r._aor && agent?.name && r._aor.name.toLowerCase() !== agent.name.toLowerCase()).map(r => ({
            kind: 'AOR_LOSS', severity: 'red',
            insured: r.client_name, policy: r._healthsherpaId,
            message: `Policy AOR is "${r._aor.name}" (NPN: ${r._aor.npn || '—'}), not you. Commission ownership has moved.`,
          })),
          ...rows.filter(r => r._policyStatusNotes).map(r => ({
            kind: 'CANCELLATION', severity: 'red',
            insured: r.client_name, policy: r._healthsherpaId,
            message: `${r._planStatus}: ${r._policyStatusNotes}`,
          })),
        ],
      });
    } else if (f === 'plan_advisors') {
      // Plan Advisors aggregator commission statement. One row per
      // member-month of PMPM. Surface: total $ on the statement
      // (cross-check against footer), unique households (multi-row
      // policies), and termination flags (rows with Policy Term Date
      // populated) as cancellation anomalies for the agent's review.
      const { rows, agent, footer, summary } = parsePlanAdvisorsStatement(t);
      setParsed({ format: f, rows, agent, footer, summary });
      setAnalyzed({
        summary: {
          rowCount:           summary.totalRows,
          totalCommThisPeriod:summary.totalAmount,
          totalYtdCommission: null,
          activeContracts:    summary.uniquePolicies,
          bySource:           { 'Plan Advisors': summary.totalRows },
          byPlan:             summary.byPlanType,
          byCarrier:          summary.byCarrier,
          // Statement-level extras:
          footerTotal:        footer.totalAmount,
          footerMismatch:     summary.footerMismatch,
          householdCount:     summary.householdCount,
          uniquePolicies:     summary.uniquePolicies,
        },
        anomalies: [
          ...rows.filter(r => r.termination_date).map(r => ({
            kind: 'CANCELLATION', severity: 'red',
            insured: r.client_name, policy: r.member_id,
            message: `Policy terminated ${r.termination_date}. PMPM stops next period.`,
          })),
          ...(summary.footerMismatch ? [{
            kind: 'AUDIT_MISMATCH', severity: 'red', insured: '—',
            policy: '—',
            message: `Sum of detail rows ($${summary.totalAmount.toFixed(2)}) doesn't match the file's Total Amount ($${(footer.totalAmount || 0).toFixed(2)}). Re-download the statement before importing.`,
          }] : []),
        ],
      });
    } else if (f === 'uhc_statement') {
      // UHC commission statement. Per-line transaction ledger
      // classified into 5 actions (new / pmpm / chargeback /
      // reinstatement / adjustment). Surface chargebacks loud and
      // early — those are the rows that need retention follow-up
      // BEFORE the agent commits.
      const { rows, summary, statementDate, carrier } = parseUhcStatement(t);
      setParsed({ format: f, rows, statementDate, carrier, summary });
      setAnalyzed({
        summary: {
          rowCount:           summary.rowCount,
          totalCommThisPeriod:summary.netAmount,
          totalYtdCommission: null,
          activeContracts:    summary.byAction.new + summary.byAction.pmpm,
          bySource:           { 'UHC Statement': summary.rowCount },
          byPlan:             summary.products,
          byCarrier:          { [carrier || 'UnitedHealthcare']: summary.rowCount },
          uhc: {
            statementDate,
            ...summary.byAction,
            grossAmount:        summary.grossAmount,
            chargebackAmount:   summary.chargebackAmount,
            netAmount:          summary.netAmount,
          },
        },
        anomalies: [
          ...summary.chargebackClients.map(c => ({
            kind: 'CHARGEBACK', severity: 'red',
            insured: c.name, policy: c.member_id,
            message: `Chargeback ${c.amount < 0 ? `-$${Math.abs(c.amount).toFixed(2)}` : `$${c.amount.toFixed(2)}`}. Auto-creates retention task on commit.`,
          })),
          ...summary.newEnrollments.slice(0, 5).map(c => ({
            kind: 'NEW_ENROLLMENT', severity: 'green',
            insured: c.name, policy: c.member_id,
            message: `New enrollment: ${c.amount != null ? `$${c.amount.toFixed(2)}` : ''} first-month commission.`,
          })),
        ],
      });
    } else if (f === 'jarvis') {
      // UHC Jarvis Book of Business CSV. One row per covered member.
      // The summary breakdown feeds the preview chip; per-plan-type
      // counts surface in the header so an agent sees how their book
      // splits across IFP / Medicare / ancillary at a glance.
      const { rows, agent, summary } = parseJarvisCsv(t);
      setParsed({ format: f, rows, agent });
      setAnalyzed({
        summary: {
          rowCount:           summary.total,
          totalCommThisPeriod:0,     // Jarvis doesn't carry commission $
          totalYtdCommission: null,
          activeContracts:    summary.byStatus.active || 0,
          bySource:           { 'Jarvis · UHC': summary.total },
          byPlan:             summary.byPlanType,
          byCarrier:          { 'UHC / Golden Rule': summary.total },
        },
        // Surface terminated rows as cancellation anomalies so the
        // agent sees who dropped before the import lands them in the
        // book as active members. (UHC marks terms with planStatus=T.)
        anomalies: rows
          .filter(r => r.status === 'cancelled' || r.status === 'lapsed')
          .map(r => ({
            kind: r.status === 'lapsed' ? 'DROPPED' : 'CANCELLATION',
            severity: r.status === 'lapsed' ? 'amber' : 'red',
            insured: r.client_name,
            policy:  r._productRaw,
            message: `Jarvis flagged this member as ${r._statusRaw} (${r.status}).`,
          })),
      });
    } else {
      const { rows } = parseGenericCsv(t);
      setParsed({ format: f, rows });
      setAnalyzed({
        summary: {
          rowCount: rows.length,
          totalCommThisPeriod: rows.reduce((s, r) => s + (Number(r.monthly_commission) || 0), 0),
          totalYtdCommission:  null,
          activeContracts: rows.length,
          bySource: {},
          byPlan:   rows.reduce((acc, r) => { if (r.plan_name) acc[r.plan_name] = (acc[r.plan_name] || 0) + 1; return acc; }, {}),
          byCarrier: rows.reduce((acc, r) => { if (r.carrier) acc[r.carrier] = (acc[r.carrier] || 0) + 1; return acc; }, {}),
        },
        anomalies: rows
          .filter(r => r.termination_date)
          .map(r => ({ kind: 'CANCELLATION', severity: 'red', insured: r.client_name, policy: r.member_id, message: `Terminated ${r.termination_date}.` })),
      });
    }
  }

  // Plan the import — runs matching across all parsed rows + the
  // owner's existing roster and produces the Duplicate Prevention
  // Dashboard. No DB writes; the agent can re-plan or cancel.
  async function runPlan() {
    if (!currentUser?.id || !parsed?.rows?.length) return;
    setPlanning(true);
    try {
      const ctx = await loadOwnerMatcherContext(currentUser.id);
      const p = planImport({
        ownerId: currentUser.id,
        parsedRows: parsed.rows,
        format: parsed.format,
        ctx,
      });
      setPlan(p);
      // Default decision on any flagged duplicates is "skip".
      const d = {};
      (p.duplicates || []).forEach(it => { d[it.rowIndex] = 'skip'; });
      setDecisions(d);
    } catch (err) {
      showToast(`Plan failed: ${err.message || err}`);
    } finally {
      setPlanning(false);
    }
  }

  // Commit the plan — runs every action through the planner,
  // creates the import_runs row + per-row import_run_items, fires
  // audit_logs for every client touched.
  async function commitImport() {
    if (!currentUser?.id) return;
    // UHC commission statements take a different path — they don't
    // create clients/policies, they record transactions against
    // existing policies. Route to commitUhcStatement, skip the
    // duplicate-prevention planner entirely.
    if (parsed?.format === 'uhc_statement') {
      setImporting(true);
      try {
        const result = await commitUhcStatement({
          supabase,
          ownerId: currentUser.id,
          parsed,
          fileName,
          currentUser,
        });
        const c = result.counts;
        showToast(`UHC statement: ${c.written} tx · ${c.linked} linked to policies · ${c.chargebacks} chargeback${c.chargebacks === 1 ? '' : 's'} · ${c.lossEvents} loss event${c.lossEvents === 1 ? '' : 's'} filed${c.unresolved ? ` · ${c.unresolved} unmatched` : ''}.`);
        if (result.errors.length > 0) {
          // eslint-disable-next-line no-console
          console.warn('[uhc commit] partial errors:', result.errors);
        }
        setText(''); setParsed(null); setAnalyzed(null); setFormat(null);
        setPlan(null); setDecisions({}); setFileName(null);
        onImported();
      } catch (err) {
        showToast(`UHC commit failed: ${err.message || err}`, 'error');
      } finally {
        setImporting(false);
      }
      return;
    }
    if (!plan) {
      // Auto-plan on first commit click so the agent can still
      // "Import" without explicitly opening the dashboard.
      await runPlan();
      return;
    }
    setImporting(true);
    try {
      const result = await plannerCommitImport({
        ownerId: currentUser.id,
        currentUser,
        plan,
        fileName,
        format: parsed.format,
        sourceCarrier: parsed.format === 'hcsc' ? 'BCBS (HCSC)'
                     : parsed.format === 'jarvis' ? 'UHC (Jarvis)'
                     : parsed.format === 'healthsherpa' ? 'HealthSherpa'
                     : null,
        decisions,
      });

      // HCSC chargeback bucket persistence — same as before. The
      // planner doesn't own this path because chargebacks live in
      // their own table, not on the client/policy chain.
      let chargebacksWritten = 0;
      if (parsed.format === 'hcsc' && parsed.chargebackPayloads?.length) {
        try {
          const { data, error } = await supabase
            .from('health_chargebacks')
            .upsert(parsed.chargebackPayloads, { onConflict: 'owner_id,statement_ref,bucket,period_ending' })
            .select();
          if (!error) chargebacksWritten = data?.length || 0;
        } catch { /* swallow — chargebacks are advisory */ }
      }

      const c = result.counts;
      const cbNote = chargebacksWritten ? ` · ${chargebacksWritten} chargeback${chargebacksWritten === 1 ? '' : 's'} logged` : '';
      showToast(`Import committed: ${c.newClients} new · ${c.updatedClients} updated · ${c.newPolicies} new polic${c.newPolicies === 1 ? 'y' : 'ies'}${c.skipped ? ` · ${c.skipped} skipped` : ''}${c.failed ? ` · ${c.failed} failed` : ''}${cbNote}.`);

      // Reset.
      setText(''); setParsed(null); setAnalyzed(null); setFormat(null);
      setPlan(null); setDecisions({}); setFileName(null);
      onImported();
    } catch (err) {
      showToast(`Commit failed: ${err.message || err}`);
    } finally {
      setImporting(false);
    }
  }

  const fmt$ = (n) => '$' + (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

  return (
    <div className="bg-white border-2 border-slate-300 p-4">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">Import</div>
          <div className="text-base font-black text-slate-900 uppercase tracking-tight mt-0.5">Drag-drop client list or statement</div>
          <div className="text-xs text-slate-500 mt-0.5">Auto-detects HCSC (BCBS IL/MT/NM/OK/TX), HealthSherpa, Plan Advisors, UHC / Jarvis, or generic CSV. PDFs are text-extracted in your browser — no upload, no AI cost.</div>
        </div>
        {format && <span className={`text-[0.6rem] font-black uppercase tracking-wider px-2 py-1 ${format === 'hcsc' ? 'bg-blue-100 text-blue-800' : format === 'healthsherpa' ? 'bg-cyan-100 text-cyan-800' : format === 'healthsherpa_legacy' ? 'bg-slate-100 text-slate-600' : format === 'jarvis' ? 'bg-teal-100 text-teal-800' : format === 'plan_advisors' ? 'bg-emerald-100 text-emerald-800' : format === 'uhc_statement' ? 'bg-violet-100 text-violet-800' : 'bg-slate-100 text-slate-700'}`}>{format === 'jarvis' ? 'Jarvis · UHC' : format === 'plan_advisors' ? 'Plan Advisors' : format === 'healthsherpa' ? 'HealthSherpa · BoB' : format === 'uhc_statement' ? 'UHC · Commission' : format}</span>}
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); ingestFiles(e.dataTransfer.files); }}
        className={`border-4 border-dashed ${dragOver ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 bg-slate-50'} p-10 text-center transition-colors`}
      >
        <div className="text-3xl">{dragOver ? '' : ''}</div>
        <div className="text-base md:text-lg font-black text-slate-800 uppercase tracking-tight mt-2">
          {dragOver ? 'Drop to import' : 'Drag & drop client list or carrier statement here'}
        </div>
        <div className="text-xs text-slate-500 mt-1">CSV · TSV · TXT · PDF · multiple files · auto-detects HCSC / HealthSherpa / UHC PDF</div>
        <label className="mt-4 inline-block bg-slate-900 hover:bg-black text-white text-xs font-black uppercase tracking-tight px-4 py-2 cursor-pointer">
          or choose files
          <input
            type="file" multiple accept=".csv,.tsv,.txt,.pdf,application/pdf"
            onChange={(e) => ingestFiles(e.target.files)}
            className="hidden"
          />
        </label>
      </div>

      <textarea
        value={text}
        onChange={(e) => { setText(e.target.value); parseText(e.target.value); }}
        placeholder="Paste statement rows or client-list CSV here…"
        rows={6}
        className="mt-3 w-full font-mono text-xs border border-slate-300 px-3 py-2"
      />

      {analyzed && parsed?.rows?.length > 0 && (
        <div className="mt-3 space-y-2">
          {/* Statement header chip — only shown for HCSC where the page-2
              summary parsed successfully. Confirms statement ref, period
              ending, and producer back to the agent. */}
          {parsed.format === 'hcsc' && parsed.summaryDoc && (parsed.summaryDoc.statementRef || parsed.summaryDoc.periodEnding) && (
            <div className="border-2 border-blue-300 bg-blue-50 px-3 py-2 text-xs text-blue-900 flex items-center gap-3 flex-wrap">
              <strong className="uppercase tracking-wider text-[0.55rem] font-black text-blue-700">HCSC Statement</strong>
              {parsed.summaryDoc.statementRef && <span><strong>Ref:</strong> {parsed.summaryDoc.statementRef}</span>}
              {parsed.summaryDoc.periodEnding && <span><strong>Period ending:</strong> {parsed.summaryDoc.periodEnding}</span>}
              {parsed.summaryDoc.producer && <span><strong>Producer:</strong> {parsed.summaryDoc.producer}</span>}
              {parsed.chargebackPayloads?.length > 0 && <span className="ml-auto bg-red-200 text-red-900 text-[0.55rem] font-black uppercase tracking-wider px-2 py-0.5">{parsed.chargebackPayloads.length} chargeback{parsed.chargebackPayloads.length === 1 ? '' : 's'}</span>}
            </div>
          )}

          {/* Jarvis confirmation strip — confirms agent identity +
              UHC product mix so the agent eyeballs that they pulled
              the right book before committing the import. */}
          {parsed.format === 'jarvis' && parsed.agent && (
            <div className="border-2 border-teal-300 bg-teal-50 px-3 py-2 text-xs text-teal-900 flex items-center gap-3 flex-wrap">
              <strong className="uppercase tracking-wider text-[0.55rem] font-black text-teal-700">Jarvis · UHC</strong>
              {parsed.agent.agentName && <span><strong>Agent:</strong> {parsed.agent.agentName}</span>}
              {parsed.agent.agentId   && <span><strong>UHC ID:</strong> {parsed.agent.agentId}</span>}
              {parsed.agent.agentNpn  && <span><strong>NPN:</strong> {parsed.agent.agentNpn}</span>}
              {Object.keys(analyzed.summary.byPlan || {}).length > 0 && (
                <span className="ml-auto flex items-center gap-1.5">
                  {Object.entries(analyzed.summary.byPlan).map(([pt, n]) => (
                    <span key={pt} className="bg-teal-200 text-teal-900 text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5">
                      {(HEALTH_PLAN_TYPE_BY_ID[pt]?.short || pt)} · {n}
                    </span>
                  ))}
                </span>
              )}
            </div>
          )}

          {/* Plan Advisors confirmation strip — agent identity +
              per-carrier roll-up so the operator eyeballs the file
              before committing. Footer mismatch is surfaced inline
              with the agent block so it's impossible to miss. */}
          {parsed.format === 'plan_advisors' && (
            <div className={`border-2 ${analyzed?.summary?.footerMismatch ? 'border-rose-400 bg-rose-50' : 'border-emerald-300 bg-emerald-50'} px-3 py-2 text-xs ${analyzed?.summary?.footerMismatch ? 'text-rose-900' : 'text-emerald-900'} flex items-center gap-3 flex-wrap`}>
              <strong className="uppercase tracking-wider text-[0.55rem] font-black">Plan Advisors · Aggregator</strong>
              {parsed.agent?.payeeName       && <span><strong>Payee:</strong> {parsed.agent.payeeName}</span>}
              {parsed.agent?.payeeNpn        && <span><strong>NPN:</strong> {parsed.agent.payeeNpn}</span>}
              {parsed.agent?.paymentDate     && <span><strong>Paid:</strong> {parsed.agent.paymentDate}</span>}
              {parsed.agent?.statementDate   && <span><strong>Statement:</strong> {parsed.agent.statementDate}</span>}
              {analyzed?.summary?.householdCount > 0 && <span><strong>Households:</strong> {analyzed.summary.householdCount}</span>}
              {parsed.footer?.totalAmount != null && (
                <span className="ml-auto">
                  <strong>File total:</strong> {fmt$(parsed.footer.totalAmount)}
                  {analyzed?.summary?.footerMismatch && (
                    <span className="ml-2 bg-rose-200 text-rose-900 text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5">
                      ≠ detail sum {fmt$(analyzed.summary.totalCommThisPeriod)}
                    </span>
                  )}
                </span>
              )}
              {Object.keys(analyzed?.summary?.byCarrier || {}).length > 0 && (
                <div className="basis-full flex items-center gap-1.5 mt-1">
                  {Object.entries(analyzed.summary.byCarrier).map(([c, n]) => (
                    <span key={c} className="bg-emerald-200 text-emerald-900 text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5">
                      {c} · {n}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* HealthSherpa confirmation strip — operator-level
              intelligence on the book: agent identity, gross / subsidy
              / net premium roll-up, household member count, AOR
              ownership, plus compliance flags (DMI/SVI outstanding,
              consent missing, grace period, past due). */}
          {parsed.format === 'healthsherpa' && (
            <div className="border-2 border-cyan-300 bg-cyan-50 px-3 py-2 text-xs text-cyan-900 flex items-start gap-3 flex-wrap">
              <div className="basis-full flex items-center gap-3 flex-wrap">
                <strong className="uppercase tracking-wider text-[0.55rem] font-black text-cyan-700">HealthSherpa · BoB</strong>
                {parsed.agent?.name    && <span><strong>Agent:</strong> {parsed.agent.name}</span>}
                {parsed.agent?.npn     && <span><strong>NPN:</strong> {parsed.agent.npn}</span>}
                {parsed.agent?.agency  && <span className="text-cyan-700"><strong>Agency:</strong> {parsed.agent.agency}</span>}
                <span className="ml-auto">
                  <strong>{analyzed.summary.rowCount}</strong> apps · <strong>{analyzed.summary.totalHealthMembers}</strong> total members
                </span>
              </div>
              <div className="basis-full flex items-center gap-2 flex-wrap">
                <span className="bg-cyan-200 text-cyan-900 text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5">
                  Gross {fmt$(analyzed.summary.totalGrossPremium)}/mo
                </span>
                <span className="bg-emerald-200 text-emerald-900 text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5">
                  Subsidy {fmt$(analyzed.summary.totalSubsidy)}/mo
                </span>
                <span className="bg-amber-200 text-amber-900 text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5">
                  Net {fmt$(analyzed.summary.totalNetPremium)}/mo
                </span>
                {analyzed.summary.aepRollCount > 0 && (
                  <span className="bg-violet-200 text-violet-900 text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5">
                    AEP renewed · {analyzed.summary.aepRollCount}
                  </span>
                )}
                {Object.entries(analyzed.summary.byCarrier || {}).slice(0, 6).map(([c, n]) => (
                  <span key={c} className="bg-white border border-cyan-300 text-cyan-800 text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5">
                    {c.split(' ')[0]} · {n}
                  </span>
                ))}
              </div>
              {(analyzed.summary.pastDueRows > 0 || analyzed.summary.gracePeriodRows > 0 ||
                analyzed.summary.dmiOutstanding > 0 || analyzed.summary.sviOutstanding > 0 ||
                analyzed.summary.consentMissing > 0 || analyzed.summary.nonAgentAorCount > 0) && (
                <div className="basis-full flex items-center gap-2 flex-wrap pt-1 border-t border-cyan-200">
                  <span className="text-[0.55rem] font-black uppercase tracking-wider text-rose-700">Operator flags:</span>
                  {analyzed.summary.gracePeriodRows > 0 && (
                    <span className="bg-rose-200 text-rose-900 text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5">
                      Grace period · {analyzed.summary.gracePeriodRows}
                    </span>
                  )}
                  {analyzed.summary.pastDueRows > 0 && (
                    <span className="bg-amber-200 text-amber-900 text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5">
                      Past due · {analyzed.summary.pastDueRows} · {fmt$(analyzed.summary.pastDueAmount)}
                    </span>
                  )}
                  {analyzed.summary.dmiOutstanding > 0 && (
                    <span className="bg-amber-200 text-amber-900 text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5">
                      DMI outstanding · {analyzed.summary.dmiOutstanding}
                    </span>
                  )}
                  {analyzed.summary.sviOutstanding > 0 && (
                    <span className="bg-amber-200 text-amber-900 text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5">
                      SVI outstanding · {analyzed.summary.sviOutstanding}
                    </span>
                  )}
                  {analyzed.summary.nonAgentAorCount > 0 && (
                    <span className="bg-rose-200 text-rose-900 text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5">
                      Lost AOR · {analyzed.summary.nonAgentAorCount}
                    </span>
                  )}
                  {analyzed.summary.consentMissing > 0 && (
                    <span className="bg-rose-200 text-rose-900 text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5">
                      Consent missing · {analyzed.summary.consentMissing}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* CPA summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="bg-slate-50 border border-slate-200 p-2"><div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Rows</div><div className="text-xl font-black">{analyzed.summary.rowCount}</div></div>
            <div className="bg-emerald-50 border border-emerald-200 p-2"><div className="text-[0.55rem] font-black uppercase tracking-wider text-emerald-700">Total comm</div><div className="text-xl font-black text-emerald-800">{fmt$(analyzed.summary.totalCommThisPeriod)}</div></div>
            <div className="bg-sky-50 border border-sky-200 p-2"><div className="text-[0.55rem] font-black uppercase tracking-wider text-sky-700">Contracts</div><div className="text-xl font-black text-sky-800">{analyzed.summary.activeContracts}</div></div>
            <div className="bg-violet-50 border border-violet-200 p-2"><div className="text-[0.55rem] font-black uppercase tracking-wider text-violet-700">Anomalies</div><div className="text-xl font-black text-violet-800">{analyzed.anomalies.length}</div></div>
          </div>

          {/* Anomalies */}
          {analyzed.anomalies.length > 0 && (
            <div className="border-l-4 border-amber-500 bg-amber-50 p-3 max-h-40 overflow-auto">
              <div className="text-[0.6rem] font-black uppercase tracking-[0.15em] text-amber-700 mb-1">⚠ {analyzed.anomalies.length} flag{analyzed.anomalies.length === 1 ? '' : 's'} a CPA would chase</div>
              <div className="space-y-1">
                {analyzed.anomalies.slice(0, 30).map((a, i) => (
                  <div key={i} className="text-xs text-amber-900"><strong className="uppercase tracking-wider text-[0.55rem] mr-1">{a.kind}</strong> {a.insured || a.policy || '—'}: {a.message}</div>
                ))}
                {analyzed.anomalies.length > 30 && <div className="text-[0.6rem] uppercase tracking-wider text-amber-600">+ {analyzed.anomalies.length - 30} more on import…</div>}
              </div>
            </div>
          )}

          {/* Preview rows (first 10) */}
          <div className="border border-slate-200 overflow-auto max-h-60">
            <table className="w-full text-xs">
              <thead className="bg-slate-100">
                <tr>
                  {(parsed.format === 'hcsc'
                    ? ['Policy #', 'Insured', 'Eff', 'Cancel', 'Contracts', 'Rate', 'Comm', 'YTD', 'Vendor']
                    : parsed.format === 'jarvis'
                    ? ['Member', 'DOB', 'Phone', 'City · State', 'Product', 'Plan Type', 'Status']
                    : parsed.format === 'plan_advisors'
                    ? ['Insured', 'Policy', 'Carrier', 'Plan', 'Payout', 'Term', 'Comm']
                    : parsed.format === 'healthsherpa'
                    ? ['Insured', 'Carrier · Plan', 'Status', 'Net', 'Subsidy', 'DMI/SVI', 'Past Due', 'AOR']
                    : ['Insured', 'Plan', 'Carrier', 'Eff', 'Renewal', 'Premium', 'Comm', 'Status']
                  ).map(h => <th key={h} className="text-left px-2 py-1 font-bold">{h}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {parsed.rows.slice(0, 10).map((r, i) =>
                  parsed.format === 'hcsc' ? (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-2 py-1 font-mono">{r.policy_number || '—'}</td>
                      <td className="px-2 py-1 font-bold">{r.insured_name || '—'}</td>
                      <td className="px-2 py-1 font-mono">{r.orig_eff_dt || '—'}</td>
                      <td className="px-2 py-1 font-mono text-red-600">{r.cancel_dt || ''}</td>
                      <td className="px-2 py-1 text-center">{r.contracts}</td>
                      <td className="px-2 py-1 text-right">{fmt$(r.comm_rate)}</td>
                      <td className="px-2 py-1 text-right font-bold text-emerald-700">{fmt$(r.comm_amt)}</td>
                      <td className="px-2 py-1 text-right">{fmt$(r.ytd_commission)}</td>
                      <td className="px-2 py-1 text-[0.55rem] uppercase tracking-wider text-slate-500">{r.source_vendor || ''}</td>
                    </tr>
                  ) : parsed.format === 'jarvis' ? (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-2 py-1 font-bold"><ClientNameLink name={r.client_name} openClientProfile={openClientProfile} className="font-bold hover:underline" fallback="—"/></td>
                      <td className="px-2 py-1 font-mono">{r.client_dob || '—'}</td>
                      <td className="px-2 py-1 font-mono">{r.client_phone || '—'}</td>
                      <td className="px-2 py-1 text-slate-600">{r._address ? `${r._address.city || '—'} · ${r._address.state || ''}` : '—'}</td>
                      <td className="px-2 py-1 text-[0.55rem] uppercase tracking-wider text-slate-600">{r._productRaw || '—'}</td>
                      <td className="px-2 py-1 text-[0.6rem] uppercase tracking-wider text-teal-700">{HEALTH_PLAN_TYPE_BY_ID[r.plan_type]?.short || r.plan_type}</td>
                      <td className={`px-2 py-1 text-[0.55rem] uppercase font-bold ${r.status === 'active' ? 'text-emerald-700' : r.status === 'cancelled' ? 'text-red-700' : 'text-amber-700'}`}>{r.status}</td>
                    </tr>
                  ) : parsed.format === 'plan_advisors' ? (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-2 py-1 font-bold"><ClientNameLink name={r.client_name} openClientProfile={openClientProfile} className="font-bold hover:underline" fallback="—"/></td>
                      <td className="px-2 py-1 font-mono text-[0.6rem]">{r.member_id || '—'}</td>
                      <td className="px-2 py-1">{r.carrier || '—'}</td>
                      <td className="px-2 py-1 text-[0.6rem] uppercase tracking-wider text-emerald-700">{HEALTH_PLAN_TYPE_BY_ID[r.plan_type]?.short || r.plan_type}</td>
                      <td className="px-2 py-1 text-[0.55rem] uppercase tracking-wider text-slate-600">{r._payoutType || '—'}</td>
                      <td className="px-2 py-1 font-mono text-red-600 text-[0.6rem]">{r.termination_date || ''}</td>
                      <td className="px-2 py-1 text-right font-bold text-emerald-700">{fmt$(r.monthly_commission)}</td>
                    </tr>
                  ) : parsed.format === 'healthsherpa' ? (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-2 py-1">
                        <div className="font-bold"><ClientNameLink name={r.client_name} openClientProfile={openClientProfile} className="font-bold hover:underline" fallback="—"/></div>
                        {r._household?.length > 1 && (
                          <div className="text-[0.55rem] uppercase tracking-wider text-slate-500">+ {r._household.length - 1} member{r._household.length === 2 ? '' : 's'}</div>
                        )}
                      </td>
                      <td className="px-2 py-1">
                        <div>{r.carrier || '—'}</div>
                        <div className="text-[0.55rem] uppercase tracking-wider text-cyan-700">{r._planName || ''} {r._metalLevel ? `· ${r._metalLevel}` : ''}</div>
                      </td>
                      <td className={`px-2 py-1 text-[0.55rem] uppercase font-bold ${r.status === 'active' ? 'text-emerald-700' : r.status === 'cancelled' ? 'text-red-700' : 'text-amber-700'}`}>
                        {r.status || '—'}
                        {r._gracePeriod && <div className="text-[0.5rem] tracking-wider text-rose-700">grace</div>}
                      </td>
                      <td className="px-2 py-1 text-right font-mono text-[0.6rem] text-amber-700">{fmt$(r._netPremium)}</td>
                      <td className="px-2 py-1 text-right font-mono text-[0.6rem] text-emerald-700">{fmt$(r._subsidy)}</td>
                      <td className="px-2 py-1 text-[0.55rem] font-mono">
                        {r._dmiOutstanding > 0 && <span className="text-amber-700">DMI·{r._dmiOutstanding} </span>}
                        {r._sviOutstanding > 0 && <span className="text-amber-700">SVI·{r._sviOutstanding}</span>}
                        {!r._dmiOutstanding && !r._sviOutstanding && <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-2 py-1 text-right font-mono text-[0.6rem]">
                        {r._pastDueAmount > 0
                          ? <span className="text-rose-700">{fmt$(r._pastDueAmount)}</span>
                          : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-2 py-1 text-[0.55rem] uppercase tracking-wider">
                        {r._aorIsOperator === false
                          ? <span className="text-rose-700 font-bold">{r._policyAorName || 'other'}</span>
                          : <span className="text-slate-500">you</span>}
                      </td>
                    </tr>
                  ) : (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-2 py-1 font-bold"><ClientNameLink name={r.client_name} openClientProfile={openClientProfile} className="font-bold hover:underline" fallback="—"/></td>
                      <td className="px-2 py-1">{r.plan_name || '—'}</td>
                      <td className="px-2 py-1">{r.carrier || '—'}</td>
                      <td className="px-2 py-1 font-mono">{r.effective_date || '—'}</td>
                      <td className="px-2 py-1 font-mono">{r.renewal_date || '—'}</td>
                      <td className="px-2 py-1 text-right">{r.monthly_premium || '—'}</td>
                      <td className="px-2 py-1 text-right text-emerald-700">{r.monthly_commission || '—'}</td>
                      <td className="px-2 py-1 text-[0.55rem] uppercase">{r.status}</td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
            {parsed.rows.length > 10 && <div className="px-2 py-1 text-[0.6rem] uppercase tracking-wider text-slate-500 bg-slate-50">+ {parsed.rows.length - 10} more rows hidden in preview</div>}
          </div>

          {/* Duplicate Prevention Dashboard — the agent reviews
              what's a new client vs an update vs a flagged duplicate
              BEFORE anything writes. Auto-populated on first
              "Preview duplicate check" click. */}
          {plan && (
            <div className="border-2 border-slate-800 bg-white">
              <div className="bg-slate-900 px-4 py-3 flex items-baseline justify-between flex-wrap gap-2">
                <div>
                  <div className="text-slate-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">Duplicate Prevention Dashboard</div>
                  <div className="text-white text-base font-black uppercase tracking-tight mt-0.5">Review before commit</div>
                </div>
                <div className="text-[0.6rem] uppercase tracking-wider text-slate-400">
                  {plan.stats.totalRows} row{plan.stats.totalRows === 1 ? '' : 's'} planned
                </div>
              </div>

              {/* Four bucket counters */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-b border-slate-200">
                <div className="bg-emerald-50 border-r border-slate-200 p-3">
                  <div className="text-[0.55rem] font-black uppercase tracking-wider text-emerald-700">New Clients</div>
                  <div className="text-2xl font-black text-emerald-900">{plan.stats.newClients}</div>
                </div>
                <div className="bg-sky-50 border-r border-slate-200 p-3">
                  <div className="text-[0.55rem] font-black uppercase tracking-wider text-sky-700">Existing Updated</div>
                  <div className="text-2xl font-black text-sky-900">{plan.stats.updates}</div>
                </div>
                <div className="bg-amber-50 border-r border-slate-200 p-3">
                  <div className="text-[0.55rem] font-black uppercase tracking-wider text-amber-700">Potential Duplicates</div>
                  <div className="text-2xl font-black text-amber-900">{plan.stats.duplicates}</div>
                </div>
                <div className="bg-rose-50 p-3">
                  <div className="text-[0.55rem] font-black uppercase tracking-wider text-rose-700">Failed</div>
                  <div className="text-2xl font-black text-rose-900">{plan.stats.failed}</div>
                </div>
              </div>

              {/* Updates list — what existing clients will change */}
              {plan.updates.length > 0 && (
                <details open className="border-b border-slate-200">
                  <summary className="cursor-pointer px-4 py-2 bg-sky-50 text-[0.6rem] font-black uppercase tracking-wider text-sky-800">
                    {plan.updates.length} existing client{plan.updates.length === 1 ? '' : 's'} will be updated
                  </summary>
                  <div className="max-h-56 overflow-auto divide-y divide-slate-100">
                    {plan.updates.slice(0, 50).map((u, i) => (
                      <div key={i} className="px-4 py-2 text-xs flex items-baseline gap-3 flex-wrap">
                        <span className="font-mono text-slate-400 text-[0.55rem]">#{u.rowIndex}</span>
                        <span className="font-bold text-slate-900 truncate">{u.candidate.name}</span>
                        <span className="text-[0.55rem] font-black uppercase tracking-wider bg-sky-100 text-sky-800 px-1.5 py-0.5">
                          matched on {u.matchedOn?.replace(/_/g, ' ')}
                        </span>
                        {Object.keys(u.changes || {}).length > 0 && (
                          <span className="text-slate-600">
                            {Object.entries(u.changes).map(([k, [, v]]) => (
                              <span key={k} className="mr-2"><strong>{k}</strong>: <span className="font-mono">{String(v).slice(0, 40)}</span></span>
                            ))}
                          </span>
                        )}
                      </div>
                    ))}
                    {plan.updates.length > 50 && <div className="px-4 py-2 text-[0.6rem] uppercase tracking-wider text-slate-500 bg-slate-50">+ {plan.updates.length - 50} more</div>}
                  </div>
                </details>
              )}

              {/* Duplicates — agent picks Skip / Create New for each */}
              {plan.duplicates.length > 0 && (
                <details open className="border-b border-slate-200">
                  <summary className="cursor-pointer px-4 py-2 bg-amber-50 text-[0.6rem] font-black uppercase tracking-wider text-amber-800">
                    {plan.duplicates.length} potential duplicate{plan.duplicates.length === 1 ? '' : 's'} — pick one per row
                  </summary>
                  <div className="max-h-72 overflow-auto divide-y divide-slate-100">
                    {plan.duplicates.map((d, i) => (
                      <div key={i} className="px-4 py-2 text-xs flex items-center gap-3 flex-wrap">
                        <span className="font-mono text-slate-400 text-[0.55rem]">#{d.rowIndex}</span>
                        <span className="font-bold text-slate-900 truncate flex-1 min-w-[140px]">{d.candidate.name}</span>
                        <span className="text-[0.55rem] uppercase tracking-wider text-amber-700">
                          {d.kind === 'intra_batch' ? `same as row #${d.sameAsRowIndex}` : 'multi-match'}
                        </span>
                        <div className="inline-flex border border-slate-300 text-[0.55rem] font-black uppercase tracking-wider">
                          {['skip', 'create_new'].map(opt => (
                            <button
                              key={opt}
                              onClick={() => setDecisions(prev => ({ ...prev, [d.rowIndex]: opt }))}
                              className={`px-2 py-1 ${(decisions[d.rowIndex] || 'skip') === opt ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
                            >
                              {opt === 'create_new' ? 'Create New' : 'Skip'}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {/* New clients list — quick eyeball */}
              {plan.newClients.length > 0 && (
                <details className="border-b border-slate-200">
                  <summary className="cursor-pointer px-4 py-2 bg-emerald-50 text-[0.6rem] font-black uppercase tracking-wider text-emerald-800">
                    {plan.newClients.length} new client{plan.newClients.length === 1 ? '' : 's'} will be created
                  </summary>
                  <div className="max-h-56 overflow-auto divide-y divide-slate-100">
                    {plan.newClients.slice(0, 50).map((n, i) => (
                      <div key={i} className="px-4 py-2 text-xs flex items-baseline gap-3 flex-wrap">
                        <span className="font-mono text-slate-400 text-[0.55rem]">#{n.rowIndex}</span>
                        <span className="font-bold text-slate-900 truncate">{n.candidate.name}</span>
                        <span className="text-slate-500 text-[0.6rem]">{[n.candidate.phone, n.candidate.email, n.candidate.dob].filter(Boolean).join(' · ')}</span>
                      </div>
                    ))}
                    {plan.newClients.length > 50 && <div className="px-4 py-2 text-[0.6rem] uppercase tracking-wider text-slate-500 bg-slate-50">+ {plan.newClients.length - 50} more</div>}
                  </div>
                </details>
              )}

              {/* Failed rows — read-only */}
              {plan.failed.length > 0 && (
                <details className="border-b border-slate-200">
                  <summary className="cursor-pointer px-4 py-2 bg-rose-50 text-[0.6rem] font-black uppercase tracking-wider text-rose-800">
                    {plan.failed.length} row{plan.failed.length === 1 ? '' : 's'} can't be imported
                  </summary>
                  <div className="max-h-40 overflow-auto divide-y divide-slate-100">
                    {plan.failed.slice(0, 30).map((f, i) => (
                      <div key={i} className="px-4 py-2 text-xs">
                        <span className="font-mono text-slate-400 text-[0.55rem]">#{f.rowIndex}</span>
                        <span className="text-rose-700 ml-2">{f.reason}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button onClick={() => { setText(''); setParsed(null); setAnalyzed(null); setFormat(null); setPlan(null); setDecisions({}); setFileName(null); }}
              className="text-xs font-bold uppercase text-slate-500 hover:text-slate-900">Clear</button>
            {/* UHC statements skip the duplicate-prevention planner —
                they update existing policies, they don't create them. */}
            {!plan && parsed?.format !== 'uhc_statement' && (
              <button onClick={runPlan} disabled={planning}
                className="bg-slate-900 hover:bg-black disabled:opacity-50 text-white text-xs font-black uppercase tracking-tight px-4 py-2">
                {planning ? 'Checking…' : '🔎 Preview duplicate check'}
              </button>
            )}
            <button onClick={commitImport} disabled={importing}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-black uppercase tracking-tight px-4 py-2">
              {importing ? 'Importing…' : parsed?.format === 'uhc_statement' ? `Commit ${parsed.rows.length} transaction${parsed.rows.length === 1 ? '' : 's'}` : plan ? `Commit ${plan.stats.totalRows} row${plan.stats.totalRows === 1 ? '' : 's'}` : `Import ${parsed.rows.length} row${parsed.rows.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Address heuristics for the legacy "address in notes" migration helper.
// The Jarvis BoB parser (pre-044) dumped a ` · `-joined address string into
// notes because there was nowhere structured to put it. This pair of
// helpers detects those legacy values + parses them back into the new
// columns. Used by the Edit Policy modal's "Move address from notes →"
// button so the agent can opt-in per row rather than risking a mass-
// backfill on notes that contain legitimate narrative text.
function looksLikeAddress(s) {
  if (!s) return false;
  const str = String(s);
  // Has a 5-digit ZIP somewhere AND a state-like ", XX " or ", XX$" pattern.
  const hasZip   = /\b\d{5}(-\d{4})?\b/.test(str);
  const hasState = /,\s*[A-Z]{2}\b/.test(str);
  return hasZip && hasState;
}
function parseLooseAddress(s) {
  const empty = { address1: '', address2: '', city: '', state: '', zip: '', remainder: '' };
  if (!s) return empty;
  const str = String(s).trim();
  // Common forms we've seen:
  //   "123 Main St · Apt 4 · CITY, ST 12345"
  //   "123 Main St, CITY, ST 12345"
  //   "123 Main St Apt 4 CITY, ST 12345"
  // Strategy: pull the ZIP first, then the state before it, then the
  // city before the state, then everything before the city is the
  // street + unit (split on " · " or " Apt ").
  const zipMatch = str.match(/\b(\d{5}(?:-\d{4})?)\b/);
  if (!zipMatch) return { ...empty, remainder: str };
  const zip = zipMatch[1];
  const beforeZip = str.slice(0, zipMatch.index).replace(/[,\s]+$/, '');
  const stateMatch = beforeZip.match(/,\s*([A-Z]{2})\s*$/);
  if (!stateMatch) return { ...empty, zip, remainder: str };
  const state = stateMatch[1];
  const beforeState = beforeZip.slice(0, stateMatch.index).replace(/[,\s]+$/, '');
  // The city sits between the last separator (· or , or " before state")
  // and the state. Try comma first, then ` · `.
  let city = '';
  let street = '';
  const lastComma = beforeState.lastIndexOf(',');
  const lastDot   = beforeState.lastIndexOf(' · ');
  const splitAt   = Math.max(lastComma, lastDot);
  if (splitAt >= 0) {
    city   = beforeState.slice(splitAt + (lastComma > lastDot ? 1 : 3)).trim();
    street = beforeState.slice(0, splitAt).trim();
  } else {
    // Fallback: assume last whitespace-separated word is the city.
    const parts = beforeState.split(/\s+/);
    city   = parts.pop() || '';
    street = parts.join(' ');
  }
  // Pull out an Apt / Unit / # segment from the street.
  let address1 = street;
  let address2 = '';
  const aptMatch = street.match(/^(.*?)(?:\s*·\s*|\s+)(?:Apt|Apartment|Unit|Ste|Suite|#)\s*([\w-]+)\s*(.*)$/i);
  if (aptMatch) {
    address1 = (aptMatch[1] + ' ' + (aptMatch[3] || '')).trim().replace(/[·\s]+$/, '');
    address2 = `Apt ${aptMatch[2]}`;
  } else {
    // Sometimes the parser put the unit between separators only.
    const dotMatch = street.match(/^(.*?)\s*·\s*(Apt\s*[\w-]+)\s*·?\s*(.*)$/i);
    if (dotMatch) {
      address1 = (dotMatch[1] + ' ' + (dotMatch[3] || '')).trim().replace(/[·\s]+$/, '');
      address2 = dotMatch[2];
    }
  }
  return { address1, address2, city, state, zip, remainder: '' };
}

// ════════════════════════════════════════════════════════════════════
// HEALTH COMMISSIONS VIEW
//
// The Health-vertical commissions screen. Life commissions live in
// 'paycalc' / 'policies' / 'persistency' and pull from transactions —
// none of that is relevant here. This view runs entirely off
// health_policies + health_loss_events:
//
//   - Active MRR     (sum monthly_commission, status=active)
//   - Lost MRR       (sum monthly_commission on payment_stopped + cancelled events, last 90d)
//   - Subsidy MRR    (sum subsidy_amount, status=active)
//   - Carrier mix    (MRR per carrier, sortable, top N)
//   - Plan-type mix  (ACA vs Medicare vs Ancillary)
//   - Recently paid  (last 30 days, ordered by last_commission_at)
//   - Top earners    (highest monthly_commission, top 10)
// ════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════
// HEALTH LIFECYCLE BUILDER
//
// The agent-facing editor for the Health-journey SMS automations.
// The cron at /api/lifecycle/enqueue-health (12:45 UTC daily) walks
// every active health_policies row, lazy-seeds these 7 templates if
// they don't exist for the operator, and writes message_queue rows
// anchored to effective_date + offset. The send.js cron drains the
// queue every 15 minutes through the existing compliance gauntlet
// (consent, quiet hours, A2P 10DLC, wallet).
//
// This view:
//   - Loads the 7 health templates from message_templates (or shows
//     defaults if the operator hasn't been seeded yet)
//   - Lets the operator edit each body in place
//   - Shows the next 25 health-journey queued messages so they can
//     verify what's about to fire
//
// Phase 2 (deferred): Life-journey + Cross-Sell-journey editors,
// per-template A/B variants, open/response analytics, trigger-based
// campaigns (chargeback, OEP, birthday, life events).
// ════════════════════════════════════════════════════════════════════

// Mirrors api/lifecycle/_healthTouchpoints.js. Kept here as a constant
// so the UI can render defaults BEFORE the cron has seeded anything.
const HEALTH_TOUCHPOINTS_SEED = [
  { key: 'health_welcome',  label: 'Welcome',           day:   0, marketing: false, body: "Hi {first_name}, this is {agent_name} with For The People Insurance Group. Your health coverage is active. Save our number and reach out anytime you need help. Reply STOP to opt out." },
  { key: 'health_portal',   label: 'Member Portal',     day:   7, marketing: false, body: "Hi {first_name}, have you set up your member portal yet? This helps you access ID cards, doctors, prescriptions, and benefits. Reply HELP if you need assistance." },
  { key: 'health_checkin',  label: 'First Check-In',    day:  30, marketing: false, body: "Hi {first_name}, just checking in. Have you used your benefits yet? Need help finding a doctor, specialist, dentist, or prescription coverage? Reply here anytime." },
  { key: 'health_review',   label: 'Coverage Review',   day:  90, marketing: true,  body: "Hi {first_name}, many clients don't realize they may still need dental, vision, accident, or hospital coverage. Want a quick benefits review? Reply YES." },
  { key: 'health_midyear',  label: 'Mid-Year Review',   day: 180, marketing: false, body: "Hi {first_name}, it's been 6 months since your enrollment. Any changes in income, household size, address, or employer coverage? Let us know so we can keep your coverage updated." },
  { key: 'health_referral', label: 'Referral Request',  day: 270, marketing: true,  body: "Hi {first_name}, know someone who needs health insurance? We pay up to {referral_amount} for referrals that enroll. Reply with their name and number." },
  { key: 'health_annual',   label: 'Annual Review',     day: 365, marketing: false, recurring: true, body: "Hi {first_name}, it's time for your annual policy review. We can review your current plan, doctors, prescriptions, and available options. Reply REVIEW to schedule." },
];

function HealthLifecycleView({ currentUser, settings = {}, showToast = () => {}, onGotoTab = () => {} }) {
  const [templates, setTemplates] = React.useState({});  // touchpoint_key → { body, is_marketing, active, id }
  const [queue, setQueue] = React.useState([]);
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [savingKey, setSavingKey] = React.useState(null);
  const [editing, setEditing] = React.useState({});  // local body overrides before save

  const ownerId = currentUser?.id;

  const load = React.useCallback(async () => {
    if (!ownerId) return;
    setLoading(true);
    const [tplRes, qRes, sentRes] = await Promise.all([
      supabase.from('message_templates')
        .select('id, touchpoint_key, body, is_marketing, active, journey')
        .eq('owner_id', ownerId)
        .eq('journey', 'health'),
      supabase.from('message_queue')
        .select('id, client_id, touchpoint_key, scheduled_for, status, error')
        .eq('owner_id', ownerId)
        .eq('journey', 'health')
        .eq('status', 'pending')
        .order('scheduled_for', { ascending: true })
        .limit(25),
      supabase.from('message_queue')
        .select('status', { count: 'exact', head: false })
        .eq('owner_id', ownerId)
        .eq('journey', 'health'),
    ]);
    const map = {};
    (tplRes.data || []).forEach(t => { map[t.touchpoint_key] = t; });
    setTemplates(map);
    setQueue(qRes.data || []);
    const byStatus = (sentRes.data || []).reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});
    setStats({
      total:    sentRes.data?.length || 0,
      pending:  byStatus.pending  || 0,
      sent:     byStatus.sent     || 0,
      skipped:  byStatus.skipped  || 0,
      failed:   byStatus.failed   || 0,
    });
    setLoading(false);
  }, [ownerId]);
  React.useEffect(() => { load(); }, [load]);

  const bodyFor = (tp) => {
    if (editing[tp.key] != null) return editing[tp.key];
    return templates[tp.key]?.body ?? tp.body;
  };
  const isDirty = (tp) => editing[tp.key] != null && editing[tp.key] !== (templates[tp.key]?.body ?? tp.body);

  const saveTemplate = async (tp) => {
    if (!ownerId || !isDirty(tp)) return;
    setSavingKey(tp.key);
    const row = {
      owner_id:       ownerId,
      journey:        'health',
      touchpoint_key: tp.key,
      channel:        'sms',
      body:           editing[tp.key],
      is_marketing:   tp.marketing,
      active:         true,
    };
    const { error } = await supabase
      .from('message_templates')
      .upsert(row, { onConflict: 'owner_id,touchpoint_key,channel' });
    if (error) {
      showToast(`Save failed: ${error.message}`, 'error');
    } else {
      showToast(`${tp.label} template saved.`, 'success');
      setEditing(prev => { const next = { ...prev }; delete next[tp.key]; return next; });
      await load();
    }
    setSavingKey(null);
  };

  const triggerNow = async () => {
    try {
      const res = await apiFetch('/api/lifecycle/enqueue-health', { method: 'GET' });
      showToast(`Enqueue ran: ${res.newly_enqueued ?? 0} new messages scheduled.`, 'success');
      await load();
    } catch (err) {
      showToast(`Enqueue failed: ${err.message || err}`, 'error');
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-black border-l-4 border-emerald-500 px-6 py-5 shadow-2xl flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-emerald-400 text-[0.6rem] font-black uppercase tracking-[0.3em]">Health Lifecycle Engine</div>
          <div className="text-white text-xl md:text-2xl font-black mt-1.5 uppercase tracking-tight">7-touch journey for every active ACA / Medicare client</div>
          <div className="text-slate-400 text-xs mt-1.5">
            Auto-enrolls on policy effective date · daily cron at 12:45 UTC · drains every 15 minutes via send.js
          </div>
        </div>
        <button onClick={triggerNow} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase tracking-tight px-4 py-2 shadow">
          Run Enqueue Now →
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border-2 border-slate-300 bg-white">
          <SummaryTile label="Pending"  value={stats.pending}  accent="amber"/>
          <SummaryTile label="Sent"     value={stats.sent}     accent="emerald"/>
          <SummaryTile label="Skipped"  value={stats.skipped}  accent="slate"/>
          <SummaryTile label="Failed"   value={stats.failed}   accent="rose"/>
          <SummaryTile label="Total"    value={stats.total}    accent="sky"/>
        </div>
      )}

      {/* Templates */}
      <div className="space-y-2">
        {HEALTH_TOUCHPOINTS_SEED.map(tp => {
          const seeded = !!templates[tp.key];
          const dirty = isDirty(tp);
          return (
            <div key={tp.key} className="bg-white border-2 border-slate-200">
              <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-baseline justify-between flex-wrap gap-2">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-400">Day {tp.day}{tp.recurring ? ' · recurring' : ''}</span>
                  <span className="text-sm font-black text-slate-900">{tp.label}</span>
                  <span className="text-[0.55rem] font-mono text-slate-400">{tp.key}</span>
                  <span className={`text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5 ${tp.marketing ? 'bg-violet-100 text-violet-800' : 'bg-slate-100 text-slate-700'}`}>
                    {tp.marketing ? 'Marketing' : 'Care'}
                  </span>
                  {!seeded && (
                    <span className="text-[0.55rem] uppercase tracking-wider text-slate-500 italic">default (not yet seeded)</span>
                  )}
                </div>
                <button onClick={() => saveTemplate(tp)} disabled={!dirty || savingKey === tp.key}
                  className="text-[0.6rem] font-black uppercase tracking-wider px-3 py-1.5 bg-emerald-600 text-white disabled:opacity-40 hover:bg-emerald-700">
                  {savingKey === tp.key ? 'Saving…' : dirty ? 'Save' : 'Saved'}
                </button>
              </div>
              <textarea
                value={bodyFor(tp)}
                onChange={e => setEditing(prev => ({ ...prev, [tp.key]: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 text-sm font-mono border-0 resize-y focus:outline-none focus:bg-slate-50"
              />
              <div className="px-3 py-1 bg-slate-50 border-t border-slate-200 text-[0.6rem] text-slate-500">
                Merge fields: <span className="font-mono">{'{first_name}'}</span> · <span className="font-mono">{'{agent_name}'}</span> · <span className="font-mono">{'{referral_amount}'}</span> · {bodyFor(tp).length}/160 chars
              </div>
            </div>
          );
        })}
      </div>

      {/* Next 25 queued */}
      <div className="bg-white border border-slate-200">
        <div className="px-4 py-2 border-b border-slate-200">
          <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">Next pending in the health queue</div>
          <div className="text-xs text-slate-700">Drained by /api/lifecycle/send every 15 minutes</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {['Scheduled', 'Client', 'Touchpoint', 'Status'].map(h => (
                <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">Loading…</td></tr>
            ) : queue.length === 0 ? (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">Queue is empty. Click "Run Enqueue Now" or wait for the daily cron at 12:45 UTC.</td></tr>
            ) : queue.map(q => (
              <tr key={q.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 text-[0.65rem] font-mono text-slate-600 whitespace-nowrap">{new Date(q.scheduled_for).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs font-mono text-slate-400">{String(q.client_id).slice(0, 8)}…</td>
                <td className="px-3 py-2 text-xs">{q.touchpoint_key}</td>
                <td className="px-3 py-2 text-[0.55rem] font-black uppercase tracking-wider text-amber-700">{q.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// LIFE + CROSS-SELL + TRIGGER lifecycle editors
//
// These three views share the same shape as HealthLifecycleView (5
// stats tiles, template cards with inline edit, run-now button,
// pending-queue table) but differ in the journey filter they hit
// against message_templates / message_queue, the touchpoints they
// seed, and the cron endpoint the "Run now" button kicks. We don't
// refactor HealthLifecycleView in place to keep that surface stable;
// we add a shared helper + three thin wrappers below.
// ═══════════════════════════════════════════════════════════════════

const LIFE_TOUCHPOINTS_SEED = [
  { key: 'life_delivery',         label: 'Policy Delivery',        day:   0, marketing: false, body: "Hi {first_name}, congratulations on protecting your family. Your life insurance policy is active. Save our number and contact us anytime with questions." },
  { key: 'life_beneficiary',      label: 'Beneficiary Review',     day:  30, marketing: false, body: "Hi {first_name}, have you reviewed your beneficiaries recently? It's important to make sure everything is up to date. Reply REVIEW if you'd like assistance." },
  { key: 'life_family',           label: 'Family Protection',      day:  90, marketing: false, body: "Hi {first_name}, life changes fast. Have you had any changes in income, marriage, children, or home ownership since getting your policy?" },
  { key: 'life_checkup',          label: 'Policy Check-Up',        day: 180, marketing: false, body: "Hi {first_name}, just checking in. Have your protection goals changed? We can review your current coverage and make sure your family remains protected." },
  { key: 'life_referral',         label: 'Referral Request',       day: 270, marketing: true,  body: "Hi {first_name}, if you know someone who needs life insurance, we'd love to help. We pay up to {referral_amount} for referrals that become clients." },
  { key: 'life_annual',           label: 'Annual Review',          day: 365, marketing: false, recurring: true, body: "Hi {first_name}, it's your policy anniversary. Let's review your coverage, beneficiaries, and future goals to make sure your protection still fits your needs." },
  { key: 'life_future_planning',  label: 'Future Planning',        day: 540, marketing: true,  body: "Hi {first_name}, many clients use life insurance as part of a larger retirement and legacy strategy. Reply PLAN if you'd like to explore your options." },
];

const CROSS_SELL_TOUCHPOINTS_SEED = [
  { key: 'crosssell_health_to_life', label: 'Health → Life',  day:  60, marketing: true, body: "Hi {first_name}, your health coverage is settled — now's a good time to think about life insurance for your family. Reply YES for a free quote (Term, Whole Life, IUL, Mortgage Protection, or Final Expense)." },
  { key: 'crosssell_life_to_health', label: 'Life → Health',  day:  30, marketing: true, body: "Hi {first_name}, your life policy is set. Got health coverage too? We work with ACA, Private PPO, Dental, Vision, Accident, and Hospital Indemnity carriers. Reply HEALTH for a no-cost review." },
];

const TRIGGER_TEMPLATES_SEED = [
  { key: 'trigger_chargeback',      label: 'Chargeback Alert',      kind: 'chargeback',      marketing: false, body: "Hi {first_name}, your carrier flagged a payment issue on your policy. Reply or call us right away so we can keep your coverage active — we can usually fix it same-day." },
  { key: 'trigger_birthday',        label: 'Birthday',              kind: 'birthday',        marketing: false, body: "Happy birthday, {first_name}! Wishing you a great year ahead from everyone at For The People Insurance Group. Reply if you'd like a quick check-in on your coverage." },
  { key: 'trigger_oep_approaching', label: 'OEP Approaching',       kind: 'oep_approaching', marketing: false, body: "Hi {first_name}, your ACA Open Enrollment renewal is coming up. I'll review your plan options + subsidy and call you with a recommendation. Reply STOP to skip the review." },
];

function LifecycleEditorView({
  currentUser, showToast = () => {},
  journey,                     // 'health' | 'life' | 'cross_sell'
  title,                       // page title
  subtitle,                    // page subtitle
  cadenceNote,                 // strip under the headline
  accent,                      // 'emerald' | 'amber' | 'violet' | 'rose'
  seed,                        // touchpoints array
  cronPath,                    // '/api/lifecycle/enqueue-life'
  isTrigger = false,           // hides the day column for trigger templates
}) {
  const [templates, setTemplates] = React.useState({});
  const [queue, setQueue] = React.useState([]);
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [savingKey, setSavingKey] = React.useState(null);
  const [editing, setEditing] = React.useState({});
  const ownerId = currentUser?.id;

  const load = React.useCallback(async () => {
    if (!ownerId) return;
    setLoading(true);
    const [tplRes, qRes, sentRes] = await Promise.all([
      supabase.from('message_templates')
        .select('id, touchpoint_key, body, is_marketing, active, journey, trigger_kind')
        .eq('owner_id', ownerId)
        .eq('journey', journey),
      supabase.from('message_queue')
        .select('id, client_id, touchpoint_key, scheduled_for, status, error, trigger_kind')
        .eq('owner_id', ownerId)
        .eq('journey', journey)
        .eq('status', 'pending')
        .order('scheduled_for', { ascending: true })
        .limit(25),
      supabase.from('message_queue')
        .select('status', { count: 'exact', head: false })
        .eq('owner_id', ownerId)
        .eq('journey', journey),
    ]);
    const map = {};
    (tplRes.data || []).forEach(t => { map[t.touchpoint_key] = t; });
    setTemplates(map);
    setQueue(qRes.data || []);
    const byStatus = (sentRes.data || []).reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});
    setStats({
      total:    sentRes.data?.length || 0,
      pending:  byStatus.pending  || 0,
      sent:     byStatus.sent     || 0,
      skipped:  byStatus.skipped  || 0,
      failed:   byStatus.failed   || 0,
    });
    setLoading(false);
  }, [ownerId, journey]);
  React.useEffect(() => { load(); }, [load]);

  const bodyFor = (tp) => editing[tp.key] != null ? editing[tp.key] : (templates[tp.key]?.body ?? tp.body);
  const isDirty = (tp) => editing[tp.key] != null && editing[tp.key] !== (templates[tp.key]?.body ?? tp.body);

  const saveTemplate = async (tp) => {
    if (!ownerId || !isDirty(tp)) return;
    setSavingKey(tp.key);
    const row = {
      owner_id:       ownerId,
      journey,
      touchpoint_key: tp.key,
      channel:        'sms',
      body:           editing[tp.key],
      is_marketing:   tp.marketing,
      active:         true,
      ...(tp.kind ? { trigger_kind: tp.kind } : {}),
    };
    const { error } = await supabase
      .from('message_templates')
      .upsert(row, { onConflict: 'owner_id,touchpoint_key,channel' });
    if (error) {
      showToast(`Save failed: ${error.message}`, 'error');
    } else {
      showToast(`${tp.label} template saved.`, 'success');
      setEditing(prev => { const next = { ...prev }; delete next[tp.key]; return next; });
      await load();
    }
    setSavingKey(null);
  };

  const triggerNow = async () => {
    if (!cronPath) return;
    try {
      const res = await apiFetch(cronPath, { method: 'GET' });
      showToast(`Cron ran: ${res.newly_enqueued ?? res.candidates ?? 0} new messages scheduled.`, 'success');
      await load();
    } catch (err) {
      showToast(`Cron failed: ${err.message || err}`, 'error');
    }
  };

  const headerBorder =
    accent === 'amber'   ? 'border-amber-500'   :
    accent === 'violet'  ? 'border-violet-500'  :
    accent === 'rose'    ? 'border-rose-500'    :
                           'border-emerald-500';
  const accentText =
    accent === 'amber'   ? 'text-amber-400'     :
    accent === 'violet'  ? 'text-violet-400'    :
    accent === 'rose'    ? 'text-rose-400'      :
                           'text-emerald-400';
  const buttonClass =
    accent === 'amber'   ? 'bg-amber-600 hover:bg-amber-700'     :
    accent === 'violet'  ? 'bg-violet-600 hover:bg-violet-700'   :
    accent === 'rose'    ? 'bg-rose-600 hover:bg-rose-700'       :
                           'bg-emerald-600 hover:bg-emerald-700';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className={`bg-black border-l-4 ${headerBorder} px-6 py-5 shadow-2xl flex items-center justify-between gap-4 flex-wrap`}>
        <div>
          <div className={`${accentText} text-[0.6rem] font-black uppercase tracking-[0.3em]`}>{title}</div>
          <div className="text-white text-xl md:text-2xl font-black mt-1.5 uppercase tracking-tight">{subtitle}</div>
          <div className="text-slate-400 text-xs mt-1.5">{cadenceNote}</div>
        </div>
        {cronPath && (
          <button onClick={triggerNow} className={`${buttonClass} text-white text-xs font-black uppercase tracking-tight px-4 py-2 shadow`}>
            Run cron now →
          </button>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border-2 border-slate-300 bg-white">
          <SummaryTile label="Pending"  value={stats.pending}  accent="amber"/>
          <SummaryTile label="Sent"     value={stats.sent}     accent="emerald"/>
          <SummaryTile label="Skipped"  value={stats.skipped}  accent="slate"/>
          <SummaryTile label="Failed"   value={stats.failed}   accent="rose"/>
          <SummaryTile label="Total"    value={stats.total}    accent="sky"/>
        </div>
      )}

      {/* Templates */}
      <div className="space-y-2">
        {seed.map(tp => {
          const seeded = !!templates[tp.key];
          const dirty = isDirty(tp);
          return (
            <div key={tp.key} className="bg-white border-2 border-slate-200">
              <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-baseline justify-between flex-wrap gap-2">
                <div className="flex items-baseline gap-3 flex-wrap">
                  {!isTrigger && (
                    <span className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-400">Day {tp.day}{tp.recurring ? ' · recurring' : ''}</span>
                  )}
                  {isTrigger && (
                    <span className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-rose-700">Trigger</span>
                  )}
                  <span className="text-sm font-black text-slate-900">{tp.label}</span>
                  <span className="text-[0.55rem] font-mono text-slate-400">{tp.key}</span>
                  <span className={`text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5 ${tp.marketing ? 'bg-violet-100 text-violet-800' : 'bg-slate-100 text-slate-700'}`}>
                    {tp.marketing ? 'Marketing' : 'Care'}
                  </span>
                  {!seeded && (
                    <span className="text-[0.55rem] uppercase tracking-wider text-slate-500 italic">default (not yet seeded)</span>
                  )}
                </div>
                <button onClick={() => saveTemplate(tp)} disabled={!dirty || savingKey === tp.key}
                  className={`text-[0.6rem] font-black uppercase tracking-wider px-3 py-1.5 ${buttonClass} text-white disabled:opacity-40`}>
                  {savingKey === tp.key ? 'Saving…' : dirty ? 'Save' : 'Saved'}
                </button>
              </div>
              <textarea
                value={bodyFor(tp)}
                onChange={e => setEditing(prev => ({ ...prev, [tp.key]: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 text-sm font-mono border-0 resize-y focus:outline-none focus:bg-slate-50"
              />
              <div className="px-3 py-1 bg-slate-50 border-t border-slate-200 text-[0.6rem] text-slate-500">
                Merge fields: <span className="font-mono">{'{first_name}'}</span> · <span className="font-mono">{'{agent_name}'}</span> · <span className="font-mono">{'{referral_amount}'}</span> · {bodyFor(tp).length}/160 chars
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200">
        <div className="px-4 py-2 border-b border-slate-200">
          <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">Next pending in this queue</div>
          <div className="text-xs text-slate-700">Drained by /api/lifecycle/send every 15 minutes</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>{['Scheduled', 'Client', 'Touchpoint', 'Status'].map(h => (
              <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">{h}</th>
            ))}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">Loading…</td></tr>
            ) : queue.length === 0 ? (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">Queue is empty. Click "Run cron now" or wait for the daily cron.</td></tr>
            ) : queue.map(q => (
              <tr key={q.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 text-[0.65rem] font-mono text-slate-600 whitespace-nowrap">{new Date(q.scheduled_for).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs font-mono text-slate-400">{String(q.client_id).slice(0, 8)}…</td>
                <td className="px-3 py-2 text-xs">{q.touchpoint_key}</td>
                <td className="px-3 py-2 text-[0.55rem] font-black uppercase tracking-wider text-amber-700">{q.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LifeLifecycleView({ currentUser, showToast }) {
  return (
    <LifecycleEditorView
      currentUser={currentUser} showToast={showToast}
      journey="life"
      title="Life Lifecycle Engine"
      subtitle="7-touch journey for every active Term / Whole / IUL / FE client"
      cadenceNote="Auto-enrolls on policy issue date · daily cron at 13:00 UTC · drains every 15 minutes via send.js"
      accent="amber"
      seed={LIFE_TOUCHPOINTS_SEED}
      cronPath="/api/lifecycle/enqueue-life"
    />
  );
}

function CrossSellLifecycleView({ currentUser, showToast }) {
  return (
    <LifecycleEditorView
      currentUser={currentUser} showToast={showToast}
      journey="cross_sell"
      title="Cross-Sell Engine"
      subtitle="Health → Life @ 60d · Life → Health @ 30d"
      cadenceNote="Only enrolls clients who don't yet own a policy in the target vertical · daily cron at 13:15 UTC"
      accent="violet"
      seed={CROSS_SELL_TOUCHPOINTS_SEED}
      cronPath="/api/lifecycle/enqueue-crosssell"
    />
  );
}

function TriggerCampaignsView({ currentUser, showToast }) {
  return (
    <LifecycleEditorView
      currentUser={currentUser} showToast={showToast}
      journey="health"  // mixed — chargeback + OEP are health, birthday is life. Filter shows union via 'health'.
      title="Trigger Campaigns"
      subtitle="Event-driven SMS · chargeback · birthday · OEP approaching"
      cadenceNote="Three crons scan daily at 13:30 (chargeback) / 13:45 (birthday) / 14:00 (OEP) UTC. Each fires the moment its event lands."
      accent="rose"
      seed={TRIGGER_TEMPLATES_SEED}
      cronPath={null}  // multiple crons — no single 'run now'
      isTrigger
    />
  );
}

// ═══════════════════════════════════════════════════════════════════
// AUTOMATION MAP — first-class in-CRM view of every automation.
//
// Reads directly from the same seed constants the lifecycle editors
// consume (HEALTH_TOUCHPOINTS_SEED / LIFE_TOUCHPOINTS_SEED /
// TRIGGER_TEMPLATES_SEED / CROSS_SELL_TOUCHPOINTS_SEED) so this stays
// in sync when someone edits a template.
//
// Three domains: AI systems · Life vertical · Health vertical. Each
// shows its cadence with per-touch attached leads (drawn from the
// operator's actual pipeline via the `leads` prop). Search + filter
// chips work across all three.
// ═══════════════════════════════════════════════════════════════════

const AUTOMATION_AGENTS = [
  { key: 'NOVA',    role: 'ACA + PPO nurture',    desc: 'Six-touch fear-removal ladder for new marketplace prospects: cost fear → eligibility → complexity → deadline. SMS <280 chars, email as full copy.', fires: 'Hourly cron', stop: 'Positive reply · STOP · 6-touch cap', ref: 'api/_novaAgent.js' },
  { key: 'ANCHOR',  role: 'ACA retention',        desc: 'Closes marketplace issues autonomously: document requests, failed payments, AOR transfers, plan changes. Rejects its own copy if it hallucinates dollar amounts.', fires: 'Every 15 min', stop: 'status=solved · deadline · 4-touch cap', ref: 'api/_anchorAgent.js' },
  { key: 'RETAIN',  role: 'Life retention',       desc: 'Pre-empts lapse: NSF recovery, draft pre-reminders, grace-period urgency, win-back. Adapts tone by payment-risk score; escalates above 60 to a human intervention call.', fires: 'Daily 14:00 UTC', stop: 'Grace period · reinstatement · human handoff', ref: 'api/_retainAgent.js' },
  { key: 'CONSENT', role: 'SOA + form chase',     desc: 'Chases Medicare SOA (CMS MED-136) and ACA consent-to-assist forms until the client signs. Touch 1 immediately, then 24h / 48h / 96h.', fires: 'On form creation', stop: 'Signed · STOP · 4-touch cap → agent call', ref: 'api/_consentAgent.js' },
  { key: 'ENROLL',  role: 'Medicare enrollment',  desc: 'Assists AEP re-shop, orchestrates SOA capture, deflects senior-service scams. Never recommends a plan; SOA mandatory before any plan discussion.', fires: 'On demand · workflow', stop: 'TPMO disclaimer required on every touch', ref: 'api/_enrollAgent.js' },
  { key: 'GUIDE',   role: 'In-app teaching',      desc: "The floating AI launcher. Answers questions about the operator's current tab. Not autonomous; answers only when asked. WHY first, HOW second, NEXT third.", fires: 'User prompt only', stop: 'Never sends — chat only', ref: 'api/_guideAgent.js' },
];

function AutomationMapView({ leads = [], onGotoTab = () => {} }) {
  const [search, setSearch]         = React.useState('');
  const [filter, setFilter]         = React.useState('all'); // 'all' | 'ai' | 'life' | 'health'
  const [openIds, setOpenIds]       = React.useState({});    // stringified id → true (touch cards)
  const [openLeads, setOpenLeads]   = React.useState({});    // leadId → true

  const q = search.trim().toLowerCase();

  // Split the operator's book by vertical using the same predicate
  // HealthShell does so the counts here match what the sidebar shows.
  const lifeLeads   = React.useMemo(() => (leads || []).filter(l => (l.vertical || 'life') === 'life'),   [leads]);
  const healthLeads = React.useMemo(() => (leads || []).filter(l => (l.vertical || 'life') !== 'life'),   [leads]);

  // Attachment: which touchpoint each lead is currently sitting on,
  // inferred from lead.stage. Active clients land on the drip; every
  // other stage stays in the lifecycle timeline above the cadence.
  const cadenceAttach = React.useCallback((dom, touchDay) => {
    const pool = dom === 'life' ? lifeLeads : healthLeads;
    return pool.filter(l => {
      // Active clients receive the drip; approximate day-since-issue
      // from lastContactedAt or statusChangedAt if we have it.
      if (l.status !== 'closed_won' && l.stage !== 'client_active') return false;
      // Assign every active client to day 0 by default when we don't
      // have a real issue_date; touchpoints beyond day 0 stay empty
      // in demo mode (the cron would populate over time in prod).
      const anchor = l.statusChangedAt ? new Date(l.statusChangedAt).getTime() : Date.now();
      const daysAgo = Math.max(0, Math.floor((Date.now() - anchor) / 86400000));
      // Assign each lead to the LATEST touchpoint whose day threshold
      // they've crossed. Approximates the actual queue's "current
      // step" for a client mid-drip.
      const cadence = dom === 'life' ? LIFE_TOUCHPOINTS_SEED : HEALTH_TOUCHPOINTS_SEED;
      let latestIdx = 0;
      cadence.forEach((tp, i) => { if (daysAgo >= tp.day) latestIdx = i; });
      return cadence[latestIdx]?.day === touchDay;
    });
  }, [lifeLeads, healthLeads]);

  // AI agent attachment — read lead.assigned_ai directly if present.
  const agentAttach = React.useCallback((agentKey) => {
    return (leads || []).filter(l => l.assigned_ai === agentKey);
  }, [leads]);

  const matchLead = React.useCallback((lead) => {
    if (!q) return true;
    return (lead.name || '').toLowerCase().includes(q)
        || (lead.phone || '').toLowerCase().includes(q)
        || (lead.assigned_ai || '').toLowerCase().includes(q);
  }, [q]);

  const domainVisible = (dom) => filter === 'all' || filter === dom;

  const toggle = (id, setState) => setState(s => ({ ...s, [id]: !s[id] }));

  return (
    <div className="max-w-none mx-auto space-y-5">
      {/* Header */}
      <div className="bg-slate-900 border-l-4 border-teal-500 px-6 py-5">
        <div className="text-teal-300 text-[0.6rem] font-black uppercase tracking-[0.3em]">Internal reference</div>
        <div className="text-white text-xl md:text-2xl font-black mt-1.5 uppercase tracking-tight">Automation map</div>
        <div className="text-slate-400 text-xs mt-1.5">Every AI agent · every SMS drip · every trigger. Reads the same seed constants the live editors write to, so it stays in sync when a template changes.</div>
      </div>

      {/* Controls */}
      <div className="bg-white border border-slate-200 p-3 flex items-center gap-3 flex-wrap sticky top-2 z-30">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search a lead by name, phone, or agent…"
          className="flex-1 min-w-[200px] border border-slate-300 px-3 py-2 text-sm rounded"
        />
        <div className="flex gap-1 text-[0.6rem] font-black uppercase tracking-wider">
          {[
            { id: 'all',    label: 'All'    },
            { id: 'ai',     label: 'AI'     },
            { id: 'life',   label: 'Life'   },
            { id: 'health', label: 'Health' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-2 border transition-colors ${
                filter === f.id
                  ? (f.id === 'life'   ? 'bg-rose-700 text-white border-rose-700'
                     : f.id === 'health' ? 'bg-teal-700 text-white border-teal-700'
                     : f.id === 'ai'    ? 'bg-violet-700 text-white border-violet-700'
                     : 'bg-slate-900 text-white border-slate-900')
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* ═══ AI systems ═══════════════════════════════════════════ */}
      {domainVisible('ai') && (
        <section className="space-y-3">
          <div className="border-t-2 border-violet-700 pt-3">
            <div className="text-[0.55rem] font-black uppercase tracking-[0.22em] text-slate-400">Domain</div>
            <h2 className="text-2xl font-black tracking-tight text-violet-800 mt-1">AI systems</h2>
            <p className="text-xs text-slate-600 mt-1 max-w-2xl">Six named agents share one adapter layer (<code className="font-mono text-[11px] bg-slate-100 px-1">api/_ai.js</code>). Every send goes through the demo seal, consent gate, quiet-hours clamp, and wallet cap before Relay.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {AUTOMATION_AGENTS.map(a => {
              const attached = agentAttach(a.key).filter(matchLead);
              return (
                <AmAgentCard
                  key={a.key}
                  agent={a}
                  leads={attached}
                  openLeads={openLeads}
                  onLeadToggle={id => toggle(id, setOpenLeads)}
                />
              );
            })}
          </div>
        </section>
      )}

      {/* ═══ Life vertical ═══════════════════════════════════════ */}
      {domainVisible('life') && (
        <AmDomainSection
          domainKey="life"
          title="Life vertical"
          accentBar="border-rose-700"
          accentText="text-rose-800"
          eyebrow="Mortgage protection · Term · IUL · Whole · Final expense"
          engineTitle="Life Lifecycle Engine"
          engineSubtitle="7-touch journey for every active Term / Whole / IUL / FE client"
          engineNote="Auto-enrolls on policy effective date · daily cron at 12:45 UTC · drains every 15 min via send.js"
          seed={LIFE_TOUCHPOINTS_SEED}
          bookSize={lifeLeads.length}
          openIds={openIds}
          onToggle={id => toggle(id, setOpenIds)}
          cadenceAttach={cadenceAttach}
          matchLead={matchLead}
          openLeads={openLeads}
          onLeadToggle={id => toggle(id, setOpenLeads)}
        />
      )}

      {/* ═══ Health vertical ══════════════════════════════════════ */}
      {domainVisible('health') && (
        <AmDomainSection
          domainKey="health"
          title="Health vertical"
          accentBar="border-teal-700"
          accentText="text-teal-800"
          eyebrow="Medicare · ACA · Ancillary"
          engineTitle="Health Lifecycle Engine"
          engineSubtitle="7-touch journey for every active ACA / Medicare client"
          engineNote="Auto-enrolls on policy effective date · daily cron at 12:45 UTC · drains every 15 min via send.js"
          seed={HEALTH_TOUCHPOINTS_SEED}
          bookSize={healthLeads.length}
          openIds={openIds}
          onToggle={id => toggle(id, setOpenIds)}
          cadenceAttach={cadenceAttach}
          matchLead={matchLead}
          openLeads={openLeads}
          onLeadToggle={id => toggle(id, setOpenLeads)}
        />
      )}

      {/* ═══ Triggers + cross-sell ═══════════════════════════════ */}
      {(domainVisible('life') || domainVisible('health')) && (
        <section className="space-y-3">
          <div className="border-t-2 border-slate-400 pt-3">
            <div className="text-[0.55rem] font-black uppercase tracking-[0.22em] text-slate-400">Event-driven</div>
            <h2 className="text-2xl font-black tracking-tight text-slate-800 mt-1">Trigger &amp; cross-sell campaigns</h2>
            <p className="text-xs text-slate-600 mt-1 max-w-2xl">Fires when the CRM detects a specific signal — a chargeback landing, a birthday matching, an OEP window opening, or a cross-sell eligibility.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[...TRIGGER_TEMPLATES_SEED, ...CROSS_SELL_TOUCHPOINTS_SEED].map((tp, i) => (
              <AmTouchCard
                key={tp.key}
                touch={tp}
                idPrefix={`trig-${i}`}
                isOpen={openIds[`trig-${i}`]}
                onToggle={() => toggle(`trig-${i}`, setOpenIds)}
                attached={[]}
                openLeads={openLeads}
                onLeadToggle={id => toggle(id, setOpenLeads)}
                showDay={typeof tp.day === 'number'}
              />
            ))}
          </div>
        </section>
      )}

      <div className="text-[0.55rem] uppercase tracking-wider text-slate-400 text-center py-4">
        Templates pulled from HEALTH_TOUCHPOINTS_SEED · LIFE_TOUCHPOINTS_SEED · TRIGGER_TEMPLATES_SEED · CROSS_SELL_TOUCHPOINTS_SEED
      </div>
    </div>
  );
}

// One AI agent card.
function AmAgentCard({ agent, leads, openLeads, onLeadToggle }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="bg-white border border-slate-200 p-4">
      <div className="h-1 w-8 bg-violet-700 mb-3"/>
      <div className="text-lg font-black text-slate-900">{agent.key}</div>
      <div className="text-[0.6rem] font-black uppercase tracking-[0.15em] text-violet-700 mt-0.5">{agent.role}</div>
      <p className="text-xs text-slate-600 mt-2 leading-snug">{agent.desc}</p>
      <dl className="mt-3 pt-2 border-t border-slate-100 text-[0.65rem] leading-relaxed">
        <div className="flex gap-2"><dt className="text-slate-400 uppercase tracking-wider min-w-[38px]">Fires</dt><dd className="text-slate-700">{agent.fires}</dd></div>
        <div className="flex gap-2"><dt className="text-slate-400 uppercase tracking-wider min-w-[38px]">Stop</dt><dd className="text-slate-700">{agent.stop}</dd></div>
        <div className="flex gap-2"><dt className="text-slate-400 uppercase tracking-wider min-w-[38px]">Ref</dt><dd className="text-slate-500 font-mono">{agent.ref}</dd></div>
      </dl>
      {leads.length > 0 && (
        <button
          onClick={() => setOpen(o => !o)}
          className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between text-[0.6rem] font-black uppercase tracking-wider text-slate-500 hover:text-slate-900 w-full"
        >
          <span>Attached leads</span>
          <span className="text-violet-700">{leads.length} {open ? '▾' : '▸'}</span>
        </button>
      )}
      {open && leads.length > 0 && (
        <div className="mt-2 space-y-1">
          {leads.map(l => (
            <AmLeadRow key={l.id} lead={l} isOpen={openLeads[l.id]} onToggle={() => onLeadToggle(l.id)}/>
          ))}
        </div>
      )}
    </div>
  );
}

// Domain section (Life or Health) — engine strip + cadence list.
function AmDomainSection({
  domainKey, title, accentBar, accentText, eyebrow,
  engineTitle, engineSubtitle, engineNote, seed, bookSize,
  cadenceAttach, matchLead, openIds, onToggle,
  openLeads, onLeadToggle,
}) {
  return (
    <section className="space-y-3">
      <div className={`border-t-2 ${accentBar} pt-3`}>
        <div className="text-[0.55rem] font-black uppercase tracking-[0.22em] text-slate-400">Domain</div>
        <h2 className={`text-2xl font-black tracking-tight ${accentText} mt-1`}>{title}</h2>
        <p className="text-xs text-slate-600 mt-1 max-w-2xl">{eyebrow} · {bookSize} lead{bookSize === 1 ? '' : 's'} in book</p>
      </div>
      <div className="bg-slate-900 px-6 py-4">
        <div className={`text-[0.55rem] font-black uppercase tracking-[0.22em] ${domainKey === 'life' ? 'text-rose-300' : 'text-teal-300'}`}>{engineTitle}</div>
        <div className="text-white text-base md:text-lg font-black tracking-tight mt-1">{engineSubtitle}</div>
        <div className="text-slate-400 text-[11px] font-mono mt-1">{engineNote}</div>
      </div>
      <div className="space-y-2">
        {seed.map((tp, i) => {
          const idKey = `${domainKey}-${i}`;
          const attached = cadenceAttach(domainKey, tp.day).filter(matchLead);
          return (
            <AmTouchCard
              key={tp.key}
              touch={tp}
              idPrefix={idKey}
              isOpen={openIds[idKey]}
              onToggle={() => onToggle(idKey)}
              attached={attached}
              openLeads={openLeads}
              onLeadToggle={onLeadToggle}
              showDay={true}
              domainKey={domainKey}
            />
          );
        })}
      </div>
    </section>
  );
}

// A single touchpoint card. Matches the /life_lifecycle · /health_lifecycle
// visual so operators recognize it — day badge, key, care/marketing tag,
// SMS body with placeholder highlight, char count.
function AmTouchCard({ touch, idPrefix, isOpen, onToggle, attached, openLeads, onLeadToggle, showDay = true, domainKey }) {
  const chars = (touch.body || '').length;
  const over = chars > 160;
  const tag = touch.is_marketing || touch.marketing ? 'Marketing'
            : touch.recurring                        ? 'Recurring'
            :                                          'Care';
  const tagCls = tag === 'Marketing' ? 'bg-amber-100 text-amber-800 border-amber-300'
                : tag === 'Recurring' ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                :                       'bg-slate-100 text-slate-700 border-slate-300';
  const dayCls = domainKey === 'life'   ? 'text-rose-700'
                : domainKey === 'health' ? 'text-teal-700'
                :                          'text-slate-600';
  const merge = Array.from((touch.body || '').matchAll(/\{[^}]+\}/g)).map(m => m[0]);
  const uniqMerge = [...new Set(merge)];

  return (
    <div className="bg-white border border-slate-200">
      <div className="px-4 py-2 border-b border-slate-100 flex items-baseline gap-3 flex-wrap">
        <div className={`text-[0.55rem] font-black uppercase tracking-[0.15em] font-mono ${dayCls} min-w-[54px]`}>
          {showDay && typeof touch.day === 'number' ? `Day ${touch.day}` : 'Event'}
        </div>
        <div className="text-sm font-black text-slate-900 flex items-baseline gap-2">
          {touch.label}
          <code className="text-[10px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5">{touch.key}</code>
        </div>
        <span className={`text-[9px] font-black uppercase tracking-wider border px-1.5 py-0.5 ${tagCls}`}>{tag}</span>
        <span className={`ml-auto text-[10px] font-mono ${over ? 'text-rose-600' : 'text-slate-400'}`}>{chars}/160</span>
      </div>
      <div className="px-4 py-3 bg-slate-50 text-sm text-slate-800 leading-snug italic">
        {(touch.body || '').split(/(\{[^}]+\})/).map((part, i) =>
          /^\{[^}]+\}$/.test(part)
            ? <span key={i} className="not-italic font-mono text-[12px] text-slate-500">{part}</span>
            : <React.Fragment key={i}>{part}</React.Fragment>
        )}
      </div>
      <div className="px-4 py-1.5 flex items-center gap-3 flex-wrap text-[10px] font-mono text-slate-400">
        <span>Merge: {uniqMerge.length ? uniqMerge.join(' · ') : '—'}</span>
      </div>
      {attached.length > 0 && (
        <div className="border-t border-slate-100">
          <button
            onClick={onToggle}
            className="w-full flex items-center justify-between px-4 py-2 text-[0.6rem] font-black uppercase tracking-wider text-slate-500 hover:text-slate-900"
          >
            <span>Currently scheduled</span>
            <span className={domainKey === 'life' ? 'text-rose-700' : domainKey === 'health' ? 'text-teal-700' : 'text-slate-700'}>
              {attached.length} {isOpen ? '▾' : '▸'}
            </span>
          </button>
          {isOpen && (
            <div className="px-4 pb-3 space-y-1">
              {attached.map(l => (
                <AmLeadRow key={l.id} lead={l} isOpen={openLeads[l.id]} onToggle={() => onLeadToggle(l.id)}/>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// A single lead row — click to expand product / phone / stage / next.
function AmLeadRow({ lead, isOpen, onToggle }) {
  return (
    <div className="border border-slate-200">
      <button
        onClick={onToggle}
        className="w-full flex items-baseline justify-between gap-3 px-3 py-2 text-left hover:bg-slate-50"
      >
        <span className="text-sm font-bold text-slate-900 truncate">{lead.name || '(no name)'}</span>
        <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 font-mono">{lead.assigned_ai || lead.ai || '—'}</span>
      </button>
      {isOpen && (
        <div className="border-t border-slate-100 bg-slate-50 px-3 py-2 text-[11px] text-slate-700 space-y-0.5">
          {lead.phone      && <div><span className="text-slate-400 uppercase tracking-wider">Phone</span> <span className="font-mono">{lead.phone}</span></div>}
          {lead.leadType   && <div><span className="text-slate-400 uppercase tracking-wider">Product</span> {lead.leadType}</div>}
          {lead.stage      && <div><span className="text-slate-400 uppercase tracking-wider">Stage</span> {lead.stage.replace(/_/g, ' ')}</div>}
          {lead.next_action && <div><span className="text-slate-400 uppercase tracking-wider">Next</span> {lead.next_action}</div>}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// AI WEBINAR BUILDER — Medicare-focused event funnel generator.
//
// Event type is the single source of truth. Educational events and
// sales/marketing events are legally different animals under CMS
// rules (Medicare Marketing Guidelines § 40.7 educational vs § 30
// marketing), and the cascade below turns that split into concrete
// UI copy, disclosures, cadence gates, and what the AI is allowed
// to generate — so a rules mismatch can't slip through unless the
// operator hand-edits the payload after generation, which the
// server-side publish check refuses.
//
// Post-generation, artifacts flow into the existing stack:
//   · registrants  → clients table (dedup by phone / email)
//   · reminders    → message_queue (opt-in gated, quiet-hours safe)
//   · bookings     → calendar + soa_v2_records at booking time
//   · consents     → hash-chained audit log
// ═══════════════════════════════════════════════════════════════════

const WEBINAR_RULES = {
  edu: {
    label:    'Educational',
    subtitle: '"Medicare 101" style — plain-English education. No plan-specific benefits, no enrollment. Widest audience, softest ask.',
    // CMS §40.7 baseline. Rules render Y/N; Ns block generation of
    // plan-specific slide content + disclosure copy.
    rules: [
      { ok: true,  text: 'Advertised explicitly as educational — banner auto-added to landing + deck' },
      { ok: true,  text: 'Meals allowed · business cards allowed' },
      { ok: true,  text: 'May schedule future 1-on-1 appointments and collect booking interest' },
      { ok: false, text: 'NO plan-specific benefits, premiums, or comparisons' },
      { ok: false, text: 'NO enrollment activity or sales pressure on-air' },
      { ok: false, text: 'NO immediately-following sales event at the same venue' },
    ],
    // Landing preview
    landing: {
      kicker: 'FREE-TO-ATTEND EDUCATIONAL EVENT · NO-COST · NO OBLIGATION',
      title:  'Turning 65? Understand your Medicare choices in one clear hour.',
      sub:    'A plain-English walkthrough of Parts A, B, C and D, what enrollment windows actually matter, and the questions to ask before you choose anything. Educational only — no specific plans will be discussed and nothing will be sold.',
      cta:    'Save my seat →',
    },
    disclosure: 'This is an educational event. No plan-specific benefits will be discussed and no enrollment will take place. Not affiliated with or endorsed by the U.S. Government, the Centers for Medicare & Medicaid Services, or the federal Medicare program. Accommodations for persons with special needs available — call (480) 555-0192 (TTY 711).',
    deck:   '14 slides · "Medicare, plainly" — parts A–D, timelines, myths, Q&A. Zero plan names, zero benefit claims.',
    script: 'Word-for-word open with required disclosures in the first minute · Q&A guardrails: plan-specific questions deflect to a booked 1-on-1.',
    contactOptional: { ok: true, text: 'Contact info optional to attend (sales events also cannot require it)' },
    kickerTone: 'amber',
  },
  sales: {
    label:    'Sales / Marketing',
    subtitle: 'Plan-specific — used inside AEP. Carrier reporting required. Stricter rules, harder CTA.',
    rules: [
      { ok: true,  text: 'Plan-specific benefits + comparisons allowed' },
      { ok: true,  text: 'Reported to carriers before the event — filing checklist auto-generated' },
      { ok: true,  text: 'TPMO + plan-count disclosure in first minute — scripted in' },
      { ok: false, text: 'NO meals (light snacks only) · NO "free" language on advertising' },
      { ok: false, text: 'Cannot require contact info to attend' },
      { ok: false, text: 'NO claims of government affiliation or "best plan" superlatives' },
    ],
    landing: {
      kicker: 'MEDICARE PLAN OPTIONS EVENT · NO-COST TO ATTEND',
      title:  'AEP is coming — review 2027 Medicare Advantage options for Maricopa County.',
      sub:    'A licensed agent will present specific plan options available in your area for the upcoming year — premiums, networks, and drug coverage — and how to compare them for your situation.',
      cta:    'Register — no obligation →',
    },
    disclosure: 'We do not offer every plan available in your area. Currently we represent 6 organizations which offer 41 products in your area. Please contact Medicare.gov, 1-800-MEDICARE, or your local State Health Insurance Assistance Program (SHIP) to get information on all of your options. Not affiliated with or endorsed by the U.S. Government, CMS, or the federal Medicare program. Attendance does not obligate enrollment. Accommodations available — call (480) 555-0192 (TTY 711). Event reported to participating carriers.',
    deck:   '16 slides · plan-specific: 2027 options, premiums, networks, drug tiers — every claim traced to filed plan documents.',
    script: 'Word-for-word open including full TPMO + plan-count line in the first minute · plan-benefit claims must match filed materials; superlatives blocked.',
    contactOptional: { ok: false, text: 'Cannot require contact info to attend — registration marked optional' },
    kickerTone: 'teal',
  },
};

const WEBINAR_TOPICS = [
  'Turning 65: your Medicare decisions, explained',
  'AEP prep: what to check before Oct 15',
  'Medigap vs. Medicare Advantage — the honest tradeoffs',
  'Part D drug coverage — how to avoid the coverage-gap surprise',
  'Retiring before 65: bridging to Medicare',
  'Custom topic…',
];

const WEBINAR_AUDIENCES = [
  'Turning 65 in next 6 months (T65)',
  'Existing book — annual review invite',
  'Adult children helping parents decide',
  'Recently disenrolled from an employer plan',
];

function WebinarBuilderView({ currentUser, settings = {}, showToast = () => {}, onGotoTab = () => {} }) {
  const [eventType, setEventType] = React.useState('edu');
  const [topic,     setTopic]     = React.useState(WEBINAR_TOPICS[0]);
  const [audience,  setAudience]  = React.useState(WEBINAR_AUDIENCES[0]);
  const [when,      setWhen]      = React.useState('Thu, Aug 6 · 6:00 PM MST · Zoom');
  const [host,      setHost]      = React.useState(() => {
    const name = settings?.ownerName || currentUser?.name || 'Maria Santos';
    const npn  = settings?.ownerProdNum || '18834412';
    return `${name} · ${settings?.businessName || 'Sunbelt Senior Insurance'} · NPN ${npn}`;
  });

  const rules = WEBINAR_RULES[eventType];
  const isEdu = eventType === 'edu';

  function fakeGenerate() {
    showToast('Generation runs server-side in the live build — prohibited-language re-check gates publish.');
  }

  return (
    <div className="max-w-none mx-auto space-y-4">
      {/* Compliance-gate demo banner — matches the pattern on every
          other Medicare surface (SOA, ENROLL, AUDIT). */}
      <div className="border border-amber-300 bg-amber-50 px-4 py-2 text-[11px] text-amber-900 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-amber-500 inline-block"/>
        <span className="font-mono uppercase tracking-wider text-[10px] text-amber-700">Sample data · template preview</span>
        <span>Medicare compliance rails active. Pending compliance-attorney sign-off before go-live — same gate as every other Medicare surface.</span>
      </div>

      {/* Hero */}
      <div className="bg-black border-l-4 border-teal-500 px-6 py-5 flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="text-teal-300 text-[0.6rem] font-black uppercase tracking-[0.3em]">Medicare · event funnel</div>
          <div className="text-white text-xl md:text-2xl font-black uppercase tracking-tight mt-1.5">AI Webinar Builder</div>
          <div className="text-slate-400 text-xs mt-1.5 max-w-2xl">
            Pick the event type, tell the AI your topic + audience — it builds the whole funnel: landing page, deck, script, reminder cadence, and the attended / no-show branches. Event type is the single source of truth: it decides what the AI is allowed to generate.
          </div>
        </div>
        <div className="flex gap-2">
          <button className="bg-slate-800 hover:bg-slate-700 text-white text-xs font-black uppercase tracking-tight px-3 py-2">My events</button>
          <button onClick={fakeGenerate} className="bg-teal-600 hover:bg-teal-700 text-white text-xs font-black uppercase tracking-tight px-3 py-2">⚡ Generate funnel</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4">
        {/* LEFT — controls */}
        <div className="space-y-4">
          {/* 1. Event type + rule cascade */}
          <div className="bg-white border border-slate-200 p-4">
            <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-500 mb-3">
              1 · Event type <span className="text-teal-700">— cascades everything</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'edu',   ...WEBINAR_RULES.edu },
                { id: 'sales', ...WEBINAR_RULES.sales },
              ].map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setEventType(t.id)}
                  className={`border p-3 text-left transition-colors ${eventType === t.id ? 'border-teal-600 bg-teal-50' : 'border-slate-200 bg-white hover:border-slate-400'}`}
                >
                  <div className="text-sm font-black text-slate-900">{t.label}</div>
                  <div className="text-[10px] text-slate-500 mt-1 leading-snug">{t.subtitle}</div>
                </button>
              ))}
            </div>
            <div className="mt-3 border border-slate-200 bg-slate-50 p-3">
              <div className="text-[9px] font-black uppercase tracking-[0.14em] text-teal-700 mb-2">
                CMS rules for this event type — enforced, not suggested
              </div>
              <ul className="space-y-1">
                {rules.rules.map((r, i) => (
                  <li key={i} className="flex gap-2 text-[11.5px] text-slate-800 leading-snug">
                    <span className={`shrink-0 font-mono font-bold ${r.ok ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {r.ok ? '✓' : '✕'}
                    </span>
                    <span>{r.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* 2. AI inputs */}
          <div className="bg-white border border-slate-200 p-4">
            <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-500 mb-3">
              2 · Tell the AI
            </div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Topic</label>
            <select value={topic} onChange={e => setTopic(e.target.value)} className="w-full border border-slate-300 px-2 py-1.5 text-sm bg-white mb-3">
              {WEBINAR_TOPICS.map(t => <option key={t}>{t}</option>)}
            </select>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Audience</label>
            <select value={audience} onChange={e => setAudience(e.target.value)} className="w-full border border-slate-300 px-2 py-1.5 text-sm bg-white mb-3">
              {WEBINAR_AUDIENCES.map(a => <option key={a}>{a}</option>)}
            </select>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Date &amp; time</label>
            <input value={when} onChange={e => setWhen(e.target.value)} className="w-full border border-slate-300 px-2 py-1.5 text-sm mb-3"/>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Host</label>
            <input value={host} onChange={e => setHost(e.target.value)} className="w-full border border-slate-300 px-2 py-1.5 text-sm mb-3"/>
            <button onClick={fakeGenerate} className="w-full bg-teal-600 hover:bg-teal-700 text-white text-xs font-black uppercase tracking-tight px-3 py-2">⚡ Generate all assets</button>
            <div className="text-[11px] text-slate-500 mt-3 pt-2 border-t border-slate-100 leading-snug">
              The AI drafts everything; <strong className="text-amber-700">nothing publishes without your review</strong>. Prohibited language (e.g. "free", implied government affiliation, plan claims on educational events) is blocked at generation AND re-checked server-side before publish.
            </div>
          </div>

          {/* 3. Registration + consent invariants */}
          <div className="bg-white border border-slate-200 p-4">
            <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-500 mb-3">
              3 · Registration &amp; consent
            </div>
            <ul className="space-y-2">
              {[
                { ok: true, text: (
                  <>Registration form captures <strong className="text-slate-900">express written consent</strong> — exact text · timestamp · IP → hash-chained audit trail</>
                )},
                { ok: true, text: 'Reminders (SMS + email) ride that consent; quiet hours enforced at fire time' },
                { ok: rules.contactOptional.ok, text: rules.contactOptional.text },
                { ok: true, text: 'Registrants become CRM contacts, tagged to the event, deduped against the book' },
              ].map((r, i) => (
                <li key={i} className="flex gap-2 text-[11.5px] text-slate-800 leading-snug">
                  <span className={`shrink-0 font-mono font-bold ${r.ok ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {r.ok ? '✓' : '✕'}
                  </span>
                  <span>{r.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* RIGHT — preview + assets + funnel */}
        <div className="space-y-4">
          {/* Landing page preview — mock browser chrome */}
          <div className="border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 flex items-center gap-2 text-[10px] text-slate-500">
              <span className="w-2.5 h-2.5 rounded-full bg-slate-300"/>
              <span className="w-2.5 h-2.5 rounded-full bg-slate-300"/>
              <span className="w-2.5 h-2.5 rounded-full bg-slate-300"/>
              <span className="ml-1 font-mono">
                Live preview — sunbeltsenior.com/events/{isEdu ? 'medicare-101' : 'aep-plans-2027'}
              </span>
            </div>
            <div className="bg-gradient-to-b from-slate-900 to-black text-slate-100 px-6 py-8">
              <div className={`text-[9px] font-black uppercase tracking-[0.25em] mb-2 ${rules.kickerTone === 'teal' ? 'text-teal-300' : 'text-amber-300'}`}>
                {rules.landing.kicker}
              </div>
              <h3 className="font-serif text-2xl md:text-3xl font-bold leading-tight max-w-lg text-white" style={{ fontFamily: 'Georgia, serif' }}>
                {rules.landing.title}
              </h3>
              <p className="text-slate-400 text-sm mt-3 max-w-lg leading-relaxed">
                {rules.landing.sub}
              </p>
              <div className="flex gap-4 mt-4 text-xs text-slate-300 flex-wrap">
                <span>📅 <strong className="text-amber-300">{when.split('·')[0]?.trim()}</strong></span>
                <span>🕕 <strong className="text-amber-300">{when.split('·')[1]?.trim()}</strong></span>
                <span>💻 <strong className="text-amber-300">{when.split('·')[2]?.trim() || 'Live on Zoom'}</strong></span>
              </div>
              <button className="mt-5 bg-gradient-to-b from-amber-400 to-amber-500 text-slate-900 font-bold px-6 py-2.5 text-sm">
                {rules.landing.cta}
              </button>
              <div className="mt-6 pt-4 border-t border-slate-800 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-800 grid place-items-center text-amber-400 font-bold text-xs">
                  {host.split(' ').map(s => s[0]).slice(0, 2).join('')}
                </div>
                <div className="text-xs">
                  <div className="font-bold text-white">Hosted by {host.split('·')[0]?.trim()}</div>
                  <div className="text-slate-500">{host.split('·').slice(1).join(' ·').trim()}</div>
                </div>
              </div>
            </div>
            <div className="bg-slate-950 border-t border-slate-800 px-6 py-3">
              <div className="text-[9px] font-black uppercase tracking-[0.14em] text-amber-400 mb-1.5">
                Required disclosures — auto-attached, not editable
              </div>
              <div className="text-[10px] text-slate-500 leading-relaxed">{rules.disclosure}</div>
            </div>
          </div>

          {/* Generated assets */}
          <div className="bg-white border border-slate-200 p-4">
            <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-500 mb-3">
              Generated assets <span className="text-teal-700">· one click, whole funnel</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
              {[
                { title: 'Slide deck',       body: rules.deck,   file: 'deck.pptx · editable' },
                { title: 'Host script',      body: rules.script, file: 'script.pdf · 9 pages' },
                { title: 'Reminder cadence', body: 'Confirm → T-24h email → T-3h email → T-30m SMS. All consent-gated, quiet-hours safe.', file: '4 touches · auto' },
                { title: 'Replay + follow-up', body: 'Replay page (7-day expiry) + attended / no-show branches below.', file: 'auto-publishes' },
              ].map(a => (
                <div key={a.title} className="border border-slate-200 p-3 bg-slate-50">
                  <div className="text-[10px] font-black uppercase tracking-wider text-teal-700">{a.title}</div>
                  <div className="text-xs text-slate-700 mt-1 leading-snug">{a.body}</div>
                  <div className="text-[10px] font-mono text-slate-400 mt-2">{a.file}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Cadence timeline */}
          <div className="bg-white border border-slate-200 p-4">
            <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-500 mb-3">
              Reminder cadence <span className="text-teal-700">· consent-gated</span>
            </div>
            <div className="flex items-stretch gap-1 overflow-x-auto pb-1">
              {[
                { kind: 'EMAIL', body: 'Instant confirm + calendar file', when: 'on registration' },
                { kind: 'EMAIL', body: '"Tomorrow — what we’ll cover"',  when: 'T-24 hours' },
                { kind: 'EMAIL', body: '"Starting soon + your link"',       when: 'T-3 hours' },
                { kind: 'SMS',   body: '"We’re live in 30 min — link"',   when: 'T-30 min · opted-in only' },
              ].map((s, i, all) => (
                <React.Fragment key={i}>
                  <div className="shrink-0 w-40 border border-slate-200 bg-slate-50 p-2.5">
                    <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 ${s.kind === 'EMAIL' ? 'bg-sky-100 text-sky-800' : 'bg-amber-100 text-amber-800'}`}>
                      {s.kind}
                    </span>
                    <div className="text-[11.5px] text-slate-800 font-semibold mt-1.5 leading-snug">{s.body}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{s.when}</div>
                  </div>
                  {i < all.length - 1 && <div className="shrink-0 self-center text-slate-300 text-xl leading-none px-1">→</div>}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Post-event branches */}
          <div className="bg-white border border-slate-200 p-4">
            <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-500 mb-3">
              After the event <span className="text-teal-700">· the funnel&#39;s actual job</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="border border-slate-200 bg-emerald-50/30 p-3">
                <div className="text-[10px] font-black uppercase tracking-wider text-emerald-800">Attended · watched 20+ min</div>
                <ul className="text-[11.5px] text-slate-700 leading-relaxed mt-2 space-y-1 list-disc pl-4">
                  <li>Thank-you email + replay link (7 days)</li>
                  <li>CTA: <strong>book a no-cost 1-on-1 review</strong> — booking flow captures SOA digitally</li>
                  <li>SOA cooling-period rule enforced by the scheduler (books outside the window automatically; CMS exceptions handled)</li>
                  <li>No booking in 4 days → one value email, then into standard nurture</li>
                </ul>
              </div>
              <div className="border border-slate-200 bg-amber-50/30 p-3">
                <div className="text-[10px] font-black uppercase tracking-wider text-amber-800">No-show / partial</div>
                <ul className="text-[11.5px] text-slate-700 leading-relaxed mt-2 space-y-1 list-disc pl-4">
                  <li>"Sorry we missed you" + replay (email-first)</li>
                  <li>Day 2: top-3-takeaways email + soft booking CTA</li>
                  <li>Day 5: last replay reminder — then hard stop, into long-term nurture</li>
                  <li>Watched replay 20+ min → auto-moves to Attended branch</li>
                </ul>
              </div>
            </div>
            <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-2 mt-3 leading-snug">
              Bookings land on the agent&#39;s calendar; SOA + consent artifacts attach to the contact; every touch passes the gates at fire time. <strong className="text-amber-700">Nothing in the follow-up discusses plan benefits</strong> — that conversation happens in the SOA&#39;d appointment.
            </div>
          </div>

          {/* Analytics funnel */}
          <div className="bg-white border border-slate-200 p-4">
            <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-500 mb-3">
              Event analytics <span className="text-teal-700">· the numbers that matter</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
              {[
                { n: '214',  l: 'Registered',           tone: 'text-slate-900' },
                { n: '121',  l: 'Attended · 57%',       tone: 'text-slate-900' },
                { n: '88',   l: '20+ min engaged',      tone: 'text-slate-900' },
                { n: '34',   l: 'Reviews booked',       tone: 'text-teal-700' },
                { n: '31',   l: 'SOA captured',         tone: 'text-emerald-700' },
                { n: 'idle', l: 'Enrollments — AEP',    tone: 'text-slate-500' },
              ].map(s => (
                <div key={s.l} className="border border-slate-200 bg-slate-50 p-2.5 text-center">
                  <div className={`text-lg font-black tabular-nums font-mono ${s.tone}`}>{s.n}</div>
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 mt-0.5">{s.l}</div>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-teal-700 text-center pt-3 border-t border-slate-100 mt-3">
              Registered → attended → engaged → booked → SOA — same flow-viz component as the Automation Map, fed by event state.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// LIFE POLICY CONSERVATION ENGINE
//
// VA-facing daily queue for working carrier conservation reports.
// Upload an MOO lapse report / Americo pending-lapse / Transamerica
// returned-mail / chargeback statement → AI parses every row →
// cases land in conservation_cases → VA works the queue with one-
// click SMS / call / note / escalate / mark-saved actions.
//
// Daily cron at /api/conservation/scan-followups advances cases
// per their issue-type cadence (critical lapse: D0 D1 D2 D3 D5 D7;
// returned-mail: D0 D2 D5; etc), auto-sends follow-up texts, and
// auto-escalates day-5 critical cases to the licensed agent.
// ═══════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════
// LIFE COMMISSION INTELLIGENCE
//
// Upload a Mutual of Omaha / Americo / Transamerica / F&G commission
// statement PDF → Claude reads the SUMMARY + DETAILS sheets → preview
// the parsed rows + producer roll-up → one-click commit that:
//
//   1. Writes a commission_statements row (vertical='life')
//   2. Inserts one commission_transactions row per line with
//      producer_number + override_amount + override_to (team leader)
//   3. Upserts clients by policy_number (the primary join key)
//   4. Opens a conservation_cases row for every chargeback so the
//      retention VA queue picks it up
//
// Plus a Producer Mapping admin panel where the operator wires
// producer_number → CRM user + team. Override calculations run at
// commit time using each producer's team config (Cam Team: 100% of
// comm_amt; Harrison Team: 5% of comm_premium; etc).
// ═══════════════════════════════════════════════════════════════════
function LifeCommissionsView({ currentUser, settings = {}, showToast = () => {}, openClientProfile = () => {} }) {
  const [view, setView] = React.useState('downline');  // 'downline' | 'import' | 'producers' | 'history'
  return (
    <div className="space-y-4">
      <div className="bg-black border-l-4 border-amber-500 px-6 py-5 shadow-2xl flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-amber-400 text-[0.6rem] font-black uppercase tracking-[0.3em]">Life Commission Intelligence</div>
          <div className="text-white text-xl md:text-2xl font-black mt-1.5 uppercase tracking-tight">Mutual of Omaha · Americo · Trans · F&amp;G</div>
          <div className="text-slate-400 text-xs mt-1.5">Upload any life-carrier commission statement → AI reads it → cases land, overrides compute, chargebacks fire retention tasks.</div>
        </div>
        <div className="inline-flex bg-slate-800 border border-slate-700 p-0.5 text-xs font-bold uppercase tracking-tight">
          {[
            ['downline', 'Downline $'],
            ['import',    'Import'],
            ['producers', 'Producer Map'],
            ['history',   'Statement History'],
          ].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)}
              className={`px-3 py-1.5 ${view === k ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white'}`}>{l}</button>
          ))}
        </div>
      </div>
      {view === 'downline'  && <LifeDownlineEarningsPanel currentUser={currentUser} showToast={showToast} openClientProfile={openClientProfile}/>}
      {view === 'import'    && <LifeMooImportPanel currentUser={currentUser} showToast={showToast} settings={settings} openClientProfile={openClientProfile}/>}
      {view === 'producers' && <LifeProducerMapPanel currentUser={currentUser} showToast={showToast}/>}
      {view === 'history'   && <LifeStatementHistoryPanel currentUser={currentUser} showToast={showToast}/>}
    </div>
  );
}

// ── Downline Earnings Panel ─────────────────────────────────────
// Shows the operator's override income from every producer mapped
// to a team they lead — sourced from commission_transactions where
// override_to = currentUser.id and paid_date in the selected range.
//
// Six date-range presets plus a custom from/to picker. Selection
// persists to localStorage so the dashboard remembers what view
// the operator was last on.
//
// Tiles → producer breakdown → team breakdown → monthly trend
// chart → per-policy detail. Every section honors the active
// date range.
function LifeDownlineEarningsPanel({ currentUser, showToast = () => {}, openClientProfile = () => {} }) {
  const ownerId = currentUser?.id;

  // ── Date range state ────────────────────────────────────────
  // Persisted to localStorage so closing/reopening preserves the
  // operator's last view. Preset 'mtd' = month to date, etc.
  const [preset, setPreset] = React.useState(() => {
    try { return localStorage.getItem('life_downline:preset') || 'mtd'; }
    catch { return 'mtd'; }
  });
  const [customFrom, setCustomFrom] = React.useState(() => {
    try { return localStorage.getItem('life_downline:from') || ''; }
    catch { return ''; }
  });
  const [customTo, setCustomTo] = React.useState(() => {
    try { return localStorage.getItem('life_downline:to') || ''; }
    catch { return ''; }
  });
  React.useEffect(() => {
    try {
      localStorage.setItem('life_downline:preset', preset);
      localStorage.setItem('life_downline:from', customFrom);
      localStorage.setItem('life_downline:to',   customTo);
    } catch {}
  }, [preset, customFrom, customTo]);

  // Resolve preset → { from, to } ISO date strings.
  const { fromDate, toDate, rangeLabel } = React.useMemo(() => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    const dayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (preset === 'today') {
      return { fromDate: iso(dayStart), toDate: iso(dayStart), rangeLabel: 'Today' };
    }
    if (preset === 'mtd') {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { fromDate: iso(start), toDate: iso(dayStart), rangeLabel: 'Month-to-Date' };
    }
    if (preset === 'last_month') {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end   = new Date(today.getFullYear(), today.getMonth(), 0);
      return { fromDate: iso(start), toDate: iso(end), rangeLabel: 'Last Month' };
    }
    if (preset === 'qtd') {
      const q = Math.floor(today.getMonth() / 3);
      const start = new Date(today.getFullYear(), q * 3, 1);
      return { fromDate: iso(start), toDate: iso(dayStart), rangeLabel: 'Quarter-to-Date' };
    }
    if (preset === 'ytd') {
      const start = new Date(today.getFullYear(), 0, 1);
      return { fromDate: iso(start), toDate: iso(dayStart), rangeLabel: 'Year-to-Date' };
    }
    if (preset === 'last_12mo') {
      const start = new Date(today.getFullYear(), today.getMonth() - 11, 1);
      return { fromDate: iso(start), toDate: iso(dayStart), rangeLabel: 'Last 12 Months' };
    }
    if (preset === 'custom') {
      return {
        fromDate: customFrom || iso(new Date(today.getFullYear(), 0, 1)),
        toDate:   customTo   || iso(dayStart),
        rangeLabel: customFrom && customTo ? `${customFrom} → ${customTo}` : 'Custom',
      };
    }
    return { fromDate: iso(dayStart), toDate: iso(dayStart), rangeLabel: 'Today' };
  }, [preset, customFrom, customTo]);

  // ── Data ────────────────────────────────────────────────────
  const [rows, setRows] = React.useState([]);
  const [producers, setProducers] = React.useState({});  // producer_number → { display_name, team_id, team_name }
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!ownerId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [txRes, prodRes, teamRes] = await Promise.all([
        supabase.from('commission_transactions')
          .select('id, statement_id, policy_number, client_name, producer_number, action, amount, paid_date, override_amount, override_to, override_basis, carrier, product_label, effective_date, raw')
          .eq('owner_id', ownerId)
          .eq('override_to', ownerId)
          .gte('paid_date', fromDate)
          .lte('paid_date', toDate)
          .order('paid_date', { ascending: false })
          .limit(5000),
        supabase.from('life_producers')
          .select('producer_number, display_name, team_id')
          .eq('owner_id', ownerId),
        supabase.from('life_teams')
          .select('id, name')
          .eq('owner_id', ownerId),
      ]);
      if (cancelled) return;
      if (txRes.error) {
        // eslint-disable-next-line no-console
        console.warn('[downline] tx query failed', txRes.error.message);
      }
      setRows(txRes.data || []);
      const teamById = new Map((teamRes.data || []).map(t => [t.id, t.name]));
      const map = {};
      (prodRes.data || []).forEach(p => {
        map[p.producer_number] = {
          display_name: p.display_name,
          team_id:      p.team_id,
          team_name:    p.team_id ? (teamById.get(p.team_id) || 'Unknown Team') : null,
        };
      });
      setProducers(map);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [ownerId, fromDate, toDate]);

  const fmt$ = (n) => '$' + (Math.round((Number(n) || 0) * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ── Aggregations ────────────────────────────────────────────
  const stats = React.useMemo(() => {
    let total = 0;
    let cbTotal = 0;
    const producerSet = new Set();
    const teamSet = new Set();
    const policySet = new Set();
    for (const r of rows) {
      const v = Number(r.override_amount) || 0;
      total += v;
      if (v < 0) cbTotal += Math.abs(v);
      if (r.producer_number) producerSet.add(r.producer_number);
      const teamName = producers[r.producer_number]?.team_name;
      if (teamName) teamSet.add(teamName);
      if (r.policy_number) policySet.add(r.policy_number);
    }
    return {
      total,
      cbTotal,
      net: total,
      producerCount: producerSet.size,
      teamCount: teamSet.size,
      policyCount: policySet.size,
      rowCount: rows.length,
    };
  }, [rows, producers]);

  const byProducer = React.useMemo(() => {
    const map = {};
    for (const r of rows) {
      const pn = r.producer_number || '—';
      if (!map[pn]) {
        const meta = producers[pn] || {};
        map[pn] = {
          producer_number: pn,
          name:    meta.display_name || `Pending Map · ${pn}`,
          team:    meta.team_name || null,
          override: 0,
          policy_count: 0,
          gross_commission: 0,
          policies: new Set(),
        };
      }
      const slot = map[pn];
      slot.override += Number(r.override_amount) || 0;
      slot.gross_commission += Number(r.amount) || 0;
      if (r.policy_number) slot.policies.add(r.policy_number);
    }
    return Object.values(map).map(s => ({
      ...s,
      policy_count: s.policies.size,
    })).sort((a, b) => b.override - a.override);
  }, [rows, producers]);

  const byTeam = React.useMemo(() => {
    const map = {};
    for (const r of rows) {
      const teamName = producers[r.producer_number]?.team_name || 'Unassigned';
      if (!map[teamName]) map[teamName] = { team: teamName, override: 0, policy_count: 0, producers: new Set(), policies: new Set() };
      const slot = map[teamName];
      slot.override += Number(r.override_amount) || 0;
      if (r.producer_number) slot.producers.add(r.producer_number);
      if (r.policy_number)   slot.policies.add(r.policy_number);
    }
    return Object.values(map).map(s => ({
      ...s,
      policy_count: s.policies.size,
      producer_count: s.producers.size,
    })).sort((a, b) => b.override - a.override);
  }, [rows, producers]);

  const monthlyTrend = React.useMemo(() => {
    const map = {};
    const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    for (const r of rows) {
      if (!r.paid_date) continue;
      const key = String(r.paid_date).slice(0, 7);  // YYYY-MM
      if (!map[key]) map[key] = { month: key, override: 0, label: key };
      map[key].override += Number(r.override_amount) || 0;
    }
    return Object.values(map).map(m => {
      const [y, mm] = m.month.split('-');
      return { ...m, label: `${MONTHS[Number(mm) - 1] || mm} ${y}` };
    }).sort((a, b) => a.month.localeCompare(b.month));
  }, [rows]);

  return (
    <div className="space-y-4">
      {/* Date-range picker */}
      <div className="bg-white border-2 border-amber-300 p-3 flex items-center gap-2 flex-wrap">
        <span className="text-[0.55rem] font-black uppercase tracking-wider text-amber-700">Date range</span>
        {[
          ['today',      'Today'],
          ['mtd',        'MTD'],
          ['last_month', 'Last Month'],
          ['qtd',        'QTD'],
          ['ytd',        'YTD'],
          ['last_12mo',  'Last 12 Mo'],
          ['custom',     'Custom'],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setPreset(k)}
            className={`text-xs font-bold uppercase tracking-tight px-3 py-1.5 ${preset === k ? 'bg-amber-600 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
            {l}
          </button>
        ))}
        {preset === 'custom' && (
          <span className="flex items-center gap-2 ml-2">
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="text-xs border border-slate-300 px-2 py-1"/>
            <span className="text-slate-500">→</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="text-xs border border-slate-300 px-2 py-1"/>
          </span>
        )}
        <span className="ml-auto text-[0.65rem] uppercase tracking-wider text-slate-500">
          {rangeLabel} · <span className="font-mono">{fromDate}</span> → <span className="font-mono">{toDate}</span>
        </span>
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border-2 border-slate-300 bg-white">
        <SummaryTile label="Override $ Total"   value={fmt$(stats.total)}        accent={stats.total >= 0 ? 'emerald' : 'rose'}/>
        <SummaryTile label="Producers"          value={stats.producerCount}      accent="sky"/>
        <SummaryTile label="Teams"              value={stats.teamCount}          accent="amber"/>
        <SummaryTile label="Policies"           value={stats.policyCount}        accent="slate"/>
        <SummaryTile label="Rows"               value={stats.rowCount}           accent="violet"/>
      </div>

      {loading && <div className="text-sm italic text-slate-500">Loading downline transactions…</div>}

      {!loading && rows.length === 0 && (
        <div className="border-2 border-dashed border-slate-300 px-6 py-12 text-center text-slate-500 text-sm">
          No override transactions found in this date range. Either no downline production yet, or no producers mapped to teams you lead. Open <strong>Producer Map</strong> to assign producers to a team whose leader_user_id is you.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <>
          {/* By Team */}
          {byTeam.length > 0 && (
            <div className="bg-white border border-slate-200">
              <div className="px-4 py-2 border-b border-slate-200">
                <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">By Team</div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {['Team', 'Producers', 'Policies', 'Override $'].map(h => (
                      <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {byTeam.map(t => (
                    <tr key={t.team} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-sm font-bold">{t.team}</td>
                      <td className="px-3 py-2 text-xs tabular-nums">{t.producer_count}</td>
                      <td className="px-3 py-2 text-xs tabular-nums">{t.policy_count}</td>
                      <td className={`px-3 py-2 text-sm tabular-nums font-bold ${t.override >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmt$(t.override)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* By Producer */}
          <div className="bg-white border border-slate-200">
            <div className="px-4 py-2 border-b border-slate-200">
              <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">By Producer · Sorted by override $</div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  {['Producer', 'Team', 'Policies', 'Their Commissions', 'Your Override'].map(h => (
                    <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {byProducer.map(p => (
                  <tr key={p.producer_number} className="hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <div className="font-bold text-slate-900">{p.name}</div>
                      <div className="text-[0.6rem] font-mono text-slate-400">{p.producer_number}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">{p.team || <span className="italic text-slate-400">unassigned</span>}</td>
                    <td className="px-3 py-2 text-xs tabular-nums">{p.policy_count}</td>
                    <td className="px-3 py-2 text-xs tabular-nums text-slate-600">{fmt$(p.gross_commission)}</td>
                    <td className={`px-3 py-2 text-sm tabular-nums font-bold ${p.override >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmt$(p.override)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Monthly trend chart */}
          {monthlyTrend.length > 1 && (
            <div className="bg-white border border-slate-200 p-4">
              <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500 mb-3">Monthly Trend · Override $ paid</div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={monthlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                  <XAxis dataKey="label" tick={{ fontSize: 11 }}/>
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => fmt$(v).replace(/\.00$/, '')}/>
                  <Tooltip formatter={(v) => [fmt$(v), 'Override']}/>
                  <Bar dataKey="override" fill="#d97706"/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Per-policy detail (first 100) */}
          <div className="bg-white border border-slate-200">
            <div className="px-4 py-2 border-b border-slate-200 flex items-baseline justify-between">
              <div>
                <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">Transaction Detail</div>
                <div className="text-xs text-slate-700">Most recent first · capped at 100 rows for the table view</div>
              </div>
              <div className="text-[0.6rem] uppercase tracking-wider text-slate-500">{rows.length} total</div>
            </div>
            <div className="overflow-auto max-h-96">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    {['Paid', 'Producer', 'Policy', 'Client', 'Action', 'Commission', 'Override'].map(h => (
                      <th key={h} className="text-left px-2 py-1 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.slice(0, 100).map(r => (
                    <tr key={r.id} className={`hover:bg-slate-50 ${r.action === 'chargeback' ? 'bg-rose-50' : ''}`}>
                      <td className="px-2 py-1 font-mono text-[0.65rem] text-slate-600">{r.paid_date || '—'}</td>
                      <td className="px-2 py-1">
                        <div className="font-bold">{producers[r.producer_number]?.display_name || `Pending · ${r.producer_number}`}</div>
                        <div className="text-[0.55rem] font-mono text-slate-400">{r.producer_number}</div>
                      </td>
                      <td className="px-2 py-1 font-mono text-[0.65rem]">{r.policy_number}</td>
                      <td className="px-2 py-1"><ClientNameLink name={r.client_name} openClientProfile={openClientProfile} className="hover:underline"/></td>
                      <td className="px-2 py-1">
                        <span className={`text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5 ${
                          r.action === 'chargeback' ? 'bg-rose-200 text-rose-900' :
                          r.action === 'new'        ? 'bg-emerald-100 text-emerald-800' :
                          r.action === 'renewal'    ? 'bg-sky-100 text-sky-800' :
                          r.action === 'advance'    ? 'bg-amber-100 text-amber-800' :
                                                      'bg-slate-100 text-slate-700'
                        }`}>{r.action}</span>
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums text-slate-600">{fmt$(r.amount)}</td>
                      <td className={`px-2 py-1 text-right tabular-nums font-bold ${Number(r.override_amount) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmt$(r.override_amount)}</td>
                    </tr>
                  ))}
                  {rows.length > 100 && (
                    <tr><td colSpan={7} className="px-2 py-2 text-center text-[0.65rem] text-slate-500">+ {rows.length - 100} more rows in this range</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function LifeMooImportPanel({ currentUser, showToast, openClientProfile = () => {} }) {
  const [parsed, setParsed] = React.useState(null);
  const [parsing, setParsing] = React.useState(false);
  const [committing, setCommitting] = React.useState(false);
  const [fileName, setFileName] = React.useState(null);
  const fileRef = React.useRef(null);

  const fmt$ = (n) => '$' + (Math.round((Number(n) || 0) * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Try the deterministic Node-side parser first (free + fast +
  // unit-tested). Fall back to the LLM-vision endpoint if the
  // deterministic parser doesn't recognize the layout or throws.
  // Operator can force the LLM path by holding shift while clicking
  // the upload button (debug shortcut).
  async function tryDeterministicParse(file) {
    const pdfjs = await loadPdfJs();
    const buf = await file.arrayBuffer();
    const doc = await pdfjs.getDocument({ data: new Uint8Array(buf) }).promise;
    const pageText = [];
    for (let i = 1; i <= doc.numPages; i++) {
      const page = await doc.getPage(i);
      const content = await page.getTextContent();
      pageText.push(pageItemsToLayoutText(content?.items || []));
    }
    const res = await apiFetch('/api/life/parse-moo-deterministic', {
      method: 'POST',
      body: JSON.stringify({ pageText }),
    });
    return res; // det envelope
  }

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      showToast('Statement PDF over 20 MB — split into chapters first.', 'error');
      return;
    }
    setParsing(true);
    setParsed(null);
    setFileName(file.name);
    let parser = 'deterministic';
    try {
      // ── Fast path: deterministic Node parser ────────────────
      try {
        const det = await tryDeterministicParse(file);
        const bridged = bridgeDeterministicToLlm(det);
        if (!bridged || !bridged.rows || bridged.rows.length === 0) {
          throw new Error('deterministic parser returned no rows');
        }
        setParsed(bridged);
        const ck = det?.checksums;
        const ckMsg = ck && ck.ok === false
          ? ` · ⚠ checksum delta ${fmt$(ck.delta || 0)}`
          : ck && ck.ok === true
            ? ' · ✓ checksum'
            : '';
        showToast(`Parsed ${bridged.rows.length} rows · ${fmt$(bridged.totals?.net || 0)} net (deterministic)${ckMsg}.`, 'success');
        return;
      } catch (detErr) {
        // eslint-disable-next-line no-console
        console.warn('[moo-import] deterministic failed, falling back to LLM:', detErr?.message || detErr);
        parser = 'llm_fallback';
      }
      // ── Fallback: LLM-vision endpoint (kept unchanged) ──────
      const b64 = await fileToBase64Inline(file);
      const res = await apiFetch('/api/life/parse-moo-statement', {
        method: 'POST',
        body: JSON.stringify({ pdfBase64: b64, mimeType: file.type || 'application/pdf' }),
      });
      setParsed(res);
      showToast(`Parsed ${res.rows?.length || 0} rows · ${fmt$(res.totals?.net || 0)} net (AI fallback).`, 'success');
    } catch (err) {
      showToast(`Parse failed (${parser}): ${err.message || err}`, 'error');
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const commit = async () => {
    if (!parsed) return;
    setCommitting(true);
    try {
      const res = await apiFetch('/api/life/commit-moo-statement', {
        method: 'POST',
        body: JSON.stringify({
          statement_meta: parsed.statement_meta,
          rows:           parsed.rows,
          fileName,
        }),
      });
      showToast(
        `Committed: ${res.transactions_written} tx · ${res.clients_created} new clients · ${res.producers_new} unmapped producers · ${fmt$(res.overrides_total)} overrides · ${res.chargebacks_filed} chargeback${res.chargebacks_filed === 1 ? '' : 's'} filed.`,
        'success'
      );
      if (res.errors?.length) {
        // eslint-disable-next-line no-console
        console.warn('[life-commit] partial errors:', res.errors);
      }
      setParsed(null);
      setFileName(null);
    } catch (err) {
      showToast(`Commit failed: ${err.message || err}`, 'error');
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="bg-white border-2 border-amber-300 p-6 text-center">
        <div className="text-amber-700 text-[0.55rem] font-black uppercase tracking-[0.25em]">Step 1 · Upload the carrier statement PDF</div>
        <div className="text-slate-900 text-lg font-black mt-1">Drop your Mutual of Omaha (or other life-carrier) statement</div>
        <div className="text-slate-600 text-xs mt-2">AI reads both sheets · extracts every row · matches producers + policies before you commit.</div>
        <button onClick={() => fileRef.current?.click()} disabled={parsing}
          className="mt-4 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-xs font-black uppercase tracking-tight px-5 py-2.5 shadow">
          {parsing ? 'Reading…' : 'Choose PDF'}
        </button>
        <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden"
          onChange={e => handleFile(e.target.files?.[0])}/>
      </div>

      {parsed && (
        <>
          {/* Statement meta strip */}
          <div className="bg-white border-2 border-violet-300 px-4 py-3 flex items-baseline justify-between flex-wrap gap-3">
            <div className="flex items-baseline gap-4 flex-wrap text-xs">
              <span className="text-violet-700 text-[0.55rem] font-black uppercase tracking-[0.2em]">Statement Preview</span>
              {parsed.statement_meta?.carrier && <span><strong>Carrier:</strong> {parsed.statement_meta.carrier}</span>}
              {parsed.statement_meta?.statement_date && <span><strong>Date:</strong> {parsed.statement_meta.statement_date}</span>}
              {parsed.statement_meta?.agency_party_id && <span><strong>Party ID:</strong> {parsed.statement_meta.agency_party_id}</span>}
              {parsed.statement_meta?.agency_name && <span><strong>Agency:</strong> {parsed.statement_meta.agency_name}</span>}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => { setParsed(null); setFileName(null); }} disabled={committing}
                className="text-xs font-bold uppercase text-slate-500 hover:text-slate-900">Discard</button>
              <button onClick={commit} disabled={committing}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-black uppercase tracking-tight px-3 py-1.5">
                {committing ? 'Committing…' : `Commit ${parsed.rows?.length || 0} transactions`}
              </button>
            </div>
          </div>

          {/* Totals tiles */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border-2 border-slate-300 bg-white">
            <SummaryTile label="Rows"        value={parsed.totals?.row_count || 0}                  accent="slate"/>
            <SummaryTile label="New Sales"   value={parsed.totals?.by_action?.new || 0}             accent="emerald"/>
            <SummaryTile label="Renewals"    value={parsed.totals?.by_action?.renewal || 0}         accent="sky"/>
            <SummaryTile label="Chargebacks" value={parsed.totals?.by_action?.chargeback || 0}      accent="rose"/>
            <SummaryTile label="Net $"       value={fmt$(parsed.totals?.net || 0)}                  accent={Number(parsed.totals?.net) >= 0 ? 'emerald' : 'rose'}/>
          </div>

          {/* Producer roll-up */}
          {Object.keys(parsed.producer_summary || {}).length > 0 && (
            <div className="bg-white border border-slate-200">
              <div className="px-4 py-2 border-b border-slate-200">
                <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">By Producer</div>
                <div className="text-xs text-slate-700">Producers with no CRM mapping show as "Pending Map" after commit.</div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {['Producer #', 'Rows', 'Gross', 'CB $', 'Net'].map(h => (
                      <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {Object.entries(parsed.producer_summary).sort((a, b) => (b[1].net - a[1].net)).map(([pn, s]) => (
                    <tr key={pn} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-xs font-mono font-bold">{pn}</td>
                      <td className="px-3 py-2 text-xs tabular-nums">{s.row_count}</td>
                      <td className="px-3 py-2 text-xs tabular-nums text-emerald-700">{fmt$(s.gross)}</td>
                      <td className="px-3 py-2 text-xs tabular-nums text-rose-700">{fmt$(s.chargebacks)}</td>
                      <td className={`px-3 py-2 text-xs tabular-nums font-bold ${s.net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmt$(s.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Detail preview (first 25 rows) */}
          <div className="bg-white border border-slate-200">
            <div className="px-4 py-2 border-b border-slate-200">
              <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">First 25 detail rows</div>
            </div>
            <div className="overflow-auto max-h-96">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    {['Policy', 'Client', 'ST', 'Producer', 'Issue Date', 'Plan', 'Action', 'Premium', 'Commission'].map(h => (
                      <th key={h} className="text-left px-2 py-1 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {parsed.rows.slice(0, 25).map((r, i) => (
                    <tr key={i} className={`${r.action === 'chargeback' ? 'bg-rose-50' : ''}`}>
                      <td className="px-2 py-1 font-mono font-bold">{r.policy_number}</td>
                      <td className="px-2 py-1"><ClientNameLink name={r.client_name} openClientProfile={openClientProfile} className="hover:underline"/></td>
                      <td className="px-2 py-1 font-mono">{r.state}</td>
                      <td className="px-2 py-1 font-mono">{r.producer_number}</td>
                      <td className="px-2 py-1 font-mono text-[0.6rem] text-slate-600">{r.issue_date || '—'}</td>
                      <td className="px-2 py-1 text-[0.6rem]">{r.plan_code || '—'}</td>
                      <td className="px-2 py-1">
                        <span className={`text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5 ${
                          r.action === 'chargeback' ? 'bg-rose-200 text-rose-900' :
                          r.action === 'new'        ? 'bg-emerald-100 text-emerald-800' :
                          r.action === 'renewal'    ? 'bg-sky-100 text-sky-800' :
                          r.action === 'advance'    ? 'bg-amber-100 text-amber-800' :
                                                      'bg-slate-100 text-slate-700'
                        }`}>{r.action}</span>
                      </td>
                      <td className="px-2 py-1 text-right font-mono tabular-nums">{fmt$(r.comm_premium)}</td>
                      <td className={`px-2 py-1 text-right font-mono tabular-nums font-bold ${Number(r.comm_amount) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {fmt$(r.comm_amount)}
                      </td>
                    </tr>
                  ))}
                  {parsed.rows.length > 25 && (
                    <tr><td colSpan={9} className="px-2 py-1 text-center text-[0.6rem] text-slate-500">+ {parsed.rows.length - 25} more rows</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function LifeProducerMapPanel({ currentUser, showToast }) {
  const [producers, setProducers] = React.useState([]);
  const [teams, setTeams]         = React.useState([]);
  const [loading, setLoading]     = React.useState(true);
  const [newTeam, setNewTeam] = React.useState({ name: '', leader_name: '', override_basis: 'comm_amt', override_rate: '0' });
  const ownerId = currentUser?.id;

  const load = React.useCallback(async () => {
    if (!ownerId) return;
    setLoading(true);
    const [p, t] = await Promise.all([
      supabase.from('life_producers').select('*').eq('owner_id', ownerId).order('producer_number'),
      supabase.from('life_teams').select('*').eq('owner_id', ownerId).order('name'),
    ]);
    setProducers(p.data || []);
    setTeams(t.data || []);
    setLoading(false);
  }, [ownerId]);
  React.useEffect(() => { load(); }, [load]);

  const updateProducer = async (pn, patch) => {
    const { error } = await supabase.from('life_producers')
      .update(patch).eq('owner_id', ownerId).eq('producer_number', pn);
    if (error) showToast(`Update failed: ${error.message}`, 'error');
    else { showToast('Saved.', 'success'); load(); }
  };

  const addTeam = async () => {
    if (!newTeam.name.trim()) { showToast('Team name required.', 'error'); return; }
    const { error } = await supabase.from('life_teams').insert({
      owner_id:       ownerId,
      name:           newTeam.name.trim(),
      leader_name:    newTeam.leader_name.trim() || null,
      override_basis: newTeam.override_basis,
      override_rate:  Number(newTeam.override_rate) || 0,
    });
    if (error) showToast(`Add failed: ${error.message}`, 'error');
    else {
      setNewTeam({ name: '', leader_name: '', override_basis: 'comm_amt', override_rate: '0' });
      load();
    }
  };

  const deleteTeam = async (id) => {
    if (!(await hqConfirm({
      title: 'Delete this team?',
      body: 'Producers mapped to it will lose their team assignment.',
      confirmLabel: 'Delete team', danger: true,
    }))) return;
    await supabase.from('life_teams').delete().eq('id', id);
    load();
  };

  return (
    <div className="space-y-4">
      {/* Teams */}
      <div className="bg-white border-2 border-amber-300">
        <div className="px-4 py-2 border-b border-amber-200 bg-amber-50">
          <div className="text-amber-700 text-[0.55rem] font-black uppercase tracking-[0.2em]">Teams</div>
          <div className="text-xs text-slate-700">Override rules per team. Applied to every transaction by every producer mapped to the team.</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {['Name', 'Leader', 'Basis', 'Rate', 'Members', ''].map(h => (
                <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={6} className="px-3 py-4 text-center text-slate-400">Loading…</td></tr>}
            {!loading && teams.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-4 text-center text-slate-400">No teams yet. Add one below.</td></tr>
            )}
            {teams.map(t => {
              const members = producers.filter(p => p.team_id === t.id).length;
              return (
                <tr key={t.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-bold">{t.name}</td>
                  <td className="px-3 py-2 text-xs">{t.leader_name || '—'}</td>
                  <td className="px-3 py-2 text-[0.65rem] font-mono">{t.override_basis}</td>
                  <td className="px-3 py-2 text-xs font-mono">
                    {t.override_basis === 'flat_fee' ? `$${Number(t.override_rate).toFixed(2)}` : `${(Number(t.override_rate) * 100).toFixed(2)}%`}
                  </td>
                  <td className="px-3 py-2 text-xs tabular-nums">{members}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => deleteTeam(t.id)} className="text-[0.55rem] uppercase tracking-wider text-rose-700 hover:text-rose-900 underline">delete</button>
                  </td>
                </tr>
              );
            })}
            <tr className="bg-slate-50">
              <td className="px-3 py-2"><input value={newTeam.name} onChange={e => setNewTeam(s => ({ ...s, name: e.target.value }))} placeholder="Team name" className="text-xs border border-slate-300 px-2 py-1 w-full"/></td>
              <td className="px-3 py-2"><input value={newTeam.leader_name} onChange={e => setNewTeam(s => ({ ...s, leader_name: e.target.value }))} placeholder="Leader name" className="text-xs border border-slate-300 px-2 py-1 w-full"/></td>
              <td className="px-3 py-2">
                <select value={newTeam.override_basis} onChange={e => setNewTeam(s => ({ ...s, override_basis: e.target.value }))} className="text-xs border border-slate-300 px-2 py-1 bg-white">
                  <option value="comm_amt">% of comm $</option>
                  <option value="comm_premium">% of premium</option>
                  <option value="flat_fee">Flat $ / row</option>
                </select>
              </td>
              <td className="px-3 py-2"><input value={newTeam.override_rate} onChange={e => setNewTeam(s => ({ ...s, override_rate: e.target.value }))} placeholder={newTeam.override_basis === 'flat_fee' ? '25' : '0.05'} className="text-xs border border-slate-300 px-2 py-1 w-20 font-mono"/></td>
              <td className="px-3 py-2"/>
              <td className="px-3 py-2 text-right"><button onClick={addTeam} className="text-[0.6rem] font-black uppercase tracking-tight bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5">Add Team</button></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Producers */}
      <div className="bg-white border-2 border-slate-300">
        <div className="px-4 py-2 border-b border-slate-200">
          <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">Producer Mapping</div>
          <div className="text-xs text-slate-700">{producers.length} producer{producers.length === 1 ? '' : 's'} known. Pending Map rows auto-create on import.</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {['Producer #', 'Display Name', 'Team', 'CRM User ID', ''].map(h => (
                <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-400">Loading…</td></tr>}
            {!loading && producers.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-400">No producers mapped yet. Run an import to seed Pending Map rows.</td></tr>
            )}
            {producers.map(p => (
              <ProducerMapRow key={p.producer_number} p={p} teams={teams} onUpdate={updateProducer}/>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProducerMapRow({ p, teams, onUpdate }) {
  const [name, setName]     = React.useState(p.display_name || '');
  const [teamId, setTeamId] = React.useState(p.team_id || '');
  const [userId, setUserId] = React.useState(p.user_id || '');
  const dirty = name !== (p.display_name || '') || teamId !== (p.team_id || '') || userId !== (p.user_id || '');
  return (
    <tr className="hover:bg-slate-50">
      <td className="px-3 py-2 font-mono font-bold">{p.producer_number}</td>
      <td className="px-3 py-2">
        <input value={name} onChange={e => setName(e.target.value)} className="text-xs border border-slate-300 px-2 py-1 w-full"/>
      </td>
      <td className="px-3 py-2">
        <select value={teamId} onChange={e => setTeamId(e.target.value)} className="text-xs border border-slate-300 px-2 py-1 bg-white">
          <option value="">— no team —</option>
          {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </td>
      <td className="px-3 py-2">
        <input value={userId} onChange={e => setUserId(e.target.value)} placeholder="auth.users.id (optional)" className="text-xs border border-slate-300 px-2 py-1 w-full font-mono"/>
      </td>
      <td className="px-3 py-2 text-right">
        <button onClick={() => onUpdate(p.producer_number, { display_name: name, team_id: teamId || null, user_id: userId || null })}
          disabled={!dirty}
          className="text-[0.6rem] font-black uppercase tracking-tight bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 px-2.5 py-1">
          Save
        </button>
      </td>
    </tr>
  );
}

function LifeStatementHistoryPanel({ currentUser }) {
  const [statements, setStatements] = React.useState([]);
  const [loading, setLoading]       = React.useState(true);
  const ownerId = currentUser?.id;

  React.useEffect(() => {
    if (!ownerId) return;
    setLoading(true);
    supabase.from('commission_statements')
      .select('id, carrier, carrier_short, statement_date, total_rows, gross_amount, chargeback_amount, net_amount, source_filename, new_count, chargeback_count, created_at')
      .eq('owner_id', ownerId)
      .eq('vertical', 'life')
      .order('statement_date', { ascending: false })
      .limit(50)
      .then(({ data }) => {
        setStatements(data || []);
        setLoading(false);
      });
  }, [ownerId]);

  const fmt$ = (n) => '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div className="bg-white border border-slate-200">
      <div className="px-4 py-2 border-b border-slate-200">
        <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">Imported Life Statements</div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50">
          <tr>
            {['Date', 'Carrier', 'File', 'Rows', 'New', 'CB', 'Gross', 'CB $', 'Net'].map(h => (
              <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {loading && <tr><td colSpan={9} className="px-3 py-4 text-center text-slate-400">Loading…</td></tr>}
          {!loading && statements.length === 0 && (
            <tr><td colSpan={9} className="px-3 py-4 text-center text-slate-400">No life statements imported yet.</td></tr>
          )}
          {statements.map(s => (
            <tr key={s.id} className="hover:bg-slate-50">
              <td className="px-3 py-2 text-xs font-mono">{s.statement_date || '—'}</td>
              <td className="px-3 py-2 text-xs font-bold">{s.carrier_short || s.carrier || '—'}</td>
              <td className="px-3 py-2 text-[0.6rem] text-slate-500 max-w-[280px] truncate" title={s.source_filename || ''}>{s.source_filename || '—'}</td>
              <td className="px-3 py-2 text-xs tabular-nums">{s.total_rows}</td>
              <td className="px-3 py-2 text-xs tabular-nums text-emerald-700">{s.new_count || 0}</td>
              <td className="px-3 py-2 text-xs tabular-nums text-rose-700">{s.chargeback_count || 0}</td>
              <td className="px-3 py-2 text-xs tabular-nums">{fmt$(s.gross_amount || 0)}</td>
              <td className="px-3 py-2 text-xs tabular-nums text-rose-700">{fmt$(s.chargeback_amount || 0)}</td>
              <td className={`px-3 py-2 text-xs tabular-nums font-bold ${Number(s.net_amount) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmt$(s.net_amount || 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LifeConservationView({ currentUser, settings = {}, showToast = () => {}, openClientProfile = () => {} }) {
  const [cases,    setCases]    = React.useState([]);
  const [loading,  setLoading]  = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const [parsed,    setParsed]    = React.useState(null);     // { upload_id, rows, summary }
  const [committing, setCommitting] = React.useState(false);
  const [filter,   setFilter]   = React.useState('open');     // 'open'|'critical_today'|'mine'|'all'|'closed'
  const [activeCase, setActiveCase] = React.useState(null);
  const [actions,  setActions]    = React.useState([]);       // for activeCase
  const fileRef = React.useRef(null);

  const ownerId = currentUser?.id;

  const load = React.useCallback(async () => {
    if (!ownerId) return;
    setLoading(true);
    const { data } = await supabase
      .from('conservation_cases')
      .select('*')
      .eq('owner_id', ownerId)
      .order('priority', { ascending: true })
      .order('deadline', { ascending: true, nullsFirst: false })
      .limit(500);
    setCases(data || []);
    setLoading(false);
  }, [ownerId]);
  React.useEffect(() => { load(); }, [load]);

  // ── Filter buckets ──────────────────────────────────────────
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const visible = React.useMemo(() => {
    const isOpen = (c) => !['policy_saved', 'policy_lapsed', 'closed'].includes(c.status);
    if (filter === 'closed')         return cases.filter(c => !isOpen(c));
    if (filter === 'all')            return cases;
    if (filter === 'mine')           return cases.filter(c => isOpen(c) && c.assigned_va === ownerId);
    if (filter === 'critical_today') return cases.filter(c => isOpen(c) && c.priority === 'critical' && (!c.deadline || c.deadline <= todayStr || c.deadline === todayStr));
    return cases.filter(isOpen);                                 // 'open'
  }, [cases, filter, ownerId, todayStr]);

  const stats = React.useMemo(() => {
    const open = cases.filter(c => !['policy_saved', 'policy_lapsed', 'closed'].includes(c.status));
    return {
      open: open.length,
      critical:  open.filter(c => c.priority === 'critical').length,
      saved:     cases.filter(c => c.status === 'policy_saved').length,
      lapsed:    cases.filter(c => c.status === 'policy_lapsed').length,
      escalated: open.filter(c => c.status === 'escalated').length,
      amountAtRisk: open.reduce((s, c) => s + (Number(c.amount_due) || 0), 0),
    };
  }, [cases]);

  const fmt$ = (n) => n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ── Upload + parse a report ─────────────────────────────────
  async function fileToB64(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload  = () => {
        const s = String(r.result || '');
        const i = s.indexOf(',');
        resolve(i >= 0 ? s.slice(i + 1) : s);
      };
      r.onerror = () => reject(r.error);
      r.readAsDataURL(file);
    });
  }
  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      showToast('Report is over 20 MB — split into chapters first.', 'error');
      return;
    }
    setUploading(true);
    setParsed(null);
    try {
      const b64 = await fileToB64(file);
      const res = await apiFetch('/api/conservation/parse-report', {
        method: 'POST',
        body: JSON.stringify({
          pdfBase64: b64,
          mimeType:  file.type || 'application/pdf',
          filename:  file.name,
        }),
      });
      setParsed(res);
      showToast(`Parsed ${res.rows?.length || 0} row${(res.rows?.length || 0) === 1 ? '' : 's'} · ${Object.keys(res.summary?.byIssue || {}).join(', ')}`, 'success');
    } catch (err) {
      showToast(`Parse failed: ${err.message || err}`, 'error');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const commitParsed = async (sendFirstSms) => {
    if (!parsed) return;
    setCommitting(true);
    try {
      const res = await apiFetch('/api/conservation/commit-cases', {
        method: 'POST',
        body: JSON.stringify({
          upload_id: parsed.upload_id,
          rows:      parsed.rows,
          send_first_sms: !!sendFirstSms,
        }),
      });
      showToast(`Committed ${res.committed} case${res.committed === 1 ? '' : 's'} · ${res.matched} matched · ${res.sms_enqueued} SMS queued.`, 'success');
      setParsed(null);
      await load();
    } catch (err) {
      showToast(`Commit failed: ${err.message || err}`, 'error');
    } finally {
      setCommitting(false);
    }
  };

  // ── Open a case panel ───────────────────────────────────────
  const openCase = async (c) => {
    setActiveCase(c);
    const { data } = await supabase
      .from('conservation_actions')
      .select('*')
      .eq('case_id', c.id)
      .order('created_at', { ascending: true });
    setActions(data || []);
  };

  const setStatus = async (newStatus, note) => {
    if (!activeCase) return;
    const update = { status: newStatus };
    if (['policy_saved', 'policy_lapsed', 'closed'].includes(newStatus)) {
      update.resolved_at = new Date().toISOString();
      if (note) update.resolution_note = note;
    }
    const { error } = await supabase.from('conservation_cases').update(update).eq('id', activeCase.id);
    if (error) { showToast(`Status update failed: ${error.message}`, 'error'); return; }
    await supabase.from('conservation_actions').insert({
      owner_id: ownerId, case_id: activeCase.id,
      kind: 'status_change',
      status_from: activeCase.status, status_to: newStatus,
      message: note || `Status: ${activeCase.status} → ${newStatus}`,
      performed_by: ownerId,
    });
    setActiveCase({ ...activeCase, status: newStatus });
    await load();
    await openCase({ ...activeCase, status: newStatus });
  };

  const logNote = async (note) => {
    if (!activeCase || !note) return;
    await supabase.from('conservation_actions').insert({
      owner_id: ownerId, case_id: activeCase.id, kind: 'note', message: note, performed_by: ownerId,
    });
    await openCase(activeCase);
  };

  const logCall = async (outcome, note) => {
    if (!activeCase) return;
    await supabase.from('conservation_actions').insert({
      owner_id: ownerId, case_id: activeCase.id, kind: 'call',
      channel: 'voice', outcome, message: note || '', performed_by: ownerId,
    });
    await supabase.from('conservation_cases')
      .update({ last_contact_at: new Date().toISOString(), contact_attempts: (activeCase.contact_attempts || 0) + 1 })
      .eq('id', activeCase.id);
    showToast(`Call logged · ${outcome}.`, 'success');
    await load();
    await openCase(activeCase);
  };

  const escalateToAgent = async () => {
    await setStatus('escalated', 'VA escalated to licensed agent.');
  };

  // ── Rendering helpers ───────────────────────────────────────
  const issueLabel = (k) => ({
    lapse_notice:        'Lapse Notice',
    minimum_payment_due: 'Min Payment Due',
    failed_payment:      'Failed Payment',
    returned_mail:       'Returned Mail',
    chargeback:          'Chargeback',
    bad_phone:           'Bad Phone',
    bad_email:           'Bad Email',
    policy_service:      'Policy Service',
    termination_risk:    'Termination Risk',
  }[k] || k);

  const priorityTone = (p) => ({
    critical: 'bg-rose-100 text-rose-800',
    high:     'bg-amber-100 text-amber-800',
    medium:   'bg-sky-100 text-sky-800',
    low:      'bg-slate-100 text-slate-700',
  }[p] || 'bg-slate-100 text-slate-700');

  const statusTone = (s) => ({
    new:                'bg-slate-100 text-slate-700',
    contact_attempted:  'bg-sky-100 text-sky-800',
    text_sent:          'bg-sky-100 text-sky-800',
    client_replied:     'bg-violet-100 text-violet-800',
    promise_to_pay:     'bg-amber-100 text-amber-800',
    payment_link_sent:  'bg-amber-100 text-amber-800',
    address_confirmed:  'bg-emerald-100 text-emerald-800',
    carrier_updated:    'bg-emerald-100 text-emerald-800',
    escalated:          'bg-rose-100 text-rose-800',
    policy_saved:       'bg-emerald-200 text-emerald-900 font-black',
    policy_lapsed:      'bg-slate-300 text-slate-800',
    closed:             'bg-slate-200 text-slate-700',
  }[s] || 'bg-slate-100 text-slate-700');

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-black border-l-4 border-rose-500 px-6 py-5 shadow-2xl flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-rose-400 text-[0.6rem] font-black uppercase tracking-[0.3em]">Life Policy Conservation</div>
          <div className="text-white text-xl md:text-2xl font-black mt-1.5 uppercase tracking-tight">Save policies before they lapse</div>
          <div className="text-slate-400 text-xs mt-1.5">{stats.open} open · {stats.critical} critical · {stats.escalated} escalated · {fmt$(stats.amountAtRisk)} at risk · {stats.saved} saved · {stats.lapsed} lapsed</div>
        </div>
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          className="bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-xs font-black uppercase tracking-tight px-4 py-2 shadow">
          {uploading ? 'Parsing…' : 'Upload carrier report →'}
        </button>
        <input ref={fileRef} type="file" accept="application/pdf,.pdf,.csv,.txt" className="hidden"
          onChange={e => handleUpload(e.target.files?.[0])}/>
      </div>

      {/* Parsed preview */}
      {parsed && (
        <div className="bg-white border-2 border-violet-300 px-4 py-3 space-y-2">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <div className="text-violet-700 text-[0.55rem] font-black uppercase tracking-[0.2em]">Parsed · review before commit</div>
              <div className="text-sm font-bold text-slate-900">{parsed.rows?.length || 0} row{(parsed.rows?.length || 0) === 1 ? '' : 's'} extracted from {parsed.source_carrier || 'report'}</div>
              <div className="text-[0.65rem] text-slate-500 mt-0.5">By issue: {Object.entries(parsed.summary?.byIssue || {}).map(([k, v]) => `${issueLabel(k)} ×${v}`).join(' · ')}</div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setParsed(null)} disabled={committing} className="text-xs font-bold uppercase text-slate-500 hover:text-slate-900">Discard</button>
              <button onClick={() => commitParsed(false)} disabled={committing} className="text-xs font-black uppercase tracking-tight px-3 py-1.5 bg-slate-900 hover:bg-black text-white disabled:opacity-50">
                Commit cases (no SMS)
              </button>
              <button onClick={() => commitParsed(true)} disabled={committing} className="text-xs font-black uppercase tracking-tight px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50">
                {committing ? 'Committing…' : 'Commit + send first SMS'}
              </button>
            </div>
          </div>
          {/* Top 5 preview rows */}
          <div className="border border-violet-200 max-h-56 overflow-auto">
            <table className="w-full text-xs">
              <thead className="bg-violet-50">
                <tr>
                  {['Client', 'Carrier', 'Issue', 'Priority', 'Deadline', 'Amount'].map(h => (
                    <th key={h} className="text-left px-2 py-1 text-[0.55rem] font-black uppercase tracking-wider text-violet-700">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-violet-100">
                {parsed.rows.slice(0, 10).map((r, i) => (
                  <tr key={i}>
                    <td className="px-2 py-1 font-bold"><ClientNameLink name={r.client_name} openClientProfile={openClientProfile} className="font-bold hover:underline"/></td>
                    <td className="px-2 py-1 text-slate-700">{r.carrier}</td>
                    <td className="px-2 py-1 text-[0.6rem]">{issueLabel(r.issue_type)}</td>
                    <td className="px-2 py-1"><span className={`text-[0.55rem] font-black uppercase px-1.5 py-0.5 ${priorityTone(r.priority)}`}>{r.priority}</span></td>
                    <td className="px-2 py-1 font-mono text-slate-600">{r.deadline || '—'}</td>
                    <td className="px-2 py-1 font-mono text-emerald-700">{fmt$(r.amount_due)}</td>
                  </tr>
                ))}
                {parsed.rows.length > 10 && (
                  <tr><td colSpan={6} className="px-2 py-1 text-center text-[0.6rem] text-violet-700">+ {parsed.rows.length - 10} more</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Filter pills */}
      <div className="bg-white border border-slate-200 px-4 py-2 flex items-center gap-2 flex-wrap">
        {[
          ['open',           `Open · ${stats.open}`],
          ['critical_today', `Critical today · ${stats.critical}`],
          ['mine',           'Mine'],
          ['closed',         'Closed / Saved'],
          ['all',            `All · ${cases.length}`],
        ].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`text-xs font-bold uppercase tracking-tight px-3 py-1.5 ${filter === k ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Case queue */}
      <div className="bg-white border border-slate-200 overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {['Priority', 'Client', 'Carrier', 'Issue', 'Deadline', 'Amount Due', 'Status', 'Days Open', ''].map(h => (
                <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={9} className="px-3 py-10 text-center text-slate-400">Loading…</td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-10 text-center text-slate-400">No cases match this filter.</td></tr>
            ) : visible.map(c => {
              const daysOpen = Math.floor((Date.now() - new Date(c.created_at).getTime()) / (24 * 3600 * 1000));
              return (
                <tr key={c.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openCase(c)}>
                  <td className="px-3 py-2"><span className={`text-[0.55rem] font-black uppercase tracking-wider px-2 py-0.5 ${priorityTone(c.priority)}`}>{c.priority}</span></td>
                  <td className="px-3 py-2">
                    <div className="font-bold text-slate-900"><ClientNameLink name={c.client_name} openClientProfile={openClientProfile} className="font-bold text-slate-900 hover:underline"/></div>
                    <div className="text-[0.6rem] text-slate-500 font-mono">{c.client_phone || '—'}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div>{c.carrier || '—'}</div>
                    <div className="text-[0.6rem] text-slate-500 font-mono">{c.policy_number || '—'}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">{issueLabel(c.issue_type)}</td>
                  <td className="px-3 py-2 text-xs font-mono text-slate-600">{c.deadline || '—'}</td>
                  <td className="px-3 py-2 text-xs font-mono text-emerald-700 font-bold">{fmt$(c.amount_due)}</td>
                  <td className="px-3 py-2"><span className={`text-[0.55rem] font-black uppercase tracking-wider px-2 py-0.5 ${statusTone(c.status)}`}>{c.status?.replace(/_/g, ' ')}</span></td>
                  <td className="px-3 py-2 text-xs tabular-nums">{daysOpen}d</td>
                  <td className="px-3 py-2 text-right text-[0.6rem] uppercase tracking-wider text-rose-700 hover:text-rose-900 underline">Open →</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Case detail modal */}
      {activeCase && (
        <ConservationCaseDetail
          c={activeCase}
          actions={actions}
          onClose={() => { setActiveCase(null); setActions([]); }}
          onSetStatus={setStatus}
          onLogNote={logNote}
          onLogCall={logCall}
          onEscalate={escalateToAgent}
          issueLabel={issueLabel}
          statusTone={statusTone}
          priorityTone={priorityTone}
          fmt$={fmt$}
          openClientProfile={openClientProfile}
        />
      )}
    </div>
  );
}

function ConservationCaseDetail({ c, actions, onClose, onSetStatus, onLogNote, onLogCall, onEscalate, issueLabel, statusTone, priorityTone, fmt$, openClientProfile = () => {} }) {
  const [note, setNote] = React.useState('');
  const [callNote, setCallNote] = React.useState('');
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-4xl max-h-[92vh] overflow-auto shadow-2xl border-2 border-slate-900" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-black border-l-4 border-rose-500 px-6 py-4 flex items-baseline justify-between gap-4 flex-wrap sticky top-0 z-10">
          <div>
            <div className="text-rose-400 text-[0.55rem] font-black uppercase tracking-[0.3em]">Conservation Case</div>
            <div className="text-white text-xl font-black uppercase tracking-tight mt-0.5"><ClientNameLink name={c.client_name} openClientProfile={openClientProfile} className="text-white hover:underline"/></div>
            <div className="text-slate-400 text-xs mt-1 flex items-baseline gap-3 flex-wrap">
              <span>{c.client_phone || '—'}</span>
              {c.client_email && <span>{c.client_email}</span>}
              {c.client_address && <span className="text-slate-500">{c.client_address}</span>}
            </div>
          </div>
          <button aria-label="Close" onClick={onClose} className="text-slate-300 hover:text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 border border-slate-600 hover:border-slate-400">Close (Esc)</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Top tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className={`p-3 ${priorityTone(c.priority)}`}>
              <div className="text-[0.55rem] font-black uppercase tracking-wider opacity-70">Priority</div>
              <div className="text-lg font-black">{c.priority}</div>
            </div>
            <div className={`p-3 ${statusTone(c.status)}`}>
              <div className="text-[0.55rem] font-black uppercase tracking-wider opacity-70">Status</div>
              <div className="text-sm font-black">{c.status?.replace(/_/g, ' ')}</div>
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200">
              <div className="text-[0.55rem] font-black uppercase tracking-wider text-amber-700">Deadline</div>
              <div className="text-lg font-black text-amber-900">{c.deadline || '—'}</div>
            </div>
            <div className="p-3 bg-emerald-50 border border-emerald-200">
              <div className="text-[0.55rem] font-black uppercase tracking-wider text-emerald-700">Amount Due</div>
              <div className="text-lg font-black text-emerald-900">{fmt$(c.amount_due)}</div>
            </div>
          </div>

          {/* Policy details */}
          <div className="bg-slate-50 border border-slate-200 p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div><div className="text-[0.55rem] uppercase tracking-wider text-slate-500">Carrier</div><div className="font-bold">{c.carrier || '—'}</div></div>
            <div><div className="text-[0.55rem] uppercase tracking-wider text-slate-500">Policy</div><div className="font-mono">{c.policy_number || '—'}</div></div>
            <div><div className="text-[0.55rem] uppercase tracking-wider text-slate-500">Plan type</div><div>{c.policy_type || '—'}</div></div>
            <div><div className="text-[0.55rem] uppercase tracking-wider text-slate-500">Issue</div><div className="font-bold text-rose-700">{issueLabel(c.issue_type)}</div></div>
            <div><div className="text-[0.55rem] uppercase tracking-wider text-slate-500">Paid-to</div><div className="font-mono">{c.paid_to_date || '—'}</div></div>
            <div><div className="text-[0.55rem] uppercase tracking-wider text-slate-500">Event date</div><div className="font-mono">{c.event_date || '—'}</div></div>
            <div><div className="text-[0.55rem] uppercase tracking-wider text-slate-500">Premium</div><div className="font-mono">{fmt$(c.premium)}</div></div>
            <div><div className="text-[0.55rem] uppercase tracking-wider text-slate-500">Termination</div><div className="font-mono text-rose-700">{c.termination_date || '—'}</div></div>
          </div>

          {c.event_description && (
            <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-slate-900">
              <div className="text-[0.55rem] font-black uppercase tracking-wider text-amber-700 mb-1">Carrier note (verbatim)</div>
              <div className="italic">{c.event_description}</div>
            </div>
          )}

          {/* Status actions */}
          <div className="bg-white border-2 border-slate-200 p-3 space-y-2">
            <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Move to status</div>
            <div className="flex items-center gap-2 flex-wrap">
              {[
                ['promise_to_pay',    'Promise to pay',     'amber'],
                ['payment_link_sent', 'Payment link sent',  'amber'],
                ['address_confirmed', 'Address confirmed',  'emerald'],
                ['carrier_updated',   'Carrier updated',    'emerald'],
                ['policy_saved',      'Policy SAVED ✓',     'emerald-bold'],
                ['policy_lapsed',     'Policy lapsed',      'slate'],
                ['closed',            'Close case',         'slate'],
              ].map(([s, label, tone]) => (
                <button key={s} onClick={() => onSetStatus(s)} disabled={c.status === s}
                  className={`text-[0.6rem] font-black uppercase tracking-wider px-2.5 py-1.5 disabled:opacity-40 ${
                    tone === 'emerald-bold' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' :
                    tone === 'emerald'      ? 'bg-emerald-100 hover:bg-emerald-200 text-emerald-900' :
                    tone === 'amber'        ? 'bg-amber-100 hover:bg-amber-200 text-amber-900' :
                                              'bg-slate-100 hover:bg-slate-200 text-slate-800'
                  }`}>
                  {label}
                </button>
              ))}
              <button onClick={onEscalate} disabled={c.status === 'escalated'}
                className="ml-auto text-[0.6rem] font-black uppercase tracking-wider px-2.5 py-1.5 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white">
                ⚠ Escalate to agent
              </button>
            </div>
          </div>

          {/* Log a call */}
          <div className="bg-white border border-slate-200 p-3 space-y-2">
            <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Log a call</div>
            <textarea rows={2} value={callNote} onChange={e => setCallNote(e.target.value)}
              placeholder="What did the client say? Promise to pay date? Address change?"
              className="w-full text-xs border border-slate-300 px-2 py-1.5 font-mono"/>
            <div className="flex items-center gap-2 flex-wrap">
              {['answered','no_answer','left_voicemail','disconnected','dnc'].map(o => (
                <button key={o} onClick={() => { onLogCall(o, callNote); setCallNote(''); }}
                  className="text-[0.55rem] font-black uppercase tracking-wider px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-800">
                  {o.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Add a note */}
          <div className="bg-white border border-slate-200 p-3 space-y-2">
            <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Add a note</div>
            <textarea rows={2} value={note} onChange={e => setNote(e.target.value)}
              placeholder="Notes the next person on this case should know."
              className="w-full text-xs border border-slate-300 px-2 py-1.5 font-mono"/>
            <button onClick={() => { onLogNote(note); setNote(''); }} disabled={!note.trim()}
              className="text-[0.6rem] font-black uppercase tracking-wider px-3 py-1.5 bg-slate-900 hover:bg-black text-white disabled:opacity-40">
              Save note
            </button>
          </div>

          {/* Timeline */}
          <div className="bg-white border border-slate-200 p-3 space-y-2">
            <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Timeline ({actions.length})</div>
            <div className="max-h-72 overflow-auto space-y-1.5">
              {actions.length === 0
                ? <div className="text-xs italic text-slate-400">No activity yet.</div>
                : actions.slice().reverse().map(a => (
                    <div key={a.id} className="border-l-2 border-slate-200 pl-3 py-1">
                      <div className="text-[0.55rem] uppercase tracking-wider text-slate-500 flex items-center gap-2">
                        <span className="font-bold">{a.kind.replace(/_/g, ' ')}</span>
                        {a.is_ai && <span className="bg-violet-100 text-violet-800 px-1.5 py-0.5">AI</span>}
                        <span className="font-mono text-slate-400">{new Date(a.created_at).toLocaleString()}</span>
                      </div>
                      {a.kind === 'status_change' ? (
                        <div className="text-xs text-slate-700 mt-0.5">{a.status_from?.replace(/_/g, ' ')} → {a.status_to?.replace(/_/g, ' ')}{a.message ? ` · ${a.message}` : ''}</div>
                      ) : (
                        <div className="text-xs text-slate-800 mt-0.5 whitespace-pre-wrap">{a.message}{a.outcome ? ` · ${a.outcome}` : ''}</div>
                      )}
                    </div>
                  ))}
            </div>
          </div>

          <div className="text-[0.6rem] uppercase tracking-wider text-slate-400 text-right pt-2">
            Cron <span className="font-mono">scan-followups</span> runs daily and advances this case per the {issueLabel(c.issue_type).toLowerCase()} cadence.
          </div>
        </div>
      </div>
    </div>
  );
}

function HealthCommissionsView({ currentUser, settings = {}, showToast = () => {}, onGotoTab = () => {}, openClientProfile = () => {} }) {
  const [policies, setPolicies] = React.useState([]);
  const [losses,   setLosses]   = React.useState([]);
  // Transaction-level ledger from migration 045 / UHC import. Optional —
  // if the operator hasn't run 045 or hasn't imported a UHC statement,
  // these stay empty and the legacy MRR roll-up still renders cleanly.
  const [transactions, setTransactions] = React.useState([]);
  const [statements, setStatements]     = React.useState([]);
  const [loading,  setLoading]  = React.useState(true);
  const ownerId = currentUser?.id;

  React.useEffect(() => {
    if (!ownerId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const day90 = new Date(Date.now() - 90 * 24 * 3600 * 1000).toISOString();
      const day30iso = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);
      const [p, l, tx, st] = await Promise.all([
        supabase.from('health_policies')
          .select('id, client_name, carrier, plan_name, plan_type, status, monthly_premium, monthly_commission, subsidy_amount, effective_date, last_commission_at, termination_date, health_score')
          .eq('owner_id', ownerId),
        supabase.from('health_loss_events')
          .select('id, client_name, plan_name, plan_type, reason, monthly_commission, lost_at')
          .eq('owner_id', ownerId)
          .gte('lost_at', day90),
        // Last 30 days of commission transactions. Soft-fails if the
        // table doesn't exist (045 not yet applied).
        supabase.from('commission_transactions')
          .select('id, policy_id, client_name, carrier, product_label, action, amount, paid_date, statement_id')
          .eq('owner_id', ownerId)
          .gte('paid_date', day30iso)
          .order('paid_date', { ascending: false })
          .limit(200),
        supabase.from('commission_statements')
          .select('id, carrier, carrier_short, statement_date, total_rows, new_count, pmpm_count, chargeback_count, gross_amount, chargeback_amount, net_amount, source_filename')
          .eq('owner_id', ownerId)
          .order('statement_date', { ascending: false })
          .limit(8),
      ]);
      if (cancelled) return;
      setPolicies(p.data || []);
      setLosses(l.data || []);
      setTransactions(tx.error ? [] : (tx.data || []));
      setStatements(st.error ? [] : (st.data || []));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [ownerId]);

  const fmt$ = (n) => '$' + Math.round(Number(n) || 0).toLocaleString();

  const active = policies.filter(p => (p.status || 'active') === 'active');

  // ── Roll-ups ──
  // Some import paths (HealthSherpa, Jarvis BoB) carry premium but NOT
  // commission — the carrier-side rate sits on the broker's
  // commission statement which is a separate import. When most
  // policies have null monthly_commission, the legacy roll-up shows
  // $0 across the board which reads as broken. We surface that
  // condition explicitly + offer a premium-based ESTIMATE using the
  // industry-standard ACA broker rate so the operator sees a
  // realistic ballpark while they wait for the commission statement.
  const stats = React.useMemo(() => {
    const activeMrr  = active.reduce((s, p) => s + (Number(p.monthly_commission) || 0), 0);
    const activeApe  = activeMrr * 12;
    const subsidyMrr = active.reduce((s, p) => s + (Number(p.subsidy_amount) || 0), 0);
    const premiumMrr = active.reduce((s, p) => s + (Number(p.monthly_premium) || 0), 0);
    const lostMrr    = losses.reduce((s, l) => s + (Number(l.monthly_commission) || 0), 0);
    const policiesWithCommission = active.filter(p => Number(p.monthly_commission) > 0).length;
    const policiesWithPremium    = active.filter(p => Number(p.monthly_premium)    > 0).length;
    // Estimated commission — ~$22 PMPM is the typical ACA broker
    // rate. We assume one billable member per active policy. This is
    // a placeholder until real commission lands; we never store it.
    const estimatedMrr = policiesWithCommission < (active.length / 2)
      ? active.length * 22
      : null;
    return {
      activeMrr, activeApe, subsidyMrr, premiumMrr, lostMrr,
      count: active.length, lostCount: losses.length,
      policiesWithCommission, policiesWithPremium,
      estimatedMrr,
    };
  }, [active, losses]);

  // ── Carrier mix ──
  const byCarrier = React.useMemo(() => {
    const map = {};
    for (const p of active) {
      const k = p.carrier || 'Unknown';
      const r = map[k] || (map[k] = { carrier: k, count: 0, mrr: 0 });
      r.count++;
      r.mrr += Number(p.monthly_commission) || 0;
    }
    return Object.values(map).sort((a, b) => b.mrr - a.mrr);
  }, [active]);

  // ── Plan-type mix ──
  const byPlanType = React.useMemo(() => {
    const map = {};
    for (const p of active) {
      const k = HEALTH_PLAN_TYPE_BY_ID?.[p.plan_type]?.label || p.plan_type || 'Other';
      const r = map[k] || (map[k] = { label: k, count: 0, mrr: 0 });
      r.count++;
      r.mrr += Number(p.monthly_commission) || 0;
    }
    return Object.values(map).sort((a, b) => b.mrr - a.mrr);
  }, [active]);

  // ── Recently paid ──
  const day30 = new Date(Date.now() - 30 * 24 * 3600 * 1000);
  const recentlyPaid = React.useMemo(() => {
    return active
      .filter(p => p.last_commission_at && new Date(p.last_commission_at) >= day30)
      .sort((a, b) => new Date(b.last_commission_at) - new Date(a.last_commission_at))
      .slice(0, 15);
  }, [active]);

  // ── Top earners ──
  const topEarners = React.useMemo(() => {
    return [...active]
      .filter(p => p.monthly_commission)
      .sort((a, b) => (Number(b.monthly_commission) || 0) - (Number(a.monthly_commission) || 0))
      .slice(0, 10);
  }, [active]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-black border-l-4 border-sky-500 px-6 py-5 shadow-2xl flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sky-400 text-[0.6rem] font-black uppercase tracking-[0.3em]">Health Commissions</div>
          <div className="text-white text-xl md:text-2xl font-black mt-1.5 uppercase tracking-tight">{fmt$(stats.activeMrr)}/mo active MRR · {stats.count} policies</div>
          <div className="text-slate-400 text-xs mt-1.5">Annualized: {fmt$(stats.activeApe)} · Subsidy passthrough {fmt$(stats.subsidyMrr)}/mo · Lost MRR (90d) {fmt$(stats.lostMrr)}</div>
        </div>
        <button onClick={() => onGotoTab('health_reconciliation')} className="bg-sky-600 hover:bg-sky-700 text-white text-xs font-black uppercase tracking-tight px-4 py-2 shadow">Reconcile →</button>
      </div>

      {loading ? (
        <div className="text-sm italic text-slate-500">Loading…</div>
      ) : (
        <>
          {/* Empty-state explainer — shown when fewer than half of
              active policies carry commission $. Tells the operator
              WHY MRR shows $0 + offers the import path that fixes it. */}
          {stats.count > 0 && stats.estimatedMrr != null && (
            <div className="bg-amber-50 border-2 border-amber-300 px-4 py-3 flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[300px]">
                <div className="text-amber-800 text-[0.55rem] font-black uppercase tracking-[0.25em]">Why is Active MRR $0?</div>
                <div className="text-slate-900 text-sm font-semibold mt-1">
                  Only {stats.policiesWithCommission} of {stats.count} active polic{stats.policiesWithCommission === 1 ? 'y has' : 'ies have'} commission $ on record.
                </div>
                <div className="text-slate-700 text-xs mt-1.5 leading-snug">
                  HealthSherpa and Jarvis BoB exports carry the client + premium but NOT your commission rate — that lives on a separate carrier statement.
                  Drop a <strong>Plan Advisors</strong>, <strong>HCSC</strong>, or <strong>UHC</strong> commission statement into Health Import to populate the real numbers.
                  Estimated MRR at the industry-standard $22 PMPM: <span className="font-bold text-emerald-700">{fmt$(stats.estimatedMrr)}/mo</span> (placeholder until real data lands).
                </div>
              </div>
              <button
                onClick={() => onGotoTab('health_policies')}
                className="bg-amber-700 hover:bg-amber-800 text-white text-xs font-black uppercase tracking-tight px-4 py-2 rounded-md whitespace-nowrap shadow"
              >
                Import a commission statement →
              </button>
            </div>
          )}

          {/* Tiles */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border-2 border-slate-300 bg-white">
            <SummaryTile
              label={stats.estimatedMrr != null ? 'Active MRR (est.)' : 'Active MRR'}
              value={stats.estimatedMrr != null ? fmt$(stats.estimatedMrr) : fmt$(stats.activeMrr)}
              accent="emerald"
            />
            <SummaryTile
              label={stats.estimatedMrr != null ? 'Annualized (est.)' : 'Annualized'}
              value={fmt$((stats.estimatedMrr != null ? stats.estimatedMrr : stats.activeMrr) * 12)}
              accent="sky"
            />
            <SummaryTile label="Premium MRR"  value={fmt$(stats.premiumMrr)} accent="amber"/>
            <SummaryTile label="Subsidy MRR"  value={fmt$(stats.subsidyMrr)} accent="slate"/>
            <SummaryTile label="Lost (90d)"   value={fmt$(stats.lostMrr)}    accent="rose"/>
          </div>

          {/* ── Commission Intelligence: this-month action mix.
              Only renders once commission_transactions has data — i.e.
              after the operator has uploaded a UHC commission
              statement via Health Import. */}
          {transactions.length > 0 && (() => {
            const today = new Date();
            const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
            const thisMonth = transactions.filter(t => t.paid_date && t.paid_date >= monthStart);
            const counts = { new: 0, pmpm: 0, chargeback: 0, reinstatement: 0, adjustment: 0, other: 0 };
            let gross = 0, chargebacks = 0;
            for (const t of thisMonth) {
              counts[t.action] = (counts[t.action] || 0) + 1;
              const amt = Number(t.amount) || 0;
              if (amt >= 0) gross += amt; else chargebacks += Math.abs(amt);
            }
            const net = gross - chargebacks;
            return (
              <div className="bg-white border-2 border-violet-300">
                <div className="px-4 py-2 border-b border-violet-200 bg-violet-50 flex items-baseline justify-between flex-wrap gap-2">
                  <div>
                    <div className="text-violet-700 text-[0.55rem] font-black uppercase tracking-[0.2em]">Commission Intelligence · This Month</div>
                    <div className="text-xs text-slate-700">From <span className="font-mono">commission_transactions</span> · {thisMonth.length} transaction{thisMonth.length === 1 ? '' : 's'} since {monthStart}</div>
                  </div>
                  <button onClick={() => onGotoTab('health_policies')} className="text-[0.6rem] font-black uppercase tracking-wider text-violet-700 hover:text-violet-900 underline">Import a UHC statement →</button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-0">
                  <SummaryTile label="NEW"          value={counts.new || 0}              accent="emerald"/>
                  <SummaryTile label="PMPM"         value={counts.pmpm || 0}             accent="sky"/>
                  <SummaryTile label="Chargebacks"  value={counts.chargeback || 0}       accent="rose"/>
                  <SummaryTile label="Gross"        value={fmt$(gross)}                  accent="amber"/>
                  <SummaryTile label="Net"          value={`${net >= 0 ? '' : '-'}${fmt$(Math.abs(net))}`} accent={net >= 0 ? 'emerald' : 'rose'}/>
                </div>
              </div>
            );
          })()}

          {/* ── Statement Activity ── one row per uploaded statement
              with per-action breakdown + net. Reads from
              commission_statements (sized 8). */}
          {statements.length > 0 && (
            <div className="bg-white border border-slate-200">
              <div className="px-4 py-2 border-b border-slate-200">
                <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">Statement Activity</div>
                <div className="text-xs text-slate-700">{statements.length} uploaded statement{statements.length === 1 ? '' : 's'} · most recent first</div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {['Date', 'Carrier', 'File', 'Rows', 'New', 'PMPM', 'CB', 'Gross', 'CB $', 'Net'].map(h => (
                      <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {statements.map(s => (
                    <tr key={s.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-xs font-mono text-slate-600 whitespace-nowrap">{s.statement_date || '—'}</td>
                      <td className="px-3 py-2 text-xs font-bold">{s.carrier_short || s.carrier || '—'}</td>
                      <td className="px-3 py-2 text-[0.6rem] text-slate-500 max-w-[260px] truncate" title={s.source_filename || ''}>{s.source_filename || '—'}</td>
                      <td className="px-3 py-2 text-xs tabular-nums">{s.total_rows}</td>
                      <td className="px-3 py-2 text-xs tabular-nums text-emerald-700 font-bold">{s.new_count || 0}</td>
                      <td className="px-3 py-2 text-xs tabular-nums text-sky-700">{s.pmpm_count || 0}</td>
                      <td className="px-3 py-2 text-xs tabular-nums text-rose-700 font-bold">{s.chargeback_count || 0}</td>
                      <td className="px-3 py-2 text-xs tabular-nums">{fmt$(s.gross_amount || 0)}</td>
                      <td className="px-3 py-2 text-xs tabular-nums text-rose-700">{fmt$(s.chargeback_amount || 0)}</td>
                      <td className={`px-3 py-2 text-xs tabular-nums font-bold ${Number(s.net_amount) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmt$(s.net_amount || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── At-risk policies (health_score < 50). Powered by the
              UHC ledger rolling-score writeback. */}
          {(() => {
            const atRisk = policies
              .filter(p => p.health_score != null && p.health_score < 50)
              .sort((a, b) => (a.health_score || 0) - (b.health_score || 0))
              .slice(0, 10);
            if (atRisk.length === 0) return null;
            return (
              <div className="bg-white border-2 border-rose-300">
                <div className="px-4 py-2 border-b border-rose-200 bg-rose-50 flex items-baseline justify-between gap-2 flex-wrap">
                  <div>
                    <div className="text-rose-700 text-[0.55rem] font-black uppercase tracking-[0.2em]">At-Risk Policies · Health Score &lt; 50</div>
                    <div className="text-xs text-slate-700">Bottom 10 by rolling commission-health score</div>
                  </div>
                  <button onClick={() => onGotoTab('health_reconciliation')} className="text-[0.6rem] font-black uppercase tracking-wider text-rose-700 hover:text-rose-900 underline">Open Reconciliation →</button>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      {['Client', 'Carrier', 'Plan', 'Last paid', 'MRR', 'Score'].map(h => (
                        <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {atRisk.map(p => (
                      <tr key={p.id} className="hover:bg-slate-50">
                        <td className="px-3 py-2 text-xs font-bold"><ClientNameLink name={p.client_name} openClientProfile={openClientProfile} className="text-xs font-bold hover:underline" fallback="—"/></td>
                        <td className="px-3 py-2 text-xs">{p.carrier || '—'}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{p.plan_name || '—'}</td>
                        <td className="px-3 py-2 text-[0.65rem] font-mono text-slate-500">{p.last_commission_at ? String(p.last_commission_at).slice(0, 10) : 'never'}</td>
                        <td className="px-3 py-2 text-xs tabular-nums">{p.monthly_commission != null ? fmt$(p.monthly_commission) : '—'}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[0.6rem] font-black uppercase tracking-wider px-2 py-0.5 rounded ${p.health_score <= 25 ? 'bg-rose-200 text-rose-900' : 'bg-amber-200 text-amber-900'}`}>{p.health_score}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}

          {/* Two-column body */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Carrier mix */}
            <div className="bg-white border border-slate-200">
              <div className="px-4 py-2 border-b border-slate-200">
                <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">By Carrier</div>
                <div className="text-xs text-slate-700">{byCarrier.length} carrier{byCarrier.length === 1 ? '' : 's'} in the book</div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {['Carrier', 'Policies', 'MRR', '% of book'].map(h => (
                      <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {byCarrier.slice(0, 12).map(r => (
                    <tr key={r.carrier} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-xs font-bold">{r.carrier}</td>
                      <td className="px-3 py-2 text-xs tabular-nums">{r.count}</td>
                      <td className="px-3 py-2 text-xs tabular-nums font-bold text-emerald-700">{fmt$(r.mrr)}</td>
                      <td className="px-3 py-2 text-[0.6rem] font-mono text-slate-500">{stats.activeMrr > 0 ? `${Math.round((r.mrr / stats.activeMrr) * 100)}%` : '—'}</td>
                    </tr>
                  ))}
                  {byCarrier.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400 italic">No carriers yet.</td></tr>}
                </tbody>
              </table>
            </div>

            {/* Plan type mix */}
            <div className="bg-white border border-slate-200">
              <div className="px-4 py-2 border-b border-slate-200">
                <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">By Plan Type</div>
                <div className="text-xs text-slate-700">{byPlanType.length} line{byPlanType.length === 1 ? '' : 's'} of business</div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {['Plan type', 'Policies', 'MRR', '% of book'].map(h => (
                      <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {byPlanType.map(r => (
                    <tr key={r.label} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-xs font-bold">{r.label}</td>
                      <td className="px-3 py-2 text-xs tabular-nums">{r.count}</td>
                      <td className="px-3 py-2 text-xs tabular-nums font-bold text-emerald-700">{fmt$(r.mrr)}</td>
                      <td className="px-3 py-2 text-[0.6rem] font-mono text-slate-500">{stats.activeMrr > 0 ? `${Math.round((r.mrr / stats.activeMrr) * 100)}%` : '—'}</td>
                    </tr>
                  ))}
                  {byPlanType.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400 italic">No active policies yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recently paid + Top earners */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200">
              <div className="px-4 py-2 border-b border-slate-200">
                <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">Recently Paid (last 30d)</div>
                <div className="text-xs text-slate-700">{recentlyPaid.length} active polic{recentlyPaid.length === 1 ? 'y' : 'ies'} with a fresh commission</div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {['Client', 'Carrier', 'Last paid', 'MRR'].map(h => (
                      <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {recentlyPaid.map(p => (
                    <tr key={p.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-xs font-bold"><ClientNameLink name={p.client_name} openClientProfile={openClientProfile} className="text-xs font-bold hover:underline" fallback="—"/></td>
                      <td className="px-3 py-2 text-xs">{p.carrier || '—'}</td>
                      <td className="px-3 py-2 text-[0.65rem] font-mono text-slate-600">{String(p.last_commission_at).slice(0, 10)}</td>
                      <td className="px-3 py-2 text-xs tabular-nums font-bold text-emerald-700">{p.monthly_commission != null ? fmt$(p.monthly_commission) : '—'}</td>
                    </tr>
                  ))}
                  {recentlyPaid.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400 italic">No commissions paid in the last 30 days.</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="bg-white border border-slate-200">
              <div className="px-4 py-2 border-b border-slate-200">
                <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">Top Earners</div>
                <div className="text-xs text-slate-700">Highest-MRR active policies</div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {['#', 'Client', 'Carrier · Plan', 'MRR'].map(h => (
                      <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {topEarners.map((p, i) => (
                    <tr key={p.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-[0.6rem] font-mono text-slate-400">{i + 1}</td>
                      <td className="px-3 py-2 text-xs font-bold"><ClientNameLink name={p.client_name} openClientProfile={openClientProfile} className="text-xs font-bold hover:underline" fallback="—"/></td>
                      <td className="px-3 py-2 text-xs">
                        <div>{p.carrier || '—'}</div>
                        <div className="text-[0.6rem] text-slate-500">{p.plan_name || '—'}</div>
                      </td>
                      <td className="px-3 py-2 text-xs tabular-nums font-bold text-emerald-700">{fmt$(p.monthly_commission || 0)}</td>
                    </tr>
                  ))}
                  {topEarners.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400 italic">No commission data yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="text-[0.6rem] uppercase tracking-wider text-slate-400 px-1">
            Data source: <span className="font-mono">health_policies.monthly_commission</span> (active rows) · <span className="font-mono">health_loss_events</span> (last 90d). Life-side commissions are tracked separately under the Life Insurance workspace.
          </div>
        </>
      )}
    </div>
  );
}

function HealthPoliciesView({ currentUser, settings = {}, showToast = () => {}, openClientProfile = () => {}, onGotoTab = () => {} }) {
  const [policies, setPolicies] = React.useState([]);
  const [loading, setLoading]   = React.useState(true);
  const [editing, setEditing]   = React.useState(null);
  // Profile modal — opened when the agent clicks a client name in the
  // table. Shows the consolidated 360° view: every policy this client
  // has (active, lapsed, archived), linked plan benefits, contact +
  // demographics, loss events, and the inline Plan Assistant.
  const [profileClient, setProfileClient] = React.useState(null); // { name, id? }
  // Plan Intelligence library — pulled once so the edit modal's
  // "Linked plan" dropdown can let the agent attach the policy to a
  // specific SBC row. Setting health_policies.plan_id powers the
  // grounded Plan Assistant in the client profile + the Reconciliation
  // Engine's roster→benefits join.
  const [carrierPlans, setCarrierPlans] = React.useState([]);
  React.useEffect(() => {
    if (!currentUser?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('carrier_plans')
        .select('id, carrier_short, plan_name, state, metal_level, network_type')
        .eq('owner_id', currentUser.id)
        .eq('status', 'active')
        .order('carrier_short', { ascending: true });
      if (!cancelled) setCarrierPlans(data || []);
    })();
    return () => { cancelled = true; };
  }, [currentUser?.id]);
  const [search, setSearch]     = React.useState('');
  const [typeFilter, setTypeFilter] = React.useState('all');
  const [statusFilter, setStatusFilter] = React.useState('all');
  // Category filter — collapses the 9 plan_type values into the four
  // groups the agent actually thinks about: ACA, Medicare, Private PPO,
  // and Ancillary. Persisted to localStorage so the choice survives
  // reloads and matches the Life/Health book filter pattern elsewhere.
  const [categoryFilter, setCategoryFilter] = React.useState(() => {
    try { return localStorage.getItem('healthPolicies:category') || 'all'; }
    catch { return 'all'; }
  });
  React.useEffect(() => {
    try { localStorage.setItem('healthPolicies:category', categoryFilter); } catch {}
  }, [categoryFilter]);
  // Choose category + drop a stale plan-type filter in one step. Doing
  // this on click (rather than in an effect) sidesteps the effect-deps
  // cycle where we'd read+write typeFilter from the same effect.
  const chooseCategory = (cat) => {
    setCategoryFilter(cat);
    if (cat !== 'all' && typeFilter !== 'all' && PLAN_CATEGORY[typeFilter] !== cat) {
      setTypeFilter('all');
    }
  };

  // plan_type → category mapping. Source of truth for the pill row,
  // the per-section counts, and the body filter.
  const PLAN_CATEGORY = {
    aca_marketplace:     'aca',
    short_term:          'aca',
    medicare_advantage:  'medicare',
    medicare_supplement: 'medicare',
    medicare_pdp:        'medicare',
    private_ppo:         'private',
    dental_vision:       'ancillary',
    hospital_indemnity:  'ancillary',
    other:               'ancillary',
  };
  const categoryOf = (pt) => PLAN_CATEGORY[pt] || 'ancillary';

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('health_policies').select('*').order('client_name');
      if (error) console.warn('[HealthPolicies] load failed:', error.message);
      setPolicies(data || []);
    } catch (err) {
      // Network blip / RLS gate / missing table — leave the roster
      // empty rather than spinning forever. Real failures show up in
      // the console so the operator can still diagnose.
      console.warn('[HealthPolicies] load threw:', err);
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const filtered = policies.filter(p => {
    if (categoryFilter !== 'all' && categoryOf(p.plan_type) !== categoryFilter) return false;
    if (typeFilter !== 'all' && p.plan_type !== typeFilter) return false;
    if (statusFilter !== 'all' && (p.status || 'active') !== statusFilter) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (p.client_name || '').toLowerCase().includes(s)
      || (p.carrier || '').toLowerCase().includes(s)
      || (p.plan_name || '').toLowerCase().includes(s)
      || (p.member_id || '').toLowerCase().includes(s)
      || (p.lead_source || '').toLowerCase().includes(s)
      || (p.client_phone || '').includes(s);
  });

  async function savePolicy(p) {
    const isNew = !!p.__new;
    const row = {
      owner_id: currentUser?.id,
      client_id: p.client_id || null,
      client_name: (p.client_name || '').trim(),
      client_phone: (p.client_phone || '').trim() || null,
      client_email: (p.client_email || '').trim() || null,
      client_dob: p.client_dob || null,
      client_age: p.client_age ? Number(p.client_age) : null,
      carrier: p.carrier || null,
      plan_name: p.plan_name || null,
      plan_type: p.plan_type || 'other',
      member_id: p.member_id || null,
      monthly_premium: p.monthly_premium ? Number(p.monthly_premium) : null,
      monthly_commission: p.monthly_commission ? Number(p.monthly_commission) : null,
      subsidy_amount: p.subsidy_amount ? Number(p.subsidy_amount) : null,
      effective_date: p.effective_date || null,
      renewal_date: p.renewal_date || null,
      status: p.status || 'active',
      lead_source: p.lead_source || null,
      notes: p.notes || null,
      agent_prod_num: p.agent_prod_num || settings?.ownerProdNum || null,
      plan_id: p.plan_id || null,
      client_address:  p.client_address  || null,
      client_address2: p.client_address2 || null,
      client_city:     p.client_city     || null,
      client_state:    p.client_state    || null,
      client_zip:      p.client_zip      || null,
      client_county:   p.client_county   || null,
    };
    if (!row.client_name) { showToast('Client name required.'); return; }
    if (isNew) {
      const { data, error } = await supabase.from('health_policies').insert(row).select().single();
      if (error) { showToast(`Save failed: ${error.message}`); return; }
      setPolicies(prev => [...prev, data].sort((a, b) => (a.client_name || '').localeCompare(b.client_name || '')));
      // Plan Knowledge Brain: version-at-sale snapshot. Freezes the
      // linked carrier_plans row into policy_plan_snapshots so a
      // future brochure update doesn't rewrite what the client
      // actually bought. Fires only on brand-new policies where the
      // agent linked a plan (row.plan_id set). Non-blocking — a
      // snapshot failure logs a console warning; the policy save
      // itself is source-of-truth and already succeeded above.
      if (row.plan_id) {
        try {
          const sessRes = await supabase.auth.getSession();
          const jwt = sessRes?.data?.session?.access_token;
          const snapRes = await fetch('/api/policies/plan-snapshot', {
            method: 'POST',
            headers: {
              'content-type': 'application/json',
              ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
            },
            body: JSON.stringify({
              policy_id:       data.id,
              policy_type:     'health',
              carrier_plan_id: row.plan_id,
              issued_at:       row.effective_date || new Date().toISOString(),
              notes:           'Auto-snapshot on policy creation',
            }),
          });
          if (!snapRes.ok) {
            const err = await snapRes.text().catch(() => '');
            // eslint-disable-next-line no-console
            console.warn('[plan-snapshot] non-fatal capture failure:', snapRes.status, err.slice(0, 200));
          }
        } catch (snapErr) {
          // eslint-disable-next-line no-console
          console.warn('[plan-snapshot] non-fatal capture threw:', snapErr?.message || snapErr);
        }
      }
    } else {
      const { error } = await supabase.from('health_policies').update(row).eq('id', p.id);
      if (error) { showToast(`Save failed: ${error.message}`); return; }
      setPolicies(prev => prev.map(x => x.id === p.id ? { ...x, ...row } : x));
    }
    setEditing(null);
    showToast(isNew ? 'Health policy added.' : 'Saved.');
  }
  async function deletePolicy(p) {
    if (!(await hqConfirm({
      title: `Delete health policy for ${p.client_name}?`,
      confirmLabel: 'Delete policy', danger: true,
    }))) return;
    await supabase.from('health_policies').delete().eq('id', p.id);
    setPolicies(prev => prev.filter(x => x.id !== p.id));
    setEditing(null);
  }

  const fmt$ = (n) => '$' + Math.round(Number(n) || 0).toLocaleString();
  const newRow = () => ({ __new: true, plan_type: 'medicare_advantage', status: 'active' });

  // Reference-aligned tone map. Per the FTPIG status vocabulary, lapsed
  // and expired share the same lifecycle state (teal) — read-side they're
  // the same thing. We keep both keys so existing DB rows with either
  // value paint correctly without a data migration.
  const statusTone = (s) => {
    switch (s) {
      case 'active':    return 'bg-emerald-100 text-emerald-800';
      case 'pending':   return 'bg-amber-100 text-amber-800';
      case 'renewed':   return 'bg-orange-100 text-orange-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'lapsed':
      case 'expired':   return 'bg-teal-100 text-teal-800';
      default:          return 'bg-slate-100 text-slate-700';
    }
  };
  const statusLabel = (s) => POLICY_LABEL[s] || (s || 'active');
  const agentDisplay = (p) => p.agent_prod_num
    ? (settings?.ownerProdNum && p.agent_prod_num === settings.ownerProdNum
        ? (settings?.ownerName || 'You')
        : p.agent_prod_num)
    : (settings?.ownerName || '—');
  const summary = React.useMemo(() => {
    const active = filtered.filter(p => (p.status || 'active') === 'active');
    // BUG-012 — flag active policies missing premium so the operator
    // sees why MRR reads $0 even with hundreds of policies on file.
    const missingPremium = active.filter(p => !(Number(p.monthly_premium) > 0));
    return {
      total: filtered.length,
      active: active.length,
      mrr: active.reduce((s, p) => s + (Number(p.monthly_commission) || 0), 0),
      missing_premium_count: missingPremium.length,
      missing_premium_share: active.length > 0 ? missingPremium.length / active.length : 0,
    };
  }, [filtered]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-black border-l-4 border-sky-500 px-6 py-5 shadow-2xl flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sky-400 text-[0.6rem] font-black uppercase tracking-[0.3em]">Policy Vault</div>
          <div className="text-white text-xl md:text-2xl font-black mt-1.5 uppercase tracking-tight">Active client policies — renewals, effective dates, coverage details</div>
          <div className="text-slate-400 text-xs mt-1.5">{summary.active} active · {summary.total} on file · ${summary.mrr.toLocaleString('en-US', { maximumFractionDigits: 0 })}/mo MRR</div>
        </div>
        <button onClick={() => setEditing(newRow())} className="bg-sky-600 hover:bg-sky-700 text-white text-xs font-black uppercase tracking-tight px-4 py-2 shadow">+ Add Policy</button>
      </div>

      {/* BUG-012 — data-completeness banner. When the import pipeline
          dropped premiums on a non-trivial slice of the in-force book,
          the agency dashboard reads "$0 MRR" silently. Surface it here
          with a click-to-filter affordance so the operator can repair
          the gap policy by policy. */}
      {summary.missing_premium_count > 0 && summary.missing_premium_share >= 0.2 && (
        <div className="bg-amber-50 border border-amber-300 rounded-md px-4 py-3 flex items-start gap-3">
          <div className="text-amber-700 font-bold text-base leading-none mt-0.5">⚠</div>
          <div className="flex-1 text-xs leading-relaxed text-amber-900">
            <strong>{summary.missing_premium_count} of {summary.active} active policies have no monthly premium on file</strong>
            {' '}({Math.round(summary.missing_premium_share * 100)}% of the book). MRR + commission reporting will read low until these are filled in. Most common cause: a carrier import that didn't include the premium column. Open a row and add the premium, or re-import with the premium field mapped.
          </div>
        </div>
      )}

      {/* Drag-drop import + paste */}
      <HealthImportCard currentUser={currentUser} settings={settings} onImported={load} showToast={showToast} openClientProfile={openClientProfile}/>

      {/* Category pills — one-click section filters. Counts come from
          the live policy roster; the plan_type dropdown below stays
          available for finer-grained drilldown inside a category. */}
      {(() => {
        const catCounts = policies.reduce((acc, p) => {
          const c = categoryOf(p.plan_type);
          acc[c] = (acc[c] || 0) + 1;
          return acc;
        }, { aca: 0, medicare: 0, private: 0, ancillary: 0 });
        const cats = [
          { id: 'all',       label: 'All',         count: policies.length,  accent: 'slate'   },
          { id: 'aca',       label: 'ACA',         count: catCounts.aca,    accent: 'teal'    },
          { id: 'medicare',  label: 'Medicare',    count: catCounts.medicare, accent: 'blue'  },
          { id: 'private',   label: 'Private PPO', count: catCounts.private,  accent: 'violet'},
          { id: 'ancillary', label: 'Ancillary',   count: catCounts.ancillary, accent: 'amber'},
        ];
        return (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Section</span>
            {cats.map(c => {
              const active = categoryFilter === c.id;
              const activeClass =
                c.accent === 'teal'   ? 'bg-teal-600 text-white border-teal-700'     :
                c.accent === 'blue'   ? 'bg-blue-600 text-white border-blue-700'     :
                c.accent === 'violet' ? 'bg-violet-600 text-white border-violet-700' :
                c.accent === 'amber'  ? 'bg-amber-600 text-white border-amber-700'   :
                                        'bg-slate-900 text-white border-slate-900';
              return (
                <button
                  key={c.id}
                  onClick={() => chooseCategory(c.id)}
                  className={`px-3 py-1.5 text-sm font-semibold rounded-lg border transition-colors ${
                    active ? activeClass : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {c.label} <span className="text-xs opacity-70 ml-1">({c.count})</span>
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* Finer filters — plan type + status dropdowns scope INSIDE the
          chosen section. The plan-type dropdown only lists plans that
          belong to the active category so the agent can't accidentally
          pick a Medicare plan while filtered to ACA. */}
      <div className="bg-white border border-slate-200 p-3 flex items-center gap-2 flex-wrap">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by client name or plan…" className="flex-1 min-w-[220px] border border-slate-300 px-3 py-1.5 text-sm focus:border-sky-500 focus:outline-none"/>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="border border-slate-300 px-2 py-1.5 text-sm bg-white">
          <option value="all">All plan types</option>
          {HEALTH_PLAN_TYPES
            .filter(t => categoryFilter === 'all' || categoryOf(t.id) === categoryFilter)
            .map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-slate-300 px-2 py-1.5 text-sm bg-white">
          <option value="all">All statuses</option>
          {['active','pending','renewed','cancelled','lapsed'].map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
        </select>
      </div>

      {/* Policy Vault — wide table per screenshot spec */}
      <div className="bg-white border border-slate-200">
        <div className="px-4 py-2 border-b border-slate-200 flex items-baseline gap-3">
          <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">
            {categoryFilter === 'aca'       ? 'ACA Clients'
            : categoryFilter === 'medicare' ? 'Medicare Clients'
            : categoryFilter === 'private'  ? 'Private PPO Clients'
            : categoryFilter === 'ancillary'? 'Ancillary Policies'
            : 'All Policies'}
          </div>
          <div className="text-[0.6rem] uppercase tracking-wider text-slate-400">{filtered.length} shown</div>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                {['Client', 'Phone', 'Plan', 'Carrier', 'Effective Date', 'Renewal Date', 'Monthly Premium', 'Status', 'Lead Source', 'Agent', 'Notes', ''].map(h => (
                  <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={12} className="px-3 py-10 text-center text-slate-400">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={12} className="px-3 py-10 text-center text-slate-400">No health policies yet. Click <strong>+ Add Policy</strong> or drop a statement above.</td></tr>
              ) : filtered.map(p => (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-bold text-slate-900 whitespace-nowrap">
                    {p.client_name
                      ? <button
                          onClick={() => setProfileClient({ name: p.client_name, id: p.client_id || null })}
                          className="text-sky-700 hover:text-sky-900 hover:underline text-left">
                          {p.client_name}
                        </button>
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">{p.client_phone || '—'}</td>
                  <td className="px-3 py-2 text-xs">{p.plan_name || (HEALTH_PLAN_TYPE_BY_ID[p.plan_type]?.short) || '—'}</td>
                  <td className="px-3 py-2 text-xs">{p.carrier || '—'}</td>
                  <td className="px-3 py-2 text-xs font-mono text-slate-600 whitespace-nowrap">{p.effective_date || '—'}</td>
                  <td className="px-3 py-2 text-xs font-mono text-slate-600 whitespace-nowrap">{p.renewal_date || '—'}</td>
                  <td className="px-3 py-2 text-sm tabular-nums whitespace-nowrap">{p.monthly_premium != null ? fmt$(p.monthly_premium) : '—'}</td>
                  <td className="px-3 py-2"><span className={`text-[0.55rem] font-black uppercase tracking-wider px-2 py-0.5 whitespace-nowrap ${statusTone(p.status)}`}>{statusLabel(p.status || 'active')}</span></td>
                  <td className="px-3 py-2 text-xs text-slate-700 whitespace-nowrap">{p.lead_source || '—'}</td>
                  <td className="px-3 py-2 text-xs text-slate-700 whitespace-nowrap">{agentDisplay(p)}</td>
                  <td className="px-3 py-2 text-xs text-slate-500 max-w-[200px] truncate" title={p.notes || ''}>{p.notes || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap"><button onClick={() => setEditing({ ...p })} className="text-[0.6rem] font-black uppercase tracking-tight text-sky-600 hover:text-sky-800">Edit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {profileClient && (
        <HealthClientProfileModal
          clientName={profileClient.name}
          clientId={profileClient.id}
          currentUser={currentUser}
          settings={settings}
          onClose={() => setProfileClient(null)}
          onEditPolicy={(p) => { setProfileClient(null); setEditing({ ...p }); }}
          onGotoTab={onGotoTab}
          showToast={showToast}
        />
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="bg-black border-l-4 border-sky-500 px-5 py-4">
              <div className="text-sky-400 text-[0.6rem] font-black uppercase tracking-[0.2em]">Health Policy</div>
              <div className="text-white text-lg font-black uppercase tracking-tight mt-1">{editing.__new ? '+ New Policy' : editing.client_name}</div>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <label className="block"><span className="text-xs text-slate-500">Client name *</span><input value={editing.client_name || ''} onChange={e => setEditing(p => ({ ...p, client_name: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
                <label className="block"><span className="text-xs text-slate-500">Phone</span><input value={editing.client_phone || ''} onChange={e => setEditing(p => ({ ...p, client_phone: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
                <label className="block"><span className="text-xs text-slate-500">Email</span><input type="email" value={editing.client_email || ''} onChange={e => setEditing(p => ({ ...p, client_email: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
                <label className="block"><span className="text-xs text-slate-500">DOB</span><input type="date" value={editing.client_dob || ''} onChange={e => setEditing(p => ({ ...p, client_dob: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
                {/* ── Address block ──
                    Structured destination for address data — the
                    parsers (Jarvis, HealthSherpa, etc.) write here,
                    NOT to notes. The "Move address from notes →"
                    helper appears when an existing row still has an
                    address-shaped string in notes from a legacy
                    import. */}
                <div className="md:col-span-2 border border-slate-200 bg-slate-50 p-3 space-y-2">
                  <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-600 flex items-center justify-between">
                    <span>Address</span>
                    {looksLikeAddress(editing.notes) && !editing.client_address && (
                      <button
                        type="button"
                        onClick={() => setEditing(p => {
                          const parsed = parseLooseAddress(p.notes);
                          return {
                            ...p,
                            client_address:  parsed.address1  || p.client_address,
                            client_address2: parsed.address2  || p.client_address2,
                            client_city:     parsed.city      || p.client_city,
                            client_state:    parsed.state     || p.client_state,
                            client_zip:      parsed.zip       || p.client_zip,
                            notes:           parsed.remainder || null,
                          };
                        })}
                        className="text-[0.55rem] font-black uppercase tracking-wider text-sky-700 hover:text-sky-900 underline"
                      >Move address from notes →</button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <label className="block md:col-span-2">
                      <span className="text-xs text-slate-500">Street address</span>
                      <input value={editing.client_address || ''} onChange={e => setEditing(p => ({ ...p, client_address: e.target.value }))} placeholder="123 Main St" className="mt-1 w-full border border-slate-300 px-2 py-1.5"/>
                    </label>
                    <label className="block">
                      <span className="text-xs text-slate-500">Apt / Unit</span>
                      <input value={editing.client_address2 || ''} onChange={e => setEditing(p => ({ ...p, client_address2: e.target.value }))} placeholder="Apt 2045" className="mt-1 w-full border border-slate-300 px-2 py-1.5"/>
                    </label>
                    <label className="block">
                      <span className="text-xs text-slate-500">City</span>
                      <input value={editing.client_city || ''} onChange={e => setEditing(p => ({ ...p, client_city: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5"/>
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <label className="block">
                        <span className="text-xs text-slate-500">State</span>
                        <input value={editing.client_state || ''} maxLength={2} onChange={e => setEditing(p => ({ ...p, client_state: e.target.value.toUpperCase().slice(0, 2) }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 font-mono"/>
                      </label>
                      <label className="block">
                        <span className="text-xs text-slate-500">ZIP</span>
                        <input value={editing.client_zip || ''} onChange={e => setEditing(p => ({ ...p, client_zip: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 font-mono"/>
                      </label>
                    </div>
                  </div>
                </div>
                <label className="block"><span className="text-xs text-slate-500">Plan type *</span>
                  <select value={editing.plan_type || 'medicare_advantage'} onChange={e => setEditing(p => ({ ...p, plan_type: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 bg-white">
                    {HEALTH_PLAN_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </label>
                <label className="block"><span className="text-xs text-slate-500">Carrier</span>
                  <select value={editing.carrier || ''} onChange={e => setEditing(p => ({ ...p, carrier: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 bg-white">
                    <option value="">— select —</option>
                    {HEALTH_CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                <label className="block md:col-span-2"><span className="text-xs text-slate-500">Plan name</span><input value={editing.plan_name || ''} onChange={e => setEditing(p => ({ ...p, plan_name: e.target.value }))} placeholder="e.g. MA Plus PPO 2026, Silver 73 HMO" className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
                <label className="block"><span className="text-xs text-slate-500">Member ID</span><input value={editing.member_id || ''} onChange={e => setEditing(p => ({ ...p, member_id: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 font-mono"/></label>
                <label className="block"><span className="text-xs text-slate-500">Status</span>
                  <select value={editing.status || 'active'} onChange={e => setEditing(p => ({ ...p, status: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 bg-white">
                    {['active','pending','renewed','cancelled','lapsed'].map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
                  </select>
                </label>
                <label className="block"><span className="text-xs text-slate-500">Effective date</span><input type="date" value={editing.effective_date || ''} onChange={e => setEditing(p => ({ ...p, effective_date: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
                <label className="block"><span className="text-xs text-slate-500">Renewal date *</span><input type="date" value={editing.renewal_date || ''} onChange={e => setEditing(p => ({ ...p, renewal_date: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
                <label className="block"><span className="text-xs text-slate-500">Monthly premium ($)</span><input type="number" value={editing.monthly_premium || ''} onChange={e => setEditing(p => ({ ...p, monthly_premium: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 font-mono"/></label>
                <label className="block"><span className="text-xs text-slate-500">Monthly commission ($)</span><input type="number" value={editing.monthly_commission || ''} onChange={e => setEditing(p => ({ ...p, monthly_commission: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 font-mono"/></label>
                <label className="block md:col-span-2"><span className="text-xs text-slate-500">APTC / subsidy ($)</span><input type="number" value={editing.subsidy_amount || ''} onChange={e => setEditing(p => ({ ...p, subsidy_amount: e.target.value }))} placeholder="ACA only" className="mt-1 w-full border border-slate-300 px-2 py-1.5 font-mono"/></label>
                <label className="block"><span className="text-xs text-slate-500">Lead source</span><input value={editing.lead_source || ''} onChange={e => setEditing(p => ({ ...p, lead_source: e.target.value }))} placeholder="AGILITY · HealthSherpa · Referral" className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
                <label className="block"><span className="text-xs text-slate-500">Writing agent #</span><input value={editing.agent_prod_num || ''} onChange={e => setEditing(p => ({ ...p, agent_prod_num: e.target.value }))} placeholder={settings?.ownerProdNum || ''} className="mt-1 w-full border border-slate-300 px-2 py-1.5 font-mono"/></label>
                <label className="block md:col-span-2">
                  <span className="text-xs text-slate-500 flex items-center gap-2">
                    Linked plan (Plan Intelligence)
                    {carrierPlans.length === 0 && (
                      <span className="text-[0.55rem] uppercase tracking-wider text-slate-400">— upload SBCs in Plan Intelligence to populate</span>
                    )}
                  </span>
                  <select
                    value={editing.plan_id || ''}
                    onChange={e => setEditing(p => ({ ...p, plan_id: e.target.value || null }))}
                    className="mt-1 w-full border border-slate-300 px-2 py-1.5 bg-white"
                  >
                    <option value="">— not linked —</option>
                    {carrierPlans.map(cp => (
                      <option key={cp.id} value={cp.id}>
                        {[cp.carrier_short, cp.plan_name, cp.state, cp.metal_level, cp.network_type].filter(Boolean).join(' · ')}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block md:col-span-2"><span className="text-xs text-slate-500">Notes</span><textarea rows={2} value={editing.notes || ''} onChange={e => setEditing(p => ({ ...p, notes: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
              </div>
            </div>
            <div className="bg-slate-50 border-t border-slate-200 px-5 py-3 flex justify-between items-center">
              {!editing.__new ? <button onClick={() => deletePolicy(editing)} className="text-xs text-red-600 hover:text-red-800 underline">Delete</button> : <span/>}
              <div className="flex gap-2">
                <button onClick={() => setEditing(null)} className="text-xs font-bold uppercase text-slate-600 hover:text-slate-900 px-3 py-1.5">Cancel</button>
                <button onClick={() => savePolicy(editing)} disabled={!editing.client_name?.trim()} className="bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white text-xs font-black uppercase tracking-tight px-4 py-2">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// HEALTH CLIENT PROFILE MODAL — 360° view for one health client.
//
// Triggered when the agent clicks a client name in the Policy Vault.
// Loads everything we know about this person across the health
// vertical into one focused modal:
//
//   1. Identity + contact   (from clients table OR best policy row)
//   2. Every policy ever    (active, pending, cancelled, lapsed)
//   3. Linked plan benefits (carrier_plans joined via plan_id)
//   4. Loss events           (health_loss_events on any of their policies)
//   5. Plan Assistant Q&A   (inline RAG against the most recent active
//                            policy's linked plan)
//
// Client identification: we prefer client_id when set; otherwise we
// match by client_name (case-insensitive, trimmed). Phone is used as
// a tie-breaker when multiple distinct clients share a name.
// ════════════════════════════════════════════════════════════════════
// ── Profile-modal computation helpers ──────────────────────────────
function ageFromDobIso(dobIso) {
  if (!dobIso) return null;
  const t = new Date();
  const b = new Date(dobIso);
  if (Number.isNaN(b.getTime())) return null;
  let a = t.getFullYear() - b.getFullYear();
  const m = t.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < b.getDate())) a--;
  return a >= 0 ? a : null;
}
function formatPhonePretty(phone) {
  if (!phone) return null;
  const d = String(phone).replace(/\D/g, '');
  if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  if (d.length === 11 && d[0] === '1') return `(${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`;
  return phone;
}
function detectCoverage(policies) {
  const active = (policies || []).filter(p => (p.status || 'active') === 'active');
  const has = (re, types) => active.some(p => {
    if (types?.includes(p.plan_type)) return true;
    if (!re) return false;
    return re.test(p.plan_name || '') || re.test(p.plan_type || '');
  });
  return {
    health:            has(null, ['aca_marketplace','short_term','medicare_advantage','medicare_supplement','medicare_pdp','private_ppo']),
    dental:            has(/dental/i, ['dental_vision']),
    vision:            has(/vision/i, ['dental_vision']),
    accident:          has(/accident/i),
    hospitalIndemnity: has(/hospital\s*indemnity/i, ['hospital_indemnity']),
    criticalIllness:   has(/critical\s*illness/i),
    life:              has(/term\s*life|whole\s*life|iul|final\s*expense/i),
    incomeProtection:  has(/disability|income\s*protection/i),
  };
}
// Conservative monthly-commission ranges for ancillary cross-sells —
// powers the "Potential Monthly Revenue" line in the Coverage Stack
// Builder. Tuned to typical small-policy commission, not enrollment
// peaks; we'd rather under-promise than dangle a number the agent
// can't realistically hit.
const ANCILLARY_COMMISSION_RANGE = {
  dental:            { low: 3,  high: 10 },
  vision:            { low: 2,  high: 5  },
  accident:          { low: 5,  high: 15 },
  hospitalIndemnity: { low: 8,  high: 25 },
  criticalIllness:   { low: 5,  high: 20 },
  life:              { low: 10, high: 60 },
};
function estimatePotentialMrr(coverage) {
  let low = 0, high = 0;
  const lines = [];
  for (const [key, range] of Object.entries(ANCILLARY_COMMISSION_RANGE)) {
    if (!coverage[key]) { low += range.low; high += range.high; lines.push({ key, ...range }); }
  }
  return { low, high, lines };
}
const STACK_LINES = ['health','dental','vision','accident','hospitalIndemnity','life'];
const STACK_LABEL = {
  health: 'ACA / Medicare Health',
  dental: 'Dental',
  vision: 'Vision',
  accident: 'Accident',
  hospitalIndemnity: 'Hospital Indemnity',
  criticalIllness: 'Critical Illness',
  life: 'Life Insurance',
  incomeProtection: 'Income Protection (Disability)',
};
function computeStackScore(coverage) {
  const filled = STACK_LINES.filter(l => coverage[l]).length;
  return {
    filled, total: STACK_LINES.length,
    pct: Math.round((filled / STACK_LINES.length) * 100),
    items: STACK_LINES.map(l => ({ key: l, has: coverage[l], label: STACK_LABEL[l] })),
  };
}
function computeRetentionScore({ age, activeCount, coverage }) {
  let score = 50;
  if (age != null && age < 35)       score += 25;
  else if (age != null && age < 50)  score += 15;
  else if (age != null && age < 65)  score += 5;
  if (activeCount > 1)               score += 10;
  if (coverage.health)               score += 5;
  if (coverage.life)                 score += 10;
  score = Math.min(100, Math.max(0, score));
  const band = score >= 75 ? 'High' : score >= 50 ? 'Medium' : 'Low';
  return { score, band };
}
function computeMissingData(primary) {
  if (!primary) return [];
  const m = [];
  if (!primary.member_id)        m.push({ key: 'member_id', label: 'Member ID' });
  if (!primary.effective_date)   m.push({ key: 'effective_date', label: 'Effective Date' });
  if (!primary.renewal_date)     m.push({ key: 'renewal_date', label: 'Renewal Date' });
  if (primary.monthly_premium    == null) m.push({ key: 'monthly_premium', label: 'Premium' });
  if (primary.plan_type === 'aca_marketplace' && primary.subsidy_amount == null) m.push({ key: 'subsidy_amount', label: 'Subsidy' });
  if (primary.monthly_commission == null) m.push({ key: 'monthly_commission', label: 'Commission' });
  return m;
}
function computeRecommendedActions({ missingData, coverage, age }) {
  const a = [];
  if (missingData.length > 0) {
    a.push({ id: 'verify_details',  label: 'Verify policy details (carrier portal / HealthSherpa)' });
    if (missingData.some(m => m.key === 'monthly_premium' || m.key === 'subsidy_amount'))
      a.push({ id: 'update_premium', label: 'Update premium + subsidy from latest statement' });
    if (missingData.some(m => m.key === 'monthly_commission'))
      a.push({ id: 'update_commission', label: 'Pull commission amount from latest carrier statement' });
    if (missingData.some(m => m.key === 'effective_date' || m.key === 'renewal_date'))
      a.push({ id: 'fix_dates', label: 'Fill in effective + renewal dates' });
  }
  if (!coverage.dental)            a.push({ id: 'offer_dental',   label: 'Offer dental coverage' });
  if (!coverage.vision)            a.push({ id: 'offer_vision',   label: 'Offer vision coverage' });
  if (!coverage.accident)          a.push({ id: 'offer_accident', label: 'Discuss accident protection' });
  if (!coverage.hospitalIndemnity) a.push({ id: 'offer_hi',       label: 'Discuss hospital indemnity' });
                                   a.push({ id: 'annual_review',  label: 'Schedule annual review' });
  if (!coverage.life && age != null && age >= 25)
                                   a.push({ id: 'life_followup',  label: 'Add life insurance follow-up task' });
  return a;
}

function HealthClientProfileModal({ clientName, clientId, currentUser, settings = {}, onClose, onEditPolicy, onGotoTab, showToast }) {
  // The cockpit's sequenced-play renders navigate-kind steps as
  // clickable; clicking dispatches ct:close-health-profile-modal so
  // we can hand off cleanly to the target tab.
  React.useEffect(() => {
    const close = () => { try { onClose?.(); } catch {} };
    window.addEventListener('ct:close-health-profile-modal', close);
    return () => window.removeEventListener('ct:close-health-profile-modal', close);
  }, [onClose]);
  const [policies,  setPolicies]   = React.useState([]);
  const [plans,     setPlans]      = React.useState({});  // map plan_id → carrier_plans row
  const [losses,    setLosses]     = React.useState([]);
  const [client,    setClient]     = React.useState(null);
  const [loading,   setLoading]    = React.useState(true);
  // Client 360 tabbed layout — matches the FTPIG demo grouping.
  // Overview holds the everyday agent view (cockpit + snapshot +
  // policies + scoring). Specific tabs surface a single deep-dive
  // section. Pre-Call Briefing stays ABOVE the tab bar so it's
  // always visible.
  const [profileTab, setProfileTab] = React.useState('overview');
  const [reviewOpen, setReviewOpen] = React.useState(false);
  const showInTab = React.useCallback(
    (t) => profileTab === t,
    [profileTab]
  );

  const ownerId = currentUser?.id;
  const fmt$ = (n) => '$' + Math.round(Number(n) || 0).toLocaleString();

  React.useEffect(() => {
    if (!ownerId || (!clientName && !clientId)) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      // 1. Policies — match by client_id if we have it, else by name.
      let polQuery = supabase
        .from('health_policies')
        .select('*')
        .eq('owner_id', ownerId);
      if (clientId)        polQuery = polQuery.eq('client_id', clientId);
      else if (clientName) polQuery = polQuery.ilike('client_name', clientName.trim());
      const { data: pols } = await polQuery.order('effective_date', { ascending: false, nullsFirst: false });
      if (cancelled) return;
      setPolicies(pols || []);

      // 2. Linked plans — one query for the distinct plan_ids on these
      // policies so each card can render full benefits inline.
      const planIds = Array.from(new Set((pols || []).map(p => p.plan_id).filter(Boolean)));
      if (planIds.length > 0) {
        const { data: planRows } = await supabase
          .from('carrier_plans')
          .select('*')
          .in('id', planIds);
        if (!cancelled) {
          const map = {};
          (planRows || []).forEach(pl => { map[pl.id] = pl; });
          setPlans(map);
        }
      }

      // 3. Loss events
      const policyIds = (pols || []).map(p => p.id);
      if (policyIds.length > 0) {
        const { data: lossRows } = await supabase
          .from('health_loss_events')
          .select('id, policy_id, reason, notes, lost_at, acknowledged_at, monthly_commission')
          .eq('owner_id', ownerId)
          .in('policy_id', policyIds)
          .order('lost_at', { ascending: false });
        if (!cancelled) setLosses(lossRows || []);
      }

      // 4. clients row (demographics) — best-effort
      if (clientId) {
        const { data: c } = await supabase
          .from('clients')
          .select('id, name, phone, email, dob, address')
          .eq('owner_id', ownerId)
          .eq('id', clientId)
          .maybeSingle();
        if (!cancelled && c) setClient(c);
      } else if (clientName) {
        const { data: c } = await supabase
          .from('clients')
          .select('id, name, phone, email, dob, address')
          .eq('owner_id', ownerId)
          .ilike('name', clientName.trim())
          .maybeSingle();
        if (!cancelled && c) setClient(c);
      }

      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [ownerId, clientName, clientId]);

  // Top-of-card summary fields — derive from the most recently
  // effective active policy; fall back to first row if none active.
  const primary = React.useMemo(() => {
    const active = policies.find(p => (p.status || 'active') === 'active');
    return active || policies[0] || null;
  }, [policies]);

  const totalActiveMrr = policies
    .filter(p => (p.status || 'active') === 'active')
    .reduce((s, p) => s + (Number(p.monthly_commission) || 0), 0);
  const totalActivePremium = policies
    .filter(p => (p.status || 'active') === 'active')
    .reduce((s, p) => s + (Number(p.monthly_premium) || 0), 0);

  const lifetimeCommission = policies.reduce(
    (s, p) => s + (Number(p.monthly_commission) || 0) * monthsBetween(p.effective_date, p.termination_date || new Date()), 0
  );

  // Status counters
  const counts = policies.reduce((acc, p) => {
    const s = (p.status || 'active').toLowerCase();
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  // Client-360 header data derived up-front so the header render is
  // clean. Matches the FTPIG demo's identity strip: avatar + name +
  // sector + age + county + Health chip + Risk chip.
  const _hdrAge = (() => {
    const dob = primary?.client_dob || client?.dob;
    if (!dob) return null;
    try {
      const d = new Date(dob + 'T00:00:00Z');
      if (Number.isNaN(d.getTime())) return null;
      const now = new Date();
      let a = now.getUTCFullYear() - d.getUTCFullYear();
      const m = now.getUTCMonth() - d.getUTCMonth();
      if (m < 0 || (m === 0 && now.getUTCDate() < d.getUTCDate())) a--;
      return a;
    } catch { return null; }
  })();
  const _hdrSector = (() => {
    if (policies.some(p => /medic|advantage|supplement/i.test(String(p.plan_type || '')))) return 'Medicare';
    return 'ACA';
  })();
  const _hdrLocation = (() => {
    const county = primary?.client_county || '';
    const state = primary?.client_state || '';
    if (county && state) return `${county} Co., ${state}`;
    return state || '';
  })();
  // Fall back to the primary policy's client_name when the caller
  // doesn't thread it in as a prop — the health-tab routing at
  // line 5918 passes clientName={null} because it only knows the
  // client_id at open time. Without this fallback the header
  // renders "?" for the avatar and "—" for the name.
  const _hdrName = clientName || client?.name || primary?.client_name || '';
  const _hdrInitials = String(_hdrName || '?')
    .trim().split(/\s+/).slice(0, 2).map(p => p[0] || '').join('').toUpperCase();
  // Simple health + risk scoring for the header chip (kept in-line so
  // the chip renders without extra fetches — matches ClientHealthScoreBadge
  // shape but simplified for header context).
  const _hdrHealth = (() => {
    const active = counts.active || 0;
    const lapsed = (counts.cancelled || 0) + (counts.lapsed || 0);
    let s = 0;
    if (active > 0) s += 40;
    if (lapsed === 0) s += 20;
    // Coverage completeness proxy
    const products = new Set(policies.filter(p => p.status === 'active').map(p => String(p.plan_type || '').toLowerCase()));
    const gaps = ['dental','vision','life','accident','critical'].filter(k => !Array.from(products).some(pt => pt.includes(k))).length;
    s += Math.min(20, (5 - gaps) * 4);
    // Renewal window
    const renewals = policies.filter(p => p.status === 'active' && p.renewal_date).map(p => p.renewal_date).sort();
    if (renewals[0]) {
      const days = Math.round((new Date(renewals[0]).getTime() - Date.now()) / 86_400_000);
      if (days > 30) s += 20;
      else if (days > 7) s += 12;
      else if (days > 0) s += 6;
    } else s += 20;
    return Math.max(0, Math.min(100, s));
  })();
  const _hdrRisk = Math.max(0, Math.min(100, 100 - _hdrHealth + ((counts.lapsed || 0) > 0 ? 15 : 0)));

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white w-full max-w-5xl my-8 shadow-2xl rounded-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* HEADER — matches the FTPIG demo: dark navy panel, big
            initials avatar on left, client name in caps, then a chip
            row with sector · age · location · Health · Risk. */}
        <div className="bg-[#24274E] px-5 py-4 flex items-center gap-4 sticky top-0 z-20 rounded-t-2xl">
          <div className="shrink-0 w-14 h-14 rounded-xl bg-[#2e3266] text-white flex items-center justify-center font-black text-[19px] tracking-wide" style={{ fontFamily: 'Bebas Neue, system-ui, sans-serif' }}>
            {_hdrInitials || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white text-[26px] font-black uppercase tracking-tight leading-none" style={{ fontFamily: 'Bebas Neue, system-ui, sans-serif' }}>
              {_hdrName || '—'}
            </div>
            <div className="mt-2 flex items-center gap-4 flex-wrap text-[12px]">
              <span className={`inline-block font-black uppercase tracking-wider rounded-full px-2.5 py-0.5 text-[10.5px] ${
                _hdrSector === 'Medicare' ? 'bg-amber-500/25 text-amber-200' : 'bg-sky-500/25 text-sky-200'
              }`}>
                {_hdrSector}
              </span>
              {_hdrAge != null && (
                <span className="text-slate-300">
                  <span className="text-slate-500 text-[10.5px] uppercase tracking-wider font-bold mr-1">Age</span>
                  <span className="text-white font-bold">{_hdrAge}</span>
                </span>
              )}
              {_hdrLocation && <span className="text-slate-300">{_hdrLocation}</span>}
              <span className="text-slate-300">
                <span className="text-slate-500 text-[10.5px] uppercase tracking-wider font-bold mr-1">Health</span>
                <span className={`font-bold ${_hdrHealth >= 75 ? 'text-emerald-300' : _hdrHealth >= 50 ? 'text-amber-300' : 'text-rose-300'}`}>{_hdrHealth}</span>
              </span>
              <span className="text-slate-300">
                <span className="text-slate-500 text-[10.5px] uppercase tracking-wider font-bold mr-1">Risk</span>
                <span className={`font-bold ${_hdrRisk >= 50 ? 'text-rose-300' : _hdrRisk >= 20 ? 'text-amber-300' : 'text-emerald-300'}`}>{_hdrRisk}%</span>
              </span>
              <TurningSixtyFiveCountdown
                clientId={clientId || client?.id}
                ownerId={ownerId}
                dob={primary?.client_dob || client?.dob}
              />
            </div>
          </div>
          {_hdrSector === 'Medicare' && (
            <button
              onClick={() => setReviewOpen(true)}
              className="shrink-0 text-[10.5px] font-bold uppercase tracking-wider bg-[#e8a020] hover:bg-[#d49318] text-slate-900 rounded-lg px-3 py-2"
              title="Start 7-step annual review"
            >
              Start review
            </button>
          )}
          <button
            onClick={onClose}
            className="shrink-0 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-lg"
            title="Close"
          >✕</button>
        </div>

        {/* Body — professional sectioned layout */}
        <div className="p-5 space-y-6">
          {loading && <div className="text-sm text-slate-500 italic">Loading profile…</div>}
          {!loading && policies.length === 0 && (
            <div className="border-2 border-dashed border-slate-300 px-6 py-12 text-center text-slate-500 text-sm">
              No policies on file for this client.
            </div>
          )}

          {!loading && policies.length > 0 && (() => {
            const age              = ageFromDobIso(primary?.client_dob || client?.dob);
            const coverage         = detectCoverage(policies);
            const stack            = computeStackScore(coverage);
            const potentialMrr     = estimatePotentialMrr(coverage);
            const missingData      = computeMissingData(primary);
            const retention        = computeRetentionScore({ age, activeCount: counts.active || 0, coverage });
            const recAction        = computeRecommendedActions({ missingData, coverage, age });
            const leadSources      = Array.from(new Set(policies.map(p => p.lead_source).filter(Boolean)));
            const agentNpns        = Array.from(new Set(policies.map(p => p.agent_prod_num).filter(Boolean)));

            return (
              <>
                {/* ─ PRE-CALL BRIEFING ─ blueprint AI Chief of Staff
                    pre-call surface. Renders BEFORE the tabs so the
                    agent sees "you're about to call this person —
                    here's what matters" briefing regardless of which
                    tab they're on. */}
                {(clientId || client?.id) && (
                  <PreCallBriefingPanel clientId={clientId || client?.id}/>
                )}

                {/* ─ CLIENT 360 TAB BAR ─ matches FTPIG demo:
                    horizontal row, minimal style, gold underline on
                    the active tab. Sticky under the header. */}
                <div className="sticky top-[88px] bg-white border-b border-slate-200 -mx-5 px-5 z-10 flex gap-4 overflow-x-auto">
                  {[
                    ['overview',   'Overview'],
                    ['medical',    'Medical'],
                    ['household',  'Household'],
                    ['relationship', 'Relationship'],
                    ['compliance', 'Compliance'],
                    ['documents',  'Documents'],
                    ['timeline',   'Timeline'],
                    ['crosssell',  'Cross-Sell'],
                  ].map(([id, label]) => (
                    <button
                      key={id}
                      onClick={() => setProfileTab(id)}
                      className={`text-[13px] font-semibold py-3 border-b-2 whitespace-nowrap transition-colors ${
                        profileTab === id
                          ? 'text-slate-900 border-[#e8a020]'
                          : 'text-slate-500 border-transparent hover:text-slate-800'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {/* ─ SNAPSHOT ─ matches the FTPIG demo's plain grid of
                    identity + preferences. Reads from the primary
                    health_policies row and any subsidy snapshot data
                    already loaded. Renders only on Overview. */}
                {showInTab('overview') && (
                  <ClientSnapshotBlock
                    primary={primary}
                    client={client}
                    clientId={clientId || client?.id}
                    ownerId={ownerId}
                  />
                )}

                {/* ─ CONTACT ─ FTPIG demo's Contact block. Renders
                    only on Overview. */}
                {showInTab('overview') && (
                  <ClientContactBlock primary={primary} client={client}/>
                )}

                {/* ─ ADDRESS + LAST CONTACT ─ demo match */}
                {showInTab('overview') && (
                  <OverviewAddressBlock
                    primary={primary}
                    clientId={clientId || client?.id}
                    ownerId={ownerId}
                  />
                )}

                {/* ─ INSURANCE SUMMARY ─ demo match: carrier, plan,
                    metal, effective, renewal, premium, APTC,
                    deductible, MOOP. */}
                {showInTab('overview') && primary && (
                  <OverviewInsuranceBlock primary={primary}/>
                )}

                {/* ─ AI MISSING INFO DETECTOR ─ scores identity +
                    context fields the compliance packet expects,
                    surfaces gaps before submission. */}
                {showInTab('overview') && (clientId || client?.id) && ownerId && (
                  <MissingInfoDetector
                    clientId={clientId || client?.id}
                    ownerId={ownerId}
                    onGotoTab={onGotoTab}
                  />
                )}

                {/* ─ RELATIONSHIP TAB ─ FTPIG demo: memory paragraph
                    + Who they are grid. Was previously empty. */}
                {showInTab('relationship') && (clientId || client?.id) && ownerId && (
                  <RelationshipTabView
                    clientId={clientId || client?.id}
                    ownerId={ownerId}
                  />
                )}

                {/* ─ RELATIONSHIP SCORE ─ AI Client Success Manager's
                    signature widget. Reuses the reusable meter — same
                    pattern as the Compliance Readiness Meter. */}
                {showInTab('relationship') && (clientId || client?.id) && ownerId && (
                  <RelationshipScoreInline
                    clientId={clientId || client?.id}
                    ownerId={ownerId}
                  />
                )}

                {/* ─ MEDICAL · PROVIDERS + PRESCRIPTIONS + CONDITIONS ─
                    Demo-shape structured block, sits above the
                    freeform MedicalProfilePanel. */}
                {showInTab('medical') && (clientId || client?.id) && ownerId && (
                  <MedicalTabView
                    clientId={clientId || client?.id}
                    ownerId={ownerId}
                  />
                )}

                {/* ─ HOUSEHOLD · Per-person coverage map ─ green/red */}
                {showInTab('household') && (
                  <HouseholdCoverageMap
                    policies={policies}
                    primary={primary}
                    clientName={clientName}
                    clientId={clientId || client?.id}
                    ownerId={ownerId}
                  />
                )}

                {/* ─ HOUSEHOLD · Caregiver / POA / authorized rep ─ */}
                {showInTab('household') && (clientId || client?.id) && ownerId && (
                  <CaregiverPortalBlock
                    clientId={clientId || client?.id}
                    ownerId={ownerId}
                  />
                )}

                {/* ─ COMPLIANCE · 7-item ready-to-submit checklist ─ */}
                {showInTab('compliance') && (clientId || client?.id) && (
                  <ComplianceChecklistBlock clientId={clientId || client?.id}/>
                )}

                {/* ─ DOCUMENTS · 4-item required-docs checklist ─ */}
                {showInTab('documents') && (clientId || client?.id) && ownerId && (
                  <DocumentChecklistBlock
                    clientId={clientId || client?.id}
                    ownerId={ownerId}
                  />
                )}

                {showInTab('documents') && (clientId || client?.id) && (
                  <div style={{ marginTop: 12 }}>
                    <AnocUploadBlock clientId={clientId || client?.id}/>
                  </div>
                )}

                {/* ─ CROSS-SELL · dollar-per-year gap list ─ */}
                {showInTab('crosssell') && (
                  <CrossSellOpportunitiesBlock
                    policies={policies}
                    showToast={showToast}
                  />
                )}

                {/* ─ AI PROFILE COCKPIT ─ duplicates + AEP clock + money + talk track. */}
                {showInTab('overview') && (clientId || client?.id) && (
                  <HealthProfileCockpit
                    clientId={clientId || client?.id}
                    policies={policies}
                    onPoliciesChanged={() => {
                      // Trigger the modal's useEffect to refetch policies +
                      // recompute downstream counters when a merge happens.
                      // Cheapest path: bounce the cancellation key by toggling
                      // a local state... but the modal already keys on
                      // [ownerId, clientName, clientId]. Easiest: ask the
                      // user to refresh by reopening, OR force-reload here.
                      // We'll do a soft reload via window event the
                      // surrounding HealthClientsView listens for.
                      try { window.dispatchEvent(new CustomEvent('ct:health-client-changed', { detail: { clientId: clientId || client?.id } })); } catch {}
                    }}
                    onGotoTab={onGotoTab}
                    showToast={showToast}
                  />
                )}

                {/* ─ HOUSEHOLD + BENEFICIARIES + COMPLIANCE TRAIL ─ */}
                {showInTab('household') && (
                <SectionBlock number="◆" title="Household" subtitle="Family roster · beneficiaries · renewal countdown · disclosure trail">
                  <HealthHouseholdPanel
                    clientId={clientId || client?.id}
                    currentUser={currentUser}
                    showToast={showToast}
                  />
                </SectionBlock>
                )}

                {/* ─ MEDICAL PROFILE ─ blueprint modules #2 Medical
                    Profile, #13 Medication Monitor, #14 Doctor
                    Intelligence, #15 AI Client Memory. All four
                    collapse into one data model (client_medical_profile,
                    migration 136) and one panel. Soft-degrades to a
                    "run the migration" hint until the table exists. */}
                {showInTab('medical') && (clientId || client?.id) && ownerId && (
                  <SectionBlock number="◆" title="Medical profile" subtitle="Doctors · prescriptions · care team · personal fabric — the AI checks these on every renewal + surfaces them pre-call">
                    <MedicalProfilePanel
                      clientId={clientId || client?.id}
                      ownerId={ownerId}
                      showToast={showToast}
                    />
                  </SectionBlock>
                )}

                {/* ─ COMPLIANCE CENTER ─ blueprint module #4. Scores
                    every client 0-100 and shows the safe-to-submit
                    badge. Same underlying data as ClientFormsPanel
                    (audit packet). */}
                {showInTab('compliance') && (clientId || client?.id) && (
                  <SectionBlock number="◆" title="Compliance Center" subtitle="Blueprint #4 — the score that decides whether a submission is safe. Nothing should submit until this hits 100.">
                    <ClientComplianceScorePanel clientId={clientId || client?.id}/>
                  </SectionBlock>
                )}

                {/* ─ ACA COMPLIANCE AI ─ blueprint module #5. Runs
                    the pre-submission rule suite over 15+ conditions
                    (income vs FPL, address, SEP eligibility,
                    duplicate applications, missing consent, stale
                    docs, name-reuse fraud shape). Complements #4:
                    #4 = "are the compliance ARTIFACTS present"
                    #5 = "does the DATA on the record look right" */}
                {showInTab('compliance') && (clientId || client?.id) && (
                  <SectionBlock number="◆" title="ACA Compliance AI" subtitle="Blueprint #5 — pre-submission rule pass. Catches the data-level violations that void contracts BEFORE the enroll button is pushed.">
                    <AcaComplianceCheckPanel clientId={clientId || client?.id}/>
                  </SectionBlock>
                )}

                {/* ─ DOCUMENT VAULT ─ blueprint module #7. Upload +
                    classify + OCR + full-text search over every doc
                    tied to this client. Storage in Supabase private
                    bucket; OCR via Claude vision. Soft-degrades to
                    a setup banner until migration 137 + the
                    client_documents storage bucket are both in
                    place. */}
                {showInTab('documents') && (clientId || client?.id) && (
                  <SectionBlock number="◆" title="Document Vault" subtitle="Blueprint #7 — every document, every client, OCR-searchable. Driver's license · tax forms · policy docs · carrier correspondence.">
                    <ClientDocumentVaultPanel clientId={clientId || client?.id} showToast={showToast}/>
                  </SectionBlock>
                )}

                {/* ─ BOOK-OF-BUSINESS HEALTH SCORE ─ blueprint #18.
                    Seven-dimension per-client scoring computed
                    client-side from data the modal already has. */}
                {showInTab('overview') && (clientId || client?.id) && ownerId && (
                  <SectionBlock number="◆" title="Client Health Score" subtitle="Blueprint #18 — seven-dimension read of comms, renewal, referral, cross-sell, compliance, payment, engagement.">
                    <ClientHealthScoreBadge
                      clientId={clientId || client?.id}
                      ownerId={ownerId}
                      policies={policies}
                    />
                  </SectionBlock>
                )}

                {/* ─ TIMELINE ─ clean demo-shape dot / date / text list.
                    Reads the same sources ClientAITimeline used
                    (audit events + messages + policies) but renders
                    them in the FTPIG demo's simpler visual. */}
                {showInTab('timeline') && (clientId || client?.id) && (
                  <ContactWorkflowCard clientId={clientId || client?.id} targetType="client"/>
                )}
                {showInTab('timeline') && (clientId || client?.id) && ownerId && (
                  <HealthTimelineBlock
                    clientId={clientId || client?.id}
                    ownerId={ownerId}
                  />
                )}

                {/* ─ FORMS & DOCUMENTS — every signed compliance form
                       tied to this client + missing-form CTAs. Source
                       of truth is /api/audit/packet so panel + audit
                       packet stay 100% consistent. ─ */}
                {showInTab('compliance') && (clientId || client?.id) && (
                  <SectionBlock
                    number="◆"
                    title="Forms & Documents"
                    subtitle="Every compliance form on this client · signed timestamps · server-captured IP · cert downloads"
                    headerAction={
                      <a
                        href={`/api/audit/packet?client_id=${encodeURIComponent(clientId || client?.id)}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-[10px] font-bold uppercase tracking-wider text-sky-700 hover:text-sky-900"
                        title="Open the full audit packet — every event, every signature, every server-captured timestamp"
                      >
                        ⬇ Download audit packet (JSON)
                      </a>
                    }
                  >
                    <ClientFormsPanel
                      clientId={clientId || client?.id}
                      clientName={clientName || client?.name}
                      onCaptureNow={(formType) => {
                        // Stash prefill on window so SOATrackingView can pick it
                        // up when the SOA tab mounts. Then close the modal and
                        // navigate to the SOA tab.
                        try {
                          window.__soaSendPrefill = {
                            form_type: formType,
                            beneficiary_name:  clientName || client?.name || '',
                            beneficiary_phone: primary?.client_phone || client?.phone || '',
                            beneficiary_email: primary?.client_email || client?.email || '',
                            beneficiary_id:    clientId || client?.id || null,
                          };
                        } catch {}
                        onClose?.();
                        onGotoTab?.('soa');
                      }}
                      showToast={showToast}
                    />
                  </SectionBlock>
                )}

                {/* ─ 1. CLIENT SNAPSHOT ─ */}
                {showInTab('overview') && (
                <SectionBlock number="1" title="Client Snapshot" subtitle="The 30-second view">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <SnapshotCard heading="Coverage Status" accent="emerald">
                      <SnapRow icon="✓" tone="emerald" label="Active Policies" value={counts.active || 0}/>
                      <SnapRow icon="×" tone="rose"    label="Cancelled / Lapsed" value={(counts.cancelled || 0) + (counts.lapsed || 0)}/>
                      <SnapRow icon="·" tone="slate"   label="Total Policies" value={policies.length}/>
                    </SnapshotCard>
                    <SnapshotCard heading="Revenue Metrics" accent="sky">
                      <SnapRow label="Monthly Premium" value={totalActivePremium > 0 ? fmt$(totalActivePremium) : `$0 (Missing Data)`}/>
                      <SnapRow label="Monthly Commission" value={totalActiveMrr > 0 ? fmt$(totalActiveMrr) : `$0 (Missing Data)`}/>
                      <SnapRow label="Estimated Lifetime" value={fmt$(lifetimeCommission)}/>
                    </SnapshotCard>
                    <SnapshotCard heading="Assigned Agent" accent="slate">
                      <SnapRow label="Writing Agent NPN" value={agentNpns.length > 0 ? agentNpns.join(', ') : (settings?.ownerProdNum || '—')}/>
                      <SnapRow label="Operator" value={settings?.ownerName || currentUser?.name || '—'}/>
                    </SnapshotCard>
                    <SnapshotCard heading="Lead Sources" accent="violet">
                      {leadSources.length === 0
                        ? <div className="text-xs italic text-slate-500">No lead source on file.</div>
                        : <ul className="text-xs space-y-0.5">{leadSources.map(s => <li key={s} className="text-slate-800">• {s}</li>)}</ul>}
                    </SnapshotCard>
                  </div>
                </SectionBlock>
                )}

                {/* ─ 2. POLICY CENTER ─ */}
                {showInTab('overview') && (
                <SectionBlock number="2" title="Policy Center" subtitle={`${policies.length} polic${policies.length === 1 ? 'y' : 'ies'} on file`}>
                  <div className="space-y-3">
                    {policies.map((p, i) => {
                      const linkedPlan = p.plan_id ? plans[p.plan_id] : null;
                      const policyLosses = losses.filter(l => l.policy_id === p.id);
                      return (
                        <PolicyCard
                          key={p.id}
                          index={i + 1}
                          policy={p}
                          linkedPlan={linkedPlan}
                          losses={policyLosses}
                          onEdit={() => onEditPolicy(p)}
                          fmt$={fmt$}
                          showToast={showToast}
                        />
                      );
                    })}
                  </div>
                </SectionBlock>
                )}

                {/* ─ 3. AI COVERAGE ANALYSIS ─ */}
                {showInTab('crosssell') && (
                <SectionBlock number="3" title="AI Coverage Analysis" subtitle="What they have, what's open, and what to sell next">
                  <CrossSellGapAnalysis policies={policies} primary={primary}/>
                </SectionBlock>
                )}

                {/* ─ 4. COVERAGE STACK BUILDER ─ */}
                {showInTab('crosssell') && (
                <SectionBlock number="4" title="Coverage Stack Builder" subtitle="The right way to think about the next conversation">
                  <CoverageStackPanel stack={stack} potentialMrr={potentialMrr}/>
                </SectionBlock>
                )}

                {/* ─ 5. AI RETENTION SCORE ─
                    Sections 5 (Data Quality Alerts) and 7 (Next Agent
                    Actions) were retired — the cockpit at the top of
                    the modal carries the authoritative Enrich panel
                    (replaces Data Quality) and the Sequenced Play
                    (replaces the flat checklist). Keeping them caused
                    duplication and operator confusion about which
                    list was the source of truth. */}
                {showInTab('crosssell') && (
                <SectionBlock number="5" title="AI Retention Score" subtitle="Likelihood × CLV">
                  <RetentionScorePanel score={retention} age={age} coverage={coverage} counts={counts}/>
                </SectionBlock>
                )}

                {/* Plan Assistant — keeps its own header */}
                {showInTab('overview') && primary?.plan_id && plans[primary.plan_id] && (
                  <SectionBlock number="6" title="Plan Assistant" subtitle="Grounded SBC Q&A scoped to the active linked plan">
                    <ClientPlanAssistant client={{ displayName: clientName }} currentUser={currentUser}/>
                  </SectionBlock>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {reviewOpen && (
        <AnnualReviewWizard
          clientId={clientId || client?.id}
          ownerId={ownerId}
          clientName={clientName || client?.name}
          onClose={() => setReviewOpen(false)}
          showToast={showToast}
        />
      )}
    </div>
  );
}

// ── Client 360 Snapshot block — FTPIG demo grid. Reads primary
//    health_policies row for identity + subsidy_snapshots for income
//    + client_medical_profile for language/contact preferences.
// Inline wrapper — loads Relationship Meter inputs and renders the
// detail panel. Kept adjacent to ClientSnapshotBlock and the other
// modal-scoped fetchers so the pattern is consistent.
function RelationshipScoreInline({ clientId, ownerId }) {
  const [state, setState] = React.useState({ loading: true });
  React.useEffect(() => {
    if (!clientId || !ownerId) { setState({ loading: false }); return; }
    let cancelled = false;
    (async () => {
      const inputs = await loadRelationshipInputs(clientId, ownerId);
      if (cancelled) return;
      setState({ loading: false, ...computeRelationshipScore(inputs) });
    })();
    return () => { cancelled = true; };
  }, [clientId, ownerId]);
  if (state.loading) return null;
  return <RelationshipDetailPanel pct={state.pct} bucket={state.bucket} factors={state.factors} weakestFactor={state.weakestFactor}/>;
}

function ClientSnapshotBlock({ primary, client, clientId, ownerId }) {
  const [profile, setProfile] = React.useState(null);
  const [income, setIncome]   = React.useState(null);

  React.useEffect(() => {
    if (!clientId || !ownerId) return;
    let cancelled = false;
    (async () => {
      try {
        const [{ data: mp }, { data: sub }] = await Promise.all([
          supabase.from('client_medical_profile')
            .select('preferred_language, preferred_contact_method, best_time_to_call')
            .eq('owner_id', ownerId).eq('client_id', clientId).maybeSingle(),
          supabase.from('aca_subsidy_snapshots')
            .select('annual_income, household_size, created_at')
            .eq('client_id', clientId)
            .order('created_at', { ascending: false })
            .limit(1).maybeSingle(),
        ]);
        if (cancelled) return;
        setProfile(mp || null);
        setIncome(sub || null);
      } catch { /* swallow — soft-degrade */ }
    })();
    return () => { cancelled = true; };
  }, [clientId, ownerId]);

  const dob = primary?.client_dob || client?.dob;
  const fmtDob = (d) => {
    if (!d) return null;
    try { return new Date(d + 'T00:00:00Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return d; }
  };
  const turns65Days = (() => {
    if (!dob) return null;
    try {
      const d = new Date(dob + 'T00:00:00Z');
      const b65 = new Date(Date.UTC(d.getUTCFullYear() + 65, d.getUTCMonth(), d.getUTCDate()));
      return Math.round((b65.getTime() - Date.now()) / 86_400_000);
    } catch { return null; }
  })();

  const cell = (label, value) => (
    <div>
      <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-[14px] text-slate-900 mt-1">{value || <span className="text-slate-400 italic">—</span>}</div>
    </div>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500 mb-4">Snapshot</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
        {cell('Date of birth', fmtDob(dob))}
        {cell('Preferred language', profile?.preferred_language)}
        {cell('Household size', income?.household_size ? String(income.household_size) : null)}
        {cell('Annual income', income?.annual_income ? `$${Math.round(income.annual_income).toLocaleString()}` : null)}
        {cell('Contact preference', profile?.preferred_contact_method)}
        {cell('Best time to call', profile?.best_time_to_call)}
        {cell('Turns 65 in', turns65Days != null && turns65Days > 0 ? `${turns65Days.toLocaleString()} days` : (turns65Days != null ? 'Already 65+' : null))}
      </div>
    </div>
  );
}

// ── Client 360 Contact block — FTPIG demo grid.
function ClientContactBlock({ primary, client }) {
  const cell = (label, value) => (
    <div>
      <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-[14px] text-slate-900 mt-1">{value || <span className="text-slate-400 italic">—</span>}</div>
    </div>
  );
  const phone = primary?.client_phone || client?.phone;
  const email = primary?.client_email || client?.email;
  const homePhone = primary?.client_home_phone || null;
  const emergency = primary?.emergency_contact_name || null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500 mb-4">Contact</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
        {cell('Mobile', phone)}
        {cell('Home', homePhone)}
        {cell('Email', email)}
        {cell('Emergency contact', emergency)}
      </div>
    </div>
  );
}

// ── Section wrapper — numbered header + subtitle + content frame.
function SectionBlock({ number, title, subtitle, headerAction, children }) {
  return (
    <section>
      <div className="flex items-baseline gap-3 mb-2 pb-1 border-b border-slate-300">
        {number && <span className="text-[0.55rem] font-black uppercase tracking-[0.3em] text-slate-400">#{number}</span>}
        <h3 className="text-sm font-black uppercase tracking-tight text-slate-900">{title}</h3>
        {subtitle && <span className="text-[0.6rem] uppercase tracking-wider text-slate-500">{subtitle}</span>}
        {headerAction && <span className="ml-auto">{headerAction}</span>}
      </div>
      {children}
    </section>
  );
}

// ── Snapshot card — colored panel with a heading + tight stat rows.
function SnapshotCard({ heading, accent, children }) {
  const tone =
    accent === 'emerald' ? 'border-emerald-300 bg-emerald-50' :
    accent === 'sky'     ? 'border-sky-300     bg-sky-50' :
    accent === 'violet'  ? 'border-violet-300  bg-violet-50' :
    accent === 'rose'    ? 'border-rose-300    bg-rose-50' :
                           'border-slate-300   bg-slate-50';
  const headingTone =
    accent === 'emerald' ? 'text-emerald-700' :
    accent === 'sky'     ? 'text-sky-700' :
    accent === 'violet'  ? 'text-violet-700' :
    accent === 'rose'    ? 'text-rose-700' :
                           'text-slate-700';
  return (
    <div className={`border-2 ${tone} px-3 py-2`}>
      <div className={`text-[0.55rem] font-black uppercase tracking-wider ${headingTone} mb-1.5`}>{heading}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function SnapRow({ icon, tone, label, value }) {
  const toneClass =
    tone === 'emerald' ? 'text-emerald-700' :
    tone === 'rose'    ? 'text-rose-700' :
    tone === 'slate'   ? 'text-slate-500' :
                         'text-slate-700';
  return (
    <div className="flex items-baseline gap-2 text-xs">
      {icon && <span className={`${toneClass} font-bold w-3`}>{icon}</span>}
      <span className="text-[0.65rem] uppercase tracking-wider text-slate-500 flex-1">{label}</span>
      <span className="font-bold text-slate-900 tabular-nums">{value}</span>
    </div>
  );
}

// ── Coverage Stack Builder — score gauge + filled/missing lists +
// estimated potential MRR. Built on the existing detectCoverage()
// output so it stays in sync with the AI Coverage Analysis section.
function CoverageStackPanel({ stack, potentialMrr }) {
  return (
    <div className="border-2 border-violet-300 bg-white">
      {/* Score bar */}
      <div className="bg-violet-50 px-4 py-3 border-b border-violet-200 flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-[0.55rem] font-black uppercase tracking-wider text-violet-700">Coverage Stack Score</div>
          <div className="text-3xl font-black text-violet-900 tabular-nums">{stack.pct}%</div>
          <div className="text-[0.6rem] uppercase tracking-wider text-slate-500">{stack.filled} of {stack.total} core lines filled</div>
        </div>
        <div className="text-right">
          <div className="text-[0.55rem] font-black uppercase tracking-wider text-emerald-700">Potential Monthly Revenue</div>
          <div className="text-2xl font-black text-emerald-900 tabular-nums">${potentialMrr.low}–${potentialMrr.high}</div>
          <div className="text-[0.6rem] uppercase tracking-wider text-slate-500">if every gap closes</div>
        </div>
      </div>
      {/* Bar visualization */}
      <div className="px-4 py-2 border-b border-violet-200">
        <div className="flex w-full h-2 bg-slate-100 overflow-hidden">
          {stack.items.map(it => (
            <div key={it.key}
              className={`flex-1 ${it.has ? 'bg-emerald-500' : 'bg-slate-200'} ${it.key !== stack.items[stack.items.length - 1].key ? 'border-r-2 border-white' : ''}`}
              title={`${it.label}: ${it.has ? 'covered' : 'open'}`}
            />
          ))}
        </div>
      </div>
      {/* Lists */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
        <div className="border-r border-violet-200 px-4 py-3">
          <div className="text-[0.55rem] font-black uppercase tracking-wider text-emerald-700 mb-1.5">Current</div>
          {stack.items.filter(i => i.has).length === 0
            ? <div className="text-xs italic text-slate-500">No active lines.</div>
            : <ul className="text-xs space-y-0.5">
                {stack.items.filter(i => i.has).map(i => (
                  <li key={i.key} className="text-emerald-900">✓ {i.label}</li>
                ))}
              </ul>}
        </div>
        <div className="px-4 py-3">
          <div className="text-[0.55rem] font-black uppercase tracking-wider text-amber-700 mb-1.5">Recommended Add-Ons</div>
          {stack.items.filter(i => !i.has).length === 0
            ? <div className="text-xs italic text-slate-500">Stack complete — focus on retention.</div>
            : <ul className="text-xs space-y-0.5">
                {stack.items.filter(i => !i.has).map(i => (
                  <li key={i.key} className="text-amber-900">□ {i.label}</li>
                ))}
              </ul>}
        </div>
      </div>
      <div className="px-4 py-2 bg-slate-50 border-t border-violet-200 text-[0.7rem] text-slate-600">
        <strong className="text-slate-800">Why:</strong> {
          stack.filled === 1
            ? 'Client only has major medical coverage. No ancillary protection currently attached.'
            : stack.filled === stack.total
              ? 'Full stack — switch focus from cross-sell to retention and annual reviews.'
              : `${stack.filled} of ${stack.total} lines covered. Lead with the highest-impact gap first.`
        }
      </div>
    </div>
  );
}

// ── Data Quality Alerts — surfaces every missing field on the
// primary active policy + a recommended remediation source. Renders
// nothing when the policy is clean.
function DataQualityPanel({ missing, policies }) {
  const sourceHint = (() => {
    const sources = Array.from(new Set(policies.map(p => p.lead_source).filter(Boolean)));
    if (sources.some(s => /healthsherpa/i.test(s))) return 'HealthSherpa export';
    if (sources.some(s => /jarvis/i.test(s)))       return 'Jarvis (UHC IFP portal)';
    if (sources.some(s => /plan\s*advisors/i.test(s))) return 'Plan Advisors statement';
    return 'carrier portal or commission statement';
  })();
  return (
    <div className="border-2 border-rose-300 bg-rose-50 px-4 py-3">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-0.5 text-xs text-rose-900 mb-2">
        {missing.map(m => <div key={m.key}>⚠ Missing {m.label}</div>)}
      </div>
      <div className="text-[0.7rem] text-rose-800 border-t border-rose-200 pt-2">
        <strong>Recommended action:</strong> verify enrollment records through the {sourceHint}; once updated, re-import the file to refresh this row.
      </div>
    </div>
  );
}

// ── Retention Score — 0-100 gauge with band + opportunities + CLV
// signal. Score is rule-based: younger + multi-policy + has health +
// has life all add points; bands at 75 / 50.
function RetentionScorePanel({ score, age, coverage, counts }) {
  const bandTone =
    score.band === 'High'   ? 'text-emerald-700' :
    score.band === 'Medium' ? 'text-amber-700' :
                              'text-rose-700';
  const opportunities = [
    'Annual policy review',
    !coverage.life            && 'Life insurance discussion',
    !(coverage.dental && coverage.vision) && 'Dental/Vision enrollment',
    !coverage.accident        && 'Accident coverage review',
    !coverage.criticalIllness && 'Critical illness positioning',
  ].filter(Boolean);
  const clv = score.score >= 75 ? 'High' : score.score >= 50 ? 'Medium' : 'Low';
  return (
    <div className="border-2 border-sky-300 bg-sky-50 px-4 py-3 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4 items-start">
      <div>
        <div className="text-[0.55rem] font-black uppercase tracking-wider text-sky-700">Retention Score</div>
        <div className="text-4xl font-black text-sky-900 tabular-nums">{score.score}</div>
        <div className={`text-sm font-black uppercase tracking-wider mt-0.5 ${bandTone}`}>{score.band} Band</div>
        <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mt-2">
          Age {age != null ? age : '—'} · {counts.active || 0} active polic{(counts.active || 0) === 1 ? 'y' : 'ies'}
        </div>
      </div>
      <div className="space-y-3 text-xs">
        <div>
          <div className="text-[0.55rem] font-black uppercase tracking-wider text-sky-700 mb-1">Retention Opportunities</div>
          <ul className="space-y-0.5">
            {opportunities.map(o => <li key={o} className="text-slate-800">• {o}</li>)}
          </ul>
        </div>
        <div className="flex gap-4">
          <div>
            <div className="text-[0.55rem] font-black uppercase tracking-wider text-emerald-700">CLV Potential</div>
            <div className="text-sm font-bold text-emerald-900">{clv}</div>
          </div>
          <div>
            <div className="text-[0.55rem] font-black uppercase tracking-wider text-amber-700">Cross-Sell Potential</div>
            <div className="text-sm font-bold text-amber-900">
              {STACK_LINES.filter(l => !coverage[l]).length >= 3 ? 'High' :
               STACK_LINES.filter(l => !coverage[l]).length >= 1 ? 'Medium' : 'Low'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Next Agent Actions — persistent per-client checklist. State
// survives page refresh + re-opening the modal via localStorage.
function ActionsChecklist({ actions, clientKey }) {
  const storageKey = `client_actions:${String(clientKey).toLowerCase().replace(/\s+/g, '_')}`;
  const [checks, setChecks] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '{}'); }
    catch { return {}; }
  });
  React.useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(checks)); } catch {}
  }, [storageKey, checks]);

  const toggle = (id) => setChecks(c => ({ ...c, [id]: !c[id] }));
  const completedCount = Object.values(checks).filter(Boolean).length;

  return (
    <div className="border-2 border-slate-300 bg-white">
      <div className="px-4 py-2 border-b border-slate-200 flex items-baseline justify-between">
        <div className="text-[0.6rem] uppercase tracking-wider text-slate-600">
          {completedCount} of {actions.length} done
        </div>
        {completedCount > 0 && (
          <button onClick={() => setChecks({})}
            className="text-[0.55rem] uppercase tracking-wider text-slate-500 hover:text-slate-800 underline">
            Reset
          </button>
        )}
      </div>
      <ul className="divide-y divide-slate-100">
        {actions.map(a => (
          <li key={a.id} className="px-4 py-2 flex items-start gap-3 hover:bg-slate-50">
            <input
              type="checkbox"
              checked={!!checks[a.id]}
              onChange={() => toggle(a.id)}
              className="mt-0.5"
            />
            <span className={`text-sm ${checks[a.id] ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
              {a.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── CrossSellGapAnalysis ───────────────────────────────────────────
// Per-client gap analysis. Inspects every active policy for what
// category of coverage it provides, surfaces what's missing, ranks
// the next conversation the agent should have. The numbers / fields
// it checks for completeness are the ones that matter for both
// commissioning AND for AEP renewal recall (member_id, effective,
// renewal, premium, subsidy, commission).
//
// The recommendation logic is rule-based on purpose — every line
// item needs to be defensible to a compliance auditor. AI-generated
// talking points come from the Plan Assistant Q&A panel above.
function CrossSellGapAnalysis({ policies, primary }) {
  // Coverage categories. Detection is by plan_type when reliable,
  // falling back to plan_name regex for the supplemental lines.
  const active = (policies || []).filter(p => (p.status || 'active') === 'active');
  const has = (re, types) => active.some(p => {
    if (types?.includes(p.plan_type)) return true;
    if (!re) return false;
    return re.test(p.plan_name || '') || re.test(p.plan_type || '');
  });

  const coverage = {
    health: has(null, ['aca_marketplace', 'short_term', 'medicare_advantage',
                       'medicare_supplement', 'medicare_pdp', 'private_ppo']),
    dental: has(/dental/i, ['dental_vision']),
    vision: has(/vision/i, ['dental_vision']),
    accident:           has(/accident/i),
    hospitalIndemnity:  has(/hospital\s*indemnity/i, ['hospital_indemnity']),
    criticalIllness:    has(/critical\s*illness/i),
    life:               has(/term\s*life|whole\s*life|iul|final\s*expense/i),
    incomeProtection:   has(/disability|income\s*protection/i),
  };

  const LABEL = {
    health:            'ACA / Medicare Health Insurance',
    dental:            'Dental',
    vision:            'Vision',
    accident:          'Accident Coverage',
    hospitalIndemnity: 'Hospital Indemnity',
    criticalIllness:   'Critical Illness',
    life:              'Life Insurance',
    incomeProtection:  'Income Protection (Disability)',
  };

  const covered = [];
  const gaps    = [];
  Object.entries(coverage).forEach(([k, v]) => {
    (v ? covered : gaps).push(LABEL[k]);
  });

  // ── Missing-data audit on the primary policy ─────────────────
  const missing = [];
  if (primary) {
    if (!primary.member_id)        missing.push('Member ID');
    if (!primary.effective_date)   missing.push('Effective Date');
    if (!primary.renewal_date)     missing.push('Renewal Date');
    if (primary.monthly_premium    == null) missing.push('Premium');
    if (primary.plan_type === 'aca_marketplace' && primary.subsidy_amount == null) missing.push('Subsidy');
    if (primary.monthly_commission == null) missing.push('Commission');
  }

  // ── Recommendation engine ────────────────────────────────────
  // Base plan = the highest-priority active health line. Add-ons =
  // the gap categories the agent can attach to that base, ranked
  // by typical ACA agent close rate (Dental + Vision lead, then
  // Accident + Hospital Indemnity, then Critical Illness).
  const basePlan = (() => {
    const order = ['medicare_advantage','medicare_supplement','medicare_pdp',
                   'aca_marketplace','short_term','private_ppo'];
    const pick = active.find(p => order.includes(p.plan_type));
    if (!pick) return null;
    return pick.plan_name || HEALTH_PLAN_TYPE_BY_ID?.[pick.plan_type]?.label || pick.plan_type;
  })();

  const addOnPriority = ['dental','vision','accident','hospitalIndemnity','criticalIllness'];
  const addOns = addOnPriority
    .filter(k => !coverage[k])
    .map(k => LABEL[k]);

  const futureDiscussion = ['Term Life', 'IUL', 'Retirement Planning'];

  return (
    <div className="border-2 border-violet-300 bg-violet-50 p-4 space-y-3 mt-2">
      <div className="text-violet-700 text-[0.55rem] font-black uppercase tracking-[0.3em]">Gap Analysis & Cross-Sell</div>

      {/* Missing-data audit was here. Removed — the cockpit's Enrich
          Suggestions panel at the top of the modal is now the single
          place this lives, with confidence levels (auto-applyable vs
          requires re-import vs requires statement) and a one-click
          Apply All. Duplicating the list here pushed operators to
          fill values by hand instead of using the one-click path. */}

      {/* Current vs gaps */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-emerald-50 border border-emerald-200 px-3 py-2">
          <div className="text-[0.55rem] font-black uppercase tracking-wider text-emerald-700 mb-1">Current Coverage</div>
          {covered.length === 0
            ? <div className="text-xs text-slate-500 italic">No active coverage on file.</div>
            : <ul className="text-xs text-emerald-900 space-y-0.5">{covered.map(c => <li key={c}>✓ {c}</li>)}</ul>}
        </div>
        <div className="bg-amber-50 border border-amber-200 px-3 py-2">
          <div className="text-[0.55rem] font-black uppercase tracking-wider text-amber-700 mb-1">Potential Gaps</div>
          {gaps.length === 0
            ? <div className="text-xs text-slate-500 italic">Fully covered across our lines.</div>
            : <ul className="text-xs text-amber-900 space-y-0.5">{gaps.map(g => <li key={g}>○ {g}</li>)}</ul>}
        </div>
      </div>

      {/* Recommendation */}
      <div className="bg-white border border-violet-200 px-3 py-2 space-y-2">
        <div className="text-[0.55rem] font-black uppercase tracking-wider text-violet-700">Cross-Sell Recommendation</div>
        {basePlan && (
          <div className="text-xs">
            <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Base plan:</span>
            <span className="ml-2 font-bold text-slate-900">{basePlan}</span>
          </div>
        )}
        {addOns.length > 0 && (
          <div>
            <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Recommended Add-Ons</div>
            <ol className="text-xs text-slate-800 list-decimal pl-5 mt-1 space-y-0.5">
              {addOns.map(a => <li key={a}>{a}</li>)}
            </ol>
          </div>
        )}
        <div>
          <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Future Discussion</div>
          <ul className="text-xs text-slate-800 pl-5 mt-1 space-y-0.5">
            {futureDiscussion.map(f => <li key={f}>• {f}</li>)}
          </ul>
        </div>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, accent }) {
  const tone =
    accent === 'emerald' ? 'bg-emerald-50 text-emerald-900' :
    accent === 'amber'   ? 'bg-amber-50   text-amber-900'   :
    accent === 'rose'    ? 'bg-rose-50    text-rose-900'    :
    accent === 'sky'     ? 'bg-sky-50     text-sky-900'     :
                           'bg-slate-50   text-slate-900';
  return (
    <div className={`${tone} border-r border-slate-200 last:border-r-0 px-3 py-3`}>
      <div className="text-[0.55rem] font-black uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-xl font-black mt-0.5">{value}</div>
    </div>
  );
}

// One policy card — every field from health_policies, plus the linked
// plan's cost-sharing roll-up + any loss events attached to it.
function PolicyCard({ policy: p, linkedPlan, losses, onEdit, fmt$, index, showToast = () => {} }) {
  const statusTone =
    (p.status || 'active') === 'active'   ? 'bg-emerald-100 text-emerald-800' :
    p.status === 'pending'                ? 'bg-amber-100   text-amber-800'   :
    p.status === 'renewed'                ? 'bg-orange-100  text-orange-800'  :
    p.status === 'cancelled'              ? 'bg-red-100     text-red-800'     :
    (p.status === 'lapsed' || p.status === 'expired') ? 'bg-teal-100 text-teal-800' :
                                            'bg-slate-100   text-slate-700';
  return (
    <div className="border-2 border-slate-200 bg-white">
      {/* Card header — Carrier · Product · Status */}
      <div className="border-b-2 border-slate-300 bg-slate-50 px-4 py-2 flex items-baseline justify-between gap-3 flex-wrap">
        <div className="flex items-baseline gap-3 flex-wrap">
          {index && <span className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-400">Policy #{index}</span>}
          <div>
            <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Carrier · {p.carrier || '—'}</div>
            <div className="text-sm font-black text-slate-900">{p.plan_name || HEALTH_PLAN_TYPE_BY_ID[p.plan_type]?.label || p.plan_type || 'Untitled plan'}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[0.55rem] font-black uppercase tracking-wider px-2 py-0.5 ${statusTone}`}>{p.status || 'active'}</span>
          <button onClick={onEdit} className="text-[0.6rem] font-black uppercase tracking-tight text-sky-700 hover:text-sky-900 underline">Edit</button>
        </div>
      </div>

      {/* Policy Details sub-section */}
      <div>
        <div className="px-3 pt-2 text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Policy Details</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 text-xs">
          <PolicyField label="Plan type"        value={HEALTH_PLAN_TYPE_BY_ID[p.plan_type]?.label || p.plan_type}/>
          <PolicyField label="Member ID"        value={p.member_id} mono/>
          <PolicyField label="Effective Date"   value={p.effective_date} mono/>
          <PolicyField label="Renewal Date"     value={p.renewal_date} mono/>
          <PolicyField label="Termination"      value={p.termination_date} mono accent={p.termination_date ? 'rose' : null}/>
          <PolicyField label="Lead source"      value={p.lead_source}/>
          <PolicyField label="Writing agent #"  value={p.agent_prod_num} mono/>
          <PolicyField label="Phone (on file)"  value={p.client_phone}/>
        </div>
      </div>

      {/* Financials sub-section */}
      <div className="border-t border-slate-100">
        <div className="px-3 pt-2 text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Financials</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 text-xs">
          <PolicyField label="Premium / mo"     value={p.monthly_premium != null ? fmt$(p.monthly_premium) : null} mono/>
          <PolicyField label="Subsidy / mo"     value={p.subsidy_amount != null ? fmt$(p.subsidy_amount) : null} mono/>
          <PolicyField label="Commission / mo"  value={p.monthly_commission != null ? fmt$(p.monthly_commission) : null} mono accent="emerald"/>
          <PolicyField label="Last commission"  value={p.last_commission_at ? String(p.last_commission_at).slice(0, 10) : null} mono/>
        </div>
      </div>

      {p.notes && (() => {
        // The HealthSherpa parser used to dump the client's address
        // into notes for older imports. Detect that case and:
        //   - Relabel the block as "Address (in notes)" instead of
        //     pretending it's real notes content
        //   - Offer a one-click migrate that copies the parsed pieces
        //     into the structured columns and clears notes
        // For real notes content, render as Notes normally.
        const isAddress = looksLikeAddress(p.notes) && !p.client_address;
        const migrateAddress = async () => {
          const parsed = parseLooseAddress(p.notes);
          try {
            const { error } = await supabase.from('health_policies').update({
              client_address:  parsed.address1  || p.client_address,
              client_address2: parsed.address2  || p.client_address2,
              client_city:     parsed.city      || p.client_city,
              client_state:    parsed.state     || p.client_state,
              client_zip:      parsed.zip       || p.client_zip,
              notes:           parsed.remainder || null,
            }).eq('id', p.id);
            if (error) { showToast?.(`Migrate failed: ${error.message}`, 'error'); return; }
            showToast?.('Address moved to structured fields.', 'success');
            try { window.dispatchEvent(new CustomEvent('ct:health-client-changed', { detail: { clientId: p.client_id } })); } catch {}
          } catch (e) {
            showToast?.(`Migrate failed: ${e.message}`, 'error');
          }
        };
        return (
          <div className={`px-3 py-2 border-t border-slate-100 ${isAddress ? 'bg-sky-50' : 'bg-amber-50'}`}>
            <div className={`text-[0.55rem] font-black uppercase tracking-wider mb-0.5 flex items-center justify-between gap-2 ${isAddress ? 'text-sky-700' : 'text-amber-700'}`}>
              <span>{isAddress ? 'Address (in notes — should be structured)' : 'Notes'}</span>
              {isAddress && (
                <button
                  type="button"
                  onClick={migrateAddress}
                  className="text-[0.55rem] font-black uppercase tracking-wider text-sky-700 hover:text-sky-900 underline"
                >
                  Move to address fields →
                </button>
              )}
            </div>
            <div className="text-xs text-slate-800 whitespace-pre-wrap">{p.notes}</div>
          </div>
        );
      })()}

      {/* Linked plan benefits — the killer feature */}
      {linkedPlan && (
        <div className="border-t border-slate-200 bg-cyan-50">
          <div className="px-3 py-2 flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <div className="text-[0.55rem] font-black uppercase tracking-wider text-cyan-700">Linked plan · benefits</div>
              <div className="text-xs font-bold text-cyan-900">
                {[linkedPlan.carrier_short || linkedPlan.carrier, linkedPlan.plan_name, linkedPlan.state, linkedPlan.metal_level, linkedPlan.network_type].filter(Boolean).join(' · ')}
              </div>
            </div>
            {linkedPlan.referral_required != null && (
              <span className="text-[0.55rem] font-black uppercase tracking-wider text-cyan-900">
                Referral: <span className={linkedPlan.referral_required ? 'text-amber-700' : 'text-emerald-700'}>{linkedPlan.referral_required ? 'Yes' : 'No'}</span>
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 text-xs">
            <PolicyField label="Deductible (ind)"  value={linkedPlan.deductible_individual != null ? fmt$(linkedPlan.deductible_individual) : null} mono/>
            <PolicyField label="OOP max (ind)"     value={linkedPlan.oop_max_individual != null ? fmt$(linkedPlan.oop_max_individual) : null} mono/>
            <PolicyField label="PCP visit"         value={linkedPlan.pcp_visit}/>
            <PolicyField label="Specialist"        value={linkedPlan.specialist_visit}/>
            <PolicyField label="Urgent care"       value={linkedPlan.urgent_care}/>
            <PolicyField label="ER"                value={linkedPlan.emergency_room}/>
            <PolicyField label="Imaging"           value={linkedPlan.imaging}/>
            <PolicyField label="Generic Rx"        value={linkedPlan.generic_rx}/>
            <PolicyField label="Preferred brand"   value={linkedPlan.preferred_brand_rx}/>
            <PolicyField label="Non-preferred"     value={linkedPlan.non_preferred_brand_rx}/>
            <PolicyField label="Specialty Rx"      value={linkedPlan.specialty_rx}/>
            <PolicyField label="Mental health (OP)" value={linkedPlan.mental_health_outpatient}/>
          </div>
        </div>
      )}

      {/* Loss events on this policy */}
      {losses.length > 0 && (
        <div className="border-t border-slate-200 bg-rose-50 px-3 py-2 space-y-1">
          <div className="text-[0.55rem] font-black uppercase tracking-wider text-rose-700">Loss events</div>
          {losses.map(l => (
            <div key={l.id} className="text-xs text-rose-900 flex items-baseline gap-2 flex-wrap">
              <span className="font-bold">{l.reason}</span>
              <span className="font-mono">{l.lost_at ? String(l.lost_at).slice(0, 10) : '—'}</span>
              {l.monthly_commission > 0 && <span>· ${l.monthly_commission}/mo</span>}
              {l.acknowledged_at && <span className="text-[0.55rem] uppercase tracking-wider text-rose-500">acknowledged</span>}
              {l.notes && <span className="text-slate-700">— {l.notes}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PolicyField({ label, value, mono, accent }) {
  const tone =
    accent === 'rose'    ? 'text-rose-700' :
    accent === 'emerald' ? 'text-emerald-700 font-black' :
                           'text-slate-900';
  return (
    <div className="px-3 py-2 border-r border-b border-slate-100 last:border-r-0">
      <div className="text-[0.5rem] font-black uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`${tone} ${mono ? 'font-mono' : ''} truncate`}>{value || '—'}</div>
    </div>
  );
}

function monthsBetween(start, end) {
  if (!start) return 0;
  const s = new Date(start);
  const e = end ? new Date(end) : new Date();
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
  const diff = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
  return Math.max(0, diff);
}

// ════════════════════════════════════════════════════════════════════
// CMS SCOPE OF APPOINTMENT (SOA) — Medicare compliance gate.
// CMS rule: SOA must be signed ≥48 hours before any Medicare sales
// appointment (or documented same-day exception). The SOA names
// exactly which products may be discussed. Without a valid SOA on
// file, Medicare enrollment cannot be marked complete.
// ════════════════════════════════════════════════════════════════════

const MEDICARE_PRODUCT_TYPES = [
  { id: 'MA',       label: 'Medicare Advantage (MA-only)' },
  { id: 'MAPD',     label: 'Medicare Advantage + Drug (MAPD)' },
  { id: 'PDP',      label: 'Stand-alone Part D (PDP)' },
  { id: 'Med Supp', label: 'Medicare Supplement (Medigap)' },
];

function SignaturePad({ value, onChange, height = 140 }) {
  const ref = React.useRef(null);
  const drawing = React.useRef(false);
  const lastPt = React.useRef(null);

  React.useEffect(() => {
    const cvs = ref.current;
    if (!cvs) return;
    const ctx = cvs.getContext('2d');
    const ratio = window.devicePixelRatio || 1;
    cvs.width = cvs.clientWidth * ratio;
    cvs.height = height * ratio;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cvs.clientWidth, height);
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, cvs.clientWidth, height);
      img.src = value;
    }
  }, [height, value]);

  function ptFromEvent(e) {
    const cvs = ref.current;
    const rect = cvs.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: (t?.clientX ?? e.clientX) - rect.left, y: (t?.clientY ?? e.clientY) - rect.top };
  }
  function start(e) { e.preventDefault(); drawing.current = true; lastPt.current = ptFromEvent(e); }
  function move(e) {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = ref.current.getContext('2d');
    const pt = ptFromEvent(e);
    ctx.beginPath();
    ctx.moveTo(lastPt.current.x, lastPt.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastPt.current = pt;
  }
  function end() {
    if (!drawing.current) return;
    drawing.current = false;
    if (onChange) onChange(ref.current.toDataURL('image/png'));
  }
  function clear() {
    const ctx = ref.current.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, ref.current.clientWidth, height);
    if (onChange) onChange(null);
  }

  return (
    <div className="border-2 border-slate-300 bg-white" style={{ height }}>
      <canvas
        ref={ref}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        style={{ width: '100%', height: '100%', cursor: 'crosshair', touchAction: 'none' }}
      />
      <button onClick={clear} type="button" className="text-[0.55rem] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900 px-2 py-1">
        Clear signature
      </button>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// SOA VIEW — Two-signature flow per CMS MED-136.
//
// Flow:
//   1. Agent composes via "+ Send for Signature" modal — beneficiary
//      info, products to discuss, agency block. Row created with
//      status='pending_client'. Magic link /soa/<token> sent.
//   2. Beneficiary signs at /soa/<token>. Status → 'pending_agent'.
//   3. Banner alerts the agent — they open the row, countersign,
//      enter plans-actually-represented + appointment-completed-date.
//      Status → 'completed'.
//   4. Print-to-PDF for compliance archive.
//
// Legacy in-person rows (signed via the pre-2025 single-sig flow)
// still render correctly as 'completed' rows with the existing
// beneficiary signature image.
// ════════════════════════════════════════════════════════════════════
function SOAView({ currentUser, settings = {}, showToast = () => {} }) {
  const [clients, setClients] = React.useState([]);
  const [records, setRecords] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [composing, setComposing] = React.useState(false);
  const [detail, setDetail]   = React.useState(null);
  const [countersigning, setCountersigning] = React.useState(null); // row to countersign
  const [filter, setFilter]   = React.useState('all');

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      await sweepExpiredSoas();
      const [c, r] = await Promise.all([
        supabase.from('clients').select('id, name, phone, sector').order('name'),
        fetchOwnerSoas({ limit: 500 }),
      ]);
      setClients(c.data || []);
      setRecords(r || []);
    } finally {
      setLoading(false);
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const filtered = filter === 'all' ? records : records.filter(r => (r.status || 'completed') === filter);
  const counts = records.reduce((acc, r) => {
    const s = r.status || 'completed';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const pendingAgentCount = counts.pending_agent || 0;

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' }) : '';

  function statusBadge(s) {
    const m = {
      pending_client: { label: 'Pending Client', cls: 'bg-amber-100 text-amber-800' },
      pending_agent:  { label: 'Needs Countersign', cls: 'bg-rose-100 text-rose-800' },
      completed:      { label: 'Completed',  cls: 'bg-emerald-100 text-emerald-800' },
      expired:        { label: 'Expired',    cls: 'bg-slate-200 text-slate-700' },
      revoked:        { label: 'Revoked',    cls: 'bg-violet-100 text-violet-800' },
    };
    const x = m[s] || { label: s, cls: 'bg-slate-100 text-slate-700' };
    return <span className={`text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5 ${x.cls}`}>{x.label}</span>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-black border-l-4 border-blue-500 px-6 py-5 shadow-2xl flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-blue-400 text-[0.6rem] font-black uppercase tracking-[0.3em]">Scope of Appointment</div>
          <div className="text-white text-xl md:text-2xl font-black mt-1.5 uppercase tracking-tight">CMS MED-136 · Two-Signature Flow</div>
          <div className="text-slate-400 text-xs mt-1.5">
            {records.length} record{records.length === 1 ? '' : 's'} · {counts.pending_client || 0} awaiting client · {counts.pending_agent || 0} awaiting your countersign · {counts.completed || 0} completed
          </div>
        </div>
        <button onClick={() => setComposing(true)} className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-black uppercase tracking-tight px-4 py-2 shadow">+ Send SOA</button>
      </div>

      {pendingAgentCount > 0 && (
        <div className="bg-rose-50 border-2 border-rose-300 p-3 flex items-center gap-3 flex-wrap">
          <div className="flex-1">
            <strong className="uppercase tracking-wider text-[0.6rem] font-black text-rose-700">⚠ {pendingAgentCount} client{pendingAgentCount === 1 ? '' : 's'} signed — needs your countersign</strong>
            <div className="text-xs text-rose-900 mt-1">Per CMS MED-136, the SOA is not valid until both signatures are captured. Click any "Needs Countersign" row below to add your signature.</div>
          </div>
          <button
            onClick={() => setFilter('pending_agent')}
            className="bg-rose-600 hover:bg-rose-700 text-white text-[0.6rem] font-black uppercase tracking-tight px-3 py-1.5"
          >Show those rows</button>
        </div>
      )}

      <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
        <strong className="uppercase tracking-wider text-[0.6rem] font-black text-amber-700">Compliance Reminder</strong>
        <div className="mt-1">Medicare enrollment CANNOT be marked complete without an SOA signed ≥48 hours before the appointment (or a documented same-day exception), a recorded call, and current AHIP + carrier certs. CMS audit retention: 10 years.</div>
      </div>

      {/* Filter pills */}
      <div className="flex items-center gap-2 flex-wrap">
        {['all','pending_client','pending_agent','completed','expired','revoked'].map(k => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`text-xs font-bold uppercase tracking-tight px-3 py-1.5 border-2 ${filter === k ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
          >
            {k.replace('_', ' ')} {k !== 'all' && counts[k] != null && <span className="opacity-70 ml-1">({counts[k] || 0})</span>}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {['Beneficiary', 'Products', 'Status', 'Client Signed', 'Agent Signed', 'Appointment', ''].map(h => (
                <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (<tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>)
              : filtered.length === 0 ? (<tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">No SOAs match. Click + Send SOA to start.</td></tr>)
              : filtered.map(r => {
                const s = r.status || 'completed';
                const rowCls = s === 'pending_agent' ? 'hover:bg-rose-50 cursor-pointer bg-rose-50/30' : 'hover:bg-slate-50 cursor-pointer';
                return (
                  <tr key={r.id} className={rowCls} onClick={() => s === 'pending_agent' ? setCountersigning(r) : setDetail(r)}>
                    <td className="px-3 py-2"><div className="font-bold text-slate-900">{r.beneficiary_name}</div><div className="text-[0.6rem] uppercase tracking-wider text-slate-400">{r.beneficiary_phone || ''}</div></td>
                    <td className="px-3 py-2 text-xs">{(r.products || []).map(p => <span key={p} className="inline-block bg-blue-100 text-blue-800 text-[0.55rem] font-black uppercase tracking-wider px-2 py-0.5 mr-1">{SOA_PRODUCT_LABEL[p] ? p.toUpperCase() : p}</span>)}</td>
                    <td className="px-3 py-2">{statusBadge(s)}</td>
                    <td className="px-3 py-2 text-xs font-mono">{fmtDate(r.signed_at)}</td>
                    <td className="px-3 py-2 text-xs font-mono">{fmtDate(r.agent_signed_at)}</td>
                    <td className="px-3 py-2 text-xs font-mono">{r.appointment_at ? fmtDate(r.appointment_at) : '—'}</td>
                    <td className="px-3 py-2 text-[0.55rem] uppercase tracking-wider">
                      {s === 'pending_agent' ? <span className="text-rose-700 font-black">Countersign →</span>
                       : s === 'pending_client' ? <span className="text-amber-700">Copy URL →</span>
                       : <span className="text-blue-700">View →</span>}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {composing && (
        <SoaComposeModal
          currentUser={currentUser}
          settings={settings}
          clients={clients}
          onClose={() => setComposing(false)}
          onCreated={(row) => {
            setComposing(false);
            setDetail(row);
            setRecords(prev => [row, ...prev]);
            audit({
              ownerId: currentUser?.id,
              actorId: currentUser?.id,
              actorName: currentUser?.name,
              targetType: 'soa_records',
              targetId: row.id,
              targetLabel: row.beneficiary_name,
              action: AUDIT_ACTION.CONSENT_SENT,
              metadata: { kind: 'soa', products: row.products, sent_via: row.sent_via },
            });
            showToast(`SOA sent to ${row.beneficiary_name}.`);
          }}
          showToast={showToast}
        />
      )}

      {countersigning && (
        <SoaCountersignModal
          row={countersigning}
          currentUser={currentUser}
          onClose={() => setCountersigning(null)}
          onCompleted={(updated) => {
            setCountersigning(null);
            setDetail(updated);
            setRecords(prev => prev.map(x => x.id === updated.id ? updated : x));
            audit({
              ownerId: currentUser?.id,
              actorId: currentUser?.id,
              actorName: currentUser?.name,
              targetType: 'soa_records',
              targetId: updated.id,
              targetLabel: updated.beneficiary_name,
              action: AUDIT_ACTION.CONSENT_SIGNED,
              metadata: { kind: 'soa_countersign' },
            });
            showToast('Countersigned. SOA is now complete.');
          }}
          showToast={showToast}
        />
      )}

      {detail && (
        <SoaDetailModal
          row={detail}
          currentUser={currentUser}
          onClose={() => setDetail(null)}
          onAcknowledged={(updated) => setRecords(prev => prev.map(x => x.id === updated.id ? updated : x))}
        />
      )}
    </div>
  );
}

// ── Compose modal — sends a magic link to the beneficiary ──────
function SoaComposeModal({ currentUser, settings = {}, clients = [], onClose, onCreated, showToast }) {
  const [clientId, setClientId] = React.useState('');
  const [name, setName]         = React.useState('');
  const [phone, setPhone]       = React.useState('');
  const [email, setEmail]       = React.useState('');
  const [address, setAddress]   = React.useState('');
  const [products, setProducts] = React.useState([]);
  const [contactMethod, setContactMethod] = React.useState('phone');
  const [appointmentAt, setAppointmentAt] = React.useState('');
  const [sendVia, setSendVia]   = React.useState('sms');
  const [agentName, setAgentName] = React.useState(settings.ownerName || currentUser?.name || '');
  const [agentNpn,  setAgentNpn]  = React.useState(settings.ownerNpn || settings.npn || '');
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function toggleProduct(id) {
    setProducts(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  async function send() {
    if (!name.trim()) { showToast('Beneficiary name required.'); return; }
    if (products.length === 0) { showToast('Mark at least one product.'); return; }
    if (!agentName.trim() || !agentNpn.trim()) { showToast('Agent name + NPN required.'); return; }
    if (sendVia === 'sms' && !phone.trim()) { showToast('Phone required for SMS delivery.'); return; }
    setSubmitting(true);
    try {
      const row = await createSoa({
        ownerId: currentUser?.id,
        clientId: clientId || null,
        beneficiaryName: name.trim(),
        beneficiaryAddress: address.trim() || null,
        beneficiaryPhone: phone.trim() || null,
        beneficiaryEmail: email.trim() || null,
        products,
        agentName: agentName.trim(),
        agentNpn:  agentNpn.trim(),
        contactMethod,
        appointmentAt: appointmentAt || null,
        sentVia: sendVia,
      });
      // Best-effort SMS delivery via the existing lifecycle endpoint.
      // If env not configured the row is still persisted; agent can copy
      // the URL from the detail modal.
      if (sendVia === 'sms' || sendVia === 'both') {
        try {
          const body = `${agentName || 'Your agent'} sent you a Scope of Appointment to review and sign for your Medicare meeting: ${row.signUrl}`;
          const { data: { session } } = await supabase.auth.getSession();
          const jwt = session?.access_token;
          await fetch('/api/sms/send-manual', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
            body: JSON.stringify({ adhoc_phone: phone, body }),
          }).catch(() => null);
        } catch {}
      }
      onCreated(row);
    } catch (err) {
      showToast(`Send failed: ${err.message || err}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-slate-900/70 z-50 flex items-center justify-center p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="bg-slate-900 border-l-4 border-blue-500 px-4 py-3 flex items-start justify-between gap-3">
          <div>
            <div className="text-blue-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">New SOA</div>
            <div className="text-white text-lg font-black uppercase tracking-tight">Send for signature</div>
          </div>
          <button aria-label="Close" onClick={onClose} className="text-white/60 hover:text-white">✕</button>
        </div>

        <div className="p-4 space-y-3">
          {/* Client picker */}
          <label className="block">
            <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Link to existing client (optional)</span>
            <select value={clientId} onChange={e => {
              const c = clients.find(x => x.id === e.target.value);
              setClientId(e.target.value);
              if (c) { setName(c.name || name); setPhone(c.phone || phone); }
            }} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm bg-white">
              <option value="">— manual entry —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}{c.sector === 'medicare' ? ' · Medicare' : ''}</option>)}
            </select>
          </label>

          {/* Beneficiary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Beneficiary name *</span>
              <input value={name} onChange={e => setName(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm"/>
            </label>
            <label className="block">
              <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Phone</span>
              <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="(555) 123-4567" className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm"/>
            </label>
            <label className="block">
              <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Email</span>
              <input value={email} onChange={e => setEmail(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm"/>
            </label>
            <label className="block">
              <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Address</span>
              <input value={address} onChange={e => setAddress(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm"/>
            </label>
          </div>

          {/* Products — canonical CMS catalog */}
          <div>
            <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500 mb-1.5">Products to discuss *</div>
            <div className="space-y-2">
              {SOA_PRODUCT_CATALOG.map(section => (
                <div key={section.section} className="border border-slate-200">
                  <div className="bg-slate-50 border-b border-slate-200 px-3 py-1.5 text-[0.6rem] font-black uppercase tracking-wider text-slate-700">{section.section}</div>
                  <div className="divide-y divide-slate-100">
                    {section.items.map(p => (
                      <label key={p.id} className={`flex items-start gap-2.5 p-2.5 cursor-pointer ${products.includes(p.id) ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                        <input type="checkbox" checked={products.includes(p.id)} onChange={() => toggleProduct(p.id)} className="mt-1 accent-blue-600 h-4 w-4"/>
                        <span className="text-xs text-slate-800"><strong>{p.label}</strong>{p.description ? <span className="text-slate-600"> — {p.description}</span> : null}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Agent block + appointment + send-via */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <label className="block">
              <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Agent name *</span>
              <input value={agentName} onChange={e => setAgentName(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm"/>
            </label>
            <label className="block">
              <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Agent NPN *</span>
              <input value={agentNpn} onChange={e => setAgentNpn(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm font-mono"/>
            </label>
            <label className="block">
              <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Initial Method of Contact</span>
              <select value={contactMethod} onChange={e => setContactMethod(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm bg-white">
                {SOA_CONTACT_METHODS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </label>
            <label className="block md:col-span-2">
              <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Appointment date / time (optional — 48h rule)</span>
              <input type="datetime-local" value={appointmentAt} onChange={e => setAppointmentAt(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm"/>
            </label>
            <label className="block">
              <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Send via</span>
              <select value={sendVia} onChange={e => setSendVia(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm bg-white">
                <option value="sms">SMS</option>
                <option value="email">Email</option>
                <option value="both">SMS + Email</option>
                <option value="link_copy">Copy link only</option>
              </select>
            </label>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
            <button aria-label="Close" onClick={onClose} className="text-sm text-slate-600 hover:text-slate-900 px-3 py-2">Cancel</button>
            <button onClick={send} disabled={submitting} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-black uppercase tracking-tight px-4 py-2">
              {submitting ? 'Sending…' : 'Generate + Send SOA link'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Agent countersign modal ────────────────────────────────────
function SoaCountersignModal({ row, currentUser, onClose, onCompleted, showToast }) {
  const [signature, setSignature]   = React.useState('');
  const [plansRepresented, setPlansRepresented] = React.useState('');
  const [appointmentCompleted, setAppointmentCompleted] = React.useState(new Date().toISOString().slice(0, 10));
  const [planUseOnly, setPlanUseOnly] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function submit() {
    if (!signature) { showToast('Agent signature required.'); return; }
    setSubmitting(true);
    try {
      await agentCountersign({
        soaId: row.id,
        signatureDataUrl: signature,
        plansRepresented: plansRepresented.trim() || null,
        appointmentCompletedDate: appointmentCompleted || null,
        planUseOnly: planUseOnly.trim() || null,
      });
      const updated = {
        ...row,
        status: 'completed',
        agent_signature_data_url: signature,
        agent_signed_at: new Date().toISOString(),
        plans_represented: plansRepresented.trim() || null,
        appointment_completed_date: appointmentCompleted || null,
        plan_use_only: planUseOnly.trim() || null,
      };
      onCompleted(updated);
    } catch (err) {
      showToast(`Countersign failed: ${err.message || err}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-slate-900/70 z-50 flex items-center justify-center p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="bg-rose-900 border-l-4 border-rose-400 px-4 py-3 flex items-start justify-between gap-3">
          <div>
            <div className="text-rose-200 text-[0.55rem] font-black uppercase tracking-[0.3em]">Countersign required</div>
            <div className="text-white text-lg font-black uppercase tracking-tight">{row.beneficiary_name}</div>
            <div className="text-rose-100 text-xs mt-1">Client signed at {row.signed_at ? new Date(row.signed_at).toLocaleString() : '—'} from IP {row.client_signature_ip || '—'}. Complete the agent fields + sign.</div>
          </div>
          <button aria-label="Close" onClick={onClose} className="text-white/60 hover:text-white">✕</button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Plan(s) you actually represented during the meeting</span>
              <textarea rows={2} value={plansRepresented} onChange={e => setPlansRepresented(e.target.value)} placeholder="e.g. Humana MA-PD H1234-002, UHC PDP S5921-456" className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm"/>
            </label>
            <label className="block">
              <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Date Appointment Completed</span>
              <input type="date" value={appointmentCompleted} onChange={e => setAppointmentCompleted(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm"/>
            </label>
            <label className="block md:col-span-2">
              <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Plan Use Only</span>
              <input value={planUseOnly} onChange={e => setPlanUseOnly(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm"/>
            </label>
          </div>

          <div>
            <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500 mb-1.5">Agent's Signature *</div>
            <SignaturePad value={signature} onChange={setSignature}/>
            <div className="text-[0.55rem] text-slate-400 italic mt-1 uppercase tracking-wider">Archived per CMS 10-year retention.</div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
            <button aria-label="Close" onClick={onClose} className="text-sm text-slate-600 hover:text-slate-900 px-3 py-2">Cancel</button>
            <button onClick={submit} disabled={submitting} className="bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-xs font-black uppercase tracking-tight px-4 py-2">
              {submitting ? 'Saving…' : 'Sign + Complete SOA'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Detail / Print modal ───────────────────────────────────────
function SoaDetailModal({ row, currentUser, onClose, onAcknowledged }) {
  const [copied, setCopied] = React.useState(false);
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const ackRef = React.useRef(false);
  React.useEffect(() => {
    // Mark "I've seen the signed SOA" when the agent opens it.
    // Guarded with a ref so this runs once per (row.id) even when
    // the parent re-renders and re-passes a new row object.
    if (ackRef.current) return;
    if (row.status === 'pending_agent' && !row.client_signed_notified_at) {
      ackRef.current = true;
      acknowledgeClientSigned(row.id).then((ok) => {
        if (ok && onAcknowledged) onAcknowledged({ ...row, client_signed_notified_at: new Date().toISOString() });
      });
    }
  }, [row, onAcknowledged]);
  function copyLink() {
    if (!row.token) return;
    const url = buildSoaUrl(row.token);
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }
  return (
    <div className="fixed inset-0 bg-slate-900/70 z-50 flex items-center justify-center p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="bg-slate-900 border-l-4 border-blue-500 px-4 py-3 flex items-start justify-between gap-3">
          <div>
            <div className="text-blue-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">SOA Detail</div>
            <div className="text-white text-lg font-black uppercase tracking-tight">{row.beneficiary_name}</div>
          </div>
          <button aria-label="Close" onClick={onClose} className="text-white/60 hover:text-white">✕</button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-[0.6rem] uppercase tracking-wider text-slate-500">Status: <strong>{(row.status || 'completed').replace('_', ' ')}</strong></div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <Receipt label="Beneficiary" value={row.beneficiary_name}/>
            <Receipt label="Phone" value={row.beneficiary_phone || '—'}/>
            <Receipt label="Address" value={row.beneficiary_address || '—'} full/>
            <Receipt label="Products" value={(row.products || []).join(' · ') || '—'} full/>
            <Receipt label="Agent" value={row.agent_name}/>
            <Receipt label="Agent NPN" value={row.agent_npn}/>
            <Receipt label="Contact method" value={row.contact_method || '—'}/>
            <Receipt label="Appointment" value={row.appointment_at ? new Date(row.appointment_at).toLocaleString() : '—'}/>
            <Receipt label="Client signed" value={row.signed_at ? new Date(row.signed_at).toLocaleString() : '—'}/>
            <Receipt label="Client IP" value={row.client_signature_ip || row.signed_ip || '—'}/>
            <Receipt label="Agent signed" value={row.agent_signed_at ? new Date(row.agent_signed_at).toLocaleString() : '—'}/>
            <Receipt label="Plans represented" value={row.plans_represented || '—'} full/>
            <Receipt label="Appt completed" value={row.appointment_completed_date || '—'}/>
            <Receipt label="Plan Use Only" value={row.plan_use_only || '—'}/>
          </div>

          {row.signature_data_url && (
            <div className="bg-slate-50 border border-slate-200 p-3">
              <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500 mb-2">Client signature</div>
              <img src={row.signature_data_url} alt="Client signature" className="bg-white border border-slate-300 max-h-32"/>
            </div>
          )}
          {row.agent_signature_data_url && (
            <div className="bg-slate-50 border border-slate-200 p-3">
              <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500 mb-2">Agent signature</div>
              <img src={row.agent_signature_data_url} alt="Agent signature" className="bg-white border border-slate-300 max-h-32"/>
            </div>
          )}

          <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-200 flex-wrap">
            <div className="flex gap-2 flex-wrap">
              {row.token && row.status === 'pending_client' && (
                <button onClick={copyLink} className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold uppercase tracking-tight px-3 py-2">
                  {copied ? 'Copied ✓' : 'Copy sign URL'}
                </button>
              )}
              {row.status === 'completed' && (
                <button onClick={() => printSoaReceipt(row)} className="bg-slate-900 hover:bg-black text-white text-xs font-bold uppercase tracking-tight px-3 py-2">
                  Print receipt (PDF)
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Print-to-PDF rendering of the canonical CMS MED-136 form.
// Opens a new window with the full form layout + both signatures
// inline. Agent triggers Save-as-PDF in the print dialog.
function printSoaReceipt(row) {
  const esc = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  const products = new Set(row.products || []);
  const productBlocks = SOA_PRODUCT_CATALOG.map(section => `
    <h3 style="background:#f1f5f9;padding:6px 10px;font-size:11pt;margin:14px 0 0 0">${esc(section.section)}</h3>
    ${section.items.map(p => `
      <div style="display:flex;gap:10px;padding:10px;border-bottom:1px solid #e2e8f0">
        <div style="width:18px;height:18px;border:1.5px solid #0f172a;${products.has(p.id) ? 'background:#0f172a;color:#fff;' : ''}display:flex;align-items:center;justify-content:center;font-weight:700;font-size:10pt;flex-shrink:0">${products.has(p.id) ? '✓' : ''}</div>
        <div style="font-size:10.5pt"><strong>${esc(p.label)}</strong>${p.description ? ` — ${esc(p.description)}` : ''}</div>
      </div>`).join('')}
  `).join('');
  const html = `<!doctype html><html><head>
<meta charset="utf-8"/>
<title>SOA — ${esc(row.beneficiary_name || '')}</title>
<style>
  body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif;color:#0f172a;margin:0;padding:36px;line-height:1.5;font-size:11pt}
  h1{font-size:20pt;font-weight:800;text-align:center;margin:0 0 14px 0}
  p{margin:0 0 10px 0}
  .grid{display:grid;grid-template-columns:1fr 1fr;gap:18px;margin-top:18px}
  .block{border:1px solid #cbd5e1;padding:12px}
  .block h3{font-size:11pt;margin:0 0 8px 0;font-weight:700;border-bottom:1px solid #cbd5e1;padding-bottom:6px}
  .row{margin:0 0 5px 0;font-size:10pt}
  .row strong{font-weight:600}
  .sig{margin-top:6px;border:1px solid #cbd5e1;background:#fff;padding:6px;display:inline-block}
  .sig img{max-height:90px;display:block}
  hr{border:none;border-top:1px solid #cbd5e1;margin:16px 0}
  .small{font-size:9pt;color:#64748b;font-style:italic;text-align:center}
  .formid{font-size:8pt;color:#94a3b8;font-family:monospace;text-align:right;margin-top:8px}
  @media print{ body{padding:18px} }
</style>
</head><body>
<h1>Scope of Sales Appointment Confirmation Form</h1>
<p>${esc(SOA_DISCLOSURE.replace(/\n\n/g, '</p><p>'))}</p>
<p style="text-align:center;font-weight:700;margin-top:14px">Please mark beside the type of product(s) you want the agent to discuss.</p>
${productBlocks}
<p style="margin-top:16px">${esc(SOA_SIGNATURE_BLOCK.replace(/\n\n/g, '</p><p>'))}</p>

<div class="grid">
  <div class="block">
    <h3>Beneficiary or Authorized Representative Signature and Signature Date:</h3>
    <div class="row"><strong>Name:</strong> ${esc(row.beneficiary_name || '')}</div>
    ${row.beneficiary_phone   ? `<div class="row"><strong>Phone:</strong> ${esc(row.beneficiary_phone)}</div>` : ''}
    ${row.beneficiary_address ? `<div class="row"><strong>Address:</strong> ${esc(row.beneficiary_address)}</div>` : ''}
    ${row.signature_data_url  ? `<div class="sig"><img src="${esc(row.signature_data_url)}" alt="Client signature"/></div>` : ''}
    <div class="row"><strong>Signature Date:</strong> ${esc(formatSoaSignDateLong(row.signed_at))}</div>
    ${row.client_signature_ip ? `<div class="row"><strong>IP Address:</strong> ${esc(row.client_signature_ip)}</div>` : ''}
    ${row.rep_name           ? `<div class="row"><strong>Representative's Name:</strong> ${esc(row.rep_name)}</div>` : ''}
    ${row.rep_relationship   ? `<div class="row"><strong>Relationship to Beneficiary:</strong> ${esc(row.rep_relationship)}</div>` : ''}
  </div>
  <div class="block">
    <h3>To Be Completed By Agent:</h3>
    <div class="row"><strong>Agent Name:</strong> ${esc(row.agent_name || '')}</div>
    <div class="row"><strong>Agent NPN:</strong> ${esc(row.agent_npn || '')}</div>
    <div class="row"><strong>Initial Method of Contact:</strong> ${esc((SOA_CONTACT_METHODS.find(m => m.id === row.contact_method) || {}).label || row.contact_method || '—')}</div>
    ${row.agent_signature_data_url ? `<div class="sig"><img src="${esc(row.agent_signature_data_url)}" alt="Agent signature"/></div>` : `<div class="row" style="color:#64748b;font-style:italic">(Agent signature pending)</div>`}
    ${row.agent_signed_at         ? `<div class="row"><strong>Agent Signature Date:</strong> ${esc(formatSoaSignDateLong(row.agent_signed_at))}</div>` : ''}
    ${row.plans_represented       ? `<div class="row"><strong>Plan(s) the agent represented during this meeting:</strong> ${esc(row.plans_represented)}</div>` : ''}
    ${row.appointment_completed_date ? `<div class="row"><strong>Date Appointment Completed:</strong> ${esc(row.appointment_completed_date)}</div>` : ''}
    ${row.plan_use_only           ? `<div class="row"><strong>[Plan Use Only:]</strong> ${esc(row.plan_use_only)}</div>` : ''}
  </div>
</div>
<hr/>
<div class="small">${esc(SOA_RETENTION_NOTE)}</div>
<div class="formid">${esc(SOA_FORM_ID)}</div>
<script>window.addEventListener('load', function(){ setTimeout(function(){ window.print(); }, 250); });</script>
</body></html>`;
  const w = window.open('', '_blank', 'width=900,height=1100');
  if (!w) return;
  w.document.write(html);
  w.document.close();
}

// ════════════════════════════════════════════════════════════════════
// CERT TRACKER CARD — AHIP + per-carrier certifications.
// Embedded in Settings. Tracks expiry; gates Medicare enrollment
// downstream when any cert is expired.
// ════════════════════════════════════════════════════════════════════
function CertTrackerCard({ currentUser, showToast = () => {} }) {
  const [certs, setCerts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [adding, setAdding] = React.useState(false);
  const [draft, setDraft] = React.useState({ cert_type: 'AHIP', cert_plan_year: new Date().getUTCFullYear() + 1, cert_number: '', completed_at: '', expires_at: '' });

  const load = React.useCallback(async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    const { data } = await supabase.from('agent_certifications').select('*').eq('owner_id', currentUser.id).order('expires_at', { ascending: true });
    setCerts(data || []);
    setLoading(false);
  }, [currentUser?.id]);
  React.useEffect(() => { load(); }, [load]);

  async function add() {
    if (!draft.cert_type?.trim() || !draft.completed_at || !draft.expires_at) { showToast('Type + dates required.'); return; }
    const { data, error } = await supabase.from('agent_certifications').insert({
      owner_id: currentUser.id, cert_type: draft.cert_type.trim(),
      cert_plan_year: draft.cert_plan_year ? Number(draft.cert_plan_year) : null,
      cert_number: draft.cert_number || null, completed_at: draft.completed_at, expires_at: draft.expires_at,
    }).select().single();
    if (error) { showToast(`Failed: ${error.message}`); return; }
    setCerts(prev => [...prev, data].sort((a, b) => new Date(a.expires_at) - new Date(b.expires_at)));
    setAdding(false);
    setDraft({ cert_type: 'AHIP', cert_plan_year: new Date().getUTCFullYear() + 1, cert_number: '', completed_at: '', expires_at: '' });
    showToast('Cert added.');
  }
  async function del(id) {
    if (!(await hqConfirm({
      title: 'Delete this cert?',
      confirmLabel: 'Delete', danger: true,
    }))) return;
    await supabase.from('agent_certifications').delete().eq('id', id);
    setCerts(prev => prev.filter(c => c.id !== id));
  }

  const today = new Date();
  const certStatus = (c) => {
    const daysLeft = Math.floor((new Date(c.expires_at) - today) / 86400000);
    if (daysLeft < 0)  return { label: `⚠ EXPIRED ${Math.abs(daysLeft)}d`, tone: 'red' };
    if (daysLeft < 30) return { label: `⚠ ${daysLeft}d LEFT`, tone: 'amber' };
    if (daysLeft < 90) return { label: `${daysLeft}d`, tone: 'sky' };
    return { label: `${daysLeft}d`, tone: 'emerald' };
  };
  const anyExpired = certs.some(c => new Date(c.expires_at) < today);

  return (
    <div className="bg-white border-2 border-slate-200 p-5">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">Medicare Compliance</div>
          <div className="text-lg font-black text-slate-900 uppercase tracking-tight mt-1">Certification Tracker</div>
        </div>
        <button onClick={() => setAdding(s => !s)} className="text-[0.6rem] font-black uppercase tracking-tight bg-slate-900 text-white px-3 py-1.5">+ Add Cert</button>
      </div>

      {anyExpired && (
        <div className="bg-red-50 border-2 border-red-300 p-2.5 mb-3 text-xs text-red-900">
          <strong className="uppercase tracking-wider text-[0.55rem] font-black text-red-700">⚠ Expired cert detected</strong> — Medicare enrollment marking is blocked until renewed.
        </div>
      )}

      {adding && (
        <div className="bg-slate-50 border border-slate-200 p-3 mb-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <label><span className="text-xs text-slate-500">Cert type</span><input value={draft.cert_type} onChange={e => setDraft(d => ({ ...d, cert_type: e.target.value }))} placeholder="AHIP / UHC Medicare / Humana / etc." className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
          <label><span className="text-xs text-slate-500">Plan year</span><input type="number" value={draft.cert_plan_year} onChange={e => setDraft(d => ({ ...d, cert_plan_year: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 font-mono"/></label>
          <label><span className="text-xs text-slate-500">Cert #</span><input value={draft.cert_number} onChange={e => setDraft(d => ({ ...d, cert_number: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 font-mono"/></label>
          <label><span className="text-xs text-slate-500">Completed</span><input type="date" value={draft.completed_at} onChange={e => setDraft(d => ({ ...d, completed_at: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
          <label className="md:col-span-2"><span className="text-xs text-slate-500">Expires *</span><input type="date" value={draft.expires_at} onChange={e => setDraft(d => ({ ...d, expires_at: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button onClick={() => setAdding(false)} className="text-xs font-bold uppercase text-slate-500 hover:text-slate-900">Cancel</button>
            <button onClick={add} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase tracking-tight px-3 py-1.5">Save</button>
          </div>
        </div>
      )}

      {loading ? (<div className="text-sm text-slate-400 italic py-3 text-center">Loading…</div>)
        : certs.length === 0 ? (<div className="text-sm text-slate-400 italic py-3 text-center">No certs on file. Add AHIP + each Medicare carrier you sell.</div>)
        : (
          <div className="space-y-1.5">
            {certs.map(c => {
              const s = certStatus(c);
              return (
                <div key={c.id} className={`flex items-center justify-between gap-3 border-l-4 border-${s.tone}-500 border-2 border-r-slate-200 border-t-slate-200 border-b-slate-200 p-2`}>
                  <div className="min-w-0">
                    <div className="font-bold text-slate-900 text-sm">{c.cert_type}{c.cert_plan_year ? ` · PY${c.cert_plan_year}` : ''}</div>
                    <div className="text-xs text-slate-500">{c.cert_number || ''} · Completed {c.completed_at} · Expires {c.expires_at}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[0.6rem] font-black uppercase tracking-wider text-${s.tone}-700 bg-${s.tone}-50 border border-${s.tone}-200 px-2 py-0.5`}>{s.label}</span>
                    <button onClick={() => del(c.id)} className="text-[0.55rem] font-bold uppercase text-red-600 hover:text-red-800">DEL</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// FTPIG HEALTH UNDERWRITING — Eligibility Checker + Product Reference
//
// Reads from src/lib/ftpigUnderwritingData.js (UHC / Golden Rule
// indemnity + supplemental products). Two views:
//   • HealthEligibilityCheckerView — age + state + (h/w/sex) → pass/fail
//     per product, w/ TriTerm rate tier display + reasons + compliance.
//   • HealthProductsReferenceView — full product catalog, filter by
//     category, see UW type + age range + chart per product.
// ════════════════════════════════════════════════════════════════════

function HealthEligibilityCheckerView({ onGotoTab = () => {} }) {
  const [form, setForm] = React.useState({ age: '', state: '', height: '', weight: '', sex: 'M' });
  const [showAll, setShowAll] = React.useState(false);
  // v2 — agent-entered client notes / medical conditions. We parse
  // colloquial phrases through phraseAliases and assess HEALTH
  // insurability (disqualifying conditions force the GI/ACA path).
  const [conditionsText, setConditionsText] = React.useState('');
  const [conditionTags, setConditionTags] = React.useState([]); // agent-curated additions

  // Audit 2026-07-02 · #5 Health portal deep-audit: eligibility
  // required manually typing Age/State/Height/Weight/Sex every time.
  // At scale operators skip it or do it inconsistently — that's how
  // E&O claims happen. Look up an existing client by name / phone /
  // email and pre-fill every form field from their row.
  const [clientQuery, setClientQuery]         = React.useState('');
  const [clientMatches, setClientMatches]     = React.useState([]);
  const [showClientResults, setShowClientResults] = React.useState(false);
  const [loadedFromClient, setLoadedFromClient]   = React.useState(null);
  React.useEffect(() => {
    const q = clientQuery.trim();
    if (q.length < 2) { setClientMatches([]); return; }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        // OR search across name / phone / email / state so the
        // operator can type "TX" and get everyone in Texas, or a
        // last-4 of a phone.
        const digits = q.replace(/\D/g, '');
        const orClauses = [`name.ilike.%${q}%`, `email.ilike.%${q}%`];
        if (digits.length >= 4) orClauses.push(`phone.ilike.%${digits}%`);
        const { data } = await supabase
          .from('clients')
          .select('id, name, phone, email, dob, state_abbr, height_in, weight_lb, sex')
          .or(orClauses.join(','))
          .limit(12);
        if (!alive) return;
        setClientMatches(data || []);
        setShowClientResults(true);
      } catch { if (alive) setClientMatches([]); }
    }, 220);
    return () => { alive = false; clearTimeout(t); };
  }, [clientQuery]);

  const applyClient = (c) => {
    // Compute age from dob at query time so the number is always
    // accurate — a client's DOB doesn't change, but their age does.
    let age = '';
    if (c.dob) {
      const d = new Date(c.dob + 'T00:00:00Z');
      if (Number.isFinite(d.getTime())) {
        const now = new Date();
        let a = now.getUTCFullYear() - d.getUTCFullYear();
        const mDiff = now.getUTCMonth() - d.getUTCMonth();
        if (mDiff < 0 || (mDiff === 0 && now.getUTCDate() < d.getUTCDate())) a--;
        age = String(a);
      }
    }
    setForm({
      age,
      state:  c.state_abbr || '',
      height: c.height_in ? String(c.height_in) : '',
      weight: c.weight_lb ? String(c.weight_lb) : '',
      sex:    c.sex || 'M',
    });
    setLoadedFromClient({ id: c.id, name: c.name });
    setShowClientResults(false);
    setClientQuery('');
  };
  const clearLoadedClient = () => {
    setForm({ age: '', state: '', height: '', weight: '', sex: 'M' });
    setLoadedFromClient(null);
  };

  const insurabilityAssessment = React.useMemo(() => {
    return assessHealthInsurability(`${conditionsText}\n${conditionTags.join(', ')}`);
  }, [conditionsText, conditionTags]);

  const results = React.useMemo(() => {
    const h = form.height ? Number(form.height) : null;
    const w = form.weight ? Number(form.weight) : null;
    const a = form.age ? Number(form.age) : null;
    if (a == null && !form.state && !h) return [];
    return checkEligibility({ age: a, state: form.state, height: h, weight: w, sex: form.sex });
  }, [form]);

  const passing = results.filter(r => r.pass);
  const failing = results.filter(r => !r.pass);
  const cats = Object.fromEntries(FTPIG_CATEGORIES.map(c => [c.id, c]));
  const allFlags = new Set();
  passing.forEach(r => r.flags?.forEach(f => allFlags.add(f.id)));
  const flagsToShow = COMPLIANCE_FLAGS.filter(f => allFlags.has(f.id));

  return (
    <div className="space-y-4">
      <div className="bg-black border-l-4 border-sky-500 px-6 py-5 shadow-2xl">
        <div className="text-sky-400 text-[0.6rem] font-black uppercase tracking-[0.3em]">Eligibility Checker</div>
        <div className="text-white text-xl md:text-2xl font-black mt-1.5 uppercase tracking-tight">UHC / Golden Rule Products</div>
        <div className="text-slate-400 text-xs mt-1.5">19 products · age + state + H/W → instant pass/fail per product. <strong className="text-amber-300">Verify in broker portal before binding.</strong></div>
      </div>

      {/* Look up client — pre-fill every profile field in one click */}
      <div className="bg-sky-50 border-2 border-sky-200 p-4">
        <div className="flex items-baseline justify-between gap-2 mb-2 flex-wrap">
          <div>
            <div className="text-[0.6rem] font-black uppercase tracking-[0.15em] text-sky-800">Load an existing client</div>
            <div className="text-xs text-sky-900 mt-0.5">Search by name, phone, or email — every field below auto-fills.</div>
          </div>
          {loadedFromClient && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-wider bg-emerald-100 border border-emerald-300 text-emerald-800 px-2 py-1">
                ✓ Loaded from {loadedFromClient.name}
              </span>
              <button onClick={clearLoadedClient} className="text-[11px] text-sky-700 hover:text-sky-900 font-semibold underline">
                Clear
              </button>
            </div>
          )}
        </div>
        <div className="relative">
          <input
            value={clientQuery}
            onChange={e => setClientQuery(e.target.value)}
            placeholder="Search 237 clients…"
            className="w-full border border-sky-300 bg-white px-3 py-2 text-sm rounded-md focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
            onFocus={() => clientMatches.length > 0 && setShowClientResults(true)}
            onBlur={() => setTimeout(() => setShowClientResults(false), 180)}
          />
          {showClientResults && clientMatches.length > 0 && (
            <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-300 rounded-md shadow-lg z-20 max-h-64 overflow-y-auto">
              {clientMatches.map(c => {
                const missing = [
                  !c.dob        ? 'DOB'    : null,
                  !c.state_abbr ? 'state'  : null,
                  !c.height_in  ? 'height' : null,
                  !c.weight_lb  ? 'weight' : null,
                  !c.sex        ? 'sex'    : null,
                ].filter(Boolean);
                return (
                  <button
                    key={c.id}
                    onMouseDown={() => applyClient(c)}
                    className="w-full text-left px-3 py-2 hover:bg-sky-50 border-b border-slate-100 last:border-0"
                  >
                    <div className="text-sm font-bold text-slate-900">{c.name || '(unnamed)'}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      {c.phone && <span>{c.phone}</span>}
                      {c.email && <span>· {c.email}</span>}
                      {c.state_abbr && <span>· {c.state_abbr}</span>}
                      {c.dob && <span>· DOB {c.dob}</span>}
                    </div>
                    {missing.length > 0 && (
                      <div className="text-[10px] text-amber-700 mt-0.5">
                        ⚠ missing: {missing.join(', ')}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {showClientResults && clientMatches.length === 0 && clientQuery.trim().length >= 2 && (
            <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-300 rounded-md shadow-lg z-20 px-3 py-2 text-xs text-slate-500">
              No client matches. Type manually below — or add the client on the Health Clients tab first.
            </div>
          )}
        </div>
      </div>

      {/* Inputs */}
      <div className="bg-white border-2 border-slate-200 p-5">
        <div className="text-[0.6rem] font-black uppercase tracking-[0.15em] text-slate-500 mb-3">Client Profile</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
          <label><span className="text-xs text-slate-500">Age</span>
            <input type="number" value={form.age} onChange={e => setForm(f => ({ ...f, age: e.target.value }))} placeholder="e.g. 55" className="mt-1 w-full border border-slate-300 px-2 py-1.5 font-mono"/></label>
          <label><span className="text-xs text-slate-500">State</span>
            <input value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value.toUpperCase().slice(0, 2) }))} placeholder="e.g. TX" maxLength={2} className="mt-1 w-full border border-slate-300 px-2 py-1.5 font-mono uppercase"/></label>
          <label><span className="text-xs text-slate-500">Height (in)</span>
            <input type="number" value={form.height} onChange={e => setForm(f => ({ ...f, height: e.target.value }))} placeholder="e.g. 70" className="mt-1 w-full border border-slate-300 px-2 py-1.5 font-mono"/></label>
          <label><span className="text-xs text-slate-500">Weight (lb)</span>
            <input type="number" value={form.weight} onChange={e => setForm(f => ({ ...f, weight: e.target.value }))} placeholder="e.g. 180" className="mt-1 w-full border border-slate-300 px-2 py-1.5 font-mono"/></label>
          <label><span className="text-xs text-slate-500">Sex</span>
            <select value={form.sex} onChange={e => setForm(f => ({ ...f, sex: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 bg-white">
              <option value="M">Male</option><option value="F">Female</option><option value="X">Other</option>
            </select></label>
        </div>
      </div>

      {/* Medical conditions / notes — free-text the agent fills in
          from intake. Routes through phraseAliases.js so colloquial
          phrasing ("on oxygen", "mini stroke", "takes insulin") gets
          recognized as the canonical underwriting condition. */}
      <div className={`bg-white border-2 ${insurabilityAssessment.insurable ? 'border-emerald-300' : 'border-red-400'} p-5`}>
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
          <div>
            <div className="text-[0.6rem] font-black uppercase tracking-[0.15em] text-slate-500">Client Medical Notes</div>
            <div className="text-base font-black text-slate-900 uppercase tracking-tight mt-0.5">Conditions · Meds · Risk Factors</div>
            <div className="text-xs text-slate-500 mt-1">Write how the client told it to you. Slang and colloquialisms work — "on oxygen", "takes insulin", "mini stroke 6 months ago", etc.</div>
          </div>
          <div className={`text-[0.55rem] font-black uppercase tracking-wider px-2 py-1 border-2 ${insurabilityAssessment.insurable ? 'border-emerald-500 text-emerald-700 bg-emerald-50' : 'border-red-500 text-red-700 bg-red-50'}`}>
            {insurabilityAssessment.insurable ? '✓ Insurable (SI path)' : '⚠ GI / ACA path required'}
          </div>
        </div>
        <textarea
          rows={4}
          value={conditionsText}
          onChange={e => setConditionsText(e.target.value)}
          placeholder={'Diabetes type 2, takes metformin daily.\nMini stroke 3 years ago, fully recovered, no residuals.\nNot on any other meds. Active — walks 4 miles a day.'}
          className="w-full border border-slate-300 px-3 py-2 text-sm font-mono"
        />
        {/* Detected conditions chips */}
        {insurabilityAssessment.hits.length > 0 && (
          <div className="mt-3">
            <div className="text-[0.55rem] font-black uppercase tracking-[0.15em] text-slate-500 mb-1.5">Detected from notes ({insurabilityAssessment.hits.length})</div>
            <div className="flex flex-wrap gap-1.5">
              {insurabilityAssessment.hits.map((h, i) => {
                const isDisq = h.conditionFragments.some(f =>
                  insurabilityAssessment.disqualifiers.some(d => d.toLowerCase().includes(String(f).toLowerCase()) || String(f).toLowerCase().includes(d.toLowerCase()))
                );
                return (
                  <span key={i} className={`inline-flex items-center gap-1 px-2 py-1 text-[0.65rem] font-bold ${isDisq ? 'bg-red-100 text-red-800 border border-red-300' : 'bg-slate-100 text-slate-800 border border-slate-300'}`}>
                    "{h.matched}" → <strong>{h.conditionFragments.join(' / ')}</strong>
                    {isDisq && <span className="ml-0.5 text-[0.55rem] font-black uppercase tracking-wider text-red-700">·DISQ</span>}
                  </span>
                );
              })}
            </div>
          </div>
        )}
        {/* Manual tag input */}
        <div className="mt-3">
          <div className="text-[0.55rem] font-black uppercase tracking-[0.15em] text-slate-500 mb-1.5">Quick add common conditions</div>
          <div className="flex flex-wrap gap-1.5">
            {['Diabetes', 'High BP', 'High Cholesterol', 'Anxiety', 'Depression', 'Sleep Apnea', 'Asthma', 'Acid Reflux', 'Thyroid', 'Arthritis'].map(t => (
              <button key={t}
                onClick={() => setConditionTags(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])}
                className={`text-xs font-bold px-2 py-1 border-2 ${conditionTags.includes(t) ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'}`}>
                {conditionTags.includes(t) ? '✓ ' : '+ '}{t}
              </button>
            ))}
          </div>
        </div>
        {/* Buckets summary + advice */}
        {insurabilityAssessment.buckets.length > 0 && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <div className="text-[0.55rem] font-black uppercase tracking-[0.15em] text-slate-500 mb-1">Affected systems</div>
              <div className="flex flex-wrap gap-1">
                {insurabilityAssessment.buckets.map(b => (
                  <span key={b} className="text-[0.6rem] font-black uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200 px-1.5 py-0.5">{b}</span>
                ))}
              </div>
            </div>
            {insurabilityAssessment.disqualifiers.length > 0 && (
              <div>
                <div className="text-[0.55rem] font-black uppercase tracking-[0.15em] text-red-700 mb-1">Underwriting decline</div>
                <div className="flex flex-wrap gap-1">
                  {insurabilityAssessment.disqualifiers.map(d => (
                    <span key={d} className="text-[0.6rem] font-black uppercase tracking-wider bg-red-100 text-red-800 border border-red-300 px-1.5 py-0.5">{d}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <div className={`mt-3 text-xs px-3 py-2 border-l-4 ${insurabilityAssessment.insurable ? 'border-emerald-500 bg-emerald-50 text-emerald-900' : 'border-red-500 bg-red-50 text-red-900'}`}>
          <strong className="uppercase tracking-wider text-[0.55rem] mr-1.5">Verdict:</strong>{insurabilityAssessment.advice}
        </div>
        <div className="mt-3 flex justify-end">
          <button
            onClick={() => {
              try {
                sessionStorage.setItem('xsell_handoff', JSON.stringify({
                  form,
                  conditionsText,
                  conditionTags,
                  insurable: insurabilityAssessment.insurable,
                  ts: Date.now(),
                }));
              } catch {}
              onGotoTab('cross_sell');
            }}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase tracking-tight px-4 py-2"
          >
            ➜ Send to Cross-Sell{insurabilityAssessment.insurable ? '' : ' (GI path)'}
          </button>
        </div>
        <div className="mt-1 text-[0.55rem] text-slate-400 italic uppercase tracking-wider">Notes carry into the simulator; insurability passed to the recommender so the right gate fires.</div>
      </div>

      {results.length > 0 && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="bg-emerald-50 border-2 border-emerald-500 p-4">
              <div className="text-[0.55rem] font-black uppercase tracking-[0.15em] text-emerald-800">✓ Quotable</div>
              <div className="text-3xl font-black text-emerald-700 tabular-nums leading-none mt-1">{passing.length}</div>
              <div className="text-[0.6rem] uppercase tracking-wider text-emerald-700 mt-0.5">of {results.length} products</div>
            </div>
            <div className="bg-red-50 border-2 border-red-300 p-4">
              <div className="text-[0.55rem] font-black uppercase tracking-[0.15em] text-red-800">✗ Ineligible</div>
              <div className="text-3xl font-black text-red-700 tabular-nums leading-none mt-1">{failing.length}</div>
              <div className="text-[0.6rem] uppercase tracking-wider text-red-700 mt-0.5">at least 1 gate</div>
            </div>
            <div className="bg-amber-50 border-2 border-amber-300 p-4">
              <div className="text-[0.55rem] font-black uppercase tracking-[0.15em] text-amber-800">⚠ Compliance flags</div>
              <div className="text-3xl font-black text-amber-700 tabular-nums leading-none mt-1">{flagsToShow.length}</div>
              <div className="text-[0.6rem] uppercase tracking-wider text-amber-700 mt-0.5">disclosure required</div>
            </div>
          </div>

          {/* Compliance flags */}
          {flagsToShow.length > 0 && (
            <div className="bg-amber-50 border-2 border-amber-300 p-4 space-y-2">
              <div className="text-[0.6rem] font-black uppercase tracking-[0.15em] text-amber-800">⚠ MUST disclose to client</div>
              {flagsToShow.map(f => (
                <div key={f.id} className="text-sm text-amber-900 border-l-4 border-amber-500 pl-3">{f.text}</div>
              ))}
            </div>
          )}

          {/* Passing products */}
          {passing.length > 0 && (
            <div className="bg-white border-2 border-emerald-200 p-5">
              <div className="text-[0.6rem] font-black uppercase tracking-[0.15em] text-emerald-700 mb-3">✓ Quote these</div>
              <div className="space-y-2">
                {passing.map(r => {
                  const c = cats[r.product.category] || { tone: 'slate' };
                  return (
                    <div key={r.product.id} className={`border-l-4 border-${c.tone}-500 border-2 border-r-slate-200 border-t-slate-200 border-b-slate-200 p-3`}>
                      <div className="flex items-baseline justify-between gap-2 flex-wrap">
                        <div className="font-bold text-slate-900">{r.product.name}</div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[0.55rem] font-black uppercase tracking-wider px-2 py-0.5 bg-${c.tone}-100 text-${c.tone}-800`}>{r.product.underwriting.join(' / ')}</span>
                          {r.hwTier && <span className="text-[0.6rem] font-bold uppercase text-amber-700 bg-amber-50 border border-amber-300 px-2 py-0.5">Tier: {r.hwTier}</span>}
                        </div>
                      </div>
                      <div className="text-[0.6rem] uppercase tracking-wider text-slate-500 mt-0.5">{cats[r.product.category]?.label} · Issue age {r.product.issue_age[0]}–{r.product.issue_age[1]}</div>
                      {r.product.notes && <div className="text-xs text-slate-600 mt-1 italic">{r.product.notes}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Failing products (collapsed by default) */}
          {failing.length > 0 && (
            <div className="bg-white border border-slate-200 p-5">
              <button onClick={() => setShowAll(s => !s)} className="flex items-baseline justify-between w-full">
                <div className="text-[0.6rem] font-black uppercase tracking-[0.15em] text-slate-500">✗ Ineligible — why</div>
                <div className="text-[0.6rem] font-bold uppercase text-slate-500">{showAll ? '▼ HIDE' : `▶ SHOW (${failing.length})`}</div>
              </button>
              {showAll && (
                <div className="mt-3 space-y-1.5">
                  {failing.map(r => (
                    <div key={r.product.id} className="border-l-4 border-red-400 border-2 border-r-slate-200 border-t-slate-200 border-b-slate-200 p-2.5">
                      <div className="font-bold text-slate-900 text-sm">{r.product.name}</div>
                      <ul className="text-xs text-red-700 mt-1 space-y-0.5">
                        {r.reasons.map((reason, i) => <li key={i}>• {reason}</li>)}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <div className="text-[0.6rem] text-slate-400 italic px-2">Disclaimer: Indemnity / supplemental plans are NOT MEC and not a substitute for major medical insurance.</div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// CROSS-SELL SIMULATOR (Phase A)
//
// The thesis of the v2 spec: don't sell one product, build a STACK.
// Pick a client (or enter demographics inline), run eligibility against
// the FTPIG product catalog, and drag passing products into a stack
// with chosen plan + agent-entered monthly rate. Live totals show the
// client's monthly cost, annualized cost, and the agent's first-year
// commission estimate (default 20% per ancillary product — editable).
//
// Phase A = manual rates. Phase B (deferred) = HealthSherpa quote API
// for real-time ACA/dental rates. The stack and compliance flags
// already work the same in both phases.
//
// Output: copy-to-text format the agent can paste into an SMS or
// follow-up email, with required disclosures inline.
// ════════════════════════════════════════════════════════════════════

function CrossSellSimulatorView({ currentUser, settings = {}, showToast = () => {} }) {
  const [clients, setClients] = React.useState([]);
  const [clientId, setClientId] = React.useState('');
  const [form, setForm] = React.useState({ age: '', state: '', height: '', weight: '', sex: 'M' });
  const [stack, setStack] = React.useState([]); // [{key, productId, plan, monthly, commissionPct}]
  const [defaultCommissionPct, setDefaultCommissionPct] = React.useState(20);
  const [copied, setCopied] = React.useState(false);
  // v2 — persisted quote tracking.
  const [quoteId, setQuoteId]       = React.useState(null);    // null = unsaved draft
  const [quoteStatus, setQuoteStatus] = React.useState('draft'); // draft|sent|bound|lost
  const [quoteNotes, setQuoteNotes] = React.useState('');
  const [savedQuotes, setSavedQuotes] = React.useState([]);
  const [saving, setSaving] = React.useState(false);

  // Smart Recommend — intake + gating variables + walked decision tree.
  const [recommendOpen, setRecommendOpen] = React.useState(false);
  const [intake, setIntake] = React.useState({
    insurable: true,
    subsidyEligible: false,
    needsMec: false,
    pregnant: false,
    answers: {},   // { q_sports: true, q_kids: false, ... }
  });
  const [recommendation, setRecommendation] = React.useState(null);
  const [externalStack, setExternalStack] = React.useState([]);   // ACA / HPG / TriTerm rows agent quoted externally

  React.useEffect(() => {
    if (!currentUser?.id) return;
    supabase
      .from('clients')
      .select('id, name, sector, dob, state_abbr, sex, height_in, weight_lb')
      .order('name')
      .then(({ data }) => setClients(data || []));
  }, [currentUser?.id]);

  // Pickup handoff from Eligibility Checker. Pre-fills demographics
  // + insurability so the recommender lights up the right gate.
  // One-shot: sessionStorage entry is removed after first read.
  React.useEffect(() => {
    try {
      const raw = sessionStorage.getItem('xsell_handoff');
      if (!raw) return;
      const h = JSON.parse(raw);
      if (h && h.ts && Date.now() - h.ts < 60000) {
        if (h.form) setForm(f => ({ ...f, ...h.form }));
        if (typeof h.insurable === 'boolean') {
          setIntake(s => ({ ...s, insurable: h.insurable, subsidyEligible: !h.insurable, needsMec: !h.insurable }));
          setRecommendOpen(true);
        }
        showToast('Demographics + insurability pulled from Eligibility Checker.');
      }
      sessionStorage.removeItem('xsell_handoff');
    } catch {}
  }, [showToast]);

  // Load saved quotes for the picked client. When clientId clears, hide.
  React.useEffect(() => {
    if (!currentUser?.id || !clientId) { setSavedQuotes([]); return; }
    supabase
      .from('cross_sell_quotes')
      .select('id, created_at, updated_at, status, total_monthly, est_commission, stack, notes, age_at_quote, state_at_quote, sex_at_quote, height_in_at_quote, weight_lb_at_quote')
      .eq('client_id', clientId)
      .order('updated_at', { ascending: false })
      .limit(20)
      .then(({ data }) => setSavedQuotes(data || []));
  }, [currentUser?.id, clientId]);

  // Prefill demographics from the picked client. DOB → age, etc.
  function pickClient(id) {
    setClientId(id);
    // Switching clients drops the current draft — the quote is bound
    // to a client, so reuse would be wrong.
    setQuoteId(null);
    setQuoteStatus('draft');
    setQuoteNotes('');
    setStack([]);
    if (!id) return;
    const c = clients.find(x => x.id === id);
    if (!c) return;
    const age = (() => {
      if (!c.dob) return '';
      const d = new Date(String(c.dob).slice(0, 10) + 'T00:00:00');
      if (Number.isNaN(d.getTime())) return '';
      return Math.floor((Date.now() - d.getTime()) / (365.25 * 86400000));
    })();
    setForm({
      age:    age || '',
      state:  c.state_abbr || '',
      height: c.height_in || '',
      weight: c.weight_lb || '',
      sex:    c.sex || 'M',
    });
  }

  // Load an existing saved quote — rehydrates stack + form snapshot.
  function loadQuote(q) {
    setQuoteId(q.id);
    setQuoteStatus(q.status || 'draft');
    setQuoteNotes(q.notes || '');
    setStack((q.stack || []).map(row => ({ ...row, key: newKey() })));
    setForm({
      age:    q.age_at_quote ?? '',
      state:  q.state_at_quote ?? '',
      height: q.height_in_at_quote ?? '',
      weight: q.weight_lb_at_quote ?? '',
      sex:    q.sex_at_quote ?? 'M',
    });
    showToast('Quote loaded — edits will overwrite on Save.');
  }

  function startNewQuote() {
    setQuoteId(null);
    setQuoteStatus('draft');
    setQuoteNotes('');
    setStack([]);
  }

  const results = React.useMemo(() => {
    const a = form.age ? Number(form.age) : null;
    const h = form.height ? Number(form.height) : null;
    const w = form.weight ? Number(form.weight) : null;
    if (a == null && !form.state && !h) return [];
    return checkEligibility({ age: a, state: form.state, height: h, weight: w, sex: form.sex });
  }, [form]);

  const passing = results.filter(r => r.pass);
  const cats = Object.fromEntries(FTPIG_CATEGORIES.map(c => [c.id, c]));

  const newKey = () => (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `s_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

  function addProduct(p) {
    setStack(prev => [...prev, {
      key: newKey(),
      productId: p.id,
      plan: Array.isArray(p.plans) ? (p.plans[0] || '') : '',
      monthly: '',
      commissionPct: defaultCommissionPct,
    }]);
  }
  function updateRow(key, patch) { setStack(prev => prev.map(r => r.key === key ? { ...r, ...patch } : r)); }
  function removeRow(key) { setStack(prev => prev.filter(r => r.key !== key)); }
  async function clearStack() {
    if (stack.length === 0) return;
    if (!(await hqConfirm({
      title: 'Clear the entire stack?',
      confirmLabel: 'Clear stack', danger: true,
    }))) return;
    setStack([]);
  }

  // Smart Recommend — run the decision tree against current form +
  // intake, then auto-fill the stack with the catalog products
  // (validated against eligibility). External-quote items (ACA,
  // HPG, TriTerm) go to a separate externalStack so the agent can
  // enter the rate they got from HealthSherpa / broker portal.
  function runRecommend() {
    const result = recommendStack({
      age:    form.age ? Number(form.age) : null,
      state:  form.state,
      sex:    form.sex,
      height: form.height ? Number(form.height) : null,
      weight: form.weight ? Number(form.weight) : null,
      insurable:       intake.insurable,
      subsidyEligible: intake.subsidyEligible,
      needsMec:        intake.needsMec,
      pregnant:        intake.pregnant,
      intakeAnswers:   intake.answers,
    });
    setRecommendation(result);
    if (result.terminal) {
      showToast('Terminal gate — see narrative below; no UHC stack returned.');
      setStack([]);
      setExternalStack([]);
      return;
    }
    // Apply catalog products to the visible stack — keep any rows
    // the agent already filled (productId-matched) so they don't
    // lose their rate work.
    const catalogItems = [...result.foundation, ...result.supplements].filter(r => r.kind === 'catalog');
    const existing = new Map(stack.map(r => [r.productId, r]));
    const next = catalogItems.map(r => {
      const prior = existing.get(r.productId);
      if (prior) return prior;
      const p = r.productMeta || FTPIG_PRODUCTS.find(x => x.id === r.productId);
      return {
        key: newKey(),
        productId: r.productId,
        plan: Array.isArray(p?.plans) ? (p.plans[0] || '') : '',
        monthly: '',
        commissionPct: defaultCommissionPct,
      };
    });
    setStack(next);
    // External: ACA / HPG / TriTerm — separate list with agent-entered rates.
    const externals = [...result.foundation, ...result.supplements, ...result.external].filter(r => r.kind === 'virtual');
    const existingExternal = new Map(externalStack.map(r => [r.virtualId, r]));
    setExternalStack(externals.map(r => {
      const prior = existingExternal.get(r.product.id);
      if (prior) return prior;
      return {
        key: newKey(),
        virtualId: r.product.id,
        name: r.product.name,
        monthly: '',
      };
    }));
    showToast(`Recommendation: ${result.gateId} → ${catalogItems.length} catalog + ${externals.length} external item${externals.length === 1 ? '' : 's'}`);
  }

  function applyPreset(preset) {
    if (!preset?.preset) return;
    setForm(f => ({
      ...f,
      age:   preset.preset.age   ?? f.age,
      state: preset.preset.state ?? f.state,
      sex:   preset.preset.sex   ?? f.sex,
    }));
    setIntake({
      insurable:       preset.preset.insurable        ?? true,
      subsidyEligible: preset.preset.subsidyEligible  ?? false,
      needsMec:        preset.preset.needsMec         ?? false,
      pregnant:        preset.preset.pregnant         ?? false,
      answers: {},
    });
    setRecommendOpen(true);
    // Defer recommend run until state has settled.
    setTimeout(() => runRecommend(), 30);
  }

  function updateExternalRate(key, monthly) {
    setExternalStack(prev => prev.map(r => r.key === key ? { ...r, monthly } : r));
  }
  function removeExternal(key) {
    setExternalStack(prev => prev.filter(r => r.key !== key));
  }

  // Total premium across catalog + external rows.
  const grandMonthly = React.useMemo(() => {
    const cat = stack.reduce((s, r) => s + (Number(r.monthly) || 0), 0);
    const ext = externalStack.reduce((s, r) => s + (Number(r.monthly) || 0), 0);
    return cat + ext;
  }, [stack, externalStack]);

  // Save (or overwrite) the current quote. Requires a client — the
  // table allows null client_id but the UX flow makes the client the
  // primary index, so we gate at the UI layer.
  async function saveQuote() {
    if (!currentUser?.id) { showToast('Not signed in.'); return; }
    if (!clientId) { showToast('Pick a client first.'); return; }
    if (stack.length === 0) { showToast('Stack is empty — nothing to save.'); return; }

    const client = clients.find(c => c.id === clientId);
    const cleanStack = stack.map(({ key, ...rest }) => ({
      productId: rest.productId,
      plan: rest.plan || '',
      monthly: Number(rest.monthly) || 0,
      commissionPct: Number(rest.commissionPct) || 0,
    }));

    const row = {
      owner_id:  currentUser.id,
      client_id: clientId,
      client_name_at_quote: client?.name || null,
      age_at_quote:        form.age    === '' ? null : Number(form.age)    || null,
      state_at_quote:      form.state  || null,
      sex_at_quote:        form.sex    || null,
      height_in_at_quote:  form.height === '' ? null : Number(form.height) || null,
      weight_lb_at_quote:  form.weight === '' ? null : Number(form.weight) || null,
      stack: cleanStack,
      total_monthly:  Number(totals.monthly.toFixed(2)),
      total_annual:   Number(totals.annual.toFixed(2)),
      est_commission: Number(totals.commission.toFixed(2)),
      status: quoteStatus,
      notes: quoteNotes || null,
    };

    setSaving(true);
    try {
      if (quoteId) {
        const { data, error } = await supabase
          .from('cross_sell_quotes')
          .update(row)
          .eq('id', quoteId)
          .select()
          .single();
        if (error) throw error;
        setSavedQuotes(prev => {
          const without = prev.filter(q => q.id !== quoteId);
          return [data, ...without];
        });
        showToast('Quote updated.');
      } else {
        const { data, error } = await supabase
          .from('cross_sell_quotes')
          .insert(row)
          .select()
          .single();
        if (error) throw error;
        setQuoteId(data.id);
        setSavedQuotes(prev => [data, ...prev]);
        showToast('Quote saved.');
      }
    } catch (e) {
      showToast(`Save failed: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  }

  async function deleteQuote(id) {
    if (!(await hqConfirm({
      title: 'Delete this quote?',
      body: 'Cannot be undone.',
      confirmLabel: 'Delete quote', danger: true,
    }))) return;
    const { error } = await supabase.from('cross_sell_quotes').delete().eq('id', id);
    if (error) { showToast(`Delete failed: ${error.message}`); return; }
    setSavedQuotes(prev => prev.filter(q => q.id !== id));
    if (quoteId === id) startNewQuote();
    showToast('Quote deleted.');
  }

  // Compliance flags that apply to anything currently in the stack.
  const stackFlags = React.useMemo(() => {
    const ids = new Set();
    stack.forEach(r => {
      const p = FTPIG_PRODUCTS.find(x => x.id === r.productId);
      if (!p) return;
      COMPLIANCE_FLAGS.filter(f => f.applies_to?.includes(p.category)).forEach(f => ids.add(f.id));
    });
    return COMPLIANCE_FLAGS.filter(f => ids.has(f.id));
  }, [stack]);

  const totals = React.useMemo(() => {
    const monthly = stack.reduce((sum, r) => sum + (Number(r.monthly) || 0), 0);
    const annual  = monthly * 12;
    const commission = stack.reduce((sum, r) => sum + ((Number(r.monthly) || 0) * 12 * (Number(r.commissionPct) || 0) / 100), 0);
    return { monthly, annual, commission };
  }, [stack]);

  const fmt$ = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

  // Copy-to-text — what the agent pastes into SMS. Disclosures inline,
  // not buried — so the message that goes to the client is compliant
  // by default.
  function copyStackText() {
    if (stack.length === 0) { showToast('Stack is empty.'); return; }
    const client = clients.find(c => c.id === clientId);
    const greet = client?.name ? `Hi ${client.name.split(' ')[0]}, ` : '';
    const lines = stack.map(r => {
      const p = FTPIG_PRODUCTS.find(x => x.id === r.productId);
      return `• ${p?.name || r.productId}${r.plan ? ` (${r.plan})` : ''} — ${fmt$(r.monthly)}/mo`;
    });
    const disclosures = [];
    if (stackFlags.some(f => f.id === 'stm_not_mec' || f.id === 'not_mec')) {
      disclosures.push('Note: Hospital, accident, and STM plans are supplemental — NOT major medical (not MEC).');
    }
    if (stackFlags.some(f => f.id === 'hy_not_insurance' || f.id === 'dental_savings_not_insurance')) {
      disclosures.push('Note: Telehealth and dental-savings memberships are NOT insurance.');
    }
    const block = [
      `${greet}here's the stack we talked about:`,
      '',
      ...lines,
      '',
      `Total: ${fmt$(totals.monthly)}/mo (${fmt$(totals.annual)}/yr)`,
      '',
      ...disclosures,
    ].join('\n');
    navigator.clipboard?.writeText(block).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      showToast('Stack copied — paste into SMS.');
    }).catch(() => showToast('Copy failed — clipboard blocked.'));
  }

  return (
    <div className="space-y-4">
      <div className="bg-black border-l-4 border-emerald-500 px-6 py-5 shadow-2xl">
        <div className="text-emerald-400 text-[0.6rem] font-black uppercase tracking-[0.3em]">Cross-Sell Simulator</div>
        <div className="text-white text-xl md:text-2xl font-black mt-1.5 uppercase tracking-tight">Build a Stack · Phase A · Manual Rates</div>
        <div className="text-slate-400 text-xs mt-1.5">Pick a client → eligibility runs against UHC / Golden Rule + Teladoc → add products → enter rate from your broker portal. <strong className="text-emerald-300">Verify rates before quoting.</strong></div>
      </div>

      {/* Client + demographics */}
      <div className="bg-white border border-slate-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 text-sm">
          <label className="block md:col-span-2">
            <span className="text-xs text-slate-500">Client</span>
            <select value={clientId} onChange={e => pickClient(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-1.5 bg-white">
              <option value="">— manual entry —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}{c.sector === 'medicare' ? ' · Medicare' : ''}</option>)}
            </select>
          </label>
          <label className="block"><span className="text-xs text-slate-500">Age</span><input type="number" value={form.age} onChange={e => setForm(f => ({ ...f, age: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
          <label className="block"><span className="text-xs text-slate-500">State</span><input value={form.state} maxLength={2} onChange={e => setForm(f => ({ ...f, state: e.target.value.toUpperCase() }))} placeholder="TX" className="mt-1 w-full border border-slate-300 px-2 py-1.5 uppercase font-mono"/></label>
          <label className="block"><span className="text-xs text-slate-500">Height (in)</span><input type="number" value={form.height} onChange={e => setForm(f => ({ ...f, height: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
          <label className="block"><span className="text-xs text-slate-500">Weight (lb)</span><input type="number" value={form.weight} onChange={e => setForm(f => ({ ...f, weight: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
          <label className="block"><span className="text-xs text-slate-500">Sex</span>
            <select value={form.sex} onChange={e => setForm(f => ({ ...f, sex: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 bg-white">
              <option value="M">M</option><option value="F">F</option>
            </select>
          </label>
        </div>
      </div>

      {/* Preset chips — one-click client profiles. */}
      <div className="bg-white border border-slate-200 p-3">
        <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500 mb-2">⚡ Quick presets · client profile</div>
        <div className="flex flex-wrap gap-2">
          {getPresets().map(p => (
            <button key={p.id} onClick={() => applyPreset(p)}
              className="bg-slate-50 border-2 border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/60 px-3 py-2 text-left text-sm font-bold transition-colors">
              <div className="text-slate-900">{p.label}</div>
              <div className="text-[0.6rem] uppercase tracking-wider text-slate-500 mt-0.5">{p.match}</div>
              {p.monthlyEstimateUsd && <div className="text-[0.6rem] text-emerald-700 font-black mt-1">~${p.monthlyEstimateUsd}/mo example</div>}
            </button>
          ))}
        </div>
      </div>

      {/* Smart Recommend — intake + decision tree. */}
      <div className="bg-white border-2 border-emerald-500 p-4">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-emerald-700">Smart Recommend</div>
            <div className="text-lg font-black text-slate-900 uppercase tracking-tight mt-0.5">Strategy-driven stack builder</div>
            <div className="text-xs text-slate-500 mt-1">Walks the FTPIG decision tree (pregnancy → IL → near-Medicare → insurability → subsidy/MEC → healthy underwritten) to recommend a layered stack.</div>
          </div>
          <button onClick={() => setRecommendOpen(o => !o)} className="bg-slate-900 hover:bg-black text-white text-xs font-black uppercase tracking-tight px-3 py-1.5">
            {recommendOpen ? '▲ Collapse' : '▼ Intake'}
          </button>
        </div>

        {recommendOpen && (
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { id: 'insurable',       label: 'Insurable (passes SI underwriting)?', invert: false },
                { id: 'subsidyEligible', label: 'Subsidy-eligible (ACA APTC)?',         invert: false },
                { id: 'needsMec',        label: 'Needs MEC (mandate state or wants ACA)?', invert: false },
                { id: 'pregnant',        label: 'Existing pregnancy?',                   invert: false },
              ].map(g => (
                <label key={g.id} className={`border-2 ${intake[g.id] ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 bg-white'} p-2 cursor-pointer flex items-start gap-2`}>
                  <input type="checkbox" className="mt-0.5" checked={!!intake[g.id]} onChange={e => setIntake(s => ({ ...s, [g.id]: e.target.checked }))}/>
                  <span className="text-xs font-bold text-slate-800">{g.label}</span>
                </label>
              ))}
            </div>
            <div>
              <div className="text-[0.6rem] font-black uppercase tracking-[0.15em] text-slate-500 mb-1.5">Intake — check what applies</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                {getIntakeQuestions().map(q => (
                  <label key={q.id} className={`border ${intake.answers[q.id] ? 'border-slate-900 bg-slate-50' : 'border-slate-200'} p-1.5 cursor-pointer flex items-start gap-2`}>
                    <input type="checkbox" className="mt-0.5" checked={!!intake.answers[q.id]} onChange={e => setIntake(s => ({ ...s, answers: { ...s.answers, [q.id]: e.target.checked } }))}/>
                    <span className="text-xs text-slate-800">{q.q}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={runRecommend} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase tracking-tight px-4 py-2">
                Recommend Stack
              </button>
            </div>
          </div>
        )}

        {recommendation && (
          <div className="mt-4 border-t-2 border-emerald-200 pt-3 space-y-2">
            <div className="text-[0.6rem] font-black uppercase tracking-[0.15em] text-emerald-700">Gate matched · {recommendation.gateId}</div>
            <div className="text-sm text-slate-800">{recommendation.narrative}</div>
            {recommendation.dropped?.length > 0 && (
              <div className="bg-amber-50 border border-amber-300 px-3 py-2 text-xs text-amber-900">
                <strong className="uppercase text-[0.55rem] tracking-wider">Dropped for eligibility:</strong>
                {recommendation.dropped.map((d, i) => <div key={i}>· {d.name}: {d.reasons?.join(' · ')}</div>)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* External-quote stack — ACA / HPG / TriTerm. Agent enters
          the rate they got from HealthSherpa or the broker portal. */}
      {externalStack.length > 0 && (
        <div className="bg-white border-2 border-indigo-400 p-4">
          <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-indigo-700 mb-3">External Quote · agent fills in from HealthSherpa / broker portal</div>
          <div className="space-y-2">
            {externalStack.map(row => {
              const vp = STRATEGY_VIRTUAL_PRODUCTS[Object.keys(STRATEGY_VIRTUAL_PRODUCTS).find(k => STRATEGY_VIRTUAL_PRODUCTS[k].id === row.virtualId)] || { name: row.name, note: '', mec: false, requiresExternalQuote: 'broker_portal' };
              return (
                <div key={row.key} className="border border-slate-200 p-2 grid grid-cols-1 md:grid-cols-[1fr_120px_60px] gap-2 items-center">
                  <div>
                    <div className="font-bold text-sm text-slate-900">{vp.name}{vp.mec && <span className="ml-2 text-[0.55rem] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5">MEC</span>}</div>
                    <div className="text-[0.6rem] uppercase tracking-wider text-slate-500">{vp.requiresExternalQuote === 'healthsherpa' ? 'Quote in HealthSherpa with NPN' : 'Quote in broker portal'}{vp.note ? ` · ${vp.note}` : ''}</div>
                  </div>
                  <label className="block">
                    <span className="text-[0.55rem] uppercase tracking-wider text-slate-500">$/mo (from external quote)</span>
                    <input type="number" min="0" step="0.01" value={row.monthly} onChange={e => updateExternalRate(row.key, e.target.value)} className="mt-0.5 w-full border border-slate-300 px-1.5 py-1 text-sm font-mono"/>
                  </label>
                  <button onClick={() => removeExternal(row.key)} className="text-[0.55rem] font-bold uppercase text-red-600 hover:text-red-800 self-end">DEL</button>
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex items-baseline justify-between border-t border-indigo-100 pt-2">
            <div className="text-[0.55rem] font-black uppercase tracking-[0.15em] text-slate-500">Combined monthly (catalog + external)</div>
            <div className="text-2xl font-black tracking-tight text-slate-900">${grandMonthly.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Eligible products column */}
        <div className="bg-white border border-slate-200 p-4 min-h-[300px]">
          <div className="flex items-baseline justify-between mb-3">
            <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">Eligible Products</div>
            <div className="text-xs text-slate-400">{passing.length} pass · {results.length - passing.length} fail</div>
          </div>
          {results.length === 0 ? (
            <div className="text-sm text-slate-400 italic py-8 text-center">Enter age + state to run eligibility.</div>
          ) : passing.length === 0 ? (
            <div className="text-sm text-slate-400 italic py-8 text-center">Nothing passes. Try a different state or check the H/W chart.</div>
          ) : (
            <div className="space-y-1.5">
              {passing.map(({ product: p }) => {
                const cat = cats[p.category];
                const inStack = stack.some(r => r.productId === p.id);
                return (
                  <div key={p.id} className={`flex items-center justify-between border-l-4 border-${cat?.tone || 'slate'}-500 border-2 border-r-slate-200 border-t-slate-200 border-b-slate-200 p-2`}>
                    <div className="min-w-0 mr-2">
                      <div className="font-bold text-slate-900 text-sm leading-tight">{p.name}</div>
                      <div className="text-[0.6rem] uppercase tracking-wider text-slate-500">{cat?.label || p.category} · {p.carrier}</div>
                    </div>
                    <button
                      onClick={() => addProduct(p)}
                      disabled={inStack}
                      className={`text-[0.6rem] font-black uppercase tracking-tight px-2.5 py-1.5 ${inStack ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}
                    >
                      {inStack ? '✓ In Stack' : '+ Add'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Stack column */}
        <div className="bg-white border-2 border-emerald-500 p-4 min-h-[300px]">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-emerald-700">The Stack</div>
              <div className="text-lg font-black text-slate-900 uppercase tracking-tight mt-0.5">{stack.length} product{stack.length === 1 ? '' : 's'}</div>
            </div>
            <div className="flex gap-2">
              <label className="text-[0.6rem] flex items-center gap-1">
                Default %
                <input type="number" min="0" max="100" value={defaultCommissionPct} onChange={e => setDefaultCommissionPct(Number(e.target.value) || 0)} className="w-12 border border-slate-300 px-1 py-0.5 font-mono text-sm"/>
              </label>
              {stack.length > 0 && <button onClick={clearStack} className="text-[0.6rem] font-bold uppercase tracking-wider text-red-600 hover:text-red-800">Clear</button>}
            </div>
          </div>
          {stack.length === 0 ? (
            <div className="text-sm text-slate-400 italic py-8 text-center">Add products from the left to start building.</div>
          ) : (
            <div className="space-y-2">
              {stack.map(row => {
                const p = FTPIG_PRODUCTS.find(x => x.id === row.productId);
                if (!p) return null;
                return (
                  <div key={row.key} className="border border-slate-200 p-2">
                    <div className="flex items-baseline justify-between gap-2 mb-1.5">
                      <div className="font-bold text-sm text-slate-900 truncate">{p.name}</div>
                      <button onClick={() => removeRow(row.key)} className="text-[0.55rem] font-bold uppercase text-red-600 hover:text-red-800">DEL</button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <label className="block">
                        <span className="text-[0.55rem] uppercase tracking-wider text-slate-500">Plan</span>
                        <select value={row.plan} onChange={e => updateRow(row.key, { plan: e.target.value })} className="mt-0.5 w-full border border-slate-300 px-1.5 py-1 text-sm bg-white">
                          {(p.plans || []).map(pl => <option key={pl} value={pl}>{pl}</option>)}
                        </select>
                      </label>
                      <label className="block">
                        <span className="text-[0.55rem] uppercase tracking-wider text-slate-500">$/mo</span>
                        <input type="number" min="0" step="0.01" value={row.monthly} onChange={e => updateRow(row.key, { monthly: e.target.value })} className="mt-0.5 w-full border border-slate-300 px-1.5 py-1 text-sm font-mono"/>
                      </label>
                      <label className="block">
                        <span className="text-[0.55rem] uppercase tracking-wider text-slate-500">Comm %</span>
                        <input type="number" min="0" max="100" value={row.commissionPct} onChange={e => updateRow(row.key, { commissionPct: e.target.value })} className="mt-0.5 w-full border border-slate-300 px-1.5 py-1 text-sm font-mono"/>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Totals — big numbers, Bezos style. */}
          {stack.length > 0 && (
            <div className="mt-4 border-t-2 border-emerald-200 pt-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-[0.55rem] font-black uppercase tracking-[0.15em] text-slate-500">Client / mo</div>
                  <div className="text-2xl font-black text-slate-900 tracking-tight">{fmt$(totals.monthly)}</div>
                </div>
                <div>
                  <div className="text-[0.55rem] font-black uppercase tracking-[0.15em] text-slate-500">Annualized</div>
                  <div className="text-2xl font-black text-slate-900 tracking-tight">{fmt$(totals.annual)}</div>
                </div>
                <div>
                  <div className="text-[0.55rem] font-black uppercase tracking-[0.15em] text-emerald-700">Yr 1 Comm</div>
                  <div className="text-2xl font-black text-emerald-700 tracking-tight">{fmt$(totals.commission)}</div>
                </div>
              </div>
              {/* Quote status + notes — saved with the row. */}
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-[0.55rem] uppercase tracking-wider text-slate-500">Status</span>
                  <select value={quoteStatus} onChange={e => setQuoteStatus(e.target.value)} className="mt-0.5 w-full border border-slate-300 px-1.5 py-1 text-sm bg-white">
                    <option value="draft">Draft</option>
                    <option value="sent">Sent to client</option>
                    <option value="bound">Bound ✓</option>
                    <option value="lost">Lost ✗</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-[0.55rem] uppercase tracking-wider text-slate-500">Notes</span>
                  <input value={quoteNotes} onChange={e => setQuoteNotes(e.target.value)} placeholder="Quote round 2, after objection…" className="mt-0.5 w-full border border-slate-300 px-1.5 py-1 text-sm"/>
                </label>
              </div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  onClick={saveQuote}
                  disabled={saving || !clientId}
                  className={`text-xs font-black uppercase tracking-tight py-2.5 ${
                    !clientId
                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      : 'bg-slate-900 hover:bg-black text-white'
                  }`}
                  title={!clientId ? 'Pick a client to save' : (quoteId ? 'Overwrites existing quote' : 'Creates new quote')}
                >
                  {saving ? 'Saving…' : (quoteId ? 'Update Quote' : 'Save Quote')}
                </button>
                <button
                  onClick={copyStackText}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase tracking-tight py-2.5"
                >
                  {copied ? '✓ Copied' : 'Copy to text'}
                </button>
              </div>
              {quoteId && (
                <button onClick={startNewQuote} className="mt-2 w-full text-[0.6rem] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900">
                  ↺ Start a new quote (don't overwrite)
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* HealthSherpa NPN-attributed quote link — used whenever the
          recommended stack has ACA as foundation. */}
      <HealthSherpaDeeplinkCard settings={settings} clientZip={''} clientState={form.state} showToast={showToast}/>

      {/* Compliance flags pulled from products currently in the stack. */}
      {stackFlags.length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-300 p-3 space-y-1.5">
          <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-amber-700">⚠ Required Disclosures · per product</div>
          {stackFlags.map(f => (
            <div key={f.id} className="text-xs text-amber-900">
              <strong className="uppercase tracking-wider text-[0.55rem] mr-1.5">{f.label || f.id}:</strong>{f.text || f.summary || ''}
            </div>
          ))}
        </div>
      )}

      {/* Strategy-driven compliance stamps from the recommender. */}
      {recommendation?.compliance?.length > 0 && (
        <div className="bg-rose-50 border-2 border-rose-300 p-3 space-y-1.5">
          <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-rose-700">Strategy Compliance Stamps · always disclose</div>
          {recommendation.compliance.map(f => (
            <div key={f.id} className="text-xs text-rose-900">
              <strong className="uppercase tracking-wider text-[0.55rem] mr-1.5">{f.id}:</strong>{f.text}
            </div>
          ))}
        </div>
      )}

      {/* Saved quotes for the picked client — load to rehydrate. */}
      {clientId && savedQuotes.length > 0 && (
        <div className="bg-white border border-slate-200 p-4">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">Saved Quotes</div>
              <div className="text-base font-black text-slate-900 uppercase tracking-tight mt-0.5">{savedQuotes.length} on file for this client</div>
            </div>
          </div>
          <div className="space-y-1.5">
            {savedQuotes.map(q => {
              const dt = q.updated_at ? new Date(q.updated_at).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' }) : '';
              const tone = q.status === 'bound' ? 'emerald'
                         : q.status === 'sent'  ? 'sky'
                         : q.status === 'lost'  ? 'red'
                         : 'slate';
              const itemCount = Array.isArray(q.stack) ? q.stack.length : 0;
              return (
                <div key={q.id} className={`flex items-center justify-between gap-3 border-l-4 border-${tone}-500 border-2 border-r-slate-200 border-t-slate-200 border-b-slate-200 p-2 ${quoteId === q.id ? `bg-${tone}-50/60` : ''}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm text-slate-900 truncate">
                      {q.notes || `${itemCount} product${itemCount === 1 ? '' : 's'} · ${dt}`}
                    </div>
                    <div className="text-[0.6rem] uppercase tracking-wider text-slate-500">
                      ${(Number(q.total_monthly) || 0).toFixed(0)}/mo · ${(Number(q.est_commission) || 0).toFixed(0)} Yr-1 comm · {dt}
                    </div>
                  </div>
                  <span className={`text-[0.55rem] font-black uppercase tracking-wider text-${tone}-700 bg-${tone}-50 border border-${tone}-200 px-2 py-0.5`}>{q.status}</span>
                  <button onClick={() => loadQuote(q)} className="text-[0.6rem] font-black uppercase tracking-tight bg-slate-900 hover:bg-black text-white px-2.5 py-1.5">Load</button>
                  <button onClick={() => deleteQuote(q.id)} className="text-[0.55rem] font-bold uppercase text-red-600 hover:text-red-800">DEL</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="text-[0.6rem] text-slate-400 italic px-2">Disclaimer: Manual rates entered by the agent. Verify in the broker portal before binding. Indemnity / supplemental plans are NOT MEC and not a substitute for major medical.</div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// EXPENSES — agency overhead tracker.
// ════════════════════════════════════════════════════════════════════

const EXPENSE_CATEGORIES = [
  { id: 'leads',      label: 'Leads',       tone: 'amber'   },
  { id: 'marketing',  label: 'Marketing',   tone: 'violet'  },
  { id: 'software',   label: 'Software',    tone: 'sky'     },
  { id: 'travel',     label: 'Travel',      tone: 'emerald' },
  { id: 'office',     label: 'Office',      tone: 'slate'   },
  { id: 'licensing',  label: 'Licensing',   tone: 'blue'    },
  { id: 'errors',     label: 'E&O / Bonds', tone: 'red'     },
  { id: 'other',      label: 'Other',       tone: 'slate'   },
];

function ExpensesView({ currentUser, showToast = () => {} }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [verticalFilter, setVerticalFilter] = React.useState('all'); // all | health | life | agency
  const today = new Date().toISOString().slice(0, 10);
  const [draft, setDraft] = React.useState({ date: today, category: 'leads', vendor: '', amount: '', notes: '', vertical: 'health' });

  const load = React.useCallback(async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    const { data } = await supabase
      .from('expenses')
      .select('*')
      .eq('owner_id', currentUser.id)
      .order('date', { ascending: false })
      .limit(500);
    setRows(data || []);
    setLoading(false);
  }, [currentUser?.id]);
  React.useEffect(() => { load(); }, [load]);

  async function save() {
    if (!draft.category || draft.amount === '') { showToast('Category + amount required.'); return; }
    const { data, error } = await supabase
      .from('expenses')
      .insert({
        owner_id: currentUser.id,
        date:     draft.date || today,
        category: draft.category,
        vendor:   draft.vendor || null,
        amount:   Number(draft.amount) || 0,
        notes:    draft.notes || null,
        vertical: draft.vertical || 'agency',
      })
      .select().single();
    if (error) { showToast(`Save failed: ${error.message}`); return; }
    setRows(prev => [data, ...prev]);
    setShowForm(false);
    setDraft({ date: today, category: 'leads', vendor: '', amount: '', notes: '', vertical: 'health' });
    showToast('Expense logged.');
  }
  async function del(id) {
    if (!(await hqConfirm({
      title: 'Delete this expense?',
      confirmLabel: 'Delete expense', danger: true,
    }))) return;
    await supabase.from('expenses').delete().eq('id', id);
    setRows(prev => prev.filter(r => r.id !== id));
  }

  const filtered = verticalFilter === 'all' ? rows : rows.filter(r => r.vertical === verticalFilter);

  // Monthly aggregate of the filtered list.
  const monthAggregate = React.useMemo(() => {
    const now = new Date();
    const yMo = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
    const thisMonth = filtered.filter(r => (r.date || '').slice(0, 7) === yMo);
    const total = thisMonth.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const byCat = thisMonth.reduce((acc, r) => { acc[r.category] = (acc[r.category] || 0) + (Number(r.amount) || 0); return acc; }, {});
    return { total, byCat, count: thisMonth.length, label: yMo };
  }, [filtered]);

  const fmt$ = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-4">
      <div className="bg-black border-l-4 border-emerald-500 px-6 py-5 shadow-2xl flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-emerald-400 text-[0.6rem] font-black uppercase tracking-[0.3em]">Expenses</div>
          <div className="text-white text-xl md:text-2xl font-black mt-1.5 uppercase tracking-tight">Agency Overhead Tracker</div>
          <div className="text-slate-400 text-xs mt-1.5">{rows.length} total · {monthAggregate.count} this month</div>
        </div>
        <button onClick={() => setShowForm(s => !s)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase tracking-tight px-4 py-2">+ Log expense</button>
      </div>

      {/* Month tile + category breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="bg-white border-2 border-emerald-200 border-l-4 border-l-emerald-500 p-4 md:col-span-1">
          <div className="text-[0.55rem] font-black uppercase tracking-[0.18em] text-slate-500">{monthAggregate.label} · This month</div>
          <div className="text-3xl md:text-4xl font-black tracking-tight text-emerald-700 mt-1">{fmt$(monthAggregate.total)}</div>
          <div className="text-[0.6rem] uppercase tracking-wider text-slate-400 mt-0.5">across {monthAggregate.count} entries</div>
        </div>
        <div className="md:col-span-3 bg-white border border-slate-200 p-4">
          <div className="text-[0.6rem] font-black uppercase tracking-[0.18em] text-slate-500 mb-2">By Category · {monthAggregate.label}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {EXPENSE_CATEGORIES.map(c => {
              const amt = monthAggregate.byCat[c.id] || 0;
              if (amt === 0) return null;
              return (
                <div key={c.id} className={`border-l-4 border-${c.tone}-500 border-2 border-r-slate-200 border-t-slate-200 border-b-slate-200 p-2`}>
                  <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">{c.label}</div>
                  <div className={`text-lg font-black text-${c.tone}-800`}>{fmt$(amt)}</div>
                </div>
              );
            })}
            {Object.keys(monthAggregate.byCat).length === 0 && <div className="text-sm text-slate-400 italic md:col-span-4 py-3 text-center">No expenses this month yet.</div>}
          </div>
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex items-center gap-2 flex-wrap">
        {['all', 'health', 'life', 'agency'].map(v => (
          <button key={v} onClick={() => setVerticalFilter(v)}
            className={`text-xs font-bold uppercase tracking-tight px-3 py-1.5 ${verticalFilter === v ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
            {v === 'all' ? 'All' : v === 'health' ? 'Health' : v === 'life' ? 'Life' : 'Agency'}
          </button>
        ))}
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white border-2 border-emerald-500 p-4">
          <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-emerald-700 mb-2">New Expense</div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-2 text-sm">
            <label className="block"><span className="text-xs text-slate-500">Date</span><input type="date" value={draft.date} onChange={e => setDraft(d => ({ ...d, date: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
            <label className="block">
              <span className="text-xs text-slate-500">Category</span>
              <select value={draft.category} onChange={e => setDraft(d => ({ ...d, category: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 bg-white">
                {EXPENSE_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </label>
            <label className="block md:col-span-2"><span className="text-xs text-slate-500">Vendor</span><input value={draft.vendor} onChange={e => setDraft(d => ({ ...d, vendor: e.target.value }))} placeholder="Facebook Ads / GoDaddy / etc." className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
            <label className="block"><span className="text-xs text-slate-500">Amount</span><input type="number" step="0.01" min="0" value={draft.amount} onChange={e => setDraft(d => ({ ...d, amount: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 font-mono"/></label>
            <label className="block">
              <span className="text-xs text-slate-500">Vertical</span>
              <select value={draft.vertical} onChange={e => setDraft(d => ({ ...d, vertical: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 bg-white">
                <option value="health">Health</option>
                <option value="life">Life</option>
                <option value="agency">Agency</option>
              </select>
            </label>
            <label className="block md:col-span-6"><span className="text-xs text-slate-500">Notes</span><input value={draft.notes} onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => setShowForm(false)} className="text-xs font-bold uppercase text-slate-500 hover:text-slate-900">Cancel</button>
            <button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase tracking-tight px-4 py-2">Save</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {['Date', 'Category', 'Vendor', 'Vertical', 'Amount', ''].map(h => (
                <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No expenses logged yet.</td></tr>
            ) : filtered.map(r => {
              const cat = EXPENSE_CATEGORIES.find(c => c.id === r.category) || { label: r.category, tone: 'slate' };
              return (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 text-xs font-mono">{r.date}</td>
                  <td className="px-3 py-2 text-xs"><span className={`inline-block text-[0.55rem] font-black uppercase tracking-wider bg-${cat.tone}-100 text-${cat.tone}-800 px-1.5 py-0.5`}>{cat.label}</span></td>
                  <td className="px-3 py-2 text-xs">{r.vendor || <span className="text-slate-400">—</span>}</td>
                  <td className="px-3 py-2 text-xs">{r.vertical}</td>
                  <td className="px-3 py-2 text-sm font-mono font-bold text-slate-900">{fmt$(r.amount)}</td>
                  <td className="px-3 py-2 text-xs"><button onClick={() => del(r.id)} className="text-[0.55rem] font-bold uppercase text-red-600 hover:text-red-800">DEL</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// DAILY TRACKER — dials, contacts, appts, applications, hours.
// One upsertable row per (owner, date, vertical) so the agent ticks
// their day's activity without creating duplicate rows.
// ════════════════════════════════════════════════════════════════════

function DailyTrackerView({ currentUser, showToast = () => {} }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const today = new Date().toISOString().slice(0, 10);
  const [vertical, setVertical] = React.useState('health');
  const [draft, setDraft] = React.useState({ date: today, dials: 0, contacts: 0, appts_set: 0, apps_taken: 0, hours_worked: 0, notes: '' });

  const load = React.useCallback(async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    const { data } = await supabase
      .from('daily_activity')
      .select('*')
      .eq('owner_id', currentUser.id)
      .eq('vertical', vertical)
      .order('date', { ascending: false })
      .limit(90);
    setRows(data || []);
    // Hydrate draft from today's row if it exists.
    const t = (data || []).find(r => r.date === today);
    if (t) setDraft({ date: t.date, dials: t.dials, contacts: t.contacts, appts_set: t.appts_set, apps_taken: t.apps_taken, hours_worked: t.hours_worked, notes: t.notes || '' });
    setLoading(false);
  }, [currentUser?.id, vertical, today]);
  React.useEffect(() => { load(); }, [load]);

  async function saveToday() {
    const row = {
      owner_id: currentUser.id,
      date: draft.date || today,
      vertical,
      dials: Number(draft.dials) || 0,
      contacts: Number(draft.contacts) || 0,
      appts_set: Number(draft.appts_set) || 0,
      apps_taken: Number(draft.apps_taken) || 0,
      hours_worked: Number(draft.hours_worked) || 0,
      notes: draft.notes || null,
    };
    // Upsert on the unique (owner_id, date, vertical) index.
    const { data, error } = await supabase
      .from('daily_activity')
      .upsert(row, { onConflict: 'owner_id,date,vertical' })
      .select().single();
    if (error) { showToast(`Save failed: ${error.message}`); return; }
    setRows(prev => {
      const without = prev.filter(r => r.date !== row.date);
      return [data, ...without].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    });
    showToast(`${row.date} logged.`);
  }

  // Last-7-days aggregate.
  const week = React.useMemo(() => {
    const today0 = new Date(today + 'T00:00:00').getTime();
    const cutoff = today0 - 6 * 86400000;
    const recent = rows.filter(r => {
      const t = new Date(r.date + 'T00:00:00').getTime();
      return t >= cutoff;
    });
    return recent.reduce((acc, r) => {
      acc.dials       += Number(r.dials)        || 0;
      acc.contacts    += Number(r.contacts)     || 0;
      acc.appts_set   += Number(r.appts_set)    || 0;
      acc.apps_taken  += Number(r.apps_taken)   || 0;
      acc.hours_worked+= Number(r.hours_worked) || 0;
      return acc;
    }, { dials: 0, contacts: 0, appts_set: 0, apps_taken: 0, hours_worked: 0 });
  }, [rows, today]);

  const contactRate = week.dials > 0 ? Math.round((week.contacts / week.dials) * 100) : 0;
  const setRate     = week.contacts > 0 ? Math.round((week.appts_set / week.contacts) * 100) : 0;

  return (
    <div className="space-y-4">
      <div className="bg-black border-l-4 border-sky-500 px-6 py-5 shadow-2xl">
        <div className="text-sky-400 text-[0.6rem] font-black uppercase tracking-[0.3em]">Daily Tracker</div>
        <div className="text-white text-xl md:text-2xl font-black mt-1.5 uppercase tracking-tight">Activity · Conversion Rates</div>
        <div className="text-slate-400 text-xs mt-1.5">One row per day per vertical. Updates upsert — re-saving today overwrites today's row.</div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {['health', 'life', 'agency'].map(v => (
          <button key={v} onClick={() => setVertical(v)}
            className={`text-xs font-bold uppercase tracking-tight px-3 py-1.5 ${vertical === v ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
            {v === 'health' ? 'Health' : v === 'life' ? 'Life' : 'Agency'}
          </button>
        ))}
      </div>

      {/* 7-day KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Dials"        value={week.dials}        sub="last 7 days" tone="slate"/>
        <Kpi label="Contacts"     value={week.contacts}     sub={`${contactRate}% contact rate`} tone="sky"/>
        <Kpi label="Appts Set"    value={week.appts_set}    sub={`${setRate}% set rate`}        tone="emerald"/>
        <Kpi label="Apps Taken"   value={week.apps_taken}   sub="written"     tone="violet"/>
        <Kpi label="Hours Worked" value={week.hours_worked.toFixed(1)} sub="last 7 days" tone="amber"/>
      </div>

      {/* Today form */}
      <div className="bg-white border-2 border-sky-500 p-4">
        <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-sky-700 mb-3">Log activity</div>
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3 text-sm">
          <label className="block"><span className="text-xs text-slate-500">Date</span><input type="date" value={draft.date} onChange={e => setDraft(d => ({ ...d, date: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
          <label className="block"><span className="text-xs text-slate-500">Dials</span><input type="number" min="0" value={draft.dials} onChange={e => setDraft(d => ({ ...d, dials: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 font-mono"/></label>
          <label className="block"><span className="text-xs text-slate-500">Contacts</span><input type="number" min="0" value={draft.contacts} onChange={e => setDraft(d => ({ ...d, contacts: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 font-mono"/></label>
          <label className="block"><span className="text-xs text-slate-500">Appts set</span><input type="number" min="0" value={draft.appts_set} onChange={e => setDraft(d => ({ ...d, appts_set: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 font-mono"/></label>
          <label className="block"><span className="text-xs text-slate-500">Apps taken</span><input type="number" min="0" value={draft.apps_taken} onChange={e => setDraft(d => ({ ...d, apps_taken: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 font-mono"/></label>
          <label className="block"><span className="text-xs text-slate-500">Hours</span><input type="number" min="0" step="0.25" value={draft.hours_worked} onChange={e => setDraft(d => ({ ...d, hours_worked: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 font-mono"/></label>
          <button onClick={saveToday} className="self-end bg-sky-600 hover:bg-sky-700 text-white text-xs font-black uppercase tracking-tight px-3 py-1.5 mt-5">Save</button>
        </div>
        <label className="block mt-3 text-sm"><span className="text-xs text-slate-500">Notes (optional)</span><input value={draft.notes} onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))} placeholder="What worked, what didn't, callbacks tomorrow…" className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
      </div>

      {/* History */}
      <div className="bg-white border border-slate-200 overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {['Date', 'Dials', 'Contacts', 'Appts', 'Apps', 'Hours', 'Notes'].map(h => (
                <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">No activity logged yet. Use the form above to start.</td></tr>
            ) : rows.map(r => (
              <tr key={r.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 text-xs font-mono">{r.date}</td>
                <td className="px-3 py-2 text-xs font-mono">{r.dials}</td>
                <td className="px-3 py-2 text-xs font-mono">{r.contacts}</td>
                <td className="px-3 py-2 text-xs font-mono">{r.appts_set}</td>
                <td className="px-3 py-2 text-xs font-mono">{r.apps_taken}</td>
                <td className="px-3 py-2 text-xs font-mono">{Number(r.hours_worked).toFixed(1)}</td>
                <td className="px-3 py-2 text-xs text-slate-500 truncate max-w-[300px]">{r.notes || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// HEALTHSHERPA DEEPLINK CARD — Phase A: NPN-attributed redirect.
// Builds the URL with the agent's NPN attached so the lead lands in
// the HealthSherpa quoter with the agent already credited. Phase B
// will swap this for the Quote API + webhook persistence.
// ════════════════════════════════════════════════════════════════════

function HealthSherpaDeeplinkCard({ settings = {}, clientZip = '', clientState = '', showToast = () => {} }) {
  const [copied, setCopied] = React.useState(false);
  const npn = (settings?.ownerNpn || '').trim();
  // HealthSherpa supports several attribution params; consumer-direct
  // landing accepts ?agent_npn= and optional zipcode= prefill.
  const params = new URLSearchParams();
  if (npn)         params.set('agent_npn', npn);
  if (clientZip)   params.set('zipcode',   clientZip);
  if (clientState) params.set('state',     clientState);
  const baseUrl = 'https://www.healthsherpa.com/?source=ftpig-crm';
  const url = `${baseUrl}${params.toString() ? '&' + params.toString() : ''}`;

  function copy() {
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      showToast('HealthSherpa link copied.');
    });
  }

  return (
    <div className="bg-white border-2 border-cyan-400 p-4">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
        <div>
          <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-cyan-700">HealthSherpa · Phase A</div>
          <div className="text-base font-black text-slate-900 uppercase tracking-tight mt-0.5">NPN-Attributed Quote Link</div>
        </div>
        {!npn && <span className="text-[0.6rem] font-black uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-300 px-2 py-1">⚠ Set NPN in Settings first</span>}
      </div>
      <div className="text-xs text-slate-600">Send this link to the client. They quote ACA Marketplace plans in HealthSherpa with your NPN attached — you get credit.{clientZip ? ` ZIP ${clientZip} pre-filled.` : ' Add a ZIP to pre-fill the quoter.'}</div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <code className="flex-1 min-w-[200px] text-xs font-mono bg-slate-50 border border-slate-200 px-2 py-1.5 truncate">{url}</code>
        <button onClick={copy} disabled={!npn} className={`text-xs font-black uppercase tracking-tight px-3 py-1.5 ${npn ? 'bg-cyan-600 hover:bg-cyan-700 text-white' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}>
          {copied ? '✓ Copied' : 'Copy'}
        </button>
        <a href={url} target="_blank" rel="noopener noreferrer" className="bg-slate-900 hover:bg-black text-white text-xs font-black uppercase tracking-tight px-3 py-1.5">Open ↗</a>
      </div>
      <div className="text-[0.55rem] uppercase tracking-wider text-slate-400 mt-2">Phase B: Quote API + webhook persistence → quotes auto-write back to <code>cross_sell_quotes</code>.</div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// HEALTH PORTAL — the health agent's dedicated workspace.
//
// Sidebar of every tool a health/Medicare/ACA agent reaches for daily.
// Right pane shows KPI tiles + pipeline funnel + quick actions, scoped
// to the agent's HEALTH-vertical book only (filtered by lead.vertical
// and clients.vertical so a dual-vertical agent never sees their life
// book leak in here).
//
// Sidebar items either:
//   (a) jump into an existing tab (Calendar, MyPay, Policies, etc.)
//   (b) jump into a health-filtered wrapper (HealthClientsView)
//   (c) land on a "Phase B" placeholder where the standalone view is
//       still under construction (Expenses, Daily Tracker, Agents+VAs)
// ════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════
// HEALTH SHELL — persistent sidebar that wraps every Health sub-tab.
//
// The reference design (For The People CRM) puts the sidebar at the
// app shell level so the same nav rails the agent sees on Dashboard
// is also there on Policies, Eligibility, and Products. We do the
// same here. The shell is the lone owner of:
//   · PIPELINE (lead status filters with live counts)
//   · QUICK ACTIONS (one-click jumps the agent uses every hour)
//   · REVENUE & RISK (jump to the money / chargeback / lost-client rails)
//
// All four primary Health tabs (Dashboard / Policies / Eligibility /
// Products) render inside this shell — the right pane swaps, the rails
// stay put. Active tab is highlighted from the `activeTab` prop.
//
// Badge counts come from the same `leads` array we already keep in
// state, plus a lightweight one-time fetch for the active health
// policy count. No new tables / migrations.
// ════════════════════════════════════════════════════════════════════
// HealthShell — pass-through wrapper. The teal-accented 260px sidebar
// (Pipeline / Run the day / Protect the book / Grow the book / More…)
// was retired 2026-07-12 to fix a triple-sidebar collision: the new
// permanent Cockpit sidebar (248px, fixed) + classic root paddingLeft +
// HealthShell's own 260px column = 508px of nav chrome eating the
// viewport. Every surface HealthShell used to expose is reachable from
// the Cockpit sidebar or the classic top nav; Medicare-only tabs
// (Digital Twin, Signature Health, SEP Tracker) stay reachable via the
// classic top-nav's Health group until Phase 2 rehomes them.
// HealthShell — pure pass-through. The dark-theme skin (`a4c0f7c`) that
// force-inverted every Tailwind slate/white class to near-black was
// backed out here because the new master aesthetic prompt calls for
// a LIGHT workspace with white cards on #f4f7fb. Injecting that CSS
// would fight the intent. Health surfaces render on the standard light
// workspace like every other Cockpit surface now.
function HealthShell({ children }) {
  return <div className="space-y-3 min-w-0">{children}</div>;
}


function HealthPortalView({ currentUser, settings = {}, onGotoTab = () => {}, leads = [], showToast = () => {} }) {
  const [healthClients, setHealthClients]   = React.useState([]);
  const [healthPolicies, setHealthPolicies] = React.useState([]);
  const [certs, setCerts]                   = React.useState([]);
  const [soas, setSoas]                     = React.useState([]);
  const [activity, setActivity]             = React.useState([]);
  const [lossEvents, setLossEvents]         = React.useState([]); // every loss, ack'd or not
  const [loading, setLoading]               = React.useState(true);
  // Segmented tab control — cuts scroll length by hiding the four
  // heavyweight analytics sections behind one-at-a-time tabs. Persists
  // across renders so the operator returns to their last view.
  const [dashTab, setDashTab]               = React.useState(() => {
    try { return localStorage.getItem('hp:dashTab') || 'overview'; } catch { return 'overview'; }
  });
  React.useEffect(() => { try { localStorage.setItem('hp:dashTab', dashTab); } catch {} }, [dashTab]);

  // ── Data fetch — single batched round-trip ─────────────────────
  React.useEffect(() => {
    if (!currentUser?.id) {
      // Without a user we have nothing to load. Don't leave the
      // "Loading book…" indicator on forever — flip out of loading
      // immediately so the dashboard reads as "empty" instead of
      // "still loading". (Bug report: stuck spinner on signed-out
      // and brand-new accounts.)
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    const since28  = new Date(Date.now() - 28  * 86400000).toISOString().slice(0, 10);
    const since180 = new Date(Date.now() - 180 * 86400000).toISOString();
    Promise.all([
      supabase.from('clients')
        .select('id, name, sector, permission_to_contact, opt_in_sms')
        .eq('vertical', 'health'),
      supabase.from('health_policies')
        .select('id, client_id, client_name, status, monthly_premium, monthly_commission, renewal_date, effective_date, plan_type, plan_name, last_commission_at, termination_date, created_at'),
      supabase.from('agent_certifications')
        .select('id, cert_type, cert_plan_year, expires_at')
        .eq('owner_id', currentUser.id),
      supabase.from('soa_records')
        .select('id, client_id, beneficiary_name, signed_at, agent_signed_at, status, client_signature_ip, products, token')
        .order('created_at', { ascending: false })
        .limit(500),
      supabase.from('daily_activity')
        .select('date, dials, contacts, appts_set, apps_taken, hours_worked')
        .eq('owner_id', currentUser.id)
        .eq('vertical', 'health')
        .gte('date', since28),
      supabase.from('health_loss_events')
        .select('*')
        .eq('owner_id', currentUser.id)
        .gte('lost_at', since180)
        .order('lost_at', { ascending: false }),
    ]).then(([c, p, ce, s, a, l]) => {
      if (!alive) return;
      setHealthClients(c.data || []);
      setHealthPolicies(p.data || []);
      setCerts(ce.data || []);
      setSoas(s.data || []);
      setActivity(a.data || []);
      setLossEvents(l.data || []);
      setLoading(false);
    }).catch(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [currentUser?.id]);

  // ── Payment-stopped detector: implicit loss path. Scan active
  // policies for last_commission_at older than 45 days; insert a
  // loss event if not already logged. Runs once per mount.
  React.useEffect(() => {
    if (!currentUser?.id || healthPolicies.length === 0) return;
    const cutoff = Date.now() - 45 * 86400000;
    const candidates = healthPolicies.filter(p => {
      if (p.status !== 'active') return false;
      if (!p.last_commission_at) return false;
      const lc = new Date(p.last_commission_at + 'T00:00:00').getTime();
      return Number.isFinite(lc) && lc < cutoff;
    });
    if (candidates.length === 0) return;
    // Dedupe against events we already know about for the same policy + 'payment_stopped' on the same lost-at date.
    const existing = new Set(
      lossEvents
        .filter(e => e.reason === 'payment_stopped' && e.policy_id)
        .map(e => `${e.policy_id}|${String(e.lost_at).slice(0, 10)}`)
    );
    const toInsert = candidates
      .map(p => {
        const lostIso = new Date(new Date(p.last_commission_at).getTime() + 45 * 86400000).toISOString();
        const key = `${p.id}|${lostIso.slice(0, 10)}`;
        if (existing.has(key)) return null;
        return {
          owner_id: currentUser.id,
          policy_id: p.id,
          client_id: p.client_id || null,
          client_name: p.client_name || null,
          plan_name: p.plan_name || null,
          plan_type: p.plan_type || null,
          monthly_commission: Number(p.monthly_commission) || 0,
          lost_at: lostIso,
          reason: 'payment_stopped',
        };
      })
      .filter(Boolean);
    if (toInsert.length === 0) return;
    supabase.from('health_loss_events').insert(toInsert).select().then(({ data, error }) => {
      if (error) return;
      if ((data || []).length > 0) {
        setLossEvents(prev => [...data, ...prev]);
        showToast(`⚠ ${data.length} client${data.length === 1 ? '' : 's'} flagged — commission stopped.`);
      }
    });
    // eslint-disable-next-line
  }, [currentUser?.id, healthPolicies]);

  async function acknowledgeLoss(id) {
    const { error } = await supabase.from('health_loss_events')
      .update({ acknowledged_at: new Date().toISOString(), acknowledged_by: currentUser.id })
      .eq('id', id);
    if (error) { showToast(`Failed: ${error.message}`); return; }
    setLossEvents(prev => prev.map(e => e.id === id ? { ...e, acknowledged_at: new Date().toISOString() } : e));
  }
  async function acknowledgeAllLosses() {
    const ids = lossEvents.filter(e => !e.acknowledged_at).map(e => e.id);
    if (ids.length === 0) return;
    const now = new Date().toISOString();
    const { error } = await supabase.from('health_loss_events')
      .update({ acknowledged_at: now, acknowledged_by: currentUser.id })
      .in('id', ids);
    if (error) { showToast(`Failed: ${error.message}`); return; }
    setLossEvents(prev => prev.map(e => ids.includes(e.id) ? { ...e, acknowledged_at: now } : e));
    showToast(`Acknowledged ${ids.length} loss${ids.length === 1 ? '' : 'es'}.`);
  }
  const unackLosses = React.useMemo(() => lossEvents.filter(e => !e.acknowledged_at), [lossEvents]);
  const recentAckLosses = React.useMemo(() => {
    const cutoff = Date.now() - 30 * 86400000;
    return lossEvents.filter(e => e.acknowledged_at && new Date(e.lost_at).getTime() >= cutoff).slice(0, 5);
  }, [lossEvents]);
  const lostMrr = React.useMemo(() =>
    unackLosses.reduce((s, e) => s + (Number(e.monthly_commission) || 0), 0),
    [unackLosses]);

  // Filter pipeline to health-vertical leads only.
  const healthLeads = React.useMemo(
    () => (leads || []).filter(l => (l.vertical || 'life') !== 'life'),
    [leads]
  );

  // ── Derived state ──────────────────────────────────────────────
  const todayDate  = new Date();
  const todayISO   = todayDate.toISOString().slice(0, 10);
  const dateLabel  = todayDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  // Today's appointments, sorted chronologically.
  const todaysAppts = React.useMemo(() => {
    return healthLeads
      .filter(l => l.appointmentDate === todayISO)
      .slice()
      .sort((a, b) => (a.appointmentTime || '').localeCompare(b.appointmentTime || ''));
  }, [healthLeads, todayISO]);

  // Next-action card: imminent appointment beats hottest lead.
  const nextAction = React.useMemo(() => {
    const now = Date.now();
    const imminent = todaysAppts.find(l => {
      const dt = new Date(`${l.appointmentDate}T${l.appointmentTime || '09:00'}:00`).getTime();
      return dt >= now - 30 * 60000;
    });
    if (imminent) {
      const dt = new Date(`${imminent.appointmentDate}T${imminent.appointmentTime || '09:00'}:00`).getTime();
      const minutes = Math.round((dt - now) / 60000);
      const label = minutes >= 60 ? `Appt in ${Math.floor(minutes / 60)}h ${minutes % 60}m`
                  : minutes >= 0  ? `Appt in ${minutes} min`
                  : `${Math.abs(minutes)} min late`;
      return { type: 'appt', lead: imminent, label, urgent: minutes < 120 };
    }
    const eligible = healthLeads
      .filter(l => l.status !== 'closed' && l.status !== 'not_interested' && !l.optOut)
      .sort((a, b) => calcLeadScore(b) - calcLeadScore(a));
    if (eligible.length > 0) {
      return { type: 'hot', lead: eligible[0], label: 'Hottest lead in your pipeline', urgent: false };
    }
    return null;
  }, [todaysAppts, healthLeads]);

  // Funnel — last 30 days.
  const funnel30 = React.useMemo(() => {
    const cutoff = Date.now() - 30 * 86400000;
    const recent = healthLeads.filter(l => l.createdAt && new Date(l.createdAt).getTime() >= cutoff);
    let all = 0, contacted = 0, appt = 0, showed = 0, closed = 0;
    recent.forEach(l => {
      all++;
      if (l.firstContactedAt) contacted++;
      if (['appt_set', 'interested', 'closed'].includes(l.status)) appt++;
      if (['interested', 'closed'].includes(l.status)) showed++;
      if (l.status === 'closed') closed++;
    });
    return { all, contacted, appt, showed, closed };
  }, [healthLeads]);

  // Activity vs weekly goals.
  const weeklyGoals = settings?.healthWeeklyGoals || { dials: 60, contacts: 25, appts: 8, apps: 5, hours: 40 };
  const weekActivity = React.useMemo(() => {
    const cutoff = Date.now() - 7 * 86400000;
    return activity
      .filter(a => new Date(a.date + 'T00:00:00').getTime() >= cutoff)
      .reduce((acc, a) => {
        acc.dials    += a.dials       || 0;
        acc.contacts += a.contacts    || 0;
        acc.appts    += a.appts_set   || 0;
        acc.apps     += a.apps_taken  || 0;
        acc.hours    += Number(a.hours_worked) || 0;
        return acc;
      }, { dials: 0, contacts: 0, appts: 0, apps: 0, hours: 0 });
  }, [activity]);

  // Revenue: current MRR + 8-week trend built from policy effective dates.
  const revenueTrend = React.useMemo(() => {
    const active = healthPolicies.filter(p => p.status === 'active');
    const current = active.reduce((s, p) => s + (Number(p.monthly_commission) || 0), 0);
    const cancelled = healthPolicies.filter(p => p.status === 'cancelled' || p.status === 'lapsed').length;
    const series = Array(8).fill(0);
    const now = Date.now();
    active.forEach(p => {
      const eff = new Date(p.effective_date || p.created_at).getTime();
      if (Number.isNaN(eff)) return;
      const weeksAgo = Math.floor((now - eff) / (7 * 86400000));
      const startIdx = Math.max(0, 8 - 1 - weeksAgo);
      const mo = Number(p.monthly_commission) || 0;
      for (let i = startIdx; i < 8; i++) series[i] += mo;
    });
    const delta = series[7] - series[0];
    return { current, series, delta, cancelled };
  }, [healthPolicies]);

  // Book composition — the four health-critical breakdowns the Life
  // dashboard never needed: members by plan family, top-revenue carriers,
  // 90-day disenrollment rate, and AEP year-over-year.
  // Powered entirely by what we already load (healthPolicies + lossEvents)
  // so it stays free in terms of DB roundtrips.
  const bookComposition = React.useMemo(() => {
    const active = healthPolicies.filter(p => p.status === 'active');

    const PLAN_GROUPS = {
      Medicare:  ['medicare_advantage', 'medicare_supplement', 'medicare_pdp'],
      ACA:       ['aca_marketplace', 'private_ppo', 'short_term'],
      Ancillary: ['dental_vision', 'hospital_indemnity', 'other'],
    };
    const byGroup = {};
    Object.entries(PLAN_GROUPS).forEach(([label, types]) => {
      const slice = active.filter(p => types.includes(p.plan_type));
      byGroup[label] = {
        count: slice.length,
        mrr: slice.reduce((s, p) => s + (Number(p.monthly_commission) || 0), 0),
      };
    });

    const byCarrier = {};
    active.forEach(p => {
      // Was '(unknown)' — user bug report 2026-06-28: rendered as
      // '(unkno...' in the narrow CARRIER · TOP 5 BY MRR card.
      // 'Other' fits without truncation and reads as a real bucket.
      const c = p.carrier || 'Other';
      if (!byCarrier[c]) byCarrier[c] = { count: 0, mrr: 0 };
      byCarrier[c].count++;
      byCarrier[c].mrr += Number(p.monthly_commission) || 0;
    });
    const topCarriers = Object.entries(byCarrier)
      .sort((a, b) => b[1].mrr - a[1].mrr)
      .slice(0, 5)
      .map(([name, d]) => ({ name, ...d }));

    const cutoff = Date.now() - 90 * 86400000;
    const recentLosses = lossEvents.filter(e => new Date(e.lost_at).getTime() >= cutoff).length;
    const denom = active.length + recentLosses;
    const disenrollmentRate = denom > 0 ? (recentLosses / denom) * 100 : 0;

    // AEP window: Oct 15 – Dec 7 each year. MA only.
    const year = todayDate.getFullYear();
    const inAep = (iso, y) => {
      if (!iso) return false;
      const d = new Date(iso);
      if (d.getFullYear() !== y) return false;
      if (d.getMonth() === 9  && d.getDate() >= 15) return true;
      if (d.getMonth() === 10) return true;
      if (d.getMonth() === 11 && d.getDate() <= 7)  return true;
      return false;
    };
    const maPolicies = healthPolicies.filter(p => p.plan_type === 'medicare_advantage');
    const aepThis = maPolicies.filter(p => inAep(p.effective_date, year)).length;
    const aepLast = maPolicies.filter(p => inAep(p.effective_date, year - 1)).length;
    const aepDelta = aepLast > 0 ? Math.round(((aepThis - aepLast) / aepLast) * 100) : null;

    return { byGroup, topCarriers, recentLosses, disenrollmentRate,
             totalActive: active.length, aepThis, aepLast, aepDelta };
  }, [healthPolicies, lossEvents, todayDate]);

  // Renewals in next 30 / 60 / 90 days.
  const renewals = React.useMemo(() => {
    const out = { d30: { count: 0, mrr: 0 }, d60: { count: 0, mrr: 0 }, d90: { count: 0, mrr: 0 } };
    healthPolicies.forEach(p => {
      if (!p.renewal_date || p.status !== 'active') return;
      const days = Math.ceil((new Date(p.renewal_date).getTime() - Date.now()) / 86400000);
      const mo = Number(p.monthly_commission) || 0;
      if (days < 0)        return;
      else if (days <= 30) { out.d30.count++; out.d30.mrr += mo; }
      else if (days <= 60) { out.d60.count++; out.d60.mrr += mo; }
      else if (days <= 90) { out.d90.count++; out.d90.mrr += mo; }
    });
    return out;
  }, [healthPolicies]);

  // Hot leads — top 5.
  const hotLeads = React.useMemo(() => {
    return healthLeads
      .filter(l => l.status !== 'closed' && l.status !== 'not_interested' && !l.optOut)
      .sort((a, b) => calcLeadScore(b) - calcLeadScore(a))
      .slice(0, 5);
  }, [healthLeads]);

  // Compliance items.
  const compliance = React.useMemo(() => {
    const items = [];
    const now = Date.now();
    certs.forEach(c => {
      const days = Math.ceil((new Date(c.expires_at).getTime() - now) / 86400000);
      const lbl = `${c.cert_type}${c.cert_plan_year ? ` PY${c.cert_plan_year}` : ''}`;
      if (days < 0)       items.push({ severity: 'red',   text: `${lbl} EXPIRED ${Math.abs(days)}d ago`, tab: 'settings' });
      else if (days < 30) items.push({ severity: 'amber', text: `${lbl} expires in ${days}d`,            tab: 'settings' });
    });
    const soaClientIds = new Set(soas.map(s => s.client_id).filter(Boolean));
    const bookedMedicare = healthLeads.filter(l => l.status === 'appt_set' && l.sector === 'medicare');
    const missingSoa = bookedMedicare.length - bookedMedicare.filter(l => soaClientIds.has(l.convertedClientId)).length;
    if (missingSoa > 0) items.push({ severity: 'red', text: `${missingSoa} booked Medicare appt${missingSoa === 1 ? '' : 's'} missing SOA`, tab: 'soa' });
    const noPtc = healthClients.filter(c => !c.permission_to_contact).length;
    if (noPtc > 0) items.push({ severity: 'amber', text: `${noPtc} client${noPtc === 1 ? '' : 's'} without PTC opt-in`, tab: 'health_clients' });
    const noSms = healthClients.filter(c => !c.opt_in_sms).length;
    if (noSms > 0 && noSms !== noPtc) items.push({ severity: 'slate', text: `${noSms} client${noSms === 1 ? '' : 's'} not opted in to SMS`, tab: 'health_clients' });
    return items;
  }, [certs, soas, healthClients, healthLeads]);

  // AEP/OEP/ACA OEP countdown.
  const enrollWindow = React.useMemo(() => activeOrNextWindow(todayDate), [todayDate]);

  // Pipeline funnel summary used by sidebar badges + pipeline pills.
  const byStage = React.useMemo(() => {
    return healthLeads.reduce((acc, l) => {
      acc[l.status || 'new'] = (acc[l.status || 'new'] || 0) + 1;
      return acc;
    }, {});
  }, [healthLeads]);
  const followUpCount = React.useMemo(() => {
    const today0 = Date.now() - 86400000;
    return healthLeads.filter(l => l.followUpDate && new Date(l.followUpDate).getTime() >= today0).length;
  }, [healthLeads]);

  // Sidebar nav with live badge counts.
  const activeLeadCount = healthLeads.filter(l => l.status !== 'closed' && l.status !== 'not_interested').length;
  const sidebarBadge = (id) => {
    switch (id) {
      case 'calendar':       return todaysAppts.length;
      case 'health_clients': return healthClients.length;
      case 'health_leads':   return activeLeadCount;
      case 'health_inbox':   return activeLeadCount; // count of threads visible (same scope)
      case 'health_policies':return healthPolicies.filter(p => p.status === 'active').length;
      case 'soa':            return compliance.filter(c => c.text.includes('SOA')).reduce((s, c) => s + 1, 0);
      case 'retention':      return followUpCount;
      case 'chargebacks':    return revenueTrend.cancelled;
      case 'lost_clients':   return unackLosses.length;
      default: return null;
    }
  };
  const SIDEBAR = [
    { section: 'Workspace', items: [
      { id: 'health_portal',     label: 'Dashboard',        tab: 'health_portal'   },
      { id: 'health_calendar',   label: 'Calendar',         tab: 'health_calendar' },
      { id: 'daily_tracker',     label: 'Daily Tracker',    tab: 'daily_tracker'   },
      { id: 'health_clients',    label: 'Clients',          tab: 'health_clients'  },
      { id: 'health_leads',      label: 'Pipeline',         tab: 'health_leads'    },
      { id: 'health_enrollment', label: 'Enrollment',       tab: 'health_enrollment'},
      { id: 'health_inbox',      label: 'Inbox',            tab: 'health_inbox'    },
      { id: 'health_lifecycle',  label: 'SMS Lifecycle',    tab: 'health_lifecycle'},
    ]},
    { section: 'Production', items: [
      { id: 'health_policies',   label: 'Policies',         tab: 'health_policies' },
      { id: 'cross_sell',        label: 'Cross-Sell',       tab: 'cross_sell'      },
      { id: 'coverage_summary',  label: 'Coverage PDF',     tab: 'coverage_summary'},
      { id: 'soa',               label: 'SOA',              tab: 'soa'             },
      { id: 'health_eligibility',label: 'Eligibility',      tab: 'health_eligibility' },
      { id: 'health_plan_intel', label: 'Plan Intelligence',tab: 'health_plan_intel'},
      { id: 'aca_plan_intelligence', label: 'ACA Plan Intelligence', tab: 'aca_plan_intelligence' },
    ]},
    { section: 'Revenue & Risk', items: [
      // Health-vertical only — Life commissions / vault / retention /
      // persistency live under the Life Insurance workspace and pull
      // from transactions; nothing here touches that surface.
      { id: 'health_commissions',    label: 'Commissions',    tab: 'health_commissions' },
      { id: 'health_reconciliation', label: 'Reconciliation', tab: 'health_reconciliation' },
      { id: 'lost_clients',          label: 'Lost Clients',   tab: '__lost_anchor' },
    ]},
    { section: 'Agency', items: [
      { id: 'producers',         label: 'Agents & VAs',     tab: 'producers'      },
      { id: 'expenses',          label: 'Expenses',         tab: 'expenses'        },
      { id: 'ai_agents',         label: 'AI Agents',        tab: 'ai_agents'       },
      { id: 'crosssell_lifecycle', label: 'Cross-Sell',     tab: 'crosssell_lifecycle' },
      { id: 'trigger_campaigns', label: 'Trigger Campaigns',tab: 'trigger_campaigns'},
      { id: 'life_lifecycle',    label: 'Life SMS Cycle',   tab: 'life_lifecycle'  },
      { id: 'life_conservation', label: 'Life Conservation',tab: 'life_conservation'},
      { id: 'life_commissions',  label: 'Life Commission Intel', tab: 'life_commissions'},
    ]},
  ];

  const fmt$ = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  const pct  = (n, d) => d > 0 ? Math.round((n / d) * 100) : 0;

  // HealthShell now owns the outer grid + sidebar. This view returns
  // only the Dashboard main pane — the shell wraps it at the router.
  return (
    <>
      <div className="space-y-3">
        {/* ── Compact header — one card, chip row for today's numbers ── */}
        <div className="bg-white border border-slate-200 p-4">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-[0.55rem] font-black uppercase tracking-[0.3em] text-teal-700">Today</div>
              <div className="text-lg md:text-xl font-black text-slate-900 tracking-tight mt-0.5">{dateLabel}</div>
            </div>
            {enrollWindow && (
              <div className={`text-right ${enrollWindow.status === 'active' ? 'text-teal-700' : 'text-amber-700'}`}>
                <div className="text-[0.55rem] font-black uppercase tracking-[0.2em]">{enrollWindow.status === 'active' ? 'Window open' : 'Next window'}</div>
                <div className="text-sm font-black text-slate-900 tracking-tight mt-0.5">{enrollWindow.label}</div>
                <div className="text-[0.6rem] text-slate-500 mt-0.5">{enrollWindow.status === 'active'
                  ? `Closes ${enrollWindow.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                  : `Opens in ${enrollWindow.daysUntil}d`}</div>
              </div>
            )}
          </div>
          {/* Chip row — MRR · appts today · leads · policies · clients */}
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
            <HpChip label="MRR" value={fmt$(revenueTrend.current)} sub={`${revenueTrend.delta >= 0 ? '↑' : '↓'} ${fmt$(Math.abs(revenueTrend.delta))} 8w`} tone="teal"/>
            <HpChip label="Appts today" value={todaysAppts.length} tone="sky"/>
            <HpChip label="Active leads" value={activeLeadCount} tone="amber"/>
            <HpChip label="Policies" value={healthPolicies.filter(p => p.status === 'active').length} tone="slate"/>
            <HpChip label="Clients" value={healthClients.length} tone="slate"/>
          </div>
          {loading && <div className="text-[0.6rem] text-slate-400 italic mt-2 uppercase tracking-wider">Loading book…</div>}
        </div>

        {/* ── SOA needs-countersign banner ──────────────────────
            When a client signs the SOA, the row moves to
            status='pending_agent'. This banner fires until the agent
            opens those rows + countersigns. Critical because CMS
            requires BOTH signatures for the SOA to be valid. */}
        {(() => {
          const needsAgent = (soas || []).filter(s => s.status === 'pending_agent');
          if (needsAgent.length === 0) return null;
          return (
            <div className="bg-rose-50 border-2 border-rose-400 p-4 shadow-sm">
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <div>
                  <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-rose-700">SOA awaiting your signature</div>
                  <div className="text-xl font-black text-slate-900 uppercase tracking-tight mt-0.5">
                    {needsAgent.length} client{needsAgent.length === 1 ? '' : 's'} signed — needs your countersign
                  </div>
                  <div className="text-xs text-rose-700 mt-0.5">CMS MED-136 requires BOTH signatures before the SOA is valid. Open SOA → click any "Needs Countersign" row.</div>
                </div>
                <button onClick={() => onGotoTab('soa')} className="bg-rose-600 hover:bg-rose-700 text-white text-xs font-black uppercase tracking-tight px-3 py-1.5">
                  Open SOA
                </button>
              </div>
              <div className="space-y-1.5 mt-3">
                {needsAgent.slice(0, 5).map(s => (
                  <div key={s.id} className="bg-white border border-rose-200 p-2 flex items-center gap-3 text-xs">
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-slate-900 truncate">{s.beneficiary_name || '(no name)'}</div>
                      <div className="text-[0.55rem] uppercase tracking-wider text-slate-400">
                        Signed {s.signed_at ? new Date(s.signed_at).toLocaleString() : '—'} · IP {s.client_signature_ip || '—'}
                      </div>
                    </div>
                    <button onClick={() => onGotoTab('soa')} className="bg-rose-500 hover:bg-rose-600 text-white text-[0.55rem] font-black uppercase tracking-tight px-2 py-1">Countersign →</button>
                  </div>
                ))}
                {needsAgent.length > 5 && (
                  <div className="text-[0.55rem] uppercase tracking-wider text-rose-700 italic">+{needsAgent.length - 5} more — open SOA to see them all.</div>
                )}
              </div>
            </div>
          );
        })()}

        {/* ── Lost Clients banner — unacknowledged loss feed ─── */}
        <div id="hp-lost-clients">
          {unackLosses.length > 0 && (
            <div className="bg-rose-50 border-2 border-rose-500 p-4 shadow-sm">
              <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
                <div>
                  <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-rose-700">Off the book</div>
                  <div className="text-xl font-black text-slate-900 uppercase tracking-tight mt-0.5">
                    {unackLosses.length} client{unackLosses.length === 1 ? '' : 's'} lost · {fmt$(lostMrr)}/mo gone
                  </div>
                  <div className="text-xs text-rose-700 mt-0.5">Commission stopped or policy moved to cancelled/lapsed. Acknowledge once you've confirmed and run retention.</div>
                </div>
                <button onClick={acknowledgeAllLosses} className="bg-rose-600 hover:bg-rose-700 text-white text-xs font-black uppercase tracking-tight px-3 py-1.5">Acknowledge all</button>
              </div>
              <div className="space-y-1.5 mt-3">
                {unackLosses.map(e => {
                  const lostDate = e.lost_at ? new Date(e.lost_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
                  const reasonLabel = e.reason === 'payment_stopped' ? 'Commission stopped 45+ days'
                                    : e.reason === 'cancelled'       ? 'Cancelled'
                                    : e.reason === 'lapsed'          ? 'Lapsed'
                                    : 'Manually flagged';
                  return (
                    <div key={e.id} className="bg-white border-2 border-rose-200 p-3 flex items-center gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="font-black text-slate-900 text-sm truncate">{e.client_name || '(unnamed client)'}</div>
                        <div className="text-[0.6rem] uppercase tracking-wider text-slate-500">
                          {e.plan_name || e.plan_type || 'Plan'} · {reasonLabel} · {lostDate}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-base font-black text-rose-700">−{fmt$(e.monthly_commission)}/mo</div>
                        <div className="text-[0.55rem] uppercase tracking-wider text-slate-400">MRR lost</div>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => onGotoTab('health_clients')} className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-[0.55rem] font-black uppercase tracking-tight px-2 py-1">Open client</button>
                        <button onClick={() => onGotoTab('health_inbox')}   className="bg-amber-500 hover:bg-amber-600 text-white text-[0.55rem] font-black uppercase tracking-tight px-2 py-1">Retention SMS</button>
                        <button onClick={() => acknowledgeLoss(e.id)}      className="bg-rose-600 hover:bg-rose-700 text-white text-[0.55rem] font-black uppercase tracking-tight px-2 py-1">✓ Ack</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {unackLosses.length === 0 && recentAckLosses.length > 0 && (
            <div className="bg-white border border-slate-200 p-3">
              <div className="flex items-baseline justify-between mb-1">
                <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-500">Recently Lost · last 30 days · acknowledged</div>
                <div className="text-[0.55rem] uppercase tracking-wider text-slate-400">{recentAckLosses.length}</div>
              </div>
              <div className="space-y-0.5">
                {recentAckLosses.map(e => (
                  <div key={e.id} className="flex items-center gap-2 text-xs py-1 border-b border-slate-100 last:border-b-0">
                    <span className="font-bold text-slate-700 flex-1 truncate">{e.client_name || '—'}</span>
                    <span className="text-[0.55rem] uppercase tracking-wider text-slate-400 truncate">{e.reason.replace('_', ' ')}</span>
                    <span className="font-mono text-rose-600">−{fmt$(e.monthly_commission)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Next action + Today's agenda ─────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-3">
          {nextAction ? (
            <div className={`p-4 border-2 ${nextAction.urgent ? 'border-rose-500 bg-rose-50' : 'border-slate-900 bg-white'}`}>
              <div className={`text-[0.55rem] font-black uppercase tracking-[0.2em] ${nextAction.urgent ? 'text-rose-700' : 'text-slate-700'}`}>
                {nextAction.type === 'appt' ? 'NEXT ACTION' : 'Top of pipeline'}
              </div>
              <div className="text-xl font-black text-slate-900 tracking-tight mt-1 truncate">{nextAction.lead.name || 'Unnamed lead'}</div>
              <div className={`text-xs font-bold ${nextAction.urgent ? 'text-rose-700' : 'text-slate-500'} mt-0.5`}>{nextAction.label}</div>
              <div className="text-[0.6rem] uppercase tracking-wider text-slate-400 mt-1">
                {nextAction.lead.sector === 'medicare' ? 'Medicare' : 'ACA / Private'}
                {nextAction.lead.followUpDate ? ` · follow-up ${nextAction.lead.followUpDate.slice(0, 10)}` : ''}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {nextAction.lead.phone && <button onClick={() => requestPhoneCall({ lead: nextAction.lead })} className="bg-emerald-600 hover:bg-emerald-700 text-white text-[0.6rem] font-black uppercase tracking-tight px-3 py-1.5">Call</button>}
                {nextAction.lead.phone && <a href={`sms:${phoneClean(nextAction.lead.phone)}`} className="bg-slate-900 hover:bg-black text-white text-[0.6rem] font-black uppercase tracking-tight px-3 py-1.5">SMS</a>}
                <button onClick={() => onGotoTab('health_leads')} className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 text-[0.6rem] font-black uppercase tracking-tight px-3 py-1.5">Open ↗</button>
              </div>
            </div>
          ) : (
            <div className="p-4 border-2 border-dashed border-slate-200 bg-white text-center">
              <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-500">No leads yet</div>
              <div className="text-sm text-slate-400 mt-2">Add a lead or import from CSV to start the pipeline.</div>
              <button onClick={() => onGotoTab('health_leads')} className="mt-3 bg-emerald-600 hover:bg-emerald-700 text-white text-[0.6rem] font-black uppercase tracking-tight px-3 py-1.5">+ Add Lead</button>
            </div>
          )}
          <div className="bg-white border border-slate-200 p-4">
            <div className="flex items-baseline justify-between mb-2">
              <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-500">Today's Agenda</div>
              <div className="text-[0.55rem] uppercase tracking-wider text-slate-400">{todaysAppts.length} appointment{todaysAppts.length === 1 ? '' : 's'}</div>
            </div>
            {todaysAppts.length === 0 ? (
              <div className="text-sm text-slate-400 italic py-3 text-center">Nothing booked today. Use the Pipeline to set appointments.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {todaysAppts.map(l => (
                  <div key={l.id} className="flex items-center gap-2 py-1.5">
                    <span className="text-xs font-mono font-bold text-slate-900 w-16 shrink-0">{l.appointmentTime || '—'}</span>
                    <span className={`inline-block text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5 ${l.sector === 'medicare' ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'}`}>
                      {l.sector === 'medicare' ? 'MED' : 'ACA'}
                    </span>
                    <span className="text-sm font-bold text-slate-900 truncate flex-1">{l.name || 'Unnamed'}</span>
                    {l.phone && <button onClick={() => requestPhoneCall({ lead: l })} className="text-[0.55rem] font-black uppercase tracking-tight bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1">Call</button>}
                    {l.phone && <a href={`sms:${phoneClean(l.phone)}`} className="text-[0.55rem] font-black uppercase tracking-tight bg-slate-900 hover:bg-black text-white px-2 py-1">SMS</a>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Week vs goal — progress bars ─────────────────────── */}
        <div className="bg-white border border-slate-200 p-4">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-500">This Week · against your goal</div>
            <button onClick={() => onGotoTab('daily_tracker')} className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500 hover:text-slate-900 underline">Log activity →</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
            <HpProgressBar label="Dials"        value={weekActivity.dials}    goal={weeklyGoals.dials}    tone="slate"/>
            <HpProgressBar label="Contacts"     value={weekActivity.contacts} goal={weeklyGoals.contacts} tone="sky"/>
            <HpProgressBar label="Appts set"    value={weekActivity.appts}    goal={weeklyGoals.appts}    tone="emerald"/>
            <HpProgressBar label="Apps written" value={weekActivity.apps}     goal={weeklyGoals.apps}     tone="violet"/>
          </div>
        </div>

        {/* ── Empty-state gate ────────────────────────────────────
            When the book has NO policies, NO leads, and NO clients,
            collapse all downstream zero-value widgets (funnel, book
            composition, disenrollment, hot leads, renewals, pipeline
            pills, etc.) into a single friendly "get started" card so
            the dashboard doesn't read as a wall of alarming zeroes. */}
        {(() => {
          const isEmpty = !loading
            && healthPolicies.length === 0
            && healthLeads.length === 0
            && healthClients.length === 0;
          if (!isEmpty) return null;
          return (
            <div className="bg-white border-2 border-dashed border-slate-300 p-8 text-center">
              <div className="text-3xl mb-2">✨</div>
              <div className="text-lg font-black text-slate-900 uppercase tracking-tight">Your book is empty</div>
              <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                Add your first lead or import a client roster and the pipeline, revenue trend, compliance list, and renewal queue will fill in automatically.
              </div>
              <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
                <button onClick={() => onGotoTab('health_leads')} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase tracking-tight px-4 py-2">+ Add lead</button>
                <button onClick={() => onGotoTab('health_policies')} className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs font-black uppercase tracking-tight px-4 py-2">Import CSV</button>
              </div>
            </div>
          );
        })()}

        {/* ── Segmented tab bar — cuts scroll length by hiding the
            four analytics sections behind one-at-a-time tabs. Only
            renders when the book has anything in it (otherwise the
            empty-state gate above handles the view). */}
        {!(healthPolicies.length === 0 && healthLeads.length === 0 && healthClients.length === 0) && (
          <div className="flex items-center gap-1 border-b-2 border-slate-200">
            {[
              { id: 'overview',   label: 'Overview'   },
              { id: 'book',       label: 'Book'       },
              { id: 'compliance', label: 'Compliance' },
              { id: 'revenue',    label: 'Revenue'    },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setDashTab(t.id)}
                className={`text-xs font-bold uppercase tracking-wider px-4 py-2 -mb-0.5 border-b-2 transition-colors ${dashTab === t.id ? 'border-teal-600 text-teal-700' : 'border-transparent text-slate-500 hover:text-slate-900'}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* ── Book Composition — health-specific KPIs ─────────────
            Four breakdowns the Life dashboard never needed:
              · Members by plan family (Medicare / ACA / Ancillary)
              · Top-revenue carriers (concentration risk)
              · 90-day disenrollment rate (churn signal)
              · AEP year-over-year MA enrollments
            All computed in-memory from data already loaded. */}
        {dashTab === 'book' && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="bg-white border border-slate-200 p-4">
            <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-500 mb-3">Members · by plan family</div>
            <div className="space-y-2">
              {Object.entries(bookComposition.byGroup).map(([label, d]) => {
                const total = bookComposition.totalActive || 1;
                const pctVal = Math.round((d.count / total) * 100);
                const tone = label === 'Medicare' ? 'bg-blue-500'
                           : label === 'ACA'      ? 'bg-teal-500'
                           : 'bg-violet-500';
                return (
                  <div key={label}>
                    <div className="flex items-baseline justify-between text-xs">
                      <span className="font-bold text-slate-700">{label}</span>
                      <span className="font-mono text-slate-900">{d.count} <span className="text-slate-400">({pctVal}%)</span></span>
                    </div>
                    <div className="h-1.5 bg-slate-100 mt-1 overflow-hidden">
                      <div className={`h-full ${tone}`} style={{ width: `${pctVal}%` }}/>
                    </div>
                    <div className="text-[0.55rem] uppercase tracking-wider text-slate-400 mt-0.5">{fmt$(d.mrr)}/mo</div>
                  </div>
                );
              })}
              {bookComposition.totalActive === 0 && (
                <div className="text-xs text-slate-400 italic text-center py-2">No active policies yet</div>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-200 p-4">
            <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-500 mb-3">Carrier · top 5 by MRR</div>
            <div className="space-y-1.5">
              {bookComposition.topCarriers.map(c => (
                <div key={c.name} className="flex items-baseline justify-between text-xs gap-2">
                  <span className="font-bold text-slate-700 truncate" title={c.name}>{c.name}</span>
                  <span className="font-mono text-slate-900 shrink-0">{c.count} · {fmt$(c.mrr)}</span>
                </div>
              ))}
              {bookComposition.topCarriers.length === 0 && (
                <div className="text-xs text-slate-400 italic text-center py-2">No active policies yet</div>
              )}
              {bookComposition.topCarriers.length === 1 && bookComposition.totalActive > 5 && (
                <div className="text-[0.55rem] uppercase tracking-wider text-amber-700 mt-2">Carrier concentration risk: 100% with one carrier.</div>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-200 p-4">
            <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-500 mb-1">Disenrollment · last 90 days</div>
            <div className={`text-3xl font-black tracking-tight mt-1 ${
              bookComposition.disenrollmentRate >= 10 ? 'text-rose-700'
              : bookComposition.disenrollmentRate >= 5 ? 'text-amber-700'
              : 'text-emerald-700'
            }`}>
              {bookComposition.disenrollmentRate.toFixed(1)}%
            </div>
            <div className="text-[0.55rem] uppercase tracking-wider text-slate-400 mt-1">
              {bookComposition.recentLosses} lost · {bookComposition.totalActive} active
            </div>
            <div className="text-[0.55rem] text-slate-500 mt-2 italic">
              {bookComposition.disenrollmentRate >= 10 ? 'Above industry norm — review onboarding & expectation-setting.'
              : bookComposition.disenrollmentRate >= 5 ? 'Watch — within range but trending up.'
              : 'Healthy persistency.'}
            </div>
          </div>

          <div className="bg-white border border-slate-200 p-4">
            <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-500 mb-1">AEP · MA enrollments YoY</div>
            <div className="flex items-baseline gap-3">
              <div className="text-3xl font-black text-slate-900 tracking-tight">{bookComposition.aepThis}</div>
              {bookComposition.aepDelta != null && (
                <div className={`text-sm font-black ${bookComposition.aepDelta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {bookComposition.aepDelta >= 0 ? '↑' : '↓'} {Math.abs(bookComposition.aepDelta)}%
                </div>
              )}
            </div>
            <div className="text-[0.55rem] uppercase tracking-wider text-slate-400 mt-1">
              vs {bookComposition.aepLast} last AEP · Oct 15 – Dec 7
            </div>
            <div className="text-[0.55rem] text-slate-500 mt-2 italic">
              Counts MA policies whose effective date falls inside the AEP window.
            </div>
          </div>
        </div>

        )}

        {/* ── Funnel + Revenue trend ───────────────────────────── */}
        {dashTab === 'revenue' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-white border border-slate-200 p-4">
            <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-500 mb-2">Funnel · last 30 days</div>
            <div className="space-y-1">
              <HpFunnelStage label="Leads"     count={funnel30.all}       conv={null}                                       tone="slate"/>
              <HpFunnelStage label="Contacted" count={funnel30.contacted} conv={pct(funnel30.contacted, funnel30.all)}      tone="sky"/>
              <HpFunnelStage label="Appt set"  count={funnel30.appt}      conv={pct(funnel30.appt, funnel30.contacted)}     tone="amber"/>
              <HpFunnelStage label="Showed"    count={funnel30.showed}    conv={pct(funnel30.showed, funnel30.appt)}        tone="emerald"/>
              <HpFunnelStage label="Closed"    count={funnel30.closed}    conv={pct(funnel30.closed, funnel30.showed)}      tone="violet"/>
            </div>
          </div>
          <div className="bg-white border border-slate-200 p-4 flex flex-col">
            <div className="flex items-baseline justify-between mb-2">
              <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-500">Revenue · 8-week trend</div>
              <div className={`text-[0.55rem] font-black uppercase tracking-wider ${revenueTrend.delta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                {revenueTrend.delta >= 0 ? '↑' : '↓'} {fmt$(Math.abs(revenueTrend.delta))}
              </div>
            </div>
            <HpSparkline series={revenueTrend.series} color={revenueTrend.delta >= 0 ? '#047857' : '#be123c'}/>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-[0.55rem] uppercase tracking-wider text-slate-500">Current</div>
                <div className="text-base font-black text-slate-900">{fmt$(revenueTrend.current)}/mo</div>
              </div>
              <div>
                <div className="text-[0.55rem] uppercase tracking-wider text-slate-500">8w ago</div>
                <div className="text-base font-black text-slate-900">{fmt$(revenueTrend.series[0] || 0)}/mo</div>
              </div>
              <div>
                <div className="text-[0.55rem] uppercase tracking-wider text-slate-500">Annualized</div>
                <div className="text-base font-black text-emerald-700">{fmt$(revenueTrend.current * 12)}</div>
              </div>
            </div>
          </div>
        </div>

        )}

        {/* ── Overview tab — Hot Leads + Pipeline pills + Quick
            Eligibility, the everyday "who do I call next" view. */}
        {dashTab === 'overview' && (
          <div className="space-y-3">
            <div className="bg-white border border-slate-200 p-4">
              <div className="flex items-baseline justify-between mb-2">
                <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-rose-700">Hot Leads</div>
                <button onClick={() => onGotoTab('health_leads')} className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500 hover:text-slate-900 underline">All →</button>
              </div>
              {hotLeads.length === 0 ? (
                <div className="text-sm text-slate-400 italic py-3 text-center">No active leads.</div>
              ) : (
                <div className="space-y-1">
                  {hotLeads.map(l => (
                    <button key={l.id} onClick={() => onGotoTab('health_leads')} className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 text-left">
                      <span className="text-sm font-bold text-slate-900 truncate flex-1">{l.name || 'Unnamed'}</span>
                      <LeadScoreBadge score={calcLeadScore(l)} lead={l}/>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white border border-slate-200 p-4">
              <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-500 mb-3">Pipeline · click a stage</div>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'All',       count: healthLeads.length,         tone: 'slate'   },
                  { label: 'Follow-up', count: followUpCount,              tone: 'amber'   },
                  { label: 'Booked',    count: byStage.appt_set || 0,      tone: 'sky'     },
                  { label: 'Showed',    count: byStage.interested || 0,    tone: 'emerald' },
                  { label: 'Closed',    count: byStage.closed || 0,        tone: 'violet'  },
                  { label: 'No-show',   count: byStage.no_show || 0,       tone: 'red'     },
                  { label: 'Resched',   count: byStage.reschedule || 0,    tone: 'amber'   },
                  { label: 'Not int.',  count: byStage.not_interested || 0,tone: 'slate'   },
                ].map(p => (
                  <button key={p.label} onClick={() => onGotoTab('health_leads')}
                    className={`bg-${p.tone}-50 border-2 border-${p.tone}-200 hover:border-${p.tone}-500 text-${p.tone}-800 px-3 py-1.5 text-sm font-bold flex items-center gap-2`}>
                    <span>{p.label}</span>
                    <span className={`bg-${p.tone}-200 text-${p.tone}-900 text-xs font-black px-1.5 py-0.5`}>{p.count}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Eligibility — pinned mid-call validator. Full
                surface still lives at /health_eligibility. */}
            <HpQuickEligibility onGotoTab={onGotoTab}/>
          </div>
        )}

        {/* ── Book tab — Renewals queue lives with the composition
            cards above so the operator can spot concentration risk
            + upcoming renewals in the same view. */}
        {dashTab === 'book' && (
          <div className="bg-white border border-slate-200 p-4">
            <div className="flex items-baseline justify-between mb-2">
              <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-sky-700">Renewals · next 90 days</div>
              <button onClick={() => onGotoTab('health_policies')} className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500 hover:text-slate-900 underline">All →</button>
            </div>
            <div className="space-y-1.5">
              <HpRenewalRow label="≤ 30 days" data={renewals.d30} tone="red"/>
              <HpRenewalRow label="31–60 days" data={renewals.d60} tone="amber"/>
              <HpRenewalRow label="61–90 days" data={renewals.d90} tone="sky"/>
            </div>
          </div>
        )}

        {/* ── Compliance tab — full compliance list. */}
        {dashTab === 'compliance' && (
          <div className="bg-white border border-slate-200 p-4">
            <div className="flex items-baseline justify-between mb-2">
              <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-amber-700">⚠ Compliance</div>
              <button onClick={() => onGotoTab('settings')} className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500 hover:text-slate-900 underline">Manage →</button>
            </div>
            {compliance.length === 0 ? (
              <div className="text-sm text-emerald-700 italic py-3 text-center">✓ All clear.</div>
            ) : (
              <div className="space-y-1">
                {compliance.map((c, i) => (
                  <button key={i} onClick={() => onGotoTab(c.tab)} className={`w-full text-left text-xs px-2 py-1.5 border-l-4 border-${c.severity}-500 bg-${c.severity}-50 hover:bg-${c.severity}-100`}>
                    {c.text}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Quick Actions — 5 most-used, rest tucked behind
            "More" collapsible per design-review feedback. */}
        <QuickActionsCard onGotoTab={onGotoTab}/>

        <div className="text-[0.55rem] text-slate-400 italic px-2 uppercase tracking-wider">Health-vertical only. Life book stays hidden so the two surfaces don't mix.</div>
      </div>
    </>
  );
}

// ── Health Portal sub-components ────────────────────────────────
function QuickActionsCard({ onGotoTab }) {
  const [expanded, setExpanded] = React.useState(false);
  const primary = [
    { icon: <UserPlus size={20}/>,           label: 'Add Lead',      tab: 'health_leads' },
    { icon: <CalendarIcon size={20}/>,       label: 'Calendar',      tab: 'calendar' },
    { icon: <ClipboardSignature size={20}/>, label: 'Sign SOA',      tab: 'soa' },
    { icon: <Stethoscope size={20}/>,        label: 'Eligibility',   tab: 'health_eligibility' },
    { icon: <Activity size={20}/>,           label: 'Log Activity',  tab: 'daily_tracker' },
  ];
  const secondary = [
    { icon: <FilePlus size={20}/>,   label: 'Coverage PDF',  tab: 'coverage_summary' },
    { icon: <Layers size={20}/>,     label: 'Cross-Sell',    tab: 'cross_sell' },
    { icon: <DollarSign size={20}/>, label: 'Log Expense',   tab: 'expenses' },
  ];
  const items = expanded ? [...primary, ...secondary] : primary;
  return (
    <div className="bg-white border border-slate-200 p-4">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-500">Quick Actions</div>
        <button onClick={() => setExpanded(e => !e)} className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500 hover:text-slate-900 underline">
          {expanded ? 'Less' : 'More'} {expanded ? '▲' : '▼'}
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {items.map(a => (
          <QuickAction key={a.label} icon={a.icon} label={a.label} onClick={() => onGotoTab(a.tab)}/>
        ))}
      </div>
    </div>
  );
}
function HpChip({ label, value, sub, tone = 'slate' }) {
  const toneBg   = tone === 'teal'  ? 'bg-teal-50 border-teal-200 text-teal-900'
                 : tone === 'sky'   ? 'bg-sky-50 border-sky-200 text-sky-900'
                 : tone === 'amber' ? 'bg-amber-50 border-amber-200 text-amber-900'
                 :                    'bg-slate-50 border-slate-200 text-slate-900';
  return (
    <div className={`border ${toneBg} px-3 py-2`}>
      <div className="text-[0.55rem] font-black uppercase tracking-[0.15em] opacity-70">{label}</div>
      <div className="text-lg font-black tabular-nums leading-tight mt-0.5">{value}</div>
      {sub && <div className="text-[0.55rem] uppercase tracking-wider opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
}
function HpProgressBar({ label, value, goal, tone = 'slate' }) {
  const pct = goal > 0 ? Math.min(100, Math.round((value / goal) * 100)) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between text-sm">
        <span className="font-bold text-slate-900">{label}</span>
        <span className="font-mono text-slate-700"><strong>{value}</strong> <span className="text-slate-400">/ {goal}</span></span>
      </div>
      <div className="mt-1 h-2 bg-slate-100 overflow-hidden">
        <div className={`h-full bg-${tone}-500`} style={{ width: `${pct}%` }}/>
      </div>
      <div className={`text-[0.55rem] uppercase tracking-wider mt-0.5 ${pct >= 100 ? 'text-emerald-700' : pct >= 70 ? 'text-slate-500' : 'text-rose-700'}`}>
        {pct >= 100 ? '✓ goal hit' : `${pct}% of weekly goal`}
      </div>
    </div>
  );
}
function HpFunnelStage({ label, count, conv, tone = 'slate' }) {
  return (
    <div className={`flex items-center gap-2 border-l-4 border-${tone}-500 bg-${tone}-50 px-2 py-1.5`}>
      <span className="text-sm font-bold text-slate-900 flex-1">{label}</span>
      <span className="text-base font-black tabular-nums">{count}</span>
      {conv !== null && conv !== undefined && (
        <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500 w-14 text-right">{conv}% conv</span>
      )}
    </div>
  );
}
function HpRenewalRow({ label, data, tone = 'slate' }) {
  const { count = 0, mrr = 0 } = data || {};
  return (
    <div className={`flex items-center justify-between border-l-4 border-${tone}-500 bg-${tone}-50 px-2 py-1.5`}>
      <span className="text-xs font-bold text-slate-900">{label}</span>
      <span className="text-xs"><strong className="tabular-nums">{count}</strong> <span className="text-slate-500">policies · ${(mrr || 0).toFixed(0)}/mo</span></span>
    </div>
  );
}
function HpSparkline({ series = [], color = '#0f172a' }) {
  if (!series.length) return <div className="h-10 bg-slate-50"/>;
  const max = Math.max(...series, 1);
  const min = Math.min(...series, 0);
  const range = max - min || 1;
  const W = 100, H = 28;
  const pts = series.map((v, i) => {
    const x = (i / (series.length - 1 || 1)) * W;
    const y = H - ((v - min) / range) * H;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const path = `M ${pts.join(' L ')}`;
  const area = `${path} L ${W},${H} L 0,${H} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-12 mt-1">
      <path d={area} fill={color} opacity={0.12}/>
      <path d={path} fill="none" stroke={color} strokeWidth="1.5"/>
    </svg>
  );
}

function Kpi({ label, value, sub, tone = 'slate' }) {
  return (
    <div className={`bg-white border-2 border-${tone}-200 border-l-4 border-l-${tone}-500 p-4`}>
      <div className="text-[0.55rem] font-black uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className={`text-3xl md:text-4xl font-black tracking-tight text-${tone}-900 mt-1`}>{value}</div>
      <div className="text-[0.6rem] uppercase tracking-wider text-slate-400 mt-0.5">{sub}</div>
    </div>
  );
}
// Health Quick Action tile. `icon` can be a React node (preferred — a
// lucide icon component) OR a string. An empty string still renders
// the button without a hollow gap because we only mount the icon slot
// when there's something to show.
function QuickAction({ icon, label, onClick }) {
  const hasIcon = icon !== '' && icon != null;
  return (
    <button
      onClick={onClick}
      className="bg-white border-2 border-slate-200 hover:border-teal-500 hover:bg-teal-50/50 p-3 text-left transition-colors flex items-center gap-2.5 group"
    >
      {hasIcon && (
        <span className="text-teal-600 group-hover:text-teal-700 shrink-0 flex items-center">
          {typeof icon === 'string' ? <span className="text-xl">{icon}</span> : icon}
        </span>
      )}
      <span className="text-sm font-bold text-slate-800 group-hover:text-teal-900 truncate">{label}</span>
    </button>
  );
}

// Pinned quick-eligibility widget for the Health Portal. Thin wrapper
// over recommendStack — captures the four highest-signal inputs (age,
// state, insurable Y/N, MEC needed?) and surfaces the strategy name +
// first product. Full conditions / presets / underwriting questions
// stay in the dedicated /health_eligibility tab.
function HpQuickEligibility({ onGotoTab }) {
  const [age, setAge]               = React.useState('');
  const [state, setState]           = React.useState('');
  const [insurable, setInsurable]   = React.useState('yes'); // 'yes' | 'no' | 'unsure'
  const [needsMec, setNeedsMec]     = React.useState(false);
  const [subsidyEligible, setSubsidyEligible] = React.useState(true);
  const [result, setResult]         = React.useState(null);

  function runCheck() {
    const ageNum = Number(age);
    if (!ageNum || ageNum < 0 || ageNum > 130) { setResult({ error: 'Enter a valid age.' }); return; }
    if (!state || state.length !== 2)         { setResult({ error: 'Enter a 2-letter state.' }); return; }
    try {
      const r = recommendStack({
        age: ageNum,
        state: state.toUpperCase(),
        sex: null,
        height: null,
        weight: null,
        insurable: insurable === 'yes' ? true : insurable === 'no' ? false : null,
        subsidyEligible,
        needsMec,
        pregnant: false,
        intakeAnswers: {},
      });
      setResult({ ok: true, ...r });
    } catch (e) {
      setResult({ error: e.message || 'Unable to compute.' });
    }
  }

  return (
    <div className="bg-white border-2 border-teal-300 p-4">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-teal-700">Quick Eligibility</div>
          <div className="text-base font-black text-slate-900 uppercase tracking-tight">30-second prospect check</div>
        </div>
        <button onClick={() => onGotoTab('health_eligibility')} className="text-[0.55rem] font-black uppercase tracking-wider text-teal-700 hover:text-teal-900 underline">Full checker →</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
        <label className="block">
          <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Age</span>
          <input
            type="text" inputMode="numeric"
            value={age}
            onChange={e => setAge(e.target.value.replace(/[^\d]/g, ''))}
            placeholder="38"
            className="mt-0.5 w-full border border-slate-300 px-2 py-1.5 text-sm focus:border-teal-500 focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">State</span>
          <input
            type="text"
            value={state}
            onChange={e => setState(e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 2))}
            placeholder="TX"
            className="mt-0.5 w-full border border-slate-300 px-2 py-1.5 text-sm uppercase focus:border-teal-500 focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Insurable</span>
          <select
            value={insurable}
            onChange={e => setInsurable(e.target.value)}
            className="mt-0.5 w-full border border-slate-300 px-2 py-1.5 text-sm bg-white focus:border-teal-500 focus:outline-none"
          >
            <option value="yes">Healthy</option>
            <option value="no">Pre-ex / decline</option>
            <option value="unsure">Unsure</option>
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-xs font-bold text-slate-700 self-end pb-1.5">
          <input type="checkbox" checked={subsidyEligible} onChange={e => setSubsidyEligible(e.target.checked)} className="accent-teal-600"/>
          <span>Subsidy eligible</span>
        </label>
        <label className="flex items-center gap-1.5 text-xs font-bold text-slate-700 self-end pb-1.5">
          <input type="checkbox" checked={needsMec} onChange={e => setNeedsMec(e.target.checked)} className="accent-teal-600"/>
          <span>Needs MEC</span>
        </label>
      </div>

      <div className="flex items-center gap-3">
        <button onClick={runCheck} className="bg-teal-600 hover:bg-teal-700 text-white text-xs font-black uppercase tracking-tight px-4 py-2">Run check</button>
        {result && !result.error && (
          <div className="flex-1 min-w-0">
            <div className="text-[0.55rem] uppercase tracking-wider text-slate-400">Recommendation</div>
            <div className="text-sm font-bold text-slate-900 truncate">
              {result.strategy?.label || result.strategy || 'No match'}
              {Array.isArray(result.products) && result.products[0] && (
                <span className="text-slate-500 font-normal"> · start with {result.products[0].name || result.products[0]}</span>
              )}
            </div>
            {result.reason && <div className="text-[0.6rem] text-slate-500 truncate italic mt-0.5">{result.reason}</div>}
          </div>
        )}
        {result?.error && <div className="text-sm text-rose-700 font-bold">{result.error}</div>}
      </div>

      <div className="text-[0.55rem] text-slate-400 italic mt-2 uppercase tracking-wider">
        For deeper underwriting (conditions, BMI, prescriptions), open the full Eligibility Checker.
      </div>
    </div>
  );
}
function RrCard({ tone, icon, label, value, onClick }) {
  return (
    <button onClick={onClick} className={`bg-${tone}-50 border-2 border-${tone}-200 hover:border-${tone}-500 p-3 text-left transition-colors`}>
      <div className="flex items-center gap-2">
        <span className="text-xl">{icon}</span>
        <span className={`text-[0.55rem] font-black uppercase tracking-[0.15em] text-${tone}-700`}>{label}</span>
      </div>
      <div className={`text-xl font-black tracking-tight text-${tone}-900 mt-1`}>{value}</div>
    </button>
  );
}
function HealthPortalPlaceholder({ placeholderKey, onClose }) {
  const titles = {
    expenses:      'Expenses — agency overhead tracker',
    daily_tracker: 'Daily Tracker — dials, contacts, hours logged',
  };
  return (
    <div className="bg-white border-2 border-amber-300 p-6">
      <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-amber-700">Phase B · Under Construction</div>
      <div className="text-xl font-black text-slate-900 uppercase tracking-tight mt-1">{titles[placeholderKey] || placeholderKey}</div>
      <div className="text-sm text-slate-600 mt-2">This view is scheduled for the next Health Portal phase. The data model + RLS is already provisioned; the UI lands shortly.</div>
      <button aria-label="Close" onClick={onClose} className="mt-4 bg-slate-900 hover:bg-black text-white text-xs font-black uppercase tracking-tight px-4 py-2">← Back to Dashboard</button>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// HEALTH LEADS / HEALTH INBOX — vertical-isolated wrappers.
//
// These reuse the full LeadsView and SmsInboxView components but pass
// in a leads list filtered to vertical='health' only. The setLeads
// wrapper merges health-pipeline updates back into the global leads
// array WITHOUT touching life-vertical entries, so a dual-vertical
// agent's life book is never modified from inside the Health Portal.
//
// New leads created inside these wrappers default to vertical='health'
// via the defaultVertical prop on LeadFormModal.
// ════════════════════════════════════════════════════════════════════

// Health portal includes every non-life vertical: 'health' (ACA / PPO /
// short-term), 'medicare' (MA / Supplement / PDP), and 'dental_vision'.
// The strict === 'health' check used to leak Medicare + ancillary leads
// out of the sidebar counts and out of HealthLeadsView, which meant a
// demo seeded with 10 ACA + 3 Medicare + 1 dental leads rendered only
// the 10 ACA rows and the sidebar Pipeline showed 0 badges anywhere
// Medicare or dental sat.
function isHealthVertical(l) {
  const v = l?.vertical || 'life';
  return v !== 'life';
}
// An imported lead that never got a vertical assigned. These land with a blank
// vertical, so isHealthVertical() treats them as life and they never show on the
// health Sales Floor — the "my imported leads are missing" complaint. We don't
// silently reclassify them (that would hide unsorted imports from life agents in
// both-mode); instead the health view offers a one-click "show them here" that
// tags exactly this set to 'health'. Explicit, scoped, and reversible via the
// per-lead vertical tag. Only matches truly-unsorted imports (blank/unknown),
// never a lead an operator deliberately marked 'life'.
function isUnsortedImport(l) {
  if (!l) return false;
  const imported = l.imported === true || !!l.fp_id;
  const v = String(l.vertical || '').trim().toLowerCase();
  return imported && (v === '' || v === 'unknown');
}
function makeHealthLeadFilter(leads = []) {
  return leads.filter(isHealthVertical);
}
function makeHealthSetLeads(setLeads) {
  return (updater) => {
    setLeads(prev => {
      const lifePart   = prev.filter(l => !isHealthVertical(l));
      const healthPart = prev.filter(isHealthVertical);
      const next = typeof updater === 'function' ? updater(healthPart) : updater;
      // Mark every entry coming back from the health pipeline as health
      // so a sector-cleared lead doesn't silently fall back to life.
      // Preserve any pre-existing granular vertical (medicare, dental_vision).
      const tagged = (next || []).map(l => ({ ...l, vertical: l.vertical || 'health' }));
      return [...lifePart, ...tagged];
    });
  };
}

function HealthLeadsView(props) {
  const { leads = [], setLeads, showToast, ...rest } = props;
  const healthLeads = React.useMemo(() => makeHealthLeadFilter(leads), [leads]);
  const setHealthLeads = React.useMemo(() => makeHealthSetLeads(setLeads), [setLeads]);
  // Imported leads that arrived without a vertical don't appear above (see
  // isUnsortedImport). Surface them with a one-click fix so the operator never
  // has to re-import to make them show up on the Sales Floor.
  const unsorted = React.useMemo(() => (leads || []).filter(isUnsortedImport), [leads]);
  const showImportedHere = React.useCallback(() => {
    const n = (leads || []).filter(isUnsortedImport).length;
    setLeads(prev => (prev || []).map(l => isUnsortedImport(l) ? { ...l, vertical: 'health' } : l));
    if (showToast) showToast(`Added ${n} imported lead${n === 1 ? '' : 's'} to your Sales Floor`, 'success');
  }, [leads, setLeads, showToast]);
  return (
    <div className="space-y-3">
      <div className="bg-black border-l-4 border-emerald-500 px-6 py-4 shadow-lg flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-emerald-400 text-[0.6rem] font-black uppercase tracking-[0.3em]">Health Pipeline · isolated</div>
          <div className="text-white text-lg md:text-xl font-black mt-0.5 uppercase tracking-tight">{healthLeads.length} health-vertical lead{healthLeads.length === 1 ? '' : 's'}</div>
        </div>
        <span className="text-[0.55rem] uppercase tracking-wider text-slate-400 bg-slate-900/60 border border-slate-700 px-2 py-1">Life-vertical leads hidden</span>
      </div>
      {unsorted.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm text-amber-900">
            <span className="font-bold">{unsorted.length} imported lead{unsorted.length === 1 ? '' : 's'}</span> {unsorted.length === 1 ? "isn't" : "aren't"} on your Sales Floor yet — {unsorted.length === 1 ? 'it' : 'they'} came in without a category.
          </div>
          <button
            onClick={showImportedHere}
            className="shrink-0 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold uppercase tracking-wide px-4 py-2 rounded-md transition-colors"
          >
            Add {unsorted.length === 1 ? 'it' : 'them'} to my Sales Floor
          </button>
        </div>
      )}
      <LeadsView {...rest} showToast={showToast} leads={healthLeads} setLeads={setHealthLeads} defaultVertical="health"/>
    </div>
  );
}

function HealthInboxView(props) {
  const { leads = [], setLeads, ...rest } = props;
  const healthLeads = React.useMemo(() => makeHealthLeadFilter(leads), [leads]);
  const setHealthLeads = React.useMemo(() => makeHealthSetLeads(setLeads), [setLeads]);
  return (
    <div className="space-y-3">
      <div className="bg-black border-l-4 border-emerald-500 px-6 py-4 shadow-lg flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-emerald-400 text-[0.6rem] font-black uppercase tracking-[0.3em]">Health Inbox · isolated</div>
          <div className="text-white text-lg md:text-xl font-black mt-0.5 uppercase tracking-tight">SMS threads · health-vertical only</div>
        </div>
        <span className="text-[0.55rem] uppercase tracking-wider text-slate-400 bg-slate-900/60 border border-slate-700 px-2 py-1">Life threads hidden</span>
      </div>
      <SmsInboxView {...rest} leads={healthLeads} setLeads={setHealthLeads}/>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// HEALTH CLIENTS — clients table filtered to vertical='health'.
// Keeps the agent's health book visually separated from life book.
// ════════════════════════════════════════════════════════════════════

// ── Health: Clients & Policies merged wrapper ─────────────────
// Same shape as ClientsAndPoliciesView above but for the health
// vertical. Persists its sub-tab choice under a separate key so
// the operator can be on different surfaces in Life vs Health
// without one resetting the other.
const HCP_SUBTAB_KEY = 'crm:health_clients_policies_subtab';

function HealthClientsAndPoliciesView(props) {
  const [subTab, setSubTab] = React.useState(() => {
    try {
      const v = localStorage.getItem(HCP_SUBTAB_KEY);
      return v === 'policies' ? 'policies' : 'clients';
    } catch { return 'clients'; }
  });
  React.useEffect(() => {
    try { localStorage.setItem(HCP_SUBTAB_KEY, subTab); } catch {}
  }, [subTab]);

  // Health surfaces both views inside a HealthShell (sidebar +
  // workspace-aware chrome). Render the wrapper inside the same
  // shell so the sub-tab toggle sits where the operator expects
  // it — right above the workspace content.
  return (
    <HealthShell activeTab="health_clients" onGotoTab={props.onGotoTab} currentUser={props.currentUser} leads={[]} showToast={props.showToast}>
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8 pt-3">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
          <button
            onClick={() => setSubTab('clients')}
            className={`text-xs font-bold uppercase tracking-tight px-3 py-1.5 rounded-md transition-colors ${subTab === 'clients' ? 'bg-teal-700 text-white' : 'text-slate-600 hover:text-slate-900'}`}
          >
            👥 Clients
          </button>
          <button
            onClick={() => setSubTab('policies')}
            className={`text-xs font-bold uppercase tracking-tight px-3 py-1.5 rounded-md transition-colors ${subTab === 'policies' ? 'bg-teal-700 text-white' : 'text-slate-600 hover:text-slate-900'}`}
          >
            📄 Policies
          </button>
        </div>
      </div>
      {subTab === 'clients'
        ? <HealthClientsView {...props}/>
        : <HealthPoliciesView currentUser={props.currentUser} settings={props.settings} showToast={props.showToast} openClientProfile={props.openClientProfile} onGotoTab={props.onGotoTab}/>}
    </HealthShell>
  );
}

function HealthClientsView({ currentUser, settings = {}, showToast = () => {}, onGotoTab = () => {} }) {
  const [clients, setClients] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [sectorFilter, setSectorFilter] = React.useState('all');
  // Audit 2026-07-02: sort was already wired on the column headers
  // but the affordance was too subtle. Adding explicit filter pills
  // for the fields the operator actually acts on — PTC status, SMS
  // opt-in, state, carrier — and a visible sort control so the
  // book can be sliced without hunting for the ▲/▼ arrows.
  const [ptcFilter, setPtcFilter] = React.useState('all');       // all | yes | no
  const [smsFilter, setSmsFilter] = React.useState('all');       // all | yes | no
  const [stateFilter, setStateFilter] = React.useState('all');   // 'all' or state abbr
  const [carrierFilter, setCarrierFilter] = React.useState('all'); // 'all' or carrier name
  // Column sort — clicking a header sets the sort key; clicking the
  // same header again flips direction. Persisted to localStorage so
  // the operator's chosen view sticks between sessions.
  const [sortKey, setSortKey] = React.useState(() => {
    try { return localStorage.getItem('health_clients:sort_key') || 'name'; } catch { return 'name'; }
  });
  const [sortDir, setSortDir] = React.useState(() => {
    try { return localStorage.getItem('health_clients:sort_dir') || 'asc'; } catch { return 'asc'; }
  });
  React.useEffect(() => {
    try {
      localStorage.setItem('health_clients:sort_key', sortKey);
      localStorage.setItem('health_clients:sort_dir', sortDir);
    } catch {}
  }, [sortKey, sortDir]);
  // Opens the full 360 client profile modal (same one Policy Vault
  // uses). Editing a policy from inside that modal kicks back to the
  // Policy Vault tab where the edit modal already lives.
  const [profileClient, setProfileClient] = React.useState(null);

  React.useEffect(() => {
    if (!currentUser?.id) return;
    setLoading(true);
    supabase.from('clients')
      .select('id, name, phone, email, sector, dob, state_abbr, current_carrier, current_plan_type, lis_dual_status, opt_in_sms, permission_to_contact, created_at')
      .eq('vertical', 'health')
      .order('name')
      .then(({ data }) => {
        setClients(data || []);
        setLoading(false);
      });
  }, [currentUser?.id]);

  const lc = search.trim().toLowerCase();
  const filtered = clients.filter(c => {
    if (sectorFilter !== 'all' && c.sector !== sectorFilter) return false;
    if (ptcFilter === 'yes' && !c.permission_to_contact) return false;
    if (ptcFilter === 'no'  &&  c.permission_to_contact) return false;
    if (smsFilter === 'yes' && !c.opt_in_sms) return false;
    if (smsFilter === 'no'  &&  c.opt_in_sms) return false;
    if (stateFilter !== 'all' && (c.state_abbr || '') !== stateFilter) return false;
    if (carrierFilter !== 'all' && (c.current_carrier || '') !== carrierFilter) return false;
    if (!lc) return true;
    const blob = `${c.name || ''} ${c.phone || ''} ${c.email || ''} ${c.current_carrier || ''}`.toLowerCase();
    return blob.includes(lc);
  });

  // Option lists for the state + carrier dropdowns, derived from the
  // real book. Filtering to "no results" is possible if e.g. the book
  // has 0 clients in TX; that's honest and expected.
  const stateOptions = React.useMemo(() => {
    const s = new Set();
    for (const c of clients) { if (c.state_abbr) s.add(c.state_abbr); }
    return [...s].sort();
  }, [clients]);
  const carrierOptions = React.useMemo(() => {
    const s = new Set();
    for (const c of clients) { if (c.current_carrier) s.add(c.current_carrier); }
    return [...s].sort();
  }, [clients]);

  const activeFilterCount =
      (sectorFilter !== 'all' ? 1 : 0)
    + (ptcFilter !== 'all' ? 1 : 0)
    + (smsFilter !== 'all' ? 1 : 0)
    + (stateFilter !== 'all' ? 1 : 0)
    + (carrierFilter !== 'all' ? 1 : 0)
    + (lc ? 1 : 0);

  const resetFilters = () => {
    setSearch(''); setSectorFilter('all'); setPtcFilter('all');
    setSmsFilter('all'); setStateFilter('all'); setCarrierFilter('all');
  };

  // Pull a comparable value for the current sort column. Empty/null
  // values always sort to the end regardless of direction so they
  // don't clutter the top of the table.
  const sortValue = (c) => {
    switch (sortKey) {
      case 'sector':           return c.sector === 'medicare' ? 'Medicare' : 'ACA';
      case 'dob':              return c.dob || '';
      case 'state_abbr':       return c.state_abbr || '';
      case 'current_carrier':  return c.current_carrier || '';
      case 'current_plan_type':return c.current_plan_type || '';
      case 'permission_to_contact': return c.permission_to_contact ? 1 : 0;
      case 'opt_in_sms':       return c.opt_in_sms ? 1 : 0;
      case 'name':
      default:                 return c.name || '';
    }
  };
  const sorted = [...filtered].sort((a, b) => {
    const va = sortValue(a);
    const vb = sortValue(b);
    const aEmpty = va === '' || va == null;
    const bEmpty = vb === '' || vb == null;
    if (aEmpty && !bEmpty) return 1;
    if (!aEmpty && bEmpty) return -1;
    if (aEmpty && bEmpty)  return 0;
    let cmp;
    if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb;
    else                                                  cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };
  const SORT_COLUMNS = [
    { label: 'Client',          key: 'name' },
    { label: 'Sector',          key: 'sector' },
    { label: 'DOB',             key: 'dob' },
    { label: 'State',           key: 'state_abbr' },
    { label: 'Current carrier', key: 'current_carrier' },
    { label: 'Plan type',       key: 'current_plan_type' },
    { label: 'PTC',             key: 'permission_to_contact' },
    { label: 'SMS opt-in',      key: 'opt_in_sms' },
  ];

  const counts = clients.reduce((acc, c) => {
    const s = c.sector === 'medicare' ? 'medicare' : 'aca_private';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="bg-black border-l-4 border-emerald-500 px-6 py-5 shadow-2xl">
        <div className="text-emerald-400 text-[0.6rem] font-black uppercase tracking-[0.3em]">Health Clients</div>
        <div className="text-white text-xl md:text-2xl font-black mt-1.5 uppercase tracking-tight">{clients.length} client{clients.length === 1 ? '' : 's'} in the health book</div>
        <div className="text-slate-400 text-xs mt-1.5">{counts.aca_private || 0} ACA/Private · {counts.medicare || 0} Medicare</div>
      </div>

      <div className="bg-white border border-slate-200 p-3 space-y-2.5">
        {/* Row 1: search + sector pills */}
        <div className="flex items-center gap-2 flex-wrap">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, phone, email, carrier…" className="flex-1 min-w-[220px] border border-slate-300 px-2 py-1.5 text-sm"/>
          {['all', 'aca_private', 'medicare'].map(s => (
            <button key={s} onClick={() => setSectorFilter(s)}
              className={`text-xs font-bold uppercase tracking-tight px-3 py-1.5 ${sectorFilter === s ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
              {s === 'all' ? 'All' : s === 'medicare' ? 'Medicare' : 'ACA / Private'}
            </button>
          ))}
        </div>

        {/* Row 2: PTC + SMS opt-in + state + carrier + explicit sort */}
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 pr-1">Filter:</span>

          <div className="inline-flex items-center border border-slate-200 rounded">
            <span className="px-2 py-1 text-[10px] uppercase tracking-wider font-bold text-slate-500 border-r border-slate-200 bg-slate-50">PTC</span>
            {['all', 'yes', 'no'].map(v => (
              <button key={v} onClick={() => setPtcFilter(v)}
                className={`px-2 py-1 text-[11px] font-semibold ${ptcFilter === v ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-50'}`}>
                {v === 'all' ? 'All' : v === 'yes' ? 'Yes' : 'No'}
              </button>
            ))}
          </div>

          <div className="inline-flex items-center border border-slate-200 rounded">
            <span className="px-2 py-1 text-[10px] uppercase tracking-wider font-bold text-slate-500 border-r border-slate-200 bg-slate-50">SMS opt-in</span>
            {['all', 'yes', 'no'].map(v => (
              <button key={v} onClick={() => setSmsFilter(v)}
                className={`px-2 py-1 text-[11px] font-semibold ${smsFilter === v ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-50'}`}>
                {v === 'all' ? 'All' : v === 'yes' ? 'Yes' : 'No'}
              </button>
            ))}
          </div>

          {stateOptions.length > 0 && (
            <label className="inline-flex items-center gap-1">
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">State</span>
              <select value={stateFilter} onChange={e => setStateFilter(e.target.value)}
                className="border border-slate-200 rounded px-2 py-1 text-xs bg-white">
                <option value="all">All ({stateOptions.length})</option>
                {stateOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          )}

          {carrierOptions.length > 0 && (
            <label className="inline-flex items-center gap-1">
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Carrier</span>
              <select value={carrierFilter} onChange={e => setCarrierFilter(e.target.value)}
                className="border border-slate-200 rounded px-2 py-1 text-xs bg-white max-w-[160px]">
                <option value="all">All ({carrierOptions.length})</option>
                {carrierOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          )}

          <label className="inline-flex items-center gap-1 ml-2">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Sort</span>
            <select value={sortKey} onChange={e => { setSortKey(e.target.value); setSortDir('asc'); }}
              className="border border-slate-200 rounded px-2 py-1 text-xs bg-white">
              {SORT_COLUMNS.map(col => <option key={col.key} value={col.key}>{col.label}</option>)}
            </select>
            <button onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
              className="border border-slate-200 rounded px-2 py-1 text-xs bg-white hover:bg-slate-50 font-mono"
              title={`Sort direction: ${sortDir === 'asc' ? 'ascending' : 'descending'} — click to flip`}>
              {sortDir === 'asc' ? '▲ A→Z' : '▼ Z→A'}
            </button>
          </label>

          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-slate-500 font-mono tabular-nums">
              {sorted.length === clients.length
                ? `${clients.length}`
                : `${sorted.length} of ${clients.length}`}
            </span>
            {activeFilterCount > 0 && (
              <button onClick={resetFilters}
                className="text-[11px] font-semibold text-slate-500 hover:text-slate-900 underline">
                Clear filters ({activeFilterCount})
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {SORT_COLUMNS.map(col => {
                const isActive = sortKey === col.key;
                return (
                  <th key={col.key}
                      onClick={() => toggleSort(col.key)}
                      className={`text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] cursor-pointer select-none hover:bg-slate-100 ${isActive ? 'text-slate-900' : 'text-slate-500'}`}
                      title={isActive ? `Sorted ${sortDir === 'asc' ? 'ascending' : 'descending'} — click to flip` : 'Click to sort'}>
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      <span className={`text-[0.7rem] ${isActive ? 'opacity-100' : 'opacity-30'}`}>
                        {isActive ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
                      </span>
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>
            ) : sorted.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">{clients.length === 0 ? 'No health-vertical clients yet. Convert a lead with sector=Medicare/ACA to build the book.' : 'No clients match.'}</td></tr>
            ) : sorted.map(c => (
              <tr key={c.id} className="hover:bg-slate-50">
                <td className="px-3 py-2">
                  <button
                    onClick={() => setProfileClient({ name: c.name, id: c.id })}
                    className="text-left">
                    <div className="font-bold text-sky-700 hover:text-sky-900 hover:underline">{c.name}</div>
                    <div className="text-[0.6rem] uppercase tracking-wider text-slate-400">{c.phone || c.email || ''}</div>
                  </button>
                </td>
                <td className="px-3 py-2 text-xs">
                  <span className={`inline-block text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5 ${c.sector === 'medicare' ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'}`}>
                    {c.sector === 'medicare' ? 'Medicare' : 'ACA'}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs font-mono">{c.dob || '—'}</td>
                <td className="px-3 py-2 text-xs font-mono">{c.state_abbr || '—'}</td>
                <td className="px-3 py-2 text-xs">{c.current_carrier || '—'}</td>
                <td className="px-3 py-2 text-xs">{c.current_plan_type || '—'}</td>
                <td className="px-3 py-2 text-xs">{c.permission_to_contact ? '✓' : <span className="text-red-600">✗</span>}</td>
                <td className="px-3 py-2 text-xs">{c.opt_in_sms ? '✓' : <span className="text-slate-400">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {profileClient && (
        <HealthClientProfileModal
          clientName={profileClient.name}
          clientId={profileClient.id}
          currentUser={currentUser}
          settings={settings}
          onClose={() => setProfileClient(null)}
          onEditPolicy={() => { setProfileClient(null); onGotoTab('health_policies'); showToast('Open the Policy Vault tab to edit policy fields.'); }}
          onGotoTab={onGotoTab}
          showToast={showToast}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// COVERAGE SUMMARY — brochure-grade per-client PDF generator.
//
// The agent picks a client, ticks the plans in their stack, enters
// monthly rates, and the app produces an 8-page printable packet
// modeled on the For-The-People design:
//   1. Cover     — brand mark, plan chips, prepared-for, agent
//   2. Snapshot  — "here's everything you signed up for"
//   3-N. Plans   — one card per plan with benefit table + footnote
//   N+1. Examples— "what your package actually pays" scenarios
//   N+2. Use it  — claims + provider quick links
//   Last. Closer — "I'm in your corner"
//
// Print path: in-page <style> tag carries the print rules. The agent
// clicks "Print to PDF" → window.print() → Save as PDF in the browser
// dialog. Same output across Chrome, Safari, Firefox.
// ════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════
// HEALTH CONSENT MANAGEMENT  (Priority 1 — compliance / E&O)
//
// The agent-side counterpart to /consent/<token>. From here the
// agent can:
//   · See every consent ever sent (pending / signed / expired /
//     declined / revoked) with the receipts inline
//   · Send a new consent form to a client or lead via SMS / email
//     / copy-link
//   · Revoke a signed consent (records the revocation reason)
//
// Every action emits an audit log row so the agency owner can
// later prove who sent what, when, from which IP.
// ════════════════════════════════════════════════════════════════════

function ConsentBadge({ status, signedAt }) {
  // Tiny pill used in client / lead lists. Tone follows the FTPIG
  // status vocabulary — emerald for done, amber for pending,
  // slate for expired, rose for revoked.
  const map = {
    signed:   { label: 'Signed',   cls: 'bg-emerald-100 text-emerald-800' },
    pending:  { label: 'Pending',  cls: 'bg-amber-100 text-amber-800' },
    expired:  { label: 'Expired',  cls: 'bg-slate-200 text-slate-700' },
    declined: { label: 'Declined', cls: 'bg-violet-100 text-violet-800' },
    revoked:  { label: 'Revoked',  cls: 'bg-rose-100 text-rose-800' },
  };
  const m = map[status] || { label: status || 'None', cls: 'bg-slate-100 text-slate-700' };
  const tip = signedAt ? `Signed ${new Date(signedAt).toLocaleString()}` : undefined;
  return (
    <span title={tip} className={`text-[0.55rem] font-black uppercase tracking-wider px-1.5 py-0.5 ${m.cls}`}>{m.label}</span>
  );
}

function HealthConsentsView({ currentUser, settings = {}, onGotoTab = () => {}, showToast = () => {}, openClientProfile = () => {} }) {
  const [consents, setConsents]   = React.useState([]);
  const [loading, setLoading]     = React.useState(true);
  const [filter, setFilter]       = React.useState('all'); // all | pending | signed | expired | declined | revoked
  const [composing, setComposing] = React.useState(false);
  const [detail, setDetail]       = React.useState(null);

  const reload = React.useCallback(async () => {
    if (!currentUser?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      await sweepExpiredConsents();
      const rows = await fetchOwnerConsents({ limit: 500 });
      setConsents(rows);
    } catch (err) {
      console.warn('[HealthConsentsView] load failed:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  React.useEffect(() => { reload(); }, [reload]);

  const filtered = filter === 'all' ? consents : consents.filter(c => c.status === filter);
  const counts = consents.reduce((acc, c) => {
    acc[c.status] = (acc[c.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {/* Hero */}
      <div className="bg-black border-l-4 border-teal-500 p-4">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="text-teal-300 text-[0.6rem] font-black uppercase tracking-[0.3em]">Consent &amp; Compliance</div>
            <div className="text-white text-2xl font-black uppercase tracking-tight">Audit-grade authorization tracking</div>
            <div className="text-slate-400 text-xs mt-1">
              {consents.length} total · {counts.pending || 0} pending · {counts.signed || 0} signed
              {counts.declined ? ` · ${counts.declined} declined` : ''}
              {counts.expired ? ` · ${counts.expired} expired` : ''}
            </div>
          </div>
          <button
            onClick={() => setComposing(true)}
            className="bg-teal-600 hover:bg-teal-700 text-white text-xs font-black uppercase tracking-tight px-4 py-2"
          >
            + Send Consent Form
          </button>
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex items-center gap-2 flex-wrap">
        {['all','pending','signed','declined','expired','revoked'].map(k => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`text-xs font-bold uppercase tracking-tight px-3 py-1.5 border-2 ${filter === k ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
          >
            {k} {k !== 'all' && counts[k] != null && <span className="opacity-70 ml-1">({counts[k] || 0})</span>}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {['Client', 'Scope', 'Plans', 'Sent', 'Status', 'Signed at', 'IP', ''].map(h => (
                <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan="8" className="px-3 py-8 text-center text-sm text-slate-400 italic">Loading consents…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan="8" className="px-3 py-8 text-center text-sm text-slate-400 italic">
                No consent records yet. Click "+ Send Consent Form" to send your first one.
              </td></tr>
            )}
            {filtered.map(c => (
              <tr key={c.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setDetail(c)}>
                <td className="px-3 py-2 font-bold text-slate-900 whitespace-nowrap"><ClientNameLink name={c.client_name} openClientProfile={openClientProfile} className="font-bold text-slate-900 hover:underline"/></td>
                <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">{CONSENT_SCOPE_LABEL[c.scope] || c.scope}</td>
                <td className="px-3 py-2 text-[0.6rem] uppercase tracking-wider text-slate-500">{(c.plan_types || []).slice(0, 3).join(' · ') || '—'}</td>
                <td className="px-3 py-2 text-xs text-slate-500 font-mono whitespace-nowrap">{c.sent_at ? new Date(c.sent_at).toLocaleDateString() : '—'}</td>
                <td className="px-3 py-2"><ConsentBadge status={c.status} signedAt={c.signed_at}/></td>
                <td className="px-3 py-2 text-xs text-slate-500 font-mono whitespace-nowrap">{c.signed_at ? new Date(c.signed_at).toLocaleString() : '—'}</td>
                <td className="px-3 py-2 text-[0.6rem] text-slate-400 font-mono">{c.signed_ip || ''}</td>
                <td className="px-3 py-2 text-[0.55rem] uppercase tracking-wider text-teal-700">View →</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {composing && (
        <ConsentComposeModal
          currentUser={currentUser}
          settings={settings}
          onClose={() => setComposing(false)}
          onCreated={(c) => {
            setComposing(false);
            setDetail(c);
            reload();
            audit({
              ownerId: currentUser.id,
              actorId: currentUser.id,
              actorName: currentUser.name,
              targetType: 'health_consents',
              targetId: c.id,
              targetLabel: c.client_name,
              action: AUDIT_ACTION.CONSENT_SENT,
              metadata: { scope: c.scope, plan_types: c.plan_types, sent_via: c.sent_via },
            });
            showToast(`Consent link sent to ${c.client_name}.`);
          }}
          showToast={showToast}
        />
      )}

      {detail && (
        <ConsentDetailModal
          consent={detail}
          currentUser={currentUser}
          onClose={() => setDetail(null)}
          onRevoked={() => { setDetail(null); reload(); }}
          showToast={showToast}
        />
      )}
    </div>
  );
}

function ConsentComposeModal({ currentUser, settings = {}, onClose, onCreated, showToast }) {
  const [scope, setScope]       = React.useState('enrollment_call');
  const [name, setName]         = React.useState('');
  const [phone, setPhone]       = React.useState('');
  const [email, setEmail]       = React.useState('');
  const [dob, setDob]           = React.useState('');
  const [planTypes, setPlanTypes] = React.useState(['aca_marketplace']);
  const [sendVia, setSendVia]   = React.useState('sms'); // sms | email | both | link_copy
  const [text, setText]         = React.useState(DEFAULT_CONSENT_TEXT.enrollment_call);
  const [submitting, setSubmitting] = React.useState(false);

  // Agency / Agent identity block. Pre-fills from settings — the
  // agent can override per-send. Snapshotted onto the consent row.
  // Reads tolerantly so any field that isn't in settings yet just
  // shows as blank for the agent to fill in.
  const [agencyName, setAgencyName] = React.useState(settings.businessName || settings.agencyName || '');
  const [agencyNpn,  setAgencyNpn]  = React.useState(settings.agencyNpn  || '');
  const [agentName,  setAgentName]  = React.useState(settings.ownerName  || currentUser?.name || '');
  const [agentNpn,   setAgentNpn]   = React.useState(settings.ownerNpn   || settings.npn || '');
  const [agentPhone, setAgentPhone] = React.useState(settings.ownerPhone || settings.phone || '');
  const [agentEmail, setAgentEmail] = React.useState(settings.ownerEmail || currentUser?.email || '');

  React.useEffect(() => {
    setText(DEFAULT_CONSENT_TEXT[scope] || DEFAULT_CONSENT_TEXT.general);
  }, [scope]);

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const togglePlan = (pt) => setPlanTypes(prev =>
    prev.includes(pt) ? prev.filter(x => x !== pt) : [...prev, pt]
  );

  async function send() {
    if (!name.trim()) { showToast('Client name required.'); return; }
    if (sendVia === 'sms'   && !phone.trim()) { showToast('Phone required for SMS delivery.'); return; }
    if (sendVia === 'email' && !email.trim()) { showToast('Email required for email delivery.'); return; }
    setSubmitting(true);
    try {
      const c = await createConsent({
        ownerId: currentUser.id,
        clientName: name.trim(),
        clientDob:  dob || null,
        clientEmail:email.trim() || null,
        clientPhone:phone.trim() || null,
        planTypes,
        scope,
        consentText: text,
        sentVia: sendVia,
        agencyName: agencyName.trim() || null,
        agencyNpn:  agencyNpn.trim()  || null,
        agentName:  agentName.trim()  || null,
        agentNpn:   agentNpn.trim()   || null,
        agentPhone: agentPhone.trim() || null,
        agentEmail: agentEmail.trim() || null,
      });

      // SMS / email delivery hook. We try the lifecycle SMS pipeline
      // for SMS sends; for email or link-copy we just present the
      // URL to the agent and let them copy it manually.
      const link = c.signUrl;
      if (sendVia === 'sms' || sendVia === 'both') {
        try {
          const body = `${currentUser.name || 'Your agent'} sent you a quick consent form to sign for your health plan: ${link}`;
          const { data: { session } } = await supabase.auth.getSession();
          const jwt = session?.access_token;
          await fetch('/api/sms/send-manual', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
            body: JSON.stringify({ adhoc_phone: phone, body }),
          }).catch(() => null);
        } catch {}
      }
      onCreated(c);
    } catch (err) {
      showToast(`Send failed: ${err.message || err}`);
    } finally {
      setSubmitting(false);
    }
  }

  const PLAN_OPTIONS = [
    ['aca_marketplace',     'ACA Marketplace'],
    ['medicare_advantage',  'Medicare Advantage'],
    ['medicare_supplement', 'Medicare Supplement'],
    ['medicare_pdp',        'Medicare Part D'],
    ['private_ppo',         'Private PPO'],
    ['short_term',          'Short-Term Medical'],
    ['dental_vision',       'Dental / Vision'],
    ['hospital_indemnity',  'Hospital Indemnity'],
  ];

  return (
    <div className="fixed inset-0 bg-slate-900/70 z-50 flex items-center justify-center p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="bg-slate-900 border-l-4 border-teal-500 px-4 py-3 flex items-start justify-between gap-3">
          <div>
            <div className="text-teal-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">New Consent</div>
            <div className="text-white text-lg font-black uppercase tracking-tight">Send consent form</div>
          </div>
          <button aria-label="Close" onClick={onClose} className="text-white/60 hover:text-white">✕</button>
        </div>

        <div className="p-4 space-y-3">
          {/* Scope */}
          <label className="block">
            <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Consent type</span>
            <select value={scope} onChange={e => setScope(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm bg-white">
              {Object.entries(CONSENT_SCOPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>

          {/* Client identity */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Client name *</span>
              <input value={name} onChange={e => setName(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm" placeholder="Full legal name"/>
            </label>
            <label className="block">
              <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">DOB</span>
              <input type="date" value={dob} onChange={e => setDob(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm"/>
            </label>
            <label className="block">
              <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Phone</span>
              <input value={phone} onChange={e => setPhone(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm" placeholder="(555) 123-4567"/>
            </label>
            <label className="block">
              <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Email</span>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm" placeholder="name@example.com"/>
            </label>
          </div>

          {/* Plan types */}
          <div>
            <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500 mb-1.5">Plans authorized</div>
            <div className="flex flex-wrap gap-1.5">
              {PLAN_OPTIONS.map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => togglePlan(k)}
                  className={`text-xs font-bold uppercase tracking-tight px-2.5 py-1 border-2 ${planTypes.includes(k) ? 'bg-teal-600 text-white border-teal-700' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Agency / Agent identity block — pre-filled from settings,
              snapshotted onto the consent row. Matches the CMS form
              layout's "Company/Agent Representative" panel. */}
          <details className="bg-slate-50 border border-slate-200 p-3" open>
            <summary className="cursor-pointer text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Agency &amp; Agent identity · snapshotted on row</summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
              <label className="block">
                <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Name of Agency</span>
                <input value={agencyName} onChange={e => setAgencyName(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm"/>
              </label>
              <label className="block">
                <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Agency NPN</span>
                <input value={agencyNpn} onChange={e => setAgencyNpn(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm"/>
              </label>
              <label className="block">
                <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Name of Agent</span>
                <input value={agentName} onChange={e => setAgentName(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm"/>
              </label>
              <label className="block">
                <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Agent NPN</span>
                <input value={agentNpn} onChange={e => setAgentNpn(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm"/>
              </label>
              <label className="block">
                <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Phone Number</span>
                <input value={agentPhone} onChange={e => setAgentPhone(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm"/>
              </label>
              <label className="block">
                <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Email Address</span>
                <input value={agentEmail} onChange={e => setAgentEmail(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm"/>
              </label>
            </div>
          </details>

          {/* Disclosure text */}
          <label className="block">
            <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Disclosure text · client will read this</span>
            <textarea value={text} onChange={e => setText(e.target.value)} rows={8} className="mt-1 w-full border border-slate-300 px-2 py-2 text-xs leading-relaxed font-mono"/>
            <span className="block text-[0.55rem] text-slate-400 italic mt-1">Placeholders <code>{'{client_name}'}</code> and <code>{'{agent_name}'}</code> are filled in automatically on send. Default text for Enrollment / Quoting Consent is the canonical CMS FFM template — do not reword.</span>
          </label>

          {/* Delivery */}
          <div>
            <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500 mb-1.5">Send via</div>
            <div className="flex gap-1.5">
              {[['sms','SMS'], ['email','Email'], ['both','SMS + Email'], ['link_copy','Copy link only']].map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setSendVia(k)}
                  className={`text-xs font-bold uppercase tracking-tight px-3 py-1.5 border-2 ${sendVia === k ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
            <button aria-label="Close" onClick={onClose} className="text-sm text-slate-600 hover:text-slate-900 px-3 py-2">Cancel</button>
            <button
              onClick={send}
              disabled={submitting}
              className="bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-xs font-black uppercase tracking-tight px-4 py-2"
            >
              {submitting ? 'Sending…' : 'Generate + Send link'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConsentDetailModal({ consent, currentUser, onClose, onRevoked, showToast }) {
  const [revoking, setRevoking] = React.useState(false);
  const [reason, setReason]     = React.useState('');
  const [copied, setCopied]     = React.useState(false);

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function doRevoke() {
    try {
      await revokeConsent({ consentId: consent.id, ownerId: currentUser.id, reason });
      audit({
        ownerId: currentUser.id,
        actorId: currentUser.id,
        actorName: currentUser.name,
        targetType: 'health_consents',
        targetId: consent.id,
        targetLabel: consent.client_name,
        action: AUDIT_ACTION.CONSENT_REVOKED,
        metadata: { reason },
      });
      showToast('Consent revoked.');
      onRevoked();
    } catch (err) {
      showToast(`Revoke failed: ${err.message || err}`);
    }
  }

  function copyLink() {
    const url = buildSignUrl(consent.token);
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => showToast('Clipboard blocked — link: ' + url));
  }

  return (
    <div className="fixed inset-0 bg-slate-900/70 z-50 flex items-center justify-center p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="bg-slate-900 border-l-4 border-teal-500 px-4 py-3 flex items-start justify-between gap-3">
          <div>
            <div className="text-teal-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">Consent Detail</div>
            <div className="text-white text-lg font-black uppercase tracking-tight">{consent.client_name}</div>
          </div>
          <button aria-label="Close" onClick={onClose} className="text-white/60 hover:text-white">✕</button>
        </div>

        <div className="p-4 space-y-3">
          <div className="flex items-baseline justify-between flex-wrap gap-3">
            <ConsentBadge status={consent.status} signedAt={consent.signed_at}/>
            <div className="text-[0.6rem] uppercase tracking-wider text-slate-500">{CONSENT_SCOPE_LABEL[consent.scope] || consent.scope}</div>
          </div>

          {/* Receipts */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <Receipt label="Sent" value={consent.sent_at ? new Date(consent.sent_at).toLocaleString() : '—'}/>
            <Receipt label="Sent via" value={consent.sent_via || '—'}/>
            <Receipt label="Expires" value={consent.expires_at ? new Date(consent.expires_at).toLocaleString() : '—'}/>
            <Receipt label="Signed at" value={consent.signed_at ? new Date(consent.signed_at).toLocaleString() : '—'}/>
            <Receipt label="IP" value={consent.signed_ip || '—'}/>
            <Receipt label="Device fingerprint" value={consent.device_fingerprint ? consent.device_fingerprint.slice(0, 16) + '…' : '—'}/>
            <Receipt label="User-Agent" value={consent.signed_user_agent || '—'} mono full/>
            {consent.signed_geolocation && (
              <Receipt label="Approx location" value={`${consent.signed_geolocation.lat?.toFixed?.(3)}, ${consent.signed_geolocation.lng?.toFixed?.(3)} · ±${Math.round(consent.signed_geolocation.accuracy)}m`} full/>
            )}
          </div>

          {/* Disclosure text snapshot */}
          <details className="bg-slate-50 border border-slate-200 p-3">
            <summary className="cursor-pointer text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Disclosure text shown to client</summary>
            <div className="text-xs text-slate-700 mt-2 leading-relaxed whitespace-pre-wrap">{consent.consent_text}</div>
          </details>

          {/* Signature */}
          {consent.signature_data_url && (
            <div className="bg-slate-50 border border-slate-200 p-3">
              <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500 mb-2">Signature on file</div>
              <img src={consent.signature_data_url} alt="Client signature" className="bg-white border border-slate-300 max-h-32"/>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-200 flex-wrap">
            <div className="flex gap-2 flex-wrap">
              <button onClick={copyLink} className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold uppercase tracking-tight px-3 py-2">
                {copied ? 'Copied ✓' : 'Copy sign URL'}
              </button>
              {consent.status === 'signed' && (
                <button onClick={() => printReceipt(consent)} className="bg-slate-900 hover:bg-black text-white text-xs font-bold uppercase tracking-tight px-3 py-2">
                  Print receipt (PDF)
                </button>
              )}
            </div>
            {consent.status === 'signed' && !revoking && (
              <button onClick={() => setRevoking(true)} className="bg-rose-100 hover:bg-rose-200 text-rose-800 text-xs font-bold uppercase tracking-tight px-3 py-2">
                Revoke consent
              </button>
            )}
          </div>

          {revoking && (
            <div className="bg-rose-50 border-2 border-rose-300 p-3">
              <div className="text-[0.55rem] font-black uppercase tracking-wider text-rose-700 mb-2">Revoking consent</div>
              <input
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Reason (optional — kept on the audit trail)"
                className="w-full border border-rose-300 px-2 py-2 text-xs"
              />
              <div className="flex items-center justify-end gap-2 mt-2">
                <button onClick={() => setRevoking(false)} className="text-xs text-slate-600 hover:text-slate-900 px-2 py-1">Cancel</button>
                <button onClick={doRevoke} className="bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold uppercase tracking-tight px-3 py-1.5">Confirm revoke</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// printReceipt — opens a new window with a print-styled, single-page
// rendering of the signed consent that mirrors the canonical CMS
// FFM form. The agent uses Save-as-PDF in the browser print dialog
// to download a compliance-archivable copy.
function printReceipt(consent) {
  const fmtSignDate = (iso) => formatSignDateLong(iso) || iso || '—';
  const fmtDob = (iso) => {
    if (!iso) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return m ? `${m[2]}/${m[3]}/${m[1]}` : iso;
  };
  const esc = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  const html = `<!doctype html><html><head>
<meta charset="utf-8"/>
<title>Consent receipt — ${esc(consent.client_name || '')}</title>
<style>
  *{box-sizing:border-box}
  body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif;color:#0f172a;margin:0;padding:48px;line-height:1.6;font-size:11pt}
  h1,h2,h3{margin:0 0 10px 0;font-weight:600}
  h3{font-size:13pt;margin-top:0}
  .body{white-space:pre-wrap;margin:0 0 24px 0}
  .grid{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:24px}
  .block{border:1px solid #cbd5e1;padding:14px}
  .block .row{margin:0 0 6px 0;font-size:10.5pt}
  .block strong{font-weight:600}
  .sig{margin-top:8px;border:1px solid #cbd5e1;background:#fff;padding:8px;display:inline-block}
  .sig img{max-height:120px;display:block}
  .meta{font-size:10pt;color:#334155;margin-top:6px}
  hr{border:none;border-top:1px solid #cbd5e1;margin:18px 0}
  @media print{ body{padding:24px} button{display:none} }
</style>
</head><body>
<div class="body">${esc(consent.consent_text || '')}</div>
<div class="grid">
  <div class="block">
    <h3>Company / Agent Representative</h3>
    ${consent.agency_name  ? `<div class="row"><strong>Name of Agency:</strong> ${esc(consent.agency_name)}</div>`  : ''}
    ${consent.agency_npn   ? `<div class="row"><strong>Agency NPN:</strong> ${esc(consent.agency_npn)}</div>`       : ''}
    ${consent.agent_name   ? `<div class="row"><strong>Name of Agent:</strong> ${esc(consent.agent_name)}</div>`    : ''}
    ${consent.agent_npn    ? `<div class="row"><strong>Agent NPN:</strong> ${esc(consent.agent_npn)}</div>`         : ''}
    ${consent.agent_phone  ? `<div class="row"><strong>Phone Number:</strong> ${esc(consent.agent_phone)}</div>`    : ''}
    ${consent.agent_email  ? `<div class="row"><strong>Email Address:</strong> ${esc(consent.agent_email)}</div>`   : ''}
  </div>
  <div class="block">
    <h3>Household Contact and/or Authorized Representative</h3>
    <div class="row"><strong>Full Name:</strong> ${esc(consent.client_name || '')}</div>
    ${consent.client_phone ? `<div class="row"><strong>Phone Number:</strong> ${esc(consent.client_phone)}</div>`    : ''}
    ${consent.client_email ? `<div class="row"><strong>Email Address:</strong> ${esc(consent.client_email)}</div>`   : ''}
    ${consent.client_dob   ? `<div class="row"><strong>Date of Birth:</strong> ${esc(fmtDob(consent.client_dob))}</div>` : ''}
    ${consent.signed_ip    ? `<div class="row"><strong>Submission IP:</strong> ${esc(consent.signed_ip)}</div>`      : ''}
  </div>
</div>
<h2 style="font-size:14pt;font-weight:700;margin-top:12px">Signature:</h2>
${consent.signature_data_url ? `<div class="sig"><img src="${esc(consent.signature_data_url)}" alt="Signature"/></div>` : `<div class="meta">(no signature on file)</div>`}
<div class="meta" style="margin-top:14px"><strong>Sign Date:</strong> ${esc(fmtSignDate(consent.signed_at))}</div>
${consent.signed_ip ? `<div class="meta"><strong>IP Address:</strong> ${esc(consent.signed_ip)}</div>` : ''}
<hr/>
<div class="meta">${esc(({
  enrollment_call:  'CMS Federally-Facilitated Marketplace consent.',
  sms_marketing:    'SMS marketing (TCPA / A2P) authorization.',
  plan_of_record:   'Plan of Record (Agent of Record) appointment.',
  hipaa:            'HIPAA authorization.',
  general:          'General authorization.',
})[consent.scope] || '')} Recorded by the agent's CRM with cryptographic integrity. Status: ${esc((consent.status || '').toUpperCase())}.</div>
<script>window.addEventListener('load', function(){ setTimeout(function(){ window.print(); }, 250); });</script>
</body></html>`;
  const w = window.open('', '_blank', 'width=900,height=1100');
  if (!w) return;
  w.document.write(html);
  w.document.close();
}

function Receipt({ label, value, mono = false, full = false }) {
  return (
    <div className={`bg-slate-50 border border-slate-200 p-2 ${full ? 'col-span-2' : ''}`}>
      <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-slate-800 mt-0.5 truncate ${mono ? 'font-mono text-[0.6rem]' : 'text-xs'}`} title={value}>{value}</div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// AUDIT LOG VIEW  (Priority 5 — agent activity audit)
// ════════════════════════════════════════════════════════════════════
function HealthAuditLogView({ currentUser }) {
  const [rows, setRows]       = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [actionFilter, setActionFilter] = React.useState('all');

  React.useEffect(() => {
    let alive = true;
    if (!currentUser?.id) { setLoading(false); return; }
    setLoading(true);
    fetchOwnerActivity({ limit: 500 })
      .then(r => { if (alive) { setRows(r); setLoading(false); } })
      .catch(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [currentUser?.id]);

  const filtered = actionFilter === 'all' ? rows : rows.filter(r => r.action === actionFilter);
  const actionCounts = rows.reduce((acc, r) => {
    acc[r.action] = (acc[r.action] || 0) + 1;
    return acc;
  }, {});

  // Group actions into families for the filter pill row.
  const FAMILY = {
    'Consent':  ['consent_sent','consent_signed','consent_declined','consent_revoked','consent_expired'],
    'Calls':    ['call','sms_sent','sms_received','email_sent'],
    'Policy':   ['policy_created','policy_updated','policy_deleted','policy_renewed','policy_cancelled'],
    'Pipeline': ['lead_status','lead_created','lead_converted'],
    'Workflow': ['workflow_enroll','workflow_step','workflow_complete','workflow_exit'],
  };

  return (
    <div className="space-y-4">
      <div className="bg-black border-l-4 border-violet-500 p-4">
        <div className="text-violet-300 text-[0.6rem] font-black uppercase tracking-[0.3em]">Audit Log</div>
        <div className="text-white text-2xl font-black uppercase tracking-tight">Every action, timestamped + attributed</div>
        <div className="text-slate-400 text-xs mt-1">{rows.length} events on record · used for E&amp;O defense, ops review, and replay when a client says "you never called me".</div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setActionFilter('all')} className={`text-xs font-bold uppercase tracking-tight px-3 py-1.5 border-2 ${actionFilter === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200'}`}>
          All ({rows.length})
        </button>
        {Object.entries(FAMILY).map(([fam, actions]) => {
          const count = actions.reduce((s, a) => s + (actionCounts[a] || 0), 0);
          return (
            <details key={fam} className="inline-block">
              <summary className={`text-xs font-bold uppercase tracking-tight px-3 py-1.5 border-2 cursor-pointer ${actions.includes(actionFilter) ? 'bg-violet-600 text-white border-violet-700' : 'bg-white text-slate-700 border-slate-200'}`}>
                {fam} ({count}) ▾
              </summary>
              <div className="absolute z-10 bg-white border border-slate-200 shadow-lg mt-1 p-1 min-w-[180px]">
                {actions.map(a => (
                  <button
                    key={a}
                    onClick={() => setActionFilter(a)}
                    className={`block w-full text-left text-xs px-2 py-1 hover:bg-slate-100 ${actionFilter === a ? 'bg-slate-900 text-white' : ''}`}
                  >
                    {a.replace(/_/g, ' ')} ({actionCounts[a] || 0})
                  </button>
                ))}
              </div>
            </details>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {['When', 'Actor', 'Action', 'Target', 'Detail', 'IP'].map(h => (
                <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan="6" className="px-3 py-8 text-center text-sm text-slate-400 italic">Loading audit log…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan="6" className="px-3 py-8 text-center text-sm text-slate-400 italic">No audit events yet. Actions you take in the Health workspace will start landing here.</td></tr>
            )}
            {filtered.map(r => (
              <tr key={r.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 text-xs font-mono text-slate-500 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs text-slate-700 whitespace-nowrap">{r.actor_name || '—'}</td>
                <td className="px-3 py-2 text-[0.6rem] font-black uppercase tracking-wider text-violet-700 whitespace-nowrap">{r.action.replace(/_/g, ' ')}</td>
                <td className="px-3 py-2 text-xs text-slate-700">{r.target_label || r.target_id?.slice(0, 8) || r.target_type}</td>
                <td className="px-3 py-2 text-[0.6rem] text-slate-500 font-mono max-w-xs truncate" title={JSON.stringify(r.metadata)}>{Object.keys(r.metadata || {}).length > 0 ? JSON.stringify(r.metadata) : ''}</td>
                <td className="px-3 py-2 text-[0.6rem] text-slate-400 font-mono">{r.ip_address || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Embedded variant — shown inside a client / lead drawer.
function AuditLogTimeline({ targetType, targetId, limit = 50 }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchAuditTimeline({ targetType, targetId, limit })
      .then(r => { if (alive) { setRows(r); setLoading(false); } });
    return () => { alive = false; };
  }, [targetType, targetId, limit]);
  if (loading) return <div className="text-xs text-slate-400 italic text-center py-3">Loading timeline…</div>;
  if (rows.length === 0) return <div className="text-xs text-slate-400 italic text-center py-3">No activity recorded yet.</div>;
  return (
    <div className="space-y-1.5 max-h-72 overflow-auto">
      {rows.map(r => (
        <div key={r.id} className="flex items-start gap-2 text-xs border-l-2 border-violet-300 pl-2 py-0.5">
          <span className="text-[0.55rem] font-mono text-slate-400 whitespace-nowrap w-32 shrink-0">{new Date(r.created_at).toLocaleString()}</span>
          <span className="text-[0.6rem] font-black uppercase tracking-wider text-violet-700 whitespace-nowrap">{r.action.replace(/_/g, ' ')}</span>
          <span className="text-slate-600 truncate">{r.actor_name || ''}</span>
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// VISUAL WORKFLOW BUILDER  (Priority 3 — automation upgrade)
//
// JSON-graph builder. The agent picks a trigger, then chains nodes
// (SMS / Delay / Branch / Flag / Task / Exit). The chain renders
// top-to-bottom with insert-here buttons between every pair.
//
// This is the v1 of the visual builder — a clean linear-with-
// branches editor. A free-form graph editor (react-flow) is the
// natural v2 once the agent base proves out the v1 mental model.
// ════════════════════════════════════════════════════════════════════

const WORKFLOW_TRIGGERS = [
  { kind: 'lead_inactive_days',    label: 'Lead hasn\'t responded in N days', defaults: { days: 3 } },
  { kind: 'consent_signed',        label: 'Consent signed',                   defaults: {} },
  { kind: 'policy_effectuated',    label: 'Policy effectuated',               defaults: {} },
  { kind: 'lead_status_change',    label: 'Lead status changes to',           defaults: { to: 'appt_set' } },
  { kind: 'aep_window_opens',      label: 'AEP window opens (Oct 15)',        defaults: {} },
];

const WORKFLOW_NODES = [
  { kind: 'sms',    label: 'Send SMS',         defaults: { templateKey: '' } },
  { kind: 'email',  label: 'Send Email',       defaults: { subject: '', body: '' } },
  { kind: 'delay',  label: 'Wait',             defaults: { hours: 48 } },
  { kind: 'branch', label: 'Branch on',        defaults: { condition: 'lead_replied', yesNode: '', noNode: '' } },
  { kind: 'flag',   label: 'Flag with tag',    defaults: { tag: 'needs_agent' } },
  { kind: 'task',   label: 'Create task',      defaults: { title: 'Call lead back' } },
  { kind: 'exit',   label: 'End workflow',     defaults: { reason: 'completed' } },
];

function HealthWorkflowsView({ currentUser, showToast = () => {} }) {
  const [workflows, setWorkflows] = React.useState([]);
  const [loading, setLoading]     = React.useState(true);
  const [editing, setEditing]     = React.useState(null);

  const reload = React.useCallback(async () => {
    if (!currentUser?.id) { setLoading(false); return; }
    setLoading(true);
    const { data } = await supabase.from('workflows').select('*').order('updated_at', { ascending: false });
    setWorkflows(data || []);
    setLoading(false);
  }, [currentUser?.id]);

  React.useEffect(() => { reload(); }, [reload]);

  async function createNew() {
    const def = {
      trigger: { kind: 'lead_inactive_days', days: 3 },
      nodes:   [{ id: 'n1', kind: 'sms', templateKey: 'follow_up_3d' }],
      edges:   [['start', 'n1']],
    };
    const { data, error } = await supabase
      .from('workflows')
      .insert({ owner_id: currentUser.id, name: 'New workflow', vertical: 'health', status: 'draft', definition: def })
      .select().single();
    if (error) { showToast(`Create failed: ${error.message}`); return; }
    setEditing(data);
    reload();
  }

  async function toggleActive(w) {
    const next = w.status === 'active' ? 'paused' : 'active';
    await supabase.from('workflows').update({ status: next }).eq('id', w.id);
    reload();
  }

  return (
    <div className="space-y-4">
      <div className="bg-black border-l-4 border-sky-500 p-4">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="text-sky-300 text-[0.6rem] font-black uppercase tracking-[0.3em]">Automations</div>
            <div className="text-white text-2xl font-black uppercase tracking-tight">Visual workflow builder</div>
            <div className="text-slate-400 text-xs mt-1">If/then chains for follow-up, retention, AEP outreach, lapse-rescue.</div>
          </div>
          <button onClick={createNew} className="bg-sky-600 hover:bg-sky-700 text-white text-xs font-black uppercase tracking-tight px-4 py-2">+ New Workflow</button>
        </div>
      </div>

      {/* Visual map — one flow-diagram per workflow so operators
          SEE what each automation does at a glance, not just its
          name in a table. */}
      {!loading && workflows.length > 0 && (
        <div className="bg-white border border-slate-200 p-4 space-y-4">
          <div className="text-[0.55rem] font-black uppercase tracking-[0.2em] text-slate-500">Visual map · {workflows.length} workflow{workflows.length === 1 ? '' : 's'}</div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {workflows.map(w => (
              <WorkflowFlowCard
                key={w.id}
                workflow={w}
                onEdit={() => setEditing(w)}
                onToggle={() => toggleActive(w)}
              />
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {['Name', 'Trigger', 'Steps', 'Enrolled', 'Completed', 'Status', ''].map(h => (
                <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan="7" className="px-3 py-8 text-center text-sm text-slate-400 italic">Loading…</td></tr>}
            {!loading && workflows.length === 0 && (
              <tr><td colSpan="7" className="px-3 py-8 text-center text-sm text-slate-400 italic">No automations yet. Click "+ New Workflow" to start.</td></tr>
            )}
            {workflows.map(w => (
              <tr key={w.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 font-bold text-slate-900 cursor-pointer" onClick={() => setEditing(w)}>{w.name}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{(w.definition?.trigger?.kind || '—').replace(/_/g, ' ')}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{(w.definition?.nodes || []).length}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{w.enrolled_count || 0}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{w.completed_count || 0}</td>
                <td className="px-3 py-2">
                  <button onClick={() => toggleActive(w)} className={`text-[0.55rem] font-black uppercase tracking-wider px-2 py-0.5 ${w.status === 'active' ? 'bg-emerald-100 text-emerald-800' : w.status === 'paused' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}`}>
                    {w.status}
                  </button>
                </td>
                <td className="px-3 py-2 text-[0.55rem] uppercase tracking-wider text-sky-700 cursor-pointer" onClick={() => setEditing(w)}>Edit →</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <WorkflowEditorModal
          workflow={editing}
          currentUser={currentUser}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
          showToast={showToast}
        />
      )}
    </div>
  );
}

// One workflow rendered as a horizontal flow chart. Trigger → nodes →
// end. Each node badge shows its kind (SMS / email / wait / condition /
// AI / call) and a one-line description of what it does. Compact
// enough that a book of 20 workflows fits on one screen.
function WorkflowFlowCard({ workflow, onEdit, onToggle }) {
  const def   = workflow.definition || {};
  const nodes = Array.isArray(def.nodes)   ? def.nodes   : [];
  const trig  = def.trigger || {};
  const trigLabel = String(trig.kind || 'manual').replace(/_/g, ' ');
  const status = workflow.status || 'draft';

  const KIND_META = {
    sms:        { label: 'SMS',       tone: 'bg-emerald-100 text-emerald-800 border-emerald-300', dot: 'bg-emerald-500' },
    email:      { label: 'Email',     tone: 'bg-sky-100 text-sky-800 border-sky-300',             dot: 'bg-sky-500' },
    wait:       { label: 'Wait',      tone: 'bg-slate-100 text-slate-700 border-slate-300',       dot: 'bg-slate-400' },
    condition:  { label: 'If',        tone: 'bg-amber-100 text-amber-800 border-amber-300',       dot: 'bg-amber-500' },
    call:       { label: 'Call',      tone: 'bg-violet-100 text-violet-800 border-violet-300',    dot: 'bg-violet-500' },
    ai:         { label: 'AI',        tone: 'bg-indigo-100 text-indigo-800 border-indigo-300',    dot: 'bg-indigo-500' },
    task:       { label: 'Task',      tone: 'bg-rose-100 text-rose-800 border-rose-300',          dot: 'bg-rose-500' },
    tag:        { label: 'Tag',       tone: 'bg-teal-100 text-teal-800 border-teal-300',          dot: 'bg-teal-500' },
    end:        { label: 'End',       tone: 'bg-slate-900 text-white border-slate-900',           dot: 'bg-slate-900' },
  };

  const statusCls = status === 'active' ? 'bg-emerald-100 text-emerald-800'
                  : status === 'paused' ? 'bg-amber-100 text-amber-800'
                  :                        'bg-slate-100 text-slate-700';

  const nodeSummary = (n) => {
    if (n.templateKey) return String(n.templateKey).replace(/_/g, ' ');
    if (n.kind === 'wait' && n.days) return `${n.days}d`;
    if (n.kind === 'wait' && n.hours) return `${n.hours}h`;
    if (n.kind === 'condition' && n.field) return `${n.field} ${n.op || ''} ${n.value || ''}`.trim();
    if (n.kind === 'tag' && n.tag) return String(n.tag);
    return '';
  };

  return (
    <div className="border border-slate-200 bg-white p-3 flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="text-sm font-black text-slate-900 truncate flex-1 min-w-0">{workflow.name || '(unnamed)'}</div>
        <span className={`text-[0.55rem] font-black uppercase tracking-wider px-2 py-0.5 ${statusCls}`}>{status}</span>
        <div className="flex gap-1">
          <button onClick={onToggle} className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500 hover:text-slate-900 px-1.5 py-0.5 border border-slate-200">{status === 'active' ? 'Pause' : 'Activate'}</button>
          <button onClick={onEdit}   className="text-[0.55rem] font-black uppercase tracking-wider text-sky-700 hover:text-sky-900 px-1.5 py-0.5 border border-sky-200">Edit</button>
        </div>
      </div>

      {/* Flow row — Trigger → node → node → End */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1" role="list">
        <div className="shrink-0 border border-slate-900 bg-slate-900 text-white px-2 py-1 text-[0.55rem] font-black uppercase tracking-wider" title={`Trigger: ${trigLabel}`}>
          ⚡ {trigLabel}
        </div>
        {nodes.length === 0 ? (
          <>
            <div className="text-slate-300 text-lg leading-none">→</div>
            <div className="shrink-0 border border-dashed border-slate-300 text-slate-400 px-2 py-1 text-[0.55rem] uppercase tracking-wider italic">
              no steps yet
            </div>
          </>
        ) : nodes.slice(0, 8).map((n, i) => {
          const meta = KIND_META[n.kind] || { label: n.kind || '?', tone: 'bg-slate-100 text-slate-700 border-slate-300' };
          const sum = nodeSummary(n);
          return (
            <React.Fragment key={n.id || i}>
              <div className="text-slate-300 text-lg leading-none shrink-0">→</div>
              <div className={`shrink-0 border ${meta.tone} px-2 py-1 text-[0.6rem] font-bold uppercase tracking-wider max-w-[140px] truncate`} title={`${meta.label}${sum ? ' · ' + sum : ''}`}>
                {meta.label}{sum && <span className="ml-1 font-normal opacity-80 normal-case tracking-normal">· {sum}</span>}
              </div>
            </React.Fragment>
          );
        })}
        {nodes.length > 8 && (
          <>
            <div className="text-slate-300 text-lg leading-none shrink-0">→</div>
            <div className="shrink-0 text-[0.55rem] uppercase tracking-wider text-slate-500 italic">
              +{nodes.length - 8} more
            </div>
          </>
        )}
        <div className="text-slate-300 text-lg leading-none shrink-0">→</div>
        <div className="shrink-0 border border-slate-900 bg-slate-900 text-white px-2 py-1 text-[0.55rem] font-black uppercase tracking-wider">
          ✓ End
        </div>
      </div>

      {/* Footer — enrolled / completed counts + step count */}
      <div className="flex items-center gap-3 text-[0.6rem] font-mono text-slate-500 pt-1 border-t border-slate-100">
        <span>Steps <strong className="text-slate-900 tabular-nums">{nodes.length}</strong></span>
        <span>Enrolled <strong className="text-slate-900 tabular-nums">{workflow.enrolled_count || 0}</strong></span>
        <span>Completed <strong className="text-slate-900 tabular-nums">{workflow.completed_count || 0}</strong></span>
      </div>
    </div>
  );
}

function WorkflowEditorModal({ workflow, currentUser, onClose, onSaved, showToast }) {
  const [name, setName]     = React.useState(workflow.name);
  const [def, setDef]       = React.useState(workflow.definition || { trigger: { kind: 'lead_inactive_days', days: 3 }, nodes: [], edges: [] });
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function updateTrigger(patch) {
    setDef(prev => ({ ...prev, trigger: { ...prev.trigger, ...patch } }));
  }

  function addNode(kind, atIdx) {
    const tmpl = WORKFLOW_NODES.find(n => n.kind === kind);
    const id = 'n' + (def.nodes.length + 1);
    const node = { id, kind, ...tmpl.defaults };
    setDef(prev => {
      const nodes = [...prev.nodes];
      const at = atIdx == null ? nodes.length : atIdx;
      nodes.splice(at, 0, node);
      return { ...prev, nodes };
    });
  }

  function updateNode(idx, patch) {
    setDef(prev => {
      const nodes = [...prev.nodes];
      nodes[idx] = { ...nodes[idx], ...patch };
      return { ...prev, nodes };
    });
  }

  function removeNode(idx) {
    setDef(prev => ({ ...prev, nodes: prev.nodes.filter((_, i) => i !== idx) }));
  }

  async function save() {
    setSaving(true);
    const { error } = await supabase
      .from('workflows')
      .update({ name, definition: def })
      .eq('id', workflow.id);
    setSaving(false);
    if (error) { showToast(`Save failed: ${error.message}`); return; }
    showToast('Saved.');
    onSaved();
  }

  return (
    <div className="fixed inset-0 bg-slate-900/70 z-50 flex items-center justify-center p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="bg-slate-900 border-l-4 border-sky-500 px-4 py-3 flex items-start justify-between gap-3">
          <input value={name} onChange={e => setName(e.target.value)} className="bg-transparent text-white text-lg font-black uppercase tracking-tight border-b border-white/30 focus:border-sky-400 focus:outline-none flex-1"/>
          <button aria-label="Close" onClick={onClose} className="text-white/60 hover:text-white">✕</button>
        </div>

        <div className="p-4 space-y-3">
          {/* Trigger */}
          <div className="bg-amber-50 border-2 border-amber-300 p-3">
            <div className="text-[0.55rem] font-black uppercase tracking-wider text-amber-700 mb-1.5">When this happens (trigger)</div>
            <select
              value={def.trigger?.kind || 'lead_inactive_days'}
              onChange={e => {
                const t = WORKFLOW_TRIGGERS.find(x => x.kind === e.target.value);
                updateTrigger({ kind: e.target.value, ...(t?.defaults || {}) });
              }}
              className="w-full border border-amber-300 px-2 py-2 text-sm bg-white"
            >
              {WORKFLOW_TRIGGERS.map(t => <option key={t.kind} value={t.kind}>{t.label}</option>)}
            </select>
            {def.trigger?.kind === 'lead_inactive_days' && (
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span>Days inactive:</span>
                <input type="number" value={def.trigger.days || 3} onChange={e => updateTrigger({ days: Number(e.target.value) || 3 })} className="w-20 border border-amber-300 px-2 py-1"/>
              </div>
            )}
            {def.trigger?.kind === 'lead_status_change' && (
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span>New status:</span>
                <input value={def.trigger.to || ''} onChange={e => updateTrigger({ to: e.target.value })} className="flex-1 border border-amber-300 px-2 py-1"/>
              </div>
            )}
          </div>

          {/* Steps */}
          <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Then run these steps</div>
          {def.nodes.length === 0 && (
            <div className="bg-slate-50 border-2 border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 italic">
              Add your first step ↓
            </div>
          )}
          {def.nodes.map((node, idx) => (
            <div key={node.id} className="bg-white border-2 border-slate-200 p-3">
              <div className="flex items-baseline justify-between mb-2">
                <div className="text-[0.55rem] font-black uppercase tracking-wider text-sky-700">{idx + 1}. {WORKFLOW_NODES.find(n => n.kind === node.kind)?.label || node.kind}</div>
                <button onClick={() => removeNode(idx)} className="text-[0.55rem] font-bold text-rose-600 hover:text-rose-800 uppercase tracking-wider">Remove</button>
              </div>
              {node.kind === 'sms' && (
                <input value={node.templateKey || ''} onChange={e => updateNode(idx, { templateKey: e.target.value })} placeholder="Template key (e.g. follow_up_3d)" className="w-full border border-slate-300 px-2 py-1.5 text-xs"/>
              )}
              {node.kind === 'email' && (
                <div className="space-y-1.5">
                  <input value={node.subject || ''} onChange={e => updateNode(idx, { subject: e.target.value })} placeholder="Subject" className="w-full border border-slate-300 px-2 py-1.5 text-xs"/>
                  <textarea value={node.body || ''} onChange={e => updateNode(idx, { body: e.target.value })} placeholder="Body" rows={3} className="w-full border border-slate-300 px-2 py-1.5 text-xs"/>
                </div>
              )}
              {node.kind === 'delay' && (
                <div className="flex items-center gap-2 text-xs">
                  <span>Wait</span>
                  <input type="number" value={node.hours || 48} onChange={e => updateNode(idx, { hours: Number(e.target.value) || 1 })} className="w-20 border border-slate-300 px-2 py-1"/>
                  <span>hours, then continue.</span>
                </div>
              )}
              {node.kind === 'branch' && (
                <div className="text-xs">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span>If</span>
                    <input value={node.condition || ''} onChange={e => updateNode(idx, { condition: e.target.value })} placeholder="lead_replied" className="flex-1 border border-slate-300 px-2 py-1"/>
                  </div>
                  <div className="text-slate-500 italic">YES exits workflow; NO continues to next step.</div>
                </div>
              )}
              {node.kind === 'flag' && (
                <input value={node.tag || ''} onChange={e => updateNode(idx, { tag: e.target.value })} placeholder="needs_agent" className="w-full border border-slate-300 px-2 py-1.5 text-xs"/>
              )}
              {node.kind === 'task' && (
                <input value={node.title || ''} onChange={e => updateNode(idx, { title: e.target.value })} placeholder="Call lead back" className="w-full border border-slate-300 px-2 py-1.5 text-xs"/>
              )}
            </div>
          ))}

          {/* Add step row */}
          <div className="bg-sky-50 border-2 border-dashed border-sky-300 p-3 flex items-center gap-2 flex-wrap">
            <span className="text-[0.55rem] font-black uppercase tracking-wider text-sky-700">Add step:</span>
            {WORKFLOW_NODES.map(n => (
              <button
                key={n.kind}
                onClick={() => addNode(n.kind)}
                className="bg-white border border-sky-300 hover:bg-sky-100 text-sky-700 text-[0.6rem] font-bold uppercase tracking-tight px-2.5 py-1"
              >
                {n.label}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
            <button aria-label="Close" onClick={onClose} className="text-sm text-slate-600 hover:text-slate-900 px-3 py-2">Cancel</button>
            <button onClick={save} disabled={saving} className="bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white text-xs font-black uppercase tracking-tight px-4 py-2">
              {saving ? 'Saving…' : 'Save workflow'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// HEALTH ENROLLMENT PIPELINE
//
// The biggest structural gap between the Life and Health workspaces:
// Life had a full pending → placed underwriting funnel; Health had no
// equivalent. This view fills that gap. It is a READ-VIEW over the
// existing health_policies + soa_records tables (no new migration);
// stages are computed from `status`, `effective_date`, `created_at`
// and `last_commission_at` — fields we already track.
//
// Two pipelines, two shapes:
//
//   ACA / Marketplace                  Medicare
//   ─────────────────                  ────────
//   Applied                            SOA Signed
//     ↓                                  ↓
//   Effectuated                        App Submitted (<14d)
//     ↓                                  ↓
//   Active (carrier paying)            CMS Processing (>14d, RED)
//                                        ↓
//                                      Enrolled
//
// "Stuck" detection — anything pending for more than 14 days flips
// rose. For Medicare, that's a CMS processing flag (something's wrong
// at the carrier or in the app). For ACA, it's a placement risk.
//
// CTA per card flips status forward. We DON'T let the agent skip
// stages — the carrier sets effectuation; we just record it.
// ════════════════════════════════════════════════════════════════════

function HealthEnrollmentPipelineView({ currentUser, settings = {}, onGotoTab = () => {}, showToast = () => {}, openClientProfile = () => {} }) {
  const [policies, setPolicies]     = React.useState([]);
  const [soaRecords, setSoaRecords] = React.useState([]);
  const [loading, setLoading]       = React.useState(true);
  const [vertical, setVertical]     = React.useState(() => {
    try { return localStorage.getItem('enrollPipeline:vertical') || 'both'; }
    catch { return 'both'; }
  });
  React.useEffect(() => {
    try { localStorage.setItem('enrollPipeline:vertical', vertical); } catch {}
  }, [vertical]);

  const reload = React.useCallback(async () => {
    setLoading(true);
    // Don't ORDER BY a column that may not exist (updated_at landed in
    // some migrations but not others). created_at is on every row.
    const [polRes, soaRes] = await Promise.all([
      supabase.from('health_policies').select('*').order('created_at', { ascending: false }),
      supabase.from('soa_records').select('id, client_id, beneficiary_name, signed_at, products, contact_method').order('signed_at', { ascending: false }).limit(200),
    ]);
    if (polRes.error) {
      // eslint-disable-next-line no-console
      console.warn('[EnrollmentPipeline] health_policies load failed:', polRes.error.message);
    }
    setPolicies(polRes.data || []);
    setSoaRecords(soaRes.data || []);
    setLoading(false);
  }, []);
  React.useEffect(() => { reload(); }, [reload]);

  // More resilient plan-vertical detection. The strict plan_type
  // enum-list miss rate is high in practice: HealthSherpa sets
  // 'aca_marketplace' but Jarvis BoB rows + manually-added rows
  // sometimes carry null plan_type or free-text. Fall back to
  // plan_name regex matching so the buckets actually fill.
  const isAcaPlan = (p) => {
    if (!p) return false;
    const pt = (p.plan_type || '').toLowerCase();
    if (['aca_marketplace', 'private_ppo', 'short_term'].includes(pt)) return true;
    const name = `${p.plan_name || ''} ${p.carrier || ''}`.toLowerCase();
    if (/aca|marketplace|ffm|on.?exchange|silver|bronze|gold|platinum|ambetter|oscar|molina/.test(name)) return true;
    return false;
  };
  const isMedicarePlan = (p) => {
    if (!p) return false;
    const pt = (p.plan_type || '').toLowerCase();
    if (['medicare_advantage', 'medicare_supplement', 'medicare_pdp'].includes(pt)) return true;
    const name = `${p.plan_name || ''}`.toLowerCase();
    if (/medicare|advantage|medigap|part d|pdp/.test(name)) return true;
    return false;
  };

  const today = React.useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const daysSince = React.useCallback((iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return Math.floor((today - d) / 86400000);
  }, [today]);

  const stages = React.useMemo(() => {
    const aca = { applied: [], effectuated: [], active: [] };
    const med = { soa: [], applied: [], processing: [], enrolled: [] };
    const unclassified = [];

    const clientIdsWithMedicarePolicy = new Set(
      policies.filter(p => isMedicarePlan(p) && p.client_id).map(p => p.client_id)
    );
    soaRecords.forEach(s => {
      if (s.client_id && !clientIdsWithMedicarePolicy.has(s.client_id)) {
        med.soa.push(s);
      }
    });

    policies.forEach(p => {
      const status = (p.status || 'active').toLowerCase();
      // Skip terminal states — they belong in the Lost Clients view,
      // not the active-enrollment pipeline.
      if (['cancelled', 'lapsed', 'expired', 'terminated'].includes(status)) return;

      const effDate = p.effective_date ? new Date(p.effective_date) : null;
      if (effDate) effDate.setHours(0,0,0,0);
      const effInFuture    = effDate && effDate > today;
      const effPastOrToday = effDate && effDate <= today;
      const noEff          = !effDate;

      if (isAcaPlan(p)) {
        // Applied: explicitly pending, OR active-with-future-eff, OR
        // active without an effective_date set (data-quality bucket —
        // shows up so the agent can fix it).
        if (status === 'pending' || status === 'submitted' || (status === 'active' && effInFuture)) {
          aca.applied.push(p);
        } else if (status === 'active' && effPastOrToday && !p.last_commission_at) {
          aca.effectuated.push(p);
        } else if (status === 'active' && p.last_commission_at) {
          aca.active.push(p);
        } else if (status === 'active' && noEff) {
          // No effective date but otherwise active. Treat as
          // effectuated so it surfaces for the agent to fix.
          aca.effectuated.push(p);
        }
      } else if (isMedicarePlan(p)) {
        if (status === 'pending' || status === 'submitted') {
          const ageDays = daysSince(p.created_at) || 0;
          if (ageDays < 14) med.applied.push(p);
          else med.processing.push(p);
        } else if (status === 'active') {
          med.enrolled.push(p);
        }
      } else {
        unclassified.push(p);
      }
    });

    return { aca, med, unclassified };
  }, [policies, soaRecords, today, daysSince]);

  const kpis = React.useMemo(() => {
    const inFlight = [
      ...stages.aca.applied, ...stages.aca.effectuated,
      ...stages.med.applied, ...stages.med.processing,
    ];
    const pendingMrr = inFlight.reduce((s, p) => s + (Number(p.monthly_commission) || 0), 0);
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const effectuatedThisMonth = policies.filter(p => {
      if (!p.effective_date) return false;
      const d = new Date(p.effective_date); d.setHours(0,0,0,0);
      return d >= startOfMonth && d <= today;
    }).length;
    const stuckCount = stages.med.processing.length
      + stages.aca.applied.filter(p => (daysSince(p.created_at) || 0) > 14).length;
    return { inFlight: inFlight.length, pendingMrr, effectuatedThisMonth, stuckCount };
  }, [stages, policies, today, daysSince]);

  async function moveToEffectuated(p) {
    const eff = p.effective_date || new Date().toISOString().slice(0,10);
    const { error } = await supabase.from('health_policies').update({ status: 'active', effective_date: eff }).eq('id', p.id);
    if (error) { showToast(`Update failed: ${error.message}`); return; }
    setPolicies(prev => prev.map(x => x.id === p.id ? { ...x, status: 'active', effective_date: eff } : x));
    showToast('Marked Effectuated.');
  }
  async function moveToActive(p) {
    const stamp = new Date().toISOString().slice(0,10);
    const { error } = await supabase.from('health_policies').update({ status: 'active', last_commission_at: stamp }).eq('id', p.id);
    if (error) { showToast(`Update failed: ${error.message}`); return; }
    setPolicies(prev => prev.map(x => x.id === p.id ? { ...x, status: 'active', last_commission_at: stamp } : x));
    showToast('Marked Active.');
  }

  const fmt$ = (n) => '$' + Math.round(Number(n) || 0).toLocaleString();

  function Card({ p, action, urgent }) {
    const ageDays = daysSince(p.created_at) || 0;
    return (
      <div className={`bg-white border-2 ${urgent ? 'border-rose-400' : 'border-slate-200'} p-3 mb-2 hover:border-teal-400 transition-colors`}>
        <div className="text-sm font-bold text-slate-900 truncate">
          <ClientNameLink name={p.client_name} openClientProfile={openClientProfile} className="font-bold text-slate-900 hover:underline" fallback="(no name)"/>
        </div>
        <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mt-0.5 truncate">{p.plan_name || '—'}</div>
        <div className="flex items-center justify-between mt-1 text-[0.6rem]">
          <span className="text-slate-400 uppercase tracking-wider truncate">{p.carrier || '—'}</span>
          {Number(p.monthly_commission) > 0 && <span className="font-mono font-bold text-emerald-700 shrink-0 ml-2">{fmt$(p.monthly_commission)}/mo</span>}
        </div>
        <div className="flex items-center justify-between gap-2 mt-2">
          <span className={`text-[0.55rem] uppercase tracking-wider ${urgent ? 'text-rose-700 font-black' : 'text-slate-400'}`}>{ageDays}d in stage</span>
          {action}
        </div>
      </div>
    );
  }

  function SoaCard({ s }) {
    return (
      <div className="bg-white border-2 border-violet-200 p-3 mb-2">
        <div className="text-sm font-bold text-slate-900 truncate">{s.beneficiary_name || '(no name)'}</div>
        <div className="text-[0.6rem] uppercase tracking-wider text-violet-700 mt-0.5 truncate">SOA on file · {(s.products || []).join(', ') || '—'}</div>
        <div className="text-[0.55rem] uppercase tracking-wider text-slate-400 mt-1">Awaiting application</div>
        <button onClick={() => onGotoTab('soa')} className="mt-2 text-[0.55rem] uppercase tracking-wider text-violet-700 hover:underline">Open SOA →</button>
      </div>
    );
  }

  function Column({ title, count, color, hint, children }) {
    return (
      <div className={`flex flex-col bg-slate-50 border-t-4 ${color} p-3 min-h-[280px]`}>
        <div className="flex items-baseline justify-between mb-1">
          <div className="text-[0.6rem] font-black uppercase tracking-[0.18em] text-slate-700">{title}</div>
          <div className="text-xs font-mono font-bold text-slate-900">{count}</div>
        </div>
        {hint && <div className="text-[0.55rem] text-slate-400 mb-2 italic">{hint}</div>}
        <div className="flex-1 overflow-y-auto max-h-[560px] pr-1">
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Hero — vertical toggle + KPIs */}
      <div className="bg-black border-l-4 border-teal-500 p-4">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="text-teal-300 text-[0.6rem] font-black uppercase tracking-[0.3em]">Enrollment Pipeline</div>
            <div className="text-white text-2xl font-black uppercase tracking-tight">From submitted app to active commissions</div>
            <div className="text-slate-400 text-xs mt-1">
              {kpis.inFlight} in flight · {fmt$(kpis.pendingMrr)} MRR pending · {kpis.effectuatedThisMonth} effectuated this month
              {kpis.stuckCount > 0 && <span className="text-rose-400"> · {kpis.stuckCount} stuck &gt;14d</span>}
            </div>
          </div>
          <div className="inline-flex bg-slate-800 border border-slate-700 p-0.5 text-xs font-bold uppercase tracking-tight">
            {[['both', 'Both'], ['aca', 'ACA'], ['medicare', 'Medicare']].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setVertical(k)}
                className={`px-3 py-1.5 transition-colors ${vertical === k ? 'bg-teal-600 text-white' : 'text-slate-400 hover:text-white'}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && <div className="text-sm text-slate-400 italic text-center py-12 bg-white border border-slate-200">Loading pipeline…</div>}

      {/* Diagnostic strip — surfaces total-policies-loaded so an empty
          pipeline is immediately explained ('zero rows in DB' vs
          'rows exist but bucket filters excluded them'). */}
      {!loading && (
        <div className="bg-white border border-slate-200 px-4 py-2 flex items-baseline justify-between gap-3 flex-wrap text-xs">
          <div className="flex items-baseline gap-3 flex-wrap text-[0.65rem] uppercase tracking-wider text-slate-500">
            <span><strong className="text-slate-900 font-mono">{policies.length}</strong> health policies loaded</span>
            <span>·</span>
            <span><strong className="text-slate-900 font-mono">{policies.filter(p => (p.status || 'active') === 'active').length}</strong> active</span>
            <span>·</span>
            <span><strong className="text-slate-900 font-mono">{policies.filter(p => (p.status || '') === 'pending').length}</strong> pending</span>
            <span>·</span>
            <span><strong className="text-slate-900 font-mono">{policies.filter(p => ['cancelled','lapsed','expired','terminated'].includes((p.status || '').toLowerCase())).length}</strong> cancelled/lapsed (not shown)</span>
            {stages.unclassified.length > 0 && (
              <>
                <span>·</span>
                <span className="text-amber-700"><strong className="font-mono">{stages.unclassified.length}</strong> unclassified (see below)</span>
              </>
            )}
          </div>
          <button onClick={reload} className="text-[0.6rem] uppercase tracking-wider text-sky-700 hover:text-sky-900 underline">Refresh</button>
        </div>
      )}

      {/* Unclassified bucket — when plan_type is missing AND plan_name
          doesn't match any of the ACA/Medicare regex hints. Lets the
          agent see the rows and fix the plan_type. */}
      {!loading && stages.unclassified.length > 0 && (
        <div className="bg-white border-2 border-amber-300">
          <div className="px-4 py-2 border-b border-amber-200 bg-amber-50">
            <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-amber-700">Unclassified · {stages.unclassified.length}</div>
            <div className="text-xs text-amber-900">These policies have no recognizable plan_type or plan_name. Edit each one and set the Plan type so it slots into the right pipeline.</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-2 max-h-[300px] overflow-auto">
            {stages.unclassified.slice(0, 16).map(p => (
              <div key={p.id} className="bg-white border border-amber-200 p-2 text-xs">
                <div className="font-bold text-slate-900 truncate">
                  <ClientNameLink name={p.client_name} openClientProfile={openClientProfile} className="font-bold text-slate-900 hover:underline" fallback="—"/>
                </div>
                <div className="text-[0.6rem] text-slate-500 truncate">{p.carrier || 'no carrier'} · {p.plan_name || 'no plan'}</div>
                <div className="text-[0.55rem] uppercase tracking-wider text-amber-700 mt-0.5">plan_type: {p.plan_type || 'null'}</div>
              </div>
            ))}
            {stages.unclassified.length > 16 && (
              <div className="col-span-full text-[0.6rem] uppercase tracking-wider text-amber-700">
                + {stages.unclassified.length - 16} more — open the Policy Vault to fix in bulk.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ACA pipeline */}
      {!loading && (vertical === 'both' || vertical === 'aca') && (
        <div className="bg-white border border-slate-200">
          <div className="px-4 py-2 border-b border-slate-200 flex items-baseline justify-between">
            <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-700">ACA / Marketplace</div>
            <div className="text-[0.55rem] uppercase tracking-wider text-slate-400">Applied → Effectuated → Active</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-2">
            <Column title="Applied" count={stages.aca.applied.length} color="border-amber-500" hint="App submitted · effective date in future or pending">
              {stages.aca.applied.slice(0, 20).map(p => (
                <Card key={p.id} p={p}
                  urgent={(daysSince(p.created_at) || 0) > 14}
                  action={<button onClick={() => moveToEffectuated(p)} className="bg-teal-600 hover:bg-teal-700 text-white text-[0.5rem] font-black uppercase tracking-wider px-2 py-0.5">Effectuated</button>}
                />
              ))}
              {stages.aca.applied.length > 20 && (
                <button onClick={() => onGotoTab('health_policies')} className="w-full text-[0.55rem] uppercase tracking-wider text-teal-700 hover:underline py-2">
                  +{stages.aca.applied.length - 20} more · open Policy Vault →
                </button>
              )}
              {stages.aca.applied.length === 0 && <div className="text-xs text-slate-400 italic text-center py-4">No applied yet</div>}
            </Column>
            <Column title="Effectuated" count={stages.aca.effectuated.length} color="border-sky-500" hint="Coverage live · waiting on first commission">
              {stages.aca.effectuated.slice(0, 20).map(p => (
                <Card key={p.id} p={p}
                  action={<button onClick={() => moveToActive(p)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-[0.5rem] font-black uppercase tracking-wider px-2 py-0.5">Mark Active</button>}
                />
              ))}
              {stages.aca.effectuated.length > 20 && (
                <button onClick={() => onGotoTab('health_policies')} className="w-full text-[0.55rem] uppercase tracking-wider text-teal-700 hover:underline py-2">
                  +{stages.aca.effectuated.length - 20} more · open Policy Vault →
                </button>
              )}
              {stages.aca.effectuated.length === 0 && <div className="text-xs text-slate-400 italic text-center py-4">No effectuations waiting</div>}
            </Column>
            <Column title="Active" count={stages.aca.active.length} color="border-emerald-500" hint="Carrier is paying">
              {stages.aca.active.slice(0, 20).map(p => <Card key={p.id} p={p} action={null} />)}
              {stages.aca.active.length > 20 && (
                <button onClick={() => onGotoTab('health_policies')} className="w-full text-[0.55rem] uppercase tracking-wider text-teal-700 hover:underline py-2">
                  +{stages.aca.active.length - 20} more · open Policy Vault →
                </button>
              )}
              {stages.aca.active.length === 0 && <div className="text-xs text-slate-400 italic text-center py-4">No active policies</div>}
            </Column>
          </div>
        </div>
      )}

      {/* Medicare pipeline */}
      {!loading && (vertical === 'both' || vertical === 'medicare') && (
        <div className="bg-white border border-slate-200">
          <div className="px-4 py-2 border-b border-slate-200 flex items-baseline justify-between">
            <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-700">Medicare</div>
            <div className="text-[0.55rem] uppercase tracking-wider text-slate-400">SOA → App → Processing → Enrolled</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 p-2">
            <Column title="SOA Signed" count={stages.med.soa.length} color="border-violet-500" hint="Authorization on file · no app yet">
              {stages.med.soa.slice(0, 20).map(s => <SoaCard key={s.id} s={s} />)}
              {stages.med.soa.length > 20 && (
                <button onClick={() => onGotoTab('soa')} className="w-full text-[0.55rem] uppercase tracking-wider text-teal-700 hover:underline py-2">
                  +{stages.med.soa.length - 20} more · open SOA Tracking →
                </button>
              )}
              {stages.med.soa.length === 0 && <div className="text-xs text-slate-400 italic text-center py-4">All SOAs converted</div>}
            </Column>
            <Column title="App Submitted" count={stages.med.applied.length} color="border-amber-500" hint="Within 14-day CMS window">
              {stages.med.applied.slice(0, 20).map(p => (
                <Card key={p.id} p={p}
                  action={<button onClick={() => moveToActive(p)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-[0.5rem] font-black uppercase tracking-wider px-2 py-0.5">Enrolled</button>}
                />
              ))}
              {stages.med.applied.length > 20 && (
                <button onClick={() => onGotoTab('health_policies')} className="w-full text-[0.55rem] uppercase tracking-wider text-teal-700 hover:underline py-2">
                  +{stages.med.applied.length - 20} more · open Policy Vault →
                </button>
              )}
              {stages.med.applied.length === 0 && <div className="text-xs text-slate-400 italic text-center py-4">Nothing pending</div>}
            </Column>
            <Column title="CMS Processing" count={stages.med.processing.length} color="border-rose-500" hint="&gt;14 days · investigate / call carrier">
              {stages.med.processing.slice(0, 20).map(p => (
                <Card key={p.id} p={p} urgent
                  action={<button onClick={() => moveToActive(p)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-[0.5rem] font-black uppercase tracking-wider px-2 py-0.5">Enrolled</button>}
                />
              ))}
              {stages.med.processing.length > 20 && (
                <button onClick={() => onGotoTab('health_policies')} className="w-full text-[0.55rem] uppercase tracking-wider text-teal-700 hover:underline py-2">
                  +{stages.med.processing.length - 20} more · open Policy Vault →
                </button>
              )}
              {stages.med.processing.length === 0 && <div className="text-xs text-slate-400 italic text-center py-4">No stuck apps</div>}
            </Column>
            <Column title="Enrolled" count={stages.med.enrolled.length} color="border-emerald-500" hint="Active member">
              {stages.med.enrolled.slice(0, 20).map(p => <Card key={p.id} p={p} action={null} />)}
              {stages.med.enrolled.length > 20 && (
                <button onClick={() => onGotoTab('health_policies')} className="w-full text-[0.55rem] uppercase tracking-wider text-teal-700 hover:underline py-2">
                  +{stages.med.enrolled.length - 20} more · open Policy Vault →
                </button>
              )}
              {stages.med.enrolled.length === 0 && <div className="text-xs text-slate-400 italic text-center py-4">No active enrollments</div>}
            </Column>
          </div>
        </div>
      )}
    </div>
  );
}

function CoverageSummaryView({ currentUser, settings = {}, showToast = () => {} }) {
  const [clients, setClients] = React.useState([]);
  const [policiesByClient, setPoliciesByClient] = React.useState({});
  const [savedSummaries, setSavedSummaries] = React.useState([]);
  const [clientId, setClientId] = React.useState('');
  const [summaryId, setSummaryId] = React.useState(null);
  const [printMode, setPrintMode] = React.useState(false);
  const [doctorList, setDoctorList] = React.useState(null);
  const [lookingUpDoctors, setLookingUpDoctors] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const [meta, setMeta] = React.useState({
    coverage_start_date: '',
    client_zip: '',
    client_state: '',
    notes: '',
  });
  // Agent block — prefilled from settings but editable per packet.
  const [agentBlock, setAgentBlock] = React.useState({
    name: '', phone: '', email: '', npn: '', agency: '',
  });
  // The plan stack: [{key, templateId, plan, monthly}]
  const [plans, setPlans] = React.useState([]);

  React.useEffect(() => {
    if (!currentUser?.id) return;
    setAgentBlock(prev => ({
      name:   prev.name   || settings?.ownerName  || '',
      phone:  prev.phone  || settings?.ownerPhone || '',
      email:  prev.email  || settings?.ownerEmail || currentUser?.email || '',
      npn:    prev.npn    || settings?.ownerNpn   || '',
      agency: prev.agency || settings?.agencyName || 'For The People Insurance Group',
    }));
  }, [currentUser?.id, currentUser?.email, settings?.ownerName, settings?.ownerPhone, settings?.ownerEmail, settings?.ownerNpn, settings?.agencyName]);

  React.useEffect(() => {
    if (!currentUser?.id) return;
    // Health-vertical clients only — this is the Health workspace.
    supabase.from('clients')
      .select('id, name, sector, dob, state_abbr, vertical')
      .eq('vertical', 'health')
      .order('name')
      .then(({ data }) => setClients(data || []));
  }, [currentUser?.id]);

  // Pull health_policies for the picked client → use plan_type as
  // a hint for which templates to pre-select.
  React.useEffect(() => {
    if (!clientId) return;
    if (policiesByClient[clientId]) return;
    supabase.from('health_policies')
      .select('id, plan_name, plan_type, carrier, monthly_premium')
      .eq('client_id', clientId)
      .then(({ data }) => setPoliciesByClient(prev => ({ ...prev, [clientId]: data || [] })));
  }, [clientId, policiesByClient]);

  // Past saved summaries for this client.
  React.useEffect(() => {
    if (!currentUser?.id || !clientId) { setSavedSummaries([]); return; }
    supabase.from('coverage_summaries')
      .select('id, created_at, updated_at, plans, total_monthly, coverage_start_date, notes')
      .eq('client_id', clientId)
      .order('updated_at', { ascending: false })
      .limit(10)
      .then(({ data }) => setSavedSummaries(data || []));
  }, [currentUser?.id, clientId]);

  const client = clients.find(c => c.id === clientId);

  function newKey() {
    return (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `c_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
  }
  function addPlan(templateId) {
    if (plans.some(p => p.templateId === templateId)) return;
    setPlans(prev => [...prev, { key: newKey(), templateId, plan: '', monthly: '' }]);
  }
  function updatePlan(key, patch) { setPlans(prev => prev.map(p => p.key === key ? { ...p, ...patch } : p)); }
  function removePlan(key) { setPlans(prev => prev.filter(p => p.key !== key)); }
  async function clearPlans() {
    if (plans.length === 0) return;
    if (!(await hqConfirm({
      title: 'Clear the plan stack?',
      confirmLabel: 'Clear stack', danger: true,
    }))) return;
    setPlans([]);
  }
  function pickClient(id) {
    setClientId(id);
    setSummaryId(null);
    setPlans([]);
    setMeta({ coverage_start_date: '', client_zip: '', client_state: '', notes: '' });
    if (!id) return;
    const c = clients.find(x => x.id === id);
    if (c) setMeta(m => ({ ...m, client_state: c.state_abbr || '' }));
  }

  const totals = React.useMemo(() => {
    const monthly = plans.reduce((sum, p) => sum + (Number(p.monthly) || 0), 0);
    return { monthly, annual: monthly * 12 };
  }, [plans]);

  async function saveSummary() {
    if (!currentUser?.id) { showToast('Not signed in.'); return; }
    if (!clientId) { showToast('Pick a client first.'); return; }
    if (plans.length === 0) { showToast('Add at least one plan.'); return; }
    const row = {
      owner_id: currentUser.id,
      client_id: clientId,
      client_name_at_issue: client?.name || null,
      client_zip:   meta.client_zip   || null,
      client_state: meta.client_state || null,
      coverage_start_date: meta.coverage_start_date || null,
      agent_name:  agentBlock.name   || null,
      agent_phone: agentBlock.phone  || null,
      agent_email: agentBlock.email  || null,
      agent_npn:   agentBlock.npn    || null,
      agency_name: agentBlock.agency || null,
      plans: plans.map(({ key, ...rest }) => ({
        templateId: rest.templateId,
        plan: rest.plan || '',
        monthly: Number(rest.monthly) || 0,
      })),
      total_monthly: Number(totals.monthly.toFixed(2)),
      notes: meta.notes || null,
    };
    setSaving(true);
    try {
      if (summaryId) {
        const { data, error } = await supabase.from('coverage_summaries').update(row).eq('id', summaryId).select().single();
        if (error) throw error;
        setSavedSummaries(prev => [data, ...prev.filter(s => s.id !== summaryId)]);
        showToast('Coverage summary updated.');
      } else {
        const { data, error } = await supabase.from('coverage_summaries').insert(row).select().single();
        if (error) throw error;
        setSummaryId(data.id);
        setSavedSummaries(prev => [data, ...prev]);
        showToast('Coverage summary saved.');
      }
    } catch (e) {
      showToast(`Save failed: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  }

  function loadSummary(s) {
    setSummaryId(s.id);
    setPlans((s.plans || []).map(p => ({ ...p, key: newKey() })));
    setMeta({
      coverage_start_date: s.coverage_start_date || '',
      client_zip:   s.client_zip   || '',
      client_state: s.client_state || '',
      notes:        s.notes        || '',
    });
    showToast('Summary loaded.');
  }

  async function deleteSummary(id) {
    if (!(await hqConfirm({
      title: 'Delete this summary?',
      body: 'Cannot be undone.',
      confirmLabel: 'Delete summary', danger: true,
    }))) return;
    const { error } = await supabase.from('coverage_summaries').delete().eq('id', id);
    if (error) { showToast(`Delete failed: ${error.message}`); return; }
    setSavedSummaries(prev => prev.filter(s => s.id !== id));
    if (summaryId === id) {
      setSummaryId(null);
      setPlans([]);
    }
    showToast('Summary deleted.');
  }

  async function openPrint() {
    if (plans.length === 0) { showToast('Add at least one plan first.'); return; }
    // Best-effort: fetch nearby providers if we have a ZIP. The print
    // packet renders even if this fails; we just fall back to the
    // static "search Google Maps" links when no live data is ready.
    if (meta.client_zip && /^\d{5}$/.test(String(meta.client_zip).trim())) {
      try {
        setLookingUpDoctors(true);
        const res = await apiFetch('/api/coverage/find-doctors', {
          method: 'POST',
          body: JSON.stringify({
            zip: String(meta.client_zip).trim(),
            state: meta.client_state || '',
            radius_miles: 5,
            per_category: 5,
          }),
        });
        setDoctorList(res || null);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[CoverageSummary] doctor lookup failed:', err.message || err);
        setDoctorList(null);
      } finally {
        setLookingUpDoctors(false);
      }
    } else {
      setDoctorList(null);
    }
    setPrintMode(true);
    // Wait for layout, then trigger the print dialog.
    setTimeout(() => { window.print(); }, 120);
  }
  React.useEffect(() => {
    function onAfterPrint() { setPrintMode(false); }
    window.addEventListener('afterprint', onAfterPrint);
    return () => window.removeEventListener('afterprint', onAfterPrint);
  }, []);

  // Print mode renders the packet only — app chrome hidden by CSS.
  if (printMode) {
    return (
      <>
        <CoverageSummaryPrintLayout
          client={client}
          meta={meta}
          agentBlock={agentBlock}
          plans={plans}
          totals={totals}
          doctorList={doctorList}
        />
        <button
          onClick={() => setPrintMode(false)}
          className="no-print fixed top-4 right-4 z-50 bg-slate-900 text-white text-xs font-black uppercase tracking-tight px-4 py-2 shadow-lg"
        >
          ✕ Exit print view
        </button>
      </>
    );
  }

  const accentClass = (a) => ({
    blue:   { bar: 'border-blue-500',   text: 'text-blue-700',   chip: 'bg-blue-100 text-blue-800',     dot: 'bg-blue-500' },
    red:    { bar: 'border-red-500',    text: 'text-red-700',    chip: 'bg-red-100 text-red-800',       dot: 'bg-red-500' },
    teal:   { bar: 'border-teal-500',   text: 'text-teal-700',   chip: 'bg-teal-100 text-teal-800',     dot: 'bg-teal-500' },
    violet: { bar: 'border-violet-500', text: 'text-violet-700', chip: 'bg-violet-100 text-violet-800', dot: 'bg-violet-500' },
    green:  { bar: 'border-green-500',  text: 'text-green-700',  chip: 'bg-green-100 text-green-800',   dot: 'bg-green-500' },
    sky:    { bar: 'border-sky-500',    text: 'text-sky-700',    chip: 'bg-sky-100 text-sky-800',       dot: 'bg-sky-500' },
    amber:  { bar: 'border-amber-500',  text: 'text-amber-700',  chip: 'bg-amber-100 text-amber-800',   dot: 'bg-amber-500' },
  }[a] || { bar: 'border-slate-500', text: 'text-slate-700', chip: 'bg-slate-100 text-slate-800', dot: 'bg-slate-500' });

  return (
    <div className="space-y-4">
      <div className="bg-black border-l-4 border-indigo-500 px-6 py-5 shadow-2xl">
        <div className="text-indigo-400 text-[0.6rem] font-black uppercase tracking-[0.3em]">Coverage Summary</div>
        <div className="text-white text-xl md:text-2xl font-black mt-1.5 uppercase tracking-tight">Plain-English Packet · Print-to-PDF</div>
        <div className="text-slate-400 text-xs mt-1.5">Pick a health-vertical client, build the plan stack, and generate a brochure-grade PDF the client can keep.</div>
      </div>

      {/* Client + meta */}
      <div className="bg-white border border-slate-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
          <label className="block md:col-span-2">
            <span className="text-xs text-slate-500">Client (health vertical)</span>
            <select value={clientId} onChange={e => pickClient(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-1.5 bg-white">
              <option value="">— pick a client —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}{c.sector === 'medicare' ? ' · Medicare' : ' · ACA/Private'}</option>)}
            </select>
            {clients.length === 0 && (
              <div className="text-[0.6rem] text-amber-700 mt-1 uppercase tracking-wider">No health-vertical clients yet. Convert a lead with sector=Medicare or ACA, or flip an existing client to vertical=health.</div>
            )}
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Coverage start date</span>
            <input type="date" value={meta.coverage_start_date} onChange={e => setMeta(m => ({ ...m, coverage_start_date: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5"/>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Client ZIP</span>
            <input value={meta.client_zip} onChange={e => setMeta(m => ({ ...m, client_zip: e.target.value }))} placeholder="77002" maxLength={5} className="mt-1 w-full border border-slate-300 px-2 py-1.5 font-mono"/>
          </label>
        </div>
      </div>

      {/* Agent block */}
      <div className="bg-white border border-slate-200 p-4">
        <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500 mb-2">Agent &amp; Agency · prints in the cover footer + closer page</div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 text-sm">
          <label className="block"><span className="text-xs text-slate-500">Agent name</span><input value={agentBlock.name} onChange={e => setAgentBlock(b => ({ ...b, name: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
          <label className="block"><span className="text-xs text-slate-500">Phone</span><input value={agentBlock.phone} onChange={e => setAgentBlock(b => ({ ...b, phone: e.target.value }))} placeholder="(832) 270-9921" className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
          <label className="block"><span className="text-xs text-slate-500">Email</span><input value={agentBlock.email} onChange={e => setAgentBlock(b => ({ ...b, email: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
          <label className="block"><span className="text-xs text-slate-500">NPN</span><input value={agentBlock.npn} onChange={e => setAgentBlock(b => ({ ...b, npn: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 font-mono"/></label>
          <label className="block"><span className="text-xs text-slate-500">Agency</span><input value={agentBlock.agency} onChange={e => setAgentBlock(b => ({ ...b, agency: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5"/></label>
        </div>
      </div>

      {/* Plan library + stack */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 p-4">
          <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500 mb-3">Plan library — click + to add</div>
          <div className="space-y-1.5">
            {COVERAGE_TEMPLATES.map(t => {
              const a = accentClass(t.accent);
              const inStack = plans.some(p => p.templateId === t.id);
              return (
                <div key={t.id} className={`flex items-center justify-between border-l-4 ${a.bar} border-2 border-r-slate-200 border-t-slate-200 border-b-slate-200 p-2`}>
                  <div className="min-w-0 mr-2">
                    <div className="font-bold text-slate-900 text-sm leading-tight">{t.name}</div>
                    <div className="text-[0.6rem] uppercase tracking-wider text-slate-500">{t.tagline}</div>
                  </div>
                  <button onClick={() => addPlan(t.id)} disabled={inStack}
                    className={`text-[0.6rem] font-black uppercase tracking-tight px-2.5 py-1.5 ${inStack ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}>
                    {inStack ? '✓ Added' : '+ Add'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white border-2 border-indigo-500 p-4">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-indigo-700">The Packet</div>
              <div className="text-lg font-black text-slate-900 uppercase tracking-tight mt-0.5">{plans.length} plan{plans.length === 1 ? '' : 's'}</div>
            </div>
            {plans.length > 0 && <button onClick={clearPlans} className="text-[0.6rem] font-bold uppercase tracking-wider text-red-600 hover:text-red-800">Clear</button>}
          </div>
          {plans.length === 0 ? (
            <div className="text-sm text-slate-400 italic py-8 text-center">Add plans from the left to build the packet.</div>
          ) : (
            <div className="space-y-2">
              {plans.map(row => {
                const t = TEMPLATE_BY_ID[row.templateId];
                if (!t) return null;
                const a = accentClass(t.accent);
                return (
                  <div key={row.key} className={`border-2 border-l-4 ${a.bar} border-r-slate-200 border-t-slate-200 border-b-slate-200 p-2`}>
                    <div className="flex items-baseline justify-between gap-2 mb-1.5">
                      <div className="font-bold text-sm text-slate-900 truncate">{t.name}</div>
                      <button onClick={() => removePlan(row.key)} className="text-[0.55rem] font-bold uppercase text-red-600 hover:text-red-800">DEL</button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <label className="block">
                        <span className="text-[0.55rem] uppercase tracking-wider text-slate-500">Plan variant (optional)</span>
                        <input value={row.plan} onChange={e => updatePlan(row.key, { plan: e.target.value })} placeholder={t.short} className="mt-0.5 w-full border border-slate-300 px-1.5 py-1 text-sm"/>
                      </label>
                      <label className="block">
                        <span className="text-[0.55rem] uppercase tracking-wider text-slate-500">$/mo</span>
                        <input type="number" min="0" step="0.01" value={row.monthly} onChange={e => updatePlan(row.key, { monthly: e.target.value })} className="mt-0.5 w-full border border-slate-300 px-1.5 py-1 text-sm font-mono"/>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {plans.length > 0 && (
            <div className="mt-4 border-t-2 border-indigo-200 pt-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-center">
                <div>
                  <div className="text-[0.55rem] font-black uppercase tracking-[0.15em] text-slate-500">Monthly investment</div>
                  <div className="text-3xl font-black text-slate-900 tracking-tight">${totals.monthly.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                </div>
                <div>
                  <div className="text-[0.55rem] font-black uppercase tracking-[0.15em] text-slate-500">Annualized</div>
                  <div className="text-3xl font-black text-slate-900 tracking-tight">${totals.annual.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button onClick={saveSummary} disabled={saving || !clientId} className={`text-xs font-black uppercase tracking-tight py-2.5 ${!clientId ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-slate-900 hover:bg-black text-white'}`}>
                  {saving ? 'Saving…' : (summaryId ? 'Update' : 'Save')}
                </button>
                <button onClick={openPrint} disabled={lookingUpDoctors} className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-xs font-black uppercase tracking-tight py-2.5">
                  {lookingUpDoctors ? 'Finding doctors…' : 'Print to PDF'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Saved summaries */}
      {clientId && savedSummaries.length > 0 && (
        <div className="bg-white border border-slate-200 p-4">
          <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500 mb-2">Saved Summaries for this client</div>
          <div className="space-y-1.5">
            {savedSummaries.map(s => {
              const dt = s.updated_at ? new Date(s.updated_at).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' }) : '';
              const ct = Array.isArray(s.plans) ? s.plans.length : 0;
              return (
                <div key={s.id} className={`flex items-center justify-between gap-3 border-l-4 border-indigo-500 border-2 border-r-slate-200 border-t-slate-200 border-b-slate-200 p-2 ${summaryId === s.id ? 'bg-indigo-50/60' : ''}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm text-slate-900 truncate">{s.notes || `${ct} plan${ct === 1 ? '' : 's'} · ${dt}`}</div>
                    <div className="text-[0.6rem] uppercase tracking-wider text-slate-500">${(Number(s.total_monthly) || 0).toFixed(0)}/mo · saved {dt}</div>
                  </div>
                  <button onClick={() => loadSummary(s)} className="text-[0.6rem] font-black uppercase tracking-tight bg-slate-900 hover:bg-black text-white px-2.5 py-1.5">Load</button>
                  <button onClick={() => deleteSummary(s.id)} className="text-[0.55rem] font-bold uppercase text-red-600 hover:text-red-800">DEL</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* HealthSherpa NPN-attributed quote link — for ACA stacks. */}
      <HealthSherpaDeeplinkCard settings={settings} clientZip={meta.client_zip} clientState={meta.client_state} showToast={showToast}/>

      {/* Provider quick links — live preview of what'll print. */}
      <ProviderQuickLinksCard zip={meta.client_zip} state={meta.client_state}/>

      <div className="text-[0.6rem] text-slate-400 italic px-2">Disclaimer: Coverage summaries are agent-prepared. Verify each plan's benefit schedule + state-specific terms in the carrier policy documents before delivery.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// PRINT LAYOUT — only rendered when print mode is on.
// All print-only @page rules + body scoping live inside this component.
// The non-print app chrome is hidden by `.no-print { display: none }`.
// ─────────────────────────────────────────────────────────────────────
function CoverageSummaryPrintLayout({ client, meta, agentBlock, plans, totals, doctorList }) {
  const filledPlans = plans
    .map(p => ({ ...p, template: TEMPLATE_BY_ID[p.templateId] }))
    .filter(p => p.template);

  const fmt$ = (n) => {
    if (n === null || n === undefined) return '';
    const num = Number(n);
    if (!Number.isFinite(num)) return '';
    return `$${num.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  };

  const links = providerLinks({ zip: meta.client_zip, state: meta.client_state });

  return (
    <div className="coverage-print-root">
      {/* Print scoping — hide app chrome and force A4-ish letter page. */}
      <style>{`
        @media print {
          @page { size: Letter; margin: 0; }
          html, body { background: #fff !important; }
          /* Hide everything that isn't part of the packet. */
          body * { visibility: hidden; }
          .coverage-print-root, .coverage-print-root * { visibility: visible; }
          .coverage-print-root { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
        }
        .cs-page {
          width: 8.5in;
          min-height: 11in;
          margin: 0 auto 14px;
          background: #fff;
          padding: 0.5in 0.6in 0.6in;
          box-sizing: border-box;
          box-shadow: 0 6px 32px rgba(15,23,42,.10);
          page-break-after: always;
          font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, system-ui, sans-serif;
          color: #0f172a;
        }
        .cs-page:last-child { page-break-after: auto; }
        .cs-cover {
          background: linear-gradient(160deg, #0b1a3f 0%, #131e54 60%, #1e1a55 100%);
          color: #fff;
          padding: 0.7in 0.7in 0.7in;
          position: relative;
        }
        .cs-h1 { font-size: 44px; font-weight: 900; letter-spacing: -0.02em; line-height: 1.0; }
        .cs-h2 { font-size: 22px; font-weight: 900; letter-spacing: -0.01em; }
        .cs-h3 { font-size: 16px; font-weight: 800; letter-spacing: -0.005em; }
        .cs-eyebrow { font-size: 10px; letter-spacing: 0.22em; text-transform: uppercase; font-weight: 800; }
        .cs-chip { display: inline-flex; align-items: center; gap: 6px; border-radius: 999px; padding: 6px 12px; font-size: 11px; font-weight: 700; }
        .cs-chip-dot { width: 8px; height: 8px; border-radius: 999px; }
        .cs-card { border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; }
        .cs-card-head {
          padding: 14px 18px; display: flex; justify-content: space-between; align-items: center; gap: 10px;
          color: #fff;
        }
        .cs-card-head .cs-h2 { color: #fff; }
        .cs-row {
          display: grid; grid-template-columns: 1fr 1fr; gap: 6px 24px;
          padding: 14px 18px;
        }
        .cs-row-line {
          display: flex; justify-content: space-between; align-items: baseline;
          border-bottom: 1px dashed #e2e8f0; padding: 6px 0;
        }
        .cs-row-line:last-child { border-bottom: 0; }
        .cs-row-line strong { color: #047857; font-weight: 800; }
        .cs-callout {
          margin: 12px 18px; padding: 12px 14px;
          background: #fef9c3; border: 1px solid #fde68a; border-radius: 8px;
          font-size: 12px;
        }
        .cs-foot { padding: 0 18px 16px; font-size: 11px; color: #475569; }
        .cs-pagefoot { position: absolute; bottom: 0.4in; left: 0.6in; right: 0.6in;
          display: flex; justify-content: space-between; font-size: 10px; color: #94a3b8; }
        .accent-blue   { background: #1e3a8a; }
        .accent-red    { background: #b91c1c; }
        .accent-teal   { background: #0d9488; }
        .accent-violet { background: #6d28d9; }
        .accent-green  { background: #15803d; }
        .accent-sky    { background: #0369a1; }
        .accent-amber  { background: #b45309; }
        .accent-bar-blue   { border-left: 4px solid #1e3a8a; }
        .accent-bar-red    { border-left: 4px solid #b91c1c; }
        .accent-bar-teal   { border-left: 4px solid #0d9488; }
        .accent-bar-violet { border-left: 4px solid #6d28d9; }
        .accent-bar-green  { border-left: 4px solid #15803d; }
        .accent-bar-sky    { border-left: 4px solid #0369a1; }
        .accent-bar-amber  { border-left: 4px solid #b45309; }
        .cs-num-circle {
          width: 30px; height: 30px; border-radius: 8px; color: #fff;
          display: inline-flex; align-items: center; justify-content: center;
          font-weight: 900; font-size: 13px;
        }
        .cs-overview-row {
          display: grid; grid-template-columns: 44px 1fr auto; gap: 14px; align-items: center;
          border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px; margin-bottom: 8px;
        }
        .cs-scenario {
          border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px; margin-bottom: 10px;
        }
        .cs-scenario-line {
          display: flex; justify-content: space-between; font-size: 12px;
          padding: 4px 0; border-bottom: 1px dashed #e2e8f0;
        }
        .cs-scenario-total {
          display: flex; justify-content: space-between; align-items: baseline;
          padding-top: 8px; margin-top: 6px; border-top: 2px solid #0f172a;
          font-weight: 900;
        }
        .cs-providers {
          display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;
        }
        .cs-provider-card {
          border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px;
          display: grid; grid-template-columns: 30px 1fr auto; gap: 10px; align-items: center;
        }
        .cs-step {
          display: grid; grid-template-columns: 30px 1fr; gap: 12px; margin-bottom: 12px;
        }
        .cs-step-num {
          width: 26px; height: 26px; border-radius: 999px; background: #0f172a; color: #fff;
          display: flex; align-items: center; justify-content: center; font-weight: 900; font-size: 12px;
        }
      `}</style>

      {/* ─── COVER PAGE ─── */}
      <div className="cs-page cs-cover">
        <div className="cs-eyebrow" style={{ color: '#cbd5e1' }}>
          <span style={{ display: 'inline-block', width: 28, height: 28, background: '#fff', borderRadius: 6, color: '#0b1a3f', textAlign: 'center', lineHeight: '28px', fontWeight: 900, marginRight: 8 }}>FTP</span>
          {agentBlock.agency || 'For The People Insurance Group'}
        </div>
        <div style={{ marginTop: '4.5in' }}>
          <div className="cs-h1">Your Coverage<br/>Summary</div>
          <div style={{ fontSize: 14, color: '#cbd5e1', marginTop: 14, maxWidth: 480 }}>
            A clear, plain-English breakdown of the protection package you just put in place — what each plan pays, how to use it, and who to call.
          </div>
          <div style={{ marginTop: 22, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {filledPlans.map(p => {
              const a = p.template.accent;
              return (
                <div key={p.key} className="cs-chip" style={{ background: 'rgba(255,255,255,.10)', color: '#fff' }}>
                  <span className={`cs-chip-dot accent-${a}`}></span>
                  {p.template.short} — {p.template.tagline.split(' · ')[0]}
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 28, height: 1, background: 'rgba(255,255,255,.15)' }}></div>
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 20 }}>
            <div>
              <div className="cs-eyebrow" style={{ color: '#94a3b8' }}>Prepared for</div>
              <div style={{ fontSize: 16, fontWeight: 800, marginTop: 4 }}>{client?.name || '[ Client Name ]'}</div>
              <div className="cs-eyebrow" style={{ color: '#94a3b8', marginTop: 10 }}>Coverage begins</div>
              <div style={{ fontSize: 16, fontWeight: 800, marginTop: 4 }}>{meta.coverage_start_date || '[ Start Date ]'}</div>
            </div>
            <div style={{ textAlign: 'right', fontSize: 12, color: '#e2e8f0' }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#fff' }}>{agentBlock.name || '[ Agent Name ]'}</div>
              <div style={{ marginTop: 2 }}>{agentBlock.agency}</div>
              <div style={{ marginTop: 2 }}>{agentBlock.phone}</div>
              <div style={{ marginTop: 2 }}>{agentBlock.email}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── SNAPSHOT PAGE ─── */}
      <div className="cs-page">
        <div className="cs-eyebrow" style={{ color: '#b91c1c' }}>Welcome</div>
        <div className="cs-h1" style={{ fontSize: 30, marginTop: 4 }}>Here's everything you signed up for</div>
        <div style={{ fontSize: 13, color: '#334155', marginTop: 8, maxWidth: 640 }}>
          You built a {wordNum(filledPlans.length)}-part protection package. Think of it as layers — each one puts <strong>cash in your pocket</strong> for a different kind of event, so a hospital stay, an accident, or a serious diagnosis doesn't turn into a financial emergency.
        </div>
        <div style={{ marginTop: 18 }}>
          {filledPlans.map((p, idx) => (
            <div key={p.key} className="cs-overview-row">
              <div className={`cs-num-circle accent-${p.template.accent}`}>{idx + 1}</div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 14 }}>{p.template.short} <span style={{ fontWeight: 500, color: '#64748b' }}>· {p.template.tagline}</span></div>
                <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>{p.template.intro?.split('.')[0] + '.'}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 18, fontWeight: 900 }}>{p.template.highlightKpi.value}</div>
                <div className="cs-eyebrow" style={{ color: '#94a3b8' }}>{p.template.highlightKpi.label}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 14, padding: 14, background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12 }}>
          <strong>ⓘ Good to know — how these plans work.</strong> These are <strong>fixed-benefit (indemnity) and supplemental plans</strong>. They pay you a set cash amount for covered services, regardless of the actual bill, and the money is paid directly to you (or your provider). They are <strong>not ACA major-medical insurance</strong> and aren't meant to replace comprehensive coverage — they're built to put cash in your hands fast and soften out-of-pocket costs.
        </div>
        <div style={{ marginTop: 10, fontSize: 13, color: '#334155' }}>
          Your total monthly investment for this package: <strong>{fmt$(totals.monthly)}/mo</strong>. The next pages break down exactly what each plan pays.
        </div>
        <CsPageFooter agent={agentBlock} page={2} total={filledPlans.length + 5}/>
      </div>

      {/* ─── PLAN PAGES ─── */}
      {filledPlans.map((p, idx) => (
        <div key={p.key} className={`cs-page accent-bar-${p.template.accent}`}>
          <div className="cs-eyebrow" style={{ color: accentHex(p.template.accent) }}>Plan {idx + 1} — {p.template.short}</div>
          <div className="cs-h1" style={{ fontSize: 26, marginTop: 4 }}>{p.template.name}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>{p.template.tagline}</div>
          {p.template.intro && (
            <div style={{ fontSize: 13, marginTop: 12, color: '#334155' }}>{p.template.intro}</div>
          )}
          <div className="cs-card" style={{ marginTop: 14 }}>
            <div className={`cs-card-head accent-${p.template.accent}`}>
              <div>
                <div className="cs-h3" style={{ color: '#fff' }}>{p.template.short}</div>
                <div style={{ fontSize: 11, opacity: 0.85 }}>{p.template.carrier}</div>
              </div>
              <div className="cs-chip" style={{ background: 'rgba(255,255,255,.18)', color: '#fff' }}>
                {p.template.badge}
              </div>
            </div>
            <div className="cs-row">
              {p.template.rows.map((r, i) => (
                <React.Fragment key={i}>
                  <div className="cs-row-line">
                    <span>{r.left.label} <span style={{ color: '#94a3b8', fontSize: 11 }}>{r.left.sub}</span></span>
                    <strong>{r.left.value}</strong>
                  </div>
                  <div className="cs-row-line">
                    <span>{r.right.label} <span style={{ color: '#94a3b8', fontSize: 11 }}>{r.right.sub}</span></span>
                    <strong>{r.right.value}</strong>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
          {p.template.callout && (
            <div className="cs-callout">
              <strong>{p.template.callout.title}.</strong> {p.template.callout.body}
            </div>
          )}
          {p.monthly > 0 && (
            <div style={{ marginTop: 12, fontSize: 12, color: '#475569' }}>
              <strong>Your premium for this plan:</strong> {fmt$(p.monthly)}/mo
            </div>
          )}
          <div style={{ marginTop: 10, fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>{p.template.footnote}</div>
          <CsPageFooter agent={agentBlock} page={3 + idx} total={filledPlans.length + 5}/>
        </div>
      ))}

      {/* ─── REAL-DOLLAR EXAMPLES PAGE ─── */}
      <div className="cs-page">
        <div className="cs-eyebrow" style={{ color: '#1e3a8a' }}>What your package actually pays</div>
        <div className="cs-h1" style={{ fontSize: 26, marginTop: 4 }}>Real-dollar examples</div>
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>How the layers stack up</div>
        <div style={{ marginTop: 14 }}>
          {COVERAGE_SCENARIOS
            .filter(s => s.requires.every(req => filledPlans.some(p => p.templateId === req)))
            .map(s => {
              const total = s.lines.reduce((sum, l) => sum + (Number(l.value) || 0), 0);
              return (
                <div key={s.id} className="cs-scenario">
                  <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 8 }}>{s.icon} {s.headline}</div>
                  {s.lines.map((l, i) => (
                    <div key={i} className="cs-scenario-line">
                      <span style={{ color: '#475569' }}>{l.label}</span>
                      <span>{l.mark ?? `+${fmt$(l.value)}`}</span>
                    </div>
                  ))}
                  <div className="cs-scenario-total">
                    <span>{s.totalLabel}</span>
                    <span style={{ color: '#047857' }}>{s.totalMark ?? fmt$(total)}</span>
                  </div>
                </div>
              );
            })}
        </div>
        <div style={{ marginTop: 14, padding: 12, background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12 }}>
          <strong>The takeaway.</strong> Because these are cash-benefit plans, the money comes to <strong>you</strong> — to cover deductibles, rent, childcare, lost income, or anything else. They pay <strong>in addition to</strong> any other insurance you may have. Amounts above are illustrations based on your plan's benefit schedule; actual payouts depend on the specific services received.
        </div>
        <CsPageFooter agent={agentBlock} page={3 + filledPlans.length} total={filledPlans.length + 5}/>
      </div>

      {/* ─── HOW TO USE COVERAGE + PROVIDERS ─── */}
      <div className="cs-page">
        <div className="cs-eyebrow" style={{ color: '#1e3a8a' }}>How to use your coverage</div>
        <div className="cs-h1" style={{ fontSize: 26, marginTop: 4 }}>Filing a claim &amp; finding providers</div>
        <div style={{ marginTop: 14 }}>
          {[
            { n: 1, h: 'See your doctor or provider.', b: 'Use any provider you like. For your hospital/medical and dental plans, in-network providers give you the biggest discounts.' },
            { n: 2, h: 'File your claim online.', b: 'Go to uhcmemberhub.com, print the claim form for the service, complete it, and submit it. Benefits are paid directly to you.' },
            { n: 3, h: 'Get paid.', b: 'Your fixed cash benefit is sent to you (or your provider if you assign it). Use it however you need.' },
            { n: 4, h: 'Need a doctor now?', b: 'Use your $0 Teladoc virtual visits included with Guard 6000 — 24/7 by app, web, or phone.' },
          ].map(step => (
            <div key={step.n} className="cs-step">
              <div className="cs-step-num">{step.n}</div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 13 }}>{step.h}</div>
                <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>{step.b}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="cs-h3" style={{ marginTop: 6, marginBottom: 6, color: '#1e3a8a' }}>Finding in-network providers {meta.client_zip ? `· near ${meta.client_zip}` : ''}</div>

        {/* Live doctor list — populated by /api/coverage/find-doctors
            when the agent clicked Print. Renders actual names + phones
            + addresses + distance so the client can call today. */}
        {doctorList && doctorList.total_found > 0 && (
          <div style={{ marginBottom: 14 }}>
            {doctorList.categories.filter(c => c.results.length > 0).slice(0, 6).map(cat => (
              <div key={cat.id} style={{ marginBottom: 10, pageBreakInside: 'avoid' }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#0f172a', borderBottom: '2px solid #0f172a', paddingBottom: 3, marginBottom: 4 }}>
                  {cat.label} <span style={{ fontWeight: 400, fontSize: 10, color: '#64748b', marginLeft: 6 }}>· {cat.results.length} nearby</span>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <tbody>
                    {cat.results.map((r, ix) => (
                      <tr key={ix} style={{ borderBottom: '1px dotted #cbd5e1' }}>
                        <td style={{ padding: '4px 6px 4px 0', fontWeight: 700, color: '#0f172a', width: '34%' }}>{r.name}</td>
                        <td style={{ padding: '4px 6px', color: '#475569' }}>{r.address || '—'}</td>
                        <td style={{ padding: '4px 6px', whiteSpace: 'nowrap', color: '#0369a1', fontWeight: 700 }}>{r.phone || ''}</td>
                        <td style={{ padding: '4px 0 4px 6px', textAlign: 'right', whiteSpace: 'nowrap', color: '#64748b', fontFamily: 'monospace' }}>{r.distance_mi != null ? `${r.distance_mi} mi` : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
        {doctorList && doctorList.total_found === 0 && (
          <div style={{ marginBottom: 12, fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>
            No nearby providers found at this ZIP. Use the carrier portals below.
          </div>
        )}

        {/* Carrier portals — kept as a backup row. With a live doctor
            list above, these become "where to verify in-network status";
            without one, they're the only way to find providers. */}
        <div className="cs-h3" style={{ marginTop: 6, marginBottom: 6, color: '#1e3a8a', fontSize: 13 }}>
          {doctorList && doctorList.total_found > 0 ? 'Verify in-network · carrier portals' : 'Carrier portals'}
        </div>
        <div className="cs-providers">
          {links.filter(l => l.primaryUrl).slice(0, 8).map(l => (
            <div key={l.id} className="cs-provider-card">
              <div style={{ fontSize: 22 }}>{l.icon}</div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 13 }}>{l.label}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{l.sub}</div>
              </div>
              <div style={{ textAlign: 'right', fontSize: 11, color: '#0369a1', fontWeight: 700 }}>{l.primaryHost}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 10, fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>
          Always confirm in-network status before your visit. {doctorList ? `Listings sourced from OpenStreetMap within ${doctorList.radius_miles} mi of ${doctorList.zip}.` : meta.client_zip ? `Listings tailored to ZIP ${meta.client_zip}.` : 'Add your ZIP for nearer matches.'}
        </div>
        <CsPageFooter agent={agentBlock} page={4 + filledPlans.length} total={filledPlans.length + 5}/>
      </div>

      {/* ─── CLOSER PAGE ─── */}
      <div className="cs-page cs-cover" style={{ background: 'linear-gradient(160deg, #0b1a3f 0%, #131e54 60%, #1e1a55 100%)', color: '#fff' }}>
        <div style={{ marginTop: '3.6in' }}>
          <div className="cs-eyebrow" style={{ color: '#fca5a5' }}>I'm in your corner</div>
          <div className="cs-h1" style={{ marginTop: 6 }}>Questions? I'm one<br/>call or text away.</div>
          <div style={{ fontSize: 14, color: '#cbd5e1', marginTop: 14, maxWidth: 460 }}>
            You're covered — and you're not on your own. Whether it's a claim, a provider question, or you just want to make sure you're using your benefits the right way, reach out anytime. That's what I'm here for.
          </div>
          <div style={{ marginTop: 28, padding: 18, border: '1px solid rgba(255,255,255,.20)', borderRadius: 12, maxWidth: 460 }}>
            <div style={{ fontSize: 18, fontWeight: 900 }}>{agentBlock.name || '[ Agent Name ]'}</div>
            <div className="cs-eyebrow" style={{ color: '#cbd5e1', marginTop: 4 }}>{agentBlock.agency}</div>
            <div style={{ marginTop: 12, fontSize: 14 }}>{agentBlock.phone || '—'}</div>
            <div style={{ marginTop: 6, fontSize: 14 }}>{agentBlock.email || '—'}</div>
            {agentBlock.npn && <div style={{ marginTop: 6, fontSize: 11, color: '#94a3b8' }}>NPN {agentBlock.npn}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function wordNum(n) {
  return ['zero','one','two','three','four','five','six','seven','eight','nine','ten'][n] || String(n);
}
function accentHex(a) {
  return ({ blue: '#1e3a8a', red: '#b91c1c', teal: '#0d9488', violet: '#6d28d9', green: '#15803d', sky: '#0369a1', amber: '#b45309' })[a] || '#475569';
}
function CsPageFooter({ agent, page, total }) {
  return (
    <div className="cs-pagefoot">
      <span><strong style={{ color: '#475569' }}>{agent.name || ''}</strong> · {agent.agency || ''}</span>
      <span>Page {page} of {total}</span>
      <span>{agent.phone || ''} · {agent.email || ''}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Provider quick links card — also embedded inline in the PDF.
// ─────────────────────────────────────────────────────────────────────
function ProviderQuickLinksCard({ zip = '', state = '' }) {
  const links = providerLinks({ zip, state });
  return (
    <div className="bg-white border border-slate-200 p-4">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">Provider Quick Links</div>
          <div className="text-lg font-black text-slate-900 uppercase tracking-tight mt-0.5">{zip ? `Near ${zip}` : 'Add a ZIP to localize'}</div>
        </div>
        <div className="text-[0.55rem] uppercase tracking-wider text-slate-400">Prints in the packet</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {links.map(l => (
          <a key={l.id} href={l.primaryUrl || '#'} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 border-2 border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/60 p-2 transition-colors">
            <span className="text-xl">{l.icon}</span>
            <span className="min-w-0">
              <span className="block font-bold text-sm text-slate-900 truncate">{l.label}</span>
              <span className="block text-[0.6rem] uppercase tracking-wider text-slate-500 truncate">{l.sub}</span>
            </span>
          </a>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// HEALTH PRODUCT FAMILIES (Family-level filter on the catalog).
//
// The existing FTPIG_PRODUCTS catalog covers UHC / Golden Rule ancillary
// and supplemental only. Health agents also sell three other families:
// Medicare Advantage, Medicare Supplement, and ACA Marketplace — but
// those aren't single-carrier products, they're cross-carrier plan
// families. We surface them as reference cards rather than try to
// shoehorn them into the product table.
// ─────────────────────────────────────────────────────────────────────
const HEALTH_PRODUCT_FAMILIES = {
  ancillary: {
    label: 'Ancillary · UHC / Golden Rule',
    tone:  'sky',
    sub:   'Indemnity / supplemental / STM. NOT MEC.',
  },
  medicare: {
    label: 'Medicare',
    tone:  'blue',
    sub:   'Advantage · Supplement · Part D.',
  },
  aca: {
    label: 'ACA · Marketplace',
    tone:  'teal',
    sub:   'Metal tiers via HealthSherpa / FFM.',
  },
};

const MEDICARE_REFERENCE_CARDS = [
  { id: 'ma_hmo', name: 'Medicare Advantage — HMO',
    summary: '$0–$30 premiums common · in-network only · plan-issued PCP referrals · drug coverage built in',
    carriers: 'Humana, UHC, Aetna, BCBS, Cigna, Centene',
    comp:    'CMS-set initial: ~$626 first year · ~$313 renewal Y2+ · PR/HI/AK ~$695',
    aep:     'AEP enrollment: Oct 15 – Dec 7. OEP: Jan 1 – Mar 31 (one switch).' },
  { id: 'ma_ppo', name: 'Medicare Advantage — PPO',
    summary: 'Out-of-network coverage at higher cost · no referrals · drug coverage built in',
    carriers: 'Humana, UHC, Aetna, BCBS',
    comp:    'Same CMS-set rate as HMO.',
    aep:     'AEP enrollment: Oct 15 – Dec 7.' },
  { id: 'medsupp', name: 'Medicare Supplement (Medigap)',
    summary: 'Standardized federally · Plan G most common at 65 · pairs with Original Medicare + standalone PDP',
    carriers: 'Mutual of Omaha, Aetna, Cigna, Humana, UHC, Anthem',
    comp:    'First-year ~20% of annual premium · renewals 10% yrs 2–6 then 5% lifetime',
    aep:     'No annual enrollment window. Best window: 6 months from Part B start (guaranteed issue).' },
  { id: 'pdp', name: 'Medicare Part D (PDP)',
    summary: 'Standalone Rx coverage for clients on Original Medicare or Medigap',
    carriers: 'Wellcare, SilverScript, Humana, UHC',
    comp:    'CMS-set: ~$109 initial · ~$55 renewal',
    aep:     'AEP: Oct 15 – Dec 7. Cross-sell with Medigap.' },
];

const ACA_REFERENCE_CARDS = [
  { id: 'bronze',   name: 'Bronze',   summary: 'Lowest premium · highest deductible (~$7K) · 60% actuarial value · catastrophic-only mindset' },
  { id: 'silver',   name: 'Silver',   summary: '70% AV · only tier with cost-sharing reduction (CSR) for 100–250% FPL · CSR 94 / 87 / 73 / none' },
  { id: 'gold',     name: 'Gold',     summary: '80% AV · higher premium, lower OOP · better for known healthcare users' },
  { id: 'platinum', name: 'Platinum', summary: '90% AV · highest premium · for clients with significant ongoing care' },
];

// ═══════════════════════════════════════════════════════════════════
// RECONCILIATION ENGINE — BoB ↔ commission cross-reference.
//
// A health book on paper looks healthy. The carrier paying that book
// tells the truth. The gap is where revenue silently dies: a client
// cancels at the carrier, the agent's BoB still shows them as active,
// and the loss only shows up 60 days later when checks stop landing.
//
// This view does one job: surface every policy where the ROSTER says
// "active" but the COMMISSION RECORD says "not paid in N days". It
// buckets policies by payment-recency vs status so the agent can
// triage every morning:
//
//   BUCKET            DEFINITION                                  ACTION
//   ──────────────────────────────────────────────────────────────────
//   Healthy           active · paid within 45 days                None
//   At Risk           active · 45-60 days since last payment       Call carrier
//   Silent Loss       active · 60+ days since payment (or never)   Mark as lost
//   Reinstated        cancelled · paid within 45 days              Restore status
//
// The "Mark as lost" button inserts a row into health_loss_events
// (reason = 'payment_stopped'), feeding the existing Lost Clients
// dashboard + the unack banner. From there the agent can re-shop or
// route to the retention workflow.
//
// MULTI-TENANCY GUARANTEE
// ──────────────────────────────────────────────────────────────────
// All queries are RLS-scoped to the operator. No cross-tenant read.
// ═══════════════════════════════════════════════════════════════════
function HealthReconciliationView({ currentUser, settings = {}, showToast = () => {}, onGotoTab = () => {}, openClientProfile = () => {} }) {
  const [policies, setPolicies] = React.useState([]);
  const [losses,   setLosses]   = React.useState(new Set());  // policy_ids with an unacked payment_stopped event
  const [loading,  setLoading]  = React.useState(true);
  const [filter,   setFilter]   = React.useState('silent');   // which bucket to show
  const [carrierFilter, setCarrierFilter] = React.useState('');

  const ownerId = currentUser?.id;

  React.useEffect(() => {
    if (!ownerId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [policyRes, lossRes] = await Promise.all([
        supabase.from('health_policies')
          .select('id, client_id, client_name, client_phone, carrier, plan_name, plan_type, plan_id, member_id, status, monthly_premium, monthly_commission, effective_date, renewal_date, termination_date, last_commission_at')
          .eq('owner_id', ownerId)
          .order('last_commission_at', { ascending: true, nullsFirst: true }),
        supabase.from('health_loss_events')
          .select('policy_id, reason, acknowledged_at')
          .eq('owner_id', ownerId)
          .eq('reason', 'payment_stopped')
          .is('acknowledged_at', null),
      ]);
      if (cancelled) return;
      setPolicies(policyRes.data || []);
      setLosses(new Set((lossRes.data || []).map(l => l.policy_id).filter(Boolean)));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [ownerId]);

  // ── Bucket the roster ──────────────────────────────────────────
  const today = new Date();
  const DAY = 24 * 60 * 60 * 1000;
  const daysSince = (dStr) => {
    if (!dStr) return Infinity;
    const d = new Date(dStr);
    if (Number.isNaN(d.getTime())) return Infinity;
    return Math.floor((today - d) / DAY);
  };

  // Brand-new policies that haven't been paid yet are NOT silent
  // losses — they're just waiting for the first commission run. The
  // grace window: effective_date within the last 60 days.
  const isBrandNew = (p) => {
    if (!p.effective_date) return false;
    return daysSince(p.effective_date) <= 60;
  };

  const bucketed = React.useMemo(() => {
    const out = { healthy: [], atRisk: [], silentLoss: [], reinstated: [] };
    for (const p of policies) {
      if (carrierFilter && (p.carrier || '') !== carrierFilter) continue;
      const status = (p.status || 'active').toLowerCase();
      const gap    = daysSince(p.last_commission_at);
      if (status === 'cancelled') {
        if (Number.isFinite(gap) && gap <= 45) {
          out.reinstated.push({ ...p, _gap: gap });
        }
        continue;
      }
      if (status !== 'active') continue;
      if (gap <= 45)                         out.healthy.push({ ...p, _gap: gap });
      else if (gap <= 60)                    out.atRisk.push({ ...p, _gap: gap });
      else if (Number.isFinite(gap))         out.silentLoss.push({ ...p, _gap: gap });
      else if (isBrandNew(p))                out.healthy.push({ ...p, _gap: null, _brandNew: true });
      else                                   out.silentLoss.push({ ...p, _gap: null });
    }
    return out;
  }, [policies, carrierFilter]);

  const totalMrrAtRisk = bucketed.silentLoss.reduce(
    (s, p) => s + (Number(p.monthly_commission) || 0), 0
  );
  const totalMrrShown =
    filter === 'healthy'   ? bucketed.healthy.reduce(   (s, p) => s + (Number(p.monthly_commission) || 0), 0) :
    filter === 'atRisk'    ? bucketed.atRisk.reduce(    (s, p) => s + (Number(p.monthly_commission) || 0), 0) :
    filter === 'silent'    ? totalMrrAtRisk :
    filter === 'reinstated'? bucketed.reinstated.reduce((s, p) => s + (Number(p.monthly_commission) || 0), 0) :
    0;

  const carriers = React.useMemo(() =>
    Array.from(new Set(policies.map(p => p.carrier).filter(Boolean))).sort()
  , [policies]);

  const visible =
    filter === 'healthy'    ? bucketed.healthy    :
    filter === 'atRisk'     ? bucketed.atRisk     :
    filter === 'silent'     ? bucketed.silentLoss :
    filter === 'reinstated' ? bucketed.reinstated :
    [];

  const fmt$ = (n) => '$' + Math.round(Number(n) || 0).toLocaleString();

  // ── Mark as lost — fires the existing payment_stopped event ────
  const markLost = async (p) => {
    if (!(await hqConfirm({
      title: `Mark ${p.client_name} as lost?`,
      body: `Files a payment_stopped event for ${p.carrier || 'unknown carrier'}. The row moves to the Lost Clients dashboard.`,
      confirmLabel: 'Mark as lost', danger: true,
    }))) return;
    const lostDate = p.last_commission_at
      ? new Date(new Date(p.last_commission_at).getTime() + 30 * DAY).toISOString()
      : new Date().toISOString();
    const { error } = await supabase.from('health_loss_events').insert({
      owner_id:           ownerId,
      policy_id:          p.id,
      client_id:          p.client_id || null,
      client_name:        p.client_name || null,
      plan_name:          p.plan_name || null,
      plan_type:          p.plan_type || null,
      monthly_commission: Number(p.monthly_commission) || 0,
      lost_at:            lostDate,
      reason:             'payment_stopped',
      notes:              p._gap == null
                            ? 'Filed from Reconciliation Engine — no commission record found for this policy.'
                            : `Filed from Reconciliation Engine — ${p._gap} days since last commission.`,
    });
    if (error) {
      // Most likely the dedupe unique index — same policy/reason/day.
      showToast(`Already filed: ${error.message}`, 'error');
      return;
    }
    setLosses(prev => new Set([...prev, p.id]));
    showToast('Loss event filed.', 'success');
  };

  const restoreStatus = async (p) => {
    const { error } = await supabase.from('health_policies').update({ status: 'active' }).eq('id', p.id);
    if (error) { showToast(`Restore failed: ${error.message}`, 'error'); return; }
    setPolicies(prev => prev.map(x => x.id === p.id ? { ...x, status: 'active' } : x));
    showToast('Policy restored to active.', 'success');
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="border-2 border-slate-900 bg-slate-900 px-4 py-3 flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-rose-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">Reconciliation Engine</div>
          <div className="text-white text-base font-black uppercase tracking-tight mt-0.5">BoB ↔ Commission · Silent disenrollment defense</div>
        </div>
        <div className="text-right">
          <div className="text-[0.55rem] uppercase tracking-wider text-slate-400">Silent-loss MRR at risk</div>
          <div className="text-2xl font-black text-rose-300">{fmt$(totalMrrAtRisk)}</div>
        </div>
      </div>

      {/* Bucket strip — click to switch what's shown below */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-2 border-slate-300 bg-white">
        <BucketTile
          label="Healthy"      count={bucketed.healthy.length}    accent="emerald"
          active={filter === 'healthy'}    onClick={() => setFilter('healthy')}
          sub="active · paid ≤ 45d"
        />
        <BucketTile
          label="At Risk"      count={bucketed.atRisk.length}     accent="amber"
          active={filter === 'atRisk'}     onClick={() => setFilter('atRisk')}
          sub="active · 45-60d gap"
        />
        <BucketTile
          label="Silent Loss"  count={bucketed.silentLoss.length} accent="rose"
          active={filter === 'silent'}     onClick={() => setFilter('silent')}
          sub="active · 60d+ or never paid"
        />
        <BucketTile
          label="Reinstated"   count={bucketed.reinstated.length} accent="sky"
          active={filter === 'reinstated'} onClick={() => setFilter('reinstated')}
          sub="cancelled · paid ≤ 45d"
        />
      </div>

      {/* Filter row */}
      <div className="border border-slate-300 bg-white px-4 py-2 flex items-center gap-3 flex-wrap text-xs">
        <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-700">Carrier</span>
        <select value={carrierFilter} onChange={e => setCarrierFilter(e.target.value)}
          className="text-xs border border-slate-300 px-2 py-1">
          <option value="">All carriers</option>
          {carriers.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="ml-auto text-[0.6rem] uppercase tracking-wider text-slate-500">
          {visible.length} row{visible.length === 1 ? '' : 's'} · {fmt$(totalMrrShown)}/mo MRR
        </span>
      </div>

      {/* Body */}
      <div className="bg-white border border-slate-200 overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {['Client', 'Phone', 'Carrier · Plan', 'Member ID', 'Eff', 'Last paid', 'Gap', 'MRR', 'Status', ''].map(h => (
                <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={10} className="px-3 py-10 text-center text-slate-400">Loading…</td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={10} className="px-3 py-10 text-center text-slate-400">
                {filter === 'silent'
                  ? 'No silent-loss policies. Every active policy is being paid.'
                  : 'No rows match this bucket.'}
              </td></tr>
            ) : visible.map(p => {
              const lossFiled = losses.has(p.id);
              return (
                <tr key={p.id} className={`hover:bg-slate-50 ${lossFiled ? 'opacity-60' : ''}`}>
                  <td className="px-3 py-2 font-bold text-slate-900 whitespace-nowrap"><ClientNameLink name={p.client_name} openClientProfile={openClientProfile} className="font-bold text-slate-900 hover:underline" fallback="—"/></td>
                  <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">{p.client_phone || '—'}</td>
                  <td className="px-3 py-2 text-xs">
                    <div>{p.carrier || '—'}</div>
                    <div className="text-[0.6rem] text-slate-500">{p.plan_name || '—'}</div>
                  </td>
                  <td className="px-3 py-2 text-[0.65rem] font-mono text-slate-600 whitespace-nowrap">{p.member_id || '—'}</td>
                  <td className="px-3 py-2 text-xs font-mono text-slate-600 whitespace-nowrap">{p.effective_date || '—'}</td>
                  <td className="px-3 py-2 text-xs font-mono text-slate-600 whitespace-nowrap">{p.last_commission_at ? String(p.last_commission_at).slice(0, 10) : 'never'}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">
                    {p._gap == null ? (
                      <span className="text-rose-700 font-black">never paid</span>
                    ) : (
                      <span className={
                        p._gap <= 45 ? 'text-emerald-700 font-black' :
                        p._gap <= 60 ? 'text-amber-700  font-black' :
                                       'text-rose-700   font-black'
                      }>{p._gap}d</span>
                    )}
                    {p._brandNew && <span className="ml-1 text-[0.55rem] uppercase tracking-wider text-slate-400">new</span>}
                  </td>
                  <td className="px-3 py-2 text-sm tabular-nums whitespace-nowrap">{p.monthly_commission != null ? fmt$(p.monthly_commission) : '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[0.55rem] font-black uppercase tracking-wider px-2 py-0.5 ${(p.status || 'active') === 'active' ? 'bg-emerald-100 text-emerald-800' : (p.status === 'cancelled') ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-700'}`}>
                      {p.status || 'active'}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">
                    {filter === 'reinstated' ? (
                      <button onClick={() => restoreStatus(p)}
                        className="text-[0.6rem] font-black uppercase tracking-tight text-sky-700 hover:text-sky-900 underline">
                        Restore active
                      </button>
                    ) : lossFiled ? (
                      <span className="text-[0.55rem] uppercase tracking-wider text-rose-500 italic">loss filed</span>
                    ) : (
                      <div className="flex gap-2 justify-end">
                        {p.plan_id && (
                          <button onClick={() => onGotoTab('health_plan_intel')}
                            className="text-[0.6rem] font-black uppercase tracking-tight text-cyan-700 hover:text-cyan-900 underline">
                            Plan
                          </button>
                        )}
                        {(filter === 'silent' || filter === 'atRisk') && (
                          <button onClick={() => markLost(p)}
                            className="text-[0.6rem] font-black uppercase tracking-tight text-rose-700 hover:text-rose-900 underline">
                            Mark lost
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footnote */}
      <div className="text-[0.65rem] uppercase tracking-wider text-slate-400 px-1">
        Cross-reference key: <span className="font-mono">(carrier, member_id)</span>. Importing a HealthSherpa BoB sets <span className="font-mono">member_id</span> to the carrier-assigned policy ID, so a later Plan Advisors / HCSC commission statement lands an UPDATE on the same row and refreshes <span className="font-mono">last_commission_at</span>. That single field is what powers this whole engine.
      </div>
    </div>
  );
}

function BucketTile({ label, count, accent, active, onClick, sub }) {
  const tone =
    accent === 'emerald' ? (active ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-900') :
    accent === 'amber'   ? (active ? 'bg-amber-600   text-white' : 'bg-amber-50   text-amber-900')   :
    accent === 'rose'    ? (active ? 'bg-rose-600    text-white' : 'bg-rose-50    text-rose-900')    :
                           (active ? 'bg-sky-600     text-white' : 'bg-sky-50     text-sky-900');
  return (
    <button onClick={onClick} className={`${tone} border-r border-slate-200 last:border-r-0 p-4 text-left hover:brightness-105`}>
      <div className={`text-[0.55rem] font-black uppercase tracking-wider ${active ? 'opacity-90' : 'opacity-70'}`}>{label}</div>
      <div className="text-2xl font-black mt-0.5">{count}</div>
      <div className={`text-[0.55rem] uppercase tracking-wider mt-1 ${active ? 'opacity-90' : 'opacity-60'}`}>{sub}</div>
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════
// AI AGENTS HUB
//
// One screen, four agents, on-demand triggers, run history.
// Built on the universal /api/agents/ runtime that logs every
// invocation to ai_agent_runs.
//
//   Morning Briefing   — daily standup-in-your-inbox
//   Prioritize Leads   — top-N to call today
//   Lapse Risk         — predicts health-policy churn before it lands
//   Performance Coach  — weekly per-producer note
// ═══════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════
// DEMOS VIEW
//
// Operator-side dashboard for the demo funnel. Lists every upcoming
// demo from `demo_reminders`, joined to `demo_prep_answers` by email
// so we can show a "Prep Submitted ✓" badge at a glance.
//
// What an operator does here:
//   1. Scan upcoming demos (Today / Tomorrow / This Week / All).
//   2. See which prospects filled the prep form (so they can pre-read
//      the #1 pain before the call).
//   3. See which reminders have fired (T-24h / T-1h / T-15m).
//   4. Click "Mark Outcome" after a demo: closed in room / needs
//      follow-up / no-show / cancelled. That drives the post-demo
//      close sequence automatically.
//   5. Open the prospect's prep answers inline (modal).
//
// All reads/writes go through the service-role-only endpoints
// because demo_reminders + demo_prep_answers have no public SELECT
// policy — only the rep account can see them.
// ════════════════════════════════════════════════════════════════════
function DemosView({ currentUser, showToast = () => {} }) {
  const [demos, setDemos]     = React.useState([]);
  const [preps, setPreps]     = React.useState({}); // email → prep row
  const [loading, setLoading] = React.useState(true);
  const [horizon, setHorizon] = React.useState('upcoming'); // upcoming | today | tomorrow | week | past
  const [openPrep, setOpenPrep] = React.useState(null);     // prep row for modal

  const fmt$ = (n) => '$' + (Number(n) || 0).toLocaleString('en-US');

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const [demosRes, prepsRes] = await Promise.all([
        supabase.from('demo_reminders')
          .select('*')
          .order('scheduled_at', { ascending: true }),
        supabase.from('demo_prep_answers')
          .select('*')
          .order('submitted_at', { ascending: false }),
      ]);
      setDemos(demosRes.data || []);
      const byEmail = {};
      (prepsRes.data || []).forEach(p => {
        const k = String(p.email || '').toLowerCase();
        if (k && !byEmail[k]) byEmail[k] = p; // most-recent wins (already sorted desc)
      });
      setPreps(byEmail);
    } catch (err) {
      showToast(`Could not load demos: ${err.message || err}`, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  // ── Horizon filter ─────────────────────────────────────────────
  const filtered = React.useMemo(() => {
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    const endOfToday    = new Date(); endOfToday.setHours(23, 59, 59, 999);
    const endOfTomorrow = new Date(endOfToday.getTime() + dayMs);
    const endOfWeek     = new Date(now + 7 * dayMs);
    return demos.filter(d => {
      const t = new Date(d.scheduled_at).getTime();
      if (horizon === 'past')     return t < now;
      if (horizon === 'today')    return t >= now && t <= endOfToday.getTime();
      if (horizon === 'tomorrow') return t > endOfToday.getTime() && t <= endOfTomorrow.getTime();
      if (horizon === 'week')     return t >= now && t <= endOfWeek.getTime();
      return t >= now - 60 * 60 * 1000; // 'upcoming' shows recently-past + future
    });
  }, [demos, horizon]);

  // Counts for the pills.
  const counts = React.useMemo(() => {
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    const endOfToday    = new Date(); endOfToday.setHours(23, 59, 59, 999);
    const endOfTomorrow = new Date(endOfToday.getTime() + dayMs);
    const endOfWeek     = new Date(now + 7 * dayMs);
    let upcoming = 0, today = 0, tomorrow = 0, week = 0, past = 0, soon = 0;
    for (const d of demos) {
      const t = new Date(d.scheduled_at).getTime();
      if (t < now) past++;
      if (t >= now - 60 * 60 * 1000) upcoming++;
      if (t >= now && t <= endOfToday.getTime()) today++;
      if (t > endOfToday.getTime() && t <= endOfTomorrow.getTime()) tomorrow++;
      if (t >= now && t <= endOfWeek.getTime()) week++;
      if (t >= now && (t - now) <= 30 * 60 * 1000) soon++;
    }
    return { upcoming, today, tomorrow, week, past, soon };
  }, [demos]);

  // ── Outcome action ─────────────────────────────────────────────
  async function logOutcome(demo, outcome) {
    try {
      const r = await apiFetch('/api/demo/log-outcome', {
        method: 'POST',
        body: JSON.stringify({ id: demo.id, outcome }),
      });
      if (r?.ok) {
        showToast(`Outcome logged: ${outcome.replace(/_/g, ' ')}`, 'success');
        await refresh();
      }
    } catch (err) {
      showToast(`Could not log outcome: ${err.message || err}`, 'error');
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 via-amber-900 to-amber-700 text-white rounded-2xl p-6">
        <div className="text-amber-200 text-xs font-semibold uppercase tracking-wider">Demo Pipeline</div>
        <h2 className="text-3xl font-bold mt-1">Your demo funnel · in one screen</h2>
        <p className="text-amber-100 text-sm mt-2 max-w-3xl">
          Every booked demo, every prep submission, every reminder. Click any demo to see the prep answers the prospect filled in.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5">
          <DemoStatTile label="Starting < 30 min" value={counts.soon} tone={counts.soon > 0 ? 'red' : 'slate'}/>
          <DemoStatTile label="Today" value={counts.today} tone="emerald"/>
          <DemoStatTile label="Tomorrow" value={counts.tomorrow} tone="amber"/>
          <DemoStatTile label="This week" value={counts.week} tone="sky"/>
          <DemoStatTile label="All upcoming" value={counts.upcoming} tone="slate"/>
        </div>
      </div>

      {/* Filter strip */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-2 flex-wrap">
        <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Horizon</span>
        {[
          ['upcoming', 'Upcoming'],
          ['today',    'Today'],
          ['tomorrow', 'Tomorrow'],
          ['week',     'This week'],
          ['past',     'Past'],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setHorizon(k)}
            className={`text-xs font-bold uppercase tracking-tight px-3 py-1.5 rounded ${horizon === k ? 'bg-amber-600 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
            {l}
          </button>
        ))}
        <button onClick={refresh} className="ml-auto text-xs font-bold uppercase tracking-tight px-3 py-1.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700">
          Refresh
        </button>
      </div>

      {/* Demo list */}
      {loading && <div className="text-sm italic text-slate-500">Loading demos…</div>}

      {!loading && filtered.length === 0 && (
        <div className="border-2 border-dashed border-slate-300 px-6 py-16 text-center text-slate-500 text-sm rounded-xl bg-white">
          <div className="text-base font-bold text-slate-700">No demos in this window.</div>
          <div className="text-xs mt-2">When a prospect books on your calendar, they'll appear here automatically.</div>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map(d => {
            const prep = preps[String(d.prospect_email || '').toLowerCase()];
            const t = new Date(d.scheduled_at).getTime();
            const minsAway = Math.round((t - Date.now()) / 60000);
            const inPast = minsAway < 0;
            const startingSoon = minsAway >= 0 && minsAway <= 30;
            const outcome = d.outcome || null;
            return (
              <div key={d.id} className={`border-2 rounded-xl p-4 ${startingSoon ? 'border-rose-400 bg-rose-50' : outcome === 'closed_won' ? 'border-emerald-400 bg-emerald-50' : outcome === 'no_show' || outcome === 'closed_lost' || outcome === 'cancelled' ? 'border-slate-300 bg-slate-50 opacity-70' : 'border-slate-200 bg-white'}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[280px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-lg font-black text-slate-900">{d.prospect_name}</span>
                      {prep && (
                        <span className="bg-emerald-100 text-emerald-800 text-[0.6rem] font-black uppercase tracking-wider px-2 py-0.5 rounded-full" title={`Prep submitted ${new Date(prep.submitted_at).toLocaleString()}`}>
                          ✓ Prep Submitted
                        </span>
                      )}
                      {!prep && (
                        <span className="bg-amber-100 text-amber-800 text-[0.6rem] font-black uppercase tracking-wider px-2 py-0.5 rounded-full">
                          ⏳ Prep pending
                        </span>
                      )}
                      <span className="bg-slate-100 text-slate-700 text-[0.55rem] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded">
                        {d.source}
                      </span>
                      {outcome && (
                        <span className={`text-[0.6rem] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${outcome === 'closed_won' ? 'bg-emerald-200 text-emerald-900' : outcome === 'closed_lost' ? 'bg-rose-200 text-rose-900' : outcome === 'no_show' ? 'bg-slate-200 text-slate-700' : outcome === 'needs_followup' ? 'bg-amber-200 text-amber-900' : 'bg-slate-200 text-slate-700'}`}>
                          {outcome.replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-600 mt-1 flex items-center gap-3 flex-wrap">
                      <span className={`font-mono ${startingSoon ? 'text-rose-700 font-bold' : 'text-slate-700'}`}>
                        {new Date(d.scheduled_at).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </span>
                      {!inPast && minsAway < 60 * 24 && (
                        <span className={`text-xs font-bold ${startingSoon ? 'text-rose-700' : 'text-slate-500'}`}>
                          {minsAway < 60 ? `in ${minsAway} min` : `in ${Math.round(minsAway/60)}h`}
                        </span>
                      )}
                      {inPast && (
                        <span className="text-xs font-bold text-slate-500">{Math.abs(minsAway) < 60 ? `${Math.abs(minsAway)} min ago` : `${Math.round(Math.abs(minsAway)/60)}h ago`}</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                      {d.prospect_email && <a href={`mailto:${d.prospect_email}`} className="text-sky-700 hover:underline">{d.prospect_email}</a>}
                      {d.prospect_phone && <button onClick={() => requestPhoneCall({ phone: d.prospect_phone, name: d.prospect_name })} className="text-sky-700 hover:underline font-mono bg-transparent border-0 cursor-pointer p-0">{d.prospect_phone}</button>}
                    </div>

                    {/* Reminder cadence indicator */}
                    <div className="text-[10px] text-slate-500 mt-2 flex items-center gap-3 flex-wrap">
                      <span className="font-bold uppercase tracking-wider">Reminders:</span>
                      <span className={d.t_minus_24_sent_at ? 'text-emerald-700' : 'text-slate-400'}>{d.t_minus_24_sent_at ? '✓ T-24h' : '○ T-24h'}</span>
                      <span className={d.t_minus_1_sent_at ? 'text-emerald-700' : 'text-slate-400'}>{d.t_minus_1_sent_at ? '✓ T-1h' : '○ T-1h'}</span>
                      <span className={d.t_minus_15_sent_at ? 'text-emerald-700' : 'text-slate-400'}>{d.t_minus_15_sent_at ? '✓ T-15m' : '○ T-15m'}</span>
                      {(d.post_demo_0_sent_at || d.post_demo_24_sent_at || d.post_demo_48_sent_at || d.post_demo_72_sent_at) && (
                        <>
                          <span className="text-slate-300">·</span>
                          <span className="font-bold uppercase tracking-wider">Post-demo:</span>
                          <span className={d.post_demo_0_sent_at ? 'text-emerald-700' : 'text-slate-400'}>{d.post_demo_0_sent_at ? '✓ Recap' : '○ Recap'}</span>
                          <span className={d.post_demo_24_sent_at ? 'text-emerald-700' : 'text-slate-400'}>{d.post_demo_24_sent_at ? '✓ 24h' : '○ 24h'}</span>
                          <span className={d.post_demo_48_sent_at ? 'text-emerald-700' : 'text-slate-400'}>{d.post_demo_48_sent_at ? '✓ 48h' : '○ 48h'}</span>
                          <span className={d.post_demo_72_sent_at ? 'text-emerald-700' : 'text-slate-400'}>{d.post_demo_72_sent_at ? '✓ 72h' : '○ 72h'}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5 items-end">
                    {d.meeting_url && (
                      <a href={d.meeting_url} target="_blank" rel="noopener noreferrer"
                         className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-tight px-3 py-1.5 rounded">
                        Join meeting →
                      </a>
                    )}
                    {prep && (
                      <button onClick={() => setOpenPrep(prep)} className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold uppercase tracking-tight px-3 py-1.5 rounded">
                        View prep answers
                      </button>
                    )}
                  </div>
                </div>

                {/* Pain preview if prep submitted */}
                {prep?.pain && (
                  <div className="mt-3 bg-amber-50 border-l-4 border-amber-400 px-3 py-2 rounded text-sm text-slate-800">
                    <span className="text-[0.55rem] font-black uppercase tracking-wider text-amber-700">#1 thing breaking:</span>
                    <div className="mt-0.5 italic">"{prep.pain}"</div>
                  </div>
                )}

                {/* Outcome actions — show after the demo time has passed */}
                {inPast && !outcome && (
                  <div className="mt-3 pt-3 border-t border-slate-200 flex items-center gap-2 flex-wrap">
                    <span className="text-[0.6rem] font-black uppercase tracking-wider text-slate-500 mr-2">Mark outcome:</span>
                    <button onClick={() => logOutcome(d, 'closed_won')} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded">✓ Closed in room</button>
                    <button onClick={() => logOutcome(d, 'needs_followup')} className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold px-3 py-1.5 rounded">⌛ Needs follow-up</button>
                    <button onClick={() => logOutcome(d, 'no_show')} className="bg-slate-600 hover:bg-slate-700 text-white text-xs font-bold px-3 py-1.5 rounded">⌀ No-show</button>
                    <button onClick={() => logOutcome(d, 'closed_lost')} className="bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold px-3 py-1.5 rounded">✗ Lost</button>
                    <button onClick={() => logOutcome(d, 'cancelled')} className="bg-slate-300 hover:bg-slate-400 text-slate-800 text-xs font-bold px-3 py-1.5 rounded">Cancelled</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Prep answers modal */}
      {openPrep && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpenPrep(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-br from-slate-900 to-amber-900 text-white p-5 rounded-t-2xl">
              <div className="text-amber-200 text-[0.6rem] font-black uppercase tracking-[0.3em]">Prep Answers</div>
              <div className="text-xl font-black mt-1">{openPrep.name}</div>
              <div className="text-amber-100 text-xs mt-1">{openPrep.email}</div>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <PrepRow label="Lines written" value={Array.isArray(openPrep.lines) ? openPrep.lines.join(' · ') : '—'}/>
              <PrepRow label="Team size"     value={openPrep.team || '—'}/>
              <PrepRow label="Current stack" value={openPrep.stack || '—'}/>
              <PrepRow label="Lead volume"   value={openPrep.lead_volume || '—'}/>
              <PrepRow label="Current CPA"   value={openPrep.cpa || '—'} highlight/>
              <div>
                <div className="text-[0.55rem] font-black uppercase tracking-wider text-amber-700 mb-1">#1 thing breaking</div>
                <div className="bg-amber-50 border-l-4 border-amber-500 px-3 py-2 rounded text-slate-800 italic">"{openPrep.pain || '—'}"</div>
              </div>
              <div className="text-[10px] text-slate-400 pt-3 border-t border-slate-100">
                Submitted {new Date(openPrep.submitted_at).toLocaleString()}
              </div>
            </div>
            <div className="px-5 pb-5">
              <button onClick={() => setOpenPrep(null)} className="w-full bg-slate-900 hover:bg-black text-white font-bold py-2.5 rounded-lg text-sm">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DemoStatTile({ label, value, tone = 'slate' }) {
  const toneClasses = {
    red:     'bg-red-500/20 text-red-100 border-red-400/30',
    emerald: 'bg-emerald-500/20 text-emerald-100 border-emerald-400/30',
    amber:   'bg-amber-500/20 text-amber-100 border-amber-400/30',
    sky:     'bg-sky-500/20 text-sky-100 border-sky-400/30',
    slate:   'bg-white/10 text-slate-100 border-white/20',
  };
  return (
    <div className={`border rounded-lg p-3 ${toneClasses[tone] || toneClasses.slate}`}>
      <div className="text-[0.55rem] font-black uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-black tabular-nums mt-1">{value}</div>
    </div>
  );
}

function PrepRow({ label, value, highlight = false }) {
  return (
    <div className="flex items-baseline gap-3">
      <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500 w-28 shrink-0">{label}</div>
      <div className={`flex-1 ${highlight ? 'font-bold text-slate-900' : 'text-slate-700'}`}>{value}</div>
    </div>
  );
}

function AIAgentsView({ currentUser, settings = {}, leads = [], calls = [], producers = {}, transactions = [], onGotoTab = () => {}, showToast = () => {} }) {
  const [runs, setRuns]       = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy]       = React.useState(null); // agent_key currently running
  const ownerId = currentUser?.id;

  const loadRuns = React.useCallback(async () => {
    if (!ownerId) return;
    setLoading(true);
    const { data } = await supabase
      .from('ai_agent_runs')
      .select('*')
      .eq('owner_id', ownerId)
      .order('started_at', { ascending: false })
      .limit(40);
    setRuns(data || []);
    setLoading(false);
  }, [ownerId]);
  React.useEffect(() => { loadRuns(); }, [loadRuns]);

  const latestByAgent = React.useMemo(() => {
    const out = {};
    for (const r of runs) {
      if (!out[r.agent_key] && r.status === 'succeeded') out[r.agent_key] = r;
    }
    return out;
  }, [runs]);

  const trigger = async (agentKey, body = {}) => {
    setBusy(agentKey);
    try {
      const res = await apiFetch(`/api/agents/${agentKey.replace(/_/g, '-')}`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      showToast(res?.summary || `${agentKey} ran`, 'success');
      await loadRuns();
    } catch (err) {
      showToast(`${agentKey} failed: ${err.message || err}`, 'error');
    } finally {
      setBusy(null);
    }
  };

  // Triggers per agent — supplies the body the endpoint needs.
  const runMorning   = () => trigger('morning_brief');
  const runLapseRisk = () => trigger('lapse_risk');
  const runPrioritize = () => trigger('prioritize_leads', {
    leads, calls, topN: 10,
  });
  const runCoach = () => trigger('coach', {
    producers: Object.entries(producers).map(([pk, p]) => ({ ...p, prodNum: pk })),
    transactions,
    leads,
    window_days: 7,
  });

  return (
    <div className="space-y-4">
      <div className="border-2 border-slate-900 bg-slate-900 px-4 py-3 flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-violet-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">AI Agents</div>
          <div className="text-white text-base font-black uppercase tracking-tight mt-0.5">Briefings · Prioritization · Risk · Coaching</div>
        </div>
        <div className="text-[0.6rem] uppercase tracking-wider text-slate-400">
          {runs.length} run{runs.length === 1 ? '' : 's'} on file
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <AgentCard
          agentKey="morning_brief"
          title="Morning Briefing"
          accent="amber"
          description="Daily standup — state of the agency, top action for today, alerts, focus list. Runs automatically every morning; tap to regenerate."
          latest={latestByAgent['morning_brief']}
          busy={busy === 'morning_brief'}
          onRun={runMorning}
          onGotoTab={onGotoTab}
          renderResult={renderBriefingResult}
        />
        <AgentCard
          agentKey="prioritize_leads"
          title="Prioritize Leads"
          accent="emerald"
          description={`Scores ${leads.length || 'your'} leads and ranks the top 10 to call FIRST today. Uses recency, engagement, premium fit, and your tags.`}
          latest={latestByAgent['prioritize_leads']}
          busy={busy === 'prioritize_leads'}
          onRun={runPrioritize}
          disabled={leads.length === 0}
          disabledHint="No leads in pool — import some first."
          onGotoTab={onGotoTab}
          renderResult={renderPrioritizeResult}
        />
        <AgentCard
          agentKey="lapse_risk"
          title="Lapse / Persistency Risk"
          accent="rose"
          description="Scores active health policies for lapse risk using commission-gap aging. Catches silent disenrollment before the carrier files it."
          latest={latestByAgent['lapse_risk']}
          busy={busy === 'lapse_risk'}
          onRun={runLapseRisk}
          onGotoTab={onGotoTab}
          renderResult={renderLapseRiskResult}
        />
        <AgentCard
          agentKey="coach"
          title="Performance Coach"
          accent="sky"
          description="One paragraph per producer, weekly. Specific numbers, one suggested action each. Flags going-inactive and chargeback spikes."
          latest={latestByAgent['coach']}
          busy={busy === 'coach'}
          onRun={runCoach}
          disabled={Object.keys(producers || {}).length === 0}
          disabledHint="No producers loaded — wait for transactions to import."
          onGotoTab={onGotoTab}
          renderResult={renderCoachResult}
        />
      </div>

      {/* Run history */}
      <div className="bg-white border border-slate-200">
        <div className="px-4 py-2 border-b border-slate-200 flex items-baseline justify-between">
          <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">Recent runs</div>
          <button onClick={loadRuns} className="text-[0.55rem] uppercase tracking-wider text-slate-500 hover:text-slate-800 underline">Refresh</button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {['When', 'Agent', 'Trigger', 'Status', 'Summary', 'Model', 'Latency', 'Cost'].map(h => (
                <th key={h} className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-400">Loading…</td></tr>
            ) : runs.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-400">No runs yet. Trigger any agent above.</td></tr>
            ) : runs.slice(0, 25).map(r => (
              <tr key={r.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 text-xs font-mono text-slate-600 whitespace-nowrap">{r.started_at ? new Date(r.started_at).toLocaleString() : '—'}</td>
                <td className="px-3 py-2 text-xs font-bold text-slate-900">{r.agent_key}</td>
                <td className="px-3 py-2 text-[0.55rem] uppercase tracking-wider text-slate-500">{r.trigger}</td>
                <td className="px-3 py-2">
                  <span className={`text-[0.55rem] font-black uppercase tracking-wider px-2 py-0.5 ${
                    r.status === 'succeeded' ? 'bg-emerald-100 text-emerald-800' :
                    r.status === 'failed'    ? 'bg-rose-100    text-rose-800'    :
                    r.status === 'running'   ? 'bg-amber-100   text-amber-800'   :
                                               'bg-slate-100   text-slate-700'
                  }`}>{r.status}</span>
                </td>
                <td className="px-3 py-2 text-xs text-slate-700 max-w-[400px] truncate" title={r.summary || ''}>{r.summary || '—'}</td>
                <td className="px-3 py-2 text-[0.55rem] uppercase tracking-wider text-slate-500">{r.model || '—'}</td>
                <td className="px-3 py-2 text-[0.6rem] font-mono text-slate-500">{r.latency_ms ? `${r.latency_ms}ms` : '—'}</td>
                <td className="px-3 py-2 text-[0.6rem] font-mono text-slate-500">{r.cost_usd != null ? `$${Number(r.cost_usd).toFixed(3)}` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AgentCard({ agentKey, title, accent, description, latest, busy, onRun, disabled, disabledHint, onGotoTab, renderResult, openClientProfile = () => {} }) {
  const accentBorder =
    accent === 'amber'   ? 'border-amber-400'   :
    accent === 'emerald' ? 'border-emerald-400' :
    accent === 'rose'    ? 'border-rose-400'    :
    accent === 'sky'     ? 'border-sky-400'     :
                           'border-slate-400';
  const accentText =
    accent === 'amber'   ? 'text-amber-700'   :
    accent === 'emerald' ? 'text-emerald-700' :
    accent === 'rose'    ? 'text-rose-700'    :
    accent === 'sky'     ? 'text-sky-700'     :
                           'text-slate-700';
  const accentBtn =
    accent === 'amber'   ? 'bg-amber-600   hover:bg-amber-700'   :
    accent === 'emerald' ? 'bg-emerald-600 hover:bg-emerald-700' :
    accent === 'rose'    ? 'bg-rose-600    hover:bg-rose-700'    :
    accent === 'sky'     ? 'bg-sky-600     hover:bg-sky-700'     :
                           'bg-slate-700   hover:bg-slate-800';
  return (
    <div className={`bg-white border-2 ${accentBorder}`}>
      <div className="px-4 py-3 border-b border-slate-200 flex items-baseline justify-between gap-2 flex-wrap">
        <div>
          <div className={`text-[0.55rem] font-black uppercase tracking-[0.2em] ${accentText}`}>Agent · {agentKey}</div>
          <div className="text-sm font-black text-slate-900 mt-0.5">{title}</div>
        </div>
        <button
          onClick={onRun}
          disabled={busy || disabled}
          title={disabled ? disabledHint : undefined}
          className={`text-xs font-black uppercase tracking-wider px-3 py-1.5 text-white disabled:opacity-40 ${accentBtn}`}
        >{busy ? 'Running…' : 'Run now'}</button>
      </div>
      <div className="p-3 text-xs text-slate-600">{description}</div>
      {disabled && disabledHint && (
        <div className="px-3 pb-3 text-[0.6rem] uppercase tracking-wider text-amber-700">{disabledHint}</div>
      )}
      {latest && (
        <div className="border-t border-slate-200 bg-slate-50 px-3 py-2">
          <div className="text-[0.55rem] uppercase tracking-wider text-slate-500 flex items-center gap-2">
            <span>Latest result</span>
            <span className="font-mono text-slate-400">{new Date(latest.started_at).toLocaleString()}</span>
          </div>
          <div className="mt-1.5">{renderResult ? renderResult(latest.result, onGotoTab, openClientProfile) : <pre className="text-[0.6rem] font-mono overflow-auto">{JSON.stringify(latest.result, null, 2).slice(0, 500)}</pre>}</div>
        </div>
      )}
    </div>
  );
}

// ── Per-agent result renderers ─────────────────────────────────────
function renderBriefingResult(r, onGotoTab) {
  if (!r) return null;
  return (
    <div className="space-y-2 text-xs">
      {r.headline && <div className="text-sm font-bold text-slate-900">{r.headline}</div>}
      {r.top_action && (
        <div className="bg-amber-50 border-l-4 border-amber-600 px-2 py-1.5">
          <div className="text-[0.55rem] font-black uppercase tracking-wider text-amber-700">Top action</div>
          <div className="text-slate-900 font-bold mt-0.5">{r.top_action}</div>
        </div>
      )}
      {Array.isArray(r.alerts) && r.alerts.length > 0 && (
        <div>
          <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500 mb-1">Alerts</div>
          {r.alerts.map((a, i) => (
            <div key={i} className={`text-[0.7rem] py-0.5 ${
              a.severity === 'high'   ? 'text-rose-800'  :
              a.severity === 'medium' ? 'text-amber-800' :
                                        'text-slate-700'}`}>
              <span className="font-bold">{a.title}</span>{a.detail ? ` — ${a.detail}` : ''}
              {a.link_tab && <button onClick={() => onGotoTab(a.link_tab)} className="ml-1 text-sky-700 underline">open →</button>}
            </div>
          ))}
        </div>
      )}
      {Array.isArray(r.focus_list) && r.focus_list.length > 0 && (
        <div>
          <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500 mb-1">Focus list</div>
          {r.focus_list.map((f, i) => (
            <div key={i} className="text-[0.7rem] py-0.5">
              <span className="font-bold text-slate-900">{f.name}</span>
              <span className="text-[0.55rem] uppercase tracking-wider text-slate-500 ml-1">[{f.kind}]</span>
              {f.why && <span className="text-slate-600"> — {f.why}</span>}
              {f.link_tab && <button onClick={() => onGotoTab(f.link_tab)} className="ml-1 text-sky-700 underline">open →</button>}
            </div>
          ))}
        </div>
      )}
      {Array.isArray(r.wins) && r.wins.length > 0 && (
        <div>
          <div className="text-[0.55rem] font-black uppercase tracking-wider text-emerald-700 mb-1">Wins</div>
          {r.wins.map((w, i) => <div key={i} className="text-[0.7rem] text-emerald-900">✓ {w}</div>)}
        </div>
      )}
    </div>
  );
}

function renderPrioritizeResult(r) {
  if (!r) return null;
  const top = Array.isArray(r.top) ? r.top : [];
  if (top.length === 0) return <div className="text-xs italic text-slate-500">No prioritized leads.</div>;
  return (
    <div className="space-y-1.5 text-xs">
      {top.slice(0, 6).map((l, i) => (
        <div key={l.id || i} className="border-l-2 border-emerald-400 pl-2">
          <div className="flex items-baseline gap-2">
            <span className="text-[0.55rem] font-black uppercase tracking-wider text-emerald-700">#{i + 1}</span>
            <span className="font-bold text-slate-900">{l.name || '—'}</span>
            <span className="text-[0.55rem] font-mono text-slate-500">score {l.score}</span>
          </div>
          {l.suggested_next_step && <div className="text-[0.7rem] text-slate-700 mt-0.5">→ {l.suggested_next_step}</div>}
          {Array.isArray(l.reasons) && l.reasons.length > 0 && (
            <div className="text-[0.6rem] text-slate-500 mt-0.5">{l.reasons.join(' · ')}</div>
          )}
        </div>
      ))}
      {top.length > 6 && <div className="text-[0.55rem] uppercase tracking-wider text-slate-400">+ {top.length - 6} more</div>}
    </div>
  );
}

function renderLapseRiskResult(r, _onGotoTab, openClientProfile = () => {}) {
  if (!r) return null;
  const at = Array.isArray(r.at_risk) ? r.at_risk : [];
  const s  = r.summary_stats || {};
  return (
    <div className="space-y-2 text-xs">
      <div className="flex items-baseline gap-3 flex-wrap text-[0.65rem]">
        <span className="text-rose-700 font-black">{s.critical || 0} critical</span>
        <span className="text-amber-700 font-black">{s.high || 0} high</span>
        <span className="text-slate-500">{s.medium || 0} medium</span>
        <span className="ml-auto text-rose-700 font-black">${Math.round(s.mrr_at_risk || 0).toLocaleString()}/mo at risk</span>
      </div>
      {at.slice(0, 5).map((p, i) => (
        <div key={p.policy_id || i} className="border-l-2 border-rose-400 pl-2">
          <div className="flex items-baseline gap-2">
            <span className="text-[0.55rem] font-black uppercase tracking-wider text-rose-700">{p.risk_band}</span>
            <ClientNameLink name={p.client_name} openClientProfile={openClientProfile} className="font-bold text-slate-900 hover:underline"/>
            <span className="text-[0.55rem] text-slate-500">{p.carrier} · {p.plan_name}</span>
            <span className="ml-auto text-[0.55rem] font-mono text-slate-500">score {p.score}</span>
          </div>
          {p.suggested_action && <div className="text-[0.7rem] text-slate-700 mt-0.5">→ {p.suggested_action}</div>}
          {Array.isArray(p.reasons) && p.reasons.length > 0 && (
            <div className="text-[0.6rem] text-slate-500 mt-0.5">{p.reasons.join(' · ')}</div>
          )}
        </div>
      ))}
      {at.length > 5 && <div className="text-[0.55rem] uppercase tracking-wider text-slate-400">+ {at.length - 5} more</div>}
    </div>
  );
}

function renderCoachResult(r) {
  if (!r) return null;
  const coached = Array.isArray(r.coached) ? r.coached : [];
  if (coached.length === 0) return <div className="text-xs italic text-slate-500">No producers coached.</div>;
  return (
    <div className="space-y-2 text-xs">
      {coached.slice(0, 4).map((c, i) => (
        <div key={c.prod_num || i} className="border-l-2 border-sky-400 pl-2">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-bold text-slate-900">{c.name}</span>
            <span className="text-[0.55rem] uppercase tracking-wider text-sky-700">grade {c.grade}</span>
            {c.risk_flag && <span className="text-[0.55rem] uppercase tracking-wider text-rose-700 bg-rose-50 px-1.5 py-0.5">{c.risk_flag.replace(/_/g, ' ')}</span>}
          </div>
          {c.headline && <div className="text-[0.7rem] font-bold text-slate-800 mt-0.5">{c.headline}</div>}
          {c.note && <div className="text-[0.7rem] text-slate-700 mt-0.5">{c.note}</div>}
          {c.suggested_action && <div className="text-[0.7rem] text-sky-700 mt-0.5">→ {c.suggested_action}</div>}
        </div>
      ))}
      {coached.length > 4 && <div className="text-[0.55rem] uppercase tracking-wider text-slate-400">+ {coached.length - 4} more producers</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PLAN INTELLIGENCE ENGINE
//
// Agents upload an SBC / EOC / Schedule of Benefits / formulary as a
// PDF. The server forwards it to Claude (native PDF reader, no parser
// dep) which returns:
//   1. Structured fields (deductible, OOP max, copays, RX tiers)
//   2. Category text chunks for grounded RAG Q&A
//   3. Selling-points + exclusions lists
//
// Three operator surfaces in one tab:
//   ► Upload + structured extraction
//   ► Library grid (filter by carrier · state · metal)
//   ► Grounded Q&A — single plan OR a filter set; the answer cites
//     only what's in the document corpus, never invents a copay.
// ═══════════════════════════════════════════════════════════════════
function PlanIntelligenceView({ currentUser, showToast = () => {} }) {
  const [plans,    setPlans]    = React.useState([]);
  const [loading,  setLoading]  = React.useState(true);
  const [filter,   setFilter]   = React.useState({ carrier: '', state: '', metal: '' });
  const [selected, setSelected] = React.useState(null);     // single plan focus
  const [uploading, setUploading] = React.useState(false);
  const fileRef = React.useRef(null);

  const [question,    setQuestion]    = React.useState('');
  const [askMode,     setAskMode]     = React.useState('agent');
  const [asking,      setAsking]      = React.useState(false);
  const [answer,      setAnswer]      = React.useState(null);

  // Comparison mode — set of plan ids the agent has checked off
  // for a side-by-side handout. Activating compareMode shows
  // checkboxes on each card and a "Print comparison" button.
  const [compareMode, setCompareMode] = React.useState(false);
  const [compareIds,  setCompareIds]  = React.useState(new Set());
  const [printing,    setPrinting]    = React.useState(false);

  const ownerId = currentUser?.id;

  // ── Load library ───────────────────────────────────────────────
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!ownerId) return;
      setLoading(true);
      const { data, error } = await supabase
        .from('carrier_plans')
        .select('*')
        .eq('owner_id', ownerId)
        .eq('status', 'active')
        .order('created_at', { ascending: false });
      if (cancelled) return;
      if (error) showToast(`Load failed: ${error.message}`, 'error');
      setPlans(data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [ownerId, showToast]);

  // ── Derived: filter dropdown options + filtered list ───────────
  const carriers = React.useMemo(() =>
    Array.from(new Set(plans.map(p => p.carrier_short).filter(Boolean))).sort()
  , [plans]);
  const states = React.useMemo(() =>
    Array.from(new Set(plans.map(p => p.state).filter(Boolean))).sort()
  , [plans]);
  const filtered = React.useMemo(() => plans.filter(p =>
    (!filter.carrier || p.carrier_short === filter.carrier) &&
    (!filter.state   || p.state         === filter.state)   &&
    (!filter.metal   || p.metal_level   === filter.metal)
  ), [plans, filter]);

  // ── Upload + extract ───────────────────────────────────────────
  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      showToast('PDF is larger than 20 MB — split into chapters first.', 'error');
      return;
    }
    setUploading(true);
    try {
      const base64 = await fileToBase64(file);
      const body = {
        pdfBase64:  base64,
        mimeType:   file.type || 'application/pdf',
        filename:   file.name,
        sourceKind: guessSourceKind(file.name),
      };
      const res = await apiFetch('/api/plan-intelligence/extract', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (res?.plan) {
        setPlans(p => [res.plan, ...p]);
        setSelected(res.plan);
        showToast(`Extracted ${res.plan.carrier_short || res.plan.carrier || 'plan'} · ${res.chunkCount} chunks`, 'success');
      }
    } catch (err) {
      showToast(`Extract failed: ${err.message || err}`, 'error');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // ── Ask grounded question ──────────────────────────────────────
  const ask = async () => {
    if (!question.trim()) return;
    setAsking(true);
    setAnswer(null);
    try {
      const scope = selected
        ? { plan_id: selected.id }
        : { carrier: filter.carrier || undefined,
            state:   filter.state   || undefined,
            metal:   filter.metal   || undefined };
      const res = await apiFetch('/api/plan-intelligence/ask', {
        method: 'POST',
        body: JSON.stringify({
          question: question.trim(),
          mode:     askMode,
          ...scope,
        }),
      });
      setAnswer(res);
    } catch (err) {
      setAnswer({ answer: `Error: ${err.message || err}`, citedPlans: [], citedChunks: [] });
    } finally {
      setAsking(false);
    }
  };

  const archivePlan = async (plan) => {
    if (!(await hqConfirm({
      title: `Archive "${plan.carrier_short} · ${plan.plan_name}"?`,
      body: 'You can restore from the database if needed.',
      confirmLabel: 'Archive plan',
    }))) return;
    const { error } = await supabase.from('carrier_plans').update({ status: 'archived' }).eq('id', plan.id);
    if (error) { showToast(`Archive failed: ${error.message}`, 'error'); return; }
    setPlans(p => p.filter(x => x.id !== plan.id));
    if (selected?.id === plan.id) setSelected(null);
    showToast('Plan archived', 'success');
  };

  const comparePlans = plans.filter(p => compareIds.has(p.id));

  return (
    <div className="space-y-4">
      {/* Print-mode comparison sheet — renders ONLY inside @media print
          when the agent clicks "Print comparison". Drives a clean,
          consumer-facing one-page handout. */}
      {printing && comparePlans.length >= 2 && <PlanComparisonPrintLayout plans={comparePlans}/>}

      {/* Header */}
      <div className="border-2 border-slate-900 bg-slate-900 px-4 py-3 flex items-baseline justify-between flex-wrap gap-2 pi-screen">
        <div>
          <div className="text-cyan-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">Plan Intelligence Engine</div>
          <div className="text-white text-base font-black uppercase tracking-tight mt-0.5">Carrier Knowledge Base · Grounded Q&amp;A</div>
        </div>
        <div className="text-[0.6rem] uppercase tracking-wider text-slate-400">
          {plans.length} plan{plans.length === 1 ? '' : 's'} indexed · {carriers.length} carrier{carriers.length === 1 ? '' : 's'} · {states.length} state{states.length === 1 ? '' : 's'}
        </div>
      </div>

      {/* Upload strip */}
      <div className="border-2 border-cyan-300 bg-cyan-50 px-4 py-3 flex items-center gap-3 flex-wrap">
        <div className="text-[0.55rem] font-black uppercase tracking-wider text-cyan-700">Upload SBC / EOC / Schedule of Benefits / Formulary</div>
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,.pdf"
          onChange={e => handleFile(e.target.files?.[0])}
          disabled={uploading}
          className="text-xs"
        />
        {uploading && <span className="text-[0.6rem] uppercase tracking-wider text-cyan-800 animate-pulse">Reading PDF · extracting fields · indexing chunks…</span>}
        <span className="ml-auto text-[0.55rem] uppercase tracking-wider text-slate-500">AI-powered · server-side</span>
      </div>

      {/* Compare bar */}
      <div className="border-2 border-emerald-300 bg-emerald-50 px-4 py-2 flex items-center gap-3 flex-wrap">
        <label className="flex items-center gap-2 text-xs font-bold text-emerald-900">
          <input type="checkbox" checked={compareMode} onChange={e => {
            setCompareMode(e.target.checked);
            if (!e.target.checked) setCompareIds(new Set());
          }}/>
          Side-by-side comparison mode
        </label>
        {compareMode && (
          <>
            <span className="text-[0.6rem] uppercase tracking-wider text-emerald-700">
              {compareIds.size} selected · pick 2-4 plans
            </span>
            <button
              onClick={() => {
                if (compareIds.size < 2) return;
                setPrinting(true);
                setTimeout(() => {
                  window.print();
                  setTimeout(() => setPrinting(false), 500);
                }, 200);
              }}
              disabled={compareIds.size < 2}
              className="ml-auto text-xs font-black uppercase tracking-wider px-3 py-1.5 bg-emerald-700 text-white disabled:opacity-40 hover:bg-emerald-800"
            >
              Print comparison →
            </button>
          </>
        )}
      </div>

      {/* Filter row */}
      <div className="border border-slate-300 bg-white px-4 py-2 flex items-center gap-3 flex-wrap">
        <span className="text-[0.55rem] font-black uppercase tracking-wider text-slate-700">Filter</span>
        <select value={filter.carrier} onChange={e => setFilter(f => ({ ...f, carrier: e.target.value }))}
          className="text-xs border border-slate-300 px-2 py-1">
          <option value="">All carriers</option>
          {carriers.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filter.state} onChange={e => setFilter(f => ({ ...f, state: e.target.value }))}
          className="text-xs border border-slate-300 px-2 py-1">
          <option value="">All states</option>
          {states.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filter.metal} onChange={e => setFilter(f => ({ ...f, metal: e.target.value }))}
          className="text-xs border border-slate-300 px-2 py-1">
          <option value="">All metal levels</option>
          <option value="Bronze">Bronze</option>
          <option value="Silver">Silver</option>
          <option value="Gold">Gold</option>
          <option value="Platinum">Platinum</option>
          <option value="Catastrophic">Catastrophic</option>
        </select>
        {(filter.carrier || filter.state || filter.metal) && (
          <button onClick={() => setFilter({ carrier: '', state: '', metal: '' })}
            className="text-[0.6rem] uppercase tracking-wider text-slate-600 underline">clear</button>
        )}
        <span className="ml-auto text-[0.6rem] uppercase tracking-wider text-slate-500">{filtered.length} match{filtered.length === 1 ? '' : 'es'}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-4">
        {/* Library */}
        <div className="space-y-2">
          {loading && <div className="text-xs text-slate-500 italic">Loading library…</div>}
          {!loading && filtered.length === 0 && (
            <div className="border-2 border-dashed border-slate-300 px-6 py-10 text-center text-slate-500 text-sm">
              {plans.length === 0
                ? 'Upload your first SBC to start building the knowledge base.'
                : 'No plans match these filters.'}
            </div>
          )}
          {filtered.map(p => (
            <PlanCard key={p.id}
              plan={p}
              selected={selected?.id === p.id}
              onSelect={() => setSelected(selected?.id === p.id ? null : p)}
              onArchive={() => archivePlan(p)}
              compareMode={compareMode}
              compareChecked={compareIds.has(p.id)}
              onToggleCompare={() => {
                setCompareIds(prev => {
                  const next = new Set(prev);
                  if (next.has(p.id)) next.delete(p.id);
                  else if (next.size < 4) next.add(p.id);
                  return next;
                });
              }}
            />
          ))}
        </div>

        {/* Q&A column */}
        <div className="space-y-3">
          <div className="border-2 border-slate-900 bg-white">
            <div className="bg-slate-900 px-3 py-2">
              <div className="text-cyan-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">Grounded Q&amp;A</div>
              <div className="text-white text-xs font-bold mt-0.5">
                {selected
                  ? `Scoped to: ${selected.carrier_short || selected.carrier} · ${selected.plan_name}`
                  : filter.carrier || filter.state || filter.metal
                    ? `Scoped to filter (${filtered.length} plan${filtered.length === 1 ? '' : 's'})`
                    : 'Scoped to entire library'}
              </div>
            </div>
            <div className="p-3 space-y-2">
              <textarea
                rows={3}
                value={question}
                onChange={e => setQuestion(e.target.value)}
                placeholder={selected
                  ? 'e.g. What is the specialist copay?'
                  : 'e.g. Which Texas Silver plan has the lowest OOP max?'}
                className="w-full text-xs border border-slate-300 px-2 py-1.5 font-mono resize-y"
              />
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex border border-slate-300">
                  {['agent', 'consumer', 'comparison'].map(m => (
                    <button key={m}
                      onClick={() => setAskMode(m)}
                      className={`text-[0.55rem] font-black uppercase tracking-wider px-2 py-1 ${askMode === m ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-100'}`}>
                      {m}
                    </button>
                  ))}
                </div>
                <button
                  onClick={ask}
                  disabled={!question.trim() || asking}
                  className="ml-auto text-xs font-black uppercase tracking-wider px-3 py-1.5 bg-cyan-600 text-white disabled:opacity-50 hover:bg-cyan-700"
                >{asking ? 'Asking…' : 'Ask'}</button>
              </div>
            </div>
          </div>

          {answer && (
            <div className="border-2 border-cyan-700 bg-white p-3 text-xs space-y-2">
              <div className="text-[0.55rem] font-black uppercase tracking-wider text-cyan-700">Answer</div>
              <div className="whitespace-pre-wrap font-sans leading-snug text-slate-900">{answer.answer}</div>
              {answer.citedChunks?.length > 0 && (
                <details className="pt-1 border-t border-slate-200">
                  <summary className="cursor-pointer text-[0.55rem] font-black uppercase tracking-wider text-slate-600">
                    {answer.citedChunks.length} cited chunk{answer.citedChunks.length === 1 ? '' : 's'}
                  </summary>
                  <div className="space-y-1.5 mt-2">
                    {answer.citedChunks.map(c => (
                      <div key={c.id} className="border-l-2 border-cyan-300 pl-2">
                        <div className="text-[0.55rem] font-black uppercase tracking-wider text-cyan-700">{c.title}</div>
                        <div className="text-[0.65rem] text-slate-700 font-mono">{c.snippet}</div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}

          {/* Suggested questions */}
          {!answer && (
            <div className="border border-slate-200 bg-white p-3 text-xs space-y-1">
              <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-600">Try asking</div>
              {[
                'What is the specialist copay?',
                'How much would an MRI cost?',
                'Does this plan require a referral to see a specialist?',
                'What are the prescription drug tiers and copays?',
                'What is the in-network out-of-pocket maximum?',
                'What is NOT covered by this plan?',
                'Which plans have $0 primary care visits?',
                'Compare specialist copay and OOP max across these plans.',
              ].map(q => (
                <button key={q}
                  onClick={() => setQuestion(q)}
                  className="block text-left text-cyan-700 hover:text-cyan-900 hover:underline w-full">
                  → {q}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Library card — structured fields + selling points + exclusions ─
function PlanCard({ plan, selected, onSelect, onArchive, compareMode, compareChecked, onToggleCompare }) {
  const fmt$ = (n) => n == null ? '—' : `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  return (
    <div
      className={`border-2 ${selected ? 'border-cyan-700 bg-cyan-50' : compareChecked ? 'border-emerald-700 bg-emerald-50' : 'border-slate-300 bg-white hover:border-slate-500'} cursor-pointer`}
      onClick={onSelect}
    >
      <div className="px-3 py-2 border-b border-slate-200 flex items-baseline justify-between flex-wrap gap-2">
        <div className="flex items-baseline gap-2">
          {compareMode && (
            <input
              type="checkbox"
              checked={compareChecked}
              onClick={e => e.stopPropagation()}
              onChange={onToggleCompare}
              className="mt-1"
            />
          )}
          <div>
            <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">
              {plan.carrier_short || plan.carrier || '—'}{plan.state ? ` · ${plan.state}` : ''}{plan.metal_level ? ` · ${plan.metal_level}` : ''}{plan.network_type ? ` · ${plan.network_type}` : ''}
            </div>
            <div className="text-sm font-black text-slate-900">{plan.plan_name || 'Untitled plan'}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {plan.plan_id_hios && <span className="font-mono text-[0.55rem] text-slate-400">{plan.plan_id_hios}</span>}
          <button
            onClick={e => { e.stopPropagation(); onArchive(); }}
            className="text-[0.55rem] uppercase tracking-wider text-rose-700 hover:text-rose-900 underline"
          >archive</button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 text-[0.65rem]">
        <FieldCell label="Deductible (ind)"   value={fmt$(plan.deductible_individual)} />
        <FieldCell label="Deductible (fam)"   value={fmt$(plan.deductible_family)} />
        <FieldCell label="OOP max (ind)"      value={fmt$(plan.oop_max_individual)} />
        <FieldCell label="OOP max (fam)"      value={fmt$(plan.oop_max_family)} />
        <FieldCell label="PCP visit"          value={plan.pcp_visit} />
        <FieldCell label="Specialist"         value={plan.specialist_visit} />
        <FieldCell label="Urgent care"        value={plan.urgent_care} />
        <FieldCell label="ER"                 value={plan.emergency_room} />
        <FieldCell label="Generic Rx"         value={plan.generic_rx} />
        <FieldCell label="Preferred brand"    value={plan.preferred_brand_rx} />
        <FieldCell label="Non-preferred"      value={plan.non_preferred_brand_rx} />
        <FieldCell label="Specialty Rx"       value={plan.specialty_rx} />
      </div>
      {(plan.selling_points?.length || plan.exclusions?.length) > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-slate-200">
          {plan.selling_points?.length > 0 && (
            <div className="p-3 border-r border-slate-200 bg-emerald-50">
              <div className="text-[0.55rem] font-black uppercase tracking-wider text-emerald-700 mb-1">Selling points</div>
              <ul className="text-[0.7rem] text-emerald-900 space-y-0.5">
                {plan.selling_points.slice(0, 6).map((s, i) => <li key={i}>✓ {s}</li>)}
              </ul>
            </div>
          )}
          {plan.exclusions?.length > 0 && (
            <div className="p-3 bg-rose-50">
              <div className="text-[0.55rem] font-black uppercase tracking-wider text-rose-700 mb-1">Not covered</div>
              <ul className="text-[0.7rem] text-rose-900 space-y-0.5">
                {plan.exclusions.slice(0, 6).map((s, i) => <li key={i}>× {s}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
      {plan.referral_required != null && (
        <div className="px-3 py-1 border-t border-slate-200 text-[0.6rem] uppercase tracking-wider text-slate-600 flex items-center gap-2">
          <span>Referral required:</span>
          <span className={`font-black ${plan.referral_required ? 'text-amber-700' : 'text-emerald-700'}`}>
            {plan.referral_required ? 'Yes' : 'No'}
          </span>
        </div>
      )}
    </div>
  );
}

function FieldCell({ label, value }) {
  return (
    <div className="px-3 py-1.5 border-r border-b border-slate-100 last:border-r-0">
      <div className="text-[0.5rem] font-black uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-slate-900 font-mono truncate">{value || '—'}</div>
    </div>
  );
}

// ── Plan Comparison print layout — 2-4 plans side-by-side ──────────
// Renders a consumer-friendly single-page handout the agent can hand
// to a prospect. Hidden on screen via the inline @media rules; only
// becomes visible when window.print() is fired. We hide the rest of
// the app chrome with .pi-screen { display: none } in print mode so
// only this layout is on the page.
function PlanComparisonPrintLayout({ plans }) {
  const fmt$ = (n) => n == null ? '—' : `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  const COLS = plans.length;
  const ROWS = [
    ['Deductible (ind)',  p => fmt$(p.deductible_individual)],
    ['Deductible (fam)',  p => fmt$(p.deductible_family)],
    ['OOP max (ind)',     p => fmt$(p.oop_max_individual)],
    ['OOP max (fam)',     p => fmt$(p.oop_max_family)],
    ['Primary care',      p => p.pcp_visit || '—'],
    ['Specialist',        p => p.specialist_visit || '—'],
    ['Urgent care',       p => p.urgent_care || '—'],
    ['Emergency room',    p => p.emergency_room || '—'],
    ['Preventive',        p => p.preventive_care || '—'],
    ['Diagnostic test',   p => p.diagnostic_test || '—'],
    ['Imaging (MRI/CT)',  p => p.imaging || '—'],
    ['Inpatient hospital',p => p.inpatient_hospital || '—'],
    ['Outpatient surgery',p => p.outpatient_surgery || '—'],
    ['Generic Rx',        p => p.generic_rx || '—'],
    ['Preferred brand',   p => p.preferred_brand_rx || '—'],
    ['Non-preferred',     p => p.non_preferred_brand_rx || '—'],
    ['Specialty Rx',      p => p.specialty_rx || '—'],
    ['Mental health (OP)',p => p.mental_health_outpatient || '—'],
    ['Mental health (IP)',p => p.mental_health_inpatient || '—'],
    ['Maternity office',  p => p.maternity_office || '—'],
    ['Maternity delivery',p => p.maternity_delivery || '—'],
    ['Referral required', p => p.referral_required === true ? 'Yes' : p.referral_required === false ? 'No' : '—'],
  ];
  return (
    <div className="pi-print">
      <style>{`
        @media screen { .pi-print { display: none; } }
        @media print {
          @page { size: letter landscape; margin: 0.4in; }
          body * { visibility: hidden !important; }
          .pi-screen { display: none !important; }
          .pi-print, .pi-print * { visibility: visible !important; }
          .pi-print { position: absolute; left: 0; top: 0; width: 100%; }
        }
        .pi-print { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif; color: #0f172a; }
        .pi-print .pi-title { font-size: 18px; font-weight: 900; letter-spacing: -0.02em; text-transform: uppercase; }
        .pi-print .pi-sub   { font-size: 9px;  letter-spacing: 0.2em; text-transform: uppercase; color: #64748b; }
        .pi-print table     { width: 100%; border-collapse: collapse; margin-top: 12px; }
        .pi-print th, .pi-print td { border: 1px solid #cbd5e1; padding: 6px 8px; font-size: 11px; text-align: left; vertical-align: top; }
        .pi-print th        { background: #0f172a; color: #fff; font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; }
        .pi-print td.label  { background: #f1f5f9; font-weight: 700; font-size: 10px; letter-spacing: 0.05em; text-transform: uppercase; color: #475569; width: 18%; }
        .pi-print .pi-plan-h{ font-size: 12px; font-weight: 800; }
        .pi-print .pi-meta  { font-size: 9px; opacity: 0.85; margin-top: 2px; letter-spacing: 0.05em; text-transform: uppercase; }
        .pi-print .pi-foot  { margin-top: 14px; font-size: 9px; color: #64748b; }
        .pi-print .pi-section { margin-top: 12px; padding: 6px 8px; background: #ecfeff; font-size: 9px; font-weight: 800; letter-spacing: 0.15em; text-transform: uppercase; color: #0e7490; border-left: 3px solid #0891b2; }
      `}</style>
      <div>
        <div className="pi-title">Plan Comparison · {COLS} Plans</div>
        <div className="pi-sub">Prepared for client review</div>
      </div>
      <table>
        <thead>
          <tr>
            <th style={{ width: '18%' }}>Benefit</th>
            {plans.map(p => (
              <th key={p.id}>
                <div className="pi-plan-h">{p.carrier_short || p.carrier || '—'} · {p.plan_name || '—'}</div>
                <div className="pi-meta">{[p.state, p.metal_level, p.network_type].filter(Boolean).join(' · ') || '—'}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ROWS.map(([label, fn]) => (
            <tr key={label}>
              <td className="label">{label}</td>
              {plans.map(p => <td key={p.id}>{fn(p)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="pi-section">Selling points · what wins for each plan</div>
      <table>
        <tbody>
          {plans.map(p => (
            <tr key={p.id}>
              <td className="label">{p.carrier_short || p.carrier} · {p.plan_name}</td>
              <td>{(p.selling_points || []).slice(0, 6).join(' · ') || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="pi-section">Not covered</div>
      <table>
        <tbody>
          {plans.map(p => (
            <tr key={p.id}>
              <td className="label">{p.carrier_short || p.carrier} · {p.plan_name}</td>
              <td>{(p.exclusions || []).slice(0, 8).join(' · ') || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="pi-foot">
        Cost-sharing figures pulled verbatim from each plan's Summary of Benefits and Coverage (SBC). In-network amounts shown. Out-of-network amounts differ. This handout is a side-by-side reference, not a complete description of benefits; refer to the plan document for full terms, exclusions, and limitations.
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => {
      const result = String(reader.result || '');
      const comma  = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function guessSourceKind(name) {
  const s = String(name || '').toLowerCase();
  if (s.includes('sbc')        || s.includes('summary of benefits'))  return 'sbc';
  if (s.includes('formulary')  || s.includes('drug list'))            return 'formulary';
  if (s.includes('eoc')        || s.includes('evidence of coverage')) return 'eoc';
  if (s.includes('schedule of benefits'))                             return 'schedule_of_benefits';
  if (s.includes('brochure'))                                         return 'brochure';
  if (s.includes('provider'))                                         return 'provider_guide';
  return 'sbc';
}

function HealthProductsReferenceView() {
  const [family, setFamily] = React.useState('ancillary');
  const [cat, setCat]       = React.useState('all');
  const cats = Object.fromEntries(FTPIG_CATEGORIES.map(c => [c.id, c]));
  const visible = FTPIG_PRODUCTS.filter(p => cat === 'all' || p.category === cat);
  const counts = {};
  for (const p of FTPIG_PRODUCTS) counts[p.category] = (counts[p.category] || 0) + 1;

  return (
    <div className="space-y-4">
      <div className="bg-black border-l-4 border-teal-500 px-6 py-5 shadow-2xl">
        <div className="text-teal-300 text-[0.6rem] font-black uppercase tracking-[0.3em]">Product Reference · Health</div>
        <div className="text-white text-xl md:text-2xl font-black mt-1.5 uppercase tracking-tight">{HEALTH_PRODUCT_FAMILIES[family].label}</div>
        <div className="text-slate-400 text-xs mt-1.5">{HEALTH_PRODUCT_FAMILIES[family].sub}</div>
      </div>

      {/* Family selector — sits above category filter */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(HEALTH_PRODUCT_FAMILIES).map(([k, f]) => (
          <button
            key={k}
            onClick={() => setFamily(k)}
            className={`text-[0.6rem] font-black uppercase tracking-tight px-4 py-2 border-2 ${family === k ? `bg-${f.tone}-600 text-white border-${f.tone}-700` : `bg-white text-${f.tone}-700 border-${f.tone}-200 hover:border-${f.tone}-500`}`}
          >
            {f.label.split(' · ')[0]}
          </button>
        ))}
      </div>

      {/* Medicare family — reference cards */}
      {family === 'medicare' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {MEDICARE_REFERENCE_CARDS.map(p => (
            <div key={p.id} className="bg-white border-2 border-blue-200 p-4">
              <div className="text-base font-black text-slate-900 uppercase tracking-tight">{p.name}</div>
              <div className="text-xs text-slate-600 mt-1.5">{p.summary}</div>
              <div className="mt-3 space-y-1.5">
                <div className="text-[0.55rem] font-black uppercase tracking-wider text-slate-500">Major carriers</div>
                <div className="text-xs text-slate-700">{p.carriers}</div>
              </div>
              <div className="mt-2 space-y-1.5">
                <div className="text-[0.55rem] font-black uppercase tracking-wider text-emerald-700">Commission shape</div>
                <div className="text-xs text-slate-700">{p.comp}</div>
              </div>
              <div className="mt-2 space-y-1.5">
                <div className="text-[0.55rem] font-black uppercase tracking-wider text-amber-700">Enrollment window</div>
                <div className="text-xs text-slate-700">{p.aep}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ACA family — metal-tier reference */}
      {family === 'aca' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {ACA_REFERENCE_CARDS.map(p => {
              const tone = p.id === 'bronze' ? 'orange' : p.id === 'silver' ? 'slate' : p.id === 'gold' ? 'amber' : 'sky';
              return (
                <div key={p.id} className={`bg-white border-2 border-${tone}-200 p-4`}>
                  <div className={`text-[0.55rem] font-black uppercase tracking-[0.2em] text-${tone}-700`}>Metal Tier</div>
                  <div className="text-lg font-black text-slate-900 uppercase tracking-tight mt-0.5">{p.name}</div>
                  <div className="text-xs text-slate-600 mt-2">{p.summary}</div>
                </div>
              );
            })}
          </div>
          <div className="bg-white border border-slate-200 p-4">
            <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-teal-700 mb-2">ACA · How commission works</div>
            <div className="text-xs text-slate-700 space-y-1.5">
              <div><strong>PMPM</strong> — per member, per month. Carrier pays you ~$20/member-month for as long as the member stays on the plan. Multi-member households compound fast.</div>
              <div><strong>OEP</strong> — Nov 1 – Jan 15 nationally. Outside OEP, Special Enrollment Periods (SEP) require a qualifying life event.</div>
              <div><strong>CSR levels</strong> — Silver plans only. CSR 94 / 87 / 73 / none — the lower the number, the better the cost-sharing reduction for the client.</div>
              <div><strong>APTC</strong> — Advance Premium Tax Credit. Subsidy lowers premium at enrollment. Income misreporting causes clawback at tax time.</div>
              <div><strong>HealthSherpa</strong> — primary enrollment portal. NPN-attributed deeplink in /coverage_summary.</div>
            </div>
          </div>
        </>
      )}

      {/* Ancillary catalog — the existing FTPIG table */}
      {family === 'ancillary' && (
        <>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setCat('all')} className={`text-[0.6rem] font-black uppercase tracking-tight px-3 py-2 border-2 ${cat === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}>
              ALL · {FTPIG_PRODUCTS.length}
            </button>
            {FTPIG_CATEGORIES.map(c => (
              <button key={c.id} onClick={() => setCat(c.id)} className={`text-[0.6rem] font-black uppercase tracking-tight px-3 py-2 border-2 ${cat === c.id ? `bg-${c.tone}-600 text-white border-${c.tone}-700` : `bg-white text-${c.tone}-700 border-${c.tone}-200 hover:border-${c.tone}-500`}`}>
                {c.label} · {counts[c.id] || 0}
              </button>
            ))}
          </div>

          {/* Product list */}
          <div className="bg-white border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">Product</th>
                  <th className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">Category</th>
                  <th className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">UW</th>
                  <th className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">Issue Age</th>
                  <th className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">H/W Chart</th>
                  <th className="text-left px-3 py-2 text-[0.55rem] font-black uppercase tracking-[0.1em] text-slate-500">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visible.map(p => {
                  const c = cats[p.category];
                  return (
                    <tr key={p.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-bold text-slate-900">{p.name}</td>
                      <td className="px-3 py-2"><span className={`text-[0.55rem] font-black uppercase tracking-wider px-2 py-0.5 bg-${c?.tone}-100 text-${c?.tone}-800`}>{c?.label}</span></td>
                      <td className="px-3 py-2 text-xs font-mono">{p.underwriting.join(' / ')}</td>
                      <td className="px-3 py-2 text-xs font-mono">{p.issue_age[0]}–{p.issue_age[1]}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">{p.hw_chart || '—'}</td>
                      <td className="px-3 py-2 text-xs text-slate-600 italic">{p.notes || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="text-[0.6rem] text-slate-400 italic px-2">Indemnity / supplemental plans are NOT MEC and not a substitute for major medical insurance. Always verify in the broker portal before binding.</div>
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// APPOINTMENT REMINDERS — manual scheduling + Google Calendar subscribe
//
// Two halves in one panel:
//   1. Schedule a reminder for a client — sets up SMS at 24h + 1h
//      before the appointment automatically.
//   2. Subscribe in Google Calendar — generates a public ICS URL the
//      agent pastes into Google Calendar (Other calendars → Add by URL)
//      so every appointment shows up on their phone with native alerts.
// ════════════════════════════════════════════════════════════════════
function AppointmentRemindersPanel({ currentUser, showToast = () => {} }) {
  const [clients, setClients] = React.useState([]);
  const [upcoming, setUpcoming] = React.useState([]);
  const [feedToken, setFeedToken] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [draft, setDraft] = React.useState({
    client_id: '', client_name: '', client_phone: '',
    appointment_at: '', title: '', location: 'Phone', notes: '',
    reminder_24h_enabled: true, reminder_1h_enabled: true,
  });

  const load = React.useCallback(async () => {
    if (!currentUser?.id) return;
    const [c, a, t] = await Promise.all([
      supabase.from('clients').select('id, name, phone, timezone').order('name'),
      supabase.from('appointment_reminders').select('*').eq('status', 'scheduled').gte('appointment_at', new Date().toISOString()).order('appointment_at').limit(50),
      supabase.from('calendar_feed_tokens').select('token').eq('owner_id', currentUser.id).maybeSingle(),
    ]);
    setClients(c.data || []);
    setUpcoming(a.data || []);
    setFeedToken(t.data?.token || null);
  }, [currentUser?.id]);

  React.useEffect(() => { load(); }, [load]);

  async function generateFeedToken() {
    if (!currentUser?.id) return;
    setBusy(true);
    try {
      const tok = crypto.getRandomValues(new Uint8Array(24));
      const token = Array.from(tok).map(b => b.toString(16).padStart(2, '0')).join('');
      const { error } = await supabase.from('calendar_feed_tokens').upsert({
        owner_id: currentUser.id, token,
      }, { onConflict: 'owner_id' });
      if (error) { showToast(`Failed: ${error.message}`); return; }
      setFeedToken(token);
      showToast('Calendar feed URL ready');
    } finally {
      setBusy(false);
    }
  }
  async function rotateToken() {
    if (!(await hqConfirm({
      title: 'Rotate the feed URL?',
      body: 'Anything currently subscribed will need to re-subscribe with the new URL.',
      confirmLabel: 'Rotate URL', danger: true,
    }))) return;
    await supabase.from('calendar_feed_tokens').delete().eq('owner_id', currentUser.id);
    setFeedToken(null);
    showToast('Feed URL revoked. Generate a new one below.');
  }

  async function scheduleAppointment() {
    if (!draft.appointment_at) { showToast('Pick a date + time.'); return; }
    let name = draft.client_name?.trim();
    let phone = draft.client_phone?.trim();
    let tz = 'America/Chicago';
    if (draft.client_id) {
      const c = clients.find(x => x.id === draft.client_id);
      if (c) { name = c.name; phone = c.phone; tz = c.timezone || 'America/Chicago'; }
    }
    if (!name) { showToast('Client name required.'); return; }
    if (!phone) { showToast('Phone required for reminders.'); return; }
    const { data, error } = await supabase.from('appointment_reminders').insert({
      owner_id: currentUser.id,
      client_id: draft.client_id || null,
      appointment_at: new Date(draft.appointment_at).toISOString(),
      title: draft.title || null,
      location: draft.location || null,
      notes: draft.notes || null,
      client_name: name,
      client_phone: phone,
      client_timezone: tz,
      reminder_24h_enabled: !!draft.reminder_24h_enabled,
      reminder_1h_enabled:  !!draft.reminder_1h_enabled,
      status: 'scheduled',
    }).select().single();
    if (error) { showToast(`Failed: ${error.message}`); return; }
    setUpcoming(prev => [...prev, data].sort((a, b) => new Date(a.appointment_at) - new Date(b.appointment_at)));
    setDraft({ client_id: '', client_name: '', client_phone: '', appointment_at: '', title: '', location: 'Phone', notes: '', reminder_24h_enabled: true, reminder_1h_enabled: true });
    showToast('Appointment scheduled');
    // Fire-and-forget: push to the operator's connected Google
    // Calendar. The endpoint short-circuits silently if no link is
    // active, so this is safe to call always.
    apiFetch('/api/google-calendar/upsert-event', {
      method: 'POST', body: JSON.stringify({ appointment_id: data.id }),
    }).catch(() => { /* surfaces in google_sync_error column instead */ });
  }
  async function cancelAppt(id) {
    if (!(await hqConfirm({
      title: 'Cancel this appointment?',
      body: 'Reminders will stop.',
      confirmLabel: 'Cancel appointment', danger: true,
    }))) return;
    await supabase.from('appointment_reminders').update({ status: 'cancelled' }).eq('id', id);
    setUpcoming(prev => prev.filter(x => x.id !== id));
    // Mirror the cancel to Google so it disappears from the operator's
    // phone calendar. Safe to call when no link exists.
    apiFetch('/api/google-calendar/delete-event', {
      method: 'POST', body: JSON.stringify({ appointment_id: id }),
    }).catch(() => { /* best-effort */ });
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const feedUrl = feedToken ? `${origin}/api/calendar/feed?token=${feedToken}` : '';
  function copyFeedUrl() {
    if (!feedUrl) return;
    navigator.clipboard?.writeText(feedUrl);
    showToast('Calendar feed URL copied');
  }

  const fmtAt = (iso) => new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  return (
    <div className="bg-white border-2 border-slate-200 p-5 space-y-5">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-500">Calendar</div>
          <div className="text-lg font-black text-slate-900 uppercase tracking-tight mt-1">Appointment Reminders</div>
        </div>
        <div className="text-[0.6rem] uppercase tracking-wider text-slate-400">{upcoming.length} upcoming</div>
      </div>

      {/* Google Calendar subscribe */}
      <div className="bg-blue-50 border-2 border-blue-200 p-4">
        <div className="text-[0.6rem] font-black uppercase tracking-[0.15em] text-blue-800 mb-1">Subscribe in Google Calendar</div>
        <div className="text-xs text-blue-900 mb-3">
          Generates a private URL. Paste into Google Calendar → <strong>Other calendars</strong> → <strong>+ Add by URL</strong>. Every appointment shows up on your phone with a 15-min reminder built in.
        </div>
        {feedToken ? (
          <>
            <div className="bg-white border border-blue-300 px-3 py-2 font-mono text-[10px] break-all">{feedUrl}</div>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <button onClick={copyFeedUrl} className="bg-blue-700 hover:bg-blue-800 text-white text-[0.6rem] font-black uppercase tracking-tight px-3 py-1.5">Copy URL</button>
              <button onClick={rotateToken} className="text-[0.6rem] font-bold uppercase text-red-700 hover:text-red-900 underline">Rotate / revoke</button>
            </div>
          </>
        ) : (
          <button onClick={generateFeedToken} disabled={busy} className="bg-blue-700 hover:bg-blue-800 disabled:opacity-50 text-white text-xs font-black uppercase tracking-tight px-4 py-2">
            Generate Calendar Feed URL
          </button>
        )}
      </div>

      {/* Schedule a reminder */}
      <div className="border-2 border-slate-200 p-4">
        <div className="text-[0.6rem] font-black uppercase tracking-[0.15em] text-slate-700 mb-2">+ Schedule appointment</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <label className="block">
            <span className="text-xs text-slate-500">Existing client (optional)</span>
            <select value={draft.client_id} onChange={e => setDraft(d => ({ ...d, client_id: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 bg-white">
              <option value="">— manual entry —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Date &amp; time *</span>
            <input type="datetime-local" value={draft.appointment_at} onChange={e => setDraft(d => ({ ...d, appointment_at: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5"/>
          </label>
          {!draft.client_id && (
            <>
              <label className="block">
                <span className="text-xs text-slate-500">Client name *</span>
                <input value={draft.client_name} onChange={e => setDraft(d => ({ ...d, client_name: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5"/>
              </label>
              <label className="block">
                <span className="text-xs text-slate-500">Phone (E.164) *</span>
                <input value={draft.client_phone} onChange={e => setDraft(d => ({ ...d, client_phone: e.target.value }))} placeholder="+15125551234" className="mt-1 w-full border border-slate-300 px-2 py-1.5 font-mono"/>
              </label>
            </>
          )}
          <label className="block">
            <span className="text-xs text-slate-500">Title</span>
            <input value={draft.title} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} placeholder="Quote review, First check-in…" className="mt-1 w-full border border-slate-300 px-2 py-1.5"/>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Location</span>
            <input value={draft.location} onChange={e => setDraft(d => ({ ...d, location: e.target.value }))} placeholder="Phone / Address / Zoom URL" className="mt-1 w-full border border-slate-300 px-2 py-1.5"/>
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs text-slate-500">Notes</span>
            <textarea rows={2} value={draft.notes} onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))} className="mt-1 w-full border border-slate-300 px-2 py-1.5 text-sm"/>
          </label>
        </div>
        <div className="flex items-center justify-between mt-3 flex-wrap gap-3">
          <div className="flex items-center gap-3 text-xs">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={draft.reminder_24h_enabled} onChange={e => setDraft(d => ({ ...d, reminder_24h_enabled: e.target.checked }))}/>
              <span className="font-bold uppercase tracking-wider text-slate-700">24h SMS</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={draft.reminder_1h_enabled} onChange={e => setDraft(d => ({ ...d, reminder_1h_enabled: e.target.checked }))}/>
              <span className="font-bold uppercase tracking-wider text-slate-700">1h SMS</span>
            </label>
          </div>
          <button onClick={scheduleAppointment} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase tracking-tight px-4 py-2">
            Schedule
          </button>
        </div>
      </div>

      {/* Upcoming list */}
      <div>
        <div className="text-[0.6rem] font-black uppercase tracking-[0.15em] text-slate-700 mb-2">Upcoming</div>
        {upcoming.length === 0 ? (
          <div className="text-xs text-slate-400 italic py-3 text-center">No upcoming appointments scheduled.</div>
        ) : (
          <div className="space-y-1.5">
            {upcoming.map(a => (
              <div key={a.id} className="flex items-center justify-between gap-3 border border-slate-200 p-2 text-sm">
                <div className="min-w-0">
                  <div className="font-bold text-slate-900 truncate">{a.client_name} {a.title && <span className="font-normal text-slate-500">· {a.title}</span>}</div>
                  <div className="text-xs text-slate-500">{fmtAt(a.appointment_at)} · {a.location || '—'} · {a.client_phone}</div>
                  <div className="text-[0.55rem] uppercase tracking-wider mt-0.5 flex gap-2">
                    <span className={a.reminder_24h_sent_at ? 'text-emerald-700' : 'text-slate-400'}>{a.reminder_24h_sent_at ? '✓ 24h sent' : a.reminder_24h_enabled ? '· 24h pending' : '· 24h off'}</span>
                    <span className={a.reminder_1h_sent_at ? 'text-emerald-700' : 'text-slate-400'}>{a.reminder_1h_sent_at ? '✓ 1h sent' : a.reminder_1h_enabled ? '· 1h pending' : '· 1h off'}</span>
                  </div>
                </div>
                <button onClick={() => cancelAppt(a.id)} className="text-[0.6rem] font-bold uppercase text-red-700 hover:text-red-900">Cancel</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// AI Accountant — chat panel
//
// Conversational CFO/FP&A agent the operator can ask money questions
// to. Has read-only tools that compute the same math as the P&L,
// Overrides, Retention, and Persistency views — but synthesizes,
// benchmarks, and recommends.
//
// Architecture mirrors OnboardingAgent — tools defined on the server
// (api/agents/accountant.js), executed here, tool_results posted back
// to continue the conversation.
//
// The accountant ALWAYS pairs numbers with benchmarks, frames in $,
// and ends with a ranked action. Skeptical, terse, honest.
// ════════════════════════════════════════════════════════════════════

function AccountantChat({
  loading, open, setOpen,
  chat = [], setChat,
  transactions = [], producers = {}, producerList = [], leads = [], calls = [],
  settings = {}, goalConfig, financialConfig, conservation = [],
  getCarrier = () => 'Unknown', currentUser = null,
}) {
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState('');
  const scrollRef = React.useRef(null);

  const cfg = { ...DEFAULT_FINANCIAL_CONFIG, ...(financialConfig || {}) };
  const goal = { ...DEFAULT_GOAL_CONFIG, ...(goalConfig || {}) };

  // ── Tool handler implementations ──────────────────────────────────
  // All read-only. Same math as the dashboards, packaged as JSON the
  // accountant can analyze and quote back in conversation.
  const tools = React.useMemo(() => ({
    readPnL: () => {
      const now = Date.now();
      const DAY = 1000 * 60 * 60 * 24;
      const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
      const ytdStart   = new Date(new Date().getFullYear(), 0, 1).getTime();
      const w90Start   = now - 90 * DAY;
      const inWindow = (tx, from) => {
        const d = parseDate(tx.activityDate);
        return d && d.getTime() >= from && d.getTime() <= now;
      };
      const agg = (txs) => {
        let g = 0, cb = 0;
        for (const tx of txs) {
          const v = Number(tx.netToAccount) || 0;
          if (v >= 0) g += v; else cb += Math.abs(v);
        }
        return { gross: g, chargebacks: cb, net: g - cb };
      };
      const mtdRaw = agg(transactions.filter(t => inWindow(t, monthStart)));
      const w90Raw = agg(transactions.filter(t => inWindow(t, w90Start)));
      const ytdRaw = agg(transactions.filter(t => inWindow(t, ytdStart)));
      const leadSp = (period) => (leads || [])
        .filter(l => {
          const ca = Date.parse(l.createdAt || '');
          return Number.isFinite(ca) && ca >= period;
        })
        .reduce((s, l) => s + (Number(l.cost) || 0), 0);
      const realizedComm = goal.avgAnnualPremium * goal.commissionRate * (1 - goal.lapseRate);
      const dealsNeeded = Math.ceil(goal.monthlyTarget / realizedComm);
      const vasNeeded = Math.max(1, Math.ceil((dealsNeeded * 5 / goal.dialToContact / goal.contactToAppt / goal.apptToShow / goal.showToSale) / goal.workingDays / goal.dialsPerVaPerDay));
      const monthsElapsed = (now - ytdStart) / (DAY * 30);
      const pnl = (raw, leadSp, vaScale, overhead) => {
        const overridePaid = raw.net * cfg.avgProducerContractLevel;
        const agencyOverride = raw.net - overridePaid;
        const netContribution = agencyOverride - leadSp - vaScale - overhead;
        return { ...raw, overridePaid, agencyOverride, leadSpend: leadSp, vaCost: vaScale, overhead, netContribution };
      };
      return {
        mtd: pnl(mtdRaw, leadSp(monthStart), vasNeeded * goal.vaBasePay * 0.5, cfg.overheadMonthly * 0.5),
        trailing90: pnl(w90Raw, leadSp(w90Start), vasNeeded * goal.vaBasePay * 3, cfg.overheadMonthly * 3),
        ytd: pnl(ytdRaw, leadSp(ytdStart), vasNeeded * goal.vaBasePay * monthsElapsed, cfg.overheadMonthly * monthsElapsed),
        note: 'PnL math: net commission = gross - chargebacks; agency override = net * (1 - avgProducerContractLevel). Lead/VA/overhead are period-scaled.',
      };
    },
    readOverrideIntelligence: () => {
      const now = Date.now();
      const w90Start = now - 90 * 24 * 60 * 60 * 1000;
      const tx90 = transactions.filter(t => {
        const d = parseDate(t.activityDate);
        return d && d.getTime() >= w90Start;
      });
      const byProd = {};
      for (const tx of tx90) {
        const k = tx.prodNum || '(unassigned)';
        if (!byProd[k]) byProd[k] = { prodNum: k, gross: 0, chargebacks: 0 };
        const v = Number(tx.netToAccount) || 0;
        if (v >= 0) byProd[k].gross += v; else byProd[k].chargebacks += Math.abs(v);
      }
      const rows = Object.values(byProd).map(r => {
        const lvl = (producers[r.prodNum]?.contractLevel != null ? producers[r.prodNum].contractLevel : cfg.avgProducerContractLevel);
        const net = r.gross - r.chargebacks;
        const overrideEarned = net * (1 - lvl);
        const name = getProducerName ? getProducerName(r.prodNum, producers) : r.prodNum;
        const cbRate = r.gross > 0 ? r.chargebacks / r.gross : 0;
        return { name, prodNum: r.prodNum, contractLevel: lvl, overrideEarned, gross: r.gross, chargebacks: r.chargebacks, chargebackRate: cbRate };
      }).sort((a, b) => b.overrideEarned - a.overrideEarned);
      const total = rows.reduce((s, r) => s + Math.max(0, r.overrideEarned), 0);
      const shares = rows.map(r => Math.max(0, r.overrideEarned) / Math.max(1, total));
      const hhi = Math.round(shares.reduce((s, x) => s + (x * 100) ** 2, 0));
      return {
        total_override_90d: total,
        producer_count: rows.length,
        top_3: rows.slice(0, 3),
        concentration_hhi: hhi,
        top1_share_pct: shares[0] ? +(shares[0] * 100).toFixed(1) : 0,
        top3_share_pct: +(shares.slice(0, 3).reduce((s, x) => s + x, 0) * 100).toFixed(1),
      };
    },
    readCashFlow: () => {
      const realizedComm = goal.avgAnnualPremium * goal.commissionRate * (1 - goal.lapseRate);
      const dealsNeeded = Math.ceil(goal.monthlyTarget / realizedComm);
      const expectedCashIn30 = dealsNeeded * goal.avgAnnualPremium * goal.commissionRate * cfg.advanceRate;
      const vasNeeded = Math.max(1, Math.ceil((dealsNeeded * 5) / (goal.dialToContact * goal.contactToAppt * goal.apptToShow * goal.showToSale) / goal.workingDays / goal.dialsPerVaPerDay));
      const monthLeadSp = (leads || []).slice(-30).reduce((s, l) => s + (Number(l.cost) || 0), 0);
      const expectedOverridePaid = expectedCashIn30 * cfg.avgProducerContractLevel;
      const cashOut30 = expectedOverridePaid + monthLeadSp + (vasNeeded * goal.vaBasePay) + cfg.overheadMonthly;
      const netCash30 = expectedCashIn30 - cashOut30;
      const now = Date.now();
      const w90Tx = transactions.filter(t => {
        const d = parseDate(t.activityDate);
        return d && d.getTime() >= now - 90 * 24 * 60 * 60 * 1000;
      });
      const w90Gross = w90Tx.filter(t => (t.netToAccount || 0) > 0).reduce((s, t) => s + Number(t.netToAccount), 0);
      const reserveNeeded = w90Gross * goal.lapseRate;
      return {
        next_30d_cash_in: expectedCashIn30,
        next_30d_cash_out: cashOut30,
        next_30d_net_cash: netCash30,
        chargeback_reserve_needed: reserveNeeded,
        working_capital_floor: cashOut30 + reserveNeeded,
        note: 'Cash IN uses goalConfig forecast × advance %. Cash OUT covers override paid, lead spend, VA, overhead. Reserve = 90d gross × lapse rate.',
      };
    },
    readUnitEconomics: () => {
      const realizedFirstYearComm = goal.avgAnnualPremium * goal.commissionRate * (1 - goal.lapseRate);
      const renewalLTV = goal.avgAnnualPremium * cfg.renewalRate * cfg.expectedClientYears * (1 - goal.lapseRate);
      const ltv = realizedFirstYearComm + renewalLTV;
      const now = Date.now();
      const w90Start = now - 90 * 24 * 60 * 60 * 1000;
      const leadSp90 = (leads || [])
        .filter(l => Date.parse(l.createdAt || '') >= w90Start)
        .reduce((s, l) => s + (Number(l.cost) || 0), 0);
      const dealsClosed90 = (leads || []).filter(l => l.status === 'closed' && Date.parse(l.closedAt || l.updatedAt || '') >= w90Start).length;
      const cac = dealsClosed90 > 0 ? leadSp90 / dealsClosed90 : null;
      const ltvCac = cac ? ltv / cac : null;
      return {
        ltv_per_deal: ltv,
        ltv_breakdown: { first_year_realized: realizedFirstYearComm, renewals_total: renewalLTV },
        blended_cac: cac,
        deals_closed_90d: dealsClosed90,
        ltv_cac_ratio: ltvCac,
        contribution_per_deal: cac ? realizedFirstYearComm - cac : null,
        contribution_margin: cac ? (realizedFirstYearComm - cac) / realizedFirstYearComm : null,
      };
    },
    readLeadROIBySource: () => {
      const sources = {};
      (leads || []).forEach(l => {
        const s = l.source || '(unknown)';
        if (!sources[s]) sources[s] = { source: s, leads: 0, spend: 0, closed: 0, premium: 0 };
        sources[s].leads++;
        sources[s].spend += Number(l.cost) || 0;
        if (l.status === 'closed') {
          sources[s].closed++;
          sources[s].premium += Number(l.premium) || 0;
        }
      });
      return Object.values(sources).map(r => ({
        ...r,
        cost_per_lead: r.leads > 0 ? r.spend / r.leads : 0,
        cpa: r.closed > 0 ? r.spend / r.closed : null,
        close_rate: r.leads > 0 ? (r.closed / r.leads) * 100 : 0,
        annualized_premium_per_close: r.closed > 0 ? (r.premium * 12) / r.closed : 0,
        roi_pct: r.spend > 0 ? (((r.premium * 12) - r.spend) / r.spend) * 100 : null,
      })).sort((a, b) => b.leads - a.leads);
    },
    readPersistencyHealth: () => {
      const policies = {};
      for (const tx of transactions) {
        if (!tx.policy) continue;
        const k = `${tx.prodNum || ''}|${tx.mga || ''}|${tx.policy}`;
        if (!policies[k]) policies[k] = { issueDate: tx.issueDate, premium: 0, totalCommission: 0, lapsed: false };
        const v = Number(tx.netToAccount) || 0;
        policies[k].totalCommission += v;
        if (tx.commPremium && Math.abs(tx.commPremium) > policies[k].premium) policies[k].premium = Math.abs(tx.commPremium);
        if (v < 0) policies[k].lapsed = true;
      }
      const polArr = Object.values(policies);
      const active = polArr.filter(p => !p.lapsed && p.totalCommission > 0);
      const persistencyRate = polArr.length > 0 ? active.length / polArr.length : 0;
      const inForceMonthly = active.reduce((s, p) => s + p.premium, 0);
      const inForceAnnual = inForceMonthly * 12;
      const adjustedMult = cfg.enterpriseValueMultiple * Math.max(0.5, Math.min(1.3, persistencyRate / 0.75));
      return {
        active_policies: active.length,
        total_policies: polArr.length,
        persistency_rate: +(persistencyRate * 100).toFixed(1),
        monthly_in_force_ap: inForceMonthly,
        annualized_in_force_ap: inForceAnnual,
        persistency_adjusted_multiple: +adjustedMult.toFixed(2),
        estimated_enterprise_value: Math.round(inForceAnnual * adjustedMult),
      };
    },
    readAtRiskBook: () => {
      // Quick approximation — full risk math lives in RetentionHub
      const now = Date.now();
      const w90Start = now - 90 * 24 * 60 * 60 * 1000;
      const recentLapse = transactions.filter(t => {
        const v = Number(t.netToAccount) || 0;
        if (v >= 0) return false;
        const d = parseDate(t.activityDate);
        return d && d.getTime() >= w90Start;
      });
      const lapsedClients = new Set(recentLapse.map(t => `${t.insured || ''}|${t.policy || ''}`));
      const atRiskMrr = recentLapse.reduce((s, t) => s + Math.abs(Number(t.commPremium) || 0), 0);
      return {
        lapsed_in_last_90d: lapsedClients.size,
        at_risk_mrr_estimate: atRiskMrr,
        recently_lapsed_chargeback_amount: recentLapse.reduce((s, t) => s + Math.abs(Number(t.netToAccount) || 0), 0),
        note: 'Use the Retention Hub for full scoring — this is the 90-day rollup.',
      };
    },
    readGoalProgress: () => {
      const realizedComm = goal.avgAnnualPremium * goal.commissionRate * (1 - goal.lapseRate);
      const dealsNeeded = Math.ceil(goal.monthlyTarget / realizedComm);
      const dialsNeeded = Math.ceil(dealsNeeded * 5 / (goal.dialToContact * goal.contactToAppt * goal.apptToShow * goal.showToSale));
      const dailyDialTarget = Math.round(dialsNeeded / goal.workingDays);
      const today = new Date().toISOString().slice(0, 10);
      const monthStartStr = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
      const monthCalls = calls.filter(c => c.direction === 'outbound' && (c.date || '') >= monthStartStr);
      const dialsMtd = monthCalls.length;
      const dialsToday = monthCalls.filter(c => c.date === today).length;
      const closedThisMonth = leads.filter(l => l.status === 'closed' && (l.closedAt || l.updatedAt || '') >= monthStartStr);
      const commMtd = closedThisMonth.reduce((s, l) => s + (Number(l.premium) || 0) * 12 * goal.commissionRate * (1 - goal.lapseRate), 0);
      let workdaysElapsed = 0;
      for (let d = new Date(new Date().getFullYear(), new Date().getMonth(), 1); d <= new Date() && workdaysElapsed < goal.workingDays; d.setDate(d.getDate() + 1)) {
        const dow = d.getDay();
        if (dow !== 0 && dow !== 6) workdaysElapsed++;
      }
      const expected = (goal.monthlyTarget * workdaysElapsed) / goal.workingDays;
      const pace = commMtd >= expected ? 'ON_TRACK' : commMtd >= expected * 0.75 ? 'WATCH' : 'BEHIND';
      return {
        monthly_target: goal.monthlyTarget,
        deals_needed_month: dealsNeeded,
        dials_needed_month: dialsNeeded,
        daily_dial_target: dailyDialTarget,
        dials_mtd: dialsMtd,
        dials_today: dialsToday,
        sales_mtd: closedThisMonth.length,
        realized_commission_mtd: Math.round(commMtd),
        expected_by_now: Math.round(expected),
        pace,
      };
    },
    compareToBenchmark: ({ metric, value }) => {
      // FEX/MP/IUL industry benchmarks — sourced from the framework + research
      const benchmarks = {
        persistency_13mo:        { elite: 85, healthy_min: 75, distressed_below: 70, unit: '%' },
        chargeback_rate:         { elite: 10, healthy_max: 15, distressed_above: 20, unit: '% of FYC' },
        placement_rate:          { elite: 70, healthy_min: 55, distressed_below: 55, unit: '%' },
        contact_rate:            { elite: 25, healthy_min: 15, distressed_below: 10, unit: '%' },
        lead_to_app:             { elite: 6,  healthy_min: 3,  distressed_below: 2,  unit: '%' },
        close_rate:              { elite: 30, healthy_min: 20, distressed_below: 15, unit: '%' },
        ltv_cac:                 { elite: 5,  healthy_min: 3,  distressed_below: 3,  unit: 'x' },
        contribution_margin:     { elite: 60, healthy_min: 40, distressed_below: 30, unit: '%' },
        override_spread:         { elite: 25, healthy_min: 15, distressed_below: 10, unit: 'pts' },
        concentration_hhi:       { elite: 1500, healthy_max: 2500, distressed_above: 2500, unit: 'HHI', reverse: true },
        agent_year1_retention:   { elite: 50, healthy_min: 25, distressed_below: 10, unit: '%' },
        time_to_first_policy:    { elite: 14, healthy_max: 30, distressed_above: 60, unit: 'days', reverse: true },
        net_contribution_margin: { elite: 40, healthy_min: 20, distressed_below: 10, unit: '%' },
        cac_per_deal:            { elite: 100, healthy_max: 200, distressed_above: 300, unit: '$', reverse: true },
      };
      const b = benchmarks[metric];
      if (!b) return { error: `Unknown metric: ${metric}` };
      let tier;
      if (b.reverse) {
        if (value <= b.elite) tier = 'ELITE';
        else if (value <= (b.healthy_max || b.healthy_min)) tier = 'HEALTHY';
        else tier = 'DISTRESSED';
      } else {
        if (value >= b.elite) tier = 'ELITE';
        else if (value >= b.healthy_min) tier = 'HEALTHY';
        else tier = 'DISTRESSED';
      }
      return { metric, value, tier, benchmarks: b };
    },
    flagAnomalies: () => {
      const flags = [];
      const pers = tools.readPersistencyHealth();
      if (pers.persistency_rate < 70) flags.push({ severity: 'high', issue: `Persistency ${pers.persistency_rate}% is below 70% threshold — book is bleeding.` });
      const goalP = tools.readGoalProgress();
      if (goalP.pace === 'BEHIND') flags.push({ severity: 'high', issue: `Monthly pace is BEHIND. Realized ${fmt(goalP.realized_commission_mtd)} vs expected ${fmt(goalP.expected_by_now)}.` });
      const overrides = tools.readOverrideIntelligence();
      if (overrides.top1_share_pct > 40) flags.push({ severity: 'high', issue: `${overrides.top1_share_pct}% of override revenue concentrated in one producer. Key-agent risk.` });
      if (overrides.concentration_hhi > 2500) flags.push({ severity: 'medium', issue: `HHI ${overrides.concentration_hhi} — highly concentrated portfolio. PE diligence discount.` });
      const unitEcon = tools.readUnitEconomics();
      if (unitEcon.ltv_cac_ratio != null && unitEcon.ltv_cac_ratio < 3) flags.push({ severity: 'high', issue: `LTV/CAC ${unitEcon.ltv_cac_ratio.toFixed(1)}× below 3× threshold. Cut low-ROI lead sources.` });
      const cash = tools.readCashFlow();
      if (cash.next_30d_net_cash < 0) flags.push({ severity: 'critical', issue: `Next 30d net cash projected NEGATIVE (${fmt(cash.next_30d_net_cash)}). Cut spend or raise capital.` });
      const atRisk = tools.readAtRiskBook();
      if (atRisk.lapsed_in_last_90d > 5) flags.push({ severity: 'medium', issue: `${atRisk.lapsed_in_last_90d} policies lapsed in last 90d. Activate Win-Back drip + review carrier mix.` });
      return { flags, count: flags.length };
    },
  }), [transactions, producers, leads, calls, conservation, goal.monthlyTarget, cfg.avgProducerContractLevel, cfg.overheadMonthly]);

  // Auto-scroll
  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [chat, busy]);

  async function sendUserMessage(text) {
    const trimmed = (text || '').trim();
    if (!trimmed) return;
    const userMsg = { role: 'user', content: trimmed };
    const next = [...(chat || []), userMsg];
    setChat(next);
    setInput('');
    await runConversation(next);
  }
  async function runConversation(currentMessages, depth = 0) {
    if (depth > 10) {
      setChat(m => [...m, { role: 'assistant', content: [{ type: 'text', text: '(stopped — too many tool calls)' }] }]);
      return;
    }
    setBusy(true);
    try {
      const res = await fetch('/api/agents/accountant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await bearer()) },
        body: JSON.stringify({
          messages: currentMessages,
          systemContext: {
            ownerName: settings.ownerName || '',
            agencyName: settings.agencyName || '',
          },
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        setChat(m => [...m, { role: 'assistant', content: [{ type: 'text', text: `(error: ${json.error || res.statusText})` }] }]);
        return;
      }
      const blocks = Array.isArray(json.content) ? json.content : [];
      const withAssistant = [...currentMessages, { role: 'assistant', content: blocks }];
      setChat(withAssistant);
      const toolUses = blocks.filter(b => b && b.type === 'tool_use');
      if (toolUses.length === 0) return;
      const toolResults = await Promise.all(toolUses.map(async (tu) => {
        const handler = tools[tu.name];
        let result;
        try { result = handler ? await handler(tu.input || {}) : { error: `unknown tool: ${tu.name}` }; }
        catch (e) { result = { error: e.message || String(e) }; }
        return { type: 'tool_result', tool_use_id: tu.id, content: JSON.stringify(result) };
      }));
      const withResults = [...withAssistant, { role: 'user', content: toolResults }];
      setChat(withResults);
      await runConversation(withResults, depth + 1);
    } catch (err) {
      setChat(m => [...m, { role: 'assistant', content: [{ type: 'text', text: `(network error: ${err.message || err})` }] }]);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return null;
  if (!open) return null;

  return (
    <div className="fixed inset-y-0 right-0 z-40 w-full sm:w-[460px] bg-white shadow-2xl border-l border-slate-200 flex flex-col">
      <div className="bg-gradient-to-br from-emerald-800 to-slate-900 text-white px-4 py-3 flex items-center justify-between gap-2">
        <div>
          <div className="text-xs uppercase tracking-wider text-emerald-300">AI Accountant</div>
          <div className="font-semibold">Your CFO / FP&A director</div>
          <div className="text-[10px] text-emerald-200 mt-0.5">Read-only. Asks numbers, recommends, benchmarks against the industry.</div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setChat([])} title="Restart" className="text-emerald-200 hover:text-white p-1.5 text-xs">↺</button>
          <button onClick={() => setOpen(false)} title="Close" className="text-emerald-200 hover:text-white p-1.5"><X size={18}/></button>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-auto px-3 py-3 space-y-2 bg-slate-50">
        {chat.length === 0 && (
          <div className="text-center text-sm text-slate-500 px-4 py-8 space-y-3">
            <div>Ask the accountant anything money-related.</div>
            <div className="grid gap-1.5">
              {[
                'How am I doing this month?',
                'What\'s my LTV/CAC?',
                'Should I increase lead spend?',
                'Which producer is most at risk?',
                'What\'s the cash flow look like next 30 days?',
                'Where is my override leaking?',
                'What\'s my enterprise value?',
              ].map((q) => (
                <button key={q} onClick={() => sendUserMessage(q)} className="text-xs text-left bg-white hover:bg-emerald-50 border border-slate-200 hover:border-emerald-300 rounded px-2 py-1.5 text-slate-700">{q}</button>
              ))}
            </div>
          </div>
        )}
        {chat.map((m, i) => <AccountantMessage key={i} msg={m}/>)}
        {busy && (
          <div className="flex items-center gap-2 text-xs text-slate-500 italic px-3 py-2">
            <span className="inline-block w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"/>
            <span>analyzing…</span>
          </div>
        )}
      </div>

      <div className="border-t border-slate-200 p-3 bg-white">
        <form onSubmit={(e) => { e.preventDefault(); sendUserMessage(input); }} className="flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={busy ? 'analyzing…' : 'Ask a money question…'}
            disabled={busy}
            className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none"
          />
          <button type="submit" disabled={busy || !input.trim()} className="bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white text-sm font-semibold rounded-lg px-3 py-2">
            Ask
          </button>
        </form>
        <div className="text-[10px] text-slate-400 mt-1.5">
          Tools: P&L · Overrides · Cash Flow · Unit Economics · Lead ROI · Persistency · At-Risk Book · Goal Progress · Benchmarks · Anomaly flags
        </div>
      </div>
    </div>
  );
}

function AccountantMessage({ msg }) {
  if (!msg) return null;
  const isUser = msg.role === 'user';
  if (isUser && Array.isArray(msg.content) && msg.content.length > 0 && msg.content[0].type === 'tool_result') {
    return (
      <div className="text-[10px] text-slate-400 text-center my-1">
        ✓ {msg.content.length} data point{msg.content.length === 1 ? '' : 's'} pulled
      </div>
    );
  }
  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] bg-emerald-700 text-white rounded-2xl rounded-br-sm px-3 py-2 text-sm">
          {typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content)}
        </div>
      </div>
    );
  }
  const blocks = Array.isArray(msg.content) ? msg.content : [];
  return (
    <div className="flex justify-start">
      <div className="max-w-[90%] bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3 py-2 text-sm space-y-1.5">
        {blocks.map((b, i) => {
          if (!b) return null;
          if (b.type === 'text') return <div key={i} className="whitespace-pre-wrap text-slate-800 leading-relaxed">{b.text}</div>;
          if (b.type === 'tool_use') {
            return (
              <div key={i} className="text-[11px] bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1 font-mono text-emerald-700">
                pulling {b.name}…
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}
